import React, { useState, useEffect, useCallback, useMemo, useRef, lazy, Suspense } from 'react';
import { supabase } from './supabase';
// v7.49: 택배사 시스템 호환을 위해 .xls (BIFF8) 출력 — 아래 XLSX import 재활용
import * as XLSX from 'xlsx';
import FlowingMenu from './FlowingMenu';
// v7.262: BorderGlow — 행 글로우(배송시트지 실사용). 원본 CSS 미제공이라 새로 작성했고, CSS 는 컴포넌트가 런타임 주입한다.
//   v7.328: 실험실이 빠지면서 카드형(default export)은 더 이상 쓰지 않는다 → named 만 가져온다.
//   ⚠ 안 쓰는 import 를 남기면 CRA 빌드에서 no-unused-vars 경고가 나고,
//     CI 환경변수가 켜진 도커 빌드에서는 경고가 에러로 승격돼 빌드가 깨진다.
import { useRowGlow, rowGlowVars } from './BorderGlow';
// v7.328: Lightfall — 테스트 탭 실험실. ⚠ ogl 패키지 필요 ("ogl": "^1.0.11")
import Lightfall from './Lightfall';
// v7.329: Particles — 다크 모드 사이드바 배경 (같은 ogl 을 쓴다)
import Particles from './Particles';
// v7.339: NexiMap — 넥시 창고 1층 도면(SVG). 위치 코드 하나를 받아 해당 칸을 하이라이트한다.
//   ⚠ ogl 같은 외부 의존성 없음 — 순수 SVG 라 package.json 손댈 필요 없다.
import NexiMap, { NEXI_CODES, normalizeNexiCode, isNexiCode } from './NexiMap';
// v7.341: 2층 배치도. 1층과 성격이 다르다 — 1층은 칸에 '위치 코드'가 적혀 있지만
//   2층은 칸에 적힌 값이 곧 '주문코드'라 도면 자체가 데이터다.
//   데이터(NexiFloor2Data.js)는 구글시트 [2층 찾기] 탭에서 기계 추출한 것이다.
// v7.355: reactbits GlassIcons + 폴더 글리프. SVG 라 이미지 파일이 필요 없다
//   (public/ 은 NAS 에만 있어 여기서 파일을 못 넣는다).
import GlassIcons, { FolderGlyph } from './GlassIcons';
import NexiFloor2 from './NexiFloor2';
import { f2Find, F2_CODE_COUNT } from './NexiFloor2Data';
import ClickSpark from './ClickSpark';
import EventCalendar from './EventCalendar';
import Strands from './Strands';
import Minesweeper from './Minesweeper';
// v7.371: 🎆 불꽃놀이 이스터에그 — 빈 공간 10회 클릭 시 발동. 순수 canvas, 의존성 0.
import FireworksShow from './FireworksShow';
// v7.87: Mirror는 lazy load — Three.js ~200KB gzipped를 초기 번들에서 제외
//         'mirror' 이스터에그 트리거 시점에만 청크 fetch
const Mirror = lazy(() => import('./Mirror'));
// v7.91: AimTrainer도 동일 패턴 — 'aim' 트리거 시점에만 청크 fetch
const AimTrainer = lazy(() => import('./AimTrainer'));
// v7.113: MobileScan — 모바일 입고 스캔 페이지 (BarcodeDetector + getUserMedia)
const MobileScan = lazy(() => import('./MobileScan'));
// v7.118: MobileInventory — 재고 목록 모바일 전용 뷰 (카드 리스트 + 검색 + 필터)
const MobileInventory = lazy(() => import('./MobileInventory'));
// v7.119: MobileShipRequest — 제품 출고 요청 모바일 전용 뷰 (외부 사용)
const MobileShipRequest = lazy(() => import('./MobileShipRequest'));
// v7.150: MobileTxLog — 배송시트지 모바일 전용 뷰 (목록/상세 + 검색/필터 + 배송처리 진입)
const MobileTxLog = lazy(() => import('./MobileTxLog'));
// ─── v7.4: Service 레이어 — DB 책임별 격리 (토큰 효율적 작업을 위한 모듈 분리) ───
import { AliasService } from './services/AliasService';
import { MemoService } from './services/MemoService';
import { ProductService } from './services/ProductService';
import { InventoryService } from './services/InventoryService';
import { AnalyticsService } from './services/AnalyticsService';
import { DemoService, DEMO_CATEGORY_LABEL, DEMO_CATEGORY_COLOR, DEMO_STATUS_COLOR, DEMO_VALID_STATUS, DEMO_RESTORE_STATUS } from './services/DemoService';
import { ShipPendingService } from './services/ShipPendingService';
import { AttachmentService } from './services/AttachmentService';
import { PajuStockService, normalizeProductName } from './services/PajuStockService';
// v7.358: QNAP 공식 호환성 목록 (n8n 이 주 1회 수집한 사본을 읽는다)
import { QnapCompatService, QNAP_CAT, QNAP_CAT_LABEL, qnapNorm } from './services/QnapCompatService';
// v7.364: 사내 호환성 테스트 — QNAP 공식 목록과 [다른 소스] 다. 절대 한 배열에 합치지 않는다.
import { InternalCompatService, IC_RESULT, IC_RESULT_LABEL, IC_BRANDS } from './services/InternalCompatService';
import SharedFilesPanel from './SharedFilesPanel';

// ─── v7.392: 데스크톱 모드 [전면 제거] ────────────────────────
//
//   v7.373~391 에 걸쳐 [폰에서 PC 화면 보기] 를 만들려 했고, 그 과정에서
//   모바일 사용을 반복적으로 망가뜨렸다. 마지막 증상은 앱에서 모바일 UI 인데
//   문서 폭이 넓어져 좌우 스크롤이 생긴 것이다.
//
//   시도한 것과 결과 — 같은 길을 다시 파지 않기 위해 남긴다:
//     ① viewport meta 를 useEffect 에서 setAttribute        → 무시됨
//     ② 모듈 스코프에서 setAttribute                        → 무시됨
//     ③ meta 를 remove 후 새로 append (재파싱 유도)          → 무시됨 (레이아웃 폭 360 그대로)
//        ★ 크롬은 초기 파싱 이후 viewport 를 다시 잡아 주지 않는다.
//     ④ html/body/#root 에 min-width:1280 + CSS zoom        → 동작은 했지만,
//        모드가 꺼진 뒤에도 잔재가 남아 모바일 화면에 좌우 스크롤을 만들었다.
//        상태 조합(앱/폰브라우저/PC × 자동/수동 × 저장값)이 늘면서 감당이 안 됐다.
//
//   ⚠ 다시 만들 거라면 런타임 조작이 아니라 [진입 자체를 분리] 해야 한다.
//     예: /desktop 같은 별도 경로에 viewport 가 처음부터 박힌 index.html 을 두는 방식.
//     public/index.html 은 NAS 에만 있어 tarball 로 안 넘어오니 그쪽 작업이 필요하다.
//
//   지금은 원래대로 돌아간다: 폰 = 모바일 전용 화면 / PC = PC 화면.
//   ⚠ 아래 정리 코드는 [한시적] 이다. 구버전이 남긴 <style>·zoom 을 걷어낸다.
//     v7.392 이후 몇 버전 지나 전 직원이 새 빌드를 받았다고 판단되면 지워도 된다.
(function cleanupDesktopModeLeftovers() {
  if (typeof document === 'undefined') return;
  try { document.getElementById('inv-desktop-mode-style')?.remove(); } catch (e) {}
  try { document.body && document.body.classList.remove('inv-desktop-mode'); } catch (e) {}
  try { if (document.documentElement) document.documentElement.style.zoom = ''; } catch (e) {}
  try { localStorage.removeItem('inv_desktop_mode'); localStorage.removeItem('inv_desktop_zoom'); } catch (e) {}
})();


const PG = 100;
const STOCK_TYPE_ORDER = {'신품':0,'AS':1,'리페킹':2,'조립출고':3};

// ─── v7.336: 연락처 하이픈 자동 입력 ─────────────────────────────────
//   한국 번호는 국번 자리수가 제각각이라 '3-4-4 고정' 으로는 안 된다.
//     02-1234-5678(서울 2자리) / 0507-1407-8923(안심번호 4자리)
//     1588-1234(대표번호, 8자리) / 031-123-4567(지역 3자리)
//   앞자리를 보고 갈래를 정한 뒤, 입력 도중(자릿수가 덜 찬 상태)에도
//   어색하지 않게 부분 포맷을 돌려준다.
const formatPhoneNumber = (raw) => {
  const d = String(raw == null ? '' : raw).replace(/[^0-9]/g, '').slice(0, 12);
  if (!d) return '';

  // 대표번호 15xx / 16xx / 18xx — 8자리 4-4 (지역번호가 없다)
  if (/^1[568]/.test(d) && d.length <= 8) {
    return d.length > 4 ? `${d.slice(0, 4)}-${d.slice(4)}` : d;
  }
  // 안심번호 050x — 4-4-4
  if (/^050\d/.test(d)) {
    if (d.length <= 4) return d;
    if (d.length <= 8) return `${d.slice(0, 4)}-${d.slice(4)}`;
    return `${d.slice(0, 4)}-${d.slice(4, 8)}-${d.slice(8, 12)}`;
  }
  // 서울 02 — 2-3-4 또는 2-4-4
  if (d.startsWith('02')) {
    if (d.length <= 2) return d;
    if (d.length <= 5) return `${d.slice(0, 2)}-${d.slice(2)}`;
    if (d.length <= 9) return `${d.slice(0, 2)}-${d.slice(2, 5)}-${d.slice(5)}`;
    return `${d.slice(0, 2)}-${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }
  // 그 외 (010, 031, 070 …) — 3-3-4 또는 3-4-4
  if (d.length <= 3) return d;
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7, 11)}`;
};

// onChange 용. prev 를 같이 받는 이유:
//   백스페이스로 하이픈을 지우면 숫자는 그대로라 포맷 함수가 하이픈을 되살린다.
//   → 지운 것 같은데 안 지워지는 상태가 되어 무한히 눌러도 안 넘어간다.
//   길이는 줄었는데 숫자가 그대로면 '하이픈을 지우려 한 것' 으로 보고 앞 숫자를 하나 더 지운다.
const phoneOnChange = (prev, next) => {
  const prevStr = String(prev == null ? '' : prev);
  const nextStr = String(next == null ? '' : next);
  let d = nextStr.replace(/[^0-9]/g, '');
  if (nextStr.length < prevStr.length && d === prevStr.replace(/[^0-9]/g, '')) {
    d = d.slice(0, -1);
  }
  return formatPhoneNumber(d);
};

// ─── v7.329: 사이드바 배경 ──────────────────────────────────────────
//   라이트 = 해변 사진 (기존 그대로)
//   다크   = 사진 대신 Particles (WebGL 별가루) + 짙은 남색 그라디언트
//     사진 위에 어두운 막을 씌우는 방식은 결국 '어두워진 사진' 이라 답답했다.
//     다크에서는 배경 자체를 바꾼다.
//
//   ⚠ 캔버스는 절대배치 레이어로 깔고, 사이드바의 직계 자식들에는 z-index 를 준다.
//     CSS 페인트 순서상 'position 이 있고 z-index:0 인 요소' 는
//     'position 없는 일반 블록의 글자' 보다 위에 그려진다.
//     그래서 자식들을 position:relative + z-index:1 로 올리지 않으면
//     메뉴 글자가 캔버스에 덮인다.
//   ⚠ 캔버스에는 pointer-events:none 을 준다. 안 그러면 메뉴 클릭이 막힌다.
//     (moveParticlesOnHover 를 끈 이유이기도 하다 — 마우스를 받으려면
//      pointer-events 를 살려야 하는데 그러면 메뉴를 못 누른다)
const sidebarBgOf = (isDark) => isDark
  ? { bg: 'linear-gradient(180deg,#080f1c 0%,#0c1a2e 45%,#091426 100%)', invert: true, particles: true }
  : { bg: `linear-gradient(rgba(239,244,255,0.55), rgba(239,244,255,0.72)), url(${process.env.PUBLIC_URL}/sidebar-bg.jpg) center/cover no-repeat`,
      invert: false, particles: false };

// 사이드바 파티클 설정.
//   v7.332: v7.329 값(140개·크기 70)은 너무 성겼다. 사이드바는 256px 폭이라
//     3D 로 흩뿌린 입자 상당수가 화면 밖으로 나간다. 개수를 올리고
//     spread 를 좁혀(12→8) 보이는 범위에 더 모이게 했다.
//   moveParticlesOnHover: 마우스 반응 켬. 리스너는 캔버스가 아니라
//     사이드바(hoverTargetRef)에 붙는다 — 캔버스는 pointer-events:none 이라
//     이벤트를 못 받기 때문이다. 자세한 건 Particles.jsx 로컬 변경 ③ 참고.
const SIDEBAR_PARTICLES = {
  particleColors: ['#ffffff', '#cfe9ff', '#7fe3ff'],
  particleCount: 520,
  particleSpread: 8,
  speed: 0.12,
  particleBaseSize: 110,
  sizeRandomness: 1,
  alphaParticles: true,
  disableRotation: false,
  moveParticlesOnHover: true,
  particleHoverFactor: 2,
  pixelRatio: 1,
};

// v7.333: FlowingMenu 마퀴(호버 시 흘러가는 띠) 색.
//   원래 요청대로 알파 50% 로 되돌린다.
//   v7.326 에서 0.5 로 넣었을 때 원본 라벨이 비쳐 겹쳐 보였고(v7.327 에서 0.95 로
//   덮어 가렸다), 이번에 FlowingMenu 쪽에서 띠가 올라오는 동안 라벨을 페이드 아웃하게
//   고쳤으므로 반투명이어도 겹치지 않는다.
//   ⚠ 글자색: 기존 marqueeTextColor 는 var(--bg-card)(라이트에서 흰색)라
//     흰 띠 위에 흰 글자가 됐다. 진한 남색으로 넘긴다.
const SIDEBAR_MARQUEE_BG = 'rgba(255,255,255,0.5)';
const SIDEBAR_MARQUEE_FG = '#0b1c30';

// v7.333: 현재 선택된 메뉴 색. 청록(--accent) 계열이 FlowingMenu.css 에 박혀 있어
//   App.js 에서 못 바꾸던 것을 prop 으로 뺐다. 흰 배경 + 검정 글자.
const SIDEBAR_ACTIVE_BG = '#ffffff';
const SIDEBAR_ACTIVE_FG = '#0b1c30';


// v7.318: 배송시트지 행 글로우 — [테스트] 탭에서 확정한 값 그대로.
//   세기 1.5 / 빛 크기 310px / 가장자리 감도 100 / 테두리 2px / 모서리 6px
//   감도 100 은 '중앙 0 → 가장자리 1' 선형 램프다. 임계값 없이 부드럽게 밝아진다.
// runDuration 은 '한 바퀴' 기준이다 (v7.321). 위/아래 변을 각각 절반씩 쓴다.
const SHIP_GLOW_OPTS = { glowIntensity: 1.5, glowSize: 310, edgeSensitivity: 100, ringWidth: 2, rowRadius: 6, runDuration: 4.5, runBand: 22 };
// v7.319: 행 상태색 — '일이 남았는지' 가 한눈에 보이게.
//   pending(출고요청)만 테두리를 도는 빛(.glow-run)이 따로 붙는다. 나머지는 호버 시에만.
//   취소/취소됨은 글로우 없음 — 이미 흐리게(opacity 0.55) 처리되는 행이라 굳이 강조하지 않는다.
//
// v7.322: 테마별로 다른 팔레트를 쓴다.
//   라이트에서 글로우가 안 보였던 건 모드 탓이 아니라 색 탓이었다.
//   흰 배경(#f8f9ff)에 밝기 66~70% 짜리 형광색을 올리니 묻힌 것이다.
//   → 라이트는 밝기를 40%대로 낮춰 '진한 색' 으로, 세기도 1.5 → 1.0 으로 내린다.
//     다크는 배경이 #0f172a 라 밝은 색이 맞다. 지금 값 유지.
const SHIP_GLOW_HUES_DARK = {
  pending: '0 100 66',      // 출고요청 — 빨강 (+ 도는 빛)
  shipped: '145 80 55',     // 출고완료 — 초록
  inbound: '193 100 55',    // 입고 — 청록
  return:  '285 85 70',     // 환불 — 보라
};
const SHIP_GLOW_HUES_LIGHT = {
  pending: '0 85 48',
  shipped: '145 70 32',
  inbound: '196 90 35',
  return:  '285 60 46',
};
// v7.363: NAS 호환성 [재고 보유] 카드 글로우.
//   배송시트지 출고요청과 같은 방식(BorderGlow)을 초록으로 쓴다.
//   ⚠ 옵션이 다르다. 배송시트지는 폭 1,240px 짜리 행이지만 여기는 310px 짜리 카드다:
//     ・ glowSize 310 → 150  (카드 폭에 맞춘다. 안 줄이면 카드 전체가 균일하게 떠서
//                            빛이 도는 게 아니라 그냥 밝아 보인다)
//     ・ rowRadius 6 → 10    (카드 모서리와 맞춰야 링이 뜨지 않는다)
//     ・ runDuration 4.5 → 3.2 (둘레가 짧아 같은 속도면 느리게 느껴진다)
//     ・ runBand 22 → 30     (짧은 둘레에서는 띠가 길어야 눈에 띈다)
//   ⚠ 라이트/다크 색이 다르다 — 흰 배경에 밝은 형광 초록을 올리면 묻힌다 (v7.322 교훈).
const STOCK_GLOW_OPTS = { glowIntensity: 1.4, glowSize: 150, edgeSensitivity: 100, ringWidth: 2, rowRadius: 10, runDuration: 3.2, runBand: 30 };
const STOCK_GLOW_STYLES = {
  dark:  rowGlowVars({ ...STOCK_GLOW_OPTS, glowColor: '145 85 58' }),
  light: rowGlowVars({ ...STOCK_GLOW_OPTS, glowIntensity: 1.0, glowColor: '145 72 33' }),
};

// 상태별 CSS 변수 묶음을 모듈 로드 시 한 번만 만든다 (행마다 만들 필요가 없다)
const _buildShipGlow = (hues, extra) => Object.fromEntries(
  Object.entries(hues).map(([k, hue]) => [k, rowGlowVars({ ...SHIP_GLOW_OPTS, ...extra, glowColor: hue })])
);
const SHIP_GLOW_STYLES = {
  dark:  _buildShipGlow(SHIP_GLOW_HUES_DARK,  {}),
  light: _buildShipGlow(SHIP_GLOW_HUES_LIGHT, { glowIntensity: 1.0 }),
};
// v7.323: 건별 체크박스 색 (accent-color). 상태색과 같은 계열이라 무엇이 켜지는지 바로 보인다.
const SHIP_GLOW_ACCENT = { pending: '#e53935', shipped: '#2e7d32', inbound: '#0277bd', return: '#8e24aa' };

// ─── v7.309: 파주물류센터 재고 파일 파서 ────────────────────────────
//   ERP [전체매장 재고조회 … 자사재고] 파일.
//   ⚠ 확장자는 .xls 지만 실제 내용은 EUC-KR 로 인코딩된 HTML <table> 이다.
//     (SheetJS 로는 안 열린다. 그래서 HTML 표 파싱이 주 경로다)
//     A 상품종류(품목) | B 상품명(규격) | C 리버네트워크 | D 파주물류센터 | E 합계
//   진짜 xlsx/xls 를 올리는 경우도 대비해 SheetJS 폴백을 둔다.
const _pjStripTags = (h) => String(h)
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;/gi, ' ').replace(/&amp;/gi, '&').replace(/&lt;/gi, '<').replace(/&gt;/gi, '>')
  .trim();
const _pjQty = (v) => {
  const t = String(v == null ? '' : v).replace(/[^0-9-]/g, '');
  if (!t || t === '-') return 0;
  const n = parseInt(t, 10);
  return Number.isFinite(n) ? n : 0;
};
// 헤더 행에서 '상품명' / '파주' 열 위치를 찾는다.
//   열 순서를 하드코딩(B/D)하지 않는 이유: ERP 설정에 따라 매장 열이 늘거나 순서가 바뀐다.
//   못 찾으면 그때만 B(1) / D(3) 로 폴백한다.
const _pjFindHeader = (rows) => {
  for (let i = 0; i < rows.length; i++) {
    const c = rows[i];
    const nameIdx = c.findIndex(x => /상품명|품\s*명|제품명|규격/.test(x));
    const pajuIdx = c.findIndex(x => /파주/.test(x));
    if (nameIdx >= 0 && pajuIdx >= 0) return { i, nameIdx, pajuIdx };
  }
  return null;
};
const _pjRowsFromHtml = (text) => {
  const out = [];
  const trs = text.match(/<tr[\s\S]*?<\/tr>/gi) || [];
  trs.forEach(tr => {
    const tds = tr.match(/<t[dh][\s\S]*?<\/t[dh]>/gi) || [];
    if (tds.length < 2) return;   // colspan 제목 행 제외
    out.push(tds.map(_pjStripTags));
  });
  return out;
};
// returns { ok, rows:[{product_name,qty}], snapshotDate, source, totalRows, skipped, msg }
const parsePajuStockFile = async (file) => {
  try {
    const buf = await file.arrayBuffer();
    let rows = [], source = '', text = '';
    // ① EUC-KR HTML 표 (실제 ERP 내보내기)
    try { text = new TextDecoder('euc-kr').decode(buf); } catch (e) { text = ''; }
    if (!/<t[rd][\s>]/i.test(text)) {
      try { text = new TextDecoder('utf-8').decode(buf); } catch (e) { text = ''; }
    }
    if (/<t[rd][\s>]/i.test(text)) {
      rows = _pjRowsFromHtml(text);
      source = 'html';
    } else {
      // ② 진짜 스프레드시트 폴백
      const wb = XLSX.read(buf, { type: 'array' });
      const sh = wb.Sheets[wb.SheetNames[0]];
      const aoa = XLSX.utils.sheet_to_json(sh, { header: 1, blankrows: false, defval: '' });
      rows = aoa.map(r => r.map(c => String(c == null ? '' : c).trim()));
      source = 'sheet';
    }
    if (!rows.length) return { ok: false, msg: '표를 찾지 못했습니다. 파일 형식을 확인해 주세요.' };

    const hdr = _pjFindHeader(rows);
    const nameIdx = hdr ? hdr.nameIdx : 1;
    const pajuIdx = hdr ? hdr.pajuIdx : 3;
    const start = hdr ? hdr.i + 1 : 0;

    const list = [];
    let skipped = 0;
    for (let i = start; i < rows.length; i++) {
      const c = rows[i];
      if (c.length <= Math.max(nameIdx, pajuIdx)) continue;
      const name = (c[nameIdx] || '').trim();
      if (!name) continue;
      if (/상품명|합\s*계|소\s*계|총\s*계/.test(name)) continue;   // 헤더 반복·합계 행
      const qty = _pjQty(c[pajuIdx]);
      if (qty === 0) { skipped++; continue; }   // 0·빈칸은 저장하지 않는다 (스냅샷 전량 교체라 안전)
      list.push({ product_name: name, qty });
    }

    // 기준일 — 제목의 "2026년08월06일 마감기준", 없으면 파일명의 YYYYMMDD
    let snapshotDate = null;
    const m = (text || '').match(/(\d{4})\s*년\s*(\d{1,2})\s*월\s*(\d{1,2})\s*일/);
    if (m) snapshotDate = `${m[1]}-${String(m[2]).padStart(2,'0')}-${String(m[3]).padStart(2,'0')}`;
    if (!snapshotDate) {
      const f = String(file.name || '').match(/(20\d{2})(\d{2})(\d{2})/);
      if (f) snapshotDate = `${f[1]}-${f[2]}-${f[3]}`;
    }
    return { ok: true, rows: list, snapshotDate, source, totalRows: rows.length, skipped,
             headerFound: !!hdr };
  } catch (e) {
    return { ok: false, msg: e.message || String(e) };
  }
};
// 사내 직원 목록 (작성자/요청자 드롭다운용) — 직급 순서 유지
const STAFF_LIST = ['김중규 이사','김세웅 부장','박윤영 과장','안정현 대리','최승현 주임','윤덕호 사원'];
// v7.240: 시트에 기록할 때는 직책을 뗀 이름만 쓴다 ('김중규 이사' → '김중규').
//   앱 안에서는 동명이인 구분·존칭 때문에 직책을 붙여 두지만, 배차 시트 담당자 칸은
//   이름만 들어가는 칸이라 직책이 붙으면 폭이 넘치고 필터·수식도 어긋난다.
//   ⚠ STAFF_LIST 원문은 그대로 둔다 — staffPhones 맵의 키가 풀네임이다.
//   알려진 직책일 때만 떼어내므로, 직책 없는 이름이나 자유 입력값은 손대지 않는다.
const STAFF_TITLES = ['대표','사장','부사장','전무','상무','이사','본부장','실장','팀장','부장','차장','과장','대리','주임','사원','인턴'];
const stripStaffTitle = (s) => {
  const v = (s || '').trim();
  if (!v) return '';
  const m = v.match(/^(.*\S)\s+(\S+)$/);
  if (m && STAFF_TITLES.includes(m[2])) return m[1].trim();
  return v;
};
// v7.224: 퀵 차량 추천 목록 — 출고요청 폼과 배송시트지 인라인 편집이 함께 사용
const QUICK_VEHICLES = ['오토바이','다마스','라보','1톤 트럭','2.5톤 트럭'];
// 폼팩터(form_factor) 후보 — QNAP 카탈로그의 형태 분류
const FORM_FACTORS = ['Tower NAS','Rackmount NAS','DAS','Switch','NAS','Network Adapter'];
// v7.254: 출처 뱃지 색/라벨 단일 출처. 같은 삼항 사슬이 두 군데(그룹 행/단일 행)에
//   복붙돼 있어서 DOA 를 추가하려면 양쪽을 고쳐야 했다.
const SOURCE_BADGE = {
  taiwan: { bg:'#fff4e5', fg:'#a05000', bd:'#ffb74d', icon:'🇹🇼', label:'대만' },
  paju:   { bg:'#e8f5e9', fg:'#1b5e20', bd:'#81c784', icon:'🇰🇷', label:'파주' },
  hq:     { bg:'#e3f2fd', fg:'#0d47a1', bd:'#64b5f6', icon:'🏢', label:'본사' },
  // DOA 교체품 — 노란색 (사용자 요청). 일반 입고분과 한눈에 구분되어야 한다.
  DOA:    { bg:'#fff8e1', fg:'#8a6d00', bd:'#ffd54f', icon:'⚠️', label:'DOA' },
};
const sourceBadge = (src) =>
  SOURCE_BADGE[src] || { bg:'#e3f2fd', fg:'#0d47a1', bd:'#64b5f6', icon:'🏢', label: src };

// v7.444: 첨부 다운로드 시 원본 파일명 보존.
//   저장 경로는 <ts>_<rand>_<정규화명> 이라 한글 등이 '_' 로 치환돼 있고, <a download> 속성은
//   cross-origin·일부 WebView(앱)에서 무시되어 URL 의 저장명으로 떨어진다(→ 이름 깨짐).
//   blob 으로 받아 object URL(항상 same-origin)에 download 를 걸면 원본명이 확실히 유지된다.
// v7.451: 한글 자모 → QWERTY 영문 역변환.
//   바코드 리더기가 '한글 입력' 상태면 QWERTY 키가 한글 자모로 찍혀(예: TS-1290 → ㅅㄴ-1290)
//   모델코드가 깨진다. 스캔값의 한글을 원래 눌린 영문 키로 되돌린다.
const _HG_CHO = ['r','R','s','e','E','f','a','q','Q','t','T','d','w','W','c','z','x','v','g'];
const _HG_JUNG = ['k','o','i','O','j','p','u','P','h','hk','ho','hl','y','n','nj','np','nl','b','m','ml','l'];
const _HG_JONG = ['','r','R','rt','s','sw','sg','e','f','fr','fa','fq','ft','fx','fv','fg','a','q','qt','t','T','d','w','c','z','x','v','g'];
const _HG_COMPAT = {'ㄱ':'r','ㄲ':'R','ㄳ':'rt','ㄴ':'s','ㄵ':'sw','ㄶ':'sg','ㄷ':'e','ㄸ':'E','ㄹ':'f','ㄺ':'fr','ㄻ':'fa','ㄼ':'fq','ㄽ':'ft','ㄾ':'fx','ㄿ':'fv','ㅀ':'fg','ㅁ':'a','ㅂ':'q','ㅃ':'Q','ㅄ':'qt','ㅅ':'t','ㅆ':'T','ㅇ':'d','ㅈ':'w','ㅉ':'W','ㅊ':'c','ㅋ':'z','ㅌ':'x','ㅍ':'v','ㅎ':'g','ㅏ':'k','ㅐ':'o','ㅑ':'i','ㅒ':'O','ㅓ':'j','ㅔ':'p','ㅕ':'u','ㅖ':'P','ㅗ':'h','ㅘ':'hk','ㅙ':'ho','ㅚ':'hl','ㅛ':'y','ㅜ':'n','ㅝ':'nj','ㅞ':'np','ㅟ':'nl','ㅠ':'b','ㅡ':'m','ㅢ':'ml','ㅣ':'l'};
function hangulToEng(str) {
  if (!str) return str;
  let out = '';
  for (const ch of str) {
    const code = ch.charCodeAt(0);
    if (code >= 0xAC00 && code <= 0xD7A3) {
      const s2 = code - 0xAC00;
      out += _HG_CHO[Math.floor(s2 / 588)] + _HG_JUNG[Math.floor((s2 % 588) / 28)] + _HG_JONG[s2 % 28];
    } else if (_HG_COMPAT[ch] !== undefined) {
      out += _HG_COMPAT[ch];
    } else {
      out += ch;
    }
  }
  return out;
}
// 한글이 섞여 있었을 때만 변환 + 대문자화(모델코드 관례). 순수 영문 입력은 손대지 않는다.
function normalizeScanInput(v) {
  const c = hangulToEng(v);
  return c !== v ? c.toUpperCase() : v;
}
// v7.455: IME 조합 중에는 변환하지 않는다(조합이 깨져 글자가 덮어써지는 버그 방지).
//   조합 중이면 원문 그대로, 조합이 끝난 값만 normalizeScanInput 으로 변환한다.
//   각 입력에 onCompositionEnd 를 함께 달아 조합 종료 시점에 확정 변환한다.
function imeConv(e) {
  return (e && e.nativeEvent && e.nativeEvent.isComposing) ? e.target.value : normalizeScanInput(e.target.value);
}

// v7.457: 검색어 강조 — 매칭 부분을 <mark> 로 감싼다. (대소문자 무시, 특수문자 이스케이프)
function highlightMatch(text, query) {
  const t = text == null ? '' : String(text);
  const q = (query || '').trim();
  if (!q || !t) return t;
  const esc = q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  let parts;
  try { parts = t.split(new RegExp('(' + esc + ')', 'ig')); } catch (e) { return t; }
  if (parts.length <= 1) return t;
  return parts.map((p, i) => i % 2 === 1
    ? <mark key={i} style={{background:'#ffe066',color:'#0b1c30',borderRadius:2,padding:'0 1px'}}>{p}</mark>
    : p);
}
async function downloadKeepingName(url, filename) {
  if (!url) return;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error('HTTP ' + res.status);
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl; a.download = filename || 'download';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 4000);
  } catch (e) {
    console.warn('[다운로드] blob 실패, 원본 링크로 폴백:', filename, e);
    try {
      const a = document.createElement('a');
      a.href = url; a.download = filename || ''; a.target = '_blank';
      document.body.appendChild(a); a.click(); a.remove();
    } catch (_) { window.open(url, '_blank'); }
  }
}

// v7.247: 부분 환불 — 요청 안의 시리얼을 골라서 되돌린다.
//   ★ 설계 핵심: 전량 환불되는 순간 processedAs='return' 이 붙어 기존(전체 환불)과
//     **완전히 같은 모양**이 된다. 그래서 processedAs 를 읽는 26곳(집계·필터·뱃지·요약)은
//     한 줄도 안 건드려도 된다. '부분'인 동안에만 별도로 표시하고 수량을 차감한다.
//   note 에 추가되는 필드: refundedSerials[] (누적) · refunds[] (이력)
const listFulfilledSerials = (note) => {
  const out = [];
  if (Array.isArray(note?.items)) {
    note.items.forEach(it => {
      (Array.isArray(it.fulfilledSerials) ? it.fulfilledSerials : [])
        .forEach(sn => out.push({ serial: sn, product: it.product_name || '' }));
    });
  } else if (Array.isArray(note?.fulfilledSerials)) {
    note.fulfilledSerials.forEach(sn => out.push({ serial: sn, product: '' }));
  }
  return out;
};
const refundedSetOf = (note) => new Set(Array.isArray(note?.refundedSerials) ? note.refundedSerials : []);
// 아직 환불되지 않은 시리얼
const refundableOf = (note) => {
  const done = refundedSetOf(note);
  return listFulfilledSerials(note).filter(x => !done.has(x.serial));
};

// v7.242: 퀵서비스 판정 단일 출처.
//   같은 판정이 UI 4곳 + 전송 경로 3곳에 흩어져 있었고, 제품 출고 자동 완료 경로만
//   '퀵서비스' 하나만 봐서 레거시 '퀵' 건은 진행상황이 '배차'에 멈추는 불일치가 있었다.
//   ('퀵' 실데이터는 0건 — 현재 드롭다운은 모두 '퀵서비스'를 쓴다. 그래도 판정은 남겨 둔다:
//    없애면 손으로 넣은 값 하나에 다시 갈라진다.)
const isQuickMethod = (m) => m === '퀵서비스' || m === '퀵';
// v7.187: 장부처리 3-state — bookkept 값을 정규화
//         하위호환: true → 'completed' / false·undefined → 'pending' / 'manual' → 신규 수기입력
const normalizeBookkept = (v) => {
  if (v === true || v === 'completed') return 'completed';
  if (v === 'manual') return 'manual';
  return 'pending';
};
const BOOKKEPT_STYLES = {
  completed: { label:'장부완료',   bg:'rgba(0,152,68,0.12)',   color:'var(--success)', icon:'menu_book' },
  pending:   { label:'장부 미완료', bg:'rgba(245,124,0,0.15)',  color:'var(--warning)', icon:'menu_book' },
  manual:    { label:'수기입력',    bg:'rgba(84,110,122,0.12)', color:'#546e7a', icon:'edit_note' },
};
// v7.459 (2026-09-16) — ↕ 재고 검색(그룹) 결과에 컬럼 정렬 추가 (사용자 요청)
//   그룹 모드는 sortByStockType 고정정렬이라 정렬 불가였다. 플랫 모드와 동일하게 헤더(품목코드·입고일·
//   출고일·상태) 클릭 → invSortBy/invSortDir 토글, 그룹 내 시리얼을 invRowCompare 로 정렬(동률은 재고유형 폴백).
// v7.458 (2026-09-16) — 🐛 상세보기 모달 — 준비한 시리얼이 개수 0으로 표기되던 문제 수정 (사용자 보고)
//   증상: 처리 모달엔 1/1·6/6·2/2 로 뜨는데 상세 모달은 0/1·0/6·0/2 로 표기.
//   원인: 상세 모달은 fulfilledSerials(실제 출고분=아직 0)만 셌다. 준비(pending) 시리얼은 미포함.
//   수정: 모달 열 때 ShipPendingService.loadByReqId 로 준비 시리얼을 model 별로 로드 →
//        개수 = 출고분+준비분, 시리얼 목록에도 함께 표시. 실제 출고=완료 / 준비만=준비완료 로 배지 구분.
// v7.457 (2026-09-15) — 🔦 검색어 강조 — 결과에서 매칭 단어를 형광 표시 (사용자 요청)
//   highlightMatch(text, query) 헬퍼 신설(<mark> 감싸기, 대소문자 무시, 정규식 이스케이프).
//   적용: 재고 목록(모델명·시리얼·거래처)·배송시트지(모델명·수령인/업체명·거래처)·메모 기록(내용·작성자).
// v7.456 (2026-09-15) — 👥 사용자 관리 — 작성자/요청자 목록 편집 기능 (사용자 요청)
//   하드코딩이던 STAFF_LIST 를 시드로 두고, 실제 목록은 app_settings('staffList')에서 공유 로드하는
//   staffList 상태로 전환(전 렌더 26곳 치환). 우측 상단 [메뉴]에 '사용자 관리' 항목 신설 → 모달에서
//   추가/이름수정/삭제, 즉시 공유 저장(staffPhones 와 동일 방식). 전화번호는 기존 [퀵 시트 설정] 유지.
//   ⚠ 목록에서 지워도 이미 시트에 기록된 이름은 유지된다(드롭다운에 없으면 <option> 로 폴백 표시).
// v7.455 (2026-09-14) — 🐛 스캔 한글→영문 변환이 IME 조합을 깨뜨리던 버그 수정 (사용자 보고)
//   증상: 한글 입력 상태에서 t→s 를 치면 'ts' 가 아니라 s 가 t 를 덮어썼다.
//   원인: onChange 에서 조합 '중'인 값까지 즉시 변환→setState 하니, IME 조합 영역이 깨져 다음 글자가 덮어씀.
//   수정: imeConv() — nativeEvent.isComposing 이면 원문 유지, 아니면 변환. + 각 스캔 입력에 onCompositionEnd
//        로 조합 종료 시 확정 변환. 6개 입력(제품명·입고시리얼·출고 데스크톱/모바일·#N슬롯·데모 모델명) 모두 적용.
// v7.454 (2026-09-14) — 🔗 데모 모델명 자동완성 — 입고 등록과 완전 통일 (사용자 요청)
//   v7.453 자동완성에 입고 등록과 동일한 2가지를 이식:
//     ① 별칭(alias) 매칭 — AliasService.searchByQuery + findByNames, 드롭다운에 [별칭매칭] 배지·"← 별칭" 힌트.
//        -EU 접미사 자동 매칭도 포함.
//     ② 한글→영문 변환(normalizeScanInput) — 모델명 onChange 에 적용.
// v7.453 (2026-09-09) — 🔎 데모 장비 추가 — 모델명 입력 자동완성 (사용자 요청)
//   입고 등록 제품명 칸처럼, 데모 장비 추가 모달의 모델명 입력 시 제품 카탈로그를 검색해
//   후보를 드롭다운으로 띄우고 클릭하면 채운다. searchDemoModels(ProductService.searchByName).
//   드롭다운은 포커스/입력 시 표시, blur·선택·모달 닫힘 시 정리(useEffect). 직접 입력(자유 텍스트)도 그대로 허용.
// v7.452 (2026-09-09) — ⌨ 시리얼·출고 스캔에도 한글→영문 변환 확장 (사용자 요청)
//   v7.451 을 나머지 스캔 입력에 동일 적용: 입고 시리얼·출고 시리얼 스캔(데스크톱/모바일)·#N 시리얼 슬롯.
//   ⚠ 재고 검색창(제품·시리얼·거래처·날짜)은 제외 — 거래처 한글 검색("이선" 등)을 깨뜨리면 안 되므로.
// v7.451 (2026-09-09) — ⌨ 입고 등록 제품 스캔 — 한글 자모 자동 영문 변환 (사용자 요청)
//   증상: 바코드 리더기가 '한글 입력' 상태면 QWERTY 가 한글 자모로 찍혀(TS-1290 → ㅅㄴ-1290) 모델코드가 깨졌다.
//   수정: hangulToEng() 역변환 — 스캔값의 한글 음절/자모를 원래 눌린 영문 키로 복원.
//        제품명 입력 onChange 에 적용(normalizeScanInput). 한글이 섞였을 때만 변환+대문자화, 순수 영문은 그대로.
//        (시리얼/출고 스캔 입력에도 동일 적용 가능 — 요청 시 확장)
// v7.450 (2026-09-08) — 🔍 메모 기록 — 검색 기능 추가 (사용자 요청)
//   년/월 선택 UI 아래 [🔍 검색] 버튼 → 검색 모달(내용·작성자, 월 무관 전체) → 결과 클릭 시
//   기존 상세 모달(memoHistoryDetail) 재사용. z-index: 달력 1000 < 검색 1100 < 상세 1200.
//   MemoService.searchCompleted(query) 신설 — completed_at 있는 메모를 content/author ilike 로 검색.
// v7.449 (2026-09-08) — 📐 배송시트지 — 모델명 컬럼 확장 (사용자: 제품명이 많이 줄바꿈됨)
//   원인: v7.448 에서 스테퍼·삭제버튼이 모델 블록 안으로 들어가며 모델명 가용폭이 좁아져 자주 접혔다.
//   수정: 수량 컬럼이 병합 행에선 거의 비므로 폭을 재배분 — 모델 250→330px, 수량 110→50px
//        (모델명 가용폭 약 108→188px). 헤더·데이터행 그리드 템플릿 2곳 동시 변경, minWidth 1290→1310.
// v7.448 (2026-09-08) — 🔧 첨부 4종 + 출고요청 블록화 (사용자 요청)
//   #1 tx 배송시트지 첨부 다운로드도 원본 파일명 보존 — v7.444 downloadKeepingName 적용.
//      (그동안 이 경로는 target=_blank 라 URL 저장명(<ts>_...)이 그대로 받아졌다.)
//   #2 업로드 accept 에 pdf 추가 — tx 첨부·메모 첨부 모두. 메모 첨부 검증(looksPdf)도 허용.
//   #3 RMA 건에도 파일 첨부 가능 — 배송지 셀 RMA 브랜치에 AttachmentButton 추가(disabled=처리완료).
//   #4 출고요청(ship_request) 행도 모델+수량 블록으로 통일:
//      · mergeInbound 조건을 입고 → (입고 or 출고요청)으로 일반화(데모 제외).
//      · 출고요청 편집 가능 행은 블록 안에 [− 수량 +] 스테퍼를 렌더(수량셀의 기존 스테퍼는 비활성).
//        dec/inc 로직은 기존 수량셀과 동일(collectExcessPending·updateItemQty·removePendingSerials).
//      · 처리완료/입고분은 ×수량 뱃지(읽기전용). 2개 이상이면 핸들 드래그로 순서 변경(note.items 저장).
//      · 수량 컬럼은 병합 행에서 비움(입고 다중만 합 N 유지).
//   범위 밖(그대로): 데모(fromDemoLend)·구형식(items 배열 없는 legacy).
// v7.447 (2026-09-08) — 🎯 입고행 — 단일품목도 블록으로 통일 + 헤더 [모델명 × 수량] (사용자 요청)
//   증상: v7.446 은 다중품목만 블록으로 묶어, 단일품목(예: TL-D800C ×2)은 모델명 텍스트 +
//        수량이 저 멀리 수량 컬럼에 떠서 다중품목 블록과 위치가 안 맞았다.
//   변경:
//     ① 입고 단일품목(items 배열 길이 1)을 span 대신 다중맵 경로로 흘려보내 동일 블록으로 렌더.
//        mergeInbound 조건을 items>1 → items>=1 로 확장. 드래그 핸들은 2개 이상일 때만(단일은
//        재정렬 의미 없음). 수량 컬럼은 입고 병합(모던)이면 비움(블록에 ×수량 있음).
//        ⚠ 구형식(items 배열 없는 legacy 입고)은 그대로 model-only + ×N 유지 — 수량 사라짐 방지.
//     ② 헤더 [모델명]→[모델명 × 수량], [수량]→빈칸(블록으로 통합됨). key 중복 방지로 index 키 전환.
//   범위 밖(그대로): 출고요청(수량 −/+ 스테퍼)·데모·구형식 입고.
// v7.446 (2026-09-08) — 🎯 배송시트지 입고행 — 모델명+수량 통합 블록 + 드래그 재정렬 (사용자 요청)
//   배경: 모델·수량이 grid 의 별도 셀 두 개라(v7.63), 모델명이 줄바꿈되면 두 셀 행높이가
//        따로 놀아 수량과 어긋났다("감수한 부분"이라 주석에 명시돼 있던 문제).
//   변경(입고 다중품목만, isIn && !fromDemoLend && items>1):
//     ① 모델명+×수량을 한 블록(같은 flex 행)으로 묶음 → 줄바꿈돼도 수량이 세로중앙 정렬, 안 어긋남.
//        수량 컬럼은 [합 N]만 남김(품목별 ×뱃지는 블록으로 이동).
//     ② 블록 좌측 핸들(⠿) 드래그로 위/아래 순서 변경. 드래그 중 반투명 고스트가 커서를 따라다니고,
//        원 위치 블록은 흐려짐. 커서가 다른 블록 중앙을 넘으면 실시간으로 자리 교체.
//     ③ 드롭 시 note.items 순서를 transaction_log 에 저장(낙관적 동시성 — 충돌 시 새로고침).
//        시리얼 목록 표시 순서도 자연히 따라감(각 품목에 시리얼이 붙어 있어서).
//   범위 밖(그대로): 출고요청 행(수량 −/+ 스테퍼)·단일품목·데모(fromDemoLend)·처리완료 행.
//        처리완료 입고는 블록으로 묶여 정렬은 되지만 드래그(핸들)는 비활성.
//   구현: startInbDrag/persistInboundOrder + pointermove/up useEffect + 고스트 오버레이.
//        블록에 data-inb-name, 리스트 컨테이너에 data-inb-list={item.id} 로 스코프.
// v7.445 (2026-09-08) — 🔧 유지보수 2건 (사용자 요청)
//   ① 📎 첨부 다운로드 시 원본 파일명 보존.
//      증상: 올린 파일을 받으면 이름이 '<ts>_<rand>_____.ext' 저장명으로 깨져 받아짐.
//      원인: 저장 경로는 정규화된 저장명이고(한글→'_'), <a download={원본명}> 속성이
//           cross-origin·앱 WebView 에서 무시되어 URL 의 저장명이 파일명이 됐다.
//      해법: downloadKeepingName() 헬퍼 신설 — blob 으로 받아 object URL(항상 same-origin)에
//           download 를 걸어 원본명 강제. 메모 카드 zip 칩·상세모달(이미지/zip)·라이트박스·
//           내용+첨부 순차다운로드 5곳 모두 적용. 실패 시 기존 링크 방식으로 폴백.
//   ② 🏷 배송시트지 입고행 [출처] 칩을 한글 라벨로 표기 (taiwan→대만, paju→파주).
//      원인: 그 칩만 raw source 값을 그대로 찍었다(재고목록 배지는 이미 SOURCE_BADGE 라벨 사용).
//      해법: SOURCE_BADGE[src]?.label || src 로 매핑. 저장값(코드)은 그대로 두고 표기만 한글.
//           (편집 입력은 canonical 코드 유지 — 대시보드 출처 집계·배지색 키가 코드 기반)
//   ※ v7.444 는 앱 변경 없음 — 보증상태 뷰(product_warranty_status) DB 마이그레이션에 부여한 번호.
// v7.443 (2026-09-04) — 🐛 메모 카드 푸터(입력자/작성시간)를 카드 맨 아래로 고정 (사용자 요청)
//                       증상: 큰(크게) 모드에서 내용이 짧으면 그리드가 카드를 같은 높이로 늘리는데,
//                            푸터가 내용 바로 밑에 뜨고 그 아래로 빈 공간이 남았다.
//                       원인: v7.442 회귀. 본문+첨부를 래퍼로 묶으면서 큰 모드 래퍼에 flex:1 을 안 줬다.
//                            v7.442 이전엔 본문뷰(flex:1)가 stretch 여백을 흡수해 푸터가 바닥에 붙어 있었다.
//                       수정: 큰/편집 모드 래퍼에도 flex:1 → 래퍼가 여백을 흡수, 푸터가 카드 맨 아래 고정.
//                            (작게 모드는 v7.442 에서 이미 flex:1 이라 영향 없음.)
// v7.442 (2026-09-04) — 🐛 메모 [작게] 모드에서 첨부파일이 카드 밖으로 새던 문제 수정 (사용자 요청)
//                       증상: 메모를 [작게] 로 두면 고정 높이 카드(200px) 아래로 첨부파일 칩이 삐져나감.
//                       원인: 카드는 height:200 고정 + overflow:visible(별점 배지가 top:-11 로 걸쳐 있어 hidden 불가).
//                            본문이 flex:1 로 공간을 먹고, 첨부목록은 본문의 형제라 200px 를 넘기면 그대로 새어 나갔다.
//                       해법: 본문+첨부를 하나의 스크롤 래퍼(<div flex:1 minHeight:0 overflowY:auto>)로 묶음.
//                            배지는 래퍼 밖 카드 자식이라 클리핑되지 않는다. 런타임 조작 0 — 순수 레이아웃.
//                            본문 minHeight 도 작게 모드에서 0 으로(그리디 억제) → 짧은 메모는 스크롤 없이, 넘치면 스크롤.
// v7.441 (2026-09-03) — ↩ 앱에서 [PC 화면] 버튼 다시 숨김 (v7.440 ① 롤백)
//                       사유: Capacitor WebView 가 allowNavigation 미등록 주소로의 이동을 조용히
//                            차단해 앱에서 버튼이 [눌러도 무반응] 이었다(실기 확인). 오류도 안 뜬다.
//                            폰 브라우저에서는 /desktop.html 이 정상 동작함을 확인 → 파일·코드는 정상.
//                       해결안이었던 allowNavigation 추가는 APK 재빌드가 필요해, 사용자가
//                       '앱 따로 / 링크 따로' 운용을 택했다. → 앱=모바일 화면(스캔), PC 화면=브라우저 바로가기.
//                       ⚠ v7.440 ②(PC 사이드바 [모바일 화면] 복귀 버튼)는 그대로 유지한다.
//                          폰 브라우저로 /desktop.html 에 들어온 경우의 복귀 수단이라 여전히 쓸모가 있고,
//                          실제 PC 사용자(index.html)에겐 렌더되지 않는다.
//                       ※ 앱에서 전환이 필요해지면: capacitor.config.ts 의 server.allowNavigation 에
//                          도메인 추가 + APK 재빌드. 그때 이 숨김 가드를 걷어내면 된다.
// v7.440 (2026-09-02) — 🖥 앱에서도 [PC 화면] 버튼 노출 + PC 화면에 [모바일 화면] 복귀 버튼 (사용자 요청)
//                       ① DesktopSwitchButton 의 Capacitor 숨김 해제 — 앱에서도 버튼이 보인다.
//                       ② PC 사이드바 하단(버전 표기 위)에 [모바일 화면] 버튼 추가.
//                          window.location.pathname.endsWith('/desktop.html') 일 때만 렌더 →
//                          실제 PC 사용자(index.html 진입)에게는 보이지 않는다.
//                       ⚠ 앱에는 주소창이 없고 뒤로가기가 네이티브 우회 처리된 이력(v7.166)이 있어
//                          복귀 버튼이 없으면 앱에서 PC 화면에 갇힐 수 있다. ①과 ②는 세트다.
// v7.439 (2026-09-02) — 🖥 모바일 → PC 화면 전환 (사용자 요청) + desktop.html 진입점 신설
//                       구성: ① scripts/make-desktop-html.js (postbuild) — build/index.html 을 복제해
//                                build/desktop.html 생성. viewport 만 width=1600 으로 교체.
//                                (aside 256 + 배송시트지 행 minWidth 1290 + 여백 근거. 좁히면 좌우 스크롤)
//                             ② package.json 에 "postbuild": "node scripts/make-desktop-html.js" 추가 필요.
//                             ③ src/DesktopSwitchButton.jsx 신설 — 모바일 4개 화면 브랜드 헤더 우측에
//                                [🖥 PC 화면] 버튼. window.location 이동만 한다(상태 토글 아님).
//                                Capacitor 네이티브 앱에서는 자동 숨김(바코드 스캔이 본진이라).
//                       ⚠ v7.373~391 이 19개 버전을 태우고 v7.392 에서 전면 제거된 이유가
//                          '같은 페이지에서 viewport 를 런타임 조작' 이었다. 크롬은 초기 파싱 후
//                          viewport 를 다시 잡지 않는다. 그래서 이번엔 [진입 자체를 분리] 했다.
//                          App.js 의 isMobile / 모바일 컴포넌트는 한 줄도 건드리지 않음.
//                       ※ desktop.html 미생성 시 SPA 폴백으로 index.html 이 떠 '그대로 모바일' 로 보인다.
//                          증상 발생 시 빌드 로그에서 [make-desktop-html] 줄부터 확인할 것.
// v7.438 (2026-09-02) — 🎨 배송시트지 품목명 색상 — 시리얼 등록 완료=초록(--success) / 미완=주황(--warning) (사용자 요청)
//                       판정: 등록수(fulfilled+reserved+pending) ≥ qty → 완료(초록), 미만 → 미완(주황).
//                            regColor(it) 헬퍼 신설 (shortInfo 의 held 계산 재사용). 단일·다중 두 렌더 갈래에 적용.
//                       제외: 입고/처리완료(취소·환불)/이미 출고완료 행, 재고부족(red)·외부(EXT)·면제(NOSN)·데모 품목,
//                            pending 조회 전(null). 이들은 기존 색/표기 그대로. 구형식(model-only) 레거시 갈래도 미적용.
// v7.437 (2026-09-02) — 🐛 배송시트지 품목 [모델 변경] 시 미등록→등록 교체해도 EXT 임시시리얼이 붙던 버그 수정 (사용자 리포트)
//                       원인: 변경 모달의 '변경 모드'가 saveItemProductName 에 product_name 만 넘겨 external 을 재판정하지 않았다.
//                            (v7.427 이후 note.items 는 {...it} 스프레드로 보존되므로 external:true 가 그대로 남았다.)
//                            → loadActiveRequest 가 external:true 로 모델을 만들어 출고 모달에서 EXT- 를 자동부여.
//                       변경: ① saveItemProductName(item,old,new,asExternal) — 4번째 인자로 external 재설정
//                                (true=외부 / false=등록시 external 키 제거 / undefined=기존값 유지).
//                            ② 변경 모달 change 브랜치가 추가 모드와 동일하게 카탈로그 조회로 asExternal 판정 후 전달.
//                            ③ 외부→등록 전환 시 옛 모델명으로 붙어 있던 EXT-/NOSN- 임시 pending 정리
//                               (가짜 시리얼이라 inventory·ship_hold 무관 → 재고 영향 없음).
//                       ※ 반대 방향(등록→미등록)·검색결과 선택·직접입력 모두 일관되게 동작하도록 함께 정정.
// v7.436 (2026-09-02) — 🧹 배송건 상세 모달에서 [주문취소] 버튼 제거 (사용자 요청 — [기록 삭제]와 역할 중복)
//                       변경: 미출고 상태 버튼 묶음에서 handleTxAction(item,'cancel') 버튼만 삭제.
//                       유지: ① handleTxAction 함수 (환불 경로 handleTxAction(item,'return') 28634 에서 계속 사용).
//                            ② 기존 '주문취소 처리됨'(processedAs==='cancel') 레코드의 렌더/집계/필터는 그대로 —
//                               취소된 과거 건은 여전히 [취소] 필터로 조회·표시된다. (읽기 경로 무손상)
//                       효과: 이제 신규 주문취소 진입점은 없다. 삭제가 필요하면 [기록 삭제]를 쓴다.
// v7.435 (2026-09-02) — 🐛 배송시트지 수령인칸에 요청자가 자동 표기되던 버그 수정 (사용자 리포트)
//                       원인: PC 출고요청 생성 시 dealer_name = recipientLabel || shipRequester || shipAuthor 였다.
//                            수령인(shipRecipient)을 비우면 dealer_name 이 요청자(사람 이름)로 폴백되고,
//                            배송시트지 수령인 셀이 `shipInfo.recipient || item.dealer_name` 순으로 표시해
//                            빈 수령인 자리에 요청자(예: 박윤영 과장)가 떴다. note.recipient 자체는 정상적으로 빈 값.
//                       변경: dealer_name 폴백을 `recipientLabel || null` 로 축소 — 사람 이름 폴백 제거.
//                            RMA(recipientLabel=[emsType])·수령인 입력 건은 그대로. 미입력 건은 시트에 '더블클릭'(빈 값)으로.
//                            읽는 쪽 null-safe 확인: ilike(서버)·18423 &&가드·keepsForm(별개 객체)만 .trim() 사용.
//                       ※ 모바일은 대상 아님 — 딜러/업체명 필수 + dealer_name: recipient||dealerName (사람 이름 안 샘).
//                       ※ 기존에 새어든 레코드는 백필 SQL 별도(§ dealer_name = note.requester 인 행만).
// v7.434 (2026-09-05) — 🐛 [재고부족] 오표기 수정 — 자기 건이 이미 담아둔(pending) 수량이 가용에서 빠져 있었다 (사용자 리포트)
//                       배경: v7.424 부터 배송건에 담은 시리얼은 inventory 가 ship_hold 로 바뀌어 in_stock 집계에서 빠진다.
//                            그런데 shortInfo 의 held 는 fulfilled/reserved 만 세어서, 12개를 담아둔 건이
//                            '가용 0 · 요청 12' 로 잡혀 자기가 확보한 물건인데 [재고부족]이 떴다.
//                            (예: SKY_HAWK 20TB — 32개 중 28개가 출고대기라 in_stock 0)
//                       변경: ① ShipPendingService.countByReqIds() 신설 — 행별·모델별 pending 수량 일괄 조회.
//                            ② logPendingByReq state + 로더 useEffect (미출고 ship_request 행만 대상).
//                            ③ shortInfo 의 held 에 pendingHeld 합산. 조회 전(null)이면 판정 보류(순간 오표기 방지).
// v7.433 (2026-09-05) — 🧹 배송시트지 [재고없음] 표기 폐지 · [재고부족]은 '요청 > 가용' 일 때만 (사용자 요청)
//                       배경: 재고 현황은 재고 대시보드에서 본다(재고 0=노랑, 임계 이하=주황). 배송시트지에 중복 표기할 이유가 없다.
//                       변경: ① noStock() → 항상 false (함수는 유지 — 세 렌더 갈래와 title 툴팁 분기가 모두 참조한다).
//                            ② shortInfo 의 `stock===0 이면 return null`(=재고없음에 맡기던 예외) 제거 →
//                               재고 0 인 채로 요청한 건도 [재고부족]으로 잡힌다.
//                            ③ shortInfo 에 isDemoItem 제외 추가 — serialOptional 이 유실된 (구)데모 데이터도 새지 않게.
//                       ※ [재고부족] 조건은 종전과 동일: qty > (재고 + 이미 확보한 fulfilled/reserved).
// v7.432 (2026-09-05) — ↩ v7.430 롤백 — 데모 건 행 보라 배경 제거 (사용자 요청). 행 배경/hover 를 v7.429 상태로 되돌림.
//                       ※ 데모 식별은 [데모항목] 라벨 + 보라 [품목 수정] 버튼으로 충분 — 배경색은 쓰지 않는다.
// v7.431 (2026-09-05) — 🔒 배송시트지 데모 건 — 수량 변경 차단 (사용자: "품목수정 버튼으로 수정하는게 맞을것같아")
//                       이유: 데모의 수량 = demo_rental_items 의 장비 대수. 배송시트지에서 숫자만 바꾸면
//                            대여 원본·demo_equipment(대여예약/대여중) 와 어긋난다. 입고 수량을 스테퍼로 못 바꾸게 한 것과 같은 이유.
//                       변경: ① 수량 스테퍼 조건에 !shipInfo?.fromDemoLend 추가 → 읽기 전용 표시로 떨어진다.
//                            ② 다중 품목 데모는 입고와 동일하게 '품목별 ×N + 합계' 로 표기 (합계 하나만 뜨면 정보가 준다).
//                       ※ 데모는 it.serials 가 없어(시리얼은 demoSerials) sn=0 → 개수 불일치 ⚠ 오탐 없음.
// v7.430 (2026-09-05) — 🟣 배송시트지 데모 건 행 — 반투명 보라 배경 (사용자 요청)
//                       rowBgIdle 에 note.fromDemoLend 분기 추가: rgba(123,31,162,0.07). hover 시 0.13 으로 진해진다.
//                       ⚠ rowBgIdle 은 hover 이탈 복원값으로도 쓰여서 거기에 함께 넣어야 색이 유지된다.
//                       ※ 처리완료(isProcessed)는 기존 mute 배경 우선 — 흐린 상태 표현을 덮지 않는다.
// v7.429 (2026-09-05) — ↩ 데모 [품목 수정] 으로 진입했으면 저장·취소 후 그 배송건으로 복귀 (사용자 요청)
//                       구현: openDemoLendEditModal(rental, {returnTo:'txlog', returnSearch:reqId}) — 진입 출처를 모달 state 에 보관.
//                            저장/취소/[뒤로] 모두 returnTo 를 보고 분기. txlog 면 setLogSearch(reqId)+setPage('txlog') 로
//                            그 건만 필터해서 보여준다(v7.418 [배송건] 딥링크와 동일 방식). 상단 버튼 라벨도 [배송시트지로] 로 바뀜.
//                       ※ 데모 관리에서 들어온 기존 경로는 opts 가 비어 있어 종전대로 데모 관리로 복귀 — 동작 변화 없음.
//                       ※ 확인: logSearch 는 페이지 진입 시 자동 초기화되지 않는다(지우기·필터초기화 버튼에서만) → 딥링크 유지됨.
// v7.428 (2026-09-05) — 🧪 배송시트지 데모 건 — [품목 추가] → [품목 수정] (데모 관리 편집 화면으로 이동)
//                       사용자: "데모항목으로 작성된 건은 품목추가가 아니라 품목 수정으로, 누르면 데모관리 수정 화면으로"
//                       이유: 데모 품목의 실물은 demo_equipment/demo_rental_items 가 진실의 원천이라 배송시트지에서
//                            직접 품목을 추가하면 대여 원본과 어긋나고 장비 상태(대여예약/대여중) 동기화도 안 된다.
//                            데모 편집 화면은 품목 추가/제거 시 demo_equipment 를 자동 동기화하고 배송시트지에도 반영한다.
//                       구현: note.fromDemoLend 인 행은 보라색 [품목 수정] 버튼 → openDemoLendEditModal({id: note.demoRentalId}).
//                            그 함수가 내부에서 getRentalById + setPage('demo_lend') 까지 하므로 데모 관리 [수정] 과 동일 화면.
//                            demoRentalId 가 없는 (구)데이터는 안내 alert 후 중단.
// v7.427 (2026-09-05) — 🐛 [내용 수정] 저장 시 items[] 의 부가 필드가 통째로 소실되던 문제 수정 (데모 note 유실의 근본 원인)
//                       배경: 편집 모달이 폼에 실을 때·저장할 때 items[] 를 {product_name, qty, fulfilledSerials}
//                            세 필드로만 새로 만들어 덮어썼다. 그래서 [내용 수정] 한 번이면
//                            serialOptional / serialOptionalReason / demoSerials / external / reservedSerials / model_no 가 전부 사라졌다.
//                            → 데모 건이 일반 출고건으로 바뀌어 시리얼 자동 기입이 죽고 [임시 EXT] 버튼이 뜸(사용자 리포트).
//                       변경: 폼 로드 시 원본 item 을 _orig 로 보존 → 저장 시 {..._orig} 위에 편집값(product_name/qty/fulfilledSerials)만 덮어쓴다.
//                       ※ 다른 재구성 경로(수량 스테퍼·시리얼 추가/삭제·품목 추가)는 이미 {...it} 스프레드라 영향 없음 — 확인 완료.
// v7.426 (2026-09-05) — 📅 데모 대여 수정 저장 시 이월된 배송시트지 날짜가 원래 출고일로 되돌아가던 문제 수정
//                       배경: 데모 편집 저장이 transaction_date 를 무조건 m.shipment_date 로 덮어써서,
//                            자동이월로 오늘까지 밀어둔 건을 주소·비고만 고쳐 저장해도 원래 출고일로 리셋 →
//                            배송시트지(오늘)에서 사라진 것처럼 보였다 (사용자 리포트).
//                       변경: originalSnapshot.shipment_date 와 비교해 '출고일이 실제로 바뀐 경우에만' 날짜를 갱신.
//                            안 바뀌었으면 현재 transaction_date(=이월된 오늘 날짜)를 유지한다.
//                       ⚠ 출고 완료(commitRequest)는 무죄 — 데모 원본을 참고하는 건 finalizeRental 뿐이고
//                         (demo_rentals.status pending→active, demo_equipment.location 대여예약→대여중),
//                         transaction_date 는 읽지도 쓰지도 않는다.
// v7.425 (2026-09-05) — 🐛 v7.424 회귀 수정 — 기록 삭제/주문취소 시 ship_hold 시리얼이 재고로 안 돌아오던 문제 (사용자 실기 발견)
//                       배경: v7.424 부터 '담기' 가 inventory 를 ship_hold 로 잠근다. 그런데 삭제/취소 복귀 로직은
//                            fulfilled(출고 완료)·reserved 시리얼만 되돌려서, 아직 출고 전 pending 시리얼이
//                            ship_hold 로 묶인 채 재고에서 사라졌다(= 원상복구 안 됨).
//                       변경: ① deleteShipmentCore — 삭제 시 ship_pending_serials 조회 → releaseHoldBySerial + clearByReqId.
//                            ② 주문취소(cancel) 경로에도 동일 정리 추가 (취소는 대개 출고 전이라 더 자주 걸린다).
//                            ③ holdBySerial 이 DB 오류와 상태 충돌을 구분해 반환 → 마이그레이션 미실행 시 그 사실을 그대로 알림
//                               (v7.424 는 CHECK 위반까지 "방금 다른 곳에서 담겼습니다"로 뭉개 원인 파악을 방해했다).
// v7.424 (2026-09-05) — 🔒 배송건에 담긴 시리얼을 'ship_hold' 상태로 잠금 (사용자: "배송건에 담긴 제품 상태를 변경 / ship_hold")
//                       배경: v7.423(다른 배송건 pending 교차조회로 후보 숨김)은 재고 후보가 통째로 사라지는 부작용 +
//                            버려진 pending 이 시리얼을 조용히 가리는 약점이 있었다. 근본적으로 '담김'을 inventory.status 로 표현.
//                       설계: 담기(스캔) 순간 in_stock → ship_hold. in_stock 만 보는 재고 후보/카운트에서 자동 제외 →
//                            이중 할당·미리보기 문제 동시 해결. reserved(방문수령)와 완전 분리된 별도 상태값(오버로딩 회피).
//                       ⚠ DB: inventory.status CHECK 제약에 'ship_hold' 추가 필요 → migrations/v7.424-ship-hold-status.sql (사용자 실행).
//                       생명주기: 담기→holdBySerial / 빼기·수량↓·초과정리→releaseHoldBySerial / 커밋→ship/reserveBySerial(ship_hold 허용) /
//                                앱시작→reconcileOrphanHolds(ship_pending 없는 고아 ship_hold self-heal).
//                       UI: 재고 상세 배지·도넛·칩·xlsx + 모바일 재고에 '출고대기' 노출. v7.423 findOthersBySerials 필터/게이트 제거.
// v7.423 (2026-09-05) — 🐛 출고 처리 — 같은 시리얼을 여러 배송건에 중복 추가되는 문제 수정 (사용자 실기 발견) [v7.424에서 ship_hold 방식으로 대체]
//                       배경: pending(출고 대기) 시리얼은 inventory 를 in_stock 그대로 두고 ship_pending_serials(PK req_id+serial)
//                            에만 쌓인다. 그래서 배송건1에 담은 시리얼이 배송건2의 재고 후보/스캔에서 그대로 잡혀 이중 할당됐다.
//                            (최종 commit 엔 Optimistic Lock 이 있어 물리적 이중출고는 막히나, 배송건2를 다 준비한 뒤에야 conflict → 뒤늦은 실패)
//                       변경: ① ShipPendingService.findOthersBySerials() 신설 — 다른 req_id 에 pending 인 시리얼 조회.
//                            ② handleRequestScan 추가 게이트에서 차단 (핵심): "이미 다른 배송건(REQ-…)에 담긴 시리얼".
//                            ③ 재고 시리얼 후보 드롭다운에서도 다른 건에 담긴 시리얼 숨김.
// v7.422 (2026-09-05) — 🧪 배송시트지 데모 품목 — inventory 대신 demo_equipment 참조 (사용자: "데모로 작성되면 다른 테이블 참조")
//                       배경: 데모 대여(fromDemoLend) 품목은 실물이 demo_equipment 에서 관리되는데, 배송시트지가 inventory in_stock
//                            카운트로 재고없음을 판정해 가용 장비가 많아도 [재고없음] 오표기 (예: Nytro SSD 가용多인데 재고없음).
//                       변경: ① noStock 판정에서 데모 품목(serialOptionalReason==='demo_lend' 또는 note.fromDemoLend) 제외.
//                            ② 데모 품목은 [데모항목](파랑) 표기. demo_equipment 에 그 모델이 아예 없을 때만 [⚠ 데모 미등록](빨강).
//                            ③ DemoService.listRegisteredModels() 신설 — 모델 '존재 여부' 만 조회(가용 수량 아님 → 자기 예약분 이슈 없음).
//                       ⚠ 백엔드(등록/출고의 serialOptional 시리얼면제)는 그대로 — 표시/판정 참조만 demo_equipment 로 전환.
// v7.421 (2026-09-05) — 🏷 배송시트지 입고 행 — 출처(source) 인라인 수정 (사용자: "HDD/SSD 입고 출처를 배송시트지에서도 수정")
//                       입고 행 시리얼 앞 [출처: …] 칩 더블클릭 → 입력. transaction_log.note(items[].from + 단일품목이면 최상위 from)
//                       + 해당 시리얼들의 inventory.source 를 함께 갱신 (updateInboundDate 와 동일 양쪽 동기 — 재고 대시보드 출처 집계 어긋남 방지).
// v7.420 (2026-09-05) — 🔀 재고 페이지 기본 진입 탭을 [재고 목록] 로 (사용자: "재고목록이 가장먼저 보이도록"). inventoryTab 기본값 'dashboard'→'list'. 탭 바 순서·대시보드 기능은 그대로.
// v7.419 (2026-09-05) — 🏷 모델번호(model_no) 표기 (사용자: "재고목록과 입고시에 표기")
//                       배경: HDD/SSD 입고의 모델번호는 inventory.model_no·note.items[].model_no 에 저장되나 어디에도 안 보였다.
//                       ① 재고목록: 쿼리 select·매핑에 model_no 추가. 시리얼 상세 [입고 정보 수정] 패널에
//                          [모델번호] 편집 필드 추가 (saveInventoryField 로 즉시 저장, 예: ST24000NT002).
//                       ② 배송시트지 입고 행: 시리얼 라벨 [제품명 (N):] 옆에 model_no 태그 표기 (note.items[].model_no).
//                       파일 변경: src/App.js
//
// v7.418 (2026-09-05) — 🔗 재고 목록 — 출고된 시리얼에 [배송건] 이동 버튼 (사용자: "검색에서 클릭하면 바로 해당 배송건으로")
//                       출고됨 상태 배지 옆에 [배송건] 버튼: setLogSearch(item.serial) + setPage('txlog').
//                       배송시트지 검색이 serial/model/dealer/note 를 ilike 매칭하므로, 그 시리얼이 담긴 배송건으로 바로 필터된다.
//                       두 시리얼 행 렌더(단순/reserved 분기 포함) 모두에 적용.
//                       ─ 조사 결과(사용자 문의 "HDD/SSD 모델번호 어디 출력?"): model_no 는 inventory.model_no 에 저장되지만
//                          화면 어디에도 표시되지 않는다. 오직 NAS 호환성(QnapCompatService)에서 정규화해 [재고 있는 호환부품]
//                          매칭에만 쓰인다. 재고목록/시리얼상세/배송시트지엔 카탈로그 product_name 만 뜬다.
//                       파일 변경: src/App.js
//
// v7.417 (2026-08-25) — 📎 새 메모 작성 — 작성 중 첨부 + 내용만큼 자동 확장 (사용자 요청)
//                       ・ 첨부: 아직 메모 id 가 없으므로 File 을 newMemoFiles 에 담아 두었다가 [추가] 로 메모 생성 후
//                         그 id 로 업로드(AttachmentService.uploadToDir)하고 images 반영. 작성 중 칩으로 표시·제거 가능.
//                         허용 형식은 이미지·zip (기존 첨부와 동일). 
//                       ・ textarea 자동 확장: onChange 에서 scrollHeight 로 높이 조정 (최소 120, 최대 55vh 초과 시 스크롤).
//                       파일 변경: src/App.js
//
// v7.416 (2026-08-25) — 📄 메모 기록 — 기록된 메모 상세 모달 + 내용·첨부 함께 다운로드 (사용자 요청)
//                       ・ 첨부(images: 이미지·zip)는 완료(complete) 시 행에 그대로 보존됨 — 데이터는 이미 기록으로 넘어간다.
//                         기록 뷰가 안 보여줬을 뿐이라, 상세 패널 카드에 [📎 N] 표시 + 클릭 시 상세 모달을 연다.
//                       ・ 상세 모달: 내용(renderMemoContent) + 첨부(이미지 썸네일/zip 칩) 표시.
//                       ・ [내용 + 첨부 다운로드]: 내용을 .txt 로, 첨부 각 파일을 순차 다운로드(다중 다운로드 차단 완화 지연).
//                       ・ 모달에서 [메모장으로 복원]도 가능. ESC 는 상세 모달 → 기록 모달 순으로 닫힌다.
//                       파일 변경: src/App.js
//
// v7.415 (2026-08-25) — 🐛 출고 처리 — 수량 초과 pending 시리얼 복원 버그 수정
//                       사용자: "2개 설정인데 4개가 찍혔고, 시리얼을 삭제해도 다시 4개로 돌아옴".
//                       원인: ship_pending_serials 에 qty 초과분이 남아 있으면, 출고 처리 모달 복원 시
//                             remote.data 를 모델별 잔여수량으로 자르지 않고 통째로 실어 [N/qty] 가 qty 초과.
//                             기존 초과분 정리(collectExcessPending)는 '시트지 수량 감소' 시점에만 돌아 이 경로를 못 막음.
//                       수정: loadActiveRequest 복원 마지막에 모델별 [qty − fulfilled] 로 pending 을 자르고,
//                             초과분은 ShipPendingService·localStorage 에서도 제거 → 삭제 후 되살아나지 않음.
//                             정상 스캔은 scan-cap 으로 qty 를 못 넘으므로 잘리는 건 남아 있던 초과분뿐.
//                       파일 변경: src/App.js
//
// v7.414 (2026-08-25) — 📎 메모장 — zip 파일 첨부 지원 (사용자: "메모장에 zip파일도 업로드")
//                       기존 이미지 첨부(memos.images jsonb, nginx WebDAV /attachments/memo_<id>/)를 재사용.
//                       ・ 업로드: 이미지 + zip 허용 (그 외 문서/압축은 여전히 [작업파일 공유] 탭 안내).
//                       ・ 렌더: 이미지는 썸네일, zip 은 다운로드 칩(파일 아이콘 + 이름 + 다운로드 + × 삭제).
//                       ・ input accept 에 .zip 추가, 버튼 title '이미지·zip 첨부'.
//                       ⚠ 스키마 변경 없음 — images 배열에 함께 저장(항목마다 path/name/size). 마이그레이션 불필요.
//                       파일 변경: src/App.js
//
// v7.413 (2026-08-25) — 🎯 NAS 호환성 — 용량 필터 기본값 = 첫 칩(최대 용량) (사용자: "첫번째 필터를 기본으로")
//                       ・ compatData 로드/탭 전환 후 capFilter 가 비어 있으면 최대 용량으로 자동 세팅.
//                       ・ 용량 칩 클릭을 토글(→null) 대신 [항상 선택] 으로 — 전체가 없어 null 상태를 안 만든다.
//                       파일 변경: src/App.js
//
// v7.412 (2026-08-25) — 🧹 NAS 호환성 — 용량 필터에서 [전체] 칩 제거 (사용자 요청)
//                       기본값 capFilter=null(전체) 유지 + 선택 칩 재클릭 시 null 복귀라 전체 보기는 계속 가능.
//                       파일 변경: src/App.js
//
// v7.411 (2026-08-20) — 🔠 재고 대시보드 — 파주 숫자 글자 크기 12 → 15 (본사와 동일, 사용자 요청)
//                       파일 변경: src/App.js
//
// v7.410 (2026-08-20) — 📏 재고 대시보드 — 파주-비고 간격 고정 (사용자: "파주와 비고가 너무 가까워졌어")
//                       3열로 블록이 좁아지자 간격 컬럼(1fr)이 0으로 찌그러져 파주·비고가 붙었다.
//                       CELL 4번째 컬럼 1fr → [24px 고정], 비고는 minmax(120px,1fr) 로 남는 폭을 채우게.
//                       파일 변경: src/App.js
//
// v7.409 (2026-08-20) — 🎨 재고 대시보드 — 행 테두리 + 텍스트 검정 통일 + 3열 배치 (사용자 요청)
//                       ① 제품 행마다 검은 테두리(#1a1a1a). marginBottom:-1 로 인접 행 테두리를 1px로 겹침.
//                       ② 텍스트색 검정(#111) 통일 — 모델명·본사·파주·유형표기 모두. 상태는 [배경색]만으로 표현
//                          (재고0=노랑, 부족=주황). 파주 헤더 파란색도 제거해 헤더 통일.
//                       ③ 카테고리 블록 2열 → 3열 (gap 16→14).
//                       파일 변경: src/App.js
//
// v7.408 (2026-08-20) — 🧹 재고 페이지 헤더 정리 (사용자 요청)
//                       ① 이벤트 캘린더 버튼 제거 (모달 코드는 남김 — 여는 곳만 사라져 잠든 상태).
//                       ② 파주 재고 업로드 + 새로고침을 [이벤트 캘린더 자리](페이지 헤더 우측)로 이동.
//                          대시보드 탭에서만 노출(엑셀 다운로드가 list/hdd 에서만 뜨는 것과 같은 패턴).
//                          ⚠ 페이지 헤더 스코프엔 sd(대시보드 IIFE 지역변수)가 없어 stockDashboard 를 직접 참조.
//                          숨김 <input ref={pajuFileRef}> 도 함께 옮겼다.
//                       ③ 대시보드 섹션의 [재고 대시보드] 소제목·아이콘·설명 문구 제거 (요약 토글이 첫 요소).
//                       파일 변경: src/App.js
//
// v7.407 (2026-08-20) — 🖤 재고 대시보드 — 카테고리 블록 외곽에 [검은 테두리] (사용자: "제품별 블록에 검은색 테두리")
//                       블록 헤더(상/좌/우)·테이블헤더(좌/우)·본문(좌/우/하) 테두리를 #1a1a1a 로.
//                       내부 행 구분선(테이블헤더 하단·행 사이)은 옅게 유지해 외곽만 도드라지게 했다.
//                       파일 변경: src/App.js
//
// v7.406 (2026-08-20) — 🐛 재고 대시보드 — 열 어긋남 수정 (사용자: "줄이 틀어졌어")
//                       원인: v7.405 가 모델명 컬럼폭을 [ch] 로 줬는데, ch 는 폰트 크기 비례라
//                             헤더(fontSize:10)와 행(fontSize:13)에서 픽셀폭이 달라 열이 어긋났다.
//                       수정: 폰트와 무관한 [px] 고정폭으로 (모델명 글자수 × 8px + 14). 헤더·행이 동일 px → 정렬 일치.
//                       파일 변경: src/App.js
//
// v7.405 (2026-08-20) — 🔤 재고 대시보드 — 헤더 [재고]→[본사], 본사·파주를 모델명에 밀착 (사용자 요청)
//                       CELL 을 블록마다 [모델명 최장 길이(ch)]로 고정: '<n>ch 54px 50px 1fr minmax(140px,200px)'.
//                       본사·파주가 모델명 바로 뒤에 붙고, 유연 간격(1fr) 뒤 비고는 오른쪽 고정폭.
//                       ⚠ auto/max-content 는 헤더·각 행이 별도 그리드 컨테이너라 폭이 달라져 열이 어긋난다 → ch 고정폭 사용.
//                       ⚠ 헤더 라벨만 [본사]. 범례/각주의 '재고'는 재고량 개념이라 그대로 둔다.
//                       파일 변경: src/App.js
//
// v7.404 (2026-08-20) — 📏 재고 대시보드 — 비고 칸 축소 + 파주-비고 간격 확대 (사용자: "비고란이 파주셀과 너무 가까워")
//                       CELL '1fr 68px 64px 2fr' → '1.7fr 68px 64px 26px 1fr' (모델명↑ · 26px 간격열 · 비고 2fr→1fr).
//                       헤더/본문 양쪽에 파주 뒤 빈 간격 컬럼 추가 (그리드 컬럼 수 일치).
//                       파일 변경: src/App.js
//
// v7.403 (2026-08-20) — 🔢 재고 대시보드 — 블록 내 정렬: NAS/DAS 저베이→고베이, HDD/SSD 저용량→고용량 (사용자 요청)
//                       ・ bayOf(name): QNAP 관례로 베이 수 파싱 (TS-233→2, TS-1264→12, TR-004→4).
//                       ・ capGB(name): 모델명의 용량(TB/GB) → GB 환산 (10TB→10000, 250GB→250, 1.92TB→1920).
//                       ・ NAS·DAS·HDD·SSD 블록은 이 기준으로 정렬(전역 정렬 버튼 무시), 그 외는 전역 정렬 유지.
//                       ⚠ 모델명 휴리스틱이라 특이 모델은 어긋날 수 있다 — 어긋나면 그 모델명 알려주면 규칙 보완.
//                       파일 변경: src/App.js
//
// v7.402 (2026-08-20) — 🧱 재고 대시보드 — 카테고리 블록을 [2열 그리드]로 나열 (사용자: "NAS DAS / HDD SSD 이런식")
//                       카테고리 순서대로 좌→우 배치: NAS|DAS · HDD|SSD · Switch|Memory · …
//                       alignItems:start — 짝 블록 높이가 달라도 각자 제 높이만 차지 (짧은 쪽 아래 여백).
//                       파일 변경: src/App.js
//
// v7.401 (2026-08-20) — 📊 재고 대시보드 [구글 시트]처럼 개편 (사용자 협의: 블록 나열·4컬럼·재고기준 색상)
//                       ・ 레이아웃: 카테고리별 블록을 세로로 나열 (블록 = 헤더 + 표). 3열 배열 → 블록.
//                       ・ 컬럼: 모델명 · 재고(본사) · 파주 · [비고](직접 입력). 킵·가용 컬럼 제거.
//                       ・ 색상 기준을 [가용] → [재고] 로 전환:
//                          재고 0 = 노랑, 재고 ≤ 임계 = 주황.
//                          임계: NAS 타워 ≤7 · NAS 렉 ≤2 · 그 외(스위치 포함) ≤5.
//                          NAS 타워/렉 = form_factor('렉'/'타워') 오버라이드 → 없으면 모델명 규칙(-RP 또는 랙유닛 U).
//                          ⚠ form_factor 는 v7.0부터 NULL 유지라 사실상 모델명 규칙이 판정. 지역코드(-EU/-US/-KR…) 끝은 제외.
//                       ・ 비고: dashboard_memo 테이블(product_name PK)에 upsert. 클릭 편집 · blur/Enter 저장 · Esc 취소.
//                          테이블 없어도 대시보드는 뜬다(빈 맵 폴백). 사용자가 SQL 실행 완료.
//                       파일 변경: src/App.js
//
// v7.400 (2026-08-20) — 🧭 PC 재고 — [재고 대시보드] 를 기본/첫 탭으로 (사용자: "재고 대시보드가 가장 먼저 오도록")
//                       ・ inventoryTab 기본값 'list' → 'dashboard' (진입 시 대시보드가 먼저 뜬다).
//                       ・ 탭 바 순서: 재고 대시보드 · 재고 목록 · HDD/SSD · 사용 현황 (대시보드를 맨 앞으로).
//                       ⚠ 다음 작업 예고: 재고 대시보드 UI 를 [구글 시트]처럼 개편 (별도 진행 — 범위 협의 후).
//                       파일 변경: src/App.js
//
// v7.399 (2026-08-19) — 🔎 모바일 재고 — 기본 상태 필터 [재고] → [전체] (사용자: "모든유형을 출력")
//                       증상: 판매불가 항목이 안 뜨고 재고(신품)만 보임.
//                       원인: statusFilter 기본값이 'in_stock' 이라 판매불가/예약/출고가 처음부터 걸러졌다.
//                             (유형 필터 typeFilter 는 이미 'all' 이라 유형은 문제가 아니었다.)
//                       변경 (src/MobileInventory.jsx): statusFilter 기본값 'in_stock' → 'all'.
//                          이제 재고 탭 진입 시 모든 상태가 보이고, 필요하면 상태 칩으로 좁힌다.
//                       파일 변경: src/MobileInventory.jsx (App.js 는 버전만)
//
// v7.398 (2026-08-19) — 📝 모바일 재고 — 시리얼 행에 [비고] 출력 (사용자: "재고에 비고항목을 출력")
//                       재고 비고 = inventory.memo 다 (note 는 감사용 — v7.324/데스크톱과 동일 기준).
//                       변경 (src/MobileInventory.jsx):
//                          ・ 목록 SerialRow: 시리얼 아래에 [📝 memo] 둘째 줄 추가 (있을 때만).
//                            공백만 있는 memo 는 표시 안 함 (v7.324 '비고 있음처럼 보임' 방지).
//                          ・ 상세 시트 [비고]: item.note → item.memo 로 교정.
//                            기존엔 감사용 note 를 비고로 잘못 표시하고 있었다 (버그).
//                       ⚠ 쿼리는 이미 select('*') 라 memo 가 조회된다 — 쿼리 변경 없음.
//                       ⚠ 검색(.or)은 여전히 note 만 본다 — 비고로 검색하려면 memo.ilike 를 추가해야 하나
//                         이번 범위 밖이라 손대지 않았다.
//                       파일 변경: src/MobileInventory.jsx (App.js 는 버전만)
//
// v7.397 (2026-08-19) — 🗑 모바일 배송건 삭제 기능 추가 (사용자: "모바일에서도 배송건 삭제 기능을 추가")
//                       데스크톱 handleDeleteShipment 의 파괴적 로직을 [코어로 추출]해 모바일과 공유한다.
//                       (재고 복귀·데모 대여 취소 로직을 두 벌로 두면 v7.151류 드리프트가 재발한다.)
//                       변경 (App.js):
//                          ・ deleteShipmentCore(item): 데모취소 → 시리얼 재고복귀(EXT-/NOSN- 제외) → DB삭제.
//                            반환 { ok, restoreFailed[], error? }. 확인·UI갱신은 호출측 책임.
//                          ・ deleteShipmentWarning(item): note 상태별 확인 문구 (출고완료/데모예약/일반).
//                          ・ handleDeleteShipment: 위 둘을 쓰도록 리팩터 — 데스크톱 동작은 [완전 동일].
//                          ・ <MobileTxLog onDeleteShipment=...> 로 [확인+코어] 를 콜백 전달.
//                       변경 (src/MobileTxLog.jsx):
//                          ・ 상세 뷰 하단에 [🗑 배송 기록 삭제] 버튼 — 출고 타입일 때만 노출.
//                          ・ 삭제 성공 시 상세 닫고 목록 새로고침(load). 취소면 조용히 리턴.
//                       ⚠ 출고 전용이다. 입고는 재고 처리가 반대(추가 시리얼 회수)라 버튼을 노출하지 않는다.
//                       파일 변경: src/App.js, src/MobileTxLog.jsx
//
// v7.396 (2026-08-19) — 🐛 모바일 주소 필드 교정 — v7.395 주소가 안 뜨던 문제 (사용자: "주소가없는데?")
//                       원인: 주소는 note 에 [addr](+addrDetail) 로 저장되는데 v7.395 가 [address] 를 읽었다.
//                             출고요청 대다수가 addr 을 쓰므로 목록에서 주소가 항상 비어 있었다.
//                             (모바일 상세 뷰도 같은 info.address 버그가 있어 함께 고쳤다.)
//                       변경 (src/MobileTxLog.jsx):
//                          ・ fullAddress(info) 헬퍼 신설 — 데스크톱 배송시트지와 동일하게
//                            [addr, addrDetail] 을 공백으로 잇고, 없으면 address 로 폴백.
//                          ・ 목록 카드 · 상세 뷰 주소 표기 모두 이 헬퍼를 쓰도록 교정.
//                       ⚠ 방문수령/일부 입고 건은 주소가 없어 여전히 미표시 — 정상이다.
//                       파일 변경: src/MobileTxLog.jsx (App.js 는 버전만)
//
// v7.395 (2026-08-19) — 📱 모바일 배송시트지 목록 카드 — [제품별 개수] + [주소] 표기 추가
//                       사용자: "제품별 개수 주소가 표기되어야할것같아" (모바일 UI 스크린샷)
//                       변경 (src/MobileTxLog.jsx 목록 카드):
//                          ① 제품별 개수 — 다중 품목이면 각 품목을 [이름 ×수량] 으로 펼쳐 보여준다.
//                             기존엔 [제품명 외 N건] 으로 접혀 상세를 열어야 품목별 수량을 알 수 있었다.
//                             ⚠ 단일 품목은 [총 N개] 가 곧 그 제품 개수라 이 줄을 생략한다.
//                          ② 주소 — info.address 를 [📍 주소] 로 목록에 노출 (최대 2줄).
//                             기존엔 상세 뷰(배송명세서)에만 있었다. 입고 등 주소 없으면 미표시.
//                       ⚠ 판정/데이터는 기존 rowItems()·info.address 그대로 재사용 — 새 필드 없음.
//                       파일 변경: src/MobileTxLog.jsx (App.js 는 버전만)
//
// v7.394 (2026-08-19) — ✏ v7.393 라벨 문구 수정: [수량부족] → [재고부족] (사용자: "재고부족이 맞아")
//                       표기 라벨(단일/다중)과 hover 툴팁 문구만 바꿨다. 판정 로직·팝업 제거는 v7.393 그대로.
//
// v7.393 (2026-08-19) — 🟥 배송시트지 — 재고는 있으나 [요청 수량 > 가용] 인 품목을 [수량부족] 지속 표기
//                       사용자: "재고가 부족한 상태에서 수량을 늘리면 밑에 433처럼 빨간색으로 변경 후
//                       수량부족으로 표기해줄래?" (스크린샷: TS-473A-8G 재고 1개인데 요청 2개)
//
//                       기존: + 버튼으로 가용 초과 시 confirm 팝업만 떴다 → 확인 누르면 사라져 흔적이 없다.
//                       변경: 재고없음(stock===0)과 나란히, 재고는 있으나 수량이 초과된 품목을
//                             모델명 빨강 + 아랫줄 [(수량부족)] 으로 [지속] 표기한다.
//                       ・ shortInfo(it): 가용 = in_stock + 이미 확보한 시리얼(fulfilled+reserved).
//                         qty > 가용 이면 표기. ⚠ 판정 기준은 + 버튼(inc)이 쓰던 것과 동일하게 맞췄다.
//                       ・ stock===0 은 제외한다 — 위 noStock 이 이미 [재고없음] 으로 말한다 (중복 금지).
//                       ・ 단일 품목 줄(아랫줄 태그) · 다중 품목 박스(한 줄 뱃지) 양쪽 모두 적용.
//                       ・ 팝업이 알려주던 숫자(재고/가용/요청)는 [모델명 hover 툴팁] 으로 옮겨 보존했다.
//
//                       ★ + 버튼 confirm 팝업 [제거]. 사용자가 "433처럼"(팝업 없이 지속 빨강)을 요청했고,
//                         지속 표기가 팝업을 대체하므로 둘을 함께 두면 중복·간섭이다.
//                         ⚠ 이건 [차단]이 아니라 [경고 팝업]만 제거한 것이다. 출고 [요청]은 예약일 뿐,
//                           실제 재고 차감/경합 검사는 시리얼 스캔(출고 처리)에서 그대로 한다 (SESSION §3-2).
//                         팝업을 되살리고 싶으면 inc() 한 곳만 되돌리면 된다.
//
// v7.392 (2026-08-19) — 🧹 데스크톱 모드 [전면 제거] — 모바일을 v7.372 상태로 복구
//                       사용자: "앱에서 모바일버전인데 이전과다르게 화면이 넓어져서
//                       좌우로 스크롤해야되는 문제가 생겼어. 스크롤없는 이전버전으로 돌아가야해"
//
//                       ★ v7.373~391, 19개 버전에 걸쳐 [폰에서 PC 화면 보기] 를 만들려 했고
//                         그 과정에서 모바일 사용을 반복적으로 망가뜨렸다. 접는다.
//
//                       시도한 것과 결과 — 같은 길을 다시 파지 않기 위해 남긴다:
//                         ① viewport meta 를 useEffect 에서 setAttribute   → 무시됨
//                         ② 모듈 스코프에서 setAttribute                   → 무시됨
//                         ③ meta 를 remove 후 새로 append (재파싱 유도)     → 무시됨 (폭 360 그대로)
//                            ★ 크롬은 초기 파싱 이후 viewport 를 다시 잡아 주지 않는다.
//                         ④ min-width:1280 + CSS zoom                    → 동작은 했으나
//                            모드가 꺼진 뒤 잔재가 남아 모바일에 좌우 스크롤을 만들었다.
//                            [앱 / 폰브라우저 / PC] × [자동 / 수동] × [저장값] 조합이
//                            늘면서 상태가 어긋나는 경로를 다 막을 수 없었다.
//
//                       ⚠ 다시 만들 거라면 런타임 조작이 아니라 [진입 자체를 분리] 할 것.
//                         예: /desktop 경로에 viewport 가 처음부터 박힌 index.html 을 따로 둔다.
//                         public/index.html 은 NAS 에만 있어 그쪽 작업이 필요하다.
//
//                       ・ 제거: isDesktopModeOn / applyDesktopLayout / removeDesktopLayout /
//                         applyZoom / 줌 컨트롤 / 진단 칩 / ?desktop URL 스위치 /
//                         [💻 PC 화면] 버튼 / 사이드바 [📱 모바일 화면으로] 버튼
//                       ・ isMobile 을 파생값에서 다시 [단순 state] 로 되돌렸다.
//                         ⚠ 파생값으로 만들지 말 것 — 그 구조가 위의 조합 폭발을 낳았다.
//                       ・ 한시적 정리 코드 추가 — 구버전이 남긴 <style>·zoom·localStorage 를 걷는다.
//                         ⚠ 이건 [전환기용] 이다. 전 직원이 새 빌드를 받았다고 판단되면 지워도 된다.
//
//                       ⚠ v7.373~391 의 다른 작업(재고없음 표기·이월 최초 등록일·킵 차단 해제 등)은
//                         데스크톱 모드와 무관하므로 [전부 유지] 한다. 버전을 되돌린 게 아니다.
//
// v7.391 (2026-08-19) — 🔀 접속 경로로 화면 자동 결정 + 데스크톱 레이아웃 잔재 정리 (v7.392에서 제거)
//                       사용자: "폰->PC화면  어플접속->모바일전용화면"
//
//                       ★ 판정 기준을 [사용자가 켠다] 에서 [무엇으로 접속했나] 로 바꿨다.
//                           앱(Capacitor)  → 모바일 전용 화면
//                           폰 브라우저     → PC 화면 (좁으니 줌으로 본다)
//                           PC 브라우저     → PC 화면 (모드 자체가 무의미)
//                         ⚠ 매번 사람이 고르게 하면 안 누른 사람은 못 쓰는 기능이 된다.
//                           [💻 PC 화면] 버튼과 MobileGuardPage 진입점을 걷어냈다 — 되살리지 말 것.
//                         ⚠ ?desktop=0/1 수동 지정은 남긴다. 자동 판정이 틀렸을 때의 탈출구다.
//                           수동 지정 중일 때만 우상단 진단 칩이 뜬다 (평소엔 화면이 깨끗하다).
//
//                       ・ isNativeApp() — window.Capacitor.isNativePlatform().
//                         ⚠ v7.138 부터 MobileScan 이 ML Kit 분기에 쓰던 것과 같은 판정이다.
//                           앱에서 스캔이 되는 이상 server.url 모드에서도 브리지는 주입된다
//                           (capacitor.config: server.url = https://inventura.ixit.co.kr/).
//                         ⚠ 모듈 로드 시점엔 아직 없을 수 있어 한 번에 굳히지 않는다 —
//                           DOMContentLoaded 와 컴포넌트 effect 에서 다시 평가한다.
//
//                       ⚠⚠ 같이 고친 버그 — 데스크톱 레이아웃이 [지워지지 않았다].
//                         v7.388~390 은 min-width:1280 <style> 을 모듈 로드 시 1회만 넣고
//                         지우는 경로가 없었다. 모드가 꺼져도 스타일이 남아
//                         [모바일 UI 인데 문서 폭 1280] 이 됐다 — 화면이 잘린 게 아니라
//                         문서가 넓어서 오른쪽에 빈 공간이 생기고 좌우 스크롤이 살아 있었다.
//                         → removeDesktopLayout() 신설. 켜짐/꺼짐 양쪽에서 부르고,
//                           컴포넌트 effect 와 resize 측정에서도 확인한다.
//                         ⚠ zoom 도 같이 지운다. style 만 걷으면 축소된 모바일 UI 가 남는다.
//
// v7.390 (2026-08-19) — 🖥 PC 에서 데스크톱 모드가 [확대] 로 켜지던 문제
//                       사용자: "pc에서는 줌인이 생겼는데?" (1915px PC 에서 130~150%)
//
//                       원인 두 가지가 겹쳤다:
//                         ① ?desktop=1 로 테스트한 값이 그 브라우저 localStorage 에 남았다.
//                         ② fitZoom 이 화면폭÷1280 을 그대로 써서 1915px 화면에서 1.5 가 됐다.
//                            [맞춤] 은 축소해서 전체를 보는 기능인데 확대가 돼 버렸다.
//
//                       ・ isDesktopModeOn() 에 폭 조건 추가 — 화면이 1280 이상이면 무시한다.
//                         PC 에서는 이 모드가 애초에 의미가 없다 (min-width 1280 도 무효,
//                         배율도 1 이 원본이다). 부작용만 남는다.
//                         ⚠ localStorage 값은 [지우지 않는다]. 읽을 때만 무시한다 —
//                           코드가 사용자 설정을 임의로 지우면 왜 꺼졌는지 알 수 없다.
//                       ・ fitZoom 에 상한 1 — 확대는 [＋] 버튼으로만 하게 한다.
//                       ⚠ DESKTOP_MODE_WIDTH 선언을 desktopModeUseful() 위로 올렸다.
//                         아래에 있으면 호출 시점에 TDZ 로 터진다 (v7.379 와 같은 함정).
//
//                       검증(화면폭별): 360→28% · 768→60% · 1279→100% · 1280 이상→모드 무시
//
// v7.389 (2026-08-19) — 🔍 자체 줌아웃 컨트롤 (CSS zoom)
//                       사용자: v7.388 로 문서 폭 1280 확보 → "이제 줌아웃 기능을 추가해야할것같아"
//
//                       ★ 브라우저 핀치에 기대지 않는다. index.html 의 viewport meta 가
//                         user-scalable 을 막고 있을 수 있는데 그 파일은 NAS 에만 있어
//                         손댈 수 없다. viewport 를 못 건드리니 배율도 우리가 갖는다.
//
//                       ⚠ zoom 을 #root 가 아니라 documentElement(<html>) 에 건다.
//                         #root 에 걸면 position:fixed 자식(모달·토스트)이 zoom 배율로 그려져
//                         화면 일부만 덮는 깨진 모달이 된다. html 이면 fixed 도 균일하게 축소된다.
//                       ⚠ transform:scale 이 아니라 zoom 이다. scale 은 레이아웃 크기를
//                         안 바꿔서 주변에 빈 공간이 남고 스크롤 범위가 어긋난다.
//                         zoom 은 레이아웃째로 줄여 좌우 스크롤이 실제로 사라진다.
//                       ⚠⚠ 줌 컨트롤의 모든 치수를 1/zoom 으로 나눠 [시각 크기를 일정하게] 만든다.
//                         안 하면 배율을 줄일수록 버튼이 같이 작아져서 다시 키울 수가 없다.
//                         한 번 축소하면 갇히는 셈이라 이 보정이 없으면 기능 자체가 성립하지 않는다.
//                         진단 칩도 같은 이유로 보정한다.
//                       ⚠ desktopMode 가 꺼지면 zoom 을 반드시 지운다 ('' 로).
//                         남겨 두면 모바일 UI 가 축소된 채로 뜬다.
//                         '1' 이 아니라 '' 인 이유 — '1' 로 두면 zoom 컨텍스트가 남아
//                         일부 fixed 계산이 어긋난다.
//                       ・ 배율은 localStorage 에 저장. 기본값은 화면 폭 ÷ 1280 (전체가 한눈에).
//                       ・ [−] [%] [＋] 좌하단. % 를 누르면 화면 폭에 다시 맞춘다.
//                         (우하단은 모바일의 [PC 화면] 버튼, 우상단은 진단 칩이 쓴다)
//
// v7.388 (2026-08-19) — 🧱 viewport meta 방식 폐기 → [문서 폭 1280px 강제] 로 전환
//                       사용자 실측(v7.387): 레이아웃 폭 360px — meta 를 remove 후
//                       새로 append 해도 크롬이 무시했다.
//
//                       ⚠⚠ 결론: 크롬은 초기 파싱 이후 viewport 를 다시 잡아 주지 않는다.
//                         v7.373~387 이 전부 meta 조작이었고 전부 실패했다.
//                         index.html 에 처음부터 박혀 있어야 하는데, 그 파일은 NAS 에만 있고
//                         모바일 UI 와 공용이라 width=1280 을 상시로 박을 수도 없다.
//                         → meta 를 포기한다. 이 경로로 더 시도하지 말 것.
//
//                       ★ 새 접근: viewport 는 건드리지 않고 [문서 자체를 1280px 폭] 으로 만든다.
//                         페이지가 화면보다 넓으면 크롬은 핀치 축소·확대를 허용한다.
//                         브라우저 기본 동작이라 meta 승인 여부와 무관하게 항상 먹는다.
//                       ・ html/body/#root 에 min-width:1280px, 루트 flex 컨테이너에도 minWidth.
//                         ⚠ #root 까지 걸어야 한다. body 만 넓히면 안쪽 flex 가 화면 폭 기준으로
//                           잡혀 사이드바가 화면을 다 먹는다 (v7.386 스크린샷의 그 모습).
//                       ・ main 의 내부 스크롤(maxHeight:100vh + overflow:auto)을 해제한다.
//                         ⚠ 인라인 style 은 CSS !important 로도 못 이겨서 JSX 에서 직접 갈랐다.
//                         ⚠ 축소해서 보는 화면에 안쪽 스크롤 영역이 있으면 손가락 제스처가
//                           페이지 확대가 아니라 그 영역 스크롤로 먹힌다.
//                       ⚠ 대신 처음에는 배율 1.0(왼쪽 일부만 보이는 상태)으로 뜬다.
//                         손가락을 오므려 전체를 보는 흐름이다 — 원래 요청이 그것이었다.
//
//                       ・ 진단 칩에 [화면 폭 / 문서 폭] 둘 다 찍는다.
//                         정상: 화면 360 · 문서 1280 (문서가 화면보다 넓어야 축소가 허용된다)
//                         실패: 문서도 360 — 레이아웃 강제가 안 먹은 것
//
// v7.387 (2026-08-19) — 🧩 viewport meta 재파싱 강제 + 배율 직접 계산 (실패, v7.388에서 폐기)
//                       사용자 실측(v7.386): 칩은 뜨는데 사이드바가 화면의 65~70% 를 차지.
//                       사이드바는 256px 고정이므로 역산하면 레이아웃 폭이 [여전히 ~390px] 다.
//                       → viewport 가 아예 안 먹은 것이었다 (확대 문제가 아니었다).
//
//                       ⚠⚠ 이미 파싱된 meta 의 content 만 setAttribute 로 바꾸면
//                         크롬이 무시한다. v7.373~386 이 전부 이 방식이었다.
//                         → 기존 meta 를 [remove 후 새로 append] 해 재파싱을 강제한다.
//                       ・ initial-scale 도 직접 계산해 넣는다 (화면폭 ÷ 1280).
//                         생략하면 브라우저가 맞춰 주는 게 원칙이지만, 동적 삽입 경로에서는
//                         직전 배율(1.0)을 유지해 버린다.
//                         ⚠ screen.width 가 아니라 innerWidth 우선 — screen.width 는
//                           기기 전체 폭이라 분할화면에서 실제 창 크기와 어긋난다.
//                         실측 예: 393px 폰 → 0.307 (표시폭 393px, 전체가 한눈에)
//
//                       ・ 진단 칩에 [실제 레이아웃 폭] 을 찍는다.
//                         ⚠ 이 숫자 하나가 성공/실패를 결정적으로 가른다.
//                           1280 이면 먹은 것, 390 근처면 무시된 것.
//                           "확대돼 보인다" 같은 눈대중으로는 두 상황을 구분할 수 없다.
//                         ⚠ innerWidth 가 아니라 documentElement.clientWidth 를 본다.
//                           innerWidth 는 확대 배율의 영향을 받아 시각 뷰포트를 줄 때가 있다.
//
// v7.386 (2026-08-19) — 🔎 데스크톱 모드 진단 수단 (URL 스위치 + 상태 칩)
//                       사용자: v7.385 적용 후에도 "모바일화면 그대로야"
//
//                       ★ 원인을 가르기 위한 판이다. 지금 증상만으로는
//                         ① 빌드가 아직 안 올라갔다 ② desktopMode 가 안 켜진다
//                         ③ 켜졌는데 viewport 가 안 먹는다 — 셋을 구분할 수 없다.
//
//                       ・ URL 스위치: ?desktop=1 / ?desktop=0
//                         버튼·리로드 경로를 통째로 건너뛰고 localStorage 에 직접 쓴다.
//                         ⚠ applyDesktopViewport 보다 [먼저] 실행돼야 한다 (같은 값을 읽는다).
//                         ⚠ 데스크톱 모드에서 못 빠져나올 때의 최종 탈출구이기도 하다.
//                       ・ 상태 칩: desktopMode 가 true 면 우상단에 [💻 데스크톱 모드 · 버전].
//                         칩이 보이는데 화면이 모바일이면 → viewport 문제로 좁혀진다.
//                         칩이 안 보이면 → localStorage/빌드 문제다.
//
// v7.385 (2026-08-19) — 🔍 데스크톱 모드가 [확대된 채] 뜨던 문제 수정 (viewport 적용 시점)
//                       사용자: "사이드탭에 화면을 다가리고있어. pc화면처럼 볼수는 없는거야?"
//
//                       ★ 화면이 가려진 게 아니라 [확대된 상태] 였다.
//                         viewport meta 를 React 마운트 후(useEffect)에 바꾸면 브라우저는
//                         레이아웃 폭만 1280 으로 다시 잡고 [확대 배율은 그대로 1.0 으로 둔다].
//                         → 390px 폰에 1280 폭 레이아웃이 배율 1.0 으로 그려져
//                           왼쪽 1/3(사이드바 256px + 여백)만 꽉 차 보인다.
//                           v7.373 부터 계속 이 상태였다.
//
//                       ・ viewport 적용을 [모듈 스코프] 로 올렸다 (applyDesktopViewport).
//                         viewport 는 첫 레이아웃 전에 정해져 있어야 브라우저가 화면 폭에
//                         맞는 배율을 계산한다. useEffect 는 이미 늦다.
//                       ・ toggleDesktopMode 는 localStorage 에 쓰고 window.location.reload().
//                         ⚠ 리로드가 [핵심] 이다. 실행 중에 바꾸면 위 증상이 그대로 재현된다.
//                         ⚠ setDesktopMode 를 부르지 않는다. 어차피 리로드되므로 상태만 바꾸면
//                           한 프레임 동안 [배율은 1.0 인데 PC 레이아웃] 인 깨진 화면이 스친다.
//                       ・ desktopMode state 는 읽기 전용이 됐다 (초기값 = isDesktopModeOn()).
//                         ⚠ 모듈 스코프와 [같은 키] 를 봐야 한다. 어긋나면 viewport 는 1280 인데
//                           컴포넌트는 모바일 UI 를 그리는 상태가 된다.
//
//                       ⚠ 사이드바는 그대로 둔다 (v7.383 햄버거 롤백 유지).
//                         목적이 [PC 화면 그대로 보기] 이므로 재배치가 아니라 확대로 푼다.
//
// v7.384 (2026-08-19) — ↩ v7.383 햄버거 롤백 + 초기 배율 자동화
//                       사용자: "헴버거 버튼은 롤벡해주고. 모바일에 데스크톱 화면을 그대로
//                       띄우려고해. 화면이 작아도 확대하면서 볼수있도록하는게 목적이야"
//
//                       ・ v7.383 의 서랍·백드롭·햄버거·자동닫기 effect 전부 제거.
//                         사이드바는 PC 와 똑같이 고정 컬럼으로 돌아간다.
//                         → 목적이 [PC 화면 그대로 보기] 이므로 레이아웃을 바꾸면 안 된다.
//                           작아서 안 보이는 건 [확대] 로 푸는 것이지 [재배치] 로 풀 문제가 아니다.
//                       ⚠ v7.383 의 isNarrow 조건 수정은 [남긴다]. 그건 별개의 실제 버그다
//                         (아래 v7.383 항목 참고) — 되돌리면 데스크톱 모드에서 못 빠져나온다.
//
//                       ・ viewport 에서 initial-scale 제거.
//                         ⚠ v7.373 은 0.3 을 박아 넣었는데 이건 390px 폰 기준값이다.
//                           430px 폰·태블릿에서는 필요 이상으로 축소돼 여백만 생긴다.
//                           width=1280 만 주면 브라우저가 화면 폭에 맞는 배율을 계산한다.
//                         최종: width=1280, minimum-scale=0.2, maximum-scale=5.0, user-scalable=yes
//                       ⚠ user-scalable=yes 와 넉넉한 maximum-scale 이 이 기능의 전부다.
//                         축소해서 전체를 보고 손가락으로 벌려 읽는 게 사용 방식이다.
//
// v7.383 (2026-08-19) — ☰ 데스크톱 모드 사이드바를 햄버거 서랍으로 (v7.384에서 롤백)
//                       + v7.373 탈출구 버그 수정 (이 부분은 유지)
//                       사용자: "모바일에서 좌측 탭때문에 화면이 안보이는데 모바일에서
//                       탭항목들을 햄버거로 바꿀수있어?"
//
//                       ・ desktopMode 일 때 <aside> 를 position:fixed 서랍으로 전환.
//                         translateX(-100%) ↔ 0, 백드롭, 좌상단 햄버거(52px).
//                         ⚠ 사이드바가 fixed 로 빠지면 본문이 그 자리를 차지한다 →
//                           메뉴를 열 수단이 화면에 없다. 햄버거가 유일한 진입점이다.
//                         ⚠ z-index: 백드롭 1200 < 서랍 1201 < 햄버거 1202.
//                           햄버거가 서랍 위에 있어야 열린 상태에서 닫기를 겸한다.
//                         ⚠ 백드롭은 열려 있을 때만 깐다. 항상 깔면 본문 클릭이 막힌다.
//                       ・ page 가 바뀌면 서랍을 닫는다 (useEffect 한 곳).
//                         ⚠ 메뉴 항목마다 닫기를 붙이면 20곳을 고쳐야 하고 하나만 빠져도
//                           서랍이 열린 채 본문을 가린다.
//
//                       ⚠⚠ v7.373 버그 수정 — isNarrow 는 [물리적 사실] 이 아니었다.
//                         데스크톱 모드가 viewport 를 width=1280 으로 바꾸면
//                         matchMedia('(max-width:768px)') 도 1280 을 기준으로 재평가되어
//                         isNarrow 가 false 로 뒤집힌다. 즉 (desktopMode && isNarrow) 는
//                         실기에서 영원히 false 다.
//                         → 사이드바의 [📱 모바일 화면으로 돌아가기] 버튼이 이 조건이라
//                           아예 뜨지 않았다. 설정은 localStorage 에 남으므로
//                           재접속해도 못 빠져나오는 상태였다.
//                         → 데스크톱 모드 관련 조건은 desktopMode 하나만 쓴다.
//                           이 값은 모바일 화면의 [💻 PC 화면] 버튼으로만 켜지므로
//                           그 자체가 [폰에서 켰다] 는 뜻이다.
//
// v7.382 (2026-08-19) — 🕘 이월된 건에 [최초 등록일 · N일째] 표기
//                       사용자: "배송건이 다음날로 이월되거나 계속이월될경우 처음 기입한
//                       날짜가 언제인지 알수가 없어서 이월된 건에 한해서 최초 등록일을 표기하면 좋겟어"
//
//                       ★ 새 컬럼이 필요 없다. sweepAutoDefer 는 transaction_date 만
//                         갱신하고 [행은 그대로] 두므로 created_at 에 최초 등록 시각이
//                         이미 남아 있다. 그걸 읽기만 하면 된다.
//                       ・ deferInfoOf(item) — 모듈 스코프 헬퍼. { origDate, days } 또는 null.
//
//                       ⚠ created_at 은 timestamptz 다. toISOString() 을 쓰면 UTC 라
//                         한국 오전 9시 전 등록 건이 하루 전으로 밀린다.
//                         → 로컬 getFullYear/getMonth/getDate 로 만든다 (v7.126 과 같은 함정).
//                         실측 검증: created_at 2026-08-13T02:53+09:00 → KST 에서 08.13 (정상).
//                         같은 코드가 UTC 환경에서는 08.12 로 나온다 — 브라우저 TZ 를 타는 값이다.
//                       ⚠ created_at 이 transaction_date [뒤] 면 이월이 아니다.
//                         과거 날짜로 누락 기록을 추가한 건(v7.32)이 여기 해당한다 —
//                         표시하면 거짓말이 된다. iso >= transaction_date 이면 null 을 준다.
//                       ⚠ 경과 일수는 날짜 문자열을 [로컬 정오] 로 파싱해 계산한다.
//                         자정으로 파싱하면 DST·타임존 경계에서 하루가 밀린다.
//                       ⚠ 이월된 건에만 뜬다. 모든 행에 붙이면 위 날짜와 같은 값이 두 번
//                         적히는 꼴이라 아무도 안 읽게 된다.
//
// v7.381 (2026-08-19) — ↵ (재고없음) 을 제품명 아랫줄로 (다중 품목도 단일과 동일 표기)
//                       사용자: "(재고없음) 이 줄바꿈으로 표기되면 될것같은데"
//
//                       v7.378 은 다중 품목만 같은 줄 뱃지로 처리했다 — 옆 수량 칸과의
//                       줄맞춤(minHeight 26, v7.63)을 지키려던 것이다.
//                       ⚠ 그런데 옆에 붙이면 250px 안에서 이름과 자리를 다투게 되고,
//                         결국 이름이 밀려 두 줄로 접힌다. 표기 위치만 다를 뿐 높이는
//                         똑같이 늘어나면서 읽기만 나빠진다 — 지킬 게 없는 타협이었다.
//                       → 단일/다중 모두 아랫줄로 통일. 재고없음 줄이 있는 박스는
//                         alignItems flex-start + padding 을 살짝 키워 위쪽 정렬로 맞춘다.
//
//                       ⚠ 감수한 것: 재고없음 품목의 박스가 2줄이 되어 옆 수량 칸과
//                         한 칸 어긋난다. 재고 유무를 못 알아보는 쪽이 더 나쁘다는 판단이다.
//                         (어긋나는 건 재고없음 품목이 섞인 행뿐이다)
//
// v7.380 (2026-08-19) — ✂ 배송시트지 제품명 잘림 해소 (모델명 칸 200 → 250px)
//                       사용자: "제품명이 ...으로 안가려졌으면 좋겠어"
//
//                       v7.378 이 (재고없음) 을 같은 줄에 붙이면서 200px 칸이 모자라
//                       [TS-43...] 처럼 잘렸다.
//                       ⚠ 이게 왜 심각한가 — TS-433-4G-KR 과 TS-433-8G-KR 이 화면에서
//                         똑같이 [TS-43...] 이 된다. 시트지에서 제품을 잘못 읽는 것은
//                         곧 오출고다. 정렬보다 식별이 우선이다.
//
//                       ・ 모델명 컬럼 200px → 250px (헤더/행 두 곳 동시, minWidth 1240 → 1290)
//                         ⚠ 그리드 정의가 두 군데 있다. 한쪽만 고치면 헤더와 본문이 어긋난다.
//                       ・ 다중 품목 줄에서 ellipsis 제거 → wordBreak:'break-all' 로 줄바꿈 허용.
//                         잘리는 것보다 두 줄이 되는 편이 낫다.
//                       ・ (재고없음) 을 이름 span [안쪽] 으로 넣었다.
//                         밖에 flexShrink:0 으로 두면 긴 이름에서 이름 쪽만 계속 눌린다.
//
//                       ⚠ 감수한 것: 아주 긴 모델명은 두 줄이 되어 옆 수량 칸(minHeight 26,
//                         v7.63)과 한 칸 어긋난다. 250px 면 12px 폰트로 30자 정도 들어가
//                         실사용 모델명(12~21자)은 대부분 한 줄에 끝난다.
//                         정렬이 어긋나는 쪽이 제품을 잘못 읽는 쪽보다 낫다는 판단이다.
//
// v7.379 (2026-08-19) — 🩹 v7.378 흰 화면 긴급 수정 (TDZ)
//                       증상: 배포 후 페이지가 통째로 안 뜸 (흰 화면).
//                       원인: v7.378 의 재고 조회 useEffect 를 8841행에 넣었는데
//                             거기서 쓰는 logItems 선언은 9777행이었다.
//                             ⚠ 의존성 배열 [page, logItems] 는 렌더 시점에 [즉시] 평가된다.
//                               → ReferenceError: Cannot access 'logItems' before initialization
//                       조치: 훅을 logItems / logStockByModel 선언 바로 아래로 이동.
//
//                       ⚠⚠ 이 실수는 Babel 파싱도 ESLint no-undef 도 못 잡는다.
//                         스코프에는 존재하고 [순서] 만 틀렸기 때문이다 (TDZ).
//                         → /tmp/babel-check/tdz-check.js 를 세션 검증에 추가한다.
//                           훅 의존성 배열이 자기보다 아래에서 선언된 값을 참조하면 실패시킨다.
//                       ⚠ 교훈: useEffect 를 [적당한 주제별 위치] 에 꽂지 말 것.
//                         이 파일은 274개 useState 가 위쪽에 몰려 있어서, 훅은 반드시
//                         자기가 쓰는 state 선언 [아래] 에 둬야 한다.
//
// v7.378 (2026-08-19) — 🔴 배송시트지 — 재고 없는 품목을 빨간 모델명 + (재고없음) 으로
//                       사용자: "제고없는 재품은 상품명을 빨간색으로 표기하고
//                       상품명 / (제고없음) 이런식으로 표기해줄래?"
//
//                       ★ 지금까지는 출고 처리 모달을 열어 스캔을 시도해야만 재고가 없다는 걸
//                         알 수 있었다. 시트지 목록에서 바로 보이게 한다 —
//                         재고 0 요청을 허용한(v7.374~375) 이상 [어느 건이 아직 못 나가는지]가
//                         한눈에 보여야 한다.
//
//                       ・ InventoryService.countInStockByNames(names) 신설.
//                         ⚠ 제품마다 countInStock 을 부르면 한 페이지에 수십 번 왕복한다.
//                           .in() 한 방으로 받아 클라이언트에서 센다.
//                         ⚠ 이름이 많으면 .in() URL 이 길어져 414 가 난다 → 80개씩 끊어 보낸다.
//                         ⚠ 호출한 이름 전부를 0으로 미리 깔아 둔다. undefined 와 0 을
//                           구분 못 하면 화면에서 "로딩 중"과 "재고없음"이 섞인다.
//
//                       ⚠ logStockByModel === null 은 [아직 안 받음] 이다. 0 으로 취급하면
//                         로딩 중 목록 전체가 빨갛게 깜빡인다 — 반드시 구분할 것.
//                       ⚠ 대상에서 빼는 것들 (조회·렌더 양쪽에 같은 조건):
//                           ・ 입고 행 — 아직 안 들어온 물건이라 재고 0이 당연하다.
//                             표시하면 입고 시트지가 통째로 빨개진다
//                           ・ 처리 완료(fulfilled) 건 — 방금 출고해서 0이 된 품목이
//                             빨갛게 뜨면 오해만 만든다
//                           ・ 외부(external) 품목 — 애초에 재고 추적 대상이 아니다
//                           ・ 이미 시리얼을 찍어 둔 품목 — 물건을 확보해 둔 것이라
//                             현재 재고가 0이어도 문제가 아니다
//                       ⚠ 다중 품목 줄은 (재고없음) 을 [같은 줄 뱃지] 로 붙인다.
//                         아래 줄로 내리면 minHeight 26 정렬(v7.63)이 깨져 옆 수량 칸과 어긋난다.
//                         단일 품목만 요청대로 아랫줄에 표기한다.
//
// v7.377 (2026-08-19) — ↩ v7.376 롤백 — [임시 EXT-] 버튼을 모든 품목에 되살린다
//                       사용자: "EXT버튼 있는버전으로 롤백해줄래?"
//
//                       ・ 인라인·팝업 두 인스턴스에서 !current.inCatalog 조건 제거.
//                       ・ models[].inCatalog 계산은 [남겨 둔다].
//                         findByNames 를 어차피 호출하고 있어 추가 비용이 0이고,
//                         다시 막고 싶어질 때 조건 한 줄만 붙이면 된다.
//                         ⚠ 지금은 UI 에서 쓰지 않는다 — 지우지 말고 그대로 둘 것.
//
//                       ⚠⚠ 되살렸다고 안전해진 것은 아니다. 이 버튼은 EXT-모델-01 같은
//                         [가짜 시리얼] 을 만든다. commitRequest 는 EXT- 로 시작하는 시리얼을
//                         inventory 검증 없이 통과시킨다:
//                             if (p.serial.startsWith('EXT-')) { successPending.push(p); continue; }
//                         → transaction_log 에는 출고로 남지만 inventory 는 그대로다.
//                           실물 시리얼이 있는 제품에 쓰면 [장부상 출고 + 재고 잔존] 이 되어
//                           재고가 그만큼 부풀려진다.
//                       ⚠ 실물이 있는 제품에는 입고 후 실제 시리얼을 찍는 것이 올바른 경로다.
//                         EXT- 는 재고 추적을 하지 않는 [외부 제품] 전용 수단이다.
//
// v7.376 (2026-08-19) — 🚫 카탈로그 등록 제품에는 [임시 EXT-] 버튼을 숨긴다 (v7.377에서 롤백)
//                       사용자: "카탈로그 등록제품은 임시 EXT 버튼을 없애는게 좋겠어"
//
//                       ⚠ 이 버튼은 EXT-모델-01 같은 [가짜 시리얼] 을 만들어
//                         재고 차감 없이 출고 처리한다. 실물 시리얼이 있는 제품에 쓰면
//                         재고가 그대로 어긋난다. 그런데 v7.145 이후 모든 품목에 떠 있어서,
//                         재고 0 요청(v7.374~375)을 처리하려던 사람이 바로 옆의 주황색
//                         버튼을 누를 위험이 있었다.
//                       ・ 원래 목적은 [외부 플래그가 유실된 비카탈로그 요청] 구제다.
//                         카탈로그 제품에는 애초에 필요가 없다 —
//                         재고 0이어도 입고 후 실제 시리얼을 찍는 게 올바른 경로다.
//
//                       ・ loadActiveRequest 에서 models[].inCatalog 를 심는다.
//                       ⚠ category 로 판정하면 안 된다. products.category 는 null 일 수 있어
//                         "카탈로그에 있는데 분류만 비어 있는 제품"이 미등록으로 오판된다.
//                         → findByNames 결과로 Set 을 따로 만든다.
//                       ⚠ 이 모달은 [인라인 + 팝업] 두 곳에 렌더된다. 한쪽만 고치면
//                         팝업에서는 버튼이 그대로 살아 있다 (기존에 여러 번 겪은 함정).
//                       ・ commitRequest 의 updatedModels 는 { ...m } 전개라 커밋 후에도
//                         inCatalog 가 유지된다.
//
//                       ── 같이 검증한 것 (코드 추적 + 시뮬레이션) ──
//                       재고 0 요청 → 입고 → 시리얼 스캔 → 출고 경로가 정상 동작함을 확인:
//                         ① note.items 에 available/external 이 저장되지 않는다
//                            (요청 시점 재고가 나중을 제약할 수단 자체가 없다)
//                         ② handleRequestScan 은 현재 status==='in_stock' 만 본다
//                         ③ commitRequest 에 킵·재고 수량 검사가 없다
//                            (시리얼 단위 낙관적 락 eq(status,'in_stock') 뿐)
//                       → v7.375 에서 푼 차단이 유일한 관문이었다.
//
// v7.375 (2026-08-19) — 🔖 킵 차단 해제 — 출고 요청을 막지 않는다
//                       사용자: "경고로 막아놨는데 이것도 제거하야겠다"
//
//                       ⚠ 출고 [요청] 은 출고가 아니다. 실제 재고 차감은 나중에 시리얼을
//                         찍는 출고 처리 단계에서 일어나고 거기서 다시 검사한다.
//                         요청 시점에 막으면 "입고 예정이라 미리 요청해 두는" 정상 업무가
//                         통째로 불가능해진다. v7.374 와 같은 판단이다.
//                       ・ submitShipRequest 의 return 제거 → console.warn 만 남긴다
//                       ・ 대신 경고를 [보내기 전] 최종 확인 패널로 옮겼다.
//                         ⚠ 예전에는 출고 요청 버튼을 눌러야 알 수 있었고 그것도 차단이었다.
//                           누르고 실패하는 것보다 누르기 전에 아는 편이 낫다.
//                         ⚠ 남의 킵을 건드리는 건 실제 사고로 이어진다. 차단은 풀되
//                           경고까지 없애지는 않는다 — 조용히 넘어가면 안 되는 종류다.
//                       ⚠ 재고 0인 품목은 이 경고에서 제외한다. 위 [재고없음] 이 이미
//                         말하고 있어서, 같은 제품이 두 번 경고되면 둘 다 무시하게 된다.
//                       ⚠ 킵 수량은 activeKeepsByModel(기존 state)을 쓴다. 화면 표시용이라
//                         서버 재조회를 하지 않는다 — 타이핑마다 왕복을 만들 이유가 없다.
//                         정확한 최신 값은 출고 처리 단계에서 다시 조회한다.
//
// v7.374 (2026-08-19) — 📦 재고 없는 제품도 출고 요청 가능 (재고없음 명시)
//                       사용자: "제품이 없어도 요청은 할수 있도록 하되 제고없음을 표기하면
//                       사용자입장에서 확실히 인지할수있을것같아"
//
//                       ⚠ 기존 검색은 inventory(재고 있는 것)만 봤다. 재고 0인 제품은
//                         목록에 아예 안 떠서, 사용자는 [등록 안 된 제품]인지 [재고가 없는 것]인지
//                         구분할 수 없었다. 결국 모델명을 손으로 쳐서 Enter 로 넣는
//                         우회로(v7.74)를 쓰게 된다. 검색이 안 되는 것과 재고가 없는 것은
//                         다른 사실이다 — 화면에서 갈라 보여준다.
//                       ・ searchProducts 가 inventory + products 를 [병렬로] 조회해 병합.
//                         순차로 하면 타이핑마다 왕복이 두 배가 된다.
//                       ・ 정렬: 재고 있는 것 먼저 → 수량 많은 순 → 재고 0끼리 이름순.
//                         ⚠ 재고 0을 위로 올리면 자주 쓰는 제품이 아래로 밀린다.
//                           목록 순서가 사실상 기본값 역할을 한다.
//
//                       ⚠⚠ 같이 고친 잠재 버그 — 재고 0이면 수량이 0으로 무너졌다.
//                         updateShipQty / setShipQty 가 Math.min(available, n) 을 쓰는데
//                         available=0 이면 결과가 항상 0이다. v7.74 가 재고 0 제품을 담는
//                         경로를 열어 뒀지만, +/− 를 한 번만 눌러도 qty 가 0이 되어
//                         사실상 못 쓰는 상태였다.
//                         → shipQtyCapped(i) = !external && available > 0 으로 판정.
//                           재고 없음은 [0개만 요청 가능]이 아니라 [입고 후 처리할 예약]이다.
//
//                       ・ [재고없음] 표기를 세 곳에 둔다. 한 곳만으로는 놓친다:
//                           ① 검색 드롭다운 — 고르기 전
//                           ② 담긴 품목 줄 — 여러 건 담는 동안 어느 게 없는 건지
//                           ③ 최종 확인 패널 — 보내기 직전 마지막 관문
//                       ⚠ 막지 않는다. 예약 요청은 정상 업무다 — 알리기만 한다.
//                       ⚠ 모바일(MobileShipRequest)은 손대지 않았다. 이미 재고 초과를
//                         경고만 하고 허용하는 구조다.
//
// v7.373 (2026-08-19) — 💻 데스크톱 모드 — 모바일에서 PC 화면 그대로 보기
//                       사용자: "먼저 PC화면을 그대로 모바일에서 이용할수있도록 하는버전이 필요해"
//
//                       ★ 핵심은 컴포넌트가 아니라 viewport 다.
//                         PC 컴포넌트를 390px 뷰포트에 그대로 그리면 표가 짓뭉개져서
//                         [그래도 계속 보기](v7.117)가 이미 그 상태였고 아무도 안 썼다.
//                         → meta viewport 를 width=1280 으로 강제해 브라우저가
//                           데스크톱 폭으로 레이아웃하고 화면에 맞춰 축소하게 한다.
//                           Chrome 의 [데스크톱 사이트 요청] 과 같은 원리다.
//                       ⚠ user-scalable=yes 필수. 축소된 화면에서 핀치 줌이 막히면
//                         글자가 작아 아무것도 못 읽는다 — 기능 자체가 무의미해진다.
//                       ⚠ 원래 meta 값을 기억했다가 되돌린다. public/index.html 은
//                         NAS 에만 있어 tarball 로 안 넘어온다. 하드코딩해 덮으면 안 된다.
//
//                       ・ isMobile 을 [파생값] 으로 바꿨다:
//                           isNarrow    = matchMedia(max-width:768px) — 물리적 사실
//                           desktopMode = 사용자 선택 (localStorage)
//                           isMobile    = isNarrow && !desktopMode
//                         ⚠ 이름을 그대로 둔 이유 — isMobile 참조가 50곳이다.
//                           rename 하면 그 자체가 회귀 위험이고 얻는 게 없다.
//                         ⚠ setIsMobile 은 이제 없다. 파생값이라 세터가 없다.
//
//                       ・ 진입: 우하단 떠 있는 [💻 PC 화면] 버튼 (모바일 4페이지 공통)
//                         ⚠ 모바일 헤더·탭바는 각 Mobile*.jsx 가 자기 안에서 그린다.
//                           5번째 탭을 넣으면 4개 파일을 다 고쳐야 하고 탭 폭이 깨진다.
//                         ⚠ 우하단인 이유: 모바일 헤더는 sticky top:0 이고 하단 고정 바가
//                           없다. 여기가 유일하게 아무것도 안 가리는 자리다.
//                         ⚠ z-index 900 — 모달(1000)보다 아래. 위에 뜨면 확인 버튼을 가린다.
//                       ・ 탈출: 사이드바 하단 [📱 모바일 화면으로 돌아가기]
//                         ⚠ 반드시 있어야 한다. 데스크톱 모드에서는 모바일 탭바가 사라지므로
//                           이게 없으면 localStorage 를 지우기 전까지 못 빠져나온다.
//                       ・ 모드 전환 시 페이지를 되돌리지 않는다. 화이트리스트 밖 페이지를
//                         보려고 켜는 건데 켜자마자 'ship' 으로 튕기면 켠 의미가 없다.
//
//                       ⚠ 알아둘 것 — MobileGuardPage(v7.117)는 사실상 죽은 코드다.
//                         화이트리스트 밖 페이지는 강제 리다이렉트가 먼저 걸려서
//                         안내 화면에 도달할 수가 없다. 이번엔 손대지 않고 버튼만 추가해 뒀다.
//
// v7.372 (2026-08-14) — 🎆 불꽃놀이 지속시간 ↑ + 폭죽 소리 추가
//                       사용자: "지속시간을 늘이고 폭죽 소리도 추가할수있을까?"
//
//                       ・ 지속시간 7초 → 15초 (FireworksShow duration 기본값).
//                         '남은 불꽃이 다 스러지면 종료' 로직은 그대로라, 15초 이후
//                         발사만 멈추고 마지막 폭죽 잔향까지 보여준 뒤 닫힌다.
//                       ・ 폭죽 소리 = Web Audio API [실시간 합성] (mp3/wav 파일 없음).
//                         폭발마다 ①저역 붐 ②밴드패스 노이즈 크랙 ③하이패스 크래클,
//                         발사 때 올라가는 휘파람. 화면 x 위치대로 좌우 패닝.
//
//                       ★ 왜 파일이 아니라 합성인가 — public/ 은 NAS 에만 있어 오디오
//                         파일을 넣을 수 없고, 넣어도 배포마다 누락 위험. SVG/canvas 를
//                         파일 없이 그려 온 것과 같은 이유로 소리도 코드로 만든다.
//
//                       ⚠ 사무실 배려 — master 0.6 + DynamicsCompressor 로 겹쳐 터져도
//                         찢어지지 않게 눌렀다. 그래도 소리가 있으니 볼륨 주의.
//                       ⚠ 자동재생 정책 — AudioContext 는 사용자 제스처 후에만 난다.
//                         10번째 클릭 직후 마운트라 transient activation 이 살아 재생됨.
//                         소리가 막히는 환경이어도 try/catch 로 그림은 그대로 돈다.
//                       ⚠ AudioContext 는 언마운트(open=false) 때 close() — 누수 없음.
//
// v7.371 (2026-08-14) — 🎆 이스터에그: 빈 공간 10번 클릭 → 불꽃놀이
//                       사용자: "빈공간을 10번클릭하면 화면이 어두워지고 랜덤한
//                       Spark를 출력해서 불꽃놀이처럼 보이게하면 재미있을것같아"
//
//                       ・ src/FireworksShow.jsx (신규) — 순수 canvas 2d 불꽃놀이 쇼.
//                         로켓 상승 → 정점에서 방사형 스파크 폭발 → 중력·감쇠로 스러짐.
//                         스파크 [생김새]는 최상단 <ClickSpark> 와 맞췄다(가는 선 방사형).
//                       ・ App.js: document click 핸들러로 '빈 공간 10연타' 판정 후 발동.
//
//                       ★ '빈 공간' 정의 — 두 조건을 [동시에] 만족해야 1로 센다:
//                         ① 인터랙티브 요소(a/button/input/img/table/…) 위가 아니다
//                         ② 클릭된 요소가 [자체 텍스트](정보)를 안 가진 순수 컨테이너다
//                         → 카드 글자·값을 눌러도 안 세고, 여백/배경을 눌러야 센다.
//                         ("버튼이나 정보가없는 빈공간" 이라는 요청을 그대로 옮긴 것)
//
//                       ⚠ ClickSpark 를 [재활용하지 않고] 별도 컴포넌트로 만들었다.
//                         ClickSpark 는 사용자 클릭에 반응하는 상시 효과라 '가만히 둬도
//                         알아서 터지는' 자동 불꽃놀이를 만들 수 없다. 둘은 공존한다
//                         (클릭할 때마다 파란 스파크 + 10번째에 전체 불꽃놀이 발동).
//                       ⚠ 3초 이상 텀이 벌어지면 카운터 리셋 — 우연 누적 방지.
//                       ⚠ 의존성 0(순수 canvas) → package.json·도커 빌드 무영향.
//                         open=false 면 언마운트 → 평소 성능 영향 0.
//                       ⚠ AUTH UI 아님 / DB 무관 — 순수 프런트 연출이라 마이그레이션 없음.
//
// v7.370 (2026-08-13) — 📸 박스 라벨 일괄 촬영 — 사진 1장에서 시리얼 20개 + 검산
//                       사용자: "스캔방식을 사진촬영 방식으로바꾸면 정확도가 올라갈까?"
//                       → 규격 확인 결과 **Code 39** (사용자 확인). 하단 20개가 제품 시리얼.
//
//                       ★ 먼저 정정할 것 — 촬영으로 오독이 사라지지 않는다.
//                         Code 39 오독의 물리적 원인은 [바 굵기 오판]이고, 정지 촬영은
//                         AF 락·무블러·플래시·풀 해상도로 그 원인을 줄일 뿐이다.
//                         v7.272 N회 일치 · v7.288 다수결 · v7.295 ZXing 교차검증을
//                         다 얹고도 뚫린 이유는 [같은 각도면 같은 오독이 결정적으로 재현]되기
//                         때문이다. 촬영도 이걸 못 이긴다.
//
//                       ★ 그래서 목표를 바꿨다: [정확도 향상] → [틀렸다는 걸 알 수 있게]
//                         낱개 스캔은 비교 대상이 없어 오독을 영영 못 잡는다.
//                         그런데 카톤 라벨에는 검증 수단이 인쇄되어 있다:
//                           ① QTY: 20  → 몇 개를 놓쳤는지 알 수 있는 유일한 값
//                           ② 시리얼 20개가 같은 로트 → 길이·접두어가 같다.
//                              19개가 PPG 인데 하나가 PP6 이면 그게 오독이다.
//                         한 장에서 동시에 읽으면 서로가 서로의 검증자가 된다.
//
//                       ・ src/services/BatchLabelService.js (신규)
//                         decodeImageFile → classify(시리얼/QTY/MODEL 분리) → analyze(이상치)
//                         기존 zxing-wasm · structuralCheck · suspectMisread 를 그대로 쓴다.
//                         실측 라벨(WD 20TB 카톤) 검증: 시리얼 20개 전부 8자·PPG 로 묶이고
//                         BOX ID·P/N·MLC·MODEL 은 이상치로 분리. 오독 주입 시 정확히 검출.
//                       ・ MobileScan: [🖼 박스 라벨 촬영] 버튼 + 판독 결과 확인 창
//                         (체크박스 격자 · QTY 대조 배지 · [정밀 재분석])
//
//                       ⚠ 라이브 스캔을 대체하지 않는다. 낱개는 셔터 기다리는 게 더 느리다.
//                         [카메라 시작] 아래 보조 버튼으로 둔다 — 기존 경로 무변경.
//                       ⚠ 자동으로 지우지 않는다. 이상치는 [체크 해제] 상태로 보여줄 뿐이다.
//                         v7.272 에서 이미 배운 것 — 진짜 시리얼끼리도 1글자 차이가 난다
//                         (V7GG3NMH ↔ V7GG3NRH). 자동 삭제하면 진짜 시리얼이 사라진다.
//                         1글자 차이(suspect)는 체크된 채 노란 표시만 한다.
//                       ⚠ 해상도를 깎지 않는다. 바당 픽셀 수가 곧 Code 39 판정 정확도다 —
//                         축소는 오독의 원인을 키우는 짓이다. 상한 1600만 화소는 OOM 방지선.
//                       ⚠ 정밀 재분석의 타일은 14% 겹친다. 안 겹치면 경계에 걸친 바코드가
//                         양쪽에서 잘려 영영 안 읽힌다 (시리얼이 3열이라 확률이 높다).
//                       ⚠ 촬영 사진은 따로 올리지 않고 기존 [라벨 사진] 목록에 넣는다.
//                         별도 업로드하면 어느 입고 건인지 모르는 고아 파일이 된다 (v7.286 경로 재사용).
//                       ⚠ MODEL 후보는 serials·others 를 [둘 다] 뒤진다. 제품 형태가
//                         미설정이면 15자 MODEL 이 구조 검사를 통과해 serials 쪽에 들어간다.
//
// v7.369 (2026-08-13) — 🔢 입고 수량을 품목별로 표기 (출고와 같은 줄맞춤)
//                       사용자: "입고등록할때 개수표기를 제품별로 표기하도록 변경해줄래?
//                       출고할때처럼 제품명-개수 열을 맞춰서 표기되면 좋겠어"
//
//                       기존에는 합계 하나(×12)만 찍혔다. 품목이 둘 이상이면
//                       어느 제품이 몇 개인지 알려면 시리얼 칩을 눈으로 세야 했다.
//                       이제 모델명 박스와 한 줄씩 맞춰 ×8 / ×4 로 나온다.
//
//                       ⚠ 출고와 달리 [− +] 스테퍼를 달지 않는다. 입고 수량은 등록된
//                         시리얼과 묶여 있어서 숫자만 바꾸면 재고와 어긋난다.
//                         수량을 고치는 올바른 경로는 시리얼 추가/삭제다.
//                       ⚠ minHeight 26 + gap 6 은 모델명 박스와 [같은 값] 이어야 한다.
//                         한쪽만 바꾸면 줄이 어긋난다 (v7.63 에서 맞춰 놓은 값이다)
//                       ・ 시리얼 수 ≠ 수량이면 빨간 뱃지 + ⚠ 로 알린다.
//                         입고는 수량과 시리얼이 어긋나면 그대로 재고 오차가 된다.
//                         ⚠ 시리얼 0개일 때는 표시하지 않는다 — 아직 등록 전이거나
//                           시리얼 없는 품목일 수 있어 오탐이 된다
//                       ・ 합계는 작게 [합 12] 로 남긴다. 품목별로 쪼개면서 사라진 값이라
//                         그냥 없애면 전체 수량을 볼 곳이 없어진다
//                       ・ 단일 품목 입고는 기존 그대로 ×N (쪼갤 게 없다)
//
// v7.368 (2026-08-12) — 🚪 NAS 호환성 [다시 검색] 제거 → 검색창 X 하나로
//                       사용자: "다시검색버튼은 지워버리고 x버튼을 누르면 다시 전페이지로
//                       이동되도록 해줘"
//
//                       ⚠ 같은 일을 하는 출구가 셋이었다: [다시 검색] 버튼 · 검색창 X ·
//                         브라우저 뒤로가기(v7.362). 셋 중 X 만 동작이 달라서
//                         (검색어만 지우고 모델 선택은 유지) 더 헷갈렸다.
//                       ⚠ 모델을 고르면 검색창에 그 모델명이 채워진다. 그 상태에서 X 가
//                         글자만 지우면 검색창은 비었는데 아래엔 부품 목록이 남아
//                         무엇을 보고 있는 화면인지 알 수 없게 된다 — 이게 실제 증상이었다.
//                       ・ X 조건도 compatSearch → (compatSearch || compatModel) 로 넓혔다.
//                         모델이 선택돼 있으면 검색어가 비어도 나갈 구멍이 있어야 한다
//                       ・ title 도 상황에 따라 갈린다 ([이전 화면으로] / [검색어 지우기])
//                       ⚠ v7.362 뒤로가기 핸들러와 동작이 같아야 한다 (setCompatModel(null)
//                         + setCompatSearch('')). 두 경로가 어긋나면 X 로 나간 것과
//                         뒤로가기로 나간 것의 결과가 달라진다
//
// v7.367 (2026-08-12) — 🔍 M.2 SSD 카테고리 누락 발견 및 추가 (type=23)
//                       사용자 제보: "TBS-453DX / TBS-h574TX 는 M.2 가 SSD 로 분류되어서
//                       SSD 항목이 채워져 있어야 하는데 비어있어"
//
//                       원인: 수집 카테고리가 [1, 3, 33] 뿐이라 M.2 를 아예 안 물어봤다.
//                       QNAP 페이지 Network 탭 실측 → M.2 SSD 서브탭은 type=23.
//
//                       ⚠ 문서(QNAP-COMPAT.md §5)에 "empty 36건은 정상" 이라고 적어 둔 게
//                         틀렸다. TBS 계열은 정상이 아니라 [수집 범위 밖] 이었다.
//                         "부품이 없다" 와 "안 물어봤다" 를 같은 칸에 적으면 이렇게 된다 —
//                         empty 를 정상으로 결론 내리기 전에 원문을 한 번은 봐야 한다.
//
//                       ⚠ type=1 / type=3 은 멀쩡하다 (한때 의심했지만 실측으로 배제):
//                         ・ TS-1673AU-RP 서브탭은 3.5" HDD / 2.5" SSD 하나씩인데
//                           요청은 그대로 type=1 / type=3. 서브탭은 [그 모델이 지원하는 것] 만
//                           뜨는 것이지 type 이 폼팩터별로 쪼개진 게 아니다
//                         ・ category 1 은 24,139행 중 rank 24,139 · interface 24,139 만재
//
//                       ⚠⚠ type=23 은 응답 스키마가 다르다 — rank/firmware/type 대신 spec_1~4:
//                             spec_1 = 제품군    → rank
//                             spec_2 = 인터페이스 → interface
//                             spec_4 = 펌웨어    → firmware
//                         파서 v4 의 폴백이 없으면 행은 정상 적재되는데 사양만 전부 null 이 된다.
//                         에러가 안 나서 한참 뒤에야 발견되는 종류다.
//                         폴백은 단방향이다 (rank 가 있으면 rank 우선) → 기존 동작 무변화.
//
//                       ・ migrations/v7.367-m2-ssd-category.sql (신규 — 실행 필요)
//                       ・ n8n/n8n_parser_v4.js (신규 — 「파서 v3」 노드 내용 교체)
//                       ・ n8n 「모델×카테고리 전개」 CATEGORIES 에 23 추가 ← ⚠ 이걸 빼면
//                         SQL 만 돌려봐야 아무 일도 안 일어난다
//                       ・ 화면: 카테고리 탭에 [M.2 SSD] 추가 (SSD 바로 뒤 — 둘 다 SSD 라
//                         붙어 있어야 "2.5인치는 없는데 M.2 는 있네" 가 한눈에 보인다)
//                       ・ SSD 0건인데 M.2 가 있으면 빈 화면에서 그 탭으로 보내 주는 버튼.
//                         이 안내가 없으면 "SSD 0건" 만 보고 지원 안 한다고 답하게 된다 —
//                         이번 누락도 정확히 그렇게 발견됐다
//                       ・ qm2_id (꽂을 수 있는 QM2 확장카드 목록) 는 extra 에 보존된다.
//                         화면 노출은 RPC 수정이 필요해 이번엔 보류
//
// v7.366 (2026-08-12) — 🗑 사내 테스트 선택 삭제 + 수정은 톱니바퀴 하나로
//                       사용자: "삭제버튼도 우측상단으로 옮기고 버튼을 누르면 사용자가 추가한
//                       항목에한해서 삭제할 항목을 체크후 한번에 삭제할수있도록해줘.
//                       수정은 지금 있는 삭제버튼위치로 옮겨주고 톱니바퀴 아이콘만 남겨줘"
//
//                       ・ 카드 우측 상단 = [삭제] + [테스트 결과 추가].
//                         [삭제] 를 누르면 선택 모드로 바뀌고 버튼이 [취소] + [n건 삭제] 가 된다
//                       ⚠ 행마다 휴지통을 두지 않는 이유: 목록은 310px 카드 수십 개가 격자로
//                         깔려 있다. 스크롤하다 오클릭 한 번이면 남이 적어 둔 테스트 결과가
//                         확인 없이 사라진다. 고르고 → 개수 확인하고 → 한 번에 지운다
//                       ・ 선택은 사내 행에만. 공식 행은 opacity 0.4 로 죽여 눌러 보기 전에
//                         "여긴 못 고른다" 를 알 수 있게 했다 (클릭해도 아무 일도 안 일어난다)
//                       ⚠ 선택 모드에서는 행 클릭이 [복사] 가 아니라 [선택] 이다.
//                         같은 클릭이 모드에 따라 둘 다 하면 헷갈려서 복사를 아예 막았다
//                       ⚠ 뱃지(사내/사내검증)의 onClick 을 선택 모드에서 떼어 낸다.
//                         stopPropagation 이 남아 있으면 뱃지 위만 선택이 안 먹어 고장으로 보인다
//                       ・ 삭제는 InternalCompatService.removeMany() — .in('id',[...]) 한 방.
//                         하나씩 지우면 왕복도 늘고, 중간 실패 시 [절반만 지워진] 상태가 된다
//                       ⚠ 빈 배열이면 서비스에서 먼저 막는다. 필터 없는 DELETE 로 해석되면
//                         테이블이 통째로 날아간다
//                       ・ 수정은 행 하단 오른쪽(기존 삭제 자리)에 톱니바퀴 하나만.
//                         [수정][삭제] 두 개가 310px 폭에서 날짜·담당자 줄을 밀어내고 있었다
//                       ・ 선택 모드에는 안내 배너 + [전체 선택]/[선택 해제].
//                         모드가 켜진 걸 모르면 클릭이 왜 복사가 안 되는지 알 수 없다
//
// v7.365 (2026-08-12) — 🧪 사내 테스트를 [별도 탭] 에서 [같은 목록의 제조사 그룹] 으로
//                       사용자: "사내테스트 탭으로 따로 빼지말고 QNAP 회사명 적어놓은것처럼
//                       삼성등 선택한 제조사 카테고리로 출력하면 좋을것같아.
//                       테스트결과 추가 버튼은 우측상단으로 옮겨줘"
//
//                       v7.364 의 [QNAP 공식 | 사내 테스트] 소스 토글을 걷어냈다.
//                       이제 공식 목록과 같은 목록에 들어가고, 제조사 그룹이 하나 늘어난다
//                       (QNAP 17 · 삼성 2 · Transcend 3 …). 실제로 영업이 묻는 건
//                       "이 NAS 에 뭐 꽂히나" 지 "어느 출처냐" 가 아니다. 탭을 나누면
//                       삼성 메모리가 있는지 보려고 탭을 한 번 더 눌러야 했다.
//
//                       ⚠⚠ 합친 것은 [배치] 이지 [출처] 가 아니다. 두 가지를 반드시 남긴다:
//                         ① 행마다 [🧪 사내 호환/불가] 뱃지 (제조사 그룹 헤더에도 한 번)
//                         ② 하단 출처 문구의 두 번째 문장
//                            "🧪 표시 항목은 리버네트워크 자체 테스트 결과이며 QNAP 공식 목록이 아닙니다"
//                       이 둘을 지우면 사내 결과가 QNAP 보증인 것처럼 읽힌다.
//                       목록을 합친 대가로 반드시 치러야 하는 값이다 (QNAP-COMPAT.md §1).
//
//                       ・ [테스트 결과 추가] 는 카드 우측 상단 고정.
//                         목록 안에 두면 카테고리를 바꿀 때마다 위치가 흔들린다 —
//                         "어디 있었더라" 하고 찾게 되는 버튼은 결국 안 쓰인다
//                       ・ 공식 목록에 이미 있는 부품은 행을 새로 만들지 않고 기존 행에
//                         뱃지만 붙인다. 두 줄로 나오면 어느 쪽을 믿을지 모르게 된다.
//                         그 뱃지를 누르면 바로 수정으로 들어간다 (합치면서 사내 행이
//                         사라지므로, 안 그러면 수정할 방법이 없어진다)
//                       ・ 카테고리 탭 숫자 = 공식 + 사내 합산.
//                         탭은 17인데 목록은 19개면 숫자 자체를 안 믿게 된다
//                       ・ 확장 카드 탭은 사내 결과가 1건이라도 있을 때만 뜬다
//                         (QNAP 수집 대상이 아니라 공식 건수는 영원히 0이다)
//                       ・ 공식 조회가 unlinked/none 이어도 사내 결과가 있으면 그것만 보여준다.
//                         ⚠ v7.364 는 state!=='ok' 에서 곧바로 빈 화면을 반환했다.
//                           그대로 두면 확장 카드는 영영 화면에 못 나온다
//                       ⚠ is_recommended 를 사내 결과로 덮어쓰지 않는다.
//                         [QNAP 비권장] 과 [사내 불가] 는 근거가 다른 판정이다. 한 칸에 넣으면
//                         비권장 토글로 사내 불가까지 사라져 "왜 없어졌지" 가 된다
//                       ・ 사내 [불가] 행은 재고 글로우를 끄고 모델명에 취소선 —
//                         못 쓰는 부품을 초록으로 빛나게 할 이유가 없다
//
// v7.364 (2026-08-12) — 🧪 사내 호환성 테스트 결과 (삼성 · Transcend 등 직접 검증분)
//                       사용자: "호환성에 회사 내부에서 진행한 메모리 호환성 테스트 결과가 있는데
//                       이것도 반영하고싶어" — 결과 2단계(호환/불가), 전 직원 입력, 메모리·HDD·SSD
//                       + 확장 카드까지 담을 그릇.
//
//                       ⚠⚠ 공식 목록과 [같은 테이블에 섞지 않는다]. 이게 이번 설계의 핵심이다.
//                         qnap_compatibility 는 QNAP 이 보증하는 값이고, 사내 테스트는 우리가
//                         꽂아 본 결과다. 근거의 무게가 다르다. 한 배열에 합치는 순간
//                         화면 하단 "출처 · QNAP 공식 호환성 목록" 이 거짓이 되고,
//                         고객 안내 사고가 났을 때 책임이 통째로 우리에게 온다.
//                         → 테이블 분리(internal_compat_test) + 소스 전환 버튼 + 출처 문구 분기.
//
//                       ・ migrations/v7.364-internal-compat.sql (신규 — 실행 필요)
//                         product_name 을 키로 잡았다. model_id(QNAP) 가 아니다:
//                           - QNAP 과 연결 안 된(unlinked) 모델에도 사내 테스트는 존재한다
//                           - 확장 카드는 QNAP 수집 대상이 아니라 model_id 로는 닿지 않는다
//                           - qnap_product_link 도 같은 키를 쓴다 (체계 일치)
//                         model_norm 은 생성 컬럼(공백·하이픈 제거 + 대문자)이다.
//                         ⚠ 앱의 qnapNorm() 과 규칙이 반드시 같아야 한다. 어긋나면
//                           [사내검증] 뱃지가 에러 없이 조용히 안 붙는다 (제일 찾기 어려운 종류)
//                         (product_name, category_type, model_norm) 유니크 —
//                         재테스트는 새 행이 아니라 수정이다. 행이 쌓이면 최신이 뭔지 알 수 없다
//
//                       ・ services/InternalCompatService.js (신규)
//                         upsert 를 안 쓴다. 중복 시 [조용한 덮어쓰기] 보다 [안내] 가 맞다 —
//                         남이 테스트해 둔 결과를 모르고 지우는 게 최악이다. 23505 를 잡아
//                         "기존 항목을 수정해 주세요" 로 돌려준다. 42P01(테이블 없음) 도
//                         마이그레이션 미실행이라고 그대로 말해 준다 (원인 모를 저장 실패 방지)
//
//                       ・ 화면: 카테고리 탭 위에 [QNAP 공식 | 사내 테스트] 소스 전환.
//                         - 확장 카드 탭은 사내 소스에서만 뜬다 (공식은 항상 0건이라 헛클릭만 는다)
//                         - 공식 목록 행에도 사내 결과가 있으면 [🧪 사내 호환/불가] 뱃지를 붙인다.
//                           소스로만 나누면 "공식에도 있고 우리도 해봤다" 를 알 수 없어
//                           같은 부품을 두 번 테스트하게 된다
//                         - 재고 대조·초록 글로우(v7.360/363)는 사내 목록에도 그대로 붙는다.
//                           단 [불가] 판정 행은 글로우를 끈다 — 못 쓰는 부품을 강조할 이유가 없다
//                         - 하단 출처 문구가 소스에 따라 갈린다. 사내 결과에 [QNAP 공식] 이라고
//                           적히면 그 순간 QNAP 보증을 사칭하는 셈이다
//
//                       ・ 입력: 전 직원. 필수는 제조사·모델명·결과 셋뿐이다.
//                         칸이 많다고 다 채우라고 막으면 아무도 안 쓴다.
//                         제조사는 select 가 아니라 datalist — 새 제조사가 나왔을 때
//                         입력 자체가 막히면 안 된다
//                       ・ 결과는 2단계 고정(호환/불가). '조건부' 를 넣지 않은 건 판정 기준이
//                         사람마다 갈려 결국 아무도 안 믿는 값이 되기 때문이다. 단서는 비고에 적는다
//                       ・ 부품 역방향 검색(v7.361)도 사내 테이블을 같이 뒤진다.
//                         공식에 없는 부품이 검색도 안 되면 적어 둔 의미가 없다
//                       ⚠ 다크 대비: 흰 글씨를 얹는 배경은 --purple/--danger 가 아니라
//                         -solid 를 쓴다. 다크의 --purple(#c58ade) 위 흰 글씨는 2:1 대다
//
// v7.363 (2026-08-12) — ✨ 재고 보유 부품에 초록 글로우 (배송시트지와 같은 방식)
//                       배송시트지 출고요청 행에 쓰는 BorderGlow 를 색만 초록으로 가져왔다.
//                       glow-row + glow-mark + glow-run — 같은 클래스, 같은 훅(useRowGlow).
//
//                       ⚠ 옵션은 그대로 못 쓴다. 대상 크기가 다르다:
//                           배송시트지 행   1240 × 60  (20.7:1)
//                           호환 부품 카드   310 × 55  ( 5.6:1)
//                         ・ glowSize 310 → 150
//                           카드 폭이 310px 인데 스포트라이트가 310px 이면 카드를 통째로 덮어
//                           [빛이 도는] 게 아니라 그냥 균일하게 밝아진다. 절반이어야 이동이 보인다
//                         ・ rowRadius 6 → 10 — 카드 모서리와 맞춰야 링이 뜨지 않는다
//                         ・ runDuration 4.5 → 3.2 — 둘레가 짧아 같은 속도면 느리게 느껴진다
//                         ・ runBand 22 → 30 — 짧은 둘레에서는 띠가 길어야 눈에 띈다
//                       ⚠ 라이트/다크 색을 나눴다. 흰 배경에 밝은 형광 초록을 올리면 묻힌다
//                         (v7.322 에서 배송시트지가 겪은 것과 같은 문제)
//                       ⚠ glow-mark 없이 glow-run 만 켜면 빛이 어디를 도는지 알 수 없다.
//                         상시 테두리가 궤도 역할을 한다
//                       ⚠ 실선 초록 테두리(v7.360)는 걷어냈다 — 도는 빛과 겹쳐 서로를 가린다.
//                         테두리는 transparent 로 두고 자리만 유지한다 (레이아웃 밀림 방지)
//
//                       ⚠⚠ ::after 충돌: v7.359 의 [복사됨] 표시가 .qc-row::after 였는데
//                         BorderGlow 의 .glow-row::after(커서 스포트라이트)와 같은 자리다.
//                         → 실제 요소(<span class="qc-copied">)로 바꿔 충돌을 없앴다.
//                         ⚠ 가상요소를 쓰는 컴포넌트를 얹을 땐 기존 ::before/::after 를 먼저 확인할 것
//
// v7.362 (2026-08-12) — ⬅ PC 브라우저 뒤로가기가 사이트를 벗어나던 문제 (NAS 호환성)
//                       사용자: "검색 후 뒤로가기를 누르면 첫 페이지로 갔으면 좋겠다.
//                              지금은 페이지 자체를 벗어난다."
//
//                       ⚠ 원인: 하드웨어 back 처리(v7.158~166)가 전부
//                         `if (!isMobile) return;` 로 막혀 있다. PC 에는 히스토리 관리가
//                         아예 없어서 뒤로가기가 곧장 사이트를 벗어난다.
//                         모바일 쪽은 오래 다듬어진 코드라 건드리지 않고 따로 얹었다.
//
//                       동작: [첫 화면이 아닌 상태]로 처음 들어갈 때 히스토리를 한 칸 밀고,
//                            뒤로가기가 오면 첫 화면(검색어 비움 + 선택 해제)으로 되돌린다.
//
//                       ⚠ 앱 전체에 history 를 깔지 않았다. 그러면 탭 이동이 전부 쌓여
//                         사이트를 나가려고 뒤로가기를 수십 번 눌러야 한다.
//                       ⚠ 검색 글자마다 밀면 안 된다. 'TS-453E' 를 치면 7칸이 쌓여
//                         뒤로가기를 7번 눌러야 한다 → [첫 화면 아님]으로 바뀌는 순간 딱 한 번만.
//                         검증: 7글자 입력 후에도 히스토리 1칸
//                       ⚠ 뒤로가기로 돌아온 직후 다시 밀면 무한 루프 → compatPopRef 로 구분
//                       ⚠ 사용자가 직접 [다시 검색]·[지우기]로 첫 화면에 온 경우엔
//                         플래그를 풀어 다음에 또 밀 수 있게 한다
//                       ・ 다른 탭에서 눌린 뒤로가기는 건드리지 않는다. 우리가 민 칸이
//                         남아 있으면 한 번 더 눌러야 하지만, 사이트를 벗어나는 것보다 낫다
//                       ⚠ 첫 화면에서의 뒤로가기는 그대로 사이트를 벗어난다 (정상 동작)
//
// v7.361 (2026-08-12) — 🔄 부품명으로 NAS 찾기 (역방향) + 창고 표기 한글화
//                       검색창에 메모리/HDD 모델을 넣으면 그게 들어가는 NAS 폴더만 남는다.
//
//                       ⚠ migrations 02 의 fn_qnap_compat_by_drive() 를 그대로 못 썼다:
//                         ・ 완전 일치만 된다 (upper(model_base)=key). 부분 검색 불가
//                         ・ QNAP 모델명을 돌려준다. 우리는 카탈로그 product_name 이 필요하다
//                         새 RPC 는 SQL 마이그레이션이 또 필요해서 기존 테이블 2회 조회로 처리.
//
//                       ① 모드 전환 스위치를 두지 않았다.
//                          'TS-464' 와 'WD140EFGX' 는 겹칠 일이 없다 — 사용자가 무엇을 치는지
//                          스스로 아는데 앱이 되묻는 건 군더더기다.
//                          ⚠ NAS 이름이 하나라도 맞으면 서버에 묻지 않는다. 평소엔 쿼리 0회.
//                            이름에 하나도 안 맞을 때만(3글자 이상) 부품 목록을 뒤진다
//                          ⚠ 디바운스 280ms — 로컬 필터와 달리 진짜 서버 왕복이다
//
//                       ② ⚠ 사용자 입력을 .or() 필터에 그대로 넣으면 안 된다.
//                          PostgREST 는 쉼표·괄호·마침표를 문법으로 쓰고 %/_ 는 LIKE 와일드카드다.
//                          실제 파트넘버는 영숫자·하이픈·별표라 그 밖은 털어낸다
//
//                       ③ 부품으로 걸렸을 땐 [이름 일치] 로 거르면 안 된다 —
//                          'WD140EFGX' 는 NAS 이름 어디에도 없다. productNames 집합으로 건다
//                       ④ 왜 폴더가 바뀌었는지 배너로 알린다.
//                          ⚠ 안 알리면 "왜 TS-464 만 나오지?" 하고 혼란스럽다.
//                          걸린 부품(최대 8종)과 용량을 같이 보여준다
//                       ⑤ excluded 링크는 제외 (수리 자산 등)
//
//                       ⚠ 창고 표기: inventory.warehouse 는 'hq'/'paju' 코드다.
//                         v7.360 이 그대로 노출해 [보유 hq 3] 으로 나왔다.
//                         다른 화면은 전부 한글이라 warehouseLabel() 로 맞췄다 (본사/파주)
//
// v7.360 (2026-08-12) — 📦 호환 부품에 [우리 재고] 표시
//                       "이 NAS 에 뭐가 되나" 옆에 "그 중 우리가 뭘 갖고 있나" 를 붙였다.
//
//                       ⚠ 조인 키가 까다로웠다. QNAP 은 'ST24000NT002' 같은 제조사
//                         파트넘버를 쓰는데 inventory 에는 컬럼이 둘이다:
//                           product_name — 카탈로그 SKU. QNAP 표기와 다르다
//                           model_no     — 시리얼 단위 수기 모델번호. ★ QNAP 과 같은 체계
//                         둘 다 색인에 넣는다 (model_no 가 빈 오래된 행이 있다).
//                         ⚠ 같은 행을 두 번 세지 않도록, 정규화 후 두 값이 같으면 한 번만 센다
//
//                       ⚠ 정규화가 핵심이다. QNAP-COMPAT.md §3 의 지뢰가 그대로 적용된다:
//                         'WD142KRYZ\u00a0\u00a0\u00a0\u00a0'  ← nbsp 꼬리
//                         'ST1000VN009\u200b-3JF10C'          ← zero-width space
//                         일반 trim() 으로 안 지워진다. 안 걸러내면 조인이 [조용히] 실패한다.
//                         여기에 더해 대소문자·하이픈·공백도 무시한다 — model_no 는 수기 입력이라
//                         'st24000nt002' / 'ST-24000NT002' 가 다 같은 물건이다.
//                         → qnapNorm() 을 QnapCompatService 에 두고 양쪽에서 쓴다. 6개 사례로 검증
//
//                       ⚠ 재고는 어느 NAS 를 보든 같다 → 탭 진입 시 1회만 받는다.
//                         카테고리를 바꿀 때마다 다시 받으면 의미 없는 왕복이 늘어난다.
//                       ⚠ status='in_stock' 만 받는다. 출고/불용까지 세면 "있다"고 해놓고
//                         실제로는 없는 상황이 된다
//
//                       ① 행 앞에 초록 재고 뱃지 (개수) + 초록 테두리
//                          ⚠ 배경까지 칠하면 비권장(회색)과 겹쳐 읽기 어려워진다 → 테두리만
//                       ② 2행에 창고별 내역 (예: 보유 본사 3, 파주 1) — 어디 있는지 알아야 꺼낸다
//                       ③ 요약줄에 [보유 N종 M개]
//                       ④ [재고만] 필터 토글
//                       ⑤ 브랜드 안에서 재고 보유 항목을 앞으로, 재고 있는 브랜드도 앞으로
//                          ⚠ 가진 것이 200건 중간에 파묻히면 붙인 의미가 없다
//                       ・ model_alt(괄호 파트넘버)로도 찾는다 — 재고에 그쪽으로 적혀 있을 수 있다
//
// v7.359 (2026-08-12) — 🔧 호환 부품 출력 보완 — 목록 안 필터 · 다단 · 탭 건수
//                       n8n 수집으로 실데이터(33,449행)가 들어오면서 출력 문제가 드러났다.
//                       HDD 는 한 모델에 200건이 넘어 [제조사별 나열]만으로는 못 읽는다.
//                       ⚠ QNAP-COMPAT.md 의 금지 사항은 그대로 지켰다:
//                         출처 푸터 유지 · 빈 화면 4분류 유지 · state 구분 유지
//
//                       ① 카테고리 탭에 건수 뱃지 (메모리 12 · HDD 245 · SSD 66)
//                          ⚠ 눌러 봐야 데이터 유무를 아는 건 나쁜 설계다. 0건 탭은 흐리게.
//                            단 감추지는 않는다 — 0건인 것도 정보다 (메모리 온보드 모델 등)
//                          ・ QnapCompatService.countsByCategory() 신설.
//                            head:true + count:'exact' 라 행을 하나도 안 받는다.
//                            ⚠ RPC 를 새로 만들면 SQL 마이그레이션이 또 필요해 피했다
//                          ⚠ 카테고리가 바뀌어도 건수는 그대로다 → 의존성은 modelId 만.
//                            compatData 를 통째로 넣으면 탭 누를 때마다 3회 왕복이 다시 돈다
//
//                       ② 목록 안 검색 + 용량 칩 + 비권장 숨기기
//                          ⚠ 용량 칩은 [전체 행] 기준으로 만든다. 걸러진 결과로 만들면
//                            하나 고르는 순간 다른 칩이 사라져 바꿔 고를 수가 없다
//                          ⚠ 모델·카테고리가 바뀌면 필터를 초기화한다. 안 하면 HDD 에서
//                            '14TB' 로 걸러 둔 채 메모리 탭에 가서 0건을 보고
//                            데이터가 없는 줄 안다
//                          ・ 검색은 하이픈·공백 무시 (model_alt 도 같이 본다)
//
//                       ③ 요약 줄 — N종 · 제조사 수 · <최대 용량>
//                          최대 용량은 영업에서 제일 먼저 묻는 값이다
//
//                       ④ 부품 행을 반응형 다단으로 (minmax 310px)
//                          한 줄에 하나씩 깔면 폭이 남아돌았다. 같은 높이에 3배쯤 더 보인다
//                          ・ 부가 정보는 2행으로 내리되, 없으면 아예 렌더하지 않는다
//                            (빈 줄이 생기면 카드 높이가 들쭉날쭉해진다)
//
//                       ⑤ 행 클릭 = 모델명 복사 (견적서에 옮겨 적는 일이 잦다)
//                          ⚠ 이 앱엔 토스트가 없다. 새로 만들기보다 누른 카드에 직접
//                            [복사됨]을 띄운다 — 가볍고 어느 걸 복사했는지도 분명하다
//                          ⚠ navigator.clipboard 는 https/localhost 에서만 동작한다
//                       ⚠ --success-bg 는 :root 에 없다 (--success / --success-solid 뿐).
//                         없는 변수를 쓰면 배경이 통째로 사라진다 → rgba 리터럴 사용.
//                         이번에 쓴 CSS 변수 8개는 전수 검사로 정의 여부를 확인했다
//
// v7.358 (2026-08-12) — 🔌 NAS 호환성 실데이터 연결 (QNAP 공식 목록)
//                       n8n [QNAP 호환성 동기화] 가 매주 화 03:00 수집한 사본을 읽는다.
//                       67개 모델 × HDD/SSD/메모리 = 33,449행.
//
//                       ① services/QnapCompatService.js 신설
//                       ② RPC fn_qnap_compat_for_product(제품명, 카테고리) 1회 왕복
//                       ③ 카테고리 탭 (메모리 기본 · HDD · SSD) + 제조사별 그룹
//                       ④ ⚠ [행 0개]의 원인을 구분해서 안내한다.
//                          none=목록 자체가 없음(예: TS-233 메모리 온보드) /
//                          not_synced=아직 수집 안 함 / unlinked=QNAP 모델 미연결 /
//                          sync_error=수집 실패. 넷을 뭉뚱그리면 직원이 헛수고한다.
//                       ⑤ ⚠ 하단 출처·수집시각·원문링크 표기를 지우지 말 것.
//                          QNAP 은 정확성을 보증하지 않는다(이용약관 4조). 오래된 사본으로
//                          고객에게 안내하면 책임은 리버네트워크로 온다.
//
// v7.357 (2026-08-12) — 🔎 NAS 호환성 검색 = 폴더 격자 실시간 필터
//                       ⚠ 검색창이 둘이었다 — 상단 드롭다운 + 격자 안 [목록에서 좁히기].
//                         어느 쪽에 쳐야 할지 헷갈리고, 드롭다운에서 고르면 격자가 통째로
//                         사라지고 결과 화면으로 튀어서 [폴더가 좁혀지는 걸] 볼 수 없었다.
//                       → 하나로 합쳤다. 타이핑하면 아래 폴더가 실시간으로 걸러진다.
//
//                       ① 상단 자동완성 드롭다운 제거 · 격자 안 필터 입력 제거
//                       ② 서버 검색 useEffect 제거 (v7.349~356 은 글자마다 쿼리를 날렸다)
//                          ⚠ 탭 진입 시 NAS 목록을 통째로 받아 두므로(compatAll) 서버에
//                            다시 물을 이유가 없다. 쿼리 0회 = 지연 0. 문자열 비교로 끝난다.
//                          → compatResults / compatOpen / compatLoading / compatGridFilter
//                            네 개 상태가 전부 불필요해져 제거
//                       ③ 하이픈·공백 무시 비교
//                          ⚠ 'ts453' 로도 'TS-453E-8G' 가 잡혀야 한다. 사용자가 모델명
//                            하이픈 위치를 정확히 기억할 이유가 없다.
//                            검증: ts453 / TS-453 / ts 453 셋 다 같은 결과
//                       ④ 시리즈 칩 숫자가 [검색 결과 기준] 으로 바뀐다
//                          ⚠ 전체 개수를 그대로 두면 'TS 63' 인데 눌러보니 2개인 상황이 된다
//                          ・ 0건 시리즈는 감추지 않고 흐리게만 (opacity .38).
//                            ⚠ 감추면 칩 줄이 검색할 때마다 들썩여 누르려던 칩이 사라진다
//                       ⑤ 0건일 때, 시리즈를 좁혀 둔 탓이면 [전체 시리즈에서 N건 찾기] 버튼
//                       ⑥ 격자 헤더에 현재 검색어 칩 — 눌러서 해제
//
// v7.356 (2026-08-12) — 🧊 폴더 뒤 색판이 정신없던 문제 — 호버한 칸만 살아나게
//                       ⚠ v7.355 는 GlassIcons 원본대로 색판을 항상 15deg 기울여 보여줬다.
//                         아이콘 6~8개를 크게 보여주는 원본 용도에서는 멋있지만,
//                         82칸에서는 주황 판 82개가 제각각 삐져나와 어느 게 어느 폴더 것인지
//                         구분이 안 됐다. 밀도가 다르면 같은 장식도 소음이 된다.
//                       ① 색판: 평소 opacity 0 (+ blur 2px) → 호버 시에만 0.38 로 비친다.
//                          기울기도 15deg → 9deg 로 줄였다
//                       ② 유리판: 평소 완전 투명 → 호버 시에만 유리처럼 뜬다 (배경·테두리·그림자)
//                          ⚠ backdrop-filter 도 호버 때만 건다. 82개에 상시로 걸면 무겁다
//                       ③ 결과: 평소엔 폴더만 깔끔하게 늘어서고, 가리킨 칸만 살아난다
//                       ・ 부제(사양)는 opacity 로만 숨기므로 자리는 늘 차지한다 —
//                         호버할 때 카드 높이가 튀지 않는다
//
// v7.355 (2026-08-12) — 🐛🔷 폴더가 새까맣게 나오던 문제 + GlassIcons 로 재구현
//
//                       ⚠⚠ v7.354 버그 원인: NasFolder.jsx 에 CSS import 를 빼먹었다.
//                          import './NasFolder.css' 한 줄이 없어서
//                            ・ fill 이 기본값(검정)이 되어 폴더가 새까맣게 나오고
//                            ・ .nasfd__fold 의 grid-template-rows:0fr 이 안 걸려
//                              숨어 있어야 할 사양이 전부 펼쳐져 이름 뒤에 줄줄이 붙었다
//                          FlowingMenu.jsx · Particles.jsx 는 다 갖고 있는데 이것만 빠졌다.
//                          → 앞으로 CSS 를 동반하는 컴포넌트를 만들면 import 부터 확인할 것.
//                            (babel 구문검사·eslint 로는 절대 안 잡힌다 — 파일이 없어도,
//                             import 를 안 해도 문법은 멀쩡하다)
//
//                       ① 사용자 요청대로 reactbits GlassIcons 구조로 재구현
//                          src/GlassIcons.jsx + GlassIcons.css 신설 (NasFolder 는 삭제)
//                          ・ 원본 구조 유지: icon-btn__back(색 판) / __front(유리판) /
//                            __icon(내용) / __label(이름표)
//                          ・ 원본에 없던 것 추가: onClick(원본은 핸들러가 없었다),
//                            subtitle(사양 요약 한 줄), title(전체 이름 툴팁)
//                          ・ GlassIcons.css 는 받지 못해 새로 썼다. 클래스명은 원본과 동일
//
//                       ② "아이콘 대신 이미지를 넣을 수 있나" → 된다.
//                          {item.icon} 은 JSX 노드를 그대로 렌더하는 자리라 <img> 든 <svg> 든 들어간다.
//                          ⚠ 다만 이미지 파일은 이 프로젝트에서 쓰기 나쁘다 —
//                            public/ 이 NAS 에만 있어 파일을 여기서 못 넣고(매번 직접 올려야 한다),
//                            PNG 는 다크 모드에서 색이 안 따라온다.
//                          → 폴더는 SVG(FolderGlyph)로 그려 icon 자리에 넣었다
//
//                       ③ 폴더 글리프
//                          ⚠ 앞판 회전축은 왼쪽 아래(8,118). 중앙을 축으로 돌리면 앞판이
//                            위로 올라가 안의 종이를 덮어버린다
//                          ・ 종이는 --i 로 장마다 30ms 늦게, 7px 더 솟는다
//                          ・ 다크에서는 흰 종이가 눈부셔 톤을 낮춘다
//
//                       ④ 사양은 뱃지 여러 개 → 이름 아래 한 줄 요약으로 바꿨다
//                          (v7.354 는 뱃지로 펴서 카드가 세로로 길어졌다)
//                       ⑤ .nasgrid 제거 — 격자는 GlassIcons.css 의 .icon-btns 가 담당
//
// v7.354 (2026-08-12) — 🗂 NAS 호환성 — 모델 카드를 윈도우 폴더 모양으로 (NasFolder 신설)
//                       마우스를 올리면 앞판이 기울며 열리고 안의 종이가 솟아오른다.
//
//                       ⚠ GlassIcons(사용자가 준 reactbits)는 쓰지 않았다.
//                         item.icon 자리에 <img> 를 넣는 것 자체는 된다 — JSX 노드면 무엇이든
//                         들어가는 자리다. 하지만 그 컴포넌트의 핵심은 뒤에 깔리는 색 그라디언트
//                         판 + 그 위 유리판이지 폴더 [모양]이 아니다. 폴더처럼 보이려면 어차피
//                         폴더 그림을 따로 그려야 하고, 그러면 유리 효과가 오히려 방해가 된다.
//                         (GlassIcons.css 도 없어서 어차피 새로 써야 했다)
//
//                       ⚠ 이미지 파일(PNG) 대신 SVG 로 그렸다:
//                         ・ public/ 은 NAS 에만 있어 여기서 파일을 못 넣는다 (매번 직접 올려야 한다)
//                         ・ PNG 는 다크 모드에서 색이 안 따라온다
//                         ・ 뚜껑 열림을 PNG 로 하려면 프레임을 여러 장 만들어야 한다
//
//                       ① src/NasFolder.jsx + NasFolder.css 신설
//                          ⚠ 앞판 회전축은 왼쪽 아래(8,124)다. 중앙을 축으로 돌리면 앞판이
//                            위로 올라가 안의 종이를 덮어버린다 (시제품에서 실제로 그랬다)
//                          ・ 종이는 --i 로 장마다 30ms 씩 늦게, 8px 씩 더 솟는다
//                          ・ 종이 장수 = 사양 개수-1 (1~3장). 폴더가 [차 있는] 느낌
//                          ・ 다크에서는 흰 종이가 눈부셔 톤을 낮춘다
//                          ・ 이름은 2줄에서 자른다 (…). TS-133 부터 TS-H1277AXU-RP-R7-32G 까지
//                            길이가 제각각이라 안 자르면 카드 높이가 들쭉날쭉해진다.
//                            전체 이름은 title 속성으로 볼 수 있다
//                       ② 격자 minmax 224px → 126px (폴더가 작다), gap 8 → 4
//                          ⚠ overflow 금지 — 열린 폴더와 솟은 종이가 칸 밖으로 나간다
//                          ⚠ align-items:start 필수 — 없으면 한 폴더가 열릴 때 같은 줄이 같이 늘어난다
//                       ③ v7.353 의 .nasgrid-item 계열 CSS 는 전부 삭제 (폴더로 대체)
//
// v7.353 (2026-08-12) — 📂 NAS 호환성 — 도크 제거 → 시리즈 칩 · 모델 카드는 펼쳐지는 폴더로
//                       요청 2건.
//
//                       ① [시리즈로 찾기] 도크 → 필터 칩 줄
//                          src/Dock.jsx · Dock.css 삭제 (다른 데서 안 쓴다).
//                          ・ [전체] 칩 추가. 도크였을 땐 골라야 목록이 나왔지만 필터가 된 이상
//                            기본값은 전체 보기여야 한다 → 들어오자마자 82종이 보인다
//                          ・ 격자 안 [닫기] 버튼 제거 (칩으로 해제한다), 검색창은 상단에 유지
//
//                       ② 모델 카드를 맥 폴더처럼 — 마우스를 올리면 아래로 펼쳐지며 내용을 보여준다
//                          ⚠ height:auto 로는 transition 이 안 걸린다.
//                            → grid-template-rows 0fr→1fr 기법.
//                              max-height 로 하면 값이 실제 높이보다 커서 열림이 늦게
//                              시작한 것처럼 보인다
//                          ⚠ .nasgrid 에 align-items:start 필수. 없으면 한 칸이 펼쳐질 때
//                            같은 줄 카드가 전부 같이 늘어난다
//                          ⚠ 격자 컨테이너의 overflow/maxHeight 를 걷어냈다 — 펼쳐진 부분이 잘린다.
//                            63개 × 6열 = 11줄이라 페이지 스크롤로 충분하다
//                          ・ 내용물은 순차로 떠오른다 (45ms 간격)
//                          ・ :focus-within 도 같이 걸어 키보드로도 펼쳐진다
//
//                       ③ 펼칠 내용이 없어서 모델명에서 사양을 뽑았다 (베이·기본 메모리·폼팩터·이중전원)
//                          ⚠ 카탈로그에 스펙 컬럼이 없다. 이름이 유일한 단서다.
//                            실제 모델명 29개로 검증: TS-h1887XU → 18베이, TS-932PX → 9베이 맞음
//                          ⚠ 랙마운트는 모델 토큰이 U 로 끝나는지로 본다.
//                            'RP'(이중전원)만 보면 TS-431XEU 같은 1U 모델을 놓친다
//                          ⚠ 어디까지나 이름 규칙 추정이다. 호환성 데이터가 붙으면 그쪽이 우선
//
//                       ⚠ Icon 이 className 을 안 받고 있었다 → 받게 고쳤다 (v7.352 에서).
//                         인라인 style 로는 :hover 를 표현할 수 없어 CSS 클래스가 필요하다
//
// v7.352 (2026-08-12) — ✨ NAS 호환성 제품명 격자에 호버 반응 추가
//                       "시리즈로 찾기의 도크를 제품명에도 쓸 수 있나" → 쓰면 안 된다.
//                       ⚠ 도크는 가로 한 줄이다. TS 시리즈만 63종 × 50px = 3,150px 로
//                         화면을 넘고, 확대 효과는 스크롤해야 보이는 항목엔 의미가 없다.
//                         무엇보다 도크는 아이콘 칸이라 'TS-1283XU-RP-E2124-8G' 가 안 들어간다.
//                         호버해야 이름이 뜨는데 그러면 63개를 하나씩 훑어야 한다.
//                         시리즈는 7개라 도크가 맞았고, 제품명은 63개라 격자가 맞다.
//                       → 격자 구조는 그대로 두고 칸 자체에 반응을 넣었다:
//                         ① 호버 시 살짝 떠오르며 확대 (translateY -3px + scale 1.03) + 그림자
//                         ② 마우스를 따라다니는 방사형 하이라이트
//                            ・ --mx/--my 를 onMouseMove 가 꽂는다.
//                              ⚠ 상태로 관리하면 칸마다 리렌더가 돈다 → style 프로퍼티 직접 조작
//                            ・ ::before + z-index:-1 + isolation:isolate 로 글자 뒤에 깐다
//                              (요소 자신의 배경 → 음수 z-index 자식 → 내용 순으로 그려진다)
//                         ③ 아이콘이 살짝 커지며 기울고, 오른쪽에서 화살표가 밀려 들어온다
//                         ④ 순차 등장 (22ms 간격)
//                            ⚠ 개수만큼 늘리면 뒤쪽 칸이 몇 초 뒤에 뜬다 → 20칸에서 끊는다
//                       ⚠ Icon 컴포넌트가 className 을 안 받고 있었다 → 받게 고쳤다.
//                         안 받으면 CSS 로 아이콘에 애니메이션을 걸 방법이 없다
//                         (인라인 style 로는 :hover 상태를 표현할 수 없다).
//                         기존 호출 60여 곳은 className 을 안 넘기므로 영향 없다
//                       ⚠ prefers-reduced-motion 에서 움직임만 끄고 색·테두리 변화는 남긴다
//                         — 어느 칸 위에 있는지 알려주는 건 장식이 아니라 기능이다 (v7.320 교훈)
//
// v7.351 (2026-08-12) — 🗄 NAS 호환성 — 시리즈 폴더 도크 (Dock 컴포넌트 신설)
//                       사용자가 준 Dock(macOS 도크풍 확대 메뉴) 을 NAS 목록에 적용.
//
//                       ⚠ 원본은 motion/react 를 쓰는데 이 프로젝트엔 없다.
//                         package.json 은 NAS 에만 있어 여기서 못 건드린다 → 새 의존성을 넣으면
//                         사용자가 직접 추가해야 하고 빼먹으면 빌드가 깨진다 (ogl 때 겪었다).
//                         → src/Dock.jsx 를 의존성 없이 구현했다. prop 이름은 원본 API 와 동일해
//                           나중에 motion 을 깔면 그대로 바꿔 끼울 수 있다.
//                         ・ 스프링 대신 0.12s CSS transition. 마우스를 따라가는 동안엔
//                           mousemove 가 초당 수십 번 오므로 길면 오히려 끈적하게 밀린다
//                         ・ 색은 전부 테마 변수 — 원본은 다크 전용 하드코딩이었다
//
//                       ⚠ 도크는 항목 4~8개용 UI 다. NAS 모델은 수십~수백 종이라
//                         한 줄에 늘어놓으면 못 쓴다 → 도크에는 [시리즈 폴더]만 놓고
//                         모델은 아래 격자에서 고른다. 시리즈가 8개를 넘으면 나머지는 [기타] 로 합친다
//
//                       ① nasSeries(): 첫 하이픈 앞 영문만 본다 (TS / TVS / TBS / QGD …)
//                          ⚠ 'h1887XU' 같은 중간 토큰까지 보면 시리즈가 수십 개로 쪼개진다
//                       ② 시리즈 격자에 자체 필터 — TS 처럼 모델이 많은 시리즈에 필요하다
//                       ③ 목록 적재는 category='NAS' 기준. 탭 진입 시 1회.
//                          ⚠ 검색(searchByName)은 category NULL 도 남기지만 폴더 브라우징은
//                            목록을 통째로 받는 것이라 기준이 확실해야 한다. 두 경로를 일부러
//                            다르게 뒀다 — 카테고리 비어 있는 NAS 는 검색으로 찾으면 된다
//
// v7.350 (2026-08-12) — 🐛 NAS 호환성 검색 — 지연 제거 + 드롭다운이 가려지던 문제
//                       사용자 지적 2건. 둘 다 v7.349 가 출고([출고할 제품])와 다르게 만든 탓이다.
//
//                       ① [검색 중] 지연
//                          출고는 onChange 에서 곧바로 쿼리를 날린다. 디바운스가 없다.
//                          v7.349 는 요청 수를 아끼려고 220ms 를 넣었는데 결과적으로 느려졌다.
//                          → 디바운스 제거. 타이핑 1글자 = 쿼리 1회는 이 규모에서 충분히 싸다.
//                            체감 지연이 절약한 요청 수보다 비싸다
//                          ・ alive 가드는 남긴다 — 디바운스와 무관한 문제다.
//                            "TS" 응답이 "TS-4" 응답보다 늦게 오면 옛 결과가 최신을 덮는다
//                          ・ 2글자 제한도 뺐다. "t" 한 글자로도 검색된다
//                          ・ 로딩 중에 목록을 치우면 글자마다 깜빡인다 → 이전 결과를 남기고
//                            opacity 0.55 로만. [검색 중] 문구는 첫 검색일 때만
//
//                       ② 드롭다운이 아래 카드에 가려짐
//                          ⚠ Card 는 backdrop-filter:blur(10px) 을 쓴다. 이게 스택 컨텍스트를
//                            만든다 → 드롭다운의 zIndex:1000 은 그 카드 안에서만 유효하고,
//                            뒤에 오는 카드들이 DOM 순서대로 그 위에 그려진다.
//                          → 검색 카드에 position:relative + zIndex:10.
//                            출고 [출고할 제품] 카드도 같은 이유로 이미 zIndex:10 을 갖고 있었다.
//                            v7.349 가 그걸 빠뜨린 것이다
//                          ・ 겸사겸사 positionDropdown ref 도 붙였다 (아래 공간 부족하면 위로 열림)
//
// v7.349 (2026-08-12) — 🧩 [NAS 호환성] 탭 신설 — UI 골격 + 제품 검색
//                       NAS ↔ 메모리 호환성 조회. 이번 단계는 UI 와 검색까지.
//                       호환성 데이터(어느 NAS 에 어느 메모리가 맞는지)는 아직 없다.
//                       ① 사이드바 { id:'compat', label:'NAS 호환성' } (넥시 조회 아래)
//                       ② 검색창 + 자동완성 — [출고할 제품] 과 같은 형태로 맞췄다
//                          ⚠ 출고 쪽은 InventoryService.searchInStockProducts 로 [재고가 있는] 것만
//                            찾는다. 호환성은 재고와 무관하다 — 재고 0 인 단종 모델도 조회돼야 한다.
//                            → ProductService.searchByName (products 카탈로그) 을 쓴다
//                          ⚠ 카테고리가 NULL 인 카탈로그 제품이 많다 (v7.0 이후 category 미유지).
//                            NAS 필터를 엄격히 걸면 검색이 텅 빈다 → NULL 은 남긴다
//                       ③ 디바운스 220ms + alive 가드
//                          ⚠ 매 글자마다 쿼리를 날리면 "TS-453E" 입력에 요청이 7번 나가고,
//                            응답 순서가 뒤바뀌면 옛 결과가 최신 결과를 덮는다
//                       ④ ⚠ onBlur 로 목록을 즉시 닫으면 항목 클릭이 먹지 않는다
//                            (blur 가 click 보다 먼저 온다) → 150ms 늦춰서 닫는다
//                       ⑤ 결과 영역은 자리만 잡아 뒀다. 데이터가 오면 여기를 채운다
//
// v7.348 (2026-08-11) — 🗂 사이드바 [관리] 아코디언 — 제품 표준화·재고 실사·백업을 묶음
//                       사이드바 항목이 11개까지 늘어 아래쪽이 눌렸다. 성격이 비슷한 셋을 접었다.
//                       ⚠ 'admin' 은 페이지가 아니라 아코디언 머리다. page 상태에 절대 안 들어간다.
//                         기존 page==='catalog'/'audit'/'backup' 렌더 블록은 손대지 않았다 —
//                         진입 경로만 바뀐 것이라 화면 내용은 그대로다.
//                       ① nav 에 children 을 가진 { id:'admin' } 추가
//                          FlowingMenu 는 중첩을 모른다 → navFlat 으로 평평하게 펴서 넘긴다.
//                          펼침 상태일 때만 부모 바로 뒤에 자식을 끼워 넣고 sub 플래그를 준다
//                       ② FlowingMenu 에 sub / expandable / expanded prop 신설
//                          ⚠ .flowing-menu__item 은 flex:1 이라 형제끼리 높이를 똑같이 나눠 갖는다.
//                            하위 항목도 같은 높이를 가져가면 부모와 구분이 안 되고 메뉴가 늘어난다
//                            → .flowing-menu__item--sub 는 flex:0 0 auto + 고정 38px
//                          ⚠ .flowing-menu__item--active .flowing-menu__item-link 가 !important 로
//                            17px 을 건다. 하위 활성 항목이 커지는 걸 막으려면 같은 !important 로
//                            맞서되 선택자를 더 구체적으로 써야 이긴다 (--sub.--active 2중 클래스)
//                          ・ 펼침 화살표는 항목이 가운데 정렬이라 absolute 로 오른쪽에 붙였다
//                       ③ 동작
//                          ・ [관리] 클릭 = 펼침/접기. 펼치면서 관리 밖에 있었으면
//                            첫 하위(제품 표준화 관리)로 이동. 이미 관리 안이면 화면 유지
//                          ・ 다른 메뉴로 나가면 자동으로 접는다 (관계없는 화면에서 자리만 먹는다)
//                          ・ 접힌 상태에서 관리 하위에 있으면 [관리] 머리가 활성 표시된다
//                       ④ 관리 3개 화면 상단에 AdminTabs 하위 이동 바 추가
//                          ⚠ 아코디언을 접어 두면 하위끼리 오갈 방법이 없다. 모바일은 사이드바가
//                            아예 숨겨져 있어(v7.121) 여기가 유일한 이동 수단이다
//
// v7.347 (2026-08-11) — 📏 넥시 1층 도면 표시 크기 75%
//                       좌표·비율은 손대지 않았다. viewBox 는 원본 픽셀(1345×1505) 그대로고
//                       담는 상자만 width:75% 로 줄였다 (minWidth 720 → 540 동반 조정).
//                       ⚠ viewBox 숫자를 건드리면 원본과 대조가 안 된다. 스케일은 상자에서만 준다.
//
// v7.346 (2026-08-11) — 📐 넥시 1층 도면 전면 재작성 — 원본에서 실측
//                       사용자가 내 도면과 원본을 나란히 놓은 스크린샷 8장을 보냈다.
//                       v7.339~345 의 좌표는 스크린샷을 보고 손으로 짠 근사치라 여러 군데 어긋났다.
//
//                       ⚠ 원본 도면이 xlsx 안에 있었다. 진작 확인했어야 했다:
//                         xl/media/image1.jpg (1341×1600) — [찾기] 탭에 박힌 그림.
//                         꺼내서 픽셀 단위로 측정했다. 이제 눈대중이 아니다.
//                         viewBox 도 "0 0 1345 1505" 로 원본 픽셀 그대로 쓴다 → 대조가 쉽다
//
//                       손으로 짰을 때 틀렸던 것들:
//                         ・ 블(4행 각 147)과 레(4행 각 159)의 행 높이가 다르다. 같은 격자가 아니다
//                         ・ H6~H10 행만 높이가 66 이다 (다른 랙 행은 34)
//                         ・ I1 은 36 인데 I2 는 119 다. I2~I5 도 서로 높이가 다르다
//                           (v7.339 는 I1·I2 를 같은 크기로 그렸다)
//                         ・ 상단 벽은 단이 져 있다 — 좌우 y210~223, 가운데 y225~232
//                         ・ 그린룸 안 계단은 하나가 아니라 두 개다 (위/아래 어긋나게 배치)
//                         ・ 그린룸 좌측은 쌍여닫이문이다 (문 4곳 전부 원본 호 표기를 옮겼다)
//                         ・ 레 블록 오른쪽 아래에 라벨 없는 작은 칸이 하나 있다
//                       색도 원본에서 뽑았다: 블 #DDF5FF / 레 #F3C0C9 / 그린 #CCFEF2 /
//                         옐1 #FEFCCC / 랙·I열 #F0F0F0
//                       검증: 새 렌더를 원본 이미지에 50% 겹쳐 전 칸 일치 확인
//                       코드 144칸·중복 0 은 그대로. export 시그니처도 그대로라 호출부 변경 없음
//
// v7.345 (2026-08-11) — 🐛 넥시 2층 벽선 — 모서리 삐져나옴 + 2번방 아래 뜬 막대
//                       사용자 지적 2건. 원인이 서로 달랐다.
//
//                       ① 모서리에서 가로벽이 세로벽을 지나쳐 튀어나옴
//                          v7.342~344 는 칸마다 바를 놓았다. 가로벽은 칸 폭(37px)을 꽉 채우는데
//                          세로벽은 칸 가운데 8px 이라, 모서리에서 가로벽이 세로벽 중심선을
//                          반 칸(약 18px) 지나쳐 나왔다.
//                          → 칸 단위가 아니라 [연속 구간의 중심선]으로 그리도록 재작성.
//                            구간 끝을 상황에 따라 정한다:
//                              끝 칸에 직교 벽이 붙어 있음(모서리·T자) → 그 칸의 [중심]에서 끝
//                              붙어 있지 않음(자유단·문설주)          → 그 칸의 [바깥 모서리]까지
//                            모서리에서는 양쪽 다 같은 중심점에서 끝나므로 정확히 맞물린다.
//                            사각형 304개 → 구간 19개(가로 9 · 세로 10)로 줄었다
//
//                       ② [2번방] 라벨 아래에 막대가 떠 있음
//                          [행27 열18] 이 이웃이 하나도 없는 고립된 벽 칸이었다.
//                          도면 전체에서 이거 하나뿐이고 바로 위 [행26 열18] 이 [2번방] 라벨이다.
//                          → 시트에서 라벨 밑줄로 칠해둔 칸이지 벽이 아니다. 그리지 않는다.
//                          (구간 길이 2 이상만 그리므로 자연히 빠진다)
//
// v7.344 (2026-08-11) — 🚪 넥시 2층에 문 표시 추가
//                       사용자가 손그림 도면으로 문 6곳(흰 사각형)을 알려줬다.
//                       그 위치가 시트의 벽선이 끊긴 자리와 하나씩 정확히 맞았다:
//                         열33 행19~20  5번방 ↔ 4번방    행21 열36~37  4번방 ↔ 3번방
//                         열33 행27~28  2번방 ↔ 3번방    행31 열19~20  2번방 ↔ 1번방
//                         열17 행50~51  1번방 좌측       열33 행50~51  1번방 우측
//                       ① 건축 도면식 표기 — 문설주 + 문짝 선 + 열리는 궤적(점선 호)
//                       ② 여닫는 방향: 손그림엔 방향이 없어 빈 자리가 더 많은 쪽으로 연다
//                          (문 주변 3칸을 양쪽으로 세어 비교 — swingSign)
//                       ⚠ 개구부를 길이로 자동 판별하면 안 된다. 벽선이 그냥 없는 긴 구간
//                         (열45 행22~45 등)까지 문이 되어버린다 → DOORS 목록으로만 그린다
//                       ⚠ 열33 행26 에 [3번방], 열17 행52 에 [1번방] 라벨이 들어 있어
//                         개구부 전체가 아니라 빈 칸 구간만 문으로 잡았다
//                       ⚠ SVG 는 y 가 아래로 커진다. 호의 sweep 플래그를 화면 기준으로
//                         뒤집어 생각하면 문짝이 반대편에 붙는다
//                       ③ 행53 열18~20 의 [출][입][DOOR] 는 이미 표시된 정문이라 제외
//                       ④ 2층 범례에 [문] 추가
//
// v7.343 (2026-08-11) — 🧹 넥시 2층 — 시트 음영 제거, 벽 선 + 칸만 남김
//                       사용자 지적: 엑셀 양식을 그대로 따를 필요 없다, 선이면 충분하다.
//                       회색(#F3F3F3 / #EFEFEF)은 시트에서 칠해 둔 음영일 뿐 창고 구조가 아니다.
//                       394칸 중 307칸이 빈 칸이라 옮겨 그리면 뜻 없는 회색 덩어리만 크게 남았다.
//                       ① 회색 영역 렌더 제거 (F_SHELF / F_SHELF2 상수째로 삭제)
//                       ② 남긴 것: 벽 선 · 흰 칸 · 구역 색(방·승강기·크림) · 방 표시(노랑)
//                          → 방과 승강기는 실제 공간이라 색을 유지했다
//                       ③ 머리글(화장실 / 3층 계단)이 회색을 쓰고 있어 전용 색으로 분리
//                       ④ 2층 범례에서 [선반] 빼고 [방 표시] 추가
//
// v7.342 (2026-08-11) — 🎨 넥시 2층 배치도를 1층 양식으로 재작성
//                       v7.341 은 시트를 그대로 옮겨 격자가 빽빽하고 글씨가 작았다.
//                       1층(NexiMap) 과 같은 양식으로 맞췄다. 데이터는 그대로다.
//                       ① 칸 크기를 1층 랙과 동일하게 (37 × 21). 이전 27.5 × 15
//                       ② 칸마다 흰 배경 + 둥근 모서리 + 테두리 + 사이 여백
//                       ③ 벽: [검정으로 꽉 채운 칸] → [칸 가운데를 지나는 8px 굵은 선]
//                          ⚠ 칸을 통째로 칠하면 가로벽 21px · 세로벽 37px 로 두께가 달라 보인다.
//                            칸마다 이웃한 벽 방향으로만 바를 놓고, 교차점에선 둘 다 놓아
//                            자연스럽게 십자가 되게 했다
//                       ④ 구역(방·승강기·선반)을 칸 단위 색칠 → 하나의 패널로 병합
//                          ⚠ 병합 사각형마다 테두리를 그으면 영역 안쪽에 이음새가 생긴다.
//                            → 채우기는 테두리 없이 딱 붙여 깔고, 이웃이 다른 영역인 변만
//                              regionOutline() 으로 따로 그었다
//                       ⑤ 승/강/기, 회/의/실 처럼 한 글자씩 흩어진 셀을 한 덩이 라벨로 합침.
//                          ⚠ 2칸부터 합치면 [출][입] 도 붙는다 (서로 다른 문이다) → 3칸 이상만
//                       ⑥ 값 칸 글자색은 항상 기본색. 칸 자체가 흰 배경이라 구역 색에 맞추면
//                          흰 칸 위에 주황 글씨가 떠서 안 어울린다
//                       ⑦ 빈 영역(위 2행 · 오른쪽 4열)을 viewBox 에서 잘라냈다.
//                          그대로 두면 가로 스크롤만 길어지고 도면이 한쪽으로 쏠린다
//                          → 사용 범위 행 3~53 · 열 1~49 (1938×1113 → 1790×1071)
//
// v7.341 (2026-08-11) — 🏢 넥시 조회에 2층 추가 — [1층 | 2층] 전환 + 주문코드 검색
//                       2층은 1층 그린1(계단)으로 올라오는 층이다.
//
//                       ⚠ 1층과 성격이 근본적으로 다르다:
//                         1층 — 칸에 [위치 코드](A8, 블13)가 적혀 있다. 주문코드→위치 매핑이 따로 필요
//                         2층 — 칸에 적힌 값이 곧 [주문코드]다. 도면 자체가 데이터다
//                         그래서 층을 바꾸면 검색 대상도 바뀐다 (입력값은 자동으로 비운다)
//
//                       ① src/NexiFloor2Data.js 신설 — 구글시트 [2층 찾기] 탭에서 기계 추출.
//                          ⚠ 숫자를 눈으로 옮기지 않았다. 700여 칸을 스크린샷에서 읽으면
//                            오탈자가 확정적이라 xlsx 를 받아 openpyxl 로 뽑았다.
//                          ・ 53행 × 53열 · 값셀 551개 · 주문코드 456종
//                          ・ 도면 구조가 전부 [채우기 색]에 들어 있다 (시트에 테두리가 거의 없다)
//                            검정=벽 / #F3F3F3·#EFEFEF=선반 / #FCE5CD=방 / #D9EAD3=승강기 / #FFFF00=방 라벨
//                          ・ 채우기는 행별 런렝스로 압축 (2,809칸을 통째로 나열하지 않기 위함)
//                          ⚠ "7-1" 이 구글시트에서 날짜(2024-07-01)로 변환돼 나왔다 → 추출 시 복원.
//                            재생성할 때도 같은 보정이 필요하다
//                       ② src/NexiFloor2.jsx 신설 — 격자 렌더러.
//                          ・ 배경(벽·선반·방)은 useMemo + 가로 런렝스 병합. 검색할 때마다
//                            2,800개를 다시 만들면 입력이 버벅인다
//                          ・ 글자 수에 따라 폰트 축소 (4자리 숫자가 칸을 넘지 않게)
//                          ・ 병합 셀 2개(화장실 B3:J4, 3층 계단 K3:R4) 처리
//                       ③ 같은 주문코드가 여러 칸에 있다 (801 은 3곳, 1043 도 3곳).
//                          전부 하이라이트하고 "N곳" 으로 알린다. 한 곳만 찍으면 헛걸음한다
//                       ④ "/" 기둥 출 입 DOOR 방이름 승강기 등은 주문코드가 아니라 검색 대상에서 제외
//                       ⑤ 2층은 53열이라 폭 1,450px — 가로 스크롤. 축소하면 숫자를 못 읽는다
//                       ⚠ 이 데이터는 2026-08-11 시트 스냅샷이다. 자리가 바뀌면 시트를 다시
//                         내보내 재생성해야 한다. NexiFloor2Data.js 를 직접 고치지 말 것.
//
// v7.340 (2026-08-11) — 🔧 넥시 도면 정정 — A0~G0 은 라벨이 아니라 세로 진열대다
//                       v7.339 에서 A0~G0 을 랙 그룹의 "통로 라벨"로 잘못 읽고 회색 글자로만
//                       그렸다. 사용자 정정: 세로로 세워둔 진열대이고, 그 자체가 하나의 위치다.
//                       ① RACK_GROUPS 의 label → shelf 로 바꾸고 실제 칸으로 승격
//                          → 클릭·하이라이트·검색 대상 (H1~H10 그룹은 진열대 없음 = null)
//                       ② NEXI_CODES 137 → 144칸 (A0 B0 C0 D0 E0 F0 G0 추가)
//                       ③ Cell 에 vertical 옵션 — 글자를 한 자씩 세로로 쌓는다.
//                          ⚠ writing-mode 는 렌더러 편차가 커서 tspan dy 로 직접 쌓았다.
//                       ④ I 열 폭 38 → 32. 5칸 그룹(F0/G0)의 진열대가 왼쪽으로 더 나와서
//                          자리를 비워야 했다. 계산: 5칸 그룹 셀 시작 281 → 진열대 265~277,
//                          I 열 우측끝 232+32=264 → 간격 1px 확보 (38 이면 6px 겹쳤다)
//                       ⑤ 넥시 조회 구역 분류에 '세로 진열대' 추가
//
// v7.339 (2026-08-11) — 📍 [넥시 조회] 탭 신설 — 창고 1층 도면 + 위치 하이라이트
//                       구글시트 [상품 위치] 를 Inventura 로 옮기는 1단계.
//                       이번엔 도면과 하이라이트까지만. 주문코드 → 위치 데이터는 아직 없다.
//                       ① 사이드바 맨 아래 { id:'nexi', label:'넥시 조회' } 추가
//                       ② src/NexiMap.jsx 신설 — 순수 SVG (외부 의존성 없음, package.json 무관)
//                          viewBox "0 0 600 640" 고정 · 총 137칸 · 중복/결번 검증 완료
//                          ・ 좌측 파렛트 : 블1~블16(파랑) / 레1~레16(빨강)  각 4×4
//                            ⚠ 화면 왼쪽 열이 큰 번호다 (13-16 / 9-12 / 5-8 / 1-4)
//                          ・ I 열       : I1~I5 (I1·I2 는 작은 칸)
//                          ・ 중앙 랙    : A1~A8 B1~B8 C1~C8 D1~D8 E1~E9 F1~F10 G1~G5 H1~H10
//                                        통로 라벨 A0~G0 은 그룹 왼쪽에 붙는다
//                                        5칸 행(E5~E9 등)은 오른쪽 끝(x=466)을 맞추고 왼쪽으로 늘린다
//                          ・ 우측 랙    : J1~J4 K1~K4 L1~L2 M1~M4 N1~N4 O1~O4 P1~P4 Q1~Q4
//                          ・ 기타       : 그린1(계단) · 그린2 · 사앞 · 옐1(옐로존)
//                       ③ 위치 코드 입력 → 해당 칸 파랑 채움 + 펄스 링. 칸을 클릭해도 채워진다
//                          ・ 입력 정규화: 공백 제거 + 영문 대문자화 ("a 8" → "A8")
//                          ・ 없는 코드면 경고 + 앞글자 일치 후보 최대 12개 제시
//                       ④ 펄스 애니메이션은 prefers-reduced-motion 에서 꺼지지만
//                          하이라이트 표시 자체는 남는다 (v7.320 교훈 — 기능성 표시는 끄지 않는다)
//                       ⚠ 도면은 시트 이미지를 옮긴 배치도다. 칸의 순서·이웃 관계는 원본과 같지만
//                         픽셀 치수는 실측이 아니다. 어긋나면 NexiMap.jsx 상단 좌표 상수만 고치면 된다.
//                       ⚠ 모바일은 사이드바가 숨겨져(v7.121) 아직 이 탭에 못 들어간다.
//                         창고 현장용이라 모바일 진입점이 필요하면 별도 요청 필요.
//
// v7.338 (2026-08-11) — 🐛 재고 목록 [신품] 뱃지가 세로로 쪼개지던 문제
//                       증상: 제품명이 긴 행(PATRIOT DDR5 32G PC5-44800 CL46 EVO)에서
//                            우측 [신품] 뱃지가 「신 / 품」 두 줄로 출력. 짧은 모델명은 정상.
//                       원인: 제품 셀(1fr ≈ 238px, 안쪽 206px) 안의 flex 컨테이너에서
//                            제품명(≈273px)과 뱃지(≈34px)가 둘 다 flex-shrink 기본값 1.
//                            초과분 107px 이 base 폭 비례로 배분되며 뱃지도 11.8px 를 떠안아
//                            폭 22px → 안쪽 8px 로 한 글자도 못 담고 줄바꿈된 것.
//                       수정: ① 뱃지 flexShrink:0 + whiteSpace:'nowrap' — 폭 고정
//                            ② 제품명 flex:1 + minWidth:0 + break-word — 초과분을 전부 떠안아
//                               제품명 영역만 줄어든다 (= 제품명 셀이 알아서 좁아지는 효과)
//                            ③ 셀 자체 minWidth:0 — grid item 기본 min-width:auto 로 옆 칸을
//                               미는 것 방지 (v7.335 품목 코드 침범과 같은 원리)
//                            ④ 2줄이 될 때 뱃지가 가운데 뜨지 않도록 alignItems:'flex-start'
//                       범위: 재고 목록 평면 뷰 3번째 열 1곳. 그룹 뷰는 제품명이 헤더에 있어 무관.
//
// v7.337 (2026-08-11) — 🏷 데모 장비 카테고리 변경
//                       모델 그룹을 펼치면 표 위에 [카테고리] 드롭다운이 나온다.
//                       ★ '모델 단위' 로 바꾼다 (그 모델의 전체 대수에 한 번에 적용).
//                         장비 목록은 모델명으로 묶고 그룹 배지는 첫 항목의 카테고리를 쓴다.
//                         같은 모델인데 시리얼마다 카테고리가 다르면 배지가 실제와 어긋난다.
//                         한 대만 옮길 일은 사실상 없다.
//                       ★ update 는 .in('id', ids) 한 번으로 끝낸다.
//                         한 대씩 N번 돌면 중간에 실패했을 때 반쪽만 바뀐 채로 남는다.
//                       ★ 드롭다운을 그룹 헤더가 아니라 펼침 영역에 뒀다.
//                         헤더가 <button> 이라 그 안에 <select> 를 넣으면 마크업이 깨지고
//                         클릭이 접기/펼치기로 새어 나간다.
//                       신규: DemoService.updateEquipmentCategory(ids, category)
//                         updateEquipmentField 의 화이트리스트에 끼워 넣지 않았다.
//                         그건 id 하나짜리 인라인 편집용 경로다.
//                         유효한 카테고리 코드인지 서비스에서 막는다 — DB 에 CHECK 가 없으면
//                         오타가 그대로 들어가고, 그 장비는 어느 필터에도 안 걸려 조용히 사라진다.
//                       현재 값이 목록에 없는 코드(구 데이터)면 그 값도 옵션에 남겨 둔다.
//                         안 그러면 select 가 첫 항목을 고른 것처럼 보여 오해를 부른다.
// v7.336 (2026-08-11) — ☎ 연락처 하이픈 자동 입력 + 데모 카테고리 [기타]
//                       ① 숫자만 쳐도 하이픈이 들어간다. 연락처 입력 7곳 전부.
//                          출고요청 / 퀵 기사 / 배송시트지 인라인 / 수정 모달 /
//                          데모 대여 / 킵 모달 / 직원 연락처
//                          ⚠ '3-4-4 고정' 으로는 안 된다. 국번 자리수가 제각각이다:
//                            02-1234-5678(서울 2자리) · 0507-1407-8923(안심번호 4자리)
//                            1588-1234(대표번호, 지역번호 없음) · 031-123-4567
//                            → 앞자리로 갈래를 정하고, 자릿수가 덜 찬 입력 도중에도
//                              부분 포맷을 돌려준다 (010-1 → 010-12 → 010-123 …).
//                          ★ 백스페이스 처리가 핵심이다.
//                            하이픈을 지우면 숫자는 그대로라 포맷 함수가 하이픈을 되살린다.
//                            → 아무리 눌러도 안 지워지는 상태가 된다.
//                            길이는 줄었는데 숫자가 그대로면 '하이픈을 지우려 한 것' 으로 보고
//                            앞 숫자를 하나 더 지운다 (phoneOnChange 가 prev 를 받는 이유).
//                          ※ 직원 연락처 칸만 uncontrolled(defaultValue)라 DOM 값을 직접 고친다.
//                          ※ 이미 저장된 번호는 건드리지 않는다. 새로 입력할 때만 적용된다.
//                       ② 데모 장비 카테고리에 'ETC'(기타) 추가.
//                          추가 폼·필터·집계가 모두 DEMO_CATEGORY_LABEL 을 순회하므로
//                          상수 한 곳만 고치면 전부 따라온다. 색은 #607d8b(블루그레이).
//                          ⚠ demo_equipment.category 에 CHECK/enum 제약이 있으면 저장이 거부된다.
//                             migrations/v7.336-demo-category-etc.sql ①로 먼저 확인할 것.
//                             0행이면 제약이 없다는 뜻이고 그냥 쓰면 된다.
// v7.335 (2026-08-11) — 🐛 재고 목록 [품목 코드]가 옆 칸을 침범하던 문제
//                       증상: 19자짜리 시리얼(5112506121440000029 등)이 [품목명] 위로 흘러
//                             글자가 겹쳐 보인다.
//                       원인 둘:
//                         ① 연속 숫자열에는 줄바꿈 지점이 없다.
//                            공백도 하이픈도 없으니 브라우저가 끊을 자리를 못 찾아
//                            트랙(120px)을 그대로 넘어 흘렀다.
//                         ② grid item 의 기본 min-width 는 auto 다.
//                            내용보다 작아지지 않으려 해서 트랙 폭이 무시된다.
//                       조치: word-break:break-all + overflow-wrap:anywhere 로 강제 줄바꿈,
//                             min-width:0 으로 트랙 폭을 지키게 하고, 칸을 120→156px 로 넓혔다.
//                             (156px 이면 12.5px 모노스페이스 기준 한 줄 15자 → 19자가 2줄)
//                             글자 크기 13→12.5px, title 로 전체 값 툴팁.
//                       두 곳 모두 적용 — 전체 보기 / 모델 그룹 보기.
//                       ⚠ 트랙을 넓힌 만큼 minWidth 도 같이 올렸다.
//                          안 올리면 가로 스크롤 계산이 어긋나 [상태] 칸이 잘린다.
// v7.334 (2026-08-10) — 🎨 v7.333 마무리 — 활성 메뉴가 아직 청록이던 부분
//                       v7.333 은 인라인 스타일로 덮으려 했는데 반만 먹혔다.
//                       FlowingMenu.css 를 받아 보니 이유가 명확했다:
//                         · 배경  — !important 없음 → 인라인이 이겼다 ✓ (흰색으로 바뀜)
//                         · 글자·아이콘 — `color: #00668a !important` → 인라인이 졌다 ✗
//                         · 좌측 강조 바(::before), 우측 점(::after)
//                           — 가상요소라 인라인이 애초에 닿지 않는다 ✗
//                       조치: CSS 쪽 색을 전부 변수로 빼고 FlowingMenu.jsx 가 꽂는다.
//                         CSS 변수는 상속되므로 wrap 한 곳에만 넣으면
//                         자식 요소와 가상요소까지 전부 따라온다.
//                         변수를 안 주면 fallback(기존 청록) 그대로 — 하위 호환.
//                       파생색은 JS 에서 계산한다 (fmRgba).
//                         CSS 만으로는 변수에 담긴 hex 에 알파를 입힐 수 없다.
//                         (color-mix 는 지원 폭이 걱정돼 쓰지 않았다)
//                         강조색 #0b1c30 기준: 링 0.18/0.10, 번짐 0.35/0.50, inset 0.12
//                       activeAccentColor prop 도 뒀다. 안 주면 activeTextColor 를 쓴다
//                       — 지금은 바·점도 검정으로 통일된다.
// v7.333 (2026-08-10) — 🎨 선택된 메뉴를 흰 배경 + 검정 글자로 + 마퀴 알파 50% 복귀
//                       ① 활성 메뉴 색이 FlowingMenu.css 안에 박혀 있어 App.js 에서
//                          못 바꾸고 있었다. → activeBgColor / activeTextColor prop 신설.
//                          인라인 스타일은 클래스 규칙을 이기므로 CSS 파일을 안 건드려도 된다.
//                          background 는 단축 속성이라 CSS 가 걸어 둔 그라디언트까지 같이 지운다.
//                          두 값을 안 넘기면 기존 색 그대로다 (하위 호환).
//                       ② 마퀴 알파를 원래 요청대로 0.5 로 되돌렸다.
//                          v7.326 에서 0.5 로 넣었을 때 원본 라벨이 비쳐 겹쳐 보였고
//                          v7.327 은 0.95 로 '덮어서 가리는' 임시 조치였다.
//                          ★ 이번에 원인을 없앴다 — 마퀴가 올라오는 동안 원본 버튼을
//                            페이드 아웃한다 (GSAP 타임라인에 opacity 0/1 을 얹었다).
//                            빠져나갈 때는 마퀴가 물러난 뒤 나타나게 0.15s 늦췄다.
//                       ⚠ src/FlowingMenu.css 는 여전히 없다. 활성 항목의 점(·) 같은
//                          가상요소(::after)가 CSS 에 있다면 인라인으로는 못 바꾼다.
//                          그 점이 아직 청록이면 CSS 파일이 필요하다.
// v7.332 (2026-08-10) — 🔧 파티클 밀도·마우스 반응 + 데모 비고 저장 오류
//                       ① 파티클이 너무 성겼다.
//                          사이드바는 폭 256px 라 3D 로 흩뿌린 입자 상당수가 화면 밖으로 나간다.
//                          개수 140→520, 크기 70→110, spread 12→8 (보이는 범위에 모이게),
//                          속도 0.07→0.12.
//                       ② 마우스에 반응하지 않던 문제.
//                          원인: 캔버스에 pointer-events:none 을 줬는데(안 그러면 메뉴 클릭이
//                          막힌다) 원본 Particles 는 그 캔버스 컨테이너에 mousemove 를 단다.
//                          pointer-events:none 인 요소는 mousemove 도 못 받는다.
//                          → Particles 에 hoverTargetRef prop 신설 (로컬 변경 ③).
//                            리스너는 사이드바 엘리먼트에 붙이고, 좌표 기준만 캔버스로 쓴다.
//                            mousemove 리스너는 클릭을 막지 않으므로 메뉴는 그대로 눌린다.
//                       ③ 데모 [대여로그] 비고 저장 실패:
//                          "column demo_rentals.audit_log does not exist"
//                          원인: v7.330 이 select('notes,audit_log') 로 한 번에 읽었다.
//                            audit_log 컬럼이 없는 DB(v7.43 SQL 미실행)에서는
//                            읽기 자체가 실패해 비고 저장이 시작도 못 했다.
//                          조치: 본 저장에 필요한 notes 만 먼저 읽는다.
//                            audit_log 는 저장이 끝난 뒤 따로 시도하고, 없으면 조용히 넘어간다.
//                          ⚠ supabase-js 는 오류를 throw 하지 않고 error 로 '돌려준다'.
//                            try/catch 만으로는 안 잡힌다. error 를 직접 봐야 한다.
//                            v7.330 이 try/catch 로 막았다고 여겼던 게 이 지점이다.
//                          이력을 남기고 싶으면 migrations/v7.332-demo-audit-log.sql 실행.
// v7.331 (2026-08-10) — 🐛 v7.329 빌드 실패 — Particles.jsx 의 eslint 주석
//                       증상: npm run build 가 exit 1.
//                         src/Particles.jsx  Line 243:5
//                         Definition for rule 'react-hooks/exhaustive-deps' was not found
//                       원인: React Bits 원본에 있는
//                         react-hooks/exhaustive-deps 를 끄는 지시문이
//                         이 프로젝트에 없는 규칙을 가리킨다.
//                         (ESLint 설정에 eslint-plugin-react-hooks 가 없다)
//                         존재하지 않는 규칙을 disable 하면 ESLint 는 그걸 '에러' 로 낸다.
//                         CRA 는 lint 에러를 빌드 실패로 올리므로 빌드가 통째로 멈춘다.
//                       조치: 규칙명을 뺀 `// eslint-disable-next-line` 로 바꿨다.
//                         이 프로젝트의 다른 파일들(App.js, MobileLookup.jsx)도 같은 형태다.
//                       ※ Lightfall.jsx 에는 이 주석이 없어서 영향이 없었다.
//                       ※ 앞으로 React Bits 컴포넌트를 가져올 때 확인할 것 —
//                         react-hooks/* 를 가리키는 disable 주석은 전부 걷어내야 한다.
// v7.330 (2026-08-10) — ✏️ 데모 관리 [대여로그] 비고 더블클릭 인라인 편집
//                       기존에는 [수정] 버튼 → 모달 → 저장 이었다. 비고 한 줄 고치는 데
//                       품목 편집 모달을 여는 건 과했다.
//                       · 여러 줄이라 input 이 아니라 textarea. 줄 수에 맞춰 자동으로 늘어난다.
//                       · Enter 는 줄바꿈. 저장은 Ctrl+Enter 또는 칸 밖 클릭(blur).
//                       ★ Esc 취소가 blur 저장과 충돌한다.
//                         Esc 를 누르면 textarea 가 언마운트되면서 onBlur 가 뒤이어 발생하고,
//                         그 클로저는 '취소되기 전 draft' 를 들고 있다. 그대로 두면
//                         Esc 를 눌러도 저장돼 버린다. → ref 플래그로 한 번 걸러낸다.
//                       ★ 단일 클릭이 아니라 더블클릭인 이유:
//                         이 행에는 [이력]/[수정]/[회수] 버튼이 같이 있다.
//                         클릭 편집이면 표를 훑다가 실수로 편집 상태에 들어간다.
//                         (장비 목록 탭의 비고는 클릭 편집인데, 거기는 버튼이 없다)
//                       신규: DemoService.updateRentalNotes(rentalId, notes, by)
//                         ⚠ updateRental() 을 재사용하지 않았다. 그건 품목 diff 와
//                           demo_equipment.location 복원까지 도는 무거운 경로라,
//                           비고 한 줄 고치려고 태우면 부작용 위험만 커진다.
//                         audit_log 에 edit_notes 항목을 남긴다 ([이력] 버튼에서 보인다).
//                         비고가 길 수 있어 요약은 40자에서 자른다.
//                         audit_log 컬럼이 없어도(v7.43 이전 스키마) 비고 저장은 유지된다.
//                       ※ [회수로그] 탭 비고(demo_returns.notes)는 아직 그대로다. 필요하면 붙인다.
// v7.329 (2026-08-10) — ✨ 다크 모드 사이드바 배경을 사진 → Particles 로
//                       라이트는 해변 사진 그대로. 다크만 바꾼다.
//                       사진 위에 어두운 막을 씌우던 방식은 결국 '어두워진 사진' 이라
//                       답답했다. 다크에서는 배경 자체를 짙은 남색 그라디언트 + 별가루로.
//                       신규 파일: src/Particles.jsx, src/Particles.css
//                         ⚠ ogl 을 쓴다. v7.328 에서 이미 넣었다면 추가 작업 없음.
//                         원본과 다른 곳 두 군데 (둘 다 파일 안에 이유를 적어 뒀다):
//                           ① cleanup 에 WEBGL_lose_context 반납 — 테마를 껐다 켤 때마다
//                              effect 가 다시 돌아 컨텍스트가 쌓인다
//                           ② className 기본값 '' — 원본은 undefined 라
//                              class="particles-container undefined" 가 박힌다
//                       ★ 레이어 순서에 함정이 있다.
//                         CSS 페인트 순서상 'position 이 있고 z-index:0 인 요소' 는
//                         'position 없는 일반 블록의 글자' 보다 위에 그려진다.
//                         캔버스를 absolute 로 깔기만 하면 메뉴 글자가 덮인다.
//                         → 사이드바 직계 자식 3개(로고/메뉴/하단)에 position:relative + z-index:1.
//                       ★ 캔버스에 pointer-events:none. 없으면 메뉴 클릭이 막힌다.
//                         moveParticlesOnHover 를 끈 것도 같은 이유다 — 마우스를 받으려면
//                         pointer-events 를 살려야 하는데 그러면 메뉴를 못 누른다.
//                       파티클 설정은 배경답게 낮췄다: 개수 200→140, 속도 0.1→0.07,
//                       크기 100→70, alphaParticles=true(가장자리 부드러운 원).
//                       aside 에 overflow:hidden — 캔버스가 모서리 밖으로 새지 않게.
// v7.328 (2026-08-10) — 🧪 테스트 탭 초기화 — BorderGlow 실험실 → Lightfall 실험실
//                       ⚠⚠ 배포 전 필수: package.json 에 "ogl": "^1.0.11" 추가.
//                          없으면 import 단계에서 '빌드 자체가' 실패한다 (런타임 오류 아님).
//                          추가 후 docker compose build 하면 npm install 이 받아온다.
//                       신규 파일: src/Lightfall.jsx, src/Lightfall.css
//                         · Lightfall.jsx 는 React Bits 원본 그대로 두되 한 줄만 더했다 —
//                           cleanup 에서 WEBGL_lose_context 로 컨텍스트를 명시 반납한다.
//                           원본 cleanup 의 callIfFn(renderer,'destroy') 는 아무 일도 안 한다
//                           (ogl Renderer 에 destroy() 가 없어서 typeof 검사에서 넘어간다).
//                           슬라이더를 드래그하면 컨텍스트가 계속 쌓여 브라우저 한도(보통 16개)를
//                           넘기고 "Too many active WebGL contexts" 로 화면이 검게 남는다.
//                         · Lightfall.css 는 원본 미제공이라 새로 썼다 (BorderGlow 와 같은 상황).
//                           컨테이너를 부모에 꽉 채우는 것 외엔 하는 일이 없다.
//                           ⚠ 부모에 height 가 없으면 아무것도 안 보인다.
//                       실험실 쪽 처리:
//                         · Lightfall 의 useEffect 는 의존성 배열에 모든 prop 이 들어 있어
//                           값 하나만 바뀌어도 WebGL 컨텍스트를 새로 만든다.
//                           → 슬라이더 값을 220ms 디바운스해서 넘긴다 (useDebounced).
//                         · colors 배열은 useMemo 로 참조를 고정한다. 매 렌더마다 새 배열이면
//                           디바운스를 해도 참조가 달라져 effect 가 계속 다시 돈다.
//                         · [정지] 버튼 — 값 비교할 때 프레임을 멈춘다.
//                       BorderGlow 실험실은 제거. 값 확정이 끝나 v7.318~v7.323 에서
//                       실제 배송시트지에 반영 완료됐다.
//                       ★ BorderGlow.jsx 자체는 남는다 — 행 글로우가 useRowGlow/rowGlowVars 를 쓴다.
//                         다만 카드형(default export)은 쓰는 곳이 없어져 import 에서 뺐다.
//                         안 쓰는 import 를 남기면 CI=true 인 도커 빌드에서 경고가 에러로
//                         승격돼 빌드가 깨진다.
// v7.327 (2026-08-10) — 🔧 마퀴 겹침 완화 + 배경 선택기 철거
//                       ① 'HDD/SSD 입고' 가 두 번 겹쳐 보이던 문제.
//                          원인: 마퀴 띠는 메뉴 항목 '위를 덮고' 지나가는 구조인데
//                                v7.326 에서 알파를 0.5 로 두어 덮인 원본 글자가 그대로 비쳤다.
//                          조치: 알파 0.5 → 0.95. 사진 톤은 아주 옅게만 비친다.
//                          ⚠ 진짜 '반투명 + 겹침 없음' 은 이 파일에서 못 한다.
//                             띠가 지나가는 동안 원본 라벨을 숨겨야 하는데
//                             그 코드가 src/FlowingMenu.jsx 안에 있고, 그 파일은
//                             tarball 에 안 들어온다 (public/sidebar-bg.jpg 와 같은 상황).
//                       ② v7.325 의 사이드바 배경 선택기(프리셋 6종 + 색 선택기) 제거.
//                          관련 상태·상수·UI 전부 걷어냈다. 배경은 사진 고정.
//                          ★ 다만 두 가지는 남긴다 — 둘 다 실제 버그를 고친 것이라서다.
//                            · 다크 모드용 막: 예전엔 라이트용 rgba(239,244,255,…) 고정이라
//                              다크에서 사이드바만 하얗게 떠 있었다
//                            · invert 플래그: 로고 color:'#000' / 메뉴 textColor:'#0b1c30' 이
//                              박혀 있어 다크에서 글자가 안 보였다
// v7.326 (2026-08-10) — 🎨 사이드바 메뉴 마퀴 배경을 흰색 알파 50% 로
//                       marqueeBgColor: var(--accent-solid) → rgba(255,255,255,0.5)
//                       ★ marqueeTextColor 도 같이 바꿨다.
//                         기존 값이 var(--bg-card) = 라이트에서 흰색이라,
//                         흰 띠 위에 흰 글자가 되어 호버하면 메뉴명이 사라진다.
//                         → #0b1c30 (진한 남색).
//                       ⚠ 반투명이라 띠 뒤의 원래 메뉴 글자가 비쳐 겹쳐 보일 수 있다.
//                         그러면 SIDEBAR_MARQUEE_BG 의 알파만 올리면 된다 (0.5 → 0.7 등).
//                         두 값 모두 상수로 빼서 한 줄만 고치면 되게 했다.
//                       배경은 요청대로 사진 그대로 둔다 (v7.325 선택기도 그대로).
// v7.325 (2026-08-10) — 🎨 사이드바 배경 고르기
//                       하단 [배경] 버튼 → 프리셋 6종 + 직접 고르기(color picker).
//                         사진 / 기본(카드색) / 하늘 / 민트 / 모래 / 남색
//                       저장은 localStorage 'inventura_sidebar_bg' — 이 PC 에만 적용된다.
//                         (전 직원 공유가 필요하면 app_settings 로 옮기면 된다)
//                       ★ 글자색을 배경에 맞춰 뒤집는다.
//                         기존에는 로고가 color:'#000', FlowingMenu 가 textColor:'#0b1c30' 로
//                         박혀 있었다. 밝은 해변 사진 전용 값이라 어두운 배경을 고르면
//                         글자가 배경에 묻혀 안 보인다.
//                         → resolveSidebarBg() 가 { bg, invert } 를 돌려주고
//                           로고·메뉴·구분선·하단 블록이 전부 그 값을 따른다.
//                         직접 고른 색은 상대휘도(WCAG 식)를 계산해 0.42 미만이면 밝은 글자.
//                       ⚠ 곁들여 고친 것: 사진 배경의 덮는 막이 라이트용
//                         rgba(239,244,255,…) 로 고정이라, 다크 모드에서 사이드바만
//                         하얗게 떠 있었다. 다크에서는 rgba(11,20,35,…) 막을 쓴다.
// v7.324 (2026-08-10) — 🐛 입고 등록의 [비고] 가 재고목록에 안 뜨던 문제
//                       ★ 원인: 컬럼이 달랐다. handleRegister 는 inventory.note 에만 썼는데
//                         재고목록 [비고] 칸이 보여주는 건 inventory.memo 다
//                         (saveInventoryMemo → updateBySerial({memo})).
//                         v7.254 에서 DOA 경로로 똑같은 걸 고쳤는데 입고 등록은 남아 있었다.
//                       조치: note 는 감사용으로 그대로 두고 memo 를 함께 넣는다.
//                       ※ 배송시트지 쪽(transaction_log note.memo → shipInfo.memo)은
//                         v7.304 이전부터 이미 저장·표시되고 있었다. 코드 변경 없음.
//                         혹시 그쪽도 안 보이면 값이 비어 저장된 것이므로
//                         migrations/v7.324-check-inbound-memo.sql 로 실제 값을 확인할 것.
//                       곁들여: 공백만 입력한 경우를 빈 값으로 다듬는다(trim).
//                         안 그러면 비고 칸에 ' ' 가 들어가 '비고 있음' 처럼 보인다.
//                       ※ 이미 등록된 과거 입고 건은 소급되지 않는다.
//                         note 에는 남아 있으므로 SQL 로 memo 에 복사할 수 있다 (위 파일 ③).
// v7.323 (2026-08-09) — ☑ 테두리 애니메이션을 '건별' 로 켜고 끄기
//                       v7.322 의 체크박스는 전체 on/off 였다. 요청은 건별이었다.
//                       각 행 맨 왼쪽 칸(드래그 핸들·상세보기 아이콘 아래)에 체크박스를 넣었다.
//                       ★ 저장 위치: transaction_log.note(JSON) 의 glowRun 필드.
//                         새 컬럼도 테이블도 안 만들었다 — note 는 이미 이 행의 부가 상태를
//                         담는 JSON 이고(fulfilled, autoDefer, bookkept …), 전 직원이 같은 값을
//                         본다. SQL 실행이 필요 없다.
//                       ⚠ 저장할 때 note 를 통째로 덮어쓰지 않는다. 최신 값을 다시 읽어
//                         glowRun 만 얹는다 (saveBookkept 와 같은 패턴).
//                         안 그러면 그 사이 남이 바꾼 송장번호·비고가 날아간다.
//                       ⚠ 체크박스는 즉각 반응해야 하므로 화면을 먼저 바꾸고(낙관적 반영)
//                         저장이 실패하면 되돌린다. 저장 안 된 걸 켜진 것처럼 두면 안 된다.
//                       기본값: note.glowRun 이 없으면 '출고요청만 켜짐'.
//                         → 기존 행은 손대지 않아도 지금과 똑같이 동작한다.
//                       출고완료·입고·환불 건도 체크하면 상태색으로 돌기 시작한다
//                         ('이 건 주목' 표시로 쓸 수 있다. 체크하면 상시 테두리도 같이 켜진다)
//                       필터 줄의 체크박스는 '애니메이션 (전체)' 마스터 스위치로 남긴다.
//                         꺼두면 건별 체크와 무관하게 전부 정지한다 (체크박스는 흐리게 표시).
// v7.322 (2026-08-09) — 🌗 라이트 모드에서도 행 글로우 + 애니메이션 마스터 스위치
//                       ① 라이트 모드 차단(html:not([data-theme='dark']) … opacity:0) 제거.
//                          v7.262 에 '라이트에서는 배경에 묻힌다' 고 껐었는데,
//                          묻히는 원인은 모드가 아니라 색이었다.
//                          흰 배경(#f8f9ff)에 밝기 66~70% 짜리 형광색을 올렸으니 안 보인다.
//                          → 테마별 팔레트를 따로 둔다.
//                            라이트: 밝기 32~48% 의 진한 색, 세기 1.5 → 1.0
//                            다크  : 배경이 #0f172a 라 밝은 색이 맞다. 기존 값 유지.
//                          [행 글로우] 토글도 이제 테마와 무관하게 노출된다.
//                       ② 도는 빛을 두 겹으로 갈랐다.
//                            .glow-mark — 출고요청 상시 테두리 (호버와 무관하게 늘 켜짐)
//                            .glow-run  — 거기에 얹는 시계방향 애니메이션
//                          ⚠ 예전엔 한 덩어리라 애니메이션을 끄면 출고요청 표시까지
//                            통째로 사라졌다. 이제 체크박스를 꺼도 빨간 테두리는 남는다.
//                       ③ [출고요청 애니메이션] 체크박스 추가 (필터 줄, 행 글로우 켠 상태에서만).
//                          localStorage 'inventura_ship_run' 에 남는다.
// v7.321 (2026-08-09) — ↻ 도는 빛을 진짜 시계방향 순회로
//                       v7.320 은 위/아래 테두리를 같이 왼→오 로 쓸고 지나갔다.
//                       이제 위쪽은 왼→오, 아래쪽은 오→왼 으로 돈다.
//                       레이어 3장:
//                         A 위 절반을 지나는 띠   (background-size: 22% 50%, Y=0%)
//                         B 아래 절반을 지나는 띠 (background-size: 22% 50%, Y=100%)
//                         C 항상 깔리는 옅은 테두리
//                       앞 절반은 A 만, 뒤 절반은 B 만 화면에 있고,
//                       쉬는 쪽은 pct 125%(오른쪽 밖)에 세워 둔다.
//                       → 위를 오른쪽으로 빠져나간 빛이 곧바로 아래 오른쪽에서 들어온다.
//                       ⚠ conic 으로 진짜 한 바퀴를 돌리지 않은 이유는 v7.316 과 같다.
//                          행 1240×120 이면 중심에서 위/아래 변이 각각 174°,
//                          좌우 변은 각각 5° 뿐이다. 각도를 균일하게 돌리면
//                          위아래 한가운데서 기어가다 모서리에서 폭주한다.
//                       ⚠ 좌우 변을 따로 이동시키지 않은 것도 의도다.
//                          둘레 2720px 중 좌우 변은 240px(8.8%)뿐이고,
//                          모서리 전환은 순간이라 눈에 안 띈다.
//                          (띠가 모서리에 걸칠 때 세로 테두리도 함께 밝아진다)
//                       runDuration 이 이제 '한 바퀴' 기준이라 2.6s → 4.5s
//                       (위/아래 각각 2.25s, 이전 한 번 쓸기와 비슷한 속도).
//                       띠 길이는 --run-band (기본 22%) 로 뺐다.
// v7.320 (2026-08-09) — 🐛 출고요청 '도는 빛' 이 안 움직이던 문제
//                       증상: 붉은 테두리만 둘러져 있고 애니메이션이 없다.
//                       원인 ① prefers-reduced-motion 규칙이 통째로 덮어썼다.
//                             v7.319 는 @media (prefers-reduced-motion: reduce) 에서
//                             .glow-run::before 를 animation:none + 단색 배경으로 바꿨다.
//                             OS 에서 '애니메이션 줄이기' 가 켜진 PC 라면 이 규칙이 이겨서
//                             정확히 '움직이지 않는 붉은 테두리' 가 된다. 증상과 일치한다.
//                             → 이 효과는 '아직 안 끝난 출고' 를 알리는 기능성 표시고,
//                               [행 글로우] 토글로 이미 끌 수 있다. 미디어쿼리에서 뺐다.
//                       원인 ② 설령 돌았어도 눈에 잘 안 띄었다. 띠 위치를 계산해 보니
//                             x = W * [ f*s + (1-s)*pct ]  (s=size 배율, f=이미지 내 띠 중심)
//                             v7.319 (s=220%, f=50%, pct 100%→-100%):
//                               x 가 -124px → 2852px. 행(1240px)을 한참 벗어나
//                               한 주기의 43% 는 띠가 화면 밖에 있었다.
//                               띠 폭도 행의 53%(655px)라 '쓸고 지나간다' 가 아니라
//                               '테두리 전체가 서서히 밝아졌다 어두워진다' 로 읽혔다.
//                             → s=200%, f=25%, pct 62.5%→-62.5%.
//                               x 가 -155px → 1395px, 띠 폭 310px(행의 25%).
//                               한 주기 내내 화면에 걸쳐 있다 (가시 시간 100%).
//                       원인 ③ 예방 — animation 단축 속성 안의 var() 를 낱개 속성으로 풀었다.
//                             단축 안 var() 는 치환 결과가 어긋나면 선언 전체가 무효가 되고
//                             애니메이션이 '조용히' 안 걸린다. 원인 추적이 어려워진다.
//                       주기 기본값 3s → 2.6s.
// v7.319 (2026-08-09) — ✨ 행 글로우 상태색 재정의 + 출고요청 '도는 빛'
//                       색:  출고완료 초록 / 출고요청 빨강 / 환불 보라 / 입고 청록
//                            취소·취소됨은 글로우 없음 (이미 opacity 0.55 로 흐린 행이다)
//                       ★ 출고완료 ↔ 출고요청 갈림은 shipInfo.fulfilled 로 본다.
//                         행의 [출고완료]/[출고요청] 칩이 쓰는 바로 그 플래그다.
//                         v7.318 은 이 구분 없이 출고를 전부 빨강으로 칠했다.
//                       출고요청 행에는 테두리를 도는 빛(.glow-run)이 붙는다 — 호버와 무관하게 항상.
//                         '아직 안 끝난 건' 이 목록에서 저절로 눈에 띈다. 3초에 한 바퀴.
//                       ⚠ conic 으로 '진짜 한 바퀴' 를 돌리지 않았다.
//                         행은 1240×60 처럼 납작해서, v7.316 에서 본 것과 같은 이유로
//                         conic 은 위/아래 변에만 빛이 뭉치고 좌우 변은 순식간에 지나간다.
//                         → 가로로 쓸어주는 편이 실제로 '도는' 것처럼 보인다 (둘레의 95%가 가로변).
//                         레이어 2장 — ① 움직이는 밝은 띠 ② 항상 깔리는 옅은 테두리.
//                           ②가 없으면 띠가 지나가지 않는 동안 행이 안 보인다.
//                       ::after(호버 스포트라이트)와 ::before(도는 빛)를 겹쳐 쓴다.
//                         둘 다 absolute 라 grid 10칸 배치에는 영향이 없다.
//                       prefers-reduced-motion 이면 애니메이션을 멈추고 옅은 테두리만 남긴다.
// v7.318 (2026-08-09) — ✨ 행 글로우를 배송시트지 본체에 적용 (테스트 탭 → 실사용)
//                       [테스트] 탭에서 맞춘 값 그대로 가져왔다:
//                         세기 1.5 / 빛 크기 310px / 가장자리 감도 100 / 테두리 2px / 모서리 6px
//                         적용 모드 '호버한 행만' + '행 상태색으로 글로우'
//                       감도 100 은 중앙 0 → 가장자리 1 의 선형 램프다. 임계값 없이 부드럽다.
//                       상태색 (뱃지 색 계열과 맞춤):
//                         출고 빨강 / 입고 청록 / 취소 주황 / 환불 보라 / 처리됨 무채색
//                       ★ 훅은 한 번만 부른다.
//                         useRowGlow 의 포인터 핸들러는 currentTarget 에만 쓰므로 색과 무관하다.
//                         색깔별로 훅을 5번 부르면 useCallback 이 5벌 생길 뿐이다.
//                         → BorderGlow 에 rowGlowVars() 순수 함수를 새로 export 하고
//                           상태별 CSS 변수 묶음은 모듈 로드 시 한 번만 만든다.
//                       ⚠ 라이트 모드는 CSS 가 알아서 끈다
//                          (html:not([data-theme='dark']) .glow-row::after{opacity:0}).
//                          [행 글로우] 토글은 다크 모드에서만 노출되며 localStorage 에 남는다.
//                       곁들여: 행 호버 배경을 테마 변수로 바꿨다.
//                         기존엔 '#f0f4ff'(호버) / '#fafafa'(처리됨) 하드코딩이라
//                         다크 모드에서 행에 마우스를 올리면 흰빛으로 번쩍였다.
//                         → var(--bg-accent-soft) / var(--bg-mute). 글로우도 이제 안 묻힌다.
// v7.317 (2026-08-09) — 🎨 원본 카드 글로우를 예시 이미지대로
//                       비교: 예시는 '테두리에 붙은 얇고 선명한 빛' 인데,
//                             우리 것은 카드 주변이 통째로 물든 큰 얼룩이었다.
//                       원인: ::before 헤일로가 7겹 메시 radial-gradient 를
//                             카드보다 크게(inset 35%) 깔고 크게(blur 55%) 흐렸다.
//                             배경까지 뿌옇게 밀려나 정작 테두리 선이 묻혔다.
//                       조치:
//                         · 메시 그라디언트(--gradient-one…seven) 전부 제거
//                         · --ring-paint 를 새로 두고 링(.edge-light)과 블룸(::before)이 공유.
//                           두 겹이다 — ① 커서 방향의 밝은 호 ② 항상 깔리는 팔레트 링
//                         · 블룸은 링과 같은 위치(inset: glow-padding)에 깔고 blur 42% 로만.
//                           안쪽은 .border-glow-inner 가 덮으니 바깥 번짐만 보인다
//                         · --glow-hot 신설 — 호의 한가운데를 채도 -22 / 밝기 +20 으로.
//                           예시의 '가장 밝은 곳이 흰색에 가까운' 느낌이 여기서 나온다
//                         · 링 최소 밝기 0.14 — 빛이 닿지 않는 쪽에도 얇은 테두리가 남는다
//                         · colors prop 은 --palette-1/2/3 (저알파 rgba) 로 바뀌어
//                           링을 도는 conic 의 밑바탕이 된다. 알파는 세기에 비례
//                       ※ 행(.glow-row)은 v7.316 의 커서 위치 radial 그대로다. 무변경.
// v7.316 (2026-08-09) — 🐛 행 글로우가 '가운데 작은 점' 으로만 보이던 문제
//                       증상: 배송시트지 행에 마우스를 올리면 위쪽 중앙에 희미한 점 하나.
//                       원인: conic-gradient 는 '각도' 를 균등하게 나누는데,
//                             테두리의 '길이' 는 각도에 비례하지 않는다.
//                             납작한 박스일수록 이 어긋남이 폭주한다.
//                             행 1300×60 기준 (중심에서 위쪽 테두리가 ±87.4° 를 차지):
//                               60% 밝기 θ=15° → 중앙에서 ±  8px  (테두리 폭의 1.2%)
//                               30% 밝기 θ=35° → 중앙에서 ± 21px  (3.2%)
//                               10% 밝기 θ=60° → 중앙에서 ± 52px  (8.0%)
//                             1300px 테두리에서 밝은 구간이 16px 짜리 점이었던 셈이다.
//                             θ가 커지면 tan 이 폭주해 커서를 좌우로 옮길 때 빛이 튀듯
//                             날아가 대부분 모서리 밖으로 빠졌다.
//                             (참고로 데모 카드 368×175 는 12.7% 라 그럭저럭 보였다.
//                              같은 CSS 인데 행에서만 유독 티가 난 이유다)
//                       조치: 행은 커서 좌표에 원을 놓는 radial-gradient 로 바꾼다.
//                             빛 크기가 px 로 고정돼 박스 비율과 무관해진다.
//                             --cursor-x/y 를 useRowGlow 가 px 로 꽂아준다.
//                         · opacity 도 손봤다. 예전엔 glow-on 하나로만 정해서
//                           높이 60px 행에서는 세로 중앙 ±9px 를 벗어나야 겨우 켜졌다.
//                           → 호버 중이면 항상 보이고(0.5) 가장자리에서 최대(1.0).
//                         · '모든 행 항상' 모드는 커서가 없으면 스포트라이트가 중앙에
//                           멈추므로 균일한 밑바탕을 한 겹 깔았다.
//                         · 실험실에 [빛 크기(행)] 슬라이더 추가. 기존 [퍼짐] 은
//                           각도 기반이라 이제 원본 카드(conic)에만 해당한다 —
//                           라벨을 '퍼짐 (카드 각도)' 로 바꿨다.
//                       ※ 원본 카드(BorderGlow)는 그대로 conic 이다. 비율이 온건해서
//                          v7.315 수정 뒤 의도대로 동작한다. 필요하면 같은 방식으로 바꾼다.
// v7.315 (2026-08-07) — 🐛 BorderGlow — 테두리로 갈수록 빛이 '꺼지던' 문제
//                       증상: 카드 가장자리로 마우스를 옮기면 밝아져야 하는데 반대로 사라짐.
//                       원인: 근접도를 '보이는 카드' 가 아니라 '바깥 패딩 박스' 로 쟀다.
//                         .border-glow-card 는 padding: glowRadius(26px) 로 투명 여백을 두르고,
//                         보이는 카드와 빛나는 링은 그 안쪽에 있다.
//                         그런데 getEdgeProximity 는 여백까지 포함한 rect 로 계산했다.
//                         → 데모 카드(420×227, pad 26) 실측:
//                              위치                근접도   밝기(감도30)  밝기(감도15)
//                              정중앙                0.00      0.00        0.00
//                              보이는 위쪽 테두리     0.77      0.24        0.00   ← 여기가 최대여야 함
//                              보이는 좌측 테두리     0.88      0.59        0.17
//                              바깥(안 보이는) 여백   1.00      1.00        1.00
//                           최대 밝기가 아무것도 안 보이는 여백에서 나왔다.
//                           감도를 낮추면 카드 위에서는 아예 0 이라 완전히 꺼졌다.
//                       조치: 기준을 보이는 카드의 반폭/반높이로 바꾼다.
//                             중앙 0 → 보이는 테두리 1.0 → 바깥 여백도 1.0 유지(클램프).
//                             각도는 그대로 중심 기준 (방향은 어차피 같다).
//                       곁들여: onPointerLeave 추가. 기존엔 leave 처리가 없어 마지막 값이
//                             얼어붙었고, 인트로 스윕 뒤 남아 있던 빛이 커서를 올리는 순간
//                             중앙의 낮은 값으로 뚝 떨어져 '가까이 가니 꺼진다' 로 보이기도 했다.
//                             (행용 useRowGlow 에는 원래 있던 처리다. 행은 패딩이 없어
//                              근접도 계산도 원래부터 맞았다 — 이번 수정은 카드 전용)
// v7.314 (2026-08-07) — 🐛 업로드가 조용히 아무것도 안 하던 버그 (v7.312 회귀)
//                       증상: 작업파일을 올려도 무반응, 메모에 이미지를 붙여도 안 보임.
//                             에러도 안 떴다.
//                       원인: <input type="file"> onChange 에서 순서를 뒤집어 썼다.
//                           const fl = e.target.files;   // 라이브 FileList (input 소유)
//                           e.target.value = '';         // ← 이 순간 목록이 비워진다
//                           doUpload(fl);                // fl.length === 0 → 즉시 return
//                         value='' 는 '선택된 파일 목록을 비운다'는 뜻이고,
//                         files 는 같은 FileList 인스턴스를 계속 돌려주므로
//                         미리 잡아둔 참조까지 같이 빈다.
//                       조치: Array.from() 으로 먼저 복사한 뒤 value 를 비운다.
//                         ★ v7.177 의 작업사진 첨부(handleFileChange)는 원래 이 순서였고
//                           그래서 계속 정상 동작 중이었다. 새 코드만 어긋나 있었다.
//                         · 파주 재고 업로드(v7.309)는 files?.[0] 로 File 객체를 꺼내 써서
//                           같은 실수에도 영향이 없었다. File 은 독립 객체다. 패턴만 통일.
//                       곁들여 정리:
//                         · PUT 실패 시 상태코드별 안내를 붙였다 (405=dav_methods,
//                           413=크기, 403=권한, 500=create_full_put_path)
//                         · 이미지 판정에 확장자 폴백 추가 — file.type 이 빈 문자열로 오는
//                           환경(일부 안드로이드·HEIC)에서 첨부가 거부됐다
//                         · 드롭존의 숨은 input 클릭이 부모로 버블링돼 onClick 이 다시
//                           click() 을 부르던 재진입을 stopPropagation 으로 차단
// v7.313 (2026-08-07) — 🗂 게시판을 [공유 게시판] / [작업파일 공유] 두 탭으로
//                       v7.312 는 파일 패널을 게시판 오른쪽에 폭 330 사이드바로 붙였는데,
//                       메모 카드 칸이 그만큼 좁아지고 파일 목록도 한 줄에 하나뿐이었다.
//                       탭으로 갈라서 각자 전체 폭을 쓰게 한다.
//                         · 코르크보드 상단 라벨 자리가 탭 2개로 바뀐다 (각각 건수 뱃지)
//                         · [메모 기록] · [작게/크게] 는 메모 탭에서만 보인다 (파일 탭과 무관)
//                         · 파일 탭은 fullWidth — 목록이 auto-fill 카드 그리드로 깔리고
//                           썸네일·아이콘도 커진다. 총 용량은 헤더로 올렸다
//                         · 마지막으로 본 탭을 localStorage 에 기억한다
//                       ★ 파일 패널은 '항상 마운트' 해 두고 display 로만 숨긴다.
//                         조건부 렌더로 끄면 메모 탭에 있는 동안 목록을 못 읽어서
//                         탭 뱃지의 건수가 0 으로 남는다. onCount 콜백으로 건수를 올려 받는다.
// v7.312 (2026-08-07) — 📎 메모 이미지 첨부 + 작업파일 공유란
//                       ⚠ SQL 필요: migrations/v7.312-memo-images-shared-files.sql
//                          안 돌려도 메모 탭은 뜬다 (첨부/공유만 안내 문구와 함께 막힘).
//                       ⚠ nginx.conf / docker-compose.yml 은 손댈 필요 없다.
//                          `location ^~ /attachments/` 가 이미 PUT/DELETE 를 받고 2GB 까지 허용한다.
//                          (v7.180 에서 `^~` 를 붙여 둔 덕분에 png/jpg 도 regex 에 안 걸린다)
//                       ① 메모 카드에 [🖼] 버튼 — 이미지 첨부. 썸네일이 본문 아래에 깔린다.
//                          클릭하면 라이트박스로 원본, ×로 삭제, 다운로드 버튼 제공.
//                          저장 경로 /attachments/memo_<메모id>/, 목록은 memos.images(jsonb).
//                          · memos 는 optimistic lock 이라 updateFields 로 간다.
//                            충돌하면(다른 사람이 편집 중) 저장 실패로 알리고, 이미 올라간
//                            파일은 되돌려 지운다 — 안 그러면 어디에도 안 걸린 고아가 남는다.
//                          · 새 메모 작성 폼에는 붙이지 않았다. 메모가 생기기 전에는
//                            memo_<id> 경로가 없어서다. 저장 후 카드에서 첨부하면 된다.
//                       ② 게시판 오른쪽에 [작업파일 공유] 패널 (src/SharedFilesPanel.jsx).
//                          이미지·zip·pdf·워드·엑셀 등 형식 제한 없이 올리고 받는다.
//                          드래그앤드롭 / 다중 선택 지원, 확장자별 아이콘, 이미지 썸네일.
//                          저장 경로 /attachments/shared/, 메타는 shared_files 테이블.
//                          · 파일명이 한글이면 safeName 정규식에 전부 '_' 가 되어 서로
//                            덮어쓴다 → 경로는 timestamp+랜덤으로 유일하게 만들고
//                            원본 이름은 DB 에 따로 보관한다. 다운로드는 download 속성으로
//                            원본 한글명 그대로 저장된다 (동일 출처라 동작한다).
//                          · AttachmentService.uploadToDir 신설. 기존 upload(txId,…) 는
//                            tx_<id> 로 경로가 고정돼 재사용이 안 돼서 따로 뒀다. 기존 함수 무변경.
//                       ③ App.js 가 23,000줄이라 패널은 별도 .jsx 로 뺐다
//                          (MobileScan/EventCalendar 와 같은 방식).
// v7.311 (2026-08-06) — 🐛 킵 이중 차감 — 가용이 음수로 내려가던 문제
//                       증상: TS-H1283XU-RP-E2136-32G  재고 0 · 킵 1 · 가용 -1.
//                             재고에서 킵을 잡은 건데 가용이 재고보다 작아질 수 없다.
//                       원인: 킵 수량을 두 번 뺐다.
//                         · v7.156 부터 킵에 '시리얼을 지정하면' 그 시리얼의
//                           inventory.status 가 'reserved' 로 바뀐다.
//                         · getInStockProductCounts / countInStock 은 status='in_stock' 만 센다.
//                           → 시리얼 지정 킵은 재고 집계에서 이미 빠져 있다.
//                         · 그런데 가용 = 재고 − 활성킵(qty 총합) 이었다. 한 번 더 뺀 것이다.
//                         시리얼 없이 수량만 잡은 킵은 in_stock 에 남아 있어서 정상이었다.
//                         그래서 어떤 제품은 맞고 어떤 제품은 틀린, 헷갈리는 증상이 됐다.
//                       조치: activeKeepsUnbackedByModel 신설 —
//                             킵 행마다 max(0, qty − 지정된 시리얼 수) 를 합산한다.
//                             '아직 재고에서 안 빠진' 킵 수량이다. 가용은 이것만 뺀다.
//                         · [킵] 열은 지금처럼 총 수량을 보여준다 (마우스를 올리면 내역 설명)
//                         · 킵 등록/품목 추가의 가용재고 검증 2곳도 같은 기준으로 정정.
//                           기존엔 가용이 실제보다 작게 나와 헛경고가 떴다
//                         · serials 컬럼이 jsonb / text[] / JSON 문자열 어느 쪽이어도 파싱되게 했다
//                       ⚠ 전제: v7.156 백필 SQL 이 실행돼 있어야 한다.
//                          안 돼 있으면 v7.156 이전에 만든 킵의 시리얼이 아직 in_stock 이라
//                          이번엔 반대로 가용이 과대 계상된다.
//                          migrations/v7.311-keep-reservation-check.sql 로 점검·보정할 것.
// v7.310 (2026-08-06) — 🔧 v7.309 후속 — 유형 표기 원위치 + [신품만 보기] 수량 반영
//                       ① 유형 표기 (신품/AS/리페킹/조립출고) 를 다시 모델명 아래로.
//                          v7.309 에서 재고 숫자 아래로 옮겼더니 좁은 칸에서 줄이 갈리고
//                          우측 정렬된 숫자와 뒤엉켜 오히려 읽기 어려웠다. 원래 자리로 되돌린다.
//                          재고 칸 폭도 104px → 70px 로 원복.
//                       ② [신품만 보기] 가 목록만 거르고 수량은 전체를 보여주던 문제.
//                          예: TR-002 (신품 24, 리페킹 2, 조립출고 2) 인데 재고가 28 로 표시.
//                          이제 켜면 재고 = 신품 수량, 가용 = 신품 − 킵 이 된다.
//                          rows 단계에서 stock 을 바꾸므로 정렬·0개 숨기기·색상 판정이 모두 따라온다.
//                          · 켠 동안에는 breakdown 을 숨긴다 — 남는 게 신품 하나뿐이라
//                            바로 옆 숫자를 되풀이하는 꼴이 된다
//                          · 요약 카드(재고/가용/0개/가용부족)는 토글과 무관하게 전체 기준 유지.
//                            그래서 원본 수량을 stockAll 로 따로 남겼다
// v7.309 (2026-08-06) — 📊 재고 대시보드 — 파주물류센터 열 추가 + 표기 개편
//                       ⚠ SQL 필요: migrations/v7.309-paju-stock.sql (paju_stock 테이블)
//                          미실행이어도 대시보드는 정상 동작한다 (파주 열이 전부 '-' 로만 보임).
//                       ① 그리드 컬럼: 모델명 · 재고 · [파주] · 킵 · 가용
//                          파주는 별도 창고이므로 가용(= 재고 − 킵) 계산에 넣지 않는다.
//                       ② 유형 표기 (신품/AS/리페킹/조립출고) 를 모델명 아래 → 재고 숫자 아래로.
//                          숫자와 그 구성이 세로로 붙어 읽힌다. 재고 칸을 104px 로 넓혀
//                          대부분 한 줄에 들어가게 했다. 0인 유형은 계속 생략.
//                       ③ [신품만 보기] 토글 — 재고에 '신품' 유형이 하나라도 있는 제품만 남기는
//                          목록 필터다. 숫자 자체는 전체 기준 그대로 둔다.
//                       ④ [파주 재고 업로드] 버튼 — ERP [전체매장 재고조회 … 자사재고] 파일.
//                          ★ 확장자는 .xls 지만 실제 내용은 EUC-KR HTML <table> 이다.
//                            SheetJS 로는 안 열려서 HTML 표 파싱이 주 경로고,
//                            진짜 xlsx 를 올릴 때를 대비해 SheetJS 폴백을 둔다.
//                          · 열 위치는 헤더에서 '상품명'/'파주' 를 찾아 동적으로 잡는다.
//                            ERP 설정에 따라 매장 열이 늘 수 있어서다. 못 찾으면 B/D 로 폴백.
//                          · 스냅샷이므로 업로드마다 전량 교체(delete → insert).
//                            수량 0·빈칸 행은 저장하지 않는다 (196행 중 41행만 남는다).
//                          · 기준일은 제목의 "2026년08월06일 마감기준", 없으면 파일명 YYYYMMDD.
//                       ⑤ 매칭은 제품명 정확 일치 우선, 실패 시 정규화(대문자·하이픈/공백 제거) 2차.
// v7.308 (2026-08-06) — ⚡ 카탈로그에 없는 제품 자동 외부 처리 + 품목 추가 즉시 반영
//                       ① [외부 처리로 추가] 체크박스 제거. 카탈로그(products) 조회로 자동 판정.
//                          · 직접 입력한 이름이 products 에 없으면 → external:true,
//                            EXT- 임시 시리얼이 자동으로 붙어 스캔 없이 출고까지 간다
//                          · 검색 결과에서 고른 제품은 재고 행이 실제로 있으므로 외부 아님
//                          · v7.74 원칙 유지 — '카탈로그엔 있고 재고만 0' 은 외부로 만들지 않는다
//                            (진짜 시리얼을 받아야 하는 제품이다)
//                          · 카탈로그 조회가 실패하면 외부로 단정하지 않는다.
//                            가짜 EXT- 시리얼이 기록되는 쪽이 더 위험하다
//                       ② 품목을 추가하고 [제품 출고] 를 눌러도 새 품목이 안 보이던 문제.
//                          원인: v7.73 의 재초기화 스킵.
//                            `activeRequest.reqId === reqId && pendingSerials.length > 0`
//                            이면 DB 재조회를 통째로 건너뛰어 models 가 옛날 값 그대로였다.
//                            외부/면제 품목은 열자마자 EXT-/NOSN- 이 pending 에 깔리므로
//                            사실상 항상 이 조건에 걸렸다 → 새로고침해야만 반영.
//                          조치: 스킵을 없애고 항상 최신 items 를 다시 읽는다.
//                            스킵의 원래 목적(스캔분 유실 방지)은 keepPending 으로 대체 —
//                            메모리에 있던 pendingSerials 를 복원 소스 중 최우선으로 둔다.
//                            새로 추가된 외부/면제 품목의 자동 시리얼은 품목별 잔여 수량만큼만
//                            보충해서 중복 부여를 막는다.
// v7.307 (2026-08-06) — ⚡ 배송시트지 [품목 추가] — DB에 없는 제품을 모달에서 바로 추가
//                       기존: 모달을 띄워 놓고 [수동 입력] 을 누르면 브라우저 prompt() 창이
//                             또 떠서, 검색창에 친 이름을 그 창에 다시 쳐야 했다.
//                       변경: 검색창 입력값을 그대로 쓴다. prompt() 제거.
//                         · 입력값과 정확히 같은 검색 결과가 없으면
//                           〔"TEST1111" 그대로 추가〕 버튼이 검색창 바로 아래에 뜬다
//                         · Enter 로도 동일 — 정확히 일치하는 결과가 있으면 그 제품을,
//                           없으면 입력값 그대로 적용 (대소문자 무시 비교)
//                         · "검색 결과가 없습니다" 에 다음 행동 안내를 붙였다
//                           (막다른 길로 보여서 [수동 입력] 을 못 찾는 경우가 있었음)
//                       [외부 처리로 추가] 체크박스는 그대로 함께 적용된다.
//                       제품명 '변경' 모드도 같은 모달이라 동일하게 동작한다
//                       (현재 이름과 같으면 버튼을 숨겨 무의미한 변경을 막음).
// v7.306 (2026-08-06) — ⚡ 출고 스캔 — 수량 채워지면 다음 품목으로 자동 이동
//                       기존: TS-233 (1/1) 을 찍어도 화면이 그대로 머물러서,
//                             다음 품목 탭을 마우스로 클릭 → 입력칸을 다시 클릭
//                             → 그제서야 스캔. 품목마다 손이 두 번 갔다.
//                       변경: handleRequestScan 성공 직후 현재 품목 충족 여부를 보고
//                             다음 '미완료' 품목으로 currentModel 을 전환하고
//                             시리얼 입력칸에 포커스까지 넣는다. 스캐너만으로 연속 진행.
//                         · 순환 탐색 — 현재 탭 다음부터 돌아서 처음 걸리는 미완료 품목
//                         · 외부(EXT-)·S/N면제(NOSN-) 품목은 건너뛴다
//                           (열 때 이미 자동 충족되고 입력칸 자체가 숨겨져 있음)
//                         · 전환 시 이전 모델의 재고 후보 드롭다운을 비운다
//                           (안 비우면 남은 목록에서 엉뚱한 시리얼이 선택될 수 있음)
//                         · 마지막 품목까지 끝나면 전환 없이
//                           "모든 품목 스캔 완료. [제품 출고]를 누르세요"
//                       탭을 손으로 클릭했을 때도 입력칸에 커서가 가도록 통일.
//                       포커스 ref 는 두 개다 — 출고 페이지 패널과 배송시트지 모달이
//                       같은 UI 를 각각 그리고 있어서, 모달이 열려 있으면 모달이 우선.
// v7.305 (2026-08-06) — 🐛 입고 등록 개수가 2배로 표시되던 버그
//                       증상: TS-233 하나에 시리얼 7개를 찍었는데
//                             [등록 현황] · [완료] 버튼이 "2개 제품 · 14개 시리얼".
//                       원인: 활성 배치를 두 번 셌다.
//                         const regSerials = activeBatch?.serials || [];
//                         → regBatches[regActiveIndex].serials 를 '가리키는' derived 값.
//                           (구 구조 호환용 shim. 별도 작업 버퍼가 아니다)
//                         그런데 카운트는  regBatches 전체 + regSerials  를 더했다.
//                       실제 저장은 handleRegister 가 regBatches 만 보므로 정상이었고,
//                       표시만 틀렸다. 등록된 재고에는 영향 없음.
//                       조치: regTotalProducts / regTotalSerials 를 파생 상태로 신설하고
//                             [등록 현황] 배지와 [완료] 버튼이 같은 값을 쓰게 통일.
//                             기준을 handleRegister 의 validBatches 와 일치시켰다
//                               - 시리얼 0개 배치는 제품 수에서 제외
//                               - 입력 중인 시리얼은 전체 배치와 중복 검사 후 +1
//                                 (기존엔 활성 배치만 검사해 타 배치와 중복 시 과다 계상)
// v7.304 (2026-08-06) — 🎯 인식 박스 보정 — 실측값을 '기본값'으로 (기종별 조절은 유지)
//                       v7.303 계측으로 원인과 계수를 확정했다.
//                         cp y : 1174 1437 1682 1820 1949   (줌 2x·2x·2x·1x·3x)
//                         실제 y: 1224 1428 1631 1712 1797
//                         최소자승  실제 = 0.746·cp + 355   잔차 ≤ 21px / 2122 (1%)
//                       줌이 달라도 한 직선 → 줌 무관한 고정 성질.
//                       가로도 같은 기울기(0.717~0.756) → 화면 중심 기준 균일 축소.
//                       ★ 원인: 0.742 = 2992 / 4032
//                          분석 버퍼(2992x2992)는 센서(4032x3024)의 중앙 크롭인데
//                          프리뷰는 센서 전체 폭을 쓴다. 화각이 다르다.
//                          플러그인 normalizeCornerPoints 는 둘이 같다고 가정한다.
//                          (역산한 프리뷰 영역 4032x2052 = 20:9, 화면비와 일치)
//                       조치: DEFAULT_BOX_ADJ = { sx .75, dx +13.00, sy .75, dy +13.25 }
//                          실기 확인 — cp y 1702 -> 박스 상단 1668 / 실제 막대 1674 (6px)
//                          1x·3x 양쪽에서 정렬됨.
//                       ⚠ 이 값은 기기 고유다. 2992 도 4032 도 기기가 정하고
//                          화면비에 따라 프리뷰 크롭 폭도 달라진다.
//                          -> 슬라이더는 그대로 두고 [기본값] / [무보정] 두 버튼을 뒀다.
//                          저장값이 있으면 기본값이 덮지 않는다 (사용자 조절 우선).
//                       MobileLookup 은 세로 보정만 있었다 -> 가로(sx/dx)도 추가.
//                       근본 해결(UseCaseGroup + ViewPort)은 판독률 리스크로 보류 — HANDOFF §3.
//
// v7.303 (2026-08-05) — 📏 남은 세로 오차를 '계산으로' 확정하기 위한 계측 + 잔상 축소
//                       v7.302 로 가로는 맞았다 (실측: 박스 폭 400±4px, 바코드와 겹침).
//                       세로는 박스가 막대가 아니라 문자열을 덮고 높이가 2배쯤 된다.
//                       ★ 그런데 스크린샷 픽셀만으로는 확정할 수 없었다.
//                          - 라벨이 기울어져 있어 네 꼭짓점의 외접 사각형은 원래보다 커지는 게 정상
//                          - 영상 계측(1.36배)과 정지화면(약 2배)이 서로 안 맞았다
//                          -> 여기서 계수를 추측해 넣으면 v7.301 의 실수를 반복한다.
//                       조치 (1) 원시 cornerPoints 범위를 진단에 그대로 표시한다
//                          📏 CODE x 123~456 y 789~1011
//                          화면에서 그 바코드가 실제로 있는 자리와 짝지으면
//                          선형 보정계수(배율·이동)가 연립방정식으로 바로 나온다.
//                       조치 (2) 박스 유지 700ms -> 300ms
//                          손각대에서 700ms 는 길다. 카메라가 움직이면 옛 좌표의 박스가
//                          새 화면에 남아 좌표 오차처럼 보인다.
//                          실제로 '바코드 0' 인 프레임에 박스 7개가 떠 있었고,
//                          그 잔상 때문에 영상 계측이 오염됐다.
//
// v7.302 (2026-08-05) — 📐 인식 박스 — 전제가 틀렸다. cornerPoints 는 이미 화면 픽셀이다.
//                       (모바일 v0.2.6 동반)
//                       ★ v7.301 은 방향이 반대였다. 실기 녹화에서 박스 8개가 전부
//                          화면 왼쪽 0~13% 안에 납작하게 뭉쳐 있었다.
//                       ★ 근거 — 상류 플러그인 원본을 열었다 (@capacitor-mlkit 6.2.0)
//                          BarcodeScannerHelper.normalizeCornerPoints(cp, imageSize, screenSize)
//                            scale = max(screenH/imageW, screenW/imageH)
//                            x = cp.x * scale - invisibleWidth/2
//                            y = cp.y * scale - invisibleHeight/2
//                          플러그인이 이미 cover 환산을 마쳐서 준다. 웹이 한 번 더 하고 있었다.
//                          v7.238 의 "cornerPoints 는 카메라 이미지 픽셀" 이 틀린 전제였다.
//                       ★ 픽셀 계측으로 확인 (1080x2340 화면 / 2992x2992 이미지)
//                          웹이 하던 환산  x*0.782 - 630  ->  화면 0~1080 이 -630~+215px 로 접힘
//                          예측: 박스 오른쪽 끝이 x=138px 부근에 몰린다
//                          실측: 박스 8개 전부 x 0~138~161px      -> 일치
//                       ★ 왜 예전에는 그럭저럭 맞아 보였나
//                          pickPreset 이 1080x2340(=화면 크기)을 고르는 순간에만
//                          환산이 항등식이 된다. maxObserved 가 자라며 다른 프리셋으로
//                          갈아탈 때마다 박스가 튀었다. 어긋남과 튐이 같은 뿌리였다.
//                       조치: 환산을 없앤다. 나누기 하나가 전부다.
//                          screenRectToPct(rect, screenW, screenH) = rect / screen * 100
//                          기준 화면 크기는 플러그인이 screenWidth/screenHeight 로 보낸다
//                          (모바일 v0.2.6). 구 APK 면 innerWidth*devicePixelRatio 로 추정.
//                       - 해상도 프리셋 선택 UI 폐기 (고를 것이 없다). 읽기전용 표시로 교체
//                       - IMG_PRESETS / pickPreset / imageRectToScreenPct 는 참고용으로만 잔존
//                       - MobileLookup.jsx 도 같은 전제(v7.206)라 함께 고쳤다
//                       - [위치 보정] 자동 초기화 (inv_scan_adj_basis = 'screenpx')
//                         남는 오차는 프리뷰 인셋뿐이므로 슬라이더는 유지한다
//                       - 실측 프레임 크기(2992x2992)는 좌표에 쓰지 않고 진단 표시로만 남김
//
// v7.301 (2026-08-04) — 📐 인식 박스 좌표를 '추정'에서 '실측'으로 (모바일 v0.2.5 동반)
//                       증상: 네이티브 스캔 중 박스가 바코드에 안 붙고 크기가 프레임마다 튐.
//                       ★ 원인은 하나였다 — 분석 프레임이 2992x2992 정사각으로 들어온다.
//                          (v7.296 진단 imageInfo 로 확인. 삼성 정사각 센서 모드로 보인다)
//                          IMG_PRESETS 에는 정사각이 없다 → pickPreset 의
//                            p.w >= maxX && p.h >= maxY
//                          를 만족하는 항목이 하나도 없어 마지막 폴백 1080x1920(16:9)로 떨어졌다.
//                          (1) 1:1 좌표를 16:9 로 환산   -> 위치가 어긋난다
//                          (2) maxObserved 가 자라는 동안 고르는 프리셋이 바뀐다
//                              1280 -> 1920 -> 2340 -> 2560 구간을 지나며 120ms 루프가
//                              setAutoPreset 을 다시 부른다  -> 박스 크기가 튄다
//                          두 증상이 한 원인으로 설명된다.
//                       조치: 추정을 폐기하고 네이티브가 알려주는 실제 크기를 쓴다.
//                          native : barcodeScanned 의 imageWidth/imageHeight (모바일 v0.2.5~)
//                                   규격 무관, 감지마다 온다. 이것이 본 경로다.
//                          diag   : verifierDiag 의 imageInfo "2992x2992 rot90" 를 파싱
//                                   Code 39 경로에서만 오므로 보조. 구 APK 폴백용.
//                          우선순위 = 수동 프리셋 선택 > 실측 > 종전 자동 추정
//                          (수동 선택은 실측이 틀린 기기를 위한 탈출구로 남긴다)
//                       ⚠ 회전: ML Kit cornerPoints 는 회전 보정된 좌표계인데
//                          imageInfo 는 회전 전(raw) 값이다. rot90/270 이면 스왑한다.
//                          이 기기는 정사각이라 티가 안 나지만 다른 기기에서는 반드시 갈린다.
//                          검증용으로 raw 크기와 회전각도 함께 받아 진단에 표시한다.
//                       ⚠ 기존 [위치 보정] 값은 '틀린 프리셋'을 손으로 상쇄해 둔 값이라
//                          실측 좌표계에서는 그 상쇄가 그대로 오차가 된다.
//                          -> 기준이 바뀌는 첫 순간 한 번만 자동 초기화한다
//                             (inv_scan_adj_basis = 'measured'. 이후 조절값은 유지)
//                       ROI 판정도 같은 기준(resolveImg)을 쓰므로 함께 정확해진다.
//
// v7.300 (2026-08-04) — 🔧 ZXing 정정값 '채택' 시작 (자동 교정 + 이력 표시)
//                       지금까지는 경고만 띄우고 큐에는 틀린 값이 그대로 들어갔다.
//                       오독인 걸 알면서 저장하는 상태였다.
//                       ★ 확정된 근거 (실기 누적)
//                          V7GG3E8H <- 07GG3E8H / R7GG3SBLA / V-GG3E8H   (오독 3종이 수렴)
//                          V7GG051H <- H700051H                          (라벨 인쇄로 검증)
//                          V7GETBRH <- V7GETBRL / V7GETBRK
//                          ZXing 값은 전부 V7...H 형태. 틀리는 쪽이 일관되게 ML Kit 이다.
//                       채택 조건 (하나라도 어긋나면 교정하지 않고 ML Kit 값 유지)
//                          (1) ZXing 이 읽어냈고 ML Kit 값과 다를 것
//                          (2) structuralCheck 통과 (쓰레기 바코드 유입 방지)
//                          (3) 제품별 시리얼 형태(길이/접두어/패턴)에 맞을 것
//                          -> 못 읽은 건은 손대지 않는다. 최악의 경우에도 이전과 같아질 뿐이다.
//                       ★ 소급 교정이 필수인 이유
//                          ZXing 디코드는 백그라운드라 결과가 몇 프레임 뒤에 온다.
//                          그 사이 오독값은 이미 슬롯/큐에 들어가 있다.
//                          -> 슬롯 · 큐 · 검수대기를 모두 소급해서 고친다.
//                            정정값이 이미 있으면 중복이 되므로 해당 항목을 비운다/제거한다.
//                       ★ 유입 매핑도 함께 건다 (zxFixRef)
//                          ML Kit 은 계속 같은 오독을 낸다. 유입 단계에서 안 바꾸면
//                          고쳐놓은 자리에 오독이 곧바로 다시 들어온다.
//                          settledRef 에 오독값을 잠가 슬롯 재점유도 막는다.
//                       표시: 오버레이(스캔 중) + 본문 카드(정지 후). 원래 값을 취소선으로 남긴다.
//                       해상도: 2992x2992 유지. 1280x720 요청을 기기가 무시한다(삼성 정사각 센서 모드).
//                          판독률은 이미 확보됐고(못읽음 99.4%->64%) 화소도 충분하므로 더 쫓지 않는다.
//                       SQL 마이그레이션: 없음
// v7.299 (2026-08-04) — 📐 분석 해상도 2992x2992 → 1280x720 (표준 16:9)
//                       v7.298 실기 결과: 못읽음 99.4% -> 85.6%. 해상도가 원인인 것 확인.
//                       ★ 그런데 1920x1080 을 요청했는데 2992x2992 가 왔다.
//                          setTargetResolution 은 '요청'이고 기기가 가장 가까운 지원값을 준다.
//                          정사각형이라 문제가 셋:
//                            - 16:9 프리뷰와 화면비 불일치 -> 인식 박스 좌표가 밀린다
//                            - 프레임당 900만 화소 -> ML Kit 처리 지연
//                            - 앱의 해상도 추정(자동 1080x1920)과 어긋난다
//                          -> 표준 16:9 로 내린다. 640x480 대비 3배 화소면 충분하다.
//                            개선분의 핵심은 '초고해상도'가 아니라 '640 탈출' 이었다.
//                       ★ 불일치 37 > 일치 20 이지만 나쁜 신호가 아니다.
//                          H700051H -> V7GG051H  (라벨 인쇄로 확인된 진짜 값은 V7GG051H)
//                          V7006DBH -> V700WDBH
//                          5AKKC1WH -> V7GG11WH
//                          ZXing 값은 전부 V7...H 형태인데 ML Kit 값은 앞자리가 깨져 있다.
//                          틀리는 쪽이 일관되게 ML Kit 이다.
//                          = ML Kit 이 지금까지 알려진 것보다 훨씬 많이 틀리고 있었다는 뜻.
//                       다음: ZXing 값 채택(길이/구조 필터 통과 시에만). 이 버전 결과 확인 후.
//                       ! 이 버전도 아직 '보고'만 한다. 큐에는 ML Kit 값이 들어간다.
//                       SQL 마이그레이션: 없음
// v7.298 (2026-08-04) — 📐 분석 프레임 640x480 → 1920x1080 (교차검증 성공률)
//                       ★★ 가설이 실기에서 확정됐다 ★★
//                          ZXing 1866 · 일치 7 · 불일치 5 · 못읽음 1854
//                            V7GETBRL -> V7GETBRH
//                            V7GETBRK -> V7GETBRH      <- 서로 다른 오독이 한 값으로 수렴
//                            07GG3E8H -> V7GG3E8H      <- 앞자리 0->V 교정
//                          ML Kit 이 제각각 틀리게 읽은 것을 ZXing 이 같은 값으로 바로잡았다.
//                          실제 시리얼은 전부 H 로 끝나므로 ZXing 쪽이 맞다.
//                          우연으로는 이런 수렴이 나오지 않는다. 5개 버전 만의 확정.
//                       ★ 그런데 못읽음이 99.4% 였다. 원인은 진단 줄의 '640x480'.
//                          CameraX ImageAnalysis 기본 해상도다. 플러그인이 목표 해상도를
//                          지정하지 않아 그대로 쓰였다. 화면의 '자동 (1440x2560)' 은
//                          좌표 매핑용 추정치일 뿐 실제 분석 이미지가 아니다.
//                          640x480 에서 바코드 하나를 잘라내면 조각이 100px 도 안 된다.
//                          -> 오히려 12건이나 읽어낸 것이 놀랍다.
//                       조치 (플러그인 오버라이드 = 모바일 v0.2.3, APK 재빌드 필요)
//                          (1) setTargetResolution(1920x1080)
//                              ! '요청'이다. 기기가 못 주면 가장 가까운 값이 온다.
//                                실제 적용값은 진단 줄에서 확인할 것.
//                          (2) 못 읽은 값의 재시도 상한 12회. verified 에 안 들어가서
//                              매 프레임 재시도되던 것이 1854회 낭비의 실체였다.
//                              진단 오버레이에 '포기' 카운트 추가.
//                       ! 아직 '보고'만 한다. 자동 채택은 못읽음 비율이 내려간 뒤 판단한다.
//                       SQL 마이그레이션: 없음
// v7.297 (2026-08-04) — 👁 진단을 '볼 수 있는 자리'로 옮김 (네이티브 오버레이 안)
//                       ★ v7.296 의 설계 실수 — 진단을 페이지 본문에 두었다.
//                          그런데 네이티브 모드에서는 스캔 오버레이(createPortal, zIndex 999999)가
//                          화면 전체를 덮는다. 스캔 중에는 본문에 스크롤로 도달할 수 없고,
//                          정지하면 phase 가 바뀌어 진단 블록 자체가 사라진다.
//                          → 어떤 방법으로도 볼 수 없는 자리였다. 사용자가 찾다가 못 찾았다.
//                       조치
//                          ① 진단을 네이티브 오버레이 상단 스트립 안으로 이동.
//                             스캔 중 화면 위에 항상 떠 있다.
//                          ② ref 는 리렌더를 유발하지 않으므로 zxStats state 를 신설하고
//                             기존 500ms 동기화 인터벌에 얹었다.
//                       교훈: 정보를 어디에 두느냐가 정보를 만드느냐만큼 중요하다.
//                          네이티브 모드 UI 를 추가할 때는 오버레이 안인지 본문인지 먼저 확인할 것.
//                       SQL 마이그레이션: 없음
// v7.296 (2026-08-04) — 🔬 교차검증이 '어느 단계에서' 멈추는지 계측 (진단 전용)
//                       실기: 네이티브 ML Kit 모드 정상 · 22건 인식 · 웹 v7.295 · node_modules 에
//                       ZXingVerifier.java 존재. 그런데 barcodeVerified 이벤트가 한 번도 안 옴.
//                       ★ 문제는 v7.295 코드가 실패를 전부 삼킨다는 것이었다.
//                          Code 39 가 없어서 안 도는 건지, 자르다 예외가 나는 건지,
//                          APK 에 코드가 안 들어간 건지 구분할 방법이 없었다.
//                          → 추측으로 고치면 빌드 왕복이 반복된다. 실패를 보이게 만든다.
//                       조치
//                          ① Code 39 가 0개여도 submit() 을 호출한다.
//                             '안 도는 것'과 '대상이 없는 것'을 구분해야 한다.
//                          ② verifierDiag 이벤트 신설 — 단계별 카운트를 초당 2회 보고.
//                             바코드수 / Code39수 / 제출 / 캐시건너뜀 / 크기제외 / 복사오류 / 예외메시지
//                             + 이미지 크기·회전각 + ML Kit 이 준 포맷 코드 목록
//                          ③ 복사 루프의 catch 가 조용히 continue 하던 것을 카운트+메시지 보고로 변경
//                          ④ 진단 줄은 checked>0 조건 없이 '항상' 표시.
//                             이벤트 자체가 안 오는 경우를 화면에서 구분해야 하므로.
//                       판정
//                          '이벤트 없음'      → APK 에 교차검증 코드가 없다 (clean 재빌드)
//                          Code39 0           → ML Kit 이 Code 39 로 분류하지 않는다 (포맷코드 확인)
//                          제출 0             → 캐시/크기 필터가 다 걸렀다
//                          복사오류 > 0       → 회전 매핑 좌표가 범위를 벗어난다
//                       SQL 마이그레이션: 없음
// v7.295 (2026-08-04) — 🤝 ML Kit + ZXing 교차검증 (네이티브 플러그인 패치)
//                       방향 전환. WASM 웹 경로 추적을 중단하고 네이티브에서 두 디코더를 함께 쓴다.
//                       ★ 왜 — zxing-wasm 은 zxing-cpp 포트이고, 이 라벨을 정확히 읽는 앱이
//                          쓰는 것은 Java ZXing 이다. 이름만 같고 구현이 다르다.
//                          WASM 을 살려내도 목표(오독 감소)에 도달 못 할 수 있었다.
//                       ★ 근거 — capawesome/capacitor-mlkit Discussion #123 에 동일 사례가 있다.
//                          Code 39 정확도 문제 → 메인테이너 '우리가 할 수 있는 게 없다' →
//                          보고자가 ZXing 계열로 바꾸니 매번 성공. 우리 기기만의 문제가 아니다.
//                       설계
//                          ML Kit = 찾기 (프레임당 20개 위치 + cornerPoints)
//                          ZXing  = 읽기 (그 자리만 잘라 하나씩 재판독)
//                          Code 39 는 체크digit 이 없어 오독이 자체 검증되지 않는다.
//                          독립된 디코더 둘이 일치하면 그것이 사실상 체크digit 이다.
//                       ⚠ '의심스러운 것'을 고르지 않는다 — 이 오독은 어느 각도에서 봐도
//                         정상으로 보인다(8자·구조통과·매번 최다득표). 의심 기준을 세울 수 없다.
//                         대신 '아직 확인 안 된 것'을 고르고, 확인이 끝나면 다시 안 읽는다.
//                         Code 128 은 mod-103 자체검증이 있으므로 대상에서 제외한다.
//                       구현: 새 플러그인을 짓지 않고 @capacitor-mlkit 6.2.0 을 패치한다.
//                          BarcodeScanner.java 가 이미 ImageAnalysis.Analyzer 라
//                          analyze(ImageProxy) 안에 원본 프레임이 들어와 있다.
//                          카메라·프리뷰·권한·손전등·줌·좌표는 기존 코드를 그대로 쓴다.
//                          patches/@capacitor-mlkit+barcode-scanning+6.2.0.patch (patch-package)
//                       ⚠ 이번 버전은 '보고'만 한다. ZXing 값을 자동 채택하지 않는다.
//                         회전 매핑이 틀리면 엉뚱한 값이 나올 수 있고, 그걸 채택하면
//                         멀쩡한 시리얼이 오염된다. 일치율을 먼저 보고 채택 여부를 정한다.
//                       진단: [ZXing 교차검증] 줄 — 확인/일치/불일치/못읽음 + 잘라낸 조각 크기.
//                          확인 0 = 패치 안 된 빌드 · 못읽음만 쌓임 = 자른 자리가 틀림
//                       SQL 마이그레이션: 없음
// v7.294 (2026-08-04) — 🕵 ZXing 0건 — 캔버스 캡처 자체를 검증한다 (진단 전용, 동작 변경 없음)
//                       실기 보고: v7.293 배포 후 [인식범위 제한]을 껐는데도 여전히 인식 0.
//                       → v7.293의 유력 가설이던 'ROI 크롭'은 반증됐다. 크롭이 원인이 아니다.
//                       ★ 아직 한 번도 검증 안 된 구간이 남아 있다 — video → canvas 캡처.
//                          네이티브 ML Kit 은 카메라를 '직접' 읽는다. canvas 를 거치지 않는다.
//                          따라서 'ML Kit 은 잘 읽힌다'는 사실이 canvas 캡처가 정상이라는
//                          증거가 전혀 못 된다. 웹 경로만 video → canvas → getImageData → ZXing.
//                          안드로이드 WebView 에서 drawImage(video) 가 검은 프레임을 내는 사례가 있고,
//                          그 경우 증상이 지금과 정확히 일치한다:
//                          디코더 정상 동작 · 94ms 소요 · 예외 없음 · 빈 배열.
//                       ⚠ 추측으로 코드를 더 쌓지 않는다. 이번 버전은 '보는 수단'만 만든다.
//                       조치
//                          ① 진단에 '프레임 내용' 줄 추가 — 디코더에 넘긴 이미지의 휘도
//                             평균/최소/최대/대비. 최대 2000점만 샘플링(0.1ms 미만).
//                             대비 < 25 면 '거의 단색 — 캔버스에 영상이 안 담기고 있습니다' 확정 표시.
//                          ② [🖼 입력 프레임 보기] 버튼 — ZXing 이 받은 그림을 그대로 띄운다.
//                             숫자보다 확실하고 스크린샷으로 바로 공유된다.
//                          ③ 전체 프레임 폴백 경로도 같은 통계를 남긴다(그쪽이 최종 입력이므로).
//                       판정 기준
//                          검게 나온다 → 캔버스 캡처 실패. ZXing 옵션·해상도는 무관. 캡처 경로를 바꾼다.
//                          정상으로 보인다 → 캡처는 무죄. getUserMedia constraints(해상도·초점)로 넘어간다.
//                       SQL 마이그레이션: 없음
// v7.293 (2026-08-04) — 🖼 ZXing 0건 — 디코더 입력 진단 + 전체 프레임 폴백
//                       실기: 요청 포맷 '전체(Code39 포함)' · WASM ✓ · 디코드 94ms · 그런데 인식 0.
//                       ★ 먼저 배제한 것 — 옵션·포맷은 정상이다.
//                          합성 Code 39 이미지를 만들어 '우리 옵션 그대로' 로컬에서 디코드했더니
//                          정상 판독됐다 (Code39만 / formats 생략 도 모두 성공).
//                          → 라이브러리·옵션 문제가 아니라 '넘기는 이미지' 문제다.
//                       ★ 유력 원인 — ROI 크롭.
//                          ZXing 은 ML Kit 보다 잘린 이미지에 민감하다. 바코드 양옆의
//                          조용한 여백(quiet zone)이 크롭에 잘려나가면 아예 못 읽는다.
//                          Code 39 는 start/stop 문자(*)가 있어 여백 손실에 특히 취약하다.
//                          ROI 를 바코드에 딱 붙여 잡으면 이 현상이 난다.
//                          ⚠ 'WASM 응답 0개 코드'는 예외가 아니라 '정상적으로 빈 배열'이다.
//                            (예외였다면 lastDecodeCount 가 -2 로 표시된다) — 이 구분이 진단의 핵심이었다.
//                       조치
//                          ① 진단에 '디코더 입력' 줄 추가 — 실제로 넘기는 크기와 크롭 여부.
//                             무엇을 넘기는지 안 보이면 계속 추측만 하게 된다.
//                          ② 크롭본이 0건이면 '전체 프레임'으로 한 번 더 디코드한다.
//                             살아나면 진단 줄에 '전체 프레임 폴백으로 N회 인식 — 크롭이 원인입니다'.
//                             ⚠ 매 프레임 두 번 돌리면 느려지므로 0건 + 크롭 중일 때만.
//                       SQL 마이그레이션: 없음
// v7.292 (2026-08-04) — 🔎 진단 줄에 '실제 요청 포맷' 표시 + Code128 전용 경고
//                       사용자 실기: ZXing 이 정상 로드(WASM ✓)됐는데 205프레임 인식 0.
//                       ★ 원인 — [Code128 전용]이 켜진 상태였다.
//                          decodeWithZXingWASM 이 formats 를 ['Code128','QRCode','DataMatrix'] 로
//                          넘기므로 Code 39 라벨은 아예 후보에서 빠진다. 흔들린 게 아니라 0건이었다.
//                       ★ 왜 못 알아챘나 — 진단 줄의 '네이티브 지원 포맷'에 code_39 가 보였다.
//                          그건 BarcodeDetector 의 '능력 목록'이지 실제 요청 포맷이 아니다.
//                          화면이 오히려 오해를 키웠다.
//                       조치
//                          ① '요청 포맷' 줄 신설 — 전체(Code39 포함) / Code128 전용 을 명시.
//                             Code128 전용이면 'Code 39 라벨은 인식되지 않습니다' 를 빨간 글씨로.
//                          ② 기존 줄은 '기기 지원 포맷(참고)' 로 이름을 바꿔 오해를 줄인다.
//                          ③ 최근 읽힌 규격이 Code 39 인데 [Code128 전용]을 켜면 확인창을 띄운다.
//                       ⚠ 이 설정은 앞서 진단 목적으로 켜보라고 안내했던 것이라 기기에 남아 있었다.
//                          진단용 토글이 그대로 남아 다음 실험을 오염시킨 사례다.
//                       SQL 마이그레이션: 없음
// v7.291 (2026-08-03) — 📦 ZXing 자체 호스팅 — 'WASM 상태 ✗ 실패' 해결
//                       사용자 실기 화면에 원인이 그대로 찍혀 있었다:
//                          WASM 상태: ✗ 실패 (네이티브 폴백) · 스캐너: Native BarcodeDetector
//                       → ZXing 을 고르긴 했지만 로드에 실패해 결국 ML Kit 계열로 되돌아갔다.
//                          디코더가 바뀌지 않았으므로 v7.289 의 비교 자체가 무효였다.
//                       ★ 실패 원인 두 겹
//                          (1) v7.290 이 넣은 첫 소스 /vendor/zxing-wasm/reader/index.js 가
//                              실제로는 없는 파일이었다. 리포에 사본이 없다.
//                              나머지 두 소스는 CDN 이라 외부망이 막히면 함께 실패한다.
//                          (2) ★ 스크립트를 자체 호스팅해도 WASM 은 별도로 받는다.
//                              라이브러리 내부 locateFile 기본값이
//                              https://fastly.jsdelivr.net/npm/zxing-wasm@3.1.0/... 이다.
//                              index.js 만 올려두면 스크립트는 뜨는데 디코딩에서 다시 막힌다.
//                              이 함정을 못 보면 '올렸는데 왜 안 되지'가 된다.
//                       조치
//                          ① public/vendor/zxing-wasm/reader/ 에 실제 파일 동봉
//                             index.js (38KB) + zxing_reader.wasm (1.04MB) — npm zxing-wasm@3.1.0
//                             Dockerfile 이 COPY public/ 하므로 빌드하면 /vendor/... 로 서빙된다.
//                          ② 자체 사본으로 로드됐을 때 setZXingModuleOverrides 로
//                             locateFile 을 같은 폴더로 돌린다 → WASM 도 자체 서버에서 받는다.
//                             (CDN 으로 로드된 경우는 건드리지 않는다 — 그때는 CDN 이 살아있다는 뜻)
//                       ⚠ 배포 시 src/ 만이 아니라 public/ 도 함께 올려야 한다.
//                          이번 버전은 public/vendor/ 가 새로 생겼다. 안 올리면 증상이 그대로다.
//                       ⚠ 이것으로 '오독이 줄어드는지'는 아직 모른다. 이제야 비교가 가능해진 것이다.
//                          ZXing 이 실제로 로드됐는지는 진단 줄의 'WASM 상태' 로 확인할 것.
//                       SQL 마이그레이션: 없음
// v7.290 (2026-08-03) — 🩺 ZXing 경로가 앱에서 아예 안 켜지던 원인 수정
//                       사용자 보고: v7.289 의 [ZXing] 을 골랐더니 아예 인식이 안 된다.
//                       ★ 원인 ① — startCameraWeb 첫 줄의 가드.
//                          if (bdError) { alert(bdError); return; }
//                          bdError 는 'BarcodeDetector API 없음' 일 때 설정된다.
//                          안드로이드 WebView 에는 이 API 가 없다 → 카메라가 켜지기도 전에 차단.
//                          ⚠ 이 가드는 zxing-wasm 도입(v7.136) '이전' 에 만들어진 것이다.
//                            실제 디코딩은 ZXing 이 하므로 BarcodeDetector 는 필수가 아닌데,
//                            가드만 옛날 전제로 남아 있었다. v7.289 를 만들 때 이걸 못 봤다.
//                          → ZXing 이 ready/loading 이면 진행. 둘 다 실패일 때만 막는다.
//                       ★ 원인 ② (가능성) — ZXing 을 CDN 한 곳에서만 받는다.
//                          cdn.jsdelivr.net 이 막히면 디코더가 통째로 없어진다.
//                          → 소스를 3단계로: 자체 서버(/vendor/zxing-wasm/…) → jsdelivr → unpkg
//                            첫 항목은 nginx 에 사본을 올려두면 외부망 없이도 동작한다 (권장).
//                            onerror 에서 다음 소스로 넘어간다.
//                       ★ 진단 수단 추가 — 지금까지는 어디서 막혔는지 알 방법이 없었다.
//                          설정 > 바코드 디코더 에 '디코더 상태' 표시
//                            ⏳ 로드 중 / ✓ ZXing 준비됨 / ✕ ZXing 로드 실패
//                          카메라 실패 시에도 앱/브라우저를 구분해 안내하고
//                          '[ML Kit] 으로 되돌리세요' 를 함께 알린다.
//                       ⚠ 여전히 WebView 자체가 getUserMedia 를 막는 기기가 있을 수 있다.
//                          그 경우는 앱 안에서 ZXing 을 쓸 수 없다 — ML Kit 로 되돌려야 한다.
//                          판별은 '디코더 상태'로 한다: ✓ 인데 카메라만 안 열리면 WebView 문제다.
//                       SQL 마이그레이션: 없음
// v7.289 (2026-08-03) — 🧩 앱 안에서 디코더 선택 (ML Kit ↔ ZXing)
//                       사용자 확인: ZXing 계열 앱(QR Scanner)은 같은 바코드를 안정적으로 읽는다.
//                          인식범위를 좁혀도 증상이 같았으므로 영역 문제가 아니라 '디코더' 차이다.
//                       ★ 그동안 앱에서는 ML Kit 로 고정돼 있어 비교조차 불가능했다.
//                          isNative(Capacitor 브릿지 감지) 하나로 경로가 갈렸고 선택 UI 가 없었다.
//                          MobileScan 에는 웹 경로(zxing-wasm)가 이미 있는데 앱에서 못 쓰고 있었다.
//                       변경: isNativePlatform(감지값) 과 isNative(실제 경로) 를 분리.
//                          forceWebDecoder 가 켜지면 앱 안에서도 웹 경로로 간다.
//                          isNative 를 쓰는 23개소가 전부 '실제 경로' 값을 참조하므로
//                          오버레이·video 요소·정리 로직이 일관되게 따라온다.
//                       ★ 왜 결과가 다를 수 있나 — Code 39 는 문자마다 바 굵기로 해독한다.
//                          ML Kit 은 속도 위주로 한 번에 판정하고,
//                          ZXing 은 여러 스캔선 결과를 모아 재시도하는 편이라
//                          손상·흐림이 있는 라벨에서 굵기 판정이 더 버틸 수 있다.
//                          (G 의 굵은 바가 가늘게 읽히면 0 이 된다 — 실기 V7GG051H 사례)
//                       UI: 설정 > 🧩 바코드 디코더 (앱에서만 노출)
//                          카메라 실행 중에는 전환을 막는다 (스트림이 꼬인다)
//                          ZXing 선택 시 경고 문구 — WebView 의 getUserMedia 지원은 기기를 탄다.
//                          안 켜지면 ML Kit 으로 되돌리라고 안내. 줌·손전등은 동작 안 할 수 있다.
//                       ⚠ 이건 '해결'이 아니라 '비교 수단'이다. 실제로 나아지는지는 실기 확인이 필요하다.
//                          효과가 없으면 이 라벨은 손상이 한계를 넘은 것이고 직접 입력이 답이다.
//                       SQL 마이그레이션: 없음
// v7.288 (2026-08-03) — 🗳 다수결 투표 — 같은 바코드가 읽을 때마다 값이 바뀌는 문제
//                       ★ 먼저 정정 — 이전 두 진단이 모두 틀렸다.
//                          (1) v7.287 '판독 규격 합의': 오독이 다른 규격으로 읽힌다고 가정했다.
//                              → 사용자 확인 결과 라벨이 Code 39 이고, 정답도 오독도 전부 CODE_39.
//                                전제가 무너져 아무것도 못 거른다. 이번에 걷어냈다.
//                          (2) '이어붙임(splice)' 진단: V700051H 를 두 바코드가 이어붙은 것으로 봤다.
//                              → 실기에서 세 번째 변형 V70G051H 가 나오면서 아님이 밝혀졌다.
//                                V7GG051H / V70G051H / V700051H = G 가 0 으로 읽히는 문자 혼동.
//                                Code 39 는 문자당 바 굵기로 해독하는데, 손상·흐림으로
//                                굵은 바가 가늘게 판정되면 다른 글자가 된다.
//                       ★ v7.272 'N회 일치'가 왜 뚫렸나
//                          값이 여러 갈래로 흩어지면 각 변형이 '따로따로' N회를 채운다.
//                          그래서 정답과 오답이 둘 다 등록됐다 (실기 목록에 4·7번, 5·8번 쌍).
//                       조치: 개별 카운트 → '한 바코드에서 나온 후보들끼리 경쟁'.
//                          같은 길이 · 2글자 이하 차이면 같은 바코드로 보고 한 묶음으로 센다.
//                          묶음 합계가 N회 이상이고 1위가 2위보다 앞서면 1위를 채택,
//                          나머지는 버린다. 채택값으로 교체해서 등록한다
//                          (지금 읽힌 값이 오답일 수 있으므로).
//                          변형이 있었으면 '🗳 채택(N표) · 탈락 …' 로 알린다 — 대조가 필요할 수 있다.
//                       검증
//                          흔들리는 3종 → 정답만 채택, 오독 2종 탈락
//                          깨끗한 바코드 → 2회로 즉시 통과 (기존과 동일)
//                          1글자 차이 '진짜' 시리얼 2개(V7GG3NMH·V7GG3NRH) → 각각 정상 등록
//                            ※ 이게 핵심 안전조건이다. 묶음 판정이 서로 다른 시리얼을
//                              합쳐버리면 재고가 사라진다. 시간차를 두고 들어오면 안 합쳐진다.
//                       유지: suspectMisread (v7.287 작업) — 투표를 뚫고 들어온 경우의 2차 방어.
//                          슬롯에 주황 표시로 남아 정지 후에도 대조할 수 있다. 자동 삭제는 안 한다.
//                       제거: 판독 규격 합의 로직 · 세션 규격 분포 UI
//                       ⚠ 근본 한계 — Code 39 는 체크섬이 없어 오독을 '스스로' 걸러내지 못한다.
//                          투표는 정답이 더 자주 나온다는 가정 위에 있다.
//                          바코드가 심하게 손상돼 오답이 더 자주 나오면 오답이 채택된다.
//                          그 경우는 라벨 대조가 유일한 답이다 (v7.286 증거사진 참고).
//                       SQL 마이그레이션: 없음
// v7.287 (2026-08-03) — 🔤 판독 규격 합의 — 반복되는 오독 차단
//                       사용자 보고(실기, 라벨 대조): v7.272 와 '똑같은' 오독 2건이 또 나왔다.
//                          V7GHAPGH → V7GHA7GH (P→7)  ·  V7GG051H → V700051H (GG→00)
//                          정상값과 오독값이 둘 다 목록에 있다 = 같은 바코드가 때때로 틀리게 읽힌다.
//                       ★ 내 전제가 틀렸다. v7.272 는 '오독은 무작위라 같은 값이 두 번 안 나온다'를
//                          근거로 N회 확인을 넣었다. 그런데 이 오독은 재현된다.
//                          손상 패턴이 고정돼 있으면 같은 오답이 반복되므로 N회로는 못 막는다.
//                       ★ 새 단서 — Code128 은 체크섬이 있어 한 글자 오독은 '거부되어야' 한다.
//                          통과했다는 건 그 판독이 Code128 이 아니었을 가능성이 크다.
//                          Code39·ITF 는 전체 체크섬이 없어 손상된 바가 그대로 다른 글자가 된다.
//                       조치: 세션 내 '판독 규격 합의'.
//                          한 카톤의 시리얼은 같은 규격으로 인쇄돼 있다.
//                          주 규격이 3건 이상 쌓인 뒤, 그와 다른 규격으로 읽힌 코드는 제외한다.
//                          ⚠ 3건 이상일 때만 판단 — 초반에 잘못 잠기면 안 된다.
//                          ⚠ 규격 정보가 없으면(웹 경로) 검사를 건너뛴다 — 없다고 막으면 안 된다.
//                          검증: 실기 시나리오 재현 — 오독 2건만 차단, 오탐 0건.
//                                같은 규격만 있을 때 0건 차단, 초반 3건 미만 0건 차단도 확인.
//                       설정 > 오독 방지에 '이번 세션 판독 규격' 분포 표시.
//                          규격이 섞이면 경고 문구. 원인 진단의 근거가 화면에 남는다.
//                       ⚠ 이 조치는 '오독이 다른 규격으로 읽힌다'는 가정 위에 있다.
//                          같은 Code128 으로 읽히면서 틀리는 것이라면 이걸로도 못 막는다.
//                          그 경우 [Code128 전용] + 오독방지 3회로도 안 되면 라벨 교체가 답이다.
//                       SQL 마이그레이션: 없음
// v7.286 (2026-08-03) — 📷 라벨 증거사진 — 나중에 대조하기 위한 기록
//                       사용자 목적: '이 시리얼이 정말 이 박스에 있었나' 를 나중에 대조.
//                       ★ 화면 자동 캡처가 아니라 '실물 라벨 사진'으로 만들었다. 두 가지 이유:
//                          (1) 네이티브 스캔 중에는 카메라 프리뷰가 WebView '뒤'의 네이티브 뷰다.
//                              JS 로 캡처하면 투명한 빈 이미지가 나온다.
//                              플러그인 추가 + APK 재빌드가 필요한데, 지금 배포는 src/ 만 올린다.
//                          (2) 대조 증거로는 앱 화면보다 라벨 원본이 낫다. 바코드·수량이 다 찍힌다.
//                       ★ '자동' 트리거도 넣지 않았다.
//                          v7.273 에서 박스 개수를 없앴으므로 '다 찍었다'를 판단할 근거가 없다.
//                          남은 방법은 'N초간 무감지'인데 라벨을 옮기거나 초점 잡는 중에도 발동한다.
//                          → [+ 촬영] 버튼 한 번. 오작동으로 엉뚱한 사진이 남는 것보다 낫다.
//                       구현
//                          · <input type="file" accept="image/*" capture="environment" multiple>
//                            시스템 카메라를 띄운다 → 플러그인 없이 웹·네이티브 양쪽 동작
//                          · 촬영본은 세션에 들고 있다가 [동기화] 시점에 NAS(WebDAV)로 업로드
//                            AttachmentService 재사용, 폴더 inbound_<날짜>_<ts>
//                          · 경로를 transaction_log.note 의 labelPhotos 배열에 기록
//                            (사진이 없으면 키 자체를 넣지 않는다 — 기존 note 파서에 영향 없음)
//                          · 배송시트지 상세 모달에 썸네일 표시, 클릭하면 새 탭에서 원본
//                            ★ 저장만 하고 볼 수 없으면 증거가 아니다. 보는 쪽까지 같이 만들었다.
//                       ⚠ 사진 업로드가 실패해도 입고는 진행한다. 증거 때문에 입고가 막히면 본말전도다.
//                          실패 장수만 알리고 넘어간다.
//                       ⚠ 동기화 후 세션의 사진을 비운다 — 다음 박스에 딸려가면 증거가 섞인다.
//                       UI 위치: 큐 목록 '위'. 스캔 직후 라벨을 아직 보고 있을 때 찍어야 한다.
//                          동기화 후에 찍으라고 하면 박스가 이미 치워져 있다.
//                       SQL 마이그레이션: 없음 (note 안에 저장 · attachments 는 기존 WebDAV 재사용)
// v7.285 (2026-08-03) — 🔢 '순수 숫자' 규칙 축소 — QTY 전용 차단 제거
//                       사용자 지적: QTY까지 막는 건 과하다.
//                       ★ 맞다. QTY 는 '20'·'0020' 로 2~4자라 길이 검사에서 이미 걸린다.
//                          같은 것을 두 군데서 막고 있었고, 대가로 숫자 시리얼을 전부 잃었다.
//                       변경: 순수 숫자 '전부' → EAN/UPC 길이(12·13·14)만
//                          라벨 라벨명도 '순수 숫자' → 'EAN/UPC' 로 (무엇을 막는지 드러나게)
//                       ⚠ 8자리는 넣지 않는다 — 과거에 밟은 지뢰다.
//                          v7.132 가 8/12/13/14 를 막았다가 '실제 시리얼이 걸리는 케이스'가 나와
//                          v7.133 에서 되돌린 기록이 코드 주석에 남아 있다.
//                          HDD 시리얼이 정확히 8자라 EAN-8 과 정면충돌한다.
//                       검증 (실측 라벨)
//                          QTY 20·0020            → 차단 (길이 2·4)
//                          EAN 4711103082331 등    → 차단 (EAN/UPC)
//                          UPC 763649174883        → 차단 (EAN/UPC)
//                          시리얼 V701AB5H·ZYD05G6A·QYC5801095E → 통과
//                          12345678 (8자리 숫자 시리얼) → 통과  ← 이전에는 차단됐다
//                          라벨 3종 회귀: 잘못 등록 0 / 누락 0
//                       SQL 마이그레이션: 없음
// v7.284 (2026-08-03) — 🔋 오독 방지 진행을 인식 박스에 게이지로 표시
//                       사용자 제안: 오독 방지할 때 박스를 게이지처럼 보이게.
//                       기존엔 '🔁 확인 중 1/2' 를 피드백 문구로만 알렸는데,
//                       문구는 다음 메시지에 금방 덮여서 지금 몇 번 읽혔는지 알 수 없었다.
//                       바코드를 겨눈 채로 '얼마나 찼는지'가 보여야 몇 번 더 비출지 판단할 수 있다.
//                       구현: ReadGauge — 박스 안을 왼쪽에서 오른쪽으로 채우고 우측에 n/need 표기.
//                          · 확인 중에는 박스 테두리·라벨도 청록 '확인중' 으로 전환
//                          · N=1(끔)이거나 이미 채워졌으면 표시하지 않는다
//                          · 범위 밖(outofroi) 코드는 확인 대상이 아니므로 게이지를 띄우지 않는다
//                          · 웹·네이티브 양쪽 박스에 동일 적용
//                       ⚠ 순수 표시 전용 — 판정·큐 어디에도 관여하지 않는다.
//                          판독 횟수는 기존 readTallyRef 를 읽기만 한다.
//                          네이티브는 120ms 박스 갱신 루프에서 reads 를 박스에 실어 보낸다.
//                       SQL 마이그레이션: 없음
// v7.283 (2026-08-03) — 🎯 범위 안 시리얼이 잘리던 문제 — 판정 완화 + 박스 유지
//                       사용자 보고(실기): 범위 안에 시리얼 4개가 보이는데 하나도 안 잡힌다.
//                       화면에 '범위 밖 7종 제외' · '박스 0'.
//                       ★ 진단 — 라벨의 비-시리얼 바코드는 4개뿐이다
//                          (PALLET·CARTON·EAN·UPC). 7종이 제외됐다는 건 시리얼 4개가
//                          그 안에 들어 있다는 뜻이다. 좌표 추정이 어긋나 있었다.
//                       ★ 왜 하필 지금 터졌나 — v7.268 에서 판정 기준 기본값을
//                          '전체 포함'으로 바꾼 것이 화근이다.
//                          네이티브 좌표는 preset 추정값이라 태생적으로 오차가 있는데,
//                          시리얼 바코드는 폭이 넓어 ROI 좌우 끝에 거의 닿는다.
//                          '전체 포함'은 한쪽 변만 살짝 넘어도 탈락시킨다.
//                          검증: 좌표 오차 8% 에서 중심점 4/4 통과 / 전체포함 0/4 — 증상과 일치.
//                       ① 판정 기준 기본값 'contain' → 'center' 로 되돌림.
//                          엄격하게 쓰고 싶으면 설정에서 고를 수 있다 (순서·라벨도 조정).
//                          ⚠ 기존 기기에는 'contain' 이 이미 저장돼 있다 → 일회성 완화 마이그레이션.
//                             단, 새 버전에서 일부러 고른 경우를 덮지 않도록 별도 플래그로 1회만 실행.
//                       ② ★ 더 큰 문제 — 범위 밖 코드의 인식 박스를 '지우고' 있었다 (v7.264).
//                          그래서 '박스 0' 이 되고, 앱이 바코드를 어디로 인식하는지 볼 수 없었다.
//                          → [위치 보정]으로 맞출 수단 자체가 사라진다. 손쓸 방법이 없는 상태.
//                          이제 지우지 않고 회색 '범위 밖' 박스로 남긴다.
//                          어긋난 정도가 눈에 보이므로 사용자가 직접 보정할 수 있다.
//                          (박스 갱신 루프가 매 틱 상태를 덮어쓰지 않도록 outofroi 는 예외 처리)
//                       SQL 마이그레이션: 없음
// v7.282 (2026-08-03) — 🧭 스캔 오버레이 정리 + 인식범위 조작성 개선
//                       사용자 요청 4건, 전부 실기 화면 기준.
//                       ① [🚀 ML Kit (Native)] 배지 제거 — 작업에 쓰이는 정보가 아니다.
//                       ② [🔀 자동 추가 모드 ON …] 문구 제거 — 설정에서 이미 확인된다.
//                       ③ 크기 조절을 네 모서리 전부에서.
//                          기존엔 우하단 ⤡ 하나뿐이라 박스를 위로 키우려면 이동+조절을 반복해야 했다.
//                          nw/ne/sw/se 모드 추가. west/north 면을 잡으면 반대편 변을 고정한 채 늘린다.
//                          ⚠ 손잡이를 박스 '안쪽'에 뒀다. 바깥으로 빼면 박스를 화면 끝까지 키웠을 때
//                             손잡이가 화면 밖으로 나가 잡히지 않는다.
//                       ④ 인식범위 박스가 상단/하단 버튼을 덮던 문제.
//                          박스를 최대로 키우면 줌·손전등·[스캔 정지]가 눌리지 않았다.
//                          ★ 컨테이너를 줄이는 방법은 쓸 수 없다 — ROI 값은 '화면 전체' 기준 비율이고
//                            판정식(v7.267)이 window 기준이라, 컨테이너를 줄이면 좌표계가 어긋난다.
//                          → 대신 값 자체를 안전 영역 안으로 clamp 한다.
//                            상단 스트립·하단 바의 실제 높이를 ref 로 재서 경계를 만든다.
//                            피드백 문구가 생기고 사라지며 높이가 변하므로 400ms 주기로 재측정.
//                          '전체' 프리셋과 [⟲ 초기화] 값도 세로 15~85% 로 조정.
//                          검증: 네 모서리 드래그 랜덤 30만회 — 안전영역 이탈 0건, 최소크기 8% 유지.
//                       ⑤ [⟲ 인식범위 초기화] 를 손전등 옆(상단 우측)으로 이동.
//                          박스 모서리에 붙어 있으면 박스를 키웠을 때 눌리지 않는다.
//                          박스 안내 문구도 '안쪽 끌어 이동 · 모서리로 크기' 로 갱신.
//                       SQL 마이그레이션: 없음
// v7.281 (2026-08-03) — 📏 제품 표준화 관리에 시리얼 길이 컬럼 (PC 인라인 편집 + 일괄 지정)
//                       사용자 요청: HDD 시리얼 8자 설정을 여기서도 할 수 있게.
//                       그동안은 모바일 스캔 화면에서만 지정할 수 있었다. 35개를 하나씩은 번거롭다.
//                       ① 카탈로그 표에 [시리얼 길이] 컬럼 추가 — 카테고리 셀과 같은 인라인 셀렉트
//                          미지정 / 6~16 / 20자. 미지정이면 회색 표시 (스캔 시 형태 학습으로 폴백)
//                          그리드 4열 → 5열 (1fr 170px 110px 140px 220px)
//                       ② 카테고리 필터가 걸려 있을 때 [일괄 지정] 카드 노출
//                          [8자][10자][11자][12자][해제] — 지금 보이는 목록에만 적용, 확인창 있음
//                          HDD 필터 → 8자 한 번이면 35개가 끝난다.
//                       ⚠ 접두어는 어디에서도 편집하지 않는다.
//                          접두어는 생산 로트를 따라가므로(WD 6TB=V7, 20TB=PPG)
//                          제품에 박아두면 다음 로트가 들어올 때 그 카톤이 전부 거부된다.
//                          길이만으로 실측 라벨의 쓰레기 바코드 7/7 이 걸러진다.
//                       ★ 마이그레이션 미실행 방어
//                          ProductService.list 가 serial_min_len/max_len 을 select 하는데,
//                          컬럼이 없으면 쿼리 자체가 실패해 카탈로그 화면이 통째로 안 뜬다.
//                          → 실패 시 기존 컬럼만으로 재시도한다 (noSerialCols 플래그).
//                            v7.277 SQL 실행 전에도 화면은 정상 동작한다.
//                       ProductService: updateSerialLen(id, len) 신규
//                          len=null 이면 지정 해제. 1~40 범위 검증 (0·41·문자열 거부 확인)
//                          min/max 를 같은 값으로 저장한다 — 지금은 '정확히 N자'만 쓴다.
//                       SQL 마이그레이션: v7.277-product-serial-format.sql (이미 실행했다면 불필요)
// v7.280 (2026-08-03) — 🚦 시리얼 판정 상태 표시 — '지금 무슨 기준으로 거르는지'
//                       사용자 요청: 학습 중일 때 무슨 작업 중인지 따로 표기해달라.
//                       ★ 기존 문제
//                          · 상태 패널이 hddMode 일 때만 떴다.
//                          · 제품 길이가 적용 중인지 학습 중인지 구분이 없었다.
//                          · 학습 중에는 시리얼이 '보류'되어 등록이 안 되는데
//                            그 사실이 화면에 안 드러나, 왜 큐에 안 담기는지 알 수 없었다.
//                       조치: 상태를 3가지로 명시해 스캔 화면(설정 아님)에 항상 띄운다.
//                          📐 제품 길이로 판정 중 — 8자 (제품명 · 학습 없이 바로 적용)
//                          ⏳ 형태 파악 중 — 2/3 (+ 진행 막대)
//                             '보류 중 N건 — 아직 등록되지 않았습니다' + 보류 시리얼 목록
//                          🔒 형태 확정 — 8자 · V7로 시작
//                          셋 다 아니면 표시하지 않는다 (제한 없는 상태에서 잡음 방지)
//                       ★ 탈출구 추가: [↩ 보류분 그대로 등록]
//                          시리얼이 3개 미만인 박스는 학습이 영원히 확정되지 않아
//                          보류만 쌓이고 등록이 안 된다. forceFlush() 는 서비스에 있었지만
//                          호출할 UI 가 없어 사실상 막다른 길이었다.
//                       피드백 문구도 진행이 드러나게: '보류 N건 · M개 더 찍으면 확정 — {코드}'
//                       SQL 마이그레이션: 없음
// v7.279 (2026-08-03) — 🔗 모바일 스캔에 제품 별칭 연결 (바코드 → 표준 제품명)
//                       사용자 지적: 기본 흐름은 '제품명 바코드 스캔'이고, 직접 선택은
//                                    박스에 바코드가 없을 때 쓰는 예외다.
//                                    등록되는 제품명은 제품 표준화 관리의 이름이어야 한다.
//                       ★ 확인해보니 구멍이 있었다 — MobileScan 이 AliasService 를 아예 안 썼다.
//                          classifyCodes 는 ProductService.findByNames(정확 매칭)만 보고 있었다.
//                          → 제품 표준화 관리에 별칭을 등록해도 모바일 스캔에서는 무시된다.
//                          → 박스 바코드가 카탈로그 이름과 조금만 달라도 제품으로 안 잡히고,
//                            하이픈이 없으면 '시리얼'로 오인되어 큐에 들어간다.
//                       조치: classifyCodes 에 별칭 단계 추가.
//                          판정 순서: 정확 매칭 → 리전 접미사 제거 → 별칭 → 하이픈(모델 후보) → 시리얼
//                          별칭이 맞으면 canonical 을 '별칭'이 아니라 표준 product_name 으로 세팅한다.
//                          (재고에 반드시 표준명이 들어가야 하므로. isCatalogConfirmed 도 true)
//                       신규: AliasService.lookupMany() — 한 프레임의 코드 여러 개를 한 번에 조회.
//                          ⚠ .in() 은 대소문자를 구분한다 → 원본/대문자/소문자 후보를 함께 넣고
//                            결과를 소문자 키로 정규화해 돌려준다.
//                       ★ 효과 (별칭 등록 시)
//                          0B36039(WD P/N)   시리얼 오인 → EXOS_SAS
//                          ST24000NM002H     시리얼 오인 → EXOS_SAS
//                          WD60EFPX          시리얼 오인 → IRON_WOLF
//                          V701AB5H          시리얼 (변화 없음)
//                       → WD 라벨처럼 모델 바코드가 없는 경우에도 P/N 바코드를 별칭으로 등록해두면
//                          제품명 스캔이 성립한다. 그때 제품의 시리얼 길이도 함께 잡힌다(v7.278).
//                       UI: 시리얼 길이 안내를 '바코드 먼저, 없으면 직접 선택'으로 수정.
//                           카탈로그 미등록 제품이면 경고 표시.
//                       SQL 마이그레이션: 없음 (product_aliases 테이블은 이미 존재)
// v7.278 (2026-08-03) — 🧹 형태 관리 일원화 — 제품별 길이만 남기고 카테고리 사전 폐기
//                       ★ 먼저 밝힐 것: 작업 폴더에 v7.276(카테고리 방식)과
//                          v7.277(제품 방식)이 섞여 있었다. 두 설계가 한 코드에 공존했다.
//                          v7.277 tarball 은 그 상태로 나갔으니 배포하지 말 것.
//                          이 버전에서 하나로 정리했다.
//                       사용자 결정: 제품명은 제품표준 관리 기준으로 직접 선택한다. QTY 대조는 불필요.
//                       → 제품 방식만 남기고 카테고리 사전을 걷어냈다.
//                          삭제: src/services/SerialFormatService.js 의 카테고리 API 전부
//                                migrations/v7.276-serial-formats.sql
//                                설정 화면의 제품군/제조사 셀렉트
//                       ★ 왜 '길이'만 쓰나 — 실패 유형이 둘로 갈리는데 서로 다른 장치가 필요하다.
//                          (A) 엉뚱한 바코드가 들어옴 — BOX ID·P/N·MLC·모델번호
//                              형태가 시리얼과 '다르다' → 길이 하나로 전부 걸린다.
//                              실측: 시리얼 4/4 통과 · 쓰레기 7/7 차단 (10·7·6·15·12·2·13자)
//                          (B) 진짜 시리얼이 잘못 읽힘 — V7GHAPGH → V7GHA7GH
//                              형태가 시리얼과 '똑같다' → 어떤 형태 규칙으로도 못 잡는다.
//                              여기엔 N회 확인뿐 (1회 오독 15% → 2회 0%).
//                          → 접두어·정규식·카톤 학습은 A 에 대해 길이 이상을 못 준다.
//                            권장 조합은 '제품 길이 + 오독 방지 2회' 둘이면 충분하다.
//                       판정 우선순위: 제품 길이 → (미설정이면) 카톤 학습 → 구조 필터
//                          길이가 없어도 스캔은 정상 동작한다 (기능만 꺼진다).
//                       UI: 설정 > 📐 시리얼 길이
//                          제품 선택 전엔 안내, 선택 후엔 현재 길이 표시
//                          + [8자][10자][11자][12자][방금 익힌 N자][지우기] 로 그 자리에서 저장
//                       ⚠ 접두어는 저장하지 않는다 (생산 로트를 따라간다 — WD 6TB=V7, 20TB=PPG)
//                       ⚠ 제품표준 관리(PC) 화면에는 길이 입력란이 아직 없다. 모바일에서만 저장 가능.
//                       SQL 마이그레이션: migrations/v7.277-product-serial-format.sql (이것 하나만 실행)
// v7.277 (2026-08-03) — 📐 제품 카탈로그에 시리얼 형태 — 제품 선택 = 형태 결정
//                       사용자 제안: 제품표준 관리에 저장된 제품명 기준으로 형태를 저장하면 편하다.
//                       ★ 맞다. v7.276 의 카테고리 방식보다 낫다:
//                          제품명은 스캔 중 어차피 고르는 값이다 (큐/동기화에 필수).
//                          제품군을 따로 고르게 하면 선택 단계만 늘어난다.
//                          카탈로그에 IRON_WOLF / EXOS_SATA / IRON_WOLF_PRO 처럼
//                          제품군이 이미 이름에 들어 있어 별도 분류 체계도 필요 없다.
//                       → v7.276 을 버리지 않고 '폴백'으로 남겼다. 3단 구조:
//                          ① products 의 제품별 형태          (신규, 1순위)
//                          ② serial_formats 의 카테고리 형태  (v7.276)
//                          ③ 카톤 학습 (같은 모양 3개)
//                          ④ 아무것도 없으면 구조 필터만
//                          위에서부터 값이 있는 첫 단계를 쓴다. 전부 없어도 스캔은 정상 동작.
//                       신규: migrations/v7.277-product-serial-format.sql
//                          products 에 serial_min_len / max_len / prefix / pattern 추가
//                          + category='HDD' 인 행을 8자로 일괄 채움 (기존 값이 있으면 건드리지 않음)
//                       ⚠ serial_prefix 는 비워 둔다 — 접두어는 생산 로트를 따라간다
//                          (WD 6TB=V7, 20TB=PPG). 제품에 박아두면 다음 로트가 전부 거부된다.
//                          길이만으로도 실측 라벨의 쓰레기 바코드 5/5 가 걸러진다.
//                       UI: 설정 > 시리얼 형태 사전에 출처 표시 (제품 / 카테고리 / 수동선택)
//                           + 선택된 제품에 [8·10·11·12자로 저장] · [방금 익힌 N자 저장]
//                           → 스캔하다가 그 자리에서 카탈로그에 값을 채울 수 있다.
//                       검증: 3단 폴백 우선순위 5케이스 · 실측 라벨 대조
//                             시리얼 3/3 통과 · 쓰레기 5/5 차단, 학습 0회
//                       ※ 제품표준 관리(PC) 화면의 [수정] 폼에 형태 입력란은 아직 없다.
//                          지금은 모바일 스캔 화면에서 저장한다. 필요하면 다음 버전에서 추가.
//                       SQL 마이그레이션: migrations/v7.277-product-serial-format.sql (직접 실행 필요)
// v7.276 (2026-08-03) — 📐 시리얼 형태 사전 (카테고리 > 제조사) — 학습 없이 첫 스캔부터
//                       사용자 제안: 제품별로 시리얼 형태를 미리 저장해두면 3번 찍을 필요가 없다.
//                       → 방향은 맞지만 두 가지를 바꿔서 구현했다. 근거는 아래.
//                       ★ (1) '제품별' → '카테고리 > 하위그룹(제조사)' 으로.
//                          시리얼 길이는 모델이 아니라 제조사·제품군의 성질이다.
//                          실측: WD 6TB · WD 20TB · Seagate 24TB 전부 8자.
//                          제품별이면 수백 행을 관리하고 새 모델마다 등록해야 한다.
//                          카테고리+제조사면 10여 행이면 되고 새 모델은 자동 상속.
//                          (사용자 제안 그대로 HDD>WD/Seagate/IronWolf, NAS, Switch … 구조)
//                       ★ (2) 접두어는 저장하지 않는다 (기본 NULL).
//                          실측에서 시리얼 접두어는 제품이 아니라 '생산 로트'를 따라간다:
//                            WD 6TB  MLC VL69G0 → 시리얼 V7…
//                            WD 20TB MLC PPLDE0 → 시리얼 PPG…
//                          같은 모델이라도 다음 로트가 들어오면 접두어가 바뀐다.
//                          제품에 박아두면 그 카톤 시리얼이 전부 거부된다 — 지금보다 나쁘다.
//                          검증: 새 로트(ZZQ1A2B3) — 길이만 저장 = 통과 / 접두어 저장 = 거부(작업 중단)
//                       ★ (3) '제품명 먼저 찍기 강제'는 넣지 않았다.
//                          WD 라벨에는 모델 바코드가 아예 없다.
//                          바코드가 붙은 건 BOX ID · P/N · QTY · MLC 넷뿐이고 MODEL 은 글자만 있다.
//                          강제하면 WD 카톤은 작업 자체가 불가능해진다.
//                          → 대신 설정에서 제품군을 고르는 방식. (P/N 을 제품 식별자로 쓰는 안은 다음 과제)
//                       신규: migrations/v7.276-serial-formats.sql · src/services/SerialFormatService.js
//                       ⚠ SQL 을 실행해야 동작한다. 미실행 시 조용히 비활성 →
//                          기존 '같은 모양 3개 학습' 으로 자동 폴백된다 (스캔은 정상 동작).
//                          UI 에도 미실행 상태를 안내한다.
//                       판정 우선순위: 저장된 형태 > 카톤 학습 > (없으면 구조 필터만)
//                       검증: HDD>WD(8자) 로 라벨 실측 대조 —
//                             시리얼 4/4 통과 · 쓰레기 6/6 차단, 학습 0회.
//                             깨진 정규식이 저장돼 있어도 스캔이 멈추지 않는 것 확인.
//                       SQL 마이그레이션: migrations/v7.276-serial-formats.sql (직접 실행 필요)
// v7.275 (2026-08-03) — 🔦 스캔 설정 진단 — 읽힌 규격 표시 + [제한 모두 끄기]
//                       사용자 보고(실기): HDD 가 갑자기 인식이 안 된다.
//                       ★ 배경 — v7.263~272 로 '막는 장치'를 4개나 얹었다.
//                          HDD 카톤 모드 / 인식범위(ROI) / 오독방지 N회 / Code128 전용.
//                          한꺼번에 켜면 어느 것이 막는지 사용자가 알 방법이 없다.
//                          기능을 늘린 만큼 '왜 안 되는지 보여주는' 장치가 필요했는데 빠져 있었다.
//                       ① 최근 읽힌 규격 표시 (설정 > 오독 방지)
//                          ML Kit 의 barcode.format 을 기록해 보여준다.
//                          Code128 계열이 아니면 빨간 글씨로 '[Code128 전용]을 끄세요' 안내.
//                          → 그 라벨이 Code39/ITF 인지 즉시 판별된다. 이게 없으면 짐작만 하게 된다.
//                       ② [⚡ 제한 모두 끄기] (설정 모달 상단)
//                          HDD 모드·ROI·Code128 전용을 끄고 오독방지를 1회로 되돌린다.
//                          ⚠ 시리얼 필터 규칙은 일부러 건드리지 않는다.
//                             사용자가 공들여 만든 것일 수 있고, 규칙이 원인인지는 시험기로 확인된다.
//                       ★ 증상별 원인 (문구로 구분된다)
//                          아무 반응 없음        → Code128 전용 (라벨이 Code39 계열)
//                          '🎯 범위 밖이라 제외'  → 인식범위
//                          '🔁 확인 중 1/2' 반복 → 오독방지 N회
//                          '형태가 다릅니다'      → HDD 카톤 모드
//                       ⚠ 작업 중 실수: 조건부 렌더 블록을 삽입하면서 닫는 ')}' 를 빠뜨려
//                          'Adjacent JSX elements' 오류. v7.273 과 같은 유형이다.
//                          문자열 치환으로 JSX 를 넣을 때는 여닫음을 항상 세어볼 것.
//                       SQL 마이그레이션: 없음
// v7.274 (2026-08-03) — 💬 '카톤 지문' → '시리얼 형태' 용어 교체
//                       사용자 지적: '지문'이 다른 직원 입장에서 와닿지 않는다.
//                       '카톤'은 창고에서 쓰는 말이라 문제 없지만 '지문'은 컴퓨터 쪽 비유다.
//                       → '시리얼 형태'로 통일. 문장이 자연스럽게 이어진다:
//                          형태 파악 중 / 형태 확정 / 형태가 다릅니다
//                       바뀐 화면 문구
//                          ⏳ 카톤 지문 학습 중 (N개 더)  → ⏳ 시리얼 형태 파악 중 (N개 더)
//                          🔒 카톤 지문 확정 — 8자·V7…    → 🔒 시리얼 형태 확정 — 8자·V7로 시작
//                          카톤 지문 불일치               → 형태가 다릅니다 (8자 · V7로 시작)
//                          ♻ 지문 초기화 (다음 카톤)      → ♻ 다시 익히기 (다음 박스)
//                          지문 확정 개수                 → 형태 확정에 필요한 개수
//                          지문 접두어 길이               → 앞글자 비교 자릿수
//                          '…로 시작' 의 말줄임표(…)도 뺐다 — 잘린 것처럼 보인다는 오해 방지
//                       ⚠ 코드 내부 식별자(cartonPrint·cartonFpRef·createCartonFingerprint)와
//                          개발자 주석의 '지문'은 그대로 뒀다. 화면 문구만 바꾼 것이라
//                          동작·검증 결과에는 변화가 없다 (라벨 3종 회귀 재확인: 오인 0 / 누락 0).
//                          식별자까지 바꾸려면 별도 리네이밍 작업으로.
//                       SQL 마이그레이션: 없음
// v7.273 (2026-08-03) — 🧹 모바일 스캔 UI 정리 — 설정을 톱니바퀴 모달로 분리
//                       사용자 요청: 설정류는 우측 상단 톱니바퀴로 빼고, 박스 개수와 사용 안내는 삭제.
//                       배경: v7.263~272 로 기능을 얹으면서 스캔 화면이 계속 길어졌다.
//                             정작 실제 작업(카메라·시리얼 입력)까지 스크롤이 멀어졌다.
//                       ① 헤더(RIVER NETWORK 우측)에 ⚙️ 버튼 → 하단 시트 모달
//                          옮긴 것: HDD 카톤 모드 · 오독 방지(N회 확인 + Code128 전용)
//                                   인식범위 제한(ROI) · 시리얼 필터 설정
//                          ★ 남긴 것 (의도적)
//                             · 자동 추가 / 검수 모드 — 작업마다 켰다 껐다 하는 운영 토글이다.
//                               모달에 넣으면 두 번 눌러야 해서 오히려 불편해진다.
//                             · 카톤 지문 상태 패널 — '학습 중 2/3' 같은 실시간 정보라
//                               스캔하면서 봐야 한다. 토글만 모달로 가고 상태는 화면에 남겼다.
//                       ② 박스 개수 프리셋 + 박스 진행률 제거
//                          expectedCount 는 null(무제한) 로 고정된다.
//                          ⚠ 관련 로직(resetForNextBox·boxComplete·BOX_PRESETS)은 남겨뒀다.
//                             되살릴 때 UI 만 다시 붙이면 되도록. 지금은 호출 지점이 없다.
//                       ③ 하단 '기본 사용법' 안내 블록 전체 제거
//                       ⚠ 작업 중 실수 기록: 사용 안내 블록을 통째로 잘라낼 때
//                          블록 뒤에 붙어 있던 '바깥 컨테이너 닫는 태그'까지 같이 지워져
//                          JSX 가 깨졌다(Unexpected token). 큰 블록을 인덱스로 잘라낼 때는
//                          잘라낸 구간의 여닫음 균형을 반드시 확인할 것. Babel 검사가 잡아줬다.
//                       SQL 마이그레이션: 없음
// v7.272 (2026-08-03) — 🔁 시리얼 오독 방지 — N회 일치 확인 + Code128 전용
//                       사용자 보고(실기): 시리얼이 잘못 기입된다. 카메라 문제인가?
//                       ★ 진단 — 라벨 사진과 스캔 결과를 대조했다. 카메라가 아니라 '디코딩 오류'다.
//                          V7GHAPGH → V7GHA7GH   (6번째 P→7, 1글자)
//                          V7GG051H → V700051H   (3~4번째 GG→00, 2글자)
//                          둘 다 8자·V7 시작 = 형태가 완벽히 정상이라
//                          구조 필터도 길이 지문도 절대 못 잡는다. 다른 층위의 문제다.
//                          라벨 사진상 테이프 주름·광택·매직펜 자국이 바코드를 지난다 → 마진한 판독.
//                       ① N회 일치 확인 (핵심)
//                          같은 값을 N번 읽어야 인정한다. 1회(빠름)/2회(권장)/3회(엄격).
//                          원리: 올바른 판독은 반복 재현되고, 오독은 무작위라 같은 값이 두 번 안 나온다.
//                          시뮬레이션(오독률 15%, 2만회): 1회 = 오독 15.00% / 2회 = 0.00% / 3회 = 0.00%
//                          ⚠ 구현 함정: 카운트는 lastCodeRef 가드보다 '먼저' 해야 한다.
//                             그 가드가 같은 코드의 연속 재처리를 막으므로,
//                             뒤에서 세면 영원히 1회에 머물러 아무것도 등록되지 않는다.
//                          8초 지난 집계는 폐기 (다른 라벨로 넘어갔는데 옛 카운트가 남으면 안 된다).
//                       ② Code128 전용 옵션
//                          기존엔 Code39·Codabar·ITF 까지 켜져 있었다. Code39 는 체크digit 이 선택이고
//                          ITF 는 부분 판독이 가능해, 훼손된 라벨에서 오독의 진원지가 된다.
//                          HDD 라벨은 Code128 이므로 끄면 안전하다. 웹·네이티브 양쪽 적용.
//                          (카메라 재시작 시 반영)
//                       ★ '유사 시리얼 경고'는 만들었다가 걷어냈다 — 실측으로 폐기.
//                          착상: 오독은 진짜 시리얼에서 한두 글자만 바뀐 값이니 경고하자.
//                          측정: 라벨의 진짜 시리얼 20개 중 11개가 서로 2글자 이하 차이였다.
//                                WD 시리얼은 순차 발급이라 원래 닮았다 (V7GG3NMH vs V7GG3NRH = 1글자).
//                          판단: 절반 이상에서 울리는 경고는 곧 무시된다. 없느니만 못하다.
//                          → MobileScan 상단에 이 근거를 주석으로 남겼다. 다시 넣고 싶어지면 재측정할 것.
//                       SQL 마이그레이션: 없음
// v7.271 (2026-08-03) — 🧰 시리얼 필터 — 사용자가 규칙을 직접 작성
//                       사용자 요청: 범용성 있게 사용자가 규칙을 쓸 수 있게.
//                       내장 규칙 6종은 실측 라벨 3종에서 뽑은 것이라 새 벤더는 못 막는다.
//                       ★ 정규식을 그냥 열어주지 않은 이유
//                          (1) 문법 오류 하나면 스캔 루프에서 '매 프레임' 예외가 난다
//                          (2) (a+)+ 류 중첩 수량자는 백트래킹이 폭발해 앱이 멈춘다
//                          → 구조화 규칙 6종을 기본으로, 정규식은 '고급' 항목으로 뒀다.
//                          → 정규식 방어 3중:
//                             · 저장 시 compile + 길이 200자 제한 + 중첩 수량자 heuristic 경고
//                             · 실제로 한 번 돌려 30ms 넘으면 '느림' 경고
//                             · 런타임 throw 는 그 규칙만 false 처리하고 스캔은 계속 (죽지 않는다)
//                          ※ 코드 길이가 6~20자로 제한돼 있어 폭발 반경 자체가 작다 (실측 0ms).
//                       규칙 타입: ~로 시작 / ~로 끝남 / ~를 포함 / 정확히 일치
//                                  길이가 N자 / 길이 N~M자 / 정규식(고급)
//                       ★ action 두 가지 — '예외'가 중요하다
//                          제외(block): 그 형태를 시리얼에서 뺀다
//                          예외(allow): 무조건 통과. 내장 규칙·길이 검사를 건너뛴다.
//                             → "우리 시리얼인데 내장 규칙에 잘못 걸린다" 를
//                               사용자가 코드 수정 없이 스스로 푸는 탈출구다. 이게 없으면
//                               내장 규칙이 오탐할 때 작업이 막히고 나한테 와야 한다.
//                       평가 순서: ① allow → ② 내장 6종 → ③ 길이 범위 → ④ block
//                       ★ 규칙 시험기 — 규칙만 만들 수 있게 하고 끝내면 안 된다.
//                          잘못 쓴 규칙은 '실제 스캔에서 시리얼이 조용히 사라진 뒤'에야 발견된다.
//                          바코드 값을 넣으면 통과/제외와 그 사유를 즉시 보여준다.
//                       설정 복사/붙여넣기(JSON): 설정이 브라우저별 localStorage 라 팀 공유가 안 된다.
//                          Supabase 테이블을 새로 만들지 않고 클립보드로 옮기게 했다.
//                       검증
//                          · 규칙 타입 12케이스 전부 기대와 일치 (대소문자 무시, 깨진 정규식 = false)
//                          · allow 규칙이 내장 BOX ID 차단을 무력화하는지 확인 (탈출구 동작)
//                          · JSON 왕복 후 판정 동일 · 비활성 규칙 무시 · 빈 값 규칙 무해
//                            (+ 규칙 추가 직후 빈 상태가 모든 코드를 막지 않는다)
//                          · 기존 라벨 3종 회귀 없음
//                       SQL 마이그레이션: 없음
// v7.270 (2026-08-03) — 🐛 시리얼 중복 등록 버그 수정 (큐)
//                       사용자 보고(실기 스크린샷): 대기 큐에 QYD6A01494C 가 두 번 들어갔다.
//                       ★ 원인 — queueRef.current 가 useEffect 로 동기화된다.
//                          useEffect(() => { queueRef.current = queue; }, [queue])
//                          useEffect 는 '렌더가 끝난 뒤' 실행되므로, 같은 tick 안에서
//                          appendToQueue 가 연달아 불리면 모든 호출이 '낡은' ref 를 보고
//                          중복 검사를 그냥 통과한다. 검사 코드는 멀쩡한데 보는 데이터가 옛것이다.
//                          실제로 터지는 자리 3곳:
//                            · 시리얼 슬롯 → [큐에 추가]   for 루프로 여러 건 연속 추가
//                            · 검수 대기 → [모두 확인]     forEach 로 여러 건 연속 추가
//                            · 네이티브 ML Kit 연속 감지   리스너가 빠르게 반복 호출
//                       ★ 조치
//                          ① appendToQueue 에서 queueRef.current 를 '즉시' 갱신
//                          ② setQueue 함수형 업데이트 안에서 2차 중복 검사
//                          ③ removeFromQueue / clearQueue / 동기화 성공 시에도 ref 즉시 반영
//                             — 안 하면 방금 지운 시리얼을 다시 스캔했을 때
//                               '이미 큐에 있음'으로 막힌다 (반대 방향 버그)
//                          ④ serial 은 trim 해서 저장 (앞뒤 공백 차이로 중복이 새는 것 차단)
//                       검증: 스크린샷과 같은 5건 배치를 같은 tick 에 연속 투입
//                             수정 전 중복 1건(QYD6A01494C) → 수정 후 0건. 재현·해소 확인.
//                       ⚠ 같은 패턴(ref 를 useEffect 로만 동기화 + 동기 반복문에서 참조)이
//                          다른 곳에도 있으면 같은 증상이 난다. pendingReviewsRef 도 동일 구조지만
//                          추가 경로가 단건이라 현재는 문제되지 않는다.
//                       SQL 마이그레이션: 없음
// v7.269 (2026-08-03) — ↔ 인식 박스 가로 보정 추가 (좌표 오차의 마지막 구멍)
//                       사용자 질문: "카메라 이미지 크기를 알면 오차를 줄일 수 있나?"
//                       ★ 계산해보니 답이 예상과 달랐다. 기록해 둔다.
//                          (1) 절대 크기는 아무 영향이 없다.
//                              cover 환산은 크기에 불변이다. 좌표도 이미지 크기에 비례해 커지므로
//                              720x1280 / 1440x2560 / 2160x3840 (같은 화면비) 로 추정하면
//                              결과가 완전히 같다 — 오차 0.0000%p. 검증함.
//                              → maxObserved 로 '크기'를 정확히 알아내려는 노력은 의미가 없다.
//                          (2) 영향을 주는 건 오직 '화면비'다.
//                              1080x1920(0.5625) 을 1080x2340(0.4615) 으로 잘못 잡으면 6.5%p,
//                              가로/세로가 뒤집히면 78%p 까지 벌어진다.
//                          (3) ★ 화면비 오차는 축마다 (배율·이동) 로 '정확히' 상쇄된다.
//                              top% = (offY + y*sc)/SH*100 은 y 에 대한 1차식이고,
//                              추정 화면비를 바꾸면 기울기(sc)와 절편(offY)만 변한다 = 아핀 변환.
//                              boxAdj(sy·dy) 가 바로 그 아핀 보정이라 세로는 이미 완전 보정 가능.
//                          (4) 그런데 가로에는 보정 수단이 없었다.
//                              1080x2340 을 1080x1920 으로 추정 시 가로 8.10%p 오차가 그대로 남는다.
//                              → 이게 v7.268 까지 남아 있던 잔여 오차의 정체다.
//                       조치: boxAdj 에 sx(폭 배율) · dx(좌우 이동) 추가.
//                          · 네이티브 인식 박스 렌더와 ROI 판정에 동일 적용 (v7.267 원칙 유지)
//                          · [위치 보정] 패널에 [좌↔] [폭] 슬라이더 2개 추가, 초기화도 4개 리셋
//                          · 기존 저장값에는 sx·dx 가 없으므로 { dx:0, sx:1, ...v } 로 하위호환
//                       검증: 실제/추정 화면비 4조합 × 위치 27점 최소제곱 피팅
//                          보정 전 최대오차 15.9~42.0%p → 보정 후 전부 0.000000%p
//                          = 화면비를 얼마나 틀리게 잡든 슬라이더 4개로 완전히 상쇄된다.
//                       ⚠ 뒤집어 말하면, 보정을 안 맞추면 ROI 도 그만큼 어긋난다.
//                          ROI 를 쓰기 전에 [위치 보정]으로 박스를 바코드에 겹치는 것이 선행 조건.
//                       SQL 마이그레이션: 없음
// v7.268 (2026-08-03) — 📏 ROI 경계 정밀도 — 전체 포함 판정 + 여유(margin)
//                       사용자 보고(실기): v7.267 로 꽤 정확해졌지만 선 바로 바깥이 아직 잡힌다.
//                       ★ 원인 두 갈래
//                          (1) 구조적 — 지금까지 바코드의 '중심점'만 봤다.
//                              바코드가 경계에 걸치면 중심이 1px 안쪽이어도 통과한다.
//                              절반이 밖으로 나가 있어도 마찬가지. 이건 오차가 아니라 판정 방식의 한계.
//                          (2) 추정 오차 — ML Kit 이 실제 이미지 크기를 안 알려준다.
//                              preset(1440x2560 등) 으로 추정하므로 실제와 다르면 그만큼 밀린다.
//                              이건 플러그인이 값을 주지 않는 한 완전히 없앨 수 없다.
//                       조치
//                          ① 판정 기준 선택 — 기본 '전체 포함'
//                             전체 포함: 바코드가 선에 조금이라도 걸치면 제외 (신규 기본값)
//                             중심점   : 기존 v7.267 방식 (느슨하게 쓰고 싶을 때)
//                          ② 여유(margin) 0~15% — 판정용 범위를 표시된 선보다 안쪽으로 좁힌다.
//                             (2)번 추정 오차를 흡수하는 값. 선 밖이 계속 잡히면 올린다.
//                             ⚠ 여유가 범위보다 커져 상하/좌우가 뒤집히지 않게 clamp:
//                                mx = min(margin, (w-0.02)/2) — 최소 폭 2% 는 항상 남는다.
//                                랜덤 5만회 검사에서 뒤집힘 0건.
//                       검증: 경계 걸침 케이스 — 중심점=통과 / 전체포함=제외 (보고 증상과 일치)
//                             완전히 안쪽은 두 방식 모두 통과, 완전히 바깥은 모두 제외.
//                       ※ 웹 모드는 캔버스를 실제로 잘라내므로 이 문제가 없다 (네이티브 전용 보정).
//                       SQL 마이그레이션: 없음
// v7.267 (2026-08-03) — 🎯 ROI 판정 좌표계 통일 (범위 밖인데 인식되던 문제)
//                       사용자 보고(실기): 박스 위쪽 바코드가 범위 밖인데도 등록됐다.
//                       ★ 원인 — ROI 판정과 화면 인식 박스가 '서로 다른 좌표계'였다.
//                          화면 박스(v7.238):
//                            imageRectToScreenPct(rect, preset, sw, sh) → boxAdj(sy 배율·dy 이동)
//                            · preset = [자동(1440x2560)] 셀렉트로 고른 값
//                            · boxAdj = [위치 보정] 슬라이더로 사용자가 맞춘 값
//                          ROI 판정(v7.265~266):
//                            원시 maxObserved(관측 최대 좌표) 로 따로 cover 환산. 보정 없음.
//                          → 눈에 보이는 위치는 범위 밖인데 판정은 통과. 정확히 이 증상이다.
//                       조치: ROI 판정이 화면 박스와 '완전히 같은 식'을 쓰도록 통일.
//                          imgPreset / autoPreset / boxAdj 를 ref 로 노출해 리스너에서 참조.
//                          → 이제 '보이는 대로' 걸러진다.
//                          덤: [위치 보정]으로 박스를 맞추면 ROI 정확도도 같이 올라간다.
//                              (반대로 박스가 어긋나 있으면 ROI 도 그만큼 어긋난다 —
//                               ROI 가 이상하면 먼저 [위치 보정]으로 박스를 맞출 것)
//                       검증: 스크린샷 상황 재현 — 1440x2560 이미지·ROI y 0.30~0.51 에서
//                             QYG6B019293(화면 y 0.254) 제외, 범위 안(0.40) 통과 확인.
//                       추가: 범위 밖으로 버릴 때 피드백을 띄운다 (조용히 버리면 '왜 안 찍히지'가 된다).
//                             같은 코드는 한 번만 알린다.
//                       SQL 마이그레이션: 없음
// v7.266 (2026-08-03) — 🔲 스캔 프레임 2중 표시 제거 + 제외 카운터 정정
//                       사용자 보고(실기 스크린샷): 범위가 2중으로 보인다.
//                       원인: RoiEditor 박스(청록)와 기존 '중앙 스캔 프레임'(흰/파랑 뷰파인더)이
//                             동시에 떠 있었다. 둘 다 '여기를 겨냥하라'는 뜻이라
//                             어느 쪽이 실제 판정 기준인지 알 수 없다.
//                       조치: roiEnabled 면 기존 프레임을 숨긴다 (네이티브·웹 양쪽).
//                             실제 판정 기준은 ROI 이므로 ROI 쪽을 남겼다.
//                       ⚠ 네이티브는 프레임을 감싼 flex:1 컨테이너는 남겨야 한다.
//                          지우면 상단 상태 스트립과 하단 컨트롤이 붙어버린다.
//                          → 안쪽 박스만 조건부로 감쌌다.
//                       제외 카운터: '감지 횟수' → '서로 다른 코드 수'로 변경.
//                          ML Kit 은 같은 바코드를 매 프레임 다시 던져서, 라벨 하나 비췄을 뿐인데
//                          534건으로 표시됐다(실기 확인). Set 으로 중복을 제거하고 '종'으로 표기.
//                          범위를 옮기거나 ROI 를 껐다 켜면 집계를 초기화한다.
//                       SQL 마이그레이션: 없음
// v7.265 (2026-08-03) — 👆 인식범위를 프리뷰에서 직접 끌어 조절 + v7.264 좌표계 버그 수정
//                       사용자 제안: 슬라이더 말고 화면에서 바로 박스를 끄는 방식이 직관적이다.
//                       ★ v7.264 버그 2개 — 실기 배포 전에 잡았다.
//                          (1) 좌표계 불일치.
//                              ROI 를 '영상 프레임 비율'로 저장하면서 오버레이는 '화면 비율'로 그렸다.
//                              <video>/네이티브 프리뷰가 objectFit:cover 라 화면비와 영상비가 다르면
//                              잘리는 영역이 생긴다 → 어둡게 표시된 영역과 실제 제외 영역이 어긋났다.
//                              → ROI 를 '화면 비율'로 저장(사용자가 보는 것 기준)하고,
//                                자를 때 cover 역보정으로 영상 픽셀로 되돌린다.
//                                네이티브도 이미지 좌표를 화면 좌표로 올린 뒤 비교하도록 뒤집었다.
//                          (2) 웹 모드에서 ROI 오버레이가 아예 안 보였다.
//                              v7.264 는 오버레이를 nativeScanOverlay 안에만 넣었는데
//                              이건 isNative && phase==='camera' 일 때만 렌더된다.
//                              → RoiEditor 컴포넌트로 빼서 웹·네이티브 양쪽에 붙였다.
//                       ★ RoiEditor — 참고 앱과 같은 조작 방식
//                          · 박스 안쪽을 끌면 이동 · 우하단 ⤡ 로 크기 · 좌하단 ⟲ 로 전체 복귀
//                          · Pointer Events + setPointerCapture, touchAction:'none'
//                            (터치 드래그 중 화면 스크롤/줌 제스처가 끼어들지 않게)
//                          · 최소 크기 8% — 더 작아지면 다시 잡기가 어렵다
//                          · 부모 오버레이가 pointerEvents:'none' 이므로 박스/핸들만 'auto'
//                       ★ 검증
//                          · 드래그 20만회 랜덤 퍼즈 — 화면 밖 이탈 0건, 최소 크기 유지
//                          · cover 역보정 왕복 오차 0.2% 이내 (1920x1080·1280x720·640x480·세로영상)
//                       UI: 슬라이더 4개는 제거 (드래그가 대체). 프리셋 4종은 유지 — 빠른 시작점.
//                       SQL 마이그레이션: 없음
// v7.264 (2026-08-03) — 🎯 인식범위(ROI) 설정 + 시리얼 필터 사용자 설정
//                       사용자 제안: 필터를 사용자가 정하고, 카메라 인식범위도 설정할 수 있게.
//                       ★ ROI 가 후처리 필터보다 근본적이다.
//                          HDD 카톤 라벨은 위쪽에 BOX ID·P/N·MLC·MODEL 이 몰려 있고
//                          시리얼은 아래 격자에 있다. 범위를 아래로 내리면 쓰레기 바코드는
//                          '감지 자체가 안 된다'. 필터로 거르는 것보다 확실하다.
//                       ① 웹 경로 — 진짜 ROI
//                          drawImage(video, sx,sy,sw,sh, 0,0,sw,sh) 로 캔버스를 잘라서 그린다.
//                          디코더가 잘린 영역만 보므로 범위 밖은 감지 불가 + 픽셀이 줄어 더 빠르다.
//                          ⚠ 좌표계 함정: 크롭한 캔버스는 ROI 원점 기준이라
//                            BarcodeDetector 의 boundingBox 도 ROI 기준으로 나온다.
//                            v7.238 인식 박스 오버레이가 어긋나므로 rect 에 (sx,sy)를 더해
//                            원본 프레임 좌표로 되돌린다.
//                       ② 네이티브(ML Kit) 경로 — 감지 후 거르기
//                          Capacitor 플러그인 startScan 에 ROI 옵션이 없다.
//                          → cornerPoints 중심점이 범위 밖이면 버린다. 결과는 같고 속도 이득만 없다.
//                          기준 해상도는 maxObserved(관측된 최대 좌표) 추정치를 쓴다.
//                          ⚠ 추정이 자리잡기 전에는 판정을 보류하고 통과시킨다
//                            (진짜 시리얼을 잘못 버리는 것보다 낫다).
//                       ③ 프리셋 4종 (전체 / 아래 2/3 / 중앙 가로띠 / 시리얼 격자)
//                          + 슬라이더 4개(위쪽시작·높이·왼쪽시작·너비).
//                          슬라이더는 x+w>1 이 되면 자동 보정 — 랜덤 4000회 검사로 화면 밖 이탈 0건.
//                          카메라 화면에는 범위 밖을 어둡게 덮는 오버레이 + 제외 건수 표시.
//                       ④ 시리얼 필터 사용자 설정 (v7.263 규칙을 화면에서 켜고 끈다)
//                          · 규칙 6종 개별 토글 (공백/BOX ID/WD P/N/순수숫자/HDD모델/파렛트)
//                          · 최소·최대 길이 직접 입력
//                          · 제외할 접두어 직접 입력 (쉼표 구분)
//                          · 카톤 지문 확정 개수 / 접두어 길이 조절
//                            → 접두어 길이 3 이상은 위험하다는 경고 문구를 UI 에 넣었다
//                              (실측 WD 라벨의 공통 접두어가 2자 'V7' 이라 3자면 16개가 잘린다)
//                          · [♻ 기본값으로 되돌리기]
//                          설정은 localStorage 저장. 바꾸면 지문은 초기화된다.
//                       ⚠ ROI 는 기본 OFF. 켜야 동작한다 (기존 스캔 동작 무변경).
//                       SQL 마이그레이션: 없음
// v7.263 (2026-08-03) — 💽 HDD 카톤 라벨 시리얼 오인식 차단
//                       사용자 보고: HDD 바코드 스캔 시 양식이 다양해 엉뚱한 바코드가 찍힌다.
//                       신규 파일: src/services/SerialFilterService.js
//                       ★ 진단 먼저 (실측 라벨 3종 사진에서 코드를 뽑아 기존 필터에 통과시킴)
//                          기존 isValidSerialPattern 규칙은 사실상 3개뿐이었다:
//                            6~30자 / 순수숫자 EAN 길이 아님 / PQKP·CQKP 접두어 아님
//                          → 라벨의 '필드' 바코드가 전부 시리얼로 통과. 오인 11건 / 누락 0건:
//                            BOX ID P232112272·P262711415 · P/N 0B36039·0B48724
//                            MLC VL69G0·PPLDE0 · MODEL HUS726T6TALE6L4·WUH722020CLE6L4·ST24000NM002H
//                            CASE ID CTK660818107 · PACKAGE COUNT '21 OF 40'
//                       ★ 핵심 관찰 — 한 카톤의 시리얼은 '길이가 같다'. 쓰레기는 길이가 제각각.
//                            WD 6TB   V701AB5H V7GEMZWH …  8자 (공통 접두어 V7)
//                            Seagate  ZYD05G6A ZYD0BMP8 …  8자 (ZY)
//                            WD 20TB  PPGXKG8P PPGY6ZVP …  8자 (PP)
//                          오인 11건의 길이는 6·7·10·12·13·15 — 하나도 8이 아니다.
//                       ① 구조 필터 (항상 ON)
//                          공백포함 / BOX ID(^P\d{9}$) / WD P/N(^0B\d{5}$) / 순수숫자 /
//                          HDD 모델번호(12자↑ + HUS·WUH·ST 등 접두어) / PQKP·CQKP
//                       ② 카톤 지문 (HDD 모드에서만 ON)
//                          같은 길이 3개가 모이면 (길이 + 앞2자)를 지문으로 확정.
//                          확정 전 코드는 '보류'하고 등록하지 않는다.
//                          ★ 이 보류가 콜드스타트 구멍을 막는다 — 없으면 라벨 위쪽부터 스캔했을 때
//                            BOX ID 가 첫 코드로 그냥 등록된다. 확정 순간 정산해서 맞는 것만 푼다.
//                       ★ HDD 모드를 기본 OFF 로 둔 이유 (중요)
//                          QNAP 은 모델마다 시리얼 접두어가 다르다 (QYC…·Q2A…·QW1…).
//                          지문을 켜면 먼저 잠긴 접두어와 다른 모델의 시리얼이 거부된다.
//                          실제로 테스트에서 Q2A17R04822 가 'QY…' 지문에 걸려 거부되는 것을 확인했다.
//                          → 지문은 HDD 카톤 작업일 때만 사용자가 켠다. 구조 필터만 항상 적용.
//                       ★ 설계 중 틀렸던 가정 2개 (SerialFilterService 주석에도 남김)
//                          (1) '시리얼은 영문+숫자 혼합' → 틀렸다. PPGXPXJP·PPGYDLSP 등
//                              7개가 숫자 없는 순수 영문. 이 규칙 넣으면 진짜 시리얼이 잘린다.
//                          (2) '접두어 3자로 구분' → 틀렸다. 라벨1 앞 3개가 V701/V700/V701 이라
//                              'V70' 으로 잠기는데 실제 공통 접두어는 'V7'. 3자면 16개가 잘렸다.
//                       ★ 검증 (라벨 3종 × 스캔순서 2종 = 6개 시나리오)
//                          잘못 등록 0건 / 누락 0건. 최악(쓰레기 먼저 스캔)도 통과.
//                          QNAP 회귀: QYC5801095E·Q2A17R04821 등 통과, PQKP·EAN·UPC 차단 확인.
//                       UI: 스캔 컨트롤 밴드에 [💽 HDD 카톤 모드] 토글 + 지문 상태 패널
//                           (학습 중 보류 목록 표시 / 확정 시 [♻ 지문 초기화] 버튼 — 다음 카톤용)
//                       SQL 마이그레이션: 없음
// v7.262 (2026-08-03) — 🧪 배송시트지 [테스트] 탭 + BorderGlow 실험실
//                       사용자 요청: 실제 행을 바로 고치지 말고 테스트 탭에서 먼저 실험.
//                       신규 파일: src/BorderGlow.jsx  (지우면 흔적 안 남음)
//                       ★ 받은 코드를 그대로 못 쓴 이유 3가지 — 다음에 또 헷갈리지 않게 적어 둔다.
//                          (1) BorderGlow.css 가 안 왔다. 이 컴포넌트는 효과의 실체가 CSS 에 있다.
//                              JSX 는 --cursor-angle / --edge-proximity 를 계산해 꽂아줄 뿐이다.
//                              → 변수 이름에서 역산해 CSS 를 새로 썼다. 원본과 픽셀 단위로 같진 않다.
//                                원본 CSS 를 구하면 BORDER_GLOW_CSS 문자열만 갈아끼우면 된다.
//                          (2) 배송시트지 행은 display:grid (10칸) 이고 셀이 '직속 자식'이어야 한다.
//                              BorderGlow 는 children 을 .border-glow-inner 로 한 번 감싼다.
//                              → 행을 감싸면 10칸이 한 칸으로 뭉개진다. 표가 무너진다.
//                              → 그래서 행용으로 useRowGlow() 훅을 따로 만들었다.
//                                DOM 을 안 늘리고 ::after 로만 그린다.
//                                ::after 는 position:absolute 라 grid item 으로 안 잡힌다 (배치 무영향).
//                          (3) 데모는 여백 있는 독립 카드(radius 28 / glowRadius 40)다.
//                              행은 minWidth 1240 에 1px 선으로 붙어 있고 간격이 0이라
//                              40px 글로우 패딩을 그대로 쓰면 행끼리 겹친다. → 행용 수치는 따로 잡는다.
//                       ★ CSS 를 .css 파일로 안 두고 런타임 주입한 이유:
//                          이 프로젝트에 .css import 전례가 없어서 빌드 설정 지원 여부를 확인 못 했다.
//                          MobileScan 의 document.createElement('style') 패턴을 그대로 따랐다.
//                          id='border-glow-style' 로 가드해서 중복 주입되지 않는다.
//                       ★ 성능: onPointerMove 는 React 이벤트 위임이라 커서 아래 행 1개에서만 돈다.
//                          100행을 그려도 프레임당 계산은 1행치. setProperty 만 하고 리렌더는 없다.
//                       ★ 라이트 모드에서는 CSS 에서 글로우를 아예 끈다
//                          (html:not([data-theme='dark']) .glow-row::after { opacity:0 })
//                          밝은 배경에서는 묻히고 지저분하다. 다크 전용 효과다.
//                       테스트 탭 내용:
//                          ・ 가짜 배송시트지 행 4개 (실 데이터 안 읽는다)
//                          ・ 원본 카드 형태 비교용 1개 (animated 인트로 스윕 포함)
//                          ・ 슬라이더 8종 (색상/채도/밝기/세기/퍼짐/감도/두께/둥글기)
//                          ・ 프리셋 5종 · 적용모드(호버만/항상) · 행 상태색 토글 · 설정값 복사
//                       ⚠ 실제 배송시트지 행에는 아직 아무것도 적용하지 않았다. 값 확정 후 별도 버전에서.
//                       SQL 마이그레이션: 없음
// v7.261 (2026-07-31) — 🌙 다크모드 기본 비활성화 (OS 설정 추종 제거)
//                       v7.260 은 저장값이 없을 때 OS 의 prefers-color-scheme 을 따라갔다.
//                       → OS 가 다크인 직원은 앱을 처음 열자마자 다크로 떴다.
//                         켠 적이 없는데 켜져 있으면 설정이 아니라 '고장'으로 보인다.
//                       변경: 저장값이 없으면 무조건 'light'.
//                             다크는 메뉴에서 직접 켠 사람에게만 적용된다.
//                       유지: 한 번 토글하면 localStorage('inv_theme')에 남아 다음 접속에도 유지.
//                             팔레트·치환·토글 UI 는 v7.260 그대로 (색 관련 코드 변경 없음).
//                       ⚠ 이미 v7.260 을 써 본 브라우저는 저장값이 남아 있을 수 있다.
//                          OS 다크 때문에 다크로 떴다가 그 상태로 뭔가를 눌렀다면 'dark' 가 저장됐다.
//                          그 경우는 메뉴에서 한 번 꺼 주면 이후로는 라이트로 뜬다.
//                          (일괄 초기화가 필요하면 말씀 주세요 — 저장값 폐기 코드는 넣지 않았다.
//                           명시적으로 켠 사람의 설정까지 지워버리기 때문.)
//                       SQL 마이그레이션: 없음
// v7.260 (2026-07-31) — 🌙 다크모드 1·2단계 (팔레트 + 토글 + 상위 색 CSS 변수화)
//                       HANDOFF §4 의 1단계(팔레트/토글/body) + 2단계(상위 10색 치환)를 한 번에.
//                       3단계(모바일 T 5개)·4단계(rgba/차트/뱃지)는 다음 세션.
//                       ★ 핵심 안전장치 — 라이트 모드는 픽셀 단위로 동일하다.
//                          :root 의 변수 값을 기존 hex 와 '똑같이' 넣었다.
//                          <html data-theme> 속성이 없으면 :root 만 걸리므로 화면이 안 변한다.
//                          치환 후 var() → hex 역변환해서 원본과 바이트 비교(대소문자 정규화)
//                          → 완전 일치 확인함. 색 토큰 외에는 아무것도 안 건드렸다는 뜻.
//                       ① 팔레트: 전역 <style> 맨 앞에 :root / [data-theme="dark"] 27개 변수
//                          역할 기반 이름 (hex 이름 금지):
//                            --bg-page/card/subtle/mute/info/accent-soft/accent-strong
//                            --fg-1(제목) / --fg-2(본문) / --fg-3(보조) / --fg-disabled
//                            --border / --border-mute / --border-strong / --surface-mute / --surface-inverse
//                            --accent --success --warning --danger --purple  (+ 각 -solid)
//                          ⚠ -solid 를 왜 나눴나: 같은 hex 가 '글자색'과 '채움 배경' 양쪽에 쓰인다.
//                            다크에서 글자색은 밝혀야 읽히고, 흰 글자를 얹는 채움 배경은
//                            밝히면 대비가 죽는다. 그래서 전경/테두리 → --accent,
//                            배경 → --accent-solid 로 갈랐다. 라이트에선 둘 다 같은 값이라 무해.
//                          color-scheme 도 같이 넣었다 → date input·기본 스크롤바가 따라 어두워진다.
//                       ② 토글: 우측 상단 [메뉴] 안, 자동이월 시각 행 바로 위 (v7.237 과 같은 형태)
//                          우선순위 localStorage('inv_theme') > OS(prefers-color-scheme) > light
//                          ※ v7.261 에서 OS 추종은 제거됨 (기본 라이트 고정)
//                          적용은 <html data-theme="dark">. 메뉴는 안 닫힌다 (보면서 켜고 끄라고).
//                       ③ 치환 2,393 건 — 지배 CSS 속성을 판별해 전경/배경/테두리를 각각 다른 변수로.
//                          #fff(배경) 303 · #76777d 376 · #45464d 245 · #e2e8f0 192 · #0b1c30 153
//                          #00668a 236 · #009844 217 · #c6c6cd 173 · #e65100 123 · #ba1a1a 126
//                          #7b1fa2 105 · 은은한 배경 4종(#fafbff/#eff4ff/#dce9ff/#f0f2f6) 133
//                       ★ 함정 3개 — 다음 사람이 같은 데서 안 넘어지게 적어 둔다.
//                          (1) #e2e8f0 은 199곳 중 182곳이 대문자 '#E2E8F0' 이다.
//                              소문자로만 치환하면 182곳이 조용히 남아 다크에서 흰 선으로 뜬다.
//                              → 치환은 반드시 대소문자 무시(re.I)로.
//                          (2) 주석 안의 hex 는 건드리면 안 된다 (changelog 기록이 깨진다). 35건 보존.
//                          (3) 지배 속성을 특정 못 하는 자리는 손대지 않았다 (252건 잔존).
//                              대부분 차트/도넛 색 배열, ACTION_COLOR·DEMO_CATEGORY_COLOR 같은
//                              데이터 리터럴, accentColor(체크박스), color:'#fff'(채움 위 흰 글자).
//                              color:'#fff' 110건은 '의도적으로' 남긴 것 — 다크에서도 흰 글자가 맞다.
//                       ★ 확인함 (핸드오프 §4-3 함정 대조)
//                          ・ MobileScan 네이티브 스캔: 주입 style 이 html/body 에
//                            background:transparent !important 라서 body 변수보다 우선.
//                            → 카메라 프리뷰 안 가려진다. (그래도 실기 확인 권장)
//                          ・ @media print / window.print 없음 → 인쇄 이슈 해당 없음
//                          ・ XLSX 내보내기에 색 지정 없음 (aoa_to_sheet 만) → 무관
//                       미적용 (다음 세션): 모바일 T 5개 · rgba() 542 · SVG 차트 · SOURCE_BADGE
//                       SQL 마이그레이션: 없음
// v7.259 (2026-07-31) — 🧹 보관 시리얼 표기 제거 (자동 주입만 남김)
//                       사용자 요청: 배너 아예 안 띄움. [시리얼 N개 보관] 뱃지도 불필요.
//                       근거: v7.258 로 자동 주입이 실제로 동작하게 됐으니,
//                             "보관돼 있다"를 따로 알릴 이유가 없다. 모달을 열면 이미
//                             '출고 준비 완료' 상태이고 담긴 시리얼도 그 박스에 보인다.
//                       제거:
//                          ① 출고 처리 모달의 [되돌린 출고 — 보관된 시리얼] 배너 (인라인·팝업 2곳)
//                          ② 배송시트지 행의 [🕘 시리얼 N개 보관] 뱃지
//                          ③ loadRevertedIntoPending() — 배너 버튼이 유일한 호출자였다
//                       유지:
//                          ・ 주입 로직 자체 (loadActiveRequest 안, v7.258)
//                          ・ 주입 시 상단 안내 문구 ('✓ … 시리얼 N개를 그대로 불러왔습니다')
//                          ・ [출고요청] 칩의 아이콘/tooltip 전환 (보관분 있으면 undo 아이콘)
//                            — 칩 자체의 표시라 화면이 늘지 않는다
//                          ・ 요청 상세 모달의 보관 시리얼 패널 (v7.253)
//                            → 배너를 지운 뒤 '무엇이 보관됐는지' 확인할 수 있는 유일한 곳이라 남겼다.
//                              이것도 불필요하면 말씀 주시면 함께 걷어낸다.
//                       SQL 마이그레이션: 없음
// v7.258 (2026-07-31) — 🏁 되돌림 보관분 자동 주입 — 덮어쓰기 경합 수정
//                       증상: v7.256·v7.257 을 배포해도 계속 0/1 이고 배너만 떴다.
//                       ★ 원인 (경합):
//                          loadActiveRequest 안의 순서가
//                            setActiveRequest(6852) → await ShipPendingService(6902)
//                            → setPendingSerials(initialPending)(6928)
//                          이다. setActiveRequest 후 await 앞에서 렌더가 한 번 흐르므로
//                          내 useEffect 가 그 틈에 시리얼을 주입했는데,
//                          await 가 풀린 뒤 6928 이 **통째로 덮어써** 버렸다.
//                          → 화면상 아무 일도 안 일어난 것처럼 보임. 가드 문제가 아니었다.
//                       변경 (App.js):
//                          ① 주입을 useEffect 에서 **loadActiveRequest 안**으로 이동.
//                             setPendingSerials 직전에 initialPending 자체를 병합해서 넘긴다.
//                             → 덮어쓸 주체가 없어진다. 상태 갱신도 한 번뿐.
//                          ② 조건은 그대로: 실제 스캔 시리얼이 0일 때만 주입.
//                             EXT-/NOSN- 은 모달이 만든 placeholder(v7.145)라 사용자 작업이 아니다.
//                          ③ 실제 시리얼이 채워지는 모델의 placeholder 제거 (qty 초과 방지)
//                          ④ ShipPendingService.addSerials 로 서버에도 반영 (다중 PC 공유)
//                          ⑤ 주입되면 상단에 '✓ 되돌린 출고의 시리얼 N개를 그대로 불러왔습니다' 안내
//                          ⑥ v7.256 의 useEffect / revertAutoLoadRef 제거 (경합의 원인)
//                       ※ 배너는 '아직 안 담긴 게 있을 때만' 조건 그대로 유지 —
//                          정상 흐름에선 안 보이고, 예외 상황의 복구 수단으로만 남는다.
//                       SQL 마이그레이션: 없음
// v7.257 (2026-07-31) — ✅ 되돌린 건을 열면 바로 '출고 준비 완료'
//                       사용자 요청: 보관 시리얼 표기 말고 바로 출고 준비 완료로.
//                       ★ 자동 주입이 안 걸리던 원인도 함께 수정:
//                          v7.256 가드가 `pendingSerials.length > 0` 이면 건너뛰었는데,
//                          이 모달은 외부 품목에 EXT-/NOSN- placeholder 를 **자동으로** 넣는다(v7.145).
//                          그걸 '사용자가 담아둔 것'으로 세는 바람에 주입이 영영 안 걸렸다.
//                          → 실제로 스캔한 시리얼(placeholder 아닌 것)이 있을 때만 손대지 않는다.
//                       변경 (App.js):
//                          ① 자동 주입 가드를 userPending 기준으로 (EXT-/NOSN- 제외)
//                          ② loadRevertedIntoPending — 실제 시리얼이 채워지는 모델의 placeholder 제거
//                             ・ 안 그러면 qty 1 품목에 placeholder + 실제 시리얼 2개가 담겨 수량 초과
//                          ③ [되돌린 출고 — 보관된 시리얼] 배너를 **아직 안 담긴 게 있을 때만** 렌더
//                             ・ 정상 흐름(자동 주입 성공)에서는 안 보인다.
//                               담긴 시리얼은 아래 '출고 준비 완료' 박스가 이미 보여준다
//                             ・ 자동 주입이 걸리지 않은 예외 상황의 복구 수단으로는 남긴다
//                               (배너를 통째로 없애면 그때 되살릴 방법이 사라진다)
//                       검증(가드): 빈 상태·EXT만·NOSN만 → 주입 / 실제 스캔 있음·혼합 → 미개입
//                       검증(정리): placeholder + 실제 시리얼 → 실제 시리얼만 남음
//                       SQL 마이그레이션: 없음
// v7.256 (2026-07-31) — ⚡ 보관 시리얼 자동 불러오기 (버튼 한 번 덜 누르게)
//                       사용자 요청: [보관분 불러오기] 없이 바로 '출고 준비 완료' 상태로.
//                       근거: 되돌린 건을 다시 여는 목적은 결국 그 시리얼로 내보내는 것이다.
//                             확인 클릭을 한 번 더 받을 이유가 없다.
//                       변경 (App.js):
//                          ① activeRequest.reqId 변경 시 revertedItems 를 pendingSerials 로 자동 주입
//                             ★ pendingSerials 가 **비어 있을 때만** 채운다.
//                               이미 스캔했거나 사용자가 일부를 지운 상태를 자동 로직이 덮으면 안 된다
//                             ・ revertAutoLoadRef 로 요청당 1회만 실행 (모달 닫아도 ref 유지 →
//                               같은 건을 다시 열어도 지운 시리얼이 되살아나지 않는다)
//                             ・ deps 는 reqId 하나. pendingSerials 를 넣으면 주입→상태변경→재주입 루프
//                          ② 배너는 유지 — 무엇이 보관돼 있는지 보이는 편이 낫다
//                             ・ 전부 담긴 상태면 버튼이 [출고 준비 완료] 로 비활성
//                             ・ 안내 문구도 상태에 따라 갈라진다
//                               (담김: "아래 [제품 출고]를 누르면 그대로 커밋됩니다")
//                          ③ 다른 시리얼로 내보내려면 담긴 것을 지우고 새로 스캔 — 문구에 명시
//                       ※ 자동 주입도 ShipPendingService 병행 저장을 타므로 다중 PC 공유 유지
//                       SQL 마이그레이션: 없음
// v7.255 (2026-07-31) — 📥 출고 처리 모달에서 보관 시리얼 불러오기
//                       사용자 지적: "안 보여". 맞다 — 모달을 잘못 짚었다.
//                       v7.253 은 보관 시리얼 패널을 **상세 모달**(기록 삭제/환불이 있는 쪽)에만
//                       넣었는데, 되돌린 뒤 재출고하려면 당연히 **출고 처리 모달**을 연다.
//                       거기서는 0/N 으로만 보여서 보관분이 사라진 것처럼 보였다.
//                       ⚠ 이 모달은 렌더링이 **두 벌**이다 (인라인 페이지 / 팝업).
//                          v7.145 EXT 버튼도 양쪽에 있다. 이번에도 양쪽에 넣었다.
//                       변경 (App.js):
//                          ① loadRevertedIntoPending() — 보관분을 pendingSerials 로 올린다
//                             ・ revertedItems[].idx → common.items[idx].product_name →
//                               activeRequest.models 에서 modelLogId 매칭
//                             ・ EXT-/NOSN- 접두는 external/serialOptional 로 복원
//                             ・ 이미 pending 에 있는 시리얼은 건너뛴다 (중복 클릭 방어)
//                             ・ ShipPendingService 에도 병행 저장 (다중 PC 공유 유지)
//                             ★ 커밋은 하지 않는다 — 기존 [제품 출고] 버튼이 그대로 한다.
//                               출고 경로를 새로 만들지 않아야 검증·락이 한 곳에 남는다
//                          ② 두 모달에 [되돌린 출고 — 보관된 시리얼] 패널
//                             ・ 시리얼 칩: 불러온 것은 초록 실선 + ✓, 아직인 것은 파랑 점선
//                             ・ [보관분 불러오기 (N개)] 버튼, 다 불러오면 비활성 '모두 불러옴'
//                             ・ 새로 스캔하면 커밋 시 보관분이 폐기된다는 안내 명시 (v7.253 ②)
//                       SQL 마이그레이션: 없음
// v7.254 (2026-07-31) — 🟡 DOA 기록이 재고목록에 뜨게 + DOA 출처 노랑 + 시트지 환불 취소선
//                       사용자 요청 3건.
//                       ① DOA 수리완료 시 바뀐 시리얼이 재고목록에 안 보이던 문제
//                          ★ 원인: 컬럼이 달랐다. v7.101~v7.250 은 `inventory.note` 에 썼는데
//                             재고목록 [비고] 칸이 보여주는 건 `inventory.memo` 다
//                             (saveInventoryMemo → updateBySerial({memo})). 그래서 화면은 빈칸.
//                          ・ 들어오는 시리얼 insert 에 memo 추가 — `[DOA 교체] 이전 → 바뀐`
//                          ・ 나가는 시리얼도 memo 에 덧붙인다 (note 는 감사용으로 그대로 유지)
//                            ・ 손으로 적어둔 memo 는 지우지 않고 ' / ' 로 이어 붙임
//                            ・ note/memo 각각 중복 검사 → 수리완료 재실행해도 누적 안 됨
//                          ※ 이미 처리된 과거 건은 소급 안 된다 (note 에만 남아 있음)
//                       ② 출처 뱃지 DOA → 노란색
//                          ・ SOURCE_BADGE / sourceBadge() 신설 — 같은 삼항 사슬이 두 군데
//                            (그룹 행 13305 / 단일 행 13453)에 복붙돼 있어 한쪽만 고치기 쉬웠다
//                          ・ DOA: bg #fff8e1 / fg #8a6d00 / border #ffd54f / ⚠️
//                          ・ 미등록 출처값은 기존처럼 본사 색으로 폴백
//                       ③ 배송시트지 품목 줄에도 부분환불 회색 + 취소선
//                          ・ 지금까지는 상세 모달을 열어야 어느 품목이 빠졌는지 알 수 있었다
//                          ★ 품목의 출고 시리얼이 **전부** 환불됐을 때만 줄을 긋는다.
//                            일부만 환불된 품목은 아직 나가 있는 물건이 있으므로 그대로 둔다.
//                          ・ 단일 품목 줄 / 다중 품목 줄 양쪽 적용
//                       검증: 전량환불 → 취소선 / 일부환불·환불없음·외부(시리얼 0)·필드없음 → 그대로
//                       SQL 마이그레이션: 없음
// v7.253 (2026-07-31) — 👁 되돌린 출고의 시리얼을 화면에 보이게 + 보관분 정리
//                       사용자 지적: 찍어놓은 시리얼로 출고한 뒤 출고완료→출고요청으로 바꾸면
//                                    시리얼 기록이 남아 있어야 한다.
//                       v7.251은 note.revertedItems 에 **보관은 하고 있었지만**,
//                       화면 어디에도 안 보였다. items[].fulfilledSerials 를 비우니
//                       상세 모달의 '출고 시리얼' 줄이 통째로 사라져서, 남은 단서가
//                       시트지 뱃지 tooltip 하나뿐이었다. → 기록이 없어진 것처럼 보인다.
//                       변경 (App.js):
//                          ① 요청 상세 모달에 [되돌린 출고 — 보관된 시리얼] 패널 신설
//                             ・ 품목별로 묶어 표시 (revertedItems[].idx → items[].product_name)
//                             ・ 되돌린 시각, 재출고/재스캔 시 어떻게 되는지 안내 포함
//                             ・ 점선 테두리로 '현재 출고분이 아님'을 구분
//                          ② 새로 스캔해서 출고하면 revertedItems / revertedAt 를 **삭제**
//                             ・ 안 지우면 [출고요청] 칩이 나중에 옛 시리얼을 다시 적용하려 들어
//                               같은 시리얼이 두 번 나가는 사고가 난다
//                             ・ 출고 커밋 경로(updatedNote 생성)에서 구조분해로 제거
//                       ※ 진입점이 둘이라는 점은 그대로다:
//                          [출고요청] 칩 → 보관분 그대로 재출고 (재스캔 없음)
//                          [제품 출고] 버튼 → 새로 스캔 (이때 보관분은 폐기)
//                          두 경로 모두 상세 모달 안내에 적어 두었다.
//                       SQL 마이그레이션: 없음
// v7.252 (2026-07-31) — 🧪 데모 대여 건도 출고완료 → 출고요청 되돌리기
//                       사용자 요청: 데모도 되돌릴 수 있게.
//                       배경: v7.249~v7.251 은 데모 건을 전면 차단했다.
//                             출고완료 시 finalizeRental() 이 함께 돌아 재고 밖의 것까지 바꾸기 때문:
//                                demo_rentals.status  pending → active
//                                demo_equipment.location  대여예약(업체) → 대여중(업체)
//                                audit_log 에 'ship_complete'
//                             재고만 되돌리면 "출고요청인데 대여는 active, 장비는 대여중" 이 된다.
//                       변경:
//                          ① DemoService.revertRental({rentalId, by}) 신설 — finalizeRental 의 역함수
//                             ・ status  active → pending  (.eq('status','active') 낙관적)
//                             ・ location  대여중(업체) → 대여예약(업체)
//                               ⚠ **현재 '대여중(업체)' 인 장비만** 되돌린다.
//                                 다른 곳으로 옮겨둔 장비를 덮어쓰지 않기 위한 조건.
//                             ・ audit_log 에 'ship_revert' 추가 (이력이 끊기지 않게)
//                             ★ returned / cancelled 대여는 **거부**. 회수까지 끝난 걸 되돌리면
//                               회수 기록과 어긋난다 (v7.245에서 본 종류의 불일치)
//                          ② toggleShipFulfilled — fromDemoLend 전면 차단 제거
//                             ・ 완료 → 요청: **대여 원복을 가장 먼저** 시도한다.
//                               거부되면 재고·note 를 손대지 않고 그대로 중단 → 반쯤 풀린 상태가 없다
//                             ・ 요청 → 완료: 재출고 후 finalizeRental() 로 다시 승격
//                             ・ confirm 에 대여/장비가 함께 바뀐다는 안내 추가
//                       ※ 실패해도 데이터가 반쯤 바뀌지 않도록 순서를 고정했다:
//                          대여 원복 → 재고 복귀 → note 갱신 (앞이 막히면 뒤는 실행 안 됨)
//                       SQL 마이그레이션: 없음
// v7.251 (2026-07-31) — ♻ 출고 되돌리기 — 재스캔 없이 원복 (v7.249 재설계)
//                       사용자 의도 확인: 이 토글의 용도는 **출고하지 않았는데 실수로
//                       [제품 출고]를 누른 경우** 되돌리기다. 그래서 재스캔이 없어야 한다.
//                       v7.249의 문제 2가지:
//                          ・ 되돌릴 때 fulfilledSerials 를 버려서 다시 스캔해야 했다
//                          ・ 방문수령(pickup) 건은 시리얼이 reservedSerials 에 있고 재고가
//                            'reserved' 인데 fulfilledSerials 만 봐서 **예약분을 놓쳤다**
//                            → "복귀할 재고 시리얼은 없습니다" 가 뜨고 재고는 reserved 로 남음
//                       재설계:
//                          ① collectShipSerials(note) — 품목별로 {idx, fulfilled[], reserved[]}
//                             수집. idx 를 들고 있어야 되살릴 때 원래 품목 자리로 돌아간다
//                          ② 완료 → 요청 : 재고 복귀 + 시리얼을 note.revertedItems 에 통째 보관
//                             ・ fulfilled → restoreToStock / reserved → releaseReservation
//                             ・ items[].fulfilledSerials·reservedSerials 는 비운다
//                               (목록에 남으면 이중 출고 위험)
//                          ③ 요청 → 완료 : revertedItems 가 있으면 **그 시리얼 그대로 재출고**
//                             ・ shipBySerial / reserveBySerial 로 재고를 되돌리고
//                               시리얼을 원래 품목 자리에 복원, revertedItems 삭제
//                             ・ 재고 상태가 그 사이 바뀐 시리얼은 실패 목록으로 보고
//                             ・ revertedItems 가 없으면(진짜 첫 출고) 기존대로 스캔 모달
//                          ④ 되돌린 행에 [🕘 시리얼 N개 보관] 뱃지 + 칩 아이콘/tooltip 전환
//                       검증(왕복): 다품목·방문수령·구형식 note 모두 revert→reapply 후
//                          원본과 동일. 구형식에 없던 reservedSerials 키는 만들지 않는다.
//                       ※ 낙관적 락 + 락 직전 재검사 유지 (그 사이 환불/취소 시 덮어쓰지 않음)
//                       ※ 퀵 시트는 자동 갱신되지 않는다 — 되돌린 뒤 [시트 전송] 필요
//                       SQL 마이그레이션: 없음
// v7.250 (2026-07-31) — 🔀 DOA 시리얼 교체를 '이전 → 바뀐' 으로 자동 표기
//                       사용자 요청: DOA로 시리얼이 바뀌어 들어올 때, 지금 손으로 적는 것처럼
//                                    이전시리얼 → 바뀐시리얼을 표기.
//                       배경: v7.101이 반품(들어오는) 시리얼 비고에 'DOA 전시리얼: XXX' 를
//                             남기고 있었지만 두 가지가 빠져 있었다.
//                          ・ 표기가 라벨식이라 교체 관계가 한눈에 안 읽힌다
//                          ・ **나가는 시리얼 쪽에는 아무 기록이 없다** → 재고 목록에서 옛 시리얼을
//                            보면 왜 판매불가인지, 어디로 바뀌었는지 알 수 없었다. 그래서 손으로 적으심.
//                       변경 (App.js · doRepairComplete):
//                          ① 들어오는 시리얼 비고 → `[DOA 교체] 이전 → 바뀐 (DOA 전시리얼: … / 티켓 / 양식)`
//                             ・ 'DOA 전시리얼' 문구는 남겼다 — 기존 검색 습관이 그대로 동작하도록
//                          ② 나가는 시리얼 비고에도 같은 교체 줄을 **추가** (1c 단계 신설)
//                             ・ fromDB 인 건만 (외부 DOA의 원 시리얼은 재고에 없다)
//                             ・ ⚠ 기존 비고를 덮어쓰지 않고 줄바꿈으로 덧붙인다 —
//                               손으로 적어둔 맥락이 사라지면 안 된다
//                             ・ `→ 반품시리얼` 이 이미 있으면 건너뛴다 (수리완료 재실행 시 중복 방지)
//                             ・ 이 기록이 실패해도 수리완료 자체는 막지 않는다 (경고만)
//                          ③ DOA 정보 패널 — '반품(들어오는) 시리얼' 한 줄을
//                             `시리얼 교체: 이전(취소선) → 바뀐` 형태로 교체
//                          ④ 완료 알림에도 교체 관계를 앞세워 표시
//                       ※ 이미 처리된 과거 건은 소급되지 않는다 (수리완료 시점에 기록되는 값).
//                       SQL 마이그레이션: 없음
// v7.249 (2026-07-31) — 🔁 출고완료 ↔ 출고요청 토글
//                       사용자 요청: 배송시트지 상태 칩을 눌러 서로 전환.
//                       ★ 방향이 대칭이 아니다 — 시리얼이 실제로 재고에서 빠져 있기 때문:
//                          ・ 완료 → 요청 : 나간 시리얼을 재고로 되돌리고 fulfilled 를 푼다
//                          ・ 요청 → 완료 : **어떤 시리얼이 나갔는지 앱이 모른다.**
//                            플래그만 켜면 '출고완료인데 시리얼 0개' 인 유령 행이 되고
//                            재고는 여전히 in_stock 이라 장부가 어긋난다.
//                            → [제품 출고] 모달을 열어 시리얼을 스캔받는다.
//                       변경 (App.js):
//                          ① toggleShipFulfilled(item, shipInfo) 신설
//                             ・ 요청 → 완료: loadActiveRequest + 출고 모달 오픈
//                             ・ 완료 → 요청: 되돌리기 (아래 가드 통과 시)
//                          ② 되돌리기 가드 3종 — 되돌리면 안 되는 상태를 먼저 막는다
//                             ・ processedAs 있음 (취소/환불 완료) → 거부
//                             ・ refundedSerials 있음 (부분 환불) → 거부.
//                               환불분은 이미 재고로 돌아가서 또 복귀시키면 이중 계상된다
//                             ・ fromDemoLend → 거부. 데모는 대여/회수 흐름으로 처리
//                          ③ 되돌리기 동작
//                             ・ EXT-/NOSN- 제외한 시리얼을 restoreToStock
//                               (재고에 없는 외부/무시리얼 항목은 목록에서만 제거)
//                             ・ confirm 에 복귀할 시리얼을 최대 8개까지 미리 보여준다
//                             ・ fulfilled=false, fulfilledAt 삭제, items[].fulfilledSerials 비움
//                               → 다시 [제품 출고]로 스캔해야 한다 (이중 출고 방지)
//                             ・ updated_at 낙관적 락 + 락 직전 재검사
//                               (그 사이 누가 환불/취소했으면 덮어쓰지 않고 새로고침)
//                          ④ 두 칩을 button 으로 교체 (아이콘 + tooltip 으로 방향 안내)
//                       ※ 퀵 시트에 이미 '완료'로 보낸 건을 되돌려도 시트는 바뀌지 않는다.
//                          되돌린 뒤 [시트 전송]을 다시 눌러 갱신해야 한다.
//                       SQL 마이그레이션: 없음
// v7.248 (2026-07-31) — 🩶 환불된 시리얼 회색 + 취소선
//                       사용자 요청: 부분환불된 제품은 회색 표시와 가로줄.
//                       변경 (App.js · 요청/입고 상세 모달):
//                          ・ 환불된 시리얼 칩 — 회색 글자(#9e9e9e) + line-through + 연회색 배경,
//                            앞에 ↩ 아이콘, tooltip '환불 처리됨 — 재고로 복귀'
//                          ・ 해당 시리얼의 [교체] 버튼을 숨긴다
//                            (이미 재고로 돌아간 시리얼을 다른 것으로 바꾸면 이력이 어긋난다)
//                          ・ 품목 헤더에 [환불 N] 배지 — 어느 품목에서 몇 개가 빠졌는지
//                       refundedInModal 집계 규칙:
//                          ・ 부분 환불 → note.refundedSerials 그대로
//                          ・ 전량 환불(processedAs==='return')인데 refundedSerials 가 비어 있으면
//                            = v7.246 이전에 환불된 옛 데이터 → 출고 시리얼 전체를 환불로 간주
//                            (안 그러면 옛 환불 건만 취소선이 안 그어져 들쭉날쭉해 보인다)
//                          ・ 입고 행은 대상 아님
//                       SQL 마이그레이션: 없음
// v7.247 (2026-07-31) — ↩ 부분 환불 (항목 선택 환불)
//                       사용자 요청: 건수별 전체 환불만 되던 걸 원하는 항목만 고르게.
//                       확정된 방침: 부분 환불된 건은 입출고 요약에서 **'출고 완료'로 두고
//                                    수량만 차감**한다. (사용자 확인)
//                       ★ 설계 핵심 — 전량 환불되는 순간 processedAs='return' 이 붙어
//                          기존(전체 환불)과 **완전히 같은 모양**이 된다.
//                          → processedAs 를 읽는 26곳(집계·필터·뱃지·요약·엑셀)은 무변경.
//                            '부분'인 동안에만 별도로 표시하고 수량을 뺀다. 회귀 위험 최소화.
//                       note 신규 필드:
//                          ・ refundedSerials[] — 환불된 시리얼 누적
//                          ・ refunds[] — {at, reason, serials} 이력 (여러 번 나눠 환불 가능)
//                       변경 (App.js):
//                          ① listFulfilledSerials / refundedSetOf / refundableOf 헬퍼 신설
//                             ・ items[].fulfilledSerials (다품목) 와 note.fulfilledSerials
//                               (구 단일 품목) 두 형태를 모두 읽는다
//                          ② handleTxAction(item, type, reason, selectedSerials)
//                             ・ selectedSerials 생략 시 남은 전량 → 기존 호출부 그대로 동작
//                             ・ 화면이 보낸 목록도 DB 최신값으로 다시 거른다
//                               (그 사이 다른 사람이 일부를 환불했을 수 있음)
//                             ・ 재고 복귀는 선택분만. 예약분(reservedSerials)은 전량 환불일 때만
//                             ・ 전량이면 processedAs/processedAt/refundReason 을 기존과 동일하게 세팅
//                             ・ 시리얼 기록이 아예 없는 옛 건은 '전량'으로 간주 (기존 동작 유지)
//                          ③ 환불 패널 — 시리얼 체크박스 목록 (기본 전체 선택) + 전체선택/해제
//                             ・ 부분 선택 시 "출고 완료로 유지되고 N개만 수량에서 빠진다" 경고
//                             ・ 버튼 라벨이 [부분 환불 (N개)] 로 바뀌고 confirm 문구도 갈라진다
//                          ④ 입출고 요약 modelOutCount — 환불된 시리얼을 출고 수량에서 제외
//                          ⑤ 배송시트지 행에 [↩ 부분환불 n/m] 뱃지 (출고 완료로 남으므로 필요)
//                          ⑥ 상세에 환불 이력 패널 — 회차별 일시·사유·시리얼
//                       검증 (판정 로직): 다품목 전량/부분/이어서 나머지 · 구 단일품목 ·
//                          이미 환불된 것 재선택(차단) · 출고 0건 → 모두 의도대로
//                       SQL 마이그레이션: 없음 (note JSON 필드 추가)
// v7.246 (2026-07-31) — ↩ 데모 장비 목록에 [회수] 버튼 (위치 → 리버)
//                       사용자 요청: 위치 오른쪽에 회수 버튼. 누르면 리버로 바뀌고 대여중 원복.
//                       배경: v7.245에서 드러난 이관 데이터(장비 연결 없는 대여)는 자동 복원이
//                             불가능하다. SQL로만 수습하던 걸 화면에서 1대씩 처리할 수 있게 한다.
//                       변경:
//                          ① DemoService.restoreEquipmentToRiver(id) 신설
//                             ・ location → '리버'
//                             ・ status 는 **비정상값일 때만** DEMO_RESTORE_STATUS 로 정상화
//                               ('불량'·'수리중' 처럼 멀쩡히 관리되던 값을 덮어쓰지 않는다)
//                             ★ 진행 중 대여(active/pending)에 묶인 장비는 **거부**한다.
//                               출고번호·업체를 알려주고 [대여로그]의 회수 처리로 유도.
//                               여기서 위치만 바꾸면 rental 은 active 인데 장비는 리버인 엇갈림이 생긴다.
//                          ② DEMO_VALID_STATUS / DEMO_RESTORE_STATUS export
//                             ・ 회수 후 점검을 먼저 하는 운영으로 바꾸려면
//                               DEMO_RESTORE_STATUS 를 '점검중' 으로만 고치면 된다 (한 줄)
//                          ③ 장비 목록 위치 칸에 [↩ 회수] 버튼 — location !== '리버' 일 때만 노출
//                             ・ confirm 에 "무엇이 어떻게 바뀌는지"를 미리 보여준다
//                               (상태를 안 건드리는 경우 '변경 없음' 이라고 명시)
//                             ・ 처리 후 해당 행만 교체 (전체 재조회 없음)
//                       ※ migrations/v7.245-fix-demo-return.sql 은 여러 대 일괄 정리용으로 유지.
//                          updated_at 컬럼 존재 여부를 알 수 없어 해당 참조는 제거했다.
//                       SQL 마이그레이션: 없음
// v7.245 (2026-07-31) — 🔧 데모 회수 시 장비가 안 돌아오던 문제 (조용한 실패)
//                       증상: 회수 처리했는데 장비가 계속 '대여중'·대여처에 남아 있었다.
//                       진단 (SQL 우선):
//                          ・ RET-20260731-103809 → 품목행수 0 · 장비연결수 0
//                            = demo_rental_items 행 자체가 없는 이관 대여(OUT-LEGACY-0008)
//                          ・ processReturn 5단계는 equipment_id 로 장비를 찾는데
//                            찾을 게 없으니 0건 업데이트하고 조용히 성공을 반환했다
//                          ・ 전수 조회 결과 이 문제는 이 건 하나가 아니다:
//                            location='이엘티비' 12대 · 'QKR' 8대 · '원민식' 4대 등
//                            (앱이 쓰는 형식은 '대여중(업체)' — 괄호 없는 값은 전부 이관 데이터)
//                       변경:
//                          ① DemoService.processReturn — 실제 복원 대수를 세어 반환
//                             ・ .filter(Boolean) 추가 (cancelPendingRental엔 있었는데 여기만 없었음)
//                             ・ .select('id') 로 업데이트된 행을 받아 restored 카운트
//                             ・ 반환: { return_number, itemRows, linked, restored, restoreErr }
//                          ② submitDemoReturn — restored 를 보고 결과 메시지를 나눈다
//                             ・ restored > 0  → '✓ 회수 완료 · 장비 N대 리버로 복원'
//                             ・ restored = 0  → ⚠ 경고. 왜 0인지(연결 없음/부분 연결)까지 표시하고
//                                                장비 목록에서 직접 되돌리라고 안내
//                             ・ restoreErr    → ⚠ 복원 실패 사유 표시
//                          ③ 회수 모달 결과 박스에 'warn'(주황) 상태 추가 + 줄바꿈 표시
//                       ★ 근본 원인은 못 고친다 — 레거시 대여는 장비 연결 정보가 애초에 없다.
//                          자동 복원이 불가능하므로 **조용히 넘어가지 않게** 만드는 것이 이번 수정.
//                       ※ 기존에 잘못 남은 데이터: migrations/v7.245-fix-demo-return.sql
//                          (대여처별 확인 → 진행 중 대여와 교차 검증 → location 복원 순서)
//                       ※ status 컬럼에 '대여중'·'테스트중' 같은 값이 섞여 있다.
//                          앱이 쓰는 값은 정상작동/불량/수리중/점검중/확인불가 5종뿐 —
//                          정리는 실물 상태 판단이 필요해 SQL STEP 5에 주석으로만 남겼다.
//                       SQL 마이그레이션: v7.245-fix-demo-return.sql (데이터 정정 · 선택)
// v7.244 (2026-07-31) — ⎋ Esc 로 모달 닫기 + 출고 시리얼 후보를 클릭으로 열기
//                       사용자 요청 2건 (부분 환불은 별도 — 아래 ※ 참고).
//                       ① Esc 로 모달 닫기 (App.js 전역)
//                          ・ escStackRef — 열려 있는 오버레이 31개를 우선순위 배열로 두고
//                            **가장 안쪽 하나만** 닫는다. 배열 순서가 곧 우선순위.
//                            (1) 입력 보조 8종 → (2) 팝오버/드롭다운 4종 → (3) 모달 19종
//                          ・ 배열은 매 렌더 후 useEffect 로 갱신, 리스너는 mount 시 1회만 등록
//                            → 상태 31개를 deps에 나열하지 않아도 항상 최신값을 본다
//                          ★ 로컬 onKeyDown('Escape')이 이미 있는 입력 보조를 최상단에 둔 이유:
//                            로컬·전역이 같은 Esc로 둘 다 실행되는데, 최상단이면 "이미 닫힌 것을
//                            한 번 더 닫고" 종료하므로(멱등) 뒤의 모달까지 닫히는 사고가 안 난다
//                          ・ 예외 1곳 — 장부 수기입력 팝오버(v7.239)의 Esc는 '팝오버 닫기'가
//                            아니라 '목록으로 뒤로'라, 로컬에서 e.stopPropagation() 으로 막았다
//                          ・ 한글 조합 중 Esc(조합 취소)는 무시: e.isComposing || keyCode===229
//                          ・ 제외: 환불 사유 입력·새 메모 작성처럼 **작성 중 내용이 날아가는**
//                            인라인 영역은 스택에 넣지 않았다
//                       ② 출고 시리얼 입력칸 — 클릭으로도 재고 후보 열기
//                          ・ v7.196에서 '입력이 있을 때만 표시'로 바꾸면서 ↓ 키가 유일한
//                            브라우징 경로가 됐는데, 그걸 모르면 재고 시리얼을 볼 방법이 없었다
//                          ・ onClick 추가 — 입력이 비어 있어도 전체 후보를 연다
//                          ・ 적용: 출고요청 처리 모달(13118) + 킵 출고 모달(20651) 2곳
//                          ・ placeholder: '(↓ 키: …)' → '(클릭 또는 ↓ 키: 재고 후보 보기)'
//                       ※ 부분 환불(요청 1번)은 이번 빌드에 없다. processedAs 를 읽는 지점이
//                          26곳(집계·필터·뱃지·요약)이라 데이터 모델을 먼저 확정해야 한다.
//                       SQL 마이그레이션: 없음
// v7.243 (2026-07-30) — 📍 용산구 → 지역 '관내' 자동 표기
//                       사용자 요청: 서울시 용산구일 때 지역을 '관내'로.
//                       배경: 기사 요금이 관내/관외로 갈리는데 guessRegion(v7.219)이
//                             '서울시 용산구'를 뱉어서 매번 손으로 '관내'로 고쳐 넣고 있었다.
//                       변경 (App.js · guessRegion):
//                          ・ HOME_DISTRICTS = ['용산구'] / HOME_REGION_LABEL = '관내' 신설
//                            → 사무실 이전이나 구 추가는 이 배열만 고치면 된다
//                          ・ isHomeDistrict(tokens, i) — 매칭된 구 토큰이 목록에 있고
//                            시·도 접두어가 서울이거나 아예 없을 때만 관내로 본다
//                            ⚠ '중구'처럼 여러 시에 같은 이름이 있는 구를 나중에 넣을 때
//                               '부산광역시 중구'가 관내로 잡히는 사고를 막는 가드
//                          ・ guessRegion 한 곳만 고쳐 3개 지점에 모두 반영된다:
//                            출고요청 폼의 [지역] 미리보기(13919) / 제출 시 quickRegion 저장(7109)
//                            / 시트 payload D열(8609)
//                       검증: '서울시 용산구 …' · '서울특별시 용산구 …' · '서울 용산구 …' ·
//                          '용산구 …' · '[방문수령] 서울시 용산구 …' → 모두 '관내'
//                          '서울시 금천구' · '대전광역시 서구' · '경기도 부천시 원미구' ·
//                          '부산광역시 중구' · '강원도 평창군' → 기존 동작 유지
//                       ※ [직접 입력] 체크 시의 수기 지역값이 우선하는 건 그대로 (v7.228)
//                       ※ 이미 시트에 기록된 과거 행은 그대로 — 다시 [시트 전송] 하면 갱신된다
//                       SQL 마이그레이션: 없음
// v7.242 (2026-07-30) — 🔒 퀵 시트 전송 경로 검증 + 판정 단일화
//                       사용자 요청: 퀵이 아닌 배송방법으로 출고하면 퀵 시트에 쓰이는지 검증.
//                       검증 결과: 안 쓰인다. 시트에 쓰는 경로는 정확히 3곳뿐이다.
//                          ・ [시트 전송] / [완료 처리] 버튼 → UI `if (isQuick)` 블록 안에만 렌더
//                          ・ [제품 출고] 전량 → 코드에서 method 확인
//                          ・ postQuickSheet 호출부도 이 3곳뿐, 다른 진입점 없음
//                          ・ 방문수령은 methodLabel이 '방문수령'으로 저장돼 자동 제외
//                            (v7.240까지 있던 !shipPickup 가드는 사실 중복이었다)
//                       다만 갭 2개를 찾아 함께 수정:
//                          ① 함수 레벨 method 가드 부재 (v7.241에서 생긴 구멍)
//                             ・ sendQuickToSheet / toggleQuickComplete 는 type만 확인했다.
//                               v7.240까지는 1차 전송이 제출 시점의 shipMethod 가드를 달고
//                               있었는데 v7.241에서 1차 책임을 함수로 옮기며 사라졌다.
//                             ・ 두 함수에 isQuickMethod(note.method) 가드 추가 → 방어 2겹
//                          ② 제품 출고 자동 완료 경로만 '퀵서비스' 하나만 비교 (v7.233부터)
//                             ・ UI는 '퀵서비스'|'퀵' 둘 다 퀵으로 보는데 이 경로만 아니어서,
//                               method='퀵'인 건은 1차는 기록되고 완료 전환은 안 되는 상태였다
//                             ・ → isQuickMethod() 로 통일
//                       변경 (App.js):
//                          ・ isQuickMethod(m) 신설 — 판정 단일 출처
//                          ・ 흩어져 있던 동일 판정 4곳(15471/15549/15607/19771) 치환
//                          ・ 전송 경로 3곳도 같은 함수를 통과
//                       DB 확인: method 분포 = 택배 114 / 퀵서비스 46 / 1층배송 28 /
//                          방문수령 21 / 직접 설치 5 / RMA 3 / DOA 3 (총 220)
//                          → '퀵'·'택배배송' 실데이터 0건. 레거시 판정은 남겨 뒀다 —
//                            없애면 손으로 넣은 값 하나에 판정이 다시 갈라진다.
//                       SQL 마이그레이션: 없음
// v7.241 (2026-07-30) — 🕘 배차시간을 '버튼 누른 시각'으로 (1차 시점 이동)
//                       사용자 설계: 자동작성을 [시트 전송](1차) → [제품 출고](2차) 로 옮긴다.
//                       배경: 배차시간은 n8n 행 매칭 키(Column to match on = '배차시간')다.
//                             v7.222~v7.240은 출고요청 제출 순간 확정해 넣어서 시트 배차시간이
//                             곧 등록시각이었고, 등록 시 1차 행이 이미 생겨 있으니 나중에
//                             배차시간만 바꿀 수가 없었다 (기존 행을 못 찾아 새 줄이 생긴다).
//                       변경 (App.js):
//                          ① 출고요청 제출 시 quickDispatchTime 확정 제거
//                          ② 출고요청 insert 직후의 시트 1차 자동 전송 제거
//                             ・ sendQuickSheetDraft() 삭제 (호출자 없음)
//                          ③ stampDispatchTime(note) 신설 — 시트에 **처음 올라가는 순간**
//                             배차시간을 찍고 그 뒤 동결. 세 경로 모두 이 함수를 통과한다:
//                             ・ [시트 전송]        → 보통 여기가 1차
//                             ・ [제품 출고] 전량   → 전송을 건너뛰고 출고했으면 여기가 1차
//                             ・ [완료 처리] 수동   → 위 둘을 다 건너뛴 경우 대비
//                             → 어느 경로로 시작해도 시트 행은 항상 1줄, 배차시간은 불변
//                          ④ quickStage(note) 신설 — stage 판정을 **스탬프 전 원본**으로 한다.
//                             첫 기록은 어느 버튼에서 출발했든 'draft'(행 생성)로 보낸다.
//                             n8n이 stage로 append/update를 갈라 쓰더라도 안전하도록.
//                          ⑤ [시트 전송] 미전송 상태 tooltip 문구를 새 동작에 맞게 수정
//                       ★ 구 데이터 보호: quickDispatchTime 없음 + quickSheetSentAt 있음
//                          = 이미 등록시각 기준으로 시트에 행이 있는 건 → 스탬프하지 않고
//                          기존 created_at 폴백을 그대로 태운다. 새 시각을 찍으면 중복 행이 된다.
//                       ⚠ 동작 변화: 출고요청만 등록하고 아무 버튼도 안 누르면 시트에 기록이 없다.
//                          (기존에는 등록만 해도 1차 행이 남았다) — [제품 출고] 시 자동 기록되므로
//                          실제 누락은 '요청만 하고 출고도 안 한' 건에 한정된다.
//                       ※ n8n·시트 변경 없음 — 매칭 키는 계속 배차시간이다.
//                       SQL 마이그레이션: 없음
// v7.240 (2026-07-30) — 🟢 [갱신됨] 초록 표기 + 퀵 시트 담당자 직책 제거
//                       사용자 요청 2건.
//                       ① [시트 전송] → 전송 완료 상태(갱신됨)를 초록으로
//                          ・ v7.230에서 버튼을 퀵 섹션 색(빨강 #ba1a1a)에 맞췄더니
//                            전송 전/후가 같은 빨강 계열이라 상태 구분이 안 됐다.
//                          ・ 미전송: 흰 바탕 + 빨강 테두리 (그대로) / 전송됨: 초록 #009844 채움
//                            → 옆의 [배송완료]와 같은 색 규칙이 되어 "끝난 것은 초록"으로 통일
//                       ② 배차 시트에 기록되는 담당자명에서 직책 제거 ('김중규 이사' → '김중규')
//                          ・ stripStaffTitle() 신설 — 알려진 직책이 **맨 뒤 토큰**일 때만 떼어낸다
//                            STAFF_TITLES = 대표/사장/부사장/전무/상무/이사/본부장/실장/팀장/
//                                           부장/차장/과장/대리/주임/사원/인턴
//                            직책 없는 이름·자유 입력값('외부업체 담당')은 손대지 않는다
//                          ・ 적용: buildQuickSheetPayload 의 manager(B) · requester(참고용)
//                          ・ ⚠ staffPhones[author] 조회는 **풀네임 그대로** — 맵의 키가 풀네임이다
//                          ・ ⚠ STAFF_LIST 원문 / 앱 화면 표기 / 배송시트지 작성자·요청자 칸은 무변경.
//                            직책은 사내에서 쓰는 정보이고, 떼는 건 시트로 나가는 순간만이다.
//                          ・ 수령인·딜러명은 대상 아님 (고객 이름이라 직책을 떼면 안 된다)
//                       ※ 이미 시트에 기록된 과거 행은 그대로 — 다시 [시트 전송] 하면 갱신된다
//                       SQL 마이그레이션: 없음
// v7.239 (2026-07-30) — 📝 입고 장부처리 '수기입력'에 실제 내용을 적을 수 있게
//                       사용자 요청: 입고에서 장부를 수기입력으로 바꿀 때 수기로 기입 가능하도록.
//                       배경: v7.187에서 '수기입력' 상태만 만들어 놨는데 정작 무엇을 수기로
//                             처리했는지 적을 곳이 없어, 뱃지가 '수기입력' 넉 자로만 남았다.
//                             출고 쪽 매출여부는 이미 salesStatusManual 로 같은 일을 하고 있었다.
//                       변경 (App.js — 배송시트지):
//                          ① 장부처리 팝오버에 입력 모드 추가 (mode:'manual')
//                             ・ [수기입력] 클릭 → 즉시 저장하지 않고 텍스트 입력 화면으로 전환
//                             ・ 이미 '수기입력' 상태여도 눌러서 내용 수정 가능 (isActive skip 예외)
//                             ・ Enter 또는 [저장] 으로 확정 / [뒤로]·Esc 로 목록 복귀
//                             ・ 입력칸은 uncontrolled(defaultValue + ref) — 타이핑마다
//                               시트 전체가 리렌더되지 않게 (salesStatusManual과 동일 패턴)
//                          ② note JSON 에 `bookkeptManual` 저장 (신규 필드)
//                             ・ 완료/미완료로 되돌리면 delete — 유령 텍스트가 남지 않는다
//                             ・ 빈 값으로 저장하면 뱃지에 '수기입력'만 표시
//                          ③ 입고 행 뱃지: 수기입력이면 label 대신 적어 넣은 내용을 표시
//                             (maxWidth 170 · ellipsis, 전체 내용은 tooltip)
//                          ④ saveBookkept(rowId, state, manualText) 로 저장 경로 일원화
//                             — 기존 팝오버 안에 인라인으로 있던 fetch→merge→update 를 추출
//                       ※ 입고 등록 페이지·HDD 일괄 입고의 체크박스는 그대로 (완료/미완료 2-state).
//                          수기입력은 등록 후 시트에서 지정하는 흐름을 유지.
//                       ※ note 필드 추가이므로 SQL 마이그레이션 없음. 기존 행은 bookkeptManual
//                          부재 → 지금과 동일하게 '수기입력' 표시.
//                       SQL 마이그레이션: 없음
// v7.238 (2026-07-29) — 📷 입고 스캔에 조회/실사 스캐너 이식 (줌 · 인식 박스 · 손전등)
//                       사용자 요청: 모바일 재고조사(조회) 때의 스캔 방식을 입고에도 적용.
//                       배경: v7.198~v7.210에서 MobileLookup만 개선하고 MobileScan은 의도적으로
//                             한 줄도 안 건드렸다(등록 경로 회귀 위험 회피). 그 격차를 이번에 메운다.
//                       변경 (MobileScan.jsx):
//                          ① 줌 컨트롤 (기본 2×, `inv_scan_zoom`)
//                             ・ 네이티브: ML Kit setZoomRatio/get{Min,Max}ZoomRatio (startScan 이후에만 동작)
//                             ・ 웹: track.applyConstraints({advanced:[{zoom}]})
//                             ・ 지원 기기에서만 UI 노출 (zoomRange null이면 숨김)
//                          ② 🟩 인식 박스 — 코드마다 처리 결과를 색으로 표시
//                             판별중(노랑) / 시리얼(초록) / 슬롯(초록) / 큐 추가됨(파랑) /
//                             검수 대기(주황) / 제품(보라) / 모델?(주황) / 대상 아님(회색)
//                             ・ 조회 화면의 초록·빨강 2색 대신 **입고 결과에 맞춘 배색**
//                             ・ 네이티브: cornerPoints + IMG_PRESETS 자동 추정 + [↕ 위치 보정] 슬라이더
//                             ・ 웹: BarcodeDetector boundingBox / zxing position (좌표 정확)
//                               ※ <video>가 objectFit:'cover' 라 Lookup(contain)과 환산식이 다르다
//                             ・ 0.7초 미검출 시 소멸, 120ms 주기로 상태 전이 반영
//                          ③ 네이티브 손전등 — toggleTorch가 웹 stream만 다뤄서 앱에선 무반응이었다
//                          ④ 엔진 배지 'ML Kit' → 'ML Kit (Native)'
//                       ★ 등록 로직은 그대로다:
//                          ・ boxStateOf() 는 codeClassRef/queue/slot/검수 를 **읽기만** 한다
//                          ・ decodeWithZXingWASM 반환 형태(문자열 배열) 유지 — 좌표는 별도 ref로 전달
//                          ・ processDetectedCodes / classifyCodes / appendToQueue 무변경
//                       ※ '대상 필터'는 이미 있었다 — isValidSerialPattern(v7.132)이
//                          Lookup의 isScannableCode와 동일 규칙 (EAN/UPC/ITF14 · PQKP/CQKP 제외)
//                       ※ APK 재빌드 불필요 (WebView가 웹 프론트를 불러옴)
//                       SQL 마이그레이션: 없음
// v7.237 (2026-07-29) — ⏰ 배송시트지 자동이월 시각을 우측 상단 메뉴로 이동
//                       사용자 요청: v7.234에서 퀵 시트 설정을 옮긴 것과 같은 정리.
//                       배경: [배송 목록 / 메모] 서브탭 줄 오른쪽 끝에 셀렉트가 붙어 있어
//                             탭·검색과 뒤섞여 보였고, '한 번 정하고 마는 값'이라 상시 노출할 이유가 없다.
//                       변경 (App.js — 배송시트지):
//                          ① 서브탭 줄의 [🕘 자동이월 시각 ▾] 블록 제거
//                          ② [메뉴] 드롭다운 맨 아래에 '자동이월 시각' 행 신설
//                             ・ 다른 항목과 달리 버튼이 아니라 행 안에서 바로 고르는 형태
//                               (모달을 하나 더 만들 만큼의 설정이 아님)
//                             ・ stopPropagation — 셀렉트를 조작해도 메뉴가 닫히지 않는다
//                             ・ 값이 행에 그대로 보이므로 메뉴만 열면 현재 설정 확인 가능
//                          ③ 드롭다운 minWidth 280 → 330 (셀렉트가 들어갈 자리 확보)
//                       ※ 저장 경로는 setAutoDeferMinutesPersist 그대로 (app_settings 공유 저장, v7.127)
//                       ※ 메뉴는 [배송 목록] 탭에서만 렌더되므로 [메모] 탭에서는 접근 불가
//                          — 메모 탭에서 이월 시각을 만질 일은 없다고 보고 그대로 둠
//                       SQL 마이그레이션: 없음
// v7.236 (2026-07-29) — 🔎 재고 목록 검색 중에도 입고 정보 수정
//                       사용자 요청: 검색하면 제품 상태를 못 고친다. 검색 결과에서도 수정하게 해달라.
//                       원인: 검색 시 렌더되는 '모델 그룹 카드' 뷰는 expand 를 **출고 건에만** 열어놨다.
//                             (원래 목적이 '출고된 시리얼의 배송지 확인'이었음)
//                             일반 목록 뷰에만 있던 [입고 정보 수정] 패널에 접근할 방법이 없었다.
//                       변경 (App.js — 재고 목록):
//                          ① renderInboundEditPanel(item, compact) 신설 — 입고일/제품 상태/보관 위치/
//                             출처 + 시리얼 삭제 패널을 **한 곳에서만** 렌더
//                             ・ 일반 목록(12500번대)에 인라인으로 박혀 있던 ~90줄을 이 함수로 이동
//                             ・ 두 뷰가 갈라져 한쪽만 고쳐지는 일(예: v7.189 AS 옵션 누락)을 막는다
//                          ② 검색 결과 카드의 시리얼 행: expand 조건 isShipped 제거 → 전 항목 클릭 가능
//                             ・ chevron 도 전 행에 표시 (기존엔 출고 건만 파란 화살표)
//                             ・ 출고 건은 기존 수령인/배송지 카드 + 편집 패널 둘 다 표시
//                          ③ 그룹 시리얼 목록 스크롤 박스: 해당 그룹에 열린 행이 있으면 360 → 680px
//                             (360px 안에서 편집 패널이 잘려 보였음)
//                       ※ 저장 경로는 기존 saveInventoryField 그대로 — items state 를 갱신하므로
//                          검색 결과 그룹(items 파생)도 즉시 반영된다
//                       ※ 입고일 셀 더블클릭 인라인 편집은 그대로 유지 (셀 클릭은 stopPropagation)
//                       SQL 마이그레이션: 없음
// v7.235 (2026-07-29) — 📅 입고 상세 모달에서 입고일 직접 수정
//                       배경: v7.226에서 밝혀진 stale-date 버그로 과거 날짜가 박힌 입고 건이 있고,
//                             그동안은 SQL로만 고칠 수 있었다.
//                       변경 (App.js):
//                          ① 입고 상세 모달의 '입고일'을 date input 으로 교체 (입고 타입만)
//                          ② updateInboundDate(item, newDate)
//                             ・ transaction_log.transaction_date (배송시트지 날짜)
//                             ・ 해당 입고에 포함된 시리얼들의 inventory.receipt_date
//                             **둘을 함께** 갱신 — 한쪽만 고치면 배송시트지와 재고 목록의
//                             입고일이 어긋나 오히려 더 헷갈린다
//                             ・ EXT- / NOSN- 접두 시리얼은 inventory row가 없으므로 제외
//                             ・ 변경 전 confirm 으로 영향 범위(재고 N개) 명시
//                             ・ 재고 갱신만 실패하면 경고 후 개별 수정 안내
//                               (배송시트지 날짜는 이미 반영된 상태이므로 되돌리지 않음)
//                          ③ 저장 중 input disabled + '저장 중…' 표시
//                       ※ 출고 건의 날짜는 대상 아님 — 예상 출고일/자동이월 로직과 얽혀 있어
//                          별도 검토 필요
//                       SQL 마이그레이션: 없음 (v7.226-fix-receipt-date.sql 대신 UI로 처리 가능)
// v7.234 (2026-07-29) — ⚙ 퀵 시트 설정을 우측 상단 메뉴로 이동
//                       사용자 요청: 웹훅 링크를 우측 상단 메뉴 탭으로 빼달라.
//                       변경 (App.js — 배송시트지):
//                          ① 툴바에서 [퀵 시트 웹훅] 입력란 + [담당자 번호] 버튼 제거
//                             → 툴바가 탭·검색과 뒤섞여 복잡했고, 둘 다 '한 번 설정하고 마는 값'이라
//                               상시 노출할 이유가 없다
//                          ② 우측 상단 [메뉴]에 '퀵 시트 설정' 항목 신설
//                             ・ 웹훅 미설정이면 주황 '미설정' 배지, 설정돼 있으면 초록 체크
//                               → 메뉴를 열지 않아도 설정 여부를 알 수 있게
//                          ③ 기존 담당자 번호 모달을 '퀵 시트 설정' 모달로 확장
//                             ・ 상단: n8n 웹훅 URL (blur/Enter 저장 · 모든 PC 공유 안내)
//                             ・ 하단: 담당자 번호 매핑 (STAFF_LIST 6명)
//                             ・ 두 값 모두 app_settings에 저장되는 같은 성격이라 한 화면에 묶음
//                          ④ 웹훅 미설정 alert 안내 문구를 새 위치로 갱신
//                       SQL 마이그레이션: 없음
// v7.233 (2026-07-29) — ✅ 제품 출고 완료 시 배차 시트 진행상황 자동 '완료'
//                       사용자 요청: 제품 출고를 완료하면 진행상황을 완료로 바꾸면 될 것 같다.
//                       변경 (App.js — 출고 커밋 4단계 직후):
//                          ・ updatedModels 전부 fulfilled 이고 txLogSaveFailed 아닐 때,
//                            해당 요청 row(transaction_log.serial = reqId)를 조회해
//                            method==='퀵서비스' && !quickCompletedAt 이면
//                            quickCompletedAt = 현재시각 저장 + 시트 갱신(stage:'final')
//                            → H 진행상황 '완료', K 완료시간 자동 기입
//                          ・ 이미 [완료 처리]로 완료된 건은 건너뜀 (완료시각 덮어쓰기 방지)
//                          ・ try/catch 로 감싸 실패해도 출고 결과에 영향 없음.
//                            console.warn 만 남기고, 이후 수동 [완료 처리]로 복구 가능
//                       ※ v7.229의 수동 [완료 처리] 버튼은 그대로 유지 —
//                          출고 없이 배차만 완료되는 경우(직발 등)와 완료 해제에 필요
//                       SQL 마이그레이션: 없음
// v7.232 (2026-07-29) — 🏢 퀵 시트 비고에 업체명 자동 채움
//                       사용자 설명: 퀵 비고에는 보통 업체명을 적는다.
//                       마침 note.recipient(수령인) / dealer_name 이 곧 업체명이므로 재사용.
//                          (배송시트지 '딜러/수령인 정보' = 이선디지탈 / 주식회사 씨에스텍 …)
//                       변경 (App.js):
//                          ① payload.remark 우선순위:
//                             quickRemark(직접 입력) → recipient(수령인) → dealer_name → ''
//                             ※ 별도 필드로 복사 저장하지 않고 전송 시점에 fallback —
//                               수령인을 나중에 고쳐도 시트 비고가 따라오게 하기 위함
//                          ② 출고요청 폼 비고칸: placeholder에 현재 수령인 표시 +
//                             '시트 기록: 이선디지탈 · 수령인에서 자동' 미리보기
//                          ③ 배송시트지 인라인 비고: 미입력이면 '비고 입력…' 대신
//                             수령인을 진한 글자로 보여주고 옆에 주황 '자동' 배지
//                             → 빈칸처럼 보이지만 실제로는 값이 나간다는 걸 명확히 함
//                       SQL 마이그레이션: 없음
// v7.231 (2026-07-29) — 📝 퀵 시트 전용 비고 분리 + 인라인 3칸 확대
//                       사용자 보고: 배송시트지 비고가 퀵 시트 비고로 넘어가는데 별개 정보다.
//                       원인: payload.remark 가 note.memo(배송시트지 비고)를 그대로 쓰고 있었음.
//                             배송시트지 비고는 사내 메모, 퀵 시트 비고는 기사 전달사항이라 용도가 다름.
//                       변경 (App.js):
//                          ① note.quickRemark 신설 — payload.remark(J열)는 이제 이 값만 사용.
//                             note.memo 는 배송시트지에만 남고 시트로 나가지 않는다
//                          ② 출고요청 폼: [기사님 번호] 아래에 [비고] 입력란 추가
//                             라벨에 '(퀵 시트에만 기록)' 명시해 배송시트지 비고와 혼동 방지
//                          ③ 배송시트지 인라인 편집에 비고 행 추가 (차량 · 기사님 · 비고 3칸)
//                          ④ 세 칸 크기 확대 — 값이 작아 읽기 어렵다는 피드백 반영
//                             ・ 표시: 10.5px → 12px, 아이콘 11 → 13, 값 있으면 굵게 + 연한 배경
//                             ・ 편집: 11px → 12.5px, padding 3/7 → 6/9, 테두리 1 → 1.5px
//                             ・ 값이 있는 칸은 글자색을 진하게(#0b1c30) 해 입력 여부가 한눈에 구분됨
//                       ※ v7.216~230에서 시트로 나가던 remark(=배송시트지 비고)는 이제 안 나간다.
//                          기존 건은 [비고] 칸에 다시 입력 후 [시트 전송]하면 반영된다
//                       SQL 마이그레이션: 없음
// v7.230 (2026-07-29) — 🔴 시트 연동 버튼을 퀵 표시 위로 이동 + 빨간색
//                       사용자 요청: [시트 전송] 버튼을 빨간색으로, 퀵 위쪽에 배치.
//                       변경 (App.js — 배송시트지):
//                          ① [시트 전송] · [완료 처리] 를 '배송지' 셀 뱃지 줄에서 제거하고
//                             '발송 종류' 셀 최상단(🛵 퀵 표시 위)으로 이동
//                             ※ 두 버튼은 한 쌍이라 함께 옮김 — 서로 다른 컬럼에 흩어지면
//                                동선이 끊긴다. 배송지 셀은 원래대로 [직발]/[엑셀]만 남음
//                          ② [시트 전송] 색상 초록 → 빨강 #ba1a1a (퀵 섹션 색과 통일)
//                             ・ 미전송: 흰 배경 + 빨간 테두리·글자
//                             ・ 전송됨: 빨강 채움 + 흰 글자 ('갱신됨')
//                          ③ [완료 처리]는 초록 유지 — '전송'과 '완료'는 다른 동작이라
//                             같은 색이면 구분이 어렵다
//                       SQL 마이그레이션: 없음
// v7.229 (2026-07-29) — ✅ 배차 시트 마지막 빈칸(K 완료시간) 채우기 + H 진행상황 전환
//                       남아 있던 칸: K 완료시간 (그리고 H는 '배차' 고정이었음).
//                       변경 (App.js):
//                          ① payload 연동
//                             ・ progress(H) = note.quickCompletedAt ? '완료' : '배차'
//                             ・ completedAt(K) = note.quickCompletedAt
//                             배차시간과 같은 fmtSheetDateTime 형식 사용 (시트 표기 일치)
//                          ② toggleQuickComplete(item) — 배송시트지 [완료 처리] 버튼
//                             ・ 클릭 시 note.quickCompletedAt = 현재시각 저장
//                             ・ 저장 후 곧바로 시트까지 갱신 (stage:'final') — 버튼 두 번 누를 이유 없음
//                             ・ 웹훅 미설정이면 로컬 저장만 하고 넘어감
//                             ・ 시트 갱신만 실패하면 '저장은 됐고 [시트 전송]으로 재시도 가능' 안내
//                               (완료 처리 자체를 되돌리지 않는다)
//                          ③ 완료 해제 — 이미 완료면 확인 후 quickCompletedAt 삭제.
//                             시트도 '배차' / 완료시간 공란으로 되돌아간다
//                          ④ 버튼 상태: 미완료 ⏱ '완료 처리'(흰 배경) / 완료 ✓ '배송완료'(초록 채움)
//                             tooltip에 완료시각 표시
//                       이로써 배차 시트 A~K 전 칸이 앱에서 채워진다.
//                       SQL 마이그레이션: 없음
// v7.228 (2026-07-29) — 📍 지역 칸 기본 잠금 + [직접 입력] 체크로 해제
//                       사용자 요청: 지역은 기입을 막아두고 체크버튼으로 수기 입력.
//                                   '주소에서 자동 추출' → '배송지 주소에서 자동 추출'로 문구 변경 + 주황 강조.
//                       변경 (App.js — 출고요청 폼 퀵 블록):
//                          ① state shipRegionManual 신설 (기본 false = 잠금)
//                          ② 잠금 상태: input disabled + 회색 배경, 배송지 주소 자동 추출값을 표시
//                          ③ 라벨 우측 [직접 입력] 체크박스로 해제
//                             ・ 켤 때: 자동값을 미리 채워 수정만 하면 되게
//                             ・ 끌 때: 수기값을 비워 자동값으로 원복
//                             ・ v7.218의 [자동] 버튼은 체크박스로 대체돼 제거
//                          ④ 안내 문구 '배송지 주소에서 자동 추출' / '직접 입력' 을 주황(#e65100) 강조
//                          ⑤ 추출 실패 시 안내를 '[직접 입력]을 체크해 수기로 넣어주세요'로 구체화
//                          ⑥ 저장 로직도 잠금 상태 반영 —
//                             수기값은 [직접 입력]이 켜져 있을 때만 채택 (꺼진 상태의 잔여값 무시)
//                       SQL 마이그레이션: 없음
// v7.227 (2026-07-29) — 🗓 같은 stale-date 패턴 전수 점검 + 창 복귀 시 갱신
//                       사용자 제안: 탭 이동 때도 날짜를 갱신하면 어떨까?
//                       → 탭 이동은 v7.226이 이미 처리 중(useEffect deps=[page]).
//                         전수 점검해 보니 **같은 패턴이 하나 더** 있었다.
//                       ── shipExpectedDate (예상 출고일) — 동일한 useState(today()) ──
//                          입고일보다 위험하다:
//                            ① transaction_date 가 과거로 박혀 배송시트지 기본 화면에서 사라지고
//                            ② autoDefer = !(shipExpectedDate < today()) 이므로
//                               **자동 이월까지 꺼진다** → 요청이 영영 앞으로 안 밀림
//                       변경 (App.js):
//                          ① shipDateTouchedRef 추가, page==='ship' 진입 시 오늘로 재설정
//                          ② 과거 날짜일 때만 경고 (미래는 내일 출고 예약 등 정상 사용이므로)
//                             입력칸 주황 강조 + '지난 날짜입니다 — 배송시트지에 안 보이고
//                             자동 이월도 꺼집니다' + [오늘로] 버튼
//                          ③ 출고요청 폼 리셋 시 today() 재설정 + touched 해제
//                          ④ visibilitychange / window focus 에서도 두 날짜 갱신
//                             → 탭 이동 없이 같은 화면을 켜둔 채 자정을 넘겨도 방어된다
//                               (창고 PC가 며칠씩 같은 화면을 띄워두는 실제 사용 패턴)
//                          ※ 사용자가 직접 고른 날짜는 어떤 경로에서도 덮어쓰지 않는다
//                       점검 결과: 그 외 today() 호출부(8036·9193)는 호출 시점 평가라 문제 없음
//                       SQL 마이그레이션: 없음
// v7.226 (2026-07-29) — 🗓 입고일이 페이지 로드 시각에 고정되던 문제 (원인 확정)
//                       단서: "1번 PC 등록은 배송시트지에 안 뜨고, 2번 PC 등록은 뜬다."
//                             같은 DB인데 PC마다 다르다 → 서버가 아니라 클라이언트 상태 문제.
//                       원인: const [regReceiptDate] = useState(today())
//                             useState 초기값은 **컴포넌트 마운트 시 한 번만** 평가된다.
//                             창고 PC처럼 브라우저를 며칠 켜두면 입고일이 로드 당시 날짜로 굳는다.
//                             → 오늘 입고해도 transaction_date에 과거 날짜가 저장되고,
//                               배송시트지 기본 조회 기간이 '오늘 하루'라 화면에서 사라진다.
//                               (DB에는 정상 저장돼 있으므로 '기록 실패'가 아니라 '안 보임')
//                       변경 (App.js):
//                          ① regDateTouchedRef — 사용자가 날짜를 직접 골랐는지 추적
//                          ② page==='inbound' 진입 시, 직접 고른 게 아니면 today()로 재설정
//                             (의도적으로 과거 날짜를 넣은 경우는 건드리지 않음)
//                          ③ 입고일이 오늘이 아니면 입력칸을 주황 테두리로 강조 +
//                             '⚠ 오늘이 아닙니다 — 배송시트지 기본 화면에는 안 보입니다' 안내
//                             + [오늘로] 원클릭 버튼
//                          ④ 등록 성공 후 초기화에도 today() 재설정 + touched 플래그 해제
//                       ※ v7.225의 insert 에러 노출은 그대로 유지 — 다른 실패 원인 조기 발견용
//                       SQL 마이그레이션: 없음
// v7.225 (2026-07-28) — 🩺 입고가 배송시트지에 안 뜨는 문제 — 실패를 보이게 만들기
//                       사용자 보고: 입고하면 배송시트지에 기록이 안 됨.
//                       조사 결과 (원본 대비 diff):
//                          ・ handleRegister(입고 등록) — 변경 없음. 최근 작업 범위 밖
//                          ・ loadTxLog(배송시트지 조회) — 변경 없음
//                          → 최근 업데이트가 원인일 가능성은 낮음
//                       대신 진짜 문제를 발견: **입고의 transaction_log insert가 에러를 버린다.**
//                          `await supabase.from('transaction_log').insert(txRow);` 로 결과를 무시.
//                          → insert가 실패해도 '✓ N개 시리얼 등록 완료!' 가 그대로 표시되고,
//                            재고(inventory)에는 들어갔는데 배송시트지에는 아무것도 안 뜬다.
//                            사용자 입장에서 원인을 알 방법이 전혀 없는 상태 (조용한 실패).
//                          ※ 다른 대부분의 insert는 `const { error: txErr }` 로 확인하고 있었고,
//                            입고 경로 2곳만 누락돼 있었다.
//                       변경 (App.js):
//                          ① handleRegister — txErr 확인. 실패 시 빨간 메시지로
//                             '재고 N개는 등록됐지만 배송시트지 기록에 실패' + 사유(message/code) 표시
//                             + console.error 에 txRow 전문 로깅 (원인 추적용)
//                          ② 임시 시리얼 일괄 입고 — 동일 처리
//                       ⚠ 이 버전은 원인을 **드러내는** 것이 목적이다. 다음 입고 시 뜨는
//                          에러 메시지가 실제 원인(제약 위반 / RLS / 컬럼 타입 등)을 알려준다.
//                       SQL 마이그레이션: 없음
// v7.224 (2026-07-28) — 🚚 인라인 차량 편집에도 추천 드롭다운 연결
//                       문제: v7.223 인라인 편집은 자유 입력만 가능했다. datalist가
//                             출고요청 폼 내부에만 렌더돼 배송시트지에서는 참조할 수 없었음
//                             (id가 DOM에 없으면 list 속성이 조용히 무시된다).
//                       변경 (App.js):
//                          ① QUICK_VEHICLES 모듈 상수 신설 (오토바이/다마스/라보/1톤/2.5톤)
//                          ② <datalist id="quickVehicleOptions"> 를 최상단에서 **한 번만** 렌더
//                             ・ 같은 id를 두 곳에서 정의하면 중복이라 한쪽이 무시된다 → 공용화
//                          ③ 출고요청 폼 안의 지역(local) datalist 제거 (전역 것을 참조)
//                          ④ 배송시트지 인라인 input에 list 연결 (차량 필드만)
//                             기사님 번호는 자유 입력 유지
//                       SQL 마이그레이션: 없음
// v7.223 (2026-07-28) — 🛠 배송시트지에서 차량·기사님 번호 더블클릭 편집
//                       배경: 퀵 업체에 접수하고 나서야 차량·기사님 번호를 알게 되는데,
//                             v7.220~222에서는 출고요청 폼에만 있어 등록 후 채울 방법이 없었다.
//                             (v7.222 테스트에서 452행 차량 칸이 영구히 빈 상태로 확인됨)
//                       변경 (App.js — 배송시트지 '발송 종류' 셀 · 퀵 분기):
//                          ・ 금액 입력 아래에 [차량] · [기사님] 두 줄 추가
//                          ・ 더블클릭 → 인라인 input (Enter 저장 / Esc 취소 / blur 저장)
//                            기존 rmaReturnSerial·directCompany 인라인 편집과 동일 패턴
//                          ・ 값이 없으면 '차량 입력…' 회색 placeholder 로 표시해 편집 가능함을 알림
//                          ・ 출고완료(isProcessed) 건은 편집 불가
//                       ※ saveShipFieldInline은 수정 불필요 — 일반 문자열 분기가 그대로 처리하고,
//                          빈 값 입력 시 note 키를 삭제한다 (기존 동작 재사용)
//                       ※ 출고요청 폼의 차량·기사님 번호 칸은 유지 — 미리 아는 경우도 있으므로.
//                          두 경로가 같은 note 키(quickVehicle/quickDriverPhone)를 쓰므로 값은 하나
//                       운영 흐름: 출고요청(1차 전송) → 퀵 접수 → 시트지에서 차량·기사님 입력
//                                  → [시트 전송](2차) → 시트 같은 행이 채워짐
//                       SQL 마이그레이션: 없음
// v7.222 (2026-07-28) — 🔑 배차시간을 행 매칭 키로 확정 (시트 컬럼 추가 불가 대응)
//                       제약: 해당 구글시트는 사용자가 수정할 수 없음 → reqId 컬럼 신설 불가.
//                       사용자 결정: 기존 컬럼 중 유일성이 있는 '배차시간'으로 매칭.
//                       ⚠ 이 방식의 전제: 1차와 2차가 **완전히 동일한 문자열**을 보내야 한다.
//                          v7.221은 1차=클라이언트 new Date(), 2차=DB created_at 을 써서
//                          값이 어긋나 무조건 새 행이 생기는 상태였다 (배포 전 발견).
//                       변경 (App.js):
//                          ① fmtSheetDateTime() 신설 — 시트 표기와 1:1 일치하는 문자열 생성
//                             '2026. 1. 2 오전 8:53:41' (월/일/시 0채움 없음, 분/초만 2자리)
//                             ・ toLocaleString('ko-KR')은 '2026. 1. 2. AM 8:53:41' 이 나와
//                               일(日) 뒤 점 + AM 표기 때문에 사용 불가 → 직접 조립
//                             ・ 로컬 게터만 사용 (UTC 함정 회피)
//                          ② 제출 시 note.quickDispatchTime 에 확정 저장 (퀵 + 방문수령 아님)
//                          ③ payload.dispatchTime = note.quickDispatchTime 우선.
//                             없으면(v7.222 이전 데이터) created_at을 같은 형식으로 변환
//                          ④ sendQuickSheetDraft의 합성 item 제거 (더 이상 시각을 만들지 않음)
//                       n8n 설정: Column to match on = '배차시간'
//                       ⚠ 남은 리스크: 구글시트가 이 문자열을 날짜로 파싱해 표시 형식을 바꾸면
//                          n8n이 읽어오는 값이 달라져 매칭이 깨질 수 있다. 실제 테스트 필요 —
//                          1차 후 2차를 눌러 시트 행이 1줄인지 2줄인지로 판별.
//                       SQL 마이그레이션: 없음
// v7.221 (2026-07-28) — 🔁 퀵 시트 2단계 기록 (출고요청 시 1차 → 시트 전송으로 2차 갱신)
//                       사용자 설계: 출고요청 → 1차 부분 작성 → [시트 전송] → 2차 전체 작성
//                       변경 (App.js):
//                          ① buildQuickSheetPayload(note, item, stage) 로 payload 생성 분리
//                             ・ 1차/2차가 **같은 구조**를 보낸다. stage: 'draft' | 'final'
//                             ・ "무엇이 부분/전체인지"를 코드로 못 박지 않음 — 각 시점에 채워진
//                               값만 담기고 빈 값은 n8n에서 건너뛰게 한다.
//                               (필드 분배를 하드코딩하면 운영 중 순서가 바뀔 때마다 코드 수정 필요)
//                          ② postQuickSheet(payload) — 전송만 담당
//                          ③ sendQuickSheetDraft() — 출고요청 insert 성공 직후 자동 호출.
//                             ・ 퀵 + 방문수령 아님 인 경우만
//                             ・ await 하지 않음 + try/catch — 실패해도 등록 흐름을 절대 막지 않음
//                               (요청은 이미 DB에 저장된 뒤이고, 이후 수동 버튼으로 복구 가능)
//                          ④ sendQuickToSheet() → stage:'final' 로 전송. 버튼 문구 '갱신됨'으로 변경
//                       ⚠ n8n 쪽 요구사항: reqId 를 행 매칭 키로 사용해야 한다.
//                          ・ stage='draft'  → 신규 행 append (reqId를 별도 컬럼에 함께 기록)
//                          ・ stage='final'  → reqId로 행 검색 후 update
//                          reqId 컬럼(L열 등)이 없으면 2차가 새 행을 만들어 중복된다.
//                       SQL 마이그레이션: 없음
// v7.220 (2026-07-28) — 🛵 기사님 번호 입력칸 추가 (배차 시트 I열 완성)
//                       변경 (App.js — 출고요청 폼 퀵 블록):
//                          ・ state shipQuickDriverPhone 신설 + 폼 리셋 포함
//                          ・ [구분] 아래에 [기사님 번호] 입력란 (선택)
//                          ・ note.quickDriverPhone 저장 (값 있을 때만)
//                          ・ payload.driverPhone 이 시트에서 수기 입력이던 빈 값 → 폼 값으로 연결
//                       이로써 배차 시트 A~K 중 앱에서 채우는 칸은 전부 연결됨:
//                          A 배차시간(작성시간) / B 담당자(작성자) / C 담당자 번호(매핑)
//                          D 지역(주소 추출·수정 가능) / E 차량 / F 구분(선불·착불)
//                          G 금액 / H 진행상황('배차' 고정) / I 기사님 번호 / J 비고
//                          K 완료시간만 시트에서 수기 (배차 시점엔 값이 없으므로)
//                       SQL 마이그레이션: 없음
// v7.219 (2026-07-28) — 📍 시트 '지역'에 시·도 접두어 포함
//                       사용자 보고: '서구'만 나오는데 '대전광역시 서구'까지 필요.
//                       근거: '서구'는 대전·부산·인천·광주에 모두 있어 단독으로는 식별 불가.
//                       변경 (App.js · guessRegion):
//                          ・ v7.218은 매칭된 '…구' 토큰 1개만 반환했음
//                          ・ 주소 시작부터 '…구' 토큰까지를 slice 해서 반환하도록 변경
//                            대전광역시 서구 도산로 403번길 16 …   → '대전광역시 서구'
//                            서울시 금천구 가산동 60-9 (…)        → '서울시 금천구'
//                            경기도 부천시 원미구 중동로 248       → '경기도 부천시 원미구'
//                          ・ 구가 없으면 군 → 시 순서로 대체하는 규칙은 유지
//                            강원도 평창군 대관령면 100 → '강원도 평창군'
//                            경기도 김포시 통진읍 1     → '경기도 김포시'
//                          ・ 시·도 없이 시작하는 주소는 그대로 ('광진구 아차산로 262' → '광진구')
//                       ※ 폼의 [지역] 미리보기·수동 수정은 v7.218 그대로 동작
//                       SQL 마이그레이션: 없음
// v7.218 (2026-07-28) — 📍 시트 '지역'을 '구' 단위로 확정 + 폼에서 미리보기
//                       사용자 확정 규칙: 주소에서 '구'에서 끊는다 (중구 / 금천구).
//                       변경 (App.js):
//                          ① guessRegion() 전면 교체 — 토큰 중 첫 '…구' 를 반환.
//                             없으면 '…군' → '…시' 순으로 대체 (지방 주소 대비).
//                             괄호·쉼표도 구분자로 처리해 '(디지털로 10길 37)' 같은 삽입구를 건너뜀
//                             검증: 서울시 금천구 가산동…→금천구 / 서울시 중구 을지로5길…→중구
//                                   경기도 부천시 원미구…→원미구 / 강원도 평창군…→평창군
//                                   경기도 김포시 통진읍…→김포시
//                          ② 출고요청 폼 퀵 블록에 [지역] 칸 신설
//                             ・ 시트에 들어갈 값을 그대로 미리 보여줌
//                             ・ 자동 추출이 틀리면 직접 입력 가능 (수동 우선)
//                             ・ 수동 입력 시 [자동] 버튼으로 원복
//                             ・ 추출 실패 시 주황색 안내 문구
//                          ③ state shipQuickRegion — 빈 값이면 자동 추출값 사용.
//                             제출 시 note.quickRegion 에 최종값을 확정 저장
//                             (나중에 주소를 고쳐도 시트로 보낸 지역이 흔들리지 않도록)
//                          ④ payload.region = note.quickRegion 우선, 없으면 주소에서 재추정
//                       SQL 마이그레이션: 없음
// v7.217 (2026-07-28) — 📤 퀵 시트 컬럼 매핑 (배차시간/담당자/차량/구분/진행상황)
//                       사용자 제공 시트 구조에 payload를 맞춤:
//                          A 배차시간 = 작성시간(item.created_at)
//                          B 담당자   = 작성자(note.author)
//                          C 담당자 번호 = 작성자명 → 전화번호 매핑
//                          D 지역     = 주소에서 추정 (아래 ④)
//                          E 차량     = 신규 입력칸
//                          F 구분     = 선불/착불
//                          G 금액     = quickPrice
//                          H 진행상황 = '배차' 고정
//                          I 기사님 번호 / K 완료시간 = 빈 값 (시트에서 수기 입력)
//                          J 비고     = note.memo
//                       변경 (App.js):
//                          ① state shipQuickVehicle 신설 — 퀵 가격 아래 [차량] 입력란.
//                             datalist로 오토바이/다마스/라보/1톤/2.5톤 추천 + 자유 입력.
//                             note.quickVehicle 로 저장 (값 있을 때만)
//                          ② [구분] 선불/착불 2버튼 토글 — 기존 shipQuickCOD 상태를 그대로 사용.
//                             ※ 별도 필드를 만들면 착불 체크박스와 값이 두 군데가 되어 어긋나므로
//                               기존 체크박스를 토글 UI로 대체했다 (데이터 단일 소스 유지)
//                          ③ 담당자 번호 매핑 — app_settings 키 'staffPhones' (JSON).
//                             소스에 개인 번호를 박지 않고 앱에서 편집. 설정 바 [담당자 번호] 모달.
//                             STAFF_LIST 6명 × 전화번호, blur 시 자동 저장 + 다른 PC 공유
//                          ④ guessRegion(addr) — 주소에서 행정구역 토큰 최대 3개 추출
//                             '서울 광진구 아차산로 262, 3층' → '광진구 아차산로'
//                             '경기도 부천시 원미구 중동로 248' → '경기도 부천시 원미구'
//                             ⚠ 시트 실제 표기가 들쭉날쭉해 완전 자동화는 불가 — 초안만 채우고
//                               최종 표기는 시트에서 손보는 것을 전제로 한다
//                          ⑤ app_settings 조회를 .in(['quickSheetWebhook','staffPhones']) 로 통합
//                       ※ 편집 모달(기존 요청 수정)의 차량 필드는 미포함 — 신규 등록 폼 기준
//                       SQL 마이그레이션: 없음
// v7.216 (2026-07-28) — 📤 퀵서비스 출고요청 → 구글시트 전송 (n8n 웹훅 · 수동 버튼)
//                       사용자 결정: 경로 = n8n 웹훅 / 시점 = 수동 [시트 전송] 버튼
//                       설계 근거: Inventura는 Auth 없이 Cloudflare Tunnel로 외부 공개돼 있어
//                                 구글 자격증명을 프론트엔드 번들에 넣으면 안 된다.
//                                 → Inventura는 웹훅 POST만, 구글 인증·시트 쓰기는 n8n이 서버에서 처리.
//                       변경 (App.js):
//                          ① app_settings 키 'quickSheetWebhook' 로 웹훅 URL 공유 저장
//                             ・ autoDeferMinutes와 동일 패턴 (localStorage 캐시 + Supabase upsert)
//                             ・ 하드코딩하지 않아 재빌드 없이 URL 변경 가능
//                          ② 배송목록 탭 상단에 [퀵 시트 웹훅] 입력란 (blur/Enter 시 저장)
//                          ③ sendQuickToSheet(item) — JSON POST
//                             payload: reqId, requestDate, method, recipient, phone, zip,
//                                      addr, addrDetail, quickPrice, quickCOD, salesStatus(+Manual),
//                                      author, requester, memo, naverStore,
//                                      itemsText(요약 1칸용) + items[] + totalQty
//                             ※ 시트 컬럼 매핑은 n8n 쪽에서 하도록 구조화된 값 그대로 전달
//                          ④ 배송시트지 [엑셀] 옆에 [시트 전송] 버튼 — method==='퀵서비스' 인 건만
//                             ・ 전송 성공 시 note.quickSheetSentAt 기록 → 버튼이 '전송됨'으로 바뀜
//                             ・ 재전송 시 전송 시각을 보여주고 confirm (시트 행 중복 방지)
//                             ・ 전송 중에는 disabled + '전송 중…'
//                          ⑤ 실패해도 출고요청 데이터에는 영향 없음 (부가 기능이므로 alert만)
//                       ※ 스키마 변경 없음 — note JSON에 quickSheetSentAt만 추가
//                       SQL 마이그레이션: 없음 (app_settings 테이블은 v7.127에서 이미 생성됨)
// v7.215 (2026-07-28) — 🔧 메모 편집 시 페이지가 맨 위로 튀던 문제 수정
//                       사용자 보고: 메모를 수정하려 하면 항상 페이지 최상단으로 이동.
//                       원인 2가지가 겹쳐 있었다:
//                          ① 메모 폴링 useEffect의 의존성에 editingMemoId / memoDrafts /
//                             newMemoOpen 이 들어 있었음. memoDrafts는 타이핑마다 바뀌므로
//                             **글자 하나 칠 때마다 effect가 재실행 → loadMemos() 호출 →
//                             메모 배열 전체 교체**. 스크롤 튐 + 불필요한 DB 쿼리 폭증.
//                             (interval 내부 가드는 폴링만 막았고, effect 본문의 직접 호출은
//                              무방비였음)
//                          ② 편집 진입 시 el.focus() + Range 설정이 브라우저 기본 동작으로
//                             조상 요소를 스크롤시킴
//                       변경 (App.js):
//                          ・ memoPollGuardRef 신설 — 편집 상태를 ref로만 참조하고
//                            폴링 effect 의존성은 [page, txSubTab, connected, loadMemos] 로 축소
//                            → loadMemos는 탭 진입 시 1회 + 10초 주기로만 실행
//                          ・ captureScrolls / restoreScrolls 헬퍼 추가 (모듈 스코프)
//                            편집 진입 직전 조상 스크롤 위치 전부 기록
//                          ・ el.focus({ preventScroll: true }) 로 변경 (구형 대비 try/catch)
//                          ・ selection 설정 후 즉시 + rAF 다음 프레임에 스크롤 복구
//                       SQL 마이그레이션: 없음
// v7.214 (2026-07-28) — 📐 입고 row 비고 칸이 '처리' 컬럼에 그려지던 문제 수정
//                       사용자 보고: 입고건 비고 입력칸이 비고 헤더 아래가 아니라 맨 오른쪽에 있음.
//                       원인 (v7.213의 세로 정렬과는 별개인 가로 배치 문제):
//                          행 그리드는 10컬럼 '36 100 200 110 1fr 130 150 130 1fr 120'.
//                          입고 row는 시리얼 표시를 위해 배송지 셀에 gridColumn:'5 / span 5' →
//                          5~9번을 점유. 9번이 바로 '작성자/요청자·비고' 컬럼이라
//                          비고 셀이 자동배치로 10번(처리 자리)으로, 처리 셀은 11번(암시적 컬럼)으로 밀림.
//                       변경 (App.js — 배송시트지 row):
//                          ① 배송지(시리얼) 셀 span 5 → span 4 (5~8번만 점유)
//                          ② 비고 셀에 isIn 일 때 gridColumn:'9' 명시
//                          ③ 처리 셀에 isIn 일 때 gridColumn:'10' 명시
//                             — 자동배치에 기대지 않고 고정해 회귀 방지
//                       ※ 시리얼 표시 폭이 컬럼 1개(120px)만큼 줄지만 헤더 정렬이 우선
//                       SQL 마이그레이션: 없음
// v7.213 (2026-07-28) — 📐 배송시트지 입고 row 비고 위치 정렬
//                       사용자 보고: 입고 등록 row의 비고 칸만 위로 붙어 다른 row와 어긋남.
//                       원인: v7.187에서 작성자/요청자 블록을 !isIn 조건으로 감쌌는데,
//                             입고 row에서는 그 줄이 통째로 사라지면서 비고가 셀 최상단으로 올라감.
//                       변경 (App.js — 배송시트지 작성자/요청자·비고 셀):
//                          ・ isIn 인 경우 동일 구조의 투명 placeholder 줄을 렌더
//                            (visibility:hidden · '-' / '-' · aria-hidden)
//                          ・ 높이가 동일하게 잡혀 출고 row와 비고 시작선이 맞음
//                       ※ 입고 row의 작성자/요청자 편집 불가 정책은 그대로 (표시만 자리 확보)
//                       SQL 마이그레이션: 없음
// v7.212 (2026-07-28) — 🏪 방문수령에 업체명 입력란 추가
//                       기존 방문수령 블록에는 방문 예정 시간(오전/오후)만 있고,
//                       '방문자 이름'은 수령인 필드를 재사용 중이라 업체를 적을 곳이 없었음.
//                       변경 (App.js — 출고요청 폼):
//                          ① state shipPickupCompany 신설 + 폼 리셋에 포함
//                          ② 방문수령 카드 안(방문 날짜 안내 아래)에 [업체명] 입력란 (선택)
//                             ・ 방문자 이름(shipRecipient)과는 별개 필드
//                          ③ addrLabel에 반영 →
//                             '[방문수령] 지엠솔루션 · 2026.07.28 오전 방문 예정'
//                             addr:addrLabel 로 저장되므로 배송시트지 주소 칸에 그대로 표기됨
//                          ④ note에 pickupCompany 저장 (값이 있을 때만 · 기존 directCompany와 동일 패턴)
//                          ⑤ 폼 하단 '방문수령 모드' 요약줄에도 업체명 표기
//                       ※ 스키마 변경 없음 — note JSON에만 추가
//                       SQL 마이그레이션: 없음
// v7.211 (2026-07-28) — 📄 배송 엑셀: 비고가 배송메세지로 나가던 것 제거
//                       사용자 보고: 배송시트지에서 엑셀 받으면 비고가 배송메세지 칸에 들어감.
//                       원인: v7.194에서 downloadShippingExcel의 I열(배송메세지)에
//                             note.memo를 매핑했음. 비고는 내부 메모 용도라 택배사로
//                             나가면 안 되는 정보였다.
//                       변경 (App.js · downloadShippingExcel):
//                          ・ I열 배송메세지 → 빈 값으로 원복 (v7.49 원래 동작)
//                          ・ memo 변수 제거
//                       ※ v7.194의 A열 우편번호 / B열 주소+상세주소 병합은 그대로 유지
//                       SQL 마이그레이션: 없음
// v7.210 (2026-07-27) — 🔍 인식 거리 확대: 카메라 줌 컨트롤
//                       사용자 보고: 15cm 정도만 벗어나도 바코드를 인식 못 함.
//                       원인: Code128 세로바가 얇아 거리가 멀어지면 좁은 바 1개가 1px 미만이 되어
//                             해독 자체가 불가능해진다. 해상도가 아니라 "바코드가 화면에서
//                             차지하는 픽셀 수"가 병목 → 줌이 유일한 실질적 해법.
//                       변경 (src/MobileLookup.jsx):
//                          ① 네이티브: ML Kit 플러그인 줌 API 사용
//                             getMinZoomRatio / getMaxZoomRatio 로 범위 조회(스캔 시작 후에만 동작)
//                             → setZoomRatio 로 적용. 기본 2×, localStorage 'inv_lookup_zoom' 저장
//                          ② 웹: track.getCapabilities().zoom 확인 후 applyConstraints
//                          ③ 오버레이에 줌 컨트롤 — [1× 2× 3× 5×] 버튼 + 슬라이더(0.1 단위) + 현재 배율
//                             ・ 범위를 벗어나는 프리셋 버튼은 자동 숨김
//                          ④ 시작 화면에 인식 거리 안내 (줌 위치 + 손전등 병행 권장)
//                          ⑤ 플러그인/기기가 줌을 지원하지 않으면 컨트롤 자체를 숨김 (guard)
//                       SQL 마이그레이션: 없음
// v7.209 (2026-07-27) — 📷 실사 대상 제품도 카메라로 인식
//                       사용자 요청: 실사 제품을 타이핑 대신 카메라로 고르고 싶다.
//                       변경 (src/MobileLookup.jsx):
//                          ① 실사 모드에서 제품 미선택 상태여도 카메라 시작 가능
//                             (버튼 문구: '📷 카메라로 제품 인식', 오버레이 제목: '🏷 제품 인식 중')
//                          ② detectAuditProduct(code) 2단 판별
//                             ・ 모델 바코드 → matchCatalog (리전 접미사 제거 포함)
//                               → 즉시 startAudit. 카메라를 끄지 않으므로 같은 라벨의
//                                 시리얼을 바로 이어서 훑을 수 있다
//                             ・ 시리얼 바코드 → inventory에서 product_name 역추적
//                               → 추론이므로 확인 카드로 띄우고 [이 제품으로 실사 시작] 한 번 탭
//                          ③ startAudit 진입 시 codeStateRef/박스 초기화
//                             — 제품 인식 단계에서 찍힌 코드가 실사 카운트를 오염시키지 않게
//                       ── 같이 잡은 결함 2건 ──
//                          ④ 경쟁 조건: auditProductRef를 startAudit/resetAudit에서 동기 세팅.
//                             useEffect 반영 전 프레임이 제품 인식 단계로 다시 빠지던 문제
//                          ⑤ 대상 제품의 모델 바코드가 실사 중 '목록 외'로 오인되던 문제
//                             — 같은 라벨에 계속 보이므로 리전 접미사 제거 후 대상명과 비교해 skip 처리
//                       SQL 마이그레이션: 없음
// v7.208 (2026-07-27) — 📋 제품 실사 모드 (시리얼 단위 실물 대조)
//                       사용자 요청: TS-464-8G 를 조사한다면, 재고목록의 해당 제품 시리얼 전체와
//                                   촬영한 시리얼을 대조해 맞는 것/안 맞는 것을 따로 보고 싶다.
//                       ※ 기존 데스크톱 '재고 실사'는 엑셀 기반 **모델별 수량** 대조라 성격이 다름.
//                          이번 건은 **시리얼 단위 실물 대조**로 상호 보완 관계.
//                       변경 (src/MobileLookup.jsx) — 조회 스캐너에 모드 추가:
//                          ① 상단 모드 탭 [개별 조회] / [제품 실사]
//                          ② 실사 진입 시 제품 검색(카탈로그) → 선택하면 대상 시리얼 로드
//                             ・ 기본 대상: in_stock + reserved + unsellable (현장에 있어야 할 것)
//                             ・ [출고분 포함] 체크 시 shipped 까지 (오배송·반품 확인용)
//                             ・ 최대 2000건
//                          ③ 스캔 결과 3분류 (탭 전환)
//                             ・ ✅ 대조됨 — 목록에 있고 실물 확인됨
//                             ・ ⬜ 미스캔 — 목록에 있는데 아직 안 찍힘 (상태·창고·유형 표시)
//                             ・ ⚠️ 목록 외 — 찍혔는데 목록에 없음.
//                               사유를 DB 조회로 판별: '다른 제품: XXX (재고)' 또는 'DB 미등록 시리얼'
//                          ④ 진행 바 + 상단 카운트(대조됨/미스캔/목록 외), 오버레이 제목에 N/M 표시
//                          ⑤ 박스 색 재정의(실사): 초록=대조됨 / 빨강=목록 외 / 회색=중복 스캔
//                          ⑥ 중복 스캔은 카운트에 반영 안 됨 (auditSeenRef Set)
//                          ⑦ 제품 미선택 시 카메라 시작 버튼 비활성
//                       ⚠ DB 쓰기 없음 — 대조 결과는 화면에만. 재고 반영은 사용자가 PC에서 판단.
//                       SQL 마이그레이션: 없음
// v7.207 (2026-07-27) — ↕ 박스 세로 정합 보정 슬라이더
//                       사용자 보고: 박스가 시리얼 바코드보다 조금 아래에 그려짐 (S22, 자동 1080x1920).
//                       원인 가설 2가지 — 어느 쪽이든 세로 affine 오차로 나타난다:
//                          ① ML Kit 프리뷰는 화면 전체(상태바/내비바 포함)를 덮는데
//                             WebView 좌표계는 그 인셋만큼 안쪽 → 같은 비율로 찍으면 아래로 밀림
//                          ② 실제 카메라 이미지가 1080x1920이 아니라 1080x2340(20:9)일 수 있음
//                             → 세로 배율이 어긋남
//                       인셋 크기는 기기·제스처내비 설정마다 달라 코드로 단정할 수 없으므로
//                       직접 맞추는 보정값을 노출한다 (추정 누적보다 확실함).
//                       변경 (src/MobileLookup.jsx):
//                          ① boxAdj { dy(세로 이동 %), sy(세로 배율) } — localStorage 저장
//                             렌더 시 top = raw.top*sy + dy, height = raw.height*sy
//                          ② 오버레이에 [↕ 위치 보정] 토글 + 슬라이더 2개 + [초기화]
//                             ・ dy: -20% ~ +20% (0.25 단위)
//                             ・ sy: 0.5 ~ 1.8 (0.01 단위)
//                             ・ 조절 즉시 반영·자동 저장 → 스캔하면서 겹칠 때까지 맞추면 됨
//                          ③ 해상도 프리셋 셀렉트는 그대로 유지 (1080x2340 주석 보강)
//                          ④ 오버레이 헤더 정렬 flex-start + padding 축소 (보정 패널 공간 확보)
//                       SQL 마이그레이션: 없음
// v7.206 (2026-07-27) — 🟥🟩 ML Kit 좌표로 바코드 위 박스 렌더 (웹캠 모드 폐기)
//                       v7.205 진단 결과 (사용자 실측):
//                          ・ ML Kit cornerPoints 정상 제공 — 예: [[420,880],[1030,880],[1030,960],[420,960]]
//                          ・ 웹캠 모드(BarcodeDetector+zxing)는 250회 시도에 1건 수준 → 사실상 불가
//                       → ML Kit을 유지한 채 박스를 그리는 방향으로 확정.
//                       변경 (src/MobileLookup.jsx):
//                          ① 네이티브 리스너에서 cornerPoints → rect(min/max) 산출 후 보관
//                          ② 좌표계 환산: cornerPoints는 "카메라 이미지 픽셀" 기준이고
//                             플러그인이 이미지 해상도를 알려주지 않는다.
//                             ・ IMG_PRESETS(720x1280 / 1080x1920 / 1080x2340 / 1440x2560 / 가로형)
//                             ・ 관측된 최대 좌표를 담는 가장 작은 프리셋을 자동 선택(pickPreset)
//                             ・ imageRectToScreenPct(): 프리뷰가 화면을 cover 한다고 가정해
//                               scale=max(sw/iw, sh/ih) + 중앙 오프셋으로 % 환산
//                             ・ 어긋나면 오버레이의 [박스 보정] 셀렉트로 직접 선택 가능
//                               (localStorage 'inv_lookup_imgpreset')
//                          ③ 120ms 주기 루프로 만료(0.7초) 정리 + 초록→빨강 상태 전이 반영
//                          ④ 화면 테두리 플래시는 10px→5px로 축소, 중앙 칩은 박스가 하나도
//                             없을 때만 표시 (정합 실패 대비 폴백)
//                          ⑤ 웹캠 전환 토글은 '비권장'으로 문구 변경 — 진단 용도로만 유지
//                       ⚠ 좌표계 가정(cover fit)이 기기마다 다를 수 있어 보정 셀렉트를 남겨둠
//                       SQL 마이그레이션: 없음
// v7.205 (2026-07-27) — 🩺 웹캠 모드 미인식 진단 + ML Kit 좌표 수집
//                       사용자 보고: '바코드 위 박스 표시'(웹캠 모드)에서 아예 인식이 안 됨.
//                       가설: 안드로이드 WebView에는 BarcodeDetector(Shape Detection API)가
//                             실려 있지 않은 경우가 많다. Chrome에는 있어도 WebView엔 없음.
//                             그러면 zxing-wasm만 남는데, CDN(jsdelivr) 로드가 막히면 감지 0.
//                       → 추측으로 고치지 않고 실제 상태를 화면에 노출시켜 판별한다.
//                       변경 (src/MobileLookup.jsx):
//                          ① 웹캠 모드 진단 패널
//                             ・ BarcodeDetector 사용 가능 여부
//                             ・ zxing-wasm 상태 (준비됨/로드 중/실패) + [다시 시도] 버튼
//                             ・ 스캔 중 실시간: 프레임 수 / BD 시도 / ZX 시도 / 감지 수 / 마지막 오류
//                             ・ 둘 다 불가면 대응 안내를 빨간색으로 명시
//                          ② reloadZxing() — CDN 실패 시 수동 재로드
//                          ③ ML Kit cornerPoints 원시값 수집·표시
//                             네이티브 리스너에서 barcode.cornerPoints를 그대로 문자열로 노출.
//                             값이 오면 ML Kit을 유지한 채 정확한 박스를 그릴 수 있다(다음 버전).
//                             네이티브 스캔 중엔 본문이 숨겨지므로 portal 오버레이에도 병기.
//                       SQL 마이그레이션: 없음
// v7.204 (2026-07-27) — 🟥🟩 바코드 추적 박스 (앱에서도 표시 가능하도록 모드 전환 추가)
//                       사용자 요청: 라벨 사진처럼 인식한 바코드를 따라다니며 박스 렌더.
//                                   빨강 = 인식된 것 / 초록 = 처리중.
//                       변경 (src/MobileLookup.jsx):
//                          ① 색 의미 교체 (사용자 지정)
//                             초록 pending = 조회 진행 중 / 빨강 done = 인식 완료 / 회색 skip
//                          ② codeStateRef (code → pending|done|skip) 신설
//                             박스 색을 onDetected 반환값이 아니라 이 맵에서 읽는다.
//                             → 조회가 끝나면 다음 프레임에 초록→빨강으로 전이 (추적 중 색 변화)
//                             매 프레임 setBoxes 갱신 → 카메라가 움직여도 박스가 바코드를 따라감
//                          ③ 앱(네이티브)에서도 박스를 그릴 수 있게 '바코드 위 박스 표시' 토글 신설
//                             ・ ML Kit은 프리뷰를 WebView 뒤에서 네이티브로 그려 좌표 정합 불가.
//                               체크 시 getUserMedia(웹 카메라)로 전환 → WebView가 직접 프레임을
//                               쥐므로 boundingBox 좌표가 정확히 맞는다.
//                             ・ 대신 엔진이 BarcodeDetector(+zxing 폴백)로 바뀜 → 인식률 트레이드오프.
//                               기본값 OFF(ML Kit 유지), localStorage 'inv_lookup_webcam'에 저장
//                             ・ isNative(기기 판별)와 useNative(실제 사용 경로)를 분리하고
//                               카메라 시작/정지/CSS주입/포털/렌더 분기를 모두 useNative 기준으로 변경
//                          ④ 네이티브 오버레이에 박스 표시 전환 방법 안내 문구 추가
//                       SQL 마이그레이션: 없음
// v7.203 (2026-07-27) — 🚀 조회 스캐너 엔진 배지가 안 보이던 문제 수정
//                       사용자 보고: 등록 스캔은 'ML Kit'으로 표기되는데 재고(조회) 스캔은 표기 없음.
//                       원인: v7.201에서 엔진 표시를 본문 진단 블록에 넣었는데,
//                             네이티브 스캔 중에는 barcode-scanner-active CSS가 WebView 본문을
//                             visibility:hidden 처리하므로(프리뷰가 뒤에 보이도록) 그 줄이 안 보였다.
//                             MobileScan은 처음부터 portal 오버레이 안에 배지를 두고 있었음.
//                       ※ 엔진 자체는 정상적으로 ML Kit이었다 — 표시 위치만의 문제.
//                       변경 (src/MobileLookup.jsx):
//                          ① 네이티브 portal 오버레이 헤더에 '🚀 {engine} (Native)' 배지 추가
//                             + 감지/이미 판단/대상 아님 카운터도 같은 줄에 노출
//                          ② 카메라 시작 전(idle)에도 이 기기가 쓸 엔진을 미리 표시
//                             ・ 앱: 🚀 ML Kit (앱 네이티브)
//                             ・ 웹: BarcodeDetector (+ zxing 보강 / 준비 중)
//                             ・ 폴백: zxing-wasm 단독 / 사용 불가(수동 입력 권장)
//                       SQL 마이그레이션: 없음
// v7.202 (2026-07-27) — 🟩🟥 조회 스캐너: 카메라 위 인식 박스 오버레이
//                       사용자 제안: 찾은 시리얼은 초록 반투명 박스, 처리된 건 빨강 박스로
//                                   표시하면 뭐가 인식/처리됐는지 눈으로 식별 가능.
//                       → v7.200에서 "판단 끝난 코드는 화면을 안 건드림"으로 바꾼 뒤
//                         인식 여부를 알 수 없게 된 문제를 정확히 해소한다.
//                       변경 (src/MobileLookup.jsx):
//                          ① onDetected가 판정 종류를 반환하도록 변경
//                             'new'(초록·이번에 새로 판단) / 'done'(빨강·이미 처리)
//                             / 'skip'(회색·시리얼 대상 아님)
//                          ② 웹 경로 — 실제 좌표 박스
//                             ・ BarcodeDetector: boundingBox 사용
//                             ・ zxing-wasm: position 코너 4점 → 최소/최대로 rect 산출
//                               (축소 배율 sc를 되돌려 원본 픽셀 좌표로 환산)
//                             ・ <video> objectFit 'cover' → 'contain' 변경
//                               잘리는 영역이 없어 좌표 정합이 단순해지고, 스캔 시야도 넓어짐
//                             ・ 원본 픽셀 → 표시영역 % 로 변환 후 저장 (레터박스 오프셋 반영)
//                             ・ 0.7초 이상 재감지 안 된 박스는 자동 소멸
//                             ・ 박스 위에 [확인/처리됨] + 코드 일부 라벨
//                          ③ 네이티브(ML Kit) 경로 — 화면 테두리 + 중앙 칩
//                             ・ 프리뷰가 WebView 뒤에서 네이티브로 그려져 좌표 정합을
//                               보장할 수 없음 → 정확한 박스 대신 같은 색 신호로 대체
//                             ・ 0.9초 후 자동 해제
//                          ④ 카메라 정지 시 박스/플래시 정리
//                       SQL 마이그레이션: 없음
// v7.201 (2026-07-27) — 🔦 조회 스캐너 인식률 보강 (손전등·탭포커스·zxing 폴백·진단)
//                       사용자 질문: "인식률이 낮은데 서버에서 처리해서 그런가?"
//                       → 아니오. 바코드 해독은 전부 온디바이스다.
//                         네이티브=ML Kit(오프라인), 웹=BarcodeDetector.
//                         서버(Supabase)는 코드 해독 후 조회 1회만. 인식률과 무관.
//                       실제 원인: MobileLookup 웹 경로에 MobileScan이 가진 인식 보조가
//                                 하나도 없었음 (손전등/탭포커스/zxing 폴백 전무).
//                       변경 (src/MobileLookup.jsx):
//                          ① zxing-wasm 폴백 이식 (MobileScan v7.136과 동일 CDN/버전)
//                             ・ BarcodeDetector 실패 프레임을 tryHarder로 재시도 → 2단 구성
//                             ・ canvas 축소(긴 변 1280)로 디코드 비용 억제
//                             ・ 네이티브(ML Kit)에서는 로드하지 않음
//                             ・ BarcodeDetector 미지원 브라우저도 zxing만으로 동작 가능해짐
//                          ② 손전등 토글 — 창고 조명이 인식률에 가장 큰 변수
//                             ・ 네이티브: BarcodeScanner.enableTorch/disableTorch/toggleTorch
//                             ・ 웹: track.applyConstraints({advanced:[{torch}]})
//                             ・ capability 확인 후 지원 시에만 버튼 노출
//                          ③ 탭 투 포커스 (웹) — pointsOfInterest + single-shot
//                          ④ 진단 표시: "엔진 ML Kit · 감지 37회 / 건너뜀 ..."
//                             원시 감지 수(rawRef)는 필터·캐시 이전 단계라
//                             '인식이 안 되는 것'과 'v7.200이 걸러낸 것'을 구분할 수 있다.
//                             감지 수가 오르는데 카드가 안 바뀌면 = 인식은 정상, 중복 억제 중
//                          ⑤ 한 프레임의 모든 코드를 onDetected에 전달 (기존: 첫 코드만)
//                       SQL 마이그레이션: 없음
// v7.200 (2026-07-27) — 🔎 조회 스캐너: 판단 끝난 코드는 화면에서 완전 배제
//                       사용자 보고: 출고됨 → 미등록 → 출고됨 이 수도 없이 번갈아 바뀌어
//                                   결과를 읽을 수가 없음.
//                       원인: 한 프레임에 여러 코드(시리얼 라벨 + 파렛트/카톤 라벨 등)가 잡히고,
//                             v7.199는 캐시에 있어도 큰 카드를 계속 다시 그렸음 →
//                             서로 다른 두 코드가 번갈아 카드를 점유.
//                       변경 (src/MobileLookup.jsx):
//                          ① onDetected: resultCache에 있는 코드는 화면을 전혀 건드리지 않음.
//                             hits 카운트만 조용히 누적. 지난 결과는 하단 이력 탭으로 확인.
//                             → 큰 카드는 "새로 판단된 코드"에만 반응
//                          ② isScannableCode() 이식 (MobileScan v7.132 규칙)
//                             ・ 길이 6~30 밖 / EAN·UPC·ITF14(순수 숫자 8·12·13·14자리)
//                               / 파렛트·카톤 접두어(PQKP·CQKP) → 조회 자체를 스킵
//                             → '미등록'으로 잘못 뜨던 물류 라벨 노이즈 제거
//                          ③ 스캔 중 안내 + 스킵 카운터 표시 (0.8초 주기)
//                             "건너뜀 — 이미 판단 N회 · 대상 아님 M회"
//                             화면이 멈춘 게 아니라 걸러지고 있음을 보여줌
//                          ④ [비우기]가 스킵 카운터까지 초기화
//                       SQL 마이그레이션: 없음
// v7.199 (2026-07-27) — 🔎 조회 스캐너: 중복 재조회 제거 + 제품 카탈로그 대조
//                       사용자 피드백: ① 인식률은 좋은데 같은 시리얼을 계속 다시 조회함
//                                     ② 제품명은 제품 카탈로그를 참고할 것
//                       변경 (src/MobileLookup.jsx):
//                        ── ① 중복 조회 제거
//                          ・ resultCacheRef (code → entry) 세션 캐시 신설
//                            이미 조회한 코드는 DB를 다시 치지 않고 캐시 결과만 큰 카드로 올림
//                            → 이력에 같은 코드가 여러 줄 쌓이지 않음
//                          ・ 큰 카드에 이미 떠 있는 코드는 아예 무시 (카메라가 초당 수십 번
//                            같은 라벨을 잡아도 화면이 조용함). 기존 2초 쿨다운만으로는 부족했음
//                          ・ '이미 조회 ×N' 칩으로 캐시 결과임을 표시
//                          ・ [↻] 버튼으로 캐시 무시하고 DB 강제 재조회 (다른 PC에서 상태 변경 시)
//                          ・ [비우기]는 이력 + 캐시 동시 초기화
//                        ── ② 제품 카탈로그 대조 (MobileScan v7.144와 동일 규칙)
//                          ・ REGION_SUFFIXES 도입 — TS-264-8G-EU → TS-264-8G 로 정규화 후 매칭
//                            (라벨엔 지역 코드가 붙어 나오는데 카탈로그엔 없음)
//                          ・ matchCatalog(): raw → 접미사 제거본 → 대소문자 무시 순으로 products 조회.
//                            catalogCacheRef로 이름당 1회만 질의
//                          ・ 재고 행의 product_name도 카탈로그와 대조:
//                             - 카탈로그에 없으면 '⚠ 제품 카탈로그에 없는 제품명' 경고 (표준화 대상)
//                             - 정식명이 다르면 '카탈로그 정식명' 병기
//                             - 카테고리 칩은 카탈로그 값 우선 (재고 행 category가 비어 있을 수 있음)
//                          ・ 제품명 불일치 판정도 정규화 이름끼리 비교 → 리전 접미사 오탐 제거
//                       SQL 마이그레이션: 없음
// v7.198 (2026-07-27) — 🔎 모바일 현장 조회 스캐너 (읽기 전용) 신설
//                       용도: 기존 스캔은 "등록"이었고, 이번 건은 현장에서 바코드를 찍어
//                             해당 시리얼이 DB에 있는지 / 어떤 상태인지 "확인"만 하는 도구.
//                       신규 파일: src/MobileLookup.jsx
//                          ・ 진입: 재고 탭(MobileInventory) 검색바 옆 [🔎 조회] 버튼 → 전체화면
//                          ・ 결과: 큰 카드(마지막 1건) + 아래 누적 이력 + 상단 요약 카운트
//                            ✅재고 있음 / 🔒예약 / ⚠️판매불가 / 📦출고됨 / ❌미등록 / 🏷제품 바코드
//                          ・ 조회 순서: inventory 정확일치 → ilike(대소문자 무시)
//                                       → products 카탈로그(제품 바코드 판정) → 미등록
//                          ・ 제품 바코드를 먼저 찍으면 "대조 기준"으로 잡아두고, 이후 시리얼의
//                            product_name과 다르면 ⚠ 제품명 불일치 경고
//                          ・ 스캐너: 네이티브 ML Kit(주 경로) / 웹 BarcodeDetector / 수동 입력
//                            ※ 바코드 훼손·미부착 대비로 수동 입력은 항상 노출
//                          ・ 미등록 결과에서 [입고 등록 화면으로 이동] 버튼 제공
//                          ・ 진동 피드백: 있음 짧게 / 미등록 길게 2회
//                          ・ 같은 코드 2초 내 재감지 무시 (연속 프레임 중복 방지)
//                          ・ Android 물리 back: 카메라 중이면 카메라만 종료, 아니면 오버레이 닫기
//                          ・ 네이티브 투명화 CSS를 자체 주입 (MobileScan 미마운트 상태이므로 필수).
//                            클래스명 mobile-lookup-native-overlay 로 분리해 상호 간섭 방지
//                       ⚠ DB 쓰기 경로 없음 — SELECT만 수행. MobileScan은 미변경(등록 회귀 위험 0)
//                       변경: src/MobileInventory.jsx (버튼 + 오버레이 마운트)
//                       SQL 마이그레이션: 없음
// v7.197 (2026-07-27) — 🎨 재고 목록 검색 카드에도 (신품 8, 리페킹 1, 조립출고 1) 표기 적용
//                       v7.196에서 재고 "대시보드" 탭만 바꿨는데, 사용자가 실제로 보는 화면은
//                       재고 "목록" 탭의 검색 그룹 카드였음 (다른 코드 경로).
//                       변경 (App.js — 검색 그룹 카드 2곳):
//                          ① 도넛 legend '판매가능' 옆 breakdown 뱃지 → 괄호+콤마 텍스트
//                          ② 카드 하단 '유형' 행 뱃지 → 괄호+콤마 텍스트
//                          ・ 0인 유형은 생략 (기존 뱃지도 0이면 숨김이라 동작 동일)
//                          ・ 색상은 기존 유지: 신품 #009844 / AS #546e7a / 리페킹 #e65100 / 조립출고 #004d6a
//                       ※ '출고됨' 옆 breakdown은 기존 형식 유지 (요청 범위 밖)
//                       SQL 마이그레이션: 없음
// v7.196 (2026-07-27) — 🧰 UX 4종: 시리얼 후보 기본 비표시 / 수량↓ 시리얼 동반 해제 /
//                       재고 대시보드 유형 표기 형식 / 메모 Ctrl+Z
//                       ── ① 출고 처리 시리얼 후보 dropdown 기본 비표시
//                          기존: input focus만 해도 후보 10건이 펼쳐져 화면을 가림
//                          ・ onFocus/onChange → 입력값이 있을 때만 open
//                          ・ 닫힌 상태에서 ↓ 키 → 전체 후보 열기 (브라우징 경로 유지)
//                          ・ placeholder에 "(↓ 키: 재고 후보 보기)" 안내 추가
//                          ・ 적용 2곳: 출고 처리 모달 + txlog 출고 요청 처리 패널
//                       ── ② 배송시트지 수량 감소 시 등록 시리얼 동반 해제
//                          기존 버그: qty만 줄고 ship_pending_serials는 그대로 남아
//                                    "진행 3/3"인데 "4개 시리얼이 출고 처리됩니다" 불일치
//                          ・ collectExcessPending / removePendingSerials 신설
//                          ・ 허용 pending = newQty − fulfilled. 초과분은 최근 추가분부터 해제
//                          ・ 해제 대상 시리얼 목록을 confirm으로 먼저 보여줌 (무단 삭제 방지)
//                          ・ ShipPendingService.loadByReqId에 created_at 오름차순 정렬 추가
//                       ── ③ 재고 대시보드 stock_type 표기 형식 변경
//                          Before: 신품 8 · AS 0 · 리페킹 1 · 조립출고 1  (0도 회색으로 노출)
//                          After:  (신품 8, 리페킹 1, 조립출고 1)        (0인 유형은 생략)
//                       ── ④ 메모 편집기 Ctrl+Z / Ctrl+Shift+Z · Ctrl+Y
//                          contentEditable 네이티브 undo는 편집 진입 시 innerHTML을
//                          코드로 세팅하는 순간 깨지고 execCommand와 단위가 뒤섞여 직접 구현
//                          ・ memoUndoRef { memoId, stack:[{html,caret}], idx } 스냅샷 스택
//                          ・ getCaretOffset / setCaretOffset — 텍스트 오프셋으로 커서 보존
//                          ・ 타이핑은 300ms 병합, 볼드/색상/붙여넣기는 개별 되돌림 지점
//                          ・ 편집 진입 useEffect에서 스택 초기화 (메모 간 오염 방지)
//                       파일 변경: src/App.js, src/services/ShipPendingService.js
//                       SQL 마이그레이션: 없음
// v7.195 (2026-07-24) — 📮 신규 출고요청 폼에도 다음 우편번호 검색 통합 (옵션 B)
//                       사용자 결정: v7.194의 상세뷰뿐 아니라 신규 등록 시점부터
//                                   우편번호를 정형화 → 상세뷰 편집 왕복 최소화
//                       변경 (App.js):
//                          ① state 추가: shipZip, shipAddrDetail
//                             + 킵→출고 모달(shipKeepModal)의 zip / addrDetail
//                          ② 출고요청 페이지 배송지 주소 라벨 옆에 [🔍 주소검색] 버튼
//                             ・ 다음 우편번호 팝업 → zonecode + roadAddress 자동 세팅
//                             ・ 자동완성(과거 주소 히스토리) 기능은 그대로 유지 (공존)
//                          ③ 우편번호 pill (있으면 표시) + 상세주소 input (라벨 아래)
//                             ・ 방문수령/RMA·DOA 모드에선 숨김 (기존 배송지 대체 UX 유지)
//                          ④ shipNote JSON에 zip / addrDetail 조건부 저장
//                             ・ !shipPickup && !isRma && shipZip 인 경우만 (택배 등 배송 필요)
//                          ⑤ 킵→출고 모달(shipKeepModal)도 동일 패턴 적용
//                             ・ noteObj에 zip / addrDetail 저장
//                          ⑥ 폼 리셋에 shipZip / shipAddrDetail 포함
//                       변경 없음: 배송시트지 인라인 편집 UI (요청대로 컬럼에는 X),
//                                 자동완성(주소 히스토리), addr 자유 입력 fallback
//                       파일 변경: src/App.js
// v7.194 (2026-07-24) — 📮 배송지 우편번호·상세주소 필드 신설 (우체국 OpenAPI P1 준비)
//                       배경: 우체국 계약소포 OpenAPI 자동화 (옵션 B) 진행 결정.
//                            발송지 "스토리지사업부(6536)" 확정, API 인증키 발급 완료.
//                            우체국 API 필수 필드 recZip(5자리) + recAddr1/recAddr2 분리 요구에 맞춰
//                            shipInfo 스키마 확장. 상세뷰에만 노출 (사용자 결정: 배송시트지 컬럼 X).
//                       변경 (App.js):
//                          ① 카카오(다음) 우편번호 서비스 lazy loader 신설
//                             ・ 첫 [주소검색] 클릭 시에만 스크립트 로드 (초기 번들 부담 X)
//                             ・ postcode.v2.js · 무료 · key 불필요 · CORS OK
//                             ・ loadDaumPostcode() + openDaumPostcode(oncomplete)
//                          ② 상세 모달 "배송 주소" 카드 확장 (line ~17920)
//                             ・ 상단 오른쪽 [🔍 주소검색] 버튼 — 팝업 → zonecode/roadAddress 자동 저장
//                             ・ 우편번호 pill (있으면 표시, 모노스페이스)
//                             ・ 주소 (기존 shipInfo.addr → recAddr1)
//                             ・ 상세주소 (신규 shipInfo.addrDetail → recAddr2)
//                                더블클릭 → window.prompt로 편집 (미입력 시 안내 문구)
//                          ③ 엑셀 export 함수 (v7.49 downloadShippingExcel):
//                             A열: 하드코딩 '' → shipInfo.zip
//                             B열: addr → addr + addrDetail 공백 결합 (fullAddr)
//                             I열: 하드코딩 '' → shipInfo.memo (배송메세지)
//                          ④ 스키마 변경 없음 — 신규 필드는 note JSON 안에 추가 저장
//                             (하위호환: 기존 shipInfo에 zip/addrDetail 없어도 undefined로 안전)
//                       다음 세션 (P2): Cloudflare Worker 스캐폴딩 + SEED128 검증
//                       사용자 결정 사항 (확정):
//                          ・ 계약: apprNo 1014081417 (즉납/서울용산)
//                          ・ 발송지: 스토리지사업부 6536 (Worker 기본 officeSer)
//                          ・ 프록시: Cloudflare Worker (인증키·보안키는 Worker Secrets)
//                          ・ 무게/크기: default (2kg/60cm) 사용, 실제 접수 시 우체국이 재계량
//                          ・ 초기 개발: testYn=Y 로 테스트 신청 (등기번호 'TESTREGINOAPI') 확인 후 실전 전환
//                       사용자 확인 필요 (P3 전):
//                          ・ 보안키 [보안키보기] 클릭해서 확인
//                          ・ 고객번호(custNo) — 계약고객시스템 회원정보에서 확인 (또는 Worker가 memberID로 조회)
//                       파일 변경: src/App.js
// v7.193 (2026-07-23) — 🗑 재고 목록 시리얼 단건 삭제 기능 추가
//                       사용자 요청: 재고 목록에서 제품(시리얼) 삭제 가능하도록.
//                       변경 (App.js):
//                          ① state 추가: deletingSerial (진행중 로딩 표시용)
//                          ② expand 패널 하단 "입고 정보 수정" 아래 삭제 버튼 추가
//                             ・ 왼쪽: 설명 문구 + shipped인 경우 경고
//                             ・ 오른쪽: [🗑 시리얼 삭제] 빨강 버튼 · hover 시 반전 배경
//                             ・ 클릭 시 window.confirm — 시리얼 + 제품명 표시하며 확인
//                             ・ 진행중 표시: '삭제 중...' + 회전 아이콘
//                          ③ 안전장치 (재사용):
//                             ・ InventoryService.deleteBySerial 이 이미 in_stock 상태만 허용
//                               (v7.15 도입) → 출고된 시리얼은 UI에서도 버튼 disabled + 서버에서도 거부
//                             ・ transaction_log(배송/입고 이력)는 손대지 않음 → 이력 보존
//                          ④ 삭제 성공 시 로컬 상태만 갱신 (loadInventory 재호출 없이)
//                             ・ items 배열에서 필터 · total 감소 · 확장 패널 닫힘
//                       파일 변경: src/App.js
// v7.192 (2026-07-23) — 🎨 메모 카드 세로 간격 확대 + 푸터(작성자/날짜) 검은색화
//                       사용자 리포트 2건:
//                          ① 별점 배지가 위아래 메모 카드끼리 겹침 → 세로 간격 확대
//                          ② 하단 푸터의 작성자/수정일이 카드 색상(c.accent)이라 눈에 잘 안 보임
//                             → 검은색(#0b1c30)으로 통일
//                       변경 (App.js):
//                          ① sizeConfig: 기존 gap 단일값 → gap(가로) + gapY(세로) 분리
//                             ・ s 모드: gap 14 · gapY 30 (별점 배지 상단 -11px 걸침 여유 확보)
//                             ・ l 모드: gap 22 · gapY 36
//                          ② 그리드 컨테이너: gap → columnGap + rowGap 개별 지정
//                          ③ 카드 푸터 스타일:
//                             color: c.accent → '#0b1c30' (검정)
//                             opacity: 0.75 → 0.85 (약간 진하게)
//                       파일 변경: src/App.js
// v7.191 (2026-07-23) — 🐛 v7.190 별점 배지 상단 잘림 fix
//                       사용자 리포트: 카드 상단에 걸친 별점 배지의 위쪽이 잘려서 표기됨.
//                       원인: 메모 카드 컨테이너 style에 overflow:'hidden' 있어서
//                            position:absolute + top:-11 로 카드 밖으로 튀어나온 별점 배지가
//                            부모의 clipping에 의해 잘림.
//                       변경 (App.js):
//                          ① 카드 컨테이너 overflow:'hidden' → 'visible'
//                             (본문은 이미 안쪽 view/edit div 자체가 overflowY:auto 처리하므로
//                              fixed 모드에서도 본문 삐져나옴 없음)
//                          ② 메모 그리드 컨테이너에 paddingTop:12 추가
//                             (첫 행 카드의 상단 배지가 페이지 상단으로 삐져나가 잘리는 것 방지)
//                       파일 변경: src/App.js
// v7.190 (2026-07-23) — ⭐ 메모장 별점 중요도 (1~5) + 반짝임 이펙트
//                       사용자 요청: 메모에 중요도 별점(1~5) 추가, 위치는 상단 라인에 걸치도록,
//                                   새 메모 작성 시에도 선택 가능, 별 빛나는 이펙트 포함.
//                       변경 (App.js):
//                          ① CSS keyframes 3종 신규
//                             ・ starTwinkle: 채워진 별의 밝기·drop-shadow pulse (2.4s, 각 별 delay)
//                             ・ starPop: 클릭 시 스케일 튀는 미세 애니메이션
//                             ・ starGlow: 배지 배경 은은한 pulse
//                          ② StarRating 컴포넌트 (JSX): 1~5 별, hover 프리뷰, 클릭으로 값 설정,
//                             같은 값 재클릭 시 0으로 초기화, glowing prop으로 반짝임 on/off
//                          ③ 메모 카드 상단에 리본 배지 형태로 별점 배치
//                             ・ position:absolute, top:-11px, left:14px — 카드 상단 border를
//                               가로질러 걸치는 형태 (사용자 요청 "상단 라인에 걸쳐서")
//                             ・ importance>0 이면 채워진 별 + 반짝임 애니메이션
//                             ・ importance=0 이면 은은한 outline 별 (편집 유도)
//                          ④ 새 메모 작성 폼 색상 옆에 별점 선택기 추가
//                          ⑤ MemoService.create 확장 (importance 지원, default 0)
//                          ⑥ updateMemoField 로 importance 즉시 저장 (기존 pinned/color와 동일)
//                          ⑦ newMemoImportance state 추가, addMemo 성공 시 리셋
//                       DB 마이그레이션 필수: v7.190-migration.sql
//                          memos.importance smallint DEFAULT 0 + CHECK (0~5)
//                          실행 안 하면 create 시 컬럼 missing 에러 → 사전 실행 필요
//                       파일 변경: src/App.js, src/services/MemoService.js
//                       신규 파일: migrations/v7.190-migration.sql
// v7.189 (2026-07-23) — 🐛 재고 목록 제품 상태 드롭다운: "조립" → "조립출고" 정정 + AS 추가
//                       사용자 요청: 재고 목록에서 제품 상태 변경 드롭다운에 "조립출고" 추가.
//                       진단 결과: 요청보다 큰 갭 발견
//                          ・ 드롭다운 옵션 value가 "조립" 인데 다른 모든 UI/집계/뱃지/필터는 "조립출고"
//                            기준 → 이 드롭다운으로 저장된 재고는 어디에서도 인식 안 됨
//                            (v7.185 CHECK 제약 실행 이후엔 저장 자체가 실패했을 가능성)
//                          ・ AS 옵션도 v7.184 전면 확장 시 이 드롭다운만 누락
//                       변경 (App.js, expand 패널 line 10900):
//                          <option value="조립"> → <option value="조립출고"> 정정
//                          <option value="AS">AS</option> 추가 (신품/AS/리페킹/조립출고 순)
//                       기존 데이터 정정: v7.189-migration.sql — stock_type='조립' → '조립출고' UPDATE
//                          (v7.185 실행 후에 진행 권장 · '조립' row가 0건이면 무해)
//                       파일 변경: src/App.js
//                       신규 파일: migrations/v7.189-migration.sql
// v7.188 (2026-07-23) — 📅 입출고 이벤트 캘린더에 모든 제품명·수량 표시
//                       사용자 요청: 캘린더 이벤트에 대표 모델명 하나만 나와 여러 품목이 있는
//                                   출고요청·입고 이벤트의 실제 내역 파악 어려움. 모든 제품과
//                                   수량을 추가로 표기 원함.
//                       변경 (EventCalendar.jsx):
//                          ① 헬퍼 신설
//                             ・ getEventItems(ev): note.items 배열이 있으면 [{name, qty}] 전개,
//                                                   없으면 fallback으로 [ev.model 하나] 반환
//                             ・ getEventTotalQty(items): 합계 (null은 1로 취급)
//                          ② 캘린더 셀 (각 이벤트 한 줄):
//                             ・ 1품목: "TR-004 ×3" 형식
//                             ・ 2품목 이상: "TS-873AEU-RP-4G 외 1종 ×8" 요약 표기
//                             ・ tooltip에 "TS-... ×2 / TS-... ×6" 전체 세부 표시
//                          ③ 우측 디테일 패널 (각 이벤트 카드):
//                             ・ 기존 대표 모델명 라인 → items 전체 리스트로 전개
//                             ・ 각 라인: "제품명" + 우측 pill 뱃지 "×수량"
//                             ・ 2품목 이상이면 하단에 "합계 ×총수량" 추가 표시
//                       파일 변경: src/EventCalendar.jsx (App.js는 changelog + 버전만)
// v7.187 (2026-07-23) — 📒 배송시트지 입고 row memo 표시 + 장부처리 3-state 드롭다운
//                       사용자 요청 2건:
//                          ③ 입고 등록 시 비고 입력 → 배송시트지 입고 row에도 비고 내용 표시
//                          ④ 장부완료/장부미완료 뱃지에 "수기입력" 옵션 추가 → 드롭다운으로 선택
//                       변경 (App.js):
//                          [③] 배송시트지 memo 셀:
//                             기존: display:isIn?'none':'block' 로 입고 row는 셀 자체 숨김
//                             변경: 셀 전체 표시 + 작성자/요청자 부분만 !isIn 조건으로 감쌈
//                             → 입고 row에서도 memo가 표시되고 더블클릭으로 편집 가능
//                          [④] 장부처리 뱃지 3-state 확장:
//                             ・ 상수: normalizeBookkept(v), BOOKKEPT_STYLES { completed, pending, manual }
//                             ・ 하위호환: bookkept=true → 'completed' / false/undefined → 'pending'
//                             ・ state: bookkeptPopover { rowId, x, y }
//                             ・ 배송시트지 뱃지: 기존 더블클릭 토글 → 클릭 시 팝오버 오픈
//                                (더블클릭 유지 시 실수 방지, 클릭이 3-state 선택에 자연스러움)
//                             ・ 팝오버 UI: 3개 옵션 버튼 (완료/미완료/수기입력) + 저장 후 자동 닫힘
//                             ・ 색상: 완료 녹#009844 / 미완료 주황#e65100 / 수기입력 청회색#546e7a
//                             ・ 아이콘: 완료·미완료 menu_book / 수기입력 edit_note
//                       파일 변경: src/App.js
// v7.186 (2026-07-23) — 🐛 메모 붙여넣기 [#hex] 마커 raw 노출 문제 완전 수정
//                       사용자 리포트: 다른 곳에서 텍스트 복사 후 메모에 붙여넣으면
//                                    "[#b45309]" 같은 문구가 그대로 뜨고, 색상도 변경 안 됨.
//                       두 근본 원인 (합쳐서 증상 유발):
//                          ① 브라우저가 서식 있는 텍스트를 붙여넣을 때 원본의 <span style="color:...">를
//                             그대로 삽입 → htmlToMarkup이 [#hex]...[/] 마커로 변환 → 사용자가 원치 않아도
//                             색상이 강제 적용됨.
//                          ② renderMemoContent/markupToHtml의 정규식이 `.+?`로 되어 있어 `\n`이 매치 안 됨.
//                             원본 span이 여러 라인에 걸쳐 있으면 [#hex]...[/] 짝이 개행을 포함해서 매치 실패.
//                             결과: 마커 문자열이 raw로 화면에 노출 + 색상 span 렌더링 실패.
//                       변경 (App.js):
//                          ① 메모 편집기 contentEditable에 onPaste 핸들러 추가 (line 12574 부근)
//                             ・ e.preventDefault() → clipboardData.getData('text/plain') 만 추출
//                             ・ document.execCommand('insertText') 로 순수 텍스트만 삽입 (undo 스택 유지)
//                             ・ 서식/색상 완전 제거 → [#hex] 마커 애초에 생성 X
//                          ② renderMemoContent/markupToHtml/dashboard summary plainText 3곳의 정규식을
//                             `.+?` → `[\s\S]+?` 로 교체 → 개행 걸친 색상 태그도 정상 매치
//                          ③ 렌더링 span에 whiteSpace:'pre-wrap' 추가 (여러 라인 색상 텍스트 개행 유지)
//                       기존 저장된 잘못된 마커: ②로 렌더링/편집 진입 시 정상 처리됨. 별도 청소 불필요.
//                       파일 변경: src/App.js
// v7.185 (2026-07-23) — 🐛 HDD/SSD 입고 실패 사유 노출 (AS 유형 등록 실패 진단용)
//                       사용자 리포트: v7.184 배포 후에도 AS 유형으로 입고 등록 시
//                                   "등록 완료. 단, 1개 실패: ZX229P2C" 만 뜨고 사유 안 보임.
//                       원인 추정: 매우 유력한 후보는 inventory.stock_type 컬럼의 CHECK 제약이
//                                v7.73 UI 추가 시점에 DB 쪽으로 확장되지 않은 것.
//                                즉 UI에선 'AS' 옵션 노출되지만 DB가 삽입을 거부.
//                       임시 조치 (App.js registerHddInbound):
//                          기존: alert에 시리얼만 표시 → 원인 파악 불가
//                          변경: 실패한 첫 row의 error.message를 alert에 함께 노출 +
//                                console.error로 전체 failed 배열 로깅
//                       근본 해결: v7.185-migration.sql 실행 필요
//                                inventory.stock_type CHECK 제약을 신품/AS/리페킹/조립출고로 확장
//                       파일 변경: src/App.js
//                       신규 파일: migrations/v7.185-migration.sql
// v7.184 (2026-07-23) — 🔧 AS 유형 전면 인식 + 판매가능 옆 유형 breakdown 뱃지
//                       사용자 리포트 2건:
//                         ① AS하드 입고 등록 시 처리 안됨
//                         ② 재고 목록/대시보드에서 판매가능에 신품/조립출고/리페킹 표기 원함
//                       원인 (①):
//                         v7.73에서 HDD/SSD 입고 유형에 'AS' 추가됐으나, 재고 대시보드·재고 목록의
//                         집계/뱃지/필터 UI 전반에서 'AS' 케이스가 누락 → 등록은 되지만 UI상 조립출고
//                         색으로 오표시되거나 유형 필터에 안 뜨거나 유형 breakdown에서 사라짐.
//                         (DB엔 정상 저장되지만 사용자 체감상 "처리 안됨")
//                       변경 (App.js):
//                          ① 재고 대시보드 검색 결과 grouped 초기값에 asProduct/shippedAs +
//                             inStockNewProduct/inStockRepacked/inStockAssembled/inStockAs 필드 추가
//                          ② 집계 로직에 stock_type==='AS' 케이스 추가 (shipped/nonshipped 양쪽) +
//                             status==='in_stock'인 경우 inStock* 필드에도 별도 집계 (판매가능 breakdown용)
//                          ③ 도넛 legend 옆 '판매가능' 라벨에 breakdown 뱃지 추가
//                             (신품 X · AS Y · 리페킹 Z · 조립출고 W - value>0인 것만)
//                             출고됨 breakdown에도 AS 케이스 추가
//                          ④ 유형 분포 뱃지 (line 10442)에 AS 케이스 추가
//                          ⑤ 재고 목록 유형 필터 chip에 'AS' 옵션 추가
//                          ⑥ 인라인 stock_type 뱃지 4곳(재고 목록 row, expand panel, 출고 검색결과,
//                             선택된 출고 아이템)에 AS 색상 케이스 추가 (blue-grey #546e7a)
//                          ⑦ loadAllModels 집계에도 AS 케이스 추가 (asProduct 필드)
//                          ⑧ 재고 대시보드 상단 카드(제품별 재고 현황)의 stock_type breakdown 표시에
//                             AS 추가 — sd.stockTypeCounts에서 t.AS 추출 후 신품 옆에 표시
//                       변경 (InventoryService.js):
//                          getInStockProductCounts의 stockTypeCounts 객체에 AS 필드 신설
//                          기존엔 '기타'로 묻혔던 것을 별도 분리
//                       AS 뱃지 색상: blue-grey — bg rgba(84,110,122,0.12), text #546e7a
//                          (신품 초록/AS 청회색/리페킹 주황/조립출고 파랑 순으로 시각적 구분)
//                       파일 변경: src/App.js, src/services/InventoryService.js
// v7.183 (2026-07-15) — 📒 입고 장부처리 완료 플래그 도입
//                       사용자 요청: 입고 시 "장부처리 완료" 체크박스 → 배송시트지에 상태 뱃지 표시,
//                                   더블클릭으로 편집 가능.
//                       변경 (App.js):
//                          ① State 추가:
//                             ・ regBookkept (일반 입고 등록 페이지) - bool
//                             ・ hddInbound.bookkept (HDD/SSD 일괄 입고) - bool
//                          ② UI 체크박스 (2곳):
//                             ・ 일반 입고 페이지: 입고일/비고 옆에 "📒 장부처리 완료" 체크박스
//                             ・ HDD/SSD 페이지: 입고일 우측 "미확인" 옆에 "📒 장부처리 완료"
//                          ③ Submit 로직: note JSON에 `bookkept: bool` 추가 저장
//                             ・ 일반 입고 line 9354, HDD/SSD 입고 line 7160
//                          ④ 배송시트지 렌더 (line 12793):
//                             ・ isIn 행에 뱃지 표시:
//                                • bookkept=true → 📒 장부완료 (녹색)
//                                • bookkept=false/미설정 → 📒 장부 미완료 (주황)
//                             ・ 더블클릭 → note.bookkept 토글 → transaction_log update → loadTxLog
//                             ・ tooltip: "더블클릭하여 장부처리 상태 토글"
//                       기존 데이터: bookkept 필드 없으면 falsy → "장부 미완료" 표시.
//                                    별도 SQL 마이그레이션 불필요 — 사용자가 필요한 것만 개별 편집.
//                       파일 변경: src/App.js
// v7.182 (2026-07-15) — 📱 모바일 검색창 [이동] 키로 키보드 자동 내림
//                       사용자 리포트: 검색어 입력 후 키보드의 [이동] 눌러도 키보드가 안 내려감.
//                       원인: onKeyDown 핸들러 없어서 Enter 입력이 아무 동작 안 함.
//                       변경 (모바일 검색 input 3곳):
//                          MobileInventory.jsx 제품/시리얼 검색
//                          MobileTxLog.jsx 시리얼/모델/수령인 검색
//                          MobileShipRequest.jsx 제품 검색
//                       추가 속성:
//                          ① type="search" — semantic HTML5 search input
//                          ② enterKeyHint="search" — 키보드에 "검색" 아이콘 힌트
//                          ③ inputMode="search" — 모바일 키보드 layout 힌트
//                          ④ onKeyDown Enter → blur() → 키보드 dismiss
//                       파일 변경: src/MobileInventory.jsx, src/MobileTxLog.jsx, src/MobileShipRequest.jsx
// v7.181 (2026-07-15) — 📱 모바일 재고 목록 제품별 그룹핑 (PC와 동일 방식)
//                       사용자 요청: 모바일에서 "10g2t" 검색 시 68개 시리얼이 섞여서 표시됨 →
//                                   PC처럼 3개 모델로 그룹핑, 클릭 시 시리얼 목록 펼침 원함.
//                       변경 (MobileInventory.jsx):
//                          ① items를 useMemo로 product_name 기준 그룹핑
//                             ・ 각 그룹: { product_name, stock_type, items[], counts{...} }
//                             ・ counts: total / in_stock / reserved / unsellable / shipped
//                             ・ 첫 등장 순서 유지 (Map)
//                          ② expandedGroups state (Set<product_name>) — 그룹별 펼침/접기
//                          ③ 그룹 헤더 카드:
//                             ・ 썸네일(첫 글자) + 제품명 + stock_type 뱃지
//                             ・ 총계 (큰 숫자) + 상태별 카운트 (dot + 라벨)
//                             ・ 우측 ▼ 아이콘 (펼침 시 회전)
//                             ・ 탭 시 확장
//                          ④ 펼침 시 SerialRow 리스트가 헤더 아래 표시:
//                             ・ 시리얼 · 상태뱃지 · warehouse · source
//                             ・ 탭 시 상세 시트 (기존)
//                          ⑤ 검색 결과 요약 바 + [모두 펼침]/[모두 접기] 버튼
//                       기존 flat InventoryCard 컴포넌트 삭제.
//                       파일 변경: src/MobileInventory.jsx
// v7.180 (2026-07-15) — 첨부 업로드 에러 메시지 상세화 (진단용)
//                       사용자 리포트: v7.179 PNG/JPG 낱개 업로드 3건 모두 실패, 원인 불명.
//                       변경 (App.js AttachmentButton):
//                          ・ 에러 배열로 축적 (파일별 상세 message 포함)
//                          ・ alert에 첫 3건까지 상세 표시 + "F12 콘솔 확인" 안내
//                          ・ AttachmentService.upload은 이미 HTTP status + body 반환 중이라
//                             upstream 그대로 사용
//                       사용자 후속: F12 → Console 탭 로그 or Network 탭 응답 코드 확인 필요
//                       파일 변경: src/App.js
// v7.179 (2026-07-15) — 📎 첨부 다중 이미지 지원 (PNG/JPG 낱개 여러개)
//                       사용자 요청: ZIP 대신 PNG/JPG 낱개 파일 여러 개 업로드 가능하게.
//                       변경 (App.js):
//                          ① AttachmentButton 완전 재작성:
//                             ・ note.attachments 배열로 저장 (신규 스키마)
//                             ・ note.attachment (단일 legacy) 도 읽기 유지 — 기존 ZIP 안전
//                             ・ 저장 시 legacy attachment 제거 후 attachments로 통합
//                          ② <input type="file"> multiple + accept 확장:
//                             ・ .png, .jpg, .jpeg, .gif, .webp, .zip, image/*
//                             ・ 한 번에 여러 파일 선택 가능
//                          ③ UI: 각 첨부를 chip 형태로 나열
//                             ・ [🖼️ IMG_001.jpg ×] [🖼️ IMG_002.jpg ×] [+ 사진]
//                             ・ 이미지면 image 아이콘, 그 외 attach_file 아이콘
//                             ・ 클릭 시 새 탭 (이미지=브라우저 인라인 렌더, ZIP=nginx가 download)
//                             ・ × 버튼으로 개별 삭제
//                          ④ 업로드는 순차적 (Promise.all이 아닌 sequential — 실패 격리)
//                          ⑤ 실패 시 성공한 것만 저장 + 실패 개수 안내
//                       nginx.conf 변경 없음:
//                          기존 `location ~ \.zip$` 규칙은 .png/.jpg에는 미적용 →
//                          이미지는 nginx 기본 동작(inline) 유지.
//                       파일 변경: src/App.js
// v7.178 (2026-07-15) — 📎 사진 첨부 버튼 위치 이동: 날짜 셀 → 배송지 셀 (주소 아래)
//                       사용자 요청: 날짜 셀에 너무 많은 정보가 몰려 있고 배송지 셀 하단 여유 있음.
//                       변경 (App.js):
//                          ① 날짜 셀(라인 12487)의 <AttachmentButton> 제거
//                          ② 배송지 셀 주소 텍스트(라인 12951) 아래에 <AttachmentButton> 재배치
//                       기능 동일, 위치만 이동. 첨부된 파일 데이터 무영향.
//                       파일 변경: src/App.js
// v7.177 (2026-07-15) — 📎 첨부 저장소: Supabase Storage → QNAP nginx WebDAV로 전환
//                       사용자 리포트: Supabase Storage 파일 제한 이슈 → NAS 직접 저장 원함.
//                                    사내 사용만이라 인증 없어도 OK.
//                       변경:
//                          ① AttachmentService.js 완전 재작성:
//                             ・ supabase.storage → fetch PUT/DELETE
//                             ・ URL: window.location.origin + '/attachments/tx_{id}/...'
//                             ・ path 스키마는 그대로 (기존 note.attachment 호환)
//                          ② AttachmentButton: 크기 경고 프롬프트 50MB → 500MB (NAS는 넉넉)
//                          ③ nginx.conf 별도 배포 (attachment-hosting-v7.177.tar.gz):
//                             ・ /attachments/ location + dav_methods PUT DELETE MKCOL
//                             ・ create_full_put_path on (자동 하위 디렉터리 생성)
//                             ・ client_max_body_size 2G
//                             ・ ZIP 파일에 Content-Disposition: attachment (다운로드 트리거)
//                          ④ docker-compose.yml: ./attachments 볼륨 마운트 추가 (rw)
//                       배포 전 사전 작업 (QNAP ssh):
//                          mkdir -p attachments && chmod 777 attachments
//                       Cloudflare Tunnel 경로에서 큰 파일(100MB~)은 실패 가능 →
//                          내부 IP 접속 시 우회됨.
//                       기존 Supabase Storage 'work-photos' 버킷 있으면 삭제해도 무방
//                          (사용 안 됨, 아직 파일 업로드 안 됐으니 안전).
//                       파일 변경: src/services/AttachmentService.js, src/App.js
//                                (+ 별도 nginx.conf + docker-compose.yml)
// v7.176 (2026-07-15) — 📎 배송 요청별 작업사진 ZIP 첨부 기능
//                       사용자 요청: 배송시트지 날짜 셀 아래 여유 공간에 작업사진 묶음 ZIP을
//                                   업로드/다운로드할 수 있는 버튼 추가.
//                       구현:
//                          ① services/AttachmentService.js 신규 (Supabase Storage 래퍼)
//                             ・ upload(txId, file) — 'work-photos' 버킷에 tx_{id}/{ts}_{safename}.zip
//                             ・ remove(path) — 파일 삭제
//                             ・ getPublicUrl(path) — public URL 반환 (public bucket 기준)
//                          ② AttachmentButton 함수형 컴포넌트 (App.js 내부)
//                             ・ 파일 없음: [📎 사진 첨부] 소형 dashed 버튼 → 클릭 시 파일 피커
//                             ・ 파일 있음: [📎 다운로드 XKB] + [×] 삭제 버튼
//                             ・ 50MB 초과 파일 업로드 시 확인 프롬프트
//                          ③ 배송시트지 날짜 셀 하단에 AttachmentButton 배치
//                             (라인 12608 근처, isCancel일 때 disabled)
//                          ④ metadata는 transaction_log.note JSON에 attachment: {path,name,size,uploadedAt} 저장
//                             (별도 컬럼 추가 안 함 — 기존 note 구조 확장)
//                       배포 전 필수 사전 세팅 (Supabase Dashboard):
//                          1) Storage → Create bucket 'work-photos'
//                          2) Public bucket으로 설정 (RLS 미사용)
//                          3) File size limit 조정 (원하면 50MB 이상으로)
//                       파일 변경: src/App.js, src/services/AttachmentService.js (신규)
// v7.175 (2026-07-14) — 배송처리 pending 시리얼 자동 정리(7일) 비활성화 — 수동 정리로 변경
//                       사용자 요청: "수동으로만 정리할게" — 7일 지나면 자동 삭제되던 기능 끄기.
//                       변경 (App.js):
//                          ① 앱 시작 시 실행되던 자동 정리 useEffect 주석 처리 (line 3960 근처)
//                          ② cleanupStaleShipPending / ShipPendingService.cleanupStale 함수 자체는
//                             그대로 유지 → 나중에 되돌리려면 주석 해제 한 줄이면 됨
//                          ③ 커밋 성공 시 clearShipPending 호출은 유지 → 정상 배송 완료 건은
//                             자동 삭제되므로 사용자 UX 영향 없음
//                          ④ 중단된 요청의 pending 시리얼만 무한 보존됨
//                       필요 시 수동 정리 SQL (Supabase Editor):
//                          DELETE FROM ship_pending_serials WHERE updated_at < NOW() - INTERVAL '30 days';
//                       (localStorage 폴백은 브라우저별로 남아 있음. 필요 시 개발자도구 Application → localStorage
//                        에서 'inventura_ship_pending_v1_' 접두사 키 수동 삭제)
//                       파일 변경: src/App.js (한 useEffect 블록만 주석 처리)
// v7.174 (2026-07-14) — 🕐 자동 이월 확장: 특정 날짜로 즉시 이월 기능 추가
//                       사용자 요청: 자동이월 기능에 "어느 날짜로 보낼지" 지정 가능하도록 확장.
//                                   (예: 스크린샷 사례 — "7.27 월요일 발송 예정" 요청을
//                                   해당 날짜로 옮겨두고 그 이후부터 자동이월되게)
//                       변경 (App.js):
//                          ① [자동 이월] 배지를 <span>에서 <button>으로 변경 → 클릭 가능
//                          ② deferPopover state — 클릭 시 팝오버 좌표/rowId/현재 날짜 저장
//                          ③ 팝오버 UI (fixed position, 배지 아래에):
//                             ・ 날짜 input (input type=date)
//                             ・ 프리셋 4개: 내일 / +3일 / 다음주 월 / +2주
//                             ・ [적용] [취소] 버튼
//                          ④ applyDeferDate 함수:
//                             ・ transaction_log의 transaction_date를 선택 날짜로 update
//                             ・ note 건드리지 않음 (autoDefer flag 유지 → 그 날짜 이후 매일 이월)
//                             ・ 성공 시 loadTxLog로 화면 갱신
//                          ⑤ 외부 클릭 감지 배경으로 팝오버 자동 닫힘
//                       기존 자동이월 동작은 그대로 유지 (bool flag + 매일 today로 밀림).
//                       추가된 것은 "일회성 날짜 재지정" — 예: 오늘 처리 못 하는 요청을
//                       7/27로 옮겨두면 배송시트지의 7/27 자리에 나타남.
//                       파일 변경: src/App.js
// v7.173 (2026-07-14) — 모바일 스캔 입고의 source 라벨 "모바일 스캔" → "이선" 통일
//                       사용자 요청: 모바일 스캔으로 입고된 물건이 재고 목록에 "모바일 스캔 출처"로
//                                   표시되던 것을 "이선 출처"로 변경 (v7.59 HDD 입고 페이지의
//                                   '이선' 프리셋과 통일해서 관리 편의성 향상).
//                       변경:
//                          ① MobileScan.jsx line 944: source '모바일 스캔' → '이선'
//                          ② 재고 목록 뱃지 렌더링(App.js 10187)에서 fallback으로 '이선 출처' 자연 표시
//                       기존 데이터: 별도 SQL 마이그레이션 필요 (아래 v7.173-migration.sql 참조)
//                          UPDATE inventory SET source = '이선' WHERE source = '모바일 스캔';
//                       파일 변경: src/MobileScan.jsx (+ 별도 v7.173-migration.sql)
// v7.172 (2026-07-14) — 🔍 모바일 스캔 검수 모드 (바코드 뭉개짐 대비)
//                       사용자 요청: 바코드가 뭉개져서 값을 잘못 읽는 경우가 있어서
//                                   촬영 → 값 추출 → 사용자가 확인·편집 → 큐 추가
//                                   방식으로 검수 단계 추가.
//                       변경 (MobileScan.jsx):
//                          ① 새 state: reviewMode (localStorage 'inv_review_mode' 저장)
//                                    + pendingReviews 배열 (검수 대기 시리얼 목록)
//                          ② 시리얼 감지 분기 (line 462~):
//                             ・ reviewMode ON → pendingReviews에 추가 (큐/슬롯 X)
//                             ・ reviewMode OFF → 기존 autoAdd/slot 흐름
//                          ③ 새 토글 UI: 자동 추가 아래에 "🔍 검수 모드" 체크박스
//                             ・ 자동 추가와 상호배타 (검수 ON이면 자동 추가 disabled)
//                          ④ 카메라 오버레이 하단에 검수 대기 카운트 뱃지
//                             (주황색 "🔍 검수 대기: N건")
//                          ⑤ idle 화면 상단 (피드백 배너 아래)에 검수 편집 카드:
//                             ・ 주황색 border/배경
//                             ・ 각 행: 순번 + 편집 가능 input + [✓ 확인] + [✕ 삭제]
//                             ・ 하단: [모두 확인 (N건)] + [모두 삭제]
//                          ⑥ 큐 중복 체크 유지 (이미 큐에 있으면 리뷰 추가 X)
//                       사용 흐름:
//                          ① 🔍 검수 모드 ON
//                          ② 카메라 시작 → 시리얼 스캔 → 오버레이 하단에 "검수 대기 N건" 표시
//                          ③ 스캔 정지
//                          ④ 검수 카드에서 각 값 확인/편집 → [✓]로 큐에 추가
//                             (또는 [모두 확인] 일괄 처리)
//                          ⑤ 정상 큐 → 동기화
//                       파일 변경: src/MobileScan.jsx
// v7.171 (2026-07-14) — 배송시트지 입고 행을 출고요청 아래로 sink
//                       사용자 요청: 입고도 출고요청 밑으로 내려가도록.
//                                   (오늘 하루 입고 여러 건일 때 처리 필요한 출고요청이 밀리는 문제)
//                       변경 (App.js loadTxLog, MobileTxLog load):
//                          같은 날짜 그룹 내 정렬 규칙 확장:
//                          ① [신규] 입고(inbound) → 그 외(출고/환불) 뒤로
//                          ② [기존 v7.146] 완료 outbound → pending outbound 뒤로
//                          최종 표시 순서:
//                            outbound pending → refund → outbound fulfilled → inbound
//                       파일 변경: src/App.js, src/MobileTxLog.jsx
// v7.170 (2026-07-14) — APK 사내 배포 (nginx.conf 수정)
//                       ・ /apk/ location 추가 (autoindex + 예쁜 index.html)
//                       ・ index.html no-cache (배포 후 캐시로 이전 버전 뜨는 문제 예방)
//                       ・ MIME 버그 fix: types { } 블록을 nested location으로 격리 (v7.171)
//                       파일 변경: nginx.conf, docker-compose.yml (별도 배포 tarball)
// v7.169 (2026-07-14) — 📷 모바일 스캔 다중 제품 연속 스캔 지원
//                       사용자 요청: 촬영→추가→다시촬영 반복이 번거로움.
//                                   예: TS-233 8개 → TS-464 4개 → TS-233 10개
//                                   → TS-233 18건, TS-464 4건 자동 취합되어 업로드되게.
//                       원인: MobileScan.jsx의 processDetectedCodes에서 제품 바코드 처리 게이트가
//                            `!effectivePn || !effectivePnValid` (=제품명이 비었거나 유효하지 않을 때만) 조건.
//                            → 한번 유효한 제품이 세팅되면 다른 제품 바코드는 무시.
//                            → 사용자는 카메라 종료 → productName 지움 → 다시 시작 반복해야 했음.
//                       변경 (MobileScan.jsx):
//                          ① 제품 바코드 게이트 완화:
//                             ・ `if (effectiveProductCode)` 로 변경
//                             ・ canonicalName ≠ 현재 productName 일 때만 실제 setState (동일 이름 반복 스캔은 no-op)
//                             ・ lastProductCodeRef 로 피드백 dedup 유지
//                          ② 피드백 메시지 개선:
//                             ・ 전환 케이스: "🔄 제품 전환: TS-233(8) → TS-464"
//                             ・ 초기 케이스: "📷 제품명 인식: TS-233" (기존)
//                          ③ 큐 요약 카드에 제품별 breakdown chip 표시 (제품 2개 이상일 때만):
//                             ・ `TS-233 · 18` `TS-464 · 4` 형태 파란색 pill
//                             ・ 연속 스캔 검증용 (실수 방지)
//                          ④ 사용 안내에 "🔄 여러 제품 연속 스캔 (v7.169)" 섹션 추가
//                       동기화 로직 무변경: syncNow에서 이미 byProduct 그룹핑 처리 중 (line 906~).
//                                       → 큐만 다중 제품 담기면 업로드는 자동 처리됨.
//                       파일 변경: src/MobileScan.jsx
// v7.168 (2026-07-14) — 재고 목록 reserved 뱃지 라벨 일반화 ("방문수령 예약" → "예약")
//                       사용자 리포트: 킵 지정한 시리얼이 재고 목록에서 "방문수령 예약"으로 표시됨.
//                                    킵이므로 그냥 "예약"으로 표기 필요.
//                       배경: v7.55에서 reserved 상태를 방문수령 전용으로 도입 → 라벨도 방문수령 문구.
//                            v7.156에서 킵 지정 시리얼도 reserved 상태 공유하도록 확장 →
//                            라벨은 여전히 방문수령이라 부정확.
//                       변경 (App.js):
//                          ① 재고 목록 뱃지: "방문수령 예약" → "예약"
//                          ② tooltip: "방문수령으로 예약된..." → "예약된 시리얼입니다..."
//                          ③ 도넛 차트 예약 세그먼트 title도 일반화
//                          ④ dealer_name 하단 표시는 유지 (누구를 위해 예약됐는지 여전히 유용)
//                       파일 변경: src/App.js
// v7.167 (2026-07-14) — ✅ 뒤로가기 문제 완전 해결 → 진단 뱃지 UI 제거
//                       사용자 검증 완료 (v7.166 + MainActivity.java 패치):
//                          ・ 시나리오 1: 최상위 back → 토스트 → 재클릭 종료 ✓
//                          ・ 시나리오 2: 탭 이동 후 back → 이전 탭 복귀 ✓
//                          ・ 시나리오 3: 상세뷰 back → 목록 복귀 ✓
//                          ・ 시나리오 4: 모달 back → 모달 닫기 ✓
//                       변경 (App.js):
//                          ① 우상단 빨간 debug 뱃지 UI 제거
//                          ② backDebug state와 setBackDebug 호출은 유지
//                             → 향후 회귀 발생 시 뱃지 UI 한 줄만 되살리면 재활용 가능
//                       APK 재빌드 필요 없음 (웹만 배포).
//                       처음부터 지금까지 뒤로가기 여정 요약:
//                          v7.158 : popstate 기반 초기 도입 (실제 동작 안 함)
//                          v7.159 : 탭 히스토리 + Capacitor 감지 시도 (TDZ 버그로 흰 화면)
//                          v7.161 : TDZ 버그 hotfix
//                          v7.163 : 진단 뱃지 도입 + 양방향 등록
//                          v7.165 : 뱃지 상단 이동 + 시인성 강화
//                          v7.166 : native 브릿지 도입 (window.__handleAndroidBack) — 결정타
//                          v7.167 : 뱃지 제거 (완료)
//                       파일 변경: src/App.js
// v7.166 (2026-07-14) — 🎯 뒤로가기 native 브릿지 (Capacitor bridge 미주입 문제 최종 해결)
//                       사용자 실증(v7.165 진단):
//                          ・ cap=N, app=N, listener=skip → Capacitor bridge 미주입 확정
//                          ・ 뒤로가기 시 last=- (popstate도 발동 안 함) → 즉시 앱 종료
//                          ・ 이유: Capacitor가 server.url 원격 페이지에 bridge JS를 주입 안 함
//                                (androidScheme 설정에 따라, remote URL 로드 시 기본 미주입)
//                          ・ 이 상황에서 Capacitor 기본 back = webView.goBack() 안 부르고
//                            바로 finish() → 앱 즉시 종료
//                       해결: Capacitor 프로젝트 MainActivity native 코드로 우회
//                          ⓐ MainActivity.onBackPressed() 재정의
//                          ⓑ evaluateJavascript("window.__handleAndroidBack()") 호출
//                          ⓒ 반환값 true = JS가 처리함 (종료 X)
//                                  false = JS가 처리 안 함 (native가 finish/goBack)
//                          ⓓ APK 재빌드 + 재설치 필요 (1회성)
//                       변경 (App.js):
//                          ① window.__handleAndroidBack 전역 함수 등록
//                             ・ dispatchBack() → 스택 처리 시도
//                             ・ 미처리 시 exit toast 로직 (2초 내 재클릭 = 종료 허용)
//                             ・ true/false 반환 → MainActivity가 판단
//                          ② handleBack 반환값 명시 (기존 return void → return bool)
//                          ③ 소스 태그 'native' 추가 (뱃지 last=native로 표시)
//                          ④ cleanup 시 window.__handleAndroidBack 제거
//                       Capacitor 프로젝트 별도 작업:
//                          ・ android/app/src/main/java/<패키지>/MainActivity.(java|kt) 수정
//                          ・ 코드는 별도 파일 MainActivity-패치.md 참조
//                          ・ Android Studio에서 Build → Generate Signed Bundle/APK
//                          ・ Galaxy S22에 재설치
//                       파일 변경: src/App.js
// v7.165 (2026-07-14) — 뒤로가기 debug 뱃지 상단 이동 + 버전 표시 + 시인성 강화
//                       사용자 리포트: F12 (Chrome DevTools 모바일 에뮬레이션)에서는 뱃지 보이는데
//                                    실제 갤럭시 S22 앱에서는 안 보임.
//                       가설:
//                          ① 앱이 v7.163 못 받고 이전 버전 캐시 중 (WebView 캐시)
//                          ② 하단 우측이 Android 제스처바에 가려짐
//                          ③ z-index 문제로 다른 요소에 덮임
//                       변경 (App.js):
//                          ① 위치: bottom-right → top-right (제스처바 회피)
//                          ② 색: 어두운 회색 → 빨간색 배경 (시인성 극대화)
//                          ③ 크기: 폰트 9px → 11px, 패딩 확대
//                          ④ 그림자 + 테두리 추가 (배경 어떤 색이든 눈에 띔)
//                          ⑤ APP_VERSION 표시 — 뱃지만 봐도 어느 버전인지 판별 가능
//                       파일 변경: src/App.js
// v7.163 (2026-07-14) — 🔬 뒤로가기 강화 + 진단 뱃지 (실증 확인용)
//                       사용자 리포트: v7.159/161에도 불구하고 뒤로가기 = 여전히 앱 꺼짐.
//                       가설:
//                          ① Capacitor server.url 모드에서 window.Capacitor bridge 미주입
//                          ② isNativePlatform() 감지 실패 → popstate 폴백으로 감
//                          ③ Capacitor 기본 back 동작이 goBack() 호출하지 않아 popstate 발동 안 함
//                          ④ 또는 pushState buffer 부족으로 canGoBack이 첫 back에 false 됨
//                       진단이 필요한 상황이라 실증용 debug 뱃지 도입 + 코드 견고화.
//                       변경 (App.js):
//                          ① 하드웨어 리스너 useEffect 전면 리팩터:
//                             ・ if/else → 병행 등록 (Capacitor App plugin + popstate 동시)
//                             ・ Capacitor 리스너 있으면 popstate는 하드웨어 back에 안 발동됨 (안전)
//                             ・ isNativePlatform 감지 안 되어도 AppPlugin 존재만 확인하고 시도
//                          ② pushState buffer 1 → 10 (canGoBack 오래 유지)
//                             ・ popstate마다 buffer 1개 재보충 (계속 canGoBack=true)
//                          ③ AppPlugin.exitApp 없을 때 2차 back에서 history.go(-50)로 buffer 소진 유도
//                          ④ setBackDebug state로 진단 정보 실시간 표시:
//                             ・ cap=Y/N (window.Capacitor 존재)
//                             ・ app=Y/N (Capacitor.Plugins.App 존재)
//                             ・ nat=Y/N (isNativePlatform 반환값)
//                             ・ listener=ok/fail/skip
//                             ・ buf=버퍼 개수
//                             ・ last=capacitor|popstate (최근 이벤트 소스)
//                             ・ n=이벤트 누적 카운트
//                          ⑤ 우하단 미니 뱃지 UI (탭하면 숨김) — 스크린샷으로 진단 가능
//                       사용자 확인 요청:
//                          ・ 배포 후 뱃지 스크린샷
//                          ・ 뒤로가기 한 번 눌러서 n 카운트 오르는지 (last에 뭐 뜨는지)
//                          ・ 몇 번 눌러야 앱 꺼지는지
//                       파일 변경: src/App.js
// v7.162 (2026-07-14) — 🐛 배송시트지 모바일 진입 시 PC 권장 안내 표시되던 버그 fix
//                       사용자 리포트: 모바일에서 배송시트지 페이지 열면 "PC에서 이용 권장" 안내가 뜸.
//                       원인: v7.150에서 MobileTxLog.jsx 도입했으나
//                            MOBILE_FRIENDLY_PAGES 화이트리스트(v7.117 도입)에 'txlog' 추가 누락.
//                            결과: v7.121 리다이렉트 화이트리스트(['ship','inventory','scan','txlog'])와
//                                 MOBILE_FRIENDLY_PAGES(['scan','inventory','ship']) 불일치.
//                                 → 모바일 txlog 진입 시 안내 페이지 뜨고 [계속 보기] 눌러야 실제 뷰 표시.
//                       변경 (App.js):
//                          ① MOBILE_FRIENDLY_PAGES에 'txlog' 추가
//                          ② MobileGuardPage 안내 텍스트 갱신 (배송시트지/재고/출고요청 지원 반영)
//                       파일 변경: src/App.js
// v7.161 (2026-07-14) — 🚨 hotfix: v7.159로 배포하면 흰 화면 (TDZ ReferenceError)
//                       사용자 리포트: 컴파일 후 앱 화면이 완전히 하얀색으로 뜸.
//                       원인: v7.159에서 도입한 App-level backHandler useEffect의
//                            의존성 배열 `[isMobile, outboundModalOpen, headerMenuOpen]`.
//                            useEffect는 render 시점에 즉시 호출되어 의존성 배열을 평가하는데,
//                            outboundModalOpen / headerMenuOpen 은 400줄 이상 아래에서
//                            `const [outboundModalOpen, ...] = useState(...)` 로 선언됨.
//                            const는 temporal dead zone(TDZ) — 선언 전 접근 시 ReferenceError.
//                            → 렌더 크래시 → 흰 화면.
//                       변경 (App.js):
//                          ① backStateRef ({outbound, headerMenu}) 추가 — 초기화 시점에 위치
//                          ② App-level backHandler에서 state 대신 ref 참조
//                             ・ setter는 closure body 안에서만 호출 → 실행 시점 render 완료 후
//                               이미 setter가 스코프에 있음 → 문제 없음
//                          ③ useEffect deps에서 state 제거, [isMobile]만 남김
//                          ④ state 선언부 아래에 sync effect 2개 추가 —
//                             state → backStateRef.current 동기화
//                       교훈: React 함수 컴포넌트에서 useEffect 의존성에 넣는 변수는
//                             반드시 그 useEffect 이전에 선언되어 있어야 함.
//                             const/let 은 TDZ 있음. 이후 선언 참조 시 ReferenceError.
//                       파일 변경: src/App.js
// v7.160 (2026-07-14) — 배송시트지 신규 출고요청을 목록 맨 위에 표시
//                       사용자 리포트: 새 출고요청이 배송시트지 가장 아래에 작성됨.
//                       원인: transaction_log ORDER BY의 sort_order 정렬이
//                            `ASC nullsFirst=false` (=nulls last) 였음.
//                            드래그 재정렬로 sort_order가 10/20/30 부여된 날짜에
//                            새 요청(sort_order NULL)이 들어오면 NULLS LAST 규칙에 의해
//                            비어있는 sort_order 그룹으로 밀려 최하단에 표시.
//                       변경 (App.js loadTxLog + MobileTxLog.jsx load):
//                          ・ nullsFirst: false → true
//                          ・ 결과 정렬:
//                             ① NULL 그룹 (created_at DESC 티브레이커) — 신규 요청 최신순
//                             ② non-NULL 그룹 (sort_order ASC) — 수동 드래그 순서
//                          ・ 드래그 재정렬 안 한 날짜 (모두 NULL): 기존과 동일 (신규 상단)
//                          ・ 드래그 재정렬만 있는 날짜 (모두 non-NULL): 기존과 동일 (수동 순서)
//                          ・ 둘이 섞인 날짜: 신규 NULL 상단, 수동 재정렬 하단 (사용자 요청)
//                       참고: 수동 정렬 후 신규 요청이 최상단으로 오면 이전에 사용자가 상단으로
//                            드래그한 행보다 위에 표시됨. 필요하면 다시 드래그하면 전체 재할당됨
//                            (신규 행도 sort_order 부여).
//                       파일 변경: src/App.js, src/MobileTxLog.jsx
// v7.159 (2026-07-14) — 🐛 뒤로가기 = 전 단계 이동으로 개선 (v7.158 후속)
//                       사용자 리포트: v7.158에도 불구하고 탭 화면에서 뒤로가기 = 앱 종료.
//                       원인 분석:
//                          ① 탭 간 이동(시트지↔출고↔재고↔스캔) 히스토리 추적이 아예 없음
//                             → 사용자가 시트지→스캔 이동 후 뒤로가기 = 이전 탭 복귀 로직 없음
//                             → 최상위 종료 흐름으로 바로 빠짐 (토스트 후 재클릭)
//                          ② popstate만 사용 — Capacitor에서 pushState 히스토리가
//                             canGoBack에 안 잡히는 사례가 있어 popstate 자체가 안 뜰 수 있음
//                          ③ 하드웨어 back 버튼의 정공법은 @capacitor/app 의 backButton 리스너
//                       변경 (App.js):
//                          ① pageHistoryRef — 탭 이동 자동 추적 스택 (mobile 탭 4개 간 이동만)
//                             ・ useEffect(page)로 이전 페이지 자동 push
//                             ・ 최대 20개 유지 (메모리 누수 방지)
//                          ② App-level backHandler — 히스토리 pop해서 이전 탭 이동
//                             ・ unshift로 스택 최하위 우선순위 (모달/상세 다음)
//                          ③ Capacitor 감지 → window.Capacitor.Plugins.App.addListener('backButton')
//                             ・ 네이티브 하드웨어 back 이벤트를 직접 수신 (popstate 우회)
//                             ・ 스택이 비면 exitApp() 명시 호출
//                          ④ 비 Capacitor / 감지 실패 → 기존 popstate + pushState 폴백 유지
//                          ⑤ outboundModal / headerMenu 열림 시 backHandler 등록 → back으로 닫기
//                       기대 동작:
//                          ・ 시트지 → 스캔 → back = 시트지 복귀
//                          ・ 시트지 → 상세 → back = 목록 복귀 (기존 유지)
//                          ・ outboundModal 열린 상태 → back = 모달 닫기
//                          ・ 히스토리 소진 상태에서 back = "한번 더 누르면 종료" 토스트 → 재클릭 시 종료
//                       APK 재빌드: @capacitor/app 이 이미 프로젝트에 있으면 불필요 (기본 포함).
//                                  없으면 npm i @capacitor/app + npx cap sync 필요.
//                                  없어도 popstate 폴백이 어느 정도 동작함.
//                       파일 변경: src/App.js
// v7.158 (2026-07-14) — Android 물리 뒤로가기 처리 (Capacitor 앱 즉시 종료 방지)
//                       사용자 리포트: 모바일 앱에서 뒤로가기 버튼을 누르면 앱이 바로 꺼짐.
//                       원인: Capacitor WebView에서 뒤로가기 → 웹 history가 비어 있으면
//                            네이티브가 그대로 앱을 종료.
//                       변경 (App.js):
//                          ① 전역 backHandler 스택 (window.__backHandlers) 도입
//                             ・ 각 화면(컴포넌트)이 자체 상황에 맞는 handler를 push
//                             ・ handler는 true 반환 시 처리 완료, false면 다음 handler에 위임
//                          ② isMobile일 때만 popstate 리스너 등록 + 초기 dummy pushState
//                             ・ 뒤로가기 → 스택 top부터 순회하며 실행
//                             ・ 아무도 처리 안 하면 → "한 번 더 누르면 종료" 토스트
//                             ・ 2초 이내 재클릭 → pushState 안 함 → 네이티브 종료 위임 (자동)
//                          ③ 종료 안내 토스트 UI 오버레이 (fixed bottom, 애니메이션 fadeIn)
//                       변경 (MobileTxLog.jsx):
//                          ・ view==='detail'일 때 handler 등록 → 목록으로 복귀 (기존 [←]와 동일)
//                       변경 (MobileScan.jsx):
//                          ・ phase==='camera'일 때 handler 등록 → stopCamera 호출 (idle 복귀)
//                       (MobileInventory, MobileShipRequest 내부 모달 처리는 다음 세션)
//                       APK 재빌드 불필요 (순수 웹 API로만 처리)
//                       파일 변경: src/App.js, src/MobileTxLog.jsx, src/MobileScan.jsx
// v7.157 (2026-07-14) — 🐛 배송시트지 [주문취소] 시 데모 대여 pending 미원복 버그 fix
//                       사용자 리포트: 데모 대여 등록 → 배송시트지에서 [주문취소] → 장비 위치가
//                                    '대여예약(회사)' 그대로 남음. '리버'로 원복 안 됨.
//                       원인: v7.151에서 handleDeleteShipment (배송시트지 [삭제])만 fromDemoLend
//                            감지 후 cancelPendingRental 호출. handleTxAction (배송시트지 [주문취소]/
//                            [환불])은 처리가 안 되어 데모 대여 pending이 그대로 남고 equipment
//                            location도 원복되지 않았음.
//                       변경 (App.js handleTxAction):
//                          ① note.fromDemoLend + note.demoRentalId 감지 로직 추가
//                          ② actionType==='cancel' && !fulfilled 조건에서 cancelPendingRental 호출
//                             ・ demo_rentals pending 삭제
//                             ・ demo_rental_items 삭제
//                             ・ demo_equipment.location: '대여예약(회사)' → '리버' 원복
//                          ③ fromDemoLend 케이스는 시리얼 restore 스킵 (데모는 inventory 관리 밖)
//                             — allSerials/allReserved 처리 자체를 우회
//                          ④ 환불 케이스 (fulfilled=true, 즉 active 대여를 배송시트지에서 취소)
//                             는 이 경로에서 처리 안 함 — 실무상 [회수 처리] 흐름이 정석이므로
//                             부작용 방지를 위해 그대로 진행 (경고 없이 note만 마킹)
//                       파일 변경: src/App.js
// v7.156 (2026-07-14) — 킵 지정 시리얼을 inventory.status='reserved'로 마킹 (판매가능 표시 방지)
//                       사용자 리포트: 킵에 시리얼(QPRTS17648Y)을 등록해두었는데 재고 검색에
//                                    '정상 재고 / 판매가능'으로 표기됨. 다른 요청에서 실수로 그
//                                    시리얼을 잡을 수 있음.
//                       원인: product_keeps.serials에는 시리얼이 저장되지만 inventory.status는
//                            'in_stock'을 유지해서 재고 목록에는 그대로 판매가능으로 노출.
//                       변경 (App.js):
//                          ① syncKeepSerialReservation(oldSerials, newSerials) 유틸 신규:
//                             ・ toRelease: 이전에는 있었는데 지금 없는 시리얼 → status='in_stock' 원복
//                                          (단, 현재 status='reserved'인 것만. 다른 상태는 미변경)
//                             ・ toReserve: 새로 추가된 시리얼 → status='reserved' 마킹
//                                          (단, 현재 status='in_stock'인 것만. 다른 상태는 skip)
//                          ② submitKeep (킵 신규 등록): insert 성공 후 각 row의 serials로 reserve 호출
//                          ③ addKeepItem (그룹에 품목 추가): insert 성공 후 serialsArr로 reserve
//                          ④ KeepSerialSlot onCommit (시리얼 셀 편집): prev vs new diff로 sync
//                          ⑤ updateKeepField qty 변경: 수량 감소로 잘려나간 시리얼은 원복
//                          ⑥ releaseKeep (킵 해제): active였던 킵의 serials 원복
//                          ⑦ deleteKeep (킵 완전 삭제): active였던 킵의 serials 원복
//                          ⑧ shipoutKeep (개별 출고 처리): inv.status !== 'in_stock' → 'in_stock'/'reserved' 허용
//                          ⑨ executeShipKeepGroup (그룹 출고 처리): 동일 조건 완화
//                       필수 SQL (기존 활성 킵의 시리얼도 소급 예약 처리):
//                          -- 1) serial_number 컬럼 기반 (단일 시리얼)
//                          UPDATE inventory i
//                          SET status = 'reserved'
//                          FROM product_keeps k
//                          WHERE k.status = 'active'
//                            AND k.serial_number IS NOT NULL
//                            AND k.serial_number = i.serial_number
//                            AND i.status = 'in_stock';
//                          -- 2) serials 배열 컬럼 기반 (다중 시리얼) — 컬럼 타입에 따라 문법 조정 필요
//                          -- (jsonb 경우)
//                          UPDATE inventory i SET status = 'reserved'
//                          FROM product_keeps k, jsonb_array_elements_text(k.serials::jsonb) AS s
//                          WHERE k.status = 'active' AND s <> '' AND s = i.serial_number
//                            AND i.status = 'in_stock';
//                          -- (text[] 경우)
//                          -- UPDATE inventory i SET status = 'reserved'
//                          -- FROM product_keeps k, unnest(k.serials) AS s
//                          -- WHERE k.status = 'active' AND s <> '' AND s = i.serial_number
//                          --   AND i.status = 'in_stock';
//                       파일 변경: src/App.js
// v7.155 (2026-07-14) — MobileTxLog 카드 정보 개편: 주문번호 제거, 제품명/수량/송장 강조
//                       사용자 리포트: 모바일 배송시트지 카드에 주문번호(REQ-...)는 불필요.
//                                    제품명, 수량, 송장번호가 즉시 보이면 좋겠고
//                                    클릭 시 상세에는 주소도 함께 노출되어야 함.
//                       변경 (MobileTxLog.jsx):
//                          ① 목록 카드 3줄 레이아웃 재구성:
//                             1줄: [제품명 (첫 품목) + 외 N건] — 기존 요청번호 자리 승격
//                             2줄: [총 X개 · 📮 송장번호(+N)] — 택배건에서만 노출
//                                   ・ 송장 미입력이면 '송장 미입력' 흐리게
//                                   ・ 콤마 구분 여러 건 → 첫 건 + "+N" 축약
//                             3줄: 상태뱃지 + [수령인·방법·진행률] (기존 유지)
//                          ② 상세 뷰 정보 테이블에 송장번호 행 추가
//                             ・ 여러 송장은 줄바꿈으로 각각 별도 표시
//                             ・ 배송주소는 기존과 동일 (info.address 유지)
//                       (상세 뷰 헤더에는 요청번호 여전히 표시 — 문의 시 참조용)
//                       파일 변경: src/MobileTxLog.jsx
// v7.154 (2026-07-13) — 🐛 HDD/SSD 입고가 배송시트지에 안 나타나던 버그 fix
//                       사용자 리포트: HDD 여러 개를 오늘 입고했는데 배송시트지에 입고 이력이 없음.
//                                    SQL로 확인 결과 transaction_log에 최근 inbound row가 07-10 이후로 없음.
//                                    반면 재고 목록에는 오늘 등록한 시리얼들이 정상 존재.
//                       원인: registerHddInbound 함수(HDD/SSD 입고 페이지)에서 inventory bulk INSERT는
//                            수행하지만 transaction_log INSERT를 아예 안 하고 있었음.
//                            일반 제품 입고(line 8364)와 임시 시리얼 일괄 입고(line 5125)는 tx 기록
//                            정상. HDD 입고 함수만 원래부터 누락된 채로 있었음 (v7.61 도입 이후 계속).
//                       변경 (App.js registerHddInbound 함수):
//                          ・ inventory bulkInsert 종료 후 transaction_log INSERT 추가
//                          ・ items[]: 단일 제품 (product_name/qty/serials/from/model_no/stock_type)
//                          ・ note에 category (HDD/SSD 등) + receiptDateUnknown flag 저장
//                          ・ transaction_date: inDateUnknown이어도 today() 폴백
//                            (inventory는 receipt_date=NULL 저장, tx는 today()로 배송시트지 필터에 걸림)
//                          ・ 실패한 시리얼(failed)은 tx items에서 제외 — 성공한 시리얼만 기록
//                       파일 변경: src/App.js
// v7.153 (2026-07-13) — 데모 대여 배송처리 시리얼 표기 개선 (임시 NOSN- → 실제 장비 시리얼)
//                       사용자 리포트: 데모 대여 배송처리 모달에서 시리얼이 'NOSN-{모델명}-01' 같은
//                                    임시 표기로 나옴. 실제 대여 대상 장비 시리얼로 보이도록 요청.
//                       변경 (App.js):
//                          ① 데모 등록/편집 시 note.items[].demoSerials 배열 추가 저장
//                             (m.selectedItems[].serial_number 를 모델별로 grouped)
//                          ② loadActiveRequest models 배열 생성 시 _itemDemoSerials 전달
//                          ③ autoExtPending 생성 로직 확장:
//                             ・ items[].demoSerials 있으면 실제 데모 장비 시리얼 사용
//                             ・ 없으면 기존 NOSN-{모델}-{01} 임시 시리얼 폴백 (하위호환)
//                             ・ EXT- external 케이스는 기존과 동일 (변경 없음)
//                          ④ handleDeleteShipment: fromDemoLend + wasFulfilled 시 inventory 복원 스킵
//                             (데모 시리얼은 inventory에 없어 restore 시도 시 무의미한 실패)
//                       기존 데모 대여 (v7.152 이전 등록): note.items[].demoSerials 없어 NOSN- 폴백 유지
//                       새 대여부터 실제 시리얼 표기 (v7.151 pending 흐름 + v7.153 시리얼 표기)
//                       파일 변경: src/App.js
// v7.152 (2026-07-13) — 🚨 긴급 롤백: 로그인 화면 및 Supabase Auth 게이트 전체 제거
//                       사용자가 요청하지 않은 로그인 UI가 실수로 추가되어 있어 앱 접근이 차단됨.
//                       Auth 도입은 사용자가 "우선 보류"로 결정한 사항이었는데 코드가 남아있었음.
//                       변경 (모두 제거 방향):
//                          ① src/LoginScreen.jsx 파일 삭제
//                          ② App.js:
//                             ・ LoginScreen lazy import 제거 (라인 23-24)
//                             ・ session/authLoading useState + onAuthStateChange 세션 리스너 제거 (라인 2963-2984)
//                             ・ handleLogout 함수 제거
//                             ・ Auth 게이트 렌더 제거 (authLoading 스플래시 + LoginScreen 렌더)
//                             ・ 사이드바 사용자 정보/로그아웃 버튼 → 이전 형태 '운영자' 텍스트 복원
//                             ・ 모바일 컴포넌트들 onLogout prop 전달 제거
//                          ③ MobileTxLog.jsx: onLogout prop + 헤더 로그아웃 버튼 제거
//                          ④ MobileInventory.jsx: onLogout prop 시그니처 제거
//                       파일 변경: src/App.js, src/MobileTxLog.jsx, src/MobileInventory.jsx
//                                 src/LoginScreen.jsx 삭제
//                       (Supabase Auth / RLS 도입 논의는 계속 보류 상태 유지)
// v7.151 (2026-07-13) — 데모 대여 흐름 재설계: 등록 = 예약(pending) / 배송시트지 [출고] = 대여 시작(active)
//                       사용자 요청: 대여 등록만으로 즉시 active가 되던 기존 흐름은 실물 출고 전에
//                                    이미 대여가 시작된 것으로 처리되어 UX 불일치.
//                                    → 등록은 예약 상태로 시작하고, 배송시트지 [출고]를 눌러야 대여 시작.
//                       변경 (services/DemoService.js):
//                          ① createRental: status='active' → 'pending', location='대여중' → '대여예약'
//                             ・ audit_log 초기 엔트리 문구도 '예약 등록' 으로 변경
//                             ・ 중복 등록 차단 로직에 '대여예약' prefix도 포함 (다른 pending에 잡힌 장비 방어)
//                          ② finalizeRental({rentalId, by}) — 신규:
//                             ・ status pending → active
//                             ・ equipment.location: '대여예약(회사)' → '대여중(회사)'
//                             ・ audit_log에 'ship_complete' 엔트리 추가
//                             ・ idempotent (재호출해도 안전, 이미 active면 no-op)
//                          ③ cancelPendingRental({rentalId}) — 신규:
//                             ・ pending 대여만 완전 취소 (active면 거부)
//                             ・ demo_rental_items 삭제 + equipment.location='리버' 원복 + rental hard delete
//                             ・ 없던 일 취급 (audit 흔적 X)
//                          ④ listActiveRentals: status IN ('active','pending') 두 개 함께 반환
//                             ・ UI 쪽에서 rental.status 기준으로 뱃지 표시
//                          ⑤ updateRental: 대여 status에 따라 locPrefix 분기 ('대여예약' vs '대여중')
//                             pending 상태 대여를 편집해도 정확한 prefix 유지
//                       변경 (App.js):
//                          ⑥ commitRequest (라인 4247 부근): fromDemoLend + allComplete 감지 시
//                             기존 appendRentalAudit → DemoService.finalizeRental 호출로 대체
//                             (finalizeRental 내부에서 audit_log 처리)
//                          ⑦ handleDeleteShipment (라인 7380 부근): fromDemoLend 감지 시 사전에
//                             DemoService.cancelPendingRental 호출 — 배송시트지 삭제 = 대여 취소 연동
//                             wasFulfilled 이면 이미 active이므로 취소 스킵 (안전)
//                          ⑧ 데모 페이지 활성 대여 테이블:
//                             ・ pending 뱃지 표시 (파란색 '출고 대기')
//                             ・ pending 행은 [회수] 대신 [배송시트지] 버튼 (setPage('txlog') 이동)
//                             ・ pending 행은 지연 뱃지 숨김 (아직 출고 안 됐으니 지연 개념 무의미)
//                          ⑨ 대여 등록 완료 메시지 변경: '예약 등록 완료 → 배송시트지에서 [제품 출고] 처리하면 실제 대여가 시작됩니다'
//                       필수 SQL (Supabase Editor에서 실행, demo_rentals.status에 CHECK constraint 있을 때만):
//                          -- 1) 현재 constraint 확인:
//                          --    SELECT conname, pg_get_constraintdef(oid) FROM pg_constraint
//                          --    WHERE conrelid='demo_rentals'::regclass AND contype='c';
//                          -- 2) constraint 있으면 확장:
//                          ALTER TABLE demo_rentals DROP CONSTRAINT IF EXISTS demo_rentals_status_check;
//                          ALTER TABLE demo_rentals ADD CONSTRAINT demo_rentals_status_check
//                            CHECK (status IN ('pending', 'active', 'returned', 'cancelled'));
//                       기존 active 대여들은 그대로 유지 (마이그레이션 X). 새 대여부터 새 흐름.
//                       파일 변경: src/App.js, src/services/DemoService.js
// v7.150 (2026-07-13) — 배송시트지 모바일 뷰 (MobileTxLog) + 세그먼트 4탭 재구성
//                       사용자 요청: Claude Design 1a안(카드형) 기반으로 모바일에서도 배송시트지 열람.
//                                    세그먼트 4탭 [시트지, 출고, 재고, 스캔] 순서. 뷰+검색+배송처리.
//                       변경 (MobileTxLog.jsx — 신규 ~530줄):
//                          ① 목록 뷰: 검색바 + 필터 chip (전체/대기중/완료/입고) + 날짜 네비 (‹›오늘)
//                             ・ 오늘 하루 기본 (모바일은 하루 단위, 데스크톱은 주/월 단위와 다름)
//                             ・ 검색 시 날짜 범위 무시 (데스크톱 loadTxLog와 동일 규칙)
//                             ・ 정렬: 완료건은 아래로 sink (v7.146 규칙 재현)
//                             ・ 카드 표시: 이모지 아이콘 + 요청번호 + 첫 품목명(+외 N건) + 상태뱃지 + 수령인/방법/진행률
//                          ② 상세 뷰: 배송명세서 스타일 (보내는이/받는이/주소/방법/요청자/처리자 + 품목표 + 진행률)
//                             ・ 하단 CTA: 미완료 outbound → [📦 배송 처리] 버튼 → App.js 콜백
//                             ・ 완료·입고 → [목록으로] 버튼만
//                          ③ 세그먼트 4-tab: [📄 시트지, 📦 출고, 📋 재고, 🔍 스캔] — 시트지 활성
//                       변경 (MobileScan.jsx, MobileInventory.jsx, MobileShipRequest.jsx):
//                          세그먼트 탭을 3-tab → 4-tab으로 확장 + 순서 재배치 [시트지, 출고, 재고, 스캔]
//                          각 컴포넌트에서 자신의 탭만 활성 표시
//                       변경 (App.js):
//                          ① MobileTxLog lazy import 추가
//                          ② 모바일 허용 페이지 리스트에 'txlog' 추가:
//                             기존: ['ship', 'inventory', 'scan']
//                             변경: ['ship', 'inventory', 'scan', 'txlog']
//                          ③ page==='txlog' 렌더 분기:
//                             ・ isMobile → <MobileTxLog onNavigate onProcessRequest/>
//                             ・ !isMobile → 기존 데스크톱 배송시트지 (변경 없음)
//                          ④ onProcessRequest 콜백:
//                             loadActiveRequest(reqId, model).then(→ setOutboundModalOpen(true))
//                             데스크톱 outbound modal 재사용 (모바일 UX 최적화는 후속 작업)
//                       파일 변경: src/MobileTxLog.jsx (신규), src/App.js,
//                                 src/MobileScan.jsx, src/MobileInventory.jsx, src/MobileShipRequest.jsx
// v7.149 (2026-07-13) — 배송처리 pending 시리얼 다중 PC 공유 (Supabase 동기화)
//                       배경: 사용자 요청 — 배송시트지 모달에서 입력한 시리얼이 로컬 PC의
//                             localStorage에만 저장되어 다른 PC에서 이어서 처리 불가.
//                             41개 시리얼 스캔 완료 후 커밋 전에 PC 이동/브라우저 변경 시 소실 위험.
//                       변경 (services/ShipPendingService.js — 신규):
//                          ・ Supabase 테이블 ship_pending_serials 책임 격리
//                          ・ loadByReqId / addSerial / addSerials / removeSerial /
//                            clearByReqId / cleanupStale 6개 함수 (모두 {ok, error, data?} 반환)
//                          ・ PK: (req_id, serial) — upsert 시 자동 dedup
//                       변경 (App.js):
//                          ① loadActiveRequest 초입 pending 로딩 로직 개편:
//                             1) Supabase 우선 조회
//                             2) 서버에 데이터 있음 → 진실의 원천으로 사용 + localStorage 동기화
//                             3) 서버 빈 결과 + local 있음 → local을 서버로 자동 마이그레이션
//                             4) Supabase 호출 자체 실패 (테이블 없음 등) → 조용히 localStorage 폴백
//                             5) autoExtPending 자동 생성 시 서버에도 즉시 저장 (다른 PC 공유)
//                          ② handleRequestScan: setPendingSerials 후 addSerial 병행 호출
//                          ③ removePendingSerial: 삭제 시 removeSerial 병행 호출
//                          ④ assignExternalToCurrentModel: 벌크 EXT- 부여 시 addSerials 병행
//                          ⑤ commitRequest 성공: clearShipPending + clearByReqId 병행 (다른 PC stale 제거)
//                          ⑥ 앱 시작 useEffect: cleanupStaleShipPending + ShipPendingService.cleanupStale
//                       원칙:
//                          ・ localStorage는 오프라인/장애 폴백으로 유지 (기존 v7.82 로직 그대로)
//                          ・ 모든 Supabase 호출은 catch로 조용히 실패 처리 (앱은 항상 로컬로 진행)
//                          ・ Race condition 처리: last-write-wins (upsert onConflict)
//                       필수 SQL (Supabase Editor에서 수동 실행):
//                          CREATE TABLE ship_pending_serials (
//                            req_id TEXT NOT NULL,
//                            serial TEXT NOT NULL,
//                            model TEXT NOT NULL,
//                            model_log_id BIGINT,
//                            external BOOLEAN DEFAULT false,
//                            serial_optional BOOLEAN DEFAULT false,
//                            created_at TIMESTAMPTZ DEFAULT NOW(),
//                            updated_at TIMESTAMPTZ DEFAULT NOW(),
//                            PRIMARY KEY (req_id, serial)
//                          );
//                          CREATE INDEX idx_ship_pending_req ON ship_pending_serials(req_id);
//                       파일 변경: src/App.js, src/services/ShipPendingService.js (신규)
// v7.148 (2026-07-13) — 배송시트지 드래그 핸들 [드래그 → 수정] 세로 순서 명시화
//                       사용자 리포트: 드래그가 수정 옆(오른쪽)에 붙어 렌더되어 있음.
//                                     드래그 버튼을 수정 버튼 위로 다시 배치 요청.
//                       분석: v7.147에서 flexDirection:column + 자식 순서만으로 [드래그, 수정]
//                             세로 스택을 의도했으나, 실제 렌더에서 어떤 사유로 옆으로 붙어보임.
//                             (v7.146 배치가 남은 캐시일 가능성도 있으나 방어적 명시화 진행)
//                       변경 (App.js):
//                          ① 드래그 핸들 div style에 order:0 명시
//                          ② 수정(open_in_new) div style에 order:1 명시
//                          ③ 첫 셀 컨테이너에 width:36, boxSizing:'border-box' 명시 (셀 폭 정합)
//                       파일 변경: src/App.js
// v7.147 (2026-07-10) — 배송시트지 드래그 핸들 원상복귀 + 상단 정렬
//                       사용자 리포트: v7.146에서 드래그 핸들을 날짜 셀로 옮겼는데 원래 위치가
//                                     낫다는 피드백. 대신 첫 셀 안에서 위쪽으로 위치 조정.
//                       변경 (App.js):
//                          ① 드래그 핸들 → 첫 셀(36px)로 원상복귀 (v7.145 이전 배치)
//                          ② 첫 셀 정렬을 flex-start (상단)로 변경
//                             기존: justifyContent:'center' (수직 중앙)
//                             변경: justifyContent:'flex-start' + padding 조정
//                          ③ 날짜 셀은 원래 구조로 복원 (flex/gap 제거)
//                       파일 변경: src/App.js
// v7.146 (2026-07-10) — 배송시트지 정렬 개선 + 드래그 핸들 위치 조정
//                       사용자 요청: (1) 출고완료된 건은 아래로 sink, 출고해야할 건은 상단에
//                                    (2) 드래그 핸들을 날짜 셀 왼쪽으로 이동
//                       변경 (App.js):
//                          ① loadTxLog: 페치 후 JS 안정 정렬로 재정렬
//                             ・ isRowFulfilled(row): outbound && note.fulfilled === true
//                             ・ 같은 날짜 그룹 내에서 fulfilled outbound는 아래로
//                             ・ pending outbound + inbound + 기타는 상단
//                             ・ JS sort 안정성 덕에 sort_order/created_at 원 순서 유지
//                          ② 드래그 핸들 이동
//                             기존: 36px 첫 셀에 [드래그 + 상세보기] 수직 스택
//                             변경: 36px 첫 셀엔 [상세보기]만, 드래그 핸들은 날짜 셀 왼쪽으로
//                             ・ 날짜 셀에 display:flex + gap 추가
//                             ・ 드래그 핸들 위 워터마크(NaverLogo)와 z-index 겹침 방지
//                             ・ v7.84의 draggable div 래핑 로직 유지 (Icon prop 무시 이슈 방어)
//                       파일 변경: src/App.js
// v7.145 (2026-07-10) — 🐛 외부제품 임시 시리얼(EXT-) 자동 부여 유실 버그 수정
//                       배경: 사용자 리포트 — NX523/RAIL-B02 같은 외부제품 배송처리 시 EXT- 임시
//                             시리얼이 자동 부여되지 않아 출고 진행 불가.
//                       분석: 실제 요청 JSON 확인 결과 items[NX523].external 플래그가 없음.
//                             원인은 [품목 추가] 흐름(addItemToShipment)에서 items push할 때
//                             external 플래그를 어떤 경우에도 붙이지 않는 것이었음.
//                             (자동이월 sweepAutoDefer는 transaction_date만 갱신, note 안 건드림 — 무관)
//                       변경 (App.js):
//                          ① addItemToShipment 시그니처 확장
//                             addItemToShipment(item, productName, qty = 1, asExternal = false)
//                             ・ asExternal=true → items에 external:true 플래그 부여
//                             ・ 기존 호출부 default false로 하위호환 유지
//                          ② 모델명 검색 모달 (품목 추가/변경 공용) 개선
//                             ・ 신규 state: modelSearchAsExternal (체크박스)
//                             ・ 추가 모드에서만 오렌지 체크박스 노출:
//                               "외부 처리로 추가 (임시 EXT- 시리얼 자동 부여)"
//                             ・ 검색 결과 클릭 시 / 수동 입력 시 모두 asExternal 전달
//                             ・ 모달 닫기 (배경/X) 시 체크박스 상태 리셋
//                          ③ 배송처리 모달 (인라인 + 팝업 양쪽)에 [임시 EXT- N개] 버튼 추가
//                             ・ assignExternalToCurrentModel 헬퍼: 남은 수량만큼 EXT-모델-XX 생성
//                             ・ 이미 잘못 저장된 기존 요청 (external 유실) 즉시 처리 가능
//                             ・ 위치: [시리얼 추가] 버튼 옆, 오렌지색 배지
//                             ・ 조건: 남은 수량 > 0 이고 현재 모델이 external/serialOptional 아닐 때
//                       참고: REQ-20260710-2059 등 이미 저장된 요청은 방식 ③(버튼)으로 즉시 해결.
//                              앞으로 신규 요청은 방식 ②(체크박스)로 저장 시점에 방어.
//                       파일 변경: src/App.js
// v7.144 (2026-07-10) — 모델 바코드 자동 분류 개선 (리전 접미사 stripping + 하이픈 라우팅)
//                       배경: 사용자 리포트 — QNAP 라벨 스캔 시 모델 바코드(TS-264-8G-EU)가
//                             시리얼 슬롯에 들어감. 카탈로그에는 TS-264-8G만 있어서
//                             정확 매칭 실패 → 시리얼로 잘못 분류.
//                       변경 (MobileScan.jsx):
//                          ① classifyCodes에 리전 접미사 stripping 추가
//                             ・ REGION_SUFFIXES 정규식: -EU, -US, -JP, -CN, -AP, -WW, -TW, -EA,
//                               -UK, -KR, -GB, -DE, -FR, -IT, -ES, -AU, -MX, -BR, -IN, -RU
//                             ・ 정확 매칭 실패 시 stripped 버전으로 재시도
//                             ・ 매칭 성공 시 카탈로그 정규화된 이름 사용
//                               예: TS-264-8G-EU 스캔 → catalog에 TS-264-8G → 정규화 이름 TS-264-8G로 채움
//                          ② 새 분류 클래스 'model_unknown'
//                             ・ 하이픈 포함 + 카탈로그 미등록 + 시리얼 패턴 통과 → 모델 후보로 판정
//                             ・ productName 필드로 라우팅 (productValid=false, 카탈로그 미등록 경고)
//                             ・ 시리얼 슬롯에 잘못 들어가는 것 방지
//                          ③ codeCanonicalRef: 감지 코드 → 정규화 제품명 매핑
//                          ④ processDetectedCodes 업데이트
//                             ・ effectiveProductCode = product || model_unknown 우선순위
//                             ・ setProductValid는 확정 매칭 시만 true, model_unknown은 false
//                             ・ 피드백에 카탈로그 미등록 경고 표시
//                          ⑤ 분류 색상 스킴에 model_unknown 추가 (노랑/warning)
//                             ・ 웹 진단바: T.warning
//                             ・ 네이티브 오버레이: #fcd34d (amber-300)
//                       실제 QNAP 라벨 v7.144 예상 동작:
//                          ・ TS-264-8G-EU  → stripped='TS-264-8G' → catalog에 있으면
//                                            product (canonical='TS-264-8G')
//                                          → 없으면 model_unknown (canonical=raw, 경고 표시)
//                          ・ QYC5801095E  → 하이픈 없음, 카탈로그 없음 → serial ✓
//                          ・ PQKP26022... → SHIPPING_PREFIXES → ignore
//                          ・ 순수숫자 UPC/EAN → ignore
//                       파일 변경: src/MobileScan.jsx
// v7.143 (2026-07-10) — 시리얼 다중 슬롯 입력 (한 박스의 여러 시리얼을 한 번에 관리)
//                       배경: 사용자 리포트 — "시리얼 입력칸이 하나여서 기록이 하나만 되고있어"
//                             자동추가 모드 OFF 시 새 스캔이 기존 값을 덮어씌워 여러 개 못 담음
//                       해법 (MobileScan.jsx):
//                          ① 상태 전환: scannedSerial (string) → serialInputs (string[])
//                             ・ 초기값 [''] — 항상 최소 1개 슬롯 유지
//                             ・ 각 슬롯은 독립적으로 편집/삭제 가능
//                          ② 헬퍼 함수 신설
//                             ・ fillNextSerialSlot(code) — 다음 빈 슬롯에 채움, 만석이면 새 슬롯 추가
//                             ・ updateSerialSlot(idx, val) — 개별 편집
//                             ・ removeSerialSlot(idx) — 개별 삭제
//                             ・ addEmptySerialSlot() — 수동으로 빈 슬롯 추가
//                             ・ clearAllSerialSlots() — 전체 지우기
//                             ・ addAllSerialsToQueue() — 벌크로 큐에 이동 (중복 자동 스킵)
//                          ③ UI 개편
//                             ・ 시리얼 라벨에 "N건 입력됨" 카운트 + 전체지우기 버튼
//                             ・ 각 슬롯 앞에 #N 인덱스 표시
//                             ・ 슬롯 우측에 × 삭제 버튼 (2개 이상일 때)
//                             ・ 하단 [+ 빈 슬롯 추가] 점선 버튼
//                             ・ 큐 추가 버튼이 "+ 큐에 N건 추가" (동적)
//                          ④ processDetectedCodes 수정
//                             ・ 자동추가 모드 ON: 기존 동작 유지 (직접 큐)
//                             ・ 자동추가 모드 OFF: setScannedSerial → fillNextSerialSlot
//                             ・ 중복 코드 자동 스킵 (같은 시리얼 재감지 시 무시)
//                          ⑤ 네이티브 오버레이에 대기 슬롯 카운트 표시
//                             ・ 자동추가 OFF & 슬롯 대기 있을 때: "🔖 슬롯 대기: N건 — 정지 후 큐에 추가"
//                             ・ 사용자가 전체화면 스캔 중에도 진행 상황 확인 가능
//                          ⑥ resetForNextBox 슬롯 초기화 (setScannedSerial → setSerialInputs([''])
//                       사용자 흐름:
//                          ・ (자동추가 OFF) 스캔 → 슬롯 1, 2, 3, 4 순차 채움 → 정지 → 검토 → 벌크 큐 추가
//                          ・ (자동추가 ON) 기존과 동일 — 스캔 즉시 큐에 추가
//                       파일 변경: src/MobileScan.jsx (~120줄 수정)
// v7.142 (2026-07-10) — 리테일 바코드·배송 라벨 필터 강화 (제품명 + 시리얼만 인식)
//                       배경: 사용자 리포트 — 카메라로 QNAP 박스 스캔 시 UPC 바코드가
//                             시리얼 필드로 들어감. 라벨에는 PALLET NO / CARTON NO /
//                             EAN / UPC / Model / S/N (4개) 등 다양한 바코드 공존.
//                             유저 요구: "제품명과 시리얼만" 인식.
//                       변경 (MobileScan.jsx):
//                          ① ML Kit BarcodeScanner.startScan 포맷에서 EAN/UPC 제거
//                             기존: CODE_128, ..., EAN_13, EAN_8, UPC_A, UPC_E, ITF, ...
//                             변경: CODE_128, CODE_39, CODE_93, CODABAR, ITF, QR_CODE,
//                                    DATA_MATRIX, AZTEC, PDF_417
//                             → 스캐너 자체가 리테일 바코드 감지 안 함
//                          ② ZXingWASM readBarcodes 포맷 동일하게 정리 (web 모드)
//                          ③ isValidSerialPattern 강화 (v7.133 완화 조치 부분 롤백)
//                             ・ 길이 6~30자 (기존 4~60 → 축소)
//                             ・ 순수 숫자 8/12/13/14자리 제외 (혹시 Code-128로 인코딩된
//                               UPC/EAN이 새어 들어와도 방어)
//                             ・ QNAP 배송 라벨 접두어 배제: PQKP*, CQKP*
//                                (SHIPPING_PREFIXES 상수화 → 추후 벤더 추가 확장 여지)
//                          ④ 네이티브 스캔 오버레이에 "최근 감지" 표시 추가
//                             ・ 마지막 3개 코드 + 분류 결과 (product/serial/ignore)
//                             ・ 파랑=product, 초록=serial, 빨강=ignore로 시각화
//                             ・ 필터 동작 여부 실시간 확인 가능
//                       실제 QNAP 라벨 기준 예상 동작:
//                          ・ PALLET NO PQKP...   → 접두어 필터 → ignore
//                          ・ CARTON NO CQKP...   → 접두어 필터 → ignore
//                          ・ EAN 4711103082331   → 스캐너가 감지 안 함
//                          ・ UPC 885022024681    → 스캐너가 감지 안 함
//                          ・ Item TS-264-8G-EU   → catalog 매칭 시 product, 없으면 serial
//                          ・ S/N QYC5801095E     → serial (자동 큐 추가)
//                       파일 변경: src/MobileScan.jsx
// v7.141 (2026-07-10) — 네이티브 스캔 오버레이 CSS 방어 (React 마운트 노드 ID 무관)
//                       배경: v7.140의 CSS가 body.barcode-scanner-active #root { visibility: hidden }
//                             전제로 작성됐지만, 실제 프로젝트의 React 마운트 노드 ID가 다르면
//                             (예: #app, #main 등) selector가 매치 안 되어 앱 UI가 계속 노출됨
//                       변경 (MobileScan.jsx):
//                          ① CSS selector 재작성 — ID 의존 제거
//                             기존: body.barcode-scanner-active #root { visibility: hidden }
//                             변경: body.barcode-scanner-active > *:not(.mobile-scan-native-overlay)
//                                     { visibility: hidden }
//                             ・ body 직속 자식 중 우리 오버레이가 아닌 모든 요소 숨김
//                             ・ 마운트 노드 ID가 무엇이든 정상 동작
//                          ② 오버레이 명시적 visible 규칙 유지 (부모 rule 오염 방어)
//                       파일 변경: src/MobileScan.jsx (CSS 문자열만 수정)
// v7.140 (2026-07-10) — 🐛 네이티브 스캔 시 카메라 프리뷰가 앱 뒤에 가려지던 문제 수정
//                       증상: Capacitor APK에서 스캔 시작해도 앱 UI만 보이고 카메라 화면이 안 뜸
//                             ("카메라가 어플 뒤에 보이는 것 같다"는 사용자 리포트)
//                       원인: ML Kit BarcodeScanner는 WebView 뒷면에 카메라 프리뷰 렌더링.
//                             v7.138에서 CSS로 html/body/#root의 background만 transparent 처리했지만,
//                             app.js의 outer container와 MobileScan.jsx의 outer div가 인라인 스타일로
//                             background 갖고 있어서 계속 앞을 막음. CSS 캐스케이드로는 우회 불가능.
//                       해법: 스캔 중 앱 UI 전체를 시각적으로 숨기고 (visibility: hidden on #root),
//                             fullscreen 오버레이만 createPortal로 document.body에 직접 렌더링.
//                             #root와 오버레이는 형제 관계 → #root 숨겨도 오버레이는 보임.
//                       변경 (MobileScan.jsx):
//                          ① import { createPortal } from 'react-dom' 추가
//                          ② CSS 재작성:
//                             기존: html/body/#root/data-app-root → background transparent
//                             변경: body.barcode-scanner-active #root → visibility: hidden !important
//                                   body.barcode-scanner-active .mobile-scan-native-overlay → visibility: visible
//                          ③ nativeScanOverlay = createPortal(...) — body 직접 렌더
//                             ・ position: fixed inset: 0, z-index 999999
//                             ・ background: transparent (카메라 그대로 관통)
//                             ・ 상단: 🚀 ML Kit 배지 + 현재 제품명 + 피드백 메시지
//                                     (반투명 그라디언트 상단 스트립)
//                             ・ 중앙: 파란 스캔 프레임 (4모서리 하이라이트 + 주변 어둡게)
//                             ・ 하단: 대기 큐 카운트 + 직전 시리얼 + 정지 버튼
//                                     자동 추가 모드 ON 시 안내 문구
//                             ・ pointerEvents: 'none'/'auto' 계층화 → 카메라 탭도 통과 가능
//                             ・ env(safe-area-inset-top/bottom) 반영 (iOS notch/Android 제스처 대응)
//                          ④ return을 <>...</> Fragment로 감싸서 overlay + 기본 UI 병행 렌더
//                       사용자 흐름:
//                          ・ (스캔 전) 정상 UI에서 제품명 설정 + 자동 추가 모드 ON
//                          ・ [카메라 시작] → 오버레이 fullscreen + ML Kit 카메라 뒷면
//                          ・ 시리얼 스캔 → 자동 추가 → 하단 큐 카운트 실시간 증가
//                          ・ [스캔 정지] → 정상 UI 복귀 → 동기화
//                       파일 변경: src/MobileScan.jsx (오버레이 컴포넌트 ~80줄 추가, CSS 재작성)
// v7.139 (2026-07-10) — 재고 목록 엑셀 다운로드 (제품명 + 시리얼 + 부가정보 한 번에)
//                       배경: 사용자 요청 — "재고목록에 엑셀파일로 제품명 시리얼을
//                             한번에 다운받는 기능을 추가했으면 해".
//                             지금까지 재고 목록에서 외부로 반출하려면 화면을 스크롤하면서
//                             복붙하거나 별도 도구를 써야 했음. 재고 실사/거래처 공유/
//                             인수인계 문서화 등에서 반복적 병목.
//                       접근: 이미 사내에 도입된 XLSX 라이브러리(v7.49 배송장 출력 경로)를
//                             재고 목록 헤더에 그대로 연결. 새 의존성 추가 없음, 번들 사이즈 0 증가.
//                       변경 (App.js):
//                          ① 상태 추가 — const [downloadingInvXlsx, setDownloadingInvXlsx]
//                             (연속 클릭/중복 요청 방지 및 버튼 로딩 표시)
//                          ② downloadInventoryXlsx() useCallback 함수 신설
//                             ・ 현재 3가지 필터를 서버 query에 반영:
//                                - filter (상태): all/in_stock/reserved/unsellable/shipped
//                                - stockTypeFilter (유형): all/신품/리페킹/조립출고
//                                - inventoryTab (탭): hdd일 때 category IN ('HDD','SSD')
//                             ・ 검색어(search)는 무시 — UI의 스마트 매칭(카테고리/note/dealer/
//                               날짜 등 클라이언트 로직 혼합)이 서버 query와 완전 대칭이 아니라
//                               "재고 스냅샷" 용도로 필터만 반영하는 것이 안전
//                             ・ 페이지네이션: BATCH=1000, MAX_BATCHES=100 (최대 10만 건 상한)
//                             ・ 정렬: loadInventory와 동일한 sortColMap 매핑 → 화면 정렬 유지
//                             ・ 상태 코드는 한글 라벨로 치환 (in_stock→재고, reserved→예약,
//                               unsellable→판매불가, shipped→출고)
//                             ・ 시트 컬럼: [카테고리, 제품명, 시리얼, 상태, 유형, 창고, 입고일]
//                                (사용자 명시 요청은 "제품명+시리얼"이지만 재고 실사·거래처
//                                 공유 실무에 필요한 최소 정보 5종을 함께 담아 활용도 확보)
//                             ・ 헤더 행 고정(freeze) 적용 — 스크롤 시 컬럼 헤더 유지
//                             ・ 파일명: 재고목록[_HDD_SSD][_상태][_유형]_YYYYMMDD.xlsx
//                                (어떤 조건으로 뽑았는지 파일명으로 즉시 식별)
//                             ・ 날짜 문자열은 로컬 타임존 getFullYear/getMonth/getDate 사용
//                                (v7.126 UTC 어제 버그 원칙 준수)
//                          ③ UI — 재고 목록 페이지 헤더 우측 [이벤트 캘린더] 버튼 왼쪽에
//                                 [엑셀 다운로드] 버튼 신규 배치
//                             ・ inventoryTab === 'list' | 'hdd'일 때만 노출
//                               (dashboard/analytics 탭은 별개 시각화 데이터라 부적합)
//                             ・ 아이콘: download (진행 중에는 hourglass_top으로 전환)
//                             ・ 색상: #009844 (success 그린) — 데이터 반출 액션 시각 구분
//                             ・ 진행 중 버튼 비활성화 + 반투명 + cursor:wait
//                             ・ title 속성으로 "현재 필터 조건 반영" 안내
//                          ④ 헤더 우측 컨테이너를 flex 그룹으로 변경하여 두 버튼 정렬
//
// v7.138 (2026-07-09) — 모바일 스캔 네이티브 (Capacitor + ML Kit) 브릿지
//                       배경: 웹 카메라 API의 근본적 한계 — 매크로 초점 미지원, 압축된 스트림,
//                             하드웨어 기능 접근 제한 → zxing-cpp도 결국 저품질 이미지에서 실패
//                       해법: Capacitor로 웹앱을 네이티브 앱(APK/IPA)로 감싸고
//                             스캔 시에만 Google ML Kit (오프라인, 무료, 인식률 최상위)를 호출
//                             나머지 페이지·업데이트는 웹 배포만으로 완료 (APK 재설치 불필요)
//                       변경 (MobileScan.jsx):
//                          ① Capacitor 네이티브 실행 감지
//                             ・ isNative = !!window.Capacitor?.isNativePlatform?.()
//                             ・ 웹 브라우저에서는 false → 기존 zxing-wasm 경로 유지
//                          ② startCamera/stopCamera 분기
//                             ・ isNative && startCameraNative(): ML Kit BarcodeScanner.startScan + listener
//                             ・ isNative && stopCameraNative(): stopScan + removeAllListeners + 배경 복구
//                             ・ 아니면 기존 startCameraWeb/stopCameraWeb (zxing-wasm)
//                          ③ WebView 투명 처리 — ML Kit가 뒷면에 카메라 프리뷰 렌더링
//                             ・ document.documentElement/body에 'barcode-scanner-active' 클래스 토글
//                             ・ 마운트 시 <style> 주입: html/body/#root 배경 transparent
//                          ④ processDetectedCodes(codes) 공유 함수로 리팩터
//                             ・ 웹 rAF 루프와 네이티브 리스너 둘 다 동일 코드 분류 로직 사용
//                             ・ catalog 매칭, productName/serial 자동 채움, 큐 추가, haptic 등
//                          ⑤ UI: 네이티브 모드 배지 (🚀 ML Kit) + 스캔 프레임 색상 변경 (파랑)
//                             ・ 진단 바에 "스캐너: 🚀 ML Kit (Native)" 표시
//                             ・ 웹 전용 요소 (video, torch, tap-to-focus 힌트) 숨김
//                       파일 변경: src/MobileScan.jsx
//                       ※ 별도 프로젝트: inventura-mobile (Capacitor 래퍼 + ML Kit 플러그인)
// v7.137 (2026-07-09) — 모바일 스캔 카메라 품질 강화 + WASM 응답 진단
//                       배경 (v7.136 검증): WASM은 정상 로드되고 tryHarder도 동작하지만
//                                            카메라 프리뷰가 흐릿 → zxing-cpp도 디코드 불가
//                       (스크린샷: v7.135는 텍스트 선명, v7.136은 초점 안 맞음 → 카메라 quality 이슈)
//                       변경 (MobileScan.jsx):
//                          ① getUserMedia 제약 강화
//                             ・ width/height: 1280×720 → 1920×1080 (ideal), 최소 1280×720
//                             ・ frameRate: ideal 30 (안정성)
//                             ・ advanced: [{focusMode:'continuous'}, {focusMode:'single-shot'}]
//                          ② 손전등(torch) 토글
//                             ・ 스트림 track.getCapabilities().torch로 지원 여부 확인
//                             ・ 지원 시 카메라 영역 좌상단에 [🔦 손전등] 버튼
//                             ・ 창고/어두운 환경 대응
//                          ③ 탭 투 포커스
//                             ・ 카메라 영역 탭 시 상대 좌표(0~1)를 pointsOfInterest로 전달
//                             ・ 지원 시 그 지점에 초점, 미지원 시 single-shot 트리거만
//                             ・ 하단에 "탭하면 초점" 힌트 표시
//                          ④ WASM 응답 진단 지표 추가
//                             ・ 프레임당 readBarcodes 소요시간 (ms) + 반환 코드 개수
//                             ・ 예외 발생 시 "예외" 표시
//                             ・ 평균 소요시간 (누적)
//                             ・ 진단 바에 "WASM 응답: N개 코드 · 디코드 152ms (평균 148ms)" 표시
//                       진단 목적:
//                          ・ readBarcodes가 실제로 완료되는지 (0개도 정상 완료)
//                          ・ 디코드가 너무 오래 걸리는지 (>500ms면 다운샘플 검토)
//                          ・ 예외가 반복되는지
//                       파일 변경: src/MobileScan.jsx
// v7.136 (2026-07-09) — 모바일 스캔 인식률 근본 해결: zxing-wasm (C++/WASM 포트) 도입
//                       배경 (v7.135 실패): @zxing/library UMD 빌드에서 HTMLCanvasElementLuminanceSource가
//                                            노출 안 되어 첫 프레임부터 예외 발생 → 1621프레임 계속 미감지
//                       변경 (MobileScan.jsx):
//                          ① CDN 변경: @zxing/library@0.21.3 → zxing-wasm@3.1.0
//                             ・ zxing-cpp (원조 C++ 구현)의 WebAssembly 포트
//                             ・ 순수 JS 대비 수배 빠르고 인식률 우수
//                             ・ 스크립트 ~50KB, WASM ~1MB (jsdelivr에서 lazy 로드)
//                          ② 전역 변수 변경: window.ZXing → window.ZXingWASM
//                          ③ API 대폭 간소화
//                             기존: new MultiFormatReader → HTMLCanvasElementLuminanceSource → BinaryBitmap → decode
//                             변경: await ZXingWASM.readBarcodes(imageData, { tryHarder, formats, maxNumberOfSymbols })
//                             ・ ImageData 직접 입력 (canvas → ctx.getImageData → 전달)
//                             ・ 한 함수 호출로 완결
//                          ④ WASM 사전 warmup — 스크립트 로드 직후 더미 1×1 ImageData로 wasm 로드 트리거
//                             첫 실제 스캔 시 지연 없음
//                          ⑤ 5회 연속 예외 시 native BarcodeDetector로 자동 폴백
//                          ⑥ 포맷명 케이스 변경: CODE_128 → Code128 등 (zxing-wasm 명세)
//                          ⑦ maxNumberOfSymbols: 20 — 박스에 시리얼 4~10개 있어도 한 번에 감지
//                          ⑧ 진단 바 라벨 갱신: "ZXing-WASM (tryHarder) · WASM 상태: ✓ 로드됨"
//                       참고:
//                          ・ 첫 로드 시 WASM 다운로드 ~1MB (jsdelivr CDN)
//                          ・ Cloudflare Tunnel/방화벽이 .wasm 확장자 차단 시 native 폴백
//                       파일 변경: src/MobileScan.jsx
// v7.135 (2026-07-09) — 모바일 스캔 인식률 개선: ZXing-js 도입
//                       배경: v7.134 canvas 캡처 후에도 네이티브 BarcodeDetector API 인식률이 낮음
//                             (각도, 거리, 조명이 약간만 어긋나도 실패)
//                       근본 원인: 네이티브 API 구현체 품질 편차 (특히 iOS Safari)
//                       변경 (MobileScan.jsx):
//                          ① @zxing/library UMD 빌드를 jsdelivr CDN에서 동적 로드
//                             ・ <script src="https://cdn.jsdelivr.net/npm/@zxing/library@0.21.3/umd/index.min.js">
//                             ・ 10초 타임아웃, 실패 시 네이티브로 자동 폴백
//                             ・ 로드 상태: 'loading' | 'ready' | 'failed'
//                          ② ZXing.MultiFormatReader 사용
//                             ・ TRY_HARDER 힌트 활성화 → 여러 이진화·회전 시도 (인식률↑)
//                             ・ POSSIBLE_FORMATS: Code128/39/93/Codabar/EAN/UPC/ITF/QR/DataMatrix/Aztec/PDF417
//                             ・ HTMLCanvasElementLuminanceSource로 canvas → luminance 변환
//                             ・ HybridBinarizer → BinaryBitmap → decode 파이프라인
//                          ③ 감지 루프 우선순위
//                             ・ ZXing ready → ZXing만 사용
//                             ・ ZXing 미로드/실패 → 네이티브 BarcodeDetector 폴백
//                          ④ 진단 바에 활성 스캐너 + ZXing 로드 상태 표시
//                             예: "스캐너: ZXing (TRY_HARDER) · ZXing 상태: ✓ 로드됨"
//                       참고:
//                          ・ CDN 첫 로드 시 ~200-300KB 추가 트래픽 (이후 브라우저 캐시)
//                          ・ ZXing decode는 native 대비 CPU 사용 높지만 인식률 큰 폭 개선
//                       파일 변경: src/MobileScan.jsx
// v7.134 (2026-07-09) — 모바일 스캔 바코드 미감지 대응 2단계
//                       증상 (v7.133 배포 후): 프레임은 증가하는데 인식 카운트가 0 → BarcodeDetector가 아무 것도 못 읽음
//                       원인 후보:
//                          (A) 브라우저의 BarcodeDetector가 우리가 요청한 포맷을 실제로는 지원 안 함
//                              (iOS Safari 17, 구형 Android Chrome 등)
//                          (B) detector.detect(video)가 video 요소를 직접 받으면 프레임 데이터를
//                              제대로 읽지 못하는 브라우저 있음
//                          (C) video.videoWidth === 0 (스트림은 재생 중이나 프레임 미제공)
//                       변경 (MobileScan.jsx):
//                          ① BarcodeDetector.getSupportedFormats() 조회 → supportedFormats state 저장
//                             요청 포맷과 교집합만으로 detector 생성 (지원 안 되는 포맷 요청 시 에러 방지)
//                             교집합 계산 실패 시 브라우저 전체 지원 목록으로 폴백
//                             그마저 실패하면 포맷 없이 (default all-formats)로 시도
//                          ② 요청 포맷 확대: EAN/UPC/Aztec/PDF417/Code93/Codabar까지 포함
//                             ・ 이전엔 리테일 제외 목적으로 좁혔지만 감지가 안 되면 무의미
//                             ・ 실제 시리얼이 EAN 유사 포맷일 수 있음 (드물지만)
//                          ③ 감지 방식 전환: detect(video) → detect(canvas)
//                             ・ offscreen <canvas>에 video 프레임 그린 뒤 detect
//                             ・ 일부 브라우저(특히 iOS)에서 video 직접보다 canvas가 안정적
//                             ・ drawImage 실패는 별도 오류로 표시
//                          ④ video.videoWidth/Height 실측 진단 UI 추가
//                             ・ 0×0이면 스트림은 있어도 프레임 미제공 → 재생 실패 힌트
//                          ⑤ 진단 바에 지원 포맷 표시
//                       파일 변경: src/MobileScan.jsx
// v7.133 (2026-07-09) — 모바일 스캔 바코드 미인식 대응: 패턴 완화 + 진단 UI
//                       증상 (v7.132 이후 보고): 카메라는 정상 작동하지만 바코드 인식이 안 됨
//                       원인 후보:
//                          (A) 패턴 필터 과엄격 — 순수숫자 8/12/13/14자리 방어가 실제 시리얼도 걸러냄
//                          (B) 감지 오류가 조용히 무시되어 진단 불가
//                          (C) catalog 조회 지연 중 'pending' 상태에서 아무 반응 없음
//                       변경 (MobileScan.jsx):
//                          ① isValidSerialPattern 완화
//                             기존: 길이 6~40 + 순수숫자 8/12/13/14 제외
//                             변경: 길이 4~60만 체크 (포맷 필터가 이미 EAN/UPC 차단하므로 관대하게)
//                          ② 진단용 상태 바 UI 추가 (카메라 phase에서만)
//                             ・ 📊 프레임 카운트 (rAF 루프 실행 확인)
//                             ・ 인식 카운트 (detector.detect가 결과 반환한 횟수)
//                             ・ 최근 3개 코드 + 분류 결과 (product / serial / ignore / pending)
//                             ・ 감지 오류 메시지 (있으면)
//                             ・ 30프레임 이상 인식 0 → "바코드 미감지" 경고
//                          ③ 감지 카운터/에러 refs + 500ms 간격 state 동기화 (60fps 리렌더 방지)
//                          ④ 감지 오류 시 console.warn + 상태 바에 표시 (silent catch 해제)
//                       파일 변경: src/MobileScan.jsx (약 60줄 추가)
// v7.132 (2026-07-09) — 모바일 스캔 다중 바코드 자동 분류 (제품명 vs 시리얼 vs 무시)
//                       배경: 실제 제품 박스에는 여러 바코드가 붙어있음
//                             ・ 제품명/모델 바코드 (Code-128)
//                             ・ 시리얼 바코드 (Code-128 / QR)
//                             ・ EAN-13 / UPC (리테일 판매용)
//                             ・ 배치번호, 배송 라벨 등 기타
//                          v7.131 이전: results[0]만 사용 → 아무거나 하나가 시리얼 필드로 → 오탐 다수
//                       변경 (MobileScan.jsx):
//                          ① BarcodeDetector 포맷에서 EAN/UPC 제거
//                             기존: code_128, code_39, ean_13, ean_8, upc_a, upc_e, qr_code, data_matrix, itf
//                             변경: code_128, code_39, qr_code, data_matrix, itf (산업용/시리얼용만)
//                          ② 다중 코드 처리 — 프레임의 모든 감지 결과 순회
//                             ・ Set으로 중복 제거
//                             ・ 각 코드를 catalog와 대조 (ProductService.findByNames)
//                             ・ 매칭 → 'product' / 유효 패턴 → 'serial' / 그 외 → 'ignore'
//                          ③ 분류 캐시 (codeClassRef: Map<code, class>)
//                             ・ 프레임마다 Supabase 재조회 방지
//                             ・ in-flight 중복 조회 방지 ('pending' 마킹)
//                             ・ 카탈로그 조회 실패 시 패턴 판정으로 폴백
//                          ④ 자동 채움 로직
//                             ・ productCode 감지 시 productName 자동 입력 (productValid=true일 땐 덮어쓰지 않음)
//                             ・ serialCode 감지 시 scannedSerial 입력, 자동 추가 모드면 즉시 큐 append
//                             ・ 제품명만 감지된 프레임: 짧은 haptic (40ms) + 피드백
//                             ・ 시리얼 감지: 기존 haptic (80ms) 유지
//                          ⑤ isValidSerialPattern — 순수숫자 8/12/13/14자리 방어 (포맷 필터 통과 시 폴백)
//                          ⑥ 사용 안내 문구 갱신
//                       파일 변경: src/MobileScan.jsx (약 70줄 추가/재작성)
// v7.131 (2026-07-09) — 🐛 모바일 스캔 카메라 검은 화면 버그 수정
//                       증상: 모바일에서 [카메라 시작] 클릭 시 카메라 권한은 통과하지만
//                             비디오 영역이 검은색으로만 표시 (스트림 미표시)
//                       원인: startCamera 함수에서 srcObject 할당 순서가 잘못됨
//                          ・ getUserMedia 성공 → streamRef.current = stream 저장
//                          ・ if (videoRef.current) srcObject 할당 시도
//                            ❌ 이 시점에 phase는 아직 'idle' → <video> 미 mount → videoRef.current === null
//                            ❌ if 가드에 걸려 조용히 스킵됨
//                          ・ setPhase('camera') → 이제야 <video> mount 되지만 srcObject 미할당
//                          ・ 결과: 검은 배경만 표시 (video 요소는 있지만 stream이 안 붙음)
//                          ・ 데스크톱에서 종종 작동했던 이유: getUserMedia 응답이 빨라
//                            React 재렌더 타이밍이 우연히 맞는 경우가 있었음. 모바일에선
//                            카메라 하드웨어 초기화 지연으로 재현적으로 실패
//                       변경 (MobileScan.jsx):
//                          ① startCamera에서 srcObject/play 로직 제거 → setPhase('camera')만 수행
//                          ② 신규 useEffect(phase 감시): phase === 'camera' 진입 시
//                             <video> mount 완료 후 srcObject 할당 + video.play() 호출
//                             ・ 이미 attach된 스트림이면 중복 play 방지 (guard)
//                             ・ play() 실패 시 feedback banner + console.warn
//                          ③ <video> 요소에 onClick 추가: paused 상태면 재시도 (autoplay 정책 방어)
//                       파일 변경: src/MobileScan.jsx
// v7.130 (2026-07-09) — 모바일 스캔 하단 sticky 큐 바 (스크롤 없이 큐 상태 확인 + 동기화)
//                       배경:
//                          큐에 항목이 쌓이면 스크롤을 올려야만 큐 카운트/동기화 버튼이 보임
//                          → 카메라/입력 영역에 오래 머무는 실제 스캔 흐름과 어긋남
//                       변경 (MobileScan.jsx):
//                          ① 하단 고정 (position: fixed) 큐 미리보기 바 신설
//                             ・ 좌: "대기 큐 N건" + "직전: {serial} · {제품명}" (텍스트 잘림 처리)
//                             ・ 우: [☁ 동기화] 버튼 (기존 상단 카드와 동일 로직)
//                             ・ queue.length > 0 일 때만 표시 (빈 상태 노이즈 방지)
//                             ・ safe-area-inset-bottom 반영 (iOS notch/제스처 영역 보호)
//                             ・ z-index 15 (Sticky 헤더 z-index 10 위, 자동완성 드롭다운 z-index 10 위)
//                          ② 컨텐츠 padding-bottom 88px 자동 확보 (큐 있을 때) — 마지막 카드 가림 방지
//                          ③ 기존 상단 큐 요약 카드는 유지 — 상단 스크롤 위치에서 상태 확인용
//                       파일 변경: src/MobileScan.jsx (약 20줄 추가)
// v7.129 (2026-07-09) — 모바일 스캔 탭 활성화 + PC 사이드바에서 제거
//                       배경:
//                          (1) PC에서 '📸 모바일 스캔' 사이드바 항목이 노출되지만 PC엔 카메라 없음 → 클릭 시 무용
//                          (2) v7.121에서 모바일 사이드바가 완전 제거되면서 모바일에선 스캔 페이지 접근 경로가 사라짐
//                       변경:
//                          ① App.js nav 배열에서 { id:'scan', ... } 완전 제거
//                             → PC 사이드바에 더 이상 노출 안 됨
//                          ② 페이지 강제 리다이렉트 useEffect (~2389)에 'scan' 허용
//                             기존: isMobile && page !== 'ship' && page !== 'inventory' → setPage('ship')
//                             변경: isMobile && !['ship','inventory','scan'].includes(page) → setPage('ship')
//                          ③ PC에서 'scan' 잔존 상태 방어: !isMobile && page === 'scan' → setPage('txlog')
//                          ④ MobileScan 렌더 (~9342)에 isMobile 가드 + onNavigate prop 전달
//                             기존: {page==='scan' && <Suspense><MobileScan onExit={()=>setPage('txlog')}/></Suspense>}
//                             변경: {page==='scan' && isMobile && <Suspense><MobileScan onExit={()=>setPage('inventory')} onNavigate={(p)=>setPage(p)}/></Suspense>}
//                       모바일 UX:
//                          ① MobileShipRequest.jsx / MobileInventory.jsx 세그먼트 탭 3개로 확장
//                             기존: [📦 출고요청] [📋 재고목록]
//                             변경: [📦 출고요청] [📋 재고목록] [🔍 스캔]
//                          ② MobileScan.jsx 재작성 — 1a 디자인 시스템 적용
//                             ・ T 토큰 (MobileShipRequest.jsx / MobileInventory.jsx 동일 팔레트)
//                             ・ Sticky 브랜드 헤더 (🌊 RIVER NETWORK)
//                             ・ 3-tab 세그먼트 (스캔이 활성)
//                             ・ 기존 로직 유지: BarcodeDetector, getUserMedia, 큐 관리, 자동 추가 모드, 박스 프리셋
//                             ・ props: { onExit, onNavigate } — onNavigate로 다른 세그먼트 탭 이동
//                       파일 변경: src/App.js, src/MobileScan.jsx (전체 재작성), src/MobileShipRequest.jsx (세그먼트 3-tab), src/MobileInventory.jsx (세그먼트 3-tab)
// v7.128 (2026-07-08) — 재고 대시보드: 카테고리 정렬 커스텀 순서 + stock_type 라벨 풀 텍스트
//                       변경 (App.js 재고 대시보드):
//                          ① 카테고리 정렬 커스텀 순서
//                             기존: 가나다순 (기타/미분류만 뒤로)
//                             변경: NAS → DAS → HDD → SSD → Switch → Memory → Network Adapter
//                                   → PSU → Cable/Transceiver → Rack/Accessory → 기타 → 미분류
//                             ・ CATEGORY_ORDER 배열 도입, indexOf 기반 sort
//                             ・ 목록에 없는 신규 카테고리는 알파벳순으로 기타 앞에 배치
//                          ② stock_type 라벨 축약 → 풀 텍스트
//                             기존: 신 N · 리 N · 조 N
//                             변경: 신품 N · 리페킹 N · 조립출고 N
//                             ・ title tooltip 제거 (라벨 자체가 명확해짐)
//                             ・ 폰트 9.5 → 10 (풀 텍스트에 맞춰 미세 조정)
//                       파일: App.js
// v7.127 (2026-07-08) — 자동이월 시각 공유 저장 + 데모 대여 비고에 시리얼 표기
//                       배경:
//                          (1) autoDefer 설정이 localStorage 저장이라 PC마다 값이 다름 → 혼돈
//                          (2) 데모 대여 시 배송시트지 비고에 어떤 모델/시리얼이 나갔는지 표기 필요
//                       변경:
//                          ① 신규 테이블 app_settings (key/value 공유 설정)
//                             CREATE TABLE app_settings (
//                               key TEXT PRIMARY KEY,
//                               value TEXT NOT NULL,
//                               updated_at TIMESTAMPTZ NOT NULL DEFAULT NOW(),
//                               updated_by TEXT
//                             );
//                             ⚠ Supabase SQL Editor에서 수동 실행 필요
//                          ② autoDeferMinutes 동기화 로직 (App.js)
//                             ・ 접속 시: Supabase 조회 → 성공 시 우선 적용 + localStorage 캐시
//                             ・ 저장 시: Supabase upsert + localStorage 병행
//                             ・ Supabase 실패 시 localStorage로 폴백 (오프라인 안전)
//                             ・ 새로고침 필요: 다른 PC의 변경은 재접속 시 반영
//                          ③ 데모 대여 비고 개선 (submitDemoLend, App.js ~5535, ~5471)
//                             ・ 기존: memo: '[데모 대여] {노트}'
//                             ・ 신규: memo: '[데모 대여] {노트}\n\n{모델1}\n{시리얼1}\n{시리얼2}\n\n{모델2}\n...'
//                             ・ 신규/수정 둘 다 적용
//                             ・ 예시:
//                                [데모 대여] 컴퓨존 시연용
//                                
//                                TS-873A-8G
//                                Q22I108171L
//                                Q22I108151D
//                                
//                                QXG-10G2T-X710
//                                Q214I06952B
//                                Q216I036212
//                          ④ 대여로그 자동 기록: 배송시트지의 [출고 완료] 처리 시 데모 감지 →
//                             DemoService.appendRentalAudit 호출 (fromDemoLend 플래그 활용)
//                             ・ submitOutbound 함수에서 note.fromDemoLend 체크 후 audit 추가
//                             ・ 감사 로그: "출고완료 처리 - by {처리자}" (transaction_date 기준)
//                       파일: App.js, services/DemoService.js
// v7.126 (2026-07-08) — 🐛 CRITICAL: UTC 타임존 버그 수정 (입고/출고 날짜 어제로 저장)
//                       사용자 보고: "물건을 입고했는데 배송시트지에 기록되지않고 날짜도 어제로 찍혀있어"
//                       원인:
//                          기존 today() = new Date().toISOString().split('T')[0]
//                          → toISOString()은 UTC 반환. 한국(UTC+9)에서 오전 9시 이전 작업 시:
//                            로컬 08:30 → UTC 23:30 (전일) → "2026-07-07T23:30" → 어제 날짜
//                          → transaction_date, receipt_date, sale_date 전부 어제로 저장
//                          → 배송시트지 "오늘" 필터에 안 잡힘 → 안 보임
//                       영향받은 위치:
//                          ・ App.js line 2053: 전역 today() (데스크톱 입고 등록/출고 요청)
//                          ・ App.js line 6317: saleDate fallback (out 처리)
//                          ・ MobileScan.jsx line 31: 모바일 스캔 입고
//                          ・ MobileShipRequest.jsx line 22: 모바일 출고 요청
//                          ・ services/InventoryService.js line 229, 258: shipBySerial sale_date
//                       수정:
//                          로컬 시간대 기준 YYYY-MM-DD 반환하는 헬퍼로 교체:
//                          const today = () => {
//                            const d = new Date();
//                            const y = d.getFullYear();
//                            const m = String(d.getMonth() + 1).padStart(2, '0');
//                            const day = String(d.getDate()).padStart(2, '0');
//                            return `${y}-${m}-${day}`;
//                          };
//                       주의: 이미 어제 날짜로 잘못 저장된 기존 데이터는 DB에서 수동 UPDATE 필요.
//                             예) UPDATE inventory SET receipt_date = current_date WHERE ...
//                             예) UPDATE transaction_log SET transaction_date = current_date WHERE ...
//                       참고: 주간/월간 필터의 monday/sunday.toISOString().split('T')[0] 도 같은 문제 있으나
//                             상대적 계산이라 오차 1일 이내 → 별도 이슈로 유지 (필요 시 후속 개선).
// v7.125 (2026-07-08) — 📱 모바일 출고요청 제품 검색/리스트 개선
//                       배경:
//                          ・ 검색 시 race condition 위험 (빠르게 타이핑 → 이전 응답이 최신 결과 덮음)
//                          ・ 초기 30개 리스트가 너무 길어 폼 진입 전 스크롤 부담
//                       변경 (MobileShipRequest.jsx):
//                          ① 검색 debounce (300ms) + cancellation flag
//                             ・ debouncedSearch state 도입 → useEffect로 실제 쿼리 실행
//                             ・ cleanup 시 cancelled=true로 응답 무시 (race safe)
//                          ② 초기 리스트 30 → 10 (부담 완화)
//                          ③ 스크롤 컨테이너: maxHeight 320px (제품 리스트 부분만 스크롤)
//                             ・ 아래 배송/수령인 폼에 빠르게 도달 가능
//                          ④ 카드 컴팩트화:
//                             ・ 썸네일 46 → 36px, radius 10 → 8
//                             ・ padding 9x11 → 8x10, marginBottom 8 → 6
//                             ・ font 13 → 12.5, 담기 버튼 padding 축소
//                             ・ 카테고리 line-clamp
//                          ⑤ 검색 UI 개선:
//                             ・ 검색 중일 때 loading 스피너 표시 (인풋 우측)
//                             ・ 결과 헤딩: "검색 결과 N개" (검색 시) vs "제품 조회 · 상위 10개" (초기)
//                             ・ 결과 없음 시 "매칭되는 제품이 없습니다" placeholder
//                       파일: src/MobileShipRequest.jsx
// v7.124 (2026-07-08) — 📱 모바일 출고요청: 성공 모달 + 전체 초기화 + 직발/네이버 발견성 개선
//                       변경 (MobileShipRequest.jsx):
//                          ① 성공 시 배너 대신 중앙 모달 표시
//                             ・ 초록 원형 ✓ 아이콘 + "출고요청이 접수되었습니다" + reqId (mono) + [확인]
//                             ・ 백드롭 반투명 검정 + 모달 tap 이벤트 전파 방지
//                             ・ [확인] 클릭 시 모달 닫힘 + 전체 폼 초기화
//                          ② 전체 폼 초기화 로직 (handleModalClose):
//                             ・ method → 'courier' (기본)
//                             ・ cart 비움, 검색어/결과 초기화
//                             ・ dealerName, recipient, phone, address, memo 초기화
//                             ・ expectedDate → today()
//                             ・ directDispatch, directCompany, naverStore 초기화
//                             ・ 스크롤 상단 이동
//                             ・ author/requester는 유지 (사용자 편의)
//                          ③ 직발/네이버 옵션 항상 노출 (v7.122 조건부 → 항상 표시)
//                             ・ 택배·퀵일 때만 활성 (canDirect)
//                             ・ 그 외 method는 disabled + opacity 0.5 + "택배/퀵 선택 시 활성화" 안내
//                             ・ 사용자가 이 옵션이 존재함을 즉시 인지 가능 (발견성)
//                          ④ 직접 설치 라벨 데스크톱과 일치화
//                             ・ '직접설치' → '직접 설치' (공백 포함, 배송시트지 파싱 호환)
//                       유지 사항:
//                          ・ submit 로직 (요청만 등록, 시리얼은 배송시트지에서 배정)
//                          ・ 재고/킵 검증, 스키마 호환
//                       파일: src/MobileShipRequest.jsx
// v7.123 (2026-07-08) — 📱 모바일 출고요청: 즉시완료 로직 제거 (PC 워크플로와 통일)
//                       배경: v7.122 즉시 출고완료 (FIFO 자동 배정)는 PC 워크플로와 불일치.
//                            PC는 "출고요청=제품 선택만 / 시리얼 배정=배송시트지"라는 두 단계.
//                            모바일이 임의로 시리얼까지 배정하면 데이터 정합성/책임 소재 흐려짐.
//                       변경 (MobileShipRequest.jsx):
//                          ① state 제거: fulfillNow
//                          ② submit 로직에서 즉시완료 분기 완전 제거
//                             ・ FIFO in_stock 조회, shipBySerial 호출, 충돌 롤백 등 전부 삭제
//                             ・ InventoryService import 유지 (재고/킵 검증에는 여전히 필요)
//                          ③ UI에서 즉시완료 체크박스 카드 제거
//                          ④ 제출 버튼: 항상 primary 색 + "출고요청 제출" 라벨 (fulfillNow 분기 제거)
//                          ⑤ 성공 메시지: 항상 "출고요청이 접수되었습니다"
//                       유지 (v7.122에서 추가된 정상 기능):
//                          ・ 직접설치 method (3열 그리드)
//                          ・ 직발 (directDispatch + directCompany) — 택배/퀵일 때만 노출
//                          ・ 네이버스토어 (naverStore) — 택배/퀵일 때만 노출
//                          ・ 이 flag들은 note에 저장되어 데스크톱 배송시트지가 즉시 인식
//                       흐름:
//                          모바일: 요청 등록 (제품만) → transaction_log (fulfilled: false)
//                          데스크톱: 배송시트지에서 시리얼 배정 → shipBySerial → note.fulfilled=true 갱신
//                       파일: src/MobileShipRequest.jsx
// v7.122 (2026-07-08) — 📱 모바일 출고요청 확장: 직접설치/직발/네이버 + 즉시 출고완료
//                       배경:
//                          ・ v7.121에서 직접설치/직발/네이버 제외 (로직 복잡성 이유)
//                          ・ 사용자 피드백: 완료처리도 mobile에서 체크박스로 가능하면 좋겠음
//                       변경 (MobileShipRequest.jsx):
//                          ① METHODS 확장 — 직접설치 추가 (5개 총)
//                             ・ 택배 / 퀵서비스 / 방문수령 / 1층배송 / 직접설치
//                          ② 직발/네이버 옵션 (택배·퀵일 때만 표시)
//                             ・ state: directDispatch (bool), directCompany (str), naverStore (bool)
//                             ・ 카드 내 체크박스 + 조건부 업체명 입력
//                             ・ note 스키마: direct/directCompany/naverStore — 데스크톱과 동일
//                          ③ 즉시 출고완료 체크박스 (fulfillNow)
//                             ・ ON: FIFO로 오래된 in_stock 시리얼 자동 배정
//                                    → InventoryService.shipBySerial 각 시리얼 호출 (optimistic lock)
//                                    → note.fulfilled=true, note.completedAt=ISO
//                                    → items[].fulfilledSerials=[배정된 시리얼들]
//                             ・ OFF: 기존 동작 (요청만 등록, PC에서 시리얼 배정)
//                             ・ 시리얼 부족 시 명확한 에러 + 요청 중단
//                             ・ shipBySerial 중 conflict(다른 프로세스 선점) 발생 시 즉시 롤백 alert
//                          ④ 안내 배너 문구 업데이트
//                       변경 없음: 배송시트지 데스크톱은 여전히 새로고침 필요 (자동 감지 없음).
//                                  Realtime 구독은 스코프 밖 (별도 리팩터 필요).
//                       파일: src/MobileShipRequest.jsx
// v7.121 (2026-07-08) — 📱 모바일 2탭 전용 모드 + 제품 선택 초기 로드
//                       배경: 사용자 요청 — "모바일에서는 다른 탭 없이 출고요청/재고목록 2개만".
//                            추가로 출고요청에서 제품 검색 안 하면 아무것도 표시 안 됨 → 초기 카탈로그 로드로 개선.
//                       변경 (App.js):
//                          ① 모바일 사이드바/햄버거/백드롭 완전 제거 — 세그먼트 탭만 사용
//                             ・ <aside>를 {!isMobile && (...)}로 감쌈
//                             ・ 햄버거/백드롭 conditional 삭제 (mobileNavOpen state는 유지)
//                          ② main padding 재조정: isMobile ? 0 : 32
//                             ・ 60px top 여유 제거 (햄버거 없으므로 불필요)
//                             ・ 12px 좌우 padding 제거 (컴포넌트가 edge-to-edge 렌더)
//                          ③ 페이지 강제 리다이렉트 useEffect 신규
//                             ・ isMobile && page !∈ ['ship','inventory'] → setPage('ship')
//                             ・ 데스크톱→모바일 리사이즈 또는 초기 로드 시 즉시 반응
//                          ④ MobileGuardPage 로직은 유지 (v7.117) — 이제 트리거되지 않지만 안전망
//                       변경 (MobileShipRequest.jsx / MobileInventory.jsx):
//                          ① Negative margin 제거 (`margin: '-60px -12px -12px'` → 0)
//                          ② 헤더 좌측 60px 여백 제거 (햄버거 없음)
//                          ③ 컨텐츠 padding 자체 관리 (main padding: 0에 맞춤)
//                       변경 (MobileShipRequest.jsx 로직):
//                          ① 초기 제품 리스트 자동 로드 (컴포넌트 mount 시 30개)
//                             ・ 검색어 없을 때 표시됨 → 사용자가 검색 없이도 제품 볼 수 있음
//                          ② displayProducts 파생 상태 도입
//                             ・ 검색어 있으면 검색 결과 / 없으면 초기 리스트
//                          ③ "제품 조회" 헤딩에 카운트 표시 (예: "제품 조회 · 30개")
//                       참고: 모바일 스캔 페이지는 이제 접근 불가 (사이드바 제거로).
//                            향후 필요 시 배송시트지 등에서 링크 추가 or 세그먼트에 3번째 탭 추가 가능.
// v7.120 (2026-07-08) — 🎨 모바일 UI 재설계 (Claude Design 1a 안 적용)
//                       배경: 사용자가 Claude Design 도구로 1a(카드형 레이아웃, 파란색 primary) 안 채택.
//                            v7.118/v7.119 모바일 뷰는 데스크톱 감각 잔재 → 완전 재작성.
//                       공통 디자인 토큰 (T 객체):
//                          Primary: #2563eb / Success: #16a34a / Warning: #d97706 / Danger: #dc2626
//                          카드: 흰 배경 + border #eef2f6 + radius 14-16px
//                          Pill: radius 999px, primary/gray2 색상
//                          BG: #f1f5f9 (page), #f8fafc (alt)
//                          Grays: #94a3b8 / #64748b / #334155 / #1e293b
//                       공통 구조:
//                          ① Sticky 헤더 (top:0, z-index:10, 흰 배경)
//                             ・ RIVER NETWORK 브랜드 (🌊 아이콘 + 라벨, 좌측 60px 여백은 햄버거 회피)
//                             ・ 세그먼트 탭 (📦 출고요청 / 📋 재고목록) — 클릭 시 페이지 전환
//                                onNavigate prop으로 setPage 호출
//                          ② 컨텐츠 영역 (flex 1)
//                          ③ 각 페이지별 특화 요소
//                       공통 negative margin 트릭:
//                          margin: '-60px -12px -12px'
//                          → main의 padding(60px 12px 12px)을 상쇄하여 edge-to-edge 배경 확보
//                          → 헤더/제출바가 화면 폭 전체 사용 가능
//                       MobileShipRequest v2 (405→약 380줄, 재작성):
//                          ・ 검색 결과 카드: 이니셜 썸네일(파란 primaryLight bg 46px) + 제품명 + 카테고리 + [담기]
//                          ・ 담긴 항목: 인라인 [-][qty][+] 위젯 (28px, primary +/gray -)
//                          ・ 카트 카드: "선택 품목 N종" + 라인별 [-][+][×], 하단 "총 수량 N점" (primary 강조)
//                          ・ 출고 정보 카드: 배송방법 2x2 그리드 pill (택배/퀵/방문/1층)
//                                            주소/딜러/수령인/연락처/요청자/작성자/예상일/비고
//                          ・ Sticky bottom 제출바 (📤 출고요청 제출 · N점, 그림자 + primary bg)
//                       MobileInventory v2 (308→약 320줄, 재작성):
//                          ・ 검색바 + 상태 chip row (전체/재고/예약/판매불가/출고) + 유형 chip row
//                          ・ 카드: 이니셜 썸네일 + 제품명 + 유형뱃지(우측) / 시리얼(mono) / 상태pill + 보관/출처
//                          ・ Tap → 하단 시트 (조회 전용, 닫기 primary 버튼)
//                       App.js 변경:
//                          ・ 두 컴포넌트에 onNavigate={(p)=>setPage(p)} 전달
//                          ・ 화이트리스트/렌더 분기 무변경 (v7.118/v7.119 그대로)
//                       파일 변경: src/MobileShipRequest.jsx (전체 재작성), src/MobileInventory.jsx (전체 재작성), src/App.js (onNavigate prop)
//                       주의: 세그먼트 탭 클릭 시 페이지 이동은 사이드바 자동 닫힘과 별개.
//                            페이지 이동 자체가 새 페이지 진입이므로 이월 상태 등 자동 리셋.
// v7.119 (2026-07-08) — 🚚 제품 출고 요청 모바일 전용 뷰 (외부 작업 대응)
//                       배경: 사용자 요청 — "출고요청/재고목록은 모바일 전용 페이지를 직접, 외부에서도 작업 가능하게".
//                            현장에서 폰으로 출고 요청 등록 → 사무실 복귀 후 PC에서 시리얼 배정/출고완료 처리.
//                       신규 파일: src/MobileShipRequest.jsx
//                          섹션 5개:
//                             1. 배송 방법 (택배/퀵/방문수령/1층배송 — 2×2 그리드)
//                             2. 제품 카트 (검색 + 자동완성 + 다중 카트 + 수량 조정 + 재고/킵 표시)
//                             3. 수령인 (딜러/업체명 [필수] + 최근 5개 chip / 수령인 / 전화 / 주소)
//                             4. 부가정보 (예상 출고일 date picker / 메모)
//                             5. 작성자 (STAFF_LIST select + 요청자 optional)
//                          검증:
//                             ・ 필수: 카트 ≥1, 작성자, 딜러명, 방식별 (주소 or 수령인)
//                             ・ 재고: InventoryService.countInStock() + product_keeps 조회로 가용재고 계산
//                             ・ 초과 시 confirm 알림 (강제 아님 — 사용자 판단)
//                          Insert:
//                             ・ transaction_log (serial=REQ-YYYYMMDD-XXXX, type='outbound', note=JSON)
//                             ・ note.mobileSubmit=true 플래그 저장 (데스크톱에서 필요 시 구분 가능)
//                             ・ autoDefer: 데스크톱과 동일 로직 (미래/오늘 ON, 과거 OFF)
//                          UX:
//                             ・ 등록 성공 시 카트/수령인 리셋, dealerName·author는 유지 (연속 등록)
//                             ・ 딜러명 최근 5개 localStorage 자동 저장
//                             ・ 작성자 localStorage 자동 저장 (다음 접속 시 자동 선택)
//                             ・ 스크롤 상단 자동 이동 (성공 알림 확인)
//                          제외 (PC 권장):
//                             ・ EMS/RMA/DOA — 티켓/시리얼 흐름 복잡
//                             ・ 직접설치 — 발송 방식 6개 중 5번째 제외
//                             ・ 직발 (direct) / 네이버스토어
//                             ・ 시리얼 fulfillment / 착불(COD) / 퀵 가격 세부 / 매출여부 세부
//                       App.js 변경:
//                          ・ lazy import: MobileShipRequest
//                          ・ MOBILE_FRIENDLY_PAGES에 'ship' 추가
//                          ・ ship 렌더 분기: isMobile → MobileShipRequest, 아니면 기존 데스크톱
//                       파일 변경: src/App.js (lazy + 화이트리스트 + 렌더 분기), 신규 MobileShipRequest.jsx
// v7.118 (2026-07-08) — 📦 재고 목록 모바일 전용 뷰 (카드 리스트 + 검색 + 필터)
//                       배경: v7.117 안내 프레임워크 배포 후 사용자 피드백 — "표기 방법이 근본적으로 달라야".
//                            데스크톱 테이블을 좁게 우겨 넣는 방식은 무리 → 모바일 전용 카드 리스트로 재설계.
//                       신규 파일: src/MobileInventory.jsx
//                          ・ Sticky 검색바 (300ms debounce, 아이콘 + 초기화 X)
//                          ・ 상태 chip row (전체/재고/예약/판매불가/출고) — 활성 시 상태색 배경
//                          ・ 유형 chip row (전체/신품/리페킹/조립출고) — 활성 시 유형색 배경
//                          ・ 카드 리스트 (최대 200건 로드, 이상 시 "전체 X건 중 최근 200" 안내)
//                             카드 3-row 구성:
//                                Row 1: 제품명 (bold) + 유형 뱃지 (색상)
//                                Row 2: 시리얼 (monospace) + 상태 뱃지 (Pill, 색상)
//                                Row 3: 📍 보관 · 🇰🇷/🇹🇼 출처 · 입고일
//                          ・ Tap 카드 → 하단 시트 (bottom sheet) 상세 뷰
//                                제품명/시리얼/상태/유형/카테고리/보관/출처/입고일/딜러/판매일/비고
//                                맨 아래 안내: "모바일에서는 조회 전용, 수정은 PC에서"
//                          ・ Supabase 직접 쿼리 (App.js state 의존 X — 독립 컴포넌트)
//                       App.js 변경:
//                          ・ lazy import 추가: const MobileInventory = lazy(() => import('./MobileInventory'))
//                          ・ MOBILE_FRIENDLY_PAGES에 'inventory' 추가
//                          ・ inventory 렌더 분기: isMobile → MobileInventory, 아니면 기존 데스크톱
//                       조회 전용 (v7.117 Q2 default): 카드 편집 UI 없음, 수정 링크 없음.
//                       파일 변경: src/App.js (lazy import + 화이트리스트 + 렌더 분기), 신규 MobileInventory.jsx
// v7.117 (2026-07-08) — 📱 모바일 미대응 페이지 안내 프레임워크 (C 하이브리드)
//                       배경: v7.116 사이드바 drawer로 네비는 해결됐으나 실제 페이지 컨텐츠는
//                            여전히 데스크톱 밀도로 표시 → 배송시트지·재고 같은 정보 많은 화면이
//                            폰에서 사용성 낮음. 사용자 결정: "잠깐 보기 모드 + PC 권유 안내" (옵션 C).
//                       변경:
//                          ① MOBILE_FRIENDLY_PAGES 화이트리스트: 현재 ['scan'] 만 완전 대응 표시
//                                                                (v7.118+에서 'txlog'/'inventory' 확대 예정)
//                          ② 신규 컴포넌트 MobileGuardPage (모듈 스코프):
//                             ・ 페이지 라벨 헤딩 + 💻 아이콘
//                             ・ "PC에서 이용 권장" 안내
//                             ・ 모바일 편의 페이지 리스트 (현재 scan만)
//                             ・ [📱 모바일 스캔으로 이동] primary 버튼
//                             ・ [그래도 계속 보기] outline 우회 버튼
//                          ③ state 추가: mobileBypassPage (bool, 세션 단위)
//                             ・ 페이지 변경 시 자동 리셋 useEffect
//                             ・ 다른 페이지 방문 후 재진입 시 다시 안내 표시
//                          ④ main 컨텐츠를 ternary로 감쌈:
//                             isMobile && !MOBILE_FRIENDLY_PAGES.has(page) && !mobileBypassPage
//                                → <MobileGuardPage/>
//                             else → <> 기존 페이지 렌더들 </>
//                       파일: src/App.js (컴포넌트 + state + main wrap)
//                       참고: 데스크톱 UX 완전 무변화. 모바일 스캔은 여전히 정상 진입.
//                            향후 v7.118+에서 특정 페이지의 모바일 뷰(카드 리스트 등) 개별 추가 가능.
// v7.116 (2026-07-08) — 📱 모바일 대응: 사이드바 overlay drawer + 햄버거 버튼 + 컨텐츠 리플로우
//                       배경: v7.113 모바일 스캔 페이지 추가했으나 상시 노출 사이드바(256px 고정)가
//                            폰 화면 반 이상을 차지 → 실제 사용 시 컨텐츠가 좁아 불편.
//                       변경 (App.js):
//                          ① 신규 state: isMobile (bool, matchMedia '(max-width: 768px)')
//                                        mobileNavOpen (bool, drawer 열림 상태)
//                          ② 사이드바 스타일 조건부:
//                             ・ 데스크톱: 기존 그대로 (flex 흐름 내 256px 고정)
//                             ・ 모바일:  position:fixed, top:0, left:0, height:100vh, zIndex:1000
//                                         transform: mobileNavOpen ? translateX(0) : translateX(-100%)
//                                         transition 0.28s ease-out
//                          ③ 햄버거 버튼 (모바일만): fixed 좌상단, zIndex 999, 흰 원형 + 그림자
//                                                    아이콘 menu ↔ close 토글
//                          ④ 백드롭 (모바일 + drawer 열림): 반투명 검정 + 클릭 시 drawer 닫힘
//                          ⑤ main padding: 데스크톱 32 → 모바일 16 (+ paddingTop 60 햄버거 회피)
//                          ⑥ 네비 클릭 시 drawer 자동 닫힘 (모바일에서만)
//                          ⑦ 세로 오버플로: 모바일 사이드바 내부 스크롤 (전체 nav 접근 가능)
//                       파일: src/App.js
//                       주의: 스크린 리사이즈로 데스크톱→모바일 전환 시 drawer 상태 유지되지 않음
//                            (isMobile 바뀔 때 drawer 자동 닫음). 데스크톱 UX 변경 없음.
// v7.115 (2026-07-08) — 🐛 MobileScan import 수정 — build 실패
//                       원인: v7.113 MobileScan.jsx에서 ProductService/InventoryService를
//                            default import로 작성했으나 실제로는 named export (export const X = {...}).
//                            react-scripts build 시 "does not contain a default export" 에러.
//                       수정: `import ProductService from ...` → `import { ProductService } from ...`
//                            InventoryService도 동일.
//                       파일: src/MobileScan.jsx (2줄 변경)
//                       참고: dev 서버(react-scripts start)는 일부 케이스에서 default import 관대 처리.
//                            production build는 엄격 → 배포 전 build 테스트 권장.
// v7.114 (2026-07-08) — 모바일 스캔 다중 시리얼 UX 강화 (자동 추가 + 박스 개수)
//                       배경: 실제 QNAP 박스 스티커 사진 확인 — 한 박스에 1/2/8/10/20개 시리얼.
//                            v7.113 흐름(스캔→확인→[추가]클릭)은 다수 시리얼 시 조작 부담 큼.
//                            연속 스캔을 위한 자동 추가 + 진행 표시 필요.
//                       변경 (MobileScan.jsx):
//                          ① 신규 state: autoAdd (bool, localStorage 'inv_mobileScanAutoAdd')
//                             ・ ON: 시리얼 스캔 즉시 큐 append (제품명이 카탈로그 매칭될 때만)
//                             ・ 미매칭 상태에서 스캔은 기존처럼 scannedSerial에만 세팅 (실수 방지)
//                          ② 신규 state: expectedCount (int | null)
//                             ・ 프리셋 [1][2][8][10][20][무제한] — 세션 유효 (localStorage X)
//                             ・ 현재 제품 기준 큐 카운트 표시: "박스: 3 / 8" 형태
//                             ・ 목표 도달 시 초록 배너 + [다음 박스] 버튼 (제품/개수 초기화)
//                          ③ 중복 처리 개선: 자동 추가 시 이미 큐에 있는 시리얼은 조용히 스킵 + 피드백
//                          ④ 렌더 refs 도입: autoAddRef/productNameRef/productValidRef/queueRef
//                             ・ rAF 감지 루프가 최신 state 참조 (useCallback([]) 클로저 스테일 방지)
//                          ⑤ UI 배치: 카메라 상단에 컨트롤 밴드
//                             ・ [자동추가 토글]  ·  [박스크기 프리셋]  ·  진행 표시
//                       파일 변경: src/MobileScan.jsx (App.js 무변경)
//                       주의: 자동 추가 모드에서도 시리얼 형식 검증 없음 — EAN/UPC 오스캔 시
//                            잘못된 값도 큐 진입. 사용자는 카메라를 S/N 바코드/QR에 정확히 조준 필요.
//                            큐에서 개별 삭제 가능 (기존 v7.113 유지).
// v7.113 (2026-07-07) — 📱 모바일 스캔 페이지 (PWA, 바코드/QR → 카탈로그 매칭 → Supabase 직접 입고)
//                       배경: 창고에서 폰으로 제품 바코드 스캔 → 제품명 카탈로그에서 선택 → 큐 → 한 번에 입고 처리.
//                            v7.99 자동완성 로직 + v7.40 입고 흐름을 모바일 최적 UX로 결합.
//                       구현:
//                          ① 신규 파일: src/MobileScan.jsx
//                             ・ 카메라 접근: navigator.mediaDevices.getUserMedia({video:{facingMode:'environment'}})
//                             ・ 바코드 인식: 네이티브 window.BarcodeDetector (Chrome/Edge Android, Safari 17+ iOS)
//                                          지원 안 되는 브라우저는 fallback 안내 + 수동 입력 가능
//                             ・ 인식 포맷: code_128/39, ean_13/8, upc_a/e, qr, data_matrix, itf (일반 유통 바코드 커버)
//                             ・ 제품명 자동완성: ProductService.searchByName (v7.99 재사용, canonical 이름 강제)
//                             ・ 로컬 큐: React state + localStorage 'inv_mobileScanQueue' (오프라인 안전)
//                             ・ 동기화: canonical 매칭 검증 → InventoryService.findExistingSerials 중복 확인 →
//                                        InventoryService.bulkInsert + transaction_log (제품별 그룹핑, 1 row)
//                          ② App.js 통합
//                             ・ import (lazy): const MobileScan = lazy(() => import('./MobileScan'))
//                             ・ 사이드바 nav: { id:'scan', icon:'photo_camera', label:'모바일 스캔' } (입고 등록 아래)
//                             ・ 페이지 라벨 map: scan: '모바일 스캔'
//                             ・ 페이지 렌더: {page==='scan' && <Suspense><MobileScan onExit=.../></Suspense>}
//                       입고 스키마 (모바일 → inventory row):
//                          serial_number, product_name(canonical), category(카탈로그값),
//                          stock_type='신품', status='in_stock', source='모바일 스캔',
//                          warehouse='본사', receipt_date=today(),
//                          note=`[모바일 스캔] {스캔일시 로컬 문자열}`
//                       주의: 카메라 접근은 HTTPS(또는 localhost) 필수. HTTP 배포 환경은 브라우저가 자동 차단.
//                            BarcodeDetector 미지원 브라우저는 시리얼 필드 수동 입력 필요.
//                            Naver 웨일 브라우저는 미지원 — Chrome 권장.
// v7.112 (2026-07-07) — 자동 이월 시각 30분 단위 지정 (자정~23:30, 48개 옵션)
//                       배경: v7.111에서 시간 단위(1시간)로만 지정 가능. 사용자 요청으로 30분 단위 확장.
//                       변경:
//                          ① state 리팩터: autoDeferHour (0-23) → autoDeferMinutes (0-1439, step 30)
//                             ・ 총 minutes-since-midnight 정수 (48개 유효값: 0, 30, 60, ..., 1410)
//                             ・ localStorage 키 변경: inv_autoDeferHour → inv_autoDeferMinutes
//                             ・ v7.111 값 자동 마이그레이션 (예: 9 → 540, 이후 old key 삭제)
//                          ② setTimeout 스케줄: setHours(H, 0, 30) → setHours(H, M, 30)
//                             (초 30 마진은 유지)
//                          ③ catch-up 조건: getHours() 비교 → getHours()*60 + getMinutes() 비교
//                             ・ autoDeferMinutes=510(08:30) 이면 08:30 이전 접속엔 sweep 대기, 이후엔 즉시 catch-up
//                          ④ UI: dropdown 옵션 24개 → 48개
//                             ・ HH:MM 형식 + 자정/정오는 별도 라벨
//                             ・ 렌더 시 배열 생성 (성능 부담 없음: 48개 × 간단한 문자열)
//                       파일: src/App.js
// v7.111 (2026-07-07) — 자동 이월 실행 시간 설정 (localStorage, 기본 자정)
//                       배경: v7.62에서 자정 정확 트리거 + 접속 시 catch-up sweep 로직 도입.
//                            하지만 접속 시점이 이월 지정 시점과 무관하게 매번 catch-up 되는 게
//                            사용자에겐 "접속 후 자동이월"으로 인식됨. 실행 시간을 사용자가 지정 가능하게 개선.
//                       변경:
//                          ① 신규 state: autoDeferHour (0-23) — localStorage 'inv_autoDeferHour' 저장
//                             ・ 기본값 0 (자정) → 기존 동작과 완전 동일
//                             ・ 유효 범위 밖 값은 0으로 fallback
//                          ② setTimeout 스케줄: 자정 → autoDeferHour 시각 + 30초 마진
//                             ・ scheduleNextMidnight 함수명은 유지 (내부 로직만 지정 시간 적용)
//                          ③ 접속/visibility catch-up sweep 조건:
//                             ・ Before: 무조건 실행
//                             ・ After:  now.getHours() >= autoDeferHour 인 경우에만 실행
//                                        (지정 시간이 이미 지났으면 catch-up, 아니면 setTimeout 대기)
//                             ・ 예) autoDeferHour=9 이고 오전 7시 접속 → sweep 안 함, 9시에 setTimeout으로 sweep
//                                    autoDeferHour=9 이고 오전 10시 접속 → sweep 함 (catch-up)
//                          ④ UI: txlog 페이지 sub-tabs 우측에 select 드롭다운 (자정~오후 11시)
//                             ・ 값 변경 즉시 localStorage 저장 + useEffect 재실행으로 스케줄 재계산
//                       주의: 오늘 지정 시간이 이미 지났으나 PC가 꺼져있어 sweep 미실행 케이스는 접속 시 catch-up으로 해결.
//                            반대로 지정 시간 전에 로그인하면 sweep 실행 안 됨 — 요청한 UX와 일치.
//                            서버 크론이 없는 SPA 특성상 앱이 켜져있어야 스케줄이 동작함 (기존과 동일 제약).
//                       파일: src/App.js
// v7.110 (2026-07-07) — 배송시트지 검색 확장: 직발/네이버 키워드 → JSON flag 매칭
//                       배경: 직발 모드는 note JSON에 "direct":true / "directCompany":"..." 로 저장,
//                            네이버스토어는 "naverStore":true 로 저장.
//                            사용자가 검색창에 "직발"이나 "네이버"를 입력해도 note 텍스트에 그 한글이 없어서
//                            (JSON은 영문 키만 사용) 실제 직발/네이버 요청들이 결과에 안 잡힘.
//                       변경: loadTxLog의 OR 검색 절에 조건부 확장 추가 (라인 ~3343)
//                          ・ 검색어에 "직발" 포함 시 → note.ilike.%directCompany% 추가
//                            ("directCompany"는 direct=true일 때만 저장되는 필드라 오탐 최소)
//                          ・ 검색어에 "네이버" 포함 시 → note.ilike.%naverStore% 추가
//                       주의: 복합 검색(예: "직발 컴퓨존")은 여전히 OR라서 모든 직발 결과 포함됨 (over-match).
//                            단순 키워드 검색 UX 우선 — 복합 필터는 향후 별도 요청 시 처리.
//                       파일: src/App.js
// v7.109 (2026-07-07) — 네이버스토어 뱃지 N 자 미세 남동 이동
//                       배경: v7.108 flex-center 후에도 사용자 눈에는 살짝 위·좌로 보임 → 남동 미세 조정 요청.
//                       변경: N 텍스트를 inner span으로 래핑 + transform:translate(0.5px, 0.5px).
//                            흰 사각의 크기·flex 정렬은 유지, 글자만 subpixel 이동.
//                            (파딩/box-sizing 조정보다 안전 — 뱃지 총 크기에 영향 없음)
//                       파일: src/App.js (라인 ~10287)
// v7.108 (2026-07-07) — 네이버스토어 뱃지 N 자 중앙 정렬 미세 조정
//                       배경: v7.107 뱃지의 흰 사각 안 "N"이 좌측 아래로 살짝 치우쳐 보임.
//                            원인: fontStyle:italic + letterSpacing:-0.05em 조합이 시각 중심을 이동.
//                       변경: 두 속성 제거 → flex 중앙 정렬만으로 정확한 정중앙.
//                            (Naver 로고 원본도 upright 볼드 N — 이탤릭은 애초에 스타일 오버였음)
//                       파일: src/App.js (라인 ~10287)
// v7.107 (2026-07-07) — 배송지 셀에 네이버스토어 뱃지 (직발 뱃지와 동일 위치·스타일)
//                       배경: 사용자 요청 — "직발 표기하는것처럼 네이버스토어도 표기".
//                            v7.106의 날짜 셀 워터마크는 남기고, 직발과 동일한 flex row에 뱃지 추가.
//                       변경:
//                          ① 배송지 셀의 직발/엑셀 flex row (라인 ~10239) 안에 네이버스토어 뱃지 삽입
//                             ・ 위치: 직발/[+직발추가] 이후, 엑셀 이전 (직발 → 네이버 → 엑셀 순)
//                          ② 뱃지 스타일 — 직발 뱃지 스펙 미러링:
//                             ・ 배경 #03C75A (처리완료 시 회색 #e5e7eb)
//                             ・ 흰색 텍스트 "네이버스토어"
//                             ・ 좌측에 흰 사각 안 초록 이탤릭 "N" (Naver 브랜드 톤)
//                             ・ padding/border-radius/fontSize/fontWeight 모두 직발과 동일
//                          ③ shipInfo.naverStore 플래그로만 렌더 (v7.103 저장 스키마 재사용)
//                       주의: v7.106 날짜 셀 워터마크는 유지. 필요 시 별도 요청으로 제거 가능.
//                            뱃지 클릭/토글 UI는 이번 버전에 없음 — 요청 폼에서만 설정 가능.
//                       파일: src/App.js
// v7.106 (2026-07-07) — 네이버스토어 워터마크 위치 이동: 발송종류 셀 → 날짜 셀
//                       배경: 발송종류 셀은 편집 UI(송장/가격 input) 겹침 우려 + 우측 시각 밀도 높음.
//                            사용자 요청으로 좌측 날짜 셀 배경에 배치 (전체 행 스캔 시 첫 열에서 즉시 인지).
//                       변경:
//                          ① 발송종류 셀에서 NaverLogo(fill) 제거 + wrapper의 position:relative 원복
//                             (v7.104에서 추가한 relative — 워터마크 전용이었음)
//                          ② 날짜 셀 wrapper에 position:relative + overflow:hidden 추가
//                          ③ shipInfo?.naverStore이면 날짜 셀에 NaverLogo fill 렌더
//                             ・ 알파값/크기/pointerEvents 등 fill 모드 스펙은 v7.105 그대로
//                          ④ 텍스트(날짜/시간)는 DOM 순서상 워터마크 다음에 위치 → 자연스럽게 상위 레이어
//                       스키마/state 변경 없음. shipInfo.naverStore 플래그 그대로 사용.
//                       파일: src/App.js
// v7.105 (2026-07-07) — 네이버스토어 로고 셀 전체 채움 (fill 모드 신규)
//                       배경: v7.104의 38px 우측 상단 워터마크도 여전히 작다는 피드백.
//                            "발송종류 뒤쪽 다 덮을 수 있도록" — 셀 배경 전체를 채우는 큰 워터마크 요구.
//                       변경:
//                          ① NaverLogo 컴포넌트에 fill prop 추가
//                             ・ fill=true → position:absolute + inset:4 + SVG preserveAspectRatio로 컨테이너 100% 채움
//                             ・ maxWidth 130 / maxHeight 110 (극단적 셀 크기에서 과대화 방지)
//                             ・ pointerEvents:none 유지 (클릭/편집 방해 X)
//                          ② 발송종류 셀 사용을 fill 모드로 전환:
//                             ・ Before: <NaverLogo size={38} opacity={0.22} style={{position:absolute,top:6,right:6}}/>
//                             ・ After:  <NaverLogo fill opacity={0.15}/>
//                          ③ 알파 낮춤 0.22 → 0.15 (처리완료 행: 0.12 → 0.09)
//                             크기가 커진 만큼 알파를 낮춰 텍스트 가독성 유지
//                       파일: src/App.js
//                       주의: 셀 wrapper의 position:relative + overflow:hidden 유지 (v7.104에서 이미 적용).
//                            SVG는 부모 flex-center로 자동 정렬되고 aspect ratio 1:1 보존.
// v7.104 (2026-07-07) — 네이버스토어 로고 확대: 발송종류 셀 우측 상단 워터마크 스타일
//                       배경: v7.103에서 발송종류 라벨 옆에 13px 인라인 네이버 로고를 추가했으나,
//                            사용자가 "발송종류 헤더 크기만큼 크게, 알파값 넣어서" 재요청.
//                            → 인라인 소형 표기를 셀 우측 상단에 큰 반투명 워터마크로 재배치.
//                       변경:
//                          ① NaverLogo 컴포넌트에 style prop 추가 → 외부에서 위치/사이즈 오버라이드 가능
//                          ② 발송종류 셀 wrapper에 position:relative 추가
//                          ③ shipInfo.naverStore 이면 wrapper 안쪽 top-right에 절대 위치 NaverLogo
//                             ・ size 38 (헤더 셀 컨텐츠 높이(약 33-40px)와 유사)
//                             ・ opacity 0.22 (일반) / 0.12 (처리완료 행) — 워터마크 톤
//                             ・ pointerEvents:none — 인라인 편집/클릭 방해 안 함
//                          ④ 기존 5개 인라인 소형 로고(택배/퀵/방문수령/1층배송/RMA) 제거
//                             → 셀 단위 워터마크 하나로 통합
//                       파일: src/App.js
//                       주의: overflow:hidden 유지 — 로고가 셀 밖으로 새지 않음. 겹침 시 라벨 우선.
// v7.103 (2026-07-07) — 네이버스토어 체크박스 + 발송종류 셀 네이버 로고 표기
//                       배경: 네이버스토어에서 접수된 주문은 처리 흐름 자체는 일반 출고와 동일하지만
//                            배송시트지에서 시각적으로 즉시 구별 필요 (담당자별 통계/응대 톤 다름).
//                            새 플래그를 만들어 발송종류 옆에 네이버 로고(알파 적용)를 붙임.
//                       변경 5가지:
//                          ① 신규 컴포넌트: NaverLogo (라인 ~1695)
//                             ・ 인라인 SVG (rect + 흰색 N 도형) — 외부 이미지 의존 없음
//                             ・ #03C75A 네이버 그린 배경, 반투명(opacity 0.75 default) — 사용자 요청 "알파값"
//                             ・ size/opacity/title 커스터마이즈 prop
//                          ② 신규 state: shipNaverStore (bool) — 요청 폼 (라인 ~2062)
//                          ③ 요청 폼 UI: 직발 모드 옆에 네이버스토어 체크박스 (라인 ~8714)
//                             ・ 직발 모드와 동일 조건 (courier/quick만 노출) — 사용자 요청 "직발모드 옆에"
//                             ・ 두 박스를 flex row로 나란히 배치
//                             ・ 초록 톤 (#03C75A) 강조
//                          ④ 요청 저장: shipInfo.naverStore=true 플래그 추가 (라인 ~3056)
//                             ・ 발송방식 스위치 시 자동 해제 (직발과 동일 패턴)
//                             ・ resetShipForm에서도 초기화
//                          ⑤ 배송시트지 발송종류 셀: shipInfo.naverStore 이면 NaverLogo 표시
//                             ・ 택배/퀵/방문수령/1층배송/RMA/DOA 모든 라벨 옆에 (일관성)
//                             ・ 인라인 편집으로 발송종류 변경돼도 플래그 유지 → 여전히 표시
//                       스키마 변경 없음 (transaction_log.note JSON에 필드 추가). 소급 표시 X.
//                       파일: src/App.js
// v7.102 (2026-07-07) — 재고 목록 그룹 헤더 요약 정리 (라벨/breakdown/유형 필터)
//                       배경: v7.98 도넛 legend는 상태별 %와 count만 표시 → 출고된 시리얼이 어떤 유형이었는지
//                            추적 불가. 그리고 유형 뱃지는 shipped 포함 전체 카운트라 "현재 판매가능 재고"의
//                            유형 분포를 즉시 파악하기 어려웠음. 사용자 요청으로 라벨/breakdown 재구성.
//                       변경 3가지:
//                          ① 도넛 legend 라벨 변경:
//                             ・ '보유'    → '판매가능'
//                             ・ '출고'    → '출고됨'
//                             ・ 예약/판매불가는 그대로
//                          ② Legend 뒤 (XX%) 퍼센트 표기 제거 →
//                             '출고됨' 라벨 옆에만 breakdown 표시: (신품 X개, 리페킹 Y개, 조립출고 Z개)
//                             출고된 시리얼의 stock_type 분포를 즉시 파악 가능.
//                          ③ 유형 뱃지 카운트에서 shipped 상태 제외:
//                             ・ Before: 신품/리페킹/조립출고 = 전체 시리얼 (in_stock + shipped + 기타)
//                             ・ After:  신품/리페킹/조립출고 = 판매가능 관점 (shipped 제외, 나머지 상태 포함)
//                             ・ reserved / unsellable은 여전히 포함 — 사용자가 shipped만 콕 집어 제외 요청
//                       구현:
//                          ・ 그룹 aggregation에 shippedNewProduct/shippedRepacked/shippedAssembled 카운터 추가 (라인 ~7406)
//                          ・ item.status === 'shipped' 분기 (라인 ~7462)
//                          ・ Legend 렌더링에서 s.label==='출고됨'일 때만 breakdown span 삽입 (라인 ~7578)
//                       파일: src/App.js
// v7.101 (2026-07-07) — DOA 반품 시리얼 note에 "DOA 전시리얼:" 라벨로 원 시리얼 참조 복원
//                       배경: v7.100에서 사용자 첫 지시("기존 시리얼은 등록하지말고")를 넓게 해석해 외부
//                            DOA 케이스의 note 필드에서 원 시리얼 참조까지 제거했으나, 실제 의도는
//                            "별도 inventory row는 생성하지 말고 note에는 추적용으로 남기기"였음.
//                            사용자가 "DOA전시리얼: XXXXXXXXXX 이런식으로 비고에 추가" 요청 → 원상복구 + 라벨 변경.
//                       변경:
//                          ① 반품 시리얼 inventory.note를 케이스 무관 단일 템플릿으로 통합:
//                             `[DOA 반품 신품] DOA 전시리얼: ${sn||'-'}${티켓? ' / 티켓: '+티켓 : ''}`
//                             ・ 외부 DOA + DB DOA 모두 동일 형식 (재고 목록 검색 UX 일관성)
//                             ・ 라벨 "원 시리얼" → "DOA 전시리얼" (사용자 요청 표기)
//                          ② Confirm 메시지 / Success 알림의 v7.100 개선(외부 DOA 별도 안내)은 그대로 유지
//                             — inventory row 생성 여부에 관한 설명이라 note 필드 정책과 독립.
//                       주의: DB 스키마 변경 없음. 기존에 등록된 재고 row의 note는 소급 변경 안 됨
//                            (v7.101 시점 이후 새로 등록되는 DOA 반품 시리얼만 새 형식 적용).
//                            과거 row를 일괄 정정하려면 별도 SQL migration 필요 (아직 요청 없음 → skip).
// v7.100 (2026-07-07) — DOA 외부 시리얼 수리완료 처리 정리
//                       배경: DOA + 반품 시리얼 케이스에서 원 시리얼이 외부(DB 미등록)인 경우도 있음.
//                            현재 로직상 실제 처리는 문제 없음 — 원 시리얼은 안 건드리고 반품 시리얼만 신품 등록.
//                            하지만 UI 표현이 오해 유발:
//                              ・ Confirm 메시지: "원 시리얼 XXX: 판매불가 상태 유지" — 외부 시리얼은 DB에 없어서
//                                판매불가 상태가 존재하지 않음. 사용자가 잘못된 상태 전제를 상상하게 됨.
//                              ・ 반품 시리얼 inventory row의 note 필드: "[DOA 반품 신품] 원 시리얼: XXX" —
//                                외부 시리얼은 재고 목록에서 조회 불가능한 유령 참조 (검색해도 안 나옴).
//                              ・ Success 알림에도 원 시리얼 처리 결과 언급 없음.
//                       분기 추가 (isExternalDoa = isDoa && !fromDB && hasReturn):
//                          ① Confirm 메시지:
//                             "외부 EMS(DOA) 수리완료 처리:
//                                • 원 시리얼 XXX: 외부 시리얼 (재고 미등록) — 처리 없음
//                                • 반품 시리얼 YYY: 신품으로 재고 등록 (source=DOA)
//                                • 본 EMS 기록: [수리완료] 마킹"
//                          ② 반품 시리얼 inventory.note:
//                             "[DOA 반품 신품] 외부 EMS 건${티켓? ' / 티켓: '+티켓 : ''}"
//                             → 원 시리얼 참조 제거. 반품 시리얼을 clean fresh stock으로 다룸.
//                          ③ Success 알림:
//                             "외부 EMS(DOA) 수리완료 처리되었습니다.
//                              반품 시리얼 YYY 이(가) 신품으로 재고에 등록되었습니다.
//                              (원 시리얼은 외부 건이라 재고 변경 없음)"
//                       DB DOA (fromDB=true) 케이스는 기존 로직 그대로 유지 — 원 시리얼 판매불가 상태 유지 + note 참조 유지.
//                       파일: src/App.js (doRepairComplete 라인 ~5521)
//                       주의: DB 스키마/RLS 변경 없음. 신규 재고 row도 기존 스키마 그대로 (note 문자열만 다름).
// v7.99 (2026-07-07) — RMA/DOA 제품명 자동완성 (제품 표준화 카탈로그 기반)
//                       배경: v7.78에서 DOA 반품 시리얼 신품 등록 시 inventory.product_name FK 검증 alert 추가.
//                            하지만 입력 단계에서 표준명 유도가 없어 오타/변형 이름 그대로 저장 → 나중에
//                            수리완료 시점에 alert 뜨고 처리 실패. 사용자 요청으로 사전 자동완성 UI 추가.
//                       변경 4가지:
//                          ① RMA/DOA 등록 폼의 제품명 필드에 실시간 자동완성 드롭다운
//                             ・ ProductService.searchByName(q, 10) 재사용 (기존 카탈로그/입고 검색과 동일 API)
//                             ・ 클릭 시 canonical 표준명으로 정정 입력
//                             ・ 별칭/EU 접미사 로직은 제외 — 표준명 강제 (v7.78 실패 케이스 사전 차단)
//                          ② 검증 뱃지 (입력값이 카탈로그와 정확 매칭되는지, case-insensitive):
//                             ・ 매칭 시:  ✓ 초록 "카탈로그 등록됨"
//                             ・ 미매칭 + DOA: ⚠ 주황 "카탈로그 미등록 — 수리완료 시 반품 시리얼 신품 등록 실패"
//                             ・ 미매칭 + RMA: 회색 정보 톤 "카탈로그 미등록 (RMA는 처리 가능)"
//                          ③ 시리얼 onBlur의 DB 매칭으로 제품명 자동 채워질 때 catalogHit=true 세팅
//                             (inventory.product_name → products.product_name FK 특성상 카탈로그 존재 보장)
//                             + 드롭다운 잔여 결과 클리어
//                          ④ resetShipForm에 shipRmaProductResults / shipRmaProductCatalogHit 초기화 추가
//                       파일: src/App.js
//                       주의: 자동완성 드롭다운은 onBlur 200ms 딜레이 후 닫음 (onMouseDown 선택 허용).
//                            v7.95 회귀 교훈 대비 — 새 state는 모두 렌더 안전값(null/[])로 초기화.
// v7.98 (2026-07-07) — 재고 목록 그룹 헤더 UI 개편 (도넛 차트 + 보관 뱃지 제거)
//                       배경: 사용자 요청 — 좌우 가로 막대 그래프를 원형으로, 보관 표기 제거
//                       
//                       변경 3가지:
//                          ① 가로 막대 그래프 → 도넛 차트 (SVG 90×90, 외부 라이브러리 없음)
//                             ・ 조각: 보유(초록) / 예약(파랑) / 판매불가(주황) / 출고(빨강)
//                             ・ 값 0인 조각은 생략
//                             ・ 도넛 중앙에 TOTAL 숫자 (기존 우측 별도 표기 제거)
//                             ・ 옆에 legend: 색점 · 이름 · 카운트 · (%)
//                             ・ 킵(수량 예약, g.total과 무관)은 legend에 별도 뱃지로 표시
//                             ・ SVG stroke-dasharray 트릭으로 조각 그리기 (성능 부담 없음)
//                          ② 보관(warehouse) 뱃지 블록 완전 제거 (본사/파주/기타)
//                             → 사용자 요청 — 관리상 warehouse 정보는 그룹 헤더에 불필요
//                             (개별 시리얼의 창고 편집은 확장 패널에서 유지)
//                          ③ 분포 뱃지 컨테이너: grid 3열 → flex wrap
//                             → 보관 제거 후 유연 배치 (출처 + 유형만 표시)
//                       
//                       정리:
//                          ・ stockPct 변수 제거 (가로 막대 전용이었음)
//                          ・ 우측 상단 TOTAL 별도 배지 제거 (도넛 안 중앙에 통합)
// v7.97 (2026-07-07) — 재고 목록 입고일 셀 더블클릭 인라인 편집 (달력)
//                       배경: v7.96에서 입고일 미확인 옵션을 추가했으나, 나중에 수정하려면
//                            시리얼 확장 패널을 열어야 해서 UX 번거로움 → 셀에서 바로 수정 가능하게
//                       
//                       현재 상태 확인:
//                          ・ 확장 패널 (라인 ~7610): 이미 date input으로 수정 가능 (기존)
//                          ・ 셀 자체는 읽기 전용 (라인 7457, 이번에 편집 가능하게)
//                       
//                       변경 (재고 목록 페이지):
//                          ・ state 추가: editingReceiptSerial, savingReceiptSerial
//                          ・ saveReceiptDate wrapper 함수 (saveInventoryField 재사용, 저장 표시)
//                          ・ 셀 렌더링:
//                              - 편집 모드 아님: 클릭 방지 + onDoubleClick → 편집 진입
//                                                호버 시 배경 강조 + "더블클릭으로 입고일 수정" tooltip
//                                                기존 표시 '-' → '미확인' (v7.96과 일관성)
//                              - 편집 모드: <input type=date> + autoFocus
//                                            Enter/blur = 저장, Escape = 취소
//                                            저장 중이면 disabled + 스피너
//                          ・ 브라우저 native date picker (달력 UI) 자동 활용 — 별도 라이브러리 없음
//                       
//                       확장 패널은 그대로 유지 — 다른 필드(제품 상태, 창고 등)와 함께 편집할 때 사용
// v7.96 (2026-07-07) — HDD/SSD 입고: 입고일 미확인 옵션 추가
//                       배경: 사용자 요청 — 유입 경로에서 입고일이 확인되지 않는 제품이 있어서,
//                            날짜 없이도 입고 처리 가능해야 함 (재고 목록에서 나중에 수정)
//                       
//                       변경 (모두 HDD/SSD 입고 페이지에 한정):
//                          ・ hddInbound state에 inDateUnknown 필드 추가 (boolean, 기본 false)
//                          ・ UI: 입고일 라벨 우측에 '미확인' 체크박스
//                                  - 체크 시 date input 비활성(회색+disabled) + 안내 문구
//                                  - 안내: "날짜 없이 등록됩니다 (재고 목록에서 나중에 수정 가능)"
//                          ・ registerHddInbound:
//                                  - 검증: !inDate && !inDateUnknown 이면 에러
//                                  - confirm 다이얼로그에 '⚠ 미확인' 표시
//                                  - 저장 시 receipt_date: inDateUnknown ? null : inDate
//                          ・ canRegister: (inDate || inDateUnknown) 로 확장
//                       
//                       DB: inventory.receipt_date 는 이미 NULL 허용 (기존 편집 UI가 null 저장 가능)
//                       재고 목록의 개별 편집 패널(라인 7610)에서 나중에 날짜 채워 넣기 가능
//                       
//                       일반 입고 페이지(page='inbound')는 스코프 밖 — 필요 시 후속 확장
// v7.95 (2026-07-06) — 🐛 출고 요청 저장 후 배송시트지 자동 이동 안 되던 회귀 버그 수정
//                       배경: v7.89 트래킹 삽입 시 미정의 변수 `isPickup` 참조 → ReferenceError
//                             DB insert는 성공했지만 이후 setTimeout(setPage) 미실행 → 화면 이동 X,
//                             setLoading(false) 미실행 → "처리 중..." 버튼이 그대로 남음
//                       
//                       근본 원인 (submitShipRequest, 라인 2913):
//                          AnalyticsService.trackAction('출고요청_등록', ..., {
//                            itemCount, totalQty, isRma, isPickup   ← 이 스코프에 없는 변수
//                          });
//                          함수 안에는 shipPickup만 있고 isPickup은 정의된 적 없음.
//                          → 인자 객체 평가 시 sync ReferenceError → 이후 흐름 완전 중단
//                       
//                       수정: `isPickup` → `isPickup: !!shipPickup` (실제 state 참조)
//                       
//                       참고: sync 인자 평가 예외는 함수 진입 이전에 발생하므로
//                             AnalyticsService.track 내부의 try/catch로 잡을 수 없음.
//                             호출부에서 유효한 값만 넘겨야 함 (AnalyticsService 주석에 명시).
//                       
//                       다른 트래킹 지점 재검토 완료 (모두 유효):
//                          ・ 배송_커밋: activeRequest?.author / successPending ✓
//                          ・ HDD_SSD_입고: hddInbound.source / productName / toInsert / failed ✓
//                          ・ 시리얼_교체: log.processed_by / oldSerial / newSerial / productName ✓
//                          ・ 입고_등록: validBatches / rows / skipCount ✓
// v7.94 (2026-07-06) — Mirror + AimTrainer: 게임 중 ESC 2번 클릭으로 종료
//                       배경: 사용자 요청 — UI 안내(ESC × 2)와 실제 동작 불일치
//                            게임 중 첫 ESC → pointer lock 해제만 되고,
//                            두 번째 ESC는 무시되어 왔음 (playing phase에서 handleClose 미호출)
//                       
//                       수정 (양쪽 파일 동일 패턴):
//                          ・ ESC useEffect의 onKey 로직 확장
//                          ・ 게임 중이면서 pointer lock 이미 해제된 상태 감지 시 handleClose 호출
//                          ・ 첫 ESC keydown: 렌더 useEffect의 onKeyDown이 exitPointerLock() 호출
//                          ・ 두 번째 ESC keydown: pointerLockElement=null 이므로 handleClose 실행
//                          ・ 인트로/결과 화면 ESC는 기존과 동일 (즉시 종료)
//                       
//                       참고 (브라우저 동작): 첫 ESC 시점의 keydown 이벤트에서는
//                            document.pointerLockElement가 아직 유효 → onKey는 아무것도 안 함.
//                            브라우저가 pointer lock 해제 후 두 번째 ESC keydown에서 null.
// v7.93 (2026-07-06) — AimTrainer 2가지 모드: 스프린트(30초) + 레벨 챌린지(5단계)
//                       배경: 사용자 요청 — 30초 챌린지 모드 + 레벨 클리어 모드
//                       
//                       모드 1 (Sprint):
//                          ・ 30초 동안 최대한 많이 격파 (기존 60초 → 30초)
//                          ・ 최고 기록 저장 (aim_best_score) — 기존 그대로
//                       
//                       모드 2 (Level Challenge):
//                          ・ 5개 레벨, 각 레벨별 시간 제한 + 목표 점수
//                          ・ 레벨업하며 시간 짧아지고 목표 오르고 타겟 작아짐
//                          ・ 밸런스: L1 30s/50점/size 0.50 → L5 25s/200점/size 0.30
//                          ・ 목표 달성 시 다음 레벨 자동 진행 (NEXT LEVEL 화면)
//                          ・ 실패 시 재도전 (RETRY)
//                          ・ 5단계 모두 클리어 시 🏆 ALL CLEARED (골드 그라디언트)
//                          ・ 최고 클리어 레벨 저장 (aim_level_cleared)
//                          ・ 재진입 시 클리어 레벨 + 1로 자동 이월
//                       
//                       변경 (AimTrainer.jsx):
//                          ・ GAME_DURATION → SPRINT_DURATION (30초)
//                          ・ LEVELS 배열 신규 (5개 config)
//                          ・ state: gameMode, currentLevel, clearedLevel 추가
//                          ・ phase 확장: ended | levelPassed | levelFailed | allCleared
//                          ・ startSprint / startLevel(levelNum) 함수 분리
//                          ・ backToIntro (결과 화면에서 모드 선택으로)
//                          ・ Three.js useEffect: mode에 따라 duration/count/size 변경
//                          ・ HUD: 레벨 모드일 때 "SCORE / 목표점수" + "LV n" 뱃지
//                          ・ 하단 안내: 레벨 모드는 목표점수 표시
//                          ・ IntroScreen: 스프린트/레벨 카드 2개 (모드 선택 + 진행상황)
//                          ・ LevelResultScreen 신규 (성공/실패/전체 클리어 통합)
//                          ・ EndedScreen에 "모드 선택" 버튼 추가
// v7.92 (2026-07-06) — AimTrainer 타겟을 원형(구)으로 변경
//                       배경: 사용자 요청 — 오버워치 봇 스타일(박스형 몸통+머리) → 원형 타겟
//                            Aim Lab / Kovaak's 등 aim trainer 관례에 가까움
//                       
//                       변경 (AimTrainer.jsx):
//                          ・ 타겟 geometry: BoxGeometry(body) + BoxGeometry(head) → SphereGeometry(0.45)
//                          ・ 스코어링 단순화: 헤드샷(+30) 개념 제거 → 모든 명중 +10 (HIT_SCORE)
//                          ・ mesh 그룹 → 단일 mesh (raycasting도 recursive=false로 최적화)
//                          ・ 렌더 루프에서 타겟-플레이어 회전 로직 제거 (구는 방향성 없음)
//                          ・ headshots state / setHeadshots / bodyMat/headMat 참조 제거
//                          ・ HUD: 헤드 카운트 표시 제거 (정확도 / 명중 카운트만 유지)
//                          ・ EndedScreen: 스코어 카드 3개 → 2개 (정확도 + 명중/발사)
//                          ・ 인트로 안내 문구 갱신 ('원형 타겟 격파', '노란 머리' 언급 제거)
//                          ・ 하단 조작 안내: '몸통 +10 · 헤드 +30' → '타겟 격파 +10'
//                          ・ COLORS.targetHead 삭제
//                       
//                       localStorage 최고 기록(aim_best_score)은 유지 (헤드샷 기반 이전 기록이
//                       남아있어도 신규 기록과 비교 가능 — 사용자가 직접 리셋 원하면 별도 처리)
// v7.91 (2026-07-06) — 🎯 이스터에그: 에임 연습장 (AimTrainer) — 오버워치 연습장 스타일
//                       배경: 사용자 요청 — 오버워치 연습장처럼 에임 연습 가능한 이스터에그
//                       
//                       트리거: 재고 목록 검색창에 'aim' 입력
//                              (기존 rest/mine/mirror와 동일 패턴)
//                       
//                       구조:
//                          ・ 신규 파일 src/AimTrainer.jsx (~600줄, Three.js 사용)
//                          ・ Mirror와 동일한 lazy load (초기 번들 영향 없음)
//                          ・ Three.js Raycasting으로 정확한 hit 판정
//                       
//                       게임 스펙:
//                          ・ 60초 챌린지 (Time Attack)
//                          ・ 타겟 8개 동시 존재 (몸통 빨강 큐브 + 머리 노랑 큐브)
//                          ・ 스코어: 몸통 +10 / 헤드샷 +30
//                          ・ 격파 시 500ms 후 랜덤 위치 리스폰
//                          ・ 최고 기록 localStorage 저장 (aim_best_score)
//                          ・ 플레이어 쪽 자동 회전 (타겟이 항상 정면 응시)
//                          ・ 은은한 부양 애니메이션 (지루하지 않게)
//                       
//                       UI:
//                          ・ 좌상단: 스코어(빨강 34px) / 정확도 / 명중 카운트 / 헤드샷
//                          ・ 상단 중앙: 타이머 (10초 남으면 빨강 pulse)
//                          ・ 중앙: 십자 조준선 (흰색 + 그림자)
//                          ・ 우상단: 나가기 버튼
//                          ・ 하단: 조작 안내
//                          ・ 인트로 → 게임 → 종료 화면 (스코어 카드 3개 + NEW BEST 뱃지)
//                       
//                       시각: 밝은 사격장 (하늘색 배경, 흰 벽, 회색 바닥 + 격자)
//                              Mirror의 어두운 신비함과 대조되는 밝은 톤
// v7.90 (2026-07-06) — Mirror 미니맵 추가 (현재 층 상단뷰 + 플레이어)
//                       배경: 사용자 요청 — 3D 미로에서 방향 감각/현재 위치 파악 어려움
//                       
//                       구현 방식:
//                          ・ 별도 <canvas> 오버레이 (React state 안 거치고 렌더 루프에서 직접 2D drawing)
//                          ・ Mirror.jsx의 렌더 루프(renderer.render 직후) 안에서 miniMapRef 참조
//                          ・ 크기 180x180px, 우상단 배치
//                       
//                       표시 요소:
//                          ・ 현재 층 미로 상단뷰 (은은한 격자 + 반투명 파란 벽)
//                          ・ 계단: 위(녹색)/아래(주황) 작은 점 — 현재 층 것만
//                          ・ 목표: 노란 다이아몬드 + 발광 원 (현재 층에 있을 때만, clock 기반 애니메이션)
//                          ・ 플레이어: 시선 삼각형(파란) + 중심점(흰)
//                          ・ 우상단 층 표시 "1F / 3F"
//                       
//                       레이아웃 조정:
//                          ・ 나가기 버튼 위치 top:20 → top:216 (미니맵 아래로)
//                          ・ HUD/조작 안내는 그대로
//                       
//                       정리:
//                          ・ v7.85에 남겨둔 MiniMap stub 컴포넌트 + buildLevelSnapshot 함수 제거
// v7.89 (2026-07-04) — 사용 현황(트래픽 체커) — page_analytics 신규 테이블 + 대시보드
//                       배경: 사용자 요청 — 웹앱 사용량 정량화 필요
//                       
//                       배포 전 필수: v7.89-schema-page-analytics.sql 실행 (Supabase)
//                            → page_analytics 테이블 + 인덱스 3개 + RLS 정책 생성
//                       
//                       추적 범위 (2가지):
//                          ・ 페이지 뷰: page/inventoryTab 변경 시 자동 (5초 dedupe)
//                          ・ 액션 4종: 배송_커밋, 출고요청_등록, 입고_등록, HDD_SSD_입고, 시리얼_교체
//                       
//                       스키마:
//                          page_analytics (id, event_type, event_name, session_id, staff_name, meta jsonb, created_at)
//                          - event_type: 'page_view' | 'action'
//                          - session_id: localStorage 기반 브라우저 익명 세션 (지속)
//                          - staff_name: 액션의 담당자 (페이지 뷰는 null)
//                          - meta: 확장 필드 (jsonb) — 서브 카운트/컨텍스트
//                       
//                       신규 파일:
//                          ・ src/services/AnalyticsService.js — track/trackPageView/trackAction/loadEvents/summarize
//                            * Fire-and-forget: 트래킹 실패는 앱 동작에 영향 없음 (silent catch)
//                       
//                       UI 배치: 재고 목록 페이지의 4번째 서브탭 [사용 현황]
//                          이유: 사이드바 nested 그룹화 대기 중이라 새 최상위 탭 추가는 그때 함께
//                                재고 대시보드와 성격이 달라 별도 서브탭이 명확
//                       
//                       대시보드 구성:
//                          ① 기간 선택 (오늘 / 7일 / 30일)
//                          ② 요약 카드 4 (페이지 뷰, 액션 실행, 활성 세션, 총 이벤트)
//                          ③ 시간대별 이벤트 분포 (24시간 막대)
//                          ④ 랭킹 3 (페이지별 / 액션별 / 담당자별)
//                          ⑤ 최근 이벤트 20건 (스크롤)
//                       
//                       색상: 인디고 #5c6bc0 (통계 관용)
//                       아이콘: insights
// v7.88 (2026-07-03) — Mirror WASD 앞뒤 이동 방향 수정
//                       버그: W(앞으로) 누르면 뒤로, S(뒤로) 누르면 앞으로 이동
//                       원인: Three.js 카메라 기본 forward vector는 (0,0,-1) 인데
//                            Mirror.jsx 이동 계산은 (sin(yaw), 0, cos(yaw)) = +Z 방향으로 되어 있어
//                            카메라 응시 방향과 이동 방향이 정반대였음
//                       수정 (Mirror.jsx 2줄):
//                          ・ forward vector 부호 반전: (-sin(yaw), 0, -cos(yaw))
//                          ・ 초기 yaw도 반전 (Math.PI*0.75 → -Math.PI*0.75) — 목표(+X,+Z) 응시
//                          ・ right vector는 그대로 유지 (World-space right는 forward와 독립)
// v7.87 (2026-07-03) — Mirror 이스터에그 lazy load (Three.js 번들 분리)
//                       배경: v7.85 Mirror 도입 후 컨테이너 빌드에서
//                            'Module not found: three' 실패 발생 (사용자 QNAP 빌드 로그)
//                            → 근본 원인: package.json에 three 미등재
//                            해결: `npm install three --save` 로 dependency 추가 (사용자 조치)
//                       
//                       추가 개선: 이스터에그를 위해 매번 ~200KB(gzip) three.js를
//                                  초기 번들에 포함하는 건 낭비 → React.lazy 로 분리
//                       
//                       변경 (마이너 5줄):
//                          ・ react import에 lazy, Suspense 추가
//                          ・ static `import Mirror` → `const Mirror = lazy(() => import('./Mirror'))`
//                          ・ 렌더링을 { mirrorMode && <Suspense fallback={null}>...</Suspense> } 로 감쌈
//                            → mirrorMode=false 일 때 청크 fetch도 트리거 안 됨
//                          ・ 'mirror' 검색 시점에 청크 로드 → 순간 지연 있지만 이스터에그니까 OK
// v7.86 (2026-07-03) — 출고완료 시리얼 사후 교체 (배송시트지 상세 모달)
//                       배경: 사용자 요청 — 배송시트지 상세에서 출고 완료된 배송 건의 시리얼을
//                            실제 나간 시리얼로 사후 수정 가능해야 함
//                            (스캔 실수 or 물리적으로 다른 시리얼이 실제 배송된 경우)
//                       
//                       정책:
//                          ・ 다른 모델 시리얼로 교체 불가 (엄격) — 사용자 명시 선택
//                          ・ 적용 범위: fulfilledSerials (출고완료) 만
//                            → reservedSerials(방문수령 예약)/RMA/DOA는 스코프 밖
//                       
//                       UX:
//                          ・ 상세 모달의 각 시리얼 pill 안에 편집 아이콘(연필) 추가
//                            조건: !isIn && !ext && !isProcessed && !rma && shipInfo.fulfilled
//                          ・ 클릭 시 시리얼 교체 모달 팝업 (기존 → 새 시리얼)
//                          ・ 실시간 검증(300ms debounce): 존재 + status=in_stock + 같은 모델
//                          ・ 저장 버튼은 검증 통과 시만 활성화
//                          ・ Enter로 저장, ESC로 취소
//                       
//                       처리 순서 (원자적 트랜잭션 불가 → 순차 + 롤백):
//                          ① 기존 시리얼 shipped → in_stock (restoreToStock)
//                          ② 새 시리얼 in_stock → shipped (shipBySerial, saleDate/dealer 승계)
//                          ③ ② 실패 시 기존 시리얼 shipped로 롤백 (best-effort)
//                          ④ transaction_log.note JSON 갱신 (optimistic lock: updated_at 매치)
//                             - items[itemIdx].fulfilledSerials 배열의 oldSerial → newSerial
//                             - serialSwaps 이력 배열에 push (감사용, 최근 20건)
//                          ⑤ setLogItems / setLogModalItem 리프레시
//                       
//                       실패 케이스 처리:
//                          ・ 기존 시리얼이 shipped/reserved가 아님 → 복귀 실패 알림 + 중단
//                          ・ 새 시리얼이 in_stock 상태 아님 → 롤백 + 알림
//                          ・ optimistic lock 충돌 → inventory 이미 바뀜 → 새로고침 안내
//                       
//                       수정 위치:
//                          ・ state 4개 (serialEditState/Input/Check/Busy) — 라인 1423
//                          ・ openSerialEdit/closeSerialEdit/saveSerialSwap + 검증 useEffect — 라인 5197
//                          ・ 시리얼 pill 렌더링 (편집 아이콘) — 라인 13719
//                          ・ 모달 오버레이 — Mirror 컴포넌트 다음 (라인 12923)
// v7.85 (2026-07-03) — 🔮 이스터에그: 3D 큐브 다층 미로 (Mirror)
//                       배경: 사용자 요청 — 재고 목록 검색창에 'mirror' 입력 시 Three.js 3D 미로 게임
//                            (기존 'rest'=Strands+영상, 'mine'=지뢰찾기와 동일 트리거 패턴)
//                       
//                       구조:
//                          ・ 신규 파일 src/Mirror.jsx (~700줄, Three.js 사용)
//                          ・ 5개 레벨 (5×5×2 ~ 13×13×6), 재귀 백트래킹 미로 생성
//                          ・ 층 간 이동: 발광 계단(녹색=위/주황=아래) 자동 이동 (쿨다운 1.5s)
//                          ・ 목표: 최상층 우하단 발광 옥타곤
//                          ・ 외곽: 반투명 큐브 와이어프레임 (이미지 감성)
//                          ・ 조작: WASD 이동 + 마우스 시점 + Shift 스프린트 + ESC 나가기
//                          ・ 인트로 → 게임 → 레벨업/완료 화면 UI 분리
//                          ・ 벽은 InstancedMesh로 성능 최적화 (레벨5 기준 ~4000개 벽)
//                       
//                       배포 조건:
//                          ・ 최초 배포 전에 컨테이너에서 `npm install three` 실행 필요
//                          ・ 이미 설치돼 있으면 그대로 빌드
//                       
//                       App.js 수정 4곳:
//                          ・ import Mirror from './Mirror' (라인 10)
//                          ・ const [mirrorMode, setMirrorMode] = useState(false) (라인 1420)
//                          ・ 검색 onChange에 cmd==='mirror' 트리거 추가 (라인 ~6731)
//                          ・ <Mirror open={mirrorMode} onClose=... /> 렌더 (라인 ~12923)
// v7.84 (2026-07-03) — 배송시트지 drag handle 실제 작동 (v7.81 근본 원인 수정)
//                       배경: 사용자 보고 — v7.83까지 방향 편향은 고쳤는데 여전히 드래그 자체가 작동 안 함
//                       
//                       근본 원인: Icon 컴포넌트(라인 ~1274)는 { name, size, style }만 props로 받음
//                          → v7.81의 <Icon draggable onDragStart={...} onDragEnd={...} title={...}>는
//                            draggable/onDragStart/onDragEnd/title이 전부 무시되어 실제 DOM에 반영 안 됨
//                            → 드래그 이벤트가 아예 등록되지 않아 클릭한 상태로만 있었음
//                          v7.83까지 로직만 다듬고 있었지만 실제로는 이벤트 자체가 발화 안 됐던 것
//                       
//                       메모장은 정상 작동한 이유:
//                          <div draggable onDragStart={...}> 형태로 div에 draggable을 붙였기 때문
//                          Icon은 div 안의 시각 요소로만 사용됨 (라인 ~8766)
//                       
//                       수정 (라인 ~8880):
//                          Icon → div로 감싸고 draggable/onDragStart/onDragEnd/title을 div에 이동
//                          Icon은 div 자식으로 시각 요소만 담당
//                          + hover 시 배경 하이라이트 추가 (메모장과 통일된 UX)
// v7.83 (2026-07-03) — 드래그 재정렬 splice 방향 편향 수정 (v7.81 후속)
//                       배경: 사용자 보고 — 왼→오 이동은 안 되고, 오→왼 이동만 작동
//                            (동일 UI가 방향에 따라 다르게 반응)
//                       
//                       원인: splice 방식의 인덱스 편향
//                          왼→오 (fromIdx=0, toIdx=1) 케이스:
//                          ① splice(0, 1) → [B], moved=A  ← 뒷 인덱스가 당겨짐
//                          ② newToIdx = 0 (B의 새 위치)
//                          ③ splice(0, 0, A) → [A, B]  ← 원위치! 이동 안 됨
//                          오→왼 (fromIdx=1, toIdx=0) 케이스:
//                          ① splice(1, 1) → [A], moved=B  ← 앞 아이템 인덱스 안 바뀜
//                          ② newToIdx = 0 (A의 새 위치)
//                          ③ splice(0, 0, B) → [B, A]  ← 정상 이동
//                       
//                       수정: 방향에 따라 삽입 위치 보정
//                          const isForward = fromIdx < toIdx;
//                          const insertAt = isForward ? newToIdx + 1 : newToIdx;
//                          앞→뒤 이동일 때만 dropId "다음" 위치에 삽입 → 편향 상쇄
//                       
//                       영향 범위 (같은 패턴 2곳 모두 수정):
//                          ・ reorderMemos (메모 코르크보드) — 라인 ~4516
//                          ・ reorderShipRow (배송시트지 row) — 라인 ~4551
// v7.82 (2026-07-03) — 출고 처리 pending 시리얼 새로고침 후 복원 (localStorage 지속화)
//                       배경: 사용자 보고 — 시리얼 스캔 후 페이지 새로고침하면 pending이 모두 사라짐
//                            처음부터 다시 스캔해야 하는 UX 문제
//                            v7.73(모달 닫아도 유지)의 확장 케이스 — 브라우저 세션 넘어 유지 필요
//                       
//                       설계:
//                          저장소: localStorage 사용 (구조: `inventura_ship_pending_v1_${reqId}` 키)
//                          값 형식: { pending: [...], savedAt: ISO } — savedAt은 만료 판정용
//                          만료: 7일 — 창고 처리 주기 감안, 오래된 stale 데이터 자동 정리
//                          정리 시점: (a) pendingSerials가 [] 될 때 (useEffect)
//                                    (b) 커밋 성공 후 명시 정리 (배치 업데이트 안전장치)
//                                    (c) "모드 종료" 버튼 클릭 시 명시 정리
//                                    (d) 앱 시작 시 7일 초과 항목 일괄 정리
//                       
//                       수정 항목:
//                          ・ localStorage 유틸 4개 추가 (라인 ~1127):
//                             saveShipPending / loadShipPending / clearShipPending / cleanupStaleShipPending
//                          ・ pendingSerials 변경 감지 useEffect 추가:
//                             빈 배열이면 clear, 있으면 save. activeRequest.reqId를 키로 사용
//                          ・ 앱 시작 시 cleanupStaleShipPending 1회 실행
//                          ・ loadActiveRequest에서 restored 우선 세팅:
//                             localStorage에 pending 있으면 그대로 setPendingSerials
//                             (restored는 이미 auto+수동 스캔 합쳐진 상태이므로 그대로 사용)
//                             없으면 autoExtPending으로 첫 진입 처리 (기존 동작)
//                          ・ 커밋 성공 라인(~2283)에 명시 clearShipPending 추가 (이중 보장)
//                          ・ "배송시트지로 돌아가기" 버튼(~7470)도 명시 정리
//                       
//                       사용자 워크플로:
//                          ① 시리얼 스캔 도중 실수로 페이지 새로고침
//                          ② 배송시트지에서 다시 해당 요청의 [제품 출고] 클릭
//                          ③ 스캔한 시리얼이 그대로 pending에 복원 — 이어서 계속 스캔 가능
//                          ④ 커밋 성공 시 자동 정리 (다음 요청은 깨끗한 상태로 시작)
// v7.81 (2026-07-03) — 메모 드래그 안정화 + '크기' 라벨 삭제 + 배송시트지 row 드래그 재정렬
//                       
//                       (1) 메모 드래그가 "가끔 안 되는" 원인 수정
//                           원인: v7.35에서 성능 목적으로 도입한 swap 방식의 두 결함
//                              (a) 비인접 이동 시 두 메모만 자리 바뀜 → 사이 메모들은 그대로 (사용자 기대와 불일치)
//                              (b) 우연히 두 메모의 sort_order가 같으면 swap해도 값 동일 → 위치 안 바뀜
//                           수정 (reorderMemos, 라인 ~4503):
//                              swap → insert(splice) 회귀. splice 후 그룹별로 sort_order 10단위 전면 재할당.
//                              겹침 없음 보장, 예측 가능한 결과. 성능 손실은 메모 수십 개 수준에서 미미.
//                              pinned 그룹 넘나드는 드래그는 무시 (그룹 위치가 강제되므로).
//                       
//                       (2) '크기' 라벨 제거 (라인 ~8415)
//                           카드 크기 토글에서 '크기' 텍스트 삭제. 두 버튼(작게/크게)만으로 충분히 명확.
//                       
//                       (3) 배송시트지 row 드래그 재정렬 (NEW)
//                           ・ 스코프: 같은 transaction_date(같은 날짜 그룹) 내에서만 재정렬 허용
//                             다른 날짜로 이동은 자동 이월 로직에 맡김
//                           ・ 스키마 변경: transaction_log.sort_order INT 컬럼 추가 (별도 SQL 실행 필요)
//                             기존 row는 같은 날짜별로 created_at 내림차순 순위를 10단위로 초기화
//                           ・ loadTxLog SELECT에 sort_order 포함, ORDER BY 3단계로 확장:
//                             transaction_date DESC → sort_order ASC (nullsLast) → created_at DESC
//                           ・ reorderShipRow 함수 신설: 같은 날짜 그룹만 splice 재정렬 후 병렬 UPDATE
//                           ・ 각 row 첫 셀에 drag_indicator 아이콘 추가 (draggable=true, grab 커서)
//                             open_in_new(상세보기)와 세로 배치, 처리된 row(processed)는 handle 숨김
//                           ・ row 최상위 div에 onDragOver/onDrop 핸들러:
//                             같은 날짜 그룹일 때만 boxShadow 강조로 drop zone 표시
// v7.80 (2026-07-02) — 재고 목록에 방문수령 예약/킵 시각화 + 안내 강화
//                       배경: 사용자 요청
//                          ・ 방문수령으로 예약된 시리얼임을 명확히 안내 (다른 사용자가 실수로 못 쓰게)
//                          ・ 예약됨/킵 상태를 재고 목록에 표기
//                          ・ 예약/킵 시리얼이 다른 출고에서 스캔되는지 확인
//                       
//                       조사 결과:
//                          ① reserved 시리얼 스캔 차단: 이미 v7.66에서 구현됨 ✓ (라인 1957~1959)
//                             "이미 예약된 시리얼입니다 → {예약자}" alert 후 차단
//                          ② 킵 시리얼 스캔 차단: 원리적으로 불가 ✗
//                             product_keeps는 수량 단위(qty)로 예약, 특정 시리얼과 매핑 없음
//                             등록 시점(라인 3044)에 활성 킵 수량 confirm 경고만 작동
//                       
//                       수정 항목:
//                          ・ Badge 컴포넌트: reserved(파란 #0277bd), keep(보라 #7b1fa2) variant 추가
//                          ・ 그룹 카운팅: g.reserved 필드 추가, 초기값 세팅
//                          ・ 그룹 헤더 상태 바: 예약 N (event_available 아이콘) + 킵 N (bookmark 아이콘) 요약
//                            - 예약: g.reserved 카운트 (시리얼 단위)
//                            - 킵: activeKeepsByModel[product_name] (제품 단위 총합)
//                          ・ 개별 시리얼 상태 컬럼: reserved 케이스 배지로 표시
//                            "방문수령 예약" 배지 + 예약자 dealer_name 하단 표시
//                            title tooltip: "방문수령으로 예약된 시리얼입니다. 다른 출고에 사용할 수 없습니다."
//                            드롭다운 편집 불가 (수동 상태 변경 방지 — reserved는 방문수령 완료/해제로만 전환)
//                          ・ 상태 필터 칩: "예약" 옵션 추가 (파란색 계열)
//                          ・ 필터 안내 메시지: reserved 라벨 추가
//                       
//                       사용자에게 안내 (문서화):
//                          ・ 킵은 수량 단위 예약이라 특정 시리얼과 매핑되지 않음 (스키마 한계)
//                          ・ 시리얼별 킵 관리가 필요하면 별도 스키마 개편이 필요 (v7.81 후보)
//                          ・ 현재는 등록 시점 confirm 경고 + 그룹 헤더 요약으로 대응
// v7.79 (2026-07-02) — AS 재발송 자동 NOSN 부여 완전 제거 (자동 시리얼 면제 전면 폐지 완결)
//                       배경: 사용자 보고 — HDD 출고 시 여전히 NOSN 임시 시리얼이 자동 부여됨
//                            REQ-20260702-8104 (IRON_WOLF 8TB) → NOSN-IRON_WOLF8TB-01 자동 생성
//                            "v7.70에서 RAIL 자동 매칭 제거했는데 왜?"라는 질문
//                       
//                       원인 파악:
//                          openShipRequest의 serialOptional 결정 로직에 두 자동 트리거가 있었음:
//                          (a) patternOptional = isSerialOptional(cat, m.model)
//                              → v7.70에서 항상 false 반환하도록 축소됨 ✓ (이미 처리)
//                          (b) hasAsKeyword = isManualSales && /\bA\/?S\b/i.test(salesStatusManual)
//                              → 매출여부가 "수기입력"이고 자유 텍스트에 "AS" 또는 "A/S"가 포함되면
//                                자동으로 serialOptional=true → NOSN- 시리얼 부여
//                              → 이 케이스가 사용자 이슈의 원인. 매출여부 자유 텍스트에 "AS" 포함됨
//                       
//                       사용자 결정: (a)(b) 모두 완전 제거하여 자동화 폐지, 실 시리얼 강제 스캔 통일
//                       
//                       수정 (라인 ~1810):
//                          ・ isManualSales, hasAsKeyword, patternOptional 변수 삭제
//                          ・ serialOptional 결정 조건에서 patternOptional/hasAsKeyword 제거
//                          ・ serialOptionalReason에서 'pattern', 'as_resend' 케이스 제거
//                          ・ 이제 유일한 자동 트리거는 itemOptional (items[i].serialOptional 명시)
//                             호출처: 데모 대여 자동 등록(demo_lend) 정도만
//                       
//                       완결된 자동 시리얼 면제 히스토리:
//                          v7.57: HDD/SSD 자동 면제 제거
//                          v7.70: RAIL 자동 면제 제거 (isSerialOptional 축소)
//                          v7.79: AS 키워드 자동 면제 제거 (hasAsKeyword 삭제)
//                          → 이제 자동 시리얼 면제는 완전히 사라짐. 명시적 사유만 남음.
//                       
//                       참고 — isSerialOptional 함수는 dead code 상태이나 시그니처 유지 (향후 재사용 대비)
// v7.78 (2026-07-02) — DOA 반품 시리얼 등록 FK 위반 처리 강화
//                       배경: 사용자 보고 — "반품 시리얼 신품 등록 실패: violates foreign key constraint
//                            inventory_product_name_fkey (이미 등록된 시리얼일 수 있습니다)"
//                            에러 메시지는 시리얼 중복이라고 안내했는데 실제 원인은 다름
//                       
//                       진단:
//                          ・ inventory.product_name → products.product_name FK 제약 존재
//                          ・ RMA/DOA 등록 시 rmaProductName은 자유 텍스트라 카탈로그 미등록 이름 가능
//                          ・ v7.77 코드는 카탈로그 조회를 카테고리 채우기 목적으로만 사용, 없어도 INSERT 시도
//                            → FK 위반으로 실패, 잘못된 에러 안내
//                       
//                       수정 (doRepairComplete 라인 ~4720):
//                          ・ 반품 시리얼 INSERT 전에 ProductService로 카탈로그 사전 검증
//                            findExact → findCaseInsensitive 순으로 정규화 시도
//                          ・ 카탈로그에 없으면 명확한 안내 후 중단:
//                            "제품명 X 이(가) 카탈로그에 등록되지 않았습니다. 먼저 등록 후 재시도"
//                          ・ 있으면 catalog.product_name (canonical)로 저장 → 대소문자/공백 정규화
//                          ・ 에러 유형별 안내 분기:
//                             - inventory_pkey/duplicate → "시리얼 중복" 안내
//                             - foreign key/fkey → "제품 참조 오류" 안내 (원본 메시지 포함)
//                             - 기타 → 원본 에러 그대로 표시
//                       
//                       후속 개선 여지 (별도 릴리스 후보):
//                          ・ RMA/DOA 등록 폼의 제품명 필드를 자동완성으로 카탈로그에서 선택 유도
//                            → 애초에 미등록 이름 입력 방지
// v7.77 (2026-07-02) — v7.76 DOA 워크플로 버그 수정 + UX 강화
//                       배경: v7.76에서 놓친 두 가지 — 사용자 지적으로 발견
//                          ① UX: [+ 반품 시리얼] 배지가 "더블클릭"으로만 편집 가능해서 발견 어려움
//                             안내가 title tooltip에만 있어서 실제 사용자는 지나칠 확률 큼
//                          ② 로직 버그: DOA + 반품 시리얼 미입력 상태에서 [수리 완료] 클릭 시
//                             `fromDB && sn && !hasReturn` 조건에 걸려 원 시리얼이 재고로 복구됨
//                             (RMA와 동일한 처리) → 벤더가 회수한 시리얼이 우리 재고에 복원되는 오류
//                             반품 시리얼도 등록 안 됨 → 실 재고와 DB 불일치 발생
//                       
//                       수정 방향 (사용자 결정):
//                          - UX: 배지 클릭 한 번으로 편집 진입 (더블클릭 완화)
//                          - 로직: [수리 완료] 클릭 시 DOA면 반품 시리얼 필수화
//                                  → 없으면 전용 모달로 강제 입력받기 (놓칠 걱정 없음)
//                          - 시나리오: DOA는 항상 "원 시리얼 판매불가 유지 + 반품 시리얼 신품 등록"
//                                     (원 시리얼 재고 복구 케이스는 이제 발생 안 함)
//                       
//                       수정 항목:
//                          ・ state 추가: doaReturnSerialModal (라인 ~1336)
//                          ・ handleRepairComplete → doRepairComplete 분리 (라인 ~4682)
//                             handleRepairComplete: 진입점. DOA+반품시리얼X면 모달 열고 리턴
//                             doRepairComplete: 실제 처리. 모달에서도 재사용
//                          ・ DOA 분기 정리: !isDoa일 때만 원 시리얼 재고 복구 (기존 !hasReturn 대체)
//                             → DOA는 원 시리얼 절대 복구 안 함 (반품 시리얼 유무와 무관)
//                          ・ 모달 UI 신설 (라인 ~13300 직전): 초록 강조, 제품명/원 시리얼 표시
//                             + 반품 시리얼 입력 필드 + [신품 등록 후 수리완료] 액션
//                          ・ 배송시트지 배지 클릭 완화: onDoubleClick → onClick
//                            안내 라벨 "반품 시리얼" → "반품 시리얼 입력" (액션성 강화)
//                          ・ note에 rmaReturnSerial 병합 저장 (모달로 입력한 값도 나중에 조회 가능)
// v7.76 (2026-07-02) — RMA 수령인 라벨 축소 + DOA 반품 시리얼 이중 관리
//                       
//                       (1) RMA 수령인 라벨 축소 (라인 ~2241)
//                           Before: [RMA] TS-1673AU-RP-16G  ← 제품명이 수령인 필드에 섞여 표시됨
//                           After:  [RMA]                    ← 브래킷 태그만 (또는 [DOA])
//                           제품명은 별도 rmaProductName 필드에 그대로 보존됨 (RMA 정보 카드 표시)
//                       
//                       (2) DOA 반품 시리얼 이중 관리 (새 워크플로)
//                           배경: DOA는 우리가 나간 시리얼과 벤더가 신품 교체로 회수해준 시리얼이 다름
//                                기존에는 shipRmaSerial 하나만 있어서 반품 시리얼 별도 추적 불가
//                           
//                           신규 필드: rmaReturnSerial (transaction_log.note에 저장)
//                           
//                           수정 항목:
//                              ・ state 추가: shipRmaReturnSerial (라인 ~1336)
//                              ・ resetShipForm 초기화 추가
//                              ・ 요청 저장 시 shipEmsType==='DOA'일 때만 note에 rmaReturnSerial 저장
//                              ・ RMA/DOA 등록 폼: DOA 선택 시 초록 강조 카드로 반품 시리얼 필드 노출
//                                (라인 ~7610, 사전 입력용 — 선택 사항)
//                              ・ 배송시트지 RMA 배지 옆: 반품 시리얼 표시/더블클릭 편집
//                                (라인 ~8790, 있으면 초록 배지, 없으면 [+ 반품 시리얼] 안내)
//                              ・ 로그 모달 RMA 정보 카드: 반품 시리얼 강조 표시
//                                (라인 ~13042, DOA 시 "시리얼(나가는)" / "반품(들어오는) 시리얼" 명확 구분)
//                              ・ handleRepairComplete 확장 (라인 ~4682):
//                                 - DOA + rmaReturnSerial 있음 → 원 시리얼은 판매불가 유지 (복구 X)
//                                                              + 반품 시리얼 inventory에 신품 INSERT
//                                                                (product_name, category=ProductService 조회,
//                                                                 status=in_stock, stock_type=신품,
//                                                                 source=DOA, receipt_date=today())
//                                                              + note: "[DOA 반품 신품] 원 시리얼: X / 티켓: Y"
//                                 - RMA (기존) → 원 시리얼 판매불가 → 재고 복구 (변경 없음)
//                                 - confirm 다이얼로그: 상황별 3가지 메시지로 분기
//                       
//                       워크플로 예시 (DOA):
//                          ① 출고요청에서 발송종류 EMS + DOA 선택 → 원 시리얼 입력
//                          ② 반품 예상 시리얼을 안다면 지금 입력 (선택), 아니면 나중에
//                          ③ 배송시트지에서 벤더에게 발송 처리 (원 시리얼 판매불가로 변경)
//                          ④ 벤더로부터 신품 회수 → 배송시트지에서 반품 시리얼 입력 (더블클릭)
//                          ⑤ [수리 완료] 클릭 → 반품 시리얼이 신품 재고에 자동 등록됨
// v7.75 (2026-07-02) — HDD/SSD 입고 모델 번호 선택화 + "제품 카탈로그" → "제품 표준화 관리" 리네임
//                       
//                       (1) HDD/SSD 입고 모델 번호 필수 → 선택으로 완화
//                           배경: 시리얼 단위 모델 번호(예: ST24000NT002)를 항상 알 수 없거나
//                                입고 후 확인하는 케이스가 있어 필수 검증이 워크플로 방해
//                           수정 (5곳):
//                              ・ registerHddInbound(라인 ~3726): modelNo 필수 검증 제거
//                              ・ confirm(라인 ~3744): 빈 값 시 "(미입력)" 표시
//                              ・ DB 저장(라인 ~3756): 빈 문자열 → NULL 정규화
//                              ・ canRegister 조건(라인 ~10747): modelNo.trim() 제거
//                              ・ label(라인 ~10877): "(선택)" 배지 + placeholder "(필요 시)"
//                              ・ 등록 버튼 하단 경고(라인 ~10974): 모델 번호 미입력 경고 제거
//                       
//                       (2) 탭 이름 리네임: "제품 카탈로그" → "제품 표준화 관리"
//                           배경: "카탈로그"는 소비자용 상품 진열 뉘앙스가 있어 실제 용도와 불일치
//                                실제 기능: 표준 제품명 정의, 카테고리 분류, 별칭 관리 → "표준화 관리"가 정확
//                           수정 (4곳):
//                              ・ 사이드바 nav 라벨(라인 ~5776)
//                              ・ 페이지 h1 제목(라인 ~10424)
//                              ・ 백업 페이지 테이블 라벨(라인 ~3496)
//                              ・ 신규 제품 등록 모달 헤더(라인 ~11005) → "제품 등록"으로 축약
//                           페이지 서브텍스트("마스터 제품 목록 — 표준 정의")는 새 이름과 잘 어울려 유지
//                           본문/타 페이지의 일반 명사 "카탈로그" 언급은 그대로 유지 (문맥 자연스러움)
// v7.74 (2026-07-02) — 재고 없음 시 카탈로그 확인 후 external 자동 폴백 방지
//                       배경: 사용자 보고 — WD_ULTRASTAR_SATA 4TB (HDD, 카탈로그 등록됨)를
//                            빠른 추가로 넣었더니 EXT- 임시 시리얼로 external 처리됨
//                            원인: quickAddShipItem이 InventoryService.searchInStockProducts만
//                                 조회 → in_stock 재고가 0이면 결과 비어서 external:true 폴백
//                            RAIL-B02(v7.70) 케이스와 동일 원리, 이번엔 재고만 없는 상황
//                       
//                       수정 (라인 ~2035):
//                          재고 조회 결과가 비었을 때 ProductService로 카탈로그 존재 재확인:
//                          ① 카탈로그에 있음 → external:false, category=catalog.category, available=0
//                             UX 안내: "카탈로그 등록 제품이나 현재 재고 부족 (입고 후 시리얼 스캔 필요)"
//                             워크플로: 요청 저장 → 입고 완료 → 배송시트지에서 실 시리얼 스캔
//                          ② 카탈로그에도 없음 → 기존처럼 external:true (진짜 외부 제품)
//                             UX 안내: 기존 문구 유지
//                          정규화: catalog.product_name을 canonical 이름으로 사용 (대소문자 다른 입력 정규화)
//                       
//                       주의:
//                          ・ addShipItem(자동완성 클릭)은 in_stock 결과만 리스트에 뜨므로 이 문제 없음
//                          ・ 이 수정은 quickAddShipItem(Enter 빠른 추가) 경로만 대상
// v7.73 (2026-07-02) — 출고 처리 상태 유지 + HDD 유형 축소 + 방문수령 라벨 변경
//                       
//                       (1) 출고 처리 모달 닫아도 스캔한 시리얼 유지 (사용자 요청)
//                           배경: 배송시트지 → [제품 출고] → 시리얼 여러 개 스캔 → 잠깐 다른 페이지 확인 →
//                                 돌아오면 스캔한 pending이 전부 사라짐 → 처음부터 다시 스캔해야 함
//                           수정 위치:
//                              ・ loadActiveRequest(라인 ~1584): 같은 reqId 재호출 시 pendingSerials가 있으면
//                                 재초기화 스킵. model만 다르면 currentModel만 전환.
//                              ・ closeProcessing(라인 ~13073): setActiveRequest(null)/setPendingSerials([]) 제거.
//                                 모달만 닫고 상태는 유지. 사용자가 명시적으로 "모드 종료"(X 버튼)나
//                                 실제 [제품 출고] 커밋 시에만 정리됨.
//                           워크플로:
//                              ・ 스캔 3개 → 모달 닫음 → 배송시트지 다른 요청 확인 → 원 요청 다시 [제품 출고]
//                                → 스캔한 3개가 그대로 남아있음, 이어서 계속 스캔 가능
//                              ・ 다른 요청 열면 → openShipRequest에서 새 activeRequest로 덮어쓰기 (기존 동작)
//                              ・ 완료(isComplete && pending===0) 후 [배송시트지로 돌아가기]는 그대로 초기화
//                       
//                       (2) HDD/SSD 입고 유형 옵션 축소 (라인 ~10717)
//                           변경: [신품, 리페킹, 조립출고] → [신품, AS]
//                           - HDD/SSD는 실무상 리페킹/조립출고 케이스 없음, AS 반품/재입고 케이스 신규 추가
//                           - STOCK_TYPE_ORDER에 AS 순서 추가(신품:0, AS:1, 리페킹:2, 조립출고:3)
//                             → 재고 목록 정렬 안정성 유지
//                           - 일반 입고 등록(입고 등록 페이지)은 3종 그대로 유지
//                       
//                       (3) "방문수령 완료" → "방문수령 출고" 라벨 변경 (라인 ~9094)
//                           버튼 텍스트와 confirm 문구 모두 통일 ("출고 처리" 어감)
// v7.72 (2026-07-02) — 비고 textarea 크기 확대 (2곳)
//                       배경: 사용자 보고 — 비고 칸이 너무 좁아서 여러 줄 입력이 불편
//                       수정:
//                          ① 출고요청 페이지 비고 textarea (라인 ~7715)
//                             rows: 2 → 5, minHeight: 60 → 140
//                          ② 배송시트지 인라인 편집 memo textarea (라인 ~9028, 더블클릭 편집)
//                             rows: 2 → 5, minHeight: (없음) → 100
//                             fontSize: 11 → 12, padding 소폭 확대
//                          둘 다 resize:'vertical' 유지 → 사용자가 필요 시 수동으로 더 늘릴 수 있음
// v7.71 (2026-07-02) — 출고 요청 폼 상태 유지 + 수동 초기화 버튼
//                       배경: 사용자 보고 — 출고 요청 작성 중 다른 탭 이동 후 돌아오면 폼 전체 리셋됨
//                            원인: useEffect(() => { if(page!=='ship') return; ...resetAll... }, [page])
//                                 형태로 페이지 진입 시마다 자동 초기화 (라인 4029, v7.71 이전)
//                            요구: 탭 이동 시 작업 내용 유지, 사용자가 원할 때만 수동 초기화
//                       
//                       수정:
//                          ・ 자동 초기화 useEffect 완전 제거
//                          ・ 초기화 로직을 resetShipForm() 함수로 추출 (state setter 30개 묶음)
//                          ・ 저장 성공 후에는 명시적으로 resetShipForm() 호출
//                            (setTimeout 800ms 안에 배치 → 완료 메시지 정상 노출 후 페이지 전환 시 폼 리셋)
//                          ・ 출고요청 페이지 헤더 우측에 "초기화" 버튼 신설
//                            "요청 이력" 버튼 왼쪽, 회색톤(background:#fff, border:#c6c6cd)
//                            클릭 시 window.confirm으로 실수 방지
//                       
//                       사용자 워크플로:
//                          ・ 작성 중 배송시트지 확인 → 돌아와도 그대로 (신규 동작)
//                          ・ 요청 저장 완료 → 자동 리셋 후 이력 페이지로 이동 (기존 동작 유지)
//                          ・ 처음부터 다시 시작하고 싶을 때 → 초기화 버튼 (신규)
// v7.70 (2026-06-30) — RAIL 계열 실 시리얼 관리 전환 (자동 시리얼 면제 완전 제거)
//                       배경: 사용자 요청 — RAIL 액세서리도 실 시리얼로 관리 필요
//                            현 상태: isSerialOptional에서 RAIL prefix를 하드코딩 매칭 →
//                                    카탈로그 등록해도 자동으로 serialOptional=true 처리 →
//                                    NOSN- 임시 시리얼 부여됨 (실 시리얼 스캔 흐름 진입 불가)
//                            또 하나의 방벽: DB 미등록 시 자동 external:true (EXT- prefix)
//                                          이건 사용자가 카탈로그 5개 + 실 시리얼 재고 등록으로 해결 완료
//                       
//                       수정 (라인 ~831):
//                          ・ isSerialOptional() 함수를 항상 false 반환으로 축소
//                          ・ v7.57에서 HDD/SSD 자동 면제를 제거한 조치와 대칭
//                          ・ 이제 자동 패턴 매칭 없음. 명시적 사유만 serialOptional 처리됨:
//                            - items[i].serialOptional = true (호출자가 직접 지정)
//                            - 데모 대여(demo_lend), AS 재발송(as_resend) 등 사용처별 명시
//                          ・ 함수 시그니처(category, productName)는 호출처 시그니처 안정성 위해 유지
//                       
//                       사용자 액션 완료 사항:
//                          ・ products 카탈로그에 RAIL-A02-90, RAIL-A03-57, RAIL-B02, RAIL-E02, RAIL-E03 5개 등록
//                          ・ inventory에 RAIL-A02-90 × 9, RAIL-B02 × 8 등 실 시리얼 재고 확보
//                       
//                       참고 (기존 미처리 요청 REQ-20260629-4501):
//                          ・ 이 요청은 EXT-RAIL-B02-01/02 임시 시리얼로 이미 세팅되어 있음
//                          ・ 그대로 처리하면 EXT- 시리얼로 출고 이력 남음
//                          ・ 실 시리얼로 처리하려면 해당 요청 취소 후 재작성 필요
// v7.69 (2026-06-30) — 스마트 검색 transaction_log cross-search 정밀화 (모델 노이즈 제거)
//                       배경: v7.29에서 도입한 transaction_log cross-search가 트랜잭션 단위로 매칭
//                            → 같은 출고요청에 여러 모델이 묶여있을 때, 검색어와 무관한 모델도
//                              모두 결과에 포함되어 노이즈 발생
//                       사용자 보고: HDD/SSD 탭에서 "파스카리" 검색 → IRON_WOLF_PRO 24TB
//                              (출고이력 32건 배지 포함)가 결과에 노출됨
//                            원인: 같은 출고요청 트랜잭션에 파스카리_SSD_SATA_960GB + IRON_WOLF_PRO 24TB가
//                                 함께 들어있어서 note ILIKE '%파스카리%' 매칭 시 두 모델 모두 추출됨
//
//                       수정 (라인 ~1388):
//                          ・ 각 트랜잭션마다 매칭 위치 판정 추가
//                            (a) common/note/memo/dealer_name 메타 영역에서 매칭 → 모든 items 추출
//                                (수령인/주소/배송 메모로 검색하는 트랜잭션 단위 시나리오)
//                            (b) 메타에 매칭 안 됐으면 → product_name이 매칭되는 item만 추출
//                                (모델별 검색에서 무관한 다른 모델 차단)
//                          ・ "파스카리" 케이스: 메타 매칭 X + IRON_WOLF의 product_name 매칭 X → 스킵
//                            → 결과에 IRON_WOLF가 더 이상 안 따라옴
//                          ・ "SKT T1" 같은 수령인 검색: 메타 매칭 O → 트랜잭션의 모든 모델 추출 (기존 동작 유지)
//
//                       영향 범위: 재고 목록, HDD/SSD 탭 양쪽 모두 (검색 로직 공유)
// v7.68 (2026-06-30) — 배송시트지 수량 stepper + 버튼 재고 상한 검사 추가
//                       배경: 배송시트지 row의 수량 +/- 컨트롤에서 + 버튼이 in_stock과 무관하게
//                            무한히 증가 가능 → 사용자가 실수로 재고 7개인 IRON_WOLF 8TB를
//                            8, 9, 10개로 늘려도 막히지 않음 → 출고 시리얼 스캔 단계에서야
//                            "재고 없음"으로 알게 됨 (실수 발견 지연)
//                       
//                       기존 패턴 활용: 라인 3060 (출고요청 등록), 3112 (킵 변경) 등에서
//                            이미 InventoryService.countInStock + window.confirm으로
//                            soft warning + 강제 진행 옵션을 제공하는 패턴 정착됨
//                       
//                       수정 (라인 ~8427):
//                          ・ inc 함수 (배송시트지 + 버튼)에 재고 상한 검사 추가
//                          ・ 외부(external)/면제(serialOptional) 제품은 검증 스킵
//                          ・ maxAvailable = in_stock + (이 row에 이미 잡힌 fulfilled+reserved)
//                            → 이미 출고/예약된 시리얼은 inventory에서 빠진 상태이므로 더해줘야 진실치
//                          ・ newQty > maxAvailable이면 confirm 다이얼로그 표시
//                            메시지: "현 재고 N개 / 이 요청에 이미 잡힘 M개 / 최대 가용 (N+M)개"
//                          ・ 사용자가 "예" 선택 시 진행 (다른 출고요청과 경합 시 시리얼 스캔에서 차단됨을 안내)
//                          ・ 네트워크 오류 시 검증 스킵하고 그대로 진행 (워크플로 차단 안 함)
//                       
//                       dec(-) 버튼은 기존 minQty 검사로 이미 보호됨 (라인 8423~8425, 변경 없음)
//                       
//                       다음 후보 (v7.69+):
//                          ・ addItemToShipment 호출처(품목 추가 자동완성)에도 동일 검증
//                            현재는 1개씩 추가라 위험도 낮으나 재고 0개일 때 경고하면 더 좋음
//                          ・ 동일 product의 다른 활성 ship_request에 점유된 수량을 빼서
//                            "타 요청 경합 수량"까지 보여주면 더 정확 (현재는 confirm 메시지로만 안내)
// v7.67 (2026-06-30) — 출고 모달 탭 카운트 pending 누락 보강
//                       배경: v7.66 (F) "다중 품목 탭 카운트에 pendingSerials 포함" fix가
//                            txlog 페이지의 출고 요청 처리 패널(라인 ~6588)에만 적용되고
//                            outboundModalOpen으로 열리는 별도 출고 처리 모달(라인 ~12968)에는
//                            동일 코드 블록 두 번째 위치라 빠뜨림.
//                       증상: 모달 진입 후 시리얼 2개 스캔해도 탭은 "(0/2)"로 표시
//                            (우측 진행 박스는 "2/2 대기2"로 정상)
//                       사용자 보고: 파스카리_SSD_SATA_960GB 출고 시 탭만 0/2 유지
//                       
//                       수정: 모달의 multi-model selector도 v7.66 (F)와 동일한 패턴 적용
//                          ・ fulfilledCount + pendingForModel 합산을 분자로
//                          ・ pending > 0일 때 "·대기N" 배지 추가
//                          ・ serialOptional(S/N면제) 처리도 함께 보강 (기존엔 external만 있었음)
//                       교훈: 동일한 multi-model selector 블록이 두 곳에 존재 →
//                            한쪽만 고치면 다른 화면에서 같은 버그 재발. 가능하면 컴포넌트화 권장
//                            (현재는 단일 파일 정책으로 유지, 주석으로 두 위치 cross-ref만 강화)
// v7.66 (2026-06-30) — 방문수령 conflict 진단성 + 같은 종류 버그 재발 방지 + HDD/SSD 입고 UI 정리
//                       배경: v7.55 'reserved' 도입 시 inventory_status_check CHECK 제약 갱신 누락
//                            → reserveBySerial이 silent fail (.update() 빈 배열 반환)
//                            → conflict로 오인되어 "이미 다른 사용자에 의해 처리됨" alert
//                            → 사용자가 진짜 원인을 알 수 없는 상태가 약 N일 지속
//                       2026-06-30 DB 제약 갱신 SQL로 영구 해결:
//                          ALTER TABLE inventory DROP CONSTRAINT inventory_status_check;
//                          ALTER TABLE inventory ADD CONSTRAINT inventory_status_check
//                            CHECK (status IN ('in_stock','shipped','unsellable','reserved'));
//                       
//                       코드 보강:
//                       (A) handleRequestScan에 reserved + 비-in_stock 명시적 차단
//                          ・ reserved 상태 시리얼이 commit 단계까지 가지 않고 즉시 거부
//                          ・ 알 수 없는 status는 "출고 불가 상태: '값'" 메시지로 즉시 진단 가능
//                       (B) commitRequest conflict alert에 실제 DB status 표시
//                          ・ 기존: "QY49N13284Y, ..."
//                          ・ v7.66: "QY49N13284Y[reserved], ..." — alert만 보고도 원인 파악
//                          ・ 같은 종류의 silent fail이 재발해도 즉시 status로 진단 가능
//                       (C) InventoryService 상단에 inventory.status DB 제약 가이드 주석
//                          ・ 새 status 추가 시 ALTER TABLE 동반 필요 명시
//                          ・ 히스토리 + SQL 템플릿 포함
//                       
//                       HDD/SSD 입고 UI 정리:
//                       (D) v7.65 카테고리 토글 제거 — 카탈로그를 단일 진실원천(SOT)으로 사용
//                          ・ state.category → state.selectedCategory (표시 전용)
//                          ・ 2행 grid: 1.4fr 1fr 140px → 1.5fr 1fr (간결)
//                          ・ register 함수: 카탈로그.category 가져와 inventory.category에 그대로 저장
//                          ・ 카탈로그 카테고리 NULL이면 차단 + 카탈로그 관리 페이지로 유도
//                          ・ 사용자가 카탈로그와 다른 카테고리로 등록하는 실수 방지
//                       (E) 자동완성 dropdown 가림 현상 fix
//                          ・ 원인: Card 컴포넌트의 backdropFilter:blur(10px)가 새 stacking context를 생성
//                            → dropdown z-index를 아무리 높여도 헤더 Card 내부에서만 유효
//                            → 형제 시리얼 Card가 DOM 순서상 위에 그려져서 dropdown 가림
//                          ・ 해결: 헤더 Card 자체에 position:relative + zIndex:10 부여
//                            → 헤더 Card stacking context가 시리얼 Card보다 위로 올라옴
//                          ・ dropdown z-index 50 → 9999 (보조 보강)
//                          ・ shadow 강화: rgba(0,0,0,0.1) → 0.15 (가독성 ↑)
//                          ・ 선택된 제품 박스에 카탈로그 카테고리 배지 표시
//                          ・ ellipsis 추가로 긴 제품명도 박스 안에서 깔끔히 표시
//                       
//                       출고 요청 처리 모달 카운트/dropdown 정리:
//                       (F) 다중 품목 탭 카운트에 pendingSerials 포함 (진행 박스와 일관성)
//                          ・ Before: "IRON_WOLF 24TB (0/32)" — fulfilledSerials만
//                          ・ After:  "IRON_WOLF 24TB (2/32)·대기2" — fulfilled+pending + 대기 배지
//                          ・ 진행 박스(우측 상단)와 동일한 카운트 정책
//                       (G) 자동완성 dropdown 결과 제한 15 → 50
//                          ・ 32개 재고가 있어도 dropdown 헤더가 "(15+)"로 보여 사용자 혼란
//                          ・ 일반적인 단일 모델 출고 수량이 50개를 넘는 경우는 드물어 안전한 상향
//                          ・ dropdown maxHeight 280 → 360 (한 번에 7-8개 보임)
// v7.65 (2026-06-30) — HDD/SSD 입고: 카테고리 강제 + 카탈로그 자동 보정 (Option C)
//                       배경: 카탈로그(products)의 category가 NULL인 제품을 선택하면
//                            inventory에도 NULL로 저장되어 HDD/SSD 탭 필터에서 누락
//                       사용자 보고: 32개 입고했는데 HDD/SSD 탭에 0개로 표시
//                       
//                       (1) state에 category 필드 추가 (default 'HDD')
//                       (2) UI 2행에 카테고리 토글 추가 — [HDD | SSD] 매번 명시
//                          ・ 모델 번호 옆 140px 컬럼
//                          ・ HDD = #d84315 (다홍) / SSD = #5d4037 (갈색)
//                       (3) registerHddInbound 변경
//                          ・ inventory.category = hddInbound.category (사용자 선택값, NULL 방지)
//                          ・ 카탈로그 자동 보정: products.category가 NULL이면 사용자 선택값으로 update
//                            → 다음 입고부터는 자동 채워짐, 점진적 카탈로그 정리
//                          ・ confirm 메시지에 카테고리 표시
//                       
//                       사용자 응급 보정 SQL (기존 NULL 데이터):
//                          UPDATE products SET category='HDD'
//                          WHERE category IS NULL AND product_name ILIKE 'IRON_WOLF%';
//                          UPDATE inventory SET category='HDD'
//                          WHERE category IS NULL AND model_no IS NOT NULL
//                            AND product_name IN (SELECT product_name FROM products WHERE category='HDD');
// v7.64 (2026-06-30) — HDD/SSD 입고: 제품명(카탈로그 자동완성) + 모델 번호(수기) 분리
//                       배경: 모델명 자유 입력으로 사용자가 임의 모델 만들 수 있어 카탈로그 오염 위험
//                       해결: 일반 입고 페이지처럼 카탈로그 검색만 허용, 신규 등록은 모달 거쳐서만
//                       
//                       (1) state 재구성
//                          ・ productName: 선택된 카탈로그 product_name (확정값)
//                          ・ productSearch: 자동완성 검색용 input
//                          ・ productResults: 카탈로그 + alias 검색 결과
//                          ・ productSearchOpen: dropdown 상태
//                          ・ modelNo: 시리얼 단위 모델 번호 (수기, 예: ST24000NT002)
//                          ・ 기존 modelName / modelExists 제거
//                       (2) searchHddProduct 함수 — 자동완성
//                          ・ products.product_name ilike 매칭
//                          ・ product_aliases.alias ilike 매칭 + 해당 product 가져오기
//                          ・ 결과 머지 + 카테고리 배지 + 한글 alias 표시
//                       (3) 헤더 카드 2행 구조로 변경
//                          ・ 1행: 출처 / 입고일 / 유형
//                          ・ 2행: 제품명(자동완성) / 모델 번호(수기)
//                       (4) 자동완성 UI
//                          ・ productName 선택됨 → 초록 박스 + [변경] 버튼
//                          ・ 미선택 → input + dropdown (HDD/SSD 카테고리 배지 + 한글 alias 부제)
//                          ・ 검색 0건 → [신규 등록] 버튼 (v7.61 모달 재사용)
//                       (5) v7.61 모달 의미 변경
//                          ・ 모델명(자동) → 제품명(자동) — productSearch 입력값으로 등록
//                          ・ alias는 한글 별칭 (검색 편의)
//                          ・ 등록 후 자동으로 productName 선택 상태로
//                       (6) registerHddInbound — model_no 컬럼 사용
//                          ・ product_name = productName (카탈로그 키)
//                          ・ model_no = modelNo.trim() (수기 입력)
//                          ・ category, stock_type, source, receipt_date는 기존과 동일
//                       (7) 등록 후 시리얼 + modelNo만 초기화 (제품/출처/입고일 유지)
//                       (8) 등록 버튼 옆에 진행 안내 (어느 필드가 비어있는지 표시)
//                       
//                       DB 마이그레이션 (사용자 작업 완료):
//                          ALTER TABLE inventory ADD COLUMN model_no TEXT;
// v7.63 (2026-06-30) — 배송시트지 다중 품목: 모델명 + 수량 일렬 정렬 (Option A+B)
//                       문제: 모델명이 길면 wordBreak:break-all로 줄바꿈되어 옆 수량 stepper와 어긋남
//                       
//                       (A) 각 품목을 박스로 묶음 — 시각적 그룹화
//                          ・ 모델명 row에 background rgba(0,102,138,0.04) + border 0.12 + radius 6 + padding 2/8
//                          ・ isProcessed(취소/환불) 시에는 배경 제거 (회색 처리와 충돌 방지)
//                       (B) 모델명 줄바꿈 방지 + ellipsis — 항상 한 줄 26px 고정
//                          ・ wordBreak:break-all → whiteSpace:nowrap + overflow:hidden + textOverflow:ellipsis
//                          ・ title 속성에 전체 모델명 (hover로 전체 보임)
//                       (C) 수량 stepper 각 row 외부 wrapper 추가
//                          ・ minHeight 26 + alignItems center 로 모델명 박스와 정확한 vertical align
//                          ・ stepper 자체 디자인은 그대로 (border 1px #c6c6cd, height 26)
// v7.62 (2026-06-29) — 자동 이월 자정 정확히 트리거 (사용자 보고: 9시 확인 시 이월 안 됨)
//                       원인: 기존 트리거가 ① page === 'txlog' 진입 시 ② 1시간 폴링
//                            → 다른 페이지 보고 있거나 새벽에 sleep 모드면 누락
//                       
//                       (1) page 제약 제거 — 어느 페이지에 있어도 자정에 sweep 실행
//                          ・ useEffect 트리거를 [connected]만 의존
//                          ・ sweep 후 loadTxLog는 page === 'txlog'일 때만 호출 (이전 그대로)
//                       (2) 자정 정확히 트리거 — chained setTimeout
//                          ・ 다음 자정 + 30초 (서버 시간 안전 마진) 까지 ms 계산 → setTimeout
//                          ・ sweep 완료 후 다음 자정 재예약 (재귀)
//                          ・ 1시간 폴링 제거 (자정 트리거가 더 정확)
//                       (3) visibilitychange 핸들러 추가
//                          ・ 탭이 다시 활성화될 때 sweep (노트북 sleep 깨움 / 탭 전환 복귀 대응)
//                          ・ 자정에 sleep 상태였더라도 깨어나면 즉시 보정
//                       (4) sweepAutoDefer가 처리된 row 개수 반환 (디버깅용)
// v7.61 (2026-06-29) — HDD/SSD 입고: 카탈로그 즉시 등록 모달 추가 (Option B)
//                       배경: HDD/SSD는 모델명(예 ST24000NT002)과 한글 제품명(예 아이언울프 프로 4테라)이 분리됨
//                       해결: 모델명을 product_name으로 등록 + 한글명을 product_aliases.alias로 등록
//                       
//                       (1) 모델명 입력 후 "⚠ 카탈로그에 없음" 표시 옆에 [지금 등록] 버튼
//                       (2) 클릭 시 미니 모달:
//                          ・ 모델명 (read-only, 입력값 자동)
//                          ・ 한글 제품명 입력 (alias로 저장, source='hdd_inbound')
//                          ・ 카테고리 토글: HDD / SSD
//                       (3) quickRegisterHddProduct 함수
//                          ・ products.upsert (onConflict: product_name) — 이미 있어도 OK
//                          ・ product_aliases.insert (unique_violation 23505 무시)
//                          ・ 성공 시 modelExists=true 자동 갱신 → 입고 진행 가능
//                       (4) Enter 키로 즉시 등록 (alias 입력 후)
// v7.60 (2026-06-29) — 재고 목록에 HDD/SSD 전용 탭 추가
//                       기존 [재고 목록][재고 대시보드] → [재고 목록][HDD/SSD][재고 대시보드]
//                       (1) inventoryTab에 'hdd' 값 추가
//                       (2) 'list' 블록 조건을 list || hdd 둘 다 포함 (UI 그대로 재사용)
//                       (3) loadInventory 세 곳(modelQ / allQ / 일반 q)에 카테고리 필터 추가
//                          ・ inventoryTab === 'hdd' → .in('category', ['HDD','SSD'])
//                       (4) loadInventory useCallback deps에 inventoryTab 추가
//                       (5) 탭 전환 시 pg=0으로 리셋 (페이지네이션 정합성)
// v7.59 (2026-06-29) — HDD/SSD 입고 출처 옵션 변경
//                       대만 / 파주 → 이선 (프리셋) / 수기입력 (직접 입력)
//                       수기입력 선택 시 토글 아래에 input 필드 노출 (autoFocus)
//                       state에 sourceMode 추가 ('preset' | 'manual')
//                       기본값: 이선
// v7.58 (2026-06-29) — HDD/SSD 일괄 입고 UX 변경 (v7.57 Excel 업로드 폐기)
//                       바코드 리더기 → 텍스트 출력 → 클립보드 → 앱에 paste 가 실제 워크플로
//                       Excel 양식보다 헤더 수기 입력이 훨씬 빠름
//                       
//                       (1) state 구조 변경
//                          ・ { source, inDate, modelName, stockType, serialText, parsedSerials,
//                              existingInDb, duplicatesInPaste, modelExists, registering, registerProgress }
//                          ・ inDate 기본값 today()
//                       (2) UI 재구성: 헤더 입력 카드 (출처/입고일/모델명/유형) + textarea 시리얼
//                          ・ 출처: 대만 / 파주 토글
//                          ・ 모델명: 직접 입력 + onBlur 시 카탈로그 매칭 확인 (테두리 색 변경)
//                          ・ textarea onBlur → parseHddSerials (자동 분석)
//                          ・ [분석] 버튼 별도 제공
//                       (3) parseHddSerials: split(/[\r\n\t,]+/) — 줄바꿈/탭/콤마 모두 분리자
//                          ・ paste 내 중복 자동 제거 (set 기반)
//                          ・ DB 중복 사전 조회 (chunked .in)
//                          ・ 카탈로그 매칭 확인
//                       (4) 분석 결과: 신규 등록 가능 / paste 내 중복 / DB 중복 3가지 표시
//                       (5) registerHddInbound: chunked INSERT (200개 단위) + 개별 fallback
//                          ・ 등록 후 시리얼 텍스트만 초기화 (모델/출처/입고일 유지 — 연속 입고 편의)
//                       (6) 이전 parseHddInboundFile (Excel) 제거
// v7.57 (2026-06-29) — HDD/SSD 진짜 시리얼 관리로 전환
//                       배경: 임시 시리얼(UNK-) 폐기 — 환불/교체/이력 추적 정확도 향상
//                       
//                       (1) isSerialOptional 면제 패턴 축소 — RAIL prefix만 면제
//                          ・ EXOS/IRONWOLF/BARRACUDA/ULTRASTAR/WUS/WUH/MG/MN/HUS/HUH 패턴 제거
//                          ・ _SATA / _SAS 키워드 제거
//                          ・ 안내 카드 텍스트: "HDD/RAIL 계열" → "RAIL 계열" 액세서리
//                       (2) 사이드바 메뉴 신설: 'HDD/SSD 입고' (storage 아이콘, 입고 등록 아래)
//                       (3) 새 페이지 (page='inbound_hdd') — 엑셀 일괄 입고
//                          양식: A 출처 / B 입고일 / C 제품명 / D 모델명 / E 시리얼 (F/G 출고 시점 컬럼)
//                          ・ 파일 업로드 → SheetJS로 파싱 (.xlsx/.xls/.csv)
//                          ・ 헤더 자동 탐지 ('시리얼' 키워드 있는 행)
//                          ・ 병합 셀 대응: 빈 셀이면 위 행 값 자동 이어받기 (출처/입고일/제품명/모델명)
//                          ・ 카탈로그 매칭: 모델명(D) 기준 products.product_name 일치 확인
//                          ・ 중복 시리얼 사전 조회 (대량 chunked SELECT)
//                          ・ 행별 4가지 상태: ok / no_product / duplicate / invalid
//                          ・ 카탈로그 없는 모델 목록 표시 (등록 안내)
//                          ・ [N개 일괄 등록] 버튼 — chunked INSERT (200개 단위) + 개별 fallback
//                          ・ 진행률 표시 (registerProgress 0~100)
//                       (4) 카탈로그 페이지에서 [임시 시리얼 입고] 버튼 제거 (UNK- 폐기 방침)
//                          ・ openBulkUnknownModal은 함수 그대로 둠 (호출자가 없으므로 dead code)
// v7.56 (2026-06-29) — 텍스트 문구 일부 변경 (사용자 요청)
//                       (1) 1층배송 선택 시 안내문: "건물 1층 위치한 업체로 배송합니다. 배송지/수령인을 직접 입력하세요."
//                          → "1층 물류팀을 통해 배송합니다."
//                       (2) 데모 관리 탭 이름 통일:
//                          ・ 활성 대여 → 대여로그
//                          ・ 회수 이력 → 회수로그
// v7.55 (2026-06-29) — 방문수령 2단계 흐름 도입 (예약 → 완료) + 환불 자동 복귀 (Q2)
//                       배경: 방문수령에서 시리얼 미리 찍어두고, 고객이 가져갈 때 [방문수령 완료]로 마감
//                       또한 환불 시 inventory가 in_stock으로 복귀 안 되던 버그(v7.54 보고) 해결
//                       
//                       (1) 새 inventory.status='reserved' 도입
//                          ・ in_stock → reserved (예약, 다른 출고 차단)
//                          ・ reserved → shipped (방문수령 완료 시)
//                          ・ reserved → in_stock (취소/환불 자동복귀)
//                       (2) InventoryService 함수 추가
//                          ・ reserveBySerial: in_stock → reserved
//                          ・ completePickupBySerial: reserved → shipped
//                          ・ releaseReservation: reserved → in_stock
//                          ・ restoreToStock: .eq('shipped') → .in(['shipped','reserved']) 로 확장
//                       (3) commitRequest 분기 — isPickupMode = !!common.pickup
//                          ・ 방문수령: reserveBySerial 호출 + reservedSerials에 저장 + fulfilled=false
//                          ・ 일반:    shipBySerial 호출 + fulfilledSerials에 저장 + fulfilled 갱신
//                       (4) loadActiveRequest — 방문수령은 reservedSerials를 fulfilledSerials와 합쳐서
//                          모달 카운트/검증에 정상 반영 (DB 저장 시점에는 다시 분리됨)
//                       (5) completePickupRequest 함수 — 카드의 [방문수령 완료] 버튼이 호출
//                          ・ reservedSerials를 fulfilledSerials로 이동
//                          ・ inventory를 reserved → shipped
//                          ・ fulfilled=true 마킹
//                       (6) 카드 액션 셀 (v7.55의 Q1=a)
//                          ・ 방문수령 + 시리얼 모두 예약 완료 → [방문수령 완료] 버튼 (파란색)
//                          ・ 방문수령 + 시리얼 미완료    → [시리얼 등록] 버튼 (초록색)
//                          ・ 일반 출고                   → [제품 출고] 버튼 (초록색, 기존 그대로)
//                       (7) handleTxAction (취소/환불) — fulfilledSerials + reservedSerials 모두 복귀 (Q2)
//                          ・ restoreToStock이 두 status 다 처리하므로 한 번에 일괄 복귀
//                          ・ 환불도 cancel도 동일 패턴 (이전엔 환불만 시리얼 복귀)
// v7.54 (2026-06-29) — 퀵 서비스 착불 기능 추가
//                       (1) 출고 요청 폼 — 퀵 서비스 가격 영역에 [☑ 착불] 체크박스 추가
//                          ・ state: shipQuickCOD (boolean)
//                          ・ 미리보기: "표시: 퀵(착불) (5,000원)"
//                          ・ submit 시 note.quickCOD = true 저장 (false면 키 없음)
//                       (2) 편집 모달 — 동일 체크박스 추가
//                          ・ editForm.quickCOD 필드
//                          ・ saveEditModal에서 method 변경 시 quickCOD도 함께 정리
//                       (3) 배송시트지 카드 — '퀵' 옆에 [+착불] 토글 배지
//                          ・ 클릭 한 번으로 착불 ON/OFF (saveShipFieldInline)
//                          ・ ON: 빨간 배경 + "착불" / OFF: dashed border + "+착불" (희미)
//                       (4) saveShipFieldInline에 'quickCOD' 분기 추가 (boolean 토글)
//                       (5) 폼 reset에 setShipQuickCOD(false) 추가
// v7.53 (2026-06-29) — 출고 요청 수량 직접 입력 추가
//                       기존: 출고할 제품 카드에 [−] [숫자] [+] 버튼만 (한 번에 1씩 증감)
//                       변경: 가운데 셀을 <input type="number">로 변경
//                       (1) setShipQty(model, rawValue) 함수 추가 — 직접 값 설정 (1 ~ available 클램프)
//                          ・ 외부 제품은 max 한도 없음
//                          ・ 빈 값 / NaN / 음수 → 1로 fallback
//                       (2) input UX
//                          ・ onFocus 시 자동 select (덮어쓰기 편함)
//                          ・ onBlur 시 빈 값 → 1, 초과 → available로 정규화
//                          ・ title hover: 재고 한도 안내
//                          ・ MozAppearance:'textfield' (FF 스피너 숨김)
//                       (3) +/- 버튼은 유지 — 미세 조정용 보조
// v7.52 (2026-06-29) — 출고 모달 시리얼 입력 자동완성
//                       사용자 사용 패턴: 시리얼을 키보드로 직접 입력할 때 매칭되는 재고 시리얼이 보였으면
//                       (1) state 추가: serialSuggestions, serialSuggestionsOpen, serialSuggestionIdx
//                       (2) useEffect로 디바운스(150ms) fetch — InventoryService.searchInStockSerials 활용
//                          ・ 현재 모델(activeRequest.currentModel)의 in_stock 시리얼만
//                          ・ 입력값으로 ilike '%query%' 검색
//                          ・ 이미 fulfilled/pending인 시리얼 제외
//                          ・ 외부/면제 모델은 비활성 (자동 처리되므로 자동완성 무의미)
//                          ・ limit 15
//                       (3) UI: input 하단 dropdown
//                          ・ 헤더: "📦 재고 시리얼 후보 (N개)" + 키보드 단축키 안내
//                          ・ 입력값 매칭 부분 초록 하이라이트
//                          ・ 호버 시 행 강조 + 키보드 인덱스 동기화
//                       (4) 키보드 navigation
//                          ・ ↑↓ : 후보 이동
//                          ・ Tab / Enter (선택 인덱스 ≥0) : 입력란에 시리얼 자동 입력 (추가는 별도)
//                          ・ Enter (선택 없음) : 기존 동작 (handleRequestScan)
//                          ・ Esc : dropdown 닫기
//                       (5) 적용 위치: 두 출고 input 모두 (5970 일반 영역 + 11952 outboundModalOpen 모달)
// v7.51 (2026-06-26) — 메모 드래그앤드롭 동작 swap으로 복귀 (사용자 사용 패턴)
//                       v7.50의 insert 방식은 칸반 UI 표준이지만 사용자가 SKT T1 ↔ TS-1673AU
//                       위치 교체 의도였는데 페스트페이스가 함께 시프트된 결과로 사용자 혼란 발생
//                       (1) reorderMemos: insert(splice) → swap(두 메모 sort_order만 교환)
//                       (2) sort_order 일관성: 첫 드래그 시 모든 메모에 10단위로 초기 부여
//                          ・ 두 메모만 sort_order 있고 나머지 NULL인 정합성 문제 해결
//                       (3) MemoService.updateSortOrders는 v7.50에서 추가한 그대로 활용
//                          ・ 첫 드래그: 전체 일괄 부여
//                          ・ 그 이후: 두 메모만 swap
//                       (4) listActive 정렬은 v7.50 그대로 유지 (sort_order 우선)
// v7.50 (2026-06-26) — 배송시트지 비고 표시 개선 + 메모 위치교체 버그 수정
//                       (1) 배송시트지 비고 셀: 한 줄 잘림 → 줄바꿈 보존
//                          ・ overflow:hidden + ellipsis 제거, whiteSpace:'pre-wrap' 추가
//                          ・ 폰트 볼드(700) + 진한 색(#0b1c30) + 이탤릭 제거
//                          ・ 결과: 상세보기와 동일한 멀티라인 텍스트 표시
//                       (2) 메모 위치교체 버그 수정 — 두 가지 결합 문제
//                          [원인 A] MemoService.listActive가 sort_order를 정렬에 사용 안 함
//                                  → 드래그앤드롭으로 sort_order를 바꿔도 UI는 created_at 순으로 표시되어 무효
//                          [원인 B] reorderMemos가 swap 방식 → 다른 메모들의 sort_order는 NULL인 채로
//                                  부분만 변경되어 일관성 깨짐
//                          [해결] listActive에 sort_order 정렬 추가 (NULL은 created_at fallback)
//                                + reorderMemos를 insert 방식으로 변경 (splice 후 전체 재할당)
//                                + 새 함수 MemoService.updateSortOrders(items) — 일괄 sort_order 갱신
//                                + pinned 그룹별로 sort_order = (i+1) * 10 단위 재부여 (사이 삽입 여유)
// v7.49 (2026-06-25) — 엑셀 다운로드 형식을 .xls(BIFF8 / Excel 97-2003)로 변경
//                       이유: 택배사 시스템 업로드 호환성 (구형 시스템도 .xls는 100% 지원)
//                       (1) ExcelJS → SheetJS(XLSX)로 전환 — 이미 import 되어 있는 라이브러리 재활용
//                          ・ package.json에서 'exceljs' 제거 가능 (안 써도 무해함, 빌드 사이즈만 약간 감소)
//                       (2) XLSX.write(wb, { bookType: 'biff8' }) 로 BIFF8 출력
//                       (3) 파일명: {업체명}_{YYYYMMDD}.xls
//                       (4) 행/컬럼 너비는 유지 — 색상(연두/노랑) / 테두리 / wrapText 는 미지원 (BIFF8 한계)
//                       (5) 함수 async 제거 — SheetJS는 동기 처리
// v7.48 (2026-06-25) — 엑셀 K열 제품 표시 형식 미세 변경 (가독성)
//                       'NX1457 -1EA' → 'NX1457 - 1EA' (대시 양쪽 공백)
// v7.47 (2026-06-25) — v7.46 엑셀 다운로드 마무리
//                       (1) 엑셀의 F/G/H 데이터 행 값을 빈 칸으로 변경 (헤더의 1/1/3 라벨은 그대로)
//                          ・ 사용자 요청: 의미 없는 고정값이므로 출력에서 제거
//                       (2) [엑셀] 버튼 위치 이동: 액션 셀 → 직발 추가 배지 오른쪽
//                          ・ 기존 직발 표시/추가 영역을 row flex로 묶고 그 안에 엑셀 버튼 배치
//                          ・ 직발이 있든 없든 ship_request 카드에는 항상 표시
//                          ・ 액션 셀의 원래 [엑셀] 버튼은 제거 (중복 방지)
// v7.46 (2026-06-25) — 배송시트지 출고건별 엑셀 다운로드 (한국 택배사 양식)
//                       의존성: npm install exceljs (셀 배경색 스타일링 위해 SheetJS 대신 ExcelJS)
//                       양식: A 우편번호(연두) / B 주소(연두) / C 이름(연두) / D 연락처1(노랑) / E 연락처2(노랑)
//                            / F 1(노랑) / G 1(노랑) / H 3(노랑) / I 배송메세지(노랑) / J 상품모델(노랑) / K 제품(노랑)
//                       (1) downloadShippingExcel(item) 함수 추가
//                          ・ note에서 items, trackingNo, addr, recipient, phone 추출
//                          ・ 행 수: max(items.length, trackingList.length, 1) — 모델당 1행
//                          ・ K 열: '{product_name} -{qty}EA' 형식
//                          ・ F/G/H = 1/1/3 고정값 (의미 없음, 사용자 확인)
//                          ・ 헤더 행 배경: A/B/C 연두(#C6E0B4), D~K 노랑(#FFFF00)
//                          ・ 전 셀 thin border + wrapText
//                          ・ 빈 행 4개 추가 (양식 룩 재현)
//                       (2) 파일명: {업체명}_{YYYYMMDD}.xlsx (파일시스템 금지문자는 _로 치환)
//                       (3) 배송시트지 카드의 액션 셀에 [엑셀] 버튼 (작은 outline) — 모든 ship_request에 표시
//                          ・ 출고대기 / 출고완료 / RMA / 환불 모든 상태에서 다운로드 가능
//                          ・ flex-direction: column 으로 변경, gap 4
//                       (4) 출고 카드 모달의 액션 영역에도 [엑셀 다운로드] 큰 버튼 추가 ([내용 수정] 옆)
// v7.45 (2026-06-24) — 카탈로그 카테고리 인라인 편집 + 미분류 빠른 필터
//                       배경: products 카탈로그에 등록만 되고 한 번도 입고 안 된 제품이 미분류로 잔류
//                            (지난 진단 결과 미분류 103종)
//                       (1) 카탈로그 페이지 그리드 컬럼 추가
//                          ・ 1fr 160px 220px → 1fr 170px 140px 220px (카테고리 컬럼 신설)
//                       (2) 각 행에 카테고리 native select (인라인 편집)
//                          ・ 카테고리 없으면 보라색 강조 ("미분류 (지정 필요)")
//                          ・ 변경 시 ProductService.updateMeta(id, {category}) 즉시 호출
//                          ・ 저장 성공 후 setCatalogItems 로컬 갱신 (재로드 불필요)
//                       (3) 카탈로그 상단에 카테고리 빠른 필터 칩
//                          ・ 전체 / 미분류만 / 등록된 카테고리들
//                          ・ "미분류만"은 보라 톤으로 시각 강조
//                       (4) ProductService.list: category === '__UNCAT__'이면 is null 필터
//                          ・ formFactor === '__NONE__' 패턴과 동일
//                       (5) 재고 대시보드 fallback 로직 추가
//                          ・ 기존: categoryMap[name] || '미분류' (inventory.category 기준만)
//                          ・ 변경: categoryMap[name] || p.category || '미분류'
//                          ・ inventory에 row 없어도 카탈로그에서 지정한 카테고리 인식
// v7.44 (2026-06-24) — 데이터 백업 페이지 추가 (수동 다운로드)
//                       배경: Supabase Free 플랜은 자동 백업 없음 — 사고 시 복구 어려움
//                       (1) 사이드바 nav에 '백업' 메뉴 추가 (cloud_download 아이콘, 맨 아래)
//                       (2) 백업 페이지 (page='backup')
//                          ・ 즉시 백업 카드: [전체 백업 다운로드] 버튼
//                          ・ 진행 상태: 8개 테이블 페치 진행률 + 상태 메시지
//                          ・ 자동 백업 안내 카드: n8n + QNAP NAS 가이드 참조
//                          ・ 복원 방법 안내 카드
//                       (3) BACKUP_TABLES: products, product_aliases, inventory, transaction_log,
//                          product_keeps, demo_equipment, demo_rentals, demo_rental_items
//                       (4) fetchAllRowsForBackup: 1000행 페이지네이션, safeLimit 200,000행, id 정렬
//                          ・ id 컬럼 없는 테이블 대응 (catch 후 정렬 없이 재시도)
//                       (5) 결과 JSON: { meta: {backup_version, app_version, counts}, data: {table:[rows]} }
//                          ・ 파일명: inventura-backup-YYYYMMDD.json
//                          ・ 사용자 PC에 다운로드 (수동 백업 1차 안전망)
//                       (6) n8n workflow 별도 제공 — 매일 새벽 자동 백업 + 90일 회전 보관 (NAS)
// v7.43 (2026-06-24) — 데모 대여 수정 이력 추적 (audit log)
//                       데이터: demo_rentals 테이블에 audit_log JSONB 컬럼 추가 (push 누적)
//                          SQL: ALTER TABLE demo_rentals ADD COLUMN audit_log JSONB DEFAULT '[]'::jsonb;
//                          컬럼 미존재 시 무음 실패 (try/catch) — 메인 기능에 영향 없음
//                       엔트리 구조: { ts, by, action: 'create'|'update'|'delete', changes, summary }
//                          ・ create: { ts, by, action:'create', summary:'대여 등록 (N대)' }
//                          ・ update: { ts, by, action:'update', changes:{fields, shipping, addedItems, removedItems}, summary }
//                       (1) DemoService.createRental: 대여 등록 시 첫 엔트리 자동 push
//                       (2) DemoService.updateRental: payload.auditEntry 받으면 audit_log에 append
//                       (3) computeRentalAuditEntry(original, new, by): 변경 사항 자동 비교
//                          ・ 추적 필드: company, shipment_date, expected_return_date, managers, requester, notes
//                          ・ 배송지: ship_method, address, recipient, recipient_phone
//                          ・ 품목: equipments 배열 diff (id 기준), 모델·시리얼 표시
//                          ・ 변경 사항 없으면 null 반환 (push 안 함)
//                       (4) openDemoLendEditModal: m.originalSnapshot 저장 (diff 계산용 기준값)
//                       (5) submitDemoLend의 edit 분기: newSnapshot ↔ originalSnapshot 비교 후 auditEntry 생성
//                       (6) 활성 대여 테이블에 [이력] 버튼 추가 ([수정] 옆, 회색 outline)
//                       (7) demoHistoryModal: 시간 역순 카드 리스트, 변경 항목 from→to diff 표시
//                          ・ 액션별 배지 색상 (create=초록, update=보라, delete=빨강)
//                          ・ 필드 변경 / 배송지 변경 / 품목 추가·제거 섹션별 표시
//                          ・ 빈 로그(미실행 SQL) 안내 메시지 표시
// v7.42 (2026-06-24) — 데모 대여 사후 수정 (품목/내용/배송지)
//                       (1) 활성 대여 테이블에 [수정] 버튼 추가 ([회수] 옆, 보라 outline)
//                       (2) 편집 진입 함수: openDemoLendEditModal(rental)
//                          ・ getRentalById → 기존 demo_rental_items + demo_equipment 정보 selectedItems로 복원
//                          ・ transaction_log의 DEMO-{shipment_number} 찾아서 배송지 정보 복원
//                          ・ editMode=true, editingRentalId, originalEquipmentIds 셋팅
//                       (3) demo_lend 페이지를 editMode 분기 처리
//                          ・ 페이지 헤더: 아이콘/제목/설명 분기
//                          ・ 저장 버튼: '대여 등록 실행' / '수정 저장'
//                       (4) submitDemoLend()의 editMode 분기 → DemoService.updateRental 호출
//                       (5) DemoService.updateRental(payload): rental + items diff 처리
//                          ・ 추가: demo_rental_items insert + demo_equipment.location → '대여중(업체)'
//                          ・ 제거: demo_rental_items delete + demo_equipment.location → '리버' 복원
//                          ・ 업체명 변경 시 남은 장비들의 location도 동기화
//                          ・ rental_items_text 재생성하여 저장
//                          ・ 다른 곳에서 이미 대여 중인 장비 추가 시도 시 차단
//                       (6) 편집 저장 시 transaction_log의 해당 outbound 행도 동기 업데이트
//                          ・ items / addr / recipient / phone / method / memo 모두 새 값으로
//                          ・ fulfilled 상태는 유지 (이미 출고 처리된 건 그대로)
//                       (7) DemoService.deleteRental(rentalId) 신규 — 향후 통째 취소용 (UI 미연결)
// v7.41 (2026-06-23) — 데모 대여 등록을 배송시트지(transaction_log)에 자동 등록
//                       (1) demoLendModal state에 배송지 필드 추가
//                          ・ address / recipient / recipient_phone / ship_method
//                          ・ ship_method: 택배 / 퀵 서비스 / 방문수령 / 1층배송 / 직접 설치 (5종 토글)
//                       (2) 데모 대여 페이지의 '특이사항' 아래에 '배송 정보' 섹션 추가 (대시드 구분선)
//                       (3) submitDemoLend()에서 createRental 성공 후 transaction_log에 outbound 인서트
//                          ・ reqId: `DEMO-{shipment_number}` (배송시트지에서 식별 가능)
//                          ・ fromDemoLend: true / demoRentalId / demoShipmentNumber 메타 보존
//                          ・ fulfilled: false → 출고 대기 (사용자 Q1=B 선택)
//                          ・ salesStatus: '수기입력' / salesStatusManual: '데모 대여'
//                          ・ items[].serialOptional: true / serialOptionalReason: 'demo_lend'
//                          ・ transaction_log 등록 실패해도 demo_rentals는 보존 (롤백 안 함, 경고만 표시)
//                       (4) openShipRequest의 items 매핑에 _itemSerialOptional 직접 인식 추가
//                          ・ items[i].serialOptional이 명시되면 시리얼 입력 자동 면제
//                          ・ serialOptionalReason: 'item_flag' / 'demo_lend' 등 사용처별 사유 보존
//                       (5) 시리얼 면제 안내 카드에 'demo_lend' 사유 분기 추가
// v7.40 (2026-06-23) — 사이드바 구조 개편 + 재고 대시보드 통합
//                       (1) 입고 등록을 배송시트지 헤더 메뉴 → 사이드바 별도 탭 'inbound'로 이동
//                          ・ 기존 stockInModalOpen 모달을 page='inbound' 페이지로 변환 (backdrop/close 제거)
//                          ・ 시리얼 캐시 로드 useEffect를 page==='inbound' 트리거로 변경
//                          ・ 배송시트지 헤더 메뉴의 [입고 등록] 버튼은 setPage('inbound')로 변경
//                          ・ 사용처 없는 stockInModalOpen state 제거
//                       (2) 재고 대시보드를 재고 목록 페이지의 탭으로 통합
//                          ・ inventoryTab state ('list' | 'dashboard') 추가, 페이지 헤더 아래 토글 UI
//                          ・ stock_dashboard 페이지 조건을 page==='inventory' && inventoryTab==='dashboard'로 변경
//                          ・ 사이드바 nav에서 stock_dashboard 메뉴 제거
//                          ・ 대시보드 로드 useEffect도 inventoryTab='dashboard' 진입 시로 변경
// v7.39 (2026-06-23) — v7.38 시리얼 면제 조건을 'AS' 키워드 명시 시로만 좁힘 (실수 방지)
//                       배경: '수기입력' 매출여부 자체만으로 면제 시, 매출 출고에 실수로 수기입력 두면
//                            시리얼 누락 위험. 'AS' 키워드 명시 강제로 안전성 강화.
//                       조건: common.salesStatus === '수기입력' AND /\bA\/?S\b/i.test(salesStatusManual)
//                            ・ 매칭 예: 'AS', 'as', 'A/S', 'a/s', 'AS 재배송', 'as건', '[AS]'
//                            ・ 비매칭: 'ASUS', 'asset', '거래처 직접 정산', '사은품' 등
//                       ・ serialOptionalReason: 'manual_sales' → 'as_resend' 로 변경
//                       ・ 안내 카드 문구도 'AS 재배송 — 시리얼 면제 출고'로 변경
// v7.38 (2026-06-23) — 매출여부 '수기입력' 출고 시 시리얼 입력 면제 (AS 재배송 케이스)
//                       배경: 판매 → AS 재입고 → 재배송 시, 시리얼이 이미 'shipped' 상태라
//                            출고 모달의 시리얼 검증을 통과할 수 없어 처리 불가였음
//                       해결: openShipRequest의 serialOptional 판정에 '수기입력' 매출여부 조건 추가
//                            기존 serialOptional 메커니즘(NOSN- 자동 prefix, inventory 갱신 스킵) 재활용
//                       ・ models[].serialOptionalReason: 'pattern' (HDD/RAIL) | 'manual_sales' (수기입력)
//                       ・ 안내 카드 텍스트가 사유에 따라 분기 (HDD 안내 vs AS재배송/무상교체 안내)
//                       ・ inventory.status 변경 없음 → 이미 'shipped'인 시리얼 그대로 유지
//                       ・ transaction_log에는 NOSN- prefix로 출고 흔적 기록
// v7.37 (2026-06-23) — 배송 정보에 '직접 설치' 탭 추가
//                       ・ 배송 방법 그리드 5열 → 6열 (택배/퀵/방문수령/1층배송/직접 설치/EMS)
//                       ・ 아이콘 'engineering' + 보라 톤(#7b1fa2) 안내 카드 (방문 일정 안내)
//                       ・ methodLabel 분기에 'install' → '직접 설치' 추가
//                       ・ install은 'courier'/'quick' 외이므로 직발 모드 자동 해제, 송장번호 입력 미표시
// v7.36 (2026-06-23) — 상태 드롭다운 native 화살표 중복 표시 버그 수정
//                       원인: select에 appearance:none을 줘도 일부 브라우저/환경에서
//                            value 변경 후 재렌더 시점에 native 화살표가 다시 보이는 케이스
//                       해결: 보이는 배지는 span으로, 클릭 동작은 절대위치 투명 select로 분리
//                            → 어떤 환경에서도 native UI 노출 차단
// v7.35 (2026-06-23) — 데모 장비 목록 인라인 편집 (상태 / 비고)
//                       ・ 상태 컬럼: 항상 드롭다운 표시 (배지 스타일 유지, 5개 옵션 native select)
//                          정상작동 / 불량 / 수리중 / 점검중 / 확인불가
//                       ・ 비고 컬럼: 클릭 시 텍스트 input 전환, Enter/Blur 저장, Esc 취소
//                       ・ 변경 즉시 저장 → setDemoState로 로컬 상태 갱신 (재로드 불필요)
//                       ・ DemoService.updateEquipmentField(id, field, value): 화이트리스트 검증
// v7.34 (2026-06-23) — 데모 대여 등록 페이지 2단계를 모델별 아코디언으로
//                       ・ 데모 장비 목록 페이지와 동일한 패턴: 모델 그룹 카드 + 펼침
//                       ・ 그룹 헤더 전체 선택 체크박스 (모든 시리얼 한번에 선택/해제)
//                       ・ 그룹 헤더 '체크 N/M 선택' 배지 (일부 선택 indeterminate 표시)
//                       ・ 펼침 시 시리얼별 체크박스 + 시리얼 + model_detail 미리보기
//                       ・ searchAvailableEquipment limit 30 → 500 (그룹화에 필요한 데이터량)
// v7.33 (2026-06-23) — 데모 대여 등록을 모달 → 별도 페이지로 분리
//                       ・ page='demo_lend' 신규 페이지 (시트지 구버전 UI 참조)
//                       ・ 3단계 카드 레이아웃: (1) 대분류 칩 (2) 제품 선택 + 검색 (3) 대여 정보 폼
//                       ・ 2단계+3단계 좌우 분할 (큰 화면 활용)
//                       ・ 담당자/요청자는 STAFF_LIST 드롭다운
//                       ・ 등록 성공 후 1.5초 뒤 데모 관리 페이지로 자동 복귀
//                       ・ 기존 demoLendModal 모달 본체 삭제 (124줄, dead code)
// v7.32 (2026-06-23) — 과거 날짜 출고 등록 버그 수정 (누락 기록 추가 지원)
//                       원인: 출고 등록 시 디폴트 autoDefer=true → sweep이 transaction_date < today면 today로 갱신
//                            사용자가 10일로 등록해도 즉시 23일(today)로 바뀜
//                       해결: 과거 날짜 등록 시 autoDefer 자동 OFF (선택 날짜 유지)
//                       ・ autoDefer = !allFulfilled && !(shipExpectedDate < today())
//                       ・ 예상 출고일 input 아래 안내 배너 (과거 날짜 입력 시 표시)
// v7.31 (2026-06-23) — 검색 정확도 개선
//                       원인: v7.19에서 2자 이하 검색어는 product_name+serial만 검색하도록 제한
//                            → 한글 2자(테온, 이선 등)도 dealer_name/note 매칭 안 됨
//                       해결: 2자 이상은 모든 필드 검색 (1자만 짧은 검색어 유지)
//                       ・ '출고 이력에 추가 매칭됨' 안내 카드 삭제 (사용자 요청 — 노이즈)
// v7.30 (2026-06-23) — 검색에서 시리얼 미입력 출고 요청도 안내
//                       원인: transaction_log 매칭 4건 중 fulfilledSerials가 채워진 건만 inventory에서 보임
//                       해결: 매칭됐지만 시리얼 미입력 상태인 모델을 별도 안내 카드로 표시
//                       ・ 보라색 '출고 이력에 추가 매칭됨' 카드 (검색 결과 상단)
//                       ・ 매칭된 모델명 chips 표시
//                       ・ '배송시트지에서 보기' 1클릭 버튼 (페이지 이동)
//                       ・ txMatchedModels state 추가 (items[].product_name 수집)
// v7.29 (2026-06-23) — 검색 시 transaction_log도 함께 검색 (출고 이력 매칭)
//                       원인: 출고 요청의 배송지/비고는 inventory가 아닌 transaction_log.note에 JSON으로 저장됨
//                       해결: search 모드에서 inventory + transaction_log를 병렬 query
//                       ・ transaction_log.note ILIKE '%검색어%' 매칭된 row의 items[].fulfilledSerials 추출
//                       ・ 그 시리얼들의 product_name도 modelNames에 추가
//                       ・ 그룹화 매칭 분석에 txLogMatch 추가 (우선순위 최상위)
//                       ・ 카드 헤더에 '출고이력 N건' 보라 배지 표시
//                       ・ 비검색 모드에서 txMatchedSerials 자동 리셋
//                       예: '테온' 검색 → 출고요청 비고에 '테온' 들어간 시리얼들 표시
// v7.28 (2026-06-23) — 스마트 검색 (검색어 유형 자동 감지)
//                       ・ 날짜 패턴(2026.6.12, 26-6-12 등) 감지 → receipt_date/sale_date 매칭
//                       ・ 일반 텍스트 → product_name, serial_number, note, memo, dealer_name 매칭
//                       ・ 짧은 검색어(1-2자) → 모델명+시리얼만 (DB 부담 + 노이즈 감소)
//                       ・ 매칭 위치 배지에 '날짜 N건' 추가
//                       ・ filter !== 'all'일 때 안내 배너 + '전체로 변경' 1클릭 버튼
//                       ・ placeholder: 'TS-264, 이선, 2026.6.12'
// v7.27 (2026-06-23) — UX 개선 4가지
//                       (1) 요약 카드 디폴트 접힘 (재고 대시보드 + 데모 관리 statsExpanded: false)
//                       (2) 재고 목록에 stock_type 필터 추가 (전체/신품/리페킹/조립출고)
//                          ・ 일반 query + 검색 query 둘 다 적용, 초기화 시 같이 리셋
//                       (4) 배송시트지 모델명/수량 stepper 위치 정렬
//                          ・ 모델 wrap gap: 4 → 6 (수량 stepper와 통일)
//                          ・ 다중 품목 각 행에 minHeight: 26 추가 (stepper와 동일 높이)
// v7.26 (2026-06-23) — 요약 카드 토글 (재고 대시보드 + 데모 관리)
//                       ・ 두 페이지 상단 요약 카드 영역 위에 [요약 펼치기/접기] 버튼 추가
//                       ・ 접힘 상태일 때는 우측에 한 줄 핵심 요약 표시 (재고/가용/0개 등)
//                       ・ 디폴트는 펼침 (statsExpanded: true)
// v7.25 (2026-06-23) — 데모 관리 핵심 액션 3가지 모달 (장비 추가 / 대여 등록 / 회수 처리)
//                       ・ 페이지 헤더에 [장비 추가] [대여 등록] 버튼 추가 (보라 + 초록 톤)
//                       ・ 활성 대여 테이블에 [회수] 버튼 컬럼 추가
//                       ・ DemoService에 mutation 함수 추가: addEquipment / searchAvailableEquipment / createRental / processReturn
//                       ・ 대여 등록: 카테고리 필터 + 모델/시리얼 검색 + 다중 선택 chips + 자동 OUT-YYYYMMDD-HHMMSS 출고번호
//                       ・ 회수 처리: 자동 RET-... 회수번호 + 장비 location 자동 복원 ('리버')
// v7.24 (2026-06-23) — 데모 관리 장비 목록을 모델별 아코디언으로 개편
//                       ・ 같은 모델끼리 그룹화 (예: TS-453D 2대 → 1개 카드로 합침)
//                       ・ 카드 헤더에 카테고리 + 모델명 + 가용/대여/점검 카운트 + 총 개수
//                       ・ 클릭 펼침 → 그 모델의 시리얼 목록 테이블 (시리얼·상태·구성품·위치·비고·추가일)
//                       ・ '모두 펼치기' / '모두 접기' 단축 버튼
//                       ・ DemoService.js의 supabase import 경로 수정 (../supabaseClient → ../supabase)
// v7.23 (2026-06-23) — 데모 장비 대여 관리 페이지 (조회 단계)
//                       ・ 사이드바에 '데모 관리' 메뉴 추가 (보라 톤)
//                       ・ DemoService.js 신규 (listEquipment, getStats, listActiveRentals, listReturns)
//                       ・ 페이지 구성: 요약 카드 4개 + 3개 탭 (장비목록 / 활성대여 / 회수이력)
//                       ・ 장비 목록: 카테고리 칩 필터 + 위치 필터 + 검색 + 500 limit
//                       ・ 활성 대여: 지연(회수예정 경과) 빨간 배지
//                       ・ 다음 v7.24: 대여 등록 / 회수 처리 / 장비 추가 모달
// ─── 앱 버전 — 사이드바 footer 표시 ───
// v7.22 (2026-06-23) — 재고 대시보드 시트지에 stock_type 분포 표시
//                       ・ 모델명 아래 작은 글씨로 '신 N · 리 N · 조 N' 표시 (재고 있을 때만)
//                       ・ 색상: 신품 초록 / 리페킹 주황 / 조립출고 보라 (0이면 회색)
//                       ・ InventoryService.getInStockProductCounts 반환 형식 변경
//                          ・ 기존: { product_name: count }
//                          ・ 변경: { counts: {...}, stockTypeCounts: { product_name: {신품, 리페킹, 조립출고} } }
// v7.21 (2026-06-23) — 재고 대시보드 시트지 그리드에 헤더 줄 추가
//                       ・ 각 컬럼: 모델명 | 재고 | 킵 | ★ 가용 (초록)
//                       ・ 헤더는 회색 배경 + 굵은 글씨 (의미 한눈에 파악)
// v7.20 (2026-06-23) — RMA/DOA UI 4가지 개선
//                       (1) EMS 송장번호를 배송지 셀 → 발송종류 셀 아래로 이동 (정보 정합성)
//                       (2) 모든 출고 요청 디폴트를 '출고대기'로 (이전: EMS 양식만 모드는 자동 fulfilled)
//                          - allFulfilled = false 고정, 사용자가 처리 셀에서 수동 토글
//                       (3) 수리완료 배지 색상 청록(#00668a) → 초록(#009844)
//                       (4) RMA/DOA 매출여부를 자동 '수기입력' + 'DOA'/'RMA' 라벨로 — 사용자가 셀 더블클릭으로 변경 가능
// v7.19 (2026-06-23) — 재고 목록 검색 성능 최적화
//                       ・ 짧은 검색어(2자 이하)는 서버에서 모델명+시리얼만 검색 (note/dealer 제외 → DB 부담 1/2)
//                       ・ 그룹화 forEach 안에서 매칭 위치 분석 early exit (모델 매칭이면 다른 체크 skip)
//                       ・ note + memo를 한 string으로 합쳐 toLowerCase 1번만 호출
//                       ・ 평균 toLowerCase 호출 4회 → 1~2회로 감소 (50-75% 절감)
// v7.18 (2026-06-23) — 재고 목록 검색 결과 정확도 개선
//                       ・ 매칭된 시리얼만 표시 (예: '400' 검색 시 시리얼 중 400 포함된 row만)
//                       ・ 단, 모델명 자체가 매칭되면 그 모델 전체 시리얼 표시 (의도된 모델 검색)
//                       ・ 그룹 카드 헤더에 매칭 위치 배지 추가:
//                          - '모델명 일치' (초록) / '시리얼 N건' (파랑) / '비고 N건' (보라) / '거래처 N건' (다홍)
//                       ・ 검색 결과 카운트 표시: 실제 표시 개수 + (전체 fetch N건) 안내
// v7.17 (2026-06-22) — 재고 대시보드에 카테고리별 필터 추가
//                       ・ 검색/정렬 카드 안에 카테고리 칩 줄 추가 (전체 + 11개 카테고리)
//                       ・ 각 칩에 해당 카테고리 제품 수 표시 (예: NAS (52))
//                       ・ 카테고리별 고유 색상 (NAS 초록 / HDD 다홍 / Memory 마젠타 등)
//                       ・ inventory.category 기준 매핑 — 같은 product_name은 첫 row 카테고리 사용
//                       ・ InventoryService.getProductCategoryMap 추가
// v7.16 (2026-06-22) — 이벤트 캘린더에 RMA/DOA 별도 카테고리 표시 (v7.15 보류 작업 완료)
//                       ・ 필터 칩에 'RMA/DOA' 추가 (다홍색 #d84315, 입고/출고/취소/환불과 동급)
//                       ・ 색상 분리: 일반 출고(빨강) vs RMA(다홍) vs 수리완료(청록 #00695c)
//                       ・ 라벨에 emsType 표시 (RMA / DOA / RMA 수리완료 / DOA 수리완료)
//                       ・ 카운트 5분류 — 입고/출고/RMA/취소/환불
// v7.15 (2026-06-22) — 입고/출고 처리 UI 4가지 개선
//                       (1) 입고 행에서 '출고요청/자동이월/출고완료/수리완료' 배지 모두 숨김 (!isIn 조건)
//                       (2) RMA/DOA 처리 셀에 '출고완료/출고대기' 토글 버튼 추가 (수리 완료 위에 작게)
//                          ・ toggleRmaFulfilled 함수 — note.fulfilled flag만 변경 (재고 영향 X)
//                       (3) 이벤트 캘린더 RMA/DOA 표시 — EventCalendar.js 별도 파일 필요 (보류)
//                       (4) 입고 시리얼 chip에 ❌ 개별 삭제 + [+ 시리얼] 추가 버튼
//                          ・ addInboundSerial / removeInboundSerial 함수
//                          ・ InventoryService.deleteBySerial — in_stock 상태만 삭제 허용 (안전장치)
// v7.14 (2026-06-22) — 임시 시리얼 일괄 입고 모달 (HDD/SSD 등 라벨 모를 때)
//                       ・ UNK-{모델코드}-{4자리연번} 형식 자동 생성 (예: UNK-ST2000DM008-0001)
//                       ・ 카탈로그 헤더에 '임시 시리얼 입고' 버튼 추가 (주황 톤)
//                       ・ 미리보기, 시작 번호 자동 추천 (DB의 마지막 번호+1), 중복 체크
//                       ・ inventory bulk insert + transaction_log 1행 (note에 isUnknown flag)
//                       ・ 미래 실제 시리얼 교체: 배송시트지에서 시리얼 chip 더블클릭 (v7.6 기능 재활용)
// v7.13 (2026-06-22) — 가용재고 현황 모달 + 버튼 삭제 (재고 대시보드 페이지로 통합)
//                       ・ 재고 목록 페이지 헤더의 '📦 가용재고 현황' 버튼 제거
//                       ・ 모달 본체 코드 전체 삭제 + stockSummary state 제거
//                       ・ products 카테고리 자동 분류 SQL 별도 제공
// v7.12 (2026-06-22) — 출고 요청 페이지 배송 정보 카드 폼 순서 재배치
//                       ・ 배송 방법을 카드 최상단으로 이동 (배송 방식 먼저 결정 → 관련 정보 입력 흐름)
//                       ・ 새 순서: 배송 방법 → 직발 모드 → 송장/퀵 가격 → 배송지 주소 → 수령인+연락처 → 작성자+요청자 → 비고
//                       ・ 일관성: 관련 정보를 위에서 아래로 흐름
// v7.11 (2026-06-22) — 재고 대시보드 신규 페이지 — 시트지 형식 전체화면 뷰
//                       ・ 사이드바에 [재고 대시보드] 메뉴 항목 추가 (재고 목록 아래)
//                       ・ 페이지: 5개 요약 카드(재고/킵/가용/0개/가용부족) + 3컬럼 시트지 그리드
//                       ・ 카탈로그 등록 모든 제품 표시 (0개도 노란 강조)
//                       ・ 정렬 4종 (가용 적은순/재고 많은순/가나다/0개 우선) + 0개 숨기기 토글
//                       ・ 검색 + 색상 강조 (재고0=노랑/가용부족=빨강/가용주의=주황)
// v7.10 (2026-06-22) — 가용재고 현황 모달 — 재고 목록 페이지 우측 상단
//                       ・ 헤더에 [📦 가용재고 현황] 버튼 추가 (이벤트 캘린더 옆)
//                       ・ 모달: 총 재고/활성 킵/★출고 가능/제품 종류 요약 카드
//                       ・ 제품별 리스트 (재고/킵/가용) — 가용 적은 순 정렬, 부족 빨강·주의 주황 표시
//                       ・ 검색 input + 실시간 필터
//                       ・ InventoryService.getInStockProductCounts() 추가
// v7.9 (2026-06-22) — 킵 삭제·품목추가 스크롤 버그 + 입고 기록 통째 삭제 기능
//                      ・ deleteKeep/submitAddItem/updateKeepGroupField: loadKeeps() 제거 → setKeepsItems 로컬 patch
//                      ・ submitAddItem: insert().select().single() — 새 row 즉시 array에 push
//                      ・ 입고 상세 모달에 [입고 기록 삭제] 버튼 추가 (빨강, 좌측 정렬)
//                      ・ deleteInboundRecord: in_stock 시리얼만 삭제 (출고/판매불가 보호) + transaction_log row 동시 삭제
// v7.8 (2026-06-22) — 킵 관리 수량 변경 시 페이지 맨 위로 이동 버그 수정
//                      ・ updateKeepField: loadKeeps() 호출 제거 → setKeepsItems 로컬 patch (스크롤 유지)
//                      ・ DB 업데이트도 한 번에 통합 (qty + serials + serial_number 동시) — race 방지
//                      ・ stepper +/- 버튼에 type="button" 추가 (form submit 안전장치)
// v7.7 (2026-06-22) — 카탈로그 페이지에 일괄 제품 등록 모달 추가
//                      ・ 텍스트박스 (한 줄에 하나) → ProductService.bulkInsert + 자동 중복 제거 + 결과 메시지
//                      ・ 카탈로그 헤더 "+ 일괄 등록" 버튼 추가
// v7.6 (2026-06-22) — 입고 행 시리얼 가로 나열 + 더블클릭 편집 + 부분/전체 토글
//                      ・ 입고 행 5번 셀(배송지)을 grid-column span 5로 확장 → 6~9번 셀(매출/발송/수령/작성자) display:none
//                      ・ 시리얼 chip 가로 나열 (제품명별로 그룹) — 초록 톤
//                      ・ 더블클릭으로 시리얼 인라인 편집 → InventoryService.renameSerial (inventory PK rename)
//                        + transaction_log.note 안 items[].serials 동기 업데이트 (optimistic lock)
//                      ・ 부분보기(5개) / 전체보기 토글 + 품목별 "+N개" 더보기 버튼
//                      ・ InventoryService.renameSerial 추가
// v7.5 (2026-06-22) — Service 레이어 2단계: InventoryService 추가 (가장 빈번한 도메인)
//                      ・ services/InventoryService.js — 16개 함수 (단건 조회, 카운트, 검색,
//                        시리얼 자동완성, optimistic lock 출고/복귀/수리완료, bulk insert, rename 등)
//                      ・ App.js의 inventory supabase 직접 호출 34곳 → Service 호출로 격리
//                      ・ loadInventory 검색 query 3곳은 UI 로직 강결합으로 raw 유지
//                      ・ countAll은 {ok, count} 반환 (앱 시작 시 connect 상태 판별 용)
//                      ・ 다음 단계: KeepService / TransactionService
// v7.4 (2026-06-22) — Service 레이어 도입 (1단계): 책임별 모듈 분리로 LLM 작업 토큰 절약
//                      ・ services/AliasService.js — product_aliases CRUD (7개 함수)
//                      ・ services/MemoService.js — memos CRUD + optimistic lock (9개 함수)
//                      ・ services/ProductService.js — products CRUD + 검색 (12개 함수)
//                      ・ App.js의 supabase 직접 호출 40개 → Service 메서드로 격리
//                      ・ namespace object 패턴 (export const XxxService = { ... })
// v7.3 (2026-06-22) — 입고 로그를 한 번 등록당 transaction_log 1 row로 통합 (출고와 동일한 items[] 구조)
// v7.2 (2026-06-19) — 매출여부/퀵매출여부 인라인 편집 race condition 수정 (await로 순차 처리)
// v7.1 (2026-06-19) — 외부/시리얼면제 제품 출고 처리 모달 단순화
// v7.0 (2026-06-19) — 카탈로그 단순화, 장부 표준명(alias) 강제, Keep 시스템 테이블 UI 재구성
const APP_VERSION = 'v7.459';
const APP_VERSION_DATE = '2026-08-19';
const sortByStockType = (a, b) => (STOCK_TYPE_ORDER[a.stock_type]??99) - (STOCK_TYPE_ORDER[b.stock_type]??99);
// v7.459: 재고 그룹(검색) 내 시리얼 정렬 — 선택 컬럼(serial/receipt_date/sale_date/status) 기준.
//   동률이면 기존 재고유형 순서로 폴백. 숫자 포함 시리얼은 자연 정렬(numeric).
const invRowCompare = (key, dir) => {
  const mul = dir === 'asc' ? 1 : -1;
  const val = (x) => key === 'serial' ? (x.serial || '')
    : key === 'receipt_date' ? (x.receipt_date || '')
    : key === 'sale_date' ? (x.sale_date || '')
    : key === 'status' ? (x.status || '') : '';
  return (a, b) => {
    if (!key || key === 'model' || key === 'category') return sortByStockType(a, b);
    const av = val(a), bv = val(b);
    if (av === bv) return sortByStockType(a, b);
    if (av === '' ) return 1;   // 빈 값은 항상 뒤로
    if (bv === '' ) return -1;
    return mul * String(av).localeCompare(String(bv), undefined, { numeric: true });
  };
};

// 메모 본문 렌더링 — **볼드**, [#hex]색상[/] 문법 지원
const renderMemoContent = (text, defaultColor) => {
  if (!text) return null;
  // 토큰화: **...**, [#hex]...[/], 일반 텍스트
  // v7.186: `.+?` → `[\s\S]+?` — 개행 포함 매치 (여러 라인 걸친 색상 태그가 raw로 보이던 문제)
  const tokens = [];
  const regex = /(\*\*([^*]+)\*\*|\[(#[0-9a-fA-F]{3,8})\]([\s\S]+?)\[\/\])/g;
  let lastIndex = 0;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > lastIndex) tokens.push({ type: 'text', value: text.slice(lastIndex, m.index) });
    if (m[2] !== undefined) tokens.push({ type: 'bold', value: m[2] });
    else if (m[3]) tokens.push({ type: 'color', color: m[3], value: m[4] });
    lastIndex = regex.lastIndex;
  }
  if (lastIndex < text.length) tokens.push({ type: 'text', value: text.slice(lastIndex) });
  return tokens.map((t, i) => {
    if (t.type === 'bold') return <strong key={i}>{t.value}</strong>;
    if (t.type === 'color') return <span key={i} style={{color: t.color, whiteSpace:'pre-wrap'}}>{t.value}</span>;
    return <span key={i}>{t.value}</span>;
  });
};

// v7.194: 카카오(다음) 우편번호 서비스 lazy loader
//         첫 [주소검색] 클릭 시에만 스크립트 삽입 (초기 번들 부담 없음)
//         무료·key 불필요·CORS OK · 팝업 방식
let daumPostcodeLoading = null;
async function loadDaumPostcode() {
  if (typeof window !== 'undefined' && window.daum && window.daum.Postcode) return true;
  if (daumPostcodeLoading) return daumPostcodeLoading;
  daumPostcodeLoading = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = '//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js';
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { daumPostcodeLoading = null; reject(new Error('카카오 우편번호 서비스 로드 실패')); };
    document.head.appendChild(s);
  });
  return daumPostcodeLoading;
}
// 카카오 postcode 팝업 열기 (oncomplete: {zonecode(5), address, addressType, buildingName, ...})
async function openDaumPostcode(oncomplete) {
  try {
    await loadDaumPostcode();
    // eslint-disable-next-line no-undef
    new window.daum.Postcode({ oncomplete }).open();
  } catch (e) {
    alert('우편번호 서비스 로드 실패: ' + (e.message || e));
  }
}


//         glowing=true 이고 채워진 별에 starTwinkle 애니메이션 (각 별 delay 시차)
//         hover 프리뷰 없이 클릭만 지원 (touch 친화적)
function StarRating({ value = 0, onChange, size = 14, glowing = true, gap = 2, readOnly = false }) {
  const [hover, setHover] = React.useState(0);
  const displayVal = hover || value;
  return (
    <div style={{display:'inline-flex', alignItems:'center', gap}}>
      {[1,2,3,4,5].map(n => {
        const filled = n <= displayVal;
        return (
          <span key={n}
            onClick={(e) => {
              if (readOnly || !onChange) return;
              e.stopPropagation();
              // 같은 값 재클릭 시 0으로 리셋
              onChange(value === n ? 0 : n);
              // 클릭 이펙트: 짧은 pop
              const el = e.currentTarget;
              el.style.animation = 'none';
              // reflow trigger
              // eslint-disable-next-line no-unused-expressions
              el.offsetHeight;
              el.style.animation = 'starPop 0.35s ease-out';
            }}
            onMouseEnter={() => { if (!readOnly) setHover(n); }}
            onMouseLeave={() => { if (!readOnly) setHover(0); }}
            style={{
              cursor: readOnly ? 'default' : 'pointer',
              display: 'inline-flex',
              lineHeight: 0,
              userSelect: 'none',
              // 채워진 별만 반짝임 (각 별 delay 시차로 흐르는 효과)
              animation: (glowing && filled && !readOnly) ? `starTwinkle 2.4s ease-in-out ${(n - 1) * 0.18}s infinite` : 'none',
              transformOrigin: 'center',
              transition: 'transform 0.15s',
            }}>
            <span className="material-symbols-outlined"
              style={{
                fontSize: size,
                fontVariationSettings: filled ? `'FILL' 1, 'wght' 500` : `'FILL' 0, 'wght' 400`,
                color: filled ? '#f59e0b' : '#cbd5e1',
                display: 'block',
              }}>star</span>
          </span>
        );
      })}
    </div>
  );
}

// ─── 메모 편집 (WYSIWYG) ───
// v7.196: contentEditable 캐럿 위치를 "텍스트 문자 오프셋"으로 직렬화 / 복원
//   undo 시 innerHTML을 통째로 갈아끼우므로 Range 객체는 무효화된다.
//   순수 텍스트 기준 오프셋으로 저장했다가 복원하면 서식이 바뀌어도 커서가 유지된다.
// v7.215: focus()와 Range 설정은 브라우저가 대상 요소를 화면에 보이게 하려고
//   조상 요소들을 스크롤시킨다. 편집 진입 시 페이지가 맨 위로 튀는 원인.
//   진입 직전 스크롤 위치를 모두 기억했다가 되돌린다.
const captureScrolls = (el) => {
  const list = [];
  let n = el && el.parentElement;
  while (n) { list.push([n, n.scrollTop, n.scrollLeft]); n = n.parentElement; }
  const root = document.scrollingElement || document.documentElement;
  if (root) list.push([root, root.scrollTop, root.scrollLeft]);
  return list;
};
const restoreScrolls = (list) => {
  for (const [n, top, left] of list) {
    if (n.scrollTop !== top) n.scrollTop = top;
    if (n.scrollLeft !== left) n.scrollLeft = left;
  }
};

const getCaretOffset = (el) => {
  try {
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return null;
    const range = sel.getRangeAt(0);
    if (!el.contains(range.endContainer)) return null;
    const pre = range.cloneRange();
    pre.selectNodeContents(el);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
  } catch (e) { return null; }
};

const setCaretOffset = (el, offset) => {
  if (offset == null) return;
  try {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    let remaining = offset, node = null, nodeOffset = 0;
    while (walker.nextNode()) {
      const len = walker.currentNode.nodeValue.length;
      if (remaining <= len) { node = walker.currentNode; nodeOffset = remaining; break; }
      remaining -= len;
    }
    const range = document.createRange();
    if (node) range.setStart(node, nodeOffset);
    else { range.selectNodeContents(el); range.collapse(false); }
    range.collapse(true);
    const sel = window.getSelection();
    sel.removeAllRanges();
    sel.addRange(range);
  } catch (e) {}
};

// 마크업(`**bold**`, `[#hex]text[/]`) → HTML (contenteditable에 표시)
// v7.222: 배차 시트 '배차시간' 표기 형식 — '2026. 1. 2 오전 8:53:41'
//   ・ 이 값이 행 매칭 키다. 1차/2차가 한 글자라도 다르면 새 행이 생긴다.
//   ・ toLocaleString('ko-KR')은 '2026. 7. 28. 오전 9:12:00'처럼 일(日) 뒤에 점이 붙어
//     시트 표기와 달라지므로 직접 조립한다.
//   ・ 월/일/시는 0을 채우지 않고, 분/초만 2자리로 맞춘다 (시트 실제 표기와 동일)
//   ・ UTC 함정 회피: getFullYear/getMonth/getDate 등 로컬 게터만 사용
const fmtSheetDateTime = (input) => {
  const dt = input instanceof Date ? input : new Date(input);
  if (!dt || isNaN(dt.getTime())) return '';
  const h24 = dt.getHours();
  const ampm = h24 < 12 ? '오전' : '오후';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const p2 = (n) => String(n).padStart(2, '0');
  return `${dt.getFullYear()}. ${dt.getMonth() + 1}. ${dt.getDate()} ${ampm} ${h12}:${p2(dt.getMinutes())}:${p2(dt.getSeconds())}`;
};

// v7.243: 관내 판정 — 사무실이 있는 구로 가는 건은 지역 칸에 '관내'로 적는다.
//   기사 요금 기준이 '관내/관외'로 갈리는데, 지금까지는 '서울시 용산구'로 자동 추출된 걸
//   손으로 고쳐 넣고 있었다. 자동값 자체를 '관내'로 만들어 손질을 없앤다.
//   구를 늘릴 일이 생기면 HOME_DISTRICTS 에만 추가하면 된다.
const HOME_DISTRICTS = ['용산구'];
const HOME_REGION_LABEL = '관내';
//   ⚠ '중구'처럼 여러 시에 같은 이름이 있는 구를 나중에 넣을 수 있으므로,
//     시·도 접두어가 서울이거나 아예 없을 때만 관내로 본다.
//     ('부산광역시 중구'가 관내로 잡히는 사고 방지)
const isHomeDistrict = (tokens, i) => {
  if (!HOME_DISTRICTS.includes(tokens[i])) return false;
  const prefix = tokens.slice(0, i).join(' ');
  return prefix === '' || /^서울/.test(prefix);
};

// v7.219: 배송 시트 '지역' 칸 — 주소 앞부분부터 '구'까지 잘라낸다.
//   '서구'만으론 대전/부산/인천/광주 구분이 안 되므로 시·도 접두어를 포함한다.
//   '대전광역시 서구 도산로 403번길 16 …' → '대전광역시 서구'
//   '서울시 금천구 가산동 60-9 (디지털로 10길 37) …' → '서울시 금천구'
//   '경기도 부천시 원미구 중동로 248' → '경기도 부천시 원미구'
//   구가 없으면 군 → 시 순으로 대체 ('강원도 평창군', '경기도 김포시').
// v7.243: 관내(용산구)는 구 이름 대신 '관내' 반환.
const guessRegion = (addr) => {
  if (!addr) return '';
  const clean = String(addr).replace(/^\[[^\]]*\]\s*/, '').trim(); // [방문수령] 등 라벨 제거
  const tokens = clean.split(/[\s,]+/).filter(Boolean);
  const findIdx = (re) => tokens.findIndex(t => re.test(t));
  let i = findIdx(/^[가-힣]+구$/);
  if (i < 0) i = findIdx(/^[가-힣]+군$/);
  if (i < 0) i = findIdx(/^[가-힣]+시$/);
  if (i < 0) return '';
  if (isHomeDistrict(tokens, i)) return HOME_REGION_LABEL;   // v7.243
  return tokens.slice(0, i + 1).join(' ');
};

const escapeHtml = (s) => String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
const markupToHtml = (text) => {
  if (!text) return '';
  let html = escapeHtml(text);
  // 볼드 (escape된 토큰)
  html = html.replace(/\*\*([^*]+)\*\*/g, '<b>$1</b>');
  // 색상
  // v7.186: `.+?` → `[\s\S]+?` — 개행 걸친 색상 태그 지원 (이전엔 여러 라인 span이 매치 실패해서
  //         편집기 진입 시 [#hex]...[/] 마커가 raw 텍스트로 노출됨)
  html = html.replace(/\[(#[0-9a-fA-F]{3,8})\]([\s\S]+?)\[\/\]/g, '<span style="color:$1">$2</span>');
  // 줄바꿈
  html = html.replace(/\n/g, '<br>');
  return html;
};
// rgb(r,g,b) / #hex / 컬러 이름 → #hex
const colorToHex = (color) => {
  if (!color) return null;
  const trimmed = String(color).trim();
  if (trimmed.startsWith('#')) return trimmed.length === 4 ? '#'+trimmed.slice(1).split('').map(c=>c+c).join('') : trimmed;
  const rgb = trimmed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
  if (!rgb) return null;
  const toHex = n => Number(n).toString(16).padStart(2,'0');
  return '#' + toHex(rgb[1]) + toHex(rgb[2]) + toHex(rgb[3]);
};
// contenteditable HTML → 마크업 (DB 저장용)
const htmlToMarkup = (html) => {
  if (!html) return '';
  const temp = document.createElement('div');
  temp.innerHTML = html;
  const walk = (node, isFirstChild = true) => {
    if (node.nodeType === 3) return node.nodeValue || ''; // 텍스트
    if (node.nodeType !== 1) return '';
    const tag = node.tagName.toLowerCase();
    const childrenStr = Array.from(node.childNodes).map((c,i)=>walk(c, i===0)).join('');
    if (tag === 'b' || tag === 'strong') return childrenStr ? `**${childrenStr}**` : '';
    if (tag === 'span') {
      const hex = colorToHex(node.style.color);
      return (hex && childrenStr) ? `[${hex}]${childrenStr}[/]` : childrenStr;
    }
    if (tag === 'font') {
      // execCommand('foreColor')가 Firefox/구버전 Chrome에서 만드는 태그
      const color = node.getAttribute('color') || node.style.color;
      const hex = colorToHex(color);
      return (hex && childrenStr) ? `[${hex}]${childrenStr}[/]` : childrenStr;
    }
    if (tag === 'br') return '\n';
    if (tag === 'div' || tag === 'p') {
      // 블록 요소: 첫 번째가 아니면 줄바꿈 prefix (단, 안에 br만 있으면 그대로)
      return (isFirstChild ? '' : '\n') + childrenStr;
    }
    return childrenStr;
  };
  let result = walk(temp);
  // 연속된 3개 이상 줄바꿈 정리
  return result.replace(/\n{3,}/g, '\n\n');
};

const fmtDate = d => d ? d.replace(/-/g, '.') : '-';
const fmtTime = ts => { if(!ts) return ''; try { const d = new Date(ts); const h = String(d.getHours()).padStart(2,'0'); const m = String(d.getMinutes()).padStart(2,'0'); return `${h}:${m}`; } catch(e){ return ''; } };
// v7.382: 이월 정보 — 최초 등록일과 이월 일수.
//   ★ 이월(sweepAutoDefer)은 transaction_date 만 갱신하고 행은 그대로 둔다.
//     그래서 created_at 에 [최초 등록 시각] 이 그대로 남아 있다. 별도 컬럼이 필요 없다.
//   ⚠ created_at 은 timestamptz 다. toISOString() 을 쓰면 UTC 라 오전 9시 전에
//     하루 전으로 밀린다 — 반드시 로컬 getFullYear/getMonth/getDate 로 만든다 (v7.126 과 같은 함정).
//   ⚠ created_at 이 transaction_date 보다 [뒤] 인 경우는 이월이 아니다.
//     과거 날짜로 누락 기록을 추가한 건(v7.32)이 여기 해당한다 — 표시하면 거짓말이 된다.
const deferInfoOf = (item) => {
  if (!item || !item.created_at || !item.transaction_date) return null;
  let d;
  try { d = new Date(item.created_at); } catch (e) { return null; }
  if (isNaN(d.getTime())) return null;
  const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  if (iso >= item.transaction_date) return null;   // 이월된 게 아니다
  // 경과 일수 — 문자열 날짜를 로컬 정오로 파싱해 DST/타임존 영향을 없앤다
  const toNoon = (ymd) => { const [y, m, dd] = ymd.split('-').map(Number); return new Date(y, m - 1, dd, 12, 0, 0); };
  const days = Math.round((toNoon(item.transaction_date) - toNoon(iso)) / 86400000);
  return { origDate: iso, days };
};

// v7.126: 로컬 타임존 기준 YYYY-MM-DD (기존 toISOString은 UTC라 한국 오전 9시 전 어제 날짜 반환 버그)
const today = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// v7.82: 출고 처리 pending 시리얼 localStorage 유틸 — 새로고침 후 복원
const SHIP_PENDING_STORAGE_KEY_PREFIX = 'inventura_ship_pending_v1_';
const SHIP_PENDING_EXPIRY_MS = 7 * 24 * 60 * 60 * 1000; // 7일
const saveShipPending = (reqId, pending) => {
  if (!reqId) return;
  try {
    localStorage.setItem(
      SHIP_PENDING_STORAGE_KEY_PREFIX + reqId,
      JSON.stringify({ pending, savedAt: new Date().toISOString() })
    );
  } catch(e) {}
};
const loadShipPending = (reqId) => {
  if (!reqId) return null;
  try {
    const raw = localStorage.getItem(SHIP_PENDING_STORAGE_KEY_PREFIX + reqId);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.savedAt) {
      const age = Date.now() - new Date(parsed.savedAt).getTime();
      if (age > SHIP_PENDING_EXPIRY_MS) {
        localStorage.removeItem(SHIP_PENDING_STORAGE_KEY_PREFIX + reqId);
        return null;
      }
    }
    return parsed;
  } catch(e) { return null; }
};
const clearShipPending = (reqId) => {
  if (!reqId) return;
  try { localStorage.removeItem(SHIP_PENDING_STORAGE_KEY_PREFIX + reqId); } catch(e) {}
};
const cleanupStaleShipPending = () => {
  try {
    const keys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(SHIP_PENDING_STORAGE_KEY_PREFIX)) keys.push(k);
    }
    const now = Date.now();
    for (const k of keys) {
      try {
        const raw = localStorage.getItem(k);
        if (!raw) continue;
        const parsed = JSON.parse(raw);
        if (parsed?.savedAt) {
          const age = now - new Date(parsed.savedAt).getTime();
          if (age > SHIP_PENDING_EXPIRY_MS) localStorage.removeItem(k);
        }
      } catch(e) {}
    }
  } catch(e) {}
};

// ─── 시리얼 면제 제품 판단 ───
// v7.57: HDD/SSD 패턴 면제 제거 (HDD/SSD도 진짜 시리얼로 관리하도록 변경)
// v7.70: RAIL 패턴 면제도 제거 (RAIL 액세서리도 실 시리얼 관리로 전환)
//        → 자동 패턴 매칭은 모두 사라짐. isSerialOptional은 이제 항상 false.
//        (함수 시그니처는 호출처 다수라 유지. 사용처: 재고 입고, 카탈로그 조회 등)
//        실제 serialOptional 판정은 items[i].serialOptional 명시 또는
//        데모 대여(demo_lend) / AS 재발송(as_resend) 등 명시적 사유 케이스만 남음
const isSerialOptional = (category, productName) => {
  return false;
};

// UUID v4 생성 — secure context 여부와 무관하게 동작 (사내 IP/HTTP 환경 대응)
const uuidv4 = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
      const buf = new Uint8Array(16);
      crypto.getRandomValues(buf);
      buf[6] = (buf[6] & 0x0f) | 0x40; // version 4
      buf[8] = (buf[8] & 0x3f) | 0x80; // variant 10
      const hex = Array.from(buf).map(b => b.toString(16).padStart(2, '0')).join('');
      return `${hex.slice(0,8)}-${hex.slice(8,12)}-${hex.slice(12,16)}-${hex.slice(16,20)}-${hex.slice(20)}`;
    }
  } catch (e) { /* fallthrough */ }
  // 최후의 fallback (Math.random — 약함, 그래도 충돌 가능성은 매우 낮음)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

function Badge({ children, variant = 'default' }) {
  const s = { default:{bg:'#e5eeff',c:'#45464d'}, stock:{bg:'rgba(0,152,68,0.1)',c:'#009844'}, shipped:{bg:'rgba(0,102,138,0.1)',c:'#00668a'}, error:{bg:'rgba(186,26,26,0.1)',c:'#ba1a1a'}, unsellable:{bg:'rgba(245,124,0,0.12)',c:'#e65100'}, category:{bg:'#dce9ff',c:'#004d6a'}, reserved:{bg:'rgba(64,194,253,0.15)',c:'#0277bd'}, keep:{bg:'rgba(123,31,162,0.12)',c:'#7b1fa2'}, hold:{bg:'rgba(245,124,0,0.12)',c:'#e65100'} }[variant] || {bg:'#e5eeff',c:'#45464d'};
  return <span style={{display:'inline-flex',alignItems:'center',padding:'3px 12px',borderRadius:9999,fontSize:12,fontWeight:500,background:s.bg,color:s.c,whiteSpace:'nowrap'}}>{children}</span>;
}
// v7.260: 유리 카드 배경을 변수화. 이 한 줄이 <Card> 49곳을 한꺼번에 따라오게 한다.
//   (나머지 rgba() 는 4단계로 미룸 — 여기만 안 고치면 다크에서 흰 카드가 49개 남는다)
// v7.348: 사이드바 [관리] 아코디언에 묶인 페이지들. 순서 = 하위 메뉴 순서이고
//   [0] 번이 [관리] 를 눌렀을 때 들어가는 기본 화면이다.
//   ⚠ 모듈 스코프에 둔다. 컴포넌트 안에 두면 nav/navFlat 보다 뒤에 선언될 위험이 있다 (TDZ).
// v7.351: NAS 모델명에서 시리즈를 뽑는다.
//   TS-453E-8G → TS · TVS-h874T → TVS · TS-h1887XU-RP-E2336-32G → TS
//   ⚠ 첫 하이픈 앞의 영문만 본다. 'h1887XU' 같은 중간 토큰까지 보면 시리즈가 수십 개로 쪼개진다.
const nasSeries = (name) => {
  const m = String(name || '').trim().match(/^([A-Za-z]+)/);
  return m ? m[1].toUpperCase() : '기타';
};

// v7.353: 모델명에서 사양을 뽑는다. 카탈로그에 스펙 컬럼이 없어서 이름이 유일한 단서다.
//   실제 모델명 29개로 검증했다 (TS-h1887XU → 18베이, TS-932PX → 9베이까지 맞음).
//   ⚠ 어디까지나 이름 규칙 추정이다. 호환성 데이터가 붙으면 그쪽 값이 우선이어야 한다.
const nasBays = (name) => {
  // 앞 숫자가 4자리면 앞 2자리가 베이 수 (1264 → 12), 3자리면 첫 자리 (453 → 4)
  const m = String(name || '').match(/^[A-Za-z]+-[Hh]?(\d{3,4})/);
  if (!m) return null;
  const d = m[1];
  return d.length === 4 ? parseInt(d.slice(0, 2), 10) : parseInt(d[0], 10);
};
const nasRam = (name) => {
  const m = String(name || '').match(/-(\d+)G(?:-KR)?$/);
  return m ? m[1] + 'GB' : null;
};
// 랙마운트 판별: 모델 토큰이 U 로 끝난다 (1264U · 431XEU · 855eU · h1090FU)
//   ⚠ 'RP'(이중전원)만 보면 TS-431XEU 같은 1U 모델을 놓친다.
const nasRack = (name) => {
  const t = String(name || '').split('-')[1] || '';
  return /U$/i.test(t);
};
const nasRedundantPsu = (name) => /-RP(?:-|$)/i.test(String(name || ''));

// 시리즈별 아이콘 — 없으면 dns 로 떨어진다
const SERIES_ICON = {
  TS: 'dns', TVS: 'storage', TBS: 'memory', TES: 'lan',
  QGD: 'router', QSW: 'hub', QNA: 'cable', QXP: 'settings_input_component',
};

const ADMIN_PAGES = ['catalog', 'audit', 'backup'];
const ADMIN_META = {
  catalog: { icon: 'menu_book', label: '제품 표준화 관리' },
  audit: { icon: 'fact_check', label: '재고 실사' },
  backup: { icon: 'cloud_download', label: '백업' },
};

// v7.348: 관리 3개 화면 위에 붙는 하위 이동 바.
//   사이드바 아코디언을 접어 두면 하위끼리 오갈 방법이 없어진다. 그래서 화면 안에도 둔다.
//   ⚠ 모바일은 사이드바가 아예 숨겨져 있어(v7.121) 여기가 유일한 이동 수단이다.
function AdminTabs({ page, onGo }) {
  return (
    <div style={{display:'inline-flex',gap:4,padding:4,background:'var(--bg-subtle)',border:'1px solid var(--border)',borderRadius:12,marginBottom:20,flexWrap:'wrap'}}>
      {ADMIN_PAGES.map(id => {
        const m = ADMIN_META[id], on = page === id;
        return (
          <button key={id} onClick={() => onGo(id)}
            style={{padding:'9px 16px',border:'none',borderRadius:9,cursor:'pointer',fontFamily:'inherit',
              background:on?'var(--accent)':'transparent', color:on?'#fff':'var(--fg-2)',
              fontSize:14,fontWeight:on?700:600,transition:'all 0.15s',display:'flex',alignItems:'center',gap:7}}>
            <Icon name={m.icon} size={18}/>{m.label}
          </button>
        );
      })}
    </div>
  );
}

function Card({children,style}){return <div style={{background:'var(--bg-card-glass)',backdropFilter:'blur(10px)',border:'1px solid var(--border)',borderRadius:24,...style}}>{children}</div>;}
// v7.352: className 을 받게 했다. 기존 호출 60여 곳은 안 넘기므로 영향 없다.
//   ⚠ 안 받으면 CSS 로 아이콘에 애니메이션을 걸 방법이 없다 (인라인 style 로는
//     :hover 상태를 표현할 수 없다).
function Icon({name,size=22,style,className}){return <span className={"material-symbols-outlined"+(className?" "+className:"")} style={{fontSize:size,fontVariationSettings:"'FILL' 0, 'wght' 400",...style}}>{name}</span>;}

// ─── v7.328: Lightfall 실험실 (배송시트지 > 테스트 탭) ────────────────────
//   v7.262~v7.327 의 BorderGlow 실험실은 값 확정(v7.318~v7.323 에서 실제 배송시트지에
//   반영 완료)이 끝나서 걷어냈다. 실험실만 지운 것이고 BorderGlow 자체는 남아 있다
//   — 행 글로우가 그걸 쓴다 (useRowGlow / rowGlowVars).
//
//   ⚠ 의존성: ogl. package.json 에 "ogl": "^1.0.11" 을 넣고 빌드해야 한다.
//     안 넣으면 import 에서 컴파일이 깨진다 (런타임 오류가 아니라 빌드 실패다).
//
//   ⚠ Lightfall 의 useEffect 는 의존성 배열에 모든 prop 이 들어 있다.
//     값 하나만 바뀌어도 WebGL 컨텍스트를 새로 만든다는 뜻이다.
//     슬라이더를 드래그하면 초당 수십 번 재생성되어 브라우저 한도(보통 16개)를
//     금방 넘긴다. → 아래 useDebounced 로 220ms 묶어서 넘긴다.
//     (컨텍스트 반납은 Lightfall.jsx cleanup 에서 따로 처리했다)
function useDebounced(value, delay = 220) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}

const LIGHTFALL_PRESETS = {
  default: { label: '기본 (React Bits)', colors: ['#A6C8FF', '#5227FF', '#FF9FFC'], bg: '#0A29FF' },
  river:   { label: '리버 (앱 강조색)',  colors: ['#7fe3ff', '#00668a', '#b3ecff'], bg: '#00344a' },
  aurora:  { label: '오로라',            colors: ['#8ef7c6', '#22d3ee', '#a78bfa'], bg: '#04262e' },
  ember:   { label: '엠버',              colors: ['#ffd28a', '#ff7a45', '#ff4d6d'], bg: '#2b0d16' },
  mono:    { label: '모노',              colors: ['#ffffff', '#c7d2fe', '#94a3b8'], bg: '#0b1220' },
};

function LightfallLab() {
  const [preset, setPreset] = useState('default');
  const [c1, setC1] = useState('#A6C8FF');
  const [c2, setC2] = useState('#5227FF');
  const [c3, setC3] = useState('#FF9FFC');
  const [bg, setBg] = useState('#0A29FF');
  const [speed, setSpeed] = useState(0.5);
  const [streakCount, setStreakCount] = useState(2);
  const [streakWidth, setStreakWidth] = useState(1);
  const [streakLength, setStreakLength] = useState(1);
  const [glow, setGlow] = useState(1);
  const [density, setDensity] = useState(0.6);
  const [twinkle, setTwinkle] = useState(1);
  const [zoom, setZoom] = useState(3);
  const [bgGlow, setBgGlow] = useState(0.5);
  const [opacity, setOpacity] = useState(1);
  const [mouseOn, setMouseOn] = useState(true);
  const [mouseStrength, setMouseStrength] = useState(0.5);
  const [mouseRadius, setMouseRadius] = useState(1);
  const [paused, setPaused] = useState(false);
  const [height, setHeight] = useState(600);
  const [copied, setCopied] = useState(false);

  const applyPreset = (key) => {
    const p = LIGHTFALL_PRESETS[key];
    if (!p) return;
    setPreset(key);
    setC1(p.colors[0]); setC2(p.colors[1]); setC3(p.colors[2]); setBg(p.bg);
  };

  // 슬라이더 값은 묶어서 넘긴다 (위 주석 참고)
  const d = {
    c1: useDebounced(c1), c2: useDebounced(c2), c3: useDebounced(c3), bg: useDebounced(bg),
    speed: useDebounced(speed), streakCount: useDebounced(streakCount),
    streakWidth: useDebounced(streakWidth), streakLength: useDebounced(streakLength),
    glow: useDebounced(glow), density: useDebounced(density), twinkle: useDebounced(twinkle),
    zoom: useDebounced(zoom), bgGlow: useDebounced(bgGlow), opacity: useDebounced(opacity),
    mouseStrength: useDebounced(mouseStrength), mouseRadius: useDebounced(mouseRadius),
  };
  // ★ colors 는 반드시 메모해야 한다. 매 렌더마다 새 배열을 만들면
  //   참조가 달라져서 debounce 를 해도 effect 가 계속 다시 돈다.
  const colors = React.useMemo(() => [d.c1, d.c2, d.c3], [d.c1, d.c2, d.c3]);

  const snippet =
`<Lightfall
  colors={['${d.c1}', '${d.c2}', '${d.c3}']}
  backgroundColor="${d.bg}"
  speed={${d.speed}}
  streakCount={${d.streakCount}}
  streakWidth={${d.streakWidth}}
  streakLength={${d.streakLength}}
  glow={${d.glow}}
  density={${d.density}}
  twinkle={${d.twinkle}}
  zoom={${d.zoom}}
  backgroundGlow={${d.bgGlow}}
  opacity={${d.opacity}}
  ${mouseOn ? 'mouseInteraction' : 'mouseInteraction={false}'}
  mouseStrength={${d.mouseStrength}}
  mouseRadius={${d.mouseRadius}}
/>`;

  const Slider = ({ label, value, set, min, max, step = 1, suffix = '' }) => (
    <div style={{marginBottom:12}}>
      <div style={{display:'flex',justifyContent:'space-between',fontSize:11.5,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>
        <span>{label}</span>
        <span style={{color:'var(--accent)',fontVariantNumeric:'tabular-nums'}}>{value}{suffix}</span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value}
        onChange={e=>set(parseFloat(e.target.value))}
        style={{width:'100%',accentColor:'#7b1fa2',cursor:'pointer'}}/>
    </div>
  );

  const Swatch = ({ label, value, set }) => (
    <label style={{display:'flex',alignItems:'center',gap:8,marginBottom:8,cursor:'pointer'}}>
      <span style={{width:34,height:26,borderRadius:7,border:'1px solid var(--border-strong)',background:value,flexShrink:0,position:'relative',overflow:'hidden'}}>
        <input type="color" value={value} onChange={e=>{ set(e.target.value); setPreset('custom'); }}
          style={{position:'absolute',inset:0,width:'100%',height:'100%',opacity:0,cursor:'pointer',border:'none',padding:0}}/>
      </span>
      <span style={{fontSize:11.5,fontWeight:600,color:'var(--fg-2)',flex:1}}>{label}</span>
      <span style={{fontSize:10.5,fontFamily:'monospace',color:'var(--fg-3)'}}>{value}</span>
    </label>
  );

  return (
    <div style={{display:'flex',gap:20,alignItems:'flex-start'}}>
      {/* 미리보기 */}
      <div style={{flex:1,minWidth:0}}>
        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:10}}>
          <Icon name="science" size={16} style={{color:'#7b1fa2'}}/>
          <span style={{fontSize:14,fontWeight:700}}>Lightfall 미리보기</span>
          <span style={{fontSize:12,color:'var(--fg-3)'}}>— 실 데이터와 무관합니다</span>
          <div style={{flex:1}}/>
          <button onClick={()=>setPaused(p=>!p)}
            style={{display:'inline-flex',alignItems:'center',gap:5,padding:'5px 12px',borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',
              border:`1px solid ${paused?'var(--warning)':'var(--border-strong)'}`,
              background:paused?'rgba(245,124,0,0.12)':'var(--bg-card)',
              color:paused?'var(--warning)':'var(--fg-2)'}}>
            <Icon name={paused?'play_arrow':'pause'} size={14}/>
            {paused?'재생':'정지'}
          </button>
        </div>
        <div style={{width:'100%',height,position:'relative',borderRadius:14,overflow:'hidden',border:'1px solid var(--border-strong)',background:'#000'}}>
          <Lightfall
            colors={colors}
            backgroundColor={d.bg}
            paused={paused}
            speed={d.speed}
            streakCount={d.streakCount}
            streakWidth={d.streakWidth}
            streakLength={d.streakLength}
            glow={d.glow}
            density={d.density}
            twinkle={d.twinkle}
            zoom={d.zoom}
            backgroundGlow={d.bgGlow}
            opacity={d.opacity}
            mouseInteraction={mouseOn}
            mouseStrength={d.mouseStrength}
            mouseRadius={d.mouseRadius}
          />
        </div>
        <div style={{marginTop:8,fontSize:11.5,color:'var(--fg-3)',lineHeight:1.6}}>
          마우스를 올리면 빛이 커서를 따라 번집니다. 값은 드래그를 멈춘 뒤 220ms 후에 반영됩니다
          — Lightfall 은 값이 바뀔 때마다 WebGL 컨텍스트를 새로 만들기 때문입니다.
        </div>
      </div>

      {/* 값 조절 */}
      <div style={{width:300,flexShrink:0,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:14,padding:16}}>
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:14}}>
          <Icon name="tune" size={16} style={{color:'#7b1fa2'}}/>
          <span style={{fontSize:13,fontWeight:700}}>값 조절</span>
        </div>

        <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',marginBottom:6}}>프리셋</div>
        <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:14}}>
          {Object.entries(LIGHTFALL_PRESETS).map(([k,p])=>(
            <button key={k} onClick={()=>applyPreset(k)}
              style={{padding:'4px 10px',borderRadius:9999,fontSize:11,fontWeight:600,cursor:'pointer',
                border:`1px solid ${preset===k?'#7b1fa2':'var(--border-strong)'}`,
                background:preset===k?'rgba(123,31,162,0.12)':'var(--bg-card)',
                color:preset===k?'#7b1fa2':'var(--fg-2)'}}>{p.label}</button>
          ))}
        </div>

        <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',marginBottom:6}}>색</div>
        <Swatch label="색 1" value={c1} set={setC1}/>
        <Swatch label="색 2" value={c2} set={setC2}/>
        <Swatch label="색 3" value={c3} set={setC3}/>
        <Swatch label="배경" value={bg} set={setBg}/>

        <div style={{height:1,background:'var(--border)',margin:'14px 0'}}/>

        <Slider label="속도"          value={speed}        set={setSpeed}        min={0}   max={3}  step={0.05}/>
        <Slider label="줄기 개수"      value={streakCount}  set={setStreakCount}  min={1}   max={16}/>
        <Slider label="줄기 굵기"      value={streakWidth}  set={setStreakWidth}  min={0.2} max={5}  step={0.1}/>
        <Slider label="줄기 길이"      value={streakLength} set={setStreakLength} min={0.2} max={5}  step={0.1}/>
        <Slider label="밀도"          value={density}      set={setDensity}      min={0.05} max={3} step={0.05}/>
        <Slider label="발광"          value={glow}         set={setGlow}         min={0}   max={3}  step={0.05}/>
        <Slider label="반짝임"        value={twinkle}      set={setTwinkle}      min={0}   max={1}  step={0.05}/>
        <Slider label="줌"            value={zoom}         set={setZoom}         min={0.5} max={10} step={0.1}/>
        <Slider label="배경 발광"      value={bgGlow}       set={setBgGlow}       min={0}   max={3}  step={0.05}/>
        <Slider label="불투명도"       value={opacity}      set={setOpacity}      min={0}   max={1}  step={0.05}/>
        <Slider label="미리보기 높이"   value={height}       set={setHeight}       min={200} max={900} step={20} suffix="px"/>

        <div style={{height:1,background:'var(--border)',margin:'14px 0'}}/>

        <label style={{display:'flex',alignItems:'center',gap:7,cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:10}}>
          <input type="checkbox" checked={mouseOn} onChange={e=>setMouseOn(e.target.checked)}
            style={{width:14,height:14,accentColor:'#7b1fa2',cursor:'pointer'}}/>
          마우스 반응
        </label>
        {mouseOn && (<>
          <Slider label="마우스 세기"   value={mouseStrength} set={setMouseStrength} min={0} max={3} step={0.05}/>
          <Slider label="마우스 반경"   value={mouseRadius}   set={setMouseRadius}   min={0.1} max={3} step={0.05}/>
        </>)}

        <button onClick={()=>{ navigator.clipboard?.writeText(snippet); setCopied(true); setTimeout(()=>setCopied(false),1500); }}
          style={{width:'100%',marginTop:12,padding:'9px 0',borderRadius:9,border:'none',cursor:'pointer',
            background:copied?'var(--success-solid)':'#7b1fa2',color:'#fff',fontSize:12,fontWeight:700}}>
          {copied?'복사됨!':'설정값 복사'}
        </button>
      </div>
    </div>
  );
}


// v7.103: 네이버스토어 로고 — 발송종류 셀에서 표시. 인라인 SVG(N 도형) + 알파값.
//         외부 이미지 의존 없음. size/opacity는 prop으로 조정 가능.
function NaverLogo({ size = 13, opacity = 0.75, title = '네이버스토어', style, fill = false }) {
  // v7.105: fill=true → 부모 컨테이너(position:relative) 전체를 채우는 배경 워터마크 모드
  //         SVG는 aspect ratio 유지하며 컨테이너에 맞춰 스케일. 극단 셀 크기 방지 위해 max 캡.
  const svgContent = (
    <svg viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"
      {...(fill
        ? { preserveAspectRatio: 'xMidYMid meet', style: { display:'block', width:'100%', height:'100%', maxWidth:130, maxHeight:110 } }
        : { width: size, height: size, style: { display:'block' } })}>
      <rect width="20" height="20" rx="4" fill="#03C75A"/>
      <g fill="#fff">
        <rect x="5.5" y="5" width="2.5" height="10"/>
        <rect x="12" y="5" width="2.5" height="10"/>
        <polygon points="8,5 10.5,5 12.5,15 10,15"/>
      </g>
    </svg>
  );
  if (fill) {
    return (
      <div aria-label={title} title={title} style={{position:'absolute',inset:4,display:'flex',alignItems:'center',justifyContent:'center',pointerEvents:'none',opacity,...style}}>
        {svgContent}
      </div>
    );
  }
  return (
    <span title={title} aria-label={title} style={{display:'inline-flex',verticalAlign:'middle',opacity,transition:'opacity 0.15s',...style}}>
      {svgContent}
    </span>
  );
}

// v7.176: 배송 요청별 작업사진 ZIP 첨부 컴포넌트
// v7.179: 다중 파일 (PNG/JPG 낱개 + legacy ZIP 유지)
//   • note.attachments 배열로 저장 (신규)
//   • note.attachment (단일 legacy) 는 계속 읽기 지원, 저장 시 attachments로 병합
//   • chip 형태 나열, 클릭 시 새 탭 (이미지=inline / ZIP=download)
function AttachmentButton({ item, onUpdated, disabled }) {
  const [uploading, setUploading] = useState(false);
  const [deletingPath, setDeletingPath] = useState(null);
  const fileInputRef = useRef(null);
  const noteObj = (() => { try { return JSON.parse(item.note || '{}'); } catch (e) { return {}; } })();

  // v7.179: attachments 배열 + legacy attachment 병합 표시
  const attachments = (() => {
    const arr = Array.isArray(noteObj.attachments) ? [...noteObj.attachments] : [];
    if (noteObj.attachment && noteObj.attachment.path) {
      if (!arr.some(a => a.path === noteObj.attachment.path)) {
        arr.unshift(noteObj.attachment);
      }
    }
    return arr;
  })();

  // note 갱신 — 저장 시엔 항상 attachments 배열 사용 (legacy attachment 제거)
  const saveAttachments = async (newList) => {
    const nextNote = { ...noteObj };
    if (newList && newList.length > 0) nextNote.attachments = newList;
    else delete nextNote.attachments;
    delete nextNote.attachment; // legacy 필드 정리
    const { error } = await supabase.from('transaction_log')
      .update({ note: JSON.stringify(nextNote) })
      .eq('id', item.id);
    if (error) throw error;
  };

  const handleFilePick = () => { if (!disabled && !uploading) fileInputRef.current?.click(); };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (files.length === 0) return;
    const totalMB = files.reduce((s, f) => s + f.size, 0) / 1024 / 1024;
    if (totalMB > 500) {
      if (!window.confirm(`총 ${totalMB.toFixed(1)}MB (${files.length}개) 업로드. 계속하시겠습니까?`)) return;
    }
    setUploading(true);
    const newList = [...attachments];
    const errors = [];
    for (const file of files) {
      try {
        const res = await AttachmentService.upload(item.id, file);
        if (res.ok) {
          newList.push(res.data);
        } else {
          errors.push(`${file.name}: ${res.error?.message || JSON.stringify(res.error)}`);
          console.warn('[attachment] upload failed:', file.name, res.error);
        }
      } catch (err) {
        errors.push(`${file.name}: ${err.message || String(err)}`);
        console.warn('[attachment] upload error:', file.name, err);
      }
    }
    try {
      if (newList.length > attachments.length) await saveAttachments(newList);
    } catch (err) {
      alert('저장 실패: ' + (err.message || String(err)));
      setUploading(false);
      return;
    }
    setUploading(false);
    if (errors.length > 0) {
      const successCount = files.length - errors.length;
      alert(`업로드 실패 ${errors.length}건 (성공 ${successCount}건)\n\n${errors.slice(0, 3).join('\n\n')}${errors.length > 3 ? `\n\n... 외 ${errors.length - 3}건` : ''}\n\n(F12 → Console 탭에서 자세한 로그 확인)`);
    }
    onUpdated?.();
  };

  const handleDelete = async (att) => {
    if (!window.confirm(`삭제: ${att.name || att.path}?`)) return;
    setDeletingPath(att.path);
    try {
      await AttachmentService.remove(att.path);
      await saveAttachments(attachments.filter(a => a.path !== att.path));
      onUpdated?.();
    } catch (err) {
      alert('삭제 실패: ' + (err.message || String(err)));
    } finally {
      setDeletingPath(null);
    }
  };

  const isImage = (att) => /\.(png|jpe?g|gif|webp|bmp)$/i.test(att.name || att.path || '');
  const fmtBytes = (b) => {
    if (!b || b <= 0) return '';
    if (b < 1024) return b + 'B';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + 'KB';
    return (b / 1024 / 1024).toFixed(1) + 'MB';
  };

  return (
    <div style={{marginTop:6, display:'flex', flexWrap:'wrap', alignItems:'center', gap:4}}>
      <input ref={fileInputRef} type="file"
        accept=".png,.jpg,.jpeg,.gif,.webp,.zip,.pdf,image/*,application/zip,application/pdf"
        multiple
        onChange={handleFileChange} style={{display:'none'}}/>
      {attachments.map((att, idx) => (
        <span key={att.path || idx}
          style={{display:'inline-flex', alignItems:'center', gap:1, background:'var(--bg-info)', border:'1px solid #cfd8dc', borderRadius:5, padding:'2px 2px 2px 6px', fontSize:10, fontWeight:700, lineHeight:1.3, maxWidth:150}}>
          <a href={AttachmentService.getPublicUrl(att.path)}
            target="_blank" rel="noopener noreferrer"
            onClick={(e)=>{e.preventDefault(); downloadKeepingName(AttachmentService.getPublicUrl(att.path), att.name || 'file');}}
            title={`${att.name || att.path}${att.size ? ` (${fmtBytes(att.size)})` : ''} — 클릭하여 다운로드`}
            style={{color:'var(--accent)', textDecoration:'none', display:'inline-flex', alignItems:'center', gap:3, minWidth:0}}>
            <Icon name={isImage(att) ? "image" : "attach_file"} size={11}/>
            <span style={{maxWidth:90, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
              {att.name || 'file'}
            </span>
          </a>
          {!disabled && (
            <button onClick={() => handleDelete(att)}
              disabled={deletingPath === att.path}
              title="삭제"
              style={{border:'none', background:'transparent', cursor:'pointer', color:'#c62828', padding:'0 3px', fontSize:13, fontWeight:800, lineHeight:1, fontFamily:'inherit'}}>
              {deletingPath === att.path ? '⋯' : '×'}
            </button>
          )}
        </span>
      ))}
      {!disabled && (
        <button onClick={handleFilePick} disabled={uploading}
          title="사진 여러 장 선택 가능"
          style={{padding:'3px 8px', borderRadius:5, fontSize:10, fontWeight:700, fontFamily:'inherit', background:'#f5f5f5', color:'var(--fg-3)', border:'1px dashed #cbd5e1', cursor:uploading?'wait':'pointer', display:'inline-flex', alignItems:'center', gap:3, lineHeight:1.2}}>
          <Icon name={uploading ? "hourglass_empty" : "add_photo_alternate"} size={11}/>
          {uploading ? '업로드 중...' : (attachments.length === 0 ? '사진 추가' : '+ 사진')}
        </button>
      )}
    </div>
  );
}
// v7.117: 모바일 대응 화이트리스트 — 여기 없는 페이지는 폰에서 안내 페이지 표시
//   (사용자가 "계속 보기" 누르면 원본 렌더, 페이지 이동 시 자동 리셋)
// v7.162: 'txlog' 추가 — v7.150에서 MobileTxLog 도입했으나 리스트 반영 누락으로 안내 뜨던 버그 fix
const MOBILE_FRIENDLY_PAGES = new Set(['scan', 'inventory', 'ship', 'txlog']); // v7.119: ship 추가, v7.162: txlog 추가
// v7.117: 미대응 페이지 진입 시 표시되는 안내 컴포넌트
function MobileGuardPage({ pageLabel, onContinue, onGoToScan }) {
  return (
    <div style={{padding:20, maxWidth:480, margin:'40px auto', textAlign:'center'}}>
      <div style={{fontSize:56, marginBottom:16, opacity:0.85}}>💻</div>
      <h2 style={{fontSize:22, fontWeight:800, color:'var(--fg-1)', marginBottom:8}}>{pageLabel || '이 페이지'}</h2>
      <p style={{fontSize:14, color:'var(--fg-3)', marginBottom:20, lineHeight:1.7}}>
        정보 밀도가 높은 페이지라<br/>
        <b style={{color:'var(--fg-1)'}}>PC에서 이용</b>이 권장됩니다.
      </p>
      <div style={{background:'rgba(0,102,138,0.06)', border:'1px solid rgba(0,102,138,0.15)', padding:'14px 16px', borderRadius:10, marginBottom:18, textAlign:'left', fontSize:12, color:'var(--accent)', lineHeight:1.7}}>
        <b>📱 모바일에서 편한 페이지</b><br/>
        • 배송시트지 (요청/처리 목록)<br/>
        • 제품 출고 요청<br/>
        • 재고 목록<br/>
        • 모바일 스캔 (바코드 입고)
      </div>
      <button onClick={onGoToScan}
        style={{width:'100%', padding:'14px', background:'#0277bd', border:'none', borderRadius:10, color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', marginBottom:8, display:'flex', alignItems:'center', justifyContent:'center', gap:6}}>
        📱 모바일 스캔으로 이동
      </button>
      <button onClick={onContinue}
        style={{width:'100%', padding:'11px', background:'transparent', border:'1px solid var(--border-strong)', borderRadius:10, color:'var(--fg-2)', fontSize:12, fontWeight:600, cursor:'pointer'}}>
        그래도 계속 보기 (모바일 폭)
      </button>
    </div>
  );
}
const inputStyle = {width:'100%',padding:'12px 14px',border:'1px solid var(--border-strong)',borderRadius:12,fontSize:14,background:'var(--bg-card)',fontFamily:'inherit',transition:'all 0.2s',boxSizing:'border-box'};

// ─── 드롭다운 자동 위치 전환 ── 아래 공간 부족하면 위쪽으로 열림
// callback ref로 사용: <div ref={positionDropdown} ...>
const positionDropdown = (el) => {
  if (!el) return;
  // 다음 frame에 측정 (offsetHeight 안정화 대기)
  requestAnimationFrame(() => {
    if (!el || !el.isConnected) return;
    const parent = el.parentElement;
    if (!parent) return;
    const parentRect = parent.getBoundingClientRect();
    const dropdownHeight = el.offsetHeight || 200;
    const spaceBelow = window.innerHeight - parentRect.bottom;
    const spaceAbove = parentRect.top;
    // 아래 공간이 부족하고 위쪽이 더 넓으면 위쪽으로
    if (spaceBelow < dropdownHeight + 16 && spaceAbove > spaceBelow) {
      el.style.top = 'auto';
      el.style.bottom = '100%';
      el.style.marginTop = '0';
      el.style.marginBottom = '4px';
      // 위로 열릴 땐 maxHeight를 위 공간에 맞춰 제한
      el.style.maxHeight = Math.min(dropdownHeight, spaceAbove - 16) + 'px';
    } else {
      // 아래로 열릴 때도 maxHeight 제한 (뷰포트 끝까지 잘리지 않도록)
      el.style.maxHeight = Math.min(dropdownHeight, spaceBelow - 16) + 'px';
    }
  });
};

// ─── KeepSerialSlot: 시리얼 슬롯 1개 (수량만큼 N개 표시) ───
// 메인 컴포넌트 밖에 정의 — map 안 재마운트로 포커스 잃지 않도록.
function KeepSerialSlot({ initial, isActive, productName, idx, onCommit, onSearch, supabaseClient }) {
  const [val, setVal] = React.useState(initial || '');
  const [focused, setFocused] = React.useState(false);
  const [results, setResults] = React.useState([]);
  // initial이 외부에서 바뀌면 동기화 (loadKeeps 후)
  React.useEffect(() => { setVal(initial || ''); }, [initial]);
  const doSearch = async (q) => {
    if (!productName) { setResults([]); return; }
    let query = supabaseClient.from('inventory')
      .select('serial_number')
      .eq('product_name', productName)
      .eq('status', 'in_stock')
      .order('serial_number')
      .limit(20);
    if (q && q.length > 0) query = query.ilike('serial_number', `%${q}%`);
    const { data } = await query;
    setResults(data || []);
  };
  const commit = () => {
    if (val !== (initial || '')) onCommit(idx, val);
    setFocused(false);
  };
  return (
    <div style={{position:'relative',marginBottom:4}}>
      <input
        value={val}
        onFocus={()=>{ setFocused(true); doSearch(val); }}
        onChange={e=>{ const nv=imeConv(e); setVal(nv); doSearch(nv); }} onCompositionEnd={e=>{ const nv=normalizeScanInput(e.target.value); setVal(nv); doSearch(nv); }}
        onBlur={()=>setTimeout(commit, 150)}
        onKeyDown={e=>{ if (e.key === 'Enter') { e.target.blur(); } else if (e.key === 'Escape') { setVal(initial || ''); e.target.blur(); } }}
        disabled={!isActive}
        placeholder={isActive ? `#${idx+1} 시리얼` : ''}
        style={{width:'100%',padding:'5px 8px',border:'1px solid '+(focused?'var(--purple)':'var(--border)'),borderRadius:5,fontSize:11,outline:'none',fontFamily:'monospace',background:isActive?'var(--bg-card)':'#fafafa',color:isActive?'var(--fg-1)':'var(--fg-3)'}}/>
      {focused && results.length > 0 && (
        <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:6,marginTop:2,maxHeight:160,overflowY:'auto',zIndex:40,boxShadow:'0 4px 16px rgba(0,0,0,0.15)'}}>
          <div style={{padding:'4px 8px',fontSize:10,color:'var(--fg-3)',background:'var(--bg-subtle)',borderBottom:'1px solid var(--border-mute)'}}>재고 시리얼 {results.length}건</div>
          {results.map(r => (
            <div key={r.serial_number} onMouseDown={e=>{ e.preventDefault(); setVal(r.serial_number); onCommit(idx, r.serial_number); setFocused(false); }}
              style={{padding:'5px 8px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)',fontFamily:'monospace',fontSize:11}}
              onMouseEnter={e=>e.currentTarget.style.background='#f5f0fa'}
              onMouseLeave={e=>e.currentTarget.style.background=''}>
              {r.serial_number}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const [page, setPage] = useState('txlog');
  // v7.339: 넥시 조회 — 입력한 위치 코드 (도면 하이라이트 키)
  const [nexiQuery, setNexiQuery] = useState('');
  // v7.341: 넥시 조회 층 전환 ('1' | '2'). 층마다 검색 대상이 다르다 (위치 코드 / 주문코드)
  const [nexiFloor, setNexiFloor] = useState('1');
  // v7.348: 사이드바 [관리] 아코디언 펼침 여부. 'admin' 은 페이지가 아니라 머리 항목이다.
  const [adminOpen, setAdminOpen] = useState(false);
  // v7.349: NAS 호환성 — 검색어 / 자동완성 후보 / 선택된 NAS 모델
  //   ⚠ 재고와 무관하다. 재고가 0인 단종 모델도 호환성은 조회돼야 하므로
  //     inventory 가 아니라 products(카탈로그)에서 찾는다.
  // v7.357: 검색은 이제 [폴더 격자 필터] 다. 서버 조회가 아니라 이미 받아 둔 목록을 거른다.
  //   ⚠ 그래서 compatResults / compatOpen / compatLoading 이 전부 필요 없어졌다 (제거).
  const [compatSearch, setCompatSearch] = useState('');
  const [compatModel, setCompatModel] = useState(null);
  // v7.351: 시리즈 폴더 브라우징 — 도크에서 시리즈를 고르면 그 시리즈 모델이 격자로 나온다.
  //   ⚠ 도크는 항목 4~8개용 UI 다. NAS 모델은 수십~수백 개라 한 줄에 늘어놓으면 못 쓴다.
  //     그래서 도크에는 [시리즈]만 놓고 모델은 아래 격자에서 고른다.
  const [compatAll, setCompatAll] = useState(null);      // null = 아직 안 불러옴
  const [compatSeries, setCompatSeries] = useState(null);
  // v7.358: 호환성 데이터 — QNAP 사본을 카테고리별로 읽는다.
  //   ⚠ 기본값을 메모리로 둔 건 이 탭이 원래 메모리 조회용이었기 때문이다.
  const [compatCat, setCompatCat] = useState(QNAP_CAT.MEMORY);
  const [compatData, setCompatData] = useState(null);   // null = 로딩 중
  // v7.359: 부품 목록 안에서 다시 좁히기 — HDD 는 200건이 넘어 그냥 나열하면 못 읽는다
  const [partQ, setPartQ] = useState('');            // 부품 모델명 검색
  const [capFilter, setCapFilter] = useState(null);  // 용량 필터 (GB, null=전체)
  const [hideNR, setHideNR] = useState(false);       // 비권장 숨기기
  const [compatCounts, setCompatCounts] = useState(null); // 카테고리별 건수 (탭 뱃지)
  // v7.360: 보유 재고 색인 (정규화 모델명 → {total, byWarehouse}).
  //   ⚠ 재고는 어느 NAS 를 보든 같다 → 탭 진입 시 1회만 받는다.
  const [stockIdx, setStockIdx] = useState(null);
  const [stockOnly, setStockOnly] = useState(false);   // 재고 있는 것만 보기
  // v7.361: 역방향 검색 결과. 검색어가 NAS 모델명과 하나도 안 맞을 때만 채워진다.
  //   null = 시도 안 함 / 'loading' / {parts, productNames}
  const [partLookup, setPartLookup] = useState(null);
  // v7.364: 사내 호환성 테스트.
  //   v7.365: 별도 [소스 탭] 을 걷어내고 공식 목록과 [같은 목록의 다른 제조사 그룹] 으로 합쳤다.
  //   ⚠ 합쳤다고 출처가 같아지는 건 아니다. 행마다 [🧪 사내] 뱃지를 달고
  //     하단 출처 문구에도 별도 문장을 남긴다 — 이 두 가지는 지우지 말 것.
  const [icRows, setIcRows] = useState(null);   // null = 로딩 중 / [] = 없음
  const [icModal, setIcModal] = useState(null); // null | {id?, ...form}
  const [icBusy, setIcBusy] = useState(false);
  const [icErr, setIcErr] = useState('');
  // 저장/삭제 후 목록을 다시 읽기 위한 신호. 값 자체는 의미 없다.
  const [icReload, setIcReload] = useState(0);
  // v7.366: 일괄 삭제 모드.
  //   ⚠ 선택 대상은 [사내 테스트 행] 뿐이다. QNAP 공식 행은 우리가 지울 수 있는 게 아니다.
  const [icSelMode, setIcSelMode] = useState(false);
  const [icSel, setIcSel] = useState([]);   // 선택된 id 배열

  const [connected, setConnected] = useState(null);
  // v7.116: 모바일 반응형 — 사이드바 overlay drawer
  //   v7.392: 데스크톱 모드를 걷어내면서 v7.372 이전 구조로 되돌렸다.
  //   ⚠ 파생값(isNarrow && !desktopMode)으로 만들지 말 것. 그 구조가
  //     [앱/폰브라우저/PC × 자동/수동 × 저장값] 조합을 낳아 감당이 안 됐다.
  const [isMobile, setIsMobile] = useState(() => {
    try { return typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches; } catch (e) { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // v7.117: 미대응 페이지 안내 우회 (한 세션 유지, 페이지 변경 시 자동 리셋)
  const [mobileBypassPage, setMobileBypassPage] = useState(false);
  useEffect(() => { setMobileBypassPage(false); }, [page]);
  // v7.121: 모바일에서는 ship/inventory 2개 페이지만 허용 — 다른 페이지 접근 시 강제 리다이렉트
  // v7.129: 'scan' 페이지도 모바일 허용 (세그먼트 탭 3번째로 노출)
  // v7.150: 'txlog' 페이지도 모바일 허용 (세그먼트 4탭 첫번째로 노출)
  useEffect(() => {
    if (isMobile && !['ship', 'inventory', 'scan', 'txlog'].includes(page)) {
      setPage('ship');
    }
  }, [isMobile, page]);
  // v7.129: PC에서 'scan' 페이지 상태가 잔존해도 배송시트지로 리다이렉트 (scan은 모바일 전용)
  useEffect(() => {
    if (!isMobile && page === 'scan') {
      setPage('txlog');
    }
  }, [isMobile, page]);

  // ─── v7.159: Android 물리 뒤로가기 처리 (v7.158 개선) ───────────
  // 구조:
  //   ① window.__backHandlers 스택 — 각 컴포넌트가 상황별 handler 등록.
  //      - handler() === true 반환 시 처리 완료, 다음 이벤트 대기.
  //      - 스택 최상위(가장 늦게 push된)부터 실행 → LIFO.
  //      - App-level handler는 unshift로 최하위 배치 (모달/상세 다음에 실행).
  //   ② dispatchBack() — 공용 dispatcher. Capacitor / popstate 양쪽에서 호출.
  //   ③ Capacitor 감지 시 App.addListener('backButton') 우선 사용 → popstate 우회.
  //   ④ 스택이 다 소진되면 (아무도 안 잡음) → 토스트 후 2초 내 재클릭 시 종료.
  //   ⑤ pageHistoryRef — 4개 모바일 탭 간 이동을 자동 스택 추적. App-level backHandler가 이 스택 pop.
  const [mobileExitToast, setMobileExitToast] = useState(false);
  const mobileLastBackRef = React.useRef(0);
  const pageHistoryRef = React.useRef([]);
  const isNavigatingBackRef = React.useRef(false);
  const prevPageRef = React.useRef(page);
  // v7.161: 모달 상태를 ref로 미러링 — App-level backHandler useEffect가
  //          outboundModalOpen/headerMenuOpen state를 참조하면 TDZ ReferenceError 발생
  //          (state 선언이 이 useEffect보다 훨씬 뒤에 있어서). ref로 우회.
  const backStateRef = React.useRef({ outbound: false, headerMenu: false });
  // v7.163: 뒤로가기 진단용 debug 상태 (화면 우하단 뱃지)
  //          capacitor: window.Capacitor 존재 여부 (bridge 주입)
  //          appPlugin: window.Capacitor.Plugins.App 존재 여부
  //          isNative: isNativePlatform() 반환값
  //          capListener: App.addListener('backButton') 등록 상태 ('ok'|'fail'|'skip')
  //          bufPushed: 초기 pushState buffer 개수
  //          lastEvent: 최근 back 이벤트 소스 ('capacitor'|'popstate')
  //          count: back 이벤트 누적 횟수
  const [backDebug, setBackDebug] = useState({
    capacitor: false, appPlugin: false, isNative: false,
    capListener: '-', bufPushed: 0, lastEvent: '-', count: 0,
  });

  // ─── 탭 이동 자동 히스토리 추적 ───────────────────────────────
  // page 상태 변경 시 이전 값을 push (뒤로가기 유래는 제외).
  // mobile 탭 4개 간 이동만 (다른 자동 리다이렉트는 무시).
  useEffect(() => {
    if (!isMobile) {
      // PC 전환 시 히스토리 초기화
      pageHistoryRef.current = [];
      prevPageRef.current = page;
      return;
    }
    const from = prevPageRef.current;
    const to = page;
    if (from === to) return;
    const MOBILE_TABS = ['txlog', 'ship', 'inventory', 'scan'];
    if (MOBILE_TABS.includes(from) && MOBILE_TABS.includes(to) && !isNavigatingBackRef.current) {
      pageHistoryRef.current.push(from);
      if (pageHistoryRef.current.length > 20) pageHistoryRef.current.shift();
    }
    isNavigatingBackRef.current = false;
    prevPageRef.current = page;
  }, [page, isMobile]);

  // ─── App-level backHandler (최하위 우선순위) ─────────────────
  // ⓐ outboundModal / headerMenu 열림 → 닫기
  // ⓑ 위 둘 다 아니고 pageHistory 남음 → pop해서 이전 탭 이동
  // ⓒ 다 소진 → false 반환 → 종료 토스트 흐름
  //
  // v7.161: state 대신 backStateRef.current로 읽음 (TDZ 회피).
  //          setter는 closure body 안에서 참조 — 실행 시점(popstate/backButton 발동)에는
  //          이미 render 완료되어 있어서 접근 가능.
  useEffect(() => {
    if (!isMobile) return;
    const h = () => {
      // ⓐ outboundModal
      if (backStateRef.current.outbound) {
        setOutboundModalOpen(false);
        return true;
      }
      // ⓐ headerMenu
      if (backStateRef.current.headerMenu) {
        setHeaderMenuOpen(false);
        return true;
      }
      // ⓑ pageHistory
      if (pageHistoryRef.current.length > 0) {
        const prev = pageHistoryRef.current.pop();
        isNavigatingBackRef.current = true;
        setPage(prev);
        return true;
      }
      return false;
    };
    window.__backHandlers = window.__backHandlers || [];
    window.__backHandlers.unshift(h); // 배열 앞 → reverse 후 마지막 실행 (최하위 우선순위)
    return () => { window.__backHandlers = (window.__backHandlers || []).filter(x => x !== h); };
  }, [isMobile]);

  // ─── 하드웨어 back 이벤트 리스너 (v7.163: 양방향 동시 등록, v7.166: native 브릿지 추가) ──
  //   전략 (우선순위):
  //     ⓐ Capacitor App plugin 리스너 (bridge 주입된 경우) — window.Capacitor.Plugins.App
  //     ⓑ Native MainActivity onBackPressed에서 호출 — window.__handleAndroidBack (v7.166 신규)
  //     ⓒ popstate 폴백 — Capacitor 기본 back이 goBack() 부를 때만 발동
  //   sever.url 모드 + bridge 미주입 상황(우리 경우)에서는 ⓑ가 유일한 해결책.
  //   ⓑ 활성화 조건: Capacitor 프로젝트의 MainActivity.java/kt에서
  //     onBackPressed() 재정의 → evaluateJavascript로 이 함수 호출 → APK 재빌드.
  useEffect(() => {
    if (!isMobile) return;
    if (typeof window === 'undefined') return;
    window.__backHandlers = window.__backHandlers || [];

    const dispatchBack = () => {
      const handlers = (window.__backHandlers || []).slice().reverse();
      for (const h of handlers) {
        try { if (h && h() === true) return true; }
        catch (err) { console.warn('backHandler 실행 실패:', err); }
      }
      return false;
    };

    const Cap = window.Capacitor || null;
    const AppPlugin = (Cap && Cap.Plugins && Cap.Plugins.App) || null;
    const canExitApp = () => !!(AppPlugin && typeof AppPlugin.exitApp === 'function');

    setBackDebug(prev => ({
      ...prev,
      capacitor: !!Cap,
      appPlugin: !!AppPlugin,
      isNative: !!(Cap && typeof Cap.isNativePlatform === 'function' && Cap.isNativePlatform()),
    }));

    // 공용 처리 로직 — 소스만 다르게 기록
    // 반환값:
    //   true  = JS가 처리함 (native는 종료 X)
    //   false = JS가 처리 안 함 (native가 exit/goBack)
    const handleBack = (source) => {
      setBackDebug(prev => ({ ...prev, lastEvent: source, count: (prev.count || 0) + 1 }));
      if (dispatchBack()) return true;
      // 스택 소진 → 종료 흐름
      const now = Date.now();
      if (now - mobileLastBackRef.current < 2000) {
        // 2초 내 재클릭 → 종료 허용
        if (canExitApp()) { try { AppPlugin.exitApp(); } catch (e) {} }
        // native로부터 온 경우 (source === 'native') → false 반환하면 MainActivity가 finish()
        return false;
      }
      mobileLastBackRef.current = now;
      setMobileExitToast(true);
      setTimeout(() => setMobileExitToast(false), 2000);
      return true; // 첫 클릭은 토스트만, 종료 방지
    };

    // ⓐ Capacitor App plugin 리스너 (있으면)
    let listenerHandle = null;
    let mounted = true;
    if (AppPlugin && typeof AppPlugin.addListener === 'function') {
      (async () => {
        try {
          const result = AppPlugin.addListener('backButton', () => handleBack('capacitor'));
          listenerHandle = (result && typeof result.then === 'function') ? await result : result;
          if (!mounted && listenerHandle && typeof listenerHandle.remove === 'function') {
            try { listenerHandle.remove(); } catch (e) {}
            listenerHandle = null;
          }
          setBackDebug(prev => ({ ...prev, capListener: 'ok' }));
        } catch (err) {
          console.warn('[back] Capacitor 리스너 등록 실패:', err);
          setBackDebug(prev => ({ ...prev, capListener: 'fail' }));
        }
      })();
    } else {
      setBackDebug(prev => ({ ...prev, capListener: 'skip' }));
    }

    // ⓑ v7.166: Native MainActivity에서 호출할 전역 함수 등록
    //   MainActivity.onBackPressed() → evaluateJavascript("window.__handleAndroidBack()")
    //   반환값 true = 처리됨(종료 X), false = 처리 안 됨(native가 종료 결정)
    window.__handleAndroidBack = () => {
      return handleBack('native');
    };

    // ⓒ popstate 폴백 — 항상 등록
    const BUFFER_SIZE = 10;
    const pushBuffer = (n) => {
      try {
        for (let i = 0; i < (n || BUFFER_SIZE); i++) {
          window.history.pushState({ inventura: true, at: Date.now() + i }, '');
        }
      } catch (e) {}
    };
    pushBuffer(BUFFER_SIZE);
    setBackDebug(prev => ({ ...prev, bufPushed: BUFFER_SIZE }));
    const onPopState = () => {
      handleBack('popstate');
      pushBuffer(1);
    };
    window.addEventListener('popstate', onPopState);

    return () => {
      mounted = false;
      if (listenerHandle && typeof listenerHandle.remove === 'function') {
        try { listenerHandle.remove(); } catch (e) {}
      }
      window.removeEventListener('popstate', onPopState);
      try { delete window.__handleAndroidBack; } catch (e) { window.__handleAndroidBack = null; }
    };
  }, [isMobile]);
  // ─── v7.362: 데스크톱 브라우저 뒤로가기 (NAS 호환성) ────────
  //   ⚠ 위 하드웨어 back 처리는 전부 `if (!isMobile) return;` 로 막혀 있다.
  //     PC 에는 히스토리 관리가 아예 없어서 뒤로가기가 곧장 사이트를 벗어난다.
  //     모바일 쪽은 오래 다듬어진 코드라(v7.158~166) 건드리지 않고 따로 얹는다.
  //
  //   동작: [첫 화면이 아닌 상태]로 처음 들어갈 때 히스토리를 한 칸 밀어 넣고,
  //        뒤로가기가 오면 첫 화면(검색어·선택 해제)으로 되돌린다.
  //
  //   ⚠ 앱 전체에 history 를 깔지 않는다. 그러면 탭 이동이 전부 쌓여
  //     사이트를 나가려고 뒤로가기를 수십 번 눌러야 한다. 이 탭 한 곳만 처리한다.
  //   ⚠ 검색 글자마다 밀어 넣으면 안 된다. 'TS-453E' 를 치면 7칸이 쌓여
  //     뒤로가기를 7번 눌러야 한다 → [첫 화면 아님]으로 바뀌는 순간 딱 한 번만.
  //   ⚠ 뒤로가기로 돌아온 직후엔 다시 밀어 넣으면 안 된다 (무한 루프) → compatPopRef.
  const compatPushedRef = useRef(false);
  const compatPopRef = useRef(false);
  const compatDirty = page === 'compat' && (!!compatModel || !!compatSearch.trim());

  useEffect(() => {
    if (typeof window === 'undefined' || isMobile) return;
    const onPop = () => {
      // ⚠ 다른 탭에서 눌린 뒤로가기는 건드리지 않는다.
      //   (우리가 밀어 넣은 칸이 남아 있을 수 있지만, 그건 뒤로가기 한 번을
      //    더 눌러야 하는 정도라 사이트를 벗어나는 것보다 낫다)
      if (page !== 'compat') return;
      compatPopRef.current = true;
      compatPushedRef.current = false;
      setCompatModel(null);
      setCompatSearch('');
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [page, isMobile]);

  useEffect(() => {
    if (typeof window === 'undefined' || isMobile) return;
    if (compatPopRef.current) { compatPopRef.current = false; return; }
    if (compatDirty && !compatPushedRef.current) {
      compatPushedRef.current = true;
      try { window.history.pushState({ inventuraCompat: true }, ''); } catch (e) {}
    }
    // 사용자가 직접 [다시 검색]·지우기로 첫 화면에 돌아온 경우 — 다음에 다시 밀 수 있게 푼다
    if (!compatDirty) compatPushedRef.current = false;
  }, [compatDirty, isMobile]);


  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const update = () => {
      setIsMobile(mq.matches);
      if (!mq.matches) setMobileNavOpen(false); // 데스크톱 전환 시 drawer 강제 닫음
    };
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update); // Safari legacy
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);
  const [onlineCount, setOnlineCount] = useState(1); // 동시 접속자 수 (Realtime presence)
  const [txRefreshing, setTxRefreshing] = useState(false); // 배송시트지 수동 새로고침 인디케이터
  // 배송시트지 sub-tab ('list' = 배송 목록 / 'memos' = 메모)
  const [txSubTab, setTxSubTab] = useState('list');
  // v7.174: 자동이월 배지 클릭 시 뜨는 날짜 선택 팝오버 상태
  //   { rowId, currentDate, x, y } — currentDate는 현재 transaction_date (input 기본값)
  const [deferPopover, setDeferPopover] = useState(null);
  const [deferPopoverDate, setDeferPopoverDate] = useState('');
  // v7.187: 장부처리 3-state 드롭다운 팝오버 { rowId, x, y, current }
  // v7.239: + { manual, mode } — mode:'manual' 이면 자유 텍스트 입력 화면으로 전환
  const [bookkeptPopover, setBookkeptPopover] = useState(null);
  // v7.239: 수기입력 자유 텍스트 입력칸 — 타이핑마다 App 전체가 리렌더되지 않도록
  //         uncontrolled(defaultValue + ref)로 다룬다. salesStatusManual(v7.187)과 동일 패턴.
  const bookkeptInputRef = useRef(null);
  // v7.239: 장부처리 상태 저장 — 3-state + 수기입력 자유 텍스트(note.bookkeptManual)
  //   완료/미완료로 되돌릴 때는 남아 있던 수기 텍스트를 지운다 (유령 값 방지).
  const saveBookkept = async (rowId, state, manualText) => {
    try {
      // note는 다른 필드가 함께 들어 있으므로 반드시 최신 값을 다시 읽고 병합한다
      const { data: row, error: fErr } = await supabase.from('transaction_log').select('note').eq('id', rowId).single();
      if (fErr) { alert('조회 실패: ' + fErr.message); return false; }
      const p = row?.note ? JSON.parse(row.note) : {};
      p.bookkept = state; // 명시적으로 string 저장 (기존 bool은 자동 대체됨)
      if (state === 'manual') {
        const t = (manualText || '').trim();
        if (t) p.bookkeptManual = t; else delete p.bookkeptManual;
      } else {
        delete p.bookkeptManual;
      }
      const { error } = await supabase.from('transaction_log').update({ note: JSON.stringify(p) }).eq('id', rowId);
      if (error) { alert('업데이트 실패: ' + error.message); return false; }
      loadTxLog();
      return true;
    } catch (err) {
      alert('업데이트 실패: ' + (err.message || String(err)));
      return false;
    }
  };
  const [deferSaving, setDeferSaving] = useState(false);
  // v7.111→v7.112→v7.127: 자동 이월 실행 시각 (0-1439 minutes since midnight, step 30).
  //   v7.127: Supabase app_settings 테이블 공유 저장 (PC 간 통일)
  //           localStorage는 오프라인 폴백/캐시로 유지
  const [autoDeferMinutes, setAutoDeferMinutes] = useState(() => {
    try {
      if (typeof localStorage === 'undefined') return 0;
      const newVal = localStorage.getItem('inv_autoDeferMinutes');
      if (newVal != null) {
        const p = parseInt(newVal, 10);
        if (Number.isFinite(p) && p >= 0 && p < 1440 && p % 30 === 0) return p;
      }
      // v7.111 → v7.112 마이그레이션
      const oldVal = localStorage.getItem('inv_autoDeferHour');
      if (oldVal != null) {
        const op = parseInt(oldVal, 10);
        if (Number.isFinite(op) && op >= 0 && op <= 23) {
          const converted = op * 60;
          localStorage.setItem('inv_autoDeferMinutes', String(converted));
          localStorage.removeItem('inv_autoDeferHour');
          return converted;
        }
      }
      return 0;
    } catch (e) { return 0; }
  });
  // v7.127: 접속 시 Supabase에서 최신 설정 조회 → 다른 PC의 변경 반영
  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.from('app_settings')
          .select('value').eq('key', 'autoDeferMinutes').maybeSingle();
        if (cancelled || error) return;
        if (data?.value != null) {
          const p = parseInt(data.value, 10);
          if (Number.isFinite(p) && p >= 0 && p < 1440 && p % 30 === 0) {
            setAutoDeferMinutes(p);
            try { localStorage.setItem('inv_autoDeferMinutes', String(p)); } catch(e) {}
          }
        }
      } catch (e) { /* Supabase 실패 시 localStorage 값 유지 */ }
    })();
    return () => { cancelled = true; };
  }, [connected]);
  const setAutoDeferMinutesPersist = async (m) => {
    const v = Number.isFinite(m) && m >= 0 && m < 1440 && m % 30 === 0 ? m : 0;
    setAutoDeferMinutes(v);
    try { localStorage.setItem('inv_autoDeferMinutes', String(v)); } catch(e) {}
    // v7.127: Supabase에 upsert (다른 PC와 공유)
    try {
      await supabase.from('app_settings').upsert({
        key: 'autoDeferMinutes',
        value: String(v),
        updated_at: new Date().toISOString(),
      }, { onConflict: 'key' });
    } catch(e) {
      console.warn('설정 공유 저장 실패 (localStorage만 반영):', e);
    }
  };
  // ─── v7.216: 퀵 배송 → 구글시트 자동 기록 (n8n 웹훅 경유) ───
  //   구글 자격증명을 프론트엔드에 두면 안 되므로(Inventura는 Auth 없이 외부 공개),
  //   Inventura는 n8n 웹훅만 호출하고 시트 쓰기·인증은 n8n이 서버에서 처리한다.
  //   URL은 하드코딩하지 않고 app_settings에 저장 → 재빌드 없이 변경 가능.
  const [quickSheetWebhook, setQuickSheetWebhook] = useState(() => {
    try { return localStorage.getItem('inv_quickSheetWebhook') || ''; } catch (e) { return ''; }
  });
  const [quickSheetSending, setQuickSheetSending] = useState(null); // 전송 중인 item.id
  // v7.217: 배차 시트 '담당자 번호' 칸 — 작성자(직원)명 → 전화번호 매핑.
  //   소스에 개인 번호를 박지 않고 app_settings에 두어 앱에서 수정 가능하게 한다.
  const [staffPhones, setStaffPhones] = useState({});
  // v7.456: 작성자/요청자 목록 — 하드코딩 STAFF_LIST 를 시드로, app_settings 에서 공유 로드
  const [staffList, setStaffList] = useState(STAFF_LIST);
  const [userMgmtOpen, setUserMgmtOpen] = useState(false);
  const [userMgmtNew, setUserMgmtNew] = useState('');
  const [staffPhoneModal, setStaffPhoneModal] = useState(false);
  useEffect(() => {
    if (!connected) return;
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase.from('app_settings')
          .select('key,value').in('key', ['quickSheetWebhook', 'staffPhones', 'staffList']);
        if (cancelled || error) return;
        for (const row of (data || [])) {
          if (row.key === 'quickSheetWebhook' && row.value != null) {
            setQuickSheetWebhook(row.value);
            try { localStorage.setItem('inv_quickSheetWebhook', row.value); } catch (e) {}
          }
          if (row.key === 'staffPhones' && row.value) {
            try { setStaffPhones(JSON.parse(row.value) || {}); } catch (e) {}
          }
          if (row.key === 'staffList' && row.value) {
            try { const arr = JSON.parse(row.value); if (Array.isArray(arr) && arr.length) setStaffList(arr); } catch (e) {}
          }
        }
      } catch (e) { /* 실패 시 localStorage 값 유지 */ }
    })();
    return () => { cancelled = true; };
  }, [connected]);

  const setQuickSheetWebhookPersist = async (url) => {
    const v = (url || '').trim();
    setQuickSheetWebhook(v);
    try { localStorage.setItem('inv_quickSheetWebhook', v); } catch (e) {}
    try {
      await supabase.from('app_settings').upsert({
        key: 'quickSheetWebhook', value: v, updated_at: new Date().toISOString(),
      }, { onConflict: 'key' });
    } catch (e) {
      console.warn('웹훅 URL 공유 저장 실패 (localStorage만 반영):', e);
    }
  };

  // v7.456: 사용자 목록 공유 저장 + 추가
  const setStaffListPersist = async (arr) => {
    setStaffList(arr);
    try {
      await supabase.from('app_settings').upsert({
        key: 'staffList', value: JSON.stringify(arr), updated_at: new Date().toISOString(),
      }, { onConflict: 'key' });
    } catch (e) { console.warn('사용자 목록 공유 저장 실패:', e); }
  };
  const addUser = () => {
    const v = userMgmtNew.trim();
    if (!v) return;
    if (staffList.includes(v)) { alert('이미 있는 사용자입니다.'); return; }
    setStaffListPersist([...staffList, v]);
    setUserMgmtNew('');
  };
  const setStaffPhonesPersist = async (map) => {
    setStaffPhones(map);
    try {
      await supabase.from('app_settings').upsert({
        key: 'staffPhones', value: JSON.stringify(map), updated_at: new Date().toISOString(),
      }, { onConflict: 'key' });
    } catch (e) {
      console.warn('담당자 번호 저장 실패:', e);
    }
  };

  // ─── 메모 (공유, Supabase memos 테이블) ───
  const [memos, setMemos] = useState([]);
  const [memosLoading, setMemosLoading] = useState(false);
  const [editingMemoId, setEditingMemoId] = useState(null); // 인라인 편집 중인 메모 ID
  const [memoDrafts, setMemoDrafts] = useState({}); // { [id]: draftContent } — 편집 중 임시값
  const [newMemoOpen, setNewMemoOpen] = useState(false); // 새 메모 입력 영역 열림
  const [newMemoContent, setNewMemoContent] = useState('');
  // v7.417: 새 메모 작성 중 담아둔 첨부(File 객체) — [추가] 시 생성된 메모 id 로 업로드한다.
  const [newMemoFiles, setNewMemoFiles] = useState([]);
  const newMemoFileRef = useRef(null);
  const [newMemoColor, setNewMemoColor] = useState('yellow');
  const [newMemoAuthor, setNewMemoAuthor] = useState('');
  // v7.190: 새 메모 별점 (0=미설정, 1~5)
  const [newMemoImportance, setNewMemoImportance] = useState(0);
  // 메모 카드 크기 (S / M / L) — localStorage 영속
  const [memoCardSize, setMemoCardSize] = useState(() => {
    try {
      const v = localStorage.getItem('inventura_memo_size');
      // 'm' (구버전) 또는 비어있으면 's'로 기본값
      if (v === 's' || v === 'l') return v;
      return 's';
    } catch(e){ return 's'; }
  });
  const [memoDragId, setMemoDragId] = useState(null); // 드래그 중인 메모 ID
  // v7.81: 배송시트지 row 드래그 재정렬 — 같은 transaction_date 그룹 내에서만 허용
  const [shipRowDragId, setShipRowDragId] = useState(null);
  // 메모 편집용 contenteditable ref (한 번에 한 메모만 편집 가능)
  const memoEditRef = useRef(null);
  // v7.196: 메모 편집기 undo/redo 스택 (Ctrl+Z / Ctrl+Shift+Z · Ctrl+Y)
  //   네이티브 undo는 편집 진입 시 innerHTML을 코드로 세팅하는 순간 스택이 초기화되고,
  //   execCommand(foreColor/insertText)와 섞이면 되돌림 단위가 뒤죽박죽이라 직접 구현.
  //   { html, caret } 스냅샷을 쌓고 idx를 움직여 복원한다.
  const memoUndoRef = useRef({ memoId: null, stack: [], idx: -1, lastPush: 0 });
  // 메모 기록 (완료된 메모)
  const [memoHistoryOpen, setMemoHistoryOpen] = useState(false);
  const [memoHistoryMonth, setMemoHistoryMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [completedMemos, setCompletedMemos] = useState([]);
  const [completedMemosLoading, setCompletedMemosLoading] = useState(false);
  const [memoHistorySelectedDate, setMemoHistorySelectedDate] = useState(null);
  // v7.416: 기록된 메모 클릭 시 내용+첨부를 보여주는 상세 모달 대상
  const [memoHistoryDetail, setMemoHistoryDetail] = useState(null);
  // v7.450: 메모 기록 검색
  const [memoSearchOpen, setMemoSearchOpen] = useState(false);
  const [memoSearchQuery, setMemoSearchQuery] = useState('');
  const [memoSearchResults, setMemoSearchResults] = useState([]);
  const [memoSearchLoading, setMemoSearchLoading] = useState(false);
  const [memoSearched, setMemoSearched] = useState(false);
  // 🥚 이스터에그: 재고목록에서 'rest' 입력 시 휴식 모드
  const [restMode, setRestMode] = useState(false);
  const [restPhase, setRestPhase] = useState('intro'); // 'intro' (Strands) → 'video'
  const [restVideos, setRestVideos] = useState([]);   // 자동 탐지된 영상 파일 목록
  const [restVideo, setRestVideo] = useState(null);   // 현재 재생 중인 영상 파일명
  // 🥚 이스터에그: 재고목록에서 'mine' 입력 시 지뢰찾기
  const [mineMode, setMineMode] = useState(false);
  // 🥚 이스터에그: 재고목록에서 'mirror' 입력 시 3D 큐브 미로 (v7.85)
  const [mirrorMode, setMirrorMode] = useState(false);
  // 🥚 이스터에그: 재고목록에서 'aim' 입력 시 에임 연습장 (v7.91)
  const [aimMode, setAimMode] = useState(false);
  // 🥚 이스터에그 (v7.371): '정보 없는 빈 공간'을 10번 연속 클릭 → 불꽃놀이.
  //   검색창 명령어(rest/mine/…)와 달리 [클릭] 트리거라 카운터를 ref 로 잡는다
  //   (리렌더 없이 세야 하고, 값이 바뀌어도 재구독이 필요 없다).
  const [fireworksMode, setFireworksMode] = useState(false);
  const emptyClickCountRef = useRef(0);
  const emptyClickLastRef = useRef(0);
  // v7.86: 출고완료 시리얼 사후 교체 — 상세 모달의 시리얼 pill 편집 아이콘에서 열림
  //   state 구조: null 또는 { log, itemIdx, oldSerial, productName }
  const [serialEditState, setSerialEditState] = useState(null);
  const [serialEditInput, setSerialEditInput] = useState('');
  const [serialEditCheck, setSerialEditCheck] = useState(null); // {ok, found, status, product_name, error, msg}
  const [serialEditBusy, setSerialEditBusy] = useState(false);
  const [stats, setStats] = useState({total:0,stock:0,shipped:0});
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [stockTypeFilter, setStockTypeFilter] = useState('all'); // v7.27: 신품/리페킹/조립출고 필터
  const [txMatchedSerials, setTxMatchedSerials] = useState(new Set()); // v7.29: transaction_log 매칭된 시리얼 (검색 시 채움)
  const [txMatchedModels, setTxMatchedModels] = useState(new Set()); // v7.30: transaction_log 매칭된 모델 (시리얼 미출고 포함, 안내용)
  const [pg, setPg] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const invScrollRef = React.useRef(null);
  // Inventory List 비고 (memo) 인라인 편집 state
  const [inventoryMemos, setInventoryMemos] = useState({}); // {serial: 'draft text'}
  const [savingMemoSerial, setSavingMemoSerial] = useState(null);
  // v7.97: 재고 목록 입고일 셀 더블클릭 인라인 편집
  const [editingReceiptSerial, setEditingReceiptSerial] = useState(null);
  const [savingReceiptSerial, setSavingReceiptSerial] = useState(null);
  const [savingStatusSerial, setSavingStatusSerial] = useState(null);
  const [expandedSerial, setExpandedSerial] = useState(null); // v7.236: 클릭한 행의 시리얼 (일반 목록·검색 결과 모두 전 항목)
  // v7.193: 재고 목록에서 시리얼 단건 삭제 진행중 표시
  const [deletingSerial, setDeletingSerial] = useState(null);
  const [sortCategory, setSortCategory] = useState(null); // (deprecated, 호환용)
  // 재고 목록 정렬 컬럼 — 기본은 입고일 최신순
  const [invSortBy, setInvSortBy] = useState('receipt_date'); // 'serial'|'model'|'category'|'receipt_date'|'sale_date'|'status'
  // v7.40: 재고 목록 페이지 안의 탭 — 'list' (재고 목록) | 'dashboard' (재고 대시보드)
  // v7.400: 기본 진입 탭을 [재고 대시보드] 로 (사용자 요청 "재고 대시보드가 가장 먼저 오도록").
  // v7.420: 기본 진입 탭을 [재고 목록] 로 되돌림 (사용자 요청 "재고목록이 가장먼저 보이도록"). 탭 순서는 유지.
  const [inventoryTab, setInventoryTab] = useState('list');
  const [invSortDir, setInvSortDir] = useState('desc'); // 'asc' | 'desc'
  const [calendarOpen, setCalendarOpen] = useState(false); // 이벤트 캘린더 모달
  // v7.139: 재고 목록 엑셀 다운로드 진행 상태
  const [downloadingInvXlsx, setDownloadingInvXlsx] = useState(false);
  // v7.11: 재고 대시보드 페이지
  const [stockDashboard, setStockDashboard] = useState({
    loading: false,
    products: [],   // [{product_name, ...}]
    counts: {},     // {product_name: in_stock_count}
    stockTypeCounts: {}, // v7.22: {product_name: {신품, 리페킹, 조립출고, 기타}}
    categoryMap: {}, // v7.17: {product_name: category}
    selectedCategory: 'ALL', // v7.17: 'ALL' | 카테고리명 | '__UNCAT__'
    search: '',
    sortMode: 'available_asc', // 'available_asc' | 'stock_desc' | 'name_asc' | 'zero_only'
    hideZero: false, // 0개 제품 숨기기
    statsExpanded: false, // v7.27: 디폴트 접힘
    // v7.309: 파주물류센터 재고 (ERP 파일 업로드 → Supabase paju_stock)
    paju: {},            // { 제품명: 수량 }
    pajuNorm: {},        // { 정규화 제품명: 수량 } — 표기 차이 2차 매칭
    pajuSnapshot: null,  // 'YYYY-MM-DD' 마감 기준일
    pajuReady: false,    // 테이블 조회 성공 여부 (SQL 미실행이면 false)
    pajuUploading: false,
    newOnly: false,      // v7.309: 신품 재고가 있는 제품만 보기
    dashMemo: {},        // v7.401: { product_name: 비고 } — dashboard_memo 테이블
  });
  // v7.401: 대시보드 비고 인라인 편집 상태 (직접 입력 → dashboard_memo 서버 저장)
  const [dashMemoEditing, setDashMemoEditing] = useState(null); // 편집 중 product_name
  const [dashMemoDraft, setDashMemoDraft] = useState('');
  // v7.23: 데모 관리 페이지
  const [demoState, setDemoState] = useState({
    loading: false,
    tab: 'equipment',          // 'equipment' | 'rentals' | 'returns'
    equipment: [],             // demo_equipment row 배열
    stats: {},                 // 카테고리별 통계
    activeRentals: [],         // 활성 대여
    returns: [],               // 회수 이력
    selectedCategory: 'ALL',
    locationFilter: 'all',     // 'all' | 'available' | 'loaned'
    statusFilter: 'all',
    search: '',
    expandedModels: new Set(), // v7.24: 펼친 모델명 (아코디언)
    statsExpanded: false, // v7.27: 디폴트 접힘
    editingNote: null,   // v7.35: 비고 인라인 편집 중인 장비 id { id, draft }
    editingRentalNote: null, // v7.330: 대여로그 비고 편집 중 { id, draft }
  });
  // v7.25: 데모 모달 (장비추가 / 대여등록 / 회수처리)
  const [demoAddModal, setDemoAddModal] = useState(null);
  // v7.453: 데모 모델명 자동완성
  const [demoModelResults, setDemoModelResults] = useState([]);
  const [demoModelOpen, setDemoModelOpen] = useState(false);
  useEffect(() => { if (!demoAddModal) { setDemoModelResults([]); setDemoModelOpen(false); } }, [demoAddModal]);
  // null | { category, model, serial_number, status, model_detail, location, note, loading, result }
  const [demoLendModal, setDemoLendModal] = useState(null);
  // null | { company, shipment_date, expected_return_date, managers, requester, notes,
  //          search, searchCategory, searchResults, selectedItems, loading, result }
  const [demoReturnModal, setDemoReturnModal] = useState(null);
  // v7.43: 데모 대여 수정 이력 모달 — { rental, log: [] }
  const [demoHistoryModal, setDemoHistoryModal] = useState(null);
  // v7.44: 데이터 백업 페이지 — { loading, progress(0-100), status, lastBackup?, result? }
  const [backupState, setBackupState] = useState({ loading: false, progress: 0, status: '대기 중' });
  // v7.58: HDD/SSD 일괄 입고 — 헤더 수기 입력 + 시리얼 텍스트 붙여넣기 방식
  // (v7.57의 Excel 파일 업로드 방식 폐기 — 바코드 리더기 출력 txt 한 줄당 1시리얼이 실제 워크플로)
  // v7.59: 출처 옵션 — '이선' 프리셋 / 수기입력 토글
  // v7.64: 제품명(카탈로그) + 모델명(수기) 분리. 카탈로그 매칭 강제 — 임의 등록 방지
  const [hddInbound, setHddInbound] = useState({
    source: '이선',
    sourceMode: 'preset',
    inDate: today(),
    inDateUnknown: false,       // v7.96: 입고일 미확인 옵션 (체크 시 receipt_date=null 저장)
    bookkept: false,            // v7.183: 장부처리 완료 여부
    // v7.64: 카탈로그 제품 선택
    productName: '',            // 선택된 카탈로그 product_name (확정값)
    productSearch: '',          // 자동완성 검색용 input
    productResults: [],         // 검색 결과 [{ product_name, category }]
    productSearchOpen: false,
    productSearching: false,
    // v7.64: 시리얼 단위 모델 번호 (수기, 예: ST24000NT002) — inventory.model_no
    modelNo: '',
    // v7.66: 선택된 제품의 카탈로그 카테고리 (표시용, register 시 사용)
    selectedCategory: null,
    stockType: '신품',
    serialText: '',
    parsedSerials: [],
    existingInDb: [],
    duplicatesInPaste: [],
    registering: false,
    registerProgress: 0,
    // v7.61: 카탈로그 즉시 등록 미니 모달 — 모달 내 의미 변경 (모델명→제품명)
    showQuickRegister: false,
    quickRegisterAlias: '',
    quickRegisterCategory: 'HDD',
    quickRegisterSaving: false,
  });
  // null | { rental, return_date, managers, notes, loading, result }
  // 물品 등록 (다중 제품 배치) state
  const [regBatches, setRegBatches] = useState([]); // 모든 배치: [{product:{product_name,category}, serials:[], from:'taiwan'}]
  const [regActiveIndex, setRegActiveIndex] = useState(-1); // 활성 배치 인덱스 (-1: 신규 입력 대기)
  const [regSerialInput, setRegSerialInput] = useState(''); // 현재 입력 중인 시리얼
  const regSerialInputRef = useRef(null); // 시리얼 input 포커스 자동 이동용
  // DB 시리얼 캐시 (입력 시점 빠른 중복 체크용 — 입고 모달 열릴 때 한 번 로드)
  const [regSerialCache, setRegSerialCache] = useState(() => new Set());
  const [regProductSearch, setRegProductSearch] = useState('');
  const [regProductResults, setRegProductResults] = useState([]);
  // 신제품 등록 모달 — {name, category, saveToCatalog, mode: 'new'|'alias', aliasFor?: string, aliasSearch?: string, aliasResults?: []}
  const [newProductModal, setNewProductModal] = useState(null);
  const [productCategories, setProductCategories] = useState([]); // 마스터 카테고리 목록
  // 별칭(alias) 관리 모달 — {productName, aliases: [], newAlias: '', loading: bool}
  const [aliasManageModal, setAliasManageModal] = useState(null);
  // 카탈로그 행별 별칭 개수 (배지 표시용) — { [product_name]: count }
  const [aliasCounts, setAliasCounts] = useState({});
  // 제품 카탈로그 관리 페이지
  const [catalogItems, setCatalogItems] = useState([]);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategoryFilter, setCatalogCategoryFilter] = useState('');
  const [catalogFormFactorFilter, setCatalogFormFactorFilter] = useState('');
  const [catalogEditing, setCatalogEditing] = useState(null); // {id, product_name, category, form_factor}
  // v7.7: 대량 제품 등록 모달
  const [bulkProductModal, setBulkProductModal] = useState(null); // null | {text, loading, result}
  // v7.14: 임시 시리얼 일괄 입고 모달 (HDD/SSD 등 라벨 모를 때)
  const [bulkUnknownModal, setBulkUnknownModal] = useState(null);
  // null | { product_name, product_search, product_results, model_code, qty, start_number,
  //          stock_type, source, warehouse, receipt_date, loading, result, success }
  const [catalogPg, setCatalogPg] = useState(0);
  const [catalogTotal, setCatalogTotal] = useState(0);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [regFrom, setRegFrom] = useState('taiwan'); // 새 배치의 기본 from (활성 배치 있으면 그 배치 값을 따름)
  const [regReceiptDate, setRegReceiptDate] = useState(today());
  // v7.226: useState(today())는 **페이지 로드 시 한 번만** 평가된다.
  //   창고 PC처럼 브라우저를 며칠째 켜두면 입고일이 로드 당시 날짜로 굳어버리고,
  //   배송시트지 기본 조회 기간이 '오늘 하루'라 등록해도 화면에 안 나타난다
  //   (DB에는 과거 날짜로 정상 저장 → "1번 PC는 안 보이고 2번 PC는 보임" 증상).
  //   사용자가 직접 고른 날짜는 존중하되, 손대지 않았으면 입고 페이지 진입 시 오늘로 맞춘다.
  const regDateTouchedRef = useRef(false);
  const [regNote, setRegNote] = useState('');
  const [regMsg, setRegMsg] = useState(null);
  // v7.183: 장부처리 완료 여부 — 등록 시 체크박스로 지정, note에 bookkept 저장
  const [regBookkept, setRegBookkept] = useState(false);
  // 대시보드 재고 부족 경고용
  const [allModels, setAllModels] = useState([]);
  // 재고 부족 경고 (입출고 로그 탭)
  const [lowStockThreshold, setLowStockThreshold] = useState(5);
  const [showLowStock, setShowLowStock] = useState(false);
  // ─── 킵(Keep) 관리 ───
  const [keepsItems, setKeepsItems] = useState([]);
  const [keepsLoading, setKeepsLoading] = useState(false);
  const [keepsSearch, setKeepsSearch] = useState('');
  const [keepsStatusFilter, setKeepsStatusFilter] = useState('all'); // 'all'|'active'|'shipped'|'released'
  const [keepsForm, setKeepsForm] = useState({
    // 공통 필드 (전체 킵에 적용)
    dealer_id: null,
    dealer_name: '',
    dealerSearch: '',
    dealerResults: [],
    author: '',
    requester: '',
    expected_date: '',
    memo: '',
    // 품목 리스트 — [{product_name, productSearch, productResults, qty, serial_number, serialResults, item_memo}]
    items: [{ product_name: '', productSearch: '', productResults: [], qty: 1, serials: [''], serial_number: '', serialResults: [], item_memo: '' }],
  });
  // 킵 인라인 편집 셀 — {id, field, value, serialResults?}
  const [editingKeepCell, setEditingKeepCell] = useState(null);
  // 킵 그룹 헤더 인라인 편집 셀 — {groupId, field, value}
  const [editingKeepGroupCell, setEditingKeepGroupCell] = useState(null);
  // 카드별 "품목 추가" 인라인 폼 state — { [groupId]: { product_name, productSearch, productResults, qty, serial_number, serialResults, item_memo } }
  const [addItemForms, setAddItemForms] = useState({});
  // 활성 킵 수량 맵 (모델별) — 출고요청 시 가용재고 계산용
  const [activeKeepsByModel, setActiveKeepsByModel] = useState({}); // { [product_name]: total_qty }
  // v7.311: 킵 중 '아직 재고에서 안 빠진' 수량만 따로 집계 — 가용 계산은 이쪽을 써야 한다.
  //   시리얼을 지정한 킵은 v7.156 부터 inventory.status='reserved' 로 바뀌어 in_stock 집계에서
  //   이미 빠져 있다. 거기서 킵 수량을 또 빼면 이중 차감이 된다.
  const [activeKeepsUnbackedByModel, setActiveKeepsUnbackedByModel] = useState({});
  // 출고처리 모달 — 그룹 단위 출고 시 배송시트지 작성에 필요한 추가 정보 입력
  // {group, addr, salesStatus, salesStatusManual, method, recipient, phone, author, requester}
  const [shipKeepModal, setShipKeepModal] = useState(null);
  // ─── /킵 관리 ───
  // ─── 재고 실사 (Stock Audit) ───
  const [auditFileName, setAuditFileName] = useState('');
  const [auditExcelRows, setAuditExcelRows] = useState([]); // [{model, hq, paju, total}]
  const [auditResults, setAuditResults] = useState([]); // 비교 결과
  const [auditFilter, setAuditFilter] = useState('all'); // all|match|short|over|only_excel|only_system
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditMsg, setAuditMsg] = useState(null);

  // ─── 물품 등록: 활성 배치 derived 값 (UI 호환용) ───
  const activeBatch = regActiveIndex >= 0 && regActiveIndex < regBatches.length ? regBatches[regActiveIndex] : null;
  const regProduct = activeBatch?.product || null;
  const regSerials = activeBatch?.serials || [];
  const currentFrom = activeBatch?.from || regFrom;
  // v7.305: 등록 예정 수량 — handleRegister 의 validBatches 와 '정확히 같은 기준'으로 센다.
  //   regSerials 는 regBatches[regActiveIndex].serials 를 그대로 가리키는 derived 값인데
  //   (7380 줄 주석대로 구 구조 호환용), 카운트 코드가 regBatches 전체 + regSerials 를
  //   더하고 있어서 활성 배치가 항상 두 번 세어졌다.
  //     1제품 7시리얼 → "2개 제품 · 14개 시리얼"
  //   실제 등록은 handleRegister 가 regBatches 만 보므로 정상이었고, 표시만 틀렸다.
  const regPendingSerial = regSerialInput.trim();
  const regPendingCounts = !!(
    regPendingSerial &&
    regActiveIndex >= 0 && regActiveIndex < regBatches.length &&
    !regBatches.some(b => b.serials.includes(regPendingSerial))
  );
  // 시리얼 0개인 배치는 handleRegister 가 걸러내므로 제품 수에서도 뺀다
  const regTotalProducts = regBatches.filter((b, i) =>
    b.serials.length > 0 || (regPendingCounts && i === regActiveIndex)
  ).length;
  const regTotalSerials = regBatches.reduce((s, b) => s + b.serials.length, 0) + (regPendingCounts ? 1 : 0);

  // Request fulfillment state (from txlog "제품 출고")
  const [activeRequest, setActiveRequest] = useState(null);
  // 출고 처리 모달 (배송시트지에서 [제품 출고] 클릭 시 띄움 — 페이지 이동 없이)
  const [outboundModalOpen, setOutboundModalOpen] = useState(false);
  // 입고 등록 모달 (배송시트지 우측 상단 [입고 등록] 버튼 클릭 시)
  // v7.226: 입고 페이지 진입 시, 사용자가 직접 고르지 않았다면 입고일을 오늘로 재설정
  // v7.227: 출고요청 페이지도 동일 처리
  useEffect(() => {
    const t = today();
    if (page === 'inbound' && !regDateTouchedRef.current) {
      setRegReceiptDate(prev => (prev === t ? prev : t));
    }
    if (page === 'ship' && !shipDateTouchedRef.current) {
      setShipExpectedDate(prev => (prev === t ? prev : t));
    }
  }, [page]);

  // v7.227: 창을 오래 켜둔 채 자정을 넘기면 탭 이동이 없어도 날짜가 굳는다.
  //   창고 PC는 며칠씩 같은 화면을 띄워두므로, 화면이 다시 보이거나 포커스를 얻을 때도 갱신.
  //   (사용자가 직접 고른 날짜는 여기서도 건드리지 않는다)
  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState === 'hidden') return;
      const t = today();
      if (!regDateTouchedRef.current) setRegReceiptDate(prev => (prev === t ? prev : t));
      if (!shipDateTouchedRef.current) setShipExpectedDate(prev => (prev === t ? prev : t));
    };
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
    };
  }, []);

  // v7.40: stockInModalOpen 상태 제거됨 — 입고 등록은 사이드바 전용 페이지(page='inbound')로 이동
  // 배송시트지 헤더 통합 메뉴 (재고부족/입고등록/입출고요약)
  // v7.260: 다크모드 — 테마 상태
  //   저장 우선순위: localStorage('inv_theme') > light (v7.261: OS 추종 제거)
  //   실제 적용은 <html data-theme="dark"> 속성. CSS 변수는 App.js 전역 <style>의 :root / [data-theme="dark"]에 있다.
  //   속성이 없으면 :root(라이트) 값만 걸리므로, 기존 화면은 픽셀 단위로 동일하다.
  const [theme, setTheme] = useState(() => {
    try {
      const saved = localStorage.getItem('inv_theme');
      if (saved === 'light' || saved === 'dark') return saved;
    } catch (e) {}
    // v7.261: OS 설정(prefers-color-scheme) 추종 제거 — 기본은 항상 라이트.
    //   이유: OS가 다크인 직원은 앱을 처음 열자마자 다크로 뜬다. 켠 적이 없는데 켜져 있으면
    //         '고장'으로 보인다. 다크는 메뉴에서 명시적으로 켠 사람에게만 적용한다.
    //   한 번 토글하면 localStorage('inv_theme')에 남아 다음부터 그 값이 유지된다.
    return 'light';
  });
  useEffect(() => {
    try {
      if (theme === 'dark') document.documentElement.setAttribute('data-theme', 'dark');
      else document.documentElement.removeAttribute('data-theme');
    } catch (e) {}
    try { localStorage.setItem('inv_theme', theme); } catch (e) {}
  }, [theme]);

  const [headerMenuOpen, setHeaderMenuOpen] = useState(false);
  // v7.161: App-level backHandler가 참조할 ref 미러 동기화 (위에 선언된 backStateRef)
  useEffect(() => { backStateRef.current.outbound = outboundModalOpen; }, [outboundModalOpen]);
  useEffect(() => { backStateRef.current.headerMenu = headerMenuOpen; }, [headerMenuOpen]);
  const [requestSerialInput, setRequestSerialInput] = useState('');
  // v7.52: 출고 모달 시리얼 자동완성
  const [serialSuggestions, setSerialSuggestions] = useState([]); // [{ serial_number }]
  const [serialSuggestionsOpen, setSerialSuggestionsOpen] = useState(false);
  const [serialSuggestionIdx, setSerialSuggestionIdx] = useState(-1); // 키보드 선택 인덱스
  const [requestMsg, setRequestMsg] = useState(null);
  const [pendingSerials, setPendingSerials] = useState([]); // 커밋 대기 중인 시리얼들
  // v7.82: pendingSerials 변경 시 localStorage에 자동 저장 (새로고침 후 복원용)
  //         activeRequest.reqId를 키로 사용. 빈 배열이면 삭제.
  useEffect(() => {
    const reqId = activeRequest?.reqId;
    if (!reqId) return;
    if (pendingSerials.length > 0) {
      saveShipPending(reqId, pendingSerials);
    } else {
      clearShipPending(reqId);
    }
  }, [pendingSerials, activeRequest?.reqId]);
  // v7.82: 앱 시작 시 오래된 pending 정리 (7일 초과)
  // v7.149: Supabase측 stale도 정리 (best effort, 실패 무시)
  // v7.175: 자동 정리 비활성화 — 사용자 요청에 따라 수동으로만 정리.
  //          cleanupStaleShipPending / ShipPendingService.cleanupStale 함수 자체는 그대로 유지 (필요 시 재활성화 가능).
  //          기존 동작(커밋 성공 시 clearShipPending)은 유지되므로 정상 배송 완료 건은 계속 삭제됨.
  //          중단된 요청의 pending 시리얼은 이제 무한 보존 → 필요 시 SQL로 직접 정리:
  //            DELETE FROM ship_pending_serials WHERE updated_at < NOW() - INTERVAL '30 days';
  // useEffect(() => {
  //   cleanupStaleShipPending();
  //   ShipPendingService.cleanupStale().catch(() => {});
  // }, []);
  // v7.424: 앱 시작 시 '고아 ship_hold' 정합성 회복 (연결되면 1회).
  //   ship_pending_serials 에 대응 행이 없는 ship_hold 시리얼만 in_stock 으로 되돌린다.
  //   → 어떤 해제 경로를 놓쳐 시리얼이 ship_hold 로 묶여 재고에서 사라지는 것을 self-heal.
  //   ⚠ 시간 기반 정리(v7.175 로 비활성)가 아니다. 정상 pending 은 ship_pending 행이 있어 건드리지 않는다.
  const orphanReconciledRef = useRef(false);
  useEffect(() => {
    if (!connected || orphanReconciledRef.current) return;
    orphanReconciledRef.current = true;
    InventoryService.reconcileOrphanHolds()
      .then(r => { if (r?.released) console.warn(`[ship_hold 정합성] 고아 예약 ${r.released}개 → in_stock 복구`); })
      .catch(() => {});
  }, [connected]);
  // Ship request state
  const [shipSearch, setShipSearch] = useState('');
  const [shipResults, setShipResults] = useState([]);
  const [shipItems, setShipItems] = useState([]);
  const [shipAddr, setShipAddr] = useState('');
  // v7.195: 우편번호(recZip) + 상세주소(recAddr2) — 우체국 API 대비
  const [shipZip, setShipZip] = useState('');
  const [shipAddrDetail, setShipAddrDetail] = useState('');
  // 배송지 자동완성 (이전 출고 요청에서 사용한 주소 캐시)
  const [addressHistory, setAddressHistory] = useState([]); // [{addr, recipient, phone, lastDate, count}]
  const [addrSuggestOpen, setAddrSuggestOpen] = useState(false);
  const [addrSuggestIndex, setAddrSuggestIndex] = useState(-1);
  const [shipRecipient, setShipRecipient] = useState('');
  const [shipPhone, setShipPhone] = useState('');
  const [shipRequester, setShipRequester] = useState('');
  const [shipAuthor, setShipAuthor] = useState('');
  const [shipMemo, setShipMemo] = useState(''); // 출고요청 비고 (자유 입력)
  const [shipMethod, setShipMethod] = useState('courier');
  const [shipTrackingNo, setShipTrackingNo] = useState(''); // 택배 송장번호
  const [shipQuickPrice, setShipQuickPrice] = useState(''); // 퀵서비스 가격 (원)
  const [shipQuickCOD, setShipQuickCOD] = useState(false); // v7.54: 퀵서비스 착불 여부
  // v7.217: 퀵 배차 시트의 '차량' 칸 (오토바이/다마스/라보 등) — 자유 입력 + 추천값
  const [shipQuickVehicle, setShipQuickVehicle] = useState('');
  // v7.218: 시트 '지역' 칸 — 빈 문자열이면 주소에서 자동 추출값을 사용, 값이 있으면 수동 우선
  const [shipQuickRegion, setShipQuickRegion] = useState('');
  // v7.228: 지역은 기본 잠금(자동 추출값 표시). 체크해야 수기 입력이 열린다.
  const [shipRegionManual, setShipRegionManual] = useState(false);
  // v7.220: 배차 시트 '기사님 번호'(I열) — 퀵 기사 연락처
  const [shipQuickDriverPhone, setShipQuickDriverPhone] = useState('');
  // v7.231: 퀵 배차 시트 '비고'(J열) — 배송시트지 비고와 별개의 정보
  const [shipQuickRemark, setShipQuickRemark] = useState('');
  const [shipDirectCompany, setShipDirectCompany] = useState(''); // 직발 업체명
  // v7.103: 네이버스토어 주문 여부 (직발과 동일 조건 — courier/quick 모드에서만 노출)
  const [shipNaverStore, setShipNaverStore] = useState(false);
  // RMA 필드
  const [shipRmaProductName, setShipRmaProductName] = useState('');
  const [shipRmaSerial, setShipRmaSerial] = useState('');
  const [shipRmaSerialFromDB, setShipRmaSerialFromDB] = useState(false); // 시리얼이 DB에 있는지
  const [shipRmaSerialLookupMsg, setShipRmaSerialLookupMsg] = useState(null); // {type, msg}
  const [shipRmaFormNo, setShipRmaFormNo] = useState('');
  const [shipRmaTicketNo, setShipRmaTicketNo] = useState('');
  const [shipRmaEms, setShipRmaEms] = useState('');
  const [shipRmaReturnSerial, setShipRmaReturnSerial] = useState(''); // v7.76: DOA 반품(들어오는) 시리얼
  // v7.99: RMA/DOA 제품명 자동완성 (제품 표준화 카탈로그 기반)
  //         shipRmaProductResults: 드롭다운 표시용 후보 배열 ([{product_name, category}])
  //         shipRmaProductCatalogHit: null=미검증, true=정확 매칭, false=미매칭
  const [shipRmaProductResults, setShipRmaProductResults] = useState([]);
  const [shipRmaProductCatalogHit, setShipRmaProductCatalogHit] = useState(null);
  // v7.77: [수리 완료] 클릭 시 DOA인데 반품 시리얼 없을 때 팝업으로 강제 입력받음
  //         null | { item, shipInfo, inputSerial, submitting }
  const [doaReturnSerialModal, setDoaReturnSerialModal] = useState(null);
  const [shipEmsType, setShipEmsType] = useState('RMA'); // 'RMA' or 'DOA'
  const [shipSalesStatus, setShipSalesStatus] = useState('입력필요');
  const [shipQuickSalesStatus, setShipQuickSalesStatus] = useState('입력필요');
  // 매출여부 "수기입력" 선택 시 자유 텍스트
  const [shipSalesStatusManual, setShipSalesStatusManual] = useState('');
  const [shipQuickSalesStatusManual, setShipQuickSalesStatusManual] = useState('');
  const [shipExpectedDate, setShipExpectedDate] = useState(today());
  // v7.227: regReceiptDate와 동일한 stale 문제. 여기가 더 위험한 이유 —
  //   ① transaction_date 가 과거로 박혀 배송시트지 기본 화면에서 사라지고
  //   ② autoDefer = !(shipExpectedDate < today()) 이므로 **자동 이월까지 꺼진다**
  //   미래 날짜는 정상 사용(내일 출고 예약 등)이라 '오늘 아님'이 아니라 '과거'만 경고한다.
  const shipDateTouchedRef = useRef(false);
  const [shipVisitTime, setShipVisitTime] = useState('');
  // v7.212: 방문수령 시 방문하는 업체명 (방문자 이름과 별개)
  const [shipPickupCompany, setShipPickupCompany] = useState('');
  // shipPickup은 shipMethod === 'pickup'에서 파생 (별도 state 없음)
  const shipPickup = shipMethod === 'pickup';
  const [shipMsg, setShipMsg] = useState(null);
  // Transaction Log (입출고 로그) state
  const [logItems, setLogItems] = useState([]);
  // v7.378: 배송시트지에 뜬 품목들의 현재 재고 수량 — { [product_name]: 수량 }
  //   ⚠ null = 아직 안 받음. {} 나 0 과 반드시 구분해야 한다.
  //     구분 못 하면 로딩 중에 모든 품목이 [재고없음] 으로 빨갛게 깜빡인다.
  const [logStockByModel, setLogStockByModel] = useState(null);
  // v7.422: 데모(fromDemoLend) 품목은 inventory 가 아니라 demo_equipment 를 참조한다.
  //   여기에는 배송시트지에 실린 데모 품목 모델명 중 demo_equipment 에 '등록된' 모델만 담긴다.
  //   null = 아직 조회 안 함(경고 표시 보류), Set = 조회 완료.
  const [logDemoModels, setLogDemoModels] = useState(null);
  // v7.434: 행(req_id)별·모델별로 '이미 담아둔(pending) 수량' — { [reqId]: { [model]: count } }
  //   v7.424 부터 담은 시리얼은 inventory 가 ship_hold 라 in_stock 집계에서 빠진다.
  //   그래서 [재고부족] 판정 시 자기 건이 확보해 둔 수량을 여기서 되더해 줘야 한다.
  const [logPendingByReq, setLogPendingByReq] = useState(null);

  // ─── v7.378: 배송시트지 품목들의 현재 재고 조회 ─────────────
  //   ★ 목적: 요청은 걸려 있는데 지금 재고가 없는 품목을 시트지에서 바로 알아보게 한다.
  //     지금까지는 모달을 열어 스캔을 시도해야만 재고가 없다는 걸 알 수 있었다.
  //   ⚠ 출고 건만 본다. 입고 행은 [아직 안 들어온 물건] 이라 재고 0이 당연하고,
  //     거기에 빨간 표시를 달면 입고 시트지가 통째로 빨개진다.
  //   ⚠ 이미 처리된(출고완료) 건도 제외한다. 나간 물건의 재고를 지금 세는 건 의미가 없고,
  //     방금 출고해서 0이 된 품목이 빨갛게 뜨면 오해만 만든다.
  useEffect(() => {
    if (page !== 'txlog' || !logItems.length) { setLogStockByModel(null); return; }
    let alive = true;
    const names = new Set();
    logItems.forEach(row => {
      // ⚠ 전용 파서가 없다. 시트지 렌더와 같은 방식으로 note 를 직접 읽는다.
      let info = null;
      try { const pj = JSON.parse(row.note); if (pj && pj.type) info = pj; } catch (e) {}
      if (row.type === 'inbound' || info?.type === 'inbound') return;
      if (info?.fulfilled) return;                     // 처리 완료 건 제외
      const its = Array.isArray(info?.items) ? info.items : null;
      if (its) its.forEach(it => { if (it.product_name && !it.external) names.add(it.product_name); });
      else if (row.model) names.add(row.model);
    });
    if (names.size === 0) { setLogStockByModel({}); return; }
    (async () => {
      const map = await InventoryService.countInStockByNames([...names]);
      if (alive) setLogStockByModel(map || {});
    })();
    return () => { alive = false; };
  }, [page, logItems]);

  // ─── v7.422: 배송시트지 데모 품목 → demo_equipment 등록 여부 조회 ─────────────
  //   데모 대여로 작성된 행(note.fromDemoLend)의 품목은 inventory 재고 개념이 없다
  //   (실물은 demo_equipment 에서 관리). 그래서 재고없음/재고부족 대신 [데모항목] 으로 표기하고,
  //   그 모델이 demo_equipment 에 아예 없을 때만 [데모 미등록] 경고를 띄운다.
  //   ⚠ 여기서는 '존재 여부' 만 모은다 — 가용 수량이 아니라서 자기 예약분 이슈가 없다.
  useEffect(() => {
    if (page !== 'txlog' || !logItems.length) { setLogDemoModels(null); return; }
    let alive = true;
    const names = new Set();
    logItems.forEach(row => {
      let info = null;
      try { const pj = JSON.parse(row.note); if (pj && pj.type) info = pj; } catch (e) {}
      const isDemoRow = !!info?.fromDemoLend;
      const its = Array.isArray(info?.items) ? info.items : null;
      if (its) its.forEach(it => {
        if ((isDemoRow || it.serialOptionalReason === 'demo_lend') && it.product_name) names.add(it.product_name);
      });
    });
    if (names.size === 0) { setLogDemoModels(new Set()); return; }
    (async () => {
      const set = await DemoService.listRegisteredModels([...names]);
      if (alive) setLogDemoModels(set || new Set());
    })();
    return () => { alive = false; };
  }, [page, logItems]);

  // ─── v7.434: 배송시트지 행별 pending(담아둔) 수량 조회 ─────────────
  //   [재고부족] 판정에서 '자기 건이 이미 담아둔 수량'을 가용에 되더하기 위한 것.
  //   (담긴 시리얼은 ship_hold 라 in_stock 집계에서 빠져 있다 → 안 더하면 자기 물건인데 부족으로 뜬다)
  useEffect(() => {
    if (page !== 'txlog' || !logItems.length) { setLogPendingByReq(null); return; }
    let alive = true;
    const ids = [];
    logItems.forEach(row => {
      let info = null;
      try { const pj = JSON.parse(row.note); if (pj && pj.type === 'ship_request' && !pj.fulfilled && !pj.processedAs) info = pj; } catch (e) {}
      if (info) ids.push(info.reqId || row.serial);
    });
    if (ids.length === 0) { setLogPendingByReq({}); return; }
    (async () => {
      const map = await ShipPendingService.countByReqIds(ids);
      if (alive) setLogPendingByReq(map || {});
    })();
    return () => { alive = false; };
  }, [page, logItems]);

  const [logTotal, setLogTotal] = useState(0);
  const [logSearch, setLogSearch] = useState('');
  const [logFilter, setLogFilter] = useState('all');
  const [logPg, setLogPg] = useState(0);
  const [logPeriodMode, setLogPeriodMode] = useState('day'); // 'day' | 'week' | 'month' — 기본은 오늘 하루만
  const [logPeriodOffset, setLogPeriodOffset] = useState(0); // 0=현재, -1=이전, +1=다음
  // 캘린더 피커 (배송시트지 기간 선택 — EventCalendar 모달 사용)
  const [txPickerOpen, setTxPickerOpen] = useState(false);
  const [logStats, setLogStats] = useState({inbound:0,outbound:0,weekTotal:0});
  const [showTxLogSummary, setShowTxLogSummary] = useState(false);
  // 입출고 요약 모달 — 자체 기간 (배송시트지 logPeriod와 독립)
  const [summaryMode, setSummaryMode] = useState('week'); // 'week' | 'month'
  const [summaryOffset, setSummaryOffset] = useState(0);
  const [summaryRows, setSummaryRows] = useState([]);    // 그 기간의 transaction_log 전체
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [logModalItem, setLogModalItem] = useState(null); // 클릭한 로그를 모달로 표시
  // v7.458: 상세 모달 pending(준비) 시리얼 — { [model]: [serials] }. 처리 모달과 개수 일치용.
  const [logModalPending, setLogModalPending] = useState({});
  useEffect(() => {
    if (!logModalItem) { setLogModalPending({}); return; }
    let info = null;
    try { info = JSON.parse(logModalItem.note || '{}'); } catch (e) {}
    if (!info || info.type !== 'ship_request' || info.fulfilled || info.processedAs) { setLogModalPending({}); return; }
    const reqId = info.reqId || logModalItem.serial;
    let alive = true;
    (async () => {
      const res = await ShipPendingService.loadByReqId(reqId);
      if (!alive) return;
      const map = {};
      if (res && res.ok) res.data.forEach(p => { if (p.model) (map[p.model] = map[p.model] || []).push(p.serial); });
      setLogModalPending(map);
    })();
    return () => { alive = false; };
  }, [logModalItem]);
  const [logMemos, setLogMemos] = useState({});
  // v7.6: 입고 행 시리얼 펼침 토글 + 시리얼 더블클릭 편집
  const [expandedInbound, setExpandedInbound] = useState(() => new Set()); // 행 id Set
  const [editingInboundSerial, setEditingInboundSerial] = useState(null); // {itemId, product, idx}
  // v7.421: 입고 출처(source) 인라인 편집 — {itemId, product}
  const [editingInboundSource, setEditingInboundSource] = useState(null);
  // v7.446: 입고 품목 드래그 재정렬 상태
  const [inbDrag, setInbDrag] = useState(null);
  const inbDragRef = useRef(null);
  useEffect(() => { inbDragRef.current = inbDrag; }, [inbDrag]);
  const [inboundSourceSaving, setInboundSourceSaving] = useState(false);
  // 셀 인라인 편집 drafts — 택배 송장번호 / 퀵 금액 (item.id 기준)
  const [logTrackingDrafts, setLogTrackingDrafts] = useState({}); // {itemId: '12345678'}
  const [logPriceDrafts, setLogPriceDrafts] = useState({}); // {itemId: '10000'}
  // 배송시트지 인라인 편집 — {itemId: {field: 'addr', value: 'xxx'}}
  const [inlineEditCell, setInlineEditCell] = useState(null);
  // 모델명 변경 검색 모달
  const [modelSearchModal, setModelSearchModal] = useState(null); // {item, oldName} or null
  const [modelSearchQuery, setModelSearchQuery] = useState('');
  const [modelSearchResults, setModelSearchResults] = useState([]);
  const [modelSearchLoading, setModelSearchLoading] = useState(false);
  // v7.145: 품목 추가 시 외부제품 처리 옵션 (체크박스) — items에 external:true 부여용
  // v7.308: modelSearchAsExternal(외부 처리 체크박스) 제거 — 카탈로그 조회로 자동 판정한다
  const [refundingId, setRefundingId] = useState(null); // 환불 사유 입력 중인 row id
  const [refundReason, setRefundReason] = useState('');
  const [refundSel, setRefundSel] = useState([]);   // v7.247: 부분 환불 — 선택된 시리얼
  // ─── 입출고 로그: 내용 수정 ───
  const [editingTxItem, setEditingTxItem] = useState(null);

  // ─── v7.244: Esc 로 모달 닫기 ──────────────────────────────────────
  //   겹쳐 있는 오버레이 중 **가장 안쪽 하나만** 닫는다. 순서가 곧 우선순위.
  //   ⚠ 로컬 onKeyDown('Escape')이 이미 있는 입력 보조들을 최상단에 둔 이유:
  //      로컬 핸들러와 이 전역 핸들러가 같은 Esc 로 둘 다 실행되는데,
  //      최상단에 있으면 "이미 닫힌 것을 한 번 더 닫고" 종료하므로(멱등)
  //      뒤에 있는 모달까지 같이 닫히는 사고가 안 난다.
  //      다르게 동작해야 하는 곳(수기입력 팝오버의 '뒤로')은 로컬에서
  //      e.stopPropagation() 으로 이 핸들러를 막는다.
  const escStackRef = useRef([]);
  useEffect(() => {
    escStackRef.current = [
      // (1) 입력 보조 — 로컬 Esc 핸들러와 중복 실행돼도 결과가 같아야 한다
      [serialSuggestionsOpen, () => setSerialSuggestionsOpen(false)],
      [addrSuggestOpen,       () => setAddrSuggestOpen(false)],
      [inlineEditCell,        () => setInlineEditCell(null)],
      [editingKeepCell,       () => setEditingKeepCell(null)],
      [editingKeepGroupCell,  () => setEditingKeepGroupCell(null)],
      [editingReceiptSerial,  () => setEditingReceiptSerial(null)],
      [editingInboundSerial,  () => setEditingInboundSerial(null)],
      [editingInboundSource,  () => setEditingInboundSource(null)],
      [editingMemoId,         () => setEditingMemoId(null)],
      // (2) 팝오버 / 드롭다운
      [deferPopover,          () => setDeferPopover(null)],
      [bookkeptPopover,       () => setBookkeptPopover(null)],
      [headerMenuOpen,        () => setHeaderMenuOpen(false)],
      [txPickerOpen,          () => setTxPickerOpen(false)],
      // (3) 모달 — 중첩될 수 있는 안쪽부터
      [doaReturnSerialModal,  () => setDoaReturnSerialModal(null)],
      [modelSearchModal,      () => setModelSearchModal(null)],
      [staffPhoneModal,       () => setStaffPhoneModal(false)],
      [userMgmtOpen,          () => setUserMgmtOpen(false)],
      [bulkUnknownModal,      () => setBulkUnknownModal(null)],
      [aliasManageModal,      () => setAliasManageModal(null)],
      [newProductModal,       () => setNewProductModal(null)],
      [bulkProductModal,      () => setBulkProductModal(null)],
      [demoReturnModal,       () => setDemoReturnModal(null)],
      [demoHistoryModal,      () => setDemoHistoryModal(null)],
      [demoLendModal,         () => setDemoLendModal(null)],
      [demoAddModal,          () => setDemoAddModal(null)],
      [shipKeepModal,         () => setShipKeepModal(null)],
      [editingTxItem,         () => setEditingTxItem(null)],
      [logModalItem,          () => setLogModalItem(null)],
      [calendarOpen,          () => setCalendarOpen(false)],
      [showTxLogSummary,      () => setShowTxLogSummary(false)],
      [outboundModalOpen,     () => setOutboundModalOpen(false)],
      [memoHistoryDetail,     () => setMemoHistoryDetail(null)],
      [memoSearchOpen,        () => setMemoSearchOpen(false)],
      [memoHistoryOpen,       () => setMemoHistoryOpen(false)],
      [mobileNavOpen,         () => setMobileNavOpen(false)],
    ];
  });
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (e.isComposing || e.keyCode === 229) return; // 한글 조합 중 Esc는 조합 취소용
      const hit = escStackRef.current.find(([open]) => !!open);
      if (!hit) return;
      e.preventDefault();
      hit[1]();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  const [editForm, setEditForm] = useState({ recipient:'', phone:'', addr:'', method:'택배', salesStatus:'', author:'', requester:'', trackingNo:'', quickPrice:'', quickCOD:false, items:[] });
  const [editSaving, setEditSaving] = useState(false);

  useEffect(()=>{(async()=>{const{ok,count}=await InventoryService.countAll();setConnected(ok);if(ok)setStats(p=>({...p,total:count}));})();},[]);

  // ─── 동시 접속자 추적 (Supabase Realtime Presence) ───
  // 별도 DB 테이블 불필요. 채널에 join한 클라이언트 수를 실시간 집계.
  useEffect(() => {
    const clientId = `c_${Date.now()}_${Math.random().toString(36).slice(2,9)}`;
    const channel = supabase.channel('inventura-presence', {
      config: { presence: { key: clientId } }
    });
    channel.on('presence', { event: 'sync' }, () => {
      const state = channel.presenceState();
      setOnlineCount(Object.keys(state).length || 1);
    });
    channel.subscribe(async (status) => {
      if (status === 'SUBSCRIBED') {
        await channel.track({ at: new Date().toISOString() });
      }
    });
    return () => { try { supabase.removeChannel(channel); } catch(e){} };
  }, []);



  // 모델별 재고 페이지 데이터 로드 (전체 모델 집계)
  const loadAllModels = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    // inventory에서 모든 모델 정보 가져와서 그룹핑
    const allData = await InventoryService.fetchAll('product_name,category,status,stock_type,warehouse,source,sale_date');
    const grouped = {};
    allData.forEach(r => {
      const key = r.product_name;
      if (!grouped[key]) grouped[key] = {
        model: r.product_name, category: r.category,
        in_stock: 0, shipped: 0, total: 0,
        // 출처 (source) — 어디서 왔는지
        source_taiwan: 0, source_paju: 0, source_hq: 0, source_unknown: 0,
        // 보관 위치 (warehouse) — 어디에 있는지
        wh_hq: 0, wh_paju: 0, wh_other: 0,
        newProduct: 0, repacked: 0, assembled: 0, asProduct: 0, // v7.184: AS 필드 추가
        lastShipped: null
      };
      const g = grouped[key];
      g.total++;
      if (r.status === 'in_stock') g.in_stock++;
      else if (r.status === 'shipped') {
        g.shipped++;
        if (r.sale_date && (!g.lastShipped || r.sale_date > g.lastShipped)) g.lastShipped = r.sale_date;
      }
      // 출처 집계
      if (r.source === 'taiwan') g.source_taiwan++;
      else if (r.source === 'paju') g.source_paju++;
      else if (r.source === 'hq') g.source_hq++;
      else g.source_unknown++;
      // 보관 위치 집계
      if (r.warehouse === 'paju') g.wh_paju++;
      else if (r.warehouse === 'hq' || !r.warehouse) g.wh_hq++;
      else g.wh_other++;
      if (r.stock_type === '신품') g.newProduct++;
      else if (r.stock_type === 'AS') g.asProduct++; // v7.184: AS 유형 인식
      else if (r.stock_type === '리페킹') g.repacked++;
      else if (r.stock_type === '조립출고') g.assembled++;
    });
    setAllModels(Object.values(grouped));
    setLoading(false);
  }, [connected]);
  useEffect(()=>{if(page==='txlog'){loadAllModels();}},[page,loadAllModels]);

  // v7.40: 입고 등록 페이지 진입 시 — DB의 모든 serial_number를 캐시 (중복 즉시 감지)
  useEffect(() => {
    if (page !== 'inbound') return;
    let cancelled = false;
    (async () => {
      const all = await InventoryService.fetchSerialCache({ cancelCheck: () => cancelled });
      if (!cancelled) setRegSerialCache(all);
    })();
    return () => { cancelled = true; };
  }, [page]);

  const loadInventory=useCallback(async()=>{
    if(!connected)return;
    setLoading(true);
    // 정렬 컬럼 매핑 (UI 키 → DB 컬럼)
    const sortColMap = { serial:'serial_number', model:'product_name', category:'category', receipt_date:'receipt_date', sale_date:'sale_date', status:'status' };
    const sortCol = sortColMap[invSortBy] || 'receipt_date';
    const sortAsc = invSortDir === 'asc';
    const isSearch = search.trim() !== '';
    if (isSearch) {
      // v7.28: 스마트 검색 — 검색어 유형 자동 감지
      //  - 날짜 패턴 (2026.6.12, 26-6-12 등) → receipt_date / sale_date 매칭
      //  - 일반 텍스트 → product_name, serial_number, note, memo, dealer_name 매칭
      //  - 짧은 검색어(1-2자)는 모델명+시리얼만 (DB 부담 + 노이즈 감소)
      const s = search.trim();
      const safe = s.replace(/[,():]/g, ' ').trim();
      // 날짜 패턴 감지: YYYY.MM.DD / YYYY-MM-DD / YYYY/MM/DD / YY.MM.DD 등
      let parsedDate = null;
      const dateMatch = safe.match(/^(\d{2,4})[.\-/]\s*(\d{1,2})[.\-/]\s*(\d{1,2})$/);
      if (dateMatch) {
        let [, y, mo, d] = dateMatch;
        if (y.length === 2) y = '20' + y;
        parsedDate = `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`;
      }
      // OR 구문 빌드
      const orParts = [];
      if (parsedDate) {
        // 날짜 검색 — 입고일 또는 출고일
        orParts.push(`receipt_date.eq.${parsedDate}`, `sale_date.eq.${parsedDate}`);
        // 보너스: 텍스트로도 검색해서 fallback
        if (safe.length >= 3) orParts.push(`note.ilike.%${safe}%`, `memo.ilike.%${safe}%`);
      } else if (safe.length <= 1) {
        // v7.31: 1자 검색어만 모델+시리얼로 제한 (이전 v7.19: 2자 이하 → 너무 좁음)
        // 한글 2자(예: '테온', '이선')는 의미 있는 단어이므로 모든 필드 검색
        orParts.push(`product_name.ilike.%${safe}%`, `serial_number.ilike.%${safe}%`);
      } else {
        // 일반 텍스트: 모든 필드
        orParts.push(
          `product_name.ilike.%${safe}%`,
          `serial_number.ilike.%${safe}%`,
          `note.ilike.%${safe}%`,
          `memo.ilike.%${safe}%`,
          `dealer_name.ilike.%${safe}%`
        );
      }
      const orClause = orParts.join(',');
      let modelQ = supabase.from('inventory').select('product_name').or(orClause);
      if (filter !== 'all') modelQ = modelQ.eq('status', filter);
      if (stockTypeFilter !== 'all') modelQ = modelQ.eq('stock_type', stockTypeFilter);
      // v7.60: HDD/SSD 탭에서는 카테고리 필터 강제 적용
      if (inventoryTab === 'hdd') modelQ = modelQ.in('category', ['HDD','SSD']);

      // v7.29: transaction_log에서도 검색 — 출고 요청 비고/배송지에 매칭되는 시리얼 찾음
      // 출고 요청 시 사용자가 입력한 배송지(addr), 비고(memo) 등은 inventory가 아닌 transaction_log에 JSON으로 저장됨
      const txQ = supabase.from('transaction_log')
        .select('note')
        .eq('type', 'outbound')
        .ilike('note', `%${safe}%`)
        .limit(200);
      const [modelQResult, txQResult] = await Promise.all([modelQ.limit(5000), txQ]);
      const matchedRows = modelQResult.data || [];
      const txMatched = txQResult.data || [];

      // transaction_log에서 매칭된 시리얼 추출 (note JSON 안 items[].fulfilledSerials)
      // v7.69: 매칭 위치 판정으로 모델 노이즈 제거
      //   - common/note/memo 영역에 검색어 매칭 → 트랜잭션의 모든 items 추출
      //     (수령인/주소/메모 검색 시나리오 — 트랜잭션 전체가 의미 있음)
      //   - 그 외 → product_name이 직접 매칭되는 item만 추출
      //     (모델별 검색에서 같은 트랜잭션의 무관한 다른 모델이 따라오던 문제 해결)
      //   사례: "파스카리" 검색 시, 같은 출고요청에 묶인 IRON_WOLF_PRO 24TB까지 결과에 노출되던 버그
      const safeLower = safe.toLowerCase();
      const txSerialsSet = new Set();
      const txModelsSet = new Set(); // v7.30: transaction_log 매칭된 모델들 (시리얼 매칭 없어도 안내용)
      for (const row of txMatched) {
        try {
          const info = JSON.parse(row.note);
          if (!Array.isArray(info.items)) continue;
          // common/메타 영역에서 매칭 여부 확인 (수령인/주소/메모/dealer 등)
          const metaStr = JSON.stringify({
            common: info.common || {},
            note: info.note || '',
            memo: info.memo || '',
            dealer_name: info.dealer_name || '',
          }).toLowerCase();
          const matchedInMeta = metaStr.includes(safeLower);
          info.items.forEach(it => {
            const productMatched = (it.product_name || '').toLowerCase().includes(safeLower);
            // common에 매칭 안 됐고 product_name도 매칭 안 되면 이 item 스킵 (다른 모델 노이즈 차단)
            if (!matchedInMeta && !productMatched) return;
            if (it.product_name) txModelsSet.add(it.product_name);
            if (Array.isArray(it.fulfilledSerials)) it.fulfilledSerials.forEach(s => txSerialsSet.add(s));
            if (Array.isArray(it.serials)) it.serials.forEach(s => txSerialsSet.add(s));
          });
        } catch(e) {}
      }
      setTxMatchedSerials(txSerialsSet);
      setTxMatchedModels(txModelsSet);

      // modelNames 조합: inventory 매칭 + transaction_log 시리얼들의 product_name
      const modelNamesSet = new Set(matchedRows.map(r => r.product_name).filter(Boolean));
      if (txSerialsSet.size > 0) {
        const { data: txInvRows } = await supabase.from('inventory')
          .select('product_name')
          .in('serial_number', [...txSerialsSet]);
        (txInvRows || []).forEach(r => { if (r.product_name) modelNamesSet.add(r.product_name); });
      }
      const modelNames = [...modelNamesSet];
      if (modelNames.length === 0) {
        setItems([]); setTotal(0); setLoading(false); return;
      }
      // 그 모델들의 모든 시리얼 fetch (필터 status는 적용; 검색어 무관)
      let allQ = supabase.from('inventory')
        .select('serial_number,product_name,category,status,stock_type,warehouse,source,sale_date,dealer_name,note,memo,receipt_date,model_no')
        .in('product_name', modelNames);
      if (filter !== 'all') allQ = allQ.eq('status', filter);
      if (stockTypeFilter !== 'all') allQ = allQ.eq('stock_type', stockTypeFilter);
      // v7.60: HDD/SSD 탭에서는 카테고리 필터 강제 적용
      if (inventoryTab === 'hdd') allQ = allQ.in('category', ['HDD','SSD']);
      const { data: allRows } = await allQ.order(sortCol, { ascending: sortAsc, nullsFirst: false }).limit(5000);
      if (allRows) {
        const mapped = allRows.map(x=>({serial:x.serial_number,model:x.product_name,category:x.category,status:x.status,stock_type:x.stock_type,warehouse:x.warehouse,source:x.source,sale_date:x.sale_date,dealer_name:x.dealer_name,note:x.note,memo:x.memo,receipt_date:x.receipt_date,model_no:x.model_no}));
        // 카테고리 정렬은 stock_type 그룹화를 무시
        setItems(invSortBy === 'category' ? mapped : (invSortBy === 'receipt_date' || invSortBy === 'sale_date' ? mapped : mapped.sort(sortByStockType)));
        setTotal(allRows.length);
      }
      setLoading(false);
      return;
    }
    // 일반 모드: 무한 스크롤 페이지네이션
    if (txMatchedSerials.size > 0) setTxMatchedSerials(new Set()); // v7.29: 검색 X면 리셋
    if (txMatchedModels.size > 0) setTxMatchedModels(new Set()); // v7.30: 검색 X면 리셋
    if (pg > 0) setLoadingMore(true); else setLoading(true);
    let q=supabase.from('inventory').select('serial_number,product_name,category,status,stock_type,warehouse,source,sale_date,dealer_name,note,memo,receipt_date,model_no',{count:'exact'});
    if(filter!=='all')q=q.eq('status',filter);
    if(stockTypeFilter!=='all')q=q.eq('stock_type',stockTypeFilter);
    // v7.60: HDD/SSD 탭에서는 카테고리 필터 강제 적용
    if (inventoryTab === 'hdd') q = q.in('category', ['HDD','SSD']);
    const{data,count}=await q.order(sortCol,{ascending:sortAsc,nullsFirst:false}).range(pg*PG,(pg+1)*PG-1);
    if(data){
      const mapped = data.map(x=>({serial:x.serial_number,model:x.product_name,category:x.category,status:x.status,stock_type:x.stock_type,warehouse:x.warehouse,source:x.source,sale_date:x.sale_date,dealer_name:x.dealer_name,note:x.note,memo:x.memo,receipt_date:x.receipt_date,model_no:x.model_no}));
      // 날짜/카테고리 정렬은 DB 그대로, 그 외는 stock_type 그룹화
      const sorted = (invSortBy === 'category' || invSortBy === 'receipt_date' || invSortBy === 'sale_date') ? mapped : mapped.sort(sortByStockType);
      if (pg === 0) setItems(sorted); else setItems(prev => [...prev, ...sorted]);
      setTotal(count||0);
      setHasMore(data.length >= PG);
    }
    setLoading(false);
    setLoadingMore(false);
  },[connected,search,filter,stockTypeFilter,pg,invSortBy,invSortDir,inventoryTab]);
  useEffect(()=>{if(page==='inventory')loadInventory();},[page,loadInventory]);

  // v7.139: 재고 목록 엑셀 다운로드 (제품명 + 시리얼 + 부가정보 한 번에)
  //         배경: 사용자 요청 — "재고목록에 엑셀파일로 제품명 시리얼을 한번에 다운받는 기능"
  //         동작:
  //           ・ 현재 상태 필터 (전체/재고/예약/판매불가/출고)
  //           ・ 현재 유형 필터 (전체/신품/리페킹/조립출고)
  //           ・ 현재 탭 (list=전체 / hdd=HDD·SSD 한정)
  //             위 3가지 조건에 맞는 재고를 서버에서 1000건씩 배치 fetch → xlsx 파일 생성
  //           ・ 검색어(search)는 UI 스마트 매칭 로직이 서버 query와 완전 대칭이 아니므로
  //             다운로드에서는 검색을 무시하고 필터 조건만 반영 (전체 재고 스냅샷 용도)
  //         컬럼: 카테고리 · 제품명 · 시리얼 · 상태 · 유형 · 창고 · 입고일
  //         파일명: 재고목록[_HDD_SSD][_상태][_유형]_YYYYMMDD.xlsx
  const downloadInventoryXlsx = useCallback(async () => {
    if (downloadingInvXlsx) return;
    if (!connected) { alert('DB 연결이 필요합니다.'); return; }
    setDownloadingInvXlsx(true);
    try {
      // 정렬 컬럼: loadInventory와 동일 매핑 (현재 화면 정렬 유지)
      const sortColMap = { serial:'serial_number', model:'product_name', category:'category', receipt_date:'receipt_date', sale_date:'sale_date', status:'status' };
      const sortCol = sortColMap[invSortBy] || 'receipt_date';
      const sortAsc = invSortDir === 'asc';

      // 서버 배치 fetch — 1000건씩, 최대 100회(10만 건) 안전 상한
      const BATCH = 1000;
      const MAX_BATCHES = 100;
      let all = [];
      for (let i = 0; i < MAX_BATCHES; i++) {
        let q = supabase.from('inventory')
          .select('serial_number,product_name,category,status,stock_type,warehouse,receipt_date');
        if (filter !== 'all') q = q.eq('status', filter);
        if (stockTypeFilter !== 'all') q = q.eq('stock_type', stockTypeFilter);
        if (inventoryTab === 'hdd') q = q.in('category', ['HDD','SSD']);
        const from = i * BATCH;
        const to = from + BATCH - 1;
        const { data, error } = await q.order(sortCol, { ascending: sortAsc, nullsFirst: false }).range(from, to);
        if (error) throw error;
        if (!data || data.length === 0) break;
        all = all.concat(data);
        if (data.length < BATCH) break;
      }
      if (all.length === 0) {
        alert('현재 조건에 다운로드할 재고가 없습니다.');
        return;
      }

      // 상태 코드 → 한글 라벨
      const STATUS_LABEL = { in_stock: '재고', ship_hold: '출고대기', reserved: '예약', unsellable: '판매불가', shipped: '출고' };

      // 시트 행 구성 — 헤더 + 데이터
      const rows = [['카테고리','제품명','시리얼','상태','유형','창고','입고일']];
      for (const it of all) {
        rows.push([
          it.category || '',
          it.product_name || '',
          it.serial_number || '',
          STATUS_LABEL[it.status] || it.status || '',
          it.stock_type || '',
          it.warehouse || '',
          it.receipt_date || '',
        ]);
      }

      // 시트 생성 + 컬럼 너비
      const ws = XLSX.utils.aoa_to_sheet(rows);
      ws['!cols'] = [
        { wch: 14 },  // 카테고리
        { wch: 32 },  // 제품명
        { wch: 22 },  // 시리얼
        { wch: 10 },  // 상태
        { wch: 10 },  // 유형
        { wch: 14 },  // 창고
        { wch: 14 },  // 입고일
      ];
      // 헤더 행 고정 (스크롤 시 헤더 유지)
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '재고목록');

      // 파일명 — 로컬 타임존 기준 (v7.126 원칙, UTC 어제 버그 방지)
      const d = new Date();
      const dateStr = `${d.getFullYear()}${String(d.getMonth()+1).padStart(2,'0')}${String(d.getDate()).padStart(2,'0')}`;
      const tabLabel = inventoryTab === 'hdd' ? '_HDD_SSD' : '';
      const filterLabel = filter !== 'all' ? `_${STATUS_LABEL[filter] || filter}` : '';
      const stockTypeLabel = stockTypeFilter !== 'all' ? `_${stockTypeFilter}` : '';
      const filename = `재고목록${tabLabel}${filterLabel}${stockTypeLabel}_${dateStr}.xlsx`;

      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('엑셀 다운로드 실패: ' + (e.message || e));
    } finally {
      setDownloadingInvXlsx(false);
    }
  }, [downloadingInvXlsx, connected, filter, stockTypeFilter, inventoryTab, invSortBy, invSortDir]);

  // 비고 (memo) 저장 — Inventory List 인라인 편집
  const saveInventoryMemo = async (serial, memo) => {
    setSavingMemoSerial(serial);
    const res = await InventoryService.updateBySerial(serial, { memo: memo || null });
    if (res.ok) {
      setItems(prev => prev.map(it => it.serial === serial ? { ...it, memo } : it));
      setInventoryMemos(prev => { const n = {...prev}; delete n[serial]; return n; });
    }
    setSavingMemoSerial(null);
  };

  // 제품 상태 변경 (정상재고 ↔ 판매불가) — Inventory List 인라인 편집
  const saveInventoryStatus = async (serial, newStatus) => {
    setSavingStatusSerial(serial);
    const res = await InventoryService.updateBySerial(serial, { status: newStatus });
    if (res.ok) {
      setItems(prev => prev.map(it => it.serial === serial ? { ...it, status: newStatus } : it));
      // 모델별 재고 수가 변경되므로 재고 부족 카드도 갱신
      if (typeof loadAllModels === 'function') loadAllModels();
    } else {
      alert('상태 변경 실패: ' + res.msg);
    }
    setSavingStatusSerial(null);
  };

  // 재고 항목 임의 필드 변경 (입고일/stock_type/warehouse/source 등) — Inventory expand 패널 편집
  const saveInventoryField = async (serial, field, rawValue) => {
    const value = (rawValue === '' || rawValue === undefined) ? null : rawValue;
    const res = await InventoryService.updateBySerial(serial, { [field]: value });
    if (!res.ok) {
      alert(`${field} 변경 실패: ` + res.msg);
      return false;
    }
    setItems(prev => prev.map(it => it.serial === serial ? { ...it, [field]: value } : it));
    return true;
  };

  // v7.97: 재고 목록 입고일 셀 인라인 편집 저장 wrapper
  //         편집 state 관리 + saving 표시 + 자동 종료
  const saveReceiptDate = async (serial, newValue) => {
    setSavingReceiptSerial(serial);
    const ok = await saveInventoryField(serial, 'receipt_date', newValue || null);
    setSavingReceiptSerial(null);
    setEditingReceiptSerial(null);
    return ok;
  };

  // v7.236: 입고 정보 편집 패널 — 재고 목록 "일반 목록"과 "검색 결과 카드"가 공유한다.
  //          검색 중에는 expand 자체가 출고 건에만 열려서 입고일·제품상태·보관·출처를 못 고쳤다.
  //          두 뷰가 갈라지지 않도록 마크업을 한 곳에서만 렌더한다. (compact = 카드 뷰용 축소판)
  const renderInboundEditPanel = (item, compact = false) => {
    const shipped = item.status === 'shipped';
    const fieldStyle = {...inputStyle, padding: compact ? '7px 9px' : '8px 10px', fontSize: compact ? 12 : 13};
    const labelStyle = {display:'block',fontSize:compact?10:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:5};
    return (
      <div style={{background:'var(--bg-card)',padding:compact?12:16,borderRadius:10,border:'1px solid var(--border)'}}>
        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:compact?10:14}}>
          <Icon name="inventory_2" size={compact?14:16} style={{color:'var(--success)'}}/>
          <span style={{fontSize:compact?12:13,fontWeight:700,color:'var(--fg-1)'}}>입고 정보 수정</span>
          <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:500}}>· 변경 시 즉시 저장</span>
        </div>
        <div onClick={e=>e.stopPropagation()} style={{display:'grid',gridTemplateColumns:'repeat(4, 1fr)',gap:compact?10:14}}>
          <div>
            <label style={labelStyle}>입고일</label>
            <input type="date" value={item.receipt_date || ''}
              onChange={e=>saveInventoryField(item.serial, 'receipt_date', e.target.value || null)}
              style={fieldStyle}/>
          </div>
          <div>
            <label style={labelStyle}>제품 상태</label>
            <select value={item.stock_type || ''}
              onChange={e=>saveInventoryField(item.serial, 'stock_type', e.target.value || null)}
              style={{...fieldStyle,cursor:'pointer'}}>
              <option value="">선택 안 함</option>
              <option value="신품">신품</option>
              {/* v7.189: AS 옵션 추가 (v7.184 다른 UI 전반 확장 시 이 드롭다운만 누락됐음) */}
              <option value="AS">AS</option>
              <option value="리페킹">리페킹</option>
              {/* v7.189: value "조립" → "조립출고" 정정 (버그). 다른 UI/집계는 모두 "조립출고" 기준이었음 */}
              <option value="조립출고">조립출고</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>보관 위치</label>
            <select value={item.warehouse || 'hq'}
              onChange={e=>saveInventoryField(item.serial, 'warehouse', e.target.value)}
              style={{...fieldStyle,cursor:'pointer'}}>
              <option value="hq">본사</option>
              <option value="paju">파주</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>출처</label>
            <select value={item.source || ''}
              onChange={e=>saveInventoryField(item.serial, 'source', e.target.value || null)}
              style={{...fieldStyle,cursor:'pointer'}}>
              <option value="">선택 안 함</option>
              <option value="taiwan">🇹🇼 대만</option>
              <option value="paju">🇰🇷 파주</option>
              <option value="hq">🏢 본사</option>
            </select>
          </div>
        </div>
        {/* v7.419: 모델번호 (HDD/SSD 제조사 모델, 예: ST24000NT002) — inventory.model_no. 편집 가능(즉시 저장). */}
        <div style={{marginTop:compact?10:14}}>
          <label style={labelStyle}>모델번호 <span style={{color:'var(--fg-3)',fontWeight:400}}>(HDD/SSD)</span></label>
          <input type="text" value={item.model_no || ''}
            onChange={e=>setItems(prev=>prev.map(it=>it.serial===item.serial?{...it,model_no:e.target.value}:it))}
            onBlur={e=>saveInventoryField(item.serial, 'model_no', e.target.value.trim())}
            onKeyDown={e=>{ if(e.key==='Enter'){ e.target.blur(); } }}
            placeholder="예: ST24000NT002"
            style={{...fieldStyle,fontFamily:'monospace'}}/>
        </div>
        <div style={{marginTop:compact?10:12,padding:'8px 12px',background:'var(--bg-subtle)',borderRadius:7,fontSize:11,color:'var(--fg-3)',display:'flex',alignItems:'center',gap:6}}>
          <Icon name="info" size={13} style={{color:'var(--accent)'}}/>
          품목 코드(<span style={{fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)'}}>{item.serial}</span>)와 품목명은 카탈로그에서 관리됩니다.
        </div>
        {/* v7.193: 시리얼 단건 삭제 — in_stock 상태만 허용 (InventoryService.deleteBySerial 안전장치) */}
        <div style={{marginTop:compact?10:14,paddingTop:compact?10:14,borderTop:'1px dashed var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
          <div style={{fontSize:11,color:'var(--fg-3)',flex:1,minWidth:200}}>
            이 시리얼을 재고에서 완전히 제거합니다. 배송/입고 이력(배송시트지)은 그대로 유지됩니다.
            {shipped && <span style={{color:'var(--warning)',fontWeight:700}}> · 출고된 시리얼은 삭제할 수 없습니다.</span>}
          </div>
          <button
            disabled={shipped || deletingSerial===item.serial}
            onClick={async () => {
              if (shipped) return;
              if (!window.confirm(`시리얼 [${item.serial}]\n제품: ${item.model}\n\n재고에서 완전히 삭제합니다. 계속하시겠습니까?\n(배송시트지 이력은 유지됩니다)`)) return;
              setDeletingSerial(item.serial);
              const res = await InventoryService.deleteBySerial(item.serial);
              setDeletingSerial(null);
              if (!res.ok) { alert('삭제 실패: ' + res.msg); return; }
              // 즉시 목록에서 제거 (loadInventory 재호출 대신 로컬 필터)
              setItems(prev => prev.filter(x => x.serial !== item.serial));
              setTotal(t => Math.max(0, t - 1));
              setExpandedSerial(null);
            }}
            style={{
              display:'inline-flex',alignItems:'center',gap:6,
              padding: compact ? '7px 14px' : '8px 16px',
              background: shipped ? '#f5f5f5' : (deletingSerial===item.serial ? '#fbc4c4' : 'rgba(186,26,26,0.08)'),
              color: shipped ? '#bbb' : 'var(--danger)',
              border: `1px solid ${shipped ? 'var(--border)' : 'var(--danger)'}`,
              borderRadius:8,
              fontSize:12,fontWeight:700,
              cursor: shipped || deletingSerial===item.serial ? 'not-allowed' : 'pointer',
              fontFamily:'inherit',
              transition:'all 0.15s',
            }}
            onMouseEnter={e=>{ if(!shipped && deletingSerial!==item.serial){ e.currentTarget.style.background='var(--danger-solid)'; e.currentTarget.style.color='#fff'; }}}
            onMouseLeave={e=>{ if(!shipped && deletingSerial!==item.serial){ e.currentTarget.style.background='rgba(186,26,26,0.08)'; e.currentTarget.style.color='var(--danger)'; }}}>
            <Icon name={deletingSerial===item.serial?'progress_activity':'delete_forever'} size={14} style={{animation:deletingSerial===item.serial?'spin 1s linear infinite':'none'}}/>
            {deletingSerial===item.serial?'삭제 중...':'시리얼 삭제'}
          </button>
        </div>
      </div>
    );
  };

  // 요청 기반 출고 처리: 입출고 로그의 "제품 출고" 클릭 시 사용
  const loadActiveRequest = async (reqId, model) => {
    // v7.308: 재초기화 스킵을 없앴다.
    //   기존(v7.73): 같은 reqId + pendingSerials 가 있으면 DB 재조회를 통째로 건너뛰었다.
    //     → 배송시트지에서 [품목 추가] 로 제품을 넣고 [제품 출고] 를 눌러도
    //       activeRequest.models 가 옛날 그대로라 새 품목이 안 보였다. 새로고침해야 나타났다.
    //     외부/면제 품목은 열자마자 EXT-/NOSN- 이 pending 에 자동으로 깔리므로
    //     사실상 항상 스킵 조건에 걸렸다. 그래서 재현이 잦았다.
    //   스킵의 원래 목적(스캔해 둔 pending 유실 방지)은 아래 keepPending 으로 대체한다.
    //   v7.82/v7.149 이후 pending 은 localStorage·Supabase 양쪽에 이미 영속되지만,
    //   메모리 값을 최우선으로 두는 편이 확실하다.
    const keepPending = (activeRequest?.reqId === reqId && pendingSerials.length > 0)
      ? pendingSerials : null;
    // 새 형식(items 배열) 우선 시도
    const { data } = await supabase.from('transaction_log').select('*').eq('serial', reqId).eq('type', 'outbound');
    if (!data || !data.length) { alert('요청을 찾을 수 없습니다'); return; }
    let common, models;
    // 다중 품목 (items 배열) 처리
    const firstRow = data[0];
    let firstInfo = {};
    try { firstInfo = JSON.parse(firstRow.note); } catch(e){}
    if (firstInfo.items && Array.isArray(firstInfo.items)) {
      // ✓ 새 형식: 1행에 items 배열
      common = firstInfo;
      models = firstInfo.items.map(it => ({
        id: firstRow.id,                       // 모두 같은 행 id
        itemKey: it.product_name,              // 행 내 식별자
        model: it.product_name,
        qty: it.qty,
        fulfilledSerials: it.fulfilledSerials || [],
        // v7.55: 방문수령 예약 시리얼도 함께 로드 (모달에서 표시 / 추가 입력)
        reservedSerials: it.reservedSerials || [],
        fulfilled: (it.fulfilledSerials?.length || 0) >= it.qty,
        external: !!it.external,
        // v7.41: items[i].serialOptional 직접 지정 가능 (데모 대여 자동 등록 등)
        _itemSerialOptional: !!it.serialOptional,
        _itemSerialOptionalReason: it.serialOptionalReason || null,
        // v7.153: 데모 대여의 경우 실제 장비 시리얼 목록 (autoExtPending 생성 시 우선 사용)
        _itemDemoSerials: Array.isArray(it.demoSerials) ? it.demoSerials : [],
        info: firstInfo
      }));
    } else {
      // 구 형식: 행마다 1품목 (이전 데이터 호환)
      common = firstInfo;
      models = data.map(r => {
        let info = {};
        try { info = JSON.parse(r.note); } catch(e){}
        return {
          id: r.id, model: r.model,
          qty: info.qty || 1,
          fulfilledSerials: info.fulfilledSerials || [],
          fulfilled: info.fulfilled || false,
          info
        };
      });
    }
    const targetRow = model ? models.find(r => r.model === model) : models[0];
    // 카테고리 조회 → 시리얼 면제(HDD/RAIL) 판단
    const modelNames = [...new Set(models.map(m => m.model))];
    let categoryByModel = {};
    // v7.376: 카탈로그(products) 등록 여부를 [별도로] 들고 간다.
    //   ⚠ category 로 판정하면 안 된다. products.category 는 null 일 수 있어
    //     "카탈로그에 있는데 분류만 비어 있는 제품"이 미등록으로 오판된다.
    const inCatalogSet = new Set();
    if (modelNames.length > 0) {
      const prodData = await ProductService.findByNames(modelNames);
      prodData.forEach(p => { categoryByModel[p.product_name] = p.category; inCatalogSet.add(p.product_name); });
    }
    models = models.map(m => {
      const cat = categoryByModel[m.model] || null;
      // v7.39: 매출여부 '수기입력' + 수기내용에 'AS' / 'as' / 'A/S' 키워드(word boundary)가 있을 때만 시리얼 면제
      // v7.79: AS 재발송 자동 NOSN 부여 로직 완전 제거
      //        매출여부 "수기입력" + "AS/A/S" 텍스트로 자동 시리얼 면제하던 hasAsKeyword 삭제
      //        v7.70에서 isSerialOptional(RAIL 자동 매칭) 제거한 흐름과 대칭
      //        이제 serialOptional 자동 트리거는 없음. 명시적 사유만 남음:
      //          - items[i].serialOptional = true (호출자가 직접 지정)
      //          - 데모 대여(demo_lend), 그 외 프로그램적 명시
      const itemOptional = !!m._itemSerialOptional;
      // v7.55: 방문수령이면 reservedSerials를 fulfilledSerials와 동등하게 취급 (모달 카운트/검증용)
      //        DB 저장 시점에는 commitRequest의 isPickupMode 분기로 다시 reservedSerials로 분리됨
      const isPickup = !!common?.pickup;
      const effectiveFulfilled = isPickup
        ? [...(m.fulfilledSerials || []), ...(m.reservedSerials || [])]
        : (m.fulfilledSerials || []);
      return {
        ...m,
        category: cat,
        inCatalog: inCatalogSet.has(m.model),
        fulfilledSerials: effectiveFulfilled,
        fulfilled: effectiveFulfilled.length >= m.qty,
        serialOptional: !m.external && itemOptional,
        serialOptionalReason: !m.external
          ? (m._itemSerialOptionalReason || (itemOptional ? 'item_flag' : null))
          : null,
      };
    });
    setActiveRequest({
      reqId,
      common,
      models,
      currentModel: targetRow?.model || models[0].model,
      isMultiItem: !!firstInfo.items
    });
    setRequestSerialInput('');
    setRequestMsg(null);
    // 외부 제품 + 시리얼 면제 제품은 시리얼 불필요 → 자동으로 pending에 채워둠
    // v7.153: 데모 대여의 경우 items[].demoSerials에 실제 장비 시리얼이 있으면 그것을 사용
    //         없으면 기존과 동일하게 NOSN-{모델}-{01} 임시 시리얼 폴백
    const autoExtPending = [];
    models.forEach(m => {
      if (m.external || m.serialOptional) {
        const remaining = m.qty - (m.fulfilledSerials?.length || 0);
        const demoSerials = m._itemDemoSerials || [];  // 데모 대여 시 실제 시리얼
        const alreadyUsed = m.fulfilledSerials?.length || 0;
        for (let i = 0; i < remaining; i++) {
          const idx = alreadyUsed + i + 1;
          // v7.153: 데모 대여 실제 시리얼 우선 사용. serialOptional일 때만 (external EXT-는 그대로).
          let serial;
          if (!m.external && m.serialOptional && demoSerials[alreadyUsed + i]) {
            // 실제 데모 장비 시리얼
            serial = demoSerials[alreadyUsed + i];
          } else if (m.external) {
            serial = `EXT-${m.model.replace(/\s+/g,'')}-${String(idx).padStart(2,'0')}`;
          } else {
            serial = `NOSN-${m.model.replace(/\s+/g,'')}-${String(idx).padStart(2,'0')}`;
          }
          autoExtPending.push({
            serial,
            model: m.model,
            modelLogId: m.id,
            external: !!m.external,
            serialOptional: !!m.serialOptional
          });
        }
      }
    });
    // v7.82: localStorage에 저장된 이전 세션 pending 복원 (새로고침 후 이어서 스캔)
    //         restored는 이미 auto + 수동 스캔이 합쳐진 상태이므로 우선 사용
    //         없으면 autoExtPending 그대로 (첫 진입 케이스)
    // v7.149: 다중 PC 공유 — Supabase 우선 조회, 실패/빈 결과 시 localStorage 폴백
    //         localStorage에 있고 Supabase에 없으면 자동 마이그레이션 (upsert)
    //         Supabase에 있으면 localStorage도 동기화 (오프라인 폴백 최신 유지)
    const restored = loadShipPending(reqId);
    let initialPending = null;
    let migrateSource = null;  // 'local' → 마이그레이션 필요 표시
    try {
      const remote = await ShipPendingService.loadByReqId(reqId);
      if (remote.ok && remote.data.length > 0) {
        // 서버에 데이터 있음 → 진실의 원천
        initialPending = remote.data;
        // localStorage 동기화 (다음 오프라인 대비)
        saveShipPending(reqId, remote.data);
      } else if (remote.ok && restored?.pending?.length > 0) {
        // 서버 빈 결과 + local 있음 → 마이그레이션
        initialPending = restored.pending;
        migrateSource = 'local';
      }
    } catch (e) {
      // Supabase 호출 자체 실패 (테이블 없음 등) — 조용히 localStorage 사용
    }
    if (initialPending == null) {
      // Supabase 실패 or 서버·로컬 모두 빈 상태 → 기존 폴백 흐름
      if (restored?.pending && Array.isArray(restored.pending) && restored.pending.length > 0) {
        initialPending = restored.pending;
      } else {
        initialPending = autoExtPending;
        // v7.149: 외부/면제 자동 pending도 서버에 즉시 저장 (다른 PC 공유)
        if (autoExtPending.length > 0) {
          ShipPendingService.addSerials(reqId, autoExtPending).catch(() => {});
        }
      }
    }
    // v7.308: 화면에 이미 떠 있던 pending 이 최우선 — 재조회로 스캔분이 날아가지 않게.
    if (keepPending) initialPending = keepPending;
    // v7.308: 그 사이 새로 추가된 외부/면제 품목은 자동 시리얼이 아직 없다 → 부족분만 채운다.
    //   품목별 잔여 수량을 넘지 않게 자르므로 중복 부여가 생기지 않는다.
    if (autoExtPending.length > 0) {
      const base = initialPending || [];
      const addAuto = [];
      models.forEach(m => {
        if (!m.external && !m.serialOptional) return;
        const have = base.filter(x => x.model === m.model).length;
        const need = m.qty - (m.fulfilledSerials?.length || 0) - have;
        if (need <= 0) return;
        autoExtPending
          .filter(x => x.model === m.model && !base.some(p => p.serial === x.serial))
          .slice(0, need)
          .forEach(x => addAuto.push(x));
      });
      if (addAuto.length > 0) {
        initialPending = [...base, ...addAuto];
        ShipPendingService.addSerials(reqId, addAuto).catch(() => {});
      }
    }
    // ── v7.258: 되돌림 보관분 주입 ───────────────────────────────────
    //   ★ 반드시 여기서 해야 한다. v7.256~v7.257 은 별도 useEffect 로 주입했는데,
    //     setActiveRequest(위) → await(ShipPendingService) → 이 setPendingSerials 순서라
    //     effect 가 넣어둔 시리얼을 여기서 통째로 덮어써 버렸다. (그래서 계속 0/N)
    //   조건: 실제로 스캔된 시리얼이 하나도 없을 때만. EXT-/NOSN- 은 모달이 만든
    //         placeholder(v7.145)라 '사용자 작업'으로 치지 않는다.
    try {
      const savedGroups = Array.isArray(common?.revertedItems) ? common.revertedItems : [];
      const hasSaved = savedGroups.some(g => (g.fulfilled?.length || 0) + (g.reserved?.length || 0) > 0);
      const isPlaceholder = (sn) => sn.startsWith('EXT-') || sn.startsWith('NOSN-');
      const realPending = initialPending.filter(x => !isPlaceholder(x.serial));
      if (hasSaved && realPending.length === 0) {
        const noteItems = Array.isArray(common?.items) ? common.items : [];
        const add = [];
        savedGroups.forEach(g => {
          const pname = noteItems[g.idx]?.product_name || models[0]?.model;
          const m = models.find(x => x.model === pname) || models[0];
          [...(g.fulfilled || []), ...(g.reserved || [])].forEach(sn => {
            if (initialPending.some(x => x.serial === sn) || add.some(x => x.serial === sn)) return;
            add.push({
              serial: sn, model: m?.model || pname, modelLogId: m?.id,
              external: sn.startsWith('EXT-'), serialOptional: sn.startsWith('NOSN-'),
            });
          });
        });
        if (add.length > 0) {
          // 실제 시리얼이 채워지는 모델의 placeholder 는 걷어낸다 (qty 초과 방지)
          const filled = new Set(add.map(a => a.model));
          initialPending = [
            ...initialPending.filter(x => !(filled.has(x.model) && isPlaceholder(x.serial))),
            ...add,
          ];
          ShipPendingService.addSerials(reqId, add).catch(() => {});
          setRequestMsg({ type: 'ok', msg: `✓ 되돌린 출고의 시리얼 ${add.length}개를 그대로 불러왔습니다 — [제품 출고]로 커밋하세요` });
        }
      }
    } catch (e) { console.warn('[되돌림] 보관 시리얼 주입 실패:', e); }
    // v7.415: ★ 최종 안전장치 — 모델별 pending 이 [잔여 수량 = qty − fulfilled] 을 넘지 않게 자른다.
    //   배경(사용자 리포트 "2개 설정인데 4개가 찍히고, 삭제해도 4개로 돌아옴"):
    //     배송시트지에서 qty 를 줄여도(또는 감소 confirm 취소·다른 경로로) ship_pending_serials 에 초과분이
    //     남으면, 출고 처리 모달 복원 때 remote.data 를 통째로 실어 [4/2] 처럼 뜨고, 삭제해도 재조회 시 되살아났다.
    //     기존 collectExcessPending 정리는 '시트지 수량 감소' 시점에만 돌아 이 복원 경로를 못 막았다.
    //   여기서 초과분을 잘라내고 ShipPendingService·localStorage 에서도 제거해 되살아나지 않게 한다.
    //   ⚠ 정상 스캔은 scan-cap 때문에 qty 를 넘지 않으므로, 잘리는 건 오직 [남아 있던 초과분] 뿐이다.
    try {
      const kept = [];
      const excessSn = [];
      const perModel = {};
      for (const p of (initialPending || [])) {
        const m = models.find(x => x.model === p.model);
        const quota = m ? Math.max(0, (m.qty || 0) - (m.fulfilledSerials?.length || 0)) : Infinity;
        const used = perModel[p.model] || 0;
        if (used < quota) { kept.push(p); perModel[p.model] = used + 1; }
        else { excessSn.push(p.serial); }
      }
      if (excessSn.length > 0) {
        initialPending = kept;
        saveShipPending(reqId, kept);
        for (const sn of excessSn) {
          ShipPendingService.removeSerial(reqId, sn).catch(() => {});
          // v7.424: 초과분으로 잘리는 시리얼도 ship_hold → in_stock 복귀 (재고에 묶여 사라지지 않게)
          if (sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-')) {
            InventoryService.releaseHoldBySerial(sn).catch(() => {});
          }
        }
        console.warn(`[출고 복원] 수량 초과 pending ${excessSn.length}개 정리:`, excessSn.join(', '));
      }
    } catch (e) { console.warn('[출고 복원] 초과 pending 정리 실패:', e); }
    setPendingSerials(initialPending);
    // v7.149: localStorage → Supabase 마이그레이션 (best effort)
    if (migrateSource === 'local' && initialPending.length > 0) {
      ShipPendingService.addSerials(reqId, initialPending).catch(() => {});
    }
  };

  // v7.52: 출고 모달 시리얼 자동완성 — 현재 모델의 in_stock 시리얼 매칭 후보 fetch
  // 디바운스 150ms, pending/완료된 시리얼은 제외, 외부/면제 모델은 비활성
  useEffect(() => {
    if (!activeRequest) { setSerialSuggestions([]); return; }
    const current = activeRequest.models.find(m => m.model === activeRequest.currentModel);
    if (!current || current.external || current.serialOptional) {
      setSerialSuggestions([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const list = await InventoryService.searchInStockSerials({
          productName: current.model,
          query: requestSerialInput.trim(),
          limit: 50,
        });
        // 이미 처리/대기 중인 시리얼 제외 (이 요청 자신의 것)
        //   v7.424: 다른 배송건에 담긴 시리얼은 inventory 가 ship_hold 라 searchInStockSerials(in_stock 전용)에서
        //           이미 빠진다 → 별도 교차조회(v7.423) 불필요.
        const used = new Set([
          ...(current.fulfilledSerials || []),
          ...pendingSerials.map(p => p.serial),
        ]);
        const filtered = (list || []).filter(r => !used.has(r.serial_number));
        setSerialSuggestions(filtered);
        setSerialSuggestionIdx(-1);
      } catch (e) {
        setSerialSuggestions([]);
      }
    }, 150);
    return () => clearTimeout(t);
  }, [activeRequest, requestSerialInput, pendingSerials]);

  // v7.306: 출고 시리얼 입력칸 포커스 — 같은 UI 가 두 벌(출고 페이지 / 배송시트지 모달)이라
  //   ref 도 두 개다. 모달이 열려 있으면 모달 쪽이 우선.
  const reqSerialInputRef = useRef(null);        // 출고 페이지 패널
  const reqSerialInputModalRef = useRef(null);   // 배송시트지 → 출고 처리 모달
  const focusRequestSerial = () => {
    // setState 반영(= disabled 해제) 후에 잡아야 하므로 다음 틱으로 미룬다
    setTimeout(() => {
      const el = (outboundModalOpen && reqSerialInputModalRef.current)
        || reqSerialInputRef.current
        || reqSerialInputModalRef.current;
      if (el && !el.disabled) { el.focus(); try { el.select(); } catch (_) {} }
    }, 60);
  };

  const handleRequestScan = async () => {
    if (!activeRequest) return;
    const sn = requestSerialInput.trim();
    if (!sn) return;
    const current = activeRequest.models.find(m => m.model === activeRequest.currentModel);
    if (!current) return;
    const totalCount = current.fulfilledSerials.length + pendingSerials.filter(p => p.model === current.model).length;
    if (totalCount >= current.qty) {
      setRequestMsg({type:'warn',msg:'이미 요청 수량을 모두 스캔했습니다'});
      return;
    }
    setLoading(true);
    // 시리얼 존재 + 모델 일치 + 재고 상태 검증 (DB 변경 없음)
    const item = await InventoryService.findBySerial(sn);
    if (!item) {
      setRequestMsg({type:'err',msg:`시리얼 "${sn}" — DB에 존재하지 않습니다`});
      setLoading(false); return;
    }
    if (item.product_name !== current.model) {
      setRequestMsg({type:'err',msg:`모델 불일치! 요청: ${current.model} / 시리얼: ${item.product_name}`});
      setLoading(false); return;
    }
    if (item.status === 'shipped') {
      setRequestMsg({type:'warn',msg:`이미 출고된 시리얼입니다 → ${item.dealer_name||'미지정'}`});
      setLoading(false); return;
    }
    if (item.status === 'unsellable') {
      setRequestMsg({type:'err',msg:`판매 불가 상태의 시리얼입니다. 출고할 수 없습니다.`});
      setLoading(false); return;
    }
    // v7.66: reserved (이미 다른 방문수령 요청에 예약) — commit conflict 전에 명시적 차단
    if (item.status === 'reserved') {
      setRequestMsg({type:'warn',msg:`이미 예약된 시리얼입니다 → ${item.dealer_name||'미지정'} (예약 해제 후 재시도)`});
      setLoading(false); return;
    }
    // v7.424: ship_hold (이미 다른 배송건에 담김) — 명시적 차단. (in_stock 만 담기 허용)
    if (item.status === 'ship_hold') {
      setRequestMsg({type:'warn',msg:`이미 다른 배송건에 담긴 시리얼입니다. 그 건에서 제외한 뒤 다시 시도하세요.`});
      setLoading(false); return;
    }
    // v7.66: in_stock 외 알 수 없는 status — 안전상 차단 + 진단 정보 표시
    if (item.status !== 'in_stock') {
      setRequestMsg({type:'err',msg:`출고 불가 상태: '${item.status}'. 관리자 문의가 필요합니다.`});
      setLoading(false); return;
    }
    if (current.fulfilledSerials.includes(sn) || pendingSerials.some(p => p.serial === sn)) {
      setRequestMsg({type:'warn',msg:'이미 이 요청에 스캔된 시리얼입니다'});
      setLoading(false); return;
    }
    // v7.424: 담기 = 즉시 ship_hold 로 예약 (in_stock → ship_hold).
    //   이 순간 다른 배송건의 재고 후보/스캔에서 자동으로 빠진다(이중 할당 방지).
    //   실패(=in_stock 아님) 시엔 그 사이 다른 곳에서 담았거나 상태가 바뀐 것 → 담기 취소.
    //   ⚠ EXT-/NOSN- 등 inventory 에 없는 시리얼은 여기 오지 않는다(위에서 findBySerial 로 이미 걸러짐).
    const held = await InventoryService.holdBySerial(sn, { reqId: activeRequest?.reqId || '' });
    if (!held.ok) {
      // v7.425: DB 오류(예: CHECK 제약에 ship_hold 미추가 = 마이그레이션 미실행)와
      //   상태 충돌(이미 담김/출고됨)을 구분해서 알린다. v7.424 는 둘 다 "방금 다른 곳에서 담겼다"로
      //   뭉개서 원인 파악을 어렵게 했다.
      if (held.dbError) {
        setRequestMsg({type:'err',msg:`재고 상태 변경 실패: ${held.msg} — DB 마이그레이션(ship_hold) 실행 여부를 확인하세요.`});
      } else {
        setRequestMsg({type:'warn',msg:`시리얼 "${sn}" — 방금 다른 곳에서 담겼거나 상태가 바뀌어 담을 수 없습니다. 새로고침 후 확인하세요.`});
      }
      setLoading(false); return;
    }
    // ✓ 검증·예약 통과 — pendingSerials에 추가 (DB 커밋은 [제품 출고] 버튼에서)
    const newPendingItem = { serial: sn, model: current.model, modelLogId: current.id };
    setPendingSerials(p => [...p, newPendingItem]);
    // v7.149: Supabase 병행 저장 (다른 PC와 공유 + 복원). 실패해도 로컬은 유지되므로 조용히 무시.
    if (activeRequest?.reqId) {
      ShipPendingService.addSerial(activeRequest.reqId, newPendingItem).catch(() => {});
    }
    setRequestSerialInput('');

    // v7.306: 현재 품목 수량이 채워지면 다음 미완료 품목으로 자동 전환 + 커서 이동.
    //   기존에는 1/1 을 찍고도 사용자가 탭을 직접 클릭한 뒤 입력칸을 다시 눌러야 했다.
    //   pendingSerials 는 아직 이번 건이 반영되기 전 값이므로 현재 모델에 +1 해서 센다.
    const filledOf = (m) =>
      (m.fulfilledSerials?.length || 0)
      + pendingSerials.filter(p => p.model === m.model).length
      + (m.model === current.model ? 1 : 0);
    const models = activeRequest.models || [];
    const curIdx = models.findIndex(m => m.model === current.model);
    let nextModel = null;
    for (let k = 1; k <= models.length; k++) {
      const m = models[(curIdx + k) % models.length];
      if (!m || m.model === current.model) continue;
      // 외부/SN면제 품목은 열릴 때 이미 자동 채워지고 입력칸 자체가 숨겨져 있다 → 건너뛴다
      if (m.external || m.serialOptional) continue;
      if (filledOf(m) < m.qty) { nextModel = m; break; }
    }
    const curDone = filledOf(current) >= current.qty;

    if (curDone && nextModel) {
      setActiveRequest(p => ({ ...p, currentModel: nextModel.model }));
      // 이전 모델의 재고 후보가 남아 잘못 선택되는 것을 막는다 (useEffect 가 곧 새로 채운다)
      setSerialSuggestions([]);
      setSerialSuggestionsOpen(false);
      setSerialSuggestionIdx(-1);
      setRequestMsg({type:'ok',msg:`✓ ${sn} — ${current.model} 완료 (${current.qty}/${current.qty}) → 다음: ${nextModel.model}`});
    } else if (curDone) {
      setRequestMsg({type:'ok',msg:`✓ ${sn} — 모든 품목 스캔 완료. [제품 출고]를 누르세요`});
    } else {
      setRequestMsg({type:'ok',msg:`✓ ${sn} 스캔 완료 (커밋 대기)`});
    }
    setLoading(false);
    focusRequestSerial();
  };

  const removePendingSerial = (sn) => {
    setPendingSerials(p => p.filter(x => x.serial !== sn));
    // v7.149: Supabase 병행 삭제 (다른 PC 반영)
    if (activeRequest?.reqId) {
      ShipPendingService.removeSerial(activeRequest.reqId, sn).catch(() => {});
    }
    // v7.424: 담을 때 ship_hold 로 잠갔으므로 뺄 때 in_stock 으로 되돌린다.
    //   EXT-/NOSN- 는 inventory 에 없으므로 스킵(releaseHold 가 conflict 로 no-op 되지만 호출 자체를 아낌).
    if (sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-')) {
      InventoryService.releaseHoldBySerial(sn).catch(() => {});
    }
  };

  // v7.145: 현재 모델에 남은 수량만큼 EXT- 임시 시리얼 자동 부여 (외부 처리)
  //   배경: NX523·RAIL-B02 같은 제품이 카탈로그에 등록되면 v7.74 로직에서
  //         external:true 안 붙어 자동 EXT- 생성이 스킵됨. 이럴 때 수동 트리거용.
  //   원본 external item과 동일 형식으로 pendingSerials에 append (serial: EXT-XXX-01, external: true).
  const assignExternalToCurrentModel = () => {
    if (!activeRequest) return;
    const current = activeRequest.models.find(m => m.model === activeRequest.currentModel);
    if (!current) return;
    const already = pendingSerials.filter(p => p.model === current.model).length;
    const fulfilled = current.fulfilledSerials?.length || 0;
    const remaining = current.qty - fulfilled - already;
    if (remaining <= 0) return;
    const modelKey = current.model.replace(/\s+/g, '');
    const startIdx = fulfilled + already + 1;
    const newPending = [];
    for (let i = 0; i < remaining; i++) {
      const idx = startIdx + i;
      newPending.push({
        serial: `EXT-${modelKey}-${String(idx).padStart(2, '0')}`,
        model: current.model,
        modelLogId: current.id,
        external: true,
        serialOptional: false
      });
    }
    setPendingSerials(prev => [...prev, ...newPending]);
    // v7.149: Supabase 병행 저장 (다른 PC 공유)
    if (activeRequest?.reqId && newPending.length > 0) {
      ShipPendingService.addSerials(activeRequest.reqId, newPending).catch(() => {});
    }
  };

  const commitRequest = async () => {
    if (!activeRequest || !pendingSerials.length) return;
    setLoading(true);
    const dealerLabel = activeRequest.common.recipient || activeRequest.common.requester || '';
    // v7.55: 방문수령 여부 — true이면 inventory를 reserved로 변경 (shipped 대신)
    //        + transaction_log에 reservedSerials로 저장, fulfilled=false 유지
    //        나중에 [방문수령 완료] 버튼으로 shipped + fulfilledSerials로 이동
    const isPickupMode = !!activeRequest.common?.pickup;

    // ═══════════════════════════════════════════════════════════
    // 1단계: inventory 먼저 처리 (Optimistic Lock)
    //   다른 사용자가 먼저 출고한 시리얼은 여기서 걸러짐.
    //   성공한 시리얼만 다음 단계로 진행하여 transaction_log에 반영.
    // ═══════════════════════════════════════════════════════════
    const conflictSerials = [];
    const successPending = [];
    for (const p of pendingSerials) {
      // 외부 제품(EXT-) / 시리얼 면제(NOSN-) 는 inventory에 존재하지 않으므로 검증 스킵
      if (p.external || p.serialOptional || p.serial.startsWith('EXT-') || p.serial.startsWith('NOSN-')) {
        successPending.push(p);
        continue;
      }
      // v7.55: 방문수령이면 reserved, 일반은 shipped
      const res = isPickupMode
        ? await InventoryService.reserveBySerial(p.serial, {
            dealerName: dealerLabel,
            note: '[예약] ' + (activeRequest.common?.addr || ''),
          })
        : await InventoryService.shipBySerial(p.serial, {
            dealerName: dealerLabel,
            note: activeRequest.common?.addr || '',
          });
      if (!res.ok) {
        // v7.66: conflict 발생 시 실제 DB status를 다시 조회하여 alert에 표시
        //         (예: 'reserved', 'shipped' 등 — DB constraint 위반 같은 숨은 원인 추적용)
        let statusTag = '?';
        try {
          const cur = await InventoryService.findBySerial(p.serial);
          statusTag = cur?.status || '?';
        } catch (e) { /* ignore */ }
        conflictSerials.push(`${p.serial}[${statusTag}]`);
      } else {
        successPending.push(p);
      }
    }

    // ═══════════════════════════════════════════════════════════
    // 2단계: 성공한 시리얼만 모델별로 그룹핑
    // ═══════════════════════════════════════════════════════════
    const byModel = {};
    successPending.forEach(p => {
      if (!byModel[p.model]) byModel[p.model] = [];
      byModel[p.model].push(p.serial);
    });

    // ═══════════════════════════════════════════════════════════
    // 3단계: transaction_log 업데이트 (성공 시리얼이 있을 때만)
    //   updated_at 락 + 최대 3회 재시도. 같은 요청에 다른 시리얼을
    //   동시 처리하는 경우 자동 머지.
    // ═══════════════════════════════════════════════════════════
    const updatedModels = [];
    let txLogSaveFailed = false;

    if (successPending.length > 0) {
      if (activeRequest.isMultiItem) {
        // 새 형식: 같은 행 안의 items 배열을 갱신
        const rowId = activeRequest.models[0].id;
        let saved = false;
        for (let attempt = 0; attempt < 3 && !saved; attempt++) {
          const { data: row } = await supabase.from('transaction_log').select('note,updated_at').eq('id', rowId).single();
          if (!row) break;
          let info = {};
          try { info = JSON.parse(row.note); } catch(e){}
          // v7.55: 방문수령이면 reservedSerials에, 일반은 fulfilledSerials에
          const targetKey = isPickupMode ? 'reservedSerials' : 'fulfilledSerials';
          const updatedItems = (info.items || []).map(it => {
            const newSerials = byModel[it.product_name] || [];
            const existing = it[targetKey] || [];
            const merged = [...existing, ...newSerials.filter(s => !existing.includes(s))];
            return { ...it, [targetKey]: merged };
          });
          // 방문수령은 reservedSerials만 채워졌지 fulfilled가 아니므로 false 유지
          // 일반 출고만 fulfilled 계산
          const allComplete = isPickupMode
            ? false
            : updatedItems.every(it => (it.fulfilledSerials?.length || 0) >= it.qty);
          // v7.253: 스캔으로 새로 출고하면 '되돌림 보관분'은 의미가 없어진다.
          //   남겨두면 나중에 [출고요청] 칩이 옛 시리얼을 다시 적용하려 들어 이중 출고가 된다.
          const { revertedItems: _dropRev, revertedAt: _dropRevAt, ...infoRest } = info;
          const updatedNote = JSON.stringify({
            ...infoRest, items: updatedItems, fulfilled: allComplete,
            completedAt: allComplete ? new Date().toISOString() : (info.completedAt || null)
          });
          const { data: result } = await supabase.from('transaction_log')
            .update({ note: updatedNote, dealer_name: dealerLabel })
            .eq('id', rowId)
            .eq('updated_at', row.updated_at)
            .select('id');
          if (result && result.length > 0) {
            saved = true;
            updatedModels.length = 0;
            activeRequest.models.forEach(m => {
              const it = updatedItems.find(x => x.product_name === m.model);
              // v7.55: 방문수령은 reservedSerials를 표시용으로 활용
              const fulfilledSerials = it?.fulfilledSerials || [];
              const reservedSerials = it?.reservedSerials || [];
              const fulfilled = fulfilledSerials.length >= m.qty;
              updatedModels.push({ ...m, fulfilledSerials, reservedSerials, fulfilled, info: { ...info, items: updatedItems, fulfilled: allComplete } });
            });
          } else {
            // 락 충돌: 다른 사람이 같은 요청을 갱신 중 → 짧게 쉬고 재시도
            await new Promise(r => setTimeout(r, 100 + Math.random() * 200));
          }
        }
        if (!saved) txLogSaveFailed = true;
      } else {
        // 구 형식: 모델별 행이 분리됨 (호환)
        for (const m of activeRequest.models) {
          const newSerials = byModel[m.model] || [];
          if (newSerials.length === 0) { updatedModels.push(m); continue; }
          let saved = false;
          for (let attempt = 0; attempt < 3 && !saved; attempt++) {
            const { data: row } = await supabase.from('transaction_log').select('note,updated_at,serial').eq('id', m.id).single();
            if (!row) break;
            let info = {};
            try { info = JSON.parse(row.note); } catch(e){}
            const existing = info.fulfilledSerials || [];
            const totalFulfilled = [...existing, ...newSerials.filter(s => !existing.includes(s))];
            const isComplete = totalFulfilled.length >= m.qty;
            const updatedNote = JSON.stringify({
              ...info, fulfilledSerials: totalFulfilled, fulfilled: isComplete,
              completedAt: isComplete ? new Date().toISOString() : null
            });
            const origHasReqId = row.serial?.startsWith('REQ-');
            let startIdx = 0;
            if (origHasReqId) {
              const { data: result } = await supabase.from('transaction_log').update({
                serial: newSerials[0], note: updatedNote, dealer_name: dealerLabel
              }).eq('id', m.id).eq('updated_at', row.updated_at).select('id');
              if (!result || result.length === 0) {
                await new Promise(r => setTimeout(r, 100 + Math.random() * 200));
                continue;
              }
              startIdx = 1;
            } else {
              const { data: result } = await supabase.from('transaction_log').update({ note: updatedNote })
                .eq('id', m.id).eq('updated_at', row.updated_at).select('id');
              if (!result || result.length === 0) {
                await new Promise(r => setTimeout(r, 100 + Math.random() * 200));
                continue;
              }
            }
            for (let i = startIdx; i < newSerials.length; i++) {
              await supabase.from('transaction_log').insert({
                serial: newSerials[i], model: m.model, type: 'outbound',
                dealer_name: dealerLabel, note: updatedNote,
                processed_by: m.info.requester || '',
                transaction_date: today()
              });
            }
            updatedModels.push({ ...m, fulfilledSerials: totalFulfilled, fulfilled: isComplete, info: { ...m.info, fulfilledSerials: totalFulfilled, fulfilled: isComplete } });
            saved = true;
          }
          if (!saved) { updatedModels.push(m); txLogSaveFailed = true; }
        }
      }
    } else {
      // 모든 시리얼이 충돌 — transaction_log 손대지 않음
      activeRequest.models.forEach(m => updatedModels.push(m));
    }

    setActiveRequest(p => ({ ...p, models: updatedModels }));
    setPendingSerials([]);
    // v7.82: 커밋 성공 후 localStorage에서 pending 명시 정리
    //         (useEffect의 자동 정리는 배치 업데이트로 놓칠 수 있으므로 이중 보장)
    // v7.149: Supabase 서버측 pending도 함께 정리 (다른 PC의 stale 상태 제거)
    if (activeRequest?.reqId) {
      clearShipPending(activeRequest.reqId);
      ShipPendingService.clearByReqId(activeRequest.reqId).catch(() => {});
    }
    // v7.89: 사용량 트래킹 (실제 커밋된 시리얼 개수)
    if (successPending.length > 0) {
      AnalyticsService.trackAction('배송_커밋', activeRequest?.author || '', {
        reqId: activeRequest?.reqId, serialCount: successPending.length,
      });
    }
    // v7.127: 데모 대여 감지 → 완료 시 대여로그(audit_log)에 기록
    // v7.151: 대여 status 'pending' → 'active' 승격 + equipment '대여예약' → '대여중'
    if (successPending.length > 0 && !isPickupMode && activeRequest) {
      const isFromDemo = activeRequest.common?.fromDemoLend || activeRequest.models[0]?.info?.fromDemoLend;
      const demoRentalId = activeRequest.common?.demoRentalId || activeRequest.models[0]?.info?.demoRentalId;
      const allComplete = updatedModels.every(m => m.fulfilled);
      if (isFromDemo && demoRentalId && allComplete) {
        try {
          // v7.151: finalizeRental 호출 — status/location 승격 + audit_log 'ship_complete' 추가
          //         appendRentalAudit는 finalizeRental이 내부적으로 처리하므로 별도 호출 불필요
          const res = await DemoService.finalizeRental({
            rentalId: demoRentalId,
            by: activeRequest.author || activeRequest.common?.author || '미지정',
          });
          if (!res.ok) console.warn('데모 대여 finalize 실패:', res.msg);
        } catch (e) { console.warn('데모 대여 finalize 예외:', e); }
      }
    }

    // ═══════════════════════════════════════════════════════════
    // 4단계: 결과 메시지
    // ═══════════════════════════════════════════════════════════
    if (txLogSaveFailed) {
      setRequestMsg({type:'warn', msg:'⚠ 다른 사용자가 같은 출고 요청을 동시에 수정 중입니다. 잠시 후 새로고침해주세요.'});
    } else if (conflictSerials.length > 0) {
      const okCount = successPending.length;
      if (okCount === 0) {
        setRequestMsg({type:'err', msg:`⚠ 모든 시리얼이 이미 다른 사용자에 의해 처리됨: ${conflictSerials.slice(0,3).join(', ')}${conflictSerials.length>3?' 외':''} — 다른 시리얼을 입력해주세요.`});
      } else {
        setRequestMsg({type:'warn', msg:`⚠ ${okCount}개 출고 완료. ${conflictSerials.length}개 시리얼은 다른 사용자가 먼저 처리: ${conflictSerials.slice(0,3).join(', ')}${conflictSerials.length>3?' 외':''}`});
      }
    } else {
      setRequestMsg({type:'ok', msg:`✓ ${successPending.length}개 시리얼 출고 완료!`});
    }

    // v7.233: 퀵서비스 건이 전부 출고되면 배차 시트 진행상황을 '완료'로 자동 전환
    //   ・ 수동 [완료 처리] 버튼과 동일한 결과. 출고 완료 시점이 곧 배차 완료 시점이라
    //     따로 한 번 더 누르게 하지 않는다 (누락 방지)
    //   ・ 이미 완료 처리된 건은 건너뛴다 (완료시각을 덮어쓰지 않음)
    //   ・ 실패해도 출고 결과에는 영향 없음 — 조용히 로깅만 하고, 이후 수동 버튼으로 복구 가능
    try {
      const allDone = updatedModels.length > 0 && updatedModels.every(m => m.fulfilled);
      if (allDone && !txLogSaveFailed && activeRequest?.reqId) {
        const { data: rows } = await supabase.from('transaction_log')
          .select('id,note,dealer_name,created_at')
          .eq('serial', activeRequest.reqId).limit(1);
        const row = rows && rows[0];
        if (row) {
          let n = {}; try { n = JSON.parse(row.note || '{}'); } catch (e) {}
          // v7.242: '퀵서비스' 직접 비교 → isQuickMethod() (레거시 '퀵' 건이 여기서만 빠졌었다)
          if (n.type === 'ship_request' && isQuickMethod(n.method) && !n.quickCompletedAt) {
            // v7.241: [시트 전송]을 건너뛰고 바로 출고한 경우엔 여기가 첫 기록이 된다
            //   → 그때 배차시간을 찍는다. 이미 찍혀 있으면 그 값으로 같은 행을 찾아 갱신.
            const stage = quickStage(n);
            const nextNote = { ...stampDispatchTime(n), quickCompletedAt: fmtSheetDateTime(new Date()) };
            await supabase.from('transaction_log')
              .update({ note: JSON.stringify(nextNote) }).eq('id', row.id);
            if (quickSheetWebhook) {
              const payload = buildQuickSheetPayload(nextNote, row, stage);
              await postQuickSheet(payload);
              await supabase.from('transaction_log')
                .update({ note: JSON.stringify({ ...nextNote, quickSheetSentAt: payload.sentAt }) })
                .eq('id', row.id);
            }
          }
        }
      }
    } catch (e) {
      console.warn('퀵 배차 시트 완료 전환 실패 (출고는 정상 처리됨):', e);
    }

    setLoading(false);
  };

  // Ship request: search products
  // ─── v7.351: NAS 목록 적재 (탭 진입 시 1회) ────────────────
  //   ⚠ category='NAS' 로 거른다. 검색(searchByName)은 category NULL 도 남기지만,
  //     폴더 브라우징은 목록을 통째로 받는 것이라 기준이 확실해야 한다.
  //     카탈로그에 NAS 인데 category 가 비어 있는 제품이 있으면 여기 안 나온다 →
  //     그런 건 위 검색창으로 찾으면 된다. 두 경로를 일부러 다르게 뒀다.
  useEffect(() => {
    if (page !== 'compat' || compatAll !== null) return;
    let alive = true;
    (async () => {
      const { data } = await ProductService.list({ category: 'NAS', page: 0, pageSize: 1000 });
      if (alive) setCompatAll(data || []);
    })();
    return () => { alive = false; };
  }, [page, compatAll]);

  // v7.358: 모델/카테고리가 바뀌면 호환 목록을 다시 읽는다.
  //   ⚠ compatData=null 을 [로딩 중] 으로 쓴다. 빈 배열([])과 구분해야
  //     "아직 안 왔다"와 "받아 봤더니 없다"가 섞이지 않는다.
  useEffect(() => {
    if (page !== 'compat' || !compatModel) { setCompatData(null); return; }
    let alive = true;
    setCompatData(null);
    (async () => {
      const r = await QnapCompatService.forProduct(compatModel.product_name, compatCat);
      if (alive) setCompatData(r);
    })();
    return () => { alive = false; };
  }, [page, compatModel, compatCat]);

  // v7.359: 카테고리가 바뀌어도 건수는 그대로다 → 모델이 바뀔 때만 조회한다.
  //   ⚠ compatCat 을 의존성에 넣으면 탭을 누를 때마다 3회 왕복이 더 생긴다.
  //   ⚠ 의존성에 compatData 를 통째로 넣으면 카테고리를 바꿀 때마다 객체가 새로 만들어져
  //     3회 왕복이 매번 다시 돈다. modelId 만 꺼내 쓴다.
  const compatModelId = compatData?.modelId || null;
  useEffect(() => {
    if (page !== 'compat' || !compatModelId) { setCompatCounts(null); return; }
    let alive = true;
    (async () => {
      const c = await QnapCompatService.countsByCategory(compatModelId);
      if (alive) setCompatCounts(c);
    })();
    return () => { alive = false; };
  }, [page, compatModelId]);

  // v7.364: 사내 테스트 결과 — 모델이 바뀔 때 한 번만 통째로 받는다.
  //   ⚠ compatCat 을 의존성에 넣지 않는다. 카테고리 탭 뱃지 숫자를 이 배열에서 세야 하는데,
  //     카테고리별로 나눠 받으면 [지금 안 보고 있는 탭] 의 건수를 알 수 없다.
  //   ⚠ 한 모델당 많아야 수십 건이라 통째로 받아도 전송량이 작다.
  useEffect(() => {
    if (page !== 'compat' || !compatModel) { setIcRows(null); return; }
    let alive = true;
    setIcRows(null);
    (async () => {
      const r = await InternalCompatService.forProduct(compatModel.product_name);
      if (alive) setIcRows(r || []);
    })();
    return () => { alive = false; };
  }, [page, compatModel, icReload]);

  // v7.360: 보유 재고 색인 — 탭에 들어올 때 한 번만.
  //   ⚠ compatModel/compatCat 을 의존성에 넣지 말 것. 재고는 어느 NAS 를 보든 같다.
  useEffect(() => {
    if (page !== 'compat' || stockIdx !== null) return;
    let alive = true;
    (async () => {
      const idx = await QnapCompatService.stockIndex();
      if (alive) setStockIdx(idx);
    })();
    return () => { alive = false; };
  }, [page, stockIdx]);

  // v7.361: 검색어가 NAS 모델명과 하나도 안 맞으면 [부품 모델명] 으로 다시 찾아본다.
  //   ⚠ 모드 전환 스위치를 두지 않은 이유: 'TS-464' 와 'WD140EFGX' 는 겹칠 일이 없다.
  //     사용자가 무엇을 치는지 스스로 알므로, 앱이 알아서 떨어지는 편이 낫다.
  //   ⚠ NAS 이름이 하나라도 맞으면 서버에 묻지 않는다. 평소엔 쿼리 0회다.
  //   ⚠ 디바운스 필요. 이건 로컬 필터와 달리 진짜 서버 왕복이다.
  useEffect(() => {
    const q = compatSearch.trim();
    if (page !== 'compat' || compatModel) { setPartLookup(null); return; }
    if (q.length < 3 || !compatAll) { setPartLookup(null); return; }
    const g = qnapNorm(q);
    const localHit = compatAll.some(p0 => qnapNorm(p0.product_name).includes(g));
    if (localHit) { setPartLookup(null); return; }
    let alive = true;
    setPartLookup('loading');
    const t = setTimeout(async () => {
      // v7.364: 공식 목록에 없는 부품(삼성·Transcend 등)은 사내 테스트에만 있다.
      //   ⚠ 두 곳을 다 뒤지지 않으면 "우리가 테스트한 부품인데 검색이 안 된다"가 된다.
      const [a, b] = await Promise.all([
        QnapCompatService.nasForPart(q),
        InternalCompatService.nasForPart(q),
      ]);
      if (!alive) return;
      const names = new Set([...(a?.productNames || []), ...(b?.productNames || [])]);
      // 부품 칩은 공식 쪽을 먼저 보여주고 사내 것으로 채운다 (중복 모델명은 한 번만).
      const seen = new Set();
      const parts = [...(a?.parts || []), ...(b?.parts || [])]
        .filter(pt => { const k = qnapNorm(pt.model); if (seen.has(k)) return false; seen.add(k); return true; })
        .slice(0, 8);
      setPartLookup({ parts, productNames: names });
    }, 280);
    return () => { alive = false; clearTimeout(t); };
  }, [page, compatSearch, compatAll, compatModel]);

  // v7.359: 모델이나 카테고리가 바뀌면 목록 안 필터를 초기화한다.
  //   ⚠ 안 하면 HDD 에서 '14TB' 로 걸러 둔 채 메모리 탭으로 갔을 때 0건이 나온다.
  //     사용자는 데이터가 없는 줄 안다.
  //   v7.366: 선택 삭제 모드도 같이 푼다. 다른 카테고리에서 골라 둔 id 가 남아 있으면
  //     화면에 안 보이는 항목이 [n건 삭제] 에 섞여 들어간다.
  useEffect(() => { setPartQ(''); setCapFilter(null); setStockOnly(false); setIcSelMode(false); setIcSel([]); }, [compatModel, compatCat]);
  // v7.413: [전체] 칩을 없앴으므로 용량 필터 기본값을 [첫 칩 = 최대 용량] 으로 채운다.
  //   위 리셋 effect 가 탭/모델 전환 시 capFilter=null 로 비우면, compatData 로드 후 여기서 최대 용량으로 세팅.
  //   capFilter 가 이미 있으면 두므로 사용자의 선택을 덮어쓰지 않는다.
  useEffect(() => {
    if (capFilter != null) return;
    if (!compatData || compatData.state !== 'ok' || !Array.isArray(compatData.rows)) return;
    const cps = [...new Set(compatData.rows.map(r => r.capacity_gb).filter(v => v != null))].sort((a, b) => b - a);
    if (cps.length) setCapFilter(cps[0]);
  }, [compatData, compatCat, capFilter]);

  // v7.357: 서버 검색 useEffect 제거.
  //   ⚠ v7.349~356 은 글자마다 ProductService.searchByName 을 호출했다. 이제 탭에 들어올 때
  //     NAS 목록을 통째로 받아 두므로(compatAll) 서버에 다시 물을 이유가 없다.
  //     쿼리 0회 = 지연 0. 필터는 아래 렌더에서 문자열 비교로 끝난다.

  const searchProducts = async (q) => {
    setShipSearch(q);
    if (q.length < 2) { setShipResults([]); return; }
    // v7.374: 재고(inventory) + 카탈로그(products) 를 [둘 다] 본다.
    //   ⚠ 기존에는 재고 있는 것만 검색돼서, 재고 0인 제품은 목록에 아예 안 떴다.
    //     그러면 사용자는 [등록 안 된 제품] 인지 [재고가 없는 것] 인지 구분할 수 없고,
    //     결국 모델명을 손으로 쳐서 Enter 로 넣는 우회로를 쓰게 된다 (v7.74 경로).
    //     검색이 안 되는 것과 재고가 없는 것은 다른 사실이다 — 화면에서 갈라 보여준다.
    //   ⚠ 두 요청을 병렬로 보낸다. 순차로 하면 타이핑마다 왕복이 두 배가 된다.
    const [inv, cat] = await Promise.all([
      InventoryService.searchInStockProducts(q),
      ProductService.searchByName(q, 20),
    ]);
    const grouped = {};
    (inv || []).forEach(r => {
      if (!grouped[r.product_name]) grouped[r.product_name] = { model: r.product_name, category: r.category, in_stock: 0, byType: {} };
      grouped[r.product_name].in_stock++;
      grouped[r.product_name].byType[r.stock_type] = (grouped[r.product_name].byType[r.stock_type] || 0) + 1;
    });
    // 카탈로그에만 있는 제품을 재고 0으로 채워 넣는다 (기존 항목은 덮지 않는다)
    (cat || []).forEach(p => {
      if (!grouped[p.product_name]) {
        grouped[p.product_name] = { model: p.product_name, category: p.category, in_stock: 0, byType: {} };
      }
    });
    // 재고 있는 것 먼저 · 그 안에서 수량 많은 순 · 재고 0끼리는 이름순
    //   ⚠ 재고 0을 위로 올리면 자주 쓰는 제품이 아래로 밀린다. 순서가 곧 기본값이다.
    const list = Object.values(grouped).sort((a, b) =>
      (b.in_stock > 0 ? 1 : 0) - (a.in_stock > 0 ? 1 : 0) ||
      b.in_stock - a.in_stock ||
      a.model.localeCompare(b.model));
    setShipResults(list.slice(0, 12));
  };
  const addShipItem = (item) => {
    if (shipItems.find(i => i.model === item.model)) return;
    setShipItems(prev => [...prev, { model: item.model, category: item.category, available: item.in_stock, byType: item.byType, qty: 1 }]);
    setShipSearch('');
    setShipResults([]);
  };
  // 타이핑 빠른 추가: "NX-433 5개" / "NX-433 5" / "NX-433" 형식 파싱
  const quickAddShipItem = async () => {
    const input = shipSearch.trim();
    if (!input) return;
    // 끝부분 숫자(+ 개) 추출
    const match = input.match(/^(.+?)\s+(\d+)\s*개?\s*$/);
    let modelName, qty;
    if (match) { modelName = match[1].trim(); qty = parseInt(match[2]); }
    else { modelName = input; qty = 1; }
    if (qty <= 0) {
      setShipMsg({ type: 'err', msg: '수량은 1 이상이어야 합니다.' });
      setTimeout(() => setShipMsg(null), 2500); return;
    }
    // 부분 일치 검색
    const data = await InventoryService.searchInStockProducts(modelName);
    if (!data || data.length === 0) {
      // v7.74: 재고 없음 시 카탈로그 존재 여부로 분기
      //   (a) 카탈로그에 등록된 제품 → external:false 정상 흐름 (재고 부족 예약 상태)
      //       입고 후 시리얼 스캔으로 처리 가능. WD_ULTRASTAR_SATA 4TB 같은 HDD 케이스 대응.
      //   (b) 카탈로그에도 없음 → 기존처럼 external:true 폴백 (진짜 외부 제품)
      const catalog = await ProductService.findExact(modelName)
                   || (await ProductService.findCaseInsensitive(modelName, 1))[0];
      const canonicalName = catalog?.product_name || modelName;
      if (shipItems.find(i => i.model === canonicalName)) {
        setShipMsg({ type: 'err', msg: `"${canonicalName}"은(는) 이미 추가되어 있습니다.` });
        setTimeout(() => setShipMsg(null), 2500); return;
      }
      if (catalog) {
        // 카탈로그 등록 제품 — 정상 시리얼 관리 흐름, 재고 0 상태
        setShipItems(prev => [...prev, {
          model: catalog.product_name,
          category: catalog.category || '미분류',
          available: 0, byType: {}, qty: qty
        }]);
        setShipSearch('');
        setShipResults([]);
        setShipMsg({ type: 'warn', msg: `⚠ "${catalog.product_name}" × ${qty}개 — 카탈로그 등록 제품이나 현재 재고 부족 (입고 후 시리얼 스캔 필요)` });
        setTimeout(() => setShipMsg(null), 4000);
        return;
      }
      // DB 미등록 — 외부 제품으로 추가 (재고 추적 없이)
      setShipItems(prev => [...prev, {
        model: modelName, category: '외부 제품',
        available: qty, byType: {}, qty: qty, external: true
      }]);
      setShipSearch('');
      setShipResults([]);
      setShipMsg({ type: 'warn', msg: `⚠ "${modelName}" × ${qty}개 — DB 미등록 외부 제품으로 추가됨 (재고 차감 없음)` });
      setTimeout(() => setShipMsg(null), 3500);
      return;
    }
    const grouped = {};
    data.forEach(r => {
      if (!grouped[r.product_name]) grouped[r.product_name] = { model: r.product_name, category: r.category, in_stock: 0, byType: {} };
      grouped[r.product_name].in_stock++;
      grouped[r.product_name].byType[r.stock_type] = (grouped[r.product_name].byType[r.stock_type] || 0) + 1;
    });
    const models = Object.values(grouped);
    // 여러 개 매칭 — 정확 일치 우선
    let target = models[0];
    if (models.length > 1) {
      const exact = models.find(m => m.model.toLowerCase() === modelName.toLowerCase());
      if (exact) target = exact;
      else {
        setShipMsg({ type: 'err', msg: `"${modelName}"에 일치하는 모델이 ${models.length}개입니다. 더 구체적으로 입력하세요. (${models.slice(0,3).map(m=>m.model).join(', ')}${models.length>3?'...':''})` });
        setTimeout(() => setShipMsg(null), 4000); return;
      }
    }
    // 이미 추가됐는지
    if (shipItems.find(i => i.model === target.model)) {
      setShipMsg({ type: 'err', msg: `"${target.model}"은(는) 이미 추가되어 있습니다.` });
      setTimeout(() => setShipMsg(null), 2500); return;
    }
    const effectiveQty = Math.min(qty, target.in_stock);
    setShipItems(prev => [...prev, {
      model: target.model, category: target.category,
      available: target.in_stock, byType: target.byType, qty: effectiveQty
    }]);
    setShipSearch('');
    setShipResults([]);
    const note = effectiveQty < qty ? ` (재고 ${target.in_stock}개 한도로 조정)` : '';
    setShipMsg({ type: 'ok', msg: `✓ ${target.model} × ${effectiveQty}개 추가${note}` });
    setTimeout(() => setShipMsg(null), 2500);
  };
  const removeShipItem = (model) => setShipItems(prev => prev.filter(i => i.model !== model));
  // v7.374: 재고 한도를 적용할지 판정.
  //   ⚠ available === 0 에 Math.min 을 그대로 걸면 수량이 0으로 무너진다.
  //     v7.74 가 재고 0 제품을 추가하는 경로를 열어 놨는데, +/− 를 한 번만 눌러도
  //     qty 가 0이 되어 사실상 못 쓰는 상태였다. 재고가 없다는 건 [0개만 요청 가능]이
  //     아니라 [입고 후 처리할 예약]이다 — 한도를 걸 대상이 아니다.
  const shipQtyCapped = (i) => !i.external && i.available > 0;
  const updateShipQty = (model, delta) => setShipItems(prev => prev.map(i => {
    if (i.model !== model) return i;
    const newQty = Math.max(1, i.qty + delta);
    return { ...i, qty: shipQtyCapped(i) ? Math.min(i.available, newQty) : newQty };
  }));
  // v7.53: 수량 직접 입력 — input에서 받은 raw 값을 받아 1 ~ available 범위로 클램프
  const setShipQty = (model, rawValue) => setShipItems(prev => prev.map(i => {
    if (i.model !== model) return i;
    const n = parseInt(rawValue, 10);
    if (isNaN(n) || n < 1) return { ...i, qty: 1 };
    return { ...i, qty: shipQtyCapped(i) ? Math.min(i.available, n) : n };
  }));

  const submitShipRequest = async () => {
    if (!shipItems.length && shipMethod !== 'rma') { setShipMsg({ type: 'err', msg: '출고할 제품을 선택하세요.' }); return; }
    if (shipPickup && !shipVisitTime) { setShipMsg({ type: 'err', msg: '방문수령 모드에서는 방문 예정 시간을 입력하세요.' }); return; }
    if (shipMethod === 'rma') {
      if (!shipRmaProductName.trim()) { setShipMsg({ type: 'err', msg: 'EMS 모드에서는 제품명을 입력하세요.' }); return; }
      if (!shipRmaSerial.trim()) { setShipMsg({ type: 'err', msg: 'EMS 모드에서는 시리얼을 입력하세요.' }); return; }
    }
    // 직발 체크는 택배/퀵에서만 적용
    const hasDirect = (shipMethod==='courier'||shipMethod==='quick') && shipDirectCompany.trim();
    if ((shipMethod==='courier'||shipMethod==='quick') && shipDirectCompany!=='' && !shipDirectCompany.trim()) {
      setShipMsg({ type: 'err', msg: '직발 체크 시 업체명을 입력하세요.' }); return;
    }
    // ─── 킵(Keep) 차단 검증 ───
    // 출고하려는 모델별 수량이 [가용재고 = 재고 - 활성킵]을 초과하면 차단
    if (shipMethod !== 'rma' && shipItems.length > 0) {
      // 디버그: shipItems 전체 구조 출력 (model/external 플래그 확인용)
      // 사용자 보고용 — 콘솔에 남아서 문제 발생 시 검증 가능
      console.log('[ship-validation] shipItems:', JSON.stringify(shipItems.map(i => ({ model: i.model, qty: i.qty, external: !!i.external })), null, 2));
      // 모델별 요청 수량 집계
      const reqByModel = {};
      // 견고한 검증: external이 아니고 model이 있는 item만 집계
      // (외부 제품, 또는 어떤 이유로 model이 undefined인 케이스는 검증 대상 제외)
      shipItems.forEach(it => {
        if (it.external) return;        // 외부 제품 제외
        if (!it.model) return;          // model 누락된 item은 검증 안 함 (다른 곳에서 잡힘)
        reqByModel[it.model] = (reqByModel[it.model] || 0) + it.qty;
      });
      const modelNames = Object.keys(reqByModel);
      // 검증할 대상이 없으면 (모두 외부 제품이거나 model 누락) skip
      if (modelNames.length === 0) {
        console.log('[ship-validation] 검증 대상 없음 (모두 외부 또는 model 누락) — skip');
      } else {
      // 재고 수량 일괄 조회 (status=in_stock)
      const stockByModel = {};
      for (const m of modelNames) {
        const count = await InventoryService.countInStock(m);
        stockByModel[m] = count || 0;
      }
      // 활성 킵 최신 집계 (state 캐시 대신 직접 조회 — 안전)
      const { data: keepData } = await supabase.from('product_keeps').select('product_name,qty,dealer_name').eq('status', 'active').in('product_name', modelNames);
      const keepByModel = {};
      const keepDealersByModel = {};
      (keepData || []).forEach(k => {
        keepByModel[k.product_name] = (keepByModel[k.product_name] || 0) + (k.qty || 0);
        keepDealersByModel[k.product_name] = keepDealersByModel[k.product_name] || [];
        keepDealersByModel[k.product_name].push(`${k.dealer_name}(${k.qty})`);
      });
      // 모델별 검증
      const blocked = [];
      for (const m of modelNames) {
        const available = (stockByModel[m] || 0) - (keepByModel[m] || 0);
        if (reqByModel[m] > available) {
          blocked.push({
            model: m,
            requested: reqByModel[m],
            stock: stockByModel[m] || 0,
            keep: keepByModel[m] || 0,
            available,
            dealers: (keepDealersByModel[m] || []).join(', '),
          });
        }
      }
      // v7.375: 차단하지 않는다.
      //   ⚠ 출고 [요청] 은 출고가 아니다. 실제 재고 차감은 나중에 시리얼을 찍는
      //     출고 처리 단계에서 일어나고, 거기서 다시 검사한다. 요청 시점에 막으면
      //     "입고 예정이라 미리 요청해 두는" 정상 업무가 통째로 불가능해진다.
      //     (v7.374 재고없음 요청 허용과 같은 판단이다)
      //   ⚠ 다만 조용히 넘기지도 않는다. 남의 킵을 건드리는 건 실제 사고로 이어지므로
      //     경고는 [보내기 전] 최종 확인 패널에 미리 띄운다 — 눌러서 실패하는 것보다
      //     누르기 전에 아는 편이 낫다. 여기서는 콘솔에만 남긴다.
      if (blocked.length > 0) {
        console.warn('[ship-validation] 킵 초과 상태로 요청 진행:', blocked);
      }
      } // end of else (modelNames.length > 0)
    }
    // ─── /킵 차단 검증 ───
    setLoading(true);
    setShipMsg(null);
    const isRma = shipMethod === 'rma';
    const methodLabel = shipPickup ? '방문수령' : (shipMethod === 'courier' ? '택배' : shipMethod === 'floor' ? '1층배송' : shipMethod === 'install' ? '직접 설치' : isRma ? shipEmsType : '퀵서비스');
    const addrLabel = shipPickup
      ? `[방문수령] ${shipPickupCompany.trim() ? shipPickupCompany.trim() + ' · ' : ''}${fmtDate(shipExpectedDate)} ${shipVisitTime} 방문 예정`
      : isRma
        ? `[${shipEmsType}] ${shipRmaProductName.trim()} / ${shipRmaSerial.trim()}${shipRmaTicketNo.trim()?` (티켓: ${shipRmaTicketNo.trim()})`:''}`
        : shipAddr;
    const recipientLabel = isRma ? `[${shipEmsType}]` : shipRecipient;
    const reqId = `REQ-${today().replace(/-/g,'')}-${String(Date.now()).slice(-4)}`;
    // items 배열로 통합 (확장성 고려) — external 플래그 보존
    // 외부 제품도 출고요청 상태로 두고, 사용자가 출고완료 버튼을 눌러야 처리됨
    let items;
    if (isRma && shipItems.length === 0) {
      // EMS 모드: 제품 선택 없이 RMA 양식만으로 진행 — 가상 품목 생성, external 처리로 자동 출고
      items = [{
        product_name: shipRmaProductName.trim(),
        qty: 1,
        fulfilledSerials: [shipRmaSerial.trim()],
        external: true,
        emsOnly: true
      }];
    } else {
      items = shipItems.map(i => {
        const isExt = !!i.external;
        return {
          product_name: i.model, qty: i.qty,
          fulfilledSerials: [],
          ...(isExt ? { external: true } : {})
        };
      });
    }
    // v7.20: 모든 출고 요청은 기본 '출고대기' 상태로 시작 (사용자가 처리 셀에서 토글)
    //         이전: EMS 양식만 모드는 자동 fulfilled=true → 사용자 요청으로 출고대기 디폴트
    const allFulfilled = false;
    const shipNote = JSON.stringify({
      type:'ship_request', reqId,
      addr:addrLabel, recipient:recipientLabel, phone:shipPhone,
      // v7.195: 우체국 API 대비 — 방문수령/RMA 아닌 일반 배송건에만 zip/addrDetail 저장
      ...(!shipPickup && !isRma && shipZip ? { zip: shipZip.trim() } : {}),
      ...(!shipPickup && !isRma && shipAddrDetail ? { addrDetail: shipAddrDetail.trim() } : {}),
      method:methodLabel, author:shipAuthor, requester:shipRequester,
      // v7.20: RMA/DOA는 매출여부를 자동으로 '수기입력' + emsType (DOA/RMA) 라벨로 — 사용자가 더블클릭으로 변경 가능
      salesStatus: isRma ? '수기입력' : shipSalesStatus,
      ...(isRma
        ? { salesStatusManual: shipEmsType || 'RMA' }
        : (shipSalesStatus === '수기입력' && shipSalesStatusManual.trim() ? { salesStatusManual: shipSalesStatusManual.trim() } : {})),
      ...(shipMethod === 'courier' && shipTrackingNo.split(',').map(s=>s.trim()).filter(Boolean).length > 0 ? { trackingNo: shipTrackingNo.split(',').map(s=>s.trim()).filter(Boolean).join(',') } : {}),
      ...(shipMethod === 'quick' && !shipPickup ? { quickSalesStatus: shipQuickSalesStatus } : {}),
      ...(shipMethod === 'quick' && !shipPickup && shipQuickSalesStatus === '수기입력' && shipQuickSalesStatusManual.trim() ? { quickSalesStatusManual: shipQuickSalesStatusManual.trim() } : {}),
      ...(shipMethod === 'quick' && shipQuickPrice ? { quickPrice: Number(shipQuickPrice) } : {}),
      ...(shipMethod === 'quick' && shipQuickCOD ? { quickCOD: true } : {}),
      ...(shipMethod === 'quick' && shipQuickVehicle.trim() ? { quickVehicle: shipQuickVehicle.trim() } : {}),
      ...(shipMethod === 'quick' && shipQuickDriverPhone.trim() ? { quickDriverPhone: shipQuickDriverPhone.trim() } : {}),
      ...(shipMethod === 'quick' && shipQuickRemark.trim() ? { quickRemark: shipQuickRemark.trim() } : {}),
      // v7.222: 배차시간을 등록 시점에 확정 저장 — 시트 행 매칭 키이므로 절대 흔들려선 안 된다
      // v7.241: 배차시간은 제출 시점에 찍지 않는다.
      //   시트에 처음 올라가는 순간([시트 전송] 또는 [제품 출고])에 stampDispatchTime()이 찍는다.
      //   (v7.222~v7.240은 여기서 제출 시각을 확정해 넣었다 → 시트 배차시간 = 등록시각)
      // v7.228: 시트 지역 — [직접 입력] 체크 시 수기값, 아니면 배송지 주소 자동 추출값을 확정 저장
      ...(shipMethod === 'quick' && ((shipRegionManual && shipQuickRegion.trim()) || guessRegion(shipAddr))
        ? { quickRegion: (shipRegionManual && shipQuickRegion.trim()) || guessRegion(shipAddr) } : {}),
      ...(shipPickup ? { pickup: true, pickupTime: shipVisitTime,
            ...(shipPickupCompany.trim() ? { pickupCompany: shipPickupCompany.trim() } : {}) } : {}),
      ...(hasDirect ? { direct: true, directCompany: shipDirectCompany.trim() } : {}),
      // v7.103: 네이버스토어 플래그 (택배/퀵에서 체크한 경우만 저장)
      ...(shipNaverStore && (shipMethod === 'courier' || shipMethod === 'quick') ? { naverStore: true } : {}),
      ...(isRma ? {
        rma: true,
        emsType: shipEmsType,
        rmaProductName: shipRmaProductName.trim(),
        rmaSerial: shipRmaSerial.trim(),
        rmaFormNo: shipRmaFormNo.trim(),
        rmaTicketNo: shipRmaTicketNo.trim(),
        rmaEms: shipRmaEms.trim(),
        // v7.76: DOA 반품(들어오는) 시리얼 — 나가는 시리얼(rmaSerial)과 다른 시리얼로 회수될 때 사용
        //         수리완료 처리 시 이 시리얼이 있으면 신품으로 재고에 자동 등록됨
        ...(shipEmsType === 'DOA' && shipRmaReturnSerial.trim() ? { rmaReturnSerial: shipRmaReturnSerial.trim() } : {}),
        rmaFromDB: isRma && shipRmaSerialFromDB // DB 매칭 시리얼인지
      } : {}),
      expectedDate: shipExpectedDate,
      // v7.32: 과거 날짜로 등록(누락 기록 추가) 시 자동이월 OFF — 그 날짜 유지
      //         미래/오늘 날짜만 autoDefer ON (출고일까지 매일 today로 갱신)
      autoDefer: !allFulfilled && !(shipExpectedDate && shipExpectedDate < today()),
      ...(shipMemo.trim() ? { memo: shipMemo.trim() } : {}),
      items, fulfilled: allFulfilled,
      ...(allFulfilled ? { completedAt: new Date().toISOString() } : {})
    });
    // model 컬럼: 첫 번째 품목명 (UI 렌더링 fallback)
    const modelLabel = items[0].product_name;
    const totalQty = items.reduce((s, i) => s + i.qty, 0);
    const { error } = await supabase.from('transaction_log').insert({
      serial: reqId, model: modelLabel, type: 'outbound',
      dealer_name: recipientLabel || null,  // v7.435: 수령인 미입력 시 요청자/작성자로 폴백하지 않는다 (사람 이름이 업체 필드로 새어 시트 수령인칸에 뜨던 버그)
      note: shipNote, processed_by: shipAuthor || shipRequester,
      transaction_date: shipExpectedDate || today()  // 예상 출고일 기준 (사용자 입력값)
    });
    if (!error) {
      // EMS + DB 매칭 시리얼이면 재고 상태를 판매불가로 변경
      if (isRma && shipRmaSerialFromDB && shipRmaSerial.trim()) {
        await InventoryService.markUnsellable(shipRmaSerial.trim(), {
          note: `[${shipEmsType}] ${shipRmaTicketNo.trim() ? '티켓: '+shipRmaTicketNo.trim()+' ' : ''}${fmtDate(today())} 발송`,
        });
      }
      // v7.241: 출고요청 등록 시 시트 자동 기록(1차)을 제거했다.
      //   1차 = [시트 전송] 버튼 / 2차 = [제품 출고] 로 이동.
      //   이유: 배차시간이 n8n 행 매칭 키라, 등록시각으로 행을 먼저 만들어 놓으면
      //         이후 배차시간을 '버튼 누른 시각'으로 바꿀 수 없다 (기존 행을 못 찾아 새 줄이 생긴다).
      setShipMsg({ type: 'ok', msg: `출고 요청 완료! 총 ${items.length}개 품목, ${totalQty}개 수량 — 배송시트지로 이동합니다...` });
      loadAddressHistory(); // 새 주소가 자동완성에 즉시 반영되도록
      // v7.89: 사용량 트래킹 (v7.95: 미정의 isPickup → shipPickup으로 수정 — 페이지 이동 blocking bug)
      AnalyticsService.trackAction('출고요청_등록', shipAuthor || shipRequester || '', {
        itemCount: items.length, totalQty, isRma, isPickup: !!shipPickup,
      });
      // v7.71: 저장 성공 후 setTimeout 안에서 폼 완전 초기화 (다음 요청은 깨끗한 상태로 시작)
      //        800ms 대기 중에는 items/성공 메시지가 유지되어 완료 화면이 정상 표시됨
      setTimeout(() => {
        resetShipForm();
        setPage('txlog');
      }, 800);
    } else {
      setShipMsg({ type: 'err', msg: '요청 등록 실패: ' + error.message });
    }
    setLoading(false);
  };

  // Transaction Log helpers
  const getWeekRange = (offset) => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(now);
    monday.setDate(now.getDate() + mondayOffset + (offset * 7));
    monday.setHours(0, 0, 0, 0);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);
    return {
      // v7.126: UTC 버그 방지 — 로컬 날짜 문자열 직접 생성
      start: `${monday.getFullYear()}-${String(monday.getMonth()+1).padStart(2,'0')}-${String(monday.getDate()).padStart(2,'0')}`,
      end:   `${sunday.getFullYear()}-${String(sunday.getMonth()+1).padStart(2,'0')}-${String(sunday.getDate()).padStart(2,'0')}`,
      label: `${monday.getFullYear()}.${String(monday.getMonth()+1).padStart(2,'0')}.${String(monday.getDate()).padStart(2,'0')} - ${sunday.getFullYear()}.${String(sunday.getMonth()+1).padStart(2,'0')}.${String(sunday.getDate()).padStart(2,'0')}`
    };
  };

  // 조회 기간 (이번주 / 이번달 토글 + offset)
  const getRangeForMode = (mode, offset = 0) => {
    const now = new Date();
    if (mode === 'day') {
      const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
      const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
      const dow = ['일','월','화','수','목','금','토'][d.getDay()];
      return {
        start: iso,
        end: iso,
        label: `${d.getFullYear()}.${String(d.getMonth()+1).padStart(2,'0')}.${String(d.getDate()).padStart(2,'0')} (${dow})`
      };
    }
    if (mode === 'month') {
      const first = new Date(now.getFullYear(), now.getMonth() + offset, 1);
      const last = new Date(now.getFullYear(), now.getMonth() + offset + 1, 0);
      return {
        start: `${first.getFullYear()}-${String(first.getMonth()+1).padStart(2,'0')}-${String(first.getDate()).padStart(2,'0')}`,
        end: `${last.getFullYear()}-${String(last.getMonth()+1).padStart(2,'0')}-${String(last.getDate()).padStart(2,'0')}`,
        label: `${first.getFullYear()}.${String(first.getMonth()+1).padStart(2,'0')}.${String(first.getDate()).padStart(2,'0')} - ${last.getFullYear()}.${String(last.getMonth()+1).padStart(2,'0')}.${String(last.getDate()).padStart(2,'0')}`
      };
    }
    return getWeekRange(offset); // 'week' → 이번 주 + offset
  };

  // 캘린더 피커: 특정 날짜가 속한 주/월의 offset 계산
  const calcWeekOffsetFromDate = (target) => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    const nowMondayOff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const nowMonday = new Date(now.getFullYear(), now.getMonth(), now.getDate() + nowMondayOff);
    nowMonday.setHours(0,0,0,0);
    const tDay = target.getDay();
    const tMondayOff = tDay === 0 ? -6 : 1 - tDay;
    const tMonday = new Date(target.getFullYear(), target.getMonth(), target.getDate() + tMondayOff);
    tMonday.setHours(0,0,0,0);
    return Math.round((tMonday - nowMonday) / (7 * 24 * 60 * 60 * 1000));
  };
  const calcMonthOffsetFromDate = (target) => {
    const now = new Date();
    return (target.getFullYear() - now.getFullYear()) * 12 + (target.getMonth() - now.getMonth());
  };
  const calcDayOffsetFromDate = (target) => {
    const now = new Date();
    const t0 = new Date(target.getFullYear(), target.getMonth(), target.getDate()).getTime();
    const n0 = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    return Math.round((t0 - n0) / (24 * 60 * 60 * 1000));
  };

  // 오늘/이번주/이번달 버튼 — 현재 보고 있는 날짜를 유지한 채 모드만 전환
  const switchPeriodMode = (newMode) => {
    if (newMode === logPeriodMode) return; // 같은 모드 재클릭은 무시
    const cur = getRangeForMode(logPeriodMode, logPeriodOffset);
    const [y, m, d] = cur.start.split('-').map(Number);
    const refDate = new Date(y, m - 1, d);
    const newOffset = newMode === 'day' ? calcDayOffsetFromDate(refDate)
      : newMode === 'week' ? calcWeekOffsetFromDate(refDate)
      : calcMonthOffsetFromDate(refDate);
    setLogPeriodMode(newMode);
    setLogPeriodOffset(newOffset);
    setLogPg(0);
  };

  const loadTxLog = useCallback(async () => {
    if (!connected) return;
    setLoading(true);
    const range = getRangeForMode(logPeriodMode, logPeriodOffset);
    const isSearching = !!logSearch.trim();
    let q = supabase.from('transaction_log').select('id,serial,model,type,dealer_name,note,processed_by,transaction_date,sort_order,created_at,updated_at', { count: 'exact' });
    // 검색 중일 때는 날짜 범위 무시하고 전체에서 검색
    if (!isSearching) {
      q = q.gte('transaction_date', range.start).lte('transaction_date', range.end);
    }
    if (logFilter !== 'all') q = q.eq('type', logFilter);
    if (isSearching) {
      const s = logSearch.trim();
      // 날짜 패턴 감지 (2026.06.16, 2026-06-16, 2026/06/16, 20260616 등)
      const dateMatch = s.match(/^(20\d{2})[.\-/]?(\d{1,2})[.\-/]?(\d{1,2})$/);
      const partialDateMatch = s.match(/^(20\d{2})[.\-/]?(\d{1,2})$/); // 2026.06 같은 부분 매치
      if (dateMatch) {
        const iso = `${dateMatch[1]}-${dateMatch[2].padStart(2,'0')}-${dateMatch[3].padStart(2,'0')}`;
        q = q.eq('transaction_date', iso);
      } else if (partialDateMatch) {
        const prefix = `${partialDateMatch[1]}-${partialDateMatch[2].padStart(2,'0')}`;
        q = q.gte('transaction_date', `${prefix}-01`).lte('transaction_date', `${prefix}-31`);
      } else {
        // v7.110: 검색어에 "직발"/"네이버" 포함 시 JSON flag까지 매칭
        //          - "직발": note에 "directCompany" 있는 row (direct=true일 때만 저장되는 필드)
        //          - "네이버": note에 "naverStore" 있는 row (naverStore=true일 때만 저장)
        const clauses = [
          `serial.ilike.%${s}%`,
          `model.ilike.%${s}%`,
          `dealer_name.ilike.%${s}%`,
          `note.ilike.%${s}%`,
        ];
        if (s.includes('직발')) clauses.push('note.ilike.%directCompany%');
        if (s.includes('네이버')) clauses.push('note.ilike.%naverStore%');
        q = q.or(clauses.join(','));
      }
    }
    // v7.81: transaction_date 그룹 우선 → 그룹 내 sort_order 오름차순 (수동 정렬) → created_at 내림차순 (fallback)
    // v7.160: nullsFirst: false → true — 신규 요청(sort_order=NULL)을 상단에 배치
    //          NULL 그룹이 먼저(created_at DESC 티브레이커), non-NULL 그룹(수동 재정렬)이 뒤
    const { data, count } = await q
      .order('transaction_date', { ascending: false })
      .order('sort_order', { ascending: true, nullsFirst: true })
      .order('created_at', { ascending: false })
      .range(logPg * PG, (logPg + 1) * PG - 1);
    if (data) {
      // v7.146: 같은 날짜 그룹 내에서 출고완료(fulfilled=true)된 outbound는 아래로 sink
      //   pending outbound + inbound + 기타는 상단 유지 → 처리해야 할 건이 항상 눈에 잘 띔
      //   JS 안정 정렬로 원래 sort_order/created_at 순서는 그룹 내에서 유지
      // v7.171: 입고(inbound)도 아래로 sink — 출고요청/환불 등 처리 필요 건이 위, 입고 기록은 아래로.
      //   최종 순서: outbound pending → refund → outbound fulfilled → inbound
      //   (규칙 우선순위: ① 입고 여부 ② 완료 여부)
      const isRowFulfilled = (row) => {
        if (row.type !== 'outbound' || !row.note) return false;
        try { return JSON.parse(row.note).fulfilled === true; }
        catch (e) { return false; }
      };
      const sorted = data.slice().sort((a, b) => {
        if (a.transaction_date !== b.transaction_date) return 0; // 날짜 그룹 순서 유지
        // v7.171: 입고는 항상 그 외(출고/환불) 뒤로
        const aInb = a.type === 'inbound';
        const bInb = b.type === 'inbound';
        if (aInb !== bInb) return aInb ? 1 : -1;
        // v7.146: 같은 그룹 내에서 완료된 outbound는 아래로
        const aDone = isRowFulfilled(a);
        const bDone = isRowFulfilled(b);
        if (aDone !== bDone) return aDone ? 1 : -1;
        return 0; // 그 외에는 안정 정렬로 원 순서 유지
      });
      setLogItems(sorted);
      setLogTotal(count || 0);
    }
    // Week stats — 검색 중에도 현재 기간 기준으로 계산
    const { count: inC } = await supabase.from('transaction_log').select('id', { count: 'exact', head: true }).eq('type', 'inbound').gte('transaction_date', range.start).lte('transaction_date', range.end);
    const { count: outC } = await supabase.from('transaction_log').select('id', { count: 'exact', head: true }).eq('type', 'outbound').gte('transaction_date', range.start).lte('transaction_date', range.end);
    const { count: weekC } = await supabase.from('transaction_log').select('id', { count: 'exact', head: true }).gte('transaction_date', range.start).lte('transaction_date', range.end);
    setLogStats({ inbound: inC || 0, outbound: outC || 0, weekTotal: weekC || 0 });
    setLoading(false);
  }, [connected, logSearch, logFilter, logPg, logPeriodMode, logPeriodOffset]);
  useEffect(() => { if (page === 'txlog') loadTxLog(); }, [page, loadTxLog]);
  // page가 txlog가 아닐 때 sub-tab을 list로 강제 리셋 — 다시 들어왔을 때 기본 화면 보이도록
  useEffect(() => { if (page !== 'txlog' && txSubTab !== 'list') setTxSubTab('list'); }, [page, txSubTab]);

  // 🥚 휴식 모드: Esc 키로 종료
  useEffect(() => {
    if (!restMode) return;
    const handler = (e) => { if (e.key === 'Escape') setRestMode(false); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [restMode]);

  // 🎆 이스터에그 (v7.371): 정보 없는 빈 공간을 10번 연속 클릭하면 불꽃놀이.
  //   '빈 공간' 판정 = (1) 버튼·입력·이미지 등 인터랙티브 요소 위가 아니고
  //                  (2) 클릭된 요소가 [자체 텍스트](=정보)를 안 가진 순수 컨테이너.
  //   → 카드 위 글자·값을 눌러도 안 세고, 카드 사이 여백/배경을 눌러야만 센다.
  //   ⚠ 3초 이상 텀이 벌어지면 카운터를 리셋 — 하루 종일 눌러 우연히 쌓이는 걸 막는다.
  //   ⚠ document 레벨 리스너 — 최상단 <ClickSpark> 는 stopPropagation 안 하므로
  //     클릭이 버블링돼 여기까지 온다 (스파크 효과와 카운트가 공존).
  useEffect(() => {
    // canvas 는 제외하지 않는다 — 최상단 <ClickSpark> 스파크 canvas 가 클릭 대상이 될 수
    //   있어서, 제외하면 빈 공간 클릭이 그 canvas 에 먹혀 이스터에그가 영영 안 뜬다.
    const INTERACTIVE = 'a,button,input,textarea,select,label,img,svg,video,table,th,td,[role="button"],[contenteditable="true"]';
    const hasOwnText = (el) => {
      if (!el || !el.childNodes) return false;
      for (const n of el.childNodes) { if (n.nodeType === 3 && n.textContent.trim()) return true; }
      return false;
    };
    const onDocClick = (e) => {
      if (fireworksMode) return;                        // 이미 켜져 있으면 무시
      const t = e.target;
      if (!t || t.nodeType !== 1) return;
      if (t.closest && t.closest(INTERACTIVE)) return;  // 버튼/입력/이미지 등 제외
      if (hasOwnText(t)) return;                        // 자체 텍스트(정보) 있으면 제외
      const now = Date.now();
      if (now - emptyClickLastRef.current > 3000) emptyClickCountRef.current = 0;
      emptyClickLastRef.current = now;
      emptyClickCountRef.current += 1;
      if (emptyClickCountRef.current >= 10) {
        emptyClickCountRef.current = 0;
        setFireworksMode(true);
      }
    };
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, [fireworksMode]);

  // 🥚 휴식 모드: public/rest/ 디렉터리의 영상 파일을 manifest.json 으로 자동 탐지
  //    (빌드 타임에 scripts/generate-rest-manifest.js 가 자동 생성)
  useEffect(() => {
    fetch(`${process.env.PUBLIC_URL}/rest/manifest.json`)
      .then(res => res.ok ? res.json() : [])
      .then(list => { if (Array.isArray(list)) setRestVideos(list); })
      .catch(() => {});
  }, []);

  // 🥚 휴식 모드 트리거 — Strands 인트로 → 영상으로 자동 전환
  const triggerRest = useCallback(() => {
    const pick = restVideos.length > 0
      ? restVideos[Math.floor(Math.random() * restVideos.length)]
      : null;
    setRestVideo(pick);
    setRestPhase('intro');
    setRestMode(true);
    // 2.5초 후 영상 phase 로 자동 전환
    setTimeout(() => setRestPhase('video'), 2500);
  }, [restVideos]);

  // ─── 배송지 자동완성: 이전 출고 요청 주소 캐시 로드 ───
  const loadAddressHistory = useCallback(async () => {
    if (!connected) return;
    const { data } = await supabase.from('transaction_log')
      .select('note, transaction_date')
      .eq('type', 'outbound')
      .order('created_at', { ascending: false })
      .limit(800);
    if (!data) return;
    const map = new Map();
    for (const row of data) {
      try {
        const p = JSON.parse(row.note);
        if (p.type !== 'ship_request') continue;
        if (p.pickup) continue;            // 방문수령은 주소 자동완성 대상 아님
        if (p.processedAs) continue;       // 취소/환불 처리된 건 제외
        const addr = (p.addr || '').trim();
        if (!addr) continue;
        // 첫 등장이 가장 최근 (created_at desc)
        if (!map.has(addr)) {
          map.set(addr, {
            addr,
            recipient: p.recipient || '',
            phone: p.phone || '',
            lastDate: row.transaction_date,
            count: 1
          });
        } else {
          map.get(addr).count++;
        }
      } catch(e) {}
    }
    // 사용 횟수 ↓, 그 다음 최근 ↓ 순
    const list = Array.from(map.values()).sort((a, b) => {
      if (b.count !== a.count) return b.count - a.count;
      return (b.lastDate || '').localeCompare(a.lastDate || '');
    });
    setAddressHistory(list);
  }, [connected]);

  // 출고 요청 페이지 진입 시 한 번 로드
  useEffect(() => {
    if (page === 'ship' && connected) loadAddressHistory();
  }, [page, connected, loadAddressHistory]);

  // 마스터 카테고리 로드 (입고 시 신제품 등록 모달용)
  useEffect(() => {
    if (!connected) return;
    (async () => {
      const { data } = await supabase.from('product_categories').select('name').order('name');
      if (data) setProductCategories(data.map(r => r.name));
    })();
  }, [connected]);

  // 카탈로그 관리 페이지 로드
  const CATALOG_PG_SIZE = 50;
  const loadCatalog = useCallback(async () => {
    if (!connected) return;
    setCatalogLoading(true);
    const { data, count } = await ProductService.list({
      search: catalogSearch,
      category: catalogCategoryFilter,
      formFactor: catalogFormFactorFilter,
      page: catalogPg,
      pageSize: CATALOG_PG_SIZE,
    });
    if (data) {
      setCatalogItems(data);
      setCatalogTotal(count || 0);
      // 별칭 개수 함께 로드 (현재 페이지 항목만)
      const names = data.map(d => d.product_name);
      if (names.length > 0) {
        const counts = await AliasService.countsForProducts(names);
        setAliasCounts(counts);
      } else {
        setAliasCounts({});
      }
    }
    setCatalogLoading(false);
  }, [connected, catalogSearch, catalogCategoryFilter, catalogFormFactorFilter, catalogPg]);

  // ─── 별칭(alias) 헬퍼 — AliasService 위임 (얇은 wrapper, App.js state 처리 담당) ───
  // 별칭 → 표준 제품명 조회 (대소문자 무시)
  const lookupAlias = async (name) => AliasService.lookup(name);

  // 별칭 관리 모달 열기 + 목록 로드
  const openAliasManage = async (productName) => {
    setAliasManageModal({ productName, aliases: [], newAlias: '', loading: true });
    const aliases = await AliasService.listFor(productName);
    setAliasManageModal(m => m ? { ...m, aliases, loading: false } : m);
  };

  // 별칭 추가
  const addAlias = async (productName, alias, source = 'manual') =>
    AliasService.create(productName, alias, source);

  // 별칭 삭제
  const deleteAlias = async (id) => {
    const res = await AliasService.remove(id);
    if (!res.ok) { alert(res.msg); return false; }
    return true;
  };

  useEffect(() => {
    if (page === 'catalog' && connected) loadCatalog();
  }, [page, connected, loadCatalog]);

  // v7.7: 대량 제품 등록 — 텍스트박스에 줄바꿈으로 입력 → bulkInsert
  const submitBulkProducts = async () => {
    if (!bulkProductModal) return;
    const lines = (bulkProductModal.text || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
    if (lines.length === 0) {
      setBulkProductModal(m => m ? { ...m, result: { type: 'err', msg: '제품명을 입력하세요.' } } : m);
      return;
    }
    // 입력 안의 자체 중복 제거 (대소문자 구분)
    const uniqueNames = [...new Set(lines)];
    setBulkProductModal(m => m ? { ...m, loading: true, result: null } : m);
    // 1) 이미 존재하는 제품 확인 (ProductService 이용)
    const existing = await ProductService.existingNames(uniqueNames);
    const newOnes = uniqueNames.filter(n => !existing.has(n));
    if (newOnes.length === 0) {
      setBulkProductModal(m => m ? {
        ...m,
        loading: false,
        result: { type: 'warn', msg: `입력한 ${uniqueNames.length}개 모두 이미 등록된 제품입니다.` }
      } : m);
      return;
    }
    // 2) bulk insert
    const rows = newOnes.map(name => ({ product_name: name, category: null, form_factor: null }));
    const res = await ProductService.bulkInsert(rows);
    if (!res.ok) {
      setBulkProductModal(m => m ? {
        ...m,
        loading: false,
        result: { type: 'err', msg: '등록 실패: ' + res.msg }
      } : m);
      return;
    }
    const skipped = uniqueNames.length - newOnes.length;
    const lineDup = lines.length - uniqueNames.length;
    setBulkProductModal(m => m ? {
      ...m,
      text: '',  // 성공 후 텍스트 비움 (재시도 방지)
      loading: false,
      result: {
        type: 'ok',
        msg: `✓ ${newOnes.length}개 등록 완료${skipped>0?` · ${skipped}개 중복 스킵`:''}${lineDup>0?` · ${lineDup}개 입력 중복 제거`:''}`,
      }
    } : m);
    loadCatalog(); // 카탈로그 갱신
  };

  // v7.14: 임시 시리얼 일괄 입고 — UNK-{모델코드}-{4자리연번} 형식 자동 생성
  // 핵심 흐름:
  //  1) 제품 선택 (카탈로그에서 검색)
  //  2) 모델코드(예: ST2000DM008) + 시작번호 + 수량 입력
  //  3) 미리보기로 시리얼 목록 확인
  //  4) inventory bulk insert + transaction_log 1행 (입고 type)
  //  5) 미래에 실제 시리얼 확인 시 → 입고 행에서 더블클릭 편집으로 renameSerial (v7.6 기능)
  const openBulkUnknownModal = () => {
    setBulkUnknownModal({
      product_name: '', product_search: '', product_results: [],
      model_code: '', qty: 1, start_number: 1,
      stock_type: '신품', source: 'taiwan', warehouse: 'hq', receipt_date: today(),
      loading: false, result: null, success: false,
    });
  };
  // 제품 검색 (카탈로그)
  const searchBulkUnknownProduct = async (q) => {
    setBulkUnknownModal(m => m ? { ...m, product_search: q } : m);
    if (!q || q.length < 1) {
      setBulkUnknownModal(m => m ? { ...m, product_results: [] } : m);
      return;
    }
    const data = await ProductService.searchByName(q, 10);
    setBulkUnknownModal(m => m ? { ...m, product_results: data } : m);
  };
  // 제품 선택 시 모델 코드 자동 추천 (제품명에서 공백/특수문자 제거)
  const selectBulkUnknownProduct = (productName) => {
    const autoCode = productName.replace(/[^A-Za-z0-9]/g, '');
    setBulkUnknownModal(m => m ? {
      ...m,
      product_name: productName,
      product_search: productName,
      product_results: [],
      model_code: m.model_code || autoCode, // 사용자 입력 우선
    } : m);
  };
  // 시작 번호 자동 추천 (DB에서 해당 prefix의 마지막 번호 + 1)
  const suggestBulkUnknownStartNumber = async () => {
    if (!bulkUnknownModal?.model_code) return;
    const prefix = `UNK-${bulkUnknownModal.model_code.trim()}-`;
    const { data } = await supabase.from('inventory')
      .select('serial_number')
      .ilike('serial_number', `${prefix}%`)
      .order('serial_number', { ascending: false })
      .limit(1);
    if (data && data.length > 0) {
      const m = data[0].serial_number.match(/-(\d+)$/);
      if (m) {
        const lastN = parseInt(m[1], 10);
        setBulkUnknownModal(s => s ? { ...s, start_number: lastN + 1 } : s);
        return;
      }
    }
    setBulkUnknownModal(s => s ? { ...s, start_number: 1 } : s);
  };
  // 시리얼 미리보기 생성 (현재 입력값 기준)
  const buildBulkUnknownSerials = (state) => {
    if (!state) return [];
    const code = (state.model_code || '').trim().replace(/\s+/g, '');
    const qtyN = parseInt(state.qty, 10);
    const startN = parseInt(state.start_number, 10) || 1;
    if (!code || !qtyN || qtyN <= 0) return [];
    const list = [];
    for (let i = 0; i < qtyN; i++) {
      list.push(`UNK-${code}-${String(startN + i).padStart(4, '0')}`);
    }
    return list;
  };
  // 일괄 등록 실행
  const submitBulkUnknown = async () => {
    if (!bulkUnknownModal) return;
    const m = bulkUnknownModal;
    if (!m.product_name) {
      setBulkUnknownModal(s => s ? { ...s, result: { type: 'err', msg: '제품을 선택하세요.' } } : s);
      return;
    }
    if (!m.model_code || !m.model_code.trim()) {
      setBulkUnknownModal(s => s ? { ...s, result: { type: 'err', msg: '모델 코드를 입력하세요. (예: ST2000DM008)' } } : s);
      return;
    }
    const qtyN = parseInt(m.qty, 10);
    if (!qtyN || qtyN <= 0) {
      setBulkUnknownModal(s => s ? { ...s, result: { type: 'err', msg: '수량은 1 이상이어야 합니다.' } } : s);
      return;
    }
    const serials = buildBulkUnknownSerials(m);
    if (serials.length === 0) {
      setBulkUnknownModal(s => s ? { ...s, result: { type: 'err', msg: '시리얼 생성 실패. 모델 코드/수량을 확인하세요.' } } : s);
      return;
    }
    setBulkUnknownModal(s => s ? { ...s, loading: true, result: null } : s);
    // 1) 중복 체크 (이미 같은 시리얼이 DB에 있으면 충돌)
    const existing = await InventoryService.findExistingSerials(serials);
    if (existing.size > 0) {
      const sample = [...existing].slice(0, 3).join(', ');
      setBulkUnknownModal(s => s ? {
        ...s, loading: false,
        result: { type: 'err', msg: `${existing.size}개 시리얼 충돌 (${sample}${existing.size>3?' 외':''}). 시작 번호를 변경하세요.` }
      } : s);
      return;
    }
    // 2) inventory bulk insert
    const rows = serials.map(sn => ({
      serial_number: sn,
      product_name: m.product_name,
      category: null,
      stock_type: m.stock_type,
      status: 'in_stock',
      source: m.source,
      warehouse: m.warehouse,
      receipt_date: m.receipt_date,
      note: '임시 시리얼 일괄 입고',
    }));
    const invRes = await InventoryService.bulkInsert(rows);
    if (!invRes.ok) {
      setBulkUnknownModal(s => s ? { ...s, loading: false, result: { type: 'err', msg: '등록 실패: ' + invRes.msg } } : s);
      return;
    }
    // 3) transaction_log 1행 (입고 type) — 미래 시리얼 교체 시 더블클릭 편집 가능하도록
    const recvId = 'RECV-' + Date.now() + '-unk' + Math.random().toString(36).slice(2, 5);
    const txRow = {
      serial: recvId,
      model: m.product_name,
      type: 'inbound',
      note: JSON.stringify({
        type: 'inbound',
        reqId: recvId,
        items: [{ product_name: m.product_name, qty: qtyN, serials, from: m.source }],
        memo: `임시 시리얼 일괄 입고 (모델 코드: ${m.model_code.trim()})`,
        from: m.source,
        isUnknown: true, // 임시 시리얼 flag (미래 필터링용)
      }),
      processed_by: '운영 관리자',
      transaction_date: m.receipt_date,
    };
    // v7.225: 여기도 에러를 버리고 있었음 → 확인 후 사용자에게 알린다
    const { error: txErr2 } = await supabase.from('transaction_log').insert(txRow);
    if (txErr2) console.error('임시 시리얼 입고 이력 저장 실패:', txErr2, txRow);
    setBulkUnknownModal(s => s ? {
      ...s, loading: false, success: true,
      result: txErr2
        ? { type: 'err', msg: `재고는 등록됐지만 배송시트지 기록 실패: ${txErr2.message || txErr2.code}` }
        : { type: 'ok', msg: `✓ ${qtyN}개 임시 시리얼 등록 완료 (${serials[0]} ~ ${serials[serials.length-1]})` },
    } : s);
    // 페이지 갱신
    if (page === 'inventory') loadInventory();
  };

  // 카탈로그 항목 저장
  const saveCatalogItem = async () => {
    if (!catalogEditing) return;
    const newName = catalogEditing.product_name.trim();
    const newCategory = catalogEditing.category.trim();
    const newFormFactor = (catalogEditing.form_factor || '').trim() || null;
    if (!newName || !newCategory) { alert('제품명과 카테고리를 입력하세요.'); return; }
    // 제품명이 바뀐 경우 inventory도 함께 업데이트해야 함 (FK)
    const original = catalogItems.find(i => i.id === catalogEditing.id);
    if (!original) return;
    const nameChanged = original.product_name !== newName;
    if (nameChanged) {
      // 1. 새 이름으로 products UPSERT (외래키 안전)
      const insRes = await ProductService.upsert({ product_name: newName, category: newCategory, form_factor: newFormFactor });
      if (!insRes.ok) { alert('새 이름 등록 실패: ' + insRes.msg); return; }
      // 2. inventory의 product_name 변경 (외래키 동기화)
      const invRes = await InventoryService.renameProductName(original.product_name, newName);
      if (!invRes.ok) { alert('inventory 업데이트 실패: ' + invRes.msg); return; }
      // 3. 옛 이름 삭제
      await ProductService.remove(catalogEditing.id);
    } else {
      // 카테고리/폼팩터만 변경
      const res = await ProductService.updateMeta(catalogEditing.id, { category: newCategory, form_factor: newFormFactor });
      if (!res.ok) { alert('수정 실패: ' + res.msg); return; }
    }
    setCatalogEditing(null);
    loadCatalog();
  };

  const deleteCatalogItem = async (item) => {
    // inventory에 사용 중인지 확인
    const usedCount = await InventoryService.countByProduct(item.product_name);
    if (usedCount > 0) {
      alert(`이 제품은 inventory에 ${usedCount}건 사용 중이라 삭제할 수 없습니다.\n먼저 해당 시리얼을 처리하거나 다른 제품으로 변경하세요.`);
      return;
    }
    if (!window.confirm(`제품 "${item.product_name}"을(를) 카탈로그에서 삭제하시겠습니까?`)) return;
    const res = await ProductService.remove(item.id);
    if (!res.ok) { alert('삭제 실패: ' + res.msg); return; }
    loadCatalog();
  };

  // ─── 킵(Keep) 관리 함수 ───
  // 활성 킵 수량 집계 로드 (출고요청 시 가용재고 계산용)
  const loadActiveKeeps = useCallback(async () => {
    if (!connected) return;
    const { data } = await supabase.from('product_keeps')
      .select('product_name,qty,serials,serial_number').eq('status', 'active');
    const map = {};        // 표시용 — 킵 총 수량
    const unbacked = {};   // 계산용 — 시리얼 미지정분 (아직 in_stock 에 남아 있는 수량)
    (data || []).forEach(k => {
      const qty = k.qty || 0;
      map[k.product_name] = (map[k.product_name] || 0) + qty;
      // serials 컬럼 타입이 jsonb / text[] / text(JSON 문자열) 중 무엇이어도 견디게 파싱한다
      let arr = k.serials;
      if (typeof arr === 'string') {
        try { arr = JSON.parse(arr); } catch (e) { arr = arr.trim() ? [arr] : []; }
      }
      if (!Array.isArray(arr)) arr = [];
      // 수량만큼 슬롯이 만들어지고 빈 문자열로 남는 경우가 있어(v7.156 UI) 빈 값은 센다는 의미가 없다
      let assigned = arr.map(x => String(x == null ? '' : x).trim()).filter(Boolean).length;
      if (assigned === 0 && k.serial_number && String(k.serial_number).trim()) assigned = 1;
      const rest = Math.max(0, qty - assigned);
      unbacked[k.product_name] = (unbacked[k.product_name] || 0) + rest;
    });
    setActiveKeepsByModel(map);
    setActiveKeepsUnbackedByModel(unbacked);
  }, [connected]);

  // 킵 목록 로드
  const loadKeeps = useCallback(async () => {
    if (!connected) return;
    setKeepsLoading(true);
    let q = supabase.from('product_keeps').select('*');
    if (keepsStatusFilter !== 'all') q = q.eq('status', keepsStatusFilter);
    if (keepsSearch.trim()) {
      const s = keepsSearch.trim();
      q = q.or(`product_name.ilike.%${s}%,dealer_name.ilike.%${s}%,memo.ilike.%${s}%`);
    }
    const { data } = await q.order('created_at', { ascending: false }).limit(500);
    setKeepsItems(data || []);
    setKeepsLoading(false);
  }, [connected, keepsSearch, keepsStatusFilter]);

  // 킵 폼 — 거래처 검색 (dealers 테이블)
  const searchKeepDealers = async (q) => {
    setKeepsForm(f => ({ ...f, dealerSearch: q, dealer_name: q })); // 자유 입력 허용
    if (!q || q.length < 1) { setKeepsForm(f => ({ ...f, dealerResults: [] })); return; }
    const { data } = await supabase.from('dealers').select('id,name').ilike('name', `%${q}%`).order('name').limit(8);
    setKeepsForm(f => ({ ...f, dealerResults: data || [] }));
  };

  // 킵 폼 — 제품 검색 (특정 품목 행)
  const searchKeepItemProduct = async (idx, q) => {
    setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, productSearch: q } : it) }));
    if (!q || q.length < 1) {
      setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, productResults: [] } : it) }));
      return;
    }
    const data = await ProductService.searchByName(q, 10);
    setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, productResults: data } : it) }));
  };

  // 킵 폼 — 시리얼 검색 (DB inventory 매칭, in_stock 상태만). q가 빈 문자열이면 해당 제품의 모든 in_stock 시리얼 표시.
  const searchKeepItemSerial = async (idx, q) => {
    const currentItem = keepsForm.items[idx];
    setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, serial_number: q } : it) }));
    if (!currentItem || !currentItem.product_name) {
      setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, serialResults: [] } : it) }));
      return;
    }
    const data = await InventoryService.searchInStockSerials({ productName: currentItem.product_name, query: q, limit: 20 });
    setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, serialResults: data } : it) }));
  };

  // 품목 행 — 빈 행 추가
  const addKeepItem = () => {
    setKeepsForm(f => ({ ...f, items: [...f.items, { product_name: '', productSearch: '', productResults: [], qty: 1, serials: [''], serial_number: '', serialResults: [], item_memo: '' }] }));
  };
  // 품목 행 — 제거
  const removeKeepItem = (idx) => {
    setKeepsForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx).length === 0 ? [{ product_name: '', productSearch: '', productResults: [], qty: 1, serials: [''], serial_number: '', serialResults: [], item_memo: '' }] : f.items.filter((_, i) => i !== idx) }));
  };
  // 품목 행 — 필드 변경
  const updateKeepItem = (idx, patch) => {
    setKeepsForm(f => ({ ...f, items: f.items.map((it, i) => i === idx ? { ...it, ...patch } : it) }));
  };

  // ─── v7.156: 킵 시리얼 예약 상태 동기화 ──────────────────────
  // 킵에 시리얼이 지정되면 그 시리얼의 inventory.status를 'reserved'로 마킹해서
  //   ① 재고 검색·목록에서 "정상 재고 / 판매가능"으로 표시되지 않음
  //   ② 다른 출고 요청에서 그 시리얼을 스캔·선택하는 것을 자연스럽게 차단
  //   ③ 킵 해제/삭제/편집 시 in_stock으로 원복
  // oldSerials, newSerials 는 slot 배열이거나 filled 배열 (빈 문자열 무시).
  // productName 은 로깅 참고용 (동작에는 미사용, safety를 위해 supabase 절 없음).
  const syncKeepSerialReservation = async (oldSerials, newSerials) => {
    const norm = (arr) => (arr || []).map(s => (s || '').trim()).filter(Boolean);
    const oldSet = new Set(norm(oldSerials));
    const newSet = new Set(norm(newSerials));
    const toRelease = [...oldSet].filter(s => !newSet.has(s));   // reserved → in_stock
    const toReserve = [...newSet].filter(s => !oldSet.has(s));   // in_stock → reserved
    // reserved 상태의 시리얼만 in_stock 원복 (다른 상태는 손대지 않음 — 안전)
    for (const sn of toRelease) {
      try {
        await supabase.from('inventory')
          .update({ status: 'in_stock' })
          .eq('serial_number', sn)
          .eq('status', 'reserved');
      } catch (e) { console.warn('syncKeepSerial release 실패:', sn, e); }
    }
    // in_stock 상태의 시리얼만 reserved 마킹 (shipped/unsellable/이미 reserved인 것은 skip)
    for (const sn of toReserve) {
      try {
        await supabase.from('inventory')
          .update({ status: 'reserved' })
          .eq('serial_number', sn)
          .eq('status', 'in_stock');
      } catch (e) { console.warn('syncKeepSerial reserve 실패:', sn, e); }
    }
  };

  // 킵 등록 (다중 품목)
  const submitKeep = async () => {
    const f = keepsForm;
    const dealerName = (f.dealer_name || '').trim();
    if (!dealerName) { alert('거래처를 입력하세요.'); return; }
    if (!f.items.length || !f.items.some(it => it.product_name && it.qty > 0)) {
      alert('품목을 1개 이상 추가하세요.'); return;
    }
    // 빈 행 제거 + 검증
    const validItems = f.items.filter(it => it.product_name && it.qty > 0);
    for (const it of validItems) {
      if (!it.product_name) { alert('모든 품목에서 제품을 선택하세요.'); return; }
      if (!it.qty || it.qty <= 0) { alert(`"${it.product_name}" 수량은 1 이상이어야 합니다.`); return; }
    }
    // 가용재고 검증 (모델별 합계)
    const reqByModel = {};
    validItems.forEach(it => { reqByModel[it.product_name] = (reqByModel[it.product_name] || 0) + parseInt(it.qty, 10); });
    for (const modelName of Object.keys(reqByModel)) {
      const stockCount = await InventoryService.countInStock(modelName);
      const reserved = activeKeepsByModel[modelName] || 0;
      // v7.311: countInStock 은 in_stock 만 센다. 시리얼 지정 킵은 이미 reserved 로 빠져 있으므로
      //         미지정분만 빼야 한다 (안 그러면 가용이 실제보다 작게 나와 헛경고가 뜬다)
      const reservedUnbacked = activeKeepsUnbackedByModel[modelName] || 0;
      const available = stockCount - reservedUnbacked;
      if (reqByModel[modelName] > available) {
        if (!window.confirm(`⚠ "${modelName}" 가용재고 부족\n재고 ${stockCount}개 / 활성 킵 ${reserved}개(차감 ${reservedUnbacked}개) / 가용 ${available}개\n요청: ${reqByModel[modelName]}개\n\n그래도 등록하시겠습니까?`)) return;
      }
    }
    // N개 insert — 한 번의 등록은 하나의 그룹으로 묶임 (group_id 공유)
    const groupId = uuidv4();
    const rows = validItems.map(item => {
      const serialsArr = Array.isArray(item.serials) ? item.serials.map(s => (s || '').trim()) : [];
      // 길이를 qty에 맞춤 (부족하면 빈 문자열로 채움)
      while (serialsArr.length < item.qty) serialsArr.push('');
      serialsArr.length = item.qty;
      const filledSerials = serialsArr.filter(s => s); // 입력된 시리얼만
      return {
        group_id: groupId,
        product_name: item.product_name,
        qty: parseInt(item.qty, 10),
        serials: serialsArr, // 빈 슬롯 포함 전체 (길이 = qty)
        serial_number: filledSerials[0] || null, // 호환성: 첫 입력 시리얼
        item_memo: (item.item_memo || '').trim() || null,
        dealer_id: f.dealer_id || null,
        dealer_name: dealerName,
        memo: (f.memo || '').trim() || null,
        expected_date: f.expected_date || null,
        author: (f.author || '').trim() || null,
        requester: (f.requester || '').trim() || null,
        status: 'active',
      };
    });
    const { error } = await supabase.from('product_keeps').insert(rows);
    if (error) { alert('킵 등록 실패: ' + error.message); return; }
    // v7.156: 시리얼이 지정된 킵은 해당 시리얼을 inventory.status='reserved'로 마킹
    for (const row of rows) {
      await syncKeepSerialReservation([], row.serials || []);
    }
    // 폼 리셋
    setKeepsForm({
      dealer_id: null, dealer_name: '', dealerSearch: '', dealerResults: [],
      author: '', requester: '', expected_date: '', memo: '',
      items: [{ product_name: '', productSearch: '', productResults: [], qty: 1, serials: [''], serial_number: '', serialResults: [], item_memo: '' }],
    });
    await loadKeeps();
    await loadActiveKeeps();
  };

  // 킵 해제
  const releaseKeep = async (keep) => {
    if (!window.confirm(`킵 해제 — "${keep.product_name}" × ${keep.qty}개 (${keep.dealer_name})\n계속하시겠습니까?`)) return;
    const { error } = await supabase.from('product_keeps').update({ status: 'released', released_at: new Date().toISOString() }).eq('id', keep.id);
    if (error) { alert('해제 실패: ' + error.message); return; }
    // v7.156: 해제된 킵의 예약 시리얼 in_stock 원복
    if (keep.status === 'active') {
      const serials = Array.isArray(keep.serials) ? keep.serials : (keep.serial_number ? [keep.serial_number] : []);
      await syncKeepSerialReservation(serials, []);
    }
    await loadKeeps();
    await loadActiveKeeps();
  };

  // 킵 그룹 전체 출고처리 — 그룹의 모든 active 품목을 한 번에 출고 처리 + 배송시트지 1건 생성
  // 출고완료/해제된 킵 기록 한번에 삭제 (배송시트지 출고기록은 유지)
  const purgeKeepRecords = async () => {
    const { count: doneCount } = await supabase.from('product_keeps').select('id', { count: 'exact', head: true }).in('status', ['shipped', 'released']);
    if (!doneCount) { alert('정리할 출고완료/해제 기록이 없습니다.'); return; }
    if (!window.confirm(`출고완료/해제된 킵 기록 ${doneCount}건을 영구 삭제합니다.\n\n⚠ 배송시트지의 출고 기록은 그대로 유지됩니다.\n계속하시겠습니까?`)) return;
    const { error } = await supabase.from('product_keeps').delete().in('status', ['shipped', 'released']);
    if (error) { alert('삭제 실패: ' + error.message); return; }
    await loadKeeps();
    await loadActiveKeeps();
    alert(`✓ ${doneCount}건 정리 완료`);
  };

  // 그룹 출고처리 (1단계): 배송시트지 작성에 필요한 추가 정보 입력 모달 띄움
  const markKeepGroupShipped = (group) => {
    const activeItems = group.items.filter(i => i.status === 'active');
    if (activeItems.length === 0) { alert('출고 처리할 활성 품목이 없습니다.'); return; }
    const shared = group.items[0] || {};
    setShipKeepModal({
      group,
      addr: '',
      zip: '',        // v7.195: 우편번호 (다음 우편번호 검색 결과)
      addrDetail: '', // v7.195: 상세주소 (동·호수 등)
      salesStatus: '입력필요',
      salesStatusManual: '',
      method: '택배',
      recipient: shared.dealer_name || '',
      phone: '',
      author: shared.author || '',
      requester: shared.requester || '',
    });
  };

  // 그룹 출고처리 (2단계): 모달의 정보로 실제 출고 처리 — inventory 차감 + transaction_log + keep 상태 변경
  const executeShipKeepGroup = async (m) => {
    const group = m.group;
    const activeItems = group.items.filter(i => i.status === 'active');
    if (activeItems.length === 0) { alert('출고 처리할 활성 품목이 없습니다.'); setShipKeepModal(null); return; }
    const shared = group.items[0];
    // 1. 각 품목의 시리얼이 있으면 inventory.update status='shipped'
    // v7.156: reserved 상태(킵 자신의 예약)도 shipped 처리 허용
    let invFailCount = 0;
    for (const it of activeItems) {
      const serialsArr = Array.isArray(it.serials) ? it.serials.filter(s => s && s.trim()) : (it.serial_number ? [it.serial_number.trim()] : []);
      for (const sn of serialsArr) {
        const inv = await InventoryService.findBySerialFields(sn, 'status');
        if (!inv) { invFailCount++; continue; }
        if (inv.status !== 'in_stock' && inv.status !== 'reserved') { invFailCount++; continue; }
        await InventoryService.updateBySerial(sn, {
          status: 'shipped',
          sale_date: today(),
          dealer_name: shared.dealer_name,
          note: m.addr || '',
        });
      }
    }
    // 2. transaction_log에 outbound 1건 (다중 품목) — 배송시트지 형식
    const reqId = `KEEP-${today().replace(/-/g,'')}-${String(group.id).slice(0,8)}`;
    const items = activeItems.map(it => {
      const serialsArr = Array.isArray(it.serials) ? it.serials.filter(s => s && s.trim()) : (it.serial_number ? [it.serial_number.trim()] : []);
      return {
        product_name: it.product_name,
        qty: it.qty,
        fulfilledSerials: serialsArr,
      };
    });
    const totalQty = activeItems.reduce((s, i) => s + i.qty, 0);
    const totalFulfilled = items.reduce((s, i) => s + i.fulfilledSerials.length, 0);
    const allFulfilled = totalFulfilled >= totalQty;
    const noteObj = {
      type: 'ship_request',
      reqId,
      fromKeep: true,
      keepGroupId: group.id,
      method: m.method || '택배',
      addr: (m.addr || '').trim(),
      // v7.195: 우체국 API용 우편번호 + 상세주소 (recZip / recAddr2 매핑)
      ...(m.zip ? { zip: (m.zip || '').trim() } : {}),
      ...(m.addrDetail ? { addrDetail: (m.addrDetail || '').trim() } : {}),
      recipient: (m.recipient || '').trim() || shared.dealer_name,
      phone: (m.phone || '').trim(),
      author: (m.author || '').trim(),
      requester: (m.requester || '').trim(),
      salesStatus: m.salesStatus || '입력필요',
      ...(m.salesStatus === '수기입력' && m.salesStatusManual ? { salesStatusManual: m.salesStatusManual.trim() } : {}),
      items,
      fulfilled: allFulfilled,
      ...(allFulfilled ? { completedAt: new Date().toISOString() } : {}),
      ...(shared.expected_date ? { expectedDate: shared.expected_date } : {}),
      memo: shared.memo || '[킵그룹출고]',
    };
    const modelLabel = activeItems[0].product_name;
    const { error: txErr } = await supabase.from('transaction_log').insert({
      serial: reqId,
      model: modelLabel,
      type: 'outbound',
      dealer_name: shared.dealer_name,
      note: JSON.stringify(noteObj),
      processed_by: m.author || m.requester || '',
      transaction_date: shared.expected_date || today(),
    });
    if (txErr) { alert('배송시트지 기록 생성 실패: ' + txErr.message); return; }
    // 3. 모든 active 품목 status='shipped'
    const ids = activeItems.map(i => i.id);
    const { error: kErr } = await supabase.from('product_keeps').update({
      status: 'shipped',
      shipped_at: new Date().toISOString(),
    }).in('id', ids);
    if (kErr) { alert('킵 상태 업데이트 실패: ' + kErr.message); return; }
    setShipKeepModal(null);
    await loadKeeps();
    await loadActiveKeeps();
    alert(`✓ 그룹 출고 처리 완료\n배송시트지(${reqId})에 ${activeItems.length}개 품목 기록 생성됨${invFailCount > 0 ? `\n⚠ 시리얼 ${invFailCount}건은 inventory에서 처리 안 됨 (없거나 in_stock 아님)` : ''}`);
  };

  // 호환성을 위해 보존된 구 함수 (사용하지 않음) — 신규는 markKeepGroupShipped → executeShipKeepGroup 흐름 사용
  // eslint-disable-next-line
  const _markKeepGroupShippedLegacy = async (group) => {
    const activeItems = group.items.filter(i => i.status === 'active');
    if (activeItems.length === 0) { alert('출고 처리할 활성 품목이 없습니다.'); return; }
    const shared = group.items[0];
    // 시리얼 요약
    const itemSummary = activeItems.map(it => {
      const serialsArr = Array.isArray(it.serials) ? it.serials.filter(s => s) : (it.serial_number ? [it.serial_number] : []);
      return `• ${it.product_name} × ${it.qty}개 (시리얼: ${serialsArr.length}/${it.qty})`;
    }).join('\n');
    const msg = `그룹 출고 처리 — ${activeItems.length}개 품목\n거래처: ${shared.dealer_name}\n\n${itemSummary}\n\n배송시트지에 1건의 출고 기록이 생성됩니다. 계속하시겠습니까?`;
    if (!window.confirm(msg)) return;
    // 1. 각 품목의 시리얼이 있으면 inventory.update status='shipped'
    let invFailCount = 0;
    for (const it of activeItems) {
      const serialsArr = Array.isArray(it.serials) ? it.serials.filter(s => s && s.trim()) : (it.serial_number ? [it.serial_number.trim()] : []);
      for (const sn of serialsArr) {
        const inv = await InventoryService.findBySerialFields(sn, 'status');
        if (!inv) { invFailCount++; continue; }
        if (inv.status !== 'in_stock') { invFailCount++; continue; }
        await InventoryService.updateBySerial(sn, {
          status: 'shipped',
          sale_date: today(),
          dealer_name: shared.dealer_name,
        });
      }
    }
    // 2. transaction_log에 outbound 1건 (다중 품목)
    const reqId = `KEEP-${today().replace(/-/g,'')}-${String(group.id).slice(0,8)}`;
    const items = activeItems.map(it => {
      const serialsArr = Array.isArray(it.serials) ? it.serials.filter(s => s && s.trim()) : (it.serial_number ? [it.serial_number.trim()] : []);
      return {
        product_name: it.product_name,
        qty: it.qty,
        fulfilledSerials: serialsArr,
      };
    });
    const totalQty = activeItems.reduce((s, i) => s + i.qty, 0);
    const totalFulfilled = items.reduce((s, i) => s + i.fulfilledSerials.length, 0);
    const allFulfilled = totalFulfilled >= totalQty;
    const noteObj = {
      type: 'ship_request',
      reqId,
      fromKeep: true,
      keepGroupId: group.id,
      method: '킵출고',
      addr: shared.memo || '',
      recipient: shared.dealer_name,
      author: shared.author || '',
      requester: shared.requester || '',
      salesStatus: '입력필요',
      items,
      fulfilled: allFulfilled,
      ...(allFulfilled ? { completedAt: new Date().toISOString() } : {}),
      ...(shared.expected_date ? { expectedDate: shared.expected_date } : {}),
      memo: '[킵그룹출고]',
    };
    const modelLabel = activeItems[0].product_name;
    const { error: txErr } = await supabase.from('transaction_log').insert({
      serial: reqId,
      model: modelLabel,
      type: 'outbound',
      dealer_name: shared.dealer_name,
      note: JSON.stringify(noteObj),
      processed_by: shared.author || shared.requester || '',
      transaction_date: shared.expected_date || today(),
    });
    if (txErr) { alert('배송시트지 기록 생성 실패: ' + txErr.message); return; }
    // 3. 모든 active 품목 status='shipped'
    const ids = activeItems.map(i => i.id);
    const { error: kErr } = await supabase.from('product_keeps').update({
      status: 'shipped',
      shipped_at: new Date().toISOString(),
    }).in('id', ids);
    if (kErr) { alert('킵 상태 업데이트 실패: ' + kErr.message); return; }
    await loadKeeps();
    await loadActiveKeeps();
    alert(`✓ 그룹 출고 처리 완료\n배송시트지(${reqId})에 ${activeItems.length}개 품목 기록 생성됨${invFailCount > 0 ? `\n⚠ 시리얼 ${invFailCount}건은 inventory에서 처리 안 됨 (없거나 in_stock 아님)` : ''}`);
  };

  // 킵 출고처리 — 배송시트지(transaction_log)에 출고 기록 생성 + inventory 시리얼 status 변경 + keep status='shipped' (행 단위, 호환성 유지)
  const markKeepShipped = async (keep) => {
    const hasSerial = !!(keep.serial_number && keep.serial_number.trim());
    const msg = `출고 처리 — "${keep.product_name}" × ${keep.qty}개\n거래처: ${keep.dealer_name}\n시리얼: ${hasSerial ? keep.serial_number : '(미입력 — 배송시트지에서 추후 입력)'}\n\n배송시트지에 출고 기록이 생성됩니다. 계속하시겠습니까?`;
    if (!window.confirm(msg)) return;
    // 1. 시리얼이 있으면 inventory의 해당 시리얼 status='shipped'로 변경
    if (hasSerial) {
      const sn = keep.serial_number.trim();
      const inv = await InventoryService.findBySerialFields(sn, 'status,product_name');
      if (!inv) {
        if (!window.confirm(`⚠ 시리얼 "${sn}"이 inventory에 존재하지 않습니다.\n출고 기록만 생성하고 inventory는 건드리지 않습니다. 계속하시겠습니까?`)) return;
      } else if (inv.status !== 'in_stock' && inv.status !== 'reserved') {
        // v7.156: reserved 상태(킵 자신의 예약)도 shipped 처리 허용
        alert(`⚠ 시리얼 "${sn}"의 현재 상태가 "${inv.status}"입니다. 출고 처리할 수 없습니다.`);
        return;
      } else {
        const invRes = await InventoryService.updateBySerial(sn, {
          status: 'shipped',
          sale_date: today(),
          dealer_name: keep.dealer_name,
        });
        if (!invRes.ok) { alert('inventory 업데이트 실패: ' + invRes.msg); return; }
      }
    }
    // 2. transaction_log에 outbound 기록 (출고요청 형식과 호환 — 배송시트지에 그대로 표시됨)
    const reqId = `KEEP-${today().replace(/-/g,'')}-${String(keep.id).slice(-4)}-${String(Date.now()).slice(-3)}`;
    const noteObj = {
      type: 'ship_request',
      reqId,
      fromKeep: true,
      keepId: keep.id,
      method: '킵출고',
      addr: keep.memo || '',
      recipient: keep.dealer_name,
      author: keep.author || '',
      requester: keep.requester || '',
      salesStatus: '입력필요',
      items: [{
        product_name: keep.product_name,
        qty: keep.qty,
        fulfilledSerials: hasSerial ? [keep.serial_number.trim()] : [],
      }],
      fulfilled: hasSerial,
      ...(hasSerial ? { completedAt: new Date().toISOString() } : {}),
      ...(keep.expected_date ? { expectedDate: keep.expected_date } : {}),
      ...(keep.item_memo ? { memo: `[킵] ${keep.item_memo}` } : { memo: '[킵출고]' }),
    };
    const { error: txErr } = await supabase.from('transaction_log').insert({
      serial: reqId,
      model: keep.product_name,
      type: 'outbound',
      dealer_name: keep.dealer_name,
      note: JSON.stringify(noteObj),
      processed_by: keep.author || keep.requester || '',
      transaction_date: keep.expected_date || today(),
    });
    if (txErr) { alert('배송시트지 기록 생성 실패: ' + txErr.message); return; }
    // 3. keep status='shipped'
    const { error: kErr } = await supabase.from('product_keeps').update({
      status: 'shipped',
      shipped_at: new Date().toISOString(),
    }).eq('id', keep.id);
    if (kErr) { alert('킵 상태 업데이트 실패: ' + kErr.message); return; }
    await loadKeeps();
    await loadActiveKeeps();
    alert(`✓ 출고 처리 완료\n배송시트지(${reqId})에 기록이 생성되었습니다.${hasSerial ? '' : '\n시리얼이 미입력 상태이므로 배송시트지에서 추후 시리얼을 채워주세요.'}`);
  };

  // 킵 삭제 (완전 제거 — 잘못 등록한 경우)
  const deleteKeep = async (keep) => {
    if (!window.confirm(`킵 완전 삭제 — "${keep.product_name}" × ${keep.qty}개 (${keep.dealer_name})\n기록도 함께 삭제됩니다. 계속하시겠습니까?`)) return;
    const { error } = await supabase.from('product_keeps').delete().eq('id', keep.id);
    if (error) { alert('삭제 실패: ' + error.message); return; }
    // v7.156: 삭제된 킵의 예약 시리얼 in_stock 원복 (active 상태였을 때만 의미 있음)
    if (keep.status === 'active') {
      const serials = Array.isArray(keep.serials) ? keep.serials : (keep.serial_number ? [keep.serial_number] : []);
      await syncKeepSerialReservation(serials, []);
    }
    // v7.9: 스크롤 유지 — 로컬 patch
    setKeepsItems(prev => prev.filter(k => k.id !== keep.id));
    loadActiveKeeps();
  };

  // 킵 그룹 공통 필드 업데이트 (거래처/요청자/작성자/예상출고일/메모) — 그룹의 모든 행에 적용
  const updateKeepGroupField = async (groupId, field, rawValue) => {
    const value = (rawValue === '' || rawValue == null) ? null : (typeof rawValue === 'string' ? rawValue.trim() || null : rawValue);
    const { error } = await supabase.from('product_keeps').update({ [field]: value }).eq('group_id', groupId);
    if (error) { alert('변경 실패: ' + error.message); return false; }
    // v7.9: 스크롤 유지 — 그룹의 모든 행 로컬 patch
    setKeepsItems(prev => prev.map(k => k.group_id === groupId ? { ...k, [field]: value } : k));
    return true;
  };

  // 카드별 "품목 추가" 폼 열기/닫기/업데이트
  const openAddItemForm = (groupId) => {
    setAddItemForms(s => ({ ...s, [groupId]: { product_name: '', productSearch: '', productResults: [], qty: 1, serials: [''], serial_number: '', serialResults: [], item_memo: '' } }));
  };
  const closeAddItemForm = (groupId) => {
    setAddItemForms(s => { const n = { ...s }; delete n[groupId]; return n; });
  };
  const updateAddItemForm = (groupId, patch) => {
    setAddItemForms(s => ({ ...s, [groupId]: { ...(s[groupId] || {}), ...patch } }));
  };
  // 카드별 폼 — 제품 검색
  const searchAddItemProduct = async (groupId, q) => {
    updateAddItemForm(groupId, { productSearch: q });
    if (!q || q.length < 1) { updateAddItemForm(groupId, { productResults: [] }); return; }
    const data = await ProductService.searchByName(q, 10);
    updateAddItemForm(groupId, { productResults: data });
  };
  // 카드별 폼 — 시리얼 검색 (DB 자동완성)
  const searchAddItemSerial = async (groupId, q) => {
    const f = addItemForms[groupId];
    updateAddItemForm(groupId, { serial_number: q });
    if (!f || !f.product_name) { updateAddItemForm(groupId, { serialResults: [] }); return; }
    const data = await InventoryService.searchInStockSerials({ productName: f.product_name, query: q, limit: 20 });
    updateAddItemForm(groupId, { serialResults: data });
  };
  // 카드별 폼 — 품목 추가 (같은 group_id로 INSERT, 공통 필드는 그룹의 첫 행에서 복사)
  const submitAddItem = async (group) => {
    const f = addItemForms[group.id];
    if (!f || !f.product_name) { alert('제품을 선택하세요.'); return; }
    const qty = parseInt(f.qty, 10);
    if (!qty || qty <= 0) { alert('수량은 1 이상이어야 합니다.'); return; }
    // 가용재고 검증
    const stockCount = await InventoryService.countInStock(f.product_name);
    const reserved = activeKeepsByModel[f.product_name] || 0;
    // v7.311: 시리얼 지정 킵은 이미 in_stock 에서 빠져 있다 (위 submitKeep 과 동일)
    const reservedUnbacked = activeKeepsUnbackedByModel[f.product_name] || 0;
    const available = stockCount - reservedUnbacked;
    if (qty > available) {
      if (!window.confirm(`⚠ "${f.product_name}" 가용재고 부족\n재고 ${stockCount}개 / 활성 킵 ${reserved}개(차감 ${reservedUnbacked}개) / 가용 ${available}개\n요청: ${qty}개\n\n그래도 추가하시겠습니까?`)) return;
    }
    // 그룹의 공통 필드 복사
    const shared = group.items[0];
    const serialsArr = Array.isArray(f.serials) ? f.serials.map(s => (s || '').trim()) : [];
    while (serialsArr.length < qty) serialsArr.push('');
    serialsArr.length = qty;
    const filledSerials = serialsArr.filter(s => s);
    // v7.9: insert 시 select().single()로 새 row 데이터 받음 (전체 reload 없이 로컬 patch)
    const { data: newRow, error } = await supabase.from('product_keeps').insert({
      group_id: group.id,
      product_name: f.product_name,
      qty,
      serials: serialsArr,
      serial_number: filledSerials[0] || null,
      item_memo: (f.item_memo || '').trim() || null,
      dealer_id: shared.dealer_id || null,
      dealer_name: shared.dealer_name,
      memo: shared.memo,
      expected_date: shared.expected_date,
      author: shared.author,
      requester: shared.requester,
      status: 'active',
    }).select().single();
    if (error) { alert('품목 추가 실패: ' + error.message); return; }
    // v7.156: 새로 추가된 킵 품목에 지정된 시리얼을 reserved로 마킹
    await syncKeepSerialReservation([], serialsArr);
    closeAddItemForm(group.id);
    // v7.9: 스크롤 유지 — 로컬 array에 추가
    if (newRow) setKeepsItems(prev => [...prev, newRow]);
    loadActiveKeeps();
  };

  // 킵 인라인 편집 — 시리얼 셀에서 DB 자동완성용 결과 검색 (빈 쿼리도 허용)
  const searchInlineKeepSerial = async (keep, q) => {
    if (!keep.product_name) {
      setEditingKeepCell(c => c ? { ...c, serialResults: [] } : c);
      return;
    }
    const data = await InventoryService.searchInStockSerials({ productName: keep.product_name, query: q, limit: 20 });
    setEditingKeepCell(c => c ? { ...c, serialResults: data } : c);
  };

  // 킵 인라인 필드 업데이트 (수량/시리얼/제품메모/요청자/거래처/예상출고일/작성자/메모)
  const updateKeepField = async (keep, field, rawValue) => {
    // 수량 변경 시 가용재고 재검증 (활성 킵만)
    if (field === 'qty' && keep.status === 'active') {
      const newQty = parseInt(rawValue, 10);
      if (!newQty || newQty <= 0) { alert('수량은 1 이상이어야 합니다.'); return false; }
      if (newQty > keep.qty) {
        // 증가 시: 추가분이 가용재고 안에 있는지 검증
        const stockCount = await InventoryService.countInStock(keep.product_name);
        const { data: otherKeeps } = await supabase.from('product_keeps')
          .select('qty,serials,serial_number').eq('status', 'active').eq('product_name', keep.product_name).neq('id', keep.id);
        // v7.311: 시리얼이 지정된 킵은 이미 inventory.status='reserved' 라 countInStock 에서 빠져 있다.
        //         그 몫을 또 빼면 이중 차감이 되므로 '시리얼 미지정분'만 센다.
        const _assignedCount = (k) => {
          let arr = k.serials;
          if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch (e) { arr = arr.trim() ? [arr] : []; } }
          if (!Array.isArray(arr)) arr = [];
          const n = arr.map(x => String(x == null ? '' : x).trim()).filter(Boolean).length;
          return n > 0 ? n : ((k.serial_number && String(k.serial_number).trim()) ? 1 : 0);
        };
        const reservedByOthers = (otherKeeps || []).reduce((s, k) => s + Math.max(0, (k.qty || 0) - _assignedCount(k)), 0);
        const available = stockCount - reservedByOthers;
        // 이 킵이 이미 시리얼로 확보해 둔 몫도 stockCount 에서 빠져 있다 → 새로 필요한 건 그만큼 적다
        const ownAssigned = _assignedCount(keep);
        const needFromStock = newQty - ownAssigned;
        if (needFromStock > available) {
          if (!window.confirm(`⚠ 가용재고 부족\n재고 ${stockCount}개 / 다른 활성 킵(시리얼 미지정) ${reservedByOthers}개 / 가용 ${available}개\n추가로 필요: ${needFromStock}개 (변경 수량 ${newQty} − 이미 확보한 시리얼 ${ownAssigned})\n\n그래도 변경하시겠습니까?`)) return false;
        }
      }
      // serials 배열 길이도 동기화
      const prevSerials = Array.isArray(keep.serials) ? [...keep.serials] : (keep.serial_number ? [keep.serial_number] : []);
      const cur = [...prevSerials];
      while (cur.length < newQty) cur.push('');
      cur.length = newQty;
      const newSerialNumber = cur.filter(s => s)[0] || null;
      // v7.8: DB 업데이트를 한 번에 (qty + serials + serial_number 동시) — race 방지
      const { error } = await supabase.from('product_keeps')
        .update({ qty: newQty, serials: cur, serial_number: newSerialNumber })
        .eq('id', keep.id);
      if (error) { alert('수량 변경 실패: ' + error.message); return false; }
      // v7.156: 수량 감소로 잘려나간 시리얼은 원복 (active 킵만)
      if (keep.status === 'active') {
        await syncKeepSerialReservation(prevSerials, cur);
      }
      // v7.8: 스크롤 유지 — loadKeeps() 호출 제거, 해당 row만 로컬 patch
      setKeepsItems(prev => prev.map(k => k.id === keep.id
        ? { ...k, qty: newQty, serials: cur, serial_number: newSerialNumber }
        : k
      ));
      // 가용재고 캐시는 갱신 필요 (다른 페이지에서 사용) — await 없이 백그라운드
      loadActiveKeeps();
    } else {
      // 다른 필드: 빈 문자열/null 처리
      const value = (rawValue === '' || rawValue === undefined || rawValue === null) ? null : (typeof rawValue === 'string' ? rawValue.trim() || null : rawValue);
      const patch = { [field]: value };
      const { error } = await supabase.from('product_keeps').update(patch).eq('id', keep.id);
      if (error) { alert('변경 실패: ' + error.message); return false; }
      // v7.8: 스크롤 유지 — 로컬 patch만
      setKeepsItems(prev => prev.map(k => k.id === keep.id ? { ...k, [field]: value } : k));
    }
    return true;
  };

  // 페이지 진입 시 로드
  useEffect(() => { if (page === 'keeps' && connected) loadKeeps(); }, [page, connected, loadKeeps]);
  useEffect(() => { if (connected) loadActiveKeeps(); }, [connected, loadActiveKeeps]);

  // v7.11: 재고 대시보드 진입 시 모든 제품 + 재고 카운트 로드 (v7.17: 카테고리 맵 + v7.22: stock_type 분포)
  const loadStockDashboard = useCallback(async () => {
    if (!connected) return;
    setStockDashboard(s => ({ ...s, loading: true }));
    // v7.309: 파주 재고 동시 로드. paju_stock 테이블이 없어도(SQL 미실행) 대시보드는 떠야 하므로
    //          PajuStockService.loadAll 은 던지지 않고 ok:false 로 돌려준다.
    // v7.401: 대시보드 비고(dashboard_memo)도 함께 로드. 테이블이 없어도(SQL 미실행) 대시보드는 떠야 하므로
    //          실패 시 빈 맵으로 폴백한다.
    const [productsResult, countsResult, categoryMap, pajuResult, memoResult] = await Promise.all([
      ProductService.list({ pageSize: 1000 }),
      InventoryService.getInStockProductCounts(),
      InventoryService.getProductCategoryMap(),
      PajuStockService.loadAll(),
      supabase.from('dashboard_memo').select('product_name,memo').then(r => r, () => ({ data: null })),
    ]);
    const dashMemoMap = {};
    (memoResult?.data || []).forEach(m => { if (m.product_name) dashMemoMap[m.product_name] = m.memo || ''; });
    setStockDashboard(s => ({
      ...s,
      loading: false,
      products: productsResult.data || [],
      counts: countsResult.counts || {},
      stockTypeCounts: countsResult.stockTypeCounts || {},
      categoryMap: categoryMap || {},
      paju: pajuResult.map || {},
      pajuNorm: pajuResult.normMap || {},
      pajuSnapshot: pajuResult.snapshotDate || null,
      pajuReady: !!pajuResult.ok,
      dashMemo: dashMemoMap,
    }));
  }, [connected]);
  // v7.401: 대시보드 비고 저장 — dashboard_memo upsert (product_name PK). 낙관적 갱신.
  const saveDashMemo = async (name, memo) => {
    const val = (memo || '').trim();
    setStockDashboard(s => ({ ...s, dashMemo: { ...s.dashMemo, [name]: val } }));
    setDashMemoEditing(null);
    const { error } = await supabase.from('dashboard_memo').upsert(
      { product_name: name, memo: val || null, updated_at: new Date().toISOString() },
      { onConflict: 'product_name' }
    );
    if (error) alert('비고 저장 실패: ' + error.message);
  };
  // v7.309: 파주 재고 파일 업로드 → 파싱 → Supabase 전량 교체 → 재조회
  const pajuFileRef = useRef(null);
  const handlePajuUpload = async (file) => {
    if (!file) return;
    setStockDashboard(s => ({ ...s, pajuUploading: true }));
    const parsed = await parsePajuStockFile(file);
    if (!parsed.ok) {
      setStockDashboard(s => ({ ...s, pajuUploading: false }));
      alert('파일을 읽지 못했습니다.\n\n' + (parsed.msg || '알 수 없는 오류'));
      return;
    }
    if (parsed.rows.length === 0) {
      setStockDashboard(s => ({ ...s, pajuUploading: false }));
      alert('파주 수량이 있는 행을 찾지 못했습니다.\n\n'
        + `표 ${parsed.totalRows}행을 읽었지만 파주 열 값이 모두 비어 있거나 0입니다.\n`
        + (parsed.headerFound ? '' : '※ 헤더에서 "상품명"/"파주" 열을 찾지 못해 B/D열로 가정했습니다.'));
      return;
    }
    const total = parsed.rows.reduce((a, r) => a + r.qty, 0);
    const ok = window.confirm(
      '파주물류센터 재고를 교체합니다.\n\n'
      + `· 기준일: ${parsed.snapshotDate || '(파일에서 못 찾음)'}\n`
      + `· 품목: ${parsed.rows.length}종 / 총 ${total.toLocaleString()}개\n`
      + `· 수량 0·빈칸 ${parsed.skipped}행은 제외\n\n`
      + '기존 파주 데이터는 전부 지워지고 이 파일 내용으로 대체됩니다.'
    );
    if (!ok) { setStockDashboard(s => ({ ...s, pajuUploading: false })); return; }
    const res = await PajuStockService.replaceAll(parsed.rows, parsed.snapshotDate);
    setStockDashboard(s => ({ ...s, pajuUploading: false }));
    if (!res.ok) {
      alert('저장 실패: ' + res.msg
        + '\n\n※ paju_stock 테이블이 없으면 migrations/v7.309-paju-stock.sql 을 먼저 실행해 주세요.');
      return;
    }
    await loadStockDashboard();
  };

  // v7.40: 재고 목록 페이지의 '대시보드' 탭일 때 로드 (구 stock_dashboard 진입)
  useEffect(() => { if (page === 'inventory' && inventoryTab === 'dashboard' && connected) loadStockDashboard(); }, [page, inventoryTab, connected, loadStockDashboard]);

  // ─── v7.89: page_analytics 사용 현황 ───
  const [analyticsData, setAnalyticsData] = useState(null); // {events, summary} | null
  const [analyticsRange, setAnalyticsRange] = useState('7d'); // 'today' | '7d' | '30d'
  const [analyticsBusy, setAnalyticsBusy] = useState(false);
  const loadAnalytics = useCallback(async () => {
    setAnalyticsBusy(true);
    const now = new Date();
    let fromISO;
    let days;
    if (analyticsRange === 'today') {
      const d = new Date(now); d.setHours(0, 0, 0, 0);
      fromISO = d.toISOString(); days = 1;
    } else if (analyticsRange === '7d') {
      const d = new Date(now); d.setDate(d.getDate() - 7); d.setHours(0, 0, 0, 0);
      fromISO = d.toISOString(); days = 7;
    } else {
      const d = new Date(now); d.setDate(d.getDate() - 30); d.setHours(0, 0, 0, 0);
      fromISO = d.toISOString(); days = 30;
    }
    const events = await AnalyticsService.loadEvents({ fromISO });
    const summary = AnalyticsService.summarize(events, { days });
    setAnalyticsData({ events, summary });
    setAnalyticsBusy(false);
  }, [analyticsRange]);
  useEffect(() => {
    if (page === 'inventory' && inventoryTab === 'analytics' && connected) loadAnalytics();
  }, [page, inventoryTab, connected, loadAnalytics]);

  // v7.89: 페이지 뷰 자동 트래킹 (page 또는 inventoryTab 변경 시)
  useEffect(() => {
    if (!connected) return;
    const label = page === 'inventory'
      ? `재고 목록 (${inventoryTab})`
      : (({ txlog: '배송시트지', inbound: '입고 등록', inbound_hdd: 'HDD/SSD 입고',
            ship: '출고 요청', keeps: '킵 관리', demo: '데모 관리',
            catalog: '제품 표준화 관리', audit: '재고 실사', backup: '백업',
            scan: '모바일 스캔' /* v7.113 */ })[page] || page);
    AnalyticsService.trackPageView(label);
  }, [page, inventoryTab, connected]);

  // v7.23: 데모 관리 페이지 진입 시 로드
  const loadDemoData = useCallback(async () => {
    if (!connected) return;
    setDemoState(s => ({ ...s, loading: true }));
    const [eqRes, statsRes, rentalsRes, returnsRes] = await Promise.all([
      DemoService.listEquipment(),
      DemoService.getStats(),
      DemoService.listActiveRentals(),
      DemoService.listReturns({ limit: 100 }),
    ]);
    setDemoState(s => ({
      ...s,
      loading: false,
      equipment: eqRes.data || [],
      stats: statsRes || {},
      activeRentals: rentalsRes || [],
      returns: returnsRes || [],
    }));
  }, [connected]);
  useEffect(() => { if (page === 'demo' && connected) loadDemoData(); }, [page, connected, loadDemoData]);

  // ─── v7.35: 장비 단일 필드(상태/비고) 인라인 편집 저장 ───
  // v7.246: [회수] 버튼 — 장비 1대를 '리버'로 되돌린다 (진행 중 대여는 서비스단에서 차단)
  const [demoRestoringId, setDemoRestoringId] = useState(null);
  const restoreEquipment = async (e) => {
    const invalid = !DEMO_VALID_STATUS.includes(e.status);
    const lines = [
      `[${e.serial_number}] 회수 처리`,
      '',
      `위치: ${e.location || '—'} → 리버`,
    ];
    // 멀쩡한 상태값('불량','수리중' 등)은 건드리지 않는다는 걸 미리 알려준다
    if (invalid) lines.push(`상태: ${e.status || '—'} → ${DEMO_RESTORE_STATUS}`);
    else lines.push(`상태: ${e.status} (변경 없음)`);
    lines.push('', '계속하시겠습니까?');
    if (!window.confirm(lines.join('\n'))) return;

    setDemoRestoringId(e.id);
    const res = await DemoService.restoreEquipmentToRiver(e.id);
    setDemoRestoringId(null);
    if (!res.ok) { alert(res.blocked ? res.msg : '회수 실패: ' + res.msg); return; }
    // 목록 즉시 반영 (전체 재조회 대신 해당 행만 교체)
    setDemoState(s => ({
      ...s,
      equipment: s.equipment.map(x => x.id === e.id ? { ...x, ...res.data } : x),
    }));
  };

  const saveEquipmentField = async (id, field, value) => {
    // 같은 값이면 호출 자체를 건너뛰지 않고 그대로 진행 (백엔드 noop) — 단순화
    const { data, error } = await DemoService.updateEquipmentField(id, field, value);
    if (error) {
      alert(`${field === 'status' ? '상태' : '비고'} 변경 실패: ` + error.message);
      return false;
    }
    setDemoState(s => ({
      ...s,
      equipment: s.equipment.map(e => e.id === id ? { ...e, ...data } : e),
    }));
    return true;
  };

  // ─── v7.337: 장비 카테고리 변경 (모델 단위 일괄) ─────────────────────
  //   ⚠ 왜 '모델 단위' 인가:
  //     장비 목록은 모델명으로 묶어 보여주고, 그룹의 카테고리 배지는 첫 항목 값을 쓴다.
  //     같은 모델인데 시리얼마다 카테고리가 다르면 배지가 실제와 어긋난다.
  //     한 대만 바꿀 일은 사실상 없으므로 그룹 전체를 한 번에 옮긴다.
  //   ⚠ update 는 한 번의 쿼리로 끝낸다. 한 대씩 N번 돌면 중간 실패 시
  //     반쪽만 바뀐 상태로 남는다.
  const [demoCatBusy, setDemoCatBusy] = useState(null);   // 처리 중인 모델명
  const changeGroupCategory = async (modelName, items, nextCat) => {
    const ids = (items || []).map(x => x.id);
    if (ids.length === 0) return;
    const from = DEMO_CATEGORY_LABEL[items[0].category] || items[0].category || '(없음)';
    const to = DEMO_CATEGORY_LABEL[nextCat] || nextCat;
    if (!window.confirm(`"${modelName}" ${ids.length}대의 카테고리를 바꿉니다.\n\n${from} → ${to}`)) return;
    setDemoCatBusy(modelName);
    const { error } = await DemoService.updateEquipmentCategory(ids, nextCat);
    setDemoCatBusy(null);
    if (error) {
      alert('카테고리 변경 실패: ' + error.message
        + '\n\n※ demo_equipment.category 에 CHECK/enum 제약이 걸려 있으면 거부됩니다.'
        + '\n   migrations/v7.336-demo-category-etc.sql 로 확인해 주세요.');
      return;
    }
    const idSet = new Set(ids);
    setDemoState(st => ({
      ...st,
      equipment: st.equipment.map(x => idSet.has(x.id) ? { ...x, category: nextCat } : x),
    }));
  };

  // ─── v7.330: 대여로그 [비고] 더블클릭 인라인 편집 ─────────────────────
  //   ⚠ Esc 취소가 blur 저장과 충돌한다. Esc 를 누르면 textarea 가 언마운트되면서
  //     onBlur 가 뒤이어 발생하는데, 그때 onBlur 클로저는 '취소되기 전 draft' 를 들고 있다.
  //     그대로 두면 Esc 를 눌러도 저장된다 → ref 플래그로 한 번 걸러낸다.
  const rentalNoteCancelRef = useRef(false);

  // v7.332: 사이드바 파티클이 마우스를 받을 대상. 캔버스는 pointer-events:none 이라
  //   직접 못 받는다 → 사이드바 엘리먼트에 리스너를 붙인다.
  const sidebarRef = useRef(null);

  const saveRentalNote = async (rental, draft) => {
    if (rentalNoteCancelRef.current) { rentalNoteCancelRef.current = false; return; }
    setDemoState(s => ({ ...s, editingRentalNote: null }));
    const next = (draft || '').trim();
    if (next === (rental.notes || '')) return;   // 변경 없음 — 서버 왕복 생략
    // 저장 주체: 로그인 개념이 없으므로 대여 담당자/요청자를 쓴다 (submitDemoLend 와 동일)
    const by = rental.managers || rental.requester || '미지정';
    const res = await DemoService.updateRentalNotes(rental.id, next, by);
    if (!res.ok) { alert('비고 저장 실패: ' + (res.msg || '')); return; }
    setDemoState(s => ({
      ...s,
      activeRentals: s.activeRentals.map(x => x.id === rental.id
        ? { ...x, notes: res.notes ?? (next || null), ...(res.auditLog ? { audit_log: res.auditLog } : {}) }
        : x),
    }));
  };

  // ─── v7.25: 데모 모달 액션 함수들 ───

  // 장비 추가 모달 열기
  const openDemoAddModal = () => setDemoAddModal({
    category: 'NAS_DAS', model: '', serial_number: '', status: '정상작동',
    model_detail: '', location: '리버', note: '', loading: false, result: null,
  });

  // 장비 추가 실행
  const submitDemoAdd = async () => {
    if (!demoAddModal) return;
    const m = demoAddModal;
    if (!m.model.trim()) {
      setDemoAddModal(s => s ? { ...s, result: { type: 'err', msg: '모델명을 입력하세요.' } } : s);
      return;
    }
    setDemoAddModal(s => s ? { ...s, loading: true, result: null } : s);
    const { data, error } = await DemoService.addEquipment({
      category: m.category,
      model: m.model.trim(),
      serial_number: m.serial_number.trim() || null,
      status: m.status,
      model_detail: m.model_detail.trim() || null,
      location: m.location || '리버',
      note: m.note.trim() || null,
      added_by: '운영 관리자',
    });
    if (error) {
      setDemoAddModal(s => s ? { ...s, loading: false, result: { type: 'err', msg: '추가 실패: ' + error.message } } : s);
      return;
    }
    setDemoAddModal(s => s ? { ...s, loading: false, result: { type: 'ok', msg: `✓ 추가됨 (id ${data.id})` } } : s);
    loadDemoData();
  };

  // v7.43: 데모 대여 수정 시 변경 사항 → audit 엔트리 생성
  const computeRentalAuditEntry = (original, newData, by) => {
    const fields = {};
    const FIELD_LABELS = { company: '업체명', shipment_date: '출고일', expected_return_date: '회수예정일', managers: '담당자', requester: '요청자', notes: '특이사항' };
    Object.keys(FIELD_LABELS).forEach(f => {
      const oldVal = (original[f] || '').toString();
      const newVal = (newData[f] || '').toString();
      if (oldVal !== newVal) fields[f] = { from: oldVal, to: newVal };
    });
    const shipping = {};
    const SHIP_LABELS = { ship_method: '배송방법', address: '주소', recipient: '수령인', recipient_phone: '연락처' };
    const oldShip = original.shipping || {};
    Object.keys(SHIP_LABELS).forEach(f => {
      const oldVal = (oldShip[f] || '').toString();
      const newVal = (newData[f] || '').toString();
      if (oldVal !== newVal) shipping[f] = { from: oldVal, to: newVal };
    });
    const oldEqIds = new Set((original.equipments || []).map(e => e.id));
    const newEqIds = new Set((newData.equipments || []).map(e => e.id));
    const addedItems = (newData.equipments || []).filter(e => !oldEqIds.has(e.id))
      .map(e => `${e.model}${e.serial_number ? ' · ' + e.serial_number : ''}`);
    const removedItems = (original.equipments || []).filter(e => !newEqIds.has(e.id))
      .map(e => `${e.model}${e.serial_number ? ' · ' + e.serial_number : ''}`);
    const hasFieldChanges = Object.keys(fields).length > 0;
    const hasShipChanges = Object.keys(shipping).length > 0;
    const hasItemChanges = addedItems.length > 0 || removedItems.length > 0;
    if (!hasFieldChanges && !hasShipChanges && !hasItemChanges) return null;
    const parts = [];
    Object.keys(fields).forEach(f => parts.push(`${FIELD_LABELS[f]} 변경`));
    Object.keys(shipping).forEach(f => parts.push(`${SHIP_LABELS[f]} 변경`));
    if (addedItems.length > 0) parts.push(`품목 ${addedItems.length}개 추가`);
    if (removedItems.length > 0) parts.push(`품목 ${removedItems.length}개 제거`);
    return {
      ts: new Date().toISOString(),
      by: by || '미지정',
      action: 'update',
      changes: {
        ...(hasFieldChanges ? { fields } : {}),
        ...(hasShipChanges ? { shipping } : {}),
        ...(addedItems.length ? { addedItems } : {}),
        ...(removedItems.length ? { removedItems } : {}),
      },
      summary: parts.join(', '),
    };
  };

  // v7.44: 백업 대상 테이블 정의 (n8n workflow와 동일 목록 유지)
  const BACKUP_TABLES = [
    { name: 'products', label: '제품 표준화 관리' },
    { name: 'product_aliases', label: '제품 별칭' },
    { name: 'inventory', label: '재고 (시리얼)' },
    { name: 'transaction_log', label: '입출고 이력' },
    { name: 'product_keeps', label: '킵' },
    { name: 'demo_equipment', label: '데모 장비' },
    { name: 'demo_rentals', label: '데모 대여' },
    { name: 'demo_rental_items', label: '데모 대여 품목' },
  ];

  // v7.44: 한 테이블의 모든 row를 페이지네이션으로 가져오기 (대용량 안전)
  const fetchAllRowsForBackup = async (tableName, pageSize = 1000, safeLimit = 200000) => {
    const result = [];
    let from = 0;
    while (from < safeLimit) {
      const { data, error } = await supabase.from(tableName)
        .select('*').order('id', { ascending: true })
        .range(from, from + pageSize - 1);
      if (error) {
        // 'id' 컬럼이 없는 테이블은 정렬 없이 재시도
        const { data: data2, error: err2 } = await supabase.from(tableName)
          .select('*').range(from, from + pageSize - 1);
        if (err2) throw err2;
        if (!data2 || data2.length === 0) break;
        result.push(...data2);
        if (data2.length < pageSize) break;
        from += pageSize;
        continue;
      }
      if (!data || data.length === 0) break;
      result.push(...data);
      if (data.length < pageSize) break;
      from += pageSize;
    }
    return result;
  };

  // v7.44: 전체 데이터 백업 실행 — 모든 테이블을 단일 JSON으로 묶어 다운로드
  const runFullBackup = async () => {
    setBackupState({ loading: true, progress: 0, status: '백업 시작...' });
    const result = {
      meta: {
        backup_version: '1.0',
        app_version: APP_VERSION,
        created_at: new Date().toISOString(),
        created_at_local: new Date().toLocaleString('ko-KR'),
        tables: BACKUP_TABLES.map(t => t.name),
        counts: {},
      },
      data: {},
    };
    try {
      for (let i = 0; i < BACKUP_TABLES.length; i++) {
        const t = BACKUP_TABLES[i];
        setBackupState({ loading: true, progress: Math.round((i / BACKUP_TABLES.length) * 100), status: `[${i+1}/${BACKUP_TABLES.length}] ${t.label} 페치 중...` });
        const rows = await fetchAllRowsForBackup(t.name);
        result.data[t.name] = rows;
        result.meta.counts[t.name] = rows.length;
      }
      // JSON 직렬화 + 다운로드 트리거
      const json = JSON.stringify(result, null, 2);
      const sizeKb = Math.round(json.length / 1024);
      const blob = new Blob([json], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const ts = today().replace(/-/g, '');
      const a = document.createElement('a');
      a.href = url;
      a.download = `inventura-backup-${ts}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const totalRows = Object.values(result.meta.counts).reduce((s, c) => s + c, 0);
      setBackupState({
        loading: false,
        progress: 100,
        status: `✓ 백업 완료 — ${BACKUP_TABLES.length}개 테이블, ${totalRows.toLocaleString()}개 row, ${sizeKb.toLocaleString()} KB`,
        lastBackup: { ts: new Date().toISOString(), totalRows, sizeKb, counts: result.meta.counts },
      });
    } catch (e) {
      setBackupState({ loading: false, progress: 0, status: `✗ 백업 실패: ${e.message || e}` });
    }
  };

  // v7.49: 배송시트지 출고건 → 한국 택배사 양식 .xls 다운로드 (BIFF8 / Excel 97-2003)
  // 양식: A 우편번호 / B 주소 / C 이름 / D 연락처1 / E 연락처2 / F 1 / G 1 / H 3 / I 배송메세지 / J 상품모델 / K 제품
  // 행 수: 송장 수와 모델(items) 수 중 큰 값
  // 파일명: {업체명}_{YYYYMMDD}.xls
  // 주의: BIFF8 형식이라 셀 색상/스타일은 적용 안 됨 (택배사 시스템 호환성 우선)
  // v7.221: 퀵 배차 시트 payload 생성 (1차 draft / 2차 final 공용)
  //   두 단계가 같은 구조를 보내고, n8n이 reqId로 같은 행을 찾아 갱신한다.
  //   "무엇이 부분이고 무엇이 전체"를 코드로 못 박지 않는다 —
  //   각 시점에 채워진 값만 담기고, 빈 값은 n8n에서 건너뛰면 된다.
  const buildQuickSheetPayload = (note, item, stage) => {
    const items = Array.isArray(note.items) ? note.items : [];
    const author = note.author || '';
    return {
      source: 'inventura-pro',
      version: APP_VERSION,
      stage,                                   // 'draft'(출고요청 시) | 'final'(시트 전송 버튼)
      reqId: note.reqId || (item && item.serial) || '',   // ★ 행 매칭 키

      // A 배차시간 — ★ 행 매칭 키. note에 확정 저장된 값을 그대로 재사용해야 1차/2차가 일치한다
      // v7.241: 값은 stampDispatchTime()이 첫 전송 때 찍는다. 아래 created_at 폴백은
      //         v7.240 이전에 이미 시트에 기록된 구 데이터 전용 (그 행의 키가 등록시각이라 유지)
      dispatchTime: note.quickDispatchTime
        || fmtSheetDateTime((item && (item.created_at || item.receipt_date)) || new Date()),
      // v7.240: 시트 담당자 칸은 직책 없이 이름만 ('김중규 이사' → '김중규')
      manager: stripStaffTitle(author),                                                           // B
      managerPhone: staffPhones[author] || '',   // ★ 맵 키는 풀네임이므로 원문으로 조회      // C
      region: note.quickRegion || guessRegion(note.addr || ''),                                   // D
      vehicle: note.quickVehicle || '',                                                           // E
      payType: note.quickCOD ? '착불' : '선불',                                                    // F
      amount: note.quickPrice != null ? note.quickPrice : '',                                     // G
      // v7.229: 완료 처리하면 H '배차' → '완료', K 에 완료시각이 채워진다
      progress: note.quickCompletedAt ? '완료' : '배차',                                           // H
      driverPhone: note.quickDriverPhone || '',                                                   // I
      // v7.231: 배송시트지 비고(note.memo)와 별개 — 퀵 시트 전용 비고
      // v7.232: 퀵 시트 비고에는 보통 업체명을 적는다 → 미입력 시 수령인(업체명)으로 자동 채움
      remark: note.quickRemark || note.recipient || (item && item.dealer_name) || '',             // J
      completedAt: note.quickCompletedAt || '',                                                   // K

      // 참고용 원본
      requester: stripStaffTitle(note.requester || ''),   // v7.240: 직책 제거
      recipient: note.recipient || '',
      phone: note.phone || '',
      zip: note.zip || '',
      addr: note.addr || '',
      addrDetail: note.addrDetail || '',
      salesStatus: note.quickSalesStatus || note.salesStatus || '',
      salesStatusManual: note.quickSalesStatusManual || note.salesStatusManual || '',
      naverStore: !!note.naverStore,
      itemsText: items.map(it => `${it.product_name} x${it.qty}`).join(', '),
      items: items.map(it => ({ productName: it.product_name, qty: it.qty })),
      totalQty: items.reduce((a, it) => a + (Number(it.qty) || 0), 0),
      sentAt: new Date().toISOString(),
    };
  };

  const postQuickSheet = async (payload) => {
    const url = quickSheetWebhook;
    if (!url) throw new Error('웹훅 URL이 설정되지 않았습니다.');
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error(`웹훅 응답 ${res.status}`);
  };

  // v7.221: 1차 — 출고요청 등록 직후 자동 전송 (부분 작성).
  //   실패해도 출고요청은 이미 저장된 상태이므로 절대 막지 않는다. 콘솔 경고만.
  //   이후 [시트 전송] 버튼으로 2차 갱신하면 채워진 값이 반영된다.
  // v7.241: sendQuickSheetDraft 제거 — 출고요청 등록 시 자동 전송을 없앴으므로 호출자가 없다.
  //   1차 기록은 sendQuickToSheet()(첫 호출 시 stage:'draft')가 담당한다.

  // v7.229: 퀵 배송 완료 처리 — 완료시각(K) 기록 + 진행상황(H)을 '완료'로.
  //   기록 후 곧바로 시트 갱신까지 한 번에 처리한다 (버튼 두 번 누를 이유가 없음).
  const toggleQuickComplete = async (item) => {
    let note = {};
    try { note = JSON.parse(item.note || '{}'); } catch (e) { note = {}; }
    if (note.type !== 'ship_request') return;
    if (!isQuickMethod(note.method)) return;   // v7.242: 위와 같은 이유

    const already = !!note.quickCompletedAt;
    if (already) {
      if (!window.confirm(`완료 처리를 해제합니다.\n(완료시각 ${note.quickCompletedAt})\n\n시트의 진행상황이 '배차'로 되돌아가고 완료시간이 비워집니다.\n계속하시겠습니까?`)) return;
    }
    // v7.241: 여기가 첫 기록일 수도 있으므로 배차시간을 확정해 둔다
    const stage = quickStage(note);
    const nextNote = { ...stampDispatchTime(note) };
    if (already) delete nextNote.quickCompletedAt;
    else nextNote.quickCompletedAt = fmtSheetDateTime(new Date());

    setQuickSheetSending(item.id);
    try {
      const { error } = await supabase.from('transaction_log')
        .update({ note: JSON.stringify(nextNote) }).eq('id', item.id);
      if (error) throw error;
      await loadTxLog();
      // 웹훅이 설정돼 있으면 시트도 즉시 갱신
      if (quickSheetWebhook) {
        try {
          const payload = buildQuickSheetPayload(nextNote, item, stage);
          await postQuickSheet(payload);
          const saved = JSON.stringify({ ...nextNote, quickSheetSentAt: payload.sentAt });
          await supabase.from('transaction_log').update({ note: saved }).eq('id', item.id);
          await loadTxLog();
        } catch (e) {
          alert(`완료 처리는 저장됐지만 시트 갱신에 실패했습니다.\n\n${e.message || e}\n\n[시트 전송] 버튼으로 다시 시도할 수 있습니다.`);
        }
      }
    } catch (e) {
      alert('완료 처리 저장 실패\n\n' + (e.message || String(e)));
    }
    setQuickSheetSending(null);
  };

  // v7.241: 배차시간 확정 — 시트에 이 건이 **처음 올라가는 순간**의 시각으로 찍고 그 뒤 동결한다.
  //   배차시간은 n8n의 행 매칭 키(Column to match on = '배차시간')라 한 번 정해지면 변하면 안 된다.
  //   찍는 시점을 출고요청 제출(v7.222) → 첫 전송 버튼으로 옮긴 것이 이번 변경의 핵심.
  //
  //   ⚠ 구 데이터 보호: quickDispatchTime 이 없는데 quickSheetSentAt 이 있으면
  //      "이미 created_at 기준으로 시트에 행이 있는 건"이다. 여기에 새 시각을 찍으면
  //      기존 행을 못 찾아 새 줄이 생긴다 → 그런 건은 손대지 않고 기존 폴백을 그대로 태운다.
  const stampDispatchTime = (note) => {
    if (note.quickDispatchTime) return note;          // 이미 확정됨
    if (note.quickSheetSentAt) return note;           // 구 데이터 — created_at 폴백 유지
    return { ...note, quickDispatchTime: fmtSheetDateTime(new Date()) };
  };

  // v7.241: stage 판정 — **스탬프 전 원본** note 로 물어봐야 한다.
  //   n8n이 stage 로 append/update 를 갈라 쓸 수 있으므로, 시트에 처음 올라가는 건은
  //   어느 버튼에서 출발했든 'draft'(=행 생성)로 보낸다. 세 경로가 같은 규칙을 쓴다.
  const quickStage = (note) => (note.quickDispatchTime || note.quickSheetSentAt) ? 'final' : 'draft';

  // v7.221: 2차 — 수동 [시트 전송] 버튼. 같은 reqId 행을 전체 내용으로 갱신.
  const sendQuickToSheet = async (item) => {
    let note = {};
    try { note = JSON.parse(item.note || '{}'); } catch (e) { note = {}; }
    if (note.type !== 'ship_request') return;
    // v7.242: method 가드 — 지금은 UI가 퀵 건에만 버튼을 노출하지만 방어가 한 겹뿐이었다.
    //   v7.240까지는 1차 전송이 제출 시점의 shipMethod 가드를 달고 있었는데,
    //   v7.241에서 1차 책임을 이 함수로 옮기면서 그 가드가 사라졌다.
    if (!isQuickMethod(note.method)) return;

    if (!quickSheetWebhook) {
      alert('n8n 웹훅 URL이 설정되지 않았습니다.\n\n우측 상단 [메뉴] → [퀵 시트 설정]에서 먼저 입력해주세요.');
      return;
    }

    // v7.241: 첫 전송이면 여기서 배차시간이 찍힌다 (= 버튼 누른 시각)
    const stage = quickStage(note);
    const sendNote = stampDispatchTime(note);
    const isFirst = stage === 'draft';
    const payload = buildQuickSheetPayload(sendNote, item, stage);
    setQuickSheetSending(item.id);
    try {
      await postQuickSheet(payload);
      try {
        const nextNote = JSON.stringify({ ...sendNote, quickSheetSentAt: payload.sentAt });
        const { error } = await supabase.from('transaction_log')
          .update({ note: nextNote }).eq('id', item.id);
        if (error) throw error;
        await loadTxLog();
      } catch (e) {
        console.warn('전송 시각 기록 실패:', e);
      }
      alert(isFirst
        ? `시트에 기록했습니다. (1차)\n배차시간: ${sendNote.quickDispatchTime}`
        : '시트를 갱신했습니다. (2차 · 전체 내용)');
    } catch (e) {
      alert('시트 전송 실패\n\n' + (e.message || String(e)) +
        '\n\n웹훅 URL과 n8n 워크플로가 활성 상태인지 확인해주세요.\n출고요청 데이터에는 영향이 없습니다.');
    }
    setQuickSheetSending(null);
  };

  const downloadShippingExcel = (item) => {
    try {
      // 데이터 추출
      let note = {};
      try { note = typeof item.note === 'string' ? JSON.parse(item.note || '{}') : (item.note || {}); } catch(e) {}
      const items = Array.isArray(note.items) ? note.items : [];
      const trackingList = (note.trackingNo || '').split(',').map(s => s.trim()).filter(Boolean);
      const rowCount = Math.max(items.length, trackingList.length, 1);

      // 헤더 + 데이터 행 (AOA — Array of Arrays)
      // v7.194: shipInfo.zip / addrDetail 반영 (우편번호·상세주소 정형화)
      const zip = note.zip || '';
      const addr = note.addr || '';
      const addrDetail = note.addrDetail || '';
      // B열 주소 = 도로명 + 상세주소 (공백 구분)
      const fullAddr = [addr, addrDetail].filter(Boolean).join(' ');
      const recipient = note.recipient || '';
      const phone = note.phone || '';
      // v7.211: 비고(note.memo)는 배송메세지(I열)에 넣지 않는다.
      //   v7.194에서 매핑했으나, 비고는 내부 메모 용도라 택배사로 나가면 안 됨.
      const rows = [
        ['우편번호','주소','이름','연락처1','연락처2','1','1','3','배송메세지','상품모델','제품'],
      ];
      for (let i = 0; i < rowCount; i++) {
        const it = items[i];
        // 'NX1457 - 1EA' 형식 (대시 양쪽 공백)
        const productCell = it ? `${it.product_name} - ${it.qty}EA` : '';
        rows.push([
          zip,        // A 우편번호 (v7.194)
          fullAddr,   // B 주소 (v7.194: addr + addrDetail)
          recipient,  // C 이름
          phone,      // D 연락처1
          '',         // E 연락처2
          '', '', '', // F G H 빈 값
          '',         // I 배송메세지 — v7.211: 비고 매핑 제거 (빈 값 유지)
          '',         // J 상품모델
          productCell // K 제품
        ]);
      }
      // 빈 행 4개 (사용자가 손으로 채울 여백)
      for (let i = 0; i < 4; i++) {
        rows.push(['','','','','','','','','','','']);
      }

      // 시트 생성
      const ws = XLSX.utils.aoa_to_sheet(rows);
      // 컬럼 너비 (wch = character width)
      ws['!cols'] = [
        { wch: 12 },  // A 우편번호
        { wch: 55 },  // B 주소
        { wch: 10 },  // C 이름
        { wch: 18 },  // D 연락처1
        { wch: 18 },  // E 연락처2
        { wch: 5 },   // F
        { wch: 5 },   // G
        { wch: 5 },   // H
        { wch: 14 },  // I 배송메세지
        { wch: 14 },  // J 상품모델
        { wch: 30 },  // K 제품
      ];

      // 워크북 + 시트
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, '배송');

      // 파일명: {업체명}_{YYYYMMDD}.xls
      const safe = (s) => (s || '거래처').replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_');
      const company = safe(item.dealer_name);
      const dateStr = (item.transaction_date || today()).replace(/-/g, '');
      const filename = `${company}_${dateStr}.xls`;

      // BIFF8 (.xls) 형식으로 출력
      const wbout = XLSX.write(wb, { bookType: 'biff8', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      alert('엑셀 생성 실패: ' + (e.message || e));
    }
  };

  // v7.58: 시리얼 텍스트 paste 후 파싱 + 중복 검증
  // 한 줄에 하나씩 (개행/탭/콤마 모두 분리자로 허용)
  // v7.64: 모델명 카탈로그 확인 로직 제거 — 별도 productName(카탈로그 선택)에서 검증
  const parseHddSerials = async () => {
    const txt = hddInbound.serialText || '';
    const all = txt.split(/[\r\n\t,]+/).map(s => s.trim()).filter(Boolean);
    const seen = new Set();
    const dupsInPaste = [];
    const unique = [];
    for (const s of all) {
      if (seen.has(s)) dupsInPaste.push(s);
      else { seen.add(s); unique.push(s); }
    }
    let existing = [];
    if (unique.length > 0) {
      const chunkSize = 500;
      for (let i = 0; i < unique.length; i += chunkSize) {
        const chunk = unique.slice(i, i + chunkSize);
        const { data } = await supabase.from('inventory').select('serial_number').in('serial_number', chunk);
        (data || []).forEach(r => existing.push(r.serial_number));
      }
    }
    setHddInbound(s => ({
      ...s,
      parsedSerials: unique,
      duplicatesInPaste: dupsInPaste,
      existingInDb: existing,
    }));
  };

  // v7.64: 제품명 검색 (카탈로그) — 자동완성용
  // products.product_name + product_aliases.alias 둘 다 매칭 (한글 별칭 검색 지원)
  const searchHddProduct = async (q) => {
    if (!q || q.trim().length < 1) {
      setHddInbound(s => ({ ...s, productSearch: q, productResults: [], productSearching: false }));
      return;
    }
    setHddInbound(s => ({ ...s, productSearch: q, productSearching: true }));
    try {
      // 1) products.product_name ilike 검색
      const { data: byName } = await supabase.from('products')
        .select('product_name,category')
        .ilike('product_name', `%${q.trim()}%`)
        .order('product_name')
        .limit(15);
      // 2) product_aliases.alias ilike 검색 → 해당 product_name 가져오기
      const { data: byAlias } = await supabase.from('product_aliases')
        .select('product_name,alias')
        .ilike('alias', `%${q.trim()}%`)
        .limit(15);
      // 머지 (alias 매칭은 product 카탈로그에서 category도 가져오기)
      const map = new Map();
      (byName || []).forEach(p => map.set(p.product_name, { product_name: p.product_name, category: p.category, alias: null }));
      const aliasNames = [...new Set((byAlias || []).map(a => a.product_name))].filter(n => !map.has(n));
      if (aliasNames.length > 0) {
        const { data: aliasProds } = await supabase.from('products').select('product_name,category').in('product_name', aliasNames);
        (aliasProds || []).forEach(p => {
          const matchedAlias = (byAlias || []).find(a => a.product_name === p.product_name)?.alias || null;
          map.set(p.product_name, { product_name: p.product_name, category: p.category, alias: matchedAlias });
        });
      }
      // 입력 q가 alias 매칭 결과의 alias와 정확히 매칭되면 그 alias도 보여줌
      const results = [...map.values()].sort((a, b) => a.product_name.localeCompare(b.product_name)).slice(0, 12);
      setHddInbound(s => s.productSearch === q ? { ...s, productResults: results, productSearching: false, productSearchOpen: true } : s);
    } catch (e) {
      setHddInbound(s => ({ ...s, productSearching: false }));
    }
  };

  // v7.58: HDD/SSD 일괄 등록
  // v7.64: product_name(카탈로그) + model_no(수기) 분리 저장
  // v7.66: 카테고리는 카탈로그 단일 진실원천(SOT)으로 사용 — 사용자 토글 제거
  //         카탈로그 NULL이면 신규 등록 모달을 거치도록 강제 (카탈로그 정리 누락 방지)
  const registerHddInbound = async () => {
    const { source, inDate, inDateUnknown, productName, modelNo, stockType, parsedSerials, existingInDb } = hddInbound;
    if (!productName) { alert('제품을 카탈로그에서 선택해주세요.'); return; }
    // v7.75: 모델 번호는 선택 사항 (필수 검증 제거)
    // v7.96: 입고일 미확인 옵션 도입 — inDateUnknown=true면 검증 skip, receipt_date=null 저장
    if (!inDate && !inDateUnknown) { alert('입고일을 입력하거나 "입고일 미확인" 옵션을 체크하세요.'); return; }
    if (!parsedSerials || parsedSerials.length === 0) { alert('시리얼이 없습니다. 시리얼을 붙여넣어 주세요.'); return; }
    const existingSet = new Set(existingInDb);
    const toInsert = parsedSerials.filter(s => !existingSet.has(s));
    if (toInsert.length === 0) { alert('등록할 시리얼이 없습니다. 모두 이미 DB에 있는 시리얼입니다.'); return; }

    // v7.66: 카탈로그 카테고리 확인 — NULL이면 차단
    const { data: prodMeta } = await supabase.from('products').select('category').eq('product_name', productName).single();
    const category = prodMeta?.category;
    if (!category) {
      alert(`'${productName}'의 카탈로그 카테고리가 비어있습니다.\n카탈로그 관리 페이지에서 먼저 카테고리를 지정해주세요. (HDD/SSD 등)`);
      return;
    }

    if (!window.confirm(`제품: ${productName} [${category}]\n모델: ${modelNo.trim() || '(미입력)'}\n입고일: ${inDateUnknown ? '⚠ 미확인 (날짜 없이 등록)' : inDate}\n시리얼: ${parsedSerials.length}개 중 ${toInsert.length}개 등록 (중복 ${existingInDb.length}개 제외)\n\n진행하시겠습니까?`)) return;

    setHddInbound(s => ({ ...s, registering: true, registerProgress: 0 }));
    // v7.75: 빈 값은 NULL로 저장 (기존 DB의 NULL과 일관성)
    const modelNoTrimmed = modelNo.trim() || null;

    const chunkSize = 200;
    const failed = [];
    let done = 0;
    for (let i = 0; i < toInsert.length; i += chunkSize) {
      const chunk = toInsert.slice(i, i + chunkSize).map(serial => ({
        product_name: productName,
        serial_number: serial,
        model_no: modelNoTrimmed,
        category,                         // v7.66: 카탈로그 SOT
        status: 'in_stock',
        stock_type: stockType || '신품',
        receipt_date: inDateUnknown ? null : inDate,   // v7.96: 미확인이면 NULL
        source: source || null,
      }));
      const { error } = await supabase.from('inventory').insert(chunk);
      if (error) {
        for (const row of chunk) {
          const { error: e2 } = await supabase.from('inventory').insert(row);
          if (e2) failed.push({ serial: row.serial_number, msg: e2.message });
        }
      }
      done = Math.min(i + chunkSize, toInsert.length);
      setHddInbound(s => ({ ...s, registerProgress: Math.round((done / toInsert.length) * 100) }));
    }
    setHddInbound(s => ({ ...s, registering: false, registerProgress: 100 }));
    // v7.154: 성공한 시리얼만 집계 (실패한 것 제외)
    const failedSet = new Set(failed.map(f => f.serial));
    const succeededSerials = toInsert.filter(s => !failedSet.has(s));

    // v7.154: transaction_log INSERT — 배송시트지에 입고 기록 표시
    //         (이전엔 inventory INSERT만 하고 transaction_log는 누락되어 배송시트지에 입고 이력이 안 남았음)
    //         inDateUnknown 이라도 transaction_date는 today() 폴백 (배송시트지 날짜 필터에서 잡히도록)
    if (succeededSerials.length > 0) {
      const recvId = 'RECV-' + Date.now() + '-hdd' + Math.random().toString(36).slice(2, 5);
      const txRow = {
        serial: recvId,
        model: productName,
        type: 'inbound',
        note: JSON.stringify({
          type: 'inbound',
          reqId: recvId,
          items: [{
            product_name: productName,
            qty: succeededSerials.length,
            serials: succeededSerials,
            from: source || null,
            model_no: modelNoTrimmed || undefined,
            stock_type: stockType || '신품',
          }],
          from: source || null,
          category,                             // HDD/SSD 등
          receiptDateUnknown: !!inDateUnknown,  // 입고일 미확인 flag (inventory는 NULL이지만 tx는 today())
          memo: inDateUnknown ? '입고일 미확인' : undefined,
          // v7.183: 장부처리 완료 여부
          bookkept: !!hddInbound.bookkept,
        }),
        processed_by: '운영 관리자',
        transaction_date: inDateUnknown ? today() : (inDate || today()),
      };
      const { error: txErr } = await supabase.from('transaction_log').insert(txRow);
      if (txErr) console.warn('HDD 입고 배송시트지 기록 실패:', txErr);
    }

    if (failed.length > 0) {
      // v7.185: 실패 사유 노출 — 이전엔 시리얼만 보여줘서 원인 파악 불가
      //         (예: stock_type CHECK 제약 위반 등 DB 레벨 에러를 즉시 확인 가능)
      const firstMsg = failed[0]?.msg || '(사유 미상)';
      alert(`등록 완료. 단, ${failed.length}개 실패: ${failed.slice(0,3).map(f=>f.serial).join(', ')}${failed.length>3?' 외':''}\n\n실패 사유:\n${firstMsg}`);
      console.error('[HDD/SSD 입고 실패]', failed);
    } else {
      alert(`✓ ${toInsert.length}개 시리얼 등록 완료`);
    }
    // v7.89: 사용량 트래킹
    AnalyticsService.trackAction('HDD_SSD_입고', hddInbound.source || '', {
      productName, serialCount: toInsert.length, failed: failed.length,
    });
    setHddInbound(s => ({
      ...s,
      serialText: '',
      parsedSerials: [],
      existingInDb: [],
      duplicatesInPaste: [],
      modelNo: '',
      registerProgress: 0,
    }));
  };

  // v7.61/v7.64: HDD/SSD 입고 — 카탈로그에 신규 제품을 즉시 등록 (한글명은 alias로)
  // v7.64: productSearch에 입력한 텍스트를 product_name으로 등록 + 한글명 alias로
  const quickRegisterHddProduct = async () => {
    const productName = (hddInbound.productSearch || '').trim();
    const alias = (hddInbound.quickRegisterAlias || '').trim();
    const category = hddInbound.quickRegisterCategory;
    if (!productName) { alert('제품명이 비어있습니다. 자동완성 input에 먼저 입력하세요.'); return; }
    if (!alias) { alert('한글 별칭을 입력해주세요.'); return; }
    if (!category) { alert('카테고리를 선택해주세요.'); return; }
    setHddInbound(s => ({ ...s, quickRegisterSaving: true }));
    try {
      const { error: e1 } = await supabase.from('products').upsert({
        product_name: productName, category, form_factor: null,
      }, { onConflict: 'product_name' });
      if (e1) throw new Error('카탈로그 등록 실패: ' + e1.message);
      const { error: e2 } = await supabase.from('product_aliases').insert({
        product_name: productName, alias, source: 'hdd_inbound',
      });
      if (e2 && e2.code !== '23505') {
        console.warn('alias 등록 실패 (무시):', e2.message);
      }
      // 등록 완료 후 자동 선택
      setHddInbound(s => ({
        ...s,
        productName,                  // 카탈로그에 등록한 제품을 선택 상태로
        productSearch: productName,
        productResults: [],
        productSearchOpen: false,
        selectedCategory: category,   // v7.66: 신규 등록한 카테고리도 같이 표시
        showQuickRegister: false,
        quickRegisterAlias: '',
        quickRegisterSaving: false,
      }));
      alert(`✓ 카탈로그 등록 완료\n  제품명: ${productName}\n  한글 별칭: ${alias}\n  카테고리: ${category}`);
    } catch (e) {
      setHddInbound(s => ({ ...s, quickRegisterSaving: false }));
      alert(e.message || '등록 실패');
    }
  };

  // v7.43: 데모 대여 수정 이력 모달 열기 — DB에서 audit_log 가져와 시간역순 표시
  const openDemoRentalHistory = async (rental) => {
    setDemoHistoryModal({ rental, log: null, loading: true });
    const { data, error } = await DemoService.getRentalById(rental.id);
    if (error || !data) {
      setDemoHistoryModal({ rental, log: [], loading: false, error: error?.message || '조회 실패' });
      return;
    }
    const log = Array.isArray(data.audit_log) ? data.audit_log : [];
    setDemoHistoryModal({ rental, log, loading: false });
  };

  // 대여 등록 페이지 진입 (v7.33: 모달 → 별도 페이지)
  const openDemoLendModal = () => {
    setDemoLendModal({
      company: '', shipment_date: today(), expected_return_date: '',
      managers: '', requester: '', notes: '',
      // v7.41: 배송시트지 자동 등록을 위한 배송지 정보
      address: '', recipient: '', recipient_phone: '', ship_method: '택배',
      search: '', searchCategory: '', searchResults: [],
      selectedItems: [], loading: false, result: null,
      expandedModels: new Set(), // v7.34: 모델별 아코디언 펼침 상태
      // v7.42: 편집 모드 메타
      editMode: false, editingRentalId: null, originalEquipmentIds: [],
    });
    setPage('demo_lend');
  };

  // v7.42: 편집 모드로 페이지 진입 — 활성 대여의 [수정] 버튼에서 호출
  const openDemoLendEditModal = async (rental, opts = {}) => {
    const { data: full, error } = await DemoService.getRentalById(rental.id);
    if (error || !full) {
      alert('대여 정보 조회 실패: ' + (error?.message || '알 수 없음'));
      return;
    }
    // 기존 demo_rental_items → demo_equipment 정보 가져와서 selectedItems로 변환
    const equipmentIds = (full.items || []).map(it => it.equipment_id).filter(Boolean);
    let selectedItems = [];
    if (equipmentIds.length > 0) {
      const { data: equipments } = await supabase.from('demo_equipment')
        .select('id, model, serial_number, category, location, status, model_detail, note')
        .in('id', equipmentIds);
      selectedItems = equipments || [];
    }
    // transaction_log에서 reqId=DEMO-{shipment_number} 검색해서 배송 정보 복원
    let shipMeta = { address: '', recipient: '', recipient_phone: '', ship_method: '택배' };
    const { data: txRow } = await supabase.from('transaction_log')
      .select('*').eq('serial', `DEMO-${full.shipment_number}`).maybeSingle();
    if (txRow) {
      try {
        const note = JSON.parse(txRow.note || '{}');
        shipMeta = {
          address: note.addr || '',
          recipient: note.recipient || '',
          recipient_phone: note.phone || '',
          ship_method: note.method || '택배',
        };
      } catch (e) {}
    }
    setDemoLendModal({
      company: full.company || '',
      shipment_date: full.shipment_date || today(),
      expected_return_date: full.expected_return_date || '',
      managers: full.managers || '',
      requester: full.requester || '',
      notes: full.notes || '',
      ...shipMeta,
      search: '', searchCategory: '', searchResults: [],
      selectedItems, loading: false, result: null,
      expandedModels: new Set(),
      editMode: true,
      editingRentalId: full.id,
      editingShipmentNumber: full.shipment_number,
      originalEquipmentIds: equipmentIds,
      // v7.429: 어디서 들어왔는지 — 배송시트지 [품목 수정] 으로 진입했으면 저장 후 그 건으로 돌아간다.
      //   (데모 관리에서 들어온 기존 경로는 opts 가 비어 있어 그대로 데모 관리로 복귀)
      returnTo: opts.returnTo || null,
      returnSearch: opts.returnSearch || null,
      // v7.43: 변경 이력 계산용 원본 스냅샷
      originalSnapshot: {
        company: full.company || '',
        shipment_date: full.shipment_date || '',
        expected_return_date: full.expected_return_date || '',
        managers: full.managers || '',
        requester: full.requester || '',
        notes: full.notes || '',
        shipping: shipMeta,
        equipments: selectedItems.map(e => ({ id: e.id, model: e.model, serial_number: e.serial_number })),
      },
    });
    setPage('demo_lend');
  };

  // 대여 장비 검색
  const searchDemoLendItems = async (q, cat) => {
    setDemoLendModal(s => s ? { ...s, search: q, searchCategory: cat } : s);
    const data = await DemoService.searchAvailableEquipment({ q, category: cat || null, limit: 500 });
    setDemoLendModal(s => s ? { ...s, searchResults: data } : s);
  };

  // 장비 선택 토글
  const toggleDemoLendItem = (eq) => {
    setDemoLendModal(s => {
      if (!s) return s;
      const exists = s.selectedItems.find(x => x.id === eq.id);
      const next = exists ? s.selectedItems.filter(x => x.id !== eq.id) : [...s.selectedItems, eq];
      return { ...s, selectedItems: next };
    });
  };

  // 대여 등록 실행
  const submitDemoLend = async () => {
    if (!demoLendModal) return;
    const m = demoLendModal;
    if (!m.company.trim()) {
      setDemoLendModal(s => s ? { ...s, result: { type: 'err', msg: '업체명을 입력하세요.' } } : s);
      return;
    }
    if (m.selectedItems.length === 0) {
      setDemoLendModal(s => s ? { ...s, result: { type: 'err', msg: '대여 품목을 1개 이상 선택하세요.' } } : s);
      return;
    }
    setDemoLendModal(s => s ? { ...s, loading: true, result: null } : s);

    // ─── v7.42: 편집 모드 분기 ───
    if (m.editMode && m.editingRentalId) {
      // v7.43: 변경 사항을 비교해 audit 엔트리 생성
      const newSnapshot = {
        company: m.company.trim(),
        shipment_date: m.shipment_date,
        expected_return_date: m.expected_return_date || '',
        managers: m.managers.trim(),
        requester: m.requester.trim(),
        notes: m.notes.trim(),
        ship_method: m.ship_method || '택배',
        address: (m.address || '').trim(),
        recipient: (m.recipient || '').trim(),
        recipient_phone: (m.recipient_phone || '').trim(),
        equipments: m.selectedItems.map(e => ({ id: e.id, model: e.model, serial_number: e.serial_number })),
      };
      const auditEntry = computeRentalAuditEntry(m.originalSnapshot || {}, newSnapshot, m.managers || m.requester || '미지정');

      const { data: updated, error } = await DemoService.updateRental({
        rentalId: m.editingRentalId,
        company: m.company.trim(),
        shipment_date: m.shipment_date,
        expected_return_date: m.expected_return_date || null,
        managers: m.managers.trim() || null,
        requester: m.requester.trim() || null,
        notes: m.notes.trim() || null,
        equipment_ids: m.selectedItems.map(it => it.id),
        auditEntry,
      });
      if (error) {
        setDemoLendModal(s => s ? { ...s, loading: false, result: { type: 'err', msg: '대여 수정 실패: ' + error.message } } : s);
        return;
      }
      // 배송시트지(transaction_log) 동기화
      // v7.153: items[].demoSerials — 편집 시에도 실제 시리얼 재기록
      try {
        const grouped = {};
        const modelSerials = {}; // v7.127: 비고용
        m.selectedItems.forEach(it => {
          if (!grouped[it.model]) grouped[it.model] = { product_name: it.model, qty: 0, fulfilledSerials: [], serialOptional: true, serialOptionalReason: 'demo_lend', demoSerials: [] };
          grouped[it.model].qty += 1;
          grouped[it.model].demoSerials.push(it.serial_number || '');
          if (!modelSerials[it.model]) modelSerials[it.model] = [];
          modelSerials[it.model].push(it.serial_number);
        });
        const items = Object.values(grouped);
        // v7.127: 비고 문자열에 모델/시리얼 블록 추가
        const serialBlocks = Object.entries(modelSerials).map(([model, sers]) =>
          `${model}\n${sers.join('\n')}`
        ).join('\n\n');
        const memoBase = m.notes ? `[데모 대여] ${m.notes.trim()}` : '[데모 대여]';
        const memoFull = `${memoBase}\n\n${serialBlocks}`;
        const reqId = `DEMO-${updated.shipment_number}`;
        // 기존 row 찾기 (없으면 새로 만들지 않고 경고만)
        const { data: existingTx } = await supabase.from('transaction_log').select('*').eq('serial', reqId).maybeSingle();
        if (existingTx) {
          let oldNote = {};
          try { oldNote = JSON.parse(existingTx.note || '{}'); } catch(e) {}
          const newNote = {
            ...oldNote,
            type: 'ship_request',
            reqId,
            fromDemoLend: true,
            demoRentalId: updated.id,
            demoShipmentNumber: updated.shipment_number,
            method: m.ship_method || oldNote.method || '택배',
            addr: (m.address || '').trim(),
            recipient: (m.recipient || '').trim(),
            phone: (m.recipient_phone || '').trim(),
            author: (m.managers || '').trim(),
            requester: (m.requester || '').trim(),
            salesStatus: '수기입력',
            salesStatusManual: '데모 대여',
            items,
            // fulfilled 유지 (이미 출고 처리된 경우 그대로)
            ...(m.expected_return_date ? { expectedDate: m.expected_return_date } : {}),
            memo: memoFull,
          };
          const modelLabel = items[0]?.product_name || '데모 대여';
          // v7.426: 출고일(shipment_date)이 실제로 바뀐 경우에만 배송시트지 날짜를 갱신한다.
          //   기존엔 무조건 m.shipment_date 로 덮어써서, 자동이월로 오늘 날짜까지 밀어둔 건을
          //   데모에서 주소·비고만 고쳐 저장해도 원래 출고일로 되돌아가 배송시트지에서 사라진 것처럼 보였다
          //   (사용자 리포트: "수정→저장했더니 배송시트지에서 사라졌는데?").
          //   안 바뀌었으면 현재 transaction_date(=이월된 오늘 날짜)를 그대로 유지한다.
          const origShipDate = m.originalSnapshot?.shipment_date || null;
          const shipDateChanged = !!m.shipment_date && m.shipment_date !== origShipDate;
          const nextTxDate = shipDateChanged
            ? m.shipment_date
            : (existingTx.transaction_date || m.shipment_date || null);
          await supabase.from('transaction_log').update({
            model: modelLabel,
            dealer_name: m.company.trim(),
            note: JSON.stringify(newNote),
            processed_by: m.managers || m.requester || existingTx.processed_by || '',
            transaction_date: nextTxDate,
          }).eq('id', existingTx.id);
        }
      } catch (e) {
        console.warn('배송시트지 동기화 중 예외:', e);
      }
      setDemoLendModal(s => s ? { ...s, loading: false, success: true, result: { type: 'ok', msg: `✓ 대여 수정 완료 (${updated.shipment_number})` } } : s);
      loadDemoData();
      // v7.429: 배송시트지 [품목 수정] 으로 진입했으면 저장 후 그 배송건으로 돌아간다.
      //   reqId(DEMO-{shipment_number})로 logSearch 를 걸어 해당 건만 필터해서 보여준다
      //   (v7.418 [배송건] 버튼과 같은 딥링크 방식). 데모 관리에서 들어온 경우엔 기존대로 데모 관리로.
      const backTo = m.returnTo;
      const backSearch = m.returnSearch || `DEMO-${updated.shipment_number}`;
      setTimeout(() => {
        if (backTo === 'txlog') {
          setLogSearch(backSearch);
          setPage('txlog');
        } else {
          setPage('demo');
        }
        setDemoLendModal(null);
      }, 1200);
      return;
    }

    // ─── 신규 등록 ───
    const { data, error } = await DemoService.createRental({
      company: m.company.trim(),
      shipment_date: m.shipment_date,
      expected_return_date: m.expected_return_date || null,
      managers: m.managers.trim() || null,
      requester: m.requester.trim() || null,
      notes: m.notes.trim() || null,
      equipment_ids: m.selectedItems.map(it => it.id),
    });
    if (error) {
      setDemoLendModal(s => s ? { ...s, loading: false, result: { type: 'err', msg: '대여 등록 실패: ' + error.message } } : s);
      return;
    }
    // v7.41: 배송시트지(transaction_log)에도 outbound 출고 요청으로 자동 등록
    // - fulfilled: false → 출고 대기 상태 (시리얼 면제로 [제품 출고] 한 번 누르면 완료)
    // - items[].serialOptional: true → 데모 장비는 demo_equipment에서 관리되므로 시리얼 면제
    try {
      // 모델별로 그룹핑 → items 배열 생성 + 시리얼 리스트도 생성 (v7.127)
      // v7.153: items[].demoSerials — 배송처리 시 실제 데모 장비 시리얼을 표시하기 위해 저장
      const grouped = {};
      const modelSerials = {}; // v7.127: 비고용
      m.selectedItems.forEach(it => {
        if (!grouped[it.model]) grouped[it.model] = { product_name: it.model, qty: 0, fulfilledSerials: [], serialOptional: true, serialOptionalReason: 'demo_lend', demoSerials: [] };
        grouped[it.model].qty += 1;
        grouped[it.model].demoSerials.push(it.serial_number || '');
        if (!modelSerials[it.model]) modelSerials[it.model] = [];
        modelSerials[it.model].push(it.serial_number);
      });
      const items = Object.values(grouped);
      // v7.127: 비고 문자열에 모델/시리얼 블록 추가
      const serialBlocks = Object.entries(modelSerials).map(([model, sers]) =>
        `${model}\n${sers.join('\n')}`
      ).join('\n\n');
      const memoBase = m.notes ? `[데모 대여] ${m.notes.trim()}` : '[데모 대여]';
      const memoFull = `${memoBase}\n\n${serialBlocks}`;
      const reqId = `DEMO-${data.shipment_number}`;
      const noteObj = {
        type: 'ship_request',
        reqId,
        fromDemoLend: true,
        demoRentalId: data.id,
        demoShipmentNumber: data.shipment_number,
        method: m.ship_method || '택배',
        addr: (m.address || '').trim(),
        recipient: (m.recipient || '').trim(),
        phone: (m.recipient_phone || '').trim(),
        author: (m.managers || '').trim(),
        requester: (m.requester || '').trim(),
        salesStatus: '수기입력',
        salesStatusManual: '데모 대여',
        items,
        fulfilled: false,
        ...(m.expected_return_date ? { expectedDate: m.expected_return_date } : {}),
        memo: memoFull,
      };
      const modelLabel = items[0]?.product_name || '데모 대여';
      const { error: txErr } = await supabase.from('transaction_log').insert({
        serial: reqId,
        model: modelLabel,
        type: 'outbound',
        dealer_name: m.company.trim(),
        note: JSON.stringify(noteObj),
        processed_by: m.managers || m.requester || '',
        transaction_date: m.shipment_date || today(),
      });
      if (txErr) {
        // demo_rentals는 이미 성공했으니 경고만 보여주고 진행 (롤백 안 함)
        console.warn('배송시트지 자동 등록 실패:', txErr);
        setDemoLendModal(s => s ? { ...s, loading: false, success: true, result: { type: 'ok', msg: `✓ 대여 등록 완료 (${data.shipment_number}) — 단, 배송시트지 자동 등록 실패: ${txErr.message}` } } : s);
        loadDemoData();
        setTimeout(() => { setPage('demo'); setDemoLendModal(null); }, 2500);
        return;
      }
    } catch (e) {
      console.warn('배송시트지 자동 등록 중 예외:', e);
    }
    setDemoLendModal(s => s ? { ...s, loading: false, success: true, result: { type: 'ok', msg: `✓ 대여 예약 등록 완료 (${data.shipment_number})\n→ 배송시트지에서 [제품 출고] 처리하면 실제 대여가 시작됩니다` } } : s);
    loadDemoData();
    // 2초 후 데모 관리 페이지로 복귀
    setTimeout(() => { setPage('demo'); setDemoLendModal(null); }, 2500);
  };

  // 회수 처리 모달 열기
  const openDemoReturnModal = (rental) => setDemoReturnModal({
    rental, return_date: today(), managers: rental.managers || '',
    notes: '', loading: false, result: null,
  });

  // 회수 처리 실행
  const submitDemoReturn = async () => {
    if (!demoReturnModal) return;
    const m = demoReturnModal;
    setDemoReturnModal(s => s ? { ...s, loading: true, result: null } : s);
    const { data, error } = await DemoService.processReturn({
      rental_id: m.rental.id,
      return_date: m.return_date,
      managers: m.managers.trim() || null,
      notes: m.notes.trim() || null,
    });
    if (error) {
      setDemoReturnModal(s => s ? { ...s, loading: false, result: { type: 'err', msg: '회수 실패: ' + error.message } } : s);
      return;
    }
    // v7.245: 회수 기록은 남았지만 장비가 안 돌아온 경우를 드러낸다.
    //   지금까지는 무조건 '회수 완료'만 떠서, 장비가 대여처에 남아 있는 걸
    //   나중에 장비 목록을 보고서야 알았다.
    const { itemRows = 0, linked = 0, restored = 0, restoreErr } = data || {};
    let msg = `✓ 회수 완료 (${data.return_number}) · 장비 ${restored}대 '리버'로 복원`;
    let type = 'ok';
    if (restoreErr) {
      type = 'warn';
      msg = `회수 기록은 저장됐지만 장비 위치 복원에 실패했습니다.\n(${restoreErr})\n장비 목록에서 직접 '리버'로 되돌려주세요.`;
    } else if (restored === 0) {
      type = 'warn';
      msg = itemRows === 0
        ? `회수 기록은 저장됐습니다 (${data.return_number}).\n다만 이 대여에는 장비 연결 정보가 없어 자동 복원된 장비가 없습니다.\n(이관된 옛 대여 건입니다 — 대여품목이 텍스트로만 남아 있음)\n\n장비 목록에서 해당 시리얼의 위치를 '리버'로 직접 바꿔주세요.`
        : `회수 기록은 저장됐습니다 (${data.return_number}).\n다만 품목 ${itemRows}건 중 장비에 연결된 건이 ${linked}건이라 자동 복원된 장비가 없습니다.\n\n장비 목록에서 직접 확인해주세요.`;
    }
    setDemoReturnModal(s => s ? { ...s, loading: false, result: { type, msg } } : s);
    loadDemoData();
  };
  // ─── /킵 관리 함수 ───


  // v7.71: 출고 요청 폼 수동 초기화 함수
  //        기존에는 useEffect([page])로 ship 페이지 진입 시마다 자동 초기화 → 탭 이동만 해도 작업 날아감
  //        이제는 (1) 요청 저장 성공 후 명시적 호출, (2) 사용자가 초기화 버튼 클릭 시만 실행
  //        탭 왕복 시에는 폼 상태가 유지됨
  const resetShipForm = () => {
    setShipSearch('');
    setShipResults([]);
    setShipItems([]);
    setShipAddr('');
    setShipZip('');         // v7.195
    setShipAddrDetail('');  // v7.195
    setShipRecipient('');
    setShipPhone('');
    setShipRequester('');
    setShipAuthor('');
    setShipMemo('');
    setShipMethod('courier');
    setShipTrackingNo('');
    setShipQuickPrice('');
    setShipQuickCOD(false); // v7.54
    setShipQuickVehicle(''); // v7.217
    setShipQuickRegion(''); // v7.218
    setShipRegionManual(false); // v7.228
    setShipQuickDriverPhone(''); // v7.220
    setShipQuickRemark(''); // v7.231
    setShipDirectCompany('');
    setShipNaverStore(false); // v7.103
    setShipRmaProductName('');
    setShipRmaSerial('');
    setShipRmaFormNo('');
    setShipRmaTicketNo('');
    setShipRmaEms('');
    setShipRmaReturnSerial('');
    setShipRmaSerialFromDB(false);
    setShipRmaSerialLookupMsg(null);
    setShipEmsType('RMA');
    setShipSalesStatus('입력필요');
    setShipQuickSalesStatus('입력필요');
    setShipSalesStatusManual('');
    setShipQuickSalesStatusManual('');
    setShipExpectedDate(today());
    setShipVisitTime('');
    setShipPickupCompany('');
    shipDateTouchedRef.current = false; // v7.227
    setShipExpectedDate(today());
    setShipMsg(null);
    setAddrSuggestOpen(false);
    setAddrSuggestIndex(-1);
    // v7.99: RMA/DOA 제품명 자동완성 초기화
    setShipRmaProductResults([]);
    setShipRmaProductCatalogHit(null);
  };

  // v7.99: RMA/DOA 제품명 자동완성 (제품 표준화 카탈로그 기반)
  //   - ProductService.searchByName(q, 10) 재사용
  //   - 별칭/EU 접미사 로직은 제외 (canonical 표준명만 노출 → 수리완료 FK 매칭 실패 사전 차단)
  //   - 입력 변경 시마다 대소문자 무시 정확 매칭 여부를 catalogHit 로 세팅
  const searchShipRmaProduct = async (q) => {
    setShipRmaProductName(q);
    setShipRmaProductCatalogHit(null);
    if (!q || q.length < 1) { setShipRmaProductResults([]); return; }
    const data = await ProductService.searchByName(q, 10);
    setShipRmaProductResults(data);
    const norm = q.trim().toLowerCase();
    const exact = data.find(p => p.product_name.toLowerCase() === norm);
    setShipRmaProductCatalogHit(!!exact);
  };
  const selectShipRmaProduct = (product) => {
    setShipRmaProductName(product.product_name);
    setShipRmaProductResults([]);
    setShipRmaProductCatalogHit(true);
  };

  // 입력값에 따른 자동완성 후보 (최대 7건)
  const addrSuggestions = useMemo(() => {
    const q = shipAddr.trim().toLowerCase();
    if (!q) return addressHistory.slice(0, 7);
    return addressHistory.filter(h => h.addr.toLowerCase().includes(q)).slice(0, 7);
  }, [shipAddr, addressHistory]);

  // ─── v7.312: 메모 이미지 첨부 ───────────────────────────────
  //   실체는 nginx WebDAV(/attachments/memo_<id>/), 목록은 memos.images(jsonb).
  //   memos 는 optimistic lock(updated_at 일치)을 쓰므로 저장은 updateMemoField 와 같은 경로로 간다.
  // v7.318: 배송시트지 행 글로우 on/off.
  //   포인터 핸들러는 색과 무관하므로 훅은 한 번만 부른다 (style 은 SHIP_GLOW_STYLES 사용).
  //   ⚠ 라이트 모드에서는 CSS 가 알아서 끈다 (html:not([data-theme='dark']) .glow-row::after{opacity:0}).
  //     이 토글은 '다크 모드에서도 끄고 싶을 때' 용이다.
  const shipRowGlow = useRowGlow(SHIP_GLOW_OPTS);
  // v7.363: NAS 호환성 재고 카드용 (커서 추적 — 배송시트지와 같은 훅)
  const stockGlow = useRowGlow(STOCK_GLOW_OPTS);
  const [shipGlowOn, setShipGlowOn] = useState(() => {
    try { return localStorage.getItem('inventura_ship_glow') !== 'off'; } catch (e) { return true; }
  });
  // v7.322: 전체 마스터 스위치. 건별 체크박스보다 위에 있다 (여기가 꺼지면 전부 정지).
  const [shipRunOn, setShipRunOn] = useState(() => {
    try { return localStorage.getItem('inventura_ship_run') !== 'off'; } catch (e) { return true; }
  });

  // v7.323: 건별 애니메이션 on/off — transaction_log.note(JSON) 의 glowRun 필드에 저장한다.
  //   ★ 새 컬럼/테이블을 만들지 않은 이유: note 는 이미 이 행의 부가 상태를 담는 JSON 이고
  //     (fulfilled, autoDefer, bookkept …), 전 직원이 같은 값을 본다. SQL 실행이 필요 없다.
  //   ⚠ note 에는 다른 필드가 함께 들어 있으므로 반드시 최신 값을 다시 읽고 병합한다
  //     (saveBookkept 와 같은 패턴). 통째로 덮어쓰면 남의 편집이 날아간다.
  const toggleRowGlowRun = async (item, next) => {
    // 체크박스는 즉각 반응해야 하므로 화면부터 먼저 바꾼다
    const patchLocal = (v) => setLogItems(list => list.map(r => {
      if (r.id !== item.id) return r;
      try { const p = JSON.parse(r.note); p.glowRun = v; return { ...r, note: JSON.stringify(p) }; }
      catch (e) { return r; }
    }));
    patchLocal(next);
    try {
      const { data: row, error: fErr } = await supabase
        .from('transaction_log').select('note').eq('id', item.id).single();
      if (fErr) throw fErr;
      const p = row?.note ? JSON.parse(row.note) : {};
      p.glowRun = next;
      const { error } = await supabase
        .from('transaction_log').update({ note: JSON.stringify(p) }).eq('id', item.id);
      if (error) throw error;
    } catch (err) {
      patchLocal(!next);   // 롤백 — 저장 안 된 걸 켜진 것처럼 두면 안 된다
      alert('표시 저장 실패: ' + (err.message || String(err)));
    }
  };

  // v7.313: 게시판 안쪽 탭 — 'memos'(공유 게시판) / 'files'(작업파일 공유)
  const [memoBoardTab, setMemoBoardTab] = useState(() => {
    try { return localStorage.getItem('inventura_board_tab') === 'files' ? 'files' : 'memos'; } catch (e) { return 'memos'; }
  });
  const [sharedFileCount, setSharedFileCount] = useState(0);
  const memoImgRef = useRef(null);
  // 업로드 대상 메모 id — state 가 아니라 ref 다.
  //   input.click() 은 다음 틱에 열리는데, state 로 두면 그 사이 re-render 가 끝나야만
  //   onChange 클로저가 새 값을 본다. ref 는 그 타이밍에 의존하지 않는다.
  const memoImgTargetRef = useRef(null);
  const [memoImgBusy, setMemoImgBusy] = useState(null);       // 업로드 중인 메모 id
  const [memoLightbox, setMemoLightbox] = useState(null);     // { url, name }

  // v7.314: WebDAV PUT 실패 시 자주 나오는 상태코드에 대한 안내
  const uploadHint = (msg) => {
    const m = String(msg || '');
    if (/HTTP 405/.test(m)) return '※ nginx 가 PUT 을 안 받고 있습니다. /attachments/ 블록의 dav_methods 설정을 확인해 주세요.';
    if (/HTTP 413/.test(m)) return '※ 파일이 client_max_body_size 를 넘었습니다.';
    if (/HTTP 40[13]/.test(m)) return '※ attachments 폴더 쓰기 권한을 확인해 주세요.';
    if (/HTTP 409|HTTP 500/.test(m)) return '※ 하위 폴더 생성에 실패했을 수 있습니다. create_full_put_path on 설정을 확인해 주세요.';
    return '※ 브라우저 개발자도구 Network 탭에서 /attachments/ 요청의 상태코드를 확인해 주세요.';
  };

  const memoImages = (memo) => {
    const v = memo && memo.images;
    if (Array.isArray(v)) return v;
    if (typeof v === 'string') { try { const p = JSON.parse(v); return Array.isArray(p) ? p : []; } catch (e) { return []; } }
    return [];
  };

  const handleMemoImageUpload = async (fileList) => {
    const memoId = memoImgTargetRef.current;
    const arr = Array.from(fileList || []).filter(Boolean);
    memoImgTargetRef.current = null;
    if (!memoId || arr.length === 0) return;
    const memo = memos.find(m => m.id === memoId);
    if (!memo) return;
    setMemoImgBusy(memoId);
    const added = [];
    for (const f of arr) {
      // v7.314: type 이 빈 문자열로 오는 환경(일부 안드로이드·HEIC)이 있어 확장자도 함께 본다
      // v7.414: 이미지 + zip 허용 (사용자 요청). zip 은 썸네일 대신 다운로드 칩으로 렌더한다.
      //   그 외 문서/압축은 여전히 [작업파일 공유] 탭 안내.
      const looksImage = /^image\//.test(f.type || '') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(f.name || '');
      const looksZip = /zip/i.test(f.type || '') || /\.zip$/i.test(f.name || '');
      const looksPdf = /pdf/i.test(f.type || '') || /\.pdf$/i.test(f.name || '');
      if (!looksImage && !looksZip && !looksPdf) { alert(`"${f.name}" 은(는) 이미지·zip·pdf 가 아닙니다. 메모에는 이미지·zip·pdf 만 첨부할 수 있습니다.\n\n※ 그 외 문서·압축 파일은 [작업파일 공유] 탭을 이용하세요.`); continue; }
      const up = await AttachmentService.uploadToDir(`memo_${memoId}`, f, memo.author || null);
      if (!up.ok) { setMemoImgBusy(null); alert(`"${f.name}" 업로드 실패\n\n${up.error?.message || '알 수 없는 오류'}\n\n${uploadHint(up.error?.message)}`); return; }
      added.push({ path: up.data.path, name: up.data.name, size: up.data.size, uploadedAt: up.data.uploadedAt });
    }
    if (added.length === 0) { setMemoImgBusy(null); return; }
    const next = [...memoImages(memo), ...added];
    const res = await MemoService.updateFields(memo.id, { images: next }, memo.updated_at);
    setMemoImgBusy(null);
    if (!res.ok) {
      // DB 반영 실패 → 방금 올린 파일이 어디에도 안 걸린 고아가 된다. 되돌린다.
      for (const a of added) { await AttachmentService.remove(a.path); }
      alert('이미지 저장 실패: ' + (res.msg || '')
        + '\n\n※ memos.images 컬럼이 없으면 migrations/v7.312-memo-images-shared-files.sql 을 먼저 실행해 주세요.'
        + '\n※ 다른 사람이 같은 메모를 수정 중이면 새로고침 후 다시 시도하세요.');
      return;
    }
    setMemos(ms => ms.map(m => m.id === memo.id ? res.data : m));
  };

  const removeMemoImage = async (memo, img) => {
    if (!window.confirm(`이미지 "${img.name}" 을(를) 삭제할까요?`)) return;
    const next = memoImages(memo).filter(x => x.path !== img.path);
    const res = await MemoService.updateFields(memo.id, { images: next }, memo.updated_at);
    if (!res.ok) { alert('삭제 실패: ' + (res.msg || '') + '\n\n다른 사람이 수정 중일 수 있습니다. 새로고침 후 다시 시도하세요.'); return; }
    await AttachmentService.remove(img.path);   // 404 는 성공 취급
    setMemos(ms => ms.map(m => m.id === memo.id ? res.data : m));
  };

  // ─── 메모(memos) CRUD ───
  const loadMemos = useCallback(async () => {
    if (!connected) return;
    setMemosLoading(true);
    const res = await MemoService.listActive();
    if (res.ok) setMemos(res.data);
    setMemosLoading(false);
  }, [connected]);

  // v7.215: 편집 상태는 ref로만 참조 — 의존성에 넣으면 타이핑마다 effect가 재실행되어
  //   loadMemos()가 매 글자마다 호출되고 목록이 통째로 교체된다 (스크롤 튐 + 불필요한 쿼리).
  const memoPollGuardRef = useRef({ editing: null, drafts: {}, newOpen: false });
  useEffect(() => {
    memoPollGuardRef.current = { editing: editingMemoId, drafts: memoDrafts, newOpen: newMemoOpen };
  }, [editingMemoId, memoDrafts, newMemoOpen]);

  // 페이지/탭 진입 시 메모 로드 + 10초 polling
  useEffect(() => {
    if (page !== 'txlog' || txSubTab !== 'memos' || !connected) return;
    loadMemos();
    const id = setInterval(() => {
      // 편집 중에는 폴링 일시 정지 (덮어쓰기 방지)
      const g = memoPollGuardRef.current;
      if (g.editing || Object.keys(g.drafts).length > 0 || g.newOpen) return;
      loadMemos();
    }, 10000);
    return () => clearInterval(id);
  }, [page, txSubTab, connected, loadMemos]);

  const addMemo = async () => {
    const content = newMemoContent.trim();
    if (!content) { alert('내용을 입력해주세요.'); return; }
    // v7.190: importance(별점) 함께 저장
    const res = await MemoService.create({ content, color: newMemoColor, author: newMemoAuthor, importance: newMemoImportance });
    if (!res.ok) { alert(res.msg); return; }
    let created = res.data;
    // v7.417: 작성 중 담아둔 첨부를 새 메모 id 로 업로드 후 images 반영 (기존 이미지 첨부와 동일 경로).
    if (newMemoFiles.length > 0) {
      const added = [];
      for (const f of newMemoFiles) {
        const up = await AttachmentService.uploadToDir(`memo_${created.id}`, f, newMemoAuthor || null);
        if (up.ok) added.push({ path: up.data.path, name: up.data.name, size: up.data.size, uploadedAt: up.data.uploadedAt });
        else console.warn('[새 메모 첨부] 업로드 실패:', f.name, up.error);
      }
      if (added.length > 0) {
        const upd = await MemoService.updateFields(created.id, { images: added }, created.updated_at);
        if (upd.ok) created = upd.data;
        else alert('메모는 저장됐지만 첨부 연결에 실패했습니다: ' + (upd.msg || '') + '\n\n※ 메모 카드의 첨부 버튼으로 다시 추가해 주세요.');
      }
    }
    setMemos(prev => [created, ...prev]);
    setNewMemoContent(''); setNewMemoColor('yellow'); setNewMemoAuthor(''); setNewMemoImportance(0); setNewMemoFiles([]); setNewMemoOpen(false);
  };

  const saveMemoEdit = async (memo) => {
    const draft = memoDrafts[memo.id];
    if (draft === undefined || draft === memo.content) {
      // 변경 없음
      setEditingMemoId(null);
      setMemoDrafts(d => { const nd = {...d}; delete nd[memo.id]; return nd; });
      return;
    }
    const res = await MemoService.updateContent(memo.id, draft, memo.updated_at);
    if (!res.ok) {
      alert('메모 수정 실패: 다른 사용자가 동시에 수정했습니다. 새로고침합니다.');
      loadMemos();
    } else if (res.data) {
      setMemos(prev => prev.map(m => m.id === memo.id ? res.data : m));
    }
    setEditingMemoId(null);
    setMemoDrafts(d => { const nd = {...d}; delete nd[memo.id]; return nd; });
  };

  // 메모 편집 진입 시: contenteditable의 innerHTML을 마크업→HTML로 설정 + focus + 커서 끝으로
  useEffect(() => {
    if (!editingMemoId) return;
    const el = memoEditRef.current;
    if (!el) return;
    const memo = memos.find(m => m.id === editingMemoId);
    if (!memo) return;
    el.innerHTML = markupToHtml(memo.content || '');
    // v7.215: 스크롤 튐 방지 — 현재 위치 기억 → preventScroll 포커스 → 복구
    const scrolls = captureScrolls(el);
    try { el.focus({ preventScroll: true }); } catch (e) { el.focus(); }
    // v7.196: 편집 진입 시 undo 스택 초기화 — 최초 상태를 스냅샷 0번으로
    memoUndoRef.current = {
      memoId: editingMemoId,
      stack: [{ html: el.innerHTML, caret: null }],
      idx: 0,
      lastPush: 0,
    };
    // 커서를 본문 끝으로 이동
    try {
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    } catch(e) {}
    // v7.215: selection 설정 후에도 밀릴 수 있어 즉시 + 다음 프레임 2회 복구
    restoreScrolls(scrolls);
    requestAnimationFrame(() => restoreScrolls(scrolls));
    // eslint-disable-next-line
  }, [editingMemoId]);

  // v7.196: 메모 편집기 undo 스냅샷 기록
  //   coalesce=true면 300ms 내 연속 타이핑을 한 덩어리로 묶는다 (글자마다 undo되면 답답함).
  //   볼드/색상/붙여넣기 같은 "한 방" 동작은 coalesce=false로 호출해 개별 되돌림 지점을 만든다.
  const pushMemoUndo = useCallback((coalesce = true) => {
    const el = memoEditRef.current;
    if (!el) return;
    const st = memoUndoRef.current;
    if (st.memoId !== editingMemoId) return;
    const html = el.innerHTML;
    if (st.idx >= 0 && st.stack[st.idx] && st.stack[st.idx].html === html) return;
    const now = Date.now();
    const snap = { html, caret: getCaretOffset(el) };
    if (coalesce && st.idx > 0 && now - st.lastPush < 300) {
      st.stack[st.idx] = snap; // 직전 스냅샷 교체 (연속 타이핑 병합)
    } else {
      st.stack = st.stack.slice(0, st.idx + 1);
      st.stack.push(snap);
      if (st.stack.length > 200) st.stack.shift(); // 메모리 상한
      st.idx = st.stack.length - 1;
    }
    st.lastPush = now;
  }, [editingMemoId]);

  // v7.196: undo(-1) / redo(+1) 적용
  const applyMemoUndo = useCallback((dir, memoId) => {
    const el = memoEditRef.current;
    if (!el) return false;
    const st = memoUndoRef.current;
    if (st.memoId !== memoId) return false;
    const next = st.idx + dir;
    if (next < 0 || next >= st.stack.length) return false;
    // 되돌리기 직전에 현재 상태를 마지막 칸에 반영해 둬야 redo가 최신 내용을 복원한다
    if (dir < 0 && st.idx === st.stack.length - 1) {
      st.stack[st.idx] = { html: el.innerHTML, caret: getCaretOffset(el) };
    }
    st.idx = next;
    const snap = st.stack[next];
    el.innerHTML = snap.html;
    setCaretOffset(el, snap.caret);
    st.lastPush = 0; // 복원 직후 타이핑은 병합하지 않음
    setMemoDrafts(d => ({ ...d, [memoId]: htmlToMarkup(snap.html) }));
    return true;
  }, []);

  const updateMemoField = async (memo, patch) => {
    // 색상, 핀 등 즉시 갱신 필드
    const res = await MemoService.updateFields(memo.id, patch, memo.updated_at);
    if (!res.ok) { loadMemos(); return; }
    if (res.data) {
      if ('pinned' in patch) {
        setMemos(prev => {
          const updated = prev.map(m => m.id === memo.id ? res.data : m);
          return [...updated].sort((a,b) => {
            if (a.pinned !== b.pinned) return b.pinned - a.pinned;
            const sa = a.sort_order ?? 9999, sb = b.sort_order ?? 9999;
            if (sa !== sb) return sa - sb;
            return (b.created_at || '').localeCompare(a.created_at || '');
          });
        });
      } else {
        setMemos(prev => prev.map(m => m.id === memo.id ? res.data : m));
      }
    }
  };

  const deleteMemo = async (memo) => {
    if (!window.confirm('이 메모를 삭제하시겠습니까?')) return;
    const res = await MemoService.remove(memo.id);
    if (!res.ok) { alert(res.msg); return; }
    setMemos(prev => prev.filter(m => m.id !== memo.id));
  };

  const completeMemo = async (memo) => {
    const res = await MemoService.complete(memo.id);
    if (!res.ok) {
      if (res.needsMigration) {
        alert('DB 마이그레이션이 필요합니다.\n\nSupabase SQL Editor에서 실행:\nALTER TABLE memos ADD COLUMN IF NOT EXISTS completed_at timestamptz DEFAULT NULL;');
      } else {
        alert('완료 처리 실패: ' + res.msg);
      }
      return;
    }
    setMemos(prev => prev.filter(m => m.id !== memo.id));
  };

  const restoreMemo = async (memo) => {
    const res = await MemoService.restore(memo.id);
    if (!res.ok) { alert(res.msg); return; }
    setCompletedMemos(prev => prev.filter(m => m.id !== memo.id));
    setMemos(prev => [...prev, { ...memo, completed_at: null }]);
  };

  const loadCompletedMemos = useCallback(async (monthDate) => {
    setCompletedMemosLoading(true);
    const res = await MemoService.listCompleted(monthDate);
    setCompletedMemos(res.ok ? res.data : []);
    setCompletedMemosLoading(false);
  }, []);
  // v7.450: 완료 메모 검색 실행
  const runMemoSearch = async () => {
    const q = memoSearchQuery.trim();
    if (!q) { setMemoSearchResults([]); setMemoSearched(false); return; }
    setMemoSearchLoading(true);
    const res = await MemoService.searchCompleted(q);
    setMemoSearchResults(res.ok ? res.data : []);
    setMemoSearched(true);
    setMemoSearchLoading(false);
  };

  // v7.416: 기록된 메모의 [내용 + 첨부]를 함께 다운로드.
  //   내용은 .txt 로, 첨부(images: 이미지·zip)는 각 파일을 순차 다운로드한다 (브라우저 다중 다운로드 차단 완화용 지연).
  //   ⚠ /attachments/ 는 앱과 동일 오리진이라 <a download> 로 파일명 지정 다운로드가 동작한다.
  const downloadMemoBundle = (m) => {
    if (!m) return;
    const stamp = (m.completed_at || m.updated_at || '').slice(0, 10) || 'memo';
    // 1) 내용 .txt
    try {
      const body = (m.content || '(빈 메모)');
      const blob = new Blob([body], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `메모_${stamp}_${String(m.id).slice(0,8)}.txt`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { console.warn('[메모 다운로드] 내용 실패:', e); }
    // 2) 첨부 순차 다운로드
    const atts = memoImages(m);
    atts.forEach((att, i) => {
      setTimeout(() => {
        // v7.444: 원본 파일명 유지 다운로드
        downloadKeepingName(AttachmentService.getPublicUrl(att.path), att.name || 'attachment');
      }, 350 * (i + 1));
    });
  };

  // 메모 색상 팔레트
  const memoColors = {
    yellow: { bg:'#fef3c7', border:'#fcd34d', accent:'#b45309', label:'노랑' },
    blue:   { bg:'#dbeafe', border:'#93c5fd', accent:'#1e40af', label:'파랑' },
    pink:   { bg:'#fce7f3', border:'#f9a8d4', accent:'#9d174d', label:'분홍' },
    green:  { bg:'#d1fae5', border:'#6ee7b7', accent:'#065f46', label:'초록' },
    gray:   { bg:'#e5e7eb', border:'#9ca3af', accent:'#374151', label:'회색' },
  };

  // 메모 드래그 앤 드롭 — 위치 교환(swap) 방식: A와 B의 sort_order를 서로 맞바꿈
  // v7.51: 메모 드래그앤드롭 — swap 방식 (두 메모만 자리 교체, 사용자 요청)
  // v7.50의 insert 방식은 칸반 UI 표준이지만 메모 보드 사용 패턴과 맞지 않음
  // sort_order 일관성 유지: 첫 드래그 시 전체 메모에 sort_order 초기 부여 → 이후 swap만
  const reorderMemos = async (dragId, dropId) => {
    if (dragId === dropId) return;
    // v7.81: swap 방식 폐기 → insert(splice) 방식으로 회귀
    //         (v7.35에서 성능 목적으로 swap으로 바꿨으나 두 결함 발견)
    //         (a) 비인접 이동 시 두 메모만 자리 바뀜, 사이 메모들은 그대로 → 사용자 기대와 어긋남
    //         (b) 우연히 같은 sort_order를 가지면 swap해도 값 동일 → 위치 안 바뀜
    //              → "가끔 안 되는 현상"의 정체
    //         insert 방식으로 splice 후 sort_order를 10단위로 전면 재할당하면
    //         겹침 없음 + 예측 가능한 결과. 성능 손실은 미미 (메모 수십 개 수준)
    setMemos(prev => {
      const fromIdx = prev.findIndex(m => m.id === dragId);
      const toIdx = prev.findIndex(m => m.id === dropId);
      if (fromIdx < 0 || toIdx < 0) return prev;
      const draggedMemo = prev[fromIdx];
      const targetMemo = prev[toIdx];
      // pinned 그룹 넘나드는 드래그는 무시 (그룹은 위치가 강제)
      if (!!draggedMemo.pinned !== !!targetMemo.pinned) return prev;
      // splice 이동 (v7.83: 앞→뒤 방향 splice 편향 수정)
      //   splice로 제거하면 뒤 인덱스가 당겨져서 dropId의 새 인덱스에 삽입하면 dropId "위"에 놓임 → 원위치
      //   해결: 앞→뒤 이동일 때만 dropId "다음"(newToIdx + 1) 위치에 삽입
      const isForward = fromIdx < toIdx;
      const working = prev.map(m => ({ ...m })); // 얕은 복사
      const [moved] = working.splice(fromIdx, 1);
      const newToIdx = working.findIndex(m => m.id === dropId);
      const insertAt = isForward ? newToIdx + 1 : newToIdx;
      working.splice(insertAt, 0, moved);
      // 그룹별로 sort_order 10단위 전면 재할당 (겹침 없음 보장)
      const pinned = working.filter(m => m.pinned);
      const unpinned = working.filter(m => !m.pinned);
      pinned.forEach((m, i) => { m.sort_order = (i + 1) * 10; });
      unpinned.forEach((m, i) => { m.sort_order = (i + 1) * 10; });
      const sorted = [...pinned, ...unpinned];
      // DB 업데이트 — 변경 있는 그룹만 (드래그된 메모가 속한 그룹)
      const changedGroup = draggedMemo.pinned ? pinned : unpinned;
      MemoService.updateSortOrders(changedGroup.map(m => ({ id: m.id, sort_order: m.sort_order })));
      return sorted;
    });
  };

  // v7.81: 배송시트지 row 재정렬
  //   ・ 같은 transaction_date(같은 날짜 그룹) 내에서만 허용
  //   ・ 다른 날짜로 이동은 자동 이월 로직에 맡김 (사용자가 다른 날로 옮기려면 이월 처리 사용)
  //   ・ 드래그된 row가 속한 그룹의 sort_order를 10단위로 전면 재할당 (겹침 없음 보장)
  const reorderShipRow = async (dragId, dropId) => {
    if (!dragId || !dropId || dragId === dropId) return;
    const dragRow = logItems.find(x => x.id === dragId);
    const dropRow = logItems.find(x => x.id === dropId);
    if (!dragRow || !dropRow) return;
    // 다른 날짜 그룹으로 이동은 무시 (soft block)
    if (dragRow.transaction_date !== dropRow.transaction_date) return;
    const groupDate = dragRow.transaction_date;
    // 현재 UI 순서(정렬된 logItems)에서 이 날짜 그룹만 추출
    const groupRows = logItems.filter(x => x.transaction_date === groupDate);
    const fromIdx = groupRows.findIndex(x => x.id === dragId);
    const toIdx = groupRows.findIndex(x => x.id === dropId);
    if (fromIdx < 0 || toIdx < 0) return;
    // insert 방식: splice 후 삽입 (v7.83: 앞→뒤 방향 splice 편향 수정 — reorderMemos와 동일 로직)
    const isForward = fromIdx < toIdx;
    const working = groupRows.map(r => ({ ...r }));
    const [moved] = working.splice(fromIdx, 1);
    const newToIdx = working.findIndex(x => x.id === dropId);
    const insertAt = isForward ? newToIdx + 1 : newToIdx;
    working.splice(insertAt, 0, moved);
    // 10단위 sort_order 재할당
    working.forEach((r, i) => { r.sort_order = (i + 1) * 10; });
    // logItems 상태 갱신: 그룹 내 row를 재정렬된 순서로 교체
    setLogItems(prev => {
      const newList = [];
      let workingIdx = 0;
      for (const row of prev) {
        if (row.transaction_date === groupDate) {
          newList.push(working[workingIdx++]);
        } else {
          newList.push(row);
        }
      }
      return newList;
    });
    // DB 업데이트 — 그룹 전체 병렬 UPDATE
    await Promise.all(working.map(r =>
      supabase.from('transaction_log').update({ sort_order: r.sort_order }).eq('id', r.id)
    ));
  };

  // v7.174: 자동이월 팝오버에서 [적용] 클릭 시 실행
  //   선택 날짜로 해당 row의 transaction_date를 즉시 이동.
  //   자동이월 flag는 유지 (그 날짜 이후에도 매일 이월).
  const applyDeferDate = useCallback(async () => {
    if (!deferPopover || !deferPopoverDate) return;
    if (deferPopoverDate === deferPopover.currentDate) {
      setDeferPopover(null); // 변화 없음
      return;
    }
    setDeferSaving(true);
    try {
      const { error } = await supabase.from('transaction_log')
        .update({ transaction_date: deferPopoverDate })
        .eq('id', deferPopover.rowId);
      if (error) {
        alert('이월 실패: ' + (error.message || String(error)));
        setDeferSaving(false);
        return;
      }
      setDeferPopover(null);
      setDeferSaving(false);
      loadTxLog();
    } catch (e) {
      alert('이월 실패: ' + (e.message || String(e)));
      setDeferSaving(false);
    }
  }, [deferPopover, deferPopoverDate, loadTxLog]);

  // note.autoDefer===true 이고 미출고 상태인 row 중 transaction_date < today 인 것들의
  // transaction_date 를 today 로 갱신. 출고완료(fulfilled)되면 자동으로 멈춤.
  // v7.62: 자정 정확히 트리거 + 페이지 무관 sweep (다른 페이지에 있어도 자정에 작동)
  const sweepAutoDefer = useCallback(async () => {
    const todayISO = today();
    // 지난 30일치 outbound 행 가져와서 검토 (현재 보고 있는 기간과 무관하게 sweep)
    const cutoff = new Date(); cutoff.setDate(cutoff.getDate() - 30);
    // v7.126: UTC 버그 방지 — 로컬 날짜 문자열 직접 생성
    const cutoffISO = `${cutoff.getFullYear()}-${String(cutoff.getMonth()+1).padStart(2,'0')}-${String(cutoff.getDate()).padStart(2,'0')}`;
    const { data } = await supabase.from('transaction_log')
      .select('id,note,transaction_date,updated_at')
      .eq('type','outbound')
      .gte('transaction_date', cutoffISO)
      .lt('transaction_date', todayISO);
    if (!data || data.length === 0) return 0;
    const candidates = data.filter(r => {
      if (!r.note) return false;
      let p; try { p = JSON.parse(r.note); } catch(e){ return false; }
      return !!p.autoDefer && !p.fulfilled;
    });
    if (candidates.length === 0) return 0;
    await Promise.allSettled(candidates.map(row =>
      supabase.from('transaction_log')
        .update({ transaction_date: todayISO })
        .eq('id', row.id)
        .eq('updated_at', row.updated_at)
    ));
    // sweep 후 현재 페이지가 txlog면 화면 갱신
    if (page === 'txlog') loadTxLog();
    return candidates.length;
  }, [page, loadTxLog]);

  // v7.62: 자정 정확히 트리거 — 다음 자정까지의 ms 계산 후 setTimeout
  //   ・ 1시간 폴링 대신 자정 정확히 sweep (오전 9시에 이월 안 되어 있던 버그 fix)
  //   ・ page 제약 제거 — 다른 페이지에 있어도 자정에 작동 (DB 갱신만, UI는 txlog 진입 시 반영)
  //   ・ visibility change — 탭이 다시 활성화될 때도 sweep (예: 노트북 sleep 후 깨움)
  //   ・ 페이지 진입 시 즉시 sweep (기존 유지)
  // v7.111: 자동 이월 실행 시각 커스텀 (autoDeferHour) — 기본 0 (자정)
  //   ・ setTimeout 트리거 시각 = 매일 autoDeferHour 시 + 30초
  //   ・ 접속/visibility catch-up sweep은 `now.getHours() >= autoDeferHour` 인 경우에만
  //     (지정 시각 전에 로그인하면 sweep 대기, 지정 시각 지난 후 로그인하면 즉시 catch-up)
  // v7.112: 30분 단위 확장 — autoDeferMinutes(0-1439) 기반, H/M 분리 처리
  useEffect(() => {
    if (!connected) return;
    const hh = Math.floor(autoDeferMinutes / 60);
    const mm = autoDeferMinutes % 60;
    // 1. 접속 시 catch-up — 지정 시각이 이미 지났을 때만
    const shouldCatchUp = () => {
      const now = new Date();
      return (now.getHours() * 60 + now.getMinutes()) >= autoDeferMinutes;
    };
    if (shouldCatchUp()) sweepAutoDefer();

    // 2. 지정 시각 정확히 트리거 — chained setTimeout (매일 재계산)
    let timeoutId = null;
    const scheduleNextRun = () => {
      const now = new Date();
      const next = new Date(now);
      next.setHours(hh, mm, 30, 0); // 지정 시각 + 30초 마진
      if (next.getTime() <= now.getTime()) {
        next.setDate(next.getDate() + 1); // 오늘 지정 시각 이미 지남 → 내일
      }
      const msUntilRun = next.getTime() - now.getTime();
      timeoutId = setTimeout(async () => {
        await sweepAutoDefer();
        scheduleNextRun();
      }, msUntilRun);
    };
    scheduleNextRun();

    // 3. 탭이 다시 보일 때 (노트북 깨움, 탭 전환 복귀) — 지정 시각 지난 경우만
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && shouldCatchUp()) sweepAutoDefer();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // eslint-disable-next-line
  }, [connected, autoDeferMinutes]);

  // ─── 자동 이월 토글: 모달에서 호출 ───
  const toggleAutoDefer = async (item, shipInfo) => {
    const newVal = !shipInfo.autoDefer;
    const { data: latest, error: fetchErr } = await supabase.from('transaction_log')
      .select('note, updated_at').eq('id', item.id).single();
    if (fetchErr || !latest) { alert('자동 이월 설정 실패: 행을 찾을 수 없습니다'); return; }
    let p; try { p = JSON.parse(latest.note); } catch(e){ p = {}; }
    if (p.fulfilled) { alert('이미 출고완료된 건은 자동 이월을 설정할 수 없습니다.'); return; }
    if (newVal) p.autoDefer = true; else delete p.autoDefer;
    const newNote = JSON.stringify(p);
    const { error } = await supabase.from('transaction_log')
      .update({ note: newNote })
      .eq('id', item.id)
      .eq('updated_at', latest.updated_at);
    if (error) {
      alert('자동 이월 설정 실패: 다른 사용자가 동시에 수정했습니다. 새로고침 후 다시 시도해주세요.');
      loadTxLog();
      return;
    }
    setLogModalItem({ ...item, note: newNote });
    // 켜기 시 즉시 sweep 실행 (transaction_date < today 였다면 바로 today 로 이동)
    if (newVal) { await sweepAutoDefer(); }
    loadTxLog();
  };


  // ── 입출고 요약 모달: 데이터 로드 ──
  // 모달이 열렸을 때 + summaryMode/Offset 변경 시 그 기간의 transaction_log 전체를 로드
  useEffect(() => {
    if (!showTxLogSummary) return;
    let cancelled = false;
    (async () => {
      setSummaryLoading(true);
      const range = getRangeForMode(summaryMode, summaryOffset);
      const { data } = await supabase.from('transaction_log')
        .select('id,serial,model,type,dealer_name,note,transaction_date,created_at')
        .gte('transaction_date', range.start).lte('transaction_date', range.end)
        .order('created_at', { ascending: true })
        .limit(5000); // 안전 상한
      if (!cancelled) {
        setSummaryRows(data || []);
        setSummaryLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [showTxLogSummary, summaryMode, summaryOffset]);

  // ── 백그라운드 자동 동기화 (4명 동시 작업용) ──
  // txlog 페이지에 머무는 동안 10초마다 silent reload.
  // 단, 사용자가 모달을 열고 있거나(logModalItem, editingTxItem, refundingId)
  // 메모/송장/금액을 편집 중이거나, 검색 중일 때는 일시 정지하여
  // 입력 중인 텍스트나 모달 상태를 깨뜨리지 않음.
  useEffect(() => {
    if (page !== 'txlog') return;
    const paused = !!logModalItem || !!editingTxItem || !!refundingId || !!outboundModalOpen
      || Object.keys(logMemos).length > 0
      || Object.keys(logTrackingDrafts).length > 0
      || Object.keys(logPriceDrafts).length > 0;
    if (paused) return;
    const id = setInterval(() => {
      if (logModalItem || editingTxItem || refundingId || outboundModalOpen
          || Object.keys(logMemos).length > 0
          || Object.keys(logTrackingDrafts).length > 0
          || Object.keys(logPriceDrafts).length > 0) return;
      loadTxLog();
    }, 10000); // 10초
    return () => clearInterval(id);
  }, [page, logModalItem, editingTxItem, refundingId, outboundModalOpen, logMemos, logTrackingDrafts, logPriceDrafts, loadTxLog]);

  // 출고 처리(stock_out) 페이지에서도 활성 요청이 있을 때 5초마다 재고 모델 동기화
  useEffect(() => {
    if (page !== 'stock_out' || !activeRequest) return;
    const id = setInterval(() => { loadAllModels(); }, 5000);
    return () => clearInterval(id);
  }, [page, activeRequest, loadAllModels]);

  // v7.55: 방문수령 완료 처리 — reserved 시리얼들을 shipped로 일괄 변환
  //   배경: 방문수령 출고는 1단계(시리얼 미리 찍기) + 2단계(고객 가져감) 분리
  //         1단계는 commitRequest의 isPickupMode 분기에서 reserved로 저장
  //         2단계는 이 함수가 담당 — reserved → shipped + fulfilledSerials로 이동
  const completePickupRequest = async (item) => {
    let noteObj = {};
    try { noteObj = JSON.parse(item.note); } catch(e){}
    // reservedSerials 수집 (items 배열 또는 단일)
    let allReserved = [];
    if (Array.isArray(noteObj.items)) {
      noteObj.items.forEach(it => {
        if (Array.isArray(it.reservedSerials)) allReserved.push(...it.reservedSerials);
      });
    } else if (Array.isArray(noteObj.reservedSerials)) {
      allReserved = noteObj.reservedSerials;
    }
    if (allReserved.length === 0) {
      alert('예약된 시리얼이 없습니다. 먼저 시리얼을 등록해주세요.');
      return;
    }
    // EXT-/NOSN- 제외하고 inventory에서 shipped로 변환
    const realSerials = allReserved.filter(sn => !sn.startsWith('EXT-') && !sn.startsWith('NOSN-'));
    const failed = [];
    for (const sn of realSerials) {
      const res = await InventoryService.completePickupBySerial(sn);
      if (!res.ok) failed.push(sn);
    }
    // transaction_log: reservedSerials → fulfilledSerials로 이동, fulfilled=true 마킹
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!latest) { alert('요청을 찾을 수 없습니다.'); return; }
    let latestNote = {};
    try { latestNote = JSON.parse(latest.note); } catch(e){}
    let updatedItems;
    if (Array.isArray(latestNote.items)) {
      updatedItems = latestNote.items.map(it => {
        const fulfilled = [...(it.fulfilledSerials || []), ...(it.reservedSerials || [])];
        return { ...it, fulfilledSerials: fulfilled, reservedSerials: [] };
      });
    } else {
      updatedItems = null;
    }
    const allComplete = updatedItems
      ? updatedItems.every(it => (it.fulfilledSerials?.length || 0) >= it.qty)
      : true;
    const updatedNote = JSON.stringify({
      ...latestNote,
      ...(updatedItems ? { items: updatedItems } : {
        fulfilledSerials: [...(latestNote.fulfilledSerials || []), ...(latestNote.reservedSerials || [])],
        reservedSerials: [],
      }),
      fulfilled: allComplete,
      completedAt: allComplete ? new Date().toISOString() : null,
    });
    await supabase.from('transaction_log')
      .update({ note: updatedNote })
      .eq('id', item.id)
      .eq('updated_at', latest.updated_at);
    if (failed.length > 0) {
      alert(`방문수령 완료. 단, ${failed.length}개 시리얼은 상태 변경 실패 (이미 다른 상태): ${failed.slice(0,3).join(', ')}${failed.length>3?' 외':''}`);
    }
    loadTxLog();
  };

  // Cancel / Return handlers for txlog
  // v7.247: selectedSerials — 부분 환불용. 생략하면 남은 전량 (기존 호출부 그대로 동작)
  const handleTxAction = async (item, actionType, reason, selectedSerials = null) => {
    const label = actionType === 'cancel' ? '주문취소' : '환불';
    // 이중 처리 방지 — 1차: 클라이언트 캐시 체크
    try { const p = JSON.parse(item.note); if(p.processedAs) { alert(`이미 ${p.processedAs === 'cancel' ? '주문취소' : '환불'} 처리된 건입니다.`); return; } } catch(e){}
    // 환불은 출고 완료된 건만 허용
    let noteObj = {};
    try { noteObj = JSON.parse(item.note); } catch(e){}
    if (actionType === 'return' && !noteObj.fulfilled) {
      alert('출고 완료된 건만 환불 처리할 수 있습니다.');
      return;
    }
    if (actionType === 'return' && !reason?.trim()) {
      alert('환불 사유를 입력해주세요.');
      return;
    }
    if (actionType === 'cancel' && !window.confirm(`[${item.model}] 주문취소 처리하시겠습니까?`)) return;
    setLoading(true);
    // ── 2차 체크: DB에서 최신 상태 재확인 (다른 사용자가 그 사이 처리했을 수 있음) ──
    const { data: latest } = await supabase.from('transaction_log')
      .select('note,updated_at').eq('id', item.id).single();
    if (!latest) { alert('요청을 찾을 수 없습니다.'); setLoading(false); loadTxLog(); return; }
    let latestNoteObj = {};
    try { latestNoteObj = JSON.parse(latest.note); } catch(e){}
    if (latestNoteObj.processedAs) {
      alert(`⚠ 다른 사용자가 이미 ${latestNoteObj.processedAs === 'cancel' ? '주문취소' : '환불'} 처리했습니다.`);
      setLoading(false);
      setLogModalItem(null);
      setRefundingId(null);
      loadTxLog();
      return;
    }
    // 환불 시 fulfilled 상태도 재확인
    if (actionType === 'return' && !latestNoteObj.fulfilled) {
      alert('⚠ 출고 상태가 변경되었습니다. 새로고침 후 다시 시도해주세요.');
      setLoading(false);
      loadTxLog();
      return;
    }
    // v7.157: 데모 대여 fromDemoLend 감지 — 취소/환불 시 대여 상태 원복
    //   ・ 취소 (pending 상태, 아직 미출고): cancelPendingRental → items 삭제 + equipment '리버' 원복 + rental 삭제
    //   ・ 환불 (fulfilled 상태, 이미 active): 이 흐름은 이례적. 사용자가 회수 처리로 진행하는 게 정석.
    //     하지만 방어적으로 시리얼 복귀 skip은 하고 (데모는 inventory 관리 대상 아님) alert만.
    const isFromDemo = !!latestNoteObj.fromDemoLend;
    const demoRentalId = latestNoteObj.demoRentalId;
    if (isFromDemo && demoRentalId && actionType === 'cancel' && !latestNoteObj.fulfilled) {
      try {
        const res = await DemoService.cancelPendingRental({ rentalId: demoRentalId });
        if (!res.ok) console.warn('데모 대여 취소 실패 (pending 아닐 수 있음):', res.msg);
      } catch (e) { console.warn('데모 대여 취소 예외:', e); }
    }
    // 환불/취소인 경우: 출고된(shipped) + 예약된(reserved) 시리얼들 모두 inventory에서 in_stock 복귀
    // v7.55: 방문수령 예약 상태에서 취소되거나, 출고완료 후 환불되는 두 케이스 모두 복귀 가능
    // v7.157: 데모 대여는 inventory 관리 대상이 아니므로 restore 스킵 (별개 demo_equipment 관리)
    // ── v7.247: 환불 대상 시리얼 확정 ──────────────────────────────
    //   이미 환불된 건 제외하고, 지정이 없으면 남은 전량을 대상으로 한다.
    const alreadyRefunded = refundedSetOf(latestNoteObj);
    const fulfilledAll = listFulfilledSerials(latestNoteObj).map(x => x.serial);
    const remaining = fulfilledAll.filter(sn => !alreadyRefunded.has(sn));
    let targetSerials = remaining;
    if (actionType === 'return' && fulfilledAll.length > 0) {
      if (Array.isArray(selectedSerials) && selectedSerials.length > 0) {
        // 화면이 보낸 목록이라도 DB 최신값 기준으로 한 번 더 거른다 (그 사이 처리됐을 수 있음)
        targetSerials = selectedSerials.filter(sn => remaining.includes(sn));
      }
      if (targetSerials.length === 0) {
        alert('환불할 시리얼이 없습니다.\n(이미 전부 환불되었거나 화면이 오래된 상태입니다)');
        setLoading(false); loadTxLog(); return;
      }
    } else if (actionType === 'return') {
      // 시리얼 기록이 없는 옛 출고 건 — 되돌릴 대상은 없지만 상태 마킹은 되어야 한다
      targetSerials = [];
    }
    // 남은 것을 전부 환불하면 = 전량 환불 → 기존 동작과 동일하게 processedAs 를 붙인다
    //   시리얼 기록이 아예 없는 건도 '전량'으로 본다 (v7.246 이전 동작 유지)
    const isFullRefund = fulfilledAll.length === 0
      || (alreadyRefunded.size + targetSerials.length) >= fulfilledAll.length;

    let restoreFailed = [];
    if ((actionType === 'return' || actionType === 'cancel') && !isFromDemo) {
      let allSerials = [];
      let allReserved = [];
      if (actionType === 'return') {
        // v7.247: 환불은 선택분만 되돌린다. 예약분은 전량 환불일 때만 함께 정리.
        allSerials = [...targetSerials];
        if (isFullRefund) {
          if (Array.isArray(latestNoteObj.items)) {
            latestNoteObj.items.forEach(it => {
              if (Array.isArray(it.reservedSerials)) allReserved.push(...it.reservedSerials);
            });
          } else if (Array.isArray(latestNoteObj.reservedSerials)) {
            allReserved = [...latestNoteObj.reservedSerials];
          }
        }
      } else if (Array.isArray(latestNoteObj.items)) {
        latestNoteObj.items.forEach(it => {
          if (Array.isArray(it.fulfilledSerials)) allSerials.push(...it.fulfilledSerials);
          if (Array.isArray(it.reservedSerials)) allReserved.push(...it.reservedSerials);
        });
      } else {
        if (Array.isArray(latestNoteObj.fulfilledSerials)) allSerials = latestNoteObj.fulfilledSerials;
        if (Array.isArray(latestNoteObj.reservedSerials)) allReserved = latestNoteObj.reservedSerials;
      }
      // 외부/면제 제외
      const filterFn = (sn) => !sn.startsWith('EXT-') && !sn.startsWith('NOSN-');
      allSerials = allSerials.filter(filterFn);
      allReserved = allReserved.filter(filterFn);
      // restoreToStock은 'shipped'와 'reserved' 둘 다 처리 (v7.55에서 .in 변경)
      for (const sn of [...allSerials, ...allReserved]) {
        const res = await InventoryService.restoreToStock(sn, { note: `[${actionType === 'return' ? '환불' : '취소'}] ${fmtDate(today())} ${reason || ''}`.trim() });
        if (!res.ok) restoreFailed.push(sn);
      }
    }
    // v7.425: 주문취소 시 아직 출고 전(pending)인 시리얼도 ship_hold 해제 + 서버 pending 삭제.
    //   취소는 대개 출고 전에 하므로 pending 이 남아 있는 경우가 많다. 위 복귀 로직은 fulfilled/reserved
    //   시리얼만 다루므로 ship_hold 로 잠긴 pending 을 놓친다(v7.424 회귀).
    if (actionType === 'cancel' && !isFromDemo) {
      try {
        const reqIdForPending = latestNoteObj.reqId || item.serial;
        if (reqIdForPending) {
          const pend = await ShipPendingService.loadByReqId(reqIdForPending);
          if (pend.ok && pend.data?.length) {
            for (const p of pend.data) {
              const sn = p.serial;
              if (sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-')) {
                await InventoryService.releaseHoldBySerial(sn).catch(() => {});
              }
            }
          }
          await ShipPendingService.clearByReqId(reqIdForPending).catch(() => {});
        }
      } catch (e) { console.warn('pending/ship_hold 정리 실패:', e); }
    }
    // ── 원본 행 마킹 — updated_at 락으로 동시 처리 차단 ──
    const nextNoteObj = { ...latestNoteObj };
    if (actionType === 'return') {
      // v7.247: 누적 환불 목록 + 이력. 부분이면 processedAs 를 붙이지 않는다
      //   → 행은 '출고 완료'로 남고, 집계에서 환불분 수량만 빠진다.
      nextNoteObj.refundedSerials = Array.from(new Set([...alreadyRefunded, ...targetSerials]));
      nextNoteObj.refunds = [
        ...(Array.isArray(latestNoteObj.refunds) ? latestNoteObj.refunds : []),
        { at: new Date().toISOString(), reason: reason.trim(), serials: [...targetSerials] },
      ];
      if (isFullRefund) {
        nextNoteObj.processedAs = 'return';
        nextNoteObj.processedAt = new Date().toISOString();
        nextNoteObj.refundReason = reason.trim();
      }
    } else {
      nextNoteObj.processedAs = actionType;
      nextNoteObj.processedAt = new Date().toISOString();
    }
    const markedNote = JSON.stringify(nextNoteObj);
    const { data: markResult } = await supabase.from('transaction_log')
      .update({ note: markedNote })
      .eq('id', item.id)
      .eq('updated_at', latest.updated_at)
      .select('id');
    if (!markResult || markResult.length === 0) {
      alert('⚠ 처리 중 다른 사용자가 이 요청을 변경했습니다. 화면을 새로고침합니다.');
    } else if (restoreFailed.length > 0) {
      alert(`환불 처리 완료. 단, ${restoreFailed.length}개 시리얼은 재고 복귀 실패 (이미 다른 상태): ${restoreFailed.slice(0,3).join(', ')}${restoreFailed.length>3?' 외':''}`);
    } else if (actionType === 'return' && !isFullRefund) {
      // v7.247: 부분 환불은 결과를 명시한다 — 행이 '출고 완료'로 남아 헷갈릴 수 있으므로
      const done = alreadyRefunded.size + targetSerials.length;
      alert(`부분 환불 완료 — ${targetSerials.length}개 재고 복귀\n\n이 요청의 환불 진행: ${done} / ${fulfilledAll.length}개\n남은 ${fulfilledAll.length - done}개는 출고 상태로 유지됩니다.`);
    }
    setLogModalItem(null);
    setRefundingId(null);
    setRefundReason('');
    setRefundSel([]);
    setLoading(false);
    loadTxLog();
  };

  // v7.77: DOA 반품 시리얼 필수화 + 실제 처리 로직 분리
  //         handleRepairComplete: 진입점. DOA인데 반품 시리얼 없으면 모달로 강제 입력받음
  //         doRepairComplete: 실제 DB 처리 (모달에서도 재사용)
  const handleRepairComplete = async (item, shipInfo) => {
    const isDoa = shipInfo.emsType === 'DOA';
    const returnSerial = (shipInfo.rmaReturnSerial || '').trim();
    // DOA인데 반품 시리얼 아직 없으면 → 모달 열어서 필수 입력받기
    if (isDoa && !returnSerial) {
      setDoaReturnSerialModal({ item, shipInfo, inputSerial: '', submitting: false });
      return;
    }
    // 반품 시리얼이 있거나 RMA면 바로 처리
    await doRepairComplete(item, shipInfo, returnSerial);
  };

  const doRepairComplete = async (item, shipInfo, returnSerialArg) => {
    const sn = shipInfo.rmaSerial;
    const fromDB = !!shipInfo.rmaFromDB;
    const isDoa = shipInfo.emsType === 'DOA';
    const returnSerial = (returnSerialArg || shipInfo.rmaReturnSerial || '').trim();
    const hasReturn = isDoa && !!returnSerial;
    // v7.100: 외부 DOA (원 시리얼이 DB 미등록) — 원 시리얼은 완전 무시, 반품 시리얼만 clean fresh stock으로 등록
    const isExternalDoa = isDoa && !fromDB;
    // 상황별 confirm 메시지
    let msg;
    if (hasReturn && isExternalDoa) {
      // v7.100: 외부 DOA — "판매불가 상태 유지" 표현 제거 (외부는 unsellable 상태 자체가 없음)
      msg = `외부 EMS(DOA) 수리완료 처리:\n\n• 원 시리얼 ${sn || '-'}: 외부 시리얼 (재고 미등록) — 처리 없음\n• 반품 시리얼 ${returnSerial}: 신품으로 재고 등록 (source=DOA)\n• 본 EMS 기록: [수리완료] 마킹\n\n진행하시겠습니까?`;
    } else if (hasReturn) {
      msg = `DOA 수리완료 처리:\n\n• 원 시리얼 ${sn || '-'}: 판매불가 상태 유지 (벤더 회수됨)\n• 반품 시리얼 ${returnSerial}: 신품으로 재고 등록 (source=DOA)\n• 본 EMS 기록: [수리완료] 마킹\n\n진행하시겠습니까?`;
    } else if (fromDB) {
      msg = `시리얼 ${sn} 의 수리가 완료되어 재고로 복귀시키시겠습니까?\n\n• inventory 상태: 판매불가 → 재고\n• 본 EMS 기록은 [수리완료] 상태로 마킹됩니다.`;
    } else {
      msg = `이 EMS 건을 수리완료 처리하시겠습니까?\n\n(외부 제품이므로 재고 변경 없이 기록만 [수리완료]로 마킹됩니다.)`;
    }
    if (!window.confirm(msg)) return false;
    setLoading(true);
    // 1a. RMA 경로: DB 매칭 원 시리얼이면 판매불가 → 재고 복구
    //     (DOA 반품 케이스는 원 시리얼 복구 안 함 — 회수된 상태 유지)
    if (fromDB && sn && !isDoa) {
      const res = await InventoryService.repairCompleteBySerial(sn, {
        note: `[${shipInfo.emsType || 'RMA'} 수리완료] ${fmtDate(today())}`,
      });
      if (!res.ok) {
        alert('재고 상태 복구 실패: ' + (res.msg || '시리얼이 판매불가 상태가 아닙니다'));
        setLoading(false); return false;
      }
    }
    // 1b. DOA 반품 시리얼 신품 등록 (반품 시리얼이 있을 때만)
    if (hasReturn) {
      // v7.78: 카탈로그 사전 검증 + 이름 정규화
      //         inventory.product_name은 products.product_name FK 제약이 있어서
      //         카탈로그에 없는 이름으로 INSERT하면 실패함. 미리 검증하고 canonical 이름 사용.
      let catalog = null;
      try {
        catalog = await ProductService.findExact(shipInfo.rmaProductName)
               || (await ProductService.findCaseInsensitive(shipInfo.rmaProductName, 1))[0];
      } catch(e) { /* 카탈로그 조회 실패 시 아래에서 안내 */ }
      if (!catalog) {
        alert(`반품 시리얼 등록 실패\n\n제품명 "${shipInfo.rmaProductName}" 이(가) 카탈로그(제품 표준화 관리)에 등록되지 않았습니다.\n\n먼저 "${shipInfo.rmaProductName}" 을(를) 카탈로그에 등록한 뒤 다시 시도하세요.`);
        setLoading(false); return false;
      }
      // 정규화된 canonical 이름 + 카테고리 사용
      const canonicalName = catalog.product_name;
      const category = catalog.category || null;
      // v7.101: 반품 시리얼의 note에 원 시리얼을 항상 포함 (외부/DB DOA 무관) — 역추적용.
      // v7.250: 표기를 손으로 쓰던 형식에 맞춰 화살표로 바꿨다.
      //         '이전시리얼 → 바뀐시리얼' 이 한눈에 읽히고, 재고 검색창에 옛 시리얼을 넣으면
      //         교체된 신품이 그대로 걸린다. ('DOA 전시리얼' 문구도 남겨 기존 검색 습관 유지)
      const invNote = `[DOA 교체] ${sn || '-'} → ${returnSerial}`
        + ` (DOA 전시리얼: ${sn || '-'}${shipInfo.rmaTicketNo ? ' / 티켓: ' + shipInfo.rmaTicketNo : ''}`
        + `${shipInfo.rmaFormNo ? ' / 양식: ' + shipInfo.rmaFormNo : ''})`;
      // v7.254: note 는 감사용 컬럼이고, 재고목록 [비고] 칸이 보여주는 건 memo 다.
      //   v7.101~v7.250 이 note 에만 써서 화면에는 아무것도 안 보였다.
      const { error: insErr } = await supabase.from('inventory').insert({
        product_name: canonicalName,
        serial_number: returnSerial,
        category,
        status: 'in_stock',
        stock_type: '신품',
        source: 'DOA',
        receipt_date: today(),
        note: invNote,
        memo: `[DOA 교체] ${sn || '-'} → ${returnSerial}`,
      });
      if (insErr) {
        // 에러 유형별 안내 개선
        const errMsg = insErr.message || String(insErr);
        let userMsg;
        if (/inventory_pkey|duplicate key|already exists/i.test(errMsg)) {
          userMsg = `반품 시리얼 "${returnSerial}" 은(는) 이미 재고에 등록되어 있습니다.\n다른 시리얼을 입력하거나, 재고 목록에서 기존 항목을 확인하세요.`;
        } else if (/foreign key|fkey/i.test(errMsg)) {
          userMsg = `제품 참조 오류: "${canonicalName}" 이(가) 카탈로그에서 사라졌거나 이름이 변경됐을 수 있습니다.\n\n원본 에러: ${errMsg}`;
        } else {
          userMsg = `반품 시리얼 신품 등록 실패: ${errMsg}`;
        }
        alert(userMsg);
        setLoading(false); return false;
      }
    }
    // 1c. v7.250: 원(나가는) 시리얼에도 교체 사실을 남긴다.
    //     지금까지는 반품 시리얼 쪽에만 기록이 있어서, 재고 목록에서 옛 시리얼을 보면
    //     '판매불가'인 이유와 어디로 바뀌었는지를 알 수 없었다 (그래서 손으로 적고 계셨다).
    //     ⚠ 재고에 있는 건(fromDB)만 대상. 외부 DOA의 원 시리얼은 재고에 없다.
    //     ⚠ 기존 비고는 지우지 않고 뒤에 덧붙인다 — 손으로 적어둔 맥락이 사라지면 안 된다.
    if (hasReturn && fromDB && sn) {
      try {
        const { data: oldRow } = await supabase.from('inventory')
          .select('note, memo').eq('serial_number', sn).single();
        const mark = `[DOA 교체] ${sn} → ${returnSerial}${shipInfo.rmaTicketNo ? ' / 티켓: ' + shipInfo.rmaTicketNo : ''} (${fmtDate(today())})`;
        const patch = {};
        // note — 감사용
        const prevNote = (oldRow?.note || '').trim();
        // 같은 교체를 두 번 적지 않는다 (수리완료를 다시 눌러도 중복 누적 방지)
        if (!prevNote.includes(`→ ${returnSerial}`)) {
          patch.note = prevNote ? `${prevNote}\n${mark}` : mark;
        }
        // v7.254: memo — 재고목록 [비고] 칸이 실제로 보여주는 컬럼.
        //   손으로 적어둔 내용은 지우지 않고 뒤에 덧붙인다.
        const prevMemo = (oldRow?.memo || '').trim();
        if (!prevMemo.includes(`→ ${returnSerial}`)) {
          patch.memo = prevMemo ? `${prevMemo} / ${mark}` : mark;
        }
        if (Object.keys(patch).length > 0) {
          await supabase.from('inventory').update(patch).eq('serial_number', sn);
        }
      } catch (e) {
        // 비고 기록 실패가 수리완료 자체를 막지는 않는다
        console.warn('[DOA] 원 시리얼 비고 기록 실패:', e);
      }
    }
    // 2. transaction_log의 note 업데이트 (repaired + rmaReturnSerial 반영)
    let noteObj = {};
    try { noteObj = JSON.parse(item.note); } catch(e){}
    noteObj.repaired = true;
    noteObj.repairedAt = new Date().toISOString();
    if (hasReturn) noteObj.rmaReturnSerial = returnSerial; // 모달로 입력한 경우 반영
    const updated = JSON.stringify(noteObj);
    await supabase.from('transaction_log').update({ note: updated }).eq('id', item.id);
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated } : rw));
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
    setLoading(false);
    // 완료 알림
    let successMsg = '수리완료 처리되었습니다.';
    if (hasReturn && isExternalDoa) {
      // v7.100: 외부 DOA 전용 알림 — 원 시리얼 재고 변경 없음 명시
      successMsg = `외부 EMS(DOA) 수리완료 처리되었습니다.\n\n시리얼 교체: ${sn || '-'} → ${returnSerial}\n반품 시리얼이 신품으로 재고에 등록되었습니다.\n(원 시리얼은 외부 건이라 재고 변경 없음)`;
    } else if (hasReturn) {
      // v7.250: 교체 관계를 앞에 세워 보여준다 (양쪽 비고에도 같은 문구가 남는다)
      successMsg += `\n\n시리얼 교체: ${sn || '-'} → ${returnSerial}\n반품 시리얼이 신품으로 재고에 등록되었고,\n원 시리얼 비고에도 교체 내역이 기록되었습니다.`;
    } else if (fromDB && !isDoa) {
      successMsg += '\n재고가 정상으로 복귀되었습니다.';
    }
    alert(successMsg);
    return true;
  };

  // ─── v7.86: 출고완료 시리얼 사후 교체 ──────────────────────────────
  //   시나리오: 이미 출고 완료된 배송 건에서 실제로 나간 시리얼과 기록이 다를 때
  //   흐름:
  //     ① 새 시리얼 검증: 재고 목록에 존재 + status='in_stock' + 같은 모델 (엄격)
  //     ② inventory: 기존 시리얼 shipped → in_stock (restoreToStock)
  //     ③ inventory: 새 시리얼 in_stock → shipped (shipBySerial)
  //     ④ 실패 시 롤백: 기존 시리얼 다시 shipped로 (best-effort)
  //     ⑤ transaction_log.note JSON 의 items[itemIdx].fulfilledSerials 배열 교체
  //        (optimistic lock: updated_at 매치)
  //   scope: fulfilledSerials 만 (v7.86). reservedSerials/RMA/DOA는 스코프 밖.
  const openSerialEdit = (log, itemIdx, oldSerial, productName) => {
    setSerialEditState({ log, itemIdx, oldSerial, productName });
    setSerialEditInput('');
    setSerialEditCheck(null);
  };
  const closeSerialEdit = () => {
    setSerialEditState(null);
    setSerialEditInput('');
    setSerialEditCheck(null);
  };
  const saveSerialSwap = async () => {
    if (!serialEditState || !serialEditCheck?.ok || serialEditBusy) return;
    const { log, itemIdx, oldSerial, productName } = serialEditState;
    const newSerial = serialEditInput.trim().toUpperCase();
    if (!newSerial || newSerial === oldSerial) return;
    setSerialEditBusy(true);

    // shipInfo 추출 (dealerName/saleDate 재사용)
    let noteObj = {};
    try { noteObj = JSON.parse(log.note || '{}'); } catch(e){}
    const dealerName = noteObj.recipient || log.dealer_name || '';
    const saleDate   = log.transaction_date || today(); // v7.126: UTC 버그 방지

    // ① 기존 시리얼 in_stock 복귀
    const restoreRes = await InventoryService.restoreToStock(oldSerial, {
      note: `[v7.86 시리얼 교체] ${oldSerial} → ${newSerial}`,
    });
    if (!restoreRes.ok) {
      setSerialEditBusy(false);
      alert(`기존 시리얼(${oldSerial}) 복귀 실패: ${restoreRes.conflict ? '이미 다른 상태 (재고/판매불가 등)' : (restoreRes.msg || '알 수 없는 오류')}`);
      return;
    }

    // ② 새 시리얼 shipped 처리
    const shipRes = await InventoryService.shipBySerial(newSerial, {
      dealerName, saleDate,
      note: `[v7.86 시리얼 교체] 기존 ${oldSerial} 대체`,
    });
    if (!shipRes.ok) {
      // 롤백: 기존 시리얼 다시 shipped로
      await InventoryService.shipBySerial(oldSerial, {
        dealerName, saleDate,
        note: `[v7.86 롤백] 새 시리얼 처리 실패로 복원`,
      });
      setSerialEditBusy(false);
      alert(`새 시리얼(${newSerial}) 출고 처리 실패 — 기존 시리얼 상태 복원됨\n(원인: ${shipRes.conflict ? '이미 재고가 아님' : '알 수 없는 오류'})`);
      return;
    }

    // ③ transaction_log.note 교체 (optimistic lock)
    const { data: latest } = await supabase.from('transaction_log')
      .select('note,updated_at').eq('id', log.id).single();
    if (!latest) {
      setSerialEditBusy(false);
      alert('트랜잭션 조회 실패 — inventory는 변경되었으나 로그가 갱신되지 않음. 새로고침 후 확인 필요.');
      return;
    }
    let latestNote = {};
    try { latestNote = JSON.parse(latest.note || '{}'); } catch(e){}
    if (Array.isArray(latestNote.items)) {
      latestNote.items = latestNote.items.map((it, idx) => {
        if (idx !== itemIdx) return it;
        return {
          ...it,
          fulfilledSerials: (it.fulfilledSerials || []).map(s => s === oldSerial ? newSerial : s),
        };
      });
    }
    // note 최상위에 편집 이력 (감사용, 최대 20건까지만)
    const history = Array.isArray(latestNote.serialSwaps) ? latestNote.serialSwaps : [];
    history.push({
      at: new Date().toISOString(),
      itemIdx, oldSerial, newSerial, productName,
    });
    latestNote.serialSwaps = history.slice(-20);

    const updatedNote = JSON.stringify(latestNote);
    const { error } = await supabase.from('transaction_log')
      .update({ note: updatedNote })
      .eq('id', log.id)
      .eq('updated_at', latest.updated_at);

    if (error) {
      setSerialEditBusy(false);
      alert(`로그 갱신 실패 (동시 수정 충돌 가능): ${error.message}\ninventory는 이미 교체되었으므로 새로고침 후 상태 확인 필요.`);
      return;
    }

    // ④ UI state 리프레시
    setLogItems(prev => prev.map(rw => rw.id === log.id ? { ...rw, note: updatedNote } : rw));
    setLogModalItem(prev => (prev && prev.id === log.id) ? { ...prev, note: updatedNote } : prev);
    // v7.89: 사용량 트래킹
    AnalyticsService.trackAction('시리얼_교체', log.processed_by || '', {
      logId: log.id, oldSerial, newSerial, productName,
    });
    setSerialEditBusy(false);
    closeSerialEdit();
  };
  // 실시간 검증 (300ms debounce)
  useEffect(() => {
    if (!serialEditState) return;
    const input = serialEditInput.trim().toUpperCase();
    if (!input) { setSerialEditCheck(null); return; }
    if (input === serialEditState.oldSerial) {
      setSerialEditCheck({ found: true, error: '기존 시리얼과 동일합니다.' });
      return;
    }
    const t = setTimeout(async () => {
      const inv = await InventoryService.findBySerial(input);
      if (!inv) {
        setSerialEditCheck({ found: false, msg: '재고 목록에 없는 시리얼입니다.' });
        return;
      }
      if (inv.status !== 'in_stock') {
        const statusKr = inv.status === 'shipped' ? '출고됨' : inv.status === 'reserved' ? '예약중' : inv.status === 'unsellable' ? '판매불가' : inv.status;
        setSerialEditCheck({ found: true, status: inv.status, product_name: inv.product_name, error: `현재 상태: ${statusKr} — 재고(in_stock) 상태여야 교체 가능` });
        return;
      }
      if (inv.product_name !== serialEditState.productName) {
        setSerialEditCheck({ found: true, status: 'in_stock', product_name: inv.product_name, error: `다른 모델 시리얼입니다: ${inv.product_name}` });
        return;
      }
      setSerialEditCheck({ found: true, status: 'in_stock', product_name: inv.product_name, ok: true });
    }, 300);
    return () => clearTimeout(t);
  }, [serialEditInput, serialEditState]);

  // v7.15: RMA/DOA 행의 출고 완료/대기 토글 (note.fulfilled flag만 변경, 재고는 건드리지 않음)
  // RMA는 보통 이미 외부에 보낸 시리얼이라 inventory 상태 영향 없음 — UI 표시 flag만 토글
  const toggleRmaFulfilled = async (item, shipInfo) => {
    const newFulfilled = !shipInfo.fulfilled;
    // optimistic lock
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!latest) { alert('항목을 찾을 수 없습니다.'); return; }
    let noteObj = {};
    try { noteObj = JSON.parse(latest.note || '{}'); } catch(e){}
    noteObj.fulfilled = newFulfilled;
    if (newFulfilled) {
      if (!noteObj.fulfilledAt) noteObj.fulfilledAt = new Date().toISOString();
    } else {
      delete noteObj.fulfilledAt;
    }
    const updatedNote = JSON.stringify(noteObj);
    const { error } = await supabase.from('transaction_log').update({ note: updatedNote }).eq('id', item.id).eq('updated_at', latest.updated_at);
    if (error) { alert('변경 실패: ' + error.message + '\n(다른 사용자가 동시에 수정 중일 수 있음)'); return; }
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote } : rw));
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updatedNote } : prev);
  };

  // ─── v7.249 → v7.251: 출고완료 ↔ 출고요청 토글 ─────────────────────
  //   용도: **출고하지 않았는데 실수로 [제품 출고]를 누른 경우** 되돌리기.
  //   그래서 되돌려도 찍어둔 시리얼을 버리지 않는다. 다시 켤 때 재스캔이 없어야 한다.
  //
  //   완료 → 요청 : 재고를 되돌리고, 시리얼은 note.revertedItems 에 통째로 보관
  //   요청 → 완료 : revertedItems 가 있으면 **그 시리얼 그대로** 재출고 (재스캔 없음)
  //                 없으면(진짜 첫 출고) [제품 출고] 모달을 연다
  //
  //   ⚠ 방문수령(pickup)은 시리얼이 reservedSerials 에 있고 재고가 'reserved' 다.
  //     v7.249 는 fulfilledSerials 만 봐서 예약분을 놓쳤다 → 양쪽 다 다룬다.
  const collectShipSerials = (note) => {
    // [{ idx, fulfilled:[], reserved:[] }] — 품목별로 나눠 담아야 되돌릴 때 원위치 복원이 된다
    if (Array.isArray(note.items)) {
      return note.items.map((it, idx) => ({
        idx,
        fulfilled: Array.isArray(it.fulfilledSerials) ? [...it.fulfilledSerials] : [],
        reserved: Array.isArray(it.reservedSerials) ? [...it.reservedSerials] : [],
      }));
    }
    return [{
      idx: 0,
      fulfilled: Array.isArray(note.fulfilledSerials) ? [...note.fulfilledSerials] : [],
      reserved: Array.isArray(note.reservedSerials) ? [...note.reservedSerials] : [],
    }];
  };
  const isRealSerial = (sn) => !sn.startsWith('EXT-') && !sn.startsWith('NOSN-');

  const toggleShipFulfilled = async (item, shipInfo) => {
    if (shipInfo.processedAs) { alert('이미 취소/환불 처리된 건은 상태를 바꿀 수 없습니다.'); return; }
    if (refundedSetOf(shipInfo).size > 0) {
      alert('환불 이력이 있는 건은 상태를 바꿀 수 없습니다.\n\n환불된 시리얼은 이미 재고로 복귀했습니다.');
      return;
    }

    // ── 요청 → 완료 ────────────────────────────────────────────────
    if (!shipInfo.fulfilled) {
      const saved = Array.isArray(shipInfo.revertedItems) ? shipInfo.revertedItems : null;
      const savedCount = saved
        ? saved.reduce((n, g) => n + g.fulfilled.length + g.reserved.length, 0) : 0;
      if (!saved || savedCount === 0) {
        // 되돌린 적 없는 건 = 어떤 시리얼이 나갈지 모른다 → 스캔 화면으로
        await loadActiveRequest(shipInfo.reqId || item.serial, item.model);
        setOutboundModalOpen(true);
        return;
      }
      // 되돌렸던 시리얼 그대로 재출고
      const flat = saved.flatMap(g => [...g.fulfilled, ...g.reserved]).filter(isRealSerial);
      if (!window.confirm(
        `출고요청 → 출고완료 로 되돌립니다.\n\n` +
        `직전에 찍어둔 시리얼 ${flat.length}개를 그대로 출고 처리합니다:\n` +
        `${flat.slice(0,8).join(', ')}${flat.length>8?` 외 ${flat.length-8}개`:''}\n\n` +
        `재스캔은 필요 없습니다. 계속하시겠습니까?`)) return;

      setLoading(true);
      const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
      if (!latest) { setLoading(false); alert('항목을 찾을 수 없습니다.'); return; }
      let noteObj = {};
      try { noteObj = JSON.parse(latest.note || '{}'); } catch (e) {}
      if (noteObj.fulfilled || noteObj.processedAs) {
        setLoading(false); alert('그 사이 다른 사용자가 이 건을 처리했습니다. 새로고침합니다.'); loadTxLog(); return;
      }
      const dealer = noteObj.recipient || noteObj.requester || '';
      const failed = [];
      for (const g of saved) {
        for (const sn of g.fulfilled) {
          if (!isRealSerial(sn)) continue;
          const r = await InventoryService.shipBySerial(sn, { dealerName: dealer, note: `[출고 재적용] ${fmtDate(today())}` });
          if (!r.ok) failed.push(sn);
        }
        for (const sn of g.reserved) {
          if (!isRealSerial(sn)) continue;
          const r = await InventoryService.reserveBySerial(sn, { dealerName: dealer, note: `[예약 재적용] ${fmtDate(today())}` });
          if (!r.ok) failed.push(sn);
        }
      }
      // 시리얼을 원래 품목 자리로 되돌린다
      if (Array.isArray(noteObj.items)) {
        noteObj.items = noteObj.items.map((it, idx) => {
          const g = saved.find(x => x.idx === idx);
          return g ? { ...it, fulfilledSerials: [...g.fulfilled], reservedSerials: [...g.reserved] } : it;
        });
      } else {
        const g = saved[0] || { fulfilled: [], reserved: [] };
        noteObj.fulfilledSerials = [...g.fulfilled];
        // 원래 없던 키는 만들지 않는다 (구형식 note 를 그대로 유지)
        if (g.reserved.length > 0 || Array.isArray(noteObj.reservedSerials)) {
          noteObj.reservedSerials = [...g.reserved];
        }
      }
      noteObj.fulfilled = true;
      noteObj.fulfilledAt = new Date().toISOString();
      delete noteObj.revertedItems;
      delete noteObj.revertedAt;
      // v7.252: 데모 대여면 대여 상태도 다시 승격 (pending → active, 장비 → 대여중)
      if (noteObj.fromDemoLend && noteObj.demoRentalId) {
        try {
          const r = await DemoService.finalizeRental({
            rentalId: noteObj.demoRentalId,
            by: noteObj.author || noteObj.requester || '미지정',
          });
          if (!r.ok) console.warn('데모 대여 finalize 실패:', r.msg);
        } catch (e) { console.warn('데모 대여 finalize 예외:', e); }
      }
      const updatedNote = JSON.stringify(noteObj);
      const { error } = await supabase.from('transaction_log')
        .update({ note: updatedNote }).eq('id', item.id).eq('updated_at', latest.updated_at);
      setLoading(false);
      if (error) { alert('처리 실패: ' + error.message + '\n(다른 사용자가 동시에 수정 중일 수 있음)'); loadTxLog(); return; }
      if (failed.length > 0) {
        alert(`출고완료로 되돌렸습니다.\n단, ${failed.length}개 시리얼은 재고 상태 변경 실패 (그 사이 다른 곳에서 사용됨): ${failed.slice(0,3).join(', ')}${failed.length>3?' 외':''}\n\n재고 목록에서 확인해주세요.`);
      }
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote } : rw));
      setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updatedNote } : prev);
      loadTxLog();
      return;
    }

    // ── 완료 → 요청 (되돌리기) ──────────────────────────────────────
    const groups = collectShipSerials(shipInfo);
    const flatFul = groups.flatMap(g => g.fulfilled).filter(isRealSerial);
    const flatRes = groups.flatMap(g => g.reserved).filter(isRealSerial);
    const total = flatFul.length + flatRes.length;
    const preview = [...flatFul, ...flatRes];
    if (!window.confirm(
      `출고완료 → 출고요청 으로 되돌립니다.\n\n` +
      (total > 0
        ? `시리얼 ${total}개가 재고로 복귀합니다:\n${preview.slice(0,8).join(', ')}${preview.length>8?` 외 ${preview.length-8}개`:''}\n\n`
        : '복귀할 재고 시리얼은 없습니다.\n\n') +
      (shipInfo.fromDemoLend
        ? `데모 대여도 함께 되돌립니다:\n・ 대여 상태 → 예약(pending)\n・ 장비 위치 → 대여예약\n\n`
        : '') +
      `찍어둔 시리얼은 보관되므로, 다시 [출고요청]을 누르면\n재스캔 없이 그대로 출고 처리됩니다.\n\n계속하시겠습니까?`)) return;

    setLoading(true);
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!latest) { setLoading(false); alert('항목을 찾을 수 없습니다.'); return; }
    let noteObj = {};
    try { noteObj = JSON.parse(latest.note || '{}'); } catch (e) {}
    if (noteObj.processedAs || (Array.isArray(noteObj.refundedSerials) && noteObj.refundedSerials.length)) {
      setLoading(false); alert('그 사이 다른 사용자가 이 건을 처리했습니다. 새로고침합니다.'); loadTxLog(); return;
    }

    // v7.252: 데모 대여는 **가장 먼저** 되돌린다.
    //   회수 완료된 대여처럼 되돌리면 안 되는 경우가 있는데, 재고를 먼저 건드리면
    //   그때 중단해도 이미 반쯤 풀린 상태가 된다. 실패하면 아무것도 안 바뀐 채로 끝난다.
    if (noteObj.fromDemoLend && noteObj.demoRentalId) {
      const r = await DemoService.revertRental({
        rentalId: noteObj.demoRentalId,
        by: noteObj.author || noteObj.requester || '미지정',
      });
      if (!r.ok) {
        setLoading(false);
        alert('데모 대여를 되돌릴 수 없어 중단했습니다.\n\n' + r.msg + '\n\n재고와 출고 상태는 그대로입니다.');
        return;
      }
    }

    const failed = [];
    for (const sn of flatFul) {
      const r = await InventoryService.restoreToStock(sn, { note: `[출고 되돌림] ${fmtDate(today())}` });
      if (!r.ok) failed.push(sn);
    }
    for (const sn of flatRes) {
      // 방문수령 예약분 — reserved → in_stock
      const r = await InventoryService.releaseReservation(sn, { note: `[출고 되돌림] ${fmtDate(today())}` });
      if (!r.ok) failed.push(sn);
    }

    // 시리얼을 통째로 보관하고 목록에서만 비운다 (재스캔 없이 되살리기 위함)
    noteObj.revertedItems = collectShipSerials(noteObj);
    noteObj.revertedAt = new Date().toISOString();
    noteObj.fulfilled = false;
    delete noteObj.fulfilledAt;
    if (Array.isArray(noteObj.items)) {
      noteObj.items = noteObj.items.map(it => ({ ...it, fulfilledSerials: [], reservedSerials: [] }));
    } else {
      if (Array.isArray(noteObj.fulfilledSerials)) noteObj.fulfilledSerials = [];
      if (Array.isArray(noteObj.reservedSerials)) noteObj.reservedSerials = [];
    }
    const updatedNote = JSON.stringify(noteObj);
    const { error } = await supabase.from('transaction_log')
      .update({ note: updatedNote }).eq('id', item.id).eq('updated_at', latest.updated_at);
    setLoading(false);
    if (error) { alert('되돌리기 실패: ' + error.message + '\n(다른 사용자가 동시에 수정 중일 수 있음)'); loadTxLog(); return; }
    if (failed.length > 0) {
      alert(`출고요청으로 되돌렸습니다.\n단, ${failed.length}개 시리얼은 재고 복귀 실패 (이미 다른 상태): ${failed.slice(0,3).join(', ')}${failed.length>3?' 외':''}`);
    }
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote } : rw));
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updatedNote } : prev);
    loadTxLog();
  };

  // v7.259: loadRevertedIntoPending 제거 — 배너(유일한 호출자)를 없앴다.
  //   보관분 주입은 loadActiveRequest 안에서만 일어난다 (v7.258).
  // v7.258: 자동 주입은 loadActiveRequest 안으로 옮겼다 (useEffect 방식은 덮어쓰기 경합).

  // v7.397: 배송건 삭제 확인 문구 (note 상태별) — 데스크톱/모바일 공유
  const deleteShipmentWarning = (item) => {
    let noteObj = {};
    try { noteObj = JSON.parse(item.note); } catch(e){}
    const wasFulfilled = !!noteObj.fulfilled;
    const isProcessed = !!noteObj.processedAs;
    const isFromDemo = !!noteObj.fromDemoLend;
    return wasFulfilled && !isProcessed
      ? `⚠ 이 건은 출고완료 상태입니다.\n삭제하면 재고가 in_stock으로 복귀됩니다.\n\n정말 삭제하시겠습니까?`
      : isFromDemo && !wasFulfilled
      ? `이 건은 데모 대여 예약입니다.\n삭제하면 예약된 대여도 함께 취소되고 장비가 다시 대여 가능 상태로 돌아갑니다.\n\n삭제하시겠습니까?`
      : `이 출고 기록을 영구 삭제하시겠습니까?\n(되돌릴 수 없습니다)`;
  };
  // v7.397: 배송건 삭제 코어 — 데모취소 → 재고복귀 → DB삭제. [확인·UI갱신은 호출측 책임].
  //   데스크톱(handleDeleteShipment)과 모바일(MobileTxLog onDeleteShipment)이 이 함수를 공유한다.
  //   ⚠ 출고 전용이다. 입고는 재고 처리가 반대(추가된 시리얼 회수)라 별도 경로를 써야 한다.
  //   반환: { ok, restoreFailed[], error? }
  const deleteShipmentCore = async (item) => {
    let noteObj = {};
    try { noteObj = JSON.parse(item.note); } catch(e){}
    const wasFulfilled = !!noteObj.fulfilled;
    const isProcessed = !!noteObj.processedAs;
    // v7.151: 데모 대여 pending 감지 — 삭제 시 대여도 함께 취소 (UX 일관)
    const isFromDemo = !!noteObj.fromDemoLend;
    const demoRentalId = noteObj.demoRentalId;
    // v7.151: 데모 대여 pending 취소 (fulfilled 되기 전에만) — 실패해도 삭제는 진행
    if (isFromDemo && demoRentalId && !wasFulfilled) {
      try {
        const res = await DemoService.cancelPendingRental({ rentalId: demoRentalId });
        if (!res.ok) console.warn('데모 대여 취소 실패:', res.msg);
      } catch (e) { console.warn('데모 대여 취소 예외:', e); }
    }
    // v7.425: 아직 출고 전(pending)인 시리얼 정리 — ship_hold 해제 + 서버 pending 삭제.
    //   ⚠ v7.424 부터 '담기' 가 inventory 를 ship_hold 로 잠그므로, fulfilled 되기 전에 배송건을 삭제하면
    //     시리얼이 ship_hold 로 묶인 채 재고에서 사라진다(사용자 리포트). 아래 복귀 로직은 wasFulfilled
    //     일 때만 돌아서 이 경로를 못 막았다.
    //   reqId 는 note.reqId(없으면 item.serial)가 ship_pending_serials 의 키다.
    try {
      const reqIdForPending = noteObj.reqId || item.serial;
      if (reqIdForPending) {
        const pend = await ShipPendingService.loadByReqId(reqIdForPending);
        if (pend.ok && pend.data?.length) {
          for (const p of pend.data) {
            const sn = p.serial;
            if (sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-')) {
              await InventoryService.releaseHoldBySerial(sn).catch(() => {});
            }
          }
        }
        await ShipPendingService.clearByReqId(reqIdForPending).catch(() => {});
      }
    } catch (e) { console.warn('pending/ship_hold 정리 실패:', e); }
    // 출고완료 + 미처리 상태였다면 시리얼들 inventory 복귀
    // v7.153: 데모 대여는 inventory 관리 대상이 아니므로 restore 스킵 (별개 demo_equipment 테이블 관리)
    let restoreFailed = [];
    if (wasFulfilled && !isProcessed && !isFromDemo) {
      let allSerials = [];
      if (Array.isArray(noteObj.items)) {
        noteObj.items.forEach(it => { if (Array.isArray(it.fulfilledSerials)) allSerials.push(...it.fulfilledSerials); });
      } else if (Array.isArray(noteObj.fulfilledSerials)) {
        allSerials = noteObj.fulfilledSerials;
      }
      allSerials = allSerials.filter(sn => !sn.startsWith('EXT-') && !sn.startsWith('NOSN-'));
      for (const sn of allSerials) {
        const res = await InventoryService.restoreToStock(sn, { note: `[삭제 복귀] ${fmtDate(today())}` });
        if (!res.ok) restoreFailed.push(sn);
      }
    }
    const { error } = await supabase.from('transaction_log').delete().eq('id', item.id);
    if (error) return { ok: false, error: error.message, restoreFailed };
    return { ok: true, restoreFailed };
  };
  const handleDeleteShipment = async (item) => {
    if (!window.confirm(deleteShipmentWarning(item))) return;
    setLoading(true);
    const res = await deleteShipmentCore(item);
    if (!res.ok) {
      alert('삭제 실패: ' + res.error);
      setLoading(false);
      return;
    }
    if (res.restoreFailed.length > 0) {
      alert(`기록은 삭제했으나 ${res.restoreFailed.length}개 시리얼은 재고 복귀 실패: ${res.restoreFailed.slice(0,3).join(', ')}${res.restoreFailed.length>3?' 외':''}`);
    }
    setLogModalItem(null);
    setRefundingId(null);
    setLoading(false);
    loadTxLog();
  };

  const saveMemo = async (item, memo) => {
    // 동시 작업 안전성: DB에서 최신 note를 다시 읽어서 머지
    // (다른 사용자가 그 사이 다른 필드를 바꿨을 수 있음)
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    const updated = JSON.stringify({ ...noteObj, memo });
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      // 충돌: 다른 사용자가 동시 변경 — 한번 더 시도
      const { data: retry } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
      let retryNote = {}; try { retryNote = JSON.parse(retry?.note || '{}'); } catch(e){}
      const updatedRetry = JSON.stringify({ ...retryNote, memo });
      await supabase.from('transaction_log').update({ note: updatedRetry }).eq('id', item.id);
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedRetry } : rw));
    } else {
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated, updated_at: r[0].updated_at } : rw));
    }
    setLogMemos(prev => { const n = {...prev}; delete n[item.id]; return n; });
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
  };

  // v7.6: 입고 행에서 시리얼 더블클릭 인라인 편집 → 커밋
  // 1) inventory.serial_number rename (PK 변경)
  // 2) transaction_log.note JSON 안 items[].serials 동기 업데이트 (optimistic lock)
  const commitInboundSerialEdit = async (item, productName, idx, oldSerial, newSerialRaw) => {
    const newSerial = (newSerialRaw || '').trim();
    setEditingInboundSerial(null);
    if (!newSerial || newSerial === oldSerial) return;
    // 1. inventory PK 변경
    const invRes = await InventoryService.renameSerial(oldSerial, newSerial);
    if (!invRes.ok) { alert('시리얼 변경 실패: ' + invRes.msg); return; }
    // 2. transaction_log note 안 items[].serials 동기 업데이트
    const { data: row } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!row) return;
    let info = {};
    try { info = JSON.parse(row.note); } catch(e){}
    if (!Array.isArray(info.items)) return;
    const updatedItems = info.items.map(it => {
      if (it.product_name !== productName) return it;
      const newSerials = [...(it.serials || [])];
      newSerials[idx] = newSerial;
      return { ...it, serials: newSerials };
    });
    const updatedNote = JSON.stringify({ ...info, items: updatedItems });
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updatedNote })
      .eq('id', item.id)
      .eq('updated_at', row.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      alert('⚠ 다른 사용자가 이 입고 기록을 동시에 수정했습니다. 화면을 새로고침합니다.');
      loadTxLog();
      return;
    }
    // UI 갱신
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote, updated_at: r[0].updated_at } : rw));
  };

  // v7.15: 입고 시리얼 추가 — prompt로 새 시리얼 받아서 inventory INSERT + transaction_log items[].serials 동기화
  const addInboundSerial = async (item, productName) => {
    const newRaw = window.prompt(`[${productName}] 추가할 시리얼을 입력하세요\n(빈값 또는 취소 시 등록 안 됨)`);
    if (!newRaw) return;
    const newSerial = newRaw.trim();
    if (!newSerial) return;
    // 중복 체크
    const existing = await InventoryService.findExistingSerials([newSerial]);
    if (existing.size > 0) { alert(`이미 등록된 시리얼: ${newSerial}`); return; }
    // 1. inventory INSERT — 기존 입고 row의 meta 가져오기
    let info = {};
    try { info = JSON.parse(item.note); } catch(e){}
    const sourceItem = (info.items || []).find(it => it.product_name === productName);
    const insertRow = {
      serial_number: newSerial,
      product_name: productName,
      stock_type: sourceItem?.stockType || '신품',
      status: 'in_stock',
      source: sourceItem?.from || info.from || 'hq',
      warehouse: 'hq',
      receipt_date: item.transaction_date,
      note: '시리얼 추가 (' + item.id + ')',
    };
    const invRes = await InventoryService.bulkInsert([insertRow]);
    if (!invRes.ok) { alert('시리얼 등록 실패: ' + invRes.msg); return; }
    // 2. transaction_log items[].serials 업데이트 + qty 증가
    const { data: row } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!row) return;
    let cur = {};
    try { cur = JSON.parse(row.note); } catch(e){}
    if (!Array.isArray(cur.items)) return;
    const updatedItems = cur.items.map(it => {
      if (it.product_name !== productName) return it;
      const serials = [...(it.serials || []), newSerial];
      return { ...it, serials, qty: serials.length };
    });
    const updatedNote = JSON.stringify({ ...cur, items: updatedItems });
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updatedNote }).eq('id', item.id).eq('updated_at', row.updated_at).select('id,updated_at');
    if (!r || r.length === 0) {
      alert('⚠ 다른 사용자가 이 입고를 동시 수정했습니다. 새로고침합니다.');
      loadTxLog();
      return;
    }
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote, updated_at: r[0].updated_at } : rw));
  };

  // v7.235: 입고일 수정 — transaction_log.transaction_date 와 해당 시리얼들의
  //   inventory.receipt_date 를 **함께** 갱신한다.
  //   한쪽만 고치면 배송시트지 날짜와 재고 목록 입고일이 어긋나 더 헷갈린다.
  const [recvDateSaving, setRecvDateSaving] = useState(false);
  const updateInboundDate = async (item, newDate) => {
    if (!newDate || newDate === item.transaction_date) return;
    let note = {};
    try { note = JSON.parse(item.note || '{}'); } catch (e) {}
    // 이 입고에 포함된 실제 시리얼만 수집 (외부/면제 항목은 inventory row가 없음)
    const serials = (Array.isArray(note.items) ? note.items : [])
      .flatMap(it => Array.isArray(it.serials) ? it.serials : [])
      .filter(sn => sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-'));

    const msg = `입고일을 ${fmtDate(item.transaction_date)} → ${fmtDate(newDate)} 로 변경합니다.\n\n` +
      `・ 배송시트지 날짜\n` +
      (serials.length ? `・ 재고 ${serials.length}개의 입고일\n` : '') +
      `\n계속하시겠습니까?`;
    if (!window.confirm(msg)) return;

    setRecvDateSaving(true);
    try {
      const { error: txErr } = await supabase.from('transaction_log')
        .update({ transaction_date: newDate }).eq('id', item.id);
      if (txErr) throw txErr;

      if (serials.length) {
        const { error: invErr } = await supabase.from('inventory')
          .update({ receipt_date: newDate, updated_at: new Date().toISOString() })
          .in('serial_number', serials);
        // 재고 갱신 실패는 치명적이지 않으므로 경고만 (배송시트지 날짜는 이미 바뀜)
        if (invErr) {
          console.error('재고 입고일 갱신 실패:', invErr);
          alert(`배송시트지 날짜는 변경됐지만 재고 입고일 갱신에 실패했습니다.\n\n${invErr.message || invErr.code}\n\n재고 목록에서 개별 수정이 필요합니다.`);
        }
      }
      setLogModalItem(prev => (prev && prev.id === item.id ? { ...prev, transaction_date: newDate } : prev));
      await loadTxLog();
    } catch (e) {
      alert('입고일 변경 실패\n\n' + (e.message || String(e)));
    }
    setRecvDateSaving(false);
  };

  // v7.421: 입고 출처(source) 수정 — transaction_log.note(items[].from + 단일품목이면 최상위 from)
  //   와 해당 시리얼들의 inventory.source 를 **함께** 갱신한다.
  //   ⚠ inventory.source 는 재고 대시보드 출처 집계(taiwan/paju/hq/기타)의 근거라
  //     note 만 고치면 배송시트지 출처와 재고 집계가 어긋난다 → updateInboundDate 와 동일하게 양쪽 갱신.
  const updateInboundSource = async (item, productName, newRaw) => {
    const newSource = (newRaw || '').trim();
    setEditingInboundSource(null);
    let note = {};
    try { note = JSON.parse(item.note || '{}'); } catch (e) {}
    if (!Array.isArray(note.items)) return;
    const targetItem = note.items.find(it => it.product_name === productName);
    if (!targetItem) return;
    const oldSource = ((targetItem.from ?? note.from) || '');
    if (newSource === oldSource) return;
    // 이 품목의 실제 시리얼만 (외부/시리얼면제 항목은 inventory row 없음)
    const serials = (Array.isArray(targetItem.serials) ? targetItem.serials : [])
      .filter(sn => sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-'));

    const msg = `출처를 "${oldSource || '(없음)'}" → "${newSource || '(없음)'}" 로 변경합니다.\n\n` +
      `・ 배송시트지 입고 출처\n` +
      (serials.length ? `・ 재고 ${serials.length}개의 출처\n` : '') +
      `\n계속하시겠습니까?`;
    if (!window.confirm(msg)) return;

    setInboundSourceSaving(true);
    try {
      const updatedItems = note.items.map(it =>
        it.product_name === productName ? { ...it, from: newSource || null } : it);
      const newNote = { ...note, items: updatedItems };
      // 단일 품목 입고면 최상위 from 도 동기화 (HDD/SSD 입고는 항상 단일 품목)
      if (note.items.length === 1) newNote.from = newSource || null;
      const updatedNote = JSON.stringify(newNote);
      // 낙관적 동시성 — 다른 함수들과 동일하게 updated_at 가드
      const { data: row } = await supabase.from('transaction_log')
        .select('updated_at').eq('id', item.id).single();
      const { data: r, error: txErr } = await supabase.from('transaction_log')
        .update({ note: updatedNote })
        .eq('id', item.id)
        .eq('updated_at', row?.updated_at)
        .select('id,updated_at');
      if (txErr) throw txErr;
      if (!r || r.length === 0) {
        alert('⚠ 다른 사용자가 이 입고 기록을 동시에 수정했습니다. 화면을 새로고침합니다.');
        loadTxLog();
        return;
      }
      // inventory.source 동기화 (실패해도 배송시트지 출처는 이미 바뀜 → 경고만)
      if (serials.length) {
        const { error: invErr } = await supabase.from('inventory')
          .update({ source: newSource || null, updated_at: new Date().toISOString() })
          .in('serial_number', serials);
        if (invErr) {
          console.error('재고 출처 갱신 실패:', invErr);
          alert(`배송시트지 출처는 변경됐지만 재고 출처 갱신에 실패했습니다.\n\n${invErr.message || invErr.code}\n\n재고 목록에서 개별 수정이 필요합니다.`);
        }
      }
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote, updated_at: r[0].updated_at } : rw));
      setLogModalItem(prev => (prev && prev.id === item.id ? { ...prev, note: updatedNote, updated_at: r[0].updated_at } : prev));
    } catch (e) {
      alert('출처 변경 실패\n\n' + (e.message || String(e)));
    }
    setInboundSourceSaving(false);
  };

  // v7.15: 입고 시리얼 삭제 — inventory DELETE + transaction_log items[].serials 동기화
  // 안전: in_stock 상태 시리얼만 삭제 (이미 출고된 건 거부)
  const removeInboundSerial = async (item, productName, serial) => {
    if (!serial || serial.startsWith('EXT-') || serial.startsWith('NOSN-')) {
      // 외부/시리얼면제 항목은 DB row 없음 → transaction_log만 업데이트
    } else {
      if (!window.confirm(`시리얼 "${serial}"을(를) 삭제하시겠습니까?\n• inventory에서 row 삭제\n• 입고 기록의 시리얼 목록에서도 제거됩니다`)) return;
      const delRes = await InventoryService.deleteBySerial(serial);
      if (!delRes.ok) { alert('시리얼 삭제 실패: ' + delRes.msg); return; }
    }
    // transaction_log items[].serials 업데이트 + qty 감소
    const { data: row } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    if (!row) return;
    let cur = {};
    try { cur = JSON.parse(row.note); } catch(e){}
    if (!Array.isArray(cur.items)) return;
    const updatedItems = cur.items.map(it => {
      if (it.product_name !== productName) return it;
      const serials = (it.serials || []).filter(s => s !== serial);
      return { ...it, serials, qty: serials.length };
    }).filter(it => (it.serials || []).length > 0 || (it.qty || 0) > 0); // 시리얼 0개 품목은 제거
    const updatedNote = JSON.stringify({ ...cur, items: updatedItems });
    // 모든 품목이 비면 행 자체 삭제할지? — 일단 유지 (사용자 판단)
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updatedNote }).eq('id', item.id).eq('updated_at', row.updated_at).select('id,updated_at');
    if (!r || r.length === 0) {
      alert('⚠ 다른 사용자가 이 입고를 동시 수정했습니다. 새로고침합니다.');
      loadTxLog();
      return;
    }
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedNote, updated_at: r[0].updated_at } : rw));
  };

  // v7.9: 입고 기록 통째 삭제 — transaction_log row + 관련 inventory 시리얼 일괄 삭제
  // 안전 보호: 출고/판매불가 등 in_stock 외 상태의 시리얼은 보호 (삭제 거부)
  const deleteInboundRecord = async (item) => {
    let info = {};
    try { info = JSON.parse(item.note); } catch(e){}
    const allSerials = Array.isArray(info.items)
      ? info.items.flatMap(it => (it.serials || []).filter(s => s && !s.startsWith('EXT-') && !s.startsWith('NOSN-')))
      : [];
    // 1. status 보호 — in_stock이 아닌 시리얼이 있으면 거부
    if (allSerials.length > 0) {
      const { data: rows } = await supabase.from('inventory')
        .select('serial_number,status')
        .in('serial_number', allSerials);
      const nonInStock = (rows || []).filter(r => r.status !== 'in_stock');
      if (nonInStock.length > 0) {
        alert(
          `다음 시리얼들은 재고 상태가 아니라 삭제할 수 없습니다:\n\n` +
          nonInStock.slice(0, 8).map(r => `• ${r.serial_number} (${r.status})`).join('\n') +
          (nonInStock.length > 8 ? `\n외 ${nonInStock.length - 8}개` : '') +
          `\n\n해당 시리얼들을 먼저 정리한 후 다시 시도하세요.`
        );
        return;
      }
    }
    // 2. 확인
    const summary = Array.isArray(info.items)
      ? info.items.map(it => `${it.product_name} ×${it.qty || (it.serials?.length || 0)}`).join(', ')
      : item.model || '?';
    if (!window.confirm(
      `이 입고 기록을 완전 삭제하시겠습니까?\n\n` +
      `품목: ${summary}\n` +
      `시리얼: ${allSerials.length}개\n\n` +
      `transaction_log 1행 + inventory ${allSerials.length}개가 삭제됩니다.\n복구 불가능.`
    )) return;
    // 3. inventory 시리얼들 일괄 삭제
    if (allSerials.length > 0) {
      const { error: invErr } = await supabase.from('inventory').delete().in('serial_number', allSerials);
      if (invErr) { alert('inventory 삭제 실패: ' + invErr.message); return; }
    }
    // 4. transaction_log row 삭제
    const { error: txErr } = await supabase.from('transaction_log').delete().eq('id', item.id);
    if (txErr) { alert('기록 삭제 실패: ' + txErr.message); return; }
    // 5. UI state 갱신
    setLogItems(prev => prev.filter(r => r.id !== item.id));
    setLogModalItem(null);
  };

  // 셀 인라인 저장: 택배 송장번호 / 퀵 금액 / 퀵 착불(v7.54) / 직발 업체
  // field: 'trackingNo' | 'quickPrice' | 'quickCOD' | 'directCompany'
  // value: string (빈 문자열이면 필드 삭제), quickCOD는 boolean
  const saveShipFieldInline = async (item, field, rawValue) => {
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    const merged = { ...noteObj };
    if (field === 'quickCOD') {
      // v7.54: boolean 토글 (true 저장, false면 키 삭제)
      if (rawValue) merged.quickCOD = true; else delete merged.quickCOD;
    } else if (rawValue === '' || rawValue === null || rawValue === undefined) {
      delete merged[field];
      // directCompany 비울 때 direct 플래그도 제거
      if (field === 'directCompany') delete merged.direct;
    } else if (field === 'quickPrice') {
      const n = Number(String(rawValue).replace(/[^0-9]/g, ''));
      if (n > 0) merged.quickPrice = n; else delete merged.quickPrice;
    } else if (field === 'directCompany') {
      merged.directCompany = String(rawValue).trim();
      merged.direct = true;
    } else {
      merged[field] = String(rawValue).trim();
    }
    const updated = JSON.stringify(merged);
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      // 충돌 시 재시도 (다른 사용자가 동시 변경)
      const { data: retry } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
      let retryNote = {}; try { retryNote = JSON.parse(retry?.note || '{}'); } catch(e){}
      const retryMerged = { ...retryNote };
      if (field === 'quickCOD') { if (rawValue) retryMerged.quickCOD = true; else delete retryMerged.quickCOD; }
      else if (rawValue === '' || rawValue === null || rawValue === undefined) { delete retryMerged[field]; if (field === 'directCompany') delete retryMerged.direct; }
      else if (field === 'quickPrice') { const n = Number(String(rawValue).replace(/[^0-9]/g, '')); if (n > 0) retryMerged.quickPrice = n; else delete retryMerged.quickPrice; }
      else if (field === 'directCompany') { retryMerged.directCompany = String(rawValue).trim(); retryMerged.direct = true; }
      else retryMerged[field] = String(rawValue).trim();
      const updatedRetry = JSON.stringify(retryMerged);
      await supabase.from('transaction_log').update({ note: updatedRetry }).eq('id', item.id);
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedRetry } : rw));
    } else {
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated, updated_at: r[0].updated_at } : rw));
    }
    // drafts 정리
    if (field === 'trackingNo') setLogTrackingDrafts(prev => { const n = {...prev}; delete n[item.id]; return n; });
    if (field === 'quickPrice') setLogPriceDrafts(prev => { const n = {...prev}; delete n[item.id]; return n; });
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
  };

  // 단일 품목 출고요청의 제품명 변경
  // v7.437: 4번째 인자 asExternal 추가 — 모델 변경 시 external 을 재판정한다.
  //   true = 미등록(외부, EXT- 자동부여) / false = 등록(비외부, 실물 시리얼) / undefined = 기존값 유지(하위호환).
  //   미등록→등록 교체 시 external:true 가 보존돼 EXT 임시시리얼이 계속 붙던 버그 fix.
  const saveItemProductName = async (item, oldName, newName, asExternal) => {
    const applyRename = (it) => {
      if (it.product_name !== oldName) return it;
      const next = { ...it, product_name: newName };
      if (asExternal === true) next.external = true;
      else if (asExternal === false) delete next.external;  // 등록 제품 전환 — EXT 임시시리얼 자동부여 중단
      return next;
    };
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at,model').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    if (Array.isArray(noteObj.items)) {
      noteObj.items = noteObj.items.map(applyRename);
    }
    const updated = JSON.stringify(noteObj);
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated, model: newName })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      const { data: retry } = await supabase.from('transaction_log').select('note').eq('id', item.id).single();
      let retryNote = {}; try { retryNote = JSON.parse(retry?.note || '{}'); } catch(e){}
      if (Array.isArray(retryNote.items)) retryNote.items = retryNote.items.map(applyRename);
      const updatedRetry = JSON.stringify(retryNote);
      await supabase.from('transaction_log').update({ note: updatedRetry, model: newName }).eq('id', item.id);
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedRetry, model: newName } : rw));
    } else {
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated, model: newName, updated_at: r[0].updated_at } : rw));
    }
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated, model: newName } : prev);
    // v7.437: 외부→등록 전환 시, 옛 모델명으로 붙어 있던 EXT-/NOSN- 임시 pending 정리 (가짜 시리얼 잔존 방지).
    //   EXT-/NOSN- 는 inventory 에 없어 ship_hold 도 없다 → releaseHold 불필요, 재고 영향 없음.
    if (asExternal === false) {
      try {
        const reqId = noteObj.reqId || item.serial;
        const res = await ShipPendingService.loadByReqId(reqId);
        if (res.ok) {
          const stale = (res.data || []).filter(p => p.model === oldName
            && (String(p.serial).startsWith('EXT-') || String(p.serial).startsWith('NOSN-')));
          for (const p of stale) await ShipPendingService.removeSerial(reqId, p.serial);
          if (stale.length) {
            const staleSet = new Set(stale.map(p => p.serial));
            setPendingSerials(prev => prev.filter(x => !staleSet.has(x.serial)));
          }
        }
      } catch (e) { console.warn('[모델 변경] 임시 pending 정리 실패:', e); }
    }
  };

  // 단일 품목 출고요청의 수량 변경 (+/- 버튼)
  // v7.196: 배송시트지에서 수량을 줄일 때 등록해 둔 pending 시리얼도 함께 줄이기
  //   기존 버그: qty만 줄고 ship_pending_serials에 스캔해 둔 시리얼은 그대로 남아
  //             출고 처리 모달에서 "진행 3/3"인데 "4개 시리얼이 출고 처리됩니다"처럼 불일치.
  //   허용 pending 수 = newQty − 이미 출고 확정(fulfilled) 수. 초과분은 나중에 넣은 것부터 해제.
  const collectExcessPending = async (reqId, model, allowedPending) => {
    if (!reqId || !model) return [];
    const res = await ShipPendingService.loadByReqId(reqId);
    if (!res.ok) return [];
    const mine = (res.data || []).filter(p => p.model === model);
    const excess = mine.length - Math.max(0, allowedPending);
    if (excess <= 0) return [];
    return mine.slice(-excess).map(p => p.serial); // 최근 추가분부터 해제
  };

  const removePendingSerials = async (reqId, model, serials) => {
    if (!reqId || !serials?.length) return;
    for (const sn of serials) {
      await ShipPendingService.removeSerial(reqId, sn);
      // v7.424: 수량↓ 등으로 빼는 시리얼도 ship_hold → in_stock 복귀
      if (sn && !String(sn).startsWith('EXT-') && !String(sn).startsWith('NOSN-')) {
        await InventoryService.releaseHoldBySerial(sn).catch(() => {});
      }
    }
    const removed = new Set(serials);
    // 현재 열려 있는 요청이면 화면 state도 동기화 (모달 카운트 즉시 반영)
    setPendingSerials(prev => prev.filter(p => !(p.model === model && removed.has(p.serial))));
  };

  // ─── v7.446: 입고 품목 드래그 재정렬 — 순서를 note.items 에 저장 ───
  const persistInboundOrder = async (item, orderedNames) => {
    let note = {};
    try { note = JSON.parse(item.note || '{}'); } catch (e) { return; }
    if (!Array.isArray(note.items)) return;
    const byName = new Map(note.items.map(it => [it.product_name, it]));
    const newItems = [];
    orderedNames.forEach(nm => { if (byName.has(nm)) { newItems.push(byName.get(nm)); byName.delete(nm); } });
    byName.forEach(v => newItems.push(v)); // 순서 목록에 없던 항목은 뒤에 보존
    if (newItems.length !== note.items.length) return; // 안전장치
    if (newItems.every((it, i) => it.product_name === note.items[i].product_name)) return; // 변화 없음
    const newNote = JSON.stringify({ ...note, items: newItems });
    try {
      const { data: row } = await supabase.from('transaction_log').select('updated_at').eq('id', item.id).single();
      const { data: r, error } = await supabase.from('transaction_log')
        .update({ note: newNote }).eq('id', item.id).eq('updated_at', row?.updated_at).select('id');
      if (error) { console.warn('[입고 순서] 저장 실패:', error); return; }
      if (!r || r.length === 0) { alert('⚠ 다른 사용자가 이 입고 기록을 동시에 수정했습니다. 새로고침합니다.'); loadTxLog(); return; }
      loadTxLog();
    } catch (e) { console.warn('[입고 순서] 저장 예외:', e); }
  };
  const startInbDrag = (e, item, names, it) => {
    if (e.button != null && e.button !== 0) return;
    const el = (e.currentTarget.closest && e.currentTarget.closest('[data-inb-name]')) || e.currentTarget;
    const r = el.getBoundingClientRect();
    setInbDrag({
      rowId: item.id, item,
      dragName: it.product_name, dragQty: it.qty,
      order: [...names],
      ghost: { x: e.clientX, y: e.clientY, w: r.width, offX: e.clientX - r.left, offY: e.clientY - r.top },
    });
    e.preventDefault();
    e.stopPropagation();
  };
  useEffect(() => {
    if (!inbDrag) return;
    const onMove = (e) => {
      setInbDrag(prev => {
        if (!prev) return prev;
        const ghost = { ...prev.ghost, x: e.clientX, y: e.clientY };
        const nodes = Array.from(document.querySelectorAll(`[data-inb-list="${prev.rowId}"] [data-inb-name]`));
        const ids = prev.order;
        const cur = ids.indexOf(prev.dragName);
        let target = cur;
        for (let i = 0; i < nodes.length; i++) {
          const rc = nodes[i].getBoundingClientRect();
          const mid = rc.top + rc.height / 2;
          if (e.clientY < mid) { target = i; break; }
          target = i + 1;
        }
        if (target > cur) target -= 1;
        let order = ids;
        if (target !== cur && target >= 0 && target < ids.length) {
          order = [...ids];
          order.splice(order.indexOf(prev.dragName), 1);
          order.splice(target, 0, prev.dragName);
        }
        return { ...prev, ghost, order };
      });
    };
    const onUp = () => {
      const d = inbDragRef.current;
      if (d) persistInboundOrder(d.item, d.order);
      setInbDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [inbDrag?.rowId]);

  const updateItemQty = async (item, productName, newQty) => {
    if (newQty < 1) return;
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    if (Array.isArray(noteObj.items)) {
      noteObj.items = noteObj.items.map(it => it.product_name === productName ? { ...it, qty: newQty } : it);
    }
    const updated = JSON.stringify(noteObj);
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      const { data: retry } = await supabase.from('transaction_log').select('note').eq('id', item.id).single();
      let retryNote = {}; try { retryNote = JSON.parse(retry?.note || '{}'); } catch(e){}
      if (Array.isArray(retryNote.items)) retryNote.items = retryNote.items.map(it => it.product_name === productName ? { ...it, qty: newQty } : it);
      const updatedRetry = JSON.stringify(retryNote);
      await supabase.from('transaction_log').update({ note: updatedRetry }).eq('id', item.id);
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedRetry } : rw));
    } else {
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated, updated_at: r[0].updated_at } : rw));
    }
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
  };

  // 품목 추가 (출고요청 행에)
  // v7.145: asExternal 옵션 추가 — true면 items에 external:true 플래그 부여
  //   배경: 카탈로그·재고에 없는 외부제품(NX523, RAIL-B02 등)을 [품목 추가]로 넣을 때
  //         이전엔 external 플래그 없이 저장되어 배송처리 시 EXT- 자동 부여 로직이 스킵되던 버그
  const addItemToShipment = async (item, productName, qty = 1, asExternal = false) => {
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    if (!Array.isArray(noteObj.items)) noteObj.items = [];
    // 같은 제품이 이미 있으면 수량만 증가 (external 플래그는 기존 값 유지)
    const existing = noteObj.items.find(it => it.product_name === productName);
    if (existing) { existing.qty = (existing.qty || 0) + qty; }
    else {
      const newItem = { product_name: productName, qty, fulfilledSerials: [] };
      if (asExternal) newItem.external = true;
      noteObj.items.push(newItem);
    }
    const updated = JSON.stringify(noteObj);
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      // 충돌 시 재시도
      const { data: retry } = await supabase.from('transaction_log').select('note').eq('id', item.id).single();
      let retryNote = {}; try { retryNote = JSON.parse(retry?.note || '{}'); } catch(e){}
      if (!Array.isArray(retryNote.items)) retryNote.items = [];
      const ex = retryNote.items.find(it => it.product_name === productName);
      if (ex) ex.qty = (ex.qty || 0) + qty;
      else {
        const newItem = { product_name: productName, qty, fulfilledSerials: [] };
        if (asExternal) newItem.external = true;
        retryNote.items.push(newItem);
      }
      const updatedRetry = JSON.stringify(retryNote);
      await supabase.from('transaction_log').update({ note: updatedRetry }).eq('id', item.id);
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updatedRetry } : rw));
    } else {
      setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated, updated_at: r[0].updated_at } : rw));
    }
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
  };

  // 품목 삭제 (출고요청 행에서)
  const removeItemFromShipment = async (item, productName) => {
    const { data: latest } = await supabase.from('transaction_log').select('note,updated_at').eq('id', item.id).single();
    let noteObj = {};
    try { noteObj = JSON.parse(latest?.note || item.note); } catch(e){}
    if (!Array.isArray(noteObj.items)) return;
    const target = noteObj.items.find(it => it.product_name === productName);
    if (target && (target.fulfilledSerials?.length || 0) > 0) {
      alert('이미 출고된 시리얼이 있는 품목은 삭제할 수 없습니다.');
      return;
    }
    noteObj.items = noteObj.items.filter(it => it.product_name !== productName);
    if (noteObj.items.length === 0) {
      alert('마지막 품목은 삭제할 수 없습니다. 주문 자체를 삭제하려면 기록 삭제를 이용하세요.');
      return;
    }
    const updated = JSON.stringify(noteObj);
    const { data: r } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', item.id)
      .eq('updated_at', latest?.updated_at || item.updated_at)
      .select('id,updated_at');
    if (!r || r.length === 0) {
      await supabase.from('transaction_log').update({ note: updated }).eq('id', item.id);
    }
    setLogItems(prev => prev.map(rw => rw.id === item.id ? { ...rw, note: updated } : rw));
    setLogModalItem(prev => (prev && prev.id === item.id) ? { ...prev, note: updated } : prev);
  };
  const openEditModal = (item, shipInfo) => {
    setEditingTxItem(item);
    setEditForm({
      recipient: shipInfo.recipient || '',
      phone: shipInfo.phone || '',
      addr: shipInfo.addr || '',
      method: shipInfo.method || '택배',
      salesStatus: shipInfo.salesStatus || '',
      author: shipInfo.author || '',
      requester: shipInfo.requester || '',
      trackingNo: shipInfo.trackingNo || '',
      quickPrice: shipInfo.quickPrice ? String(shipInfo.quickPrice) : '',
      quickCOD: !!shipInfo.quickCOD, // v7.54: 퀵 착불 여부
      quickSalesStatus: shipInfo.quickSalesStatus || '입력필요',
      // v7.427: 원본 item 객체를 통째로 보존한다(_orig). 편집 폼은 product_name/qty 만 다루는데
      //   기존엔 저장 시 그 두 필드로 items[] 를 새로 만들어 덮어써서, 폼에 없는 필드가 전부 소실됐다:
      //   serialOptional / serialOptionalReason / demoSerials(데모 대여) / external / reservedSerials / model_no.
      //   → 데모 건을 [내용 수정] 하면 데모 메타가 날아가 일반 출고건으로 바뀌고 시리얼 자동 기입이 죽었다
      //     (사용자 리포트: "데모 작성시 입력되던 시리얼이 초기화 되었어").
      items: Array.isArray(shipInfo.items) ? shipInfo.items.map(it => ({ product_name: it.product_name, qty: it.qty, fulfilledSerials: it.fulfilledSerials || [], _orig: it })) : [],
    });
  };
  const closeEditModal = () => { setEditingTxItem(null); setEditSaving(false); };
  const saveEditModal = async () => {
    if (!editingTxItem) return;
    setEditSaving(true);
    let noteObj = {};
    try { noteObj = JSON.parse(editingTxItem.note); } catch(e){}
    // 출고된 시리얼이 있는 품목은 qty를 줄일 수 없음 — 검증
    for (const it of editForm.items) {
      const f = (it.fulfilledSerials || []).length;
      if (it.qty < f) {
        alert(`[${it.product_name}] 이미 ${f}개 출고됨. 수량은 ${f} 이상이어야 합니다.`);
        setEditSaving(false);
        return;
      }
      if (!it.qty || it.qty < 1) {
        alert(`[${it.product_name}] 수량은 1 이상이어야 합니다.`);
        setEditSaving(false);
        return;
      }
    }
    const merged = {
      ...noteObj,
      recipient: editForm.recipient.trim(),
      phone: editForm.phone.trim(),
      addr: editForm.addr.trim(),
      method: editForm.method,
      salesStatus: editForm.salesStatus.trim(),
      author: (editForm.author || '').trim(),
      requester: editForm.requester.trim(),
      // v7.427: 원본 필드(_orig)를 먼저 펼친 뒤 폼에서 편집한 값만 덮어쓴다.
      //   폼이 다루지 않는 필드(serialOptional/serialOptionalReason/demoSerials/external/reservedSerials/model_no 등)를 보존.
      //   _orig 자체는 저장 대상이 아니므로 제외한다. (새로 추가된 품목은 _orig 가 없어 그대로 동작)
      items: editForm.items.map(it => {
        const { _orig, ...rest } = it;
        return {
          ...(_orig || {}),
          product_name: rest.product_name,
          qty: Number(rest.qty),
          fulfilledSerials: rest.fulfilledSerials || [],
        };
      }),
    };
    // 택배 송장번호 / 퀵 가격 — 현재 method에 해당하는 것만 저장, 나머진 삭제
    if (editForm.method === '택배' && (editForm.trackingNo || '').trim()) {
      merged.trackingNo = editForm.trackingNo.trim();
      delete merged.quickPrice;
      delete merged.quickCOD;
    } else if (editForm.method === '퀵서비스') {
      if (editForm.quickPrice) merged.quickPrice = Number(editForm.quickPrice);
      else delete merged.quickPrice;
      // v7.54: 퀵 착불 여부 — 체크 시만 저장, 해제 시 키 삭제
      if (editForm.quickCOD) merged.quickCOD = true; else delete merged.quickCOD;
      delete merged.trackingNo;
    } else {
      delete merged.trackingNo;
      delete merged.quickPrice;
      delete merged.quickCOD;
    }
    // 퀵 매출 여부 — 퀵서비스일 때만 저장
    if (editForm.method === '퀵서비스') {
      if (editForm.quickSalesStatus) merged.quickSalesStatus = editForm.quickSalesStatus;
    } else {
      delete merged.quickSalesStatus;
    }
    const updated = JSON.stringify(merged);
    // Optimistic Lock: 모달 열었을 때의 updated_at과 일치하는 행만 갱신
    // 다른 사용자가 그 사이 저장했다면 0행 반환 → 사용자에게 알리고 리로드
    const lockVersion = editingTxItem.updated_at;
    const { data: result, error } = await supabase.from('transaction_log')
      .update({ note: updated })
      .eq('id', editingTxItem.id)
      .eq('updated_at', lockVersion)
      .select('id,updated_at');
    if (error) { alert('저장 실패: ' + error.message); setEditSaving(false); return; }
    if (!result || result.length === 0) {
      alert('⚠ 다른 사용자가 먼저 이 요청을 수정했습니다.\n변경사항이 손실될 수 있어 저장을 취소했습니다.\n새로고침 후 다시 시도해주세요.');
      await loadTxLog();
      closeEditModal();
      return;
    }
    // 성공: 로컬 state에 새 updated_at도 반영
    setLogItems(prev => prev.map(r => r.id === editingTxItem.id ? { ...r, note: updated, updated_at: result[0].updated_at } : r));
    closeEditModal();
  };

  // 물품 등록: 제품 검색 (products 테이블 + alias 부가)
  // v7.453: 데모 장비 추가 — 모델명 자동완성 (제품 카탈로그 검색)
  const searchDemoModels = async (q) => {
    const query = (q || '').trim();
    if (query.length < 1) { setDemoModelResults([]); return; }
    let results = await ProductService.searchByName(query, 15);
    // 별칭(alias) 매칭 — 비표준 별칭을 쳐도 표준명 후보로 노출 (입고 등록과 동일)
    const aliasData = await AliasService.searchByQuery(query, 8);
    if (aliasData.length > 0) {
      const aliasProductNames = [...new Set(aliasData.map(a => a.product_name))];
      const existing = new Set(results.map(r => r.product_name));
      const missing = aliasProductNames.filter(nm => !existing.has(nm));
      if (missing.length > 0) {
        const aliasProducts = await ProductService.findByNames(missing);
        if (aliasProducts.length > 0) {
          const aliasMap = {};
          aliasData.forEach(a => {
            if (!aliasMap[a.product_name] || a.alias.toLowerCase().includes(query.toLowerCase())) {
              aliasMap[a.product_name] = a.alias;
            }
          });
          const aliasResults = aliasProducts.map(p => ({ ...p, _matchedAlias: aliasMap[p.product_name] }));
          results = [...aliasResults, ...results];
        }
      }
    }
    // -EU 접미사 자동 매칭 (입고 등록과 동일)
    if (query.endsWith('-EU') && query.length > 3) {
      const withoutEu = query.slice(0, -3);
      const strippedData = await ProductService.searchByName(withoutEu, 10);
      if (strippedData.length > 0) {
        const existing = new Set(results.map(r => r.product_name));
        const additional = strippedData.filter(r => !existing.has(r.product_name));
        results = [...additional, ...results];
      }
    }
    setDemoModelResults(results.slice(0, 20));
  };
  const searchRegProducts = async (q) => {
    setRegProductSearch(q);
    if (q.length < 1) { setRegProductResults([]); return; }
    let results = await ProductService.searchByName(q, 15);
    // 별칭(alias) 매칭 결과도 함께 노출 — 표준명으로 자동 변환되어 표시됨
    const aliasData = await AliasService.searchByQuery(q, 8);
    if (aliasData.length > 0) {
      const aliasProductNames = [...new Set(aliasData.map(a => a.product_name))];
      const existing = new Set(results.map(r => r.product_name));
      const missing = aliasProductNames.filter(n => !existing.has(n));
      if (missing.length > 0) {
        const aliasProducts = await ProductService.findByNames(missing);
        if (aliasProducts.length > 0) {
          // alias 매칭 정보를 함께 첨부 (드롭다운에서 "← 별칭: xxx" 힌트 표시용)
          const aliasMap = {};
          aliasData.forEach(a => {
            if (!aliasMap[a.product_name] || a.alias.toLowerCase().includes(q.toLowerCase())) {
              aliasMap[a.product_name] = a.alias;
            }
          });
          const aliasResults = aliasProducts.map(p => ({ ...p, _matchedAlias: aliasMap[p.product_name] }));
          results = [...aliasResults, ...results];
        }
      }
    }
    // -EU 접미사 자동 매칭 추가: 입력이 '-EU'로 끝나면 그것을 뺀 결과도 우선 노출
    if (q.endsWith('-EU') && q.length > 3) {
      const withoutEu = q.slice(0, -3);
      const strippedData = await ProductService.searchByName(withoutEu, 10);
      if (strippedData.length > 0) {
        const existing = new Set(results.map(r => r.product_name));
        const additional = strippedData.filter(r => !existing.has(r.product_name));
        results = [...additional, ...results];
      }
    }
    setRegProductResults(results.slice(0, 20));
  };

  // 활성 배치 헬퍼 (이 함수 본체 안에서만 사용; useCallback 의존성 회피)
  const getActiveBatch = () => regActiveIndex >= 0 && regActiveIndex < regBatches.length ? regBatches[regActiveIndex] : null;

  // 물품 등록: 제품 선택 (같은 제품 그룹 있으면 그 그룹 활성화, 없으면 새 그룹)
  const selectRegProduct = (product) => {
    // 이미 같은 제품 그룹이 있으면 그 그룹으로 활성화
    const existingIdx = regBatches.findIndex(b => b.product.product_name === product.product_name);
    if (existingIdx >= 0) {
      setRegActiveIndex(existingIdx);
      setRegProductSearch('');
      setRegProductResults([]);
      setRegMsg({ type: 'ok', msg: `기존 배치로 전환: ${product.product_name} (${regBatches[existingIdx].serials.length}개 시리얼)` });
      setTimeout(() => regSerialInputRef.current?.focus(), 50);
      return;
    }
    // 새 그룹 추가 + 활성화
    setRegBatches(p => [...p, { product, serials: [], from: regFrom }]);
    setRegActiveIndex(regBatches.length); // 추가 후 인덱스 = 추가 전 length
    setRegProductSearch('');
    setRegProductResults([]);
    setRegMsg(null);
    setTimeout(() => regSerialInputRef.current?.focus(), 50);
  };

  // 카탈로그에 없는 제품 → 별칭(alias) 등록 모달 (장부 기준 강제: 자동 신제품 추가 차단)
  const addNewProductBatch = (name) => {
    const trimmed = (name || '').trim();
    if (!trimmed) return;
    setNewProductModal({
      name: trimmed,
      category: '',
      formFactor: '',       // 폼팩터 (선택사항)
      saveToCatalog: true,
      mode: 'alias',         // default 'alias' — 장부 표준명으로 강제 매핑
      aliasFor: '',          // 표준 제품명
      aliasSearch: trimmed,  // 검색어 초기값 (입력된 이름으로 자동 검색 시작)
      aliasResults: [],
    });
    // 자동 검색 한 번 실행 (비슷한 표준명 찾기)
    setTimeout(() => searchAliasTarget(trimmed), 50);
  };

  // 신제품 모달 - 기존 제품 검색 (alias 등록 대상 찾기용)
  const searchAliasTarget = async (q) => {
    setNewProductModal(m => m ? { ...m, aliasSearch: q } : m);
    if (!q || q.length < 1) {
      setNewProductModal(m => m ? { ...m, aliasResults: [] } : m);
      return;
    }
    const data = await ProductService.searchByName(q, 10);
    setNewProductModal(m => m ? { ...m, aliasResults: data } : m);
  };

  // 신제품 모달에서 등록 확정
  const confirmNewProduct = async () => {
    if (!newProductModal) return;
    const name = newProductModal.name.trim();
    const mode = newProductModal.mode || 'new';

    // ─── 모드: alias (기존 제품의 별칭으로 등록) ───
    if (mode === 'alias') {
      const target = (newProductModal.aliasFor || '').trim();
      if (!target) { alert('별칭을 매핑할 기존 표준 제품을 선택하세요.'); return; }
      // 1) alias 등록
      const result = await addAlias(target, name, 'barcode');
      if (!result.ok) { alert(result.msg); return; }
      // 2) 표준 제품 정보 조회 후 배치 추가
      const prod = await ProductService.findExact(target);
      if (!prod) { alert('대상 제품을 찾을 수 없습니다.'); return; }
      setRegBatches(p => [...p, { product: prod, serials: [], from: regFrom }]);
      setRegActiveIndex(regBatches.length);
      setRegProductSearch('');
      setRegProductResults([]);
      setNewProductModal(null);
      setRegMsg({ type: 'ok', msg: `✓ 별칭 "${name}" → "${target}" 등록 완료. 앞으로 자동 변환됩니다.` });
      setTimeout(() => regSerialInputRef.current?.focus(), 80);
      return;
    }

    // ─── 모드: new (신제품 등록) ───
    const category = newProductModal.category.trim();
    const formFactor = (newProductModal.formFactor || '').trim() || null;
    const saveToCatalog = newProductModal.saveToCatalog;
    if (!name) { alert('제품명을 입력하세요.'); return; }
    if (saveToCatalog && !category) { alert('카탈로그 저장 시 카테고리는 필수입니다.'); return; }
    // 1. 체크 시에만 products 테이블에 INSERT (영구 카탈로그)
    if (saveToCatalog) {
      const res = await ProductService.create({ product_name: name, category, form_factor: formFactor });
      if (!res.ok) {
        // 이미 있으면 무시 (외래키 위반 방지용으로 INSERT 자체는 시도)
        if (!String(res.msg).includes('duplicate')) {
          alert('제품 등록 실패: ' + res.msg);
          return;
        }
      }
    } else {
      // 일회성: 이번 입고에만 사용 — products에 임시로 INSERT (외래키 위반 회피)
      const tempCategory = category || (productCategories[0] || 'QNAP');
      const res = await ProductService.upsert({ product_name: name, category: tempCategory, form_factor: formFactor });
      if (!res.ok) { alert('임시 제품 등록 실패: ' + res.msg); return; }
    }
    // 2. 배치로 추가
    const product = { product_name: name, category: category || (saveToCatalog ? category : '미분류'), isNew: true };
    setRegBatches(p => [...p, { product, serials: [], from: regFrom }]);
    setRegActiveIndex(regBatches.length);
    setRegProductSearch('');
    setRegProductResults([]);
    setNewProductModal(null);
    setRegMsg({
      type: 'ok',
      msg: saveToCatalog
        ? `✓ 신제품 "${name}" (${category}${formFactor?' · '+formFactor:''}) 카탈로그에 영구 등록 완료`
        : `✓ "${name}" — 이번 입고에만 사용 (카탈로그 미등록)`
    });
    setTimeout(() => regSerialInputRef.current?.focus(), 80);
  };

  // 물품 등록: 제품 검색 input에서 Enter
  //   1) 정확 매칭 → 자동 선택
  //   2) 별칭(alias) 매칭 → 표준명으로 자동 변환
  //   3) -EU 접미사 제거 매칭
  //   4) 대소문자 무시 / 부분 매칭
  //   5) 매칭 없음 → 신제품 등록 모달
  const handleRegProductEnter = async () => {
    const q = regProductSearch.trim();
    if (!q) return;
    // 1. 정확 매칭 (products 마스터)
    const exact = await ProductService.findExact(q);
    if (exact) {
      selectRegProduct(exact);
      return;
    }
    // 2. 별칭(alias) 매칭 — 바코드로 들어온 비표준 이름을 표준명으로 자동 변환
    const aliasHit = await lookupAlias(q);
    if (aliasHit) {
      const std = await ProductService.findExact(aliasHit.product_name);
      if (std) {
        selectRegProduct(std);
        setRegMsg({ type: 'ok', msg: `✓ 별칭 "${q}" → 표준명 "${aliasHit.product_name}"으로 자동 변환` });
        return;
      }
    }
    // 3. -EU 접미사 자동 제거 매칭 (예: TS-264-8G-EU → TS-264-8G)
    if (q.endsWith('-EU')) {
      const withoutEu = q.slice(0, -3);
      const stripped = await ProductService.findExact(withoutEu);
      if (stripped) {
        selectRegProduct(stripped);
        setRegMsg({ type: 'ok', msg: `✓ "${q}" → 카탈로그 표준명 "${stripped.product_name}" 으로 자동 변환` });
        return;
      }
    }
    // 4. 대소문자 무시 정확 매칭
    const caseInsensitive = await ProductService.findCaseInsensitive(q, 2);
    if (caseInsensitive.length === 1) {
      const found = caseInsensitive[0];
      if (found.product_name !== q) {
        setRegMsg({ type: 'ok', msg: `✓ "${q}" → 카탈로그 표준명 "${found.product_name}" 으로 자동 변환` });
      }
      selectRegProduct(found);
      return;
    }
    // 5. 부분 매칭 (정확히 1개일 때만 자동 선택)
    const partial = await ProductService.findPartial(q, 2);
    if (partial.length === 1) {
      selectRegProduct(partial[0]);
      setRegMsg({ type: 'ok', msg: `✓ "${q}" → "${partial[0].product_name}" 으로 자동 매칭` });
      return;
    }
    // 6. 매칭 없음 → 별칭(alias) 등록 모달 (장부 기준 강제)
    if (partial.length === 0) {
      setRegMsg({ type: 'warn', msg: `⚠ "${q}"는 카탈로그에 없습니다 — 표준명에 매핑(별칭 등록)이 필요합니다.` });
      addNewProductBatch(q);
    } else {
      setRegMsg({ type: 'warn', msg: `여러 제품이 매칭되었습니다 (${partial.data.length}+개). 드롭다운에서 정확한 모델을 선택하세요.` });
    }
  };

  // 물품 등록: 시리얼 누적 (활성 배치의 serials에 추가)
  const addRegSerial = async () => {
    const sn = regSerialInput.trim();
    if (!sn) return;
    const ag = getActiveBatch();
    if (!ag) { setRegMsg({ type: 'err', msg: '제품을 먼저 선택하세요.' }); return; }
    // ① 같은 세션 내 중복 (이미 이번 입력에서 추가한 시리얼)
    const allSerials = regBatches.flatMap(b => b.serials);
    if (allSerials.includes(sn)) {
      setRegMsg({ type: 'err', msg: `⚠ "${sn}" — 이번 입력에서 이미 추가됨` });
      setRegSerialInput('');
      return;
    }
    // ② 캐시(DB 사전 로드)로 빠른 1차 체크
    if (regSerialCache.has(sn)) {
      setRegMsg({ type: 'err', msg: `⚠ "${sn}" — DB에 이미 등록된 시리얼입니다 (중복 입고 방지)` });
      setRegSerialInput('');
      return;
    }
    // ③ DB 즉시 재확인 (다른 사용자가 그 사이 등록했을 수 있음 — 캐시 누락 보완)
    const existing = await InventoryService.findBySerialFields(sn, 'serial_number,status,product_name');
    if (existing) {
      // 캐시에도 반영
      setRegSerialCache(prev => { const n = new Set(prev); n.add(sn); return n; });
      const statusLabel = existing.status === 'in_stock' ? '재고 있음' : existing.status === 'shipped' ? '이미 출고됨' : existing.status === 'unsellable' ? '판매불가' : existing.status;
      setRegMsg({ type: 'err', msg: `⚠ "${sn}" — DB에 이미 등록됨 (${existing.product_name||'?'} · ${statusLabel})` });
      setRegSerialInput('');
      return;
    }
    // 통과 — 활성 배치에 추가
    setRegBatches(p => p.map((b, i) => i === regActiveIndex ? { ...b, serials: [...b.serials, sn] } : b));
    setRegSerialInput('');
    setRegMsg(null);
  };

  // 물품 등록: 활성 배치의 시리얼 제거
  const removeRegSerial = (sn) => {
    if (regActiveIndex < 0) return;
    setRegBatches(p => p.map((b, i) => i === regActiveIndex ? { ...b, serials: b.serials.filter(s => s !== sn) } : b));
  };

  // 물품 등록: 배치 활성화 (카드 클릭 시)
  const activateBatch = (idx) => {
    if (idx < 0 || idx >= regBatches.length) return;
    setRegActiveIndex(idx);
    setRegSerialInput('');
    setRegProductSearch('');
    setRegProductResults([]);
    setRegMsg(null);
  };

  // 물품 등록: 새 제품 입력 슬롯으로 (activeIndex = -1)
  const nextRegProduct = () => {
    setRegActiveIndex(-1);
    setRegSerialInput('');
    setRegProductSearch('');
    setRegMsg(null);
  };

  // 물품 등록: 배치 삭제 (activeIndex 자동 조정)
  const removeBatch = (idx) => {
    setRegBatches(p => p.filter((_, i) => i !== idx));
    if (regActiveIndex === idx) setRegActiveIndex(-1);
    else if (regActiveIndex > idx) setRegActiveIndex(regActiveIndex - 1);
  };

  // 물품 등록: From 변경 (활성 배치 있으면 그 배치의 from 변경 + 기본값도 업데이트)
  const handleSetRegFrom = (value) => {
    setRegFrom(value);
    if (regActiveIndex >= 0) {
      setRegBatches(p => p.map((b, i) => i === regActiveIndex ? { ...b, from: value } : b));
    }
  };

  // 물품 등록: 배치 사이 Tab 전환 (시리얼 input에서 호출)
  const cycleBatch = (direction) => {
    if (regBatches.length === 0) return;
    if (direction === 'next') {
      const next = regActiveIndex + 1;
      if (next < regBatches.length) setRegActiveIndex(next);
      else setRegActiveIndex(-1); // 마지막 다음 → 신규 입력 슬롯
    } else {
      const prev = regActiveIndex - 1;
      if (prev >= 0) setRegActiveIndex(prev);
    }
    setRegSerialInput('');
  };

  // 물품 등록: 모든 배치 일괄 등록
  const handleRegister = async () => {
    // v7.324: 비고는 한 번만 다듬어서 세 군데(재고 note/memo, 배송시트지 note.memo)에 같이 쓴다.
    //   공백만 친 경우를 빈 값으로 처리한다 — 안 그러면 재고목록 비고 칸에 ' ' 가 들어가
    //   '비고 있음' 처럼 보인다.
    const regNoteText = (regNote || '').trim();
    // 입력 중인 시리얼 자동 흡수
    const pendingInput = regSerialInput.trim();
    let batchesToSubmit = regBatches;
    if (pendingInput && regActiveIndex >= 0) {
      const allSerials = regBatches.flatMap(b => b.serials);
      if (!allSerials.includes(pendingInput)) {
        batchesToSubmit = regBatches.map((b, i) => i === regActiveIndex ? { ...b, serials: [...b.serials, pendingInput] } : b);
      }
    }
    // 시리얼 있는 배치만
    const validBatches = batchesToSubmit.filter(b => b.serials.length > 0);
    if (validBatches.length === 0) { setRegMsg({ type: 'err', msg: '등록할 항목이 없습니다.' }); return; }
    setLoading(true);
    setRegMsg(null);
    // 중복 확인 (모든 시리얼 일괄)
    const allSerialList = validBatches.flatMap(b => b.serials);
    const dupSet = await InventoryService.findExistingSerials(allSerialList);
    const nowIso = new Date().toISOString();
    const rows = [];
    // v7.3: 입고 transaction_log를 한 번 호출당 1 row로 통합 (출고와 동일한 items[] 구조)
    // - 같은 모델 여러 시리얼이 별도 행으로 나뉘던 문제 해결
    // - 배송시트지에서 다중 품목/수량/시리얼이 한 행으로 묶여 표시됨
    const itemsByProduct = new Map(); // product_name -> { product_name, qty, serials, from }
    let skipCount = 0;
    validBatches.forEach(batch => {
      const batchFrom = batch.from || 'taiwan';
      batch.serials.forEach(sn => {
        if (dupSet.has(sn)) { skipCount++; return; }
        rows.push({
          product_name: batch.product.product_name,
          serial_number: sn,
          stock_type: '신품',
          category: batch.product.category,
          status: 'in_stock',
          source: batchFrom,       // 출처 (어디서 왔는지: taiwan/paju)
          warehouse: 'hq',         // 보관 위치 (신규 입고는 본사 기본)
          receipt_date: regReceiptDate,
          // v7.324: 재고목록 [비고] 칸이 보여주는 건 inventory.memo 다.
          //   여기서는 note 에만 쓰고 있어서, 입고할 때 적은 비고가 재고목록에 안 떴다.
          //   (v7.254 에서 DOA 건으로 똑같은 걸 고쳤는데 입고 등록 경로는 남아 있었다)
          //   note 는 감사용으로 그대로 두고 memo 를 추가한다.
          note: regNoteText || null,
          memo: regNoteText || null,
          created_at: nowIso,
          updated_at: nowIso
        });
        // 같은 product_name + from 조합으로 묶기 (다른 from은 분리)
        const key = `${batch.product.product_name}::${batchFrom}`;
        if (!itemsByProduct.has(key)) {
          itemsByProduct.set(key, {
            product_name: batch.product.product_name,
            qty: 0,
            serials: [],
            from: batchFrom
          });
        }
        const it = itemsByProduct.get(key);
        it.qty += 1;
        it.serials.push(sn);
      });
    });
    if (rows.length === 0) {
      setRegMsg({ type: 'err', msg: `모든 시리얼이 이미 등록되어 있습니다 (${skipCount}개).` });
      setLoading(false); return;
    }
    // 통합 transaction_log row (1개)
    const inboundItems = Array.from(itemsByProduct.values());
    const recvId = 'RECV-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6);
    const txRow = {
      serial: recvId,
      model: inboundItems[0]?.product_name || '입고',
      type: 'inbound',
      note: JSON.stringify({
        type: 'inbound',
        reqId: recvId,
        items: inboundItems,
        // v7.324: 배송시트지 [비고] 칸이 읽는 값 (shipInfo.memo). trim 적용.
        memo: regNoteText || undefined,
        // 다중 출처일 때 fallback용 (단일이면 from, 다중이면 'mixed')
        from: inboundItems.every(i => i.from === inboundItems[0].from) ? inboundItems[0].from : 'mixed',
        // v7.183: 장부처리 완료 여부
        bookkept: !!regBookkept,
      }),
      processed_by: '운영 관리자',
      transaction_date: regReceiptDate || today()
    };
    // 신제품(isNew=true)을 products 테이블에 등록 (다음 검색에서 잡히도록)
    // products에 이미 있는 이름은 충돌 가능 → existing 체크 후 신규만 INSERT
    const newProductNames = [...new Set(validBatches.filter(b => b.product?.isNew).map(b => b.product.product_name))];
    if (newProductNames.length > 0) {
      const existingSet = await ProductService.existingNames(newProductNames);
      const toInsert = newProductNames
        .filter(n => !existingSet.has(n))
        .map(n => {
          const b = validBatches.find(x => x.product.product_name === n);
          return { product_name: n, category: b?.product.category || null };
        });
      if (toInsert.length > 0) {
        await ProductService.bulkInsert(toInsert);
      }
    }
    const invRes = await InventoryService.bulkInsert(rows);
    if (invRes.ok) {
      // v7.225: transaction_log insert 에러를 확인한다.
      //   기존에는 결과를 버려서, 이 insert가 실패해도 '✓ 등록 완료'가 그대로 떴다.
      //   → 재고에는 들어갔는데 배송시트지에는 아무것도 안 뜨는 상태가 되고,
      //     사용자는 원인을 알 방법이 없었다 (실패가 완전히 조용함).
      const { error: txErr } = await supabase.from('transaction_log').insert(txRow);
      if (txErr) {
        console.error('입고 이력(transaction_log) 저장 실패:', txErr, txRow);
        setRegMsg({ type: 'err', msg:
          `재고 ${rows.length}개는 등록됐지만 배송시트지 기록에 실패했습니다.\n` +
          `사유: ${txErr.message || txErr.code || '알 수 없음'}\n` +
          `재고 목록에서는 조회되며, 배송시트지에만 표시되지 않습니다.` });
        setLoading(false);
        return;
      }
      let msg = `✓ ${validBatches.length}개 제품, ${rows.length}개 시리얼 등록 완료!`;
      if (skipCount > 0) msg += ` (${skipCount}개 중복 스킵)`;
      setRegMsg({ type: 'ok', msg });
      // v7.89: 사용량 트래킹
      AnalyticsService.trackAction('입고_등록', '', {
        productCount: validBatches.length, serialCount: rows.length, skipCount,
      });
      // 캐시에 새로 등록된 시리얼 반영 (다음 입력에서 즉시 중복 감지)
      setRegSerialCache(prev => {
        const next = new Set(prev);
        rows.forEach(r => r.serial_number && next.add(r.serial_number));
        return next;
      });
      // 전체 초기화
      setRegBatches([]);
      setRegActiveIndex(-1);
      setRegSerialInput('');
      setRegProductSearch('');
      setRegNote('');
      // v7.226: 다음 등록을 위해 입고일을 오늘로 되돌린다 (장시간 열어둔 창 대비)
      regDateTouchedRef.current = false;
      setRegReceiptDate(today());
    } else {
      setRegMsg({ type: 'err', msg: '등록 실패: ' + invRes.msg });
    }
    setLoading(false);
  };

  // ─── 재고 실사: 엑셀 파일 파싱 ───
  const handleAuditFile = async (file) => {
    if (!file) return;
    setAuditLoading(true);
    setAuditMsg(null);
    setAuditFileName(file.name);
    setAuditResults([]);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(ws, { header: 1, defval: null });
      // 헤더 행 찾기: '상품종류'로 시작하는 행
      let headerIdx = rows.findIndex(r => r && String(r[0] || '').includes('상품종류'));
      if (headerIdx < 0) headerIdx = 1;
      // QNAP 카테고리 행만 추출
      const qnapRows = [];
      for (let i = headerIdx + 1; i < rows.length; i++) {
        const r = rows[i];
        if (!r) continue;
        const category = String(r[0] || '').trim();
        const model = String(r[1] || '').trim();
        if (category !== 'QNAP' || !model) continue;
        qnapRows.push({
          model,
          hq: Number(r[2]) || 0,         // 리버네트워크 = 본사
          paju: Number(r[3]) || 0,        // 파주물류센터
          total: Number(r[4]) || ((Number(r[2]) || 0) + (Number(r[3]) || 0)),
        });
      }
      setAuditExcelRows(qnapRows);
      setAuditMsg({ type: 'ok', msg: `✓ ${qnapRows.length}개 QNAP 모델 인식 완료. "비교 실행" 버튼을 누르세요.` });
    } catch (err) {
      setAuditMsg({ type: 'err', msg: '엑셀 파싱 실패: ' + err.message });
      setAuditExcelRows([]);
    }
    setAuditLoading(false);
  };

  // ─── 재고 실사: 시스템 재고 vs 엑셀 비교 실행 ───
  const runAudit = async () => {
    if (!auditExcelRows.length) {
      setAuditMsg({ type: 'err', msg: '먼저 엑셀 파일을 업로드하세요.' });
      return;
    }
    setAuditLoading(true);
    setAuditMsg(null);
    try {
      // 시스템 재고: in_stock 상태만, product_name + warehouse 별 카운트
      const { data, error } = await supabase
        .from('inventory')
        .select('product_name, warehouse')
        .eq('status', 'in_stock');
      if (error) throw error;
      // model -> {hq, paju}
      const sysMap = new Map();
      for (const row of data) {
        const m = row.product_name;
        if (!sysMap.has(m)) sysMap.set(m, { hq: 0, paju: 0 });
        const w = row.warehouse;
        if (w === 'paju') sysMap.get(m).paju += 1;
        else sysMap.get(m).hq += 1; // 본사/null은 본사로 간주
      }
      // 엑셀 model 맵
      const excelMap = new Map(auditExcelRows.map(r => [r.model, r]));
      // 모든 모델 합집합
      const allModelsSet = new Set([...sysMap.keys(), ...excelMap.keys()]);
      const results = [];
      for (const model of allModelsSet) {
        const e = excelMap.get(model);
        const s = sysMap.get(model);
        const excelHq = e?.hq ?? null;
        const excelPaju = e?.paju ?? null;
        const excelTotal = e?.total ?? null;
        const sysHq = s?.hq ?? 0;
        const sysPaju = s?.paju ?? 0;
        const sysTotal = sysHq + sysPaju;
        // 분류 (본사 기준)
        let kind;
        if (!e) kind = 'only_system';
        else if (!s) kind = 'only_excel';
        else {
          const diff = (excelHq ?? 0) - sysHq;
          if (diff === 0) kind = 'match';
          else if (diff > 0) kind = 'short'; // 장부 본사 > 2층 재고 = 2층 부족
          else kind = 'over'; // 2층 재고 > 장부 본사 = 2층 과잉
        }
        // 엑셀에 없는 모델(시스템에만 존재)만 제외
        if (kind === 'only_system') continue;
        results.push({
          model, kind,
          excelHq, excelPaju, excelTotal,
          sysHq, sysPaju, sysTotal,
          hqDiff: (excelHq ?? 0) - sysHq,
          pajuDiff: (excelPaju ?? 0) - sysPaju,
          totalDiff: (excelTotal ?? 0) - sysTotal,
        });
      }
      // 정렬: 본사 차이 절댓값 큰 순 → 일치 모델은 맨 아래
      results.sort((a, b) => {
        if (a.kind === 'match' && b.kind !== 'match') return 1;
        if (a.kind !== 'match' && b.kind === 'match') return -1;
        return Math.abs(b.hqDiff) - Math.abs(a.hqDiff);
      });
      setAuditResults(results);
      setAuditMsg({ type: 'ok', msg: `✓ 비교 완료: ${results.length}개 모델 (일치 ${results.filter(r=>r.kind==='match').length} / 차이 ${results.filter(r=>r.kind!=='match').length})` });
    } catch (err) {
      setAuditMsg({ type: 'err', msg: '비교 실패: ' + err.message });
    }
    setAuditLoading(false);
  };

  // ─── 재고 실사: CSV 내보내기 ───
  const exportAuditCsv = () => {
    if (!auditResults.length) return;
    const header = ['모델명','구분','장부 본사','2층 재고','차이'];
    const kindLabel = { match:'일치', short:'2층 부족', over:'2층 과잉', only_excel:'장부에만' };
    const rows = auditResults.map(r => [
      r.model, kindLabel[r.kind],
      r.excelHq ?? '', r.sysHq, r.hqDiff,
    ]);
    const csv = '\uFEFF' + [header, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `재고실사_${today().replace(/-/g,'')}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const nav = [
    { id: 'txlog', icon: 'assignment', label: '배송시트지' },
    { id: 'inbound', icon: 'south_west', label: '입고 등록' },
    { id: 'inbound_hdd', icon: 'storage', label: 'HDD/SSD 입고' },
    // v7.129: 'scan'은 PC 사이드바에서 제거 (모바일 전용 세그먼트 탭으로 이관)
    { id: 'ship', icon: 'local_shipping', label: '출고 요청' },
    { id: 'inventory', icon: 'inventory_2', label: '재고 목록' },
    { id: 'keeps', icon: 'bookmark', label: '킵 관리' },
    { id: 'demo', icon: 'science', label: '데모 관리' },
    // v7.339: 넥시 조회 — 구글시트 [상품 위치] 이관 1단계 (도면 + 하이라이트)
    { id: 'nexi', icon: 'pin_drop', label: '넥시 조회' },
    // v7.349: NAS ↔ 메모리 호환성 조회
    { id: 'compat', icon: 'memory', label: 'NAS 호환성' },
    // v7.348: 제품 표준화 관리 / 재고 실사 / 백업을 [관리] 아래로 묶었다.
    //   'admin' 자체는 페이지가 아니라 아코디언 머리다 — page 상태에 들어가지 않는다.
    //   각 페이지 렌더 블록(page==='catalog' 등)은 그대로 두고 진입 경로만 바뀐 것이다.
    { id: 'admin', icon: 'settings', label: '관리', children: [
      { id: 'catalog', icon: 'menu_book', label: '제품 표준화 관리' },
      { id: 'audit', icon: 'fact_check', label: '재고 실사' },
      { id: 'backup', icon: 'cloud_download', label: '백업' },
    ] },
  ];

  // 아코디언이 펼쳐졌을 때 실제로 FlowingMenu 에 넘길 평평한 목록.
  //   ⚠ FlowingMenu 는 중첩 구조를 모른다. 부모 바로 뒤에 자식을 끼워 넣고
  //     sub 플래그로 들여쓰기만 다르게 준다.
  const navFlat = [];
  nav.forEach(n => {
    if (!n.children) { navFlat.push({ ...n, active: page === n.id }); return; }
    const childActive = n.children.some(c => c.id === page);
    navFlat.push({ ...n, expandable: true, expanded: adminOpen, active: childActive && !adminOpen });
    if (adminOpen) n.children.forEach(c => navFlat.push({ ...c, sub: true, active: page === c.id }));
  });

  const shipTotalQty = shipItems.reduce((s, i) => s + i.qty, 0);

  // ─── 킵 그룹화 ── 한 번의 등록(동일 group_id) = 1개 카드
  const keepGroups = useMemo(() => {
    const groups = {};
    keepsItems.forEach(k => {
      const gid = k.group_id || `single-${k.id}`;
      if (!groups[gid]) groups[gid] = { id: gid, items: [] };
      groups[gid].items.push(k);
    });
    // 그룹 정렬: 가장 최근 created_at 기준
    const arr = Object.values(groups).map(g => ({
      ...g,
      latestCreatedAt: g.items.reduce((max, i) => {
        const t = i.created_at ? new Date(i.created_at).getTime() : 0;
        return t > max ? t : max;
      }, 0),
    }));
    arr.sort((a, b) => b.latestCreatedAt - a.latestCreatedAt);
    return arr;
  }, [keepsItems]);

  // ─── 킵(Keep) 테이블 row 렌더 헬퍼 ── 배송시트지 스타일 (킵 관리 페이지 + 배송시트지 sub-tab 공유)
  const KEEP_GRID = '36px 100px 220px 110px 1fr 130px 160px 130px 1fr 110px';
  const KEEP_MIN_WIDTH = 1380;
  const renderKeepTableHeader = () => (
    <div style={{display:'grid',gridTemplateColumns:KEEP_GRID,minWidth:KEEP_MIN_WIDTH,borderBottom:'2px solid var(--border)',background:'#f5f0fa',position:'sticky',top:0,zIndex:5}}>
      {['','날짜','__model_memo__','수량','시리얼','거래처','__author_req__','예상 출고일','메모','처리'].map(h => (
        <div key={h} style={{padding:'12px 10px',fontSize:13,fontWeight:600,color:'var(--fg-2)',lineHeight:1.3}}>
          {h === '__author_req__'
            ? <>작성자 / 요청자</>
            : h === '__model_memo__'
              ? <>모델명<div style={{fontSize:11,fontWeight:500,color:'var(--fg-3)',marginTop:2}}>제품 메모</div></>
              : h}
        </div>
      ))}
    </div>
  );
  const renderKeepGroup = (group) => {
    if (!group || !Array.isArray(group.items) || group.items.length === 0) return null;
    const shared = group.items[0] || {}; // 그룹 공통 필드 (모든 행이 동일)
    const activeItems = group.items.filter(i => i.status === 'active');
    const allShipped = group.items.every(i => i.status === 'shipped');
    const allReleased = group.items.every(i => i.status === 'released');
    const someActive = activeItems.length > 0;
    const groupEditable = someActive;

    // ── 그룹 헤더 인라인 셀 (거래처/요청자/작성자/예상출고일/메모)
    const groupCell = (field, displayValue, opts = {}) => {
      const isEditing = editingKeepGroupCell && editingKeepGroupCell.groupId === group.id && editingKeepGroupCell.field === field;
      if (!groupEditable) return <span style={opts.style || {}}>{displayValue}</span>;
      if (isEditing) {
        const commit = async (val) => { await updateKeepGroupField(group.id, field, val); setEditingKeepGroupCell(null); };
        if (opts.type === 'select') {
          return (
            <select autoFocus value={editingKeepGroupCell.value || ''} onClick={e=>e.stopPropagation()}
              onChange={e=>{ setEditingKeepGroupCell({...editingKeepGroupCell, value: e.target.value}); commit(e.target.value); }}
              onBlur={()=>setEditingKeepGroupCell(null)}
              style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:12,outline:'none',background:'var(--bg-card)',cursor:'pointer',minWidth:100}}>
              <option value="">선택 안 함</option>
              {opts.options.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          );
        }
        if (opts.type === 'date') {
          return (
            <input type="date" autoFocus value={editingKeepGroupCell.value || ''} onClick={e=>e.stopPropagation()}
              onChange={e=>setEditingKeepGroupCell({...editingKeepGroupCell, value: e.target.value})}
              onBlur={()=>commit(editingKeepGroupCell.value)}
              onKeyDown={e=>{ if (e.key === 'Enter') commit(editingKeepGroupCell.value); else if (e.key === 'Escape') setEditingKeepGroupCell(null); }}
              style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:11,outline:'none'}}/>
          );
        }
        return (
          <input autoFocus value={editingKeepGroupCell.value || ''} onClick={e=>e.stopPropagation()}
            onChange={e=>setEditingKeepGroupCell({...editingKeepGroupCell, value: e.target.value})}
            onBlur={()=>commit(editingKeepGroupCell.value)}
            onKeyDown={e=>{ if (e.key === 'Enter') commit(editingKeepGroupCell.value); else if (e.key === 'Escape') setEditingKeepGroupCell(null); }}
            placeholder={opts.placeholder || ''}
            style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:12,outline:'none',minWidth:120}}/>
        );
      }
      return (
        <span onDoubleClick={()=>setEditingKeepGroupCell({groupId: group.id, field, value: shared[field] == null ? '' : shared[field]})}
          title="더블클릭하여 수정"
          style={{cursor:'pointer',display:'inline-block',minHeight:18,...(opts.style || {})}}>
          {displayValue}
        </span>
      );
    };

    // ── 품목 행 인라인 셀 (수량/시리얼/제품메모) — 행 단위
    const itemCell = (k, field, displayValue, opts = {}) => {
      const isActive = k.status === 'active';
      const isEditing = editingKeepCell && editingKeepCell.id === k.id && editingKeepCell.field === field;
      if (!isActive) return <span style={opts.style || {}}>{displayValue}</span>;
      if (isEditing) {
        const commit = async (val) => { await updateKeepField(k, field, val); setEditingKeepCell(null); };
        if (opts.type === 'number') {
          return (
            <input type="number" min={1} autoFocus value={editingKeepCell.value} onClick={e=>e.stopPropagation()}
              onChange={e=>setEditingKeepCell({...editingKeepCell, value: e.target.value})}
              onBlur={()=>commit(parseInt(editingKeepCell.value, 10))}
              onKeyDown={e=>{ if (e.key === 'Enter') commit(parseInt(editingKeepCell.value, 10)); else if (e.key === 'Escape') setEditingKeepCell(null); }}
              style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:12,outline:'none',width:'100%',textAlign:'center',fontWeight:700}}/>
          );
        }
        if (opts.type === 'serial') {
          return (
            <div style={{position:'relative',width:'100%'}} onClick={e=>e.stopPropagation()}>
              <input autoFocus value={editingKeepCell.value || ''}
                onFocus={()=>searchInlineKeepSerial(k, editingKeepCell.value || '')}
                onChange={e=>{ setEditingKeepCell({...editingKeepCell, value: e.target.value}); searchInlineKeepSerial(k, e.target.value); }}
                onBlur={()=>setTimeout(()=>commit(editingKeepCell.value), 150)}
                onKeyDown={e=>{ if (e.key === 'Enter') commit(editingKeepCell.value); else if (e.key === 'Escape') setEditingKeepCell(null); }}
                placeholder="클릭 → DB 시리얼 표시"
                style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:12,outline:'none',width:'100%',fontFamily:'monospace'}}/>
              {editingKeepCell.serialResults && editingKeepCell.serialResults.length > 0 && (
                <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:6,marginTop:2,maxHeight:160,overflowY:'auto',zIndex:30,boxShadow:'0 4px 16px rgba(0,0,0,0.15)'}}>
                  <div style={{padding:'4px 8px',fontSize:10,color:'var(--fg-3)',background:'var(--bg-subtle)',borderBottom:'1px solid var(--border-mute)'}}>재고 시리얼 {editingKeepCell.serialResults.length}건</div>
                  {editingKeepCell.serialResults.map(r => (
                    <div key={r.serial_number} onMouseDown={e=>{ e.preventDefault(); commit(r.serial_number); }}
                      style={{padding:'6px 10px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)',fontFamily:'monospace',fontSize:11}}
                      onMouseEnter={e=>e.currentTarget.style.background='#f5f0fa'}
                      onMouseLeave={e=>e.currentTarget.style.background=''}>
                      {r.serial_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        }
        return (
          <input autoFocus value={editingKeepCell.value || ''} onClick={e=>e.stopPropagation()}
            onChange={e=>setEditingKeepCell({...editingKeepCell, value: e.target.value})}
            onBlur={()=>commit(editingKeepCell.value)}
            onKeyDown={e=>{ if (e.key === 'Enter') commit(editingKeepCell.value); else if (e.key === 'Escape') setEditingKeepCell(null); }}
            placeholder={opts.placeholder || ''}
            style={{padding:'4px 6px',border:'2px solid var(--purple)',borderRadius:5,fontSize:12,outline:'none',width:'100%'}}/>
        );
      }
      return (
        <span onDoubleClick={()=>setEditingKeepCell({id: k.id, field, value: k[field] == null ? '' : k[field], serialResults: []})}
          title="더블클릭하여 수정"
          style={{cursor:'pointer',display:'inline-block',minHeight:18,...(opts.style || {})}}>
          {displayValue}
        </span>
      );
    };

    const addForm = addItemForms[group.id];

    // 각 품목의 시리얼 슬롯 N개에 맞춰 행 높이 통일 (모델명/수량/시리얼/제품메모 열 정렬용)
    const itemBlockMinHeight = (k) => Math.max(40, (k.qty || 1) * 30 + 8);
    const statusColor = allShipped ? '#009844' : allReleased ? '#76777d' : '#7b1fa2';
    const statusIcon = allShipped ? 'check_circle' : allReleased ? 'block' : 'bookmark';
    const rowBg = allShipped || allReleased ? '#fafafa' : '#fff';
    const rowOpacity = allShipped || allReleased ? 0.62 : 1;

    return (
      <div key={group.id} style={{display:'grid',gridTemplateColumns:KEEP_GRID,minWidth:KEEP_MIN_WIDTH,borderBottom:'1px solid var(--border)',borderLeft:`3px solid ${statusColor}`,background:rowBg,opacity:rowOpacity,transition:'background 0.15s'}}
        onMouseEnter={e=>{ if (rowOpacity===1) e.currentTarget.style.background='#fcfaff'; }}
        onMouseLeave={e=>{ e.currentTarget.style.background=rowBg; }}>

        {/* 1: 상태 아이콘 */}
        <div style={{padding:'14px 6px',display:'flex',alignItems:'flex-start',justifyContent:'center'}}>
          <div title={allShipped?'전체 출고완료':allReleased?'전체 해제':'활성'} style={{width:26,height:26,borderRadius:7,background:statusColor+'1f',display:'flex',alignItems:'center',justifyContent:'center'}}>
            <Icon name={statusIcon} size={14} style={{color:statusColor}}/>
          </div>
        </div>

        {/* 2: 날짜 */}
        <div style={{padding:'14px 10px',fontSize:13,fontWeight:600,color:'var(--fg-1)'}}>
          {shared.created_at ? fmtDate(String(shared.created_at).slice(0,10)) : '-'}
          <div style={{fontSize:11,fontWeight:500,color:'var(--fg-3)',marginTop:2}}>
            {shared.created_at ? fmtTime(shared.created_at) : ''}
          </div>
          {allShipped && <div style={{marginTop:5}}><span style={{padding:'2px 7px',background:'rgba(0,152,68,0.12)',color:'var(--success)',borderRadius:5,fontSize:10,fontWeight:700}}>출고완료</span></div>}
          {allReleased && <div style={{marginTop:5}}><span style={{padding:'2px 7px',background:'rgba(0,0,0,0.06)',color:'var(--fg-3)',borderRadius:5,fontSize:10,fontWeight:700}}>해제</span></div>}
        </div>

        {/* 3: 모델명 (per item, stacked) + 제품 메모 + 품목 추가 버튼 */}
        <div style={{padding:'14px 10px',display:'flex',flexDirection:'column',gap:6}}>
          {group.items.map(k => {
            const isShipped = k.status === 'shipped';
            const isReleased = k.status === 'released';
            return (
              <div key={k.id} style={{minHeight:itemBlockMinHeight(k),display:'flex',flexDirection:'column',gap:3,opacity:k.status==='active'?1:0.6}}>
                <div style={{fontFamily:'monospace',fontWeight:700,fontSize:13,color:'var(--fg-1)',wordBreak:'break-all',lineHeight:1.35,display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                  {k.product_name}
                  {isShipped && <span style={{padding:'1px 6px',background:'rgba(0,152,68,0.12)',color:'var(--success)',borderRadius:4,fontSize:10,fontWeight:700}}>출고완료</span>}
                  {isReleased && <span style={{padding:'1px 6px',background:'rgba(0,0,0,0.08)',color:'var(--fg-3)',borderRadius:4,fontSize:10,fontWeight:700}}>해제됨</span>}
                </div>
                {/* 제품 메모 — 모델명 바로 아래 */}
                <div style={{fontSize:11,color: k.item_memo ? 'var(--fg-2)' : '#bbb',wordBreak:'break-all',lineHeight:1.4}}>
                  {itemCell(k, 'item_memo', k.item_memo || <span style={{fontStyle:'italic',color:'var(--fg-disabled)'}}>— 제품 메모 추가 —</span>, {placeholder:'제품 메모'})}
                </div>
              </div>
            );
          })}
          {/* + 품목 추가 (active 그룹) */}
          {someActive && !addForm && (
            <button onClick={()=>openAddItemForm(group.id)} title="이 그룹에 품목 추가"
              style={{marginTop:4,padding:'6px 10px',border:'1.5px dashed var(--purple)',background:'var(--bg-card)',color:'var(--purple)',borderRadius:7,fontSize:11,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:4,alignSelf:'flex-start'}}>
              <Icon name="add" size={12}/> 품목 추가
            </button>
          )}
          {/* addForm: 제품 검색 */}
          {addForm && (
            <div style={{marginTop:6,position:'relative'}}>
              {addForm.product_name ? (
                <div style={{padding:'7px 9px',background:'rgba(123,31,162,0.1)',border:'1.5px solid var(--purple)',borderRadius:7,display:'flex',alignItems:'center',gap:5}}>
                  <div style={{flex:1,fontFamily:'monospace',fontWeight:700,fontSize:12,color:'#4a148c',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{addForm.product_name}</div>
                  <button onClick={()=>updateAddItemForm(group.id, { product_name: '', productSearch: '', productResults: [], serial_number: '', serialResults: [] })}
                    style={{padding:'2px 6px',border:'1px solid var(--purple)',background:'var(--bg-card)',color:'var(--purple)',borderRadius:4,fontSize:10,fontWeight:600,cursor:'pointer'}}>변경</button>
                </div>
              ) : (
                <>
                  <input value={addForm.productSearch} onChange={e=>searchAddItemProduct(group.id, e.target.value)}
                    autoFocus placeholder="제품명 검색"
                    style={{...inputStyle,fontFamily:'monospace',fontWeight:600,fontSize:12,padding:'7px 9px'}}/>
                  {addForm.productResults && addForm.productResults.length > 0 && (
                    <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,marginTop:3,maxHeight:200,overflowY:'auto',zIndex:30,boxShadow:'0 4px 16px rgba(0,0,0,0.15)'}}>
                      {addForm.productResults.map(r => {
                        const reserved = activeKeepsByModel[r.product_name] || 0;
                        return (
                          <div key={r.product_name} onMouseDown={e=>{ e.preventDefault(); updateAddItemForm(group.id, { product_name: r.product_name, productSearch: '', productResults: [] }); }}
                            style={{padding:'7px 10px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#f5f0fa'}
                            onMouseLeave={e=>e.currentTarget.style.background=''}>
                            <div style={{fontFamily:'monospace',fontSize:12,fontWeight:700}}>{r.product_name}</div>
                            <div style={{fontSize:10,color:'var(--fg-3)',display:'flex',gap:6}}>
                              <span>{r.category}</span>
                              {reserved > 0 && <span style={{color:'var(--purple)',fontWeight:700}}>활성 킵 {reserved}개</span>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* 4: 수량 stepper (per item, stacked) */}
        <div style={{padding:'14px 6px',display:'flex',flexDirection:'column',gap:6,alignItems:'center'}}>
          {group.items.map(k => {
            const isActive = k.status === 'active';
            return (
              <div key={k.id} style={{minHeight:itemBlockMinHeight(k),display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:2,width:'100%'}}>
                {isActive ? (
                  <div style={{display:'inline-flex',alignItems:'center',gap:0,background:'#f5f0fa',borderRadius:8,border:'1.5px solid rgba(123,31,162,0.25)',overflow:'hidden',height:28}}>
                    <button type="button" onClick={()=>{ if (k.qty > 1) updateKeepField(k, 'qty', k.qty - 1); }}
                      disabled={k.qty <= 1}
                      style={{width:26,height:26,border:'none',background:k.qty<=1?'transparent':'rgba(123,31,162,0.08)',color:k.qty<=1?'#c6c6cd':'#7b1fa2',fontSize:15,fontWeight:700,cursor:k.qty<=1?'default':'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'background 0.15s'}}
                      onMouseEnter={e=>{if(k.qty>1)e.currentTarget.style.background='rgba(123,31,162,0.18)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background=k.qty<=1?'transparent':'rgba(123,31,162,0.08)';}}>−</button>
                    <span style={{minWidth:26,textAlign:'center',fontSize:13,fontWeight:700,color:'#4a148c',userSelect:'none'}}>{k.qty}</span>
                    <button type="button" onClick={()=>updateKeepField(k, 'qty', k.qty + 1)}
                      style={{width:26,height:26,border:'none',background:'rgba(123,31,162,0.08)',color:'var(--purple)',fontSize:15,fontWeight:700,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'background 0.15s'}}
                      onMouseEnter={e=>{e.currentTarget.style.background='rgba(123,31,162,0.18)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='rgba(123,31,162,0.08)';}}>+</button>
                  </div>
                ) : (
                  <span style={{fontSize:13,fontWeight:600,color:'var(--fg-3)'}}>×{k.qty}</span>
                )}
              </div>
            );
          })}
          {/* addForm 수량 */}
          {addForm && (
            <div style={{width:'100%',marginTop:6,display:'flex',justifyContent:'center'}}>
              <input type="number" min={1} value={addForm.qty}
                onChange={e=>{ const q = Math.max(1, parseInt(e.target.value || '1', 10)); const oldS = addForm.serials || ['']; const ns = [...oldS]; while (ns.length < q) ns.push(''); while (ns.length > q) ns.pop(); updateAddItemForm(group.id, { qty: q, serials: ns }); }}
                style={{...inputStyle,fontWeight:700,fontSize:12,padding:'5px 6px',textAlign:'center',width:60,height:28}}/>
            </div>
          )}
        </div>

        {/* 5: 시리얼 슬롯 + 제품 메모 (per item, stacked) */}
        <div style={{padding:'14px 10px',display:'flex',flexDirection:'column',gap:6}}>
          {group.items.map(k => {
            const isActive = k.status === 'active';
            const arr = Array.isArray(k.serials) && k.serials.length > 0 ? k.serials : (k.serial_number ? [k.serial_number] : ['']);
            const slots = [...arr];
            while (slots.length < k.qty) slots.push('');
            slots.length = k.qty;
            return (
              <div key={k.id} style={{minHeight:itemBlockMinHeight(k),display:'flex',flexDirection:'column',gap:2}}>
                <div style={{fontFamily:'monospace',fontSize:12}}>
                  {slots.map((s, sIdx) => (
                    <KeepSerialSlot
                      key={`${k.id}-${sIdx}`}
                      idx={sIdx}
                      initial={s || ''}
                      isActive={isActive}
                      productName={k.product_name}
                      supabaseClient={supabase}
                      onCommit={async (slotIdx, val) => {
                        const prevSlots = [...slots];
                        const ns = [...slots];
                        ns[slotIdx] = (val || '').trim();
                        const filled = ns.filter(x => x);
                        await supabase.from('product_keeps').update({
                          serials: ns,
                          serial_number: filled[0] || null,
                        }).eq('id', k.id);
                        // v7.156: 킵이 active 상태일 때만 예약 동기화 (완료/해제된 건은 무시)
                        if (k.status === 'active') {
                          await syncKeepSerialReservation(prevSlots, ns);
                        }
                        await loadKeeps();
                      }}
                    />
                  ))}
                </div>
              </div>
            );
          })}
          {/* addForm 시리얼 */}
          {addForm && (
            <div style={{marginTop:6,display:'flex',flexDirection:'column',gap:4}}>
              <div style={{display:'flex',flexDirection:'column',gap:0}}>
                {(addForm.serials && addForm.serials.length > 0 ? addForm.serials : ['']).map((s, sIdx) => (
                  <KeepSerialSlot
                    key={sIdx}
                    idx={sIdx}
                    initial={s || ''}
                    isActive={!!addForm.product_name}
                    productName={addForm.product_name}
                    supabaseClient={supabase}
                    onCommit={(slotIdx, val) => {
                      const ns = [...(addForm.serials || [''])];
                      while (ns.length < addForm.qty) ns.push('');
                      ns[slotIdx] = val;
                      updateAddItemForm(group.id, { serials: ns });
                    }}
                  />
                ))}
              </div>
              <input value={addForm.item_memo}
                onChange={e=>updateAddItemForm(group.id, { item_memo: e.target.value })}
                placeholder="제품 메모 (선택)"
                style={{...inputStyle,fontSize:11,padding:'5px 8px',marginTop:2}}/>
              <div style={{display:'flex',gap:6,marginTop:4}}>
                <button onClick={()=>submitAddItem(group)}
                  disabled={!addForm.product_name}
                  style={{padding:'5px 12px',border:'none',background:!addForm.product_name?'var(--surface-mute)':'var(--purple-solid)',color:'#fff',borderRadius:6,fontSize:11,fontWeight:700,cursor:!addForm.product_name?'default':'pointer'}}>저장</button>
                <button onClick={()=>closeAddItemForm(group.id)}
                  style={{padding:'5px 12px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>취소</button>
              </div>
            </div>
          )}
        </div>

        {/* 6: 거래처 (group) */}
        <div style={{padding:'14px 10px',fontSize:13,fontWeight:700,color:'var(--fg-1)',wordBreak:'break-all'}}>
          {groupCell('dealer_name', shared.dealer_name || '-')}
        </div>

        {/* 7: 작성자 / 요청자 (group) */}
        <div style={{padding:'14px 10px',fontSize:12,display:'flex',flexDirection:'column',gap:4}}>
          <div><span style={{color:'var(--fg-3)',marginRight:5,fontSize:11}}>작성자</span>{groupCell('author', shared.author || <span style={{fontStyle:'italic',color:'var(--fg-disabled)'}}>—</span>, {type:'select', options: staffList})}</div>
          <div><span style={{color:'var(--fg-3)',marginRight:5,fontSize:11}}>요청자</span>{groupCell('requester', shared.requester || <span style={{fontStyle:'italic',color:'var(--fg-disabled)'}}>—</span>, {type:'select', options: staffList})}</div>
        </div>

        {/* 8: 예상 출고일 (group) */}
        <div style={{padding:'14px 10px',fontSize:13,color:'var(--fg-2)'}}>
          {groupCell('expected_date', shared.expected_date ? fmtDate(shared.expected_date) : <span style={{fontStyle:'italic',color:'var(--fg-disabled)'}}>—</span>, {type:'date'})}
        </div>

        {/* 9: 메모 (group) */}
        <div style={{padding:'14px 10px',fontSize:12,color:'var(--fg-2)',wordBreak:'break-all'}}>
          {groupCell('memo', shared.memo || <span style={{fontStyle:'italic',color:'var(--fg-disabled)'}}>— 메모 추가 —</span>, {placeholder:'메모'})}
        </div>

        {/* 10: 처리 — 품목별 × 삭제 (per item, stacked) + 그룹 출고처리 */}
        <div style={{padding:'14px 6px',display:'flex',flexDirection:'column',gap:6,alignItems:'stretch'}}>
          {group.items.map(k => (
            <div key={k.id} style={{minHeight:itemBlockMinHeight(k),display:'flex',alignItems:'flex-start',justifyContent:'center',paddingTop:2}}>
              {k.status === 'active' && (
                <button onClick={()=>deleteKeep(k)} title="이 품목 삭제"
                  style={{width:24,height:24,border:'none',background:'transparent',color:'var(--danger)',borderRadius:5,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0,opacity:0.5,transition:'all 0.15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.opacity='1';e.currentTarget.style.background='rgba(186,26,26,0.1)';}}
                  onMouseLeave={e=>{e.currentTarget.style.opacity='0.5';e.currentTarget.style.background='transparent';}}>
                  <Icon name="close" size={14}/>
                </button>
              )}
            </div>
          ))}
          {someActive && (
            <button onClick={()=>markKeepGroupShipped(group)} title={`그룹 전체 출고처리 (${activeItems.length}품목)`}
              style={{marginTop:4,padding:'7px 8px',background:'var(--success-solid)',color:'#fff',border:'none',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',gap:4,lineHeight:1.3}}>
              <Icon name="local_shipping" size={13}/> 출고처리
            </button>
          )}
        </div>

      </div>
    );
  };

  return (
    <>
      <link href="https://fonts.googleapis.com/css2?family=Be+Vietnam+Pro:wght@400;500;600;700&display=swap" rel="stylesheet"/>
      <link href="https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap" rel="stylesheet"/>
      <style>{`:root{color-scheme:light;--bg-page:#f8f9ff;--bg-card:#fff;--bg-subtle:#fafbff;--bg-accent-soft:#eff4ff;--bg-accent-strong:#dce9ff;--bg-info:#e5eeff;--bg-mute:#f0f2f6;--border:#e2e8f0;--border-mute:#f0f2f6;--border-strong:#c6c6cd;--surface-mute:#c6c6cd;--surface-inverse:#0b1c30;--fg-1:#0b1c30;--fg-2:#45464d;--fg-3:#76777d;--fg-disabled:#c6c6cd;--accent:#00668a;--accent-solid:#00668a;--success:#009844;--success-solid:#009844;--warning:#e65100;--warning-solid:#e65100;--danger:#ba1a1a;--danger-solid:#ba1a1a;--purple:#7b1fa2;--purple-solid:#7b1fa2;--scrollbar:#cbd5e1;--bg-card-glass:rgba(255,255,255,0.8)}[data-theme="dark"]{color-scheme:dark;--bg-page:#0f172a;--bg-card:#172033;--bg-subtle:#1b2438;--bg-accent-soft:#1c2942;--bg-accent-strong:#24334f;--bg-info:#1f2c45;--bg-mute:#1e2739;--border:#28344a;--border-mute:#222c40;--border-strong:#3a4761;--surface-mute:#2b3547;--surface-inverse:#33415c;--fg-1:#e8eef7;--fg-2:#c4ccd9;--fg-3:#8f98a8;--fg-disabled:#5b6880;--accent:#4fb6dd;--accent-solid:#0d7ba4;--success:#3ec97a;--success-solid:#0a8f45;--warning:#ff8d43;--warning-solid:#c85a10;--danger:#ff6b6b;--danger-solid:#b62222;--purple:#c58ade;--purple-solid:#7e39a3;--scrollbar:#3a4761;--bg-card-glass:rgba(23,32,51,0.82)}*{box-sizing:border-box;margin:0;padding:0}body{font-family:'Be Vietnam Pro',sans-serif;background:var(--bg-page);color:var(--fg-1)}::-webkit-scrollbar{width:4px}::-webkit-scrollbar-thumb{background:var(--scrollbar);border-radius:10px}input:focus,select:focus,textarea:focus{outline:none;border-color:#40c2fd!important;box-shadow:0 0 0 3px rgba(64,194,253,0.15)!important}@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}@keyframes pulse{0%,100%{opacity:1;transform:scale(1)}50%{opacity:0.5;transform:scale(0.85)}}@keyframes shake{0%,100%{transform:translateX(0)}20%,60%{transform:translateX(-4px)}40%,80%{transform:translateX(4px)}}@keyframes pulseDot{0%{box-shadow:0 0 0 0 rgba(0,152,68,0.55)}70%{box-shadow:0 0 0 8px rgba(0,152,68,0)}100%{box-shadow:0 0 0 0 rgba(0,152,68,0)}}@keyframes fadeIn{from{opacity:0}to{opacity:1}}@keyframes starTwinkle{0%,100%{filter:drop-shadow(0 0 1px rgba(251,191,36,0.6)) drop-shadow(0 0 3px rgba(251,191,36,0.35));transform:scale(1)}50%{filter:drop-shadow(0 0 3px rgba(251,191,36,0.95)) drop-shadow(0 0 9px rgba(253,224,71,0.7));transform:scale(1.08)}}@keyframes starPop{0%{transform:scale(1)}30%{transform:scale(1.35)}60%{transform:scale(0.92)}100%{transform:scale(1)}}@keyframes starGlow{0%,100%{box-shadow:0 2px 6px rgba(251,191,36,0.35), 0 0 0 1px rgba(255,255,255,0.6) inset}50%{box-shadow:0 2px 12px rgba(251,191,36,0.7), 0 0 0 1px rgba(255,255,255,0.6) inset}}

/* ═══ v7.355: NAS 호환성 — 시리즈 칩 ═══ */

/* ── 시리즈 필터 칩 ── */
.nas-chip{display:inline-flex;align-items:center;gap:6px;padding:7px 12px;
  border:1px solid var(--border-strong);border-radius:9999px;background:var(--bg-card);
  color:var(--fg-2);font-size:13px;font-weight:600;font-family:inherit;cursor:pointer;
  transition:background .15s,border-color .15s,color .15s,transform .12s}
.nas-chip:hover{border-color:var(--accent);color:var(--accent)}
.nas-chip:active{transform:scale(.96)}
.nas-chip[data-on="1"]{background:var(--accent);border-color:var(--accent);color:#fff}
.nas-chip__n{padding:1px 7px;border-radius:9999px;background:var(--bg-mute);
  color:var(--fg-3);font-size:11px;font-weight:700}
.nas-chip[data-on="1"] .nas-chip__n{background:rgba(255,255,255,.24);color:#fff}
/* v7.357: 검색 결과 0건인 시리즈 — 지우지 않고 흐리게만 둔다.
   ⚠ 아예 감추면 칩 줄이 검색할 때마다 들썩여 누르려던 칩이 사라진다. */
.nas-chip[data-empty="1"]{opacity:.38}
.nas-chip[data-empty="1"]:hover{opacity:.72}

/* ═══ v7.359: 호환 부품 행 ═══ */
.qc-row{position:relative;transition:border-color .14s,background .14s,transform .12s}
/* ⚠ 글로우가 붙은 행(.glow-row)은 테두리 색을 건드리지 않는다.
   실선 테두리와 도는 빛이 겹치면 서로를 가린다 (v7.363). */
.qc-row:not(.glow-row):hover{border-color:var(--accent)!important}
.qc-row:hover{transform:translateY(-1px)}
.qc-row:active{transform:none}
/* ⚠ --success-bg 는 정의돼 있지 않다 (:root 에는 --success / --success-solid 뿐).
   없는 변수를 쓰면 배경이 통째로 사라진다 → rgba 리터럴을 쓴다. */
.qc-row[data-copied="1"]{background:rgba(0,152,68,0.10)!important}
[data-theme="dark"] .qc-row[data-copied="1"]{background:rgba(62,201,122,0.14)!important}
/* 복사 완료 표시 — 토스트 대신 누른 카드에 직접 띄운다.
   ⚠ v7.359 는 ::after 를 썼는데, v7.363 에서 붙인 BorderGlow 가 같은 자리를 쓴다
     (.glow-row::after = 커서 스포트라이트). 실제 요소로 바꿔 충돌을 없앴다. */
.qc-copied{position:absolute;top:6px;right:8px;z-index:4;
  padding:1px 7px;border-radius:9999px;background:var(--success-solid);color:#fff;
  font-size:10px;font-weight:700;opacity:0;pointer-events:none;transition:opacity .12s}
.qc-row[data-copied="1"] .qc-copied{opacity:1;animation:qcPop .18s ease-out}
@keyframes qcPop{from{opacity:0;transform:translateY(-3px)}to{opacity:1;transform:none}}
@media (prefers-reduced-motion: reduce){
  .qc-row{transition:border-color .14s,background .14s}
  .qc-row:hover{transform:none}
  .qc-row[data-copied="1"] .qc-copied{animation:none}
}

/* 폴더 격자는 GlassIcons.css 의 .icon-btns 가 담당한다 */
`}</style>
      {/* v7.224: 퀵 차량 추천 목록 — 페이지 전역에서 참조하도록 최상단에 한 번만 렌더.
          (출고요청 폼 / 배송시트지 인라인 편집 두 곳이 같은 id를 쓰므로 중복 정의 금지) */}
      <datalist id="quickVehicleOptions">
        {QUICK_VEHICLES.map(v => <option key={v} value={v}/>)}
      </datalist>
      {/* v7.174: 자동이월 팝오버 — 배지 클릭 시 뜸, 지정 날짜로 이월 */}
      {deferPopover && (
        <>
          {/* 외부 클릭 감지용 배경 */}
          <div onClick={()=>{ if(!deferSaving) setDeferPopover(null); }}
            style={{position:'fixed',inset:0,zIndex:99997,background:'transparent'}}/>
          {/* 팝오버 본체 */}
          <div style={{
            position:'fixed', zIndex:99998,
            left:Math.min(deferPopover.x, window.innerWidth - 280),
            top:Math.min(deferPopover.y, window.innerHeight - 200),
            width:260, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12,
            boxShadow:'0 8px 24px -6px rgba(15,23,42,0.2)',
            padding:14, fontFamily:'Be Vietnam Pro,sans-serif',
          }}>
            <div style={{fontSize:13,fontWeight:800,color:'var(--fg-1)',marginBottom:4,display:'flex',alignItems:'center',gap:6}}>
              <Icon name="schedule" size={16} style={{color:'#f57c00'}}/>
              특정 날짜로 이월
            </div>
            <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:10,lineHeight:1.5}}>
              선택한 날짜로 요청을 즉시 이동합니다.<br/>자동 이월은 유지 (그 날짜 이후 매일).
            </div>
            <input type="date" value={deferPopoverDate}
              onChange={e=>setDeferPopoverDate(e.target.value)}
              disabled={deferSaving}
              style={{width:'100%',padding:'8px 10px',fontSize:14,border:'1px solid var(--border)',borderRadius:8,marginBottom:10,fontFamily:'inherit'}}/>
            {/* 프리셋 */}
            <div style={{display:'flex',gap:5,marginBottom:12,flexWrap:'wrap'}}>
              {[
                {label:'내일',days:1},
                {label:'+3일',days:3},
                {label:'다음주 월',days:'nextMon'},
                {label:'+2주',days:14},
              ].map(p=>(
                <button key={p.label} onClick={()=>{
                  const d = new Date();
                  if (p.days === 'nextMon') {
                    const dow = d.getDay(); // 0=일 1=월
                    const add = dow === 0 ? 1 : (8 - dow); // 다음 월요일
                    d.setDate(d.getDate() + add);
                  } else {
                    d.setDate(d.getDate() + p.days);
                  }
                  const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
                  setDeferPopoverDate(iso);
                }}
                  disabled={deferSaving}
                  style={{padding:'4px 9px',fontSize:11,fontWeight:700,background:'var(--bg-page)',border:'1px solid var(--border)',borderRadius:6,cursor:'pointer',color:'var(--fg-2)',fontFamily:'inherit'}}>
                  {p.label}
                </button>
              ))}
            </div>
            <div style={{display:'flex',gap:6}}>
              <button onClick={applyDeferDate} disabled={deferSaving || !deferPopoverDate}
                style={{flex:1,padding:'8px 12px',background:deferSaving?'#cbd5e1':'#f57c00',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:800,cursor:deferSaving?'not-allowed':'pointer',fontFamily:'inherit'}}>
                {deferSaving ? '이월 중...' : '적용'}
              </button>
              <button onClick={()=>{ if(!deferSaving) setDeferPopover(null); }}
                disabled={deferSaving}
                style={{padding:'8px 14px',background:'var(--bg-card)',color:'var(--fg-3)',border:'1px solid var(--border)',borderRadius:8,fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                취소
              </button>
            </div>
          </div>
        </>
      )}
      {/* v7.187: 장부처리 3-state 드롭다운 팝오버 */}
      {/* v7.239: '수기입력' 선택 시 팝오버가 입력 모드로 전환된다 (mode:'manual') */}
      {bookkeptPopover && (
        <>
          <div onClick={()=>setBookkeptPopover(null)}
            style={{position:'fixed',inset:0,zIndex:99997,background:'transparent'}}/>
          <div style={{
            position:'fixed', zIndex:99998,
            left:Math.min(bookkeptPopover.x, window.innerWidth - (bookkeptPopover.mode==='manual'?260:220)),
            top:Math.min(bookkeptPopover.y, window.innerHeight - 200),
            width:bookkeptPopover.mode==='manual'?240:200, background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:10,
            boxShadow:'0 8px 24px -6px rgba(15,23,42,0.2)',
            padding:8, fontFamily:'Be Vietnam Pro,sans-serif',
            display:'flex', flexDirection:'column', gap:4,
          }}>
            <div style={{fontSize:10,fontWeight:800,color:'var(--fg-3)',padding:'4px 8px 2px',letterSpacing:'0.05em'}}>
              {bookkeptPopover.mode==='manual' ? '수기입력 내용' : '장부처리 상태'}
            </div>
            {bookkeptPopover.mode === 'manual' ? (
              <div onClick={e=>e.stopPropagation()} style={{display:'flex',flexDirection:'column',gap:6,padding:'0 2px 2px'}}>
                <input ref={bookkeptInputRef} autoFocus
                  defaultValue={bookkeptPopover.manual || ''}
                  placeholder="예: 세금계산서 별도 발행"
                  onKeyDown={async e => {
                    if (e.key === 'Enter') {
                      const v = e.currentTarget.value;
                      const rowId = bookkeptPopover.rowId;
                      setBookkeptPopover(null);
                      await saveBookkept(rowId, 'manual', v);
                    } else if (e.key === 'Escape') {
                      // v7.244: 전역 Esc(모달 닫기)가 뒤이어 실행돼 팝오버까지 닫는 것을 막는다.
                      //   여기서는 '팝오버 닫기'가 아니라 '목록으로 뒤로'가 맞다.
                      e.stopPropagation();
                      setBookkeptPopover(p => p ? { ...p, mode: undefined } : null);
                    }
                  }}
                  style={{padding:'7px 9px',border:'2px solid #546e7a',borderRadius:6,fontSize:12,fontWeight:600,outline:'none',background:'var(--bg-card)',fontFamily:'inherit',width:'100%',boxSizing:'border-box'}}/>
                <div style={{display:'flex',gap:6}}>
                  <button
                    onClick={()=>setBookkeptPopover(p => p ? { ...p, mode: undefined } : null)}
                    style={{flex:1,padding:'7px',border:'1px solid var(--border)',borderRadius:6,background:'var(--bg-card)',color:'var(--fg-3)',fontSize:11.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                    뒤로
                  </button>
                  <button
                    onClick={async ()=>{
                      const v = bookkeptInputRef.current ? bookkeptInputRef.current.value : '';
                      const rowId = bookkeptPopover.rowId;
                      setBookkeptPopover(null);
                      await saveBookkept(rowId, 'manual', v);
                    }}
                    style={{flex:1.4,padding:'7px',border:'none',borderRadius:6,background:'#546e7a',color:'#fff',fontSize:11.5,fontWeight:800,cursor:'pointer',fontFamily:'inherit'}}>
                    저장
                  </button>
                </div>
                <div style={{fontSize:10,color:'var(--fg-3)',lineHeight:1.45}}>
                  Enter 로도 저장됩니다. 비워두면 뱃지에 <b>수기입력</b>만 표시됩니다.
                </div>
              </div>
            ) : (
            ['completed','pending','manual'].map(k => {
              const s = BOOKKEPT_STYLES[k];
              const isActive = bookkeptPopover.current === k;
              return (
                <button key={k}
                  onClick={async () => {
                    // v7.239: 수기입력은 곧바로 저장하지 않고 입력 모드로 전환.
                    //   이미 'manual' 이어도 내용을 고칠 수 있어야 하므로 isActive 여도 진입한다.
                    if (k === 'manual') {
                      setBookkeptPopover(p => p ? { ...p, mode: 'manual' } : null);
                      return;
                    }
                    const rowId = bookkeptPopover.rowId;
                    setBookkeptPopover(null);
                    if (isActive) return; // 현재 값과 같으면 skip
                    await saveBookkept(rowId, k);
                  }}
                  style={{
                    display:'flex',alignItems:'center',gap:8,
                    padding:'8px 10px',border:'none',
                    background:isActive?s.bg:'transparent',color:s.color,
                    borderRadius:6,fontSize:12,fontWeight:isActive?800:600,cursor:'pointer',
                    textAlign:'left',fontFamily:'inherit',
                  }}
                  onMouseEnter={e=>{if(!isActive)e.currentTarget.style.background=s.bg}}
                  onMouseLeave={e=>{if(!isActive)e.currentTarget.style.background='transparent'}}>
                  <Icon name={s.icon} size={14}/>
                  <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                    {k==='manual' && bookkeptPopover.manual ? bookkeptPopover.manual : s.label}
                  </span>
                  {k==='manual' && <Icon name="chevron_right" size={13}/>}
                  {isActive && k!=='manual' && <Icon name="check" size={13}/>}
                  {isActive && k==='manual' && <Icon name="check" size={13}/>}
                </button>
              );
            })
            )}
          </div>
        </>
      )}
      {/* v7.158: 모바일 앱 종료 안내 토스트 */}
      {mobileExitToast && (
        <div style={{
          position:'fixed', bottom:'max(28px, env(safe-area-inset-bottom))', left:'50%', transform:'translateX(-50%)',
          zIndex:99999, background:'rgba(15,23,42,0.92)', color:'#fff',
          padding:'11px 20px', borderRadius:999, fontSize:13, fontWeight:700,
          fontFamily:'Be Vietnam Pro,sans-serif', letterSpacing:'-0.01em',
          boxShadow:'0 10px 30px -8px rgba(15,23,42,0.5)',
          animation:'fadeIn 0.15s ease-out',
          pointerEvents:'none',
        }}>
          한 번 더 누르면 종료됩니다
        </div>
      )}
      {/* v7.163~166 debug 뱃지 — v7.167에서 제거 (native 브릿지 검증 완료) */}
      <ClickSpark sparkColor="#3B82F6" sparkSize={10} sparkRadius={20} sparkCount={8} duration={400}>
      <div style={{display:'flex',minHeight:'100vh'}}>
        {/* v7.121: 모바일에서는 사이드바 완전 숨김 — 세그먼트 탭만 사용 */}
        {!isMobile && (() => {
          // v7.327: 배경과 글자색을 한 곳에서 정한다 (아래 로고·메뉴·구분선이 모두 이 값을 쓴다)
          const sbg = sidebarBgOf(theme === 'dark');
          const sbFg1 = sbg.invert ? '#f1f5f9' : 'var(--fg-1)';
          const sbFg2 = sbg.invert ? 'rgba(241,245,249,0.72)' : 'var(--fg-2)';
          const sbFg3 = sbg.invert ? 'rgba(241,245,249,0.55)' : 'var(--fg-3)';
          const sbLine = sbg.invert ? 'rgba(255,255,255,0.18)' : 'var(--border-strong)';
          return (
          <aside ref={sidebarRef} style={{
            width:256,
            background: sbg.bg,
            padding:24,
            display:'flex',
            flexDirection:'column',
            flexShrink:0,
            borderRight:'1px solid var(--border)',
            position:'relative',   // v7.329: 파티클 캔버스의 기준 박스
            overflow:'hidden',     // 캔버스가 모서리 밖으로 새지 않게
          }}>
          {/* v7.329: 다크 모드 배경 파티클.
              absolute 라 flex 흐름에서 빠지므로 아래 레이아웃에는 영향이 없다.
              pointer-events:none — 없으면 메뉴 클릭이 캔버스에 막힌다. */}
          {sbg.particles && (
            <div style={{position:'absolute',inset:0,zIndex:0,pointerEvents:'none',opacity:0.85}}>
              <Particles {...SIDEBAR_PARTICLES} hoverTargetRef={sidebarRef}/>
            </div>
          )}
          <div style={{marginBottom:32,position:'relative',zIndex:1}}>
            <div style={{fontSize:28,fontWeight:700,color:sbFg1,letterSpacing:'-0.01em',lineHeight:1.2}}>River</div>
            <div style={{fontSize:28,fontWeight:700,color:sbFg1,letterSpacing:'-0.01em',lineHeight:1.2}}>Network</div>
            <div style={{fontSize:14,fontWeight:600,color:sbFg2,marginTop:6}}>Logistics Management</div>
          </div>
          <div style={{flex:1,display:'flex',flexDirection:'column',margin:'0 -24px',minHeight:0,position:'relative',zIndex:1}}>
            <FlowingMenu
              items={navFlat.map(n => ({
                id: n.id,
                text: n.label,
                icon: n.icon,
                active: n.active,
                sub: n.sub,
                expandable: n.expandable,
                expanded: n.expanded,
                // v7.348: [관리] 는 페이지 이동이 아니라 펼침/접기다.
                //   펼치면서 관리 하위가 아닌 곳에 있었으면 첫 하위(제품 표준화 관리)로 들어간다.
                //   다른 메뉴로 나가면 접는다 — 펼친 채로 두면 관계없는 화면에서 자리만 먹는다.
                onClick: n.expandable
                  ? () => {
                      const opening = !adminOpen;
                      setAdminOpen(opening);
                      if (opening && !ADMIN_PAGES.includes(page)) {
                        setPage(ADMIN_PAGES[0]); setPg(0); setSearch(''); setTxSubTab('list');
                      }
                    }
                  : () => {
                      setPage(n.id); setPg(0); setSearch(''); setTxSubTab('list');
                      if (!n.sub) setAdminOpen(false);
                      if (isMobile) setMobileNavOpen(false); /* v7.116 */
                    }
              }))}
              speed={15}
              textColor={sbg.invert ? '#f1f5f9' : '#0b1c30'}
              bgColor="transparent"
              marqueeBgColor={SIDEBAR_MARQUEE_BG}
              marqueeTextColor={SIDEBAR_MARQUEE_FG}
              activeBgColor={SIDEBAR_ACTIVE_BG}
              activeTextColor={SIDEBAR_ACTIVE_FG}
              borderColor={sbg.invert ? 'rgba(255,255,255,0.16)' : 'rgba(0,102,138,0.18)'}
            />
          </div>
          <div style={{borderTop:`1px solid ${sbLine}`,paddingTop:16,marginTop:'auto',display:'flex',flexDirection:'column',gap:10,position:'relative',zIndex:1}}>
            {/* 연결 상태 */}
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{width:36,height:36,borderRadius:'50%',background:sbg.invert?'rgba(255,255,255,0.12)':'var(--bg-accent-strong)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon name="person" size={20} style={{color:sbFg2}}/></div>
              <div style={{flex:1,minWidth:0}}>
                <div style={{fontSize:13,fontWeight:600,color:sbFg1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>운영자</div>
                <div style={{fontSize:11,fontWeight:500,color:sbFg3,display:'flex',alignItems:'center',gap:4}}>
                  <span style={{width:6,height:6,borderRadius:'50%',background:connected?'var(--success-solid)':connected===false?'var(--danger-solid)':'#76777d',display:'inline-block'}}/>
                  {connected?`${stats.total.toLocaleString()}건 관리 중`:connected===false?'연결 실패':'연결 중...'}
                </div>
              </div>
            </div>
            {/* 동시 접속자 (Realtime presence) */}
            <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',background:'rgba(0,152,68,0.08)',border:'1px solid rgba(0,152,68,0.18)',borderRadius:10}}>
              <div style={{position:'relative',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <span style={{width:8,height:8,borderRadius:'50%',background:'var(--success-solid)',display:'inline-block',boxShadow:'0 0 0 0 rgba(0,152,68,0.6)',animation:'pulseDot 2s infinite'}}/>
              </div>
              <Icon name="group" size={14} style={{color:'var(--success)'}}/>
              <div style={{fontSize:11,fontWeight:600,color:sbFg1}}>
                <span style={{color:'var(--success)',fontWeight:700}}>{onlineCount}명</span> 접속 중
              </div>
            </div>

            {/* v7.440: 모바일 화면 복귀 — /desktop.html 로 들어온 경우에만 노출.
                 실제 PC 사용자(index.html)에게는 렌더되지 않는다.
                 앱은 주소창이 없어 이 버튼이 유일한 복귀 수단이다. */}
            {typeof window !== 'undefined' && window.location.pathname.endsWith('/desktop.html') && (
              <button
                onClick={() => { window.location.href = window.location.origin + '/'; }}
                style={{display:'flex',alignItems:'center',justifyContent:'center',gap:6,width:'100%',padding:'9px 10px',
                        border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-card)',color:'var(--fg-2)',
                        fontSize:11.5,fontWeight:700,fontFamily:'inherit',cursor:'pointer',marginTop:2}}>
                <Icon name="smartphone" size={14}/> 모바일 화면
              </button>
            )}

            {/* 앱 버전 */}
            <div title={`Inventura Pro ${APP_VERSION} · ${APP_VERSION_DATE}`} style={{fontSize:10,color:sbFg3,textAlign:'center',marginTop:2,fontFamily:'monospace',letterSpacing:'0.04em',opacity:0.7}}>
              Inventura Pro · {APP_VERSION}
            </div>
          </div>
        </aside>
          );
        })()}
        {/* v7.121: 사이드바 조건부 닫기 (!isMobile) 끝 */}
        <main style={{flex:1,padding:isMobile?0:32,overflow:'auto',maxHeight:'100vh',minWidth:0/* v7.121: 모바일 padding 0 (컴포넌트가 edge-to-edge 렌더) */}}>
          {/* v7.117: 모바일 미대응 페이지 안내 — 화이트리스트 외 + 우회 안 함 시 표시 */}
          {(isMobile && !MOBILE_FRIENDLY_PAGES.has(page) && !mobileBypassPage) ? (
            <MobileGuardPage
              pageLabel={({txlog:'배송시트지',inbound:'입고 등록',inbound_hdd:'HDD/SSD 입고',ship:'출고 요청',keeps:'킵 관리',demo:'데모 관리',catalog:'제품 표준화 관리',audit:'재고 실사',backup:'백업',scan:'모바일 스캔',inventory:'재고 목록'})[page] || page}
              onContinue={()=>setMobileBypassPage(true)}
              onGoToScan={()=>setPage('scan')}
            />
          ) : (<>

          {/* ═══ INVENTORY ═══ */}
          {/* v7.118: 모바일에서는 MobileInventory 컴포넌트 */}
          {page==='inventory' && isMobile && (
            <Suspense fallback={<div style={{padding:40,textAlign:'center',color:'var(--fg-3)'}}>재고 목록 로딩...</div>}>
              <MobileInventory onExit={()=>setPage('scan')} onNavigate={(p)=>setPage(p)}/>
            </Suspense>
          )}
          {page==='inventory' && !isMobile && (<div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28,gap:16}}>
              <div><h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>재고 목록</h1><p style={{fontSize:16,color:'var(--fg-2)'}}>전체 물류 센터의 통합 재고 현황을 확인하고 관리합니다.</p></div>
              <div style={{display:'flex',gap:10,alignItems:'center'}}>
                {/* v7.139: 재고 목록 엑셀 다운로드 — list/hdd 탭에서만 노출 */}
                {(inventoryTab === 'list' || inventoryTab === 'hdd') && (
                  <button onClick={downloadInventoryXlsx} disabled={downloadingInvXlsx}
                    title="현재 상태·유형·탭 필터 조건에 맞는 재고를 엑셀로 다운로드"
                    style={{display:'flex',alignItems:'center',gap:8,padding:'10px 18px',background:downloadingInvXlsx?'var(--bg-mute)':'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:12,fontSize:14,fontWeight:600,cursor:downloadingInvXlsx?'wait':'pointer',color:'var(--success)',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',transition:'all 0.15s',whiteSpace:'nowrap',opacity:downloadingInvXlsx?0.7:1}}
                    onMouseEnter={e=>{if(!downloadingInvXlsx){e.currentTarget.style.background='#e5f5ec';e.currentTarget.style.borderColor='var(--success)';}}}
                    onMouseLeave={e=>{if(!downloadingInvXlsx){e.currentTarget.style.background='var(--bg-card)';e.currentTarget.style.borderColor='var(--border-strong)';}}}>
                    <Icon name={downloadingInvXlsx ? 'hourglass_top' : 'download'} size={18} style={{color:'var(--success)'}}/>
                    {downloadingInvXlsx ? '다운로드 중...' : '엑셀 다운로드'}
                  </button>
                )}
                {/* v7.408: 이벤트 캘린더 버튼 제거. 그 자리에 파주 재고 업로드 + 새로고침을 옮긴다 (대시보드 탭에서만).
                    ⚠ 여기선 sd(대시보드 IIFE 지역변수)가 안 보이므로 stockDashboard 를 직접 참조한다. */}
                {inventoryTab === 'dashboard' && (
                  <>
                    <input ref={pajuFileRef} type="file" accept=".xls,.xlsx,.htm,.html" style={{display:'none'}}
                      onChange={e=>{ const f=(Array.from(e.target.files||[]))[0]; e.target.value=''; handlePajuUpload(f); }}/>
                    <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2}}>
                      <button onClick={()=>pajuFileRef.current?.click()} disabled={stockDashboard.pajuUploading}
                        style={{display:'flex',alignItems:'center',gap:6,padding:'10px 18px',background:'var(--bg-card)',border:'1px solid #1565c0',borderRadius:10,fontSize:14,fontWeight:600,cursor:stockDashboard.pajuUploading?'wait':'pointer',color:'#1565c0'}}>
                        <Icon name={stockDashboard.pajuUploading?'hourglass_empty':'upload_file'} size={16}/>
                        {stockDashboard.pajuUploading?'처리 중…':'파주 재고 업로드'}
                      </button>
                      <span style={{fontSize:10.5,color:'var(--fg-3)'}}>
                        {stockDashboard.pajuSnapshot
                          ? `기준일 ${stockDashboard.pajuSnapshot} · ${Object.keys(stockDashboard.paju).length}종`
                          : (stockDashboard.pajuReady ? '업로드된 파주 재고 없음' : 'paju_stock 테이블 미생성')}
                      </span>
                    </div>
                    <button onClick={loadStockDashboard} disabled={stockDashboard.loading}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'10px 18px',background:'var(--bg-card)',border:'1px solid var(--success)',borderRadius:10,fontSize:14,fontWeight:600,cursor:stockDashboard.loading?'wait':'pointer',color:'var(--success)'}}>
                      <Icon name={stockDashboard.loading?'hourglass_empty':'refresh'} size={16}/>
                      {stockDashboard.loading?'로딩 중…':'새로고침'}
                    </button>
                  </>
                )}
              </div>
            </div>
            {/* v7.40: 재고 목록 / HDD·SSD / 재고 대시보드 탭 토글 — v7.60: HDD/SSD 카테고리 전용 탭 추가 */}
            <div style={{display:'flex',gap:6,marginBottom:20,padding:4,background:'var(--bg-mute)',borderRadius:12,width:'fit-content'}}>
              {[['dashboard','dashboard','재고 대시보드','#009844'],['list','inventory_2','재고 목록','#00668a'],['hdd','storage','HDD/SSD','#d84315'],['analytics','insights','사용 현황','#5c6bc0']].map(([id,icon,label,color])=>{
                const sel = inventoryTab === id;
                return (
                  <button key={id} onClick={()=>{setInventoryTab(id);setPg(0);}}
                    style={{display:'inline-flex',alignItems:'center',gap:6,padding:'8px 18px',border:'none',borderRadius:9,fontSize:13,fontWeight:700,cursor:'pointer',transition:'all 0.15s',background:sel?'var(--bg-card)':'transparent',color:sel?color:'var(--fg-3)',boxShadow:sel?'0 1px 3px rgba(0,0,0,0.08)':'none'}}>
                    <Icon name={icon} size={16}/>
                    {label}
                  </button>
                );
              })}
            </div>
            {(inventoryTab === 'list' || inventoryTab === 'hdd') && (<>
            <Card style={{padding:24,marginBottom:20}}><div style={{display:'flex',gap:12,alignItems:'center',marginBottom:16}}><div style={{flex:1,position:'relative'}}><Icon name="search" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/><input value={search} onChange={e=>{const v=e.target.value;setSearch(v);setPg(0);const cmd=v.trim().toLowerCase();if(cmd==='rest'){triggerRest();setSearch('');}else if(cmd==='mine'){setMineMode(true);setSearch('');}else if(cmd==='mirror'){setMirrorMode(true);setSearch('');}else if(cmd==='aim'){setAimMode(true);setSearch('');}}} onKeyDown={e=>e.key==='Enter'&&loadInventory()} placeholder="제품명 · 시리얼 · 거래처 · 날짜로 검색 (예: TS-264, 이선, 2026.6.12)" style={{...inputStyle,paddingLeft:42,fontSize:16}}/></div></div>
              <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>{[['all','전체'],['in_stock','재고'],['ship_hold','출고대기'],['reserved','예약'],['unsellable','판매불가'],['shipped','출고']].map(([v,l])=>(<button key={v} onClick={()=>{setFilter(v);setPg(0);}} style={{padding:'8px 18px',border:`1px solid ${filter===v?(v==='unsellable'?'var(--warning)':v==='ship_hold'?'#e65100':v==='reserved'?'#0277bd':'#40c2fd'):'var(--border-strong)'}`,borderRadius:9999,fontSize:13,fontWeight:500,cursor:'pointer',background:filter===v?(v==='unsellable'?'rgba(245,124,0,0.12)':v==='ship_hold'?'rgba(245,124,0,0.12)':v==='reserved'?'rgba(64,194,253,0.15)':'rgba(64,194,253,0.12)'):'var(--bg-card)',color:filter===v?(v==='unsellable'?'var(--warning)':v==='ship_hold'?'#e65100':v==='reserved'?'#0277bd':'#004d6a'):'var(--fg-2)'}}>{l}</button>))}
              <span style={{width:1,height:22,background:'var(--surface-mute)',margin:'0 4px'}}/>
              <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,marginRight:2}}>유형:</span>
              {/* v7.184: AS 옵션 추가 (HDD/SSD 입고 유형에 이미 존재하는데 필터엔 누락돼 있었음) */}
              {[['all','전체'],['신품','신품'],['AS','AS'],['리페킹','리페킹'],['조립출고','조립출고']].map(([v,l])=>{
                const color = v==='신품'?'var(--success)':v==='AS'?'#546e7a':v==='리페킹'?'var(--warning)':v==='조립출고'?'var(--purple)':'var(--fg-2)';
                return (
                  <button key={v} onClick={()=>{setStockTypeFilter(v);setPg(0);}}
                    style={{padding:'6px 13px',border:`1px solid ${stockTypeFilter===v?color:'var(--fg-disabled)'}`,borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',background:stockTypeFilter===v?color+'1a':'var(--bg-card)',color:stockTypeFilter===v?color:'var(--fg-2)'}}>{l}</button>
                );
              })}
              <button onClick={()=>{setFilter('all');setStockTypeFilter('all');setSearch('');setPg(0);}} style={{padding:'8px 18px',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:13,fontWeight:500,cursor:'pointer',background:'var(--bg-card)',color:'var(--fg-3)',marginLeft:'auto'}}>초기화</button></div>
            </Card>

            {/* 검색 모드: 모델별 그룹 카드 뷰 */}
            {search.trim() ? (()=>{
              // v7.28: 매칭 위치 분석 — 날짜/모델/시리얼/비고/거래처 5가지
              const searchLower = search.trim().toLowerCase();
              // 날짜 패턴 감지 (서버 query와 동일 로직)
              let parsedDate = null;
              const dateMatch = search.trim().match(/^(\d{2,4})[.\-/]\s*(\d{1,2})[.\-/]\s*(\d{1,2})$/);
              if (dateMatch) {
                let [, y, mo, d] = dateMatch;
                if (y.length === 2) y = '20' + y;
                parsedDate = `${y}-${mo.padStart(2,'0')}-${d.padStart(2,'0')}`;
              }
              // 모델별 그룹핑 — 모델명 매칭이면 전체, 시리얼/note/dealer 매칭이면 매칭된 row만
              const grouped = {};
              items.forEach(item => {
                const k = item.model;
                if (!grouped[k]) grouped[k] = {
                  model: item.model, category: item.category, items: [],
                  in_stock: 0, shipped: 0, unsellable: 0, reserved: 0, ship_hold: 0, total: 0,
                  source_taiwan: 0, source_paju: 0, source_hq: 0, source_unknown: 0,
                  wh_hq: 0, wh_paju: 0, wh_other: 0,
                  // 유형 뱃지 (shipped 제외 non-shipped 전체 = in_stock + reserved + unsellable)
                  newProduct: 0, repacked: 0, assembled: 0,
                  asProduct: 0, // v7.184: AS 유형 추가
                  // v7.102: 출고된 시리얼의 stock_type breakdown (도넛 legend '출고됨' 옆 표기용)
                  shippedNewProduct: 0, shippedRepacked: 0, shippedAssembled: 0,
                  shippedAs: 0, // v7.184: 출고된 AS 유형 추가
                  // v7.184: 판매가능(in_stock)만의 stock_type breakdown (도넛 legend '판매가능' 옆 뱃지용)
                  inStockNewProduct: 0, inStockRepacked: 0, inStockAssembled: 0, inStockAs: 0,
                  lastShipped: null,
                  // 매칭 위치 추적
                  modelMatched: false,
                  matchedSerialCount: 0,
                  matchedNoteCount: 0,
                  matchedDealerCount: 0,
                  matchedDateCount: 0, // v7.28
                  matchedTxLogCount: 0, // v7.29: transaction_log 매칭
                };
                // 매칭 위치 분석 (early exit)
                let modelMatch = false, serialMatch = false, noteMatch = false, dealerMatch = false, dateMatch_ = false, txLogMatch = false;
                // transaction_log 매칭 우선 (출고 이력)
                if (txMatchedSerials.has(item.serial)) {
                  txLogMatch = true;
                }
                // 날짜 매칭
                else if (parsedDate && (item.receipt_date === parsedDate || item.sale_date === parsedDate)) {
                  dateMatch_ = true;
                } else if (item.model && item.model.toLowerCase().includes(searchLower)) {
                  modelMatch = true;
                } else if (item.serial && item.serial.toLowerCase().includes(searchLower)) {
                  serialMatch = true;
                } else {
                  const noteText = (item.note || '') + ' ' + (item.memo || '');
                  if (noteText && noteText.toLowerCase().includes(searchLower)) {
                    noteMatch = true;
                  } else if (item.dealer_name && item.dealer_name.toLowerCase().includes(searchLower)) {
                    dealerMatch = true;
                  } else {
                    return;
                  }
                }
                const g = grouped[k];
                if (modelMatch) g.modelMatched = true;
                if (serialMatch) g.matchedSerialCount++;
                if (noteMatch) g.matchedNoteCount++;
                if (dealerMatch) g.matchedDealerCount++;
                if (dateMatch_) g.matchedDateCount++;
                if (txLogMatch) g.matchedTxLogCount++;
                g.items.push(item);
                g.total++;
                if (item.status === 'in_stock') g.in_stock++;
                else if (item.status === 'unsellable') g.unsellable++;
                else if (item.status === 'reserved') g.reserved++; // v7.80
                else if (item.status === 'ship_hold') g.ship_hold++; // v7.424: 출고 대기 예약
                else if (item.status === 'shipped') {
                  g.shipped++;
                  if (item.sale_date && (!g.lastShipped || item.sale_date > g.lastShipped)) g.lastShipped = item.sale_date;
                }
                if (item.source === 'taiwan') g.source_taiwan++;
                else if (item.source === 'paju') g.source_paju++;
                else if (item.source === 'hq') g.source_hq++;
                else g.source_unknown++;
                if (item.warehouse === 'paju') g.wh_paju++;
                else if (item.warehouse === 'hq' || !item.warehouse) g.wh_hq++;
                else g.wh_other++;
                // v7.102: 유형 뱃지는 shipped 제외한 나머지 상태만 집계
                //          shipped는 별도 카운터로 → legend '출고됨' 옆 breakdown 표기
                // v7.184: AS 유형 추가 + in_stock 별도 집계 (판매가능 legend 옆 뱃지용)
                if (item.status === 'shipped') {
                  if (item.stock_type === '신품') g.shippedNewProduct++;
                  else if (item.stock_type === 'AS') g.shippedAs++;
                  else if (item.stock_type === '리페킹') g.shippedRepacked++;
                  else if (item.stock_type === '조립출고') g.shippedAssembled++;
                } else {
                  if (item.stock_type === '신품') g.newProduct++;
                  else if (item.stock_type === 'AS') g.asProduct++;
                  else if (item.stock_type === '리페킹') g.repacked++;
                  else if (item.stock_type === '조립출고') g.assembled++;
                  // v7.184: 판매가능(in_stock)만 별도 집계 — 도넛 legend 판매가능 옆에 뱃지 표시용
                  if (item.status === 'in_stock') {
                    if (item.stock_type === '신품') g.inStockNewProduct++;
                    else if (item.stock_type === 'AS') g.inStockAs++;
                    else if (item.stock_type === '리페킹') g.inStockRepacked++;
                    else if (item.stock_type === '조립출고') g.inStockAssembled++;
                  }
                }
              });
              // 빈 그룹 제거
              const groups = Object.values(grouped).filter(g => g.items.length > 0).sort((a,b) => b.total - a.total);

              if (loading) return <Card style={{padding:48,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>검색 중...</Card>;
              if (groups.length === 0) return <Card style={{padding:48,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>검색 결과 없음</Card>;

              return (
                <div>
                  {filter !== 'all' && (
                    <div style={{padding:'8px 14px',background:'#fff8e1',border:'1px solid #ffd54f',borderRadius:8,fontSize:12,color:'var(--warning)',marginBottom:12,display:'flex',alignItems:'center',gap:8}}>
                      <Icon name="info" size={14}/>
                      현재 <strong>{filter === 'in_stock' ? '재고' : filter === 'shipped' ? '출고' : filter === 'reserved' ? '예약' : filter === 'ship_hold' ? '출고대기' : '판매불가'}</strong> 필터가 적용 중입니다. 거래처/날짜 검색 결과가 부족하면 <button onClick={()=>{setFilter('all');setPg(0);}} style={{padding:'2px 8px',border:'none',background:'var(--warning-solid)',color:'#fff',borderRadius:5,fontSize:11,fontWeight:700,cursor:'pointer'}}>전체로 변경</button>해보세요.
                    </div>
                  )}
                  <div style={{fontSize:13,color:'var(--fg-3)',marginBottom:14}}>
                    검색 결과: <strong style={{color:'var(--fg-1)'}}>{groups.length}개 모델</strong> · <strong style={{color:'var(--fg-1)'}}>{groups.reduce((s,g) => s + g.items.length, 0)}개 시리얼</strong>
                    {' '}<span style={{color:'#bbb'}}>(전체 fetch {items.length}건)</span>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:20}}>
                    {groups.map(g => {
                      // v7.98: stockPct 제거 — 가로 막대 그래프에서만 쓰였음 (도넛으로 대체됨)
                      return (
                        <Card key={g.model} style={{overflow:'hidden'}}>
                          {/* 헤더: 모델 카드 (모델별 재고 현황 스타일) */}
                          <div style={{padding:20,borderBottom:'1px solid var(--border)',background:'linear-gradient(180deg, var(--bg-subtle) 0%, var(--bg-card) 100%)'}}>
                            <div style={{display:'flex',alignItems:'flex-start',gap:8,marginBottom:12}}>
                              <div style={{flex:1,minWidth:0}}>
                                <div style={{fontSize:18,fontWeight:700,color:'var(--fg-1)',wordBreak:'break-all',marginBottom:4}}>{highlightMatch(g.model, search)}</div>
                                <div style={{display:'flex',flexWrap:'wrap',gap:5,alignItems:'center'}}>
                                  {g.category && <Badge variant="category">{g.category}</Badge>}
                                  {/* v7.18: 매칭 위치 배지 — 어디서 검색어와 매칭됐는지 표시 */}
                                  {g.modelMatched && (
                                    <span title="모델명에 검색어 포함" style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(0,152,68,0.12)',color:'var(--success)',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="check_circle" size={11}/> 모델명 일치
                                    </span>
                                  )}
                                  {g.matchedSerialCount > 0 && (
                                    <span title={`시리얼에 "${search.trim()}" 포함`} style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(0,102,138,0.12)',color:'var(--accent)',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="qr_code" size={11}/> 시리얼 {g.matchedSerialCount}건
                                    </span>
                                  )}
                                  {g.matchedNoteCount > 0 && (
                                    <span title="비고/메모에 검색어 포함" style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(123,31,162,0.12)',color:'var(--purple)',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="sticky_note_2" size={11}/> 비고 {g.matchedNoteCount}건
                                    </span>
                                  )}
                                  {g.matchedDealerCount > 0 && (
                                    <span title="거래처명에 검색어 포함" style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(216,67,21,0.12)',color:'#d84315',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="business" size={11}/> 거래처 {g.matchedDealerCount}건
                                    </span>
                                  )}
                                  {g.matchedDateCount > 0 && (
                                    <span title="입고일/출고일이 검색 날짜와 일치" style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(245,124,0,0.12)',color:'var(--warning)',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="event" size={11}/> 날짜 {g.matchedDateCount}건
                                    </span>
                                  )}
                                  {g.matchedTxLogCount > 0 && (
                                    <span title="출고 요청 비고/배송지에 검색어 포함 (transaction_log)" style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'rgba(123,31,162,0.12)',color:'var(--purple)',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="receipt_long" size={11}/> 출고이력 {g.matchedTxLogCount}건
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            {/* v7.98: 상태 분포 도넛 차트 (기존 가로 막대 → 원형) */}
                            {(() => {
                              const keepCount = activeKeepsByModel[g.product_name] || 0;
                              // g.total에 포함되는 상태만 도넛에 표시 (킵은 별도, g.total과 무관)
                              const segments = [
                                // v7.102: 라벨 변경 — 보유→판매가능, 출고→출고됨
                                { label:'판매가능', value: g.in_stock,  color:'var(--success)' },
                                { label:'출고대기', value: g.ship_hold || 0, color:'#e65100', title:'배송건에 담겨 출고 대기 중인 시리얼' }, // v7.424
                                { label:'예약',     value: g.reserved || 0, color:'#0277bd', title:'예약된 시리얼 (방문수령/킵)' },
                                { label:'판매불가', value: g.unsellable || 0, color:'var(--warning)' },
                                { label:'출고됨',   value: g.shipped,   color:'var(--danger)' },
                              ].filter(s => s.value > 0);
                              const R = 34;
                              const C = 2 * Math.PI * R;
                              let cumOffset = 0;
                              return (
                                <div style={{marginBottom:14, display:'flex', gap:20, alignItems:'center', flexWrap:'wrap'}}>
                                  {/* 도넛 SVG */}
                                  <div style={{position:'relative', flexShrink:0}}>
                                    <svg width={90} height={90} style={{transform:'rotate(-90deg)', display:'block'}}>
                                      <circle cx={45} cy={45} r={R} fill="none" stroke="#f0f2f6" strokeWidth={12}/>
                                      {g.total > 0 && segments.map((s, i) => {
                                        const frac = s.value / g.total;
                                        const arcLen = frac * C;
                                        const gap = C - arcLen;
                                        const offset = -cumOffset;
                                        cumOffset += arcLen;
                                        return (
                                          <circle key={i} cx={45} cy={45} r={R} fill="none"
                                            stroke={s.color} strokeWidth={12}
                                            strokeDasharray={`${arcLen} ${gap}`}
                                            strokeDashoffset={offset}
                                            style={{transition:'all 0.4s'}}
                                          />
                                        );
                                      })}
                                    </svg>
                                    {/* 도넛 중앙 라벨 */}
                                    <div style={{position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', pointerEvents:'none'}}>
                                      <div style={{fontSize:9, fontWeight:700, color:'var(--fg-3)', letterSpacing:'0.05em'}}>TOTAL</div>
                                      <div style={{fontSize:20, fontWeight:700, color:'var(--fg-1)', lineHeight:1}}>{g.total}</div>
                                    </div>
                                  </div>
                                  {/* Legend */}
                                  <div style={{display:'flex', flexWrap:'wrap', gap:'6px 14px', flex:1, minWidth:0}}>
                                    {segments.map((s, i) => (
                                      <div key={i} title={s.title||''} style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:12, flexWrap:'wrap'}}>
                                        <span style={{width:10, height:10, borderRadius:'50%', background:s.color, flexShrink:0}}/>
                                        <span style={{fontWeight:600, color:s.color}}>{s.label}</span>
                                        <span style={{color:'var(--fg-2)', fontWeight:600}}>{s.value}</span>
                                        {/* v7.184: 판매가능 라벨 옆에 stock_type breakdown 뱃지 아이콘 표시 */}
                                        {/* v7.197: 뱃지 나열 → "(신품 8, 리페킹 1, 조립출고 1)" 괄호+콤마 표기 */}
                                        {s.label === '판매가능' && (()=>{
                                          const parts = [
                                            ['신품', g.inStockNewProduct, '#009844'],
                                            ['AS', g.inStockAs, '#546e7a'],
                                            ['리페킹', g.inStockRepacked, '#e65100'],
                                            ['조립출고', g.inStockAssembled, '#004d6a'],
                                          ].filter(([, n]) => n > 0);
                                          if (!parts.length) return null;
                                          return (
                                            <span style={{display:'inline-flex',alignItems:'center',flexWrap:'wrap',fontSize:11,fontWeight:700}}>
                                              <span style={{color:'#9aa0a6'}}>(</span>
                                              {parts.map(([label, n, color], i) => (
                                                <React.Fragment key={label}>
                                                  {i > 0 && <span style={{color:'#9aa0a6',marginRight:4}}>,</span>}
                                                  <span style={{color}}>{label} {n}</span>
                                                </React.Fragment>
                                              ))}
                                              <span style={{color:'#9aa0a6'}}>)</span>
                                            </span>
                                          );
                                        })()}
                                        {/* v7.102: (XX%) 제거. '출고됨' 라벨 옆에만 stock_type breakdown 표기 */}
                                        {/* v7.184: AS 케이스 추가 */}
                                        {s.label === '출고됨' && (g.shippedNewProduct>0 || g.shippedAs>0 || g.shippedRepacked>0 || g.shippedAssembled>0) && (
                                          <span style={{color:'var(--fg-3)', fontSize:11}}>
                                            ({[
                                              g.shippedNewProduct>0 && `신품 ${g.shippedNewProduct}개`,
                                              g.shippedAs>0 && `AS ${g.shippedAs}개`,
                                              g.shippedRepacked>0 && `리페킹 ${g.shippedRepacked}개`,
                                              g.shippedAssembled>0 && `조립출고 ${g.shippedAssembled}개`,
                                            ].filter(Boolean).join(', ')})
                                          </span>
                                        )}
                                      </div>
                                    ))}
                                    {keepCount > 0 && (
                                      <div title="킵(수량 단위 예약)된 수량. 시리얼별 매핑은 없으므로 등록 시점에만 경고됩니다." style={{display:'inline-flex', alignItems:'center', gap:6, fontSize:12, padding:'2px 8px', background:'rgba(123,31,162,0.08)', borderRadius:6}}>
                                        <Icon name="bookmark" size={12} style={{color:'var(--purple)'}}/>
                                        <span style={{fontWeight:600, color:'var(--purple)'}}>킵 {keepCount}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                            {/* 분포 뱃지 (v7.98: 보관 제거, 출처/유형만) */}
                            <div style={{display:'flex',flexWrap:'wrap',gap:'8px 20px',rowGap:8}}>
                              {(g.source_taiwan>0||g.source_paju>0||g.source_hq>0||g.source_unknown>0)&&(
                                <div style={{display:'flex',alignItems:'center',gap:6}}>
                                  <span style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>출처</span>
                                  {g.source_taiwan>0&&<span style={{padding:'2px 7px',borderRadius:5,fontSize:11,fontWeight:600,background:'#fff4e5',color:'#a05000',border:'1px solid #ffb74d'}}>🇹🇼 {g.source_taiwan}</span>}
                                  {g.source_paju>0&&<span style={{padding:'2px 7px',borderRadius:5,fontSize:11,fontWeight:600,background:'#e8f5e9',color:'#1b5e20',border:'1px solid #81c784'}}>🇰🇷 {g.source_paju}</span>}
                                  {g.source_hq>0&&<span style={{padding:'2px 7px',borderRadius:5,fontSize:11,fontWeight:600,background:'#e3f2fd',color:'#0d47a1',border:'1px solid #64b5f6'}}>🏢 {g.source_hq}</span>}
                                  {g.source_unknown>0&&<span style={{padding:'2px 7px',borderRadius:5,fontSize:11,fontWeight:600,background:'#f5f5f5',color:'#424242',border:'1px solid var(--border-strong)'}}>❓ {g.source_unknown}</span>}
                                </div>
                              )}
                              {(g.newProduct>0||g.asProduct>0||g.repacked>0||g.assembled>0)&&(
                                <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                  <span style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>유형</span>
                                  {/* v7.197: 뱃지 나열 → "(신품 8, 리페킹 1, 조립출고 1)" 괄호+콤마 표기 */}
                                  {(()=>{
                                    const parts = [
                                      ['신품', g.newProduct, '#009844'],
                                      ['AS', g.asProduct, '#546e7a'],
                                      ['리페킹', g.repacked, '#e65100'],
                                      ['조립출고', g.assembled, '#004d6a'],
                                    ].filter(([, n]) => n > 0);
                                    if (!parts.length) return null;
                                    return (
                                      <span style={{display:'inline-flex',alignItems:'center',flexWrap:'wrap',fontSize:11,fontWeight:600}}>
                                        <span style={{color:'#9aa0a6'}}>(</span>
                                        {parts.map(([label, n, color], i) => (
                                          <React.Fragment key={label}>
                                            {i > 0 && <span style={{color:'#9aa0a6',marginRight:4}}>,</span>}
                                            <span style={{color}}>{label} {n}</span>
                                          </React.Fragment>
                                        ))}
                                        <span style={{color:'#9aa0a6'}}>)</span>
                                      </span>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          </div>
                          {/* 시리얼 리스트 (테이블) */}
                          <div style={{overflowX:'auto'}}>
                            <div style={{display:'grid',gridTemplateColumns:'30px 156px 1fr 1fr 95px 95px 90px',minWidth:866,background:'var(--bg-subtle)',borderBottom:'1px solid var(--border)'}}>{[{h:'',key:null},{h:'품목 코드',key:'serial'},{h:'보관/출처',key:null},{h:'비고',key:null},{h:'입고일',key:'receipt_date'},{h:'출고일',key:'sale_date'},{h:'상태',key:'status'}].map((col,i)=>{
                              if(!col.key) return <div key={i} style={{padding:'10px 14px',fontSize:12,fontWeight:600,color:'var(--fg-2)'}}>{col.h}</div>;
                              const active = invSortBy === col.key;
                              return (
                                <div key={i}
                                  onClick={()=>{ if(active) setInvSortDir(d=>d==='asc'?'desc':'asc'); else { setInvSortBy(col.key); setInvSortDir('desc'); } }}
                                  style={{padding:'10px 14px',fontSize:12,fontWeight:active?700:600,color:active?'var(--accent)':'var(--fg-2)',cursor:'pointer',userSelect:'none',display:'flex',alignItems:'center',gap:3}}
                                  title="클릭하여 정렬">
                                  {col.h}
                                  <Icon name={active?(invSortDir==='asc'?'arrow_upward':'arrow_downward'):'unfold_more'} size={12} style={{color:active?'var(--accent)':'#bbb'}}/>
                                </div>
                              );
                            })}</div>
                            {/* v7.236: 이 그룹에서 expand가 열려 있으면 스크롤 박스를 키워 편집 패널이 잘리지 않게 한다 */}
                            <div style={{maxHeight:g.items.some(it=>it.serial===expandedSerial)?680:360,overflowY:'auto',transition:'max-height 0.2s'}}>
                              {g.items.slice().sort(invRowCompare(invSortBy, invSortDir)).map((item, i) => {
                                const memoVal = inventoryMemos[item.serial] !== undefined ? inventoryMemos[item.serial] : (item.memo || '');
                                const memoDirty = inventoryMemos[item.serial] !== undefined && inventoryMemos[item.serial] !== (item.memo || '');
                                const isShipped = item.status === 'shipped';
                                const isExpanded = expandedSerial === item.serial;
                                return (
                                <React.Fragment key={i}>
                                {/* v7.236: 출고 건만 열리던 expand를 전 항목으로 확장 — 검색 중에도 입고 정보 수정 가능 */}
                                <div style={{display:'grid',gridTemplateColumns:'30px 156px 1fr 1fr 95px 95px 90px',minWidth:866,borderBottom:(isExpanded||i<g.items.length-1)?'1px solid #E2E8F0':'none',transition:'all 0.15s',background:isExpanded?'var(--bg-info)':'',cursor:'pointer'}}
                                  onClick={()=>setExpandedSerial(s=>s===item.serial?null:item.serial)}
                                  onMouseEnter={e=>{if(!isExpanded)e.currentTarget.style.background='#F1F5F9'}} onMouseLeave={e=>{if(!isExpanded)e.currentTarget.style.background=''}}>
                                  <div style={{padding:'10px 6px',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--accent)'}}>
                                    <Icon name={isExpanded?'expand_more':'chevron_right'} size={16}/>
                                  </div>
                                  {/* v7.335: 위와 같은 이유 (그룹 뷰) */}
                                  <div title={item.serial}
                                    style={{padding:'10px 14px',fontSize:12.5,fontFamily:'monospace',color:'var(--fg-2)',wordBreak:'break-all',overflowWrap:'anywhere',minWidth:0,lineHeight:1.35,display:'flex',alignItems:'center'}}>{highlightMatch(item.serial, search)}</div>
                                  <div style={{padding:'10px 14px',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                    <span style={{fontSize:12,color:'var(--fg-2)'}}>{item.warehouse==='paju'?'파주':'본사'}</span>
                                    {/* v7.254: sourceBadge() 로 통일 — DOA 는 노란색 */}
                                    {item.source&&(()=>{const b=sourceBadge(item.source);return(
                                      <span style={{padding:'1px 6px',borderRadius:4,fontSize:10,fontWeight:600,background:b.bg,color:b.fg,border:`1px solid ${b.bd}`}}>{b.icon} {b.label}</span>
                                    );})()}
                                    {item.stock_type&&<span style={{padding:'1px 6px',borderRadius:4,fontSize:10,fontWeight:700,background:item.stock_type==='신품'?'rgba(0,152,68,0.1)':item.stock_type==='AS'?'rgba(84,110,122,0.12)':item.stock_type==='리페킹'?'rgba(255,152,0,0.1)':'rgba(64,194,253,0.15)',color:item.stock_type==='신품'?'var(--success)':item.stock_type==='AS'?'#546e7a':item.stock_type==='리페킹'?'var(--warning)':'#004d6a'}}>{item.stock_type}</span>}
                                  </div>
                                  <div style={{padding:'8px 10px',display:'flex',gap:4,alignItems:'center'}} onClick={e=>e.stopPropagation()}>
                                    <input value={memoVal}
                                      onChange={e=>setInventoryMemos(prev=>({...prev,[item.serial]:e.target.value}))}
                                      onKeyDown={e=>{if(e.key==='Enter'&&memoDirty){saveInventoryMemo(item.serial, memoVal);e.target.blur();}}}
                                      onBlur={()=>{if(memoDirty)saveInventoryMemo(item.serial, memoVal);}}
                                      placeholder="특이사항 입력..."
                                      style={{flex:1,minWidth:0,padding:'5px 8px',border:`1px solid ${memoDirty?'var(--accent)':'var(--border)'}`,borderRadius:6,fontSize:11,outline:'none',background:memoDirty?'var(--bg-card)':'transparent',fontFamily:'inherit',transition:'all 0.15s'}}/>
                                    {savingMemoSerial===item.serial && <Icon name="progress_activity" size={12} style={{color:'var(--accent)',animation:'spin 1s linear infinite'}}/>}
                                  </div>
                                  {/* v7.97: 입고일 셀 — 더블클릭으로 인라인 편집 (달력) */}
                                  {editingReceiptSerial === item.serial ? (
                                    <div style={{padding:'6px 10px'}} onClick={e=>e.stopPropagation()}>
                                      <input
                                        type="date"
                                        autoFocus
                                        defaultValue={item.receipt_date || ''}
                                        disabled={savingReceiptSerial === item.serial}
                                        onKeyDown={e => {
                                          if (e.key === 'Enter') { saveReceiptDate(item.serial, e.target.value); }
                                          if (e.key === 'Escape') { setEditingReceiptSerial(null); }
                                        }}
                                        onBlur={e => { saveReceiptDate(item.serial, e.target.value); }}
                                        style={{width:'100%',padding:'5px 8px',border:'1.5px solid var(--accent)',borderRadius:6,fontSize:12,outline:'none',fontFamily:'monospace',background:'var(--bg-card)'}}
                                      />
                                    </div>
                                  ) : (
                                    <div
                                      onClick={e=>e.stopPropagation()}
                                      onDoubleClick={()=>setEditingReceiptSerial(item.serial)}
                                      title="더블클릭으로 입고일 수정"
                                      style={{padding:'10px 14px',fontSize:12,color:item.receipt_date?'var(--fg-2)':'#bbb',cursor:'pointer',borderRadius:4,transition:'background 0.15s',userSelect:'none'}}
                                      onMouseEnter={e=>{e.currentTarget.style.background='rgba(0,102,138,0.08)';}}
                                      onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}
                                    >
                                      {savingReceiptSerial === item.serial
                                        ? <Icon name="progress_activity" size={12} style={{color:'var(--accent)',animation:'spin 1s linear infinite'}}/>
                                        : (item.receipt_date ? fmtDate(item.receipt_date) : '미확인')}
                                    </div>
                                  )}
                                  <div style={{padding:'10px 14px',fontSize:12,color:item.sale_date?'var(--fg-2)':'#bbb'}}>{item.sale_date?fmtDate(item.sale_date):'-'}</div>
                                  <div style={{padding:'8px 10px'}} onClick={e=>e.stopPropagation()}>
                                    {isShipped ? (
                                      <div style={{display:'inline-flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                        <Badge variant="shipped">출고됨</Badge>
                                        <button onClick={()=>{ setLogSearch(item.serial); setPage('txlog'); }} title="이 시리얼이 담긴 배송건으로 이동 (배송시트지에서 시리얼로 검색)" style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--accent)',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}><Icon name="open_in_new" size={12}/> 배송건</button>
                                      </div>
                                    ) : item.status === 'reserved' ? (
                                      // v7.80: reserved 시리얼 배지 — 안내 문구 tooltip + 예약자 표시, 편집 불가
                                      // v7.168: 라벨 "방문수령 예약" → "예약"으로 일반화
                                      //          (킵 지정 시리얼도 reserved 상태를 공유하므로 방문수령 표현이 부정확했음)
                                      <div style={{display:'inline-flex',flexDirection:'column',alignItems:'flex-start',gap:2}} title="예약된 시리얼입니다. 다른 출고에 사용할 수 없습니다.">
                                        <Badge variant="reserved"><Icon name="event_available" size={11} style={{marginRight:3}}/>예약</Badge>
                                        {item.dealer_name && <span style={{fontSize:10,color:'#0277bd',fontWeight:600,marginLeft:2}}>→ {item.dealer_name}</span>}
                                      </div>
                                    ) : item.status === 'ship_hold' ? (
                                      // v7.424: 배송건에 담겨 출고 대기 중인(ship_hold) 시리얼 — 편집 불가.
                                      //   pending 은 아직 note 에 안 실려 시리얼 검색으로는 배송건을 못 찾으므로 링크는 두지 않는다.
                                      <div style={{display:'inline-flex',flexDirection:'column',alignItems:'flex-start',gap:2}} title="배송건에 담겨 출고 대기 중인 시리얼입니다. 다른 출고에 사용할 수 없습니다. 배송건에서 빼면 정상 재고로 돌아옵니다.">
                                        <Badge variant="hold"><Icon name="pending" size={11} style={{marginRight:3}}/>출고 대기</Badge>
                                      </div>
                                    ) : (
                                      <div style={{display:'inline-flex',alignItems:'center',gap:4}}>
                                        <div style={{position:'relative',display:'inline-block'}}>
                                          <select value={item.status} onChange={e=>saveInventoryStatus(item.serial, e.target.value)} disabled={savingStatusSerial===item.serial}
                                            style={{padding:'3px 24px 3px 10px',borderRadius:9999,fontSize:11,fontWeight:600,border:'none',cursor:'pointer',backgroundColor:item.status==='unsellable'?'rgba(245,124,0,0.12)':'rgba(0,152,68,0.1)',backgroundImage:'none',color:item.status==='unsellable'?'var(--warning)':'var(--success)',appearance:'none',WebkitAppearance:'none',MozAppearance:'none',outline:'none',fontFamily:'inherit'}}>
                                            <option value="in_stock">정상 재고</option>
                                            <option value="unsellable">판매 불가</option>
                                          </select>
                                          <Icon name="arrow_drop_down" size={14} style={{position:'absolute',right:4,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:item.status==='unsellable'?'var(--warning)':'var(--success)'}}/>
                                        </div>
                                        {savingStatusSerial===item.serial && <Icon name="progress_activity" size={11} style={{color:'var(--accent)',animation:'spin 1s linear infinite'}}/>}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                {isExpanded && (
                                  <div style={{minWidth:840,padding:'12px 18px 14px 50px',background:'var(--bg-subtle)',borderBottom:i<g.items.length-1?'1px solid #E2E8F0':'none'}}>
                                    {isShipped && (
                                      <div style={{display:'grid',gridTemplateColumns:'1fr 1.4fr',gap:12,marginBottom:12}}>
                                        <div style={{background:'var(--bg-card)',padding:10,borderRadius:8,border:'1px solid var(--border)'}}>
                                          <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5}}>
                                            <Icon name="person" size={12} style={{color:'var(--accent)'}}/>
                                            <span style={{fontSize:10,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>수령인/업체명</span>
                                          </div>
                                          <div style={{fontSize:13,color:'var(--fg-1)',fontWeight:600}}>{item.dealer_name ? highlightMatch(item.dealer_name, search) : '-'}</div>
                                        </div>
                                        <div style={{background:'var(--bg-card)',padding:10,borderRadius:8,border:'1px solid var(--border)'}}>
                                          <div style={{display:'flex',alignItems:'center',gap:4,marginBottom:5}}>
                                            <Icon name="location_on" size={12} style={{color:'var(--accent)'}}/>
                                            <span style={{fontSize:10,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송지</span>
                                          </div>
                                          <div style={{fontSize:12,color:'var(--fg-1)',lineHeight:1.4,wordBreak:'break-all'}}>{item.note||'-'}</div>
                                        </div>
                                      </div>
                                    )}
                                    {/* v7.236: 일반 목록과 동일한 입고 정보 편집 (compact) */}
                                    {renderInboundEditPanel(item, true)}
                                  </div>
                                )}
                                </React.Fragment>
                              );})}
                            </div>
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                </div>
              );
            })() : (
              <Card style={{overflow:'hidden'}}>
                <div style={{overflowX:'auto'}}>
                <div style={{display:'grid',gridTemplateColumns:'36px 156px 1fr 1fr 100px 100px 1fr 110px',minWidth:1216,borderBottom:'2px solid var(--border)'}}>
                  {[
                    {h:'', key:null},
                    {h:'품목 코드', key:'serial'},
                    {h:'품목명', key:'model'},
                    {h:'비고', key:null},
                    {h:'입고일', key:'receipt_date'},
                    {h:'출고일', key:'sale_date'},
                    {h:'배송지', key:null},
                    {h:'상태', key:'status'},
                  ].map((col,i)=>{
                    if (!col.key) return <div key={i} style={{padding:'14px 16px',fontSize:13,fontWeight:600,color:'var(--fg-2)'}}>{col.h}</div>;
                    const active = invSortBy === col.key;
                    return (
                      <div key={i}
                        onClick={()=>{
                          if (active) setInvSortDir(d => d === 'asc' ? 'desc' : 'asc');
                          else { setInvSortBy(col.key); setInvSortDir('desc'); }
                          setPg(0);
                        }}
                        style={{padding:'14px 16px',fontSize:13,fontWeight:active?700:600,color:active?'var(--accent)':'var(--fg-2)',cursor:'pointer',userSelect:'none',display:'flex',alignItems:'center',gap:4,transition:'color 0.15s'}}
                        title="클릭하여 정렬">
                        {col.h}
                        <Icon name={active ? (invSortDir==='asc'?'arrow_upward':'arrow_downward') : 'unfold_more'} size={14} style={{color:active?'var(--accent)':'#bbb'}}/>
                      </div>
                    );
                  })}
                </div>
                <div ref={invScrollRef} onScroll={e=>{const el=e.target;if(el.scrollTop+el.clientHeight>=el.scrollHeight-100&&hasMore&&!loading&&!loadingMore&&!search.trim()){setPg(p=>p+1);}}} style={{maxHeight:'60vh',overflowY:'auto'}}>{items.map((item,i)=>{
                  const memoVal = inventoryMemos[item.serial] !== undefined ? inventoryMemos[item.serial] : (item.memo || '');
                  const memoDirty = inventoryMemos[item.serial] !== undefined && inventoryMemos[item.serial] !== (item.memo || '');
                  const isShipped = item.status === 'shipped';
                  const isExpanded = expandedSerial === item.serial;
                  return (
                <React.Fragment key={i}>
                <div style={{display:'grid',gridTemplateColumns:'36px 156px 1fr 1fr 100px 100px 1fr 110px',minWidth:1216,borderBottom:isExpanded?'none':'1px solid var(--border)',transition:'all 0.15s',background:isExpanded?'var(--bg-info)':'',cursor:'pointer'}}
                  onClick={()=>setExpandedSerial(s=>s===item.serial?null:item.serial)}
                  onMouseEnter={e=>{if(!isExpanded)e.currentTarget.style.background='#F1F5F9'}} onMouseLeave={e=>{if(!isExpanded)e.currentTarget.style.background=''}}>
                  <div style={{padding:'14px 8px',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--accent)'}}>
                    <Icon name={isExpanded?'expand_more':'chevron_right'} size={18}/>
                  </div>
                  {/* v7.335: 품목 코드가 길면(19자 이상 시리얼) 옆 칸을 침범했다.
                      grid 트랙은 120px 로 고정인데 셀 안의 숫자열에는 줄바꿈 지점이 없어서
                      (공백·하이픈이 없는 연속 숫자) 그대로 넘쳐 흘렀다.
                      → break-all 로 강제 줄바꿈 + minWidth:0 + 칸 폭 확대.
                      minWidth:0 이 없으면 grid item 의 기본 min-width:auto 때문에
                      트랙보다 커지려 해서 오른쪽 칸들이 밀린다. */}
                  <div title={item.serial}
                    style={{padding:'14px 16px',fontSize:12.5,fontFamily:'monospace',color:'var(--fg-2)',wordBreak:'break-all',overflowWrap:'anywhere',minWidth:0,lineHeight:1.35,display:'flex',alignItems:'center'}}>{highlightMatch(item.serial, search)}</div>
                  {/* v7.338: 제품명이 길면 [신품] 뱃지 글자가 세로로 쪼개졌다.
                      flex 자식 둘 다 flex-shrink 가 기본 1 이라 넘치는 폭이 base 폭에
                      비례해 나뉜다 → 뱃지(34px)도 22px 로 줄어 글자가 안 들어간다.
                      → 뱃지 flexShrink:0 + nowrap, 제품명이 초과분을 전부 떠안게 flex:1+minWidth:0.
                      minWidth:0 이 없으면 grid item 기본 min-width:auto 로 옆 칸을 민다 (v7.335 와 같은 원리). */}
                  <div style={{padding:'14px 16px',minWidth:0}}><div style={{display:'flex',alignItems:'flex-start',gap:6}}><span style={{flex:1,minWidth:0,fontSize:14,fontWeight:600,wordBreak:'break-word',overflowWrap:'anywhere',lineHeight:1.35}}>{item.model}</span>{item.stock_type&&<span style={{flexShrink:0,whiteSpace:'nowrap',marginTop:1,padding:'1px 7px',borderRadius:6,fontSize:10,fontWeight:700,background:item.stock_type==='신품'?'rgba(0,152,68,0.1)':item.stock_type==='AS'?'rgba(84,110,122,0.12)':item.stock_type==='리페킹'?'rgba(255,152,0,0.1)':'rgba(64,194,253,0.15)',color:item.stock_type==='신품'?'var(--success)':item.stock_type==='AS'?'#546e7a':item.stock_type==='리페킹'?'var(--warning)':'#004d6a'}}>{item.stock_type}</span>}</div><div style={{fontSize:12,color:'var(--fg-3)',marginTop:2,display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}><span>{item.warehouse==='paju'?'파주':'본사'} 보관</span>{item.source&&(()=>{const b=sourceBadge(item.source);return(<span style={{padding:'1px 6px',borderRadius:4,fontSize:10,fontWeight:600,background:b.bg,color:b.fg,border:`1px solid ${b.bd}`}}>{b.icon} {b.label} 출처</span>);})()}</div></div>
                  <div style={{padding:'10px 12px',display:'flex',gap:4,alignItems:'center'}} onClick={e=>e.stopPropagation()}>
                    <input value={memoVal}
                      onChange={e=>setInventoryMemos(prev=>({...prev,[item.serial]:e.target.value}))}
                      onKeyDown={e=>{if(e.key==='Enter'&&memoDirty){saveInventoryMemo(item.serial, memoVal);e.target.blur();}}}
                      onBlur={()=>{if(memoDirty)saveInventoryMemo(item.serial, memoVal);}}
                      placeholder="특이사항 입력..."
                      style={{flex:1,minWidth:0,padding:'7px 10px',border:`1px solid ${memoDirty?'var(--accent)':'var(--border)'}`,borderRadius:8,fontSize:12,outline:'none',background:memoDirty?'var(--bg-card)':'transparent',fontFamily:'inherit',transition:'all 0.15s'}}/>
                    {savingMemoSerial===item.serial && <Icon name="progress_activity" size={14} style={{color:'var(--accent)',animation:'spin 1s linear infinite'}}/>}
                  </div>
                  <div style={{padding:'14px 16px',fontSize:13,color:item.receipt_date?'var(--fg-1)':'#bbb'}}>{item.receipt_date?fmtDate(item.receipt_date):'-'}</div>
                  <div style={{padding:'14px 16px',fontSize:13,color:item.sale_date?'var(--fg-1)':'#bbb'}}>{item.sale_date?fmtDate(item.sale_date):'-'}</div>
                  {/* 배송지 — 출고된 항목만 표시 (거래처 + 주소) */}
                  <div style={{padding:'14px 14px',fontSize:12,lineHeight:1.45,minWidth:0,overflow:'hidden'}} title={isShipped?[item.dealer_name, item.note].filter(Boolean).join(' / '):''}>
                    {isShipped ? (
                      <div style={{display:'flex',flexDirection:'column',gap:2,minWidth:0}}>
                        {item.dealer_name && <span style={{fontWeight:600,color:'var(--fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{highlightMatch(item.dealer_name, search)}</span>}
                        {item.note && <span style={{color:'var(--fg-3)',fontSize:11,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.note}</span>}
                        {!item.dealer_name && !item.note && <span style={{color:'#bbb'}}>-</span>}
                      </div>
                    ) : (
                      <span style={{color:'#d1d5db'}}>-</span>
                    )}
                  </div>
                  <div style={{padding:'10px 14px'}} onClick={e=>e.stopPropagation()}>
                    {isShipped ? (
                      <div style={{display:'inline-flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                        <Badge variant="shipped">출고됨</Badge>
                        <button onClick={()=>{ setLogSearch(item.serial); setPage('txlog'); }} title="이 시리얼이 담긴 배송건으로 이동 (배송시트지에서 시리얼로 검색)" style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--accent)',borderRadius:7,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}><Icon name="open_in_new" size={12}/> 배송건</button>
                      </div>
                    ) : (
                      <div style={{display:'inline-flex',alignItems:'center',gap:4}}>
                        <div style={{position:'relative',display:'inline-block'}}>
                          <select value={item.status} onChange={e=>saveInventoryStatus(item.serial, e.target.value)} disabled={savingStatusSerial===item.serial}
                            style={{padding:'4px 26px 4px 12px',borderRadius:9999,fontSize:12,fontWeight:600,border:'none',cursor:'pointer',backgroundColor:item.status==='unsellable'?'rgba(245,124,0,0.12)':'rgba(0,152,68,0.1)',backgroundImage:'none',color:item.status==='unsellable'?'var(--warning)':'var(--success)',appearance:'none',WebkitAppearance:'none',MozAppearance:'none',outline:'none',fontFamily:'inherit'}}>
                            <option value="in_stock">정상 재고</option>
                            <option value="unsellable">판매 불가</option>
                          </select>
                          <Icon name="arrow_drop_down" size={16} style={{position:'absolute',right:5,top:'50%',transform:'translateY(-50%)',pointerEvents:'none',color:item.status==='unsellable'?'var(--warning)':'var(--success)'}}/>
                        </div>
                        {savingStatusSerial===item.serial && <Icon name="progress_activity" size={12} style={{color:'var(--accent)',animation:'spin 1s linear infinite'}}/>}
                      </div>
                    )}
                  </div>
                </div>
                {isExpanded && (
                  <div style={{minWidth:1180,padding:'16px 24px 20px 60px',background:'var(--bg-subtle)',borderBottom:'1px solid var(--border)'}}>
                    {/* 출고 정보 (출고된 항목만) */}
                    {isShipped && (
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1.4fr',gap:16,marginBottom:14}}>
                        <div style={{background:'var(--bg-card)',padding:14,borderRadius:10,border:'1px solid var(--border)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:8}}>
                            <Icon name="person" size={14} style={{color:'var(--accent)'}}/>
                            <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>수령인/업체명</span>
                          </div>
                          <div style={{fontSize:14,color:'var(--fg-1)',fontWeight:600}}>{item.dealer_name ? highlightMatch(item.dealer_name, search) : '-'}</div>
                        </div>
                        <div style={{background:'var(--bg-card)',padding:14,borderRadius:10,border:'1px solid var(--border)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:8}}>
                            <Icon name="location_on" size={14} style={{color:'var(--accent)'}}/>
                            <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송지</span>
                          </div>
                          <div style={{fontSize:13,color:'var(--fg-1)',lineHeight:1.5,wordBreak:'break-all'}}>{item.note||'-'}</div>
                        </div>
                      </div>
                    )}
                    {/* v7.236: 입고 정보 편집 패널 — 검색 결과 카드 뷰와 공유 (renderInboundEditPanel) */}
                    {renderInboundEditPanel(item, false)}
                  </div>
                )}
                </React.Fragment>
                );})}{!items.length&&<div style={{padding:48,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>{loading?'로딩 중...':'검색 결과 없음'}</div>}
                {loadingMore&&<div style={{padding:20,textAlign:'center',color:'var(--accent)',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',justifyContent:'center',gap:8}}><Icon name="progress_activity" size={16} style={{animation:'spin 1s linear infinite'}}/>불러오는 중...</div>}
                {!hasMore&&items.length>0&&!search.trim()&&<div style={{padding:16,textAlign:'center',color:'var(--fg-3)',fontSize:12}}>모든 항목을 불러왔습니다</div>}
                </div>
                </div>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',borderTop:'1px solid var(--border)'}}><span style={{fontSize:14,color:'var(--fg-3)'}}>총 {total.toLocaleString()}개 중 {items.length.toLocaleString()}개 표시</span></div>
              </Card>
            )}
            </>)}{/* v7.40: inventoryTab === 'list' wrap end */}
          </div>)}

          {/* ═══ STOCK IN/OUT ═══ */}
          {page==='stock_out'&&(<div>
            <div style={{marginBottom:28}}>
              <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>출고 처리</h1>
              <p style={{fontSize:16,color:'var(--fg-2)'}}>배송시트지에서 출고 요청을 받아 처리합니다.</p>
            </div>

            {/* ━━━ 출고 (Outbound) 섹션 헤더 ━━━ */}
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
              <div style={{width:36,height:36,borderRadius:10,background:'rgba(186,26,26,0.08)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Icon name="north_east" size={20} style={{color:'var(--danger)'}}/>
              </div>
              <div style={{flexShrink:0}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--danger)',textTransform:'uppercase',letterSpacing:'0.08em'}}>OUTBOUND</div>
                <div style={{fontSize:18,fontWeight:700,color:'var(--fg-1)'}}>출고 처리</div>
              </div>
              <div style={{flex:1,height:2,background:'linear-gradient(90deg, rgba(186,26,26,0.4) 0%, rgba(186,26,26,0.15) 40%, transparent 100%)',marginLeft:8,borderRadius:1}}/>
            </div>

            {/* ═══ 출고 요청 처리 패널 (항상 표시) ═══ */}
            {(()=>{
              const hasReq = !!activeRequest;
              const current = hasReq ? activeRequest.models.find(m => m.model === activeRequest.currentModel) : null;
              const c = (hasReq && activeRequest.common) || {};
              const fulfilled = current?.fulfilledSerials?.length || 0;
              const qty = current?.qty || 0;
              const pendingCount = hasReq ? pendingSerials.filter(p => p.model === activeRequest.currentModel).length : 0;
              const isComplete = hasReq && fulfilled >= qty;
              // 전체 요청 품목 모두 시리얼 등록 완료 여부 (fulfilled + pending 기준)
              const allItemsReady = hasReq && activeRequest.models.every(m => {
                const f = m.fulfilledSerials?.length || 0;
                const p = pendingSerials.filter(x => x.model === m.model).length;
                return (f + p) >= m.qty;
              });
              const totalPending = pendingSerials.length;
              return (
                <Card style={{padding:0,marginBottom:24,overflow:'hidden',border:hasReq?'2px solid var(--accent)':'1px solid var(--border-strong)',background:'var(--bg-card)',opacity:hasReq?1:0.92}}>
                  {/* Header */}
                  <div style={{padding:'18px 24px',background:hasReq?'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)':'linear-gradient(90deg, #76777d 0%, #9ca0a8 100%)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <Icon name="local_shipping" size={22} style={{color:'#fff'}}/>
                      <div>
                        <div style={{fontSize:12,opacity:0.85,fontWeight:600,letterSpacing:'0.04em'}}>출고 요청 처리 모드</div>
                        <div style={{fontSize:18,fontWeight:700,fontFamily:'monospace'}}>{hasReq?activeRequest.reqId:'대기 중 — 배송시트지에서 [제품 출고] 클릭'}</div>
                      </div>
                    </div>
                    {hasReq&&<button onClick={()=>{setActiveRequest(null);setRequestSerialInput('');setRequestMsg(null);}} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                      <Icon name="close" size={16}/> 모드 종료
                    </button>}
                  </div>
                  {/* Request info cards */}
                  <div style={{padding:24,background:'#f0f6ff'}}>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
                      <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                          <Icon name="person" size={14} style={{color:hasReq?'var(--accent)':'#9e9e9e'}}/>
                          <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>수령인</span>
                        </div>
                        <div style={{fontSize:14,fontWeight:600,color:hasReq?'var(--fg-1)':'#bbb',marginBottom:2}}>{c.recipient||'—'}</div>
                        <div style={{fontSize:12,color:hasReq?'var(--fg-2)':'#bbb'}}>{c.phone||'—'}</div>
                      </div>
                      <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                          <Icon name="location_on" size={14} style={{color:hasReq?'var(--accent)':'#9e9e9e'}}/>
                          <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 주소</span>
                        </div>
                        <div style={{fontSize:13,color:hasReq?'var(--fg-1)':'#bbb',lineHeight:1.4}}>{c.addr||'—'}</div>
                      </div>
                      <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                          <Icon name="info" size={14} style={{color:hasReq?'var(--accent)':'#9e9e9e'}}/>
                          <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 정보</span>
                        </div>
                        {hasReq?(<>
                          <div style={{display:'flex',gap:6,marginBottom:4}}>
                            <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,...(c.method==='택배'?{background:'var(--bg-accent-strong)',color:'#004d6a'}:c.method==='방문수령'?{background:'rgba(0,102,138,0.12)',color:'var(--accent)'}:{background:'rgba(186,26,26,0.08)',color:'var(--danger)'})}}>{c.method||'-'}</span>
                            {c.salesStatus&&<span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,background:'rgba(0,152,68,0.1)',color:'var(--success)'}}>{c.salesStatus}</span>}
                          </div>
                          <div style={{fontSize:12,color:'var(--fg-2)'}}>작성자/요청자: <b style={{color:'var(--accent)'}}>{c.author||'-'}</b><span style={{color:'#bbb',margin:'0 4px'}}>/</span><b>{c.requester||'-'}</b></div>
                        </>):(
                          <div style={{fontSize:13,color:'#bbb'}}>—</div>
                        )}
                      </div>
                    </div>

                    {/* Multi-model selector */}
                    {hasReq&&activeRequest.models.length>1&&(
                      <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
                        {activeRequest.models.map(m=>{
                          // v7.66: 탭 카운트에 pendingSerials도 포함 (진행 박스와 일관성)
                          const fulfilledCount = m.fulfilledSerials.length;
                          const pendingForModel = pendingSerials.filter(p => p.model === m.model).length;
                          const totalActive = fulfilledCount + pendingForModel;
                          const done = totalActive >= m.qty;
                          const isCur = m.model === activeRequest.currentModel;
                          const auto = m.external || m.serialOptional;
                          const autoColor = m.external ? '#e65100' : m.serialOptional ? '#7b1fa2' : null;
                          const autoBg = m.external ? 'rgba(245,124,0,0.08)' : m.serialOptional ? 'rgba(123,31,162,0.08)' : null;
                          return (
                            <button key={m.model} onClick={()=>{setActiveRequest(p=>({...p,currentModel:m.model}));setRequestSerialInput('');setRequestMsg(null);setSerialSuggestions([]);setSerialSuggestionsOpen(false);setSerialSuggestionIdx(-1);focusRequestSerial();}} style={{
                              padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,
                              border:auto?`1px dashed ${autoColor}`:isCur?'2px solid var(--accent)':'1px solid var(--border-strong)',
                              background:auto?autoBg:isCur?'var(--bg-accent-strong)':'var(--bg-card)',color:auto?autoColor:isCur?'#004d6a':'var(--fg-2)',
                              cursor:'pointer',display:'flex',alignItems:'center',gap:5
                            }}>
                              {m.external?<Icon name="warning_amber" size={14} style={{color:'var(--warning)'}}/>:m.serialOptional?<Icon name="bolt" size={14} style={{color:'var(--purple)'}}/>:done&&<Icon name="check_circle" size={14} style={{color:'var(--success)'}}/>}
                              {m.model} ({totalActive}/{m.qty})
                              {pendingForModel > 0 && <span style={{fontSize:10,color:'var(--warning)',fontWeight:700,marginLeft:1}}>·대기{pendingForModel}</span>}
                              {m.external&&<span style={{fontSize:10,marginLeft:2,opacity:0.8}}>외부</span>}
                              {m.serialOptional&&<span style={{fontSize:10,marginLeft:2,opacity:0.8}}>S/N면제</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}

                    {/* Scan area: model + serial input */}
                    <div style={{background:'var(--bg-card)',borderRadius:12,padding:18,border:'1px solid var(--border)',marginBottom:12}}>
                      {hasReq && current?.external && (
                        <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',marginBottom:14,background:'rgba(0,152,68,0.06)',border:'1px dashed var(--success)',borderRadius:10}}>
                          <Icon name="check_circle" size={20} style={{color:'var(--success)',flexShrink:0}}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:700,color:'var(--success)'}}>외부 제품 — 자동 준비 완료</div>
                            <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>DB 미등록 제품이므로 시리얼 등록 없이 출고 처리됩니다. 우측 하단 <b>[제품 출고]</b> 버튼을 누르세요.</div>
                          </div>
                        </div>
                      )}
                      {hasReq && current?.serialOptional && !current?.external && (
                        <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',marginBottom:14,background:'rgba(123,31,162,0.06)',border:'1px dashed var(--purple)',borderRadius:10}}>
                          <Icon name="bolt" size={20} style={{color:'var(--purple)',flexShrink:0}}/>
                          <div style={{flex:1}}>
                            <div style={{fontSize:13,fontWeight:700,color:'var(--purple)'}}>
                              {current?.serialOptionalReason === 'as_resend'
                                ? 'AS 재배송 — 시리얼 면제 출고'
                                : current?.serialOptionalReason === 'demo_lend'
                                  ? '데모 대여 — 시리얼 면제 출고'
                                  : '시리얼 면제 제품 — 자동 준비 완료'}
                            </div>
                            <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>
                              {current?.serialOptionalReason === 'as_resend' ? (
                                <>매출여부 수기입력에 <b>'AS'</b> 표기가 있어 시리얼 입력 없이 수량({current?.qty}개)만으로 출고 처리됩니다. (이미 출고된 시리얼 재배송) 우측 하단 <b>[제품 출고]</b> 버튼을 누르세요.</>
                              ) : current?.serialOptionalReason === 'demo_lend' ? (
                                <><b>데모 관리</b>에서 등록된 대여 건입니다. 시리얼은 데모 장비 시스템(demo_equipment)에서 관리되므로 시리얼 입력 없이 수량({current?.qty}개)만으로 출고 처리됩니다. 우측 하단 <b>[제품 출고]</b> 버튼을 누르세요.</>
                              ) : (
                                <>이 제품은 시리얼 입력 없이 수량({current?.qty}개)만으로 출고 처리됩니다. 우측 하단 <b>[제품 출고]</b> 버튼을 누르세요.</>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                      <div style={{display:'flex',alignItems:'center',gap:12}}>
                        <div style={{flex:'0 0 auto',display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:'var(--bg-accent-soft)',borderRadius:10,minWidth:240}}>
                          <Icon name="package_2" size={20} style={{color:hasReq?'var(--accent)':'#9e9e9e'}}/>
                          <div>
                            <div style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em',display:'flex',alignItems:'center',gap:5}}>
                              <span>출고할 제품</span>
                              {hasReq && current?.serialOptional && <span style={{padding:'1px 6px',background:'rgba(123,31,162,0.12)',color:'var(--purple)',borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:0}}>S/N 면제</span>}
                              {hasReq && current?.external && <span style={{padding:'1px 6px',background:'rgba(245,124,0,0.12)',color:'var(--warning)',borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:0}}>외부</span>}
                            </div>
                            <div style={{fontSize:15,fontWeight:700,color:hasReq?'var(--fg-1)':'#bbb'}}>{current?.model||'—'}</div>
                          </div>
                        </div>
                        {/* 시리얼 input + 스캔 버튼 — 외부/면제 제품은 숨김 (자동 처리되므로) */}
                        {!current?.external && !current?.serialOptional && (
                          <>
                            <div style={{flex:1,position:'relative'}}>
                              <Icon name="qr_code_scanner" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)',zIndex:1}}/>
                              <input
                                ref={reqSerialInputRef}
                                value={requestSerialInput}
                                onCompositionEnd={e=>setRequestSerialInput(normalizeScanInput(e.target.value))}
                                onChange={e=>{
                                  const v = imeConv(e);
                                  setRequestSerialInput(v);
                                  // v7.196: 입력이 있을 때만 후보 표시 (기본 비표시)
                                  setSerialSuggestionsOpen(v.trim().length > 0);
                                  setSerialSuggestionIdx(-1);
                                }}
                                onFocus={()=>{ if (requestSerialInput.trim().length > 0) setSerialSuggestionsOpen(true); }}
                                // v7.244: 클릭으로도 후보를 연다 — ↓ 키를 모르면 재고 시리얼을 볼 방법이 없었다.
                                //   입력이 비어 있어도 열어서 '뭘 골라야 하나'를 바로 보여준다.
                                onClick={()=>{ if (serialSuggestions.length > 0) setSerialSuggestionsOpen(true); }}
                                onBlur={()=>setTimeout(()=>setSerialSuggestionsOpen(false),150)}
                                onKeyDown={e=>{
                                  // v7.196: 닫힌 상태에서 ↓ 누르면 전체 후보 열기
                                  if (!serialSuggestionsOpen && e.key === 'ArrowDown' && serialSuggestions.length > 0) {
                                    e.preventDefault(); setSerialSuggestionsOpen(true); setSerialSuggestionIdx(0); return;
                                  }
                                  if (serialSuggestionsOpen && serialSuggestions.length > 0) {
                                    if (e.key === 'ArrowDown') {
                                      e.preventDefault();
                                      setSerialSuggestionIdx(i => Math.min(i + 1, serialSuggestions.length - 1));
                                      return;
                                    }
                                    if (e.key === 'ArrowUp') {
                                      e.preventDefault();
                                      setSerialSuggestionIdx(i => Math.max(i - 1, -1));
                                      return;
                                    }
                                    if (e.key === 'Tab' && serialSuggestionIdx >= 0) {
                                      e.preventDefault();
                                      const picked = serialSuggestions[serialSuggestionIdx].serial_number;
                                      setRequestSerialInput(picked);
                                      setSerialSuggestionsOpen(false);
                                      return;
                                    }
                                    if (e.key === 'Escape') {
                                      setSerialSuggestionsOpen(false);
                                      return;
                                    }
                                    if (e.key === 'Enter' && serialSuggestionIdx >= 0) {
                                      e.preventDefault();
                                      const picked = serialSuggestions[serialSuggestionIdx].serial_number;
                                      setRequestSerialInput(picked);
                                      setSerialSuggestionsOpen(false);
                                      // 선택 후 바로 추가하려면 다음 줄 주석 해제
                                      // setTimeout(() => handleRequestScan(), 0);
                                      return;
                                    }
                                  }
                                  if (e.key === 'Enter') handleRequestScan();
                                }}
                                placeholder={!hasReq?'요청 대기 중 — 배송시트지에서 [제품 출고]를 클릭하세요':isComplete?'요청 수량 모두 처리 완료':'시리얼 스캔 또는 입력  (클릭 또는 ↓ 키: 재고 후보 보기)'}
                                disabled={!hasReq||isComplete||loading}
                                style={{...inputStyle,paddingLeft:44,fontFamily:'monospace',fontSize:15,fontWeight:600,background:!hasReq||isComplete?'#fafafa':'var(--bg-card)'}}/>
                              {/* v7.52: 자동완성 dropdown — 현재 모델의 in_stock 시리얼 후보 */}
                              {serialSuggestionsOpen && hasReq && !isComplete && serialSuggestions.length > 0 && (
                                <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,background:'var(--bg-card)',border:'1.5px solid var(--accent)',borderRadius:8,boxShadow:'0 8px 20px rgba(0,0,0,0.12)',maxHeight:360,overflowY:'auto',zIndex:50}}>
                                  <div style={{padding:'6px 12px',background:'var(--bg-accent-soft)',fontSize:10,fontWeight:700,color:'var(--accent)',letterSpacing:'0.04em',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                    <span>📦 재고 시리얼 후보 ({serialSuggestions.length}{serialSuggestions.length >= 50 ? '+' : ''})</span>
                                    <span style={{fontSize:9,fontWeight:600,color:'var(--fg-3)'}}>↑↓ 선택 · Tab/Enter 입력 · Esc 닫기</span>
                                  </div>
                                  {serialSuggestions.map((s, i) => {
                                    const isHi = i === serialSuggestionIdx;
                                    // 입력값과 일치하는 부분 강조
                                    const q = requestSerialInput.trim().toUpperCase();
                                    const sn = s.serial_number;
                                    const matchIdx = q ? sn.toUpperCase().indexOf(q) : -1;
                                    return (
                                      <div
                                        key={sn}
                                        onMouseDown={e=>{
                                          e.preventDefault();
                                          setRequestSerialInput(sn);
                                          setSerialSuggestionsOpen(false);
                                        }}
                                        onMouseEnter={()=>setSerialSuggestionIdx(i)}
                                        style={{padding:'10px 14px',fontFamily:'monospace',fontSize:13,fontWeight:600,color:'var(--fg-1)',cursor:'pointer',background:isHi?'rgba(0,102,138,0.08)':'var(--bg-card)',borderBottom:i<serialSuggestions.length-1?'1px solid #f0f2f6':'none',display:'flex',alignItems:'center',gap:8}}>
                                        <Icon name="barcode" size={14} style={{color:'var(--fg-3)',flexShrink:0}}/>
                                        {matchIdx >= 0 ? (
                                          <>
                                            <span>{sn.slice(0, matchIdx)}</span>
                                            <span style={{background:'rgba(0,152,68,0.18)',color:'var(--success)',padding:'1px 3px',borderRadius:3}}>{sn.slice(matchIdx, matchIdx + q.length)}</span>
                                            <span>{sn.slice(matchIdx + q.length)}</span>
                                          </>
                                        ) : (
                                          <span>{sn}</span>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                            <button onClick={handleRequestScan} disabled={!hasReq||isComplete||loading||!requestSerialInput.trim()} style={{padding:'12px 22px',background:(!hasReq||isComplete)?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:(!hasReq||isComplete)?'default':'pointer',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                              <Icon name="add" size={18}/> 시리얼 추가
                            </button>
                            {/* v7.145: 임시 EXT- 부여 — 외부 플래그 유실 요청 방어
                                v7.377: v7.376 에서 카탈로그 제품에 숨겼다가 사용자 요청으로 되돌렸다.
                                ⚠ 이 버튼은 EXT-모델-01 같은 [가짜 시리얼] 을 만든다.
                                  commitRequest 가 EXT- 로 시작하는 시리얼은 inventory 를 건드리지
                                  않고 통과시키므로, 실물 시리얼이 있는 제품에 쓰면
                                  [장부상 출고 + 재고 잔존] 이 되어 재고가 부풀려진다.
                                  실물이 있는 제품에는 쓰지 말 것. */}
                            {hasReq && !isComplete && current && (()=>{
                              const filled = (current.fulfilledSerials?.length||0) + pendingSerials.filter(p=>p.model===current.model).length;
                              const remaining = current.qty - filled;
                              if (remaining <= 0) return null;
                              return (
                                <button onClick={assignExternalToCurrentModel} disabled={loading}
                                  title={`남은 ${remaining}개를 EXT-${current.model}-XX 임시 시리얼로 자동 부여`}
                                  style={{padding:'12px 14px',background:'var(--warning-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:13,fontWeight:700,cursor:loading?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap'}}>
                                  <Icon name="playlist_add_check" size={16}/> 임시 EXT- {remaining}개
                                </button>
                              );
                            })()}
                          </>
                        )}
                        {/* 외부/면제 제품일 때 — 시리얼 영역을 안내 박스로 대체 */}
                        {(current?.external || current?.serialOptional) && (
                          <div style={{flex:1,padding:'12px 16px',background:current?.external?'rgba(245,124,0,0.06)':'rgba(123,31,162,0.06)',border:`1px dashed ${current?.external?'var(--warning)':'var(--purple)'}`,borderRadius:10,display:'flex',alignItems:'center',gap:10}}>
                            <Icon name={current?.external?'warning_amber':'bolt'} size={20} style={{color:current?.external?'var(--warning)':'var(--purple)'}}/>
                            <div style={{flex:1}}>
                              <div style={{fontSize:13,fontWeight:700,color:current?.external?'var(--warning)':'var(--purple)'}}>
                                {current?.external?'외부 제품 — 시리얼 불필요':'시리얼 면제 — 수량 출고'}
                              </div>
                              <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>
                                우측 상단 <b>[제품 출고]</b> 버튼을 눌러 출고 완료 처리하세요. (수량 {current?.qty}개)
                              </div>
                            </div>
                          </div>
                        )}
                        <div style={{flex:'0 0 auto',padding:'10px 14px',background:isComplete?'rgba(0,152,68,0.1)':'var(--bg-accent-soft)',borderRadius:10,textAlign:'center',minWidth:110}}>
                          <div style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em'}}>진행</div>
                          <div style={{fontSize:18,fontWeight:700,color:isComplete?'var(--success)':hasReq?'var(--accent)':'#bbb'}}>{hasReq?`${fulfilled + pendingCount}/${qty}`:'—'}</div>
                          {pendingCount>0&&<div style={{fontSize:10,color:'var(--warning)',fontWeight:600,marginTop:2}}>대기 {pendingCount}</div>}
                        </div>
                      </div>
                      {requestMsg&&(<div style={{marginTop:12,padding:12,borderRadius:10,fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:8,background:requestMsg.type==='ok'?'rgba(0,152,68,0.08)':requestMsg.type==='err'?'rgba(186,26,26,0.08)':'rgba(255,152,0,0.1)',color:requestMsg.type==='ok'?'var(--success)':requestMsg.type==='err'?'var(--danger)':'var(--warning)'}}>
                        <Icon name={requestMsg.type==='ok'?'check_circle':requestMsg.type==='err'?'error':'warning'} size={18}/>{requestMsg.msg}
                      </div>)}
                    </div>

                    {/* v7.259: 보관 시리얼 배너 제거 — 자동 주입만으로 충분 (사용자 요청) */}
                    {/* Fulfilled serials list (이미 커밋된) */}
                    {hasReq&&current?.fulfilledSerials?.length>0&&(
                      <div style={{background:'var(--bg-card)',borderRadius:12,padding:14,border:'1px solid var(--border)',marginBottom:12}}>
                        <div style={{fontSize:12,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:8}}>출고 완료된 시리얼</div>
                        <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                          {current.fulfilledSerials.map((s,idx)=>(
                            <span key={idx} style={{display:'flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:8,background:'rgba(0,152,68,0.1)',color:'var(--success)',fontSize:12,fontWeight:600,fontFamily:'monospace'}}>
                              <Icon name="check_circle" size={13} style={{color:'var(--success)'}}/> {s}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Commit button — 모든 품목 시리얼 등록 완료 시에만 활성화 */}
                    {hasReq&&totalPending>0&&(
                      <div style={{padding:16,background:'var(--bg-card)',borderRadius:12,border:`1.5px solid ${allItemsReady?'var(--success)':'var(--border-strong)'}`}}>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
                          <div style={{display:'flex',alignItems:'center',gap:8}}>
                            <Icon name={allItemsReady?'inventory':'pending'} size={20} style={{color:allItemsReady?'var(--success)':'var(--fg-3)'}}/>
                            <div>
                              <div style={{fontSize:14,fontWeight:700,color:allItemsReady?'var(--fg-1)':'var(--fg-3)'}}>{allItemsReady?'출고 준비 완료':'시리얼 등록 진행 중'}</div>
                              <div style={{fontSize:12,color:'var(--fg-3)'}}>
                                {allItemsReady
                                  ? `${totalPending}개 시리얼이 출고 처리됩니다`
                                  : (()=>{
                                      const remaining = activeRequest.models.filter(m=>{
                                        const f=m.fulfilledSerials?.length||0;
                                        const p=pendingSerials.filter(x=>x.model===m.model).length;
                                        return (f+p)<m.qty;
                                      });
                                      return `남은 품목: ${remaining.map(m=>{const f=m.fulfilledSerials?.length||0;const p=pendingSerials.filter(x=>x.model===m.model).length;return `${m.model} ${m.qty-f-p}개`;}).join(', ')}`;
                                    })()
                                }
                              </div>
                            </div>
                          </div>
                          <button onClick={commitRequest} disabled={loading||!allItemsReady} style={{padding:'12px 28px',background:allItemsReady?'var(--success-solid)':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:allItemsReady&&!loading?'pointer':'not-allowed',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                            <Icon name="local_shipping" size={18}/> 제품 출고
                          </button>
                        </div>
                        {/* 등록한 시리얼 (모델별 그룹핑) */}
                        <div style={{borderTop:'1px dashed var(--border)',paddingTop:12,display:'flex',flexDirection:'column',gap:8}}>
                          {activeRequest.models.filter(m=>pendingSerials.some(p=>p.model===m.model)).map(m=>{
                            const modelPending = pendingSerials.filter(p=>p.model===m.model);
                            return (
                              <div key={m.model} style={{display:'flex',alignItems:'flex-start',gap:10}}>
                                <div style={{flex:'0 0 auto',padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:600,background:'var(--bg-accent-soft)',color:'var(--accent)',whiteSpace:'nowrap'}}>{m.model}</div>
                                <div style={{flex:1,display:'flex',gap:5,flexWrap:'wrap'}}>
                                  {modelPending.map((p,idx)=>(
                                    <span key={idx} style={{display:'flex',alignItems:'center',gap:3,padding:'3px 4px 3px 9px',borderRadius:6,background:'rgba(255,152,0,0.1)',color:'var(--warning)',fontSize:11,fontWeight:600,fontFamily:'monospace'}}>
                                      {p.serial}
                                      <button onClick={()=>removePendingSerial(p.serial)} style={{background:'transparent',border:'none',cursor:'pointer',padding:'0 3px',marginLeft:1,color:'var(--warning)',display:'flex',alignItems:'center'}}>
                                        <Icon name="close" size={12}/>
                                      </button>
                                    </span>
                                  ))}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Complete banner */}
                    {isComplete&&pendingSerials.length===0&&(
                      <div style={{padding:14,background:'rgba(0,152,68,0.1)',borderRadius:12,border:'1px solid rgba(0,152,68,0.2)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,color:'var(--success)',fontSize:14,fontWeight:700}}>
                          <Icon name="task_alt" size={20} style={{color:'var(--success)'}}/> 이 모델 출고 완료!
                        </div>
                        <button onClick={()=>{
                          // v7.82: 모드 명시 종료 시 localStorage도 정리
                          if (activeRequest?.reqId) clearShipPending(activeRequest.reqId);
                          setActiveRequest(null);setRequestSerialInput('');setRequestMsg(null);setPendingSerials([]);setPage('txlog');
                        }} style={{padding:'8px 18px',background:'var(--bg-card)',color:'var(--success)',border:'1px solid var(--success)',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>배송시트지로 돌아가기</button>
                      </div>
                    )}
                  </div>
                </Card>
              );
            })()}
          </div>)}

          {/* ═══ v7.40: 입고 등록 페이지 (구 모달 → 사이드바 탭) ═══ */}
          {page==='inbound' && (<div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:24}}>
              <div>
                <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>입고 등록</h1>
                <p style={{fontSize:16,color:'var(--fg-2)'}}>신규 시리얼을 재고에 등록합니다. 바코드 리더기로 다중 제품 일괄 등록이 가능합니다.</p>
              </div>
            </div>

            {/* ━━━ 입고 (Inbound) 섹션 헤더 ━━━ */}
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:14}}>
              <div style={{width:36,height:36,borderRadius:10,background:'rgba(0,102,138,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                <Icon name="south_west" size={20} style={{color:'var(--accent)'}}/>
              </div>
              <div style={{flexShrink:0}}>
                <div style={{fontSize:11,fontWeight:700,color:'var(--accent)',textTransform:'uppercase',letterSpacing:'0.08em'}}>INBOUND</div>
                <div style={{fontSize:18,fontWeight:700,color:'var(--fg-1)'}}>입고 등록</div>
              </div>
              <div style={{flex:1,height:2,background:'linear-gradient(90deg, rgba(0,102,138,0.4) 0%, rgba(0,102,138,0.15) 40%, transparent 100%)',marginLeft:8,borderRadius:1}}/>
            </div>

            {/* ═══ 물품 등록 패널 ═══ */}
            <Card style={{padding:0,marginBottom:24,overflow:'hidden',border:'2px solid var(--accent)',background:'var(--bg-card)'}}>
              {/* Header — blue gradient */}
              <div style={{padding:'18px 24px',background:'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="add_box" size={22} style={{color:'#fff'}}/>
                  <div>
                    <div style={{fontSize:12,opacity:0.85,fontWeight:600,letterSpacing:'0.04em'}}>물품 등록 모드</div>
                    <div style={{fontSize:18,fontWeight:700}}>
                      {activeBatch
                        ? `${activeBatch.product.product_name} — 배치 #${regActiveIndex+1} 활성`
                        : regBatches.length>0
                          ? `${regBatches.length}개 배치 대기 — 다음 제품 스캔 또는 배치 클릭`
                          : '대기 중 — 제품 바코드를 스캔하세요'}
                    </div>
                  </div>
                </div>
                {regBatches.length>0&&(
                  <button onClick={()=>{setRegBatches([]);setRegActiveIndex(-1);setRegSerialInput('');setRegProductSearch('');setRegMsg(null);}} style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                    <Icon name="close" size={16}/> 전체 초기화
                  </button>
                )}
              </div>

              {/* Body — light blue */}
              <div style={{padding:24,background:'#f0f6ff'}}>

                {/* 3 info cards */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
                  {/* Card 1: 현재 제품 */}
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="package_2" size={14} style={{color:regProduct?'var(--accent)':'#9e9e9e'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>현재 제품</span>
                    </div>
                    <div style={{fontSize:14,fontWeight:600,color:regProduct?'var(--fg-1)':'#bbb',marginBottom:2}}>{regProduct?.product_name||'—'}</div>
                  </div>
                  {/* Card 2: 출처 — 처음부터 선택 가능 */}
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="flight_takeoff" size={14} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>출처</span>
                    </div>
                    <div style={{display:'flex',gap:6,marginTop:2}}>
                      {[{val:'taiwan',label:'🇹🇼 대만'},{val:'paju',label:'🇰🇷 파주'}].map(opt=>(
                        <button key={opt.val} onClick={()=>handleSetRegFrom(opt.val)} style={{flex:1,padding:'5px 6px',borderRadius:6,border:'1.5px solid '+(currentFrom===opt.val?'var(--accent)':'var(--border)'),background:currentFrom===opt.val?'var(--bg-accent-strong)':'var(--bg-card)',color:currentFrom===opt.val?'var(--accent)':'var(--fg-3)',fontSize:12,fontWeight:currentFrom===opt.val?700:500,cursor:'pointer',transition:'all 0.15s'}}>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Card 3: 등록 현황 */}
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="checklist" size={14} style={{color:regTotalSerials>0?'var(--accent)':'#9e9e9e'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>등록 현황</span>
                    </div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,background:'var(--bg-accent-strong)',color:'#004d6a'}}>{regTotalProducts}개 제품</span>
                      <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,background:'rgba(0,152,68,0.1)',color:'var(--success)'}}>{regTotalSerials}개 시리얼</span>
                    </div>
                  </div>
                </div>

                {/* 배치 목록 — 클릭하면 활성화 */}
                {regBatches.length>0&&(
                  <div style={{marginBottom:14}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,fontWeight:700,color:'var(--fg-2)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:8}}>
                      <Icon name="checklist" size={14} style={{color:'var(--success)'}}/>
                      배치 목록 ({regBatches.length}개) · 카드 클릭 시 활성화
                    </label>
                    <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:240,overflowY:'auto'}}>
                      {regBatches.map((batch,idx)=>{
                        const isActive = idx === regActiveIndex;
                        const isEmpty = batch.serials.length === 0;
                        return (
                        <div key={idx} onClick={()=>activateBatch(idx)} style={{padding:'10px 14px',background:isActive?'var(--bg-accent-soft)':'var(--bg-card)',borderRadius:10,border:'2px solid '+(isActive?'var(--accent)':isEmpty?'#ffc107':'#b8e6cd'),display:'flex',alignItems:'flex-start',gap:10,cursor:isActive?'default':'pointer',transition:'all 0.15s'}}>
                          <div style={{width:22,height:22,borderRadius:6,background:isActive?'var(--accent-solid)':isEmpty?'#ffc107':'var(--success-solid)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',fontSize:11,fontWeight:700,flexShrink:0,marginTop:2}}>{idx+1}</div>
                          <div style={{flex:1,minWidth:0}}>
                            <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4,flexWrap:'wrap'}}>
                              <span style={{fontSize:13,fontWeight:600,color:'var(--fg-1)'}}>{batch.product.product_name}</span>
                              {isActive&&<span style={{padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700,background:'var(--accent-solid)',color:'#fff',letterSpacing:'0.04em'}}>활성</span>}
                              <span style={{padding:'1px 8px',borderRadius:6,fontSize:10,fontWeight:700,background:batch.from==='taiwan'?'#fff4e5':'#e8f5e9',color:batch.from==='taiwan'?'#a05000':'#1b5e20',border:'1px solid '+(batch.from==='taiwan'?'#ffb74d':'#81c784'),marginLeft:'auto'}}>
                                {batch.from==='taiwan'?'🇹🇼 대만':'🇰🇷 파주'}
                              </span>
                            </div>
                            {isEmpty?(
                              <div style={{fontSize:11,color:'#a05000',fontStyle:'italic'}}>시리얼 없음 (저장 시 스킵됨)</div>
                            ):(
                              <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                                {batch.serials.map((sn,si)=>(
                                  <span key={si} style={{padding:'2px 8px',borderRadius:6,background:isActive?'var(--bg-card)':'var(--bg-page)',border:'1px solid var(--border-strong)',fontSize:11,fontFamily:'monospace',fontWeight:600,color:'var(--fg-1)'}}>{sn}</span>
                                ))}
                              </div>
                            )}
                          </div>
                          <button onClick={(e)=>{e.stopPropagation();removeBatch(idx);}} title="배치 삭제" style={{background:'none',border:'none',cursor:'pointer',color:'var(--danger)',padding:4,flexShrink:0}}><Icon name="close" size={16}/></button>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Scan area — white card */}
                <div style={{background:'var(--bg-card)',borderRadius:12,padding:18,border:'1px solid var(--border)',marginBottom:12,position:'relative',zIndex:10}}>
                  {/* Product row — input은 항상 표시, 선택된 제품은 아래 정보 박스로 */}
                  <div style={{marginBottom:14}}>
                    <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>제품명 — 바코드 리더기로 Enter (새 제품 스캔 시 이전 작업 자동 그룹핑)</label>
                    <div style={{position:'relative'}}>
                      <Icon name="qr_code_scanner" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                      <input value={regProductSearch} onChange={e=>searchRegProducts(imeConv(e))} onCompositionEnd={e=>searchRegProducts(normalizeScanInput(e.target.value))} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();handleRegProductEnter();}}} placeholder={regProduct?'다음 제품 스캔 (이전 작업 자동 그룹핑됨)':'제품명 스캔 또는 검색...'} autoFocus={!regProduct} style={{...inputStyle,paddingLeft:44,fontFamily:'monospace',fontSize:15,fontWeight:600}}/>
                      {(regProductResults.length>0 || regProductSearch.trim().length>0)&&(
                        <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,marginTop:4,maxHeight:280,overflowY:'auto',zIndex:1000,boxShadow:'0 8px 30px rgba(0,0,0,0.15)'}}>
                          {regProductResults.map(r=>(
                            <div key={r.product_name} onClick={()=>{ selectRegProduct(r); if (r._matchedAlias) setRegMsg({ type: 'ok', msg: `✓ 별칭 "${r._matchedAlias}" → 표준명 "${r.product_name}"으로 변환` }); }} style={{padding:'10px 14px',display:'flex',alignItems:'center',gap:10,cursor:'pointer',borderBottom:'1px solid var(--border)',background:r._matchedAlias?'rgba(0,152,68,0.04)':undefined}} onMouseEnter={e=>e.currentTarget.style.background=r._matchedAlias?'rgba(0,152,68,0.1)':'#F1F5F9'} onMouseLeave={e=>e.currentTarget.style.background=r._matchedAlias?'rgba(0,152,68,0.04)':''}>
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                                  {r.product_name}
                                  {r._matchedAlias && <span style={{fontSize:10,fontWeight:700,padding:'2px 6px',background:'var(--success-solid)',color:'#fff',borderRadius:4}}>별칭매칭</span>}
                                </div>
                                <div style={{fontSize:11,color:'var(--fg-3)'}}>
                                  {r._matchedAlias && <span style={{color:'var(--success)'}}>← 별칭: {r._matchedAlias}</span>}
                                </div>
                              </div>
                            </div>
                          ))}
                          {/* 카탈로그에 없는 제품 → 별칭(alias) 등록 안내 */}
                          {regProductSearch.trim().length>0 && !regProductResults.some(r => r.product_name === regProductSearch.trim()) && (
                            <div onClick={()=>addNewProductBatch(regProductSearch.trim())}
                              style={{padding:'12px 14px',display:'flex',alignItems:'center',gap:10,cursor:'pointer',background:'#e8f5e9',borderTop:regProductResults.length>0?'2px dashed var(--success)':'none'}}
                              onMouseEnter={e=>e.currentTarget.style.background='#c8e6c9'}
                              onMouseLeave={e=>e.currentTarget.style.background='#e8f5e9'}>
                              <Icon name="link" size={18} style={{color:'var(--success)'}}/>
                              <div style={{flex:1}}>
                                <div style={{fontSize:13,fontWeight:700,color:'var(--success)'}}>
                                  장부 표준명과 매핑: <span style={{fontFamily:'monospace'}}>"{regProductSearch.trim()}"</span>
                                </div>
                                <div style={{fontSize:11,color:'#2e7d32',marginTop:1}}>카탈로그의 표준 제품을 골라 별칭으로 등록 (다음 입고부터 자동 변환)</div>
                              </div>
                              <span style={{padding:'2px 8px',background:'var(--success-solid)',color:'#fff',borderRadius:6,fontSize:10,fontWeight:700}}>ALIAS</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {/* 선택된 제품: input 바로 아래 정보 박스 */}
                    {regProduct&&(
                      <div style={{marginTop:8,display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:regProduct.isNew?'#fff8e1':'var(--bg-accent-soft)',borderRadius:8,border:regProduct.isNew?'1px dashed var(--warning)':'1px solid #40c2fd'}}>
                        <Icon name={regProduct.isNew?'fiber_new':'check_circle'} size={16} style={{color:regProduct.isNew?'var(--warning)':'var(--success)'}}/>
                        <div style={{flex:1,minWidth:0,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontSize:13,fontWeight:700,color:'var(--fg-1)'}}>{regProduct.product_name}</span>
                          {regProduct.isNew
                            ? <span style={{padding:'1px 8px',background:'var(--warning-solid)',color:'#fff',borderRadius:6,fontSize:10,fontWeight:700}}>NEW · DB 미등록</span>
                            : <span style={{fontSize:11,color:'var(--fg-3)'}}>· {regProduct.category}</span>}
                          <span style={{padding:'1px 8px',borderRadius:6,fontSize:10,fontWeight:700,background:currentFrom==='taiwan'?'#fff4e5':'#e8f5e9',color:currentFrom==='taiwan'?'#a05000':'#1b5e20',border:'1px solid '+(currentFrom==='taiwan'?'#ffb74d':'#81c784')}}>
                            {currentFrom==='taiwan'?'🇹🇼 대만':'🇰🇷 파주'}
                          </span>
                          <span style={{fontSize:11,color:'var(--fg-3)',marginLeft:'auto'}}>시리얼 {regSerials.length}개 입력 중</span>
                        </div>
                        <button onClick={()=>removeBatch(regActiveIndex)} title="이 배치 삭제 (저장 안함)" style={{background:'rgba(186,26,26,0.08)',border:'none',cursor:'pointer',color:'var(--danger)',padding:'4px 8px',borderRadius:6,fontSize:11,fontWeight:600,display:'flex',alignItems:'center',gap:3}}>
                          <Icon name="close" size={12}/> 배치 삭제
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Serial input row — mirrors 출고 요청 처리 모드 layout */}
                  <div style={{display:'flex',alignItems:'center',gap:12}}>
                    <div style={{flex:1,position:'relative'}}>
                      <Icon name="qr_code_scanner" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                      <input ref={regSerialInputRef} value={regSerialInput} onChange={e=>{setRegSerialInput(imeConv(e)); if(regMsg?.type==='err') setRegMsg(null);}} onCompositionEnd={e=>setRegSerialInput(normalizeScanInput(e.target.value))} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();addRegSerial();}else if(e.key==='Tab'&&regBatches.length>0){e.preventDefault();cycleBatch(e.shiftKey?'prev':'next');}}} placeholder={regProduct?'시리얼 스캔 또는 입력 후 Enter (Tab: 배치 전환)':'먼저 제품을 선택하세요'} disabled={!regProduct||loading} style={{...inputStyle,paddingLeft:44,fontFamily:'monospace',fontSize:15,fontWeight:600,background:!regProduct?'#fafafa':regMsg?.type==='err'?'#fff5f5':'var(--bg-card)',borderColor:regMsg?.type==='err'?'var(--danger)':undefined,animation:regMsg?.type==='err'?'shake 0.35s':'none'}}/>
                    </div>
                    <button onClick={addRegSerial} disabled={!regSerialInput.trim()||!regProduct||loading} style={{padding:'12px 22px',background:(!regProduct||!regSerialInput.trim())?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:(!regProduct||!regSerialInput.trim())?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                      <Icon name="add" size={18}/> 시리얼 추가
                    </button>
                    <div style={{flex:'0 0 auto',padding:'10px 14px',background:regSerials.length>0?'rgba(0,152,68,0.1)':'var(--bg-accent-soft)',borderRadius:10,textAlign:'center',minWidth:90}}>
                      <div style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em'}}>진행</div>
                      <div style={{fontSize:18,fontWeight:700,color:regSerials.length>0?'var(--success)':'#bbb'}}>{regProduct?`${regSerials.length}개`:'—'}</div>
                    </div>
                  </div>

                  {/* Serial chips */}
                  {regSerials.length>0&&(
                    <div style={{marginTop:12,padding:10,background:'var(--bg-page)',borderRadius:10,border:'1px dashed var(--border-strong)',display:'flex',gap:6,flexWrap:'wrap',maxHeight:140,overflowY:'auto'}}>
                      {regSerials.map((sn,idx)=>(
                        <span key={idx} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 6px 4px 10px',borderRadius:8,background:'var(--bg-accent-soft)',border:'1px solid #40c2fd',fontSize:12,fontWeight:600,fontFamily:'monospace',color:'var(--fg-1)'}}>
                          {sn}
                          <button onClick={()=>removeRegSerial(sn)} style={{background:'transparent',border:'none',cursor:'pointer',padding:'0 2px',color:'var(--danger)',display:'flex',alignItems:'center'}}><Icon name="close" size={14}/></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                {/* 다음 제품 스캔 슬롯으로 (activeIndex = -1) */}
                <button onClick={nextRegProduct} disabled={!activeBatch} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6,padding:'10px 16px',border:'1.5px dashed '+(!activeBatch?'var(--border-strong)':'var(--accent)'),borderRadius:10,fontSize:13,fontWeight:600,cursor:!activeBatch?'not-allowed':'pointer',background:'transparent',color:!activeBatch?'var(--fg-disabled)':'var(--accent)',marginBottom:14}}>
                  <Icon name="arrow_forward" size={16}/> 다음 제품 스캔 (현재 배치는 목록에 유지)
                </button>

                {/* 공통 옵션 */}
                <div style={{background:'var(--bg-card)',borderRadius:12,padding:14,border:'1px solid var(--border)',marginBottom:12}}>
                  <div style={{display:'grid',gridTemplateColumns:'200px 1fr auto',gap:12,alignItems:'end'}}>
                    <div>
                      <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>입고일</label>
                      <input type="date" value={regReceiptDate}
                        onChange={e=>{ regDateTouchedRef.current = true; setRegReceiptDate(e.target.value); }}
                        style={{...inputStyle, ...(regReceiptDate && regReceiptDate !== today() ? {borderColor:'var(--warning)', background:'#fff8f0'} : {})}}/>
                      {/* v7.226: 오늘이 아닌 날짜로 등록하면 배송시트지 기본 화면(오늘)에 안 보인다 */}
                      {regReceiptDate && regReceiptDate !== today() && (
                        <div style={{marginTop:5,fontSize:11,fontWeight:600,color:'var(--warning)',lineHeight:1.45}}>
                          ⚠ 오늘({today()})이 아닙니다 — 배송시트지 기본 화면에는 안 보입니다
                          <button type="button" onClick={()=>{ regDateTouchedRef.current = false; setRegReceiptDate(today()); }}
                            style={{marginLeft:6,padding:'1px 7px',border:'1px solid var(--warning)',borderRadius:5,background:'var(--bg-card)',color:'var(--warning)',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                            오늘로
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>비고 (모든 배치 공통)</label>
                      <input value={regNote} onChange={e=>setRegNote(e.target.value)} placeholder="메모 (선택)" style={inputStyle}/>
                    </div>
                    {/* v7.183: 장부처리 완료 체크박스 */}
                    <label style={{display:'inline-flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:regBookkept?'var(--success)':'var(--fg-3)',cursor:'pointer',padding:'8px 12px',borderRadius:8,background:regBookkept?'rgba(0,152,68,0.1)':'var(--bg-page)',border:`1px solid ${regBookkept?'var(--success)':'var(--border)'}`,transition:'all 0.15s',height:38}} title="체크 시 등록된 항목이 배송시트지에 '장부완료'로 표시됨. 나중에 배송시트지에서 더블클릭하여 토글 가능.">
                      <input type="checkbox" checked={regBookkept} onChange={e=>setRegBookkept(e.target.checked)} style={{cursor:'pointer',margin:0,width:14,height:14}}/>
                      📒 장부처리 완료
                    </label>
                  </div>
                </div>

                {/* Message */}
                {regMsg&&(<div style={{marginBottom:12,padding:14,borderRadius:12,fontSize:14,fontWeight:500,display:'flex',alignItems:'center',gap:10,background:regMsg.type==='ok'?'rgba(0,152,68,0.08)':'rgba(186,26,26,0.08)',color:regMsg.type==='ok'?'var(--success)':'var(--danger)'}}><Icon name={regMsg.type==='ok'?'check_circle':'error'} size={20}/>{regMsg.msg}</div>)}

                {/* Submit */}
                {(()=>{
                  const totalProducts = regTotalProducts;
                  const totalSerials = regTotalSerials;
                  const canSubmit = totalProducts>0 && totalSerials>0;
                  return (
                    <button onClick={handleRegister} disabled={loading||!canSubmit} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'16px 24px',border:'none',borderRadius:14,fontSize:16,fontWeight:700,cursor:canSubmit&&!loading?'pointer':'not-allowed',background:canSubmit&&!loading?'var(--success-solid)':'var(--surface-mute)',color:'#fff',transition:'background 0.2s',boxShadow:canSubmit&&!loading?'0 4px 12px rgba(0,152,68,0.2)':'none'}}>
                      <Icon name="check_circle" size={22} style={{color:'#fff'}}/>
                      {canSubmit ? `완료 — ${totalProducts}개 제품, ${totalSerials}개 시리얼 일괄 등록` : '완료'}
                    </button>
                  );
                })()}
              </div>
            </Card>
          </div>)}

          {/* ═══ v7.113: 📱 MOBILE SCAN (모바일 입고 스캔) ═══ */}
          {/* v7.129: isMobile 가드 추가 (PC에선 렌더 안 됨), onNavigate prop 전달 */}
          {page === 'scan' && isMobile && (
            <Suspense fallback={<div style={{padding:40,textAlign:'center',color:'var(--fg-3)'}}>모바일 스캔 페이지 로딩...</div>}>
              <MobileScan onExit={()=>setPage('inventory')} onNavigate={(p)=>setPage(p)}/>
            </Suspense>
          )}

          {/* ═══ SHIP REQUEST (출고 요청) ═══ */}
          {/* v7.119: 모바일에서는 MobileShipRequest 컴포넌트 */}
          {page==='ship' && isMobile && (
            <Suspense fallback={<div style={{padding:40,textAlign:'center',color:'var(--fg-3)'}}>출고 요청 폼 로딩...</div>}>
              <MobileShipRequest onExit={()=>setPage('scan')} onNavigate={(p)=>setPage(p)}/>
            </Suspense>
          )}
          {page==='ship' && !isMobile && (<div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28}}>
              <div>
                <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>제품 출고 요청</h1>
                <p style={{fontSize:16,color:'var(--fg-2)'}}>새로운 재고 출고 프로세스를 시작합니다.</p>
              </div>
              <div style={{display:'flex',gap:8}}>
                {/* v7.71: 폼 수동 초기화 버튼 — 탭 왕복 시 상태 유지 정책과 짝을 이룸 */}
                <button onClick={()=>{ if(window.confirm('작성 중인 출고 요청 내용이 모두 사라집니다.\n초기화하시겠습니까?')) resetShipForm(); }} style={{display:'flex',alignItems:'center',gap:8,padding:'10px 20px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:12,cursor:'pointer',fontSize:14,fontWeight:600,color:'var(--fg-2)'}}>
                  <Icon name="refresh" size={18}/> 초기화
                </button>
                <button onClick={()=>setPage('txlog')} style={{display:'flex',alignItems:'center',gap:8,padding:'10px 20px',background:'var(--bg-info)',border:'1px solid var(--border-strong)',borderRadius:12,cursor:'pointer',fontSize:14,fontWeight:600,color:'var(--fg-2)'}}>
                  <Icon name="history" size={18}/> 요청 이력
                </button>
              </div>
            </div>

            <div style={{display:'grid',gridTemplateColumns:'1fr 360px',gap:24}}>
              {/* Left: Product + Shipping */}
              <div style={{display:'flex',flexDirection:'column',gap:24}}>
                {/* Product Selection */}
                <Card style={{padding:24,position:'relative',zIndex:10}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:20}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <Icon name="inventory" size={20} style={{color:'var(--accent)'}}/>
                      <h2 style={{fontSize:20,fontWeight:600}}>출고할 제품</h2>
                    </div>
                    <span style={{fontSize:14,fontWeight:600,color:'var(--accent)',display:'flex',alignItems:'center',gap:4,cursor:'pointer'}}>
                      <Icon name="add_circle" size={18} style={{color:'var(--accent)'}}/> 제품 추가
                    </span>
                  </div>

                  {/* Search / Quick Add */}
                  <div style={{position:'relative',marginBottom:8}}>
                    <Icon name="search" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                    <input value={shipSearch}
                      onChange={e=>searchProducts(e.target.value)}
                      onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); quickAddShipItem(); } }}
                      placeholder="제품명/SKU 검색 또는 'TS-233-EU 5개' 형식으로 빠른 추가 (Enter)"
                      style={{...inputStyle,paddingLeft:42,fontSize:16}}/>
                    {shipResults.length>0&&(
                      <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,marginTop:4,maxHeight:320,overflowY:'auto',zIndex:1000,boxShadow:'0 12px 40px rgba(0,0,0,0.18)'}}>
                        {shipResults.map(r=>{
                          /* v7.374: 재고 0도 목록에 나온다. 다만 [고를 수 있다] 와
                             [지금 나갈 수 있다] 는 다른 사실이라 눈에 띄게 갈라 놓는다.
                             ⚠ 흐리게만 처리하면 비활성으로 오해한다 — 문구로 못박는다. */
                          const none = !r.in_stock;
                          return (
                          <div key={r.model} onClick={()=>addShipItem(r)} style={{padding:'12px 16px',display:'flex',alignItems:'center',gap:12,cursor:'pointer',borderBottom:'1px solid var(--border)',transition:'background 0.1s',background:none?'var(--bg-subtle)':undefined}} onMouseEnter={e=>e.currentTarget.style.background='#F1F5F9'} onMouseLeave={e=>e.currentTarget.style.background=none?'var(--bg-subtle)':''}>
                            <div style={{width:40,height:40,borderRadius:8,background:none?'var(--bg-mute)':'var(--bg-accent-strong)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon name={none?'production_quantity_limits':'package_2'} size={18} style={{color:none?'var(--fg-3)':'var(--fg-2)'}}/></div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:14,fontWeight:600,color:none?'var(--fg-2)':undefined}}>{r.model}</div>
                              <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3,flexWrap:'wrap'}}>
                                {none ? (
                                  <>
                                    <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:5,fontSize:10.5,fontWeight:700,
                                      background:'rgba(186,26,26,0.1)',color:'var(--danger)',border:'1px solid rgba(186,26,26,0.28)'}}>
                                      <Icon name="error_outline" size={11}/>재고없음
                                    </span>
                                    <span style={{fontSize:11.5,color:'var(--fg-3)'}}>요청은 가능 · 입고 후 시리얼 스캔으로 처리됩니다</span>
                                  </>
                                ) : (
                                  <>
                                    <span style={{fontSize:12,color:'var(--fg-3)'}}>재고 {r.in_stock}개</span>
                                    {r.byType&&Object.entries(r.byType).map(([type,n])=>(
                                      <span key={type} style={{padding:'1px 7px',borderRadius:5,fontSize:10,fontWeight:700,
                                        background:type==='신품'?'rgba(0,152,68,0.1)':type==='AS'?'rgba(84,110,122,0.12)':type==='리페킹'?'rgba(255,152,0,0.1)':'rgba(64,194,253,0.15)',
                                        color:type==='신품'?'var(--success)':type==='AS'?'#546e7a':type==='리페킹'?'var(--warning)':'#004d6a'}}>{type} ×{n}</span>
                                    ))}
                                  </>
                                )}
                              </div>
                            </div>
                            {r.category && <Badge variant="category">{r.category}</Badge>}
                          </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:14,display:'flex',alignItems:'center',gap:5}}>
                    <Icon name="keyboard" size={13} style={{color:'var(--fg-3)'}}/>
                    <span><strong style={{color:'var(--accent)'}}>Enter</strong>로 빠른 추가 · 예: <code style={{padding:'1px 5px',background:'var(--bg-accent-soft)',borderRadius:3,fontSize:11,fontFamily:'monospace',color:'var(--accent)'}}>TS-233-EU 5개</code> · DB에 없는 모델은 <strong style={{color:'var(--warning)'}}>외부 제품</strong>으로 추가됩니다</span>
                  </div>

                  {/* Selected items */}
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {!shipItems.length&&<div style={{padding:32,textAlign:'center',color:'var(--fg-3)',fontSize:14,background:'var(--bg-accent-soft)',borderRadius:12}}>제품을 검색하거나 <strong style={{color:'var(--accent)'}}>"TS-233-EU 5개"</strong> 형식으로 직접 입력 후 Enter 키</div>}
                    {shipItems.map(item=>{
                      const ext = !!item.external;
                      const bg = ext ? 'rgba(245,124,0,0.08)' : '#eff4ff';
                      const bgHover = ext ? 'rgba(245,124,0,0.14)' : '#dce9ff';
                      return (
                      <div key={item.model} style={{display:'flex',alignItems:'center',gap:12,padding:16,background:bg,border:ext?'1px dashed var(--warning)':'1px solid transparent',borderRadius:12,transition:'background 0.15s'}} onMouseEnter={e=>e.currentTarget.style.background=bgHover} onMouseLeave={e=>e.currentTarget.style.background=bg}>
                        <div style={{width:48,height:48,borderRadius:10,background:ext?'rgba(245,124,0,0.15)':'var(--bg-accent-strong)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <Icon name={ext?'help_outline':'package_2'} size={22} style={{color:ext?'var(--warning)':'var(--fg-2)'}}/>
                        </div>
                        <div style={{flex:1}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                            <span style={{fontSize:14,fontWeight:600}}>{item.model}</span>
                            {ext&&<span style={{padding:'2px 8px',borderRadius:6,fontSize:10,fontWeight:700,background:'rgba(245,124,0,0.15)',color:'var(--warning)',border:'1px solid rgba(245,124,0,0.3)',display:'inline-flex',alignItems:'center',gap:3}}><Icon name="warning_amber" size={11}/>DB 미등록</span>}
                            {/* v7.374: 담은 뒤에도 계속 보여야 한다. 드롭다운에서만 알리면
                                여러 건 담는 동안 어느 게 재고 없는 건지 잊는다. */}
                            {!ext && !item.available && (
                              <span style={{padding:'2px 8px',borderRadius:6,fontSize:10,fontWeight:700,background:'rgba(186,26,26,0.1)',color:'var(--danger)',border:'1px solid rgba(186,26,26,0.28)',display:'inline-flex',alignItems:'center',gap:3}}>
                                <Icon name="error_outline" size={11}/>재고없음
                              </span>
                            )}
                          </div>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginTop:3,flexWrap:'wrap'}}>
                            <span style={{fontSize:12,color:!ext&&!item.available?'var(--danger)':'var(--fg-3)'}}>
                              {ext ? `외부 제품 · 재고 추적 없음`
                                   : !item.available ? `현재 재고 0개 · 입고 후 시리얼 스캔으로 처리 · ${item.category}`
                                   : `재고 ${item.available}개 · ${item.category}`}
                            </span>
                            {item.byType&&Object.entries(item.byType).map(([type,n])=>(
                              <span key={type} style={{padding:'1px 7px',borderRadius:5,fontSize:10,fontWeight:700,
                                background:type==='신품'?'rgba(0,152,68,0.1)':type==='AS'?'rgba(84,110,122,0.12)':type==='리페킹'?'rgba(255,152,0,0.1)':'rgba(64,194,253,0.15)',
                                color:type==='신품'?'var(--success)':type==='AS'?'#546e7a':type==='리페킹'?'var(--warning)':'#004d6a'}}>{type} ×{n}</span>
                            ))}
                          </div>
                        </div>
                        <div style={{display:'flex',alignItems:'center',gap:8}}>
                          <button onClick={()=>updateShipQty(item.model,-1)} style={{width:32,height:32,borderRadius:'50%',border:'1px solid var(--border-strong)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',background:'var(--bg-card)',fontSize:16}}>−</button>
                          {/* v7.53: 수량 직접 입력 가능 — 외부 제품은 max 없음, 일반은 available 한도 */}
                          <input
                            type="number"
                            min={1}
                            max={shipQtyCapped(item) ? item.available : undefined}
                            value={item.qty}
                            onChange={e=>setShipQty(item.model, e.target.value)}
                            onFocus={e=>e.target.select()}
                            onBlur={e=>{
                              // 빈 값이면 1로, 한도 초과면 한도로
                              const n = parseInt(e.target.value, 10);
                              if (isNaN(n) || n < 1) setShipQty(item.model, '1');
                              else if (shipQtyCapped(item) && n > item.available) setShipQty(item.model, String(item.available));
                            }}
                            title={item.external ? '외부 제품 (재고 한도 없음)'
                                 : !item.available ? '현재 재고 없음 — 수량 제한 없이 요청할 수 있습니다'
                                 : `재고 한도: ${item.available}`}
                            style={{width:54,padding:'4px 6px',textAlign:'center',fontSize:14,fontWeight:600,border:'1.5px solid var(--border-strong)',borderRadius:6,outline:'none',background:'var(--bg-card)',MozAppearance:'textfield'}}/>
                          <button onClick={()=>updateShipQty(item.model,1)} style={{width:32,height:32,borderRadius:'50%',border:'1px solid var(--border-strong)',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer',background:'var(--bg-card)',fontSize:16}}>+</button>
                        </div>
                        <button onClick={()=>removeShipItem(item.model)} style={{background:'none',border:'none',cursor:'pointer',opacity:0.5,transition:'opacity 0.15s'}} onMouseEnter={e=>e.currentTarget.style.opacity=1} onMouseLeave={e=>e.currentTarget.style.opacity=0.5}>
                          <Icon name="delete" size={20} style={{color:'var(--danger)'}}/>
                        </button>
                      </div>
                    );})}
                  </div>
                </Card>

                {/* Shipping Info */}
                <Card style={{padding:24}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20}}>
                    <Icon name="local_shipping" size={20} style={{color:'var(--accent)'}}/>
                    <h2 style={{fontSize:20,fontWeight:600}}>배송 정보</h2>
                  </div>
                  <div style={{display:'grid',gridTemplateColumns:'1fr',gap:16}}>
                    <div>
                      <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>배송 방법</label>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(6, 1fr)',gap:8}}>
                        {[
                          ['courier','local_post_office','택배','#00668a'],
                          ['quick','electric_moped','퀵 서비스','#00668a'],
                          ['pickup','storefront','방문수령','#00668a'],
                          ['floor','warehouse','1층배송','#00668a'],
                          ['install','engineering','직접 설치','#00668a'],
                          ['rma','build','EMS','#e65100']
                        ].map(([id,icon,label,activeColor])=>(
                          <button key={id} onClick={()=>{
                            setShipMethod(id);
                            // 택배/퀵이 아닌 모드 선택 시 직발/네이버스토어 해제
                            if (id !== 'courier' && id !== 'quick') {
                              setShipDirectCompany('');
                              setShipNaverStore(false); // v7.103
                            }
                          }} style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',gap:3,padding:'12px 6px',border:`1.5px solid ${shipMethod===id?activeColor:'var(--border-strong)'}`,borderRadius:10,cursor:'pointer',fontSize:13,fontWeight:600,transition:'all 0.15s',background:shipMethod===id?(id==='rma'?'rgba(230,81,0,0.12)':'rgba(64,194,253,0.15)'):'var(--bg-card)',color:shipMethod===id?(id==='rma'?'var(--warning)':'#004d6a'):'var(--fg-2)'}}>
                            <Icon name={icon} size={20} style={{color:shipMethod===id?activeColor:'var(--fg-3)'}}/>
                            <span>{label}</span>
                          </button>
                        ))}
                      </div>
                      {/* v7.103: 직발 + 네이버스토어 체크박스 — 택배/퀵일 때만 활성 */}
                      {(shipMethod==='courier' || shipMethod==='quick') && (() => {
                        const isDirectChecked = shipDirectCompany !== '';
                        return (
                        <div style={{marginTop:10,display:'flex',gap:8,flexWrap:'wrap'}}>
                          {/* 직발 박스 */}
                          <div style={{flex:'1 1 320px',minWidth:260,padding:'12px 14px',background:isDirectChecked?'rgba(186,26,26,0.05)':'var(--bg-subtle)',border:'1px solid '+(isDirectChecked?'rgba(186,26,26,0.25)':'var(--border)'),borderRadius:10,transition:'all 0.15s'}}>
                            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',userSelect:'none'}}>
                              <input type="checkbox" checked={isDirectChecked}
                                onChange={e=>{ setShipDirectCompany(e.target.checked ? ' ' : ''); }}
                                style={{width:18,height:18,accentColor:'#ba1a1a',cursor:'pointer'}}/>
                              <Icon name="rocket_launch" size={16} style={{color:'var(--danger)'}}/>
                              <span style={{fontSize:13,fontWeight:700,color:'var(--danger)'}}>직발 모드</span>
                              <span style={{fontSize:11,color:'var(--fg-3)',marginLeft:4}}>(특정 업체에서 직접 발송)</span>
                            </label>
                            {isDirectChecked && (
                              <div style={{marginTop:10,paddingLeft:26}}>
                                <input value={shipDirectCompany.trim()} onChange={e=>setShipDirectCompany(e.target.value || ' ')} placeholder="직발 업체명 (예: 스토리지케이)" style={{...inputStyle,background:'var(--bg-card)'}} autoFocus/>
                                <div style={{marginTop:6,fontSize:11,color:'var(--fg-3)',lineHeight:1.5}}>
                                  발송종류 칸에 <b style={{padding:'1px 6px',background:'var(--danger-solid)',color:'#fff',borderRadius:3,fontWeight:700}}>{shipDirectCompany.trim() || '업체명'} 직발</b> 배지가 표시됩니다.
                                </div>
                              </div>
                            )}
                          </div>
                          {/* v7.103: 네이버스토어 박스 */}
                          <div style={{flex:'1 1 200px',minWidth:180,padding:'12px 14px',background:shipNaverStore?'rgba(3,199,90,0.06)':'var(--bg-subtle)',border:'1px solid '+(shipNaverStore?'rgba(3,199,90,0.35)':'var(--border)'),borderRadius:10,transition:'all 0.15s'}}>
                            <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',userSelect:'none'}}>
                              <input type="checkbox" checked={shipNaverStore}
                                onChange={e=>setShipNaverStore(e.target.checked)}
                                style={{width:18,height:18,accentColor:'#03C75A',cursor:'pointer'}}/>
                              <NaverLogo size={16} opacity={shipNaverStore?1:0.55}/>
                              <span style={{fontSize:13,fontWeight:700,color:'#03A853'}}>네이버스토어</span>
                            </label>
                            {shipNaverStore && (
                              <div style={{marginTop:8,fontSize:11,color:'var(--fg-3)',lineHeight:1.5,paddingLeft:26}}>
                                발송종류 옆에 <NaverLogo size={12} opacity={0.85}/> 네이버 로고가 표시됩니다.
                              </div>
                            )}
                          </div>
                        </div>
                      ); })()}
                      {/* 1층배송: 안내 */}
                      {shipMethod==='floor' && (
                        <div style={{marginTop:12,padding:'12px 14px',background:'rgba(0,152,68,0.05)',border:'1px solid rgba(0,152,68,0.2)',borderRadius:10,display:'flex',alignItems:'center',gap:8}}>
                          <Icon name="warehouse" size={18} style={{color:'var(--success)'}}/>
                          <div>
                            <div style={{fontSize:13,fontWeight:700,color:'var(--success)'}}>1층배송</div>
                            <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>1층 물류팀을 통해 배송합니다.</div>
                          </div>
                        </div>
                      )}
                      {/* v7.36: 직접 설치 안내 */}
                      {shipMethod==='install' && (
                        <div style={{marginTop:12,padding:'12px 14px',background:'rgba(123,31,162,0.05)',border:'1px solid rgba(123,31,162,0.25)',borderRadius:10,display:'flex',alignItems:'center',gap:8}}>
                          <Icon name="engineering" size={18} style={{color:'var(--purple)'}}/>
                          <div>
                            <div style={{fontSize:13,fontWeight:700,color:'var(--purple)'}}>직접 설치</div>
                            <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>엔지니어가 고객사를 방문해 직접 설치합니다. 배송지/수령인 정보와 방문 일정을 입력하세요.</div>
                          </div>
                        </div>
                      )}
                      {/* EMS: 상세 입력 */}
                      {shipMethod==='rma' && (
                        <div style={{marginTop:12,padding:'14px 16px',background:'rgba(230,81,0,0.05)',border:'1px solid rgba(230,81,0,0.3)',borderRadius:10}}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,gap:10,flexWrap:'wrap'}}>
                            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:13,fontWeight:700,color:'var(--warning)'}}>
                              <Icon name="build" size={16}/> EMS 정보
                            </div>
                            {/* RMA / DOA 선택 */}
                            <div style={{display:'flex',gap:4,background:'var(--bg-card)',padding:3,borderRadius:8,border:'1px solid rgba(230,81,0,0.3)'}}>
                              {['RMA','DOA'].map(t => (
                                <button key={t} onClick={()=>setShipEmsType(t)}
                                  style={{padding:'6px 16px',border:'none',borderRadius:6,background:shipEmsType===t?'var(--warning-solid)':'transparent',color:shipEmsType===t?'#fff':'var(--fg-3)',fontSize:12,fontWeight:700,cursor:'pointer',transition:'all 0.15s'}}>
                                  {t}
                                </button>
                              ))}
                            </div>
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                            <div style={{position:'relative'}}>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>제품명 *</label>
                              <input value={shipRmaProductName}
                                onChange={e=>searchShipRmaProduct(e.target.value)}
                                onBlur={()=>setTimeout(()=>setShipRmaProductResults([]),200)}
                                placeholder="제품명 입력 → 카탈로그 자동완성"
                                style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12}}/>
                              {/* v7.99: 자동완성 드롭다운 (카탈로그 표준명) */}
                              {shipRmaProductResults.length > 0 && (
                                <div style={{position:'absolute',top:'100%',left:0,right:0,zIndex:20,marginTop:2,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)',maxHeight:220,overflowY:'auto'}}>
                                  {shipRmaProductResults.map(p => (
                                    <div key={p.product_name}
                                      onMouseDown={()=>selectShipRmaProduct(p)}
                                      onMouseEnter={e=>e.currentTarget.style.background='#f5f5f7'}
                                      onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}
                                      style={{padding:'8px 10px',cursor:'pointer',borderBottom:'1px solid #f5f5f7',fontSize:12}}>
                                      <div style={{fontWeight:600,color:'#1a1c1e'}}>{p.product_name}</div>
                                      {p.category && <div style={{fontSize:10,color:'var(--fg-3)',marginTop:1}}>{p.category}</div>}
                                    </div>
                                  ))}
                                </div>
                              )}
                              {/* v7.99: 검증 뱃지 — 카탈로그 정확 매칭 여부 */}
                              {shipRmaProductName.trim() && shipRmaProductCatalogHit !== null && (
                                <div style={{marginTop:4,fontSize:10,padding:'4px 8px',borderRadius:5,fontWeight:600,
                                  background: shipRmaProductCatalogHit ? 'rgba(0,152,68,0.1)' : (shipEmsType==='DOA' ? 'rgba(245,124,0,0.1)' : 'rgba(0,102,138,0.08)'),
                                  color: shipRmaProductCatalogHit ? 'var(--success)' : (shipEmsType==='DOA' ? 'var(--warning)' : 'var(--fg-3)')
                                }}>
                                  {shipRmaProductCatalogHit
                                    ? '✓ 카탈로그 등록됨'
                                    : (shipEmsType==='DOA'
                                      ? '⚠ 카탈로그 미등록 — 수리완료 시 반품 시리얼 신품 등록 실패'
                                      : '카탈로그 미등록 (RMA는 처리 가능)')}
                                </div>
                              )}
                            </div>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>시리얼 *</label>
                              <input value={shipRmaSerial}
                                onChange={e=>{setShipRmaSerial(e.target.value); if(shipRmaSerialLookupMsg){setShipRmaSerialLookupMsg(null);setShipRmaSerialFromDB(false);}}}
                                onBlur={async (e)=>{
                                  const sn = e.target.value.trim();
                                  if (!sn) { setShipRmaSerialLookupMsg(null); setShipRmaSerialFromDB(false); return; }
                                  const data = await InventoryService.findBySerialFields(sn, 'serial_number,product_name,category,status');
                                  if (data) {
                                    setShipRmaSerialFromDB(true);
                                    setShipRmaProductName(data.product_name);
                                    // v7.99: inventory FK 특성상 카탈로그 존재 보장 → catalogHit=true + 드롭다운 클리어
                                    setShipRmaProductCatalogHit(true);
                                    setShipRmaProductResults([]);
                                    if (data.status === 'unsellable') {
                                      setShipRmaSerialLookupMsg({type:'warn',msg:`이미 판매불가 상태 (${data.product_name})`});
                                    } else if (data.status === 'shipped') {
                                      setShipRmaSerialLookupMsg({type:'warn',msg:`이미 출고된 시리얼 (${data.product_name}) — EMS 처리 시 판매불가로 전환됩니다`});
                                    } else {
                                      setShipRmaSerialLookupMsg({type:'ok',msg:`✓ DB 매칭: ${data.product_name} (${data.status==='in_stock'?'재고':data.status}) — EMS 발송 시 자동으로 판매불가 처리됩니다`});
                                    }
                                  } else {
                                    setShipRmaSerialFromDB(false);
                                    setShipRmaSerialLookupMsg({type:'info',msg:'DB 미등록 시리얼 — 외부 제품으로 처리됩니다'});
                                  }
                                }}
                                placeholder="예: QYWZC03763 (DB 자동 조회)" style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}/>
                              {shipRmaSerialLookupMsg && (
                                <div style={{marginTop:4,fontSize:10,padding:'4px 8px',borderRadius:5,fontWeight:600,
                                  background: shipRmaSerialLookupMsg.type==='ok'?'rgba(0,152,68,0.1)':shipRmaSerialLookupMsg.type==='warn'?'rgba(245,124,0,0.1)':'rgba(0,102,138,0.08)',
                                  color: shipRmaSerialLookupMsg.type==='ok'?'var(--success)':shipRmaSerialLookupMsg.type==='warn'?'var(--warning)':'var(--accent)'
                                }}>
                                  {shipRmaSerialLookupMsg.msg}
                                </div>
                              )}
                            </div>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>양식번호</label>
                              <input value={shipRmaFormNo} onChange={e=>setShipRmaFormNo(e.target.value)} placeholder="예: Q-202606-34519" style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}/>
                            </div>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>티켓번호</label>
                              <input value={shipRmaTicketNo} onChange={e=>setShipRmaTicketNo(e.target.value)} placeholder="예: RMA260600847" style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}/>
                            </div>
                            {/* v7.76: DOA일 때만 반품(들어오는) 시리얼 입력 필드 노출 */}
                            {shipEmsType === 'DOA' && (
                              <div style={{gridColumn:'1 / -1',padding:'10px 12px',background:'rgba(0,152,68,0.06)',border:'1px dashed var(--success)',borderRadius:8}}>
                                <label style={{display:'flex',alignItems:'center',gap:6,fontSize:11,fontWeight:700,color:'var(--success)',marginBottom:5}}>
                                  <Icon name="input" size={14}/> 반품(들어오는) 시리얼 <span style={{fontSize:10,fontWeight:500,color:'var(--fg-3)',marginLeft:4}}>(선택 — 나중에 배송시트지에서도 입력 가능)</span>
                                </label>
                                <input value={shipRmaReturnSerial} onChange={e=>setShipRmaReturnSerial(e.target.value)}
                                  placeholder="벤더로부터 신품 교체로 회수될 시리얼 (수리완료 처리 시 자동 재고 등록)"
                                  style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}/>
                                <div style={{fontSize:10,color:'var(--fg-3)',marginTop:4,lineHeight:1.4}}>
                                  ※ 수리완료 처리 시 이 시리얼이 <b>신품 / source=DOA</b>로 재고에 자동 등록됩니다.
                                  원 시리얼({shipRmaSerial.trim() || '위 시리얼'})은 판매불가 상태로 유지됩니다.
                                </div>
                              </div>
                            )}
                            <div style={{gridColumn:'1 / -1'}}>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>우체국 EMS</label>
                              <input value={shipRmaEms} onChange={e=>setShipRmaEms(e.target.value)} placeholder="예: ES 298467625 KR" style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,fontFamily:'monospace'}}/>
                            </div>
                            {/* 작성자 / 요청자 */}
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>작성자</label>
                              <select value={shipAuthor} onChange={e=>setShipAuthor(e.target.value)} style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,cursor:'pointer'}}>
                                <option value="">선택하세요</option>
                                {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:600,color:'var(--fg-2)',marginBottom:4}}>요청자</label>
                              <select value={shipRequester} onChange={e=>setShipRequester(e.target.value)} style={{...inputStyle,background:'var(--bg-card)',padding:'8px 10px',fontSize:12,cursor:'pointer'}}>
                                <option value="">선택하세요</option>
                                {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                              </select>
                            </div>
                          </div>
                          <div style={{marginTop:8,fontSize:11,color:'var(--fg-3)',lineHeight:1.5}}>
                            배송지 칸에 <b style={{padding:'1px 6px',background:'var(--warning-solid)',color:'#fff',borderRadius:3,fontWeight:700}}>{shipEmsType}</b> 주황 배지가 표시됩니다.
                          </div>
                        </div>
                      )}
                      {/* 택배: 송장번호 (여러 개) */}
                      {shipMethod==='courier' && (
                        <div style={{marginTop:12,padding:'12px 14px',background:'rgba(0,102,138,0.05)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:10}}>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--accent)',marginBottom:6}}>
                            <Icon name="qr_code_2" size={14}/> 택배 송장번호
                          </label>
                          {(()=>{
                            const arr = shipTrackingNo ? shipTrackingNo.split(',').map(s=>s.trim()) : [''];
                            const list = arr.length === 0 ? [''] : arr;
                            const updateAt = (idx, v) => {
                              const next = [...list]; next[idx] = v.replace(/[^0-9-]/g,'');
                              setShipTrackingNo(next.filter((s,i)=>i===0||s).join(','));
                            };
                            const addRow = () => setShipTrackingNo((shipTrackingNo?shipTrackingNo+',':'') + '');
                            const removeRow = (idx) => {
                              const v = list[idx];
                              if (v && v.trim() && !window.confirm(`송장번호 "${v}"를 삭제하시겠습니까?`)) return;
                              const next = [...list]; next.splice(idx,1);
                              setShipTrackingNo(next.join(','));
                            };
                            return (
                              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                                {list.map((v,idx)=>(
                                  <div key={idx} style={{display:'flex',gap:6,alignItems:'center'}}>
                                    <input value={v} onChange={e=>updateAt(idx,e.target.value)} placeholder={idx===0?'예: 6026915218862 (없으면 비워두세요)':'추가 송장번호'} style={{...inputStyle,fontFamily:'monospace',background:'var(--bg-card)',flex:1}}/>
                                    {list.length > 1 && (
                                      <button onClick={()=>removeRow(idx)} title="제거" style={{width:32,height:32,border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--danger)',flexShrink:0}}>
                                        <Icon name="close" size={14}/>
                                      </button>
                                    )}
                                  </div>
                                ))}
                                <button onClick={addRow} style={{alignSelf:'flex-start',display:'inline-flex',alignItems:'center',gap:4,padding:'6px 12px',border:'1px dashed var(--accent)',background:'transparent',color:'var(--accent)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>
                                  <Icon name="add" size={14}/> 송장번호 추가
                                </button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                      {/* 퀵: 가격 + v7.54: 착불 체크박스 */}
                      {shipMethod==='quick' && (
                        <div style={{marginTop:12,padding:'12px 14px',background:'rgba(186,26,26,0.05)',border:'1px solid rgba(186,26,26,0.2)',borderRadius:10}}>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                            <Icon name="payments" size={14}/> 퀵 서비스 가격 (원)
                          </label>
                          <div style={{position:'relative'}}>
                            <input type="text" inputMode="numeric" value={shipQuickPrice} onChange={e=>{
                              const raw = e.target.value.replace(/[^0-9]/g,'');
                              setShipQuickPrice(raw);
                            }} placeholder="예: 10000" style={{...inputStyle,fontFamily:'monospace',background:'var(--bg-card)',paddingRight:36}}/>
                            <span style={{position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--fg-3)',pointerEvents:'none'}}>원</span>
                          </div>
                          {/* v7.228: 지역 — 기본은 잠금(배송지 주소에서 자동 추출).
                              [직접 입력] 체크 시에만 수기 편집을 연다. */}
                          {(()=>{
                            const auto = guessRegion(shipAddr);
                            const finalVal = shipRegionManual ? shipQuickRegion.trim() : auto;
                            return (
                              <div style={{marginTop:10}}>
                                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,marginBottom:6}}>
                                  <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)'}}>
                                    <Icon name="pin_drop" size={14}/> 지역
                                    <span style={{fontSize:11,fontWeight:500,color:'var(--fg-3)'}}>(시트에 입력될 값)</span>
                                  </label>
                                  <label style={{display:'flex',alignItems:'center',gap:5,fontSize:11.5,fontWeight:700,color:shipRegionManual?'var(--warning)':'var(--fg-3)',cursor:'pointer',userSelect:'none'}}>
                                    <input type="checkbox" checked={shipRegionManual}
                                      onChange={e=>{
                                        const on = e.target.checked;
                                        setShipRegionManual(on);
                                        // 켤 때는 자동값을 미리 채워 수정만 하면 되게, 끌 때는 비워 자동값으로 되돌림
                                        setShipQuickRegion(on ? (shipQuickRegion.trim() || auto) : '');
                                      }}
                                      style={{width:14,height:14,margin:0,accentColor:'#e65100',cursor:'pointer'}}/>
                                    직접 입력
                                  </label>
                                </div>
                                <input value={shipRegionManual ? shipQuickRegion : auto}
                                  onChange={e=>setShipQuickRegion(e.target.value)}
                                  disabled={!shipRegionManual}
                                  placeholder={shipRegionManual ? '예: 대전광역시 서구' : '배송지 주소를 입력하면 자동으로 채워집니다'}
                                  style={{...inputStyle,
                                    background: shipRegionManual ? 'var(--bg-card)' : '#f1f5f9',
                                    color: shipRegionManual ? 'var(--fg-1)' : 'var(--fg-2)',
                                    cursor: shipRegionManual ? 'text' : 'not-allowed'}}/>
                                <div style={{marginTop:5,fontSize:11,color:finalVal?'var(--fg-3)':'var(--warning)'}}>
                                  {finalVal
                                    ? <>시트 기록: <b style={{color:'var(--success)'}}>{finalVal}</b>
                                        {!shipRegionManual && <> · <b style={{color:'var(--warning)'}}>배송지 주소에서 자동 추출</b></>}
                                        {shipRegionManual && <> · <b style={{color:'var(--warning)'}}>직접 입력</b></>}
                                      </>
                                    : shipRegionManual
                                      ? '지역을 입력해주세요'
                                      : '배송지 주소에서 구/군/시를 찾지 못했습니다 — [직접 입력]을 체크해 수기로 넣어주세요'}
                                </div>
                              </div>
                            );
                          })()}

                          {/* v7.217: 차량 (배차 시트 '차량' 칸) — 자유 입력 + datalist 추천 */}
                          <div style={{marginTop:10}}>
                            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                              <Icon name="local_shipping" size={14}/> 차량
                            </label>
                            <input list="quickVehicleOptions" value={shipQuickVehicle}
                              onChange={e=>setShipQuickVehicle(e.target.value)}
                              placeholder="오토바이 / 다마스 / 라보 …"
                              style={{...inputStyle,background:'var(--bg-card)'}}/>

                          </div>

                          {/* v7.217: 구분 — 선불/착불 선택 (기존 shipQuickCOD 상태를 그대로 사용) */}
                          <div style={{marginTop:10}}>
                            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                              <Icon name="account_balance_wallet" size={14}/> 구분
                            </label>
                            <div style={{display:'flex',gap:6}}>
                              {[[false,'선불','#00668a'],[true,'착불','#ba1a1a']].map(([val,label,color])=>(
                                <button key={label} type="button" onClick={()=>setShipQuickCOD(val)}
                                  style={{flex:1,padding:'9px 10px',borderRadius:9,cursor:'pointer',fontSize:13,fontWeight:700,fontFamily:'inherit',
                                    background: shipQuickCOD===val ? `${color}14` : 'var(--bg-card)',
                                    border:`1.5px solid ${shipQuickCOD===val ? color : 'var(--fg-disabled)'}`,
                                    color: shipQuickCOD===val ? color : 'var(--fg-3)'}}>
                                  {label}{val ? ' (수령자 결제)' : ''}
                                </button>
                              ))}
                            </div>
                          </div>

                          {/* v7.220: 기사님 번호 (배차 시트 I열) */}
                          <div style={{marginTop:10}}>
                            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                              <Icon name="support_agent" size={14}/> 기사님 번호
                              <span style={{fontSize:11,fontWeight:500,color:'var(--fg-3)'}}>(선택)</span>
                            </label>
                            <input value={shipQuickDriverPhone}
                              onChange={e=>setShipQuickDriverPhone(phoneOnChange(shipQuickDriverPhone, e.target.value))}
                              placeholder="010-0000-0000"
                              style={{...inputStyle,fontFamily:'monospace',background:'var(--bg-card)'}}/>
                          </div>

                          {/* v7.231: 퀵 시트 전용 비고 (J열) — 배송시트지 비고와 별개 */}
                          <div style={{marginTop:10}}>
                            <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                              <Icon name="sticky_note_2" size={14}/> 비고
                              <span style={{fontSize:11,fontWeight:500,color:'var(--fg-3)'}}>(퀵 시트에만 기록 · 선택)</span>
                            </label>
                            <input value={shipQuickRemark}
                              onChange={e=>setShipQuickRemark(e.target.value)}
                              placeholder={shipRecipient.trim() || '비워두면 수령인이 들어갑니다'}
                              style={{...inputStyle,background:'var(--bg-card)'}}/>
                            {/* v7.232: 비워두면 수령인(업체명)이 자동으로 들어간다 */}
                            <div style={{marginTop:5,fontSize:11,color:'var(--fg-3)'}}>
                              {shipQuickRemark.trim()
                                ? <>시트 기록: <b style={{color:'var(--success)'}}>{shipQuickRemark.trim()}</b></>
                                : shipRecipient.trim()
                                  ? <>시트 기록: <b style={{color:'var(--success)'}}>{shipRecipient.trim()}</b> · <b style={{color:'var(--warning)'}}>수령인에서 자동</b></>
                                  : '수령인을 입력하면 업체명이 자동으로 들어갑니다'}
                            </div>
                          </div>
                          {(shipQuickPrice || shipQuickCOD) && (
                            <div style={{marginTop:6,fontSize:11,color:'var(--fg-3)'}}>
                              표시: <b style={{color:'var(--danger)'}}>퀵{shipQuickCOD?'(착불)':''}{shipQuickPrice?` (${Number(shipQuickPrice).toLocaleString()}원)`:''}</b>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {shipMethod === 'rma' ? null : (
                    <>
                    {shipPickup ? (
                      <div style={{padding:14,background:'rgba(0,102,138,0.06)',border:'1px dashed var(--accent)',borderRadius:12}}>
                        <label style={{display:'flex',alignItems:'center',gap:6,fontSize:14,fontWeight:600,color:'var(--accent)',marginBottom:10}}>
                          <Icon name="schedule" size={16}/>
                          방문 예정 시간
                        </label>
                        <div style={{display:'flex',gap:8}}>
                          {['오전','오후'].map(v=>(
                            <button key={v} onClick={()=>setShipVisitTime(v)}
                              style={{flex:1,padding:'12px 16px',border:shipVisitTime===v?'2px solid var(--accent)':'1px solid var(--border-strong)',borderRadius:10,background:shipVisitTime===v?'rgba(0,102,138,0.1)':'var(--bg-card)',color:shipVisitTime===v?'var(--accent)':'var(--fg-2)',fontSize:15,fontWeight:shipVisitTime===v?700:500,cursor:'pointer',transition:'all 0.15s',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                              <Icon name={v==='오전'?'wb_sunny':'nights_stay'} size={18} style={{color:shipVisitTime===v?'var(--accent)':'var(--fg-3)'}}/>
                              {v}
                            </button>
                          ))}
                        </div>
                        <div style={{fontSize:11,color:'var(--fg-3)',marginTop:8,display:'flex',alignItems:'center',gap:4}}>
                          <Icon name="event" size={12} style={{color:'var(--accent)'}}/>
                          방문 날짜는 <strong style={{color:'var(--accent)',margin:'0 3px'}}>예상 출고일</strong> ({fmtDate(shipExpectedDate)}) 기준으로 설정됩니다.
                        </div>
                        {/* v7.212: 방문 업체명 — 방문자 이름과 별개. 배송시트지 주소 칸에 함께 표기된다 */}
                        <div style={{marginTop:12}}>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:14,fontWeight:600,color:'var(--accent)',marginBottom:8}}>
                            <Icon name="store" size={16}/>
                            업체명 <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>(선택)</span>
                          </label>
                          <input value={shipPickupCompany} onChange={e=>setShipPickupCompany(e.target.value)}
                            placeholder="방문하는 업체명" style={{...inputStyle, background:'var(--bg-card)'}}/>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:6,marginBottom:8}}>
                          <label style={{fontSize:14,fontWeight:600,color:'var(--fg-2)'}}>배송지 주소</label>
                          {/* v7.195: 다음 우편번호 검색 — 우체국 API 대비 (신규 등록 시점부터 정형화) */}
                          <button type="button"
                            onClick={() => openDaumPostcode((data) => {
                              setShipZip(data.zonecode || '');
                              setShipAddr(data.roadAddress || data.address || '');
                              setAddrSuggestOpen(false);
                            })}
                            style={{padding:'4px 10px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'rgba(0,102,138,0.08)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:6,cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:3}}>
                            <Icon name="search" size={11}/>주소검색
                          </button>
                        </div>
                        {/* 우편번호 pill (있으면 표시) */}
                        {shipZip && (
                          <div style={{marginBottom:6}}>
                            <span style={{padding:'2px 9px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--bg-accent-strong)',borderRadius:9999,fontFamily:'monospace'}}>{shipZip}</span>
                          </div>
                        )}
                        <div style={{position:'relative'}}>
                          <input
                            value={shipAddr}
                            onChange={e=>{setShipAddr(e.target.value);setAddrSuggestIndex(-1);setAddrSuggestOpen(true);}}
                            onFocus={()=>setAddrSuggestOpen(true)}
                            onBlur={()=>setTimeout(()=>setAddrSuggestOpen(false), 180)}
                            onKeyDown={(e)=>{
                              if (!addrSuggestOpen || addrSuggestions.length === 0) return;
                              if (e.key === 'ArrowDown') { e.preventDefault(); setAddrSuggestIndex(i => Math.min(addrSuggestions.length-1, i+1)); }
                              else if (e.key === 'ArrowUp') { e.preventDefault(); setAddrSuggestIndex(i => Math.max(-1, i-1)); }
                              else if (e.key === 'Enter' && addrSuggestIndex >= 0) {
                                e.preventDefault();
                                const h = addrSuggestions[addrSuggestIndex];
                                setShipAddr(h.addr);
                                if (h.recipient && !shipRecipient.trim()) setShipRecipient(h.recipient);
                                if (h.phone && !shipPhone.trim()) setShipPhone(h.phone);
                                setAddrSuggestOpen(false); setAddrSuggestIndex(-1);
                              }
                              else if (e.key === 'Escape') { setAddrSuggestOpen(false); setAddrSuggestIndex(-1); }
                            }}
                            placeholder="상세 주소를 입력하세요"
                            style={inputStyle}
                          />
                          {addrSuggestOpen && addrSuggestions.length > 0 && (
                            <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:10,boxShadow:'0 8px 24px rgba(0,0,0,0.14)',maxHeight:340,overflowY:'auto',zIndex:50}}>
                              <div style={{padding:'8px 12px',fontSize:11,fontWeight:700,color:'var(--fg-3)',borderBottom:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',alignItems:'center',gap:5,letterSpacing:'0.03em'}}>
                                <Icon name="history" size={13} style={{color:'var(--accent)'}}/>
                                {shipAddr.trim() ? `검색 결과 ${addrSuggestions.length}건` : `최근 사용 ${addrSuggestions.length}건`}
                              </div>
                              {addrSuggestions.map((h, i) => {
                                const isHover = addrSuggestIndex === i;
                                // 검색어 하이라이트
                                const q = shipAddr.trim();
                                let display = h.addr;
                                if (q) {
                                  const idx = h.addr.toLowerCase().indexOf(q.toLowerCase());
                                  if (idx >= 0) {
                                    const before = h.addr.slice(0, idx);
                                    const match = h.addr.slice(idx, idx + q.length);
                                    const after = h.addr.slice(idx + q.length);
                                    display = (<><span>{before}</span><span style={{background:'#ffeaa7',fontWeight:700,color:'var(--fg-1)'}}>{match}</span><span>{after}</span></>);
                                  }
                                }
                                return (
                                  <button key={i} type="button"
                                    onMouseDown={(e)=>{
                                      e.preventDefault();
                                      setShipAddr(h.addr);
                                      if (h.recipient && !shipRecipient.trim()) setShipRecipient(h.recipient);
                                      if (h.phone && !shipPhone.trim()) setShipPhone(h.phone);
                                      setAddrSuggestOpen(false); setAddrSuggestIndex(-1);
                                    }}
                                    onMouseEnter={()=>setAddrSuggestIndex(i)}
                                    style={{display:'block',width:'100%',padding:'10px 14px',textAlign:'left',cursor:'pointer',background:isHover?'rgba(0,102,138,0.08)':'transparent',border:'none',borderBottom:i<addrSuggestions.length-1?'1px solid #f0f4ff':'none'}}>
                                    <div style={{fontSize:13,fontWeight:600,color:'var(--fg-1)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{display}</div>
                                    <div style={{fontSize:11,color:'var(--fg-3)',marginTop:3,display:'flex',gap:10,alignItems:'center'}}>
                                      <span style={{display:'flex',alignItems:'center',gap:3}}><Icon name="person" size={11}/>{h.recipient || '-'}</span>
                                      {h.phone && <span style={{fontFamily:'monospace',color:'var(--fg-2)'}}>{h.phone}</span>}
                                      <span style={{marginLeft:'auto',color:'var(--accent)',fontWeight:700,padding:'1px 7px',borderRadius:9999,background:'rgba(0,102,138,0.08)'}}>{h.count}회</span>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        {/* v7.195: 상세주소 (동·호수 등) — 우체국 API recAddr2 매핑 */}
                        <input value={shipAddrDetail}
                          onChange={e=>setShipAddrDetail(e.target.value)}
                          placeholder="상세주소 (동·호수 등)"
                          style={{...inputStyle,marginTop:8}}/>
                      </div>
                    )}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                      <div>
                        <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>{shipPickup?'방문자 이름':'수령인/업체명'}</label>
                        <input value={shipRecipient} onChange={e=>setShipRecipient(e.target.value)} placeholder={shipPickup?'방문자 성함':'수령인 또는 업체명'} style={inputStyle}/>
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>연락처</label>
                        <input value={shipPhone} onChange={e=>setShipPhone(phoneOnChange(shipPhone, e.target.value))} placeholder="010-0000-0000" style={inputStyle}/>
                      </div>
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
                      <div>
                        <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>작성자</label>
                        <select value={shipAuthor} onChange={e=>setShipAuthor(e.target.value)} style={{...inputStyle,cursor:'pointer'}}>
                          <option value="">선택하세요</option>
                          {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>요청자</label>
                        <select value={shipRequester} onChange={e=>setShipRequester(e.target.value)} style={{...inputStyle,cursor:'pointer'}}>
                          <option value="">선택하세요</option>
                          {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>비고 <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>(선택)</span></label>
                      <textarea value={shipMemo} onChange={e=>setShipMemo(e.target.value)}
                        placeholder="추가 안내사항, 특이사항 등을 자유롭게 적으세요"
                        rows={5}
                        style={{...inputStyle,resize:'vertical',minHeight:140,fontFamily:'inherit',lineHeight:1.5}}/>
                    </div>
                    </>
                    )}
                  </div>
                </Card>
              </div>

              {/* Right: Summary */}
              <div style={{display:'flex',flexDirection:'column',gap:20,position:'sticky',top:32,alignSelf:'flex-start'}}>
                <Card style={{padding:24}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:20}}>
                    <Icon name="fact_check" size={20} style={{color:'var(--accent)'}}/>
                    <h2 style={{fontSize:20,fontWeight:600}}>최종 확인</h2>
                  </div>

                  <div style={{marginBottom:20}}>
                    <label style={{display:'block',fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>매출 여부</label>
                    <select value={shipSalesStatus} onChange={e=>setShipSalesStatus(e.target.value)} style={{...inputStyle,background:'var(--bg-info)'}}>
                      <option>입력필요</option>
                      <option>입력완료</option>
                      <option>수기입력</option>
                    </select>
                    {shipSalesStatus === '수기입력' && (
                      <input value={shipSalesStatusManual} onChange={e=>setShipSalesStatusManual(e.target.value)}
                        placeholder="수기 입력 내용 (예: 거래처 직접 정산)"
                        style={{...inputStyle,marginTop:8,background:'#fff8e1',border:'1.5px solid #f57c00'}}/>
                    )}
                    {shipMethod === 'quick' && !shipPickup && (
                      <div style={{marginTop:12}}>
                        <label style={{fontSize:14,fontWeight:600,color:'var(--fg-2)',marginBottom:8,display:'flex',alignItems:'center',gap:6}}>
                          <Icon name="electric_moped" size={16} style={{color:'var(--danger)'}}/>
                          퀵 매출여부
                        </label>
                        <select value={shipQuickSalesStatus} onChange={e=>setShipQuickSalesStatus(e.target.value)} style={{...inputStyle,background:'rgba(186,26,26,0.06)',border:'1px solid rgba(186,26,26,0.25)'}}>
                          <option>입력필요</option>
                          <option>입력완료</option>
                          <option>수기입력</option>
                        </select>
                        {shipQuickSalesStatus === '수기입력' && (
                          <input value={shipQuickSalesStatusManual} onChange={e=>setShipQuickSalesStatusManual(e.target.value)}
                            placeholder="퀵 수기 입력 내용"
                            style={{...inputStyle,marginTop:8,background:'#fff8e1',border:'1.5px solid #f57c00'}}/>
                        )}
                      </div>
                    )}
                    {shipPickup && (
                      <div style={{marginTop:12,padding:'10px 12px',background:'rgba(0,102,138,0.08)',border:'1px solid rgba(0,102,138,0.25)',borderRadius:10,display:'flex',alignItems:'center',gap:8}}>
                        <Icon name="storefront" size={16} style={{color:'var(--accent)'}}/>
                        <div style={{flex:1}}>
                          <div style={{fontSize:12,fontWeight:700,color:'var(--accent)'}}>방문수령 모드</div>
                          <div style={{fontSize:11,color:'var(--fg-2)',marginTop:1}}>
                            {/* v7.212: 요약줄에도 업체명 반영 */}
                            {shipPickupCompany.trim() ? `${shipPickupCompany.trim()} · ` : ''}방문 예정: {shipVisitTime||'시간 미입력'} ({fmtDate(shipExpectedDate)})
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  <div style={{padding:16,background:'var(--bg-page)',border:'1px solid var(--border-strong)',borderRadius:12,marginBottom:24}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                      <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>총 제품 종류</span>
                      <span style={{fontSize:14,fontWeight:700}}>{shipItems.length} 건</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                      <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>총 수량</span>
                      <span style={{fontSize:14,fontWeight:700}}>{shipTotalQty} 개</span>
                    </div>
                    {/* v7.374: 재고 없는 품목이 섞여 있으면 [보내기 직전] 에 한 번 더 알린다.
                        ⚠ 담을 때만 알리면 여러 건 담는 동안 잊는다. 여기가 마지막 관문이라
                          출고 요청 버튼 바로 위에 둔다. 막지는 않는다 — 예약 요청은 정상 업무다. */}
                    {(() => {
                      const zero = shipItems.filter(i => !i.external && !i.available);
                      if (!zero.length) return null;
                      return (
                        <div style={{marginBottom:10,padding:'9px 11px',borderRadius:9,fontSize:11.5,lineHeight:1.6,
                          background:'rgba(186,26,26,0.07)',border:'1px solid rgba(186,26,26,0.28)',color:'var(--danger)',fontWeight:600}}>
                          <Icon name="error_outline" size={13} style={{verticalAlign:'-2px',marginRight:4}}/>
                          재고없음 {zero.length}건 — {zero.map(i=>i.model).slice(0,2).join(', ')}{zero.length>2?` 외 ${zero.length-2}건`:''}
                          <div style={{fontWeight:500,marginTop:2,color:'var(--fg-3)'}}>입고 후 시리얼 스캔으로 처리됩니다. 요청은 그대로 진행할 수 있습니다.</div>
                        </div>
                      );
                    })()}
                    {/* v7.375: 킵 초과 — [보내기 전] 에 알린다.
                        ⚠ 예전에는 출고 요청 버튼을 눌러야 알 수 있었고, 그것도 차단이었다.
                          누르고 실패하는 것보다 누르기 전에 아는 편이 낫다.
                        ⚠ 재고 자체가 0인 품목은 위 [재고없음] 이 이미 말하고 있다.
                          여기서 또 세면 같은 제품이 두 번 경고돼 둘 다 무시하게 된다. */}
                    {(() => {
                      const over = shipItems.filter(i => {
                        if (i.external || !i.available) return false;
                        const keep = activeKeepsByModel[i.model] || 0;
                        return i.qty > (i.available - keep);
                      });
                      if (!over.length) return null;
                      return (
                        <div style={{marginBottom:10,padding:'9px 11px',borderRadius:9,fontSize:11.5,lineHeight:1.6,
                          background:'rgba(255,152,0,0.09)',border:'1px solid rgba(255,152,0,0.35)',color:'#8a5000',fontWeight:600}}>
                          🔖 킵으로 가용재고 부족 {over.length}건
                          {over.slice(0,3).map(i=>{
                            const keep = activeKeepsByModel[i.model] || 0;
                            return (
                              <div key={i.model} style={{fontWeight:500,marginTop:2}}>
                                • {i.model} — 요청 {i.qty} / 재고 {i.available} − 킵 {keep} = 가용 {i.available - keep}
                              </div>
                            );
                          })}
                          {over.length>3 && <div style={{fontWeight:500,marginTop:2}}>• 외 {over.length-3}건</div>}
                          <div style={{fontWeight:500,marginTop:3,color:'var(--fg-3)'}}>요청은 진행됩니다. 실제 차감은 출고 처리 때 다시 확인합니다.</div>
                        </div>
                      );
                    })()}
                    <div style={{borderTop:'1px solid var(--border-strong)',paddingTop:10,display:'flex',justifyContent:'space-between',alignItems:'center',gap:8}}>
                      <span style={{fontSize:14,fontWeight:700,color:'#000',display:'flex',alignItems:'center',gap:6}}>
                        <Icon name="event" size={16} style={{color:'var(--accent)'}}/>
                        예상 출고일
                      </span>
                      <input type="date" value={shipExpectedDate}
                        onChange={e=>{ shipDateTouchedRef.current = true; setShipExpectedDate(e.target.value); }}
                        style={{padding:'7px 10px',border:`1px solid ${shipExpectedDate && shipExpectedDate < today() ? '#e65100' : '#c6c6cd'}`,borderRadius:8,fontSize:13,fontWeight:700,color:shipExpectedDate && shipExpectedDate < today() ? '#e65100' : '#00668a',background:shipExpectedDate && shipExpectedDate < today() ? '#fff8f0' : '#fff',fontFamily:'inherit',cursor:'pointer',outline:'none'}}/>
                    </div>
                    {/* v7.227: 과거 날짜면 배송시트지에서 안 보이고 자동 이월도 꺼진다 */}
                    {shipExpectedDate && shipExpectedDate < today() && (
                      <div style={{padding:'0 2px 8px',fontSize:11,fontWeight:600,color:'var(--warning)',lineHeight:1.45}}>
                        ⚠ 지난 날짜입니다 — 배송시트지 기본 화면에 안 보이고 자동 이월도 꺼집니다
                        <button type="button" onClick={()=>{ shipDateTouchedRef.current = false; setShipExpectedDate(today()); }}
                          style={{marginLeft:6,padding:'1px 7px',border:'1px solid var(--warning)',borderRadius:5,background:'var(--bg-card)',color:'var(--warning)',fontSize:10,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                          오늘로
                        </button>
                      </div>
                    )}
                    {/* v7.32: 과거 날짜 입력 시 누락 기록 안내 */}
                    {shipExpectedDate && shipExpectedDate < today() && (
                      <div style={{marginTop:8,padding:'8px 12px',background:'rgba(245,124,0,0.08)',border:'1px solid rgba(245,124,0,0.3)',borderRadius:8,display:'flex',alignItems:'flex-start',gap:6,fontSize:11.5,color:'var(--warning)'}}>
                        <Icon name="archive" size={14} style={{marginTop:1,flexShrink:0}}/>
                        <span><strong>누락 기록으로 등록</strong>됩니다 — 자동이월 OFF (선택한 날짜 그대로 유지)</span>
                      </div>
                    )}
                  </div>

                  {shipMsg&&(<div style={{marginBottom:16,padding:14,borderRadius:12,fontSize:14,fontWeight:500,display:'flex',alignItems:'center',gap:8,
                    background:shipMsg.type==='ok'?'rgba(0,152,68,0.08)':shipMsg.type==='warn'?'rgba(245,124,0,0.1)':'rgba(186,26,26,0.08)',
                    color:shipMsg.type==='ok'?'var(--success)':shipMsg.type==='warn'?'var(--warning)':'var(--danger)'}}>
                    <Icon name={shipMsg.type==='ok'?'check_circle':shipMsg.type==='warn'?'warning':'error'} size={20}/>{shipMsg.msg}</div>)}

                  {(() => {
                    const isEmsMode = shipMethod === 'rma';
                    const canSubmit = shipItems.length > 0 || (isEmsMode && shipRmaProductName.trim() && shipRmaSerial.trim());
                    return (
                  <button onClick={submitShipRequest} disabled={loading||!canSubmit} style={{width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:10,padding:'16px 24px',background:canSubmit?(isEmsMode?'var(--warning-solid)':'#000'):'var(--surface-mute)',color:'#fff',border:'none',borderRadius:12,fontSize:14,fontWeight:600,cursor:canSubmit?'pointer':'default',marginBottom:10,boxShadow:canSubmit?'0 4px 12px rgba(0,0,0,0.15)':'none',transition:'all 0.15s'}}>
                    <Icon name="send" size={20} style={{color:'#fff'}}/>
                    {loading?'처리 중...':(isEmsMode && !shipItems.length?'EMS 발송 요청':'출고 요청')}
                  </button>
                    ); })()}
                  <button style={{width:'100%',padding:'12px 24px',background:'transparent',color:'var(--fg-3)',border:'none',borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer'}}>
                    임시 저장
                  </button>
                </Card>

                {/* Brand image area */}
                <div style={{borderRadius:24,overflow:'hidden',height:180,position:'relative',background:'#131b2e'}}>
                  <video
                    autoPlay loop muted playsInline
                    poster={`${process.env.PUBLIC_URL}/brand-bg-poster.jpg`}
                    style={{position:'absolute',inset:0,width:'100%',height:'100%',objectFit:'cover'}}
                  >
                    <source src={`${process.env.PUBLIC_URL}/brand-bg.mp4`} type="video/mp4"/>
                  </video>
                  {/* 하단 텍스트 가독성용 그라데이션 오버레이 — 영상 원본 채도 보존을 위해 약화 */}
                  <div style={{position:'absolute',inset:0,background:'linear-gradient(180deg, rgba(0,0,0,0) 50%, rgba(0,0,0,0.55) 100%)'}}/>
                  <div style={{position:'absolute',inset:0,display:'flex',flexDirection:'column',justifyContent:'flex-end',padding:24}}>
                    <p style={{color:'#fff',fontSize:14,fontWeight:600,fontStyle:'italic',textShadow:'0 2px 8px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)'}}>"신속하고 정확한 물류의 흐름, River Network"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>)}


          {/* ═══ TRANSACTION LOG (입출고 로그) ═══ */}
          {/* v7.150: 모바일에서는 MobileTxLog 컴포넌트 */}
          {page==='txlog' && isMobile && (
            <Suspense fallback={<div style={{padding:40,textAlign:'center',color:'var(--fg-3)'}}>배송시트지 로딩...</div>}>
              <MobileTxLog
                onNavigate={(p)=>setPage(p)}
               
                onProcessRequest={(reqId, model)=>{
                  // 데스크톱 outbound modal 재사용 (기존 loadActiveRequest + setOutboundModalOpen 흐름)
                  loadActiveRequest(reqId, model).then(()=>{
                    setOutboundModalOpen(true);
                  });
                }}
                onDeleteShipment={async (row)=>{
                  // v7.397: 데스크톱과 동일한 확인 문구 + 코어 삭제 로직 공유. 목록 갱신은 MobileTxLog 책임.
                  if (!window.confirm(deleteShipmentWarning(row))) return { ok:false, cancelled:true };
                  return await deleteShipmentCore(row);
                }}
              />
            </Suspense>
          )}
          {page==='txlog' && !isMobile && (()=>{
            const range = getRangeForMode(logPeriodMode, logPeriodOffset);
            const maxPages = Math.ceil(logTotal / PG);
            return (<div>
              {/* Page Header */}
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28}}>
                <div>
                  <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>배송시트지</h1>
                  <p style={{fontSize:16,color:'var(--fg-2)',display:'flex',alignItems:'center',gap:8}}>
                    실시간으로 발생하는 모든 배송 요청과 처리 내역을 확인합니다.
                    {(()=>{ const paused = !!logModalItem || !!editingTxItem || !!refundingId || Object.keys(logMemos).length > 0;
                      return (
                        <span title={paused?'편집 중에는 자동 새로고침이 일시 정지됩니다':'10초마다 자동 동기화 중'}
                          style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:11,fontWeight:600,padding:'3px 9px',borderRadius:9999,background:paused?'rgba(118,119,125,0.1)':'rgba(0,152,68,0.1)',color:paused?'var(--fg-3)':'var(--success)'}}>
                          <span style={{width:6,height:6,borderRadius:'50%',background:paused?'#76777d':'var(--success-solid)',animation:paused?'none':'pulse 1.6s ease-in-out infinite'}}/>
                          {paused?'동기화 일시정지':'자동 동기화'}
                        </span>
                      );
                    })()}
                  </p>
                  {/* Sub-tabs: 배송 목록 / 메모 */}
                  <div style={{display:'flex',gap:0,marginTop:18,borderBottom:'2px solid var(--border)',alignItems:'stretch',justifyContent:'space-between',flexWrap:'wrap'}}>
                    <div style={{display:'flex',gap:0}}>
                    <button onClick={()=>setTxSubTab('list')}
                      style={{display:'flex',alignItems:'center',gap:7,padding:'10px 18px',background:'transparent',border:'none',borderBottom:'2px solid '+(txSubTab==='list'?'var(--accent)':'transparent'),marginBottom:-2,cursor:'pointer',color:txSubTab==='list'?'var(--accent)':'var(--fg-3)',fontSize:14,fontWeight:txSubTab==='list'?700:600,transition:'all 0.15s'}}>
                      <Icon name="list_alt" size={18}/> 배송 목록
                    </button>
                    <button onClick={()=>setTxSubTab('memos')}
                      style={{display:'flex',alignItems:'center',gap:7,padding:'10px 18px',background:'transparent',border:'none',borderBottom:'2px solid '+(txSubTab==='memos'?'#f57c00':'transparent'),marginBottom:-2,cursor:'pointer',color:txSubTab==='memos'?'#f57c00':'var(--fg-3)',fontSize:14,fontWeight:txSubTab==='memos'?700:600,transition:'all 0.15s'}}>
                      <Icon name="sticky_note_2" size={18}/> 메모
                      {memos.length > 0 && <span style={{padding:'1px 7px',borderRadius:9999,background:txSubTab==='memos'?'#f57c00':'#cbd5e1',color:'#fff',fontSize:11,fontWeight:700}}>{memos.length}</span>}
                    </button>
                    {/* v7.262: 테스트 탭 — 실제 배송시트지 행을 건드리지 않고 효과를 실험하는 격리 공간.
                         여기서 값을 확정한 뒤 실제 행에 옮긴다. 실 데이터는 전혀 안 읽는다. */}
                    <button onClick={()=>setTxSubTab('test')}
                      style={{display:'flex',alignItems:'center',gap:7,padding:'10px 18px',background:'transparent',border:'none',borderBottom:'2px solid '+(txSubTab==='test'?'#7b1fa2':'transparent'),marginBottom:-2,cursor:'pointer',color:txSubTab==='test'?'#7b1fa2':'var(--fg-3)',fontSize:14,fontWeight:txSubTab==='test'?700:600,transition:'all 0.15s'}}>
                      <Icon name="science" size={18}/> 테스트
                      <span style={{padding:'1px 7px',borderRadius:9999,background:txSubTab==='test'?'#7b1fa2':'#cbd5e1',color:'#fff',fontSize:10,fontWeight:700}}>BETA</span>
                    </button>
                    </div>
                    {userMgmtOpen && (
                        <div onClick={()=>setUserMgmtOpen(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1300,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                          <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:14,width:460,maxWidth:'92vw',maxHeight:'82vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
                            <div style={{padding:'16px 18px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8}}>
                              <Icon name="group" size={20} style={{color:'#5e35b1'}}/>
                              <div style={{fontSize:15,fontWeight:800,color:'var(--fg-1)',flex:1}}>사용자 관리</div>
                              <button onClick={()=>setUserMgmtOpen(false)} style={{width:32,height:32,border:'none',background:'transparent',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)'}}><Icon name="close" size={18}/></button>
                            </div>
                            <div style={{padding:'10px 18px 4px',fontSize:11.5,color:'var(--fg-3)',lineHeight:1.5}}>
                              작성자·요청자 드롭다운에 나오는 목록입니다. 직책 포함(예: 김중규 이사)으로 입력하세요. 전화번호는 [퀵 시트 설정]에서 지정합니다. 모든 PC에 공유됩니다.
                            </div>
                            <div style={{flex:1,overflowY:'auto',padding:'8px 18px 4px',display:'flex',flexDirection:'column',gap:8}}>
                              {staffList.map((name, i) => (
                                <div key={i} style={{display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{width:18,fontSize:11,color:'var(--fg-3)',textAlign:'right',flexShrink:0}}>{i+1}</span>
                                  <input defaultValue={name}
                                    onBlur={e=>{ const v=e.target.value.trim(); if(!v){ e.target.value=name; return; } if(v!==name){ if(staffList.includes(v)){ alert('이미 있는 사용자입니다.'); e.target.value=name; return; } const next=[...staffList]; next[i]=v; setStaffListPersist(next); } }}
                                    onKeyDown={e=>{ if(e.key==='Enter') e.currentTarget.blur(); }}
                                    style={{flex:1,padding:'8px 10px',fontSize:13,border:'1px solid var(--border-strong)',borderRadius:7,color:'var(--fg-1)',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                                  <button onClick={()=>{ if(window.confirm(`"${name}" 을(를) 목록에서 삭제할까요?\n\n이미 시트에 기록된 이름은 그대로 유지됩니다.`)) setStaffListPersist(staffList.filter((_,j)=>j!==i)); }}
                                    title="삭제" style={{width:32,height:32,border:'none',background:'transparent',borderRadius:7,cursor:'pointer',color:'var(--danger)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}><Icon name="close" size={16}/></button>
                                </div>
                              ))}
                              {staffList.length === 0 && <div style={{padding:20,textAlign:'center',color:'var(--fg-3)',fontSize:12}}>등록된 사용자가 없습니다.</div>}
                            </div>
                            <div style={{padding:'12px 18px',borderTop:'1px solid var(--border)',display:'flex',gap:8}}>
                              <input value={userMgmtNew} onChange={e=>setUserMgmtNew(e.target.value)}
                                onKeyDown={e=>{ if(e.key==='Enter'){ e.preventDefault(); addUser(); } }}
                                placeholder="새 사용자 (예: 홍길동 대리)"
                                style={{flex:1,padding:'8px 10px',fontSize:13,border:'1px solid var(--border-strong)',borderRadius:7,color:'var(--fg-1)',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                              <button onClick={addUser} style={{padding:'8px 18px',border:'none',borderRadius:7,background:'#5e35b1',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',flexShrink:0}}>추가</button>
                            </div>
                          </div>
                        </div>
                      )}
                    {/* v7.217: 담당자 번호 매핑 모달 */}
                    {staffPhoneModal && (
                      <div onClick={()=>setStaffPhoneModal(false)}
                        style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.45)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                        <div onClick={e=>e.stopPropagation()}
                          style={{background:'var(--bg-card)',borderRadius:14,padding:20,width:'100%',maxWidth:420,boxShadow:'0 12px 40px rgba(0,0,0,0.25)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:4}}>
                            <Icon name="sync_alt" size={18} style={{color:'var(--danger)'}}/>
                            <span style={{fontSize:15,fontWeight:700,color:'var(--fg-1)'}}>퀵 시트 설정</span>
                          </div>
                          <div style={{fontSize:11.5,color:'var(--fg-3)',marginBottom:14,lineHeight:1.5}}>
                            퀵서비스 출고요청을 구글 배차 시트에 기록하기 위한 설정입니다.
                          </div>

                          {/* v7.234: n8n 웹훅 URL (툴바 → 이 모달로 이동) */}
                          <div style={{marginBottom:16,paddingBottom:16,borderBottom:'1px solid var(--border)'}}>
                            <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--fg-1)',marginBottom:6}}>
                              <Icon name="webhook" size={15} style={{color:'var(--danger)'}}/> n8n 웹훅 URL
                              {quickSheetWebhook && <Icon name="check_circle" size={14} style={{color:'var(--success)'}}/>}
                            </div>
                            <input
                              defaultValue={quickSheetWebhook}
                              onBlur={e=>{ if (e.target.value.trim() !== quickSheetWebhook) setQuickSheetWebhookPersist(e.target.value); }}
                              onKeyDown={e=>{ if (e.key==='Enter') e.currentTarget.blur(); }}
                              placeholder="https://n8n.example.com/webhook/quick-sheet"
                              style={{width:'100%',padding:'8px 10px',fontSize:12,border:'1px solid var(--border-strong)',borderRadius:7,background:'var(--bg-card)',color:'var(--fg-1)',fontFamily:'monospace',boxSizing:'border-box'}}/>
                            <div style={{marginTop:5,fontSize:11,color:'var(--fg-3)',lineHeight:1.45}}>
                              비워두면 [시트 전송] 버튼이 안내만 표시합니다. 모든 PC에 공유됩니다.
                            </div>
                          </div>

                          <div style={{display:'flex',alignItems:'center',gap:6,fontSize:12.5,fontWeight:700,color:'var(--fg-1)',marginBottom:4}}>
                            <Icon name="contact_phone" size={15} style={{color:'var(--success)'}}/> 담당자 번호
                          </div>
                          <div style={{fontSize:11.5,color:'var(--fg-3)',marginBottom:10,lineHeight:1.5}}>
                            시트의 <b>담당자 번호</b> 칸에 들어갑니다. 작성자 이름 기준으로 자동 매핑됩니다.
                          </div>
                          <div style={{display:'flex',flexDirection:'column',gap:8,maxHeight:'50vh',overflowY:'auto'}}>
                            {staffList.map(name => (
                              <div key={name} style={{display:'flex',alignItems:'center',gap:8}}>
                                <span style={{width:88,flexShrink:0,fontSize:12.5,fontWeight:600,color:'var(--fg-2)'}}>{name}</span>
                                {/* v7.336: 이 칸만 uncontrolled(defaultValue)라 DOM 값을 직접 고쳐 쓴다 */}
                                <input
                                  defaultValue={staffPhones[name] || ''}
                                  onChange={e=>{ e.target.value = formatPhoneNumber(e.target.value); }}
                                  onBlur={e=>{
                                    const v = e.target.value.trim();
                                    if ((staffPhones[name] || '') === v) return;
                                    const next = { ...staffPhones };
                                    if (v) next[name] = v; else delete next[name];
                                    setStaffPhonesPersist(next);
                                  }}
                                  onKeyDown={e=>{ if(e.key==='Enter') e.currentTarget.blur(); }}
                                  placeholder="010-0000-0000"
                                  style={{flex:1,padding:'7px 10px',fontSize:12.5,fontFamily:'monospace',border:'1px solid var(--border-strong)',borderRadius:7,color:'var(--fg-1)'}}/>
                              </div>
                            ))}
                          </div>
                          <button onClick={()=>setStaffPhoneModal(false)}
                            style={{width:'100%',marginTop:16,padding:'10px',border:'none',borderRadius:9,background:'var(--accent-solid)',color:'#fff',fontSize:13.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                            닫기
                          </button>
                        </div>
                      </div>
                    )}
                    {/* v7.237: 자동이월 시각 셀렉트는 우측 상단 [메뉴]로 이동 */}
                  </div>
                </div>
                {txSubTab==='list' && (
                <div style={{display:'flex',gap:8,alignItems:'flex-start',flexWrap:'wrap'}}>
                  {/* '오늘' 버튼 — 기간 박스 왼쪽 (다음 > 버튼과 거리 확보, 혼동 방지) */}
                  {logPeriodOffset !== 0 && (
                    <button onClick={()=>{setLogPeriodOffset(0);setLogPg(0);}} title="오늘로 이동"
                      style={{marginRight:8,padding:'9px 16px',display:'flex',alignItems:'center',gap:6,border:'1px solid var(--accent)',background:'var(--accent-solid)',borderRadius:12,cursor:'pointer',color:'#fff',fontSize:13,fontWeight:700,boxShadow:'0 2px 6px rgba(0,102,138,0.25)',transition:'all 0.15s'}}
                      onMouseEnter={e=>{e.currentTarget.style.background='#005475';e.currentTarget.style.transform='translateY(-1px)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='var(--accent-solid)';e.currentTarget.style.transform='translateY(0)';}}>
                      <Icon name="today" size={15}/> 오늘
                    </button>
                  )}
                  {/* 기간 네비 + 캘린더 버튼 */}
                  <div style={{display:'flex',alignItems:'center',gap:2,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:12,padding:4,boxShadow:'0 1px 3px rgba(0,0,0,0.06)'}}>
                    <button onClick={()=>{setLogPeriodOffset(o=>o-1);setLogPg(0);}} title="이전" style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',border:'none',background:'transparent',borderRadius:8,cursor:'pointer',color:'var(--fg-2)'}} onMouseEnter={e=>e.currentTarget.style.background='rgba(0,102,138,0.08)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <Icon name="chevron_left" size={18}/>
                    </button>
                    <button onClick={()=>setTxPickerOpen(true)} title="캘린더에서 날짜 선택" style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'transparent',border:'none',borderRadius:8,cursor:'pointer',color:'var(--fg-1)',fontSize:13,fontWeight:600,minWidth:170,justifyContent:'center'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(0,102,138,0.06)'}
                      onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <Icon name="event" size={16} style={{color:'var(--accent)'}}/>
                      <span>{range.label}</span>
                    </button>
                    <button onClick={()=>{setLogPeriodOffset(o=>o+1);setLogPg(0);}} title="다음" style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',border:'none',background:'transparent',borderRadius:8,cursor:'pointer',color:'var(--fg-2)'}} onMouseEnter={e=>e.currentTarget.style.background='rgba(0,102,138,0.08)'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                      <Icon name="chevron_right" size={18}/>
                    </button>
                  </div>
                  {/* 수동 새로고침 — UI 상태 + 모든 캐시 전체 초기화 */}
                  <button
                    onClick={async () => {
                      setTxRefreshing(true);
                      // 1) UI 상태 리셋
                      setLogFilter('all');
                      setLogPg(0);
                      setLogPeriodMode('day');
                      setLogPeriodOffset(0);
                      setShowLowStock(false);
                      setHeaderMenuOpen(false);
                      // 2) 인라인 편집 임시값 클리어 (송장/금액/비고 등)
                      setLogMemos({});
                      setLogTrackingDrafts({});
                      setLogPriceDrafts({});
                      // 3) 모든 캐시 재로드 (txlog 외 + 가능한 모든 데이터)
                      try {
                        await Promise.allSettled([
                          loadTxLog(),
                          loadAllModels(),
                          loadAddressHistory(),
                          txSubTab==='memos' ? loadMemos() : Promise.resolve(),
                          sweepAutoDefer()
                        ]);
                      } catch(e){}
                      setTimeout(() => setTxRefreshing(false), 600);
                    }}
                    disabled={txRefreshing}
                    title="배송시트지 새로고침 (필터·기간·토글 모두 초기화)"
                    style={{display:'flex',alignItems:'center',justifyContent:'center',width:42,height:42,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:12,cursor:txRefreshing?'not-allowed':'pointer',color:'var(--accent)',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',transition:'all 0.15s',opacity:txRefreshing?0.65:1}}
                    onMouseEnter={e=>{if(!txRefreshing){e.currentTarget.style.background='var(--bg-info)';e.currentTarget.style.borderColor='var(--accent)';}}}
                    onMouseLeave={e=>{e.currentTarget.style.background='var(--bg-card)';e.currentTarget.style.borderColor='var(--border-strong)';}}>
                    <Icon name="refresh" size={20} style={{color:'var(--accent)',animation:txRefreshing?'spin 0.6s linear':'none'}}/>
                  </button>
                  {/* 통합 메뉴 — 재고부족/입고등록/입출고요약/메모 */}
                  {(()=>{
                    const lowCount = allModels.filter(m=>m.in_stock<=lowStockThreshold).length;
                    const hasOut = allModels.some(m=>m.in_stock===0);
                    return (
                      <div style={{position:'relative'}}>
                        <button onClick={()=>setHeaderMenuOpen(v=>!v)} style={{display:'flex',alignItems:'center',gap:6,padding:'10px 20px',background:headerMenuOpen?'var(--accent-solid)':'var(--bg-card)',border:'1px solid '+(headerMenuOpen?'var(--accent)':'var(--border-strong)'),borderRadius:12,fontSize:14,fontWeight:600,cursor:'pointer',color:headerMenuOpen?'#fff':'var(--fg-2)',boxShadow:'0 1px 3px rgba(0,0,0,0.06)',transition:'all 0.15s'}}>
                          <Icon name="apps" size={18} style={{color:headerMenuOpen?'#fff':'var(--accent)'}}/>
                          메뉴
                          <Icon name={headerMenuOpen?'expand_less':'expand_more'} size={18} style={{color:headerMenuOpen?'#fff':'var(--fg-3)'}}/>
                        </button>
                        {headerMenuOpen && (
                          <>
                            <div onClick={()=>setHeaderMenuOpen(false)} style={{position:'fixed',inset:0,zIndex:50}}/>
                            <div style={{position:'absolute',top:'calc(100% + 6px)',right:0,minWidth:330,background:'var(--bg-card)',borderRadius:12,boxShadow:'0 10px 30px rgba(0,0,0,0.18)',border:'1px solid var(--border)',zIndex:60,overflow:'hidden'}}>
                              {/* 재고 부족 */}
                              <button onClick={()=>{setShowLowStock(v=>!v);setHeaderMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',background:'transparent',border:'none',borderBottom:'1px solid var(--border)',cursor:'pointer',textAlign:'left'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(229,80,57,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:36,height:36,borderRadius:10,background:lowCount>0?'rgba(245,124,0,0.12)':'rgba(0,152,68,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name={hasOut?'error':lowCount>0?'warning':'check_circle'} size={20} style={{color:hasOut?'var(--danger)':lowCount>0?'#f57c00':'var(--success)'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                                    재고 부족 {showLowStock && <span style={{fontSize:11,padding:'1px 6px',borderRadius:5,background:'var(--accent-solid)',color:'#fff',fontWeight:600}}>표시 중</span>}
                                    {lowCount>0 && !showLowStock && <span style={{background:hasOut?'var(--danger-solid)':'#f57c00',color:'#fff',borderRadius:9999,padding:'1px 7px',fontSize:11,fontWeight:700}}>{lowCount}</span>}
                                  </div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>{lowCount>0?`${lowCount}개 모델 보충 필요`:'모든 모델 정상 재고'}</div>
                                </div>
                              </button>
                              {/* 입고 등록 (v7.40: 모달 → 사이드바 탭으로 이동) */}
                              <button onClick={()=>{setPage('inbound');setHeaderMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',background:'transparent',border:'none',borderBottom:'1px solid var(--border)',cursor:'pointer',textAlign:'left'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,152,68,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(0,152,68,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name="south_west" size={20} style={{color:'var(--success)'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)'}}>입고 등록</div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>신규 시리얼을 재고에 추가</div>
                                </div>
                              </button>
                              {/* 입출고 요약 */}
                              <button onClick={()=>{setShowTxLogSummary(true);setHeaderMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',background:'transparent',border:'none',cursor:'pointer',textAlign:'left'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,102,138,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(0,102,138,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name="analytics" size={20} style={{color:'var(--accent)'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)'}}>입출고 요약</div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>주간·월간 통계와 모델별 Top</div>
                                </div>
                              </button>
                              {/* v7.456: 사용자 관리 — 작성자/요청자 목록 편집 */}
                              <button onClick={()=>{setUserMgmtOpen(true);setHeaderMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',background:'transparent',border:'none',borderTop:'1px solid var(--border)',cursor:'pointer',textAlign:'left'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(94,53,177,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(94,53,177,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name="group" size={20} style={{color:'#5e35b1'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)'}}>사용자 관리</div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>작성자·요청자 목록 편집</div>
                                </div>
                              </button>
                              {/* v7.234: 퀵 시트 설정 — 툴바에 있던 웹훅 URL·담당자 번호를 여기로 이동 */}
                              <button onClick={()=>{setStaffPhoneModal(true);setHeaderMenuOpen(false);}} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',background:'transparent',border:'none',borderTop:'1px solid var(--border)',cursor:'pointer',textAlign:'left'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(186,26,26,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(186,26,26,0.1)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name="sync_alt" size={20} style={{color:'var(--danger)'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                                    퀵 시트 설정
                                    {quickSheetWebhook
                                      ? <Icon name="check_circle" size={14} style={{color:'var(--success)'}}/>
                                      : <span style={{fontSize:10,padding:'1px 6px',borderRadius:5,background:'var(--warning-solid)',color:'#fff',fontWeight:700}}>미설정</span>}
                                  </div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>n8n 웹훅 URL · 담당자 번호</div>
                                </div>
                              </button>
                              {/* v7.237: 자동이월 시각 — 서브탭 옆 툴바에 있던 셀렉트를 여기로 이동
                                   버튼이 아니라 '행 안에서 바로 고르는' 형태. 메뉴는 닫히지 않는다 */}
                              {/* v7.260: 다크모드 토글 — v7.237 자동이월 행과 같은 형태.
                                   메뉴는 닫지 않는다 (바로 결과를 보면서 켜고 끌 수 있게) */}
                              <div title="어두운 배경으로 전환합니다. 브라우저별로 저장되며, 처음에는 OS 설정을 따릅니다."
                                onClick={e=>e.stopPropagation()}
                                style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',boxSizing:'border-box'}}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(64,194,253,0.12)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name={theme==='dark'?'dark_mode':'light_mode'} size={20} style={{color:'var(--accent)'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)'}}>다크모드</div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>
                                    {theme==='dark'?'어두운 배경 사용 중':'밝은 배경 사용 중'}
                                  </div>
                                </div>
                                <button onClick={()=>setTheme(t=>t==='dark'?'light':'dark')}
                                  aria-label="다크모드 전환"
                                  style={{flexShrink:0,width:46,height:26,borderRadius:9999,border:'1px solid '+(theme==='dark'?'var(--accent-solid)':'var(--border-strong)'),background:theme==='dark'?'var(--accent-solid)':'var(--surface-mute)',cursor:'pointer',padding:0,position:'relative',transition:'background 0.15s'}}>
                                  <span style={{position:'absolute',top:2,left:theme==='dark'?22:2,width:20,height:20,borderRadius:'50%',background:'#fff',boxShadow:'0 1px 3px rgba(0,0,0,0.25)',transition:'left 0.15s'}}/> {/* v7.260: 손잡이는 두 테마 모두 흰색 고정 — 변수로 바꾸면 다크에서 트랙에 묻힌다 */}
                                </button>
                              </div>
                              <div title="자동 이월 sweep이 실행될 시각. PC가 켜져있어야 스케줄이 동작합니다. 지정 시각 이후에 로그인하면 즉시 catch-up 실행."
                                onClick={e=>e.stopPropagation()}
                                style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 18px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',boxSizing:'border-box'}}>
                                <div style={{width:36,height:36,borderRadius:10,background:'rgba(245,124,0,0.12)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                  <Icon name="schedule" size={20} style={{color:'#f57c00'}}/>
                                </div>
                                <div style={{flex:1,minWidth:0}}>
                                  <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)'}}>자동이월 시각</div>
                                  <div style={{fontSize:11,color:'var(--fg-3)',marginTop:2}}>이 시각에 미출고 건 자동 이월</div>
                                </div>
                                <select value={autoDeferMinutes} onChange={e=>setAutoDeferMinutesPersist(parseInt(e.target.value,10))}
                                  style={{flexShrink:0,padding:'6px 8px',fontSize:12.5,fontWeight:600,border:'1px solid var(--border-strong)',borderRadius:7,background:'var(--bg-card)',cursor:'pointer',color:'var(--fg-1)',fontFamily:'inherit'}}>
                                  {Array.from({length:48}, (_, i) => {
                                    const val = i * 30; // 0, 30, 60, ..., 1410
                                    const h = Math.floor(val / 60);
                                    const m = val % 60;
                                    const hh = String(h).padStart(2,'0');
                                    const mm = String(m).padStart(2,'0');
                                    let label = `${hh}:${mm}`;
                                    if (val === 0) label = '00:00 (자정)';
                                    else if (val === 720) label = '12:00 (정오)';
                                    return <option key={val} value={val}>{label}</option>;
                                  })}
                                </select>
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })()}
                </div>
                )}
              </div>

              {/* ━━━ 메모 탭 본문 ━━━ */}
              {/* v7.328: 테스트 탭 — Lightfall 실험실 (실 데이터 접근 없음) */}
              {txSubTab==='test' && <LightfallLab/>}

              {txSubTab==='memos' && (()=>{
                const sortedMemos = [...memos].sort((a,b) => {
                  if (a.pinned !== b.pinned) return b.pinned - a.pinned;
                  const sa = a.sort_order ?? 9999, sb = b.sort_order ?? 9999;
                  if (sa !== sb) return sa - sb;
                  return (b.created_at || '').localeCompare(a.created_at || '');
                });
                // 코르크보드 배경 (코르크 텍스처 + 다크 우드 프레임)
                const corkBg = {
                  background: '#c89968',
                  backgroundImage: [
                    // 코르크 결의 미세한 점 패턴 (큰 점)
                    "radial-gradient(circle at 18% 22%, rgba(80,50,20,0.18) 1.5px, transparent 2.5px)",
                    "radial-gradient(circle at 62% 18%, rgba(80,50,20,0.15) 1.2px, transparent 2.2px)",
                    "radial-gradient(circle at 88% 42%, rgba(80,50,20,0.16) 1.4px, transparent 2.4px)",
                    "radial-gradient(circle at 32% 68%, rgba(80,50,20,0.14) 1.3px, transparent 2.3px)",
                    "radial-gradient(circle at 78% 82%, rgba(80,50,20,0.17) 1.5px, transparent 2.5px)",
                    // 코르크 미세 텍스처 (작은 점 그리드)
                    "radial-gradient(circle at 50% 50%, rgba(60,35,10,0.06) 0.5px, transparent 1.5px)",
                    // 따뜻한 빛 그라데이션
                    "radial-gradient(ellipse at 30% 20%, rgba(255,235,180,0.18) 0%, transparent 55%)",
                    "radial-gradient(ellipse at 70% 80%, rgba(120,75,30,0.18) 0%, transparent 60%)"
                  ].join(','),
                  backgroundSize: '120px 120px, 100px 100px, 130px 130px, 110px 110px, 140px 140px, 18px 18px, 100% 100%, 100% 100%',
                  border: '10px solid #5d4037',
                  borderRadius: 18,
                  padding: '28px 24px',
                  boxShadow: 'inset 0 0 70px rgba(60,35,10,0.32), inset 0 0 14px rgba(101,67,33,0.25), 0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)',
                  position: 'relative',
                  minHeight: 400
                };
                return (
                <div style={corkBg}>
                  {/* 게시판 상단 — 라벨 + 크기 토글 */}
                  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20,flexWrap:'wrap'}}>
                    {/* v7.313: 게시판 탭 — 메모 / 업로드 파일을 갈라서 본다 */}
                    <div style={{display:'flex',alignItems:'center',gap:4,padding:4,background:'rgba(93,64,55,0.4)',borderRadius:12,border:'1px solid rgba(255,235,180,0.25)',backdropFilter:'blur(4px)',boxShadow:'0 2px 6px rgba(0,0,0,0.15)'}}>
                      {[
                        {key:'memos', icon:'push_pin',      label:'공유 게시판',   count:memos.length,       rot:'rotate(-30deg)'},
                        {key:'files', icon:'folder_shared', label:'작업파일 공유', count:sharedFileCount,    rot:'none'},
                      ].map(t => {
                        const active = memoBoardTab === t.key;
                        return (
                          <button key={t.key}
                            onClick={()=>{ setMemoBoardTab(t.key); try{ localStorage.setItem('inventura_board_tab', t.key); }catch(e){} }}
                            style={{display:'flex',alignItems:'center',gap:8,padding:'7px 14px',border:'none',borderRadius:9,cursor:'pointer',
                              background:active?'#fff5e6':'transparent',color:active?'#5d4037':'#fff5e6',
                              fontSize:13,fontWeight:700,letterSpacing:'0.03em',transition:'all 0.15s'}}>
                            <Icon name={t.icon} size={16} style={{color:active?'#c66900':'#ffd180',transform:t.rot}}/>
                            {t.label}
                            <span style={{padding:'1px 8px',borderRadius:9999,background:active?'rgba(93,64,55,0.14)':'rgba(255,235,180,0.25)',fontSize:11,fontWeight:700}}>{t.count}건</span>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{flex:1}}/>
                    {/* 메모 기록 보기 버튼 — 메모 탭에서만 */}
                    {memoBoardTab==='memos' && <button onClick={()=>{ const nm = new Date(new Date().getFullYear(), new Date().getMonth(), 1); setMemoHistoryMonth(nm); setMemoHistoryOpen(true); loadCompletedMemos(nm); }}
                      style={{display:'flex',alignItems:'center',gap:5,padding:'6px 12px',background:'rgba(93,64,55,0.4)',border:'1px solid rgba(255,235,180,0.25)',borderRadius:10,color:'#fff5e6',fontSize:11,fontWeight:700,cursor:'pointer',backdropFilter:'blur(4px)',boxShadow:'0 2px 6px rgba(0,0,0,0.15)'}}>
                      <Icon name="history" size={14} style={{color:'#ffd180'}}/> 메모 기록
                    </button>}
                    {/* v7.81: '크기' 라벨 제거 — 두 버튼(작게/크게)만으로 충분 */}
                    {memoBoardTab==='memos' && <div style={{display:'flex',alignItems:'center',gap:6,padding:'5px 8px',background:'rgba(93,64,55,0.4)',borderRadius:10,border:'1px solid rgba(255,235,180,0.25)',backdropFilter:'blur(4px)',boxShadow:'0 2px 6px rgba(0,0,0,0.15)'}}>
                      {[
                        {key:'s', label:'작게', title:'작게 — 카드 안에서 스크롤로 확인'},
                        {key:'l', label:'크게', title:'크게 — 전체 내용 표시'}
                      ].map(opt => {
                        const active = memoCardSize === opt.key;
                        return (
                          <button key={opt.key} title={opt.title}
                            onClick={()=>{ setMemoCardSize(opt.key); try{ localStorage.setItem('inventura_memo_size', opt.key); }catch(e){} }}
                            style={{padding:'5px 14px',border:'none',background:active?'#fff5e6':'transparent',color:active?'#5d4037':'#fff5e6',borderRadius:7,fontSize:12,fontWeight:700,cursor:'pointer',minWidth:40,transition:'all 0.15s'}}>
                            {opt.label}
                          </button>
                        );
                      })}
                    </div>}
                  </div>
                  {/* v7.313: 탭 본문. 메모 쪽은 조건부 렌더로 끄고,
                      파일 패널은 항상 마운트한 채 display 로만 숨긴다 —
                      그래야 메모 탭에 있어도 탭 뱃지의 건수가 채워진다. */}
                  {memoBoardTab==='memos' && (<>
                  {/* 새 메모 입력 영역 */}
                  <Card style={{padding:0,marginBottom:20,overflow:'hidden',border:'2px dashed '+(newMemoOpen?'#f57c00':'rgba(255,235,180,0.6)'),background:newMemoOpen?'rgba(255,251,235,0.95)':'rgba(255,255,255,0.7)',backdropFilter:'blur(8px)'}}>
                    {!newMemoOpen ? (
                      <button onClick={()=>setNewMemoOpen(true)}
                        style={{width:'100%',padding:'18px 22px',background:'transparent',border:'none',cursor:'pointer',display:'flex',alignItems:'center',gap:10,color:'var(--fg-3)',fontSize:14,fontWeight:600,textAlign:'left'}}>
                        <Icon name="add_circle" size={22} style={{color:'#f57c00'}}/>
                        새 메모 추가
                      </button>
                    ) : (
                      <div style={{padding:'18px 22px'}}>
                        <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                          <Icon name="edit_note" size={22} style={{color:'#f57c00'}}/>
                          <div style={{fontSize:15,fontWeight:700,color:'var(--fg-1)',flex:1}}>새 메모 작성</div>
                          <button onClick={()=>{setNewMemoOpen(false);setNewMemoContent('');setNewMemoColor('yellow');setNewMemoAuthor('');setNewMemoImportance(0);setNewMemoFiles([]);}}
                            style={{width:30,height:30,borderRadius:7,border:'none',background:'transparent',cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#f0f4ff'}
                            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                            <Icon name="close" size={18}/>
                          </button>
                        </div>
                        {/* v7.417: 내용만큼 자동 확장 (scrollHeight). 최소 120, 최대 55vh 넘으면 스크롤. */}
                        <textarea
                          value={newMemoContent}
                          onChange={e=>{ setNewMemoContent(e.target.value); const t=e.target; t.style.height='auto'; t.style.height=Math.max(120, t.scrollHeight)+'px'; }}
                          autoFocus
                          placeholder="메모 내용을 입력하세요..."
                          style={{width:'100%',minHeight:120,maxHeight:'55vh',padding:'12px 14px',border:'1px solid var(--border-strong)',borderRadius:10,fontSize:14,lineHeight:1.6,fontFamily:'inherit',color:'var(--fg-1)',background:memoColors[newMemoColor].bg,resize:'none',overflow:'auto',outline:'none',boxSizing:'border-box'}}
                        />
                        {/* v7.417: 작성 중 담아둔 첨부 칩 (아직 업로드 전 — [추가] 시 업로드) */}
                        <input ref={newMemoFileRef} type="file" accept="image/*,.zip,.pdf,application/zip,application/x-zip-compressed,application/pdf" multiple style={{display:'none'}}
                          onChange={e=>{
                            const picked = Array.from(e.target.files||[]); e.target.value='';
                            const okFiles = [];
                            for (const f of picked) {
                              const looksImage = /^image\//.test(f.type||'') || /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(f.name||'');
                              const looksZip = /zip/i.test(f.type||'') || /\.zip$/i.test(f.name||'');
                              if (looksImage || looksZip) okFiles.push(f);
                              else alert(`"${f.name}" 은(는) 이미지·zip 이 아닙니다. 메모에는 이미지·zip 만 첨부할 수 있습니다.`);
                            }
                            if (okFiles.length) setNewMemoFiles(prev => [...prev, ...okFiles]);
                          }}/>
                        {newMemoFiles.length > 0 && (
                          <div style={{display:'flex',flexWrap:'wrap',gap:6,marginTop:8}}>
                            {newMemoFiles.map((f, i) => (
                              <div key={i} style={{display:'flex',alignItems:'center',gap:5,padding:'4px 8px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:7,fontSize:11,fontWeight:600,color:'var(--fg-2)'}}>
                                <Icon name={/\.zip$/i.test(f.name)?'folder_zip':'image'} size={13} style={{color:'var(--accent)'}}/>
                                <span style={{maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</span>
                                <button onClick={()=>setNewMemoFiles(prev=>prev.filter((_,j)=>j!==i))} title="제거"
                                  style={{width:15,height:15,borderRadius:'50%',border:'none',background:'rgba(186,26,26,0.9)',color:'#fff',cursor:'pointer',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                  <Icon name="close" size={10}/>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                        <div style={{display:'flex',alignItems:'center',gap:12,marginTop:12,flexWrap:'wrap'}}>
                          {/* 색상 선택 */}
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)'}}>색상</span>
                            {Object.entries(memoColors).map(([key, c]) => (
                              <button key={key} onClick={()=>setNewMemoColor(key)}
                                title={c.label}
                                style={{width:24,height:24,borderRadius:'50%',border:newMemoColor===key?'2px solid '+c.accent:'1px solid '+c.border,background:c.bg,cursor:'pointer',padding:0}}/>
                            ))}
                          </div>
                          {/* v7.190: 별점 선택 (중요도) */}
                          <div style={{display:'flex',alignItems:'center',gap:6}}>
                            <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)'}}>중요도</span>
                            <StarRating value={newMemoImportance} onChange={setNewMemoImportance} size={18} gap={3}/>
                          </div>
                          {/* 작성자 선택 (선택사항) */}
                          <select value={newMemoAuthor} onChange={e=>setNewMemoAuthor(e.target.value)}
                            style={{padding:'7px 10px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:12,fontWeight:500,color:'var(--fg-2)',background:'var(--bg-card)',outline:'none',cursor:'pointer'}}>
                            <option value="">작성자 (선택)</option>
                            {staffList.map(s => <option key={s} value={s}>{s}</option>)}
                          </select>
                          {/* v7.417: 첨부 추가 버튼 */}
                          <button onClick={()=>newMemoFileRef.current?.click()} title="이미지·zip 첨부"
                            style={{display:'flex',alignItems:'center',gap:5,padding:'8px 12px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--accent)',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                            <Icon name="attach_file" size={15}/> 첨부{newMemoFiles.length>0?` (${newMemoFiles.length})`:''}
                          </button>
                          <div style={{flex:1}}/>
                          <button onClick={addMemo} disabled={!newMemoContent.trim()}
                            style={{display:'flex',alignItems:'center',gap:6,padding:'9px 20px',border:'none',background:newMemoContent.trim()?'var(--accent-solid)':'#cbd5e1',color:'#fff',borderRadius:9,fontSize:13,fontWeight:700,cursor:newMemoContent.trim()?'pointer':'not-allowed'}}>
                            <Icon name="add" size={16}/> 추가
                          </button>
                        </div>
                      </div>
                    )}
                  </Card>

                  {/* 메모 그리드 */}
                  {memosLoading && memos.length === 0 ? (
                    <div style={{padding:60,textAlign:'center',color:'#fff5e6',fontSize:14}}>
                      <Icon name="hourglass_empty" size={28}/><br/>메모 불러오는 중...
                    </div>
                  ) : sortedMemos.length === 0 ? (
                    <Card style={{padding:60,textAlign:'center',color:'var(--fg-3)',background:'rgba(255,255,255,0.85)',backdropFilter:'blur(6px)'}}>
                      <Icon name="sticky_note_2" size={48} style={{color:'#cbd5e1'}}/>
                      <div style={{fontSize:15,fontWeight:600,color:'var(--fg-2)',marginTop:12}}>등록된 메모가 없습니다</div>
                      <div style={{fontSize:12,marginTop:6}}>위 "새 메모 추가"를 눌러 첫 메모를 작성해보세요.</div>
                    </Card>
                  ) : (
                    (() => {
                      // 크기별 설정
                      // s: 고정 크기, 카드 안에서 스크롤로 내용 확인
                      // l: 카드 크기가 내용에 맞춰 늘어남
                      const sizeConfig = {
                        // v7.192: 별점 배지가 카드 상단 밖으로 -11px 튀어나오므로 세로 gap을 넉넉히 확보
                        //         (기존 14/22 → 30/36) — 위아래 카드의 배지가 겹치지 않도록
                        s: { minCol: 240, cardMin: 200, cardMax: 200, viewMin: 130, viewMax: 130, editMin: 160, gap: 14, gapY: 30, fixed: true },
                        l: { minCol: 380, cardMin: 200, cardMax: null, viewMin: 140, viewMax: null, editMin: 320, gap: 22, gapY: 36, fixed: false }
                      };
                      const sz = sizeConfig[memoCardSize] || sizeConfig.s;
                      return (
                    <div style={{display:'grid',gridTemplateColumns:`repeat(auto-fill, minmax(${sz.minCol}px, 1fr))`,columnGap:sz.gap,rowGap:sz.gapY,paddingTop:12}}>
                      {sortedMemos.map(memo => {
                        const c = memoColors[memo.color] || memoColors.yellow;
                        const isEditing = editingMemoId === memo.id;
                        const draft = memoDrafts[memo.id];
                        return (
                          <div key={memo.id} data-memo-card
                            onDragOver={e=>{e.preventDefault();e.dataTransfer.dropEffect='move';if(memoDragId&&memoDragId!==memo.id)e.currentTarget.style.outline='2px dashed var(--accent)';}}
                            onDragLeave={e=>{e.currentTarget.style.outline='none';}}
                            onDrop={e=>{e.preventDefault();e.currentTarget.style.outline='none';if(memoDragId)reorderMemos(memoDragId,memo.id);}}
                            style={{background:c.bg,border:'1px solid '+(memo.pinned?c.accent:c.border),borderRadius:10,padding:14,display:'flex',flexDirection:'column',gap:10,minHeight:sz.cardMin,maxHeight:sz.fixed&&!isEditing?sz.cardMax:'none',height:sz.fixed&&!isEditing?sz.cardMax:'auto',overflow:'visible',boxShadow:memo.pinned?`0 8px 22px rgba(0,0,0,0.25), 0 3px 8px rgba(0,0,0,0.18), 0 0 0 2px ${c.accent}`:'0 6px 18px rgba(0,0,0,0.22), 0 2px 5px rgba(0,0,0,0.14)',transition:'all 0.18s',position:'relative'}}
                            onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px) rotate(0deg)';e.currentTarget.style.boxShadow=memo.pinned?`0 14px 32px rgba(0,0,0,0.35), 0 5px 12px rgba(0,0,0,0.22), 0 0 0 2px ${c.accent}`:'0 12px 28px rgba(0,0,0,0.32), 0 4px 10px rgba(0,0,0,0.2)';}}
                            onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow=memo.pinned?`0 8px 22px rgba(0,0,0,0.25), 0 3px 8px rgba(0,0,0,0.18), 0 0 0 2px ${c.accent}`:'0 6px 18px rgba(0,0,0,0.22), 0 2px 5px rgba(0,0,0,0.14)';}}>
                            {/* v7.190: 별점 배지 — 카드 상단 border를 가로질러 걸치는 리본 스타일 */}
                            {(() => {
                              const imp = memo.importance || 0;
                              const active = imp > 0;
                              return (
                                <div style={{
                                  position:'absolute',
                                  top:-11,
                                  left:12,
                                  padding:'3px 8px',
                                  borderRadius:9999,
                                  background: active ? 'linear-gradient(135deg, #fff8e1, #ffedb0)' : 'rgba(255,255,255,0.85)',
                                  border: active ? '1px solid #fbbf24' : '1px dashed '+c.border,
                                  display:'inline-flex',
                                  alignItems:'center',
                                  gap:0,
                                  zIndex:2,
                                  animation: active ? 'starGlow 2.6s ease-in-out infinite' : 'none',
                                  opacity: active ? 1 : 0.6,
                                  transition: 'opacity 0.2s',
                                }}
                                onMouseEnter={e=>{ if(!active) e.currentTarget.style.opacity='1'; }}
                                onMouseLeave={e=>{ if(!active) e.currentTarget.style.opacity='0.6'; }}
                                title="클릭하여 중요도 별점 설정 (같은 값 재클릭 시 초기화)">
                                  <StarRating value={imp} onChange={(v)=>updateMemoField(memo, { importance: v })} size={13} gap={1}/>
                                </div>
                              );
                            })()}
                            {/* 헤더: 핀 + 색상 + 삭제 */}
                            <div style={{display:'flex',alignItems:'center',gap:6}}>
                              <button onClick={()=>updateMemoField(memo, { pinned: !memo.pinned })}
                                title={memo.pinned?'고정 해제':'상단 고정'}
                                style={{width:26,height:26,borderRadius:6,border:'none',background:memo.pinned?c.accent:'transparent',color:memo.pinned?'#fff':c.accent,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                <Icon name={memo.pinned?'push_pin':'push_pin'} size={14} style={{transform:memo.pinned?'none':'rotate(45deg)',opacity:memo.pinned?1:0.6}}/>
                              </button>
                              {/* 색상 변경 — 작은 팔레트 */}
                              <div style={{display:'flex',gap:3,flex:1,alignItems:'center'}}>
                                {Object.entries(memoColors).map(([key, cc]) => (
                                  <button key={key} onClick={()=>updateMemoField(memo, { color: key })}
                                    title={cc.label}
                                    style={{width:14,height:14,borderRadius:'50%',border:memo.color===key?'2px solid '+cc.accent:'1px solid '+cc.border,background:cc.bg,cursor:'pointer',padding:0,opacity:memo.color===key?1:0.7}}/>
                                ))}
                              </div>
                              <button onClick={()=>{
                                  if (isEditing) {
                                    // 편집 중 → 저장
                                    saveMemoEdit(memo);
                                  } else {
                                    setEditingMemoId(memo.id);
                                    setMemoDrafts(d=>({...d,[memo.id]:memo.content}));
                                  }
                                }}
                                title={isEditing?'저장':'수정'}
                                style={{width:26,height:26,borderRadius:6,border:'none',background:isEditing?c.accent:'transparent',color:isEditing?'#fff':c.accent,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                                onMouseEnter={e=>{if(!isEditing)e.currentTarget.style.background='rgba(0,0,0,0.06)';}}
                                onMouseLeave={e=>{if(!isEditing)e.currentTarget.style.background='transparent';}}>
                                <Icon name={isEditing?'check':'edit'} size={14}/>
                              </button>
                              {/* v7.312: 이미지 첨부 */}
                              <button onClick={()=>{ memoImgTargetRef.current = memo.id; memoImgRef.current?.click(); }}
                                disabled={memoImgBusy===memo.id}
                                title="이미지·zip 첨부"
                                style={{width:26,height:26,borderRadius:6,border:'none',background:'transparent',color:memoImgBusy===memo.id?'#9ca3af':c.accent,cursor:memoImgBusy===memo.id?'wait':'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.06)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <Icon name={memoImgBusy===memo.id?'hourglass_empty':'add_photo_alternate'} size={14}/>
                              </button>
                              <button onClick={()=>deleteMemo(memo)}
                                title="삭제"
                                style={{width:26,height:26,borderRadius:6,border:'none',background:'transparent',color:'var(--danger)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(186,26,26,0.12)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <Icon name="delete" size={14}/>
                              </button>
                              <button onClick={()=>completeMemo(memo)}
                                title="완료 처리 (기록으로 보관)"
                                style={{width:26,height:26,borderRadius:6,border:'none',background:'transparent',color:'var(--success)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}
                                onMouseEnter={e=>e.currentTarget.style.background='rgba(0,152,68,0.12)'}
                                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                <Icon name="task_alt" size={14}/>
                              </button>
                              {!isEditing && <div
                                draggable
                                onDragStart={e=>{setMemoDragId(memo.id);e.dataTransfer.effectAllowed='move';const card=e.currentTarget.closest('[data-memo-card]');if(card)card.style.opacity='0.4';}}
                                onDragEnd={e=>{setMemoDragId(null);const card=e.currentTarget.closest('[data-memo-card]');if(card)card.style.opacity='1';}}
                                title="드래그하여 순서 변경"
                                style={{width:22,height:22,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',cursor:'grab',color:c.accent,opacity:0.4,marginLeft:2}}
                                onMouseEnter={e=>{e.currentTarget.style.opacity='1';e.currentTarget.style.background='rgba(0,0,0,0.06)';}}
                                onMouseLeave={e=>{e.currentTarget.style.opacity='0.4';e.currentTarget.style.background='transparent';}}>
                                <Icon name="drag_indicator" size={14}/>
                              </div>}
                            </div>
                            {/* 본문 — 더블클릭 또는 수정 버튼으로 편집 모드 */}
                            {/* v7.442: 본문+첨부를 한 스크롤 영역으로 묶는다. 작게 모드에서 첨부파일이
                                고정 높이 카드(200px)를 벗어나 아래로 새던 문제 수정. 별점 배지(absolute,
                                top:-11)는 이 래퍼 밖(카드 자식)이라 클리핑되지 않는다. */}
                            <div style={ sz.fixed && !isEditing
                              ? {flex:1,minHeight:0,overflowY:'auto',overflowX:'hidden',display:'flex',flexDirection:'column',gap:10}
                              : {flex:1,display:'flex',flexDirection:'column',gap:10} }>
                            {isEditing ? (
                              <>
                                {/* 서식 툴바 — 볼드 / 색상 (execCommand 기반 WYSIWYG) */}
                                <div style={{display:'flex',gap:6,padding:'4px 6px',background:'rgba(255,255,255,0.5)',borderRadius:6,alignItems:'center',flexWrap:'wrap'}}>
                                  <button
                                    onMouseDown={e=>e.preventDefault()}
                                    onClick={()=>{
                                      pushMemoUndo(false); // v7.196: 서식 적용 전 상태 기록
                                      document.execCommand('bold', false, null);
                                      // onInput이 자동 발동되지만 안전장치로 명시 업데이트
                                      const el = memoEditRef.current;
                                      if (el) setMemoDrafts(d=>({...d, [memo.id]: htmlToMarkup(el.innerHTML)}));
                                      pushMemoUndo(false);
                                    }}
                                    title="볼드 (선택 후 클릭)" style={{padding:'4px 10px',border:'1px solid '+c.accent,background:'var(--bg-card)',color:c.accent,borderRadius:5,fontSize:12,fontWeight:900,cursor:'pointer',minWidth:30}}>B</button>
                                  <span style={{width:1,height:18,background:'rgba(0,0,0,0.15)'}}/>
                                  <span style={{fontSize:10,fontWeight:700,color:c.accent,opacity:0.7}}>색상</span>
                                  {[
                                    ['#d32f2f','빨강'],
                                    ['#f57c00','주황'],
                                    ['#009844','초록'],
                                    ['#00668a','파랑'],
                                    ['#7b1fa2','보라'],
                                    ['#000000','검정']
                                  ].map(([hex, name]) => (
                                    <button key={hex}
                                      onMouseDown={e=>e.preventDefault()}
                                      onClick={()=>{
                                        pushMemoUndo(false); // v7.196: 색상 적용 전 상태 기록
                                        document.execCommand('styleWithCSS', false, true);
                                        document.execCommand('foreColor', false, hex);
                                        const el = memoEditRef.current;
                                        if (el) setMemoDrafts(d=>({...d, [memo.id]: htmlToMarkup(el.innerHTML)}));
                                        pushMemoUndo(false);
                                      }}
                                      title={name+' 색 (선택 후 클릭)'} style={{width:22,height:22,border:'1.5px solid '+hex,background:hex,borderRadius:5,cursor:'pointer',padding:0}}/>
                                  ))}
                                </div>
                                <div
                                  ref={memoEditRef}
                                  contentEditable
                                  suppressContentEditableWarning
                                  data-memo-id={memo.id}
                                  onInput={(e)=>{
                                    // setState updater 함수가 비동기 실행 시 e.currentTarget이 null이 될 수 있어 미리 캐싱
                                    const html = e.currentTarget ? e.currentTarget.innerHTML : '';
                                    setMemoDrafts(d=>({...d, [memo.id]: htmlToMarkup(html)}));
                                    pushMemoUndo(true); // v7.196: 연속 타이핑 병합 스냅샷
                                  }}
                                  onKeyDown={e=>{
                                    // v7.196: Ctrl/Cmd+Z 되돌리기 · Ctrl+Shift+Z / Ctrl+Y 다시하기
                                    //   네이티브 undo는 innerHTML 직접 조작과 충돌하므로 항상 가로챈다.
                                    if ((e.ctrlKey || e.metaKey) && !e.altKey) {
                                      const k = e.key.toLowerCase();
                                      if (k === 'z' && !e.shiftKey) {
                                        e.preventDefault();
                                        applyMemoUndo(-1, memo.id);
                                        return;
                                      }
                                      if ((k === 'z' && e.shiftKey) || k === 'y') {
                                        e.preventDefault();
                                        applyMemoUndo(1, memo.id);
                                        return;
                                      }
                                    }
                                    if (e.key === 'Escape') {
                                      e.preventDefault();
                                      setEditingMemoId(null);
                                      setMemoDrafts(d=>{const nd={...d};delete nd[memo.id];return nd;});
                                    } else if (e.key === 'Enter' && e.shiftKey) {
                                      e.preventDefault();
                                      // 최신 draft 즉시 반영 후 저장
                                      const latest = e.currentTarget ? htmlToMarkup(e.currentTarget.innerHTML) : '';
                                      setMemoDrafts(d=>{const nd={...d, [memo.id]: latest}; return nd;});
                                      setTimeout(()=>saveMemoEdit(memo), 0);
                                    }
                                  }}
                                  onPaste={(e)=>{
                                    // v7.186: 붙여넣기 시 서식 제거 — 원본의 <span style="color:...">가
                                    //         그대로 삽입되면 htmlToMarkup이 [#hex]...[/] 마크업을 만들고,
                                    //         nested/여러 span/개행 조합에서 [/] 닫힘 균형이 깨진 채 저장돼
                                    //         [#b45309] 문자열이 화면에 그대로 노출되며 색상 재변경도 안 됨.
                                    //         plain text만 추출해서 삽입 → 서식 완전 제거.
                                    e.preventDefault();
                                    pushMemoUndo(false); // v7.196: 붙여넣기 직전 상태를 개별 되돌림 지점으로
                                    const text = (e.clipboardData || window.clipboardData).getData('text/plain') || '';
                                    // execCommand('insertText')는 undo 스택 유지 + 커서 위치에 순수 텍스트 삽입
                                    document.execCommand('insertText', false, text);
                                    pushMemoUndo(false); // 삽입 후 상태도 기록
                                  }}
                                  onBlur={(e)=>{
                                    // 툴바 버튼 클릭은 blur 발동되지 않게 ignore
                                    if (e.relatedTarget && e.relatedTarget.closest && e.relatedTarget.closest('[data-memo-card]')) return;
                                    const latest = e.currentTarget ? htmlToMarkup(e.currentTarget.innerHTML) : '';
                                    if (latest !== memo.content) {
                                      setMemoDrafts(d=>({...d, [memo.id]: latest}));
                                      setTimeout(()=>saveMemoEdit(memo), 0);
                                    } else {
                                      setEditingMemoId(null);
                                      setMemoDrafts(d=>{const nd={...d};delete nd[memo.id];return nd;});
                                    }
                                  }}
                                  style={{flex:1,minHeight:sz.editMin,padding:'10px 12px',border:'2px solid '+c.accent,borderRadius:7,fontSize:13,lineHeight:1.55,fontFamily:'inherit',color:c.accent,background:'rgba(255,255,255,0.7)',outline:'none',boxSizing:'border-box',overflowY:'auto',whiteSpace:'pre-wrap',wordBreak:'break-word'}}
                                />
                                <div style={{fontSize:10,color:c.accent,opacity:0.6,marginTop:2,textAlign:'right'}}>Shift+Enter 저장 · Esc 취소 · Ctrl+Z 되돌리기</div>
                              </>
                            ) : (
                              <div onDoubleClick={()=>{setEditingMemoId(memo.id);setMemoDrafts(d=>({...d,[memo.id]:memo.content}));}}
                                title="더블클릭하여 수정 — 작게 모드는 스크롤로 확인"
                                style={{flex:1,minHeight:sz.fixed?0:sz.viewMin,padding:'4px 2px',fontSize:13,lineHeight:1.55,color:c.accent,whiteSpace:'pre-wrap',wordBreak:'break-word',cursor:'text',overflowY:'visible',overflowX:'hidden'}}>
                                {memo.content ? renderMemoContent(memo.content, c.accent) : <span style={{color:'#9ca3af',fontStyle:'italic'}}>(빈 메모)</span>}
                              </div>
                            )}
                            {/* v7.312: 첨부 이미지 썸네일 — 클릭하면 원본, ×로 삭제 */}
                            {(()=>{
                              const imgs = memoImages(memo);
                              if (imgs.length === 0) return null;
                              return (
                                <div style={{display:'flex',flexWrap:'wrap',gap:5,marginTop:8,paddingTop:8,borderTop:'1px dashed '+c.border}}>
                                  {imgs.map(img => {
                                    const url = AttachmentService.getPublicUrl(img.path);
                                    // v7.414: 이미지는 썸네일, 그 외(zip)는 다운로드 칩
                                    const isImg = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(img.name || img.path || '');
                                    if (!isImg) {
                                      return (
                                        <div key={img.path} style={{position:'relative',display:'flex',alignItems:'center',gap:6,padding:'6px 9px',background:'rgba(0,0,0,0.05)',border:'1px solid '+c.border,borderRadius:8,maxWidth:'100%'}}>
                                          <Icon name="folder_zip" size={16} style={{color:c.accent,flexShrink:0}}/>
                                          <a href={url} download={img.name} title={img.name}
                                            onClick={(e)=>{e.preventDefault();e.stopPropagation();downloadKeepingName(url,img.name);}}
                                            style={{fontSize:11,color:'var(--fg-1)',textDecoration:'none',fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:150}}>
                                            {img.name}
                                          </a>
                                          <button onClick={()=>removeMemoImage(memo, img)} title="첨부 삭제"
                                            style={{width:17,height:17,borderRadius:'50%',border:'none',background:'rgba(186,26,26,0.92)',color:'#fff',cursor:'pointer',padding:0,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                            <Icon name="close" size={11}/>
                                          </button>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div key={img.path} style={{position:'relative',width:56,height:56}}>
                                        <img src={url} alt={img.name} title={img.name}
                                          onClick={()=>setMemoLightbox({url, name: img.name})}
                                          style={{width:56,height:56,objectFit:'cover',borderRadius:6,border:'1px solid '+c.border,cursor:'zoom-in',display:'block'}}/>
                                        <button onClick={()=>removeMemoImage(memo, img)} title="이미지 삭제"
                                          style={{position:'absolute',top:-5,right:-5,width:17,height:17,borderRadius:'50%',border:'none',background:'rgba(186,26,26,0.92)',color:'#fff',fontSize:11,lineHeight:'17px',cursor:'pointer',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                                          <Icon name="close" size={11}/>
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                            })()}
                            </div>{/* v7.442: 본문+첨부 스크롤 래퍼 끝 */}
                            {/* 푸터: 작성자 / 수정일 — v7.192: 색상을 검은색(#0b1c30)으로 통일 (가독성) */}
                            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:6,paddingTop:8,borderTop:'1px dashed '+c.border,fontSize:10,color:'var(--fg-1)',opacity:0.85,fontWeight:600}}>
                              <span>{memo.author || '익명'}</span>
                              <span>{memo.updated_at ? new Date(memo.updated_at).toLocaleString('ko-KR',{month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-'}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                      );
                    })()
                  )}
                  </>)}
                  <div style={{display: memoBoardTab==='files' ? 'block' : 'none'}}>
                    <SharedFilesPanel staffList={staffList} connected={connected}
                      fullWidth onCount={setSharedFileCount}/>
                  </div>
                  {/* v7.312: 메모 이미지 업로드용 공용 input (카드마다 만들지 않는다) */}
                  <input ref={memoImgRef} type="file" accept="image/*,.zip,.pdf,application/zip,application/x-zip-compressed,application/pdf" multiple style={{display:'none'}}
                    onChange={e=>{ const fl=Array.from(e.target.files||[]); e.target.value=''; handleMemoImageUpload(fl); }}/>
                </div>
                );
              })()}

              {/* v7.312: 메모 이미지 원본 보기 */}
              {inbDrag && (
                <div style={{position:'fixed',left:inbDrag.ghost.x-inbDrag.ghost.offX,top:inbDrag.ghost.y-inbDrag.ghost.offY,width:inbDrag.ghost.w,pointerEvents:'none',opacity:0.9,transform:'scale(1.03) rotate(-1.2deg)',zIndex:9999,filter:'drop-shadow(0 10px 18px rgba(0,60,90,0.28))'}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,padding:'6px 8px 6px 6px',background:'var(--bg-card)',border:'1.5px solid var(--accent)',borderRadius:8}}>
                    <span style={{flexShrink:0,color:'var(--accent)',fontSize:14,lineHeight:1,letterSpacing:'-1px'}}>⠿</span>
                    <span style={{flex:1,minWidth:0,fontSize:12.5,fontWeight:700,color:'var(--fg-1)',lineHeight:1.4,wordBreak:'break-all'}}>{inbDrag.dragName}</span>
                    <span style={{flexShrink:0,fontSize:12.5,fontWeight:700,color:'var(--fg-2)',background:'rgba(0,102,138,0.06)',border:'1px solid rgba(0,102,138,0.14)',borderRadius:6,padding:'3px 9px',whiteSpace:'nowrap'}}>×{inbDrag.dragQty}</span>
                  </div>
                </div>
              )}
              {memoLightbox && (
                <div onClick={()=>setMemoLightbox(null)}
                  style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.82)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:24,cursor:'zoom-out'}}>
                  <div onClick={e=>e.stopPropagation()} style={{maxWidth:'92vw',maxHeight:'92vh',display:'flex',flexDirection:'column',gap:8,alignItems:'center'}}>
                    <img src={memoLightbox.url} alt={memoLightbox.name}
                      style={{maxWidth:'92vw',maxHeight:'82vh',objectFit:'contain',borderRadius:8,boxShadow:'0 8px 30px rgba(0,0,0,0.5)'}}/>
                    <div style={{display:'flex',alignItems:'center',gap:10,color:'#fff',fontSize:12}}>
                      <span style={{opacity:0.9}}>{memoLightbox.name}</span>
                      <a href={memoLightbox.url} download={memoLightbox.name}
                        onClick={(e)=>{e.preventDefault();downloadKeepingName(memoLightbox.url,memoLightbox.name);}}
                        style={{display:'flex',alignItems:'center',gap:4,padding:'5px 12px',background:'rgba(255,255,255,0.15)',borderRadius:8,color:'#fff',textDecoration:'none',fontWeight:700}}>
                        <Icon name="download" size={15}/> 다운로드
                      </a>
                      <button onClick={()=>setMemoLightbox(null)}
                        style={{display:'flex',alignItems:'center',gap:4,padding:'5px 12px',background:'rgba(255,255,255,0.15)',border:'none',borderRadius:8,color:'#fff',fontSize:12,fontWeight:700,cursor:'pointer'}}>
                        <Icon name="close" size={15}/> 닫기
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {txSubTab==='list' && (<>

              {/* ━━━ 재고 부족 경고 — 토글 ━━━ */}
              {showLowStock&&(()=>{
                const lowItems = allModels.filter(m => m.in_stock <= lowStockThreshold).sort((a,b) => a.in_stock - b.in_stock);
                const outCount = lowItems.filter(m => m.in_stock === 0).length;
                const criticalCount = lowItems.filter(m => m.in_stock > 0 && m.in_stock <= 2).length;
                const warningCount = lowItems.filter(m => m.in_stock > 2).length;
                const hasOut = outCount > 0;
                return (
                <Card style={{padding:0,marginBottom:20,overflow:'hidden',border:hasOut?'2px solid var(--danger)':lowItems.length>0?'2px solid #f57c00':'1px solid var(--border-strong)'}}>
                  <div style={{padding:'14px 20px',background:hasOut?'linear-gradient(90deg, #ffebee 0%, #ffcdd2 100%)':lowItems.length>0?'linear-gradient(90deg, #fff8e1 0%, #ffe0b2 100%)':'linear-gradient(90deg, #f0fbf5 0%, #c8e6c9 100%)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:16,flexWrap:'wrap'}}>
                    <div style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{width:40,height:40,borderRadius:10,background:hasOut?'rgba(186,26,26,0.15)':lowItems.length>0?'rgba(230,81,0,0.15)':'rgba(0,152,68,0.15)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <Icon name={hasOut?'error':lowItems.length>0?'warning':'check_circle'} size={22} style={{color:hasOut?'var(--danger)':lowItems.length>0?'var(--warning)':'var(--success)'}}/>
                      </div>
                      <div>
                        <div style={{fontSize:18,fontWeight:700,color:'var(--fg-1)'}}>재고 부족 경고</div>
                        <div style={{fontSize:12,color:'var(--fg-2)',display:'flex',gap:10,flexWrap:'wrap',marginTop:2}}>
                          {hasOut&&<span style={{display:'inline-flex',alignItems:'center',gap:3,fontWeight:600,color:'var(--danger)'}}><span style={{width:8,height:8,borderRadius:'50%',background:'var(--danger-solid)'}}/>품절 {outCount}개</span>}
                          {criticalCount>0&&<span style={{display:'inline-flex',alignItems:'center',gap:3,fontWeight:600,color:'var(--warning)'}}><span style={{width:8,height:8,borderRadius:'50%',background:'var(--warning-solid)'}}/>긴급 {criticalCount}개</span>}
                          {warningCount>0&&<span style={{display:'inline-flex',alignItems:'center',gap:3,fontWeight:600,color:'#f57c00'}}><span style={{width:8,height:8,borderRadius:'50%',background:'#f57c00'}}/>경고 {warningCount}개</span>}
                          {lowItems.length===0&&<span style={{fontWeight:600,color:'var(--success)'}}>✓ 모든 모델 재고 충분</span>}
                        </div>
                      </div>
                    </div>
                    <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--bg-card)',padding:'4px 8px',borderRadius:10,border:'1px solid var(--border-strong)'}}>
                      <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em'}}>임계값</span>
                      <button onClick={()=>setLowStockThreshold(Math.max(1,lowStockThreshold-1))} disabled={lowStockThreshold<=1} style={{width:24,height:24,borderRadius:6,border:'1px solid var(--border-strong)',background:'var(--bg-card)',cursor:lowStockThreshold<=1?'not-allowed':'pointer',fontSize:14,fontWeight:700,color:'var(--fg-2)',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}>−</button>
                      <input type="number" min="1" max="999" value={lowStockThreshold} onChange={e=>setLowStockThreshold(Math.max(1,Math.min(999,parseInt(e.target.value)||1)))} style={{width:44,padding:'4px 6px',border:'1px solid var(--border-strong)',borderRadius:6,fontSize:13,fontWeight:700,textAlign:'center',color:'var(--fg-1)',background:'var(--bg-card)'}}/>
                      <button onClick={()=>setLowStockThreshold(Math.min(999,lowStockThreshold+1))} style={{width:24,height:24,borderRadius:6,border:'1px solid var(--border-strong)',background:'var(--bg-card)',cursor:'pointer',fontSize:14,fontWeight:700,color:'var(--fg-2)',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}>+</button>
                      <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:600}}>개 이하</span>
                    </div>
                  </div>
                  {lowItems.length>0?(
                    <div style={{maxHeight:280,overflowY:'auto'}}>
                      {lowItems.map((m,idx)=>{
                        const isOut = m.in_stock === 0;
                        const isCritical = m.in_stock > 0 && m.in_stock <= 2;
                        const color = isOut ? 'var(--danger)' : isCritical ? 'var(--warning)' : '#f57c00';
                        const bgColor = isOut ? 'rgba(186,26,26,0.06)' : isCritical ? 'rgba(230,81,0,0.06)' : 'rgba(245,124,0,0.04)';
                        return (
                          <div key={m.model} style={{padding:'12px 20px',display:'flex',alignItems:'center',gap:14,borderBottom:idx<lowItems.length-1?'1px solid #E2E8F0':'none',background:bgColor,transition:'background 0.15s'}}>
                            <div style={{width:32,height:32,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                              <Icon name={isOut?'error':'warning'} size={16} style={{color:'#fff'}}/>
                            </div>
                            <div style={{flex:1,minWidth:0}}>
                              <div style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',marginBottom:2}}>{m.model}</div>
                              <div style={{fontSize:11,color:'var(--fg-3)',display:'flex',gap:8,alignItems:'center'}}>
                                {m.category && <Badge variant="category">{m.category}</Badge>}
                                <span>총 {m.total}개 · 출고 {m.shipped}</span>
                              </div>
                            </div>
                            <div style={{textAlign:'right',flexShrink:0}}>
                              <div style={{fontSize:24,fontWeight:700,color,lineHeight:1}}>{m.in_stock}</div>
                              <div style={{fontSize:10,color,fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',marginTop:2}}>
                                {isOut?'품절':isCritical?'긴급':'경고'}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ):(
                    <div style={{padding:'24px 20px',textAlign:'center',color:'var(--fg-3)',fontSize:13}}>
                      {loading?'재고 데이터 로딩 중...':`전체 ${allModels.length}개 모델 중 임계값(${lowStockThreshold}개) 이하인 모델이 없습니다.`}
                    </div>
                  )}
                  <div style={{padding:'10px 20px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <span style={{fontSize:12,color:'var(--fg-3)'}}>총 {allModels.length}개 모델 · 임계값 이하 {lowItems.length}개</span>
                    <button onClick={()=>setPage('inventory')} style={{display:'flex',alignItems:'center',gap:6,padding:'6px 14px',background:'var(--bg-card)',border:'1px solid var(--accent)',borderRadius:8,cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--accent)'}}>
                      재고 목록 보기 <Icon name="arrow_forward" size={14}/>
                    </button>
                  </div>
                </Card>);
              })()}

              {/* Stats Cards — 토글 */}
              {/* (입출고 요약은 모달로 분리 — 아래쪽 EventCalendar 근처에서 렌더링) */}

              {/* Filters — 칩 필터 + 검색 */}
              <Card style={{padding:'14px 20px',marginBottom:24}}>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  {[['all','전체'],['inbound','입고'],['outbound','출고'],['return','환불']].map(([v,l])=>(
                    <button key={v} onClick={()=>{setLogFilter(v);setLogPg(0);}} style={{
                      padding:'7px 18px',borderRadius:9999,fontSize:13,fontWeight:600,border:'none',cursor:'pointer',transition:'all 0.15s',
                      background:logFilter===v?'var(--accent-solid)':'var(--bg-accent-soft)',
                      color:logFilter===v?'#fff':'var(--fg-2)',
                      boxShadow:logFilter===v?'0 2px 8px rgba(0,102,138,0.25)':'none',
                    }}>{l}</button>
                  ))}
                  {/* 검색 input */}
                  <div style={{position:'relative',flex:'1 1 280px',minWidth:240,maxWidth:520}}>
                    <Icon name="search" size={16} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                    <input
                      type="text"
                      value={logSearch}
                      onChange={e=>{setLogSearch(e.target.value);setLogPg(0);}}
                      placeholder="모델명 / 업체명 / 송장번호 / 배송지 / 날짜(2026.06.16) 검색…"
                      style={{width:'100%',padding:'8px 34px 8px 34px',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}
                    />
                    {logSearch && (
                      <button onClick={()=>{setLogSearch('');setLogPg(0);}} title="검색어 지우기" style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',width:22,height:22,border:'none',background:'transparent',borderRadius:9999,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',padding:0}}>
                        <Icon name="close" size={14}/>
                      </button>
                    )}
                  </div>
                  {logSearch.trim() && (
                    <span style={{fontSize:11,padding:'4px 10px',borderRadius:9999,background:'rgba(245,124,0,0.12)',color:'var(--warning)',fontWeight:700,display:'inline-flex',alignItems:'center',gap:4}}>
                      <Icon name="filter_alt" size={12}/> 전체 기간 검색 중
                    </span>
                  )}
                  <button onClick={()=>{setLogFilter('all');setLogSearch('');setLogPeriodMode('day');setLogPeriodOffset(0);setLogPg(0);}} style={{fontSize:13,fontWeight:600,color:'var(--accent)',background:'none',border:'none',cursor:'pointer'}}>필터 초기화</button>
                  {/* v7.322: 행 글로우 토글 — 라이트/다크 모두 노출.
                       테마별 팔레트가 따로 있어 라이트에서도 색이 살아 있다. */}
                  <button onClick={()=>{ const v=!shipGlowOn; setShipGlowOn(v); try{ localStorage.setItem('inventura_ship_glow', v?'on':'off'); }catch(e){} }}
                    title="행 상태색 테두리 — 출고요청/환불/입고는 늘 표시되고, 나머지는 마우스를 올릴 때 켜집니다"
                    style={{display:'inline-flex',alignItems:'center',gap:5,padding:'4px 11px',borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',
                      border:`1px solid ${shipGlowOn?'var(--purple)':'var(--border-strong)'}`,
                      background:shipGlowOn?'rgba(123,31,162,0.10)':'transparent',
                      color:shipGlowOn?'var(--purple)':'var(--fg-3)'}}>
                    <Icon name={shipGlowOn?'auto_awesome':'auto_awesome_mosaic'} size={13}/>
                    행 글로우
                  </button>
                  {/* v7.322→v7.323: 도는 빛 마스터 스위치.
                       건별 체크박스보다 위에 있다 — 여기가 꺼지면 전부 정지한다. */}
                  {shipGlowOn && (
                    <label title="테두리를 도는 빛 전체 끄기 — 건별 체크는 각 행 왼쪽 체크박스로"
                      style={{display:'inline-flex',alignItems:'center',gap:5,fontSize:12,fontWeight:600,color:'var(--fg-2)',cursor:'pointer',userSelect:'none'}}>
                      <input type="checkbox" checked={shipRunOn}
                        onChange={e=>{ const v=e.target.checked; setShipRunOn(v); try{ localStorage.setItem('inventura_ship_run', v?'on':'off'); }catch(err){} }}
                        style={{width:14,height:14,accentColor:'#7b1fa2',cursor:'pointer'}}/>
                      애니메이션 (전체)
                    </label>
                  )}
                </div>
              </Card>

              {/* Table */}
              <Card style={{overflow:'hidden',marginBottom:24}}>
                <div style={{overflowX:'auto'}}>
                  <div style={{display:'grid',gridTemplateColumns:'36px 100px 330px 50px 1fr 130px 150px 130px 1fr 120px',minWidth:1310,borderBottom:'2px solid var(--border)',background:'var(--bg-accent-soft)'}}>
                    {['','날짜','모델명 × 수량','','배송지','매출여부','__method_track__','__recipient_info__','__author_memo__','처리'].map((h,i)=>(
                      <div key={i} style={{padding:'14px 12px',fontSize:13,fontWeight:600,color:'var(--fg-2)',lineHeight:1.3}}>
                        {h === '__author_memo__'
                          ? <>작성자 / 요청자<div style={{fontSize:11,fontWeight:500,color:'var(--fg-3)',marginTop:2}}>비고</div></>
                          : h === '__method_track__'
                            ? <>발송 종류<div style={{fontSize:11,fontWeight:500,color:'var(--fg-3)',marginTop:2}}>송장번호 / 금액</div></>
                            : h === '__recipient_info__'
                              ? <>딜러/수령인 정보<div style={{fontSize:11,fontWeight:500,color:'var(--fg-3)',marginTop:2}}>수령인 / 핸드폰번호</div></>
                              : h}
                      </div>
                    ))}
                  </div>
                  <div style={{maxHeight:580,overflowY:'auto'}}>
                    {logItems.map((item,i)=>{
                      const isIn = item.type === 'inbound';
                      const isCancel = item.type === 'cancel';
                      const isReturn = item.type === 'return';
                      const timeStr = item.created_at ? new Date(item.created_at).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'}) : '-';
                      let shipInfo = null;
                      let processedAs = null;
                      try { const p = JSON.parse(item.note); if(p.type==='ship_request'||p.type==='cancel'||p.type==='return'||p.type==='inbound') shipInfo = p; processedAs = p.processedAs || null; } catch(e){}
                      const isProcessed = !!processedAs;
                      // 취소된 행은 숨김 — 필터칩 [취소]가 활성화된 경우에만 노출
                      if (logFilter !== 'cancel' && (isCancel || (isProcessed && processedAs==='cancel'))) {
                        return null;
                      }
                      const canExpand = !!shipInfo;
                      const rowKey = item.id||i;
                      // 다중 품목 처리 (items 배열) — 신규 형식 우선
                      const items = shipInfo?.items;
                      const isMulti = Array.isArray(items) && items.length > 1;
                      const totalQty = Array.isArray(items)
                        ? items.reduce((s,it)=>s+(it.qty||0),0)
                        : (shipInfo?.qty || 0);
                      const totalFulfilled = Array.isArray(items)
                        ? items.reduce((s,it)=>s+((it.fulfilledSerials?.length)||0),0)
                        : (shipInfo?.fulfilledSerials?.length || 0);
                      const typeBadge = isCancel ? {bg:'rgba(255,152,0,0.1)',color:'var(--warning)',label:'취소'}
                        : isReturn ? {bg:'rgba(156,39,176,0.1)',color:'var(--purple)',label:'환불'}
                        : isIn ? {bg:'rgba(0,102,138,0.1)',color:'var(--accent)',label:'입고'}
                        : isProcessed ? {bg:'rgba(0,0,0,0.05)',color:'#9e9e9e',label: processedAs==='cancel'?'취소됨':'환불됨'}
                        : {bg:'rgba(186,26,26,0.08)',color:'var(--danger)',label:'출고'};
                      const rowIcon = isCancel ? 'cancel' : isReturn ? 'undo' : isProcessed ? 'block' : shipInfo && !isCancel && !isReturn ? 'local_shipping' : 'package_2';
                      const rowIconBg = isCancel ? 'rgba(255,152,0,0.1)' : isReturn ? 'rgba(156,39,176,0.1)' : isProcessed ? 'rgba(0,0,0,0.04)' : shipInfo ? 'rgba(0,102,138,0.1)' : '#dce9ff';
                      const rowIconColor = isCancel ? '#e65100' : isReturn ? '#7b1fa2' : isProcessed ? '#9e9e9e' : shipInfo ? '#00668a' : '#45464d';
                      // v7.319: 행 글로우 — 상태 판정.
                      //   출고완료/출고요청의 갈림은 shipInfo.fulfilled 다 (행의 [출고완료]/[출고요청] 칩과 같은 기준).
                      //   취소·취소됨은 null → 글로우 자체를 안 붙인다.
                      const glowKey =
                        (isCancel || (isProcessed && processedAs === 'cancel')) ? null
                        : (isReturn || (isProcessed && processedAs === 'return')) ? 'return'
                        : isIn ? 'inbound'
                        : (shipInfo && !shipInfo.fulfilled) ? 'pending'
                        : 'shipped';
                      // v7.323: 애니메이션 재생 여부는 '건별' 로 정한다.
                      //   note.glowRun 이 있으면 그 값, 없으면 기본값 — 출고요청만 켜짐.
                      //   (기존 동작 유지: 출고요청은 손대지 않아도 계속 돈다)
                      const runFlag = shipInfo && shipInfo.glowRun !== undefined
                        ? !!shipInfo.glowRun
                        : (glowKey === 'pending');
                      const runOn  = !!(shipGlowOn && shipRunOn && glowKey && runFlag);
                      // 상시 테두리 — 출고요청은 항상, 그 외는 사용자가 체크한 건만
                      const markOn = !!(shipGlowOn && glowKey && (glowKey === 'pending' || runOn));
                      const glowClass = !shipGlowOn || !glowKey ? undefined
                        : ['glow-row', markOn && 'glow-mark', runOn && 'glow-run'].filter(Boolean).join(' ');
                      // v7.318: 호버 배경을 테마 변수로. 기존엔 '#f0f4ff'/'#fafafa' 하드코딩이라
                      //   다크 모드에서 행에 마우스를 올리면 흰빛으로 번쩍였다 (글로우도 묻혔다).
                      const rowBgIdle = isProcessed ? 'var(--bg-mute)' : '';
                      return (
                        <div key={rowKey}
                            className={glowClass}
                            style={{...(glowClass ? SHIP_GLOW_STYLES[theme === 'dark' ? 'dark' : 'light'][glowKey] : null),display:'grid',gridTemplateColumns:'36px 100px 330px 50px 1fr 130px 150px 130px 1fr 120px',minWidth:1310,borderBottom:'1px solid var(--border)',transition:'all 0.15s',background:rowBgIdle,opacity:isProcessed?0.55:1}}
                            onPointerMove={glowClass ? shipRowGlow.onPointerMove : undefined}
                            onPointerLeave={glowClass ? shipRowGlow.onPointerLeave : undefined}
                            onMouseEnter={e=>{if(!isProcessed)e.currentTarget.style.background='var(--bg-accent-soft)';}}
                            onMouseLeave={e=>{e.currentTarget.style.background=rowBgIdle;}}
                            onDragOver={e=>{
                              if(!shipRowDragId||shipRowDragId===item.id)return;
                              const dragRow=logItems.find(x=>x.id===shipRowDragId);
                              if(!dragRow||dragRow.transaction_date!==item.transaction_date)return;
                              e.preventDefault();e.dataTransfer.dropEffect='move';
                              e.currentTarget.style.boxShadow='inset 0 2px 0 #00668a';
                            }}
                            onDragLeave={e=>{e.currentTarget.style.boxShadow='';}}
                            onDrop={e=>{
                              e.currentTarget.style.boxShadow='';
                              if(!shipRowDragId)return;
                              e.preventDefault();
                              reorderShipRow(shipRowDragId,item.id);
                              setShipRowDragId(null);
                            }}>
                            <div style={{width:36,boxSizing:'border-box',padding:'10px 6px 14px',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-start',gap:6}}>
                              {/* v7.84: drag handle을 div로 감싸서 draggable이 실제 DOM에 반영되도록 수정
                                  (v7.81에서 Icon에 직접 draggable을 줬는데 Icon 컴포넌트가 draggable prop을 무시해서
                                   드래그 이벤트가 아예 발생 안 하고 있던 버그)
                                  v7.147: 첫 셀 상단 정렬 (justifyContent flex-start) — 드래그 핸들을 위쪽에 배치
                                  v7.148: order 속성 명시 (드래그 order:0, 수정 order:1) — 실제 렌더 순서 확실히 */}
                              {!isProcessed && (
                                <div
                                  draggable
                                  onDragStart={e=>{
                                    setShipRowDragId(item.id);
                                    e.dataTransfer.effectAllowed='move';
                                    try{e.dataTransfer.setData('text/plain',String(item.id));}catch(err){}
                                  }}
                                  onDragEnd={()=>setShipRowDragId(null)}
                                  title="드래그하여 순서 변경 (같은 날짜 내에서만)"
                                  style={{order:0,width:22,height:22,borderRadius:5,display:'flex',alignItems:'center',justifyContent:'center',cursor:'grab',color:shipRowDragId===item.id?'var(--accent)':'#9e9e9e'}}
                                  onMouseEnter={e=>{e.currentTarget.style.background='rgba(0,0,0,0.06)';}}
                                  onMouseLeave={e=>{e.currentTarget.style.background='transparent';}}>
                                  <Icon name="drag_indicator" size={14}/>
                                </div>
                              )}
                              <div onClick={()=>{if(canExpand)setLogModalItem(item);}} title={canExpand?'클릭하여 상세보기':''} style={{order:1,cursor:canExpand?'pointer':'default'}}>
                                {canExpand ? <Icon name="open_in_new" size={16} style={{color:'var(--accent)'}}/> : <span style={{width:18}}/>}
                              </div>
                              {/* v7.323: 이 건의 테두리 애니메이션 on/off.
                                  값은 note.glowRun 에 들어가므로 전 직원이 같은 표시를 본다. */}
                              {shipGlowOn && shipInfo && glowKey && (
                                <input type="checkbox" checked={runFlag}
                                  onChange={e=>{ e.stopPropagation(); toggleRowGlowRun(item, e.target.checked); }}
                                  onClick={e=>e.stopPropagation()}
                                  title={runFlag ? '이 건의 테두리 애니메이션 끄기' : '이 건의 테두리 애니메이션 켜기'}
                                  style={{order:2,width:13,height:13,margin:0,cursor:'pointer',
                                    accentColor:SHIP_GLOW_ACCENT[glowKey]||'#7b1fa2',
                                    opacity:shipRunOn?1:0.4}}/>
                              )}
                            </div>
                            <div style={{padding:'14px 10px',fontSize:13,fontWeight:600,color:isProcessed?'#9e9e9e':'var(--fg-1)',textDecoration:isProcessed?'line-through':'none',position:'relative',overflow:'hidden'}}>
                              {/* v7.106: 네이버스토어 워터마크 — 날짜 셀 배경으로 이동 (fill 모드) */}
                              {shipInfo?.naverStore && !isIn && (
                                <NaverLogo fill opacity={isProcessed?0.09:0.15}/>
                              )}
                              {fmtDate(item.transaction_date)}<div style={{fontSize:11,fontWeight:500,color:isProcessed?'#bbb':'var(--fg-3)',marginTop:2,textDecoration:'none'}}>{fmtTime(item.created_at)}</div>
                              {/* v7.382: 이월된 건만 최초 등록일을 덧붙인다.
                                  ⚠ 이월이 반복되면 날짜 칸은 매일 오늘로 바뀌어서,
                                    이 줄이 없으면 [언제부터 밀린 건인지] 알 방법이 아예 없다.
                                  ⚠ 이월된 건에만 뜬다. 모든 행에 붙이면 같은 날짜가 두 번
                                    적히는 꼴이라 아무도 안 읽게 된다. */}
                              {(()=>{
                                const di = deferInfoOf(item);
                                if (!di) return null;
                                return (
                                  <div title={`최초 등록 ${fmtDate(di.origDate)} · ${di.days}일째 이월 중`}
                                    style={{marginTop:3,display:'inline-flex',alignItems:'center',gap:3,padding:'1px 6px',borderRadius:5,
                                      fontSize:10,fontWeight:700,lineHeight:1.4,textDecoration:'none',whiteSpace:'nowrap',
                                      background:isProcessed?'transparent':'rgba(255,152,0,0.12)',
                                      color:isProcessed?'#bbb':'#8a5000'}}>
                                    <Icon name="history" size={10}/>
                                    최초 {fmtDate(di.origDate).slice(5)} · {di.days}일째
                                  </div>
                                );
                              })()}
                            </div>
                            {/* 모델명 — 다중 품목은 줄바꿈 */}
                            <div style={{padding:'14px 10px',display:'flex',gap:6,alignItems:'flex-start'}}>
                              <div style={{width:26,height:26,borderRadius:7,background:rowIconBg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,marginTop:1}}>
                                <Icon name={rowIcon} size={13} style={{color:rowIconColor}}/>
                              </div>
                              <div data-inb-list={item.id} style={{flex:1,minWidth:0,display:'flex',flexDirection:'column',gap:6}}>
                                {/* 품목 라인들 — 잘리지 않고 줄바꿈 */}
                                {(()=>{
                                  // v7.254: 부분 환불된 품목은 이 줄에서도 회색 + 취소선.
                                  //   지금까지는 상세 모달을 열어야만 어느 품목이 빠졌는지 알 수 있었다.
                                  //   ★ 품목의 출고 시리얼이 **전부** 환불됐을 때만 줄을 긋는다.
                                  //     일부만 환불된 품목은 아직 나가 있는 물건이 있으므로 그대로 둔다.
                                  const _refunded = shipInfo ? refundedSetOf(shipInfo) : new Set();
                                  const isItemRefunded = (it) => {
                                    if (_refunded.size === 0) return false;
                                    const ser = Array.isArray(it.fulfilledSerials) ? it.fulfilledSerials : [];
                                    return ser.length > 0 && ser.every(sn => _refunded.has(sn));
                                  };
                                  /* v7.378: 재고 0 품목을 시트지에서 바로 알아보게 한다.
                                     지금까지는 모달을 열어 스캔을 시도해야만 알 수 있었다.
                                     ⚠ logStockByModel === null 은 [아직 안 받음] 이다.
                                       0 으로 취급하면 로딩 중 전체가 빨갛게 깜빡인다.
                                     ⚠ 입고·처리완료·외부 품목은 대상이 아니다 (조회 단계에서 이미 제외).
                                       여기서도 같은 조건을 걸어 두 곳이 어긋나지 않게 한다.
                                     ⚠ 이미 시리얼을 찍어 둔 품목은 제외한다 — 물건을 확보해 둔 것이라
                                       현재 재고가 0이어도 문제가 아니다. */
                                  const stockReady = logStockByModel !== null;
                                  /* v7.422: 데모 대여로 작성된 품목은 inventory 가 아니라 demo_equipment 를 참조한다.
                                       실물은 demo_equipment 에서 관리되므로 inventory in_stock 카운트로 재고없음을
                                       판정하면 안 된다(가용 장비가 많아도 0으로 잡혀 [재고없음] 오표기 — 사용자 리포트).
                                       식별: note.fromDemoLend(행) 또는 items[].serialOptionalReason==='demo_lend'(품목). */
                                  const isDemoItem = (it) => !!(it && (it.serialOptionalReason === 'demo_lend' || shipInfo?.fromDemoLend));
                                  const demoModelsReady = logDemoModels !== null;
                                  // 데모 품목인데 그 모델이 demo_equipment 에 아예 없으면 [데모 미등록] (모델명 오타/미등록만 잡는다)
                                  const demoUnregistered = (it) => {
                                    if (!isDemoItem(it) || !demoModelsReady) return false;
                                    const name = (it.product_name || '').trim();
                                    if (!name) return false;
                                    return !logDemoModels.has(name);
                                  };
                                  // v7.433: [재고없음] 표기 폐지 (사용자 요청 — 재고 현황은 재고 대시보드에서 본다).
                                  //   대시보드는 이미 재고 0 = 노랑, 임계 이하 = 주황으로 모델별 부족을 알린다.
                                  //   배송시트지에는 '이 건의 요청 수량 > 가용' 인 경우만 [재고부족]으로 남긴다(shortInfo).
                                  //   ⚠ 함수는 남겨 둔다 — 세 렌더 갈래(단일/다중/구형식)가 모두 호출하고 title 툴팁 분기도 참조한다.
                                  const noStock = () => false;
                                  const NoStockTag = () => (
                                    <span style={{display:'block',fontSize:10.5,fontWeight:700,color:'var(--danger)',lineHeight:1.3,marginTop:1}}>
                                      (재고없음)
                                    </span>
                                  );
                                  // v7.422: 데모 품목 표기 — [데모항목](중립 파랑) / demo_equipment 미등록 모델이면 [데모 미등록](빨강)
                                  const DemoTag = () => (
                                    <span title="데모 장비 (demo_equipment 참조)" style={{display:'block',fontSize:10.5,fontWeight:700,color:'#1976d2',lineHeight:1.3,marginTop:1}}>
                                      데모항목
                                    </span>
                                  );
                                  const DemoUnregTag = () => (
                                    <span title="demo_equipment 에 등록되지 않은 모델입니다. 모델명 확인 필요." style={{display:'block',fontSize:10.5,fontWeight:700,color:'var(--danger)',lineHeight:1.3,marginTop:1}}>
                                      ⚠ 데모 미등록
                                    </span>
                                  );
                                  /* v7.393: 재고는 있으나 [요청 수량 > 가용] 인 품목을 [재고부족] 으로 표기한다.
                                       재고없음(stock===0)과 구분한다 — 그쪽은 위 noStock 이 이미 [재고없음] 으로 말한다.
                                       ⚠ 판정은 + 버튼(inc)과 동일하게: 가용 = in_stock + 이미 확보한 시리얼(fulfilled+reserved).
                                       ⚠ 외부/면제 품목은 재고 개념이 없으므로 제외. 입고·처리완료·조회전(null)도 제외. */
                                  const shortInfo = (it) => {
                                    if (!stockReady || isIn || isProcessed || shipInfo?.fulfilled) return null;
                                    // v7.434: pending(담아둔) 수량 조회 전에는 판정 보류 — 자기 확보분이 빠진 채로
                                    //   순간 [재고부족]이 번쩍이는 것을 막는다(재고 조회의 stockReady 와 같은 취지).
                                    if (logPendingByReq === null) return null;
                                    if (!it || it.external || it.serialOptional || isDemoItem(it)) return null; // v7.433: 데모는 demo_equipment 참조 → 재고부족 판정 제외
                                    const name = it.product_name;
                                    if (!name) return null;
                                    const stock = logStockByModel[name] || 0;
                                    // v7.433: 기존엔 stock===0 이면 [재고없음]에 맡기고 빠졌는데, 그 라벨을 없앴으므로
                                    //   재고 0 인 채로 요청한 경우도 여기서 [재고부족]으로 잡아야 한다.
                                    // v7.434: 이 건이 이미 담아둔(pending) 수량을 가용에 되더한다.
                                    //   v7.424 부터 담긴 시리얼은 inventory 가 ship_hold 라 stock(in_stock 집계)에서 빠져 있다.
                                    //   안 더하면 12개 담아둔 건이 '가용 0 · 요청 12' 로 잡혀 자기 물건인데 [재고부족]이 뜬다.
                                    const reqIdForRow = shipInfo?.reqId || item.serial;
                                    const pendingHeld = (logPendingByReq && reqIdForRow && logPendingByReq[reqIdForRow]?.[name]) || 0;
                                    const held = (it.fulfilledSerials?.length || 0) + (it.reservedSerials?.length || 0) + pendingHeld;
                                    const maxAvailable = stock + held;
                                    const qty = it.qty || 0;
                                    if (qty <= maxAvailable) return null;
                                    return { stock, held, maxAvailable, qty };
                                  };
                                  const shortTitle = (it, info) => `${it.product_name} — 재고 부족: 요청 ${info.qty}개 · 가용 ${info.maxAvailable}개(재고 ${info.stock}${info.held ? ` + 확보 ${info.held}` : ''})`;
                                  const ShortStockTag = () => (
                                    <span style={{display:'block',fontSize:10.5,fontWeight:700,color:'var(--danger)',lineHeight:1.3,marginTop:1}}>
                                      (재고부족)
                                    </span>
                                  );
                                  // v7.438: 시리얼 등록 완료 = 초록(--success) / 미완 = 주황(--warning) (사용자 요청).
                                  //   등록수 = fulfilled + reserved + pending(logPendingByReq[reqId][name]). qty 이상이면 완료.
                                  //   대상: 미출고 출고요청의 [시리얼 관리 품목]만. 입고/처리완료/환불/재고부족(red 우선)·
                                  //         외부(EXT)/면제(NOSN)/데모 품목은 제외(각자 다른 표기 체계) → null 이면 기본색 유지.
                                  //   ⚠ pending 조회 전(logPendingByReq===null)엔 판정 보류(색 깜빡임 방지).
                                  const serialTrackable = (it) => !!it && !it.external && !it.serialOptional && !isDemoItem(it);
                                  const regColor = (it) => {
                                    if (isIn || isProcessed || shipInfo?.fulfilled) return null;
                                    if (shipInfo?.type !== 'ship_request') return null;
                                    if (!serialTrackable(it)) return null;
                                    if (logPendingByReq === null) return null;
                                    const name = it.product_name;
                                    if (!name) return null;
                                    const reqIdForRow = shipInfo?.reqId || item.serial;
                                    const pendingHeld = (logPendingByReq && reqIdForRow && logPendingByReq[reqIdForRow]?.[name]) || 0;
                                    const registered = (it.fulfilledSerials?.length || 0) + (it.reservedSerials?.length || 0) + pendingHeld;
                                    return registered >= (it.qty || 0) ? 'var(--success)' : 'var(--warning)';
                                  };
                                  // 다중 품목 (items 배열)
                                  if (Array.isArray(items) && items.length > 0) {
                                    // 단일 품목인 경우 더블클릭 → 검색 모달, 다중도 각자 더블클릭 가능
                                    if (items.length === 1 && !((isIn || shipInfo?.type === 'ship_request') && !shipInfo?.fromDemoLend)) {
                                      const it = items[0];
                                      const dm = isDemoItem(it);            // v7.422: 데모 품목
                                      const dmUnreg = demoUnregistered(it); // demo_equipment 미등록 모델
                                      const ns = noStock(it);
                                      const si = ns ? null : shortInfo(it);
                                      const red = ns || !!si || dmUnreg;
                                      return (
                                        <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setModelSearchModal({item,oldName:it.product_name,mode:'change'}); setModelSearchQuery(''); setModelSearchResults([]);}} title={dmUnreg?`${it.product_name} — demo_equipment 에 미등록된 모델입니다`:dm?`${it.product_name} — 데모 장비 (demo_equipment 참조)`:ns?`${it.product_name} — 현재 재고 없음 (입고 후 시리얼 스캔 필요)`:si?shortTitle(it,si):(isProcessed?'':'더블클릭하여 제품 검색·변경')} style={{fontSize:13,fontWeight:600,color:(isProcessed||isItemRefunded(it))?'#9e9e9e':red?'var(--danger)':(regColor(it)||'var(--fg-1)'),textDecoration:(isProcessed||isItemRefunded(it))?'line-through':'none',lineHeight:1.4,cursor:isProcessed?'default':'pointer',wordBreak:'break-all'}}>
                                          {highlightMatch(it.product_name, logSearch)}
                                          {ns && <NoStockTag/>}
                                          {si && <ShortStockTag/>}
                                          {dm && (dmUnreg ? <DemoUnregTag/> : <DemoTag/>)}
                                        </span>
                                      );
                                    }
                                    // 다중 품목: 모두 표시 — 각 품목을 한 박스로 묶어 옆 수량과 시각적 일렬 정렬 (v7.63)
                                    return (() => {
                                      const mergeInbound = !shipInfo?.fromDemoLend && Array.isArray(items) && items.length >= 1 && (isIn || shipInfo?.type === 'ship_request'); // v7.448: 출고요청도 블록화
                                      const _dragActive = inbDrag && inbDrag.rowId === item.id;
                                      const _order = (mergeInbound && _dragActive) ? inbDrag.order : items.map(x => x.product_name);
                                      const _renderItems = mergeInbound ? _order.map(nm => items.find(x => x.product_name === nm)).filter(Boolean) : items;
                                      return (
                                      <>
                                        {_renderItems.map((it, idx) => {
                                          const canRemove = !isProcessed && shipInfo?.type === 'ship_request' && (it.fulfilledSerials?.length || 0) === 0;
                                          const _sn = Array.isArray(it.serials) ? it.serials.length : 0;
                                          const _mismatch = mergeInbound && _sn > 0 && _sn !== it.qty;
                                          const _isDragged = _dragActive && inbDrag && inbDrag.dragName === it.product_name;
                                          // v7.448: 출고요청 블록 안 수량 스테퍼
                                          const _shipStep = shipInfo?.type === 'ship_request' && !isIn && !isProcessed;
                                          const _minQty = (it.fulfilledSerials?.length) || 1;
                                          const _decQty = async () => {
                                            if (it.qty <= _minQty) { alert(_minQty === 1 ? '수량은 1개 이상이어야 합니다.' : `이미 출고된 수량(${_minQty})보다 작게 변경할 수 없습니다.`); return; }
                                            const newQty = it.qty - 1;
                                            const reqId = shipInfo?.reqId || item.serial;
                                            const allowed = Math.max(0, newQty - (it.fulfilledSerials?.length || 0));
                                            const excess = await collectExcessPending(reqId, it.product_name, allowed);
                                            if (excess.length > 0) {
                                              const msg = `[${it.product_name}] 수량 ${it.qty} → ${newQty}\n\n등록해 둔 시리얼 ${excess.length}개도 함께 해제됩니다:\n` + excess.map(x => ' · ' + x).join('\n') + `\n\n계속하시겠습니까?`;
                                              if (!window.confirm(msg)) return;
                                            }
                                            await updateItemQty(item, it.product_name, newQty);
                                            if (excess.length > 0) await removePendingSerials(reqId, it.product_name, excess);
                                          };
                                          const _incQty = async () => { await updateItemQty(item, it.product_name, it.qty + 1); };
                                          const dm = isDemoItem(it);            // v7.422: 데모 품목
                                          const dmUnreg = demoUnregistered(it); // demo_equipment 미등록 모델
                                          const ns = noStock(it);
                                          const si = ns ? null : shortInfo(it);
                                          const red = ns || !!si || dmUnreg;
                                          return (
                                            /* ⚠ 다중 품목 줄은 minHeight 26 으로 옆 수량 칸과 맞춰져 있다(v7.63).
                                                 (재고없음) 을 아래 줄로 붙이면 그 정렬이 깨진다 →
                                                 여기서는 한 줄 안에 뱃지로 붙인다. */
                                            <div key={mergeInbound ? ('inb-'+it.product_name) : idx} data-inb-name={it.product_name} style={{display:'flex',alignItems:red?'flex-start':'center',gap:6,minWidth:0,minHeight:26,padding:red?'4px 8px':'2px 8px',background:isProcessed?'transparent':red?'rgba(186,26,26,0.05)':'rgba(0,102,138,0.04)',border:isProcessed?'none':`1px solid ${red?'rgba(186,26,26,0.25)':'rgba(0,102,138,0.12)'}`,borderRadius:6,opacity:_isDragged?0.35:1}}>
                                              {mergeInbound && !isProcessed && items.length > 1 && (
                                                <span onPointerDown={(e)=>startInbDrag(e, item, items.map(x=>x.product_name), it)} title="드래그하여 순서 변경" style={{flexShrink:0,cursor:'grab',color:'var(--fg-3)',fontSize:14,lineHeight:1,letterSpacing:'-1px',touchAction:'none',userSelect:'none',padding:'0 1px'}}>⠿</span>
                                              )}
                                              {/* v7.380: 제품명은 자르지 않는다.
                                                  ⚠ ellipsis 로 잘리면 TS-433-4G-KR 과 TS-433-8G-KR 이
                                                    화면에서 똑같이 [TS-43...] 이 되어 서로 구분이 안 된다.
                                                    시트지에서 제품을 잘못 읽는 것은 곧 오출고다.
                                                  ⚠ 대신 [줄바꿈] 을 허용한다 (break-all).
                                                    잘리는 것보다 두 줄이 되는 편이 낫다.
                                                  ⚠ (재고없음) 을 flexShrink:0 으로 두면 긴 이름에서
                                                    이름 쪽만 계속 눌린다 → 이름이 우선이다. */}
                                              <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setModelSearchModal({item,oldName:it.product_name,mode:'change'}); setModelSearchQuery(''); setModelSearchResults([]);}} title={dmUnreg ? (it.product_name + ' — demo_equipment 에 미등록된 모델') : dm ? (it.product_name + ' — 데모 장비 (demo_equipment 참조)') : ns ? (it.product_name + ' — 현재 재고 없음' + (isProcessed?'':' (더블클릭하여 변경)')) : si ? (shortTitle(it,si) + (isProcessed?'':' (더블클릭하여 변경)')) : (it.product_name + (isProcessed?'':' (더블클릭하여 변경)'))} style={{flex:1,fontSize:12,fontWeight:idx===0?600:500,color:(isProcessed||isItemRefunded(it))?'#9e9e9e':red?'var(--danger)':(regColor(it)||'var(--fg-1)'),textDecoration:(isProcessed||isItemRefunded(it))?'line-through':'none',lineHeight:1.4,cursor:isProcessed?'default':'pointer',wordBreak:'break-all',minWidth:0}}>
                                                {it.product_name}
                                                {/* v7.381: 같은 줄이 아니라 아랫줄에 놓는다 (단일 품목과 동일 표기).
                                                    ⚠ 옆에 붙이면 250px 안에서 이름과 자리를 다투게 되고,
                                                      결국 이름이 밀려 두 줄로 접힌다 — 표기 위치만 다를 뿐
                                                      결과는 같으면서 읽기만 나빠진다.
                                                    ⚠ 이 줄 때문에 박스가 2줄이 되어 옆 수량 칸(minHeight 26,
                                                      v7.63)과 한 칸 어긋난다. 감수한 부분이다 —
                                                      재고 유무를 못 알아보는 쪽이 더 나쁘다. */}
                                                {ns && <span style={{display:'block',fontSize:10,fontWeight:700,color:'var(--danger)',lineHeight:1.3,marginTop:1,whiteSpace:'nowrap'}}>(재고없음)</span>}
                                                {si && <span style={{display:'block',fontSize:10,fontWeight:700,color:'var(--danger)',lineHeight:1.3,marginTop:1,whiteSpace:'nowrap'}}>(재고부족)</span>}
                                                {/* v7.422: 데모 품목 표기 (inventory 재고 대신 demo_equipment 참조) */}
                                                {dm && <span style={{display:'block',fontSize:10,fontWeight:700,color:dmUnreg?'var(--danger)':'#1976d2',lineHeight:1.3,marginTop:1,whiteSpace:'nowrap'}}>{dmUnreg?'⚠ 데모 미등록':'데모항목'}</span>}
                                              </span>
                                              {mergeInbound && (_shipStep ? (
                                                <div onClick={e=>e.stopPropagation()} style={{flexShrink:0,alignSelf:'center',display:'inline-flex',alignItems:'center',border:'1px solid var(--border-strong)',borderRadius:8,overflow:'hidden',background:'var(--bg-card)',height:26}}>
                                                  <button onClick={_decQty} disabled={it.qty <= _minQty} style={{width:22,height:24,border:'none',background:'transparent',cursor:it.qty<=_minQty?'default':'pointer',color:it.qty<=_minQty?'#ccc':'#00668a',display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontSize:15,fontWeight:700,borderRight:'1px solid var(--border)'}}>−</button>
                                                  <span style={{minWidth:22,textAlign:'center',fontSize:12,fontWeight:700,color:'var(--fg-1)',padding:'0 2px'}}>{it.qty}</span>
                                                  <button onClick={_incQty} style={{width:22,height:24,border:'none',background:'transparent',cursor:'pointer',color:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontSize:15,fontWeight:700,borderLeft:'1px solid var(--border)'}}>+</button>
                                                </div>
                                              ) : (
                                                <span title={_mismatch ? (`수량 ${it.qty}개 · 등록 시리얼 ${_sn}개 불일치`) : (`${it.product_name} · ${it.qty}개`)} style={{flexShrink:0,alignSelf:'center',fontSize:12.5,fontWeight:700,lineHeight:1,padding:'3px 9px',borderRadius:6,whiteSpace:'nowrap',color:isProcessed?'#bbb':_mismatch?'var(--danger)':'var(--fg-2)',background:isProcessed?'transparent':_mismatch?'rgba(186,26,26,0.08)':'rgba(0,102,138,0.06)',border:`1px solid ${isProcessed?'transparent':_mismatch?'rgba(186,26,26,0.3)':'rgba(0,102,138,0.12)'}`}}>
                                                  ×{it.qty}{_mismatch && ' ⚠'}
                                                </span>
                                              ))}
                                              {canRemove && (
                                                <button onClick={()=>{
                                                  if (window.confirm(`품목 "${it.product_name}"을 삭제하시겠습니까?`)) removeItemFromShipment(item, it.product_name);
                                                }} title="이 품목 삭제" style={{width:18,height:18,border:'none',background:'transparent',borderRadius:4,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--danger)',flexShrink:0,padding:0,opacity:0.6}} onMouseEnter={e=>e.currentTarget.style.opacity='1'} onMouseLeave={e=>e.currentTarget.style.opacity='0.6'}>
                                                  <Icon name="close" size={12}/>
                                                </button>
                                              )}
                                            </div>
                                          );
                                        })}
                                      </>
                                      );
                                    })();
                                  }
                                  // 단일 품목 (구 형식 또는 입고 등) — model only
                                  {
                                    const pseudo = { product_name: item.model };
                                    const dm = isDemoItem(pseudo);            // v7.422: 데모 행(fromDemoLend)이면 데모항목
                                    const dmUnreg = demoUnregistered(pseudo);
                                    const ns = noStock(pseudo);
                                    const red = ns || dmUnreg;
                                    return (
                                      <span title={dmUnreg?`${item.model} — demo_equipment 에 미등록된 모델`:dm?`${item.model} — 데모 장비 (demo_equipment 참조)`:ns?`${item.model} — 현재 재고 없음`:undefined} style={{fontSize:13,fontWeight:600,color:isProcessed?'#9e9e9e':red?'var(--danger)':'var(--fg-1)',textDecoration:isProcessed?'line-through':'none',lineHeight:1.4,wordBreak:'break-all'}}>
                                        {item.model}
                                        {ns && <NoStockTag/>}
                                        {dm && (dmUnreg ? <DemoUnregTag/> : <DemoTag/>)}
                                      </span>
                                    );
                                  }
                                })()}
                                {/* + 품목 추가 버튼 — v7.428: 데모 건은 [품목 수정] 으로 바꿔 데모 관리 편집 화면으로 보낸다.
                                     데모 품목의 실물은 demo_equipment/demo_rental_items 가 진실의 원천이라,
                                     배송시트지에서 품목을 직접 추가하면 대여 원본과 어긋난다(장비 상태 동기화도 안 됨).
                                     데모 편집 화면은 품목 추가/제거 시 demo_equipment 를 자동 동기화하고 배송시트지에도 반영한다. */}
                                {Array.isArray(items) && !isProcessed && shipInfo?.type === 'ship_request' && !isIn && (
                                  shipInfo?.fromDemoLend ? (
                                    <button onClick={(e)=>{
                                      e.stopPropagation();
                                      const rid = shipInfo?.demoRentalId;
                                      if (!rid) { alert('이 데모 건의 대여 정보를 찾을 수 없습니다 (demoRentalId 없음).\n데모 관리에서 직접 수정해주세요.'); return; }
                                      openDemoLendEditModal({ id: rid }, { returnTo: 'txlog', returnSearch: item.serial });
                                    }} title="데모 관리에서 품목 수정 (demo_equipment 동기화)" style={{alignSelf:'flex-start',display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',border:'1px dashed #7b1fa2',background:'transparent',color:'#7b1fa2',borderRadius:5,fontSize:10,fontWeight:600,cursor:'pointer',marginTop:2}}>
                                      <Icon name="edit" size={11}/> 품목 수정
                                    </button>
                                  ) : (
                                    <button onClick={(e)=>{e.stopPropagation(); setModelSearchModal({item,mode:'add'}); setModelSearchQuery(''); setModelSearchResults([]);}} title="품목 추가" style={{alignSelf:'flex-start',display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',border:'1px dashed var(--accent)',background:'transparent',color:'var(--accent)',borderRadius:5,fontSize:10,fontWeight:600,cursor:'pointer',marginTop:2}}>
                                      <Icon name="add" size={11}/> 품목 추가
                                    </button>
                                  )
                                )}
                                {/* 상태 배지 */}
                                <div style={{display:'flex',gap:4,flexWrap:'wrap',marginTop:2}}>
                                  {isIn && <span style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(0,152,68,0.12)',color:'var(--success)'}}>입고</span>}
                                  {/* v7.183/v7.187: 입고 행 장부처리 뱃지 — 3-state (완료/미완료/수기입력) 드롭다운 */}
                                  {isIn && shipInfo && (() => {
                                    const bkState = normalizeBookkept(shipInfo.bookkept);
                                    const bkStyle = BOOKKEPT_STYLES[bkState];
                                    // v7.239: 수기입력이면 적어 넣은 내용을 뱃지에 그대로 보여준다
                                    const bkManual = (shipInfo.bookkeptManual || '').trim();
                                    const bkLabel = (bkState === 'manual' && bkManual) ? bkManual : bkStyle.label;
                                    return (
                                      <span
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          if (isProcessed) return;
                                          const rect = e.currentTarget.getBoundingClientRect();
                                          setBookkeptPopover({ rowId: item.id, x: rect.left, y: rect.bottom + 4, current: bkState, manual: bkManual });
                                        }}
                                        title={bkState === 'manual' && bkManual ? `수기입력: ${bkManual} — 클릭하여 수정` : '클릭하여 장부처리 상태 변경'}
                                        style={{
                                          padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,cursor:isProcessed?'default':'pointer',userSelect:'none',
                                          display:'inline-flex',alignItems:'center',gap:3,
                                          background: bkStyle.bg, color: bkStyle.color,
                                          maxWidth:170,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
                                        }}>
                                        <Icon name={bkStyle.icon} size={11}/>
                                        {bkLabel}
                                      </span>
                                    );
                                  })()}
                                  {/* v7.249: 상태 칩을 토글 버튼으로 — 요청 클릭 시 [제품 출고] 모달이 열린다 */}
                                  {shipInfo&&!isCancel&&!isReturn&&!isProcessed&&!shipInfo.fulfilled&&!isIn&&(
                                    <button onClick={e=>{e.stopPropagation(); toggleShipFulfilled(item, shipInfo);}} disabled={loading}
                                      title={Array.isArray(shipInfo.revertedItems)&&shipInfo.revertedItems.length
                                        ? '클릭하여 출고완료로 되돌리기 — 직전 시리얼 그대로, 재스캔 없음'
                                        : '클릭하여 출고 처리 — 시리얼 스캔 화면이 열립니다'}
                                      style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(245,124,0,0.15)',color:'var(--warning)',border:'none',cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name={Array.isArray(shipInfo.revertedItems)&&shipInfo.revertedItems.length?'undo':'schedule'} size={11}/>출고요청
                                    </button>
                                  )}
                                  {shipInfo&&!isCancel&&!isReturn&&!isProcessed&&!shipInfo.fulfilled&&shipInfo.autoDefer&&!isIn&&(
                                    <button
                                      onClick={e=>{
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setDeferPopover({ rowId: item.id, currentDate: item.transaction_date, x: rect.left, y: rect.bottom + 4 });
                                        setDeferPopoverDate(item.transaction_date);
                                      }}
                                      title="자동 이월 중 — 클릭하여 특정 날짜로 이월"
                                      style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(245,124,0,0.15)',color:'#f57c00',display:'inline-flex',alignItems:'center',gap:4,border:'none',cursor:'pointer',fontFamily:'inherit'}}>
                                      <Icon name="schedule" size={13}/>자동 이월
                                    </button>
                                  )}
                                  {shipInfo&&!isCancel&&!isReturn&&!isProcessed&&shipInfo.fulfilled&&!isIn&&(
                                    <button onClick={e=>{e.stopPropagation(); toggleShipFulfilled(item, shipInfo);}} disabled={loading}
                                      title="클릭하여 출고요청으로 되돌리기 — 시리얼은 보관되고 재고만 복귀합니다"
                                      style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(0,152,68,0.15)',color:'var(--success)',border:'none',cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:3}}>
                                      <Icon name="check_circle" size={11}/>출고완료
                                    </button>
                                  )}
                                  {shipInfo&&shipInfo.rma&&shipInfo.repaired&&!isIn&&<span title={shipInfo.repairedAt?'수리 완료: '+new Date(shipInfo.repairedAt).toLocaleDateString('ko-KR'):''} style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(0,152,68,0.15)',color:'var(--success)',display:'inline-flex',alignItems:'center',gap:3}}><Icon name="build_circle" size={11}/>수리완료</span>}
                                  {isProcessed&&<span style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(0,0,0,0.06)',color:'#9e9e9e'}}>{processedAs==='cancel'?'취소됨':'환불됨'}</span>}
                                  {/* v7.259: [시리얼 N개 보관] 뱃지 제거 (사용자 요청) */}
                                  {/* v7.247: 부분 환불 — 출고 완료로 남아 있으므로 별도 뱃지로 알린다 */}
                                  {!isProcessed && shipInfo && refundedSetOf(shipInfo).size > 0 && (()=>{
                                    const done = refundedSetOf(shipInfo).size;
                                    const all = listFulfilledSerials(shipInfo).length;
                                    return (
                                      <span title={`환불 ${done}개 / 출고 ${all}개 — 나머지는 출고 상태 유지`}
                                        style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(123,31,162,0.1)',color:'var(--purple)',display:'inline-flex',alignItems:'center',gap:3}}>
                                        <Icon name="undo" size={11}/>부분환불 {done}/{all}
                                      </span>
                                    );
                                  })()}
                                  {isCancel&&!isProcessed&&<span style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(255,152,0,0.15)',color:'var(--warning)'}}>주문취소</span>}
                                  {isReturn&&!isProcessed&&<span style={{padding:'4px 9px',borderRadius:6,fontSize:11,fontWeight:700,background:'rgba(156,39,176,0.15)',color:'var(--purple)'}}>환불</span>}
                                </div>
                              </div>
                            </div>
                            {/* 수량 컬럼 — - 1 + 컨트롤 (단일/다중 모두) */}
                            <div onClick={e=>e.stopPropagation()} style={{padding:'14px 6px',display:'flex',flexDirection:'column',gap:6,alignItems:'center',justifyContent:Array.isArray(items)&&items.length>1?'flex-start':'center'}}>
                              {/* v7.431: 데모 건은 수량 스테퍼를 노출하지 않는다 (사용자 요청).
                                   수량 = demo_rental_items 의 장비 대수라, 배송시트지에서 숫자만 바꾸면
                                   대여 원본·demo_equipment 상태와 어긋난다. 수정은 [품목 수정](데모 편집 화면)에서.
                                   → 아래 else 분기의 읽기 전용 수량 표시로 떨어진다. */}
                              {false /* v7.448: 출고 스테퍼는 모델 블록으로 이동 */ && Array.isArray(items) && items.length > 0 && !isProcessed && shipInfo?.type === 'ship_request' && !isIn && !shipInfo?.fromDemoLend ? (
                                items.map((it, idx) => {
                                  const minQty = (it.fulfilledSerials?.length) || 1;
                                  const dec = async () => {
                                    if (it.qty <= minQty) { alert(minQty === 1 ? '수량은 1개 이상이어야 합니다.' : `이미 출고된 수량(${minQty})보다 작게 변경할 수 없습니다.`); return; }
                                    const newQty = it.qty - 1;
                                    // v7.196: 새 수량을 넘는 pending 시리얼은 함께 해제 (확인 후)
                                    const reqId = shipInfo?.reqId || item.serial;
                                    const allowed = Math.max(0, newQty - (it.fulfilledSerials?.length || 0));
                                    const excess = await collectExcessPending(reqId, it.product_name, allowed);
                                    if (excess.length > 0) {
                                      const msg = `[${it.product_name}] 수량 ${it.qty} → ${newQty}\n\n` +
                                        `등록해 둔 시리얼 ${excess.length}개도 함께 해제됩니다:\n` +
                                        excess.map(s => ' · ' + s).join('\n') +
                                        `\n\n계속하시겠습니까?`;
                                      if (!window.confirm(msg)) return;
                                    }
                                    await updateItemQty(item, it.product_name, newQty);
                                    if (excess.length > 0) await removePendingSerials(reqId, it.product_name, excess);
                                  };
                                  // v7.393: + 버튼은 더 이상 막지도, 확인 팝업도 띄우지 않는다.
                                  //   재고 초과는 모델명 아래 [재고부족] 지속 표기로 알린다 (재고없음과 동일한 방식).
                                  //   ⚠ 출고 [요청] 은 예약일 뿐이다. 실제 재고 차감/경합 검사는 시리얼 스캔(출고 처리) 단계에서 한다.
                                  //     여기서 막으면 [입고 예정이라 미리 요청해 두는] 정상 업무가 통째로 막힌다 (SESSION §3-2).
                                  const inc = async () => {
                                    await updateItemQty(item, it.product_name, it.qty + 1);
                                  };
                                  return (
                                    // v7.63: 모델명 박스와 동일한 minHeight 26 + alignItems center 로 일렬 정렬
                                    <div key={idx} style={{minHeight:26,display:'flex',alignItems:'center',justifyContent:'center',width:'100%'}}>
                                      <div style={{display:'inline-flex',alignItems:'center',gap:0,border:'1px solid var(--border-strong)',borderRadius:8,overflow:'hidden',background:'var(--bg-card)',height:26}}>
                                        <button onClick={dec} disabled={it.qty <= minQty} style={{width:24,height:24,border:'none',background:'transparent',cursor:it.qty<=minQty?'default':'pointer',color:it.qty<=minQty?'#ccc':'#00668a',display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontSize:15,fontWeight:700,borderRight:'1px solid var(--border)'}}>−</button>
                                        <span style={{minWidth:28,textAlign:'center',fontSize:12,fontWeight:700,color:'var(--fg-1)',padding:'0 4px'}}>{it.qty}</span>
                                        <button onClick={inc} style={{width:24,height:24,border:'none',background:'transparent',cursor:'pointer',color:'var(--accent)',display:'flex',alignItems:'center',justifyContent:'center',padding:0,fontSize:15,fontWeight:700,borderLeft:'1px solid var(--border)'}}>+</button>
                                      </div>
                                    </div>
                                  );
                                })
                              ) : ((isIn || shipInfo?.fromDemoLend) && Array.isArray(items) && items.length > 1) ? (
                                /* ━━━ v7.369: 입고 — 품목별 수량을 모델명 줄에 맞춰 표기 ━━━
                                   기존에는 합계 하나(×12)만 찍혀서, 품목이 둘 이상이면
                                   어느 제품이 몇 개인지 시리얼 칩을 세어 봐야 알 수 있었다.
                                   ⚠ 출고와 달리 - + 스테퍼를 달지 않는다. 입고 수량은 등록된
                                     시리얼과 묶여 있어서 숫자만 바꾸면 재고와 어긋난다.
                                     수량을 고치려면 시리얼을 추가/삭제하는 게 맞는 경로다.
                                   ⚠ minHeight 26 + gap 6 은 모델명 박스와 같은 값이어야 한다.
                                     한쪽만 바뀌면 줄이 어긋난다 (v7.63 과 같은 이유). */
                                <>
                                  {!(isIn && !shipInfo?.fromDemoLend) && items.map((it, idx) => {
                                    const sn = Array.isArray(it.serials) ? it.serials.length : 0;
                                    // 시리얼이 하나라도 등록됐는데 수량과 안 맞으면 알린다.
                                    //   ⚠ 0개일 때는 표시하지 않는다 — 아직 등록 전이거나
                                    //     시리얼이 없는 품목일 수 있어 오탐이 된다.
                                    const mismatch = sn > 0 && sn !== it.qty;
                                    return (
                                      <div key={idx} style={{minHeight:26,display:'flex',alignItems:'center',justifyContent:'center',width:'100%'}}>
                                        <span title={mismatch ? `수량 ${it.qty}개 · 등록된 시리얼 ${sn}개 — 개수가 맞지 않습니다` : `${it.product_name} · ${it.qty}개`}
                                          style={{fontSize:12.5,fontWeight:700,lineHeight:1,padding:'4px 8px',borderRadius:6,whiteSpace:'nowrap',
                                            color: isProcessed ? '#bbb' : mismatch ? 'var(--danger)' : 'var(--fg-2)',
                                            background: isProcessed ? 'transparent' : mismatch ? 'rgba(186,26,26,0.08)' : 'rgba(0,102,138,0.06)',
                                            border: `1px solid ${isProcessed ? 'transparent' : mismatch ? 'rgba(186,26,26,0.3)' : 'rgba(0,102,138,0.12)'}`}}>
                                          ×{it.qty}{mismatch && ' ⚠'}
                                        </span>
                                      </div>
                                    );
                                  })}
                                  {/* 합계 — 품목별로 쪼개면서 사라진 값이다. 작게라도 남긴다 */}
                                  <div style={{minHeight:16,display:'flex',alignItems:'center',justifyContent:'center',width:'100%'}}>
                                    <span style={{fontSize:10.5,fontWeight:600,color:isProcessed?'#ccc':'var(--fg-3)'}}>합 {totalQty || 1}</span>
                                  </div>
                                </>
                              ) : (!shipInfo?.fromDemoLend && Array.isArray(items) && (isIn || shipInfo?.type === 'ship_request')) ? (
                                null
                              ) : (
                                <span style={{fontSize:13,fontWeight:600,color:isProcessed?'#bbb':'var(--fg-2)'}}>×{totalQty || 1}</span>
                              )}
                            </div>
                            <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed||shipInfo?.rma||isIn) return; setInlineEditCell({id:item.id,field:'addr',value:shipInfo?.addr||''});}} style={{padding:'14px 10px',fontSize:12,color:isProcessed?'#bbb':'var(--fg-2)',lineHeight:1.5,wordBreak:'break-all',cursor:isProcessed||shipInfo?.rma||isIn?'default':'text',...(isIn?{gridColumn:'5 / span 4',display:'flex',alignItems:'flex-start',flexWrap:'wrap',gap:10,overflow:'hidden'}:{})}} title={isProcessed||shipInfo?.rma||isIn?'':'더블클릭하여 수정'}>
                              {isIn ? (
                                /* ━━━ 입고: 시리얼 가로 나열 + 토글 + 더블클릭 편집 ━━━ */
                                (() => {
                                  if (!Array.isArray(items) || items.length === 0) return <span style={{color:'#bbb'}}>-</span>;
                                  const expanded = expandedInbound.has(item.id);
                                  const SHOW_LIMIT = 5;
                                  const toggleExpand = (e) => {
                                    e.stopPropagation();
                                    setExpandedInbound(prev => {
                                      const n = new Set(prev);
                                      if (n.has(item.id)) n.delete(item.id); else n.add(item.id);
                                      return n;
                                    });
                                  };
                                  // 전체 시리얼 수 (각 품목 합계) — 토글 표시 여부 결정용
                                  const totalSerials = items.reduce((s, it) => s + (it.serials?.length || 0), 0);
                                  return (
                                    <>
                                      {items.map((it, pIdx) => {
                                        const sn = it.serials || [];
                                        const visible = expanded ? sn : sn.slice(0, SHOW_LIMIT);
                                        const hiddenCount = sn.length - visible.length;
                                        return (
                                          <div key={pIdx} style={{display:'flex',alignItems:'center',gap:5,flexWrap:'wrap'}}>
                                            <span style={{fontSize:10,color:'var(--fg-3)',fontWeight:700,letterSpacing:'0.02em',marginRight:2}}>{it.product_name} ({sn.length}):</span>
                                            {/* v7.419: HDD/SSD 입고 모델번호 표기 (note.items[].model_no) */}
                                            {it.model_no && <span title="모델번호" style={{fontSize:10,fontWeight:700,color:'var(--accent)',fontFamily:'monospace',background:'rgba(0,102,138,0.08)',padding:'1px 6px',borderRadius:5,marginRight:2}}>{it.model_no}</span>}
                                            {/* v7.421: 입고 출처(source) 인라인 편집 — 더블클릭 → 입력 (note.items[].from + inventory.source 동기) */}
                                            {(() => {
                                              const srcVal = ((it.from ?? shipInfo?.from) || '');
                                              if (!srcVal) return null;
                                              const isEditingSrc = editingInboundSource &&
                                                editingInboundSource.itemId === item.id &&
                                                editingInboundSource.product === it.product_name;
                                              if (isEditingSrc) {
                                                return (
                                                  <input key="src-edit" autoFocus defaultValue={srcVal}
                                                    onClick={e=>e.stopPropagation()}
                                                    onBlur={e=>updateInboundSource(item, it.product_name, e.target.value)}
                                                    onKeyDown={e=>{
                                                      if (e.key === 'Enter') { e.target.blur(); }
                                                      else if (e.key === 'Escape') { setEditingInboundSource(null); e.target.blur(); }
                                                    }}
                                                    style={{padding:'2px 6px',border:'2px solid var(--warning)',borderRadius:5,fontSize:10,minWidth:70,maxWidth:150,outline:'none',background:'var(--bg-card)'}}/>
                                                );
                                              }
                                              return (
                                                <span title={isProcessed?'':'더블클릭하여 출처 수정'}
                                                  onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed||inboundSourceSaving) return; setEditingInboundSource({itemId:item.id,product:it.product_name});}}
                                                  style={{fontSize:10,fontWeight:700,color:'#b26a00',background:'rgba(245,124,0,0.1)',border:'1px solid rgba(245,124,0,0.28)',padding:'1px 6px',borderRadius:5,marginRight:2,cursor:isProcessed?'default':'pointer',whiteSpace:'nowrap'}}>
                                                  출처: {SOURCE_BADGE[srcVal]?.label || srcVal}
                                                </span>
                                              );
                                            })()}
                                            {visible.map((s, si) => {
                                              const isEditing = editingInboundSerial &&
                                                editingInboundSerial.itemId === item.id &&
                                                editingInboundSerial.product === it.product_name &&
                                                editingInboundSerial.idx === si;
                                              if (isEditing) {
                                                return (
                                                  <input key={si} autoFocus defaultValue={s}
                                                    onClick={e=>e.stopPropagation()}
                                                    onBlur={e=>commitInboundSerialEdit(item, it.product_name, si, s, e.target.value)}
                                                    onKeyDown={e=>{
                                                      if (e.key === 'Enter') { e.target.blur(); }
                                                      else if (e.key === 'Escape') { setEditingInboundSerial(null); e.target.blur(); }
                                                    }}
                                                    style={{padding:'2px 6px',border:'2px solid var(--accent)',borderRadius:5,fontSize:11,fontFamily:'monospace',minWidth:80,maxWidth:160,outline:'none',background:'var(--bg-card)'}}/>
                                                );
                                              }
                                              return (
                                                <span key={si}
                                                  onDoubleClick={(e)=>{e.stopPropagation(); setEditingInboundSerial({itemId:item.id,product:it.product_name,idx:si});}}
                                                  title="더블클릭하여 시리얼 수정 · ❌로 삭제"
                                                  style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 4px 2px 8px',borderRadius:5,background:'rgba(0,152,68,0.08)',border:'1px solid rgba(0,152,68,0.25)',fontSize:11,fontFamily:'monospace',fontWeight:600,color:'var(--success)',cursor:'pointer',whiteSpace:'nowrap'}}>
                                                  {s}
                                                  <button onClick={(e)=>{e.stopPropagation(); removeInboundSerial(item, it.product_name, s);}}
                                                    title="시리얼 삭제"
                                                    style={{width:14,height:14,border:'none',background:'transparent',borderRadius:3,cursor:'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',color:'var(--danger)',padding:0,opacity:0.55,marginLeft:1}}
                                                    onMouseEnter={e=>e.currentTarget.style.opacity='1'}
                                                    onMouseLeave={e=>e.currentTarget.style.opacity='0.55'}>
                                                    <Icon name="close" size={10}/>
                                                  </button>
                                                </span>
                                              );
                                            })}
                                            {hiddenCount > 0 && !expanded && (
                                              <button onClick={toggleExpand} title="모두 보기"
                                                style={{padding:'2px 8px',borderRadius:5,background:'rgba(0,102,138,0.08)',border:'1px dashed var(--accent)',fontSize:10,fontWeight:700,color:'var(--accent)',cursor:'pointer',whiteSpace:'nowrap'}}>
                                                +{hiddenCount}개
                                              </button>
                                            )}
                                            {/* v7.15: 시리얼 추가 버튼 — 마지막 시리얼 또는 그룹 끝에 표시 (펼친 상태에서만) */}
                                            {(expanded || sn.length <= SHOW_LIMIT) && (
                                              <button onClick={(e)=>{e.stopPropagation(); addInboundSerial(item, it.product_name);}}
                                                title="이 제품 시리얼 추가"
                                                style={{padding:'2px 7px',borderRadius:5,background:'rgba(0,102,138,0.04)',border:'1px dashed var(--accent)',fontSize:10,fontWeight:700,color:'var(--accent)',cursor:'pointer',whiteSpace:'nowrap',display:'inline-flex',alignItems:'center',gap:3}}>
                                                <Icon name="add" size={11}/> 시리얼
                                              </button>
                                            )}
                                          </div>
                                        );
                                      })}
                                      {/* 전체 토글 버튼 — 시리얼 6개 이상일 때만 노출 */}
                                      {totalSerials > SHOW_LIMIT && (
                                        <button onClick={toggleExpand} title={expanded?'부분만 보기':'모두 보기'}
                                          style={{padding:'2px 8px',borderRadius:5,background:'transparent',border:'1px solid var(--border-strong)',fontSize:10,fontWeight:600,color:'var(--fg-2)',cursor:'pointer',display:'inline-flex',alignItems:'center',gap:3,marginLeft:'auto'}}>
                                          <Icon name={expanded?'unfold_less':'unfold_more'} size={12}/>
                                          {expanded?'접기':'전체보기'}
                                        </button>
                                      )}
                                    </>
                                  );
                                })()
                              ) : shipInfo&&shipInfo.rma ? (
                                <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'flex-start'}}>
                                  <span style={{display:'inline-flex',alignItems:'center',gap:4,padding:'4px 10px',background:isProcessed?'#e5e7eb':'var(--warning-solid)',color:isProcessed?'#9ca3af':'#fff',borderRadius:6,fontSize:12,fontWeight:700}}>
                                    <Icon name="build" size={12}/> {shipInfo.emsType || 'RMA'}
                                  </span>
                                  {shipInfo.rmaTicketNo && <span style={{fontSize:11,color:isProcessed?'#bbb':'var(--fg-3)',fontFamily:'monospace'}}>티켓: {shipInfo.rmaTicketNo}</span>}
                                  {/* v7.76+7.77: DOA 반품 시리얼 표시 + 클릭 편집 (수리완료 전까지 수정 가능) */}
                                  {shipInfo.emsType === 'DOA' && !shipInfo.repaired && (
                                    inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='rmaReturnSerial' ? (
                                      <input autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                        onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                        onBlur={()=>{ const v=inlineEditCell.value.trim(); if(v!==(shipInfo.rmaReturnSerial||'')) saveShipFieldInline(item,'rmaReturnSerial',v); setInlineEditCell(null); }}
                                        onKeyDown={e=>{ if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} else if(e.key==='Enter'){e.preventDefault();e.target.blur();} }}
                                        placeholder="반품 시리얼 입력"
                                        style={{width:180,padding:'3px 7px',border:'2px solid var(--success)',borderRadius:5,fontSize:11,fontFamily:'monospace',outline:'none',background:'var(--bg-card)'}}/>
                                    ) : shipInfo.rmaReturnSerial ? (
                                      <span onClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'rmaReturnSerial',value:shipInfo.rmaReturnSerial||''});}}
                                        title={isProcessed?'':'클릭하여 반품 시리얼 수정'}
                                        style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',background:'rgba(0,152,68,0.12)',color:'var(--success)',borderRadius:5,fontSize:10,fontWeight:700,fontFamily:'monospace',cursor:isProcessed?'default':'pointer',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                        <Icon name="input" size={10}/> 반품: {shipInfo.rmaReturnSerial}
                                      </span>
                                    ) : (
                                      <span onClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'rmaReturnSerial',value:''});}}
                                        title={isProcessed?'':'클릭하여 반품 시리얼 입력'}
                                        style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',border:'1px dashed var(--success)',color:'var(--success)',borderRadius:5,fontSize:10,fontWeight:700,cursor:isProcessed?'default':'pointer'}}>
                                        <Icon name="add" size={10}/> 반품 시리얼 입력
                                      </span>
                                    )
                                  )}
                                  {/* v7.20: EMS는 발송종류 셀로 이동됨 */}
                                  {/* v7.448: RMA 건도 파일 첨부 가능 (사용자 요청) */}
                                  <AttachmentButton item={item} onUpdated={loadTxLog} disabled={isProcessed}/>
                                </div>
                              ) : inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='addr' ? (
                                <textarea autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                  onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                  onBlur={()=>{ if(inlineEditCell.value!==(shipInfo?.addr||'')) saveShipFieldInline(item,'addr',inlineEditCell.value); setInlineEditCell(null); }}
                                  onKeyDown={e=>{ if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} else if(e.key==='Enter'&&e.shiftKey){e.preventDefault();e.target.blur();} }}
                                  rows={2}
                                  style={{width:'100%',padding:'6px 8px',border:'2px solid var(--accent)',borderRadius:6,fontSize:12,fontFamily:'inherit',resize:'vertical',outline:'none',background:'var(--bg-card)'}}/>
                              ) : (
                                <div style={{display:'flex',flexDirection:'column',gap:4}}>
                                  {/* v7.47: 직발 표시/추가 + 엑셀 다운로드 — 같은 라인 (row flex) */}
                                  <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                  {shipInfo&&shipInfo.direct && (
                                    inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='directCompany' ? (
                                      <input autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                        onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                        onBlur={()=>{ const v=inlineEditCell.value.trim(); if(v!==(shipInfo.directCompany||'')) saveShipFieldInline(item,'directCompany',v); setInlineEditCell(null); }}
                                        onKeyDown={e=>{ if(e.key==='Enter')e.target.blur(); else if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} }}
                                        placeholder="업체명"
                                        style={{padding:'4px 8px',border:'2px solid var(--danger)',borderRadius:5,fontSize:11,fontWeight:700,outline:'none',background:'var(--bg-card)',alignSelf:'flex-start',maxWidth:'100%'}}/>
                                    ) : (
                                      <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'directCompany',value:shipInfo.directCompany||''});}} title={isProcessed?'':'더블클릭하여 업체명 수정 (빈 값 저장 시 직발 해제)'} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'3px 8px',background:isProcessed?'#e5e7eb':'var(--danger-solid)',color:isProcessed?'#9ca3af':'#fff',borderRadius:5,fontSize:11,fontWeight:700,maxWidth:'100%',cursor:isProcessed?'default':'pointer'}}>
                                        <Icon name="rocket_launch" size={11}/> {shipInfo.directCompany || '(업체명 미입력)'} 직발
                                      </span>
                                    )
                                  )}
                                  {/* 직발 추가 버튼 — 택배/퀵이고 direct 아닐 때 */}
                                  {shipInfo && !shipInfo.direct && (shipInfo.method==='택배'||shipInfo.method==='택배배송'||isQuickMethod(shipInfo.method)) && !isProcessed && (
                                    inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='directCompany' ? (
                                      <input autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                        onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                        onBlur={()=>{ const v=inlineEditCell.value.trim(); if(v) saveShipFieldInline(item,'directCompany',v); setInlineEditCell(null); }}
                                        onKeyDown={e=>{ if(e.key==='Enter')e.target.blur(); else if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} }}
                                        placeholder="직발 업체명 입력"
                                        style={{padding:'4px 8px',border:'2px solid var(--danger)',borderRadius:5,fontSize:11,fontWeight:700,outline:'none',background:'var(--bg-card)',alignSelf:'flex-start',maxWidth:'100%'}}/>
                                    ) : (
                                      <span onDoubleClick={(e)=>{e.stopPropagation(); setInlineEditCell({id:item.id,field:'directCompany',value:''});}} title="더블클릭하여 직발 추가" style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',border:'1px dashed var(--danger)',color:'var(--danger)',borderRadius:5,fontSize:10,fontWeight:600,cursor:'pointer',opacity:0.6}}>
                                        <Icon name="rocket_launch" size={10}/> + 직발 추가
                                      </span>
                                    )
                                  )}
                                  {/* v7.107: 네이버스토어 뱃지 — 직발과 동일 스타일 (배경 초록, 흰 N 아이콘 + 라벨) */}
                                  {shipInfo && shipInfo.naverStore && (
                                    <span title={isProcessed?'':'네이버스토어 주문'}
                                      style={{display:'inline-flex',alignItems:'center',gap:4,padding:'3px 8px',background:isProcessed?'#e5e7eb':'#03C75A',color:isProcessed?'#9ca3af':'#fff',borderRadius:5,fontSize:11,fontWeight:700,maxWidth:'100%'}}>
                                      <span style={{display:'inline-flex',alignItems:'center',justifyContent:'center',width:13,height:13,borderRadius:2,background:isProcessed?'#f9fafb':'var(--bg-card)',color:isProcessed?'#9ca3af':'#03C75A',fontSize:10,fontWeight:900,lineHeight:1}}><span style={{display:'inline-block',transform:'translate(0.5px, 0.5px)'}}>N</span></span>
                                      네이버스토어
                                    </span>
                                  )}
                                  {/* v7.47: 엑셀 다운로드 — 직발 우측 (ship_request 카드 모두에 표시) */}
                                  {shipInfo && shipInfo.type==='ship_request' && (
                                    <button onClick={(e)=>{e.stopPropagation(); downloadShippingExcel(item);}}
                                      title="택배사 양식 엑셀 다운로드"
                                      style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',background:'var(--bg-card)',color:'#1d6f42',border:'1px solid #1d6f42',borderRadius:5,fontSize:10,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}
                                      onMouseEnter={e=>e.currentTarget.style.background='rgba(29,111,66,0.08)'}
                                      onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                                      <Icon name="grid_on" size={10}/> 엑셀
                                    </button>
                                  )}
                                  </div>
                                  <span>{shipInfo&&shipInfo.addr ? shipInfo.addr : <span style={{color:'#bbb'}}>더블클릭하여 입력</span>}</span>
                                  {/* v7.178: 작업 사진 ZIP 첨부 (주소 아래) */}
                                  <AttachmentButton item={item} onUpdated={loadTxLog} disabled={isCancel}/>
                                </div>
                              )}
                            </div>
                            <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed||isIn) return; setInlineEditCell({id:item.id,field:'salesStatus',value:shipInfo?.salesStatus||'입력필요'});}} style={{padding:'18px 10px',display:isIn?'none':'flex',flexDirection:'column',gap:4,alignItems:'flex-start',cursor:isProcessed||isIn?'default':'pointer'}} title={isProcessed||isIn?'':'더블클릭하여 수정'}>
                              {isIn ? (
                                <span style={{color:'#bbb',fontSize:13}}>-</span>
                              ) : inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='salesStatus' ? (
                                <div onClick={e=>e.stopPropagation()} style={{display:'flex',flexDirection:'column',gap:4,width:'100%'}}>
                                  <select autoFocus value={inlineEditCell.value}
                                    onChange={async e=>{
                                      const v = e.target.value;
                                      setInlineEditCell({...inlineEditCell,value:v});
                                      // race condition 방지: salesStatus 저장 완료 후 manual 정리 (각 호출이 latest fetch + update 하기 때문에 동시 호출 시 옛 값으로 덮어쓰기 발생)
                                      await saveShipFieldInline(item,'salesStatus',v);
                                      if (v !== '수기입력') {
                                        await saveShipFieldInline(item,'salesStatusManual','');
                                        setTimeout(()=>setInlineEditCell(null),100);
                                      }
                                    }}
                                    style={{padding:'4px 8px',border:'2px solid var(--accent)',borderRadius:5,fontSize:11,fontWeight:700,outline:'none',background:'var(--bg-card)',cursor:'pointer'}}>
                                    <option value="입력필요">입력필요</option>
                                    <option value="입력완료">입력완료</option>
                                    <option value="수기입력">수기입력</option>
                                  </select>
                                  {inlineEditCell.value === '수기입력' && (
                                    <input autoFocus={false} defaultValue={shipInfo?.salesStatusManual||''}
                                      placeholder="수기 입력 내용"
                                      onBlur={(e)=>{ saveShipFieldInline(item,'salesStatusManual',e.target.value.trim()); setInlineEditCell(null); }}
                                      onKeyDown={e=>{ if(e.key==='Enter'){ saveShipFieldInline(item,'salesStatusManual',e.target.value.trim()); setInlineEditCell(null); } else if(e.key==='Escape'){ setInlineEditCell(null); } }}
                                      style={{padding:'4px 8px',border:'1.5px solid #f57c00',borderRadius:5,fontSize:11,outline:'none',background:'#fff8e1',width:'100%'}}/>
                                  )}
                                </div>
                              ) : (() => {
                                const s = shipInfo?.salesStatus;
                                if (!s) return <span style={{color:'#bbb'}}>-</span>;
                                const isDone = s === '입력완료';
                                const isManual = s === '수기입력';
                                const bg = isManual ? 'rgba(245,124,0,0.15)' : (isDone ? 'rgba(0,152,68,0.12)' : 'rgba(186,26,26,0.12)');
                                const color = isManual ? 'var(--warning)' : (isDone ? 'var(--success)' : 'var(--danger)');
                                const label = isManual && shipInfo?.salesStatusManual ? `${shipInfo.salesStatusManual}` : s;
                                return <span title={isManual?shipInfo?.salesStatusManual:''} style={{padding:'3px 9px',borderRadius:6,fontSize:11,fontWeight:800,background:bg,color,maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',display:'inline-block'}}>{label}</span>;
                              })()}
                              {shipInfo&&isQuickMethod(shipInfo.method)&&(
                                inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='quickSalesStatus' ? (
                                  <div onClick={e=>e.stopPropagation()} style={{display:'flex',flexDirection:'column',gap:4,width:'100%'}}>
                                    <select autoFocus value={inlineEditCell.value}
                                      onChange={async e=>{
                                        const v = e.target.value;
                                        setInlineEditCell({...inlineEditCell,value:v});
                                        // race condition 방지: await로 순차 처리
                                        await saveShipFieldInline(item,'quickSalesStatus',v);
                                        if (v !== '수기입력') {
                                          await saveShipFieldInline(item,'quickSalesStatusManual','');
                                          setTimeout(()=>setInlineEditCell(null),100);
                                        }
                                      }}
                                      style={{padding:'4px 8px',border:'2px solid var(--danger)',borderRadius:5,fontSize:11,fontWeight:700,outline:'none',background:'var(--bg-card)',cursor:'pointer'}}>
                                      <option value="입력필요">입력필요</option>
                                      <option value="입력완료">입력완료</option>
                                      <option value="수기입력">수기입력</option>
                                    </select>
                                    {inlineEditCell.value === '수기입력' && (
                                      <input defaultValue={shipInfo?.quickSalesStatusManual||''}
                                        placeholder="퀵 수기 입력 내용"
                                        onBlur={(e)=>{ saveShipFieldInline(item,'quickSalesStatusManual',e.target.value.trim()); setInlineEditCell(null); }}
                                        onKeyDown={e=>{ if(e.key==='Enter'){ saveShipFieldInline(item,'quickSalesStatusManual',e.target.value.trim()); setInlineEditCell(null); } else if(e.key==='Escape'){ setInlineEditCell(null); } }}
                                        style={{padding:'4px 8px',border:'1.5px solid #f57c00',borderRadius:5,fontSize:11,outline:'none',background:'#fff8e1',width:'100%'}}/>
                                    )}
                                  </div>
                                ) : (() => {
                                  const qs = shipInfo.quickSalesStatus || '입력필요';
                                  const isDone = qs === '입력완료';
                                  const isManual = qs === '수기입력';
                                  const bg = isManual ? 'rgba(245,124,0,0.15)' : (isDone ? 'rgba(0,152,68,0.12)' : 'rgba(186,26,26,0.12)');
                                  const color = isManual ? 'var(--warning)' : (isDone ? 'var(--success)' : 'var(--danger)');
                                  const label = isManual && shipInfo?.quickSalesStatusManual ? `${shipInfo.quickSalesStatusManual}` : `퀵 ${qs}`;
                                  return <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'quickSalesStatus',value:qs});}} title={isManual?shipInfo?.quickSalesStatusManual:'더블클릭하여 수정'} style={{padding:'3px 9px',borderRadius:6,fontSize:11,fontWeight:800,background:bg,color,display:'inline-flex',alignItems:'center',gap:3,cursor:isProcessed?'default':'pointer',maxWidth:140,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}><Icon name="electric_moped" size={11}/>{label}</span>;
                                })()
                              )}
                            </div>
                            {/* 발송 종류 / 송장번호·금액 — 인라인 편집 가능 */}
                            <div style={{padding:'18px 10px',overflow:'hidden',display:isIn?'none':'block'}} onClick={e=>e.stopPropagation()}>
                              {isIn ? (
                                <span style={{color:'#bbb',fontSize:13}}>-</span>
                              ) : inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='method' ? (
                                <select autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                  onChange={e=>{ saveShipFieldInline(item,'method',e.target.value); setInlineEditCell(null); }}
                                  onBlur={()=>setInlineEditCell(null)}
                                  style={{width:'100%',padding:'6px 8px',border:'2px solid var(--accent)',borderRadius:6,fontSize:12,fontWeight:600,outline:'none',background:'var(--bg-card)',cursor:'pointer'}}>
                                  <option value="택배">택배</option>
                                  <option value="퀵서비스">퀵서비스</option>
                                  <option value="방문수령">방문수령</option>
                                  <option value="1층배송">1층배송</option>
                                  <option value="RMA">RMA</option>
                                  <option value="DOA">DOA</option>
                                </select>
                              ) : (() => {
                                if (!shipInfo) return <span style={{color:'#bbb'}}>-</span>;
                                const m = shipInfo.method || '';
                                const isCourier = m === '택배' || m === '택배배송';
                                const isQuick = isQuickMethod(m);   // v7.242
                                const isPickup = m === '방문수령' || shipInfo.pickup;
                                const isDirect = m === '직발' || shipInfo.direct;
                                const isFloor = m === '1층배송';
                                const isRma = m === 'RMA' || m === 'DOA' || shipInfo.rma;
                                if (isCourier) {
                                  const draftVal = logTrackingDrafts[item.id];
                                  const savedVal = shipInfo.trackingNo || '';
                                  const currentVal = draftVal !== undefined ? draftVal : savedVal;
                                  const dirty = draftVal !== undefined && draftVal !== savedVal;
                                  const trackList = currentVal ? currentVal.split(',').map(s=>s.trim()) : [''];
                                  const updateOne = (idx, v) => {
                                    const next = [...trackList]; next[idx] = v.replace(/[^0-9-]/g,'');
                                    setLogTrackingDrafts(prev=>({...prev, [item.id]: next.join(',')}));
                                  };
                                  const addOne = () => setLogTrackingDrafts(prev=>({...prev, [item.id]: (currentVal?currentVal+',':'') + ''}));
                                  const removeOne = (idx) => {
                                    const v = trackList[idx];
                                    if (v && v.trim() && !window.confirm(`송장번호 "${v}"를 삭제하시겠습니까?`)) return;
                                    const next = [...trackList]; next.splice(idx,1);
                                    const newV = next.join(',');
                                    setLogTrackingDrafts(prev=>({...prev, [item.id]: newV}));
                                    saveShipFieldInline(item, 'trackingNo', newV);
                                  };
                                  const commit = () => { if (dirty) saveShipFieldInline(item, 'trackingNo', currentVal.split(',').map(s=>s.trim()).filter(Boolean).join(',')); };
                                  return (
                                    <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:0}}>
                                      <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:shipInfo.method||'택배'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,fontWeight:700,color:isProcessed?'#bbb':'var(--accent)',cursor:isProcessed?'default':'pointer'}}>
                                        <Icon name="local_post_office" size={13}/> 택배
                                      </div>
                                      {trackList.map((v,idx)=>(
                                        <div key={idx} style={{display:'flex',gap:2,alignItems:'center'}}>
                                          <input
                                            type="text"
                                            value={v}
                                            disabled={isProcessed}
                                            placeholder={idx===0?'송장번호':'+'+(idx+1)}
                                            onChange={e=>updateOne(idx, e.target.value)}
                                            onBlur={(e)=>{ e.target.style.borderColor=dirty?'#40c2fd':'#cbd5e1'; e.target.style.background=dirty?'var(--bg-accent-soft)':'var(--bg-card)'; commit(); }}
                                            onKeyDown={e=>{ if(e.key==='Enter') e.target.blur(); if(e.key==='Escape'){ setLogTrackingDrafts(prev=>{const n={...prev}; delete n[item.id]; return n;}); e.target.blur(); } }}
                                            onFocus={e=>{e.target.style.borderColor='#40c2fd'; e.target.style.background='var(--bg-accent-soft)';}}
                                            style={{flex:1,minWidth:0,padding:'3px 6px',border:`1px solid ${dirty?'#40c2fd':'#cbd5e1'}`,borderRadius:5,fontSize:11,fontFamily:'monospace',background:dirty?'var(--bg-accent-soft)':'var(--bg-card)',outline:'none',color:isProcessed?'#bbb':'var(--fg-1)'}}
                                          />
                                          {!isProcessed && trackList.length > 1 && (
                                            <button onClick={()=>removeOne(idx)} title="제거" style={{width:18,height:18,border:'none',background:'transparent',borderRadius:4,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--danger)',flexShrink:0,padding:0}}>
                                              <Icon name="close" size={10}/>
                                            </button>
                                          )}
                                        </div>
                                      ))}
                                      {!isProcessed && (
                                        <button onClick={addOne} title="송장번호 추가" style={{alignSelf:'flex-start',display:'inline-flex',alignItems:'center',gap:2,padding:'2px 6px',border:'1px dashed var(--accent)',background:'transparent',color:'var(--accent)',borderRadius:4,fontSize:10,fontWeight:600,cursor:'pointer'}}>
                                          <Icon name="add" size={10}/> 추가
                                        </button>
                                      )}
                                    </div>
                                  );
                                }
                                if (isQuick) {
                                  const draftVal = logPriceDrafts[item.id];
                                  const currentVal = draftVal !== undefined ? draftVal : (shipInfo.quickPrice ? String(shipInfo.quickPrice) : '');
                                  const dirty = draftVal !== undefined && Number(draftVal||0) !== Number(shipInfo.quickPrice||0);
                                  const displayFormatted = currentVal ? Number(currentVal).toLocaleString() : '';
                                  return (
                                    <div style={{display:'flex',flexDirection:'column',gap:3,minWidth:0}}>
                                      {/* v7.230: 시트 연동 버튼을 퀵 표시 위로 이동 (배송지 셀 → 발송 종류 셀) */}
                                      <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:1}}>
                                        {/* v7.240: 전송 완료(갱신됨)는 초록 — 미전송(빨강)과 한눈에 구분된다 */}
                                        <button onClick={(e)=>{e.stopPropagation(); sendQuickToSheet(item);}}
                                          disabled={quickSheetSending===item.id}
                                          title={shipInfo.quickSheetSentAt
                                            ? `2차 전송 완료 · ${new Date(shipInfo.quickSheetSentAt).toLocaleString('ko-KR')} (다시 누르면 재갱신)`
                                            : '시트에 이 건을 기록합니다 (1차). 이때의 시각이 배차시간으로 확정됩니다.'}
                                          style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',
                                            background: shipInfo.quickSheetSentAt ? 'var(--success-solid)' : 'var(--bg-card)',
                                            color: shipInfo.quickSheetSentAt ? '#fff' : 'var(--danger)',
                                            border:`1px solid ${shipInfo.quickSheetSentAt ? 'var(--success)' : 'var(--danger)'}`,borderRadius:5,fontSize:10,fontWeight:700,
                                            cursor: quickSheetSending===item.id ? 'default':'pointer',whiteSpace:'nowrap',
                                            opacity: quickSheetSending===item.id ? 0.5 : 1}}>
                                          <Icon name={shipInfo.quickSheetSentAt ? 'task_alt' : 'upload_file'} size={10}/>
                                          {quickSheetSending===item.id ? '전송 중…' : shipInfo.quickSheetSentAt ? '갱신됨' : '시트 전송'}
                                        </button>
                                        {/* v7.229: 배송 완료 처리 — 시트 H(진행상황)·K(완료시간) */}
                                        <button onClick={(e)=>{e.stopPropagation(); toggleQuickComplete(item);}}
                                          disabled={quickSheetSending===item.id}
                                          title={shipInfo.quickCompletedAt
                                            ? `완료 ${shipInfo.quickCompletedAt} · 클릭하면 해제`
                                            : '배송 완료 처리 — 시트 진행상황을 완료로 바꾸고 완료시간을 기록합니다'}
                                          style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',
                                            background: shipInfo.quickCompletedAt ? 'var(--success-solid)' : 'var(--bg-card)',
                                            color: shipInfo.quickCompletedAt ? '#fff' : 'var(--success)',
                                            border:'1px solid var(--success)',borderRadius:5,fontSize:10,fontWeight:700,
                                            cursor: quickSheetSending===item.id ? 'default':'pointer',whiteSpace:'nowrap',
                                            opacity: quickSheetSending===item.id ? 0.5 : 1}}>
                                          <Icon name={shipInfo.quickCompletedAt ? 'check_circle' : 'schedule'} size={10}/>
                                          {shipInfo.quickCompletedAt ? '배송완료' : '완료 처리'}
                                        </button>
                                      </div>
                                      <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:shipInfo.method||'퀵서비스'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,fontWeight:700,color:isProcessed?'#bbb':'var(--danger)',cursor:isProcessed?'default':'pointer'}}>
                                        <Icon name="electric_moped" size={13}/> 퀵
                                        {/* v7.54: 착불 배지 — 클릭 토글 */}
                                        <span
                                          onClick={(e)=>{e.stopPropagation(); if(isProcessed) return; saveShipFieldInline(item, 'quickCOD', !shipInfo.quickCOD);}}
                                          title={isProcessed?'':(shipInfo.quickCOD?'클릭하여 착불 해제':'클릭하여 착불 설정')}
                                          style={{padding:'1px 6px',marginLeft:2,borderRadius:4,fontSize:9,fontWeight:700,letterSpacing:0,background:shipInfo.quickCOD?'var(--danger-solid)':'transparent',color:shipInfo.quickCOD?'#fff':'var(--danger)',border:`1px ${shipInfo.quickCOD?'solid':'dashed'} var(--danger)`,cursor:isProcessed?'default':'pointer',opacity:isProcessed?0.4:1}}>
                                          {shipInfo.quickCOD ? '착불' : '+착불'}
                                        </span>
                                      </div>
                                      <div style={{position:'relative',display:'flex',alignItems:'center'}}>
                                        <input
                                          type="text"
                                          inputMode="numeric"
                                          value={displayFormatted}
                                          disabled={isProcessed}
                                          placeholder="금액"
                                          onChange={e=>{
                                            const v = e.target.value.replace(/[^0-9]/g,'');
                                            setLogPriceDrafts(prev=>({...prev, [item.id]: v}));
                                          }}
                                          onBlur={(e)=>{ e.target.style.borderColor=dirty?'#ff6e6e':'#cbd5e1'; e.target.style.background=dirty?'#fff0f0':'var(--bg-card)'; if (dirty) saveShipFieldInline(item, 'quickPrice', currentVal); }}
                                          onKeyDown={e=>{
                                            if (e.key === 'Enter') { e.target.blur(); }
                                            if (e.key === 'Escape') {
                                              setLogPriceDrafts(prev=>{ const n={...prev}; delete n[item.id]; return n; });
                                              e.target.blur();
                                            }
                                          }}
                                          onFocus={e=>{e.target.style.borderColor='#ff6e6e'; e.target.style.background='#fff0f0';}}
                                          style={{flex:1,minWidth:0,padding:'4px 22px 4px 8px',border:`1px solid ${dirty?'#ff6e6e':'#cbd5e1'}`,borderRadius:5,fontSize:11,fontFamily:'monospace',fontWeight:700,background:dirty?'#fff0f0':'var(--bg-card)',outline:'none',color:isProcessed?'#bbb':'var(--danger)',transition:'border-color 0.12s, background 0.12s'}}
                                        />
                                        <span style={{position:'absolute',right:6,fontSize:10,color:'var(--fg-3)',pointerEvents:'none'}}>원</span>
                                      </div>
                                      {/* v7.223: 차량 · 기사님 번호 — 배차 후 여기서 더블클릭 편집 (시트 E·I열)
                                          v7.231: 시트 전용 비고(J열) 추가 + 세 칸 크기 확대 */}
                                      {[
                                        ['quickVehicle', 'local_shipping', '차량', shipInfo.quickVehicle],
                                        ['quickDriverPhone', 'support_agent', '기사님', shipInfo.quickDriverPhone],
                                        // v7.232: 비고 미입력이면 수령인(업체명)이 시트에 들어감 → 그 값을 미리 보여준다
                                        ['quickRemark', 'sticky_note_2', '비고', shipInfo.quickRemark, shipInfo.recipient || item.dealer_name || ''],
                                      ].map(([field, icon, label, val, fallback]) => (
                                        inlineEditCell && inlineEditCell.id===item.id && inlineEditCell.field===field ? (
                                          <input key={field} autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                            onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                            onBlur={()=>{ const v=inlineEditCell.value.trim(); if(v!==(val||'')) saveShipFieldInline(item,field,v); setInlineEditCell(null); }}
                                            onKeyDown={e=>{
                                              if(e.key==='Enter') e.target.blur();
                                              if(e.key==='Escape'){ setInlineEditCell(null); }
                                            }}
                                            list={field==='quickVehicle'?'quickVehicleOptions':undefined}
                                            placeholder={field==='quickVehicle'?'오토바이 / 다마스 …':field==='quickDriverPhone'?'010-0000-0000':'퀵 시트 비고'}
                                            style={{width:'100%',padding:'6px 9px',border:'1.5px solid var(--danger)',borderRadius:6,fontSize:12.5,fontWeight:600,fontFamily:field==='quickDriverPhone'?'monospace':'inherit',outline:'none',color:'var(--danger)',boxSizing:'border-box'}}/>
                                        ) : (
                                          <div key={field}
                                            onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field,value:val||''});}}
                                            title={isProcessed?'':`더블클릭하여 ${label} 입력`}
                                            style={{display:'flex',alignItems:'center',gap:5,padding:'3px 5px',borderRadius:5,fontSize:12,fontWeight:(val||fallback)?600:400,color:isProcessed?'#bbb':((val||fallback)?'var(--fg-1)':'#a0a3ab'),background:val?'rgba(186,26,26,0.05)':'transparent',cursor:isProcessed?'default':'pointer',minWidth:0}}>
                                            <Icon name={icon} size={13} style={{flexShrink:0,opacity:0.75,color:val?'var(--danger)':'inherit'}}/>
                                            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',fontFamily:field==='quickDriverPhone'&&val?'monospace':'inherit'}}>
                                              {val || (fallback ? fallback : `${label} 입력…`)}
                                            </span>
                                            {!val && fallback && (
                                              <span style={{flexShrink:0,fontSize:10,color:'var(--warning)',fontWeight:700}}>자동</span>
                                            )}
                                          </div>
                                        )
                                      ))}
                                    </div>
                                  );
                                }
                                if (isPickup) {
                                  return (
                                    <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:'방문수령'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,fontWeight:700,color:isProcessed?'#bbb':'var(--accent)',cursor:isProcessed?'default':'pointer'}}>
                                      <Icon name="storefront" size={13}/> 방문수령
                                    </div>
                                  );
                                }
                                if (isFloor) {
                                  return (
                                    <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:'1층배송'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,fontWeight:700,color:isProcessed?'#bbb':'var(--success)',cursor:isProcessed?'default':'pointer'}}>
                                      <Icon name="warehouse" size={13}/> 1층배송
                                    </div>
                                  );
                                }
                                if (isRma) {
                                  return (
                                    <div style={{display:'flex',flexDirection:'column',gap:3,alignItems:'flex-start'}}>
                                      <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:shipInfo.emsType||'RMA'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{display:'flex',alignItems:'center',gap:4,fontSize:12,fontWeight:700,color:isProcessed?'#bbb':'var(--warning)',cursor:isProcessed?'default':'pointer'}}>
                                        <Icon name="build" size={13}/> {shipInfo.emsType || m || 'RMA'}
                                      </div>
                                      {/* v7.20: EMS 송장번호를 발송종류 셀 아래로 (이전: 배송지 셀에 표시) */}
                                      {shipInfo.rmaEms && (
                                        <span style={{fontSize:11,color:isProcessed?'#bbb':'var(--fg-3)',fontFamily:'monospace'}}>EMS: {shipInfo.rmaEms}</span>
                                      )}
                                    </div>
                                  );
                                }
                                return <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'method',value:'택배'});}} title={isProcessed?'':'더블클릭하여 발송종류 변경'} style={{fontSize:12,color:'var(--fg-2)',cursor:isProcessed?'default':'pointer'}}>{m || '더블클릭'}</span>;
                              })()}
                            </div>
                            <div style={{padding:'18px 10px',overflow:'hidden',display:isIn?'none':'block'}}
                              title={shipInfo ? [shipInfo.recipient, shipInfo.phone].filter(Boolean).join(' · ') : (item.dealer_name||'')}>
                              {isIn ? (
                                <span style={{color:'#bbb',fontSize:13}}>-</span>
                              ) : shipInfo ? (
                                <>
                                  {inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='recipient' ? (
                                    <input autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                      onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                      onBlur={()=>{ if(inlineEditCell.value!==(shipInfo.recipient||'')) saveShipFieldInline(item,'recipient',inlineEditCell.value); setInlineEditCell(null); }}
                                      onKeyDown={e=>{ if(e.key==='Enter')e.target.blur(); else if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} }}
                                      style={{width:'100%',padding:'4px 8px',border:'2px solid var(--accent)',borderRadius:5,fontSize:13,fontWeight:600,outline:'none',background:'var(--bg-card)'}}/>
                                  ) : (
                                    <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'recipient',value:shipInfo.recipient||''});}} title={isProcessed?'':'더블클릭하여 수정'} style={{fontSize:13,fontWeight:600,color:isProcessed?'#bbb':'var(--fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:isProcessed?'default':'text'}}>
                                      {shipInfo.recipient || item.dealer_name ? highlightMatch(shipInfo.recipient || item.dealer_name, logSearch) : <span style={{color:'#bbb',fontWeight:400}}>더블클릭</span>}
                                    </div>
                                  )}
                                  {inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='phone' ? (
                                    <input autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                      onChange={e=>setInlineEditCell({...inlineEditCell,value:phoneOnChange(inlineEditCell.value, e.target.value)})}
                                      onBlur={()=>{ if(inlineEditCell.value!==(shipInfo.phone||'')) saveShipFieldInline(item,'phone',inlineEditCell.value); setInlineEditCell(null); }}
                                      onKeyDown={e=>{ if(e.key==='Enter')e.target.blur(); else if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} }}
                                      placeholder="010-0000-0000"
                                      style={{width:'100%',padding:'3px 8px',border:'2px solid var(--accent)',borderRadius:5,fontSize:12,fontFamily:'monospace',outline:'none',background:'var(--bg-card)',marginTop:2}}/>
                                  ) : (
                                    <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'phone',value:shipInfo.phone||''});}} title={isProcessed?'':'더블클릭하여 수정'} style={{fontSize:12,fontWeight:700,color:isProcessed?'#ccc':'var(--fg-2)',marginTop:2,fontFamily:'monospace',letterSpacing:'-0.02em',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',cursor:isProcessed?'default':'text'}}>
                                      {shipInfo.phone || <span style={{color:'#bbb',fontWeight:400,fontFamily:'inherit'}}>더블클릭</span>}
                                    </div>
                                  )}
                                </>
                              ) : (
                                <span style={{fontSize:12,fontWeight:500,color:'var(--fg-2)'}}>{item.dealer_name ? highlightMatch(item.dealer_name, logSearch) : '-'}</span>
                              )}
                            </div>
                            {/* v7.187: 입고 row에도 memo 표시 (기존 display:none 제거) — 작성자/요청자 영역은 !isIn 조건으로만 표시 */}
                            <div style={{padding:'18px 10px',fontSize:12,color:'var(--fg-2)',overflow:'hidden',
                              // v7.214: 입고 row는 배송지 셀이 병합돼 자동배치가 밀린다 → 컬럼 명시
                              ...(isIn?{gridColumn:'9'}:{})}} title={[shipInfo&&(shipInfo.author||shipInfo.requester)&&!isIn?`${shipInfo.author||'-'}/${shipInfo.requester||'-'}`:'', shipInfo&&shipInfo.memo].filter(Boolean).join(' · ')}>
                              {!isIn && (
                              <div style={{fontWeight:600,color:isProcessed?'#bbb':'var(--fg-1)',display:'flex',alignItems:'center',gap:3,flexWrap:'nowrap',minWidth:0}}>
                                {inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='author' ? (
                                  <select autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                    onChange={e=>{ saveShipFieldInline(item,'author',e.target.value); setInlineEditCell(null); }}
                                    onBlur={()=>setInlineEditCell(null)}
                                    style={{padding:'3px 6px',border:'2px solid var(--accent)',borderRadius:5,fontSize:11,fontWeight:600,outline:'none',background:'var(--bg-card)',cursor:'pointer',maxWidth:'48%'}}>
                                    <option value="">-</option>
                                    {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                                    {shipInfo?.author && !staffList.includes(shipInfo.author) && <option value={shipInfo.author}>{shipInfo.author}</option>}
                                  </select>
                                ) : (
                                  <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed||isIn) return; setInlineEditCell({id:item.id,field:'author',value:shipInfo?.author||''});}} title={isProcessed||isIn?'':'더블클릭하여 수정'} style={{color:isProcessed?'#bbb':'var(--accent)',cursor:isProcessed||isIn?'default':'pointer',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                    {shipInfo?.author || <span style={{color:'#bbb',fontWeight:400}}>-</span>}
                                  </span>
                                )}
                                <span style={{color:'#bbb'}}>/</span>
                                {inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='requester' ? (
                                  <select autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                    onChange={e=>{ saveShipFieldInline(item,'requester',e.target.value); setInlineEditCell(null); }}
                                    onBlur={()=>setInlineEditCell(null)}
                                    style={{padding:'3px 6px',border:'2px solid var(--accent)',borderRadius:5,fontSize:11,fontWeight:600,outline:'none',background:'var(--bg-card)',cursor:'pointer',maxWidth:'48%'}}>
                                    <option value="">-</option>
                                    {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                                    {shipInfo?.requester && !staffList.includes(shipInfo.requester) && <option value={shipInfo.requester}>{shipInfo.requester}</option>}
                                  </select>
                                ) : (
                                  <span onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed||isIn) return; setInlineEditCell({id:item.id,field:'requester',value:shipInfo?.requester||''});}} title={isProcessed||isIn?'':'더블클릭하여 수정'} style={{color:isProcessed?'#bbb':'var(--fg-2)',cursor:isProcessed||isIn?'default':'pointer',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                                    {shipInfo?.requester || <span style={{color:'#bbb',fontWeight:400}}>-</span>}
                                  </span>
                                )}
                              </div>
                              )}
                              {/* v7.213: 입고 row는 작성자/요청자 줄이 없어 비고가 위로 붙었다.
                                  같은 구조의 투명 자리를 넣어 다른 row와 비고 시작 위치를 맞춘다. */}
                              {isIn && (
                                <div aria-hidden="true" style={{fontWeight:600,display:'flex',alignItems:'center',gap:3,flexWrap:'nowrap',minWidth:0,visibility:'hidden'}}>
                                  <span>-</span><span>/</span><span>-</span>
                                </div>
                              )}
                              {inlineEditCell&&inlineEditCell.id===item.id&&inlineEditCell.field==='memo' ? (
                                <textarea autoFocus value={inlineEditCell.value} onClick={e=>e.stopPropagation()}
                                  onChange={e=>setInlineEditCell({...inlineEditCell,value:e.target.value})}
                                  onBlur={()=>{ if(inlineEditCell.value!==(shipInfo?.memo||'')) saveShipFieldInline(item,'memo',inlineEditCell.value); setInlineEditCell(null); }}
                                  onKeyDown={e=>{ if(e.key==='Escape'){setInlineEditCell(null);e.target.blur();} else if(e.key==='Enter'&&e.shiftKey){e.preventDefault();e.target.blur();} }}
                                  rows={5}
                                  placeholder="비고 입력 (Shift+Enter 저장, Esc 취소)"
                                  style={{width:'100%',padding:'6px 8px',marginTop:3,border:'2px solid var(--accent)',borderRadius:5,fontSize:12,fontFamily:'inherit',resize:'vertical',outline:'none',background:'var(--bg-card)',boxSizing:'border-box',minHeight:100,lineHeight:1.5}}/>
                              ) : (
                                <div onDoubleClick={(e)=>{e.stopPropagation(); if(isProcessed) return; setInlineEditCell({id:item.id,field:'memo',value:shipInfo?.memo||''});}} title={isProcessed?'':'더블클릭하여 비고 수정'} style={{fontSize:11,color:isProcessed?'#ccc':shipInfo&&shipInfo.memo?'var(--fg-1)':'#bbb',marginTop:2,whiteSpace:'pre-wrap',wordBreak:'break-word',fontWeight:shipInfo&&shipInfo.memo?700:400,cursor:isProcessed?'default':'text',minHeight:14,lineHeight:1.5}}>
                                  {shipInfo&&shipInfo.memo ? shipInfo.memo : (isProcessed?'':'비고 입력...')}
                                </div>
                              )}
                            </div>
                            {/* 처리: 출고 대기 → [제품 출고], RMA → [출고 토글 + 수리 완료] */}
                            <div style={{padding:'10px 8px',display:'flex',alignItems:'center',justifyContent:'center',...(isIn?{gridColumn:'10'}:{})}} onClick={e=>e.stopPropagation()}>
                              {shipInfo&&shipInfo.type==='ship_request'&&shipInfo.rma&&!isProcessed&&!shipInfo.repaired ? (
                                <div style={{display:'flex',flexDirection:'column',gap:4,alignItems:'stretch'}}>
                                  {/* 출고완료/대기 토글 — RMA는 EMS 발송 상태 표시용 */}
                                  <button onClick={()=>toggleRmaFulfilled(item, shipInfo)} disabled={loading}
                                    title={shipInfo.fulfilled ? '클릭 시 출고 대기로 변경' : '클릭 시 출고 완료로 변경'}
                                    style={{display:'flex',alignItems:'center',justifyContent:'center',gap:3,padding:'5px 8px',background:shipInfo.fulfilled?'rgba(0,152,68,0.1)':'rgba(245,124,0,0.12)',color:shipInfo.fulfilled?'var(--success)':'var(--warning)',border:`1.5px solid ${shipInfo.fulfilled?'var(--success)':'#f57c00'}`,borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap'}}>
                                    <Icon name={shipInfo.fulfilled?'check_circle':'schedule'} size={12}/>
                                    {shipInfo.fulfilled?'출고완료':'출고대기'}
                                  </button>
                                  {/* 수리 완료 */}
                                  <button onClick={()=>handleRepairComplete(item, shipInfo)} disabled={loading}
                                    style={{display:'flex',alignItems:'center',justifyContent:'center',gap:4,padding:'6px 10px',background:'var(--success-solid)',color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',boxShadow:'0 2px 4px rgba(0,152,68,0.2)'}}
                                    onMouseEnter={e=>e.currentTarget.style.background='#00773a'}
                                    onMouseLeave={e=>e.currentTarget.style.background='var(--success-solid)'}>
                                    <Icon name="build_circle" size={12}/> 수리 완료
                                  </button>
                                </div>
                              ) : shipInfo&&shipInfo.type==='ship_request'&&!shipInfo.rma&&!isProcessed&&!shipInfo.fulfilled ? (
                                (() => {
                                  // v7.55: 방문수령 + 시리얼 다 예약됨 → [방문수령 완료] 버튼 표시
                                  // 일반 출고 또는 방문수령이지만 시리얼 미완료 → [제품 출고] 버튼 표시
                                  const isPickup = !!shipInfo.pickup;
                                  // 모든 모델의 reservedSerials 합산
                                  const itemsArr = Array.isArray(shipInfo.items) ? shipInfo.items : null;
                                  const totalQty = itemsArr ? itemsArr.reduce((s,it)=>s+(it.qty||0),0) : (shipInfo.qty||0);
                                  const totalReserved = itemsArr
                                    ? itemsArr.reduce((s,it)=>s+((it.reservedSerials?.length)||0),0)
                                    : (shipInfo.reservedSerials?.length || 0);
                                  const totalFulfilled = itemsArr
                                    ? itemsArr.reduce((s,it)=>s+((it.fulfilledSerials?.length)||0),0)
                                    : (shipInfo.fulfilledSerials?.length || 0);
                                  // 방문수령 + 시리얼 모두 예약 완료 + 아직 fulfilled 아님 → 방문수령 완료 가능
                                  if (isPickup && totalReserved + totalFulfilled >= totalQty && totalReserved > 0) {
                                    return (
                                      <button onClick={async ()=>{
                                        if (!window.confirm(`방문수령 출고 처리하시겠습니까?\n예약된 ${totalReserved}개 시리얼이 출고 처리됩니다.`)) return;
                                        await completePickupRequest(item);
                                      }} style={{display:'flex',alignItems:'center',gap:5,padding:'8px 12px',background:'var(--accent-solid)',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',boxShadow:'0 2px 6px rgba(0,102,138,0.25)'}}
                                        onMouseEnter={e=>e.currentTarget.style.background='#004d6a'}
                                        onMouseLeave={e=>e.currentTarget.style.background='var(--accent-solid)'}
                                        title={`예약 ${totalReserved}개 → 출고 완료 처리`}>
                                        <Icon name="storefront" size={14}/> 방문수령 출고
                                      </button>
                                    );
                                  }
                                  // 그 외: 일반 [제품 출고] 버튼
                                  return (
                                    <button onClick={async ()=>{
                                      await loadActiveRequest(shipInfo.reqId||item.serial, item.model);
                                      setOutboundModalOpen(true);
                                    }} style={{display:'flex',alignItems:'center',gap:5,padding:'8px 12px',background:'var(--success-solid)',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:'pointer',whiteSpace:'nowrap',boxShadow:'0 2px 6px rgba(0,152,68,0.25)'}}
                                      onMouseEnter={e=>e.currentTarget.style.background='#00773a'}
                                      onMouseLeave={e=>e.currentTarget.style.background='var(--success-solid)'}>
                                      <Icon name="inventory" size={14}/> {isPickup ? '시리얼 등록' : '제품 출고'}
                                    </button>
                                  );
                                })()
                              ) : <span style={{color:'#bbb',fontSize:11}}>-</span>}
                            </div>
                          </div>
                      );
                    })}
                    {!logItems.length&&<div style={{padding:60,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>{loading?'로딩 중...':'해당 기간에 내역이 없습니다'}</div>}
                  </div>
                </div>

                {/* Pagination */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'14px 20px',borderTop:'1px solid var(--border)',background:'var(--bg-accent-soft)'}}>
                  <span style={{fontSize:14,color:'var(--fg-3)'}}>총 {logTotal.toLocaleString()}건 중 {logTotal>0?logPg*PG+1:0}-{Math.min((logPg+1)*PG,logTotal)}건 표시</span>
                  <div style={{display:'flex',gap:4,alignItems:'center'}}>
                    <button disabled={logPg===0} onClick={()=>setLogPg(p=>p-1)} style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid var(--border-strong)',borderRadius:8,cursor:logPg===0?'default':'pointer',background:'var(--bg-card)',opacity:logPg===0?0.3:1}}><Icon name="chevron_left" size={18}/></button>
                    {Array.from({length:Math.min(maxPages,5)},(_,i)=>{
                      let pageNum;
                      if(maxPages<=5) pageNum=i;
                      else if(logPg<3) pageNum=i;
                      else if(logPg>=maxPages-3) pageNum=maxPages-5+i;
                      else pageNum=logPg-2+i;
                      return (
                        <button key={pageNum} onClick={()=>setLogPg(pageNum)} style={{
                          width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',
                          borderRadius:8,fontSize:13,fontWeight:600,border:'none',cursor:'pointer',transition:'all 0.15s',
                          background:logPg===pageNum?'var(--accent-solid)':'var(--bg-card)',
                          color:logPg===pageNum?'#fff':'var(--fg-2)',
                          boxShadow:logPg===pageNum?'none':'0 0 0 1px #c6c6cd',
                        }}>{pageNum+1}</button>
                      );
                    })}
                    {maxPages>5&&<span style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',fontSize:12,color:'var(--fg-3)'}}>…{maxPages}</span>}
                    <button disabled={(logPg+1)*PG>=logTotal} onClick={()=>setLogPg(p=>p+1)} style={{width:32,height:32,display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid var(--border-strong)',borderRadius:8,cursor:(logPg+1)*PG>=logTotal?'default':'pointer',background:'var(--bg-card)',opacity:(logPg+1)*PG>=logTotal?0.3:1}}><Icon name="chevron_right" size={18}/></button>
                  </div>
                </div>
              </Card>
              </>)}
            </div>);
          })()}

          {/* ═══ 내용 수정 모달 (txlog 전용) ═══ */}
          {editingTxItem && (
            <div onClick={closeEditModal} style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(4px)'}}>
              <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:20,width:'100%',maxWidth:640,maxHeight:'90vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
                {/* 헤더 */}
                <div style={{padding:'20px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <Icon name="edit_note" size={22} style={{color:'var(--accent)'}}/>
                    <h2 style={{fontSize:18,fontWeight:600,color:'var(--fg-1)'}}>출고 요청 내용 수정</h2>
                  </div>
                  <button onClick={closeEditModal} style={{width:32,height:32,border:'none',background:'transparent',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)'}} onMouseEnter={e=>e.currentTarget.style.background='#F1F5F9'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                    <Icon name="close" size={20}/>
                  </button>
                </div>
                {/* 본문 (스크롤) */}
                <div style={{padding:24,overflowY:'auto',flex:1}}>
                  {/* 수령인 & 연락처 */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>수령인 *</label>
                      <input value={editForm.recipient} onChange={e=>setEditForm(f=>({...f,recipient:e.target.value}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>연락처</label>
                      <input value={editForm.phone} onChange={e=>setEditForm(f=>({...f,phone:phoneOnChange(f.phone, e.target.value)}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                    </div>
                  </div>
                  {/* 배송 주소 */}
                  <div style={{marginBottom:14}}>
                    <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>배송 주소 *</label>
                    <textarea value={editForm.addr} onChange={e=>setEditForm(f=>({...f,addr:e.target.value}))} rows={2} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',resize:'vertical',fontFamily:'inherit',boxSizing:'border-box',lineHeight:1.5}}/>
                  </div>
                  {/* 배송 방식 & 매출여부 & 작성자 & 요청자 */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:14,marginBottom:18}}>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>배송 방식</label>
                      <select value={editForm.method} onChange={e=>setEditForm(f=>({...f,method:e.target.value}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}>
                        <option value="택배">택배</option>
                        <option value="퀵서비스">퀵서비스</option>
                        <option value="직접배송">직접배송</option>
                        <option value="방문수령">방문수령</option>
                      </select>
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>매출여부</label>
                      <select value={editForm.salesStatus || '입력필요'} onChange={e=>setEditForm(f=>({...f,salesStatus:e.target.value}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                        <option value="입력필요">입력필요</option>
                        <option value="입력완료">입력완료</option>
                      </select>
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>작성자</label>
                      <select value={editForm.author || ''} onChange={e=>setEditForm(f=>({...f,author:e.target.value}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                        <option value="">선택</option>
                        {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                        {/* 기존 데이터 호환: 목록에 없는 값이면 그대로 표시 */}
                        {editForm.author && !staffList.includes(editForm.author) && <option value={editForm.author}>{editForm.author}</option>}
                      </select>
                    </div>
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:6}}>요청자</label>
                      <select value={editForm.requester} onChange={e=>setEditForm(f=>({...f,requester:e.target.value}))} style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                        <option value="">선택</option>
                        {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                        {editForm.requester && !staffList.includes(editForm.requester) && <option value={editForm.requester}>{editForm.requester}</option>}
                      </select>
                    </div>
                  </div>
                  {/* 배송 방식별 추가 입력 — 송장번호(택배) 또는 가격(퀵) */}
                  {(editForm.method === '택배' || editForm.method === '퀵서비스') && (
                    <div style={{marginBottom:18,padding:'12px 14px',background:editForm.method==='택배'?'rgba(0,102,138,0.05)':'rgba(186,26,26,0.05)',border:`1px solid ${editForm.method==='택배'?'rgba(0,102,138,0.2)':'rgba(186,26,26,0.2)'}`,borderRadius:10}}>
                      {editForm.method === '택배' ? (
                        <>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--accent)',marginBottom:6}}>
                            <Icon name="qr_code_2" size={14}/> 택배 송장번호
                          </label>
                          <input value={editForm.trackingNo} onChange={e=>setEditForm(f=>({...f,trackingNo:e.target.value.replace(/[^0-9-]/g,'')}))} placeholder="예: 6026915218862" style={{width:'100%',padding:'10px 12px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                        </>
                      ) : (
                        <>
                          <label style={{display:'flex',alignItems:'center',gap:6,fontSize:12,fontWeight:700,color:'var(--danger)',marginBottom:6}}>
                            <Icon name="payments" size={14}/> 퀵 서비스 가격 (원)
                          </label>
                          <div style={{position:'relative'}}>
                            <input type="text" inputMode="numeric" value={editForm.quickPrice} onChange={e=>setEditForm(f=>({...f,quickPrice:e.target.value.replace(/[^0-9]/g,'')}))} placeholder="예: 10000" style={{width:'100%',padding:'10px 12px',paddingRight:34,border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                            <span style={{position:'absolute',right:14,top:'50%',transform:'translateY(-50%)',fontSize:13,color:'var(--fg-3)',pointerEvents:'none'}}>원</span>
                          </div>
                          {/* v7.54: 착불 체크박스 */}
                          <label style={{display:'inline-flex',alignItems:'center',gap:6,marginTop:8,padding:'5px 10px',background:editForm.quickCOD?'rgba(186,26,26,0.12)':'var(--bg-card)',border:`1.5px solid ${editForm.quickCOD?'var(--danger)':'var(--border-strong)'}`,borderRadius:9999,cursor:'pointer',fontSize:12,fontWeight:700,color:editForm.quickCOD?'var(--danger)':'var(--fg-2)',userSelect:'none'}}>
                            <input type="checkbox" checked={!!editForm.quickCOD} onChange={e=>setEditForm(f=>({...f,quickCOD:e.target.checked}))} style={{margin:0,cursor:'pointer'}}/>
                            착불 (수령자 결제)
                          </label>
                          {(editForm.quickPrice || editForm.quickCOD) && (
                            <div style={{marginTop:6,fontSize:11,color:'var(--fg-3)'}}>
                              표시: <b style={{color:'var(--danger)'}}>퀵{editForm.quickCOD?'(착불)':''}{editForm.quickPrice?` (${Number(editForm.quickPrice).toLocaleString()}원)`:''}</b>
                            </div>
                          )}
                          {/* 퀵 매출여부 */}
                          <div style={{marginTop:12}}>
                            <label style={{display:'flex',alignItems:'center',gap:5,fontSize:11,fontWeight:700,color:'var(--danger)',marginBottom:5}}>
                              <Icon name="electric_moped" size={12}/> 퀵 매출여부
                            </label>
                            <select value={editForm.quickSalesStatus} onChange={e=>setEditForm(f=>({...f,quickSalesStatus:e.target.value}))} style={{width:'100%',padding:'8px 10px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:12,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                              <option value="입력필요">입력필요</option>
                              <option value="입력완료">입력완료</option>
                            </select>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                  {/* 요청 품목 (수량만 수정 가능) */}
                  {editForm.items.length > 0 && (
                    <div>
                      <label style={{display:'block',fontSize:12,fontWeight:600,color:'var(--fg-2)',marginBottom:8}}>요청 품목 — 수량만 수정 가능 ({editForm.items.length}종)</label>
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        {editForm.items.map((it, idx) => {
                          const f = (it.fulfilledSerials || []).length;
                          return (
                            <div key={idx} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--bg-page)',border:'1px solid var(--border)',borderRadius:8}}>
                              <Icon name="package_2" size={16} style={{color:'var(--fg-2)'}}/>
                              <div style={{flex:1,fontSize:13,fontWeight:600,fontFamily:'monospace'}}>{it.product_name}</div>
                              {f > 0 && <span style={{fontSize:11,color:'var(--fg-3)'}}>출고 {f}개</span>}
                              <input type="number" min={Math.max(1, f)} value={it.qty} onChange={e=>{const v=e.target.value; setEditForm(form=>({...form, items: form.items.map((x,i)=> i===idx ? {...x, qty: v===''?'':Number(v)} : x )}));}} style={{width:80,padding:'6px 10px',border:'1px solid var(--border-strong)',borderRadius:6,fontSize:13,outline:'none',textAlign:'right'}}/>
                              <span style={{fontSize:12,color:'var(--fg-3)'}}>개</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{marginTop:8,fontSize:11,color:'var(--fg-3)'}}>※ 이미 출고된 시리얼이 있는 품목은 그 수량 이상으로만 변경 가능합니다. 품목 추가·삭제는 지원하지 않습니다.</div>
                    </div>
                  )}
                </div>
                {/* 푸터 */}
                <div style={{padding:'16px 24px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',background:'var(--bg-subtle)',borderRadius:'0 0 20px 20px'}}>
                  <button onClick={closeEditModal} disabled={editSaving} style={{padding:'10px 20px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:10,cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--fg-2)'}}>취소</button>
                  <button onClick={saveEditModal} disabled={editSaving||!editForm.recipient.trim()||!editForm.addr.trim()} style={{padding:'10px 24px',border:'none',background:editSaving||!editForm.recipient.trim()||!editForm.addr.trim()?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',borderRadius:10,cursor:editSaving||!editForm.recipient.trim()||!editForm.addr.trim()?'default':'pointer',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                    <Icon name="save" size={16}/> {editSaving?'저장 중...':'저장'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ═══ v7.40: STOCK DASHBOARD (재고 대시보드 — 재고 목록 페이지의 '대시보드' 탭) ═══ */}
          {page==='inventory' && inventoryTab==='dashboard' && (()=>{
            const sd = stockDashboard;
            // v7.401: 그리드 폭은 블록 렌더 안에서 CELL 로 정의한다 (구 PJ_GRID 제거 — 5컬럼→4컬럼 개편).
            // 모든 제품 + 재고 + 카테고리 + stock_type 분포 매핑
            const rows = sd.products.map(p => {
              const stock = sd.counts[p.product_name] || 0;
              const reserved = activeKeepsByModel[p.product_name] || 0;
              // v7.311: 시리얼 지정 킵은 이미 in_stock 에서 빠져 있으므로 미지정분만 뺀다
              const reservedUnbacked = activeKeepsUnbackedByModel[p.product_name] || 0;
              // v7.45: inventory.category 없으면 products.category(카탈로그 직접 지정)로 fallback
              const category = sd.categoryMap[p.product_name] || p.category || '미분류';
              const t = sd.stockTypeCounts[p.product_name] || { 신품: 0, AS: 0, 리페킹: 0, 조립출고: 0 };
              // v7.309: 파주 수량 — 제품명 정확 매칭 → 실패 시 정규화 매칭(대소문자/하이픈 차이 흡수)
              const paju = sd.paju[p.product_name]
                ?? sd.pajuNorm[normalizeProductName(p.product_name)]
                ?? 0;
              // v7.310: [신품만 보기] 는 목록 필터일 뿐 아니라 '수량 기준' 전환이다.
              //   켜면 재고에서 AS·리페킹·조립출고를 빼고 신품 수량만 센다.
              //   가용/0개/색상 판정이 전부 stock 을 보므로 여기서 한 번만 바꾸면 아래가 다 따라온다.
              //   요약 카드는 토글과 무관하게 전체 기준이어야 하므로 stockAll 을 따로 남긴다.
              const newProduct = t.신품 || 0;
              const effStock = sd.newOnly ? newProduct : stock;
              return {
                name: p.product_name, stock: effStock, stockAll: stock,
                reserved, reservedUnbacked, available: effStock - reservedUnbacked, category, paju,
                // v7.184: asProduct 추가
                newProduct, asProduct: t.AS || 0, repacked: t.리페킹 || 0, assembled: t.조립출고 || 0,
                formFactor: p.form_factor || '', // v7.401: NAS 타워/렉 오버라이드용
              };
            });
            // v7.17: 카테고리별 카운트 (필터 칩에 표시)
            const categoryCounts = rows.reduce((acc, r) => {
              acc[r.category] = (acc[r.category] || 0) + 1;
              return acc;
            }, {});
            // 카테고리 목록 — v7.128: 커스텀 순서
            //   NAS > DAS > HDD > SSD > Switch > Memory > Network Adapter > PSU
            //   > Cable/Transceiver > Rack/Accessory > 기타 > 미분류
            //   목록에 없는 카테고리는 알파벳순으로 기타 앞에 배치
            const CATEGORY_ORDER = [
              'NAS','DAS','HDD','SSD','Switch','Memory','Network Adapter','PSU',
              'Cable/Transceiver','Rack/Accessory','기타','미분류',
            ];
            const allCategories = Object.keys(categoryCounts).sort((a,b) => {
              const ai = CATEGORY_ORDER.indexOf(a);
              const bi = CATEGORY_ORDER.indexOf(b);
              const aOrd = ai >= 0 ? ai : CATEGORY_ORDER.indexOf('기타') - 0.5; // 미등록은 기타 직전
              const bOrd = bi >= 0 ? bi : CATEGORY_ORDER.indexOf('기타') - 0.5;
              if (aOrd !== bOrd) return aOrd - bOrd;
              return a.localeCompare(b);
            });
            // v7.17: 카테고리 필터
            let filtered = rows;
            if (sd.selectedCategory && sd.selectedCategory !== 'ALL') {
              filtered = rows.filter(r => r.category === sd.selectedCategory);
            }
            // 검색 필터
            const search = (sd.search || '').trim().toLowerCase();
            if (search) filtered = filtered.filter(r => r.name.toLowerCase().includes(search));
            // 0 숨기기 옵션
            if (sd.hideZero) filtered = filtered.filter(r => r.stock > 0);
            // v7.310: 신품만 보기 — 신품 재고가 있는 제품만 남긴다.
            //   수량은 이미 rows 단계에서 신품 기준으로 바뀌어 있으므로 stock > 0 과 같은 조건이다.
            if (sd.newOnly) filtered = filtered.filter(r => r.newProduct > 0);
            // 정렬
            const sorted = [...filtered];
            if (sd.sortMode === 'available_asc') {
              sorted.sort((a,b) => (a.available - b.available) || (b.stock - a.stock) || a.name.localeCompare(b.name));
            } else if (sd.sortMode === 'stock_desc') {
              sorted.sort((a,b) => (b.stock - a.stock) || a.name.localeCompare(b.name));
            } else if (sd.sortMode === 'name_asc') {
              sorted.sort((a,b) => a.name.localeCompare(b.name));
            } else if (sd.sortMode === 'zero_only') {
              sorted.sort((a,b) => (a.stock - b.stock) || a.name.localeCompare(b.name));
            }
            // 요약 통계 (전체 기준)
            // v7.310: 요약은 [신품만 보기] 와 무관하게 항상 전체 기준 (stockAll)
            const totalStock = rows.reduce((s,r) => s + r.stockAll, 0);
            const totalReserved = rows.reduce((s,r) => s + r.reserved, 0);
            const totalAvailable = totalStock - rows.reduce((s,r) => s + r.reservedUnbacked, 0);
            const zeroCount = rows.filter(r => r.stockAll === 0).length;
            const shortCount = rows.filter(r => (r.stockAll - r.reservedUnbacked) <= 0).length;
            return (
              <div>
                {/* Header */}
                {/* v7.408: 재고 대시보드 소제목/아이콘/설명 문구 제거, 파주 업로드·새로고침은 페이지 헤더로 이동.
                    이 자리에 있던 헤더 블록 전체를 걷어냈다 (요약 토글이 첫 요소가 된다). */}

                {/* v7.26: 요약 토글 버튼 + 조건부 카드 */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:sd.statsExpanded?12:16}}>
                  <button onClick={()=>setStockDashboard(s=>({...s, statsExpanded: !s.statsExpanded}))}
                    style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-subtle)'}
                    onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                    <Icon name="analytics" size={14} style={{color:'var(--success)'}}/>
                    요약 {sd.statsExpanded ? '접기' : '펼치기'}
                    <Icon name={sd.statsExpanded?'expand_less':'expand_more'} size={14}/>
                  </button>
                  {!sd.statsExpanded && (
                    <span style={{fontSize:11,color:'var(--fg-3)'}}>
                      재고 {totalStock.toLocaleString()} · 가용 <strong style={{color:'var(--success)'}}>{totalAvailable.toLocaleString()}</strong> · 0개 {zeroCount} · 가용부족 {shortCount}
                    </span>
                  )}
                </div>

                {/* 요약 카드 5개 (펼침 시) */}
                {sd.statsExpanded && (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:14,marginBottom:20}}>
                  <Card style={{padding:16,borderLeft:'4px solid var(--success)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>재고 (in_stock)</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--success)',lineHeight:1}}>{totalStock.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>개</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--purple)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>활성 킵</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--purple)',lineHeight:1}}>{totalReserved.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>개</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--success)',background:'#f4faf6'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--success)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>★ 출고 가능</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--success)',lineHeight:1}}>{totalAvailable.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>개</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--warning)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>재고 0개 제품</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--warning)',lineHeight:1}}>{zeroCount.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>종</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--danger)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>가용 부족</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--danger)',lineHeight:1}}>{shortCount.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>종</span></div>
                  </Card>
                  </div>
                )}

                {/* 필터/검색 영역 */}
                <Card style={{padding:18,marginBottom:16}}>
                  {/* v7.17: 카테고리 칩 줄 */}
                  <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',marginBottom:12,paddingBottom:12,borderBottom:'1px dashed var(--border)'}}>
                    <span style={{fontSize:12,color:'var(--fg-3)',fontWeight:600,marginRight:4}}>카테고리:</span>
                    <button onClick={()=>setStockDashboard(s=>({...s, selectedCategory:'ALL'}))}
                      style={{padding:'5px 12px',border:`1.5px solid ${sd.selectedCategory==='ALL'?'#0b1c30':'var(--border-strong)'}`,borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',background:sd.selectedCategory==='ALL'?'var(--surface-inverse)':'var(--bg-card)',color:sd.selectedCategory==='ALL'?'#fff':'var(--fg-2)'}}>
                      전체 <span style={{opacity:0.7,marginLeft:3}}>({rows.length})</span>
                    </button>
                    {allCategories.map(cat => {
                      const sel = sd.selectedCategory === cat;
                      // 카테고리별 색상
                      const colorMap = {
                        'NAS':'#009844', 'DAS':'#00695c', 'Switch':'#1976d2',
                        'Network Adapter':'#7b1fa2', 'Memory':'#c2185b',
                        'PSU':'#f57c00', 'HDD':'#d84315', 'SSD':'#5d4037',
                        'Cable/Transceiver':'#455a64', 'Rack/Accessory':'#6a1b9a',
                        '기타':'#76777d', '미분류':'#bdbdbd',
                      };
                      const color = colorMap[cat] || 'var(--fg-3)';
                      return (
                        <button key={cat} onClick={()=>setStockDashboard(s=>({...s, selectedCategory:cat}))}
                          style={{padding:'5px 12px',border:`1.5px solid ${sel?color:'var(--fg-disabled)'}`,borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',background:sel?color:'#fff',color:sel?'#fff':color,whiteSpace:'nowrap'}}>
                          {cat} <span style={{opacity:0.75,marginLeft:3}}>({categoryCounts[cat]})</span>
                        </button>
                      );
                    })}
                  </div>
                  {/* 검색 + 정렬 + 0 숨기기 */}
                  <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                    <div style={{flex:1,position:'relative',minWidth:240}}>
                      <Icon name="search" size={18} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                      <input
                        value={sd.search}
                        onChange={e=>setStockDashboard(s=>({...s, search: e.target.value}))}
                        placeholder="제품명 검색 (예: TS-264, RAM, EXOS)"
                        style={{width:'100%',padding:'10px 12px 10px 38px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:14,fontFamily:'inherit',outline:'none'}}
                        onFocus={e=>e.target.style.borderColor='var(--success)'}
                        onBlur={e=>e.target.style.borderColor='var(--border)'}
                      />
                      {sd.search && (
                        <button onClick={()=>setStockDashboard(s=>({...s, search:''}))}
                          style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',width:24,height:24,border:'none',background:'transparent',borderRadius:9999,cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <Icon name="close" size={14}/>
                        </button>
                      )}
                    </div>
                    <div style={{display:'flex',gap:6,alignItems:'center'}}>
                      <span style={{fontSize:12,color:'var(--fg-3)',fontWeight:600,marginRight:4}}>정렬:</span>
                      {[
                        ['available_asc','가용 적은 순'],
                        ['stock_desc','재고 많은 순'],
                        ['name_asc','가나다순'],
                        ['zero_only','0개 우선'],
                      ].map(([v,l])=>(
                        <button key={v} onClick={()=>setStockDashboard(s=>({...s, sortMode:v}))}
                          style={{padding:'6px 14px',border:`1.5px solid ${sd.sortMode===v?'var(--success)':'var(--border-strong)'}`,borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',background:sd.sortMode===v?'rgba(0,152,68,0.1)':'var(--bg-card)',color:sd.sortMode===v?'var(--success)':'var(--fg-2)'}}>
                          {l}
                        </button>
                      ))}
                    </div>
                    <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:12,color:'var(--fg-2)',marginLeft:8}}>
                      <input type="checkbox" checked={sd.hideZero}
                        onChange={e=>setStockDashboard(s=>({...s, hideZero: e.target.checked}))}
                        style={{cursor:'pointer'}}/>
                      0개 숨기기
                    </label>
                    {/* v7.309: 신품 재고가 있는 제품만 보기 (목록 필터 — 숫자는 그대로 전체 기준) */}
                    <button onClick={()=>setStockDashboard(s=>({...s, newOnly: !s.newOnly}))}
                      title="신품 재고가 있는 제품만 표시하고, 재고·가용 수량에서 AS·리페킹·조립출고를 제외합니다"
                      style={{display:'inline-flex',alignItems:'center',gap:5,padding:'6px 14px',border:`1.5px solid ${sd.newOnly?'#009844':'var(--border-strong)'}`,borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',background:sd.newOnly?'rgba(0,152,68,0.12)':'var(--bg-card)',color:sd.newOnly?'#009844':'var(--fg-2)'}}>
                      <Icon name={sd.newOnly?'check_circle':'radio_button_unchecked'} size={14}/>
                      신품만 보기
                    </button>
                  </div>
                </Card>

                {/* 시트지 형식 그리드 — 3컬럼 */}
                {sd.loading ? (
                  <Card style={{padding:80,textAlign:'center',color:'var(--fg-3)'}}>
                    <Icon name="hourglass_empty" size={32} style={{display:'block',margin:'0 auto 10px',opacity:0.5}}/>
                    재고 집계 중…
                  </Card>
                ) : sorted.length === 0 ? (
                  <Card style={{padding:80,textAlign:'center',color:'var(--fg-3)'}}>
                    {search ? `"${search}" — 일치하는 제품 없음` : '제품 데이터 없음'}
                  </Card>
                ) : (
                  <>
                    {/* v7.401: 재고 대시보드를 구글 시트처럼 개편.
                        ・ 카테고리별 블록을 세로로 나열 (블록 = 헤더 + 표).
                        ・ 컬럼: 모델명 · 재고(본사) · 파주 · 비고(직접 입력·저장).  킵/가용 제거.
                        ・ 색상은 [재고] 기준: 재고 0 = 노랑, 재고 ≤ 임계 = 주황.
                          임계: NAS 타워 ≤7 / NAS 렉 ≤2 / 그 외(스위치 포함) ≤5.
                          NAS 타워·렉 판정 = form_factor 오버라이드('렉'/'타워') → 없으면 모델명 규칙(-RP 또는 랙유닛 U). */}
                    {(() => {
                      // NAS 렉형 판정 (모델명 규칙 + form_factor 오버라이드)
                      const isNasRack = (r) => {
                        const ff = (r.formFactor || '').trim();
                        if (/렉|랙|rack/i.test(ff)) return true;
                        if (/타워|tower/i.test(ff)) return false;
                        const nm = (r.name || '').toUpperCase().replace(/-(EU|US|UK|AU|CN|JP|TW|KR)$/, '');
                        if (/-RP/.test(nm)) return true;              // 이중전원 = 랙마운트
                        if (/\d[A-Z]{0,2}U(-|$)/.test(nm)) return true; // 랙유닛 U (464U, 873AU, 2287XU …)
                        return false;
                      };
                      // 주황 임계치 (재고 이하이면 주황)
                      const orangeThreshold = (r) => {
                        if (r.category === 'NAS') return isNasRack(r) ? 2 : 7;
                        return 5; // 스위치 포함 나머지 전부
                      };
                      // v7.403: 베이 수 파싱 (NAS/DAS 저베이→고베이 정렬용). QNAP 모델명 관례:
                      //   TS-233→2, TS-464→4, TS-1264→12 (앞자리=베이, 뒤 2자리 제거). TR-002/004 는 숫자 그대로.
                      //   'TVS-h874'·'TL-D800S' 처럼 접두 문자 하나는 건너뛴다. 파싱 실패 시 맨 뒤로(Infinity).
                      const bayOf = (name) => {
                        const m = (name || '').toUpperCase().match(/^([A-Z]+)-([A-Z]?)(\d+)/);
                        if (!m) return Infinity;
                        const series = m[1];
                        const digits = m[3];
                        const n = parseInt(digits, 10);
                        if (!isFinite(n)) return Infinity;
                        if (series === 'TR') return n;                     // TR-002→2, TR-004→4
                        if (digits.length >= 3) return Math.floor(n / 100); // 233→2, 1264→12, 1885→18
                        return n;
                      };
                      // v7.403: 용량 파싱 (HDD/SSD 저용량→고용량 정렬용). 숫자+단위(TB/GB/T/G) → GB 로 환산.
                      //   'EXOS_SATA 10TB'→10000, '삼성_970 250GB'→250, '1.92TB'→1920. 단위 뒤 문자 있으면 제외(16GR 방지).
                      const capGB = (name) => {
                        const m = (name || '').toUpperCase().match(/(\d+(?:\.\d+)?)\s*(TB|GB|T|G)(?![A-Z])/);
                        if (!m) return Infinity;
                        const val = parseFloat(m[1]);
                        if (!isFinite(val)) return Infinity;
                        return (m[2] === 'TB' || m[2] === 'T') ? val * 1000 : val;
                      };
                      // 카테고리별 그룹 (정렬/필터 적용된 sorted 기준)
                      const byCat = {};
                      sorted.forEach(r => { (byCat[r.category] = byCat[r.category] || []).push(r); });
                      const blockCats = allCategories.filter(c => byCat[c] && byCat[c].length);
                      // v7.405: CELL(그리드 컬럼)은 블록마다 모델명 최장 길이에 맞춰 계산한다 (아래 map 내부).
                      //   ⚠ auto/max-content 는 헤더·행이 별도 컨테이너라 폭이 달라져 열이 어긋난다 → ch 고정폭을 쓴다.
                      return (
                        <>
                          <div style={{fontSize:12,color:'var(--fg-3)',marginBottom:14,display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
                            <span>총 <strong style={{color:'var(--fg-1)'}}>{sorted.length.toLocaleString()}</strong>종 · <strong style={{color:'var(--fg-1)'}}>{blockCats.length}</strong>개 카테고리 (전체 {rows.length}종)</span>
                            <span style={{display:'flex',gap:14,alignItems:'center'}}>
                              <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:11,height:11,background:'#fff59d',borderRadius:2,display:'inline-block',border:'1px solid #e6d200'}}></span>재고 0</span>
                              <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:11,height:11,background:'#ffe0b2',borderRadius:2,display:'inline-block',border:'1px solid #ffb74d'}}></span>재고 부족 (NAS타워≤7·NAS렉≤2·그외≤5)</span>
                            </span>
                          </div>
                          {/* v7.402: 카테고리 블록을 2열 그리드로 나열 (NAS|DAS, HDD|SSD … 카테고리 순서대로 좌→우).
                              alignItems:start 라 짝의 높이가 달라도 각 블록은 제 높이만 차지한다. */}
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,alignItems:'start'}}>
                          {blockCats.map(cat => {
                            // v7.403: 블록 내 정렬 — NAS/DAS 저베이→고베이, HDD/SSD 저용량→고용량. 그 외는 전역 정렬 유지.
                            let catRows = byCat[cat];
                            if (cat === 'NAS' || cat === 'DAS') {
                              catRows = [...catRows].sort((a,b) => (bayOf(a.name) - bayOf(b.name)) || a.name.localeCompare(b.name));
                            } else if (cat === 'HDD' || cat === 'SSD') {
                              catRows = [...catRows].sort((a,b) => (capGB(a.name) - capGB(b.name)) || a.name.localeCompare(b.name));
                            }
                            const catColor = '#0b1c30';
                            // v7.405: 이 블록의 모델명 최장 길이에 맞춰 모델명 컬럼 폭을 고정 → 본사·파주가 이름 바로 뒤에 붙는다.
                            //   블록마다 계산하지만 [이 블록의] 헤더·행이 같은 CELL 을 쓰므로 정렬은 맞는다 (monospace 기준 ch).
                            const nameChars = Math.min(30, Math.max(8, ...catRows.map(r => (r.name || '').length)));
                            // v7.406: ch 대신 [px] — ch 는 폰트 크기에 비례한다. 헤더(fontSize:10)·행(fontSize:13) 폰트가 달라
                            //   같은 ch 라도 픽셀폭이 달라져 헤더와 행의 모델명 컬럼이 어긋났다. 절대 px 로 고정 (monospace ≈ 8px/자 + 여백).
                            const CELL = `${nameChars * 8 + 14}px 54px 50px 24px minmax(120px, 1fr)`; // v7.410: 파주-비고 간격을 [고정 24px] 로 (3열에서 1fr 이 0으로 찌그러져 붙던 문제). 비고는 남는 폭을 채움.
                            return (
                              <div key={cat} style={{marginBottom:0,minWidth:0}}>
                                {/* 블록 헤더 */}
                                <div style={{display:'flex',alignItems:'center',gap:8,padding:'8px 14px',background:'#eef2f7',border:'1.5px solid #1a1a1a',borderBottom:'none',borderRadius:'10px 10px 0 0'}}>
                                  <span style={{fontSize:14,fontWeight:800,color:catColor,letterSpacing:'-0.01em'}}>{cat}</span>
                                  <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:9999,padding:'1px 8px'}}>{catRows.length}종</span>
                                </div>
                                {/* 표 헤더 */}
                                <div style={{display:'grid',gridTemplateColumns:CELL,padding:'8px 14px',fontSize:10,fontWeight:700,color:'var(--fg-2)',textTransform:'uppercase',letterSpacing:'0.06em',background:'#f6f7fb',borderLeft:'1.5px solid #1a1a1a',borderRight:'1.5px solid #1a1a1a',borderBottom:'1px solid var(--border-strong)'}}>
                                  <div>모델명</div>
                                  <div style={{textAlign:'right'}}>본사</div>
                                  <div style={{textAlign:'right'}}>파주</div>
                                  <div></div>{/* v7.404: 파주-비고 간격 */}
                                  <div style={{paddingLeft:2}}>비고</div>
                                </div>
                                {/* 표 본문 */}
                                <div style={{border:'1.5px solid #1a1a1a',borderTop:'none',borderRadius:'0 0 10px 10px',overflow:'hidden',background:'var(--bg-card)'}}>
                                  {catRows.map((r) => {
                                    const isZero = r.stock === 0;
                                    const isLow = !isZero && r.stock <= orangeThreshold(r);
                                    const bg = isZero ? '#fff59d' : isLow ? '#ffe0b2' : '#fff';
                                    const typeParts = (r.stock > 0 && !sd.newOnly) ? [
                                      ['신품', r.newProduct, '#009844'],
                                      ['AS', r.asProduct, '#546e7a'],
                                      ['리페킹', r.repacked, '#e65100'],
                                      ['조립출고', r.assembled, '#7b1fa2'],
                                    ].filter(([, n]) => n > 0) : [];
                                    const memoVal = sd.dashMemo[r.name] || '';
                                    const isEditing = dashMemoEditing === r.name;
                                    return (
                                      <div key={r.name} style={{display:'grid',gridTemplateColumns:CELL,padding:'8px 14px',border:'1px solid #1a1a1a',marginBottom:'-1px',fontSize:13,alignItems:'center',background:bg,minHeight:40}}>
                                        <div style={{paddingRight:6,minWidth:0}}>
                                          <div style={{fontFamily:'monospace',fontWeight:600,color:'#111',wordBreak:'break-all'}} title={r.name}>{r.name}</div>
                                          {typeParts.length > 0 && (
                                            <div style={{fontSize:10,color:'#111',marginTop:2,display:'flex',gap:0,alignItems:'center',flexWrap:'wrap'}}>
                                              <span style={{color:'#111'}}>(</span>
                                              {typeParts.map(([label, n], i) => (
                                                <React.Fragment key={label}>
                                                  {i > 0 && <span style={{color:'#111',marginRight:4}}>,</span>}
                                                  <span style={{color:'#111',fontWeight:700}}>{label} {n}</span>
                                                </React.Fragment>
                                              ))}
                                              <span style={{color:'#111'}}>)</span>
                                            </div>
                                          )}
                                        </div>
                                        <div style={{textAlign:'right',fontWeight:700,fontSize:15,color:'#111'}}>{r.stock}</div>
                                        <div style={{textAlign:'right',fontSize:15,color:r.paju>0?'#111':'#bbb',fontWeight:r.paju>0?700:400}}>{r.paju>0?r.paju:'-'}</div>
                                        <div></div>{/* v7.404: 파주-비고 간격 */}
                                        {/* 비고 — 클릭하면 편집, blur/Enter 저장, Esc 취소 */}
                                        <div style={{paddingLeft:2,minWidth:0}}>
                                          {isEditing ? (
                                            <input
                                              autoFocus
                                              value={dashMemoDraft}
                                              onChange={e=>setDashMemoDraft(e.target.value)}
                                              onBlur={()=>saveDashMemo(r.name, dashMemoDraft)}
                                              onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault(); saveDashMemo(r.name, dashMemoDraft);} else if(e.key==='Escape'){setDashMemoEditing(null);} }}
                                              placeholder="비고 입력"
                                              style={{width:'100%',fontSize:12,padding:'4px 8px',border:'1px solid var(--accent)',borderRadius:6,outline:'none',fontFamily:'inherit',background:'#fff'}}
                                            />
                                          ) : (
                                            <div
                                              onClick={()=>{ setDashMemoEditing(r.name); setDashMemoDraft(memoVal); }}
                                              title="클릭하여 비고 입력"
                                              style={{fontSize:12,color:memoVal?'var(--fg-2)':'#c0c4cc',cursor:'text',wordBreak:'break-word',lineHeight:1.4,padding:'3px 6px',borderRadius:6,minHeight:22,transition:'background 0.12s'}}
                                              onMouseEnter={e=>e.currentTarget.style.background='rgba(0,0,0,0.035)'}
                                              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                              {memoVal || '— 비고 입력 —'}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                          </div>
                          <div style={{fontSize:10,color:'var(--fg-3)',marginTop:14,padding:'0 2px'}}>
                            ※ 색상은 [재고] 기준입니다 · 재고 0 = 노랑, 재고 부족(NAS 타워 ≤7 · NAS 렉 ≤2 · 그 외 ≤5) = 주황.
                            {sd.newOnly && <strong style={{color:'#009844',marginLeft:8}}>신품만 보기 — 재고가 신품 수량 기준입니다</strong>}
                            <span style={{marginLeft:8,color:'var(--fg-3)'}}>파주는 별도 창고라 색상 판정에 넣지 않습니다.</span>
                          </div>
                        </>
                      );
                    })()}
                  </>
                )}
              </div>
            );
          })()}

          {/* ═══ v7.89 USAGE ANALYTICS (사용 현황 서브탭) ═══ */}
          {page==='inventory' && inventoryTab==='analytics' && (()=>{
            const s = analyticsData?.summary;
            const bar = (val, max) => max > 0 ? Math.max(4, Math.round(val * 100 / max)) : 0;
            const maxPage    = s?.pageRank[0]?.count || 0;
            const maxAction  = s?.actionRank[0]?.count || 0;
            const maxStaff   = s?.staffRank[0]?.count || 0;
            const maxHourly  = Math.max(...(s?.hourlyBuckets || [0]));
            const rangeLabel = { today: '오늘', '7d': '최근 7일', '30d': '최근 30일' }[analyticsRange];
            return (
              <div>
                {/* 기간 선택 + 새로고침 */}
                <div style={{display:'flex',gap:8,marginBottom:20,alignItems:'center',flexWrap:'wrap'}}>
                  <div style={{display:'flex',gap:4,padding:4,background:'var(--bg-mute)',borderRadius:10}}>
                    {[['today','오늘'],['7d','7일'],['30d','30일']].map(([id,label])=>(
                      <button key={id} onClick={()=>setAnalyticsRange(id)}
                        style={{padding:'6px 14px',border:'none',borderRadius:7,fontSize:12,fontWeight:700,cursor:'pointer',
                                background:analyticsRange===id?'var(--bg-card)':'transparent',
                                color:analyticsRange===id?'#5c6bc0':'var(--fg-3)',
                                boxShadow:analyticsRange===id?'0 1px 3px rgba(0,0,0,0.08)':'none'}}>{label}</button>
                    ))}
                  </div>
                  <button onClick={loadAnalytics} disabled={analyticsBusy}
                    style={{padding:'6px 14px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:12,fontWeight:600,cursor:analyticsBusy?'wait':'pointer',color:'var(--fg-2)',display:'inline-flex',alignItems:'center',gap:5}}>
                    <Icon name="refresh" size={14}/>{analyticsBusy?'로드 중':'새로고침'}
                  </button>
                  <div style={{fontSize:12,color:'var(--fg-3)',marginLeft:'auto'}}>
                    기간: <b style={{color:'var(--fg-1)'}}>{rangeLabel}</b>
                  </div>
                </div>

                {!s ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>데이터 로딩 중...</div>
                ) : s.totalViews + s.totalActions === 0 ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)',fontSize:14,background:'var(--bg-subtle)',borderRadius:12,border:'1px dashed var(--border-strong)'}}>
                    <Icon name="query_stats" size={40} style={{color:'var(--fg-disabled)',marginBottom:8}}/>
                    <div>{rangeLabel} 동안 기록된 이벤트가 없습니다.</div>
                    <div style={{fontSize:11,marginTop:6}}>탭 전환·주요 액션이 자동으로 기록됩니다.</div>
                  </div>
                ) : (
                  <>
                    {/* 요약 카드 */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:12,marginBottom:24}}>
                      {[
                        ['페이지 뷰',      s.totalViews,     '#5c6bc0','visibility'],
                        ['액션 실행',      s.totalActions,   '#009844','bolt'],
                        ['활성 세션',      s.uniqueSessions, '#00668a','devices'],
                        ['총 이벤트',      s.totalViews + s.totalActions, '#7b1fa2','stacked_bar_chart'],
                      ].map(([label,val,color,icon])=>(
                        <div key={label} style={{padding:'16px 18px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,boxShadow:'0 1px 3px rgba(0,0,0,0.04)'}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,fontSize:11,fontWeight:700,color:'var(--fg-3)',letterSpacing:'0.05em',textTransform:'uppercase',marginBottom:6}}>
                            <Icon name={icon} size={14} style={{color}}/>
                            {label}
                          </div>
                          <div style={{fontSize:28,fontWeight:700,color,letterSpacing:'-0.02em'}}>{val.toLocaleString()}</div>
                        </div>
                      ))}
                    </div>

                    {/* 시간대별 분포 */}
                    {s.hourlyBuckets.some(v=>v>0) && (
                      <div style={{padding:'18px 20px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,marginBottom:24}}>
                        <div style={{fontSize:12,fontWeight:700,color:'var(--fg-2)',letterSpacing:'0.04em',textTransform:'uppercase',marginBottom:14}}>시간대별 이벤트 분포</div>
                        <div style={{display:'flex',alignItems:'flex-end',gap:2,height:80,marginBottom:6}}>
                          {s.hourlyBuckets.map((v,h)=>(
                            <div key={h} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                              <div title={`${h}시: ${v}건`}
                                   style={{width:'100%',height:`${bar(v,maxHourly)}%`,background:v>0?'#5c6bc0':'var(--bg-mute)',borderRadius:'3px 3px 0 0',transition:'height 0.3s'}}/>
                            </div>
                          ))}
                        </div>
                        <div style={{display:'flex',gap:2,fontSize:9,color:'var(--fg-3)'}}>
                          {s.hourlyBuckets.map((v,h)=>(
                            <div key={h} style={{flex:1,textAlign:'center'}}>{h%3===0?h:''}</div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* 랭킹 3개 (페이지 / 액션 / 담당자) */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(300px,1fr))',gap:16,marginBottom:24}}>
                      {[
                        ['페이지별 뷰',      s.pageRank,   maxPage,   '#5c6bc0','visibility'],
                        ['액션별 실행',      s.actionRank, maxAction, '#009844','bolt'],
                        ['담당자별 액션',    s.staffRank,  maxStaff,  '#00668a','person'],
                      ].map(([title,rank,max,color,icon])=>(
                        <div key={title} style={{padding:'18px 20px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12}}>
                          <div style={{fontSize:12,fontWeight:700,color:'var(--fg-2)',letterSpacing:'0.04em',textTransform:'uppercase',marginBottom:14,display:'flex',alignItems:'center',gap:6}}>
                            <Icon name={icon} size={14} style={{color}}/>{title}
                          </div>
                          {rank.length === 0 ? (
                            <div style={{fontSize:12,color:'var(--fg-3)',padding:'20px 0',textAlign:'center'}}>기록 없음</div>
                          ) : (
                            <div style={{display:'flex',flexDirection:'column',gap:8}}>
                              {rank.slice(0,10).map(({name,count},i)=>(
                                <div key={i}>
                                  <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',fontSize:12,marginBottom:3}}>
                                    <span style={{color:'var(--fg-1)',fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',maxWidth:'70%'}}>{name}</span>
                                    <span style={{color,fontWeight:700}}>{count}</span>
                                  </div>
                                  <div style={{height:6,background:'var(--bg-mute)',borderRadius:3,overflow:'hidden'}}>
                                    <div style={{height:'100%',width:`${bar(count,max)}%`,background:color,borderRadius:3,transition:'width 0.3s'}}/>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* 최근 이벤트 (스크롤) */}
                    <div style={{padding:'18px 20px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12}}>
                      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
                        <div style={{fontSize:12,fontWeight:700,color:'var(--fg-2)',letterSpacing:'0.04em',textTransform:'uppercase',display:'flex',alignItems:'center',gap:6}}>
                          <Icon name="history" size={14} style={{color:'var(--fg-3)'}}/>최근 이벤트
                        </div>
                        <div style={{fontSize:11,color:'var(--fg-3)'}}>최근 20건</div>
                      </div>
                      <div style={{maxHeight:320,overflowY:'auto',display:'flex',flexDirection:'column',gap:4}}>
                        {analyticsData.events.slice(0,20).map((e,i)=>{
                          const dt = new Date(e.created_at);
                          const timeStr = `${String(dt.getMonth()+1).padStart(2,'0')}/${String(dt.getDate()).padStart(2,'0')} ${String(dt.getHours()).padStart(2,'0')}:${String(dt.getMinutes()).padStart(2,'0')}`;
                          const isAction = e.event_type === 'action';
                          return (
                            <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 10px',borderRadius:6,background:i%2?'var(--bg-subtle)':'var(--bg-card)',fontSize:12}}>
                              <span style={{padding:'2px 8px',borderRadius:6,background:isAction?'rgba(0,152,68,0.1)':'rgba(92,107,192,0.1)',color:isAction?'var(--success)':'#5c6bc0',fontSize:10,fontWeight:700,minWidth:52,textAlign:'center'}}>
                                {isAction?'액션':'페이지'}
                              </span>
                              <span style={{flex:1,color:'var(--fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.event_name}</span>
                              {e.staff_name && <span style={{color:'var(--accent)',fontSize:11,fontWeight:600}}>{e.staff_name}</span>}
                              <span style={{color:'var(--fg-3)',fontSize:10,fontFamily:'monospace'}}>{timeStr}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
            );
          })()}
          {page==='demo' && (()=>{
            const ds = demoState;
            // 카테고리 통계 합계
            const totalAll = Object.values(ds.stats).reduce((s,c)=>s+c.total,0);
            const availableAll = Object.values(ds.stats).reduce((s,c)=>s+c.available,0);
            const loanedAll = Object.values(ds.stats).reduce((s,c)=>s+c.loaned,0);
            const brokenAll = Object.values(ds.stats).reduce((s,c)=>s+c.broken,0);
            // 필터링된 장비
            const searchLower = (ds.search||'').trim().toLowerCase();
            let filteredEq = ds.equipment;
            if (ds.selectedCategory !== 'ALL') filteredEq = filteredEq.filter(e => e.category === ds.selectedCategory);
            if (ds.locationFilter === 'available') filteredEq = filteredEq.filter(e => e.location === '리버');
            else if (ds.locationFilter === 'loaned') filteredEq = filteredEq.filter(e => e.location && e.location.startsWith('대여중'));
            if (ds.statusFilter !== 'all') filteredEq = filteredEq.filter(e => e.status === ds.statusFilter);
            if (searchLower) {
              filteredEq = filteredEq.filter(e =>
                (e.model||'').toLowerCase().includes(searchLower) ||
                (e.serial_number||'').toLowerCase().includes(searchLower) ||
                (e.model_detail||'').toLowerCase().includes(searchLower) ||
                (e.note||'').toLowerCase().includes(searchLower)
              );
            }
            return (
              <div>
                {/* Header */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,gap:16,flexWrap:'wrap'}}>
                  <div>
                    <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                      <Icon name="science" size={32} style={{color:'var(--purple)'}}/>
                      데모 관리
                    </h1>
                    <p style={{fontSize:15,color:'var(--fg-2)'}}>데모 장비 재고 · 대여 현황 · 회수 이력을 통합 관리합니다.</p>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <button onClick={openDemoAddModal}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',background:'var(--bg-card)',border:'1.5px solid var(--success)',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',color:'var(--success)'}}>
                      <Icon name="add_circle" size={16}/> 장비 추가
                    </button>
                    <button onClick={openDemoLendModal}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',background:'var(--purple-solid)',border:'1.5px solid var(--purple)',borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',color:'#fff'}}>
                      <Icon name="local_shipping" size={16}/> 대여 등록
                    </button>
                    <button onClick={loadDemoData} disabled={ds.loading}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'10px 14px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:10,fontSize:13,fontWeight:600,cursor:ds.loading?'wait':'pointer',color:'var(--fg-2)'}}>
                      <Icon name={ds.loading?'hourglass_empty':'refresh'} size={16}/>
                      {ds.loading?'로딩 중…':'새로고침'}
                    </button>
                  </div>
                </div>

                {/* v7.26: 요약 토글 + 조건부 카드 */}
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:ds.statsExpanded?12:16}}>
                  <button onClick={()=>setDemoState(s=>({...s, statsExpanded: !s.statsExpanded}))}
                    style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}
                    onMouseEnter={e=>e.currentTarget.style.background='var(--bg-subtle)'}
                    onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                    <Icon name="analytics" size={14} style={{color:'var(--purple)'}}/>
                    요약 {ds.statsExpanded ? '접기' : '펼치기'}
                    <Icon name={ds.statsExpanded?'expand_less':'expand_more'} size={14}/>
                  </button>
                  {!ds.statsExpanded && (
                    <span style={{fontSize:11,color:'var(--fg-3)'}}>
                      총 {totalAll} · 가용 <strong style={{color:'var(--success)'}}>{availableAll}</strong> · 대여 <strong style={{color:'var(--warning)'}}>{loanedAll}</strong> · 점검 {brokenAll}
                    </span>
                  )}
                </div>

                {/* 요약 카드 4개 (펼침 시) */}
                {ds.statsExpanded && (
                <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:14,marginBottom:20}}>
                  <Card style={{padding:16,borderLeft:'4px solid #45464d'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>총 장비</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--fg-1)',lineHeight:1}}>{totalAll.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>대</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--success)',background:'#f4faf6'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--success)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>★ 대여 가능</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--success)',lineHeight:1}}>{availableAll.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>대</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--warning)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>대여 중</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--warning)',lineHeight:1}}>{loanedAll.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>대</span></div>
                  </Card>
                  <Card style={{padding:16,borderLeft:'4px solid var(--danger)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6}}>점검·불량</div>
                    <div style={{fontSize:30,fontWeight:700,color:'var(--danger)',lineHeight:1}}>{brokenAll.toLocaleString()}<span style={{fontSize:13,color:'var(--fg-3)',marginLeft:5}}>대</span></div>
                  </Card>
                </div>
                )}

                {/* 탭 */}
                <div style={{display:'flex',gap:4,marginBottom:16,borderBottom:'1.5px solid var(--border)'}}>
                  {[
                    ['equipment', '장비 목록', 'inventory_2', totalAll],
                    ['rentals', '대여로그', 'local_shipping', ds.activeRentals.length],
                    ['returns', '회수로그', 'history', ds.returns.length],
                  ].map(([key,label,icon,cnt])=>(
                    <button key={key} onClick={()=>setDemoState(s=>({...s, tab:key}))}
                      style={{padding:'10px 18px',background:'transparent',border:'none',borderBottom:`3px solid ${ds.tab===key?'var(--purple)':'transparent'}`,fontSize:14,fontWeight:700,color:ds.tab===key?'var(--purple)':'var(--fg-3)',cursor:'pointer',display:'inline-flex',alignItems:'center',gap:6,marginBottom:-1.5}}>
                      <Icon name={icon} size={16}/>{label}
                      <span style={{padding:'1px 7px',borderRadius:9999,background:ds.tab===key?'rgba(123,31,162,0.1)':'var(--bg-mute)',fontSize:11,color:ds.tab===key?'var(--purple)':'var(--fg-3)'}}>{cnt}</span>
                    </button>
                  ))}
                </div>

                {/* ─── 탭: 장비 목록 ─── */}
                {ds.tab==='equipment' && (
                  <>
                    {/* 필터/검색 */}
                    <Card style={{padding:16,marginBottom:14}}>
                      <div style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',marginBottom:12,paddingBottom:12,borderBottom:'1px dashed var(--border)'}}>
                        <span style={{fontSize:12,color:'var(--fg-3)',fontWeight:600,marginRight:4}}>카테고리:</span>
                        <button onClick={()=>setDemoState(s=>({...s, selectedCategory:'ALL'}))}
                          style={{padding:'5px 12px',border:`1.5px solid ${ds.selectedCategory==='ALL'?'#0b1c30':'var(--border-strong)'}`,borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',background:ds.selectedCategory==='ALL'?'var(--surface-inverse)':'var(--bg-card)',color:ds.selectedCategory==='ALL'?'#fff':'var(--fg-2)'}}>
                          전체 ({ds.equipment.length})
                        </button>
                        {Object.keys(DEMO_CATEGORY_LABEL).map(cat => {
                          const sel = ds.selectedCategory === cat;
                          const color = DEMO_CATEGORY_COLOR[cat];
                          const cnt = (ds.stats[cat]?.total) || 0;
                          if (cnt === 0) return null;
                          return (
                            <button key={cat} onClick={()=>setDemoState(s=>({...s, selectedCategory:cat}))}
                              style={{padding:'5px 12px',border:`1.5px solid ${sel?color:'var(--fg-disabled)'}`,borderRadius:9999,fontSize:12,fontWeight:700,cursor:'pointer',background:sel?color:'#fff',color:sel?'#fff':color}}>
                              {DEMO_CATEGORY_LABEL[cat]} ({cnt})
                            </button>
                          );
                        })}
                      </div>
                      <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                        <div style={{flex:1,position:'relative',minWidth:240}}>
                          <Icon name="search" size={18} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                          <input value={ds.search} onChange={e=>setDemoState(s=>({...s, search:e.target.value}))}
                            placeholder="모델 · 시리얼 · 비고 검색"
                            style={{width:'100%',padding:'10px 12px 10px 38px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:14,outline:'none',fontFamily:'inherit'}}
                            onFocus={e=>e.target.style.borderColor='var(--purple)'}
                            onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                          {ds.search && (
                            <button onClick={()=>setDemoState(s=>({...s, search:''}))}
                              style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',width:24,height:24,border:'none',background:'transparent',borderRadius:9999,cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                              <Icon name="close" size={14}/>
                            </button>
                          )}
                        </div>
                        <div style={{display:'flex',gap:5,alignItems:'center'}}>
                          <span style={{fontSize:12,color:'var(--fg-3)',fontWeight:600,marginRight:4}}>위치:</span>
                          {[['all','전체'],['available','리버 (대여 가능)'],['loaned','대여 중']].map(([v,l])=>(
                            <button key={v} onClick={()=>setDemoState(s=>({...s, locationFilter:v}))}
                              style={{padding:'5px 11px',border:`1.5px solid ${ds.locationFilter===v?'var(--purple)':'var(--border-strong)'}`,borderRadius:9999,fontSize:11,fontWeight:600,cursor:'pointer',background:ds.locationFilter===v?'rgba(123,31,162,0.1)':'var(--bg-card)',color:ds.locationFilter===v?'var(--purple)':'var(--fg-2)'}}>
                              {l}
                            </button>
                          ))}
                        </div>
                      </div>
                    </Card>

                    {/* 장비 목록 — 모델별 그룹 아코디언 */}
                    {ds.loading ? (
                      <Card style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>
                        <Icon name="hourglass_empty" size={28} style={{display:'block',margin:'0 auto 8px',opacity:0.5}}/>
                        로딩 중…
                      </Card>
                    ) : filteredEq.length === 0 ? (
                      <Card style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>
                        검색 조건에 맞는 장비가 없습니다.
                      </Card>
                    ) : (() => {
                      // v7.24: 모델별 그룹화
                      const groups = {};
                      filteredEq.forEach(e => {
                        const k = e.model || '(미분류)';
                        if (!groups[k]) groups[k] = { model: e.model, category: e.category, items: [] };
                        groups[k].items.push(e);
                      });
                      const sortedGroups = Object.values(groups).sort((a,b) => (a.model||'').localeCompare(b.model||''));
                      const toggleExpand = (name) => {
                        setDemoState(s => {
                          const next = new Set(s.expandedModels);
                          if (next.has(name)) next.delete(name);
                          else next.add(name);
                          return { ...s, expandedModels: next };
                        });
                      };
                      const expandAll = () => setDemoState(s => ({ ...s, expandedModels: new Set(sortedGroups.map(g => g.model || '(미분류)')) }));
                      const collapseAll = () => setDemoState(s => ({ ...s, expandedModels: new Set() }));
                      return (
                        <div>
                          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                            <span style={{fontSize:13,color:'var(--fg-3)'}}>
                              총 <strong style={{color:'var(--fg-1)'}}>{sortedGroups.length}</strong>개 모델 · <strong style={{color:'var(--fg-1)'}}>{filteredEq.length}</strong>대 (전체 {ds.equipment.length}대 중)
                            </span>
                            <div style={{display:'flex',gap:6}}>
                              <button onClick={expandAll}
                                style={{padding:'5px 12px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:11,fontWeight:600,cursor:'pointer',color:'var(--fg-2)',display:'inline-flex',alignItems:'center',gap:4}}>
                                <Icon name="unfold_more" size={13}/> 모두 펼치기
                              </button>
                              <button onClick={collapseAll}
                                style={{padding:'5px 12px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:11,fontWeight:600,cursor:'pointer',color:'var(--fg-2)',display:'inline-flex',alignItems:'center',gap:4}}>
                                <Icon name="unfold_less" size={13}/> 모두 접기
                              </button>
                            </div>
                          </div>
                          <div style={{display:'flex',flexDirection:'column',gap:8}}>
                            {sortedGroups.map(g => {
                              const name = g.model || '(미분류)';
                              const expanded = ds.expandedModels.has(name);
                              const catColor = DEMO_CATEGORY_COLOR[g.category] || '#76777d';
                              // 카운트 분석
                              const availCnt = g.items.filter(it => it.location === '리버' && it.status === '정상작동').length;
                              const loanedCnt = g.items.filter(it => it.location && it.location.startsWith('대여중')).length;
                              const brokenCnt = g.items.filter(it => it.status === '불량' || it.status === '수리중').length;
                              return (
                                <div key={name} style={{background:'var(--bg-card)',border:`1.5px solid ${expanded?catColor:'var(--border)'}`,borderRadius:12,overflow:'hidden',transition:'border-color 0.15s',boxShadow:expanded?'0 4px 12px rgba(0,0,0,0.04)':'none'}}>
                                  {/* 헤더 (클릭) */}
                                  <button onClick={()=>toggleExpand(name)}
                                    style={{width:'100%',padding:'14px 18px',background:'transparent',border:'none',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,cursor:'pointer',textAlign:'left',transition:'background 0.15s'}}
                                    onMouseEnter={e=>{if(!expanded) e.currentTarget.style.background='var(--bg-subtle)';}}
                                    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                                    <div style={{display:'flex',alignItems:'center',gap:10,flex:1,minWidth:0}}>
                                      <span style={{padding:'2px 7px',borderRadius:9999,fontSize:9.5,fontWeight:700,background:catColor+'22',color:catColor,whiteSpace:'nowrap'}}>
                                        {DEMO_CATEGORY_LABEL[g.category] || g.category}
                                      </span>
                                      <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)',fontSize:15,whiteSpace:'pre-line',wordBreak:'break-all'}}>{name}</span>
                                    </div>
                                    <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
                                      {/* 상태 미니 배지 */}
                                      {availCnt > 0 && <span title={`대여 가능: ${availCnt}`} style={{padding:'2px 8px',borderRadius:9999,fontSize:11,fontWeight:700,background:'rgba(0,152,68,0.12)',color:'var(--success)'}}>가용 {availCnt}</span>}
                                      {loanedCnt > 0 && <span title={`대여 중: ${loanedCnt}`} style={{padding:'2px 8px',borderRadius:9999,fontSize:11,fontWeight:700,background:'rgba(230,81,0,0.12)',color:'var(--warning)'}}>대여 {loanedCnt}</span>}
                                      {brokenCnt > 0 && <span title={`점검·불량: ${brokenCnt}`} style={{padding:'2px 8px',borderRadius:9999,fontSize:11,fontWeight:700,background:'rgba(186,26,26,0.12)',color:'var(--danger)'}}>점검 {brokenCnt}</span>}
                                      {/* 총 개수 */}
                                      <span style={{padding:'3px 10px',borderRadius:9999,fontSize:12,fontWeight:700,background:'#ede9fe',color:'var(--purple)'}}>{g.items.length}대</span>
                                      <Icon name={expanded?'expand_less':'expand_more'} size={20} style={{color:'var(--fg-3)',transition:'transform 0.15s'}}/>
                                    </div>
                                  </button>
                                  {/* 펼침 영역 */}
                                  {expanded && (
                                    <div style={{borderTop:'1px solid var(--border)',background:'var(--bg-subtle)'}}>
                                      {/* v7.337: 카테고리 변경 — 이 모델의 전체 대수에 한 번에 적용된다.
                                          헤더(<button>) 안이 아니라 여기 둔 이유: button 안에 select 를 넣으면
                                          유효하지 않은 마크업이고, 클릭이 접기/펼치기로 새어 나간다. */}
                                      <div style={{display:'flex',alignItems:'center',gap:8,padding:'9px 18px',borderBottom:'1px solid var(--border)',flexWrap:'wrap'}}>
                                        <Icon name="category" size={14} style={{color:'var(--fg-3)'}}/>
                                        <span style={{fontSize:11.5,fontWeight:700,color:'var(--fg-2)'}}>카테고리</span>
                                        <select value={g.category || ''}
                                          disabled={demoCatBusy === name}
                                          onChange={ev=>{ const v=ev.target.value; if(v && v!==g.category) changeGroupCategory(name, g.items, v); }}
                                          style={{padding:'4px 10px',border:`1px solid ${catColor}`,borderRadius:7,fontSize:11.5,fontWeight:700,
                                            color:catColor,background:catColor+'14',outline:'none',cursor:demoCatBusy===name?'wait':'pointer',fontFamily:'inherit'}}>
                                          {!DEMO_CATEGORY_LABEL[g.category] && <option value={g.category || ''}>{g.category || '(미분류)'}</option>}
                                          {Object.entries(DEMO_CATEGORY_LABEL).map(([v,l])=><option key={v} value={v}>{l}</option>)}
                                        </select>
                                        <span style={{fontSize:11,color:'var(--fg-3)'}}>
                                          {demoCatBusy === name ? '변경 중…' : `이 모델 ${g.items.length}대에 함께 적용됩니다`}
                                        </span>
                                      </div>
                                      <div style={{overflowX:'auto'}}>
                                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12.5}}>
                                          <thead style={{background:'var(--bg-mute)'}}>
                                            <tr>
                                              {['#','시리얼','상태','모델/구성품','위치','비고','추가일'].map(h=>(
                                                <th key={h} style={{padding:'8px 12px',textAlign:'left',fontSize:10,fontWeight:700,color:'var(--fg-2)',textTransform:'uppercase',letterSpacing:'0.04em',borderBottom:'1px solid var(--border-strong)',whiteSpace:'nowrap'}}>{h}</th>
                                              ))}
                                            </tr>
                                          </thead>
                                          <tbody>
                                            {g.items.map((e, idx) => {
                                              const isLoaned = e.location && e.location.startsWith('대여중');
                                              const stColor = DEMO_STATUS_COLOR[e.status] || DEMO_STATUS_COLOR['확인불가'];
                                              return (
                                                <tr key={e.id} style={{borderBottom:'1px solid var(--border)',background:isLoaned?'rgba(230,81,0,0.04)':'var(--bg-card)'}}>
                                                  <td style={{padding:'7px 12px',color:'#bbb',fontFamily:'monospace',width:30}}>{idx+1}</td>
                                                  <td style={{padding:'7px 12px',fontFamily:'monospace',color:'var(--fg-1)',fontWeight:600,whiteSpace:'nowrap'}}>{e.serial_number || <span style={{color:'#bbb'}}>—</span>}</td>
                                                  {/* v7.35: 상태 — 보이는 배지(span) + 투명 select 오버레이 (native 화살표 완전 제거) */}
                                                  <td style={{padding:'7px 12px',whiteSpace:'nowrap'}}>
                                                    <div style={{position:'relative',display:'inline-block'}}>
                                                      <span style={{
                                                        display:'inline-flex',
                                                        alignItems:'center',
                                                        gap:4,
                                                        padding:'2px 20px 2px 8px',
                                                        borderRadius:9999,
                                                        fontSize:10,
                                                        fontWeight:700,
                                                        background:stColor.bg,
                                                        color:stColor.fg,
                                                        border:`1px solid ${stColor.fg}33`,
                                                        cursor:'pointer',
                                                        userSelect:'none',
                                                      }}>
                                                        {e.status || '확인불가'}
                                                      </span>
                                                      <svg width="9" height="9" viewBox="0 0 24 24" fill="none"
                                                        stroke={stColor.fg} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                                                        style={{position:'absolute',right:6,top:'50%',transform:'translateY(-50%)',pointerEvents:'none'}}>
                                                        <polyline points="6 9 12 15 18 9"/>
                                                      </svg>
                                                      <select
                                                        value={e.status || '확인불가'}
                                                        onChange={ev => saveEquipmentField(e.id, 'status', ev.target.value)}
                                                        title="상태 변경"
                                                        style={{
                                                          position:'absolute',
                                                          top:0,left:0,
                                                          width:'100%',height:'100%',
                                                          opacity:0,
                                                          cursor:'pointer',
                                                          border:'none',
                                                          padding:0,margin:0,
                                                          background:'transparent',
                                                          fontSize:10,
                                                          fontFamily:'inherit',
                                                        }}
                                                      >
                                                        <option value="정상작동">정상작동</option>
                                                        <option value="불량">불량</option>
                                                        <option value="수리중">수리중</option>
                                                        <option value="점검중">점검중</option>
                                                        <option value="확인불가">확인불가</option>
                                                      </select>
                                                    </div>
                                                  </td>
                                                  <td style={{padding:'7px 12px',fontFamily:'monospace',fontSize:11,color:'var(--fg-2)',maxWidth:200,wordBreak:'break-all'}}>{e.model_detail || <span style={{color:'#bbb'}}>—</span>}</td>
                                                  {/* v7.246: 위치 + [회수] — 대여 연결이 끊긴 이관 장비를 손으로 리버 복귀 */}
                                                  <td style={{padding:'7px 12px',whiteSpace:'nowrap',color:isLoaned?'var(--warning)':'var(--success)',fontWeight:600,fontSize:11}}>
                                                    <span style={{display:'inline-flex',alignItems:'center',gap:6}}>
                                                      <span>
                                                        <Icon name={isLoaned?'pending':'check_circle'} size={11} style={{verticalAlign:'middle',marginRight:3}}/>
                                                        {e.location || '—'}
                                                      </span>
                                                      {e.location !== '리버' && (
                                                        <button
                                                          disabled={demoRestoringId === e.id}
                                                          onClick={ev => { ev.stopPropagation(); restoreEquipment(e); }}
                                                          title="위치를 '리버'로 되돌립니다"
                                                          style={{
                                                            display:'inline-flex',alignItems:'center',gap:3,
                                                            padding:'2px 8px',borderRadius:6,
                                                            border:'1px solid var(--purple)',
                                                            background: demoRestoringId === e.id ? '#e1bee7' : 'rgba(123,31,162,0.08)',
                                                            color:'var(--purple)',fontSize:10.5,fontWeight:800,
                                                            cursor: demoRestoringId === e.id ? 'default' : 'pointer',
                                                            fontFamily:'inherit',transition:'all 0.15s',
                                                          }}
                                                          onMouseEnter={ev=>{ if(demoRestoringId!==e.id){ ev.currentTarget.style.background='var(--purple-solid)'; ev.currentTarget.style.color='#fff'; }}}
                                                          onMouseLeave={ev=>{ if(demoRestoringId!==e.id){ ev.currentTarget.style.background='rgba(123,31,162,0.08)'; ev.currentTarget.style.color='var(--purple)'; }}}>
                                                          <Icon name={demoRestoringId === e.id ? 'progress_activity' : 'assignment_return'} size={11}
                                                            style={{animation: demoRestoringId === e.id ? 'spin 1s linear infinite' : 'none'}}/>
                                                          {demoRestoringId === e.id ? '처리 중' : '회수'}
                                                        </button>
                                                      )}
                                                    </span>
                                                  </td>
                                                  {/* v7.35: 비고 — 클릭 시 input 전환 (Enter/Blur 저장, Esc 취소) */}
                                                  <td style={{padding:'7px 12px',fontSize:11,color:'var(--fg-3)',maxWidth:280,minWidth:120}}>
                                                    {ds.editingNote && ds.editingNote.id === e.id ? (
                                                      <input
                                                        autoFocus
                                                        type="text"
                                                        value={ds.editingNote.draft}
                                                        onChange={ev => setDemoState(s => ({...s, editingNote:{ id:e.id, draft:ev.target.value }}))}
                                                        onBlur={async () => {
                                                          const draft = ds.editingNote ? ds.editingNote.draft : '';
                                                          if (draft !== (e.note || '')) {
                                                            await saveEquipmentField(e.id, 'note', draft);
                                                          }
                                                          setDemoState(s => ({...s, editingNote:null}));
                                                        }}
                                                        onKeyDown={ev => {
                                                          if (ev.key === 'Enter') { ev.preventDefault(); ev.currentTarget.blur(); }
                                                          else if (ev.key === 'Escape') {
                                                            ev.preventDefault();
                                                            setDemoState(s => ({...s, editingNote:null}));
                                                          }
                                                        }}
                                                        placeholder="비고 입력…"
                                                        style={{width:'100%',padding:'3px 6px',border:'1.5px solid var(--purple)',borderRadius:4,fontSize:11,fontFamily:'inherit',outline:'none',color:'var(--fg-1)',background:'var(--bg-card)'}}
                                                      />
                                                    ) : (
                                                      <span
                                                        onClick={() => setDemoState(s => ({...s, editingNote:{ id:e.id, draft:e.note || '' }}))}
                                                        title="클릭하여 비고 편집"
                                                        style={{display:'inline-block',width:'100%',minHeight:16,cursor:'text',padding:'2px 4px',borderRadius:3,color:e.note?'var(--fg-3)':'#bbb',fontStyle:e.note?'normal':'italic'}}
                                                        onMouseEnter={ev => ev.currentTarget.style.background = 'var(--bg-mute)'}
                                                        onMouseLeave={ev => ev.currentTarget.style.background = 'transparent'}
                                                      >
                                                        {e.note || '+ 비고 입력'}
                                                      </span>
                                                    )}
                                                  </td>
                                                  <td style={{padding:'7px 12px',fontSize:10,color:'#bbb',fontFamily:'monospace',whiteSpace:'nowrap'}}>
                                                    {e.added_at ? new Date(e.added_at).toLocaleDateString('ko-KR') : '—'}
                                                  </td>
                                                </tr>
                                              );
                                            })}
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* ─── 탭: 활성 대여 ─── */}
                {ds.tab==='rentals' && (
                  <Card style={{padding:0,overflow:'hidden'}}>
                    {ds.activeRentals.length === 0 ? (
                      <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>
                        <Icon name="local_shipping" size={28} style={{display:'block',margin:'0 auto 8px',opacity:0.4}}/>
                        활성 대여 없음
                      </div>
                    ) : (
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                          <thead style={{background:'#f6f7fb'}}>
                            <tr>
                              {['출고번호','업체','출고일','회수예정','담당자','요청자','대여품목','비고','처리'].map(h=>(
                                <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:11,fontWeight:700,color:'var(--fg-2)',textTransform:'uppercase',letterSpacing:'0.04em',borderBottom:'1.5px solid var(--border-strong)',whiteSpace:'nowrap'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {ds.activeRentals.map(r => {
                              // 회수 예정일 지남 여부
                              const isOverdue = r.expected_return_date && new Date(r.expected_return_date) < new Date(today());
                              // v7.151: pending 상태 표시 (출고 대기)
                              const isPending = r.status === 'pending';
                              return (
                                <tr key={r.id} style={{borderBottom:'1px solid var(--border-mute)',background:isPending?'rgba(37,99,235,0.04)':(isOverdue?'rgba(186,26,26,0.04)':'var(--bg-card)')}}>
                                  <td style={{padding:'9px 12px',fontFamily:'monospace',fontWeight:600,color:'var(--purple)',whiteSpace:'nowrap'}}>
                                    {r.shipment_number}
                                    {isPending && (
                                      <span title="배송시트지에서 [제품 출고]하면 대여가 시작됩니다"
                                        style={{marginLeft:6,padding:'2px 7px',borderRadius:9999,background:'#eff6ff',color:'#2563eb',fontSize:10,fontWeight:800,border:'1px solid #dbeafe'}}>
                                        출고 대기
                                      </span>
                                    )}
                                  </td>
                                  <td style={{padding:'9px 12px',fontWeight:700,color:'var(--fg-1)',whiteSpace:'nowrap'}}>{r.company}</td>
                                  <td style={{padding:'9px 12px',whiteSpace:'nowrap',color:'var(--fg-2)',fontFamily:'monospace',fontSize:12}}>{r.shipment_date || '—'}</td>
                                  <td style={{padding:'9px 12px',whiteSpace:'nowrap',color:isOverdue?'var(--danger)':'var(--fg-2)',fontFamily:'monospace',fontSize:12,fontWeight:isOverdue?700:400}}>
                                    {r.expected_return_date || '—'}
                                    {isOverdue && !isPending && <span style={{marginLeft:5,padding:'1px 6px',borderRadius:9999,background:'var(--danger-solid)',color:'#fff',fontSize:10}}>지연</span>}
                                  </td>
                                  <td style={{padding:'9px 12px',whiteSpace:'nowrap',fontSize:12,color:'var(--fg-2)'}}>{r.managers || '—'}</td>
                                  <td style={{padding:'9px 12px',whiteSpace:'nowrap',fontSize:12,color:'var(--fg-2)'}}>{r.requester || '—'}</td>
                                  <td style={{padding:'9px 12px',fontSize:11,color:'var(--fg-2)',fontFamily:'monospace',whiteSpace:'pre-line',maxWidth:260,lineHeight:1.5}}>{r.rental_items_text || ''}</td>
                                  {/* v7.330: 더블클릭하면 여기서 바로 고친다.
                                      단일 클릭이 아니라 더블클릭인 이유 — 이 행은 [이력]/[수정]/[회수] 버튼이
                                      같이 있어서, 클릭 편집이면 표를 훑다가 실수로 편집 상태에 들어간다. */}
                                  <td style={{padding:'9px 12px',fontSize:11,color:'var(--fg-3)',maxWidth:280,verticalAlign:'top'}}
                                    onDoubleClick={()=>{
                                      if (ds.editingRentalNote?.id === r.id) return;
                                      rentalNoteCancelRef.current = false;
                                      setDemoState(st => ({ ...st, editingRentalNote:{ id:r.id, draft:r.notes || '' } }));
                                    }}
                                    title={ds.editingRentalNote?.id === r.id ? '' : '더블클릭하여 비고 편집'}>
                                    {ds.editingRentalNote?.id === r.id ? (
                                      <>
                                        <textarea autoFocus
                                          value={ds.editingRentalNote.draft}
                                          onChange={ev=>setDemoState(st => ({ ...st, editingRentalNote:{ id:r.id, draft:ev.target.value } }))}
                                          onBlur={ev=>saveRentalNote(r, ev.target.value)}
                                          onKeyDown={ev=>{
                                            if (ev.key === 'Escape') {
                                              ev.preventDefault();
                                              rentalNoteCancelRef.current = true;
                                              setDemoState(st => ({ ...st, editingRentalNote: null }));
                                            } else if (ev.key === 'Enter' && (ev.ctrlKey || ev.metaKey)) {
                                              // 비고는 여러 줄이라 Enter 는 줄바꿈으로 둔다. 저장은 Ctrl+Enter.
                                              ev.preventDefault();
                                              ev.currentTarget.blur();
                                            }
                                          }}
                                          rows={Math.min(10, Math.max(3, String(ds.editingRentalNote.draft || '').split('\n').length + 1))}
                                          placeholder="비고 입력…"
                                          style={{width:'100%',minWidth:220,fontSize:11,lineHeight:1.5,padding:'6px 8px',
                                            border:'1.5px solid var(--purple)',borderRadius:6,outline:'none',resize:'vertical',
                                            background:'var(--bg-card)',color:'var(--fg-1)',fontFamily:'inherit',boxSizing:'border-box'}}/>
                                        <div style={{marginTop:3,fontSize:10,color:'var(--fg-3)'}}>
                                          Ctrl+Enter 저장 · Esc 취소 · 빈 칸 밖 클릭해도 저장됩니다
                                        </div>
                                      </>
                                    ) : (
                                      <span style={{display:'block',whiteSpace:'pre-wrap',lineHeight:1.5,cursor:'text',minHeight:14}}>
                                        {r.notes || <span style={{opacity:0.45}}>+ 비고 입력</span>}
                                      </span>
                                    )}
                                  </td>
                                  <td style={{padding:'9px 12px',whiteSpace:'nowrap'}}>
                                    <div style={{display:'inline-flex',alignItems:'center',gap:5}}>
                                      <button onClick={()=>openDemoRentalHistory(r)} title="수정 이력"
                                        style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 9px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        <Icon name="history" size={12}/> 이력
                                      </button>
                                      <button onClick={()=>openDemoLendEditModal(r)} title="대여 수정"
                                        style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 11px',background:'var(--bg-card)',color:'var(--purple)',border:'1.5px solid var(--purple)',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                        <Icon name="edit" size={12}/> 수정
                                      </button>
                                      {/* v7.151: pending은 회수 불가 (아직 출고 안 됨) — 배송시트지로 이동 링크 */}
                                      {isPending ? (
                                        <button onClick={()=>setPage('txlog')} title="배송시트지에서 이 건을 출고 처리하세요"
                                          style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 11px',background:'#2563eb',color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                          <Icon name="open_in_new" size={12}/> 배송시트지
                                        </button>
                                      ) : (
                                        <button onClick={()=>openDemoReturnModal(r)}
                                          style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 11px',background:'var(--success-solid)',color:'#fff',border:'none',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                                          <Icon name="assignment_return" size={12}/> 회수
                                        </button>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}

                {/* ─── 탭: 회수 이력 ─── */}
                {ds.tab==='returns' && (
                  <Card style={{padding:0,overflow:'hidden'}}>
                    {ds.returns.length === 0 ? (
                      <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>
                        <Icon name="history" size={28} style={{display:'block',margin:'0 auto 8px',opacity:0.4}}/>
                        회수 이력 없음
                      </div>
                    ) : (
                      <div style={{overflowX:'auto'}}>
                        <table style={{width:'100%',borderCollapse:'collapse',fontSize:13}}>
                          <thead style={{background:'#f6f7fb'}}>
                            <tr>
                              {['회수번호','원 출고번호','업체','출고일','회수일','담당자','대여품목','비고'].map(h=>(
                                <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:11,fontWeight:700,color:'var(--fg-2)',textTransform:'uppercase',letterSpacing:'0.04em',borderBottom:'1.5px solid var(--border-strong)',whiteSpace:'nowrap'}}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {ds.returns.map(r => (
                              <tr key={r.id} style={{borderBottom:'1px solid var(--border-mute)'}}>
                                <td style={{padding:'9px 12px',fontFamily:'monospace',fontWeight:600,color:'var(--purple)',whiteSpace:'nowrap'}}>{r.return_number}</td>
                                <td style={{padding:'9px 12px',fontFamily:'monospace',color:'var(--fg-3)',whiteSpace:'nowrap',fontSize:12}}>{r.original_shipment_number || '—'}</td>
                                <td style={{padding:'9px 12px',fontWeight:700,color:'var(--fg-1)',whiteSpace:'nowrap'}}>{r.company}</td>
                                <td style={{padding:'9px 12px',whiteSpace:'nowrap',color:'var(--fg-2)',fontFamily:'monospace',fontSize:12}}>{r.shipment_date || '—'}</td>
                                <td style={{padding:'9px 12px',whiteSpace:'nowrap',color:'var(--success)',fontFamily:'monospace',fontSize:12,fontWeight:700}}>{r.return_date}</td>
                                <td style={{padding:'9px 12px',whiteSpace:'nowrap',fontSize:12,color:'var(--fg-2)'}}>{r.managers || '—'}</td>
                                <td style={{padding:'9px 12px',fontSize:11,color:'var(--fg-2)',fontFamily:'monospace',whiteSpace:'pre-line',maxWidth:260,lineHeight:1.5}}>{r.rental_items_text || ''}</td>
                                <td style={{padding:'9px 12px',fontSize:11,color:'var(--fg-3)',maxWidth:280}}>{r.notes || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </Card>
                )}
              </div>
            );
          })()}

          {/* ═══ 🧪 v7.33 DEMO LEND PAGE (데모 대여 등록 — 별도 페이지) ═══ */}
          {page==='demo_lend' && demoLendModal && (() => {
            const m = demoLendModal;
            return (
              <div>
                {/* Header */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20,gap:16,flexWrap:'wrap'}}>
                  <div>
                    <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                      <Icon name={m.editMode ? "edit" : "local_shipping"} size={32} style={{color:'var(--purple)'}}/>
                      {m.editMode ? `데모 대여 수정 (${m.editingShipmentNumber || ''})` : '데모 제품 대여 입력'}
                    </h1>
                    <p style={{fontSize:15,color:'var(--fg-2)'}}>
                      {m.editMode
                        ? '대여 정보와 품목을 수정합니다. 품목 추가/제거 시 demo_equipment 상태가 자동 동기화되고, 배송시트지에도 반영됩니다.'
                        : '대분류를 선택하고 제품을 선택한 뒤 대여 정보를 입력하세요'}
                    </p>
                  </div>
                  {/* v7.429: 배송시트지에서 [품목 수정] 으로 진입했으면 그 배송건으로 돌아간다 */}
                  <button onClick={()=>{ if (m.returnTo==='txlog') { if (m.returnSearch) setLogSearch(m.returnSearch); setPage('txlog'); } else { setPage('demo'); } setDemoLendModal(null); }}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'10px 18px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:10,fontSize:13,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}>
                    <Icon name="arrow_back" size={16}/> {m.returnTo==='txlog' ? '배송시트지로' : '데모 관리로'}
                  </button>
                </div>

                {/* 1단계: 카테고리 선택 */}
                <Card style={{padding:20,marginBottom:16,borderTop:'3px solid var(--purple)'}}>
                  <h3 style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',marginBottom:14,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>1단계: 대분류 선택</h3>
                  <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                    {Object.entries(DEMO_CATEGORY_LABEL).map(([v,l]) => {
                      const sel = m.searchCategory === v;
                      const color = DEMO_CATEGORY_COLOR[v];
                      return (
                        <button key={v} onClick={()=>searchDemoLendItems(m.search, v)}
                          style={{padding:'10px 16px',border:`1.5px solid ${sel?color:'var(--fg-disabled)'}`,borderRadius:10,fontSize:13,fontWeight:700,cursor:'pointer',background:sel?color:'#fff',color:sel?'#fff':color,minWidth:120}}>
                          {l}
                        </button>
                      );
                    })}
                    {m.searchCategory && (
                      <button onClick={()=>searchDemoLendItems(m.search, '')}
                        style={{padding:'10px 16px',border:'1.5px dashed var(--border-strong)',borderRadius:10,fontSize:13,fontWeight:600,cursor:'pointer',background:'var(--bg-card)',color:'var(--fg-3)'}}>
                        선택 해제
                      </button>
                    )}
                  </div>
                </Card>

                {/* 2단계 + 3단계 좌우 분할 */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16,marginBottom:16}}>
                  {/* 2단계: 제품 선택 */}
                  <Card style={{padding:20,borderTop:'3px solid var(--purple)',minHeight:400,display:'flex',flexDirection:'column'}}>
                    <h3 style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',marginBottom:14,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>2단계: 제품 선택</h3>
                    {!m.searchCategory ? (
                      <div style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',gap:14}}>
                        <Icon name="touch_app" size={48} style={{color:'var(--purple)',opacity:0.4}}/>
                        <p style={{fontSize:14}}>대분류를 먼저 선택하세요</p>
                      </div>
                    ) : (
                      <>
                        <div style={{position:'relative',marginBottom:12}}>
                          <Icon name="search" size={16} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                          <input value={m.search} onChange={e=>searchDemoLendItems(e.target.value, m.searchCategory)}
                            placeholder="모델 · 시리얼로 검색 (선택 안 하면 전체)"
                            style={{width:'100%',padding:'9px 12px 9px 36px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'monospace',boxSizing:'border-box'}}
                            onFocus={e=>e.target.style.borderColor='var(--purple)'}
                            onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                        </div>
                        <div style={{flex:1,maxHeight:500,overflowY:'auto'}}>
                          {m.searchResults.length === 0 ? (
                            <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:12,background:'var(--bg-subtle)',border:'1px solid var(--border)',borderRadius:7}}>
                              대여 가능 장비가 없습니다
                            </div>
                          ) : (() => {
                            // v7.34: 모델별 그룹화
                            const groups = {};
                            m.searchResults.forEach(eq => {
                              const k = eq.model || '(미분류)';
                              if (!groups[k]) groups[k] = { model: eq.model, category: eq.category, items: [] };
                              groups[k].items.push(eq);
                            });
                            const sortedGroups = Object.values(groups).sort((a,b) => (a.model||'').localeCompare(b.model||''));
                            const toggleGroup = (name) => {
                              setDemoLendModal(s => {
                                if (!s) return s;
                                const next = new Set(s.expandedModels || new Set());
                                if (next.has(name)) next.delete(name); else next.add(name);
                                return { ...s, expandedModels: next };
                              });
                            };
                            // 모델 전체 선택/해제 토글
                            const toggleGroupAll = (group) => {
                              setDemoLendModal(s => {
                                if (!s) return s;
                                const groupIds = new Set(group.items.map(it => it.id));
                                const allSelected = group.items.every(it => s.selectedItems.find(x => x.id === it.id));
                                let next;
                                if (allSelected) {
                                  // 모두 해제
                                  next = s.selectedItems.filter(it => !groupIds.has(it.id));
                                } else {
                                  // 누락된 것 추가
                                  next = [...s.selectedItems];
                                  group.items.forEach(it => {
                                    if (!next.find(x => x.id === it.id)) next.push(it);
                                  });
                                }
                                return { ...s, selectedItems: next };
                              });
                            };
                            return (
                              <div style={{display:'flex',flexDirection:'column',gap:6}}>
                                {sortedGroups.map(g => {
                                  const name = g.model || '(미분류)';
                                  const expanded = (m.expandedModels || new Set()).has(name);
                                  const catColor = DEMO_CATEGORY_COLOR[g.category] || '#76777d';
                                  const selectedInGroup = g.items.filter(it => m.selectedItems.find(x => x.id === it.id)).length;
                                  const allInGroupSelected = g.items.length > 0 && selectedInGroup === g.items.length;
                                  const someSelected = selectedInGroup > 0 && !allInGroupSelected;
                                  return (
                                    <div key={name} style={{background:'var(--bg-card)',border:`1.5px solid ${expanded||selectedInGroup>0?catColor:'var(--border)'}`,borderRadius:8,overflow:'hidden'}}>
                                      {/* 그룹 헤더 */}
                                      <div style={{display:'flex',alignItems:'center',padding:'8px 12px',gap:8,background:expanded?catColor+'0a':'transparent'}}>
                                        {/* 전체 선택 체크박스 */}
                                        <button onClick={(e)=>{e.stopPropagation(); toggleGroupAll(g);}}
                                          title={allInGroupSelected?'모두 해제':'모두 선택'}
                                          style={{width:18,height:18,border:'none',background:'transparent',padding:0,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>
                                          <Icon name={allInGroupSelected?'check_box':someSelected?'indeterminate_check_box':'check_box_outline_blank'} size={18} style={{color:allInGroupSelected||someSelected?catColor:'#bbb'}}/>
                                        </button>
                                        <button onClick={()=>toggleGroup(name)}
                                          style={{flex:1,minWidth:0,display:'flex',alignItems:'center',gap:8,padding:0,border:'none',background:'transparent',cursor:'pointer',textAlign:'left'}}>
                                          <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)',fontSize:13,whiteSpace:'pre-line',wordBreak:'break-all',flex:1,minWidth:0}}>{name}</span>
                                          {selectedInGroup > 0 && (
                                            <span style={{padding:'1px 7px',borderRadius:9999,background:catColor,color:'#fff',fontSize:10,fontWeight:700,whiteSpace:'nowrap'}}>{selectedInGroup}/{g.items.length} 선택</span>
                                          )}
                                          <span style={{padding:'1px 7px',borderRadius:9999,background:'#ede9fe',color:'var(--purple)',fontSize:10,fontWeight:700,whiteSpace:'nowrap'}}>{g.items.length}대</span>
                                          <Icon name={expanded?'expand_less':'expand_more'} size={16} style={{color:'var(--fg-3)'}}/>
                                        </button>
                                      </div>
                                      {/* 펼침 — 시리얼별 체크박스 */}
                                      {expanded && (
                                        <div style={{borderTop:'1px solid var(--border)',background:'var(--bg-subtle)'}}>
                                          {g.items.map(eq => {
                                            const sel = !!m.selectedItems.find(x => x.id === eq.id);
                                            return (
                                              <button key={eq.id} onClick={()=>toggleDemoLendItem(eq)}
                                                style={{display:'flex',width:'100%',padding:'6px 12px 6px 38px',border:'none',background:sel?'rgba(123,31,162,0.06)':'transparent',cursor:'pointer',alignItems:'center',gap:6,borderBottom:'1px solid var(--border-mute)',textAlign:'left'}}>
                                                <Icon name={sel?'check_box':'check_box_outline_blank'} size={16} style={{color:sel?'var(--purple)':'#bbb'}}/>
                                                <span style={{fontFamily:'monospace',fontSize:11,color:'var(--fg-2)',flex:1,minWidth:0,wordBreak:'break-all'}}>{eq.serial_number || <span style={{color:'#bbb'}}>(시리얼 없음)</span>}</span>
                                                {eq.model_detail && <span style={{fontFamily:'monospace',fontSize:10,color:'var(--fg-3)',whiteSpace:'nowrap'}}>{eq.model_detail.length > 25 ? eq.model_detail.slice(0,25)+'…' : eq.model_detail}</span>}
                                              </button>
                                            );
                                          })}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>
                      </>
                    )}
                  </Card>

                  {/* 3단계: 선택 확인 및 대여 정보 */}
                  <Card style={{padding:20,borderTop:'3px solid var(--purple)'}}>
                    <h3 style={{fontSize:14,fontWeight:700,color:'var(--fg-1)',marginBottom:14,paddingBottom:8,borderBottom:'1px solid var(--border)'}}>3단계: 선택 확인 및 대여 정보</h3>
                    {/* 선택된 품목 */}
                    {m.selectedItems.length === 0 ? (
                      <div style={{padding:'30px 20px',textAlign:'center',color:'var(--fg-3)',background:'var(--bg-subtle)',borderRadius:8,marginBottom:16}}>
                        <Icon name="check_box_outline_blank" size={36} style={{color:'var(--purple)',opacity:0.3,marginBottom:8}}/>
                        <p style={{fontSize:13}}>제품을 선택하면 여기에 표시됩니다</p>
                      </div>
                    ) : (
                      <div style={{padding:12,background:'rgba(123,31,162,0.06)',borderRadius:8,marginBottom:16,border:'1px solid rgba(123,31,162,0.2)'}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--purple)',marginBottom:6,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                          <span>선택된 품목 · <strong>{m.selectedItems.length}대</strong></span>
                          <button onClick={()=>setDemoLendModal(s=>s?{...s, selectedItems:[]}:s)}
                            style={{padding:'2px 8px',background:'transparent',border:'none',color:'var(--danger)',cursor:'pointer',fontSize:11,fontWeight:700}}>모두 제거</button>
                        </div>
                        <div style={{display:'flex',flexWrap:'wrap',gap:4,maxHeight:120,overflowY:'auto'}}>
                          {m.selectedItems.map(it => (
                            <span key={it.id} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'2px 4px 2px 8px',borderRadius:5,background:'var(--bg-card)',border:'1px solid rgba(123,31,162,0.3)',fontSize:11,fontFamily:'monospace',color:'var(--purple)'}}>
                              {it.model}{it.serial_number?'·'+it.serial_number:''}
                              <button onClick={()=>toggleDemoLendItem(it)} style={{width:14,height:14,border:'none',background:'transparent',borderRadius:3,cursor:'pointer',color:'var(--danger)',padding:0,display:'inline-flex',alignItems:'center',justifyContent:'center'}}>
                                <Icon name="close" size={10}/>
                              </button>
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {/* 폼 */}
                    <div style={{display:'flex',flexDirection:'column',gap:11}}>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>업체명 *</label>
                        <input value={m.company} onChange={e=>setDemoLendModal(s=>s?{...s,company:e.target.value}:s)} placeholder="업체명 입력"
                          style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>출고날짜 *</label>
                          <input type="date" value={m.shipment_date} onChange={e=>setDemoLendModal(s=>s?{...s,shipment_date:e.target.value}:s)}
                            style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>회수 예정날짜</label>
                          <input type="date" value={m.expected_return_date} onChange={e=>setDemoLendModal(s=>s?{...s,expected_return_date:e.target.value}:s)}
                            style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                        </div>
                      </div>
                      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>담당자 *</label>
                          <select value={m.managers} onChange={e=>setDemoLendModal(s=>s?{...s,managers:e.target.value}:s)}
                            style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',background:'var(--bg-card)',cursor:'pointer',boxSizing:'border-box'}}>
                            <option value="">담당자 선택</option>
                            {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>요청자</label>
                          <select value={m.requester} onChange={e=>setDemoLendModal(s=>s?{...s,requester:e.target.value}:s)}
                            style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',background:'var(--bg-card)',cursor:'pointer',boxSizing:'border-box'}}>
                            <option value="">요청자 선택 (선택)</option>
                            {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>특이사항</label>
                        <textarea value={m.notes} onChange={e=>setDemoLendModal(s=>s?{...s,notes:e.target.value}:s)} rows={3} placeholder="추가 안내사항, 특이사항 등"
                          style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box'}}/>
                      </div>
                      {/* v7.41: 배송시트지 자동 등록을 위한 배송지 정보 */}
                      <div style={{marginTop:6,paddingTop:11,borderTop:'1px dashed var(--border)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:9}}>
                          <Icon name="local_shipping" size={14} style={{color:'var(--purple)'}}/>
                          <span style={{fontSize:11,fontWeight:700,color:'var(--purple)',letterSpacing:'0.03em'}}>배송 정보 — 배송시트지 자동 등록</span>
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:9}}>
                          <div>
                            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>배송 방법</label>
                            <div style={{display:'grid',gridTemplateColumns:'repeat(5, 1fr)',gap:5}}>
                              {['택배','퀵 서비스','방문수령','1층배송','직접 설치'].map(method => {
                                const sel = m.ship_method === method;
                                return (
                                  <button key={method} type="button" onClick={()=>setDemoLendModal(s=>s?{...s,ship_method:method}:s)}
                                    style={{padding:'7px 4px',border:`1.5px solid ${sel?'var(--purple)':'var(--border)'}`,borderRadius:7,fontSize:11,fontWeight:600,cursor:'pointer',background:sel?'rgba(123,31,162,0.08)':'var(--bg-card)',color:sel?'var(--purple)':'var(--fg-2)'}}>
                                    {method}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                          <div>
                            <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>배송지 주소</label>
                            <input value={m.address} onChange={e=>setDemoLendModal(s=>s?{...s,address:e.target.value}:s)} placeholder="예: 경기도 분당구 판교로 235 H스퀘어 N동 201호"
                              style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                          </div>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>수령인</label>
                              <input value={m.recipient} onChange={e=>setDemoLendModal(s=>s?{...s,recipient:e.target.value}:s)} placeholder="이름"
                                style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                            </div>
                            <div>
                              <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>수령인 연락처</label>
                              <input value={m.recipient_phone} onChange={e=>setDemoLendModal(s=>s?{...s,recipient_phone:phoneOnChange(s.recipient_phone, e.target.value)}:s)} placeholder="010-0000-0000"
                                style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>

                {/* 결과 메시지 + 등록 버튼 */}
                {m.result && (
                  <div style={{padding:'10px 14px',borderRadius:8,fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:8,marginBottom:12,
                    background:m.result.type==='ok'?'rgba(0,152,68,0.1)':'rgba(186,26,26,0.1)',
                    color:m.result.type==='ok'?'var(--success)':'var(--danger)'}}>
                    <Icon name={m.result.type==='ok'?'check_circle':'error'} size={16}/>{m.result.msg}
                  </div>
                )}
                <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                  <button onClick={()=>{ if (m.returnTo==='txlog') { if (m.returnSearch) setLogSearch(m.returnSearch); setPage('txlog'); } else { setPage('demo'); } setDemoLendModal(null); }} disabled={m.loading}
                    style={{padding:'12px 24px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:10,fontSize:14,fontWeight:600,cursor:'pointer'}}>취소</button>
                  <button onClick={submitDemoLend}
                    disabled={m.loading || !m.company.trim() || m.selectedItems.length===0 || !m.managers}
                    style={{
                      padding:'12px 28px',
                      background:m.loading||!m.company.trim()||m.selectedItems.length===0||!m.managers?'var(--surface-mute)':'var(--purple-solid)',
                      color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,
                      cursor:m.loading||!m.company.trim()||m.selectedItems.length===0||!m.managers?'not-allowed':'pointer',
                      display:'inline-flex',alignItems:'center',gap:6,
                      boxShadow:'0 4px 12px rgba(123,31,162,0.2)',
                    }}>
                    <Icon name={m.loading?'hourglass_empty':(m.editMode?'save':'local_shipping')} size={16}/>
                    {m.loading
                      ? (m.editMode ? '수정 중…' : '등록 중…')
                      : (m.editMode
                          ? `수정 저장 (${m.selectedItems.length}대)`
                          : `대여 등록 실행 (${m.selectedItems.length}대)`)}
                  </button>
                </div>
              </div>
            );
          })()}

          {/* ═══ PRODUCT CATALOG (제품 카탈로그 관리) ═══ */}
          {page==='catalog' && (
            <>
              {/* v7.348: 관리 하위 이동 — 사이드바 아코디언을 접었거나 모바일이면 여기가 유일한 경로다 */}
              <AdminTabs page={page} onGo={(id)=>{setPage(id);setPg(0);setSearch('');setTxSubTab('list');setAdminOpen(true);}}/>
              <div style={{display:'flex',alignItems:'center',gap:14,marginBottom:24}}>
                <Icon name="menu_book" size={28} style={{color:'var(--accent)'}}/>
                <div>
                  <h1 style={{margin:0,fontSize:24,fontWeight:700,color:'var(--fg-1)'}}>제품 표준화 관리</h1>
                  <p style={{margin:'4px 0 0',fontSize:13,color:'var(--fg-3)'}}>마스터 제품 목록 — 입고 시 등록되는 모든 제품의 표준 정의</p>
                </div>
                <div style={{flex:1}}/>
                <span style={{padding:'6px 14px',background:'var(--bg-accent-soft)',color:'var(--accent)',borderRadius:9999,fontSize:13,fontWeight:700}}>총 {catalogTotal}개</span>
              </div>
              {/* 검색 + 필터 */}
              <Card style={{padding:14,marginBottom:18}}>
                <div style={{display:'flex',gap:10,alignItems:'center',flexWrap:'wrap'}}>
                  <div style={{position:'relative',flex:'1 1 300px',minWidth:240}}>
                    <Icon name="search" size={16} style={{position:'absolute',left:11,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                    <input value={catalogSearch} onChange={e=>{setCatalogSearch(e.target.value);setCatalogPg(0);}}
                      placeholder="제품명 검색…"
                      style={{width:'100%',padding:'8px 34px 8px 34px',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                    {catalogSearch && (
                      <button onClick={()=>{setCatalogSearch('');setCatalogPg(0);}} style={{position:'absolute',right:8,top:'50%',transform:'translateY(-50%)',width:22,height:22,border:'none',background:'transparent',borderRadius:9999,cursor:'pointer',color:'var(--fg-3)',padding:0,display:'flex',alignItems:'center',justifyContent:'center'}}>
                        <Icon name="close" size={14}/>
                      </button>
                    )}
                  </div>
                  <button onClick={()=>setBulkProductModal({text:'',loading:false,result:null})}
                    style={{padding:'8px 14px',background:'var(--bg-card)',color:'var(--accent)',border:'2px solid var(--accent)',borderRadius:9999,fontSize:13,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5,marginRight:6}}>
                    <Icon name="playlist_add" size={16}/> 일괄 등록
                  </button>
                  <button onClick={()=>setCatalogEditing({id:null,product_name:'',category:'',form_factor:'',isNew:true})}
                    style={{padding:'8px 16px',background:'var(--accent-solid)',color:'#fff',border:'none',borderRadius:9999,fontSize:13,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                    <Icon name="add" size={16}/> 신제품 등록
                  </button>
                </div>
                {/* v7.45: 카테고리 빠른 필터 칩 (미분류 + 등록된 카테고리들) */}
                <div style={{display:'flex',gap:6,marginTop:10,flexWrap:'wrap',alignItems:'center'}}>
                  <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:700,marginRight:4}}>카테고리:</span>
                  <button onClick={()=>{setCatalogCategoryFilter('');setCatalogPg(0);}}
                    style={{padding:'4px 11px',border:`1.5px solid ${catalogCategoryFilter===''?'var(--accent)':'var(--border)'}`,borderRadius:9999,fontSize:11,fontWeight:700,cursor:'pointer',background:catalogCategoryFilter===''?'var(--bg-accent-soft)':'var(--bg-card)',color:catalogCategoryFilter===''?'var(--accent)':'var(--fg-3)'}}>
                    전체
                  </button>
                  <button onClick={()=>{setCatalogCategoryFilter('__UNCAT__');setCatalogPg(0);}}
                    style={{padding:'4px 11px',border:`1.5px solid ${catalogCategoryFilter==='__UNCAT__'?'var(--purple)':'var(--border)'}`,borderRadius:9999,fontSize:11,fontWeight:700,cursor:'pointer',background:catalogCategoryFilter==='__UNCAT__'?'rgba(123,31,162,0.08)':'var(--bg-card)',color:catalogCategoryFilter==='__UNCAT__'?'var(--purple)':'var(--fg-3)',display:'inline-flex',alignItems:'center',gap:4}}>
                    <Icon name="help_outline" size={12}/> 미분류만
                  </button>
                  {productCategories.map(c => (
                    <button key={c} onClick={()=>{setCatalogCategoryFilter(c);setCatalogPg(0);}}
                      style={{padding:'4px 11px',border:`1.5px solid ${catalogCategoryFilter===c?'var(--accent)':'var(--border)'}`,borderRadius:9999,fontSize:11,fontWeight:700,cursor:'pointer',background:catalogCategoryFilter===c?'var(--bg-accent-soft)':'var(--bg-card)',color:catalogCategoryFilter===c?'var(--accent)':'var(--fg-3)'}}>
                      {c}
                    </button>
                  ))}
                </div>
              </Card>
              {/* v7.281: 시리얼 길이 일괄 지정 — 카테고리 필터가 걸려 있을 때만 노출.
                   HDD 35개를 하나씩 바꾸는 건 번거롭다. 지금 보이는 목록에만 적용한다. */}
              {catalogCategoryFilter && catalogCategoryFilter !== '__UNCAT__' && catalogItems.length > 0 && (
                <Card style={{padding:'12px 16px',marginBottom:14,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap'}}>
                  <Icon name="straighten" size={17} style={{color:'var(--accent)'}}/>
                  <span style={{fontSize:13,fontWeight:700,color:'var(--fg-1)'}}>
                    {catalogCategoryFilter} 시리얼 길이 일괄 지정
                  </span>
                  <span style={{fontSize:11.5,color:'var(--fg-3)'}}>
                    현재 목록 {catalogItems.length}개에 적용
                  </span>
                  <div style={{display:'flex',gap:5,marginLeft:'auto',flexWrap:'wrap'}}>
                    {[8,10,11,12].map(n => (
                      <button key={n} onClick={async ()=>{
                          if (!window.confirm(`${catalogCategoryFilter} 목록 ${catalogItems.length}개의 시리얼 길이를 ${n}자로 설정합니다.\n\n이미 지정된 값도 덮어씁니다. 계속할까요?`)) return;
                          let ok = 0, fail = 0;
                          for (const it of catalogItems) {
                            const r = await ProductService.updateSerialLen(it.id, n);
                            if (r.ok) ok++; else fail++;
                          }
                          setCatalogItems(prev => prev.map(x => ({ ...x, serial_min_len: n, serial_max_len: n })));
                          alert(`시리얼 길이 ${n}자 설정 완료 — 성공 ${ok}건${fail ? ` · 실패 ${fail}건` : ''}`);
                        }}
                        style={{padding:'6px 14px',fontSize:12,fontWeight:700,border:'1px solid var(--border-strong)',
                          borderRadius:7,background:'var(--bg-card)',color:'var(--fg-2)',cursor:'pointer',fontFamily:'inherit'}}>
                        {n}자
                      </button>
                    ))}
                    <button onClick={async ()=>{
                        if (!window.confirm(`${catalogCategoryFilter} 목록 ${catalogItems.length}개의 시리얼 길이 지정을 해제합니다.\n\n해제하면 스캔 시 형태를 학습하게 됩니다. 계속할까요?`)) return;
                        for (const it of catalogItems) await ProductService.updateSerialLen(it.id, null);
                        setCatalogItems(prev => prev.map(x => ({ ...x, serial_min_len: null, serial_max_len: null })));
                        alert('시리얼 길이 지정을 해제했습니다');
                      }}
                      style={{padding:'6px 14px',fontSize:12,fontWeight:700,border:'1px solid var(--border-strong)',
                        borderRadius:7,background:'var(--bg-card)',color:'var(--fg-3)',cursor:'pointer',fontFamily:'inherit'}}>
                      해제
                    </button>
                  </div>
                  <div style={{flexBasis:'100%',fontSize:11,color:'var(--fg-3)',lineHeight:1.5}}>
                    스캔 시 이 길이가 아닌 바코드(BOX ID·P/N·모델번호 등)는 시리얼로 받지 않습니다.
                    접두어는 저장하지 않습니다 — 생산 로트마다 바뀌므로 박아두면 다음 입고가 막힙니다.
                  </div>
                </Card>
              )}

              {/* 카탈로그 표 — v7.45: 카테고리 컬럼 인라인 편집 복원 (대시보드 fallback용) */}
              <Card style={{padding:0,overflow:'hidden'}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 170px 110px 140px 220px',background:'var(--bg-accent-soft)',padding:'12px 16px',borderBottom:'2px solid var(--border)',fontSize:12,fontWeight:700,color:'var(--fg-2)'}}>
                  <div>제품명</div>
                  <div>카테고리</div>
                  <div title="스캔 시 이 길이가 아닌 바코드는 시리얼로 받지 않습니다">시리얼 길이</div>
                  <div>등록일</div>
                  <div style={{textAlign:'right'}}>작업</div>
                </div>
                {catalogLoading ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>로딩 중…</div>
                ) : catalogItems.length === 0 ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>검색 결과가 없습니다</div>
                ) : (
                  catalogItems.map((it,idx) => {
                    const aliasCount = aliasCounts[it.product_name] || 0;
                    return (
                    <div key={it.id} style={{display:'grid',gridTemplateColumns:'1fr 170px 110px 140px 220px',padding:'12px 16px',borderBottom:'1px solid var(--border-mute)',fontSize:13,alignItems:'center'}}>
                      <div style={{fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)',wordBreak:'break-all',display:'flex',alignItems:'center',gap:8}}>
                        {it.product_name}
                        {aliasCount > 0 && (
                          <span title={`${aliasCount}개 별칭 등록됨`} style={{display:'inline-flex',alignItems:'center',gap:3,padding:'2px 7px',background:'rgba(0,152,68,0.1)',color:'var(--success)',borderRadius:9999,fontSize:10,fontWeight:700,fontFamily:'inherit'}}>
                            <Icon name="link" size={11}/> {aliasCount}
                          </span>
                        )}
                      </div>
                      {/* v7.45: 카테고리 인라인 편집 (미분류면 강조) */}
                      <div>
                        <select
                          value={it.category || ''}
                          onChange={async e => {
                            const newCat = e.target.value || null;
                            const { ok, msg } = await ProductService.updateMeta(it.id, { category: newCat, form_factor: it.form_factor || null });
                            if (!ok) { alert('카테고리 저장 실패: ' + msg); return; }
                            setCatalogItems(prev => prev.map(x => x.id === it.id ? { ...x, category: newCat } : x));
                          }}
                          title="카테고리 변경"
                          style={{
                            width:'100%',padding:'5px 8px',fontSize:11,fontWeight:700,
                            border:`1.5px solid ${it.category ? 'var(--border-strong)' : 'var(--purple)'}`,
                            borderRadius:6,
                            background:it.category ? 'var(--bg-card)' : 'rgba(123,31,162,0.06)',
                            color:it.category ? 'var(--fg-1)' : 'var(--purple)',
                            cursor:'pointer',outline:'none',fontFamily:'inherit',boxSizing:'border-box',
                          }}>
                          <option value="">{it.category ? '— 카테고리 해제 —' : '미분류 (지정 필요)'}</option>
                          {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                      </div>
                      {/* v7.281: 시리얼 길이 인라인 편집 — 카테고리 셀과 같은 방식.
                           스캔 시 이 길이로 BOX ID·P/N·모델번호 같은 비-시리얼 바코드를 걸러낸다.
                           ⚠ 접두어는 두지 않는다 — 생산 로트를 따라가므로 다음 입고가 막힌다. */}
                      <div>
                        <select
                          value={it.serial_min_len != null ? String(it.serial_min_len) : ''}
                          onChange={async e => {
                            const v = e.target.value === '' ? null : parseInt(e.target.value, 10);
                            const { ok, msg } = await ProductService.updateSerialLen(it.id, v);
                            if (!ok) { alert('시리얼 길이 저장 실패: ' + msg); return; }
                            setCatalogItems(prev => prev.map(x => x.id === it.id
                              ? { ...x, serial_min_len: v, serial_max_len: v } : x));
                          }}
                          title={it.serial_min_len != null
                            ? `시리얼 ${it.serial_min_len}자 — 이 길이가 아닌 바코드는 시리얼로 받지 않습니다`
                            : '미지정 — 스캔 시 형태를 학습합니다'}
                          style={{
                            width:'100%',padding:'5px 8px',fontSize:11,fontWeight:700,
                            border:`1.5px solid ${it.serial_min_len != null ? 'var(--border-strong)' : 'var(--border)'}`,
                            borderRadius:6,
                            background:it.serial_min_len != null ? 'var(--bg-card)' : 'var(--bg-subtle)',
                            color:it.serial_min_len != null ? 'var(--fg-1)' : 'var(--fg-3)',
                            cursor:'pointer',outline:'none',fontFamily:'inherit',boxSizing:'border-box',
                          }}>
                          <option value="">미지정</option>
                          {[6,7,8,9,10,11,12,13,14,15,16,20].map(n => (
                            <option key={n} value={n}>{n}자</option>
                          ))}
                        </select>
                      </div>
                      <div style={{color:'var(--fg-3)',fontSize:12}}>{it.created_at ? new Date(it.created_at).toLocaleDateString('ko-KR') : '-'}</div>
                      <div style={{textAlign:'right',display:'flex',gap:6,justifyContent:'flex-end'}}>
                        <button onClick={()=>openAliasManage(it.product_name)} title="별칭 관리"
                          style={{padding:'5px 10px',border:'1px solid var(--success)',background:aliasCount>0?'rgba(0,152,68,0.08)':'var(--bg-card)',color:'var(--success)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:3}}>
                          <Icon name="link" size={12}/> 별칭
                        </button>
                        <button onClick={()=>setCatalogEditing({...it})} title="수정"
                          style={{padding:'5px 12px',border:'1px solid var(--accent)',background:'var(--bg-card)',color:'var(--accent)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>수정</button>
                        <button onClick={()=>deleteCatalogItem(it)} title="삭제"
                          style={{padding:'5px 12px',border:'1px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>삭제</button>
                      </div>
                    </div>
                    );
                  })
                )}
              </Card>
              {/* 페이지네이션 */}
              {catalogTotal > CATALOG_PG_SIZE && (
                <div style={{display:'flex',gap:10,alignItems:'center',justifyContent:'center',marginTop:18}}>
                  <button disabled={catalogPg===0} onClick={()=>setCatalogPg(p=>Math.max(0,p-1))}
                    style={{padding:'7px 14px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:catalogPg===0?'default':'pointer',color:catalogPg===0?'#bbb':'var(--fg-2)',fontSize:13,fontWeight:600}}>‹ 이전</button>
                  <span style={{fontSize:13,color:'var(--fg-3)'}}>{catalogPg+1} / {Math.ceil(catalogTotal/CATALOG_PG_SIZE)}</span>
                  <button disabled={(catalogPg+1)*CATALOG_PG_SIZE>=catalogTotal} onClick={()=>setCatalogPg(p=>p+1)}
                    style={{padding:'7px 14px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:(catalogPg+1)*CATALOG_PG_SIZE>=catalogTotal?'default':'pointer',color:(catalogPg+1)*CATALOG_PG_SIZE>=catalogTotal?'#bbb':'var(--fg-2)',fontSize:13,fontWeight:600}}>다음 ›</button>
                </div>
              )}
            </>
          )}

          {/* ═══ KEEPS 관리 페이지 ═══ */}
          {page==='keeps' && (
            <>
              <div style={{padding:'20px 0',marginBottom:20}}>
                <h1 style={{fontSize:32,fontWeight:700,color:'var(--fg-1)',letterSpacing:'-0.02em',display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="bookmark" size={32} style={{color:'var(--purple)'}}/> 킵 관리
                </h1>
                <p style={{fontSize:15,color:'var(--fg-2)',marginTop:6}}>고객이 예약한 제품을 등록하고 관리합니다. 활성 킵 수량만큼 출고요청 시 차단됩니다.</p>
              </div>

              {/* ── 신규 킵 등록 폼 ── */}
              <Card style={{padding:24,marginBottom:20,background:'rgba(123,31,162,0.04)',border:'1.5px solid rgba(123,31,162,0.2)'}}>
                <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16}}>
                  <Icon name="add_circle" size={20} style={{color:'var(--purple)'}}/>
                  <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'#4a148c'}}>신규 킵 등록</h3>
                </div>
                {/* 1행: 공통 — 거래처 / 작성자 / 요청자 / 예상 출고일 */}
                <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr 130px',gap:12,alignItems:'flex-start',marginBottom:14}}>
                  <div style={{position:'relative'}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>거래처 *</label>
                    <input value={keepsForm.dealerSearch || keepsForm.dealer_name}
                      onChange={e=>searchKeepDealers(e.target.value)}
                      onBlur={()=>setTimeout(()=>setKeepsForm(f=>({...f, dealerResults:[]})), 150)}
                      placeholder="거래처명 또는 고객명"
                      style={{...inputStyle,fontSize:13,padding:'9px 12px'}}/>
                    {keepsForm.dealerResults.length > 0 && (
                      <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:9,marginTop:4,maxHeight:200,overflowY:'auto',zIndex:10,boxShadow:'0 4px 16px rgba(0,0,0,0.1)'}}>
                        {keepsForm.dealerResults.map(d => (
                          <div key={d.id} onClick={()=>setKeepsForm(f=>({...f, dealer_id: d.id, dealer_name: d.name, dealerSearch: d.name, dealerResults: []}))}
                            style={{padding:'8px 12px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)'}}
                            onMouseEnter={e=>e.currentTarget.style.background='#f5f0fa'}
                            onMouseLeave={e=>e.currentTarget.style.background=''}>
                            <div style={{fontSize:13,fontWeight:600}}>{d.name}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>작성자</label>
                    <select value={keepsForm.author} onChange={e=>setKeepsForm(f=>({...f, author: e.target.value}))}
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',cursor:'pointer'}}>
                      <option value="">선택</option>
                      {staffList.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>요청자</label>
                    <select value={keepsForm.requester} onChange={e=>setKeepsForm(f=>({...f, requester: e.target.value}))}
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',cursor:'pointer'}}>
                      <option value="">선택</option>
                      {staffList.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>예상 출고일</label>
                    <input type="date" value={keepsForm.expected_date}
                      onChange={e=>setKeepsForm(f=>({...f, expected_date: e.target.value}))}
                      style={{...inputStyle,fontSize:12,padding:'9px 12px'}}/>
                  </div>
                </div>

                {/* 2행: 품목 리스트 */}
                <div style={{marginBottom:14}}>
                  <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:8}}>
                    <span>품목 * <span style={{fontWeight:500,color:'var(--fg-3)'}}>(여러 개 추가 가능)</span></span>
                  </label>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {keepsForm.items.map((item, idx) => (
                      <div key={idx} style={{padding:'10px 12px',background:'var(--bg-card)',border:'1.5px solid rgba(123,31,162,0.2)',borderRadius:10,display:'grid',gridTemplateColumns:'2fr 80px 1.5fr 1.8fr 32px',gap:8,alignItems:'flex-start'}}>
                        {/* 제품 검색 */}
                        <div style={{position:'relative'}}>
                          {item.product_name ? (
                            <div style={{padding:'8px 10px',background:'rgba(123,31,162,0.08)',border:'1.5px solid var(--purple)',borderRadius:7,display:'flex',alignItems:'center',gap:6}}>
                              <div style={{flex:1,fontFamily:'monospace',fontWeight:700,fontSize:12,color:'#4a148c',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{item.product_name}</div>
                              <button onClick={()=>updateKeepItem(idx, { product_name: '', productSearch: '', productResults: [], serial_number: '', serialResults: [] })}
                                style={{padding:'2px 7px',border:'1px solid var(--purple)',background:'var(--bg-card)',color:'var(--purple)',borderRadius:5,fontSize:10,fontWeight:600,cursor:'pointer'}}>변경</button>
                            </div>
                          ) : (
                            <>
                              <input value={item.productSearch} onChange={e=>searchKeepItemProduct(idx, e.target.value)}
                                placeholder="제품명 검색"
                                style={{...inputStyle,fontFamily:'monospace',fontWeight:600,fontSize:12,padding:'8px 10px'}}/>
                              {item.productResults.length > 0 && (
                                <div ref={positionDropdown} style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,marginTop:3,maxHeight:200,overflowY:'auto',zIndex:20,boxShadow:'0 4px 16px rgba(0,0,0,0.1)'}}>
                                  {item.productResults.map(r => {
                                    const reserved = activeKeepsByModel[r.product_name] || 0;
                                    return (
                                      <div key={r.product_name} onClick={()=>updateKeepItem(idx, { product_name: r.product_name, productSearch: '', productResults: [] })}
                                        style={{padding:'7px 10px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)'}}
                                        onMouseEnter={e=>e.currentTarget.style.background='#f5f0fa'}
                                        onMouseLeave={e=>e.currentTarget.style.background=''}>
                                        <div style={{fontFamily:'monospace',fontSize:12,fontWeight:700}}>{r.product_name}</div>
                                        <div style={{fontSize:10,color:'var(--fg-3)',display:'flex',gap:6}}>
                                          <span>{r.category}</span>
                                          {reserved > 0 && <span style={{color:'var(--purple)',fontWeight:700}}>활성 킵 {reserved}개</span>}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                        {/* 수량 */}
                        <input type="number" min={1} value={item.qty}
                          onChange={e=>{ const q = Math.max(1, parseInt(e.target.value || '1', 10)); const oldS = item.serials || ['']; const ns = [...oldS]; while (ns.length < q) ns.push(''); while (ns.length > q) ns.pop(); updateKeepItem(idx, { qty: q, serials: ns }); }}
                          style={{...inputStyle,fontWeight:700,fontSize:13,padding:'8px 10px',textAlign:'center'}}/>
                        {/* 시리얼 N개 — 수량만큼 슬롯, DB 자동완성 */}
                        <div style={{display:'flex',flexDirection:'column',gap:0}}>
                          {(item.serials && item.serials.length > 0 ? item.serials : ['']).map((s, sIdx) => (
                            <KeepSerialSlot
                              key={sIdx}
                              idx={sIdx}
                              initial={s || ''}
                              isActive={!!item.product_name}
                              productName={item.product_name}
                              supabaseClient={supabase}
                              onCommit={(slotIdx, val) => {
                                const ns = [...(item.serials || [''])];
                                while (ns.length < item.qty) ns.push('');
                                ns[slotIdx] = val;
                                updateKeepItem(idx, { serials: ns });
                              }}
                            />
                          ))}
                        </div>
                        {/* 제품 메모 */}
                        <input value={item.item_memo}
                          onChange={e=>updateKeepItem(idx, { item_memo: e.target.value })}
                          placeholder="제품 메모 (선택)"
                          style={{...inputStyle,fontSize:12,padding:'8px 10px'}}/>
                        {/* 삭제 */}
                        <button onClick={()=>removeKeepItem(idx)} title="품목 삭제"
                          style={{width:32,height:36,border:'1px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',borderRadius:7,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}>
                          <Icon name="close" size={14}/>
                        </button>
                      </div>
                    ))}
                  </div>
                  <button onClick={addKeepItem}
                    style={{marginTop:8,padding:'9px 16px',border:'1.5px dashed var(--purple)',background:'var(--bg-card)',color:'var(--purple)',borderRadius:8,fontSize:12,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                    <Icon name="add" size={15}/> 품목 추가
                  </button>
                </div>

                {/* 3행: 메모 + 등록 버튼 */}
                <div style={{display:'grid',gridTemplateColumns:'1fr auto',gap:12,alignItems:'flex-end'}}>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>메모</label>
                    <input value={keepsForm.memo} onChange={e=>setKeepsForm(f=>({...f, memo: e.target.value}))}
                      placeholder="전체 킵에 대한 메모 (선택)"
                      style={{...inputStyle,fontSize:13,padding:'9px 12px'}}/>
                  </div>
                  <button onClick={submitKeep}
                    disabled={!keepsForm.dealer_name.trim() || !keepsForm.items.some(it => it.product_name && it.qty > 0)}
                    style={{padding:'12px 28px',background:(!keepsForm.dealer_name.trim() || !keepsForm.items.some(it => it.product_name && it.qty > 0))?'var(--surface-mute)':'var(--purple-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:(!keepsForm.dealer_name.trim() || !keepsForm.items.some(it => it.product_name && it.qty > 0))?'default':'pointer',display:'inline-flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                    <Icon name="bookmark_add" size={18}/> 킵 등록
                  </button>
                </div>
              </Card>

              {/* ── 킵 목록 ── */}
              <Card style={{padding:0,overflow:'hidden',background:'#fafafa'}}>
                <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',background:'var(--bg-card)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexWrap:'wrap'}}>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <Icon name="list_alt" size={20} style={{color:'var(--purple)'}}/>
                    <h3 style={{fontSize:15,fontWeight:700,margin:0,color:'var(--fg-1)'}}>등록된 킵</h3>
                    <span style={{fontSize:11,color:'var(--fg-3)'}}>{keepGroups.length}건 ({keepsItems.length}품목)</span>
                  </div>
                  <div style={{display:'flex',gap:8,alignItems:'center'}}>
                    <select value={keepsStatusFilter} onChange={e=>setKeepsStatusFilter(e.target.value)}
                      style={{padding:'6px 12px',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:12,outline:'none',background:'var(--bg-card)',cursor:'pointer'}}>
                      <option value="all">전체</option>
                      <option value="active">활성</option>
                      <option value="shipped">출고완료</option>
                      <option value="released">해제됨</option>
                    </select>
                    <input value={keepsSearch} onChange={e=>setKeepsSearch(e.target.value)} placeholder="제품/거래처/메모"
                      style={{padding:'6px 14px',border:'1px solid var(--border-strong)',borderRadius:9999,fontSize:12,outline:'none',background:'var(--bg-card)',minWidth:200}}/>
                    <button onClick={purgeKeepRecords} title="출고완료/해제 기록을 일괄 삭제 (배송시트지 기록은 유지)"
                      style={{padding:'6px 12px',border:'1px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',borderRadius:9999,fontSize:12,fontWeight:600,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:5}}
                      onMouseEnter={e=>{e.currentTarget.style.background='rgba(186,26,26,0.08)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='var(--bg-card)';}}>
                      <Icon name="cleaning_services" size={14}/> 기록 정리
                    </button>
                  </div>
                </div>
                <div style={{padding:0}}>
                {keepsLoading ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>로딩 중…</div>
                ) : keepGroups.length === 0 ? (
                  <div style={{padding:60,textAlign:'center',color:'var(--fg-3)'}}>등록된 킵이 없습니다.</div>
                ) : (
                  <div style={{overflowX:'auto'}}>
                    {renderKeepTableHeader()}
                    <div>
                      {keepGroups.map(g => renderKeepGroup(g))}
                    </div>
                  </div>
                )}
                </div>
              </Card>
            </>
          )}

          {/* ═══ v7.58 HDD/SSD 일괄 입고 페이지 (헤더 수기 + 시리얼 paste) ═══ */}
          {/* v7.64: 제품명(카탈로그 자동완성) + 모델명(수기) 분리 */}
          {page==='inbound_hdd' && (() => {
            const newCount = hddInbound.parsedSerials.length - hddInbound.existingInDb.length;
            // v7.75: 모델 번호 선택화 — canRegister에서 modelNo.trim() 조건 제거
            const canRegister = hddInbound.productName && (hddInbound.inDate || hddInbound.inDateUnknown) && newCount > 0 && !hddInbound.registering;
            const noMatch = hddInbound.productSearch.trim() && hddInbound.productResults.length === 0 && !hddInbound.productSearching && !hddInbound.productName;
            return (
              <div>
                <div style={{marginBottom:24}}>
                  <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                    <Icon name="storage" size={32} style={{color:'#d84315'}}/>
                    HDD/SSD 일괄 입고
                  </h1>
                  <p style={{fontSize:15,color:'var(--fg-2)'}}>제품을 카탈로그에서 선택하고 모델 번호와 시리얼을 입력하여 등록합니다.</p>
                </div>

                {/* 헤더 입력 카드 — 2행 구조 */}
                {/* v7.66 fix: Card의 backdropFilter가 새 stacking context를 만들어 dropdown이 형제 카드에 가려짐
                              헤더 Card 자체에 position+zIndex를 줘서 시리얼 Card보다 위에 그려지게 함 */}
                <Card style={{padding:'22px 24px',marginBottom:18,position:'relative',zIndex:10}}>
                  {/* 1행: 출처 / 입고일 / 유형 */}
                  <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:14,alignItems:'end',marginBottom:14}}>
                    {/* 출처 */}
                    <div>
                      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:6,letterSpacing:'0.04em'}}>📦 출처</label>
                      <div style={{display:'flex',gap:0,border:'1px solid var(--border-strong)',borderRadius:9,overflow:'hidden',marginBottom:hddInbound.sourceMode==='manual'?8:0}}>
                        <button onClick={()=>setHddInbound(p=>({...p, sourceMode:'preset', source:'이선'}))}
                          style={{flex:1,padding:'10px 12px',background:hddInbound.sourceMode==='preset'?'var(--accent-solid)':'var(--bg-card)',color:hddInbound.sourceMode==='preset'?'#fff':'var(--fg-3)',border:'none',cursor:'pointer',fontSize:13,fontWeight:700}}>
                          이선
                        </button>
                        <button onClick={()=>setHddInbound(p=>({...p, sourceMode:'manual', source:''}))}
                          style={{flex:1,padding:'10px 12px',background:hddInbound.sourceMode==='manual'?'var(--accent-solid)':'var(--bg-card)',color:hddInbound.sourceMode==='manual'?'#fff':'var(--fg-3)',border:'none',cursor:'pointer',fontSize:13,fontWeight:700}}>
                          수기입력
                        </button>
                      </div>
                      {hddInbound.sourceMode==='manual' && (
                        <input value={hddInbound.source} onChange={e=>setHddInbound(p=>({...p, source: e.target.value}))}
                          placeholder="출처 직접 입력" autoFocus
                          style={{...inputStyle, fontSize:13, fontFamily:'monospace'}}/>
                      )}
                    </div>
                    {/* 입고일 (v7.96: 미확인 옵션 추가) */}
                    <div>
                      <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:6,letterSpacing:'0.04em'}}>
                        <span>📅 입고일</span>
                        <span style={{display:'inline-flex',gap:5,alignItems:'center'}}>
                          <label style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:10,fontWeight:600,color:hddInbound.inDateUnknown?'#f57c00':'var(--fg-3)',cursor:'pointer',padding:'2px 6px',borderRadius:4,background:hddInbound.inDateUnknown?'rgba(245,124,0,0.1)':'transparent',transition:'all 0.15s',letterSpacing:0}}>
                            <input type="checkbox" checked={hddInbound.inDateUnknown} onChange={e=>setHddInbound(p=>({...p, inDateUnknown: e.target.checked}))} style={{cursor:'pointer',margin:0}}/>
                            미확인
                          </label>
                          {/* v7.183: 장부처리 완료 체크박스 */}
                          <label style={{display:'inline-flex',alignItems:'center',gap:4,fontSize:10,fontWeight:600,color:hddInbound.bookkept?'var(--success)':'var(--fg-3)',cursor:'pointer',padding:'2px 6px',borderRadius:4,background:hddInbound.bookkept?'rgba(0,152,68,0.1)':'transparent',transition:'all 0.15s',letterSpacing:0}}>
                            <input type="checkbox" checked={!!hddInbound.bookkept} onChange={e=>setHddInbound(p=>({...p, bookkept: e.target.checked}))} style={{cursor:'pointer',margin:0}}/>
                            📒 장부처리 완료
                          </label>
                        </span>
                      </label>
                      <input type="date" value={hddInbound.inDate} onChange={e=>setHddInbound(p=>({...p, inDate: e.target.value}))}
                        disabled={hddInbound.inDateUnknown}
                        style={{...inputStyle, fontSize:14, fontFamily:'monospace', opacity: hddInbound.inDateUnknown?0.35:1, background: hddInbound.inDateUnknown?'#f5f5f7':'var(--bg-card)', cursor: hddInbound.inDateUnknown?'not-allowed':'auto'}}/>
                      {hddInbound.inDateUnknown && (
                        <div style={{fontSize:10,color:'#f57c00',marginTop:4,fontWeight:600}}>
                          ⚠ 날짜 없이 등록됩니다 (재고 목록에서 나중에 수정 가능)
                        </div>
                      )}
                    </div>
                    {/* 유형 */}
                    <div>
                      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:6,letterSpacing:'0.04em'}}>유형</label>
                      <select value={hddInbound.stockType} onChange={e=>setHddInbound(p=>({...p, stockType: e.target.value}))}
                        style={{...inputStyle, fontSize:13, cursor:'pointer'}}>
                        <option value="신품">신품</option>
                        <option value="AS">AS</option>
                      </select>
                    </div>
                  </div>

                  {/* 2행: 제품명(카탈로그 자동완성) + 모델명(수기) */}
                  {/* v7.66: 카테고리 토글 제거 — 카탈로그 SOT로 사용 */}
                  <div style={{display:'grid',gridTemplateColumns:'1.5fr 1fr',gap:14,alignItems:'end'}}>
                    {/* 제품명 — 카탈로그 자동완성 */}
                    <div style={{position:'relative'}}>
                      <label style={{display:'flex',alignItems:'center',gap:8,fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:6,letterSpacing:'0.04em',flexWrap:'wrap'}}>
                        📚 제품명 (카탈로그)
                        {hddInbound.productName && (
                          <span style={{padding:'1px 7px',background:'rgba(0,152,68,0.12)',color:'var(--success)',borderRadius:9999,fontSize:9,fontWeight:700}}>✓ 선택됨</span>
                        )}
                        {noMatch && (
                          <>
                            <span style={{color:'var(--warning)',fontSize:10}}>⚠ 카탈로그에 없음</span>
                            <button onClick={()=>setHddInbound(s=>({...s, showQuickRegister:true, quickRegisterAlias:'', quickRegisterCategory:'HDD'}))}
                              style={{padding:'2px 9px',background:'var(--warning-solid)',color:'#fff',border:'none',borderRadius:5,fontSize:10,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:3}}>
                              <Icon name="add_circle" size={11}/> 신규 등록
                            </button>
                          </>
                        )}
                      </label>
                      {hddInbound.productName ? (
                        <div style={{display:'flex',alignItems:'center',gap:8,padding:'10px 12px',background:'rgba(0,152,68,0.06)',border:'1.5px solid var(--success)',borderRadius:9}}>
                          <Icon name="check_circle" size={18} style={{color:'var(--success)'}}/>
                          <span style={{flex:1,fontSize:14,fontWeight:700,color:'var(--fg-1)',fontFamily:'monospace',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis',minWidth:0}}>{hddInbound.productName}</span>
                          {hddInbound.selectedCategory ? (
                            <span style={{padding:'3px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:hddInbound.selectedCategory==='HDD'?'rgba(216,67,21,0.12)':hddInbound.selectedCategory==='SSD'?'rgba(93,64,55,0.12)':'rgba(0,102,138,0.12)',color:hddInbound.selectedCategory==='HDD'?'#d84315':hddInbound.selectedCategory==='SSD'?'#5d4037':'var(--accent)',flexShrink:0}}>
                              {hddInbound.selectedCategory}
                            </span>
                          ) : (
                            <span style={{padding:'3px 10px',borderRadius:9999,fontSize:11,fontWeight:700,background:'#fff3e0',color:'var(--warning)',flexShrink:0}}>⚠ 카테고리 미지정</span>
                          )}
                          <button onClick={()=>setHddInbound(s=>({...s, productName:'', productSearch:'', productResults:[], productSearchOpen:false, selectedCategory:null}))}
                            style={{padding:'4px 10px',background:'var(--bg-card)',color:'var(--danger)',border:'1px solid var(--danger)',borderRadius:6,fontSize:11,fontWeight:700,cursor:'pointer',flexShrink:0}}>
                            변경
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            value={hddInbound.productSearch}
                            onChange={e=>searchHddProduct(e.target.value)}
                            onFocus={()=>setHddInbound(s=>({...s, productSearchOpen:true}))}
                            onBlur={()=>setTimeout(()=>setHddInbound(s=>({...s, productSearchOpen:false})), 200)}
                            placeholder="예: iron wolf, 아이언울프, 또는 모델명 일부"
                            style={{...inputStyle, fontSize:14}}/>
                          {hddInbound.productSearchOpen && hddInbound.productResults.length > 0 && (
                            <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:8,boxShadow:'0 8px 24px rgba(0,0,0,0.15)',maxHeight:320,overflowY:'auto',zIndex:9999}}>
                              {hddInbound.productResults.map(r => (
                                <div key={r.product_name} onMouseDown={()=>setHddInbound(s=>({...s, productName: r.product_name, productSearch: r.product_name, productSearchOpen:false, productResults:[], selectedCategory: r.category || null}))}
                                  style={{padding:'10px 14px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)',display:'flex',alignItems:'center',gap:8,transition:'background 0.1s'}}
                                  onMouseEnter={e=>e.currentTarget.style.background='var(--bg-accent-soft)'}
                                  onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                                  <div style={{flex:1,minWidth:0}}>
                                    <div style={{fontSize:13,fontWeight:700,color:'var(--fg-1)',fontFamily:'monospace',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{r.product_name}</div>
                                    {r.alias && <div style={{fontSize:11,color:'var(--fg-3)',marginTop:1}}>↳ {r.alias}</div>}
                                  </div>
                                  {r.category ? (
                                    <span style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:r.category==='HDD'?'rgba(216,67,21,0.12)':r.category==='SSD'?'rgba(93,64,55,0.12)':'rgba(0,102,138,0.12)',color:r.category==='HDD'?'#d84315':r.category==='SSD'?'#5d4037':'var(--accent)'}}>
                                      {r.category}
                                    </span>
                                  ) : (
                                    <span style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:'#fff3e0',color:'var(--warning)'}}>미지정</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                          {hddInbound.productSearching && (
                            <div style={{position:'absolute',top:'100%',left:0,marginTop:4,fontSize:11,color:'var(--fg-3)'}}>검색 중...</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* 모델명 — 수기 (v7.75: 선택 사항) */}
                    <div>
                      <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:6,letterSpacing:'0.04em'}}>
                        🔖 모델 번호 (수기) <span style={{fontSize:10,fontWeight:600,color:'var(--fg-3)',marginLeft:4}}>(선택)</span>
                        <span style={{marginLeft:6,fontSize:9,color:'var(--fg-3)',fontWeight:500}}>같은 제품이라도 시리얼 단위 모델이 다를 수 있음</span>
                      </label>
                      <input
                        value={hddInbound.modelNo}
                        onChange={e=>setHddInbound(p=>({...p, modelNo: e.target.value}))}
                        placeholder="예: ST24000NT002 (필요 시)"
                        style={{...inputStyle, fontSize:14, fontFamily:'monospace', fontWeight:600}}/>
                    </div>
                  </div>
                </Card>

                {/* 시리얼 paste 영역 */}
                <Card style={{padding:'22px 24px',marginBottom:18}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                    <label style={{display:'flex',alignItems:'center',gap:6,fontSize:13,fontWeight:700,color:'var(--fg-1)'}}>
                      <Icon name="qr_code_scanner" size={16}/> 시리얼 (한 줄에 하나)
                    </label>
                    <div style={{display:'flex',alignItems:'center',gap:8,fontSize:11,color:'var(--fg-3)'}}>
                      <span>바코드 리더기로 스캔한 텍스트(.txt) 내용을 그대로 붙여넣으세요</span>
                    </div>
                  </div>
                  <textarea
                    value={hddInbound.serialText}
                    onChange={e=>setHddInbound(p=>({...p, serialText: e.target.value}))}
                    onBlur={parseHddSerials}
                    placeholder={"ZYD2266P\nZYD20B76\nZYD1ZYAS\nZYD0YW0X\n..."}
                    rows={12}
                    style={{
                      width:'100%', padding:'12px 14px', border:'1px solid var(--border-strong)', borderRadius:10,
                      fontFamily:'monospace', fontSize:13, fontWeight:600, color:'var(--fg-1)',
                      resize:'vertical', outline:'none', background:'var(--bg-subtle)', boxSizing:'border-box',
                      lineHeight:1.6,
                    }}/>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop:10}}>
                    <button onClick={parseHddSerials}
                      disabled={!hddInbound.serialText.trim()}
                      style={{padding:'7px 16px',background:hddInbound.serialText.trim()?'var(--accent-solid)':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:8,fontSize:12,fontWeight:700,cursor:hddInbound.serialText.trim()?'pointer':'not-allowed',display:'inline-flex',alignItems:'center',gap:5}}>
                      <Icon name="search" size={14}/> 분석
                    </button>
                    {hddInbound.parsedSerials.length > 0 && (
                      <button onClick={()=>setHddInbound(p=>({...p, serialText:'', parsedSerials:[], existingInDb:[], duplicatesInPaste:[]}))}
                        style={{padding:'6px 12px',background:'var(--bg-card)',color:'var(--fg-3)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:11,fontWeight:700,cursor:'pointer'}}>
                        지우기
                      </button>
                    )}
                  </div>
                </Card>

                {/* 분석 결과 요약 */}
                {hddInbound.parsedSerials.length > 0 && (
                  <Card style={{padding:'16px 22px',marginBottom:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:'var(--fg-1)',marginBottom:10}}>📊 분석 결과</div>
                    <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:hddInbound.duplicatesInPaste.length+hddInbound.existingInDb.length>0?12:0}}>
                      <span style={{padding:'5px 11px',background:'rgba(0,152,68,0.1)',color:'var(--success)',borderRadius:9999,fontSize:11,fontWeight:700}}>
                        ✓ 신규 등록 가능 {newCount}개
                      </span>
                      {hddInbound.duplicatesInPaste.length > 0 && (
                        <span style={{padding:'5px 11px',background:'rgba(245,124,0,0.1)',color:'var(--warning)',borderRadius:9999,fontSize:11,fontWeight:700}}>
                          ⚠ 붙여넣은 텍스트 내 중복 {hddInbound.duplicatesInPaste.length}개 (자동 제거됨)
                        </span>
                      )}
                      {hddInbound.existingInDb.length > 0 && (
                        <span style={{padding:'5px 11px',background:'rgba(186,26,26,0.1)',color:'var(--danger)',borderRadius:9999,fontSize:11,fontWeight:700}}>
                          ✗ 이미 DB에 있는 시리얼 {hddInbound.existingInDb.length}개 (등록 제외)
                        </span>
                      )}
                    </div>
                    {hddInbound.existingInDb.length > 0 && (
                      <div style={{padding:'10px 12px',background:'#fff8e1',border:'1px solid #ffc107',borderRadius:7,fontSize:11,color:'var(--fg-1)',marginTop:6}}>
                        <b>이미 등록된 시리얼:</b>
                        <div style={{marginTop:4,fontFamily:'monospace',color:'var(--danger)',lineHeight:1.8}}>
                          {hddInbound.existingInDb.slice(0,20).join(', ')}{hddInbound.existingInDb.length>20?` 외 ${hddInbound.existingInDb.length-20}개`:''}
                        </div>
                      </div>
                    )}
                  </Card>
                )}

                {/* 등록 진행 */}
                {hddInbound.registering && (
                  <Card style={{padding:'14px 20px',marginBottom:14,background:'rgba(0,102,138,0.04)',border:'1px solid var(--accent)'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                      <span style={{fontSize:13,fontWeight:700,color:'#004d6a'}}>등록 진행 중...</span>
                      <span style={{fontSize:13,fontWeight:700,color:'#004d6a',fontFamily:'monospace'}}>{hddInbound.registerProgress}%</span>
                    </div>
                    <div style={{width:'100%',height:6,background:'var(--bg-accent-strong)',borderRadius:9999,overflow:'hidden'}}>
                      <div style={{width:`${hddInbound.registerProgress}%`,height:'100%',background:'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)',transition:'width 0.3s ease'}}/>
                    </div>
                  </Card>
                )}

                {/* 등록 버튼 */}
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                  <div style={{fontSize:11,color:'var(--fg-3)'}}>
                    {!hddInbound.productName && '⚠ 제품을 카탈로그에서 선택하세요.'}
                    {hddInbound.productName && newCount === 0 && hddInbound.parsedSerials.length === 0 && '⚠ 시리얼을 붙여넣고 분석하세요.'}
                  </div>
                  <button onClick={registerHddInbound} disabled={!canRegister}
                    style={{display:'inline-flex',alignItems:'center',gap:8,padding:'14px 30px',background:canRegister?'#d84315':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:canRegister?'pointer':'not-allowed',boxShadow:canRegister?'0 4px 12px rgba(216,67,21,0.25)':'none'}}>
                    <Icon name="check_circle" size={18}/>
                    {hddInbound.registering ? '등록 중...' : (newCount > 0 ? `${newCount}개 시리얼 등록` : '등록 가능한 시리얼 없음')}
                  </button>
                </div>

                {/* v7.61: 카탈로그 즉시 등록 모달 */}
                {hddInbound.showQuickRegister && (
                  <div onClick={()=>!hddInbound.quickRegisterSaving && setHddInbound(s=>({...s, showQuickRegister:false}))}
                    style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,backdropFilter:'blur(2px)'}}>
                    <div onClick={e=>e.stopPropagation()}
                      style={{background:'var(--bg-card)',borderRadius:14,padding:28,maxWidth:480,width:'90%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
                        <Icon name="library_add" size={22} style={{color:'#d84315'}}/>
                        <h3 style={{fontSize:18,fontWeight:700,color:'var(--fg-1)',margin:0}}>제품 등록</h3>
                      </div>
                      <p style={{fontSize:12,color:'var(--fg-3)',marginTop:0,marginBottom:18,lineHeight:1.5}}>
                        제품명을 카탈로그에 등록하고 한글 별칭을 함께 저장합니다. 이후 한글 이름으로도 검색 가능합니다.
                      </p>

                      <div style={{display:'flex',flexDirection:'column',gap:14}}>
                        {/* 제품명 (read-only) */}
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5,letterSpacing:'0.04em'}}>📚 제품명 (카탈로그 키)</label>
                          <div style={{padding:'10px 14px',background:'#f5f7fb',borderRadius:8,fontFamily:'monospace',fontSize:14,fontWeight:700,color:'var(--fg-1)',border:'1px solid var(--border)'}}>
                            {hddInbound.productSearch}
                          </div>
                        </div>

                        {/* 한글 별칭 */}
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5,letterSpacing:'0.04em'}}>📝 한글 별칭 (검색용)</label>
                          <input
                            value={hddInbound.quickRegisterAlias}
                            onChange={e=>setHddInbound(s=>({...s, quickRegisterAlias: e.target.value}))}
                            placeholder="예: 아이언울프 프로 4테라"
                            autoFocus
                            disabled={hddInbound.quickRegisterSaving}
                            onKeyDown={e=>{ if (e.key === 'Enter' && hddInbound.quickRegisterAlias.trim()) quickRegisterHddProduct(); }}
                            style={{...inputStyle, fontSize:14}}/>
                        </div>

                        {/* 카테고리 */}
                        <div>
                          <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5,letterSpacing:'0.04em'}}>📂 카테고리</label>
                          <div style={{display:'flex',gap:0,border:'1px solid var(--border-strong)',borderRadius:9,overflow:'hidden'}}>
                            {['HDD','SSD'].map(c => (
                              <button key={c} onClick={()=>setHddInbound(s=>({...s, quickRegisterCategory: c}))}
                                disabled={hddInbound.quickRegisterSaving}
                                style={{flex:1,padding:'11px 16px',background:hddInbound.quickRegisterCategory===c?(c==='HDD'?'#d84315':'#5d4037'):'var(--bg-card)',color:hddInbound.quickRegisterCategory===c?'#fff':'var(--fg-3)',border:'none',cursor:'pointer',fontSize:13,fontWeight:700}}>
                                {c}
                              </button>
                            ))}
                          </div>
                          <div style={{marginTop:4,fontSize:10,color:'var(--fg-3)'}}>
                            {productCategories.length > 0 && !productCategories.includes(hddInbound.quickRegisterCategory) && (
                              <>⚠ '{hddInbound.quickRegisterCategory}'가 카테고리 마스터에 없습니다. 자동 추가됩니다.</>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* 액션 버튼 */}
                      <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:24}}>
                        <button onClick={()=>setHddInbound(s=>({...s, showQuickRegister:false}))}
                          disabled={hddInbound.quickRegisterSaving}
                          style={{padding:'10px 18px',background:'var(--bg-card)',color:'var(--fg-3)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontWeight:700,cursor:hddInbound.quickRegisterSaving?'wait':'pointer'}}>
                          취소
                        </button>
                        <button onClick={quickRegisterHddProduct}
                          disabled={hddInbound.quickRegisterSaving || !hddInbound.quickRegisterAlias.trim()}
                          style={{padding:'10px 22px',background:hddInbound.quickRegisterSaving||!hddInbound.quickRegisterAlias.trim()?'var(--surface-mute)':'#d84315',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:(hddInbound.quickRegisterSaving||!hddInbound.quickRegisterAlias.trim())?'not-allowed':'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                          <Icon name="check" size={15}/> {hddInbound.quickRegisterSaving ? '등록 중...' : '등록'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {/* ═══ v7.44 BACKUP (데이터 백업) ═══ */}
          {page==='backup' && (
            <div>
              <AdminTabs page={page} onGo={(id)=>{setPage(id);setPg(0);setSearch('');setTxSubTab('list');setAdminOpen(true);}}/>
              <div style={{marginBottom:28}}>
                <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="cloud_download" size={32} style={{color:'var(--accent)'}}/>
                  데이터 백업
                </h1>
                <p style={{fontSize:16,color:'var(--fg-2)'}}>Supabase Free 플랜은 자동 백업이 제공되지 않습니다. 정기적으로 백업을 받아두세요.</p>
              </div>

              {/* 즉시 백업 카드 */}
              <Card style={{padding:0,marginBottom:20,overflow:'hidden',border:'2px solid var(--accent)'}}>
                <div style={{padding:'16px 22px',background:'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)',color:'#fff',display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="download" size={22}/>
                  <div>
                    <div style={{fontSize:12,opacity:0.85,fontWeight:600,letterSpacing:'0.04em'}}>수동 백업</div>
                    <div style={{fontSize:18,fontWeight:700}}>지금 전체 데이터 백업</div>
                  </div>
                </div>
                <div style={{padding:'22px 24px'}}>
                  <div style={{fontSize:13,color:'var(--fg-2)',marginBottom:18,lineHeight:1.6}}>
                    아래 {BACKUP_TABLES.length}개 테이블의 모든 row를 단일 JSON 파일로 묶어 다운로드합니다.
                    파일명: <code style={{background:'var(--bg-mute)',padding:'2px 6px',borderRadius:4,fontSize:12}}>inventura-backup-YYYYMMDD.json</code>
                  </div>

                  {/* 대상 테이블 칩 */}
                  <div style={{display:'flex',flexWrap:'wrap',gap:6,marginBottom:18}}>
                    {BACKUP_TABLES.map(t => (
                      <span key={t.name} style={{display:'inline-flex',alignItems:'center',gap:4,padding:'5px 11px',background:'var(--bg-accent-soft)',borderRadius:9999,fontSize:11,fontWeight:600,color:'var(--accent)'}}>
                        <Icon name="table_chart" size={11}/>
                        {t.label}
                        {backupState.lastBackup?.counts?.[t.name] != null && (
                          <span style={{color:'var(--fg-3)',marginLeft:3,fontFamily:'monospace'}}>{backupState.lastBackup.counts[t.name].toLocaleString()}</span>
                        )}
                      </span>
                    ))}
                  </div>

                  {/* 진행 상태 */}
                  {backupState.loading && (
                    <div style={{marginBottom:14,padding:'12px 14px',background:'rgba(64,194,253,0.08)',border:'1px solid rgba(64,194,253,0.3)',borderRadius:8}}>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6}}>
                        <span style={{fontSize:12,fontWeight:700,color:'#004d6a'}}>{backupState.status}</span>
                        <span style={{fontSize:12,fontWeight:700,color:'#004d6a',fontFamily:'monospace'}}>{backupState.progress}%</span>
                      </div>
                      <div style={{width:'100%',height:6,background:'var(--bg-accent-strong)',borderRadius:9999,overflow:'hidden'}}>
                        <div style={{width:`${backupState.progress}%`,height:'100%',background:'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)',transition:'width 0.3s ease'}}/>
                      </div>
                    </div>
                  )}
                  {!backupState.loading && backupState.status !== '대기 중' && (
                    <div style={{marginBottom:14,padding:'10px 14px',background:backupState.status.startsWith('✓')?'rgba(0,152,68,0.08)':'rgba(186,26,26,0.08)',border:`1px solid ${backupState.status.startsWith('✓')?'rgba(0,152,68,0.3)':'rgba(186,26,26,0.3)'}`,borderRadius:8,fontSize:13,fontWeight:600,color:backupState.status.startsWith('✓')?'var(--success)':'var(--danger)'}}>
                      {backupState.status}
                    </div>
                  )}

                  <button onClick={runFullBackup} disabled={backupState.loading || !connected}
                    style={{display:'inline-flex',alignItems:'center',gap:8,padding:'12px 24px',background:backupState.loading||!connected?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:backupState.loading||!connected?'not-allowed':'pointer',boxShadow:backupState.loading||!connected?'none':'0 4px 12px rgba(0,102,138,0.2)'}}>
                    <Icon name={backupState.loading?'progress_activity':'cloud_download'} size={18} style={backupState.loading?{animation:'spin 1s linear infinite'}:{}}/>
                    {backupState.loading?'백업 진행 중...':'전체 백업 다운로드'}
                  </button>
                </div>
              </Card>

              {/* 자동 백업 안내 카드 */}
              <Card style={{padding:'20px 24px',marginBottom:20,background:'var(--bg-subtle)',border:'1px solid var(--border-strong)'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:12}}>
                  <Icon name="schedule" size={22} style={{color:'var(--purple)'}}/>
                  <div style={{fontSize:16,fontWeight:700,color:'var(--fg-1)'}}>자동 백업 (n8n + QNAP NAS)</div>
                </div>
                <div style={{fontSize:13,color:'var(--fg-2)',lineHeight:1.7}}>
                  매일 새벽 3시에 자동으로 백업이 NAS에 저장되고 90일치(약 3개월)가 회전 보관됩니다.
                  설정은 n8n workflow JSON과 동봉된 가이드를 참고하세요.
                  <div style={{marginTop:10,padding:'10px 12px',background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:7,fontFamily:'monospace',fontSize:11,color:'var(--fg-1)'}}>
                    저장 경로 예시: <span style={{color:'var(--purple)'}}>/share/Backup/inventura/inventura-backup-YYYYMMDD.json</span>
                  </div>
                </div>
              </Card>

              {/* 복원 안내 */}
              <Card style={{padding:'20px 24px',background:'#fff8e1',border:'1px solid #ffc107'}}>
                <div style={{display:'flex',alignItems:'flex-start',gap:10}}>
                  <Icon name="info" size={20} style={{color:'#f57c00',flexShrink:0,marginTop:2}}/>
                  <div style={{fontSize:13,color:'var(--fg-2)',lineHeight:1.6}}>
                    <div style={{fontWeight:700,marginBottom:6,color:'var(--fg-1)'}}>복원 방법</div>
                    백업 파일은 <code style={{background:'rgba(0,0,0,0.05)',padding:'1px 5px',borderRadius:3,fontSize:11}}>{`{meta, data:{table:[rows]}}`}</code> 구조입니다.
                    복원이 필요할 때는 Supabase SQL Editor에서 각 테이블을 비운 뒤 JSON을 파싱해 INSERT하거나,
                    필요한 row만 골라서 부분 복원하실 수 있습니다. 사고가 나기 전에 한 번 복원 절차를 연습해두시면 좋습니다.
                  </div>
                </div>
              </Card>
            </div>
          )}

          {/* ═══ v7.349 NAS COMPATIBILITY (NAS 호환성) ═══
              NAS ↔ 메모리 호환성 조회. 이번 단계는 UI 골격 + 제품 검색까지.
              호환성 데이터(어느 NAS 에 어느 메모리가 맞는지)는 아직 붙지 않았다. */}
          {page==='compat' && (()=>{
            // v7.351: 시리즈 그룹핑 — 도크에 놓을 폴더 목록
            const seriesMap = new Map();
            (compatAll||[]).forEach(p0=>{
              const k = nasSeries(p0.product_name);
              if(!seriesMap.has(k)) seriesMap.set(k, []);
              seriesMap.get(k).push(p0);
            });
            // 모델 수 많은 순 → 같으면 이름순. 시리즈가 아무리 많아도 도크엔 8개까지만 놓고
            // 나머지는 [기타] 폴더로 합친다 (도크는 4~8개용 UI 다).
            const sorted = [...seriesMap.entries()].sort((a,b)=> b[1].length-a[1].length || a[0].localeCompare(b[0]));
            const head = sorted.slice(0,8);
            const tail = sorted.slice(8);
            const folders = [...head];
            if(tail.length) folders.push(['기타', tail.flatMap(([,v])=>v)]);

            // v7.353: 시리즈 미선택 = 전체.
            const curList = compatSeries ? (folders.find(([k])=>k===compatSeries)?.[1] || []) : (compatAll||[]);
            // v7.357: 검색어로 폴더를 좁힌다.
            //   ⚠ 하이픈·공백을 지우고 비교한다. 'ts453' 으로도 'TS-453E-8G' 가 잡혀야 한다.
            //     사용자가 모델명 하이픈 위치를 정확히 기억할 이유가 없다.
            const norm = qnapNorm;   // v7.361: 재고 대조와 같은 정규화를 쓴다
            const gf = norm(compatSearch);
            // v7.361: 부품 모델명으로 걸린 결과가 있으면 그 NAS 들만 남긴다.
            //   ⚠ 이때는 이름 일치로 거르면 안 된다 — 'WD140EFGX' 는 NAS 이름에 없다.
            const pl = (partLookup && partLookup !== 'loading' && partLookup.productNames.size)
              ? partLookup.productNames : null;
            const matches = (p0) => pl
              ? pl.has(p0.product_name)
              : (!gf || norm(p0.product_name).includes(gf));
            const gridList = curList.filter(matches);
            // 검색 중에는 시리즈 칩의 숫자도 [검색 결과 기준] 으로 바뀌어야 한다.
            //   ⚠ 전체 개수를 그대로 두면 'TS 63' 인데 눌러보니 2개만 나오는 상황이 된다.
            const matchCount = (list)=> list.filter(matches).length;
            const totalMatch = (compatAll||[]).filter(matches).length;

            // v7.364 / v7.365: 사내 테스트 파생값.
            //   ⚠ 카테고리 뱃지 숫자를 여기서 센다 — 서버에 다시 묻지 않는다.
            //   ⚠ icRows===null 은 [로딩 중] 이다. []( 없음)과 구분해야 숫자 0이 깜빡이지 않는다.
            const icList = icRows || [];
            const icCounts = {};
            icList.forEach(r => { icCounts[r.category_type] = (icCounts[r.category_type]||0) + 1; });
            // 카테고리 탭 구성.
            //   ⚠ 확장 카드는 QNAP 수집 대상이 아니라 공식 건수가 항상 0이다.
            //     사내 결과가 있을 때만 탭을 띄운다 — 늘 0인 탭은 헛클릭만 만든다.
            //   v7.367: M.2 SSD 추가. SSD 바로 뒤에 둔다 — 둘 다 SSD 라 붙어 있어야
            //     "2.5인치는 없는데 M.2 는 있네" 가 한눈에 보인다.
            const CAT_TABS = [QNAP_CAT.MEMORY, QNAP_CAT.HDD, QNAP_CAT.SSD, QNAP_CAT.M2SSD]
              .concat(icCounts[QNAP_CAT.EXPANSION] ? [QNAP_CAT.EXPANSION] : []);

            // 지금 카테고리의 사내 결과. 목록·헤더 양쪽에서 쓴다.
            const icCat = icList.filter(r => r.category_type === compatCat);
            const icSelCount = icCat.filter(r => icSel.includes(r.id)).length;

            // 입력 모달 열기 — 지금 보고 있는 NAS · 카테고리를 미리 채운다.
            //   ⚠ 여기서 안 채우면 매번 NAS 명을 손으로 쳐야 하고, 오타 한 번에
            //     다른 모델 밑으로 들어가 영영 안 보인다.
            const openIcNew = () => { setIcErr(''); setIcModal({
              product_name: compatModel ? compatModel.product_name : '',
              category_type: compatCat,
              brand:'', model:'', model_alt:'', capacity_gb:'',
              ddr_gen: compatCat===QNAP_CAT.MEMORY?'DDR4':'', speed_mhz:'', form_factor:'', is_ecc:'',
              result: IC_RESULT.OK, fw_version:'', note:'', tested_by:'', tested_at: today(),
            }); };
            const openIcEdit = (r) => { setIcErr(''); setIcModal({
              id:r.id, product_name:r.product_name, category_type:r.category_type,
              brand:r.brand||'', model:r.model||'', model_alt:r.model_alt||'',
              capacity_gb:r.capacity_gb==null?'':String(r.capacity_gb),
              ddr_gen:r.ddr_gen||'', speed_mhz:r.speed_mhz==null?'':String(r.speed_mhz),
              form_factor:r.form_factor||'', is_ecc:r.is_ecc==null?'':String(r.is_ecc),
              result:r.result||IC_RESULT.OK, fw_version:r.fw_version||'',
              note:r.note||'', tested_by:r.tested_by||'', tested_at:r.tested_at||today(),
            }); };

            return (
            <div>
              <div style={{marginBottom:24}}>
                <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="memory" size={32} style={{color:'var(--accent)'}}/>
                  NAS 호환성
                </h1>
                <p style={{fontSize:16,color:'var(--fg-2)'}}>NAS 모델을 검색하면 호환되는 메모리를 확인할 수 있습니다.</p>
              </div>

              {/* v7.357: 검색 = 폴더 격자 필터.
                  ⚠ 검색창이 둘(상단 드롭다운 + 격자 안 필터)이라 어느 쪽에 쳐야 할지 헷갈렸다.
                    격자 안 필터는 없애고 이 하나로 합쳤다. 드롭다운도 걷어냈다 —
                    고르는 순간 격자가 사라지고 결과로 튀어서, 폴더가 좁혀지는 걸 볼 수 없었다.
                    이제 타이핑하면 아래 폴더가 실시간으로 걸러진다. */}
              <Card style={{padding:20,marginBottom:16}}>
                <div style={{position:'relative'}}>
                  <Icon name="search" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)',pointerEvents:'none'}}/>
                  <input
                    value={compatSearch}
                    onChange={e=>setCompatSearch(e.target.value)}
                    placeholder="NAS 모델 또는 부품 검색 — TS-453E · WD140EFGX · RAM-16GDR4"
                    style={{...inputStyle,paddingLeft:42,paddingRight:44,fontSize:16}}/>
                  {/* v7.368: X = [이전 화면으로]. 검색어만 지우는 게 아니라 선택한 모델도 푼다.
                      ⚠ 모델을 고르면 검색창에 그 모델명이 채워진다. 그 상태에서 X 가
                        [글자만] 지우면 검색창은 비었는데 아래에는 여전히 부품 목록이 남아
                        무엇을 보고 있는 화면인지 알 수 없게 된다.
                      ⚠ 뒤로가기(v7.362)와 같은 동작이다 — 두 경로가 어긋나면 안 된다. */}
                  {(compatSearch || compatModel) && (
                    <button onClick={()=>{setCompatModel(null);setCompatSearch('');}}
                      title={compatModel ? '이전 화면으로' : '검색어 지우기'}
                      style={{position:'absolute',right:12,top:'50%',transform:'translateY(-50%)',background:'none',border:'none',cursor:'pointer',display:'flex',color:'var(--fg-3)'}}>
                      <Icon name="close" size={18}/>
                    </button>
                  )}
                </div>

                {/* v7.361: 부품으로 걸렸을 때 왜 폴더가 바뀌었는지 알려준다.
                    ⚠ 안 알려주면 "왜 TS-464 만 나오지?" 하고 혼란스럽다. */}
                {partLookup === 'loading' && (
                  <div style={{marginTop:12,fontSize:12.5,color:'var(--fg-3)',display:'flex',alignItems:'center',gap:7}}>
                    <Icon name="progress_activity" size={15}/> NAS 이름에 없는 값입니다 — 부품 목록에서 찾는 중…
                  </div>
                )}
                {partLookup && partLookup !== 'loading' && (
                  partLookup.productNames.size > 0 ? (
                    <div style={{marginTop:12,padding:'12px 14px',background:'var(--bg-accent-soft)',border:'1px solid var(--accent)',borderRadius:11}}>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--accent)',display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
                        <Icon name="swap_horiz" size={16}/>
                        이 부품을 지원하는 NAS {partLookup.productNames.size}종
                      </div>
                      <div style={{marginTop:7,display:'flex',gap:5,flexWrap:'wrap'}}>
                        {partLookup.parts.map(pt=>(
                          <span key={pt.model} style={{padding:'2px 8px',borderRadius:7,background:'var(--bg-card)',border:'1px solid var(--border)',
                            fontSize:11.5,fontWeight:600,fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace'}}>
                            {pt.model}
                            {pt.capacity_gb!=null && (
                              <b style={{marginLeft:5,color:'var(--accent)'}}>{QnapCompatService.fmtCapacity(pt.capacity_gb, pt.categoryType)}</b>
                            )}
                          </span>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div style={{marginTop:12,fontSize:12.5,color:'var(--fg-3)',display:'flex',alignItems:'center',gap:7}}>
                      <Icon name="search_off" size={15}/> NAS 이름에도 부품 목록에도 없는 값입니다.
                    </div>
                  )
                )}
              </Card>

              {/* v7.353: 시리즈 필터 — 도크를 걷어내고 칩 줄로 바꿨다 */}
              {!compatModel && (
                <Card style={{padding:'16px 20px',marginBottom:16}}>
                  <div style={{fontSize:13,fontWeight:600,color:'var(--fg-2)',marginBottom:10,display:'flex',alignItems:'center',gap:6}}>
                    <Icon name="filter_alt" size={17} style={{color:'var(--fg-3)'}}/>
                    시리즈 필터
                    {compatAll!==null && <span style={{fontWeight:500,color:'var(--fg-3)'}}>· NAS {compatAll.length}종</span>}
                  </div>
                  {compatAll===null ? (
                    <div style={{padding:'16px 0',fontSize:13,color:'var(--fg-3)'}}>불러오는 중…</div>
                  ) : folders.length===0 ? (
                    <div style={{padding:'12px 0',fontSize:13,color:'var(--fg-3)',lineHeight:1.6}}>
                      카테고리가 <b>NAS</b>로 지정된 제품이 없습니다.<br/>
                      위 검색창으로는 찾을 수 있습니다 — 카탈로그에서 카테고리를 채우면 여기에도 나옵니다.
                    </div>
                  ) : (
                    <div style={{display:'flex',gap:7,flexWrap:'wrap'}}>
                      {/* 전체 = 시리즈 해제 */}
                      <button onClick={()=>setCompatSeries(null)}
                        className="nas-chip" data-on={!compatSeries?'1':'0'}>
                        <Icon name="apps" size={16}/>전체
                        <span className="nas-chip__n">{totalMatch}</span>
                      </button>
                      {folders.map(([k,list])=>{
                        const n = matchCount(list);
                        return (
                        <button key={k} onClick={()=>setCompatSeries(compatSeries===k?null:k)}
                          className="nas-chip" data-on={compatSeries===k?'1':'0'} data-empty={n===0?'1':'0'}>
                          <Icon name={SERIES_ICON[k]||'dns'} size={16}/>{k}
                          <span className="nas-chip__n">{n}</span>
                        </button>
                        );
                      })}
                    </div>
                  )}
                </Card>
              )}

              {/* 모델 격자 */}
              {!compatModel && compatAll!==null && folders.length>0 && (
                <Card style={{padding:20,marginBottom:16}}>
                  <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:14,flexWrap:'wrap'}}>
                    <div style={{fontSize:16,fontWeight:700,display:'flex',alignItems:'center',gap:7}}>
                      <Icon name={compatSeries?(SERIES_ICON[compatSeries]||'dns'):'apps'} size={20} style={{color:'var(--accent)'}}/>
                      {compatSeries ? `${compatSeries} 시리즈` : '전체 모델'}
                      <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>· {gridList.length}종</span>
                    </div>
                    <div style={{flex:1}}/>
                    {/* v7.357: 격자 안 필터 입력 제거 — 위 검색창 하나로 합쳤다.
                        대신 지금 무엇으로 걸러졌는지 알려주는 표시를 둔다. */}
                    {compatSearch.trim() && (
                      <button onClick={()=>setCompatSearch('')}
                        className="nas-chip" data-on="1" title="검색어 지우기">
                        <Icon name="search" size={15}/>{compatSearch.trim()}
                        <Icon name="close" size={14}/>
                      </button>
                    )}
                  </div>
                  {gridList.length===0 ? (
                    <div style={{padding:'34px 0',textAlign:'center',color:'var(--fg-3)'}}>
                      <Icon name="search_off" size={30} style={{opacity:0.5}}/>
                      <div style={{marginTop:10,fontSize:13.5,fontWeight:600}}>
                        {compatSearch.trim() ? <>「{compatSearch.trim()}」과(와) 맞는 모델이 없습니다</> : '해당하는 모델이 없습니다'}
                      </div>
                      {/* 시리즈를 좁혀 둔 탓일 수 있다 — 전체에는 있는데 이 시리즈에만 없는 경우 */}
                      {compatSearch.trim() && compatSeries && totalMatch>0 && (
                        <button onClick={()=>setCompatSeries(null)}
                          style={{marginTop:10,padding:'7px 14px',border:'1px solid var(--accent)',borderRadius:9,background:'transparent',color:'var(--accent)',fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                          전체 시리즈에서 {totalMatch}건 찾기
                        </button>
                      )}
                    </div>
                  ) : (
                    /* ⚠ overflow 를 걸면 안 된다. 카드가 호버 시 아래로 펼쳐지는데
                          잘려 버린다. 63개 × 6열 = 11줄이라 페이지 스크롤로 충분하다. */
                    <GlassIcons items={gridList.map(p0=>{
                      const bays=nasBays(p0.product_name), ram=nasRam(p0.product_name);
                      const rack=nasRack(p0.product_name), rp=nasRedundantPsu(p0.product_name);
                      // 사양은 이름 아래 한 줄로 요약한다.
                      //   ⚠ v7.354 는 이걸 뱃지 여러 개로 폈다가 카드가 세로로 길어졌다.
                      const specs=[
                        bays!=null&&`${bays}베이`,
                        ram&&`기본 ${ram}`,
                        rack?'랙마운트':'타워',
                        rp&&'이중 전원',
                      ].filter(Boolean);
                      return {
                        key:p0.product_name,
                        label:p0.product_name,
                        title:p0.product_name,
                        subtitle:specs.join(' · '),
                        color:'amber',
                        // 종이 장수 = 사양 개수-1 (1~3장). 폴더가 [차 있는] 느낌을 준다
                        icon:<FolderGlyph sheets={Math.max(1,Math.min(3,specs.length-1))}/>,
                        onClick:()=>{setCompatModel(p0);setCompatSearch(p0.product_name);},
                      };
                    })}/>
                  )}
                </Card>
              )}

              {/* 결과 */}
              {!compatModel ? (
                !compatSeries && (
                <Card style={{padding:'44px 24px',textAlign:'center'}}>
                  <Icon name="memory" size={44} style={{color:'var(--fg-3)',opacity:0.45}}/>
                  <div style={{marginTop:14,fontSize:15,fontWeight:600,color:'var(--fg-2)'}}>NAS 모델을 검색하거나 시리즈를 선택해 주세요</div>
                  <div style={{marginTop:6,fontSize:13,color:'var(--fg-3)'}}>모델명 일부만 입력해도 됩니다</div>
                </Card>
                )
              ) : (
                <>
                  <Card style={{padding:22,marginBottom:16}}>
                    <div style={{display:'flex',alignItems:'flex-start',gap:14,flexWrap:'wrap'}}>
                      <div style={{width:52,height:52,borderRadius:12,background:'var(--bg-accent-strong)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                        <Icon name="dns" size={26} style={{color:'var(--accent)'}}/>
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:22,fontWeight:700,letterSpacing:'-0.01em',wordBreak:'break-word'}}>{compatModel.product_name}</div>
                        <div style={{marginTop:5,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          {compatModel.category && <Badge variant="category">{compatModel.category}</Badge>}
                        </div>
                      </div>
                      {/* v7.368: [다시 검색] 버튼 제거 — 같은 일을 하는 출구가 셋(이 버튼 ·
                          검색창 X · 브라우저 뒤로가기)이나 있었다. 검색창 X 하나로 모은다. */}
                    </div>
                  </Card>

                  {/* v7.358: 호환성 목록 — QNAP 공식 목록의 사본이다.
                      ⚠ 하단 출처/수집시각 표기를 지우지 말 것. 잘못된 안내의 책임 근거가 된다. */}
                  <Card style={{padding:24}}>
                    <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:14,flexWrap:'wrap'}}>
                      <Icon name="memory" size={20} style={{color:'var(--accent)'}}/>
                      <h2 style={{fontSize:18,fontWeight:600}}>호환 부품</h2>
                      {compatData?.qnapModel && (
                        <span style={{fontSize:12,color:'var(--fg-3)',fontWeight:500}}>
                          QNAP 기준 모델 · {compatData.qnapModel}
                        </span>
                      )}
                      {/* v7.365/366: 사내 테스트 추가·삭제 — 우측 상단 고정.
                          ⚠ 목록 안에 두면 카테고리를 바꿀 때마다 위치가 흔들린다.
                            "어디 있었더라" 하고 찾게 되는 버튼은 결국 안 쓰인다.
                          ⚠ 삭제는 [선택 모드] 다. 행마다 휴지통을 두면 스크롤 중 오클릭 한 번에
                            남이 적어 둔 테스트 결과가 사라진다. 고르고 → 확인하고 → 한 번에 지운다. */}
                      <span style={{marginLeft:'auto',display:'flex',gap:6,flexShrink:0}}>
                        {!icSelMode ? (
                          <>
                            {icCat.length>0 && (
                              <button onClick={()=>{setIcSelMode(true);setIcSel([]);}}
                                title="사내 테스트 결과 선택 삭제"
                                style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 13px',
                                  border:'1px solid var(--border-strong)',borderRadius:10,background:'var(--bg-card)',
                                  color:'var(--fg-2)',fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                                <Icon name="delete" size={16}/>삭제
                              </button>
                            )}
                            <button onClick={openIcNew}
                              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',
                                border:'1px solid var(--purple)',borderRadius:10,background:'var(--bg-card)',
                                color:'var(--purple)',fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                              <Icon name="science" size={16}/>테스트 결과 추가
                            </button>
                          </>
                        ) : (
                          <>
                            <button onClick={()=>{setIcSelMode(false);setIcSel([]);}}
                              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 13px',
                                border:'1px solid var(--border-strong)',borderRadius:10,background:'var(--bg-card)',
                                color:'var(--fg-2)',fontSize:12.5,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                              취소
                            </button>
                            {/* ⚠ 0건이면 비활성. 눌러 봐야 "선택된 항목이 없습니다" 만 나온다 */}
                            <button disabled={icSelCount===0}
                              onClick={async()=>{
                                const ids = icCat.filter(r=>icSel.includes(r.id)).map(r=>r.id);
                                if (!ids.length) return;
                                if (!window.confirm(`선택한 사내 테스트 결과 ${ids.length}건을 삭제할까요?\n되돌릴 수 없습니다.`)) return;
                                const res = await InternalCompatService.removeMany(ids);
                                if (!res.ok) { window.alert(`삭제 실패: ${res.error}`); return; }
                                setIcSelMode(false); setIcSel([]); setIcReload(v=>v+1);
                              }}
                              style={{display:'inline-flex',alignItems:'center',gap:6,padding:'7px 14px',border:'none',borderRadius:10,
                                background:icSelCount===0?'var(--surface-mute)':'var(--danger-solid)',color:'#fff',
                                fontSize:12.5,fontWeight:700,cursor:icSelCount===0?'not-allowed':'pointer',fontFamily:'inherit'}}>
                              <Icon name="delete" size={16}/>{icSelCount}건 삭제
                            </button>
                          </>
                        )}
                      </span>
                    </div>

                    {/* 카테고리 전환 */}
                    <div style={{display:'inline-flex',gap:4,padding:4,background:'var(--bg-subtle)',border:'1px solid var(--border)',borderRadius:11,marginBottom:16}}>
                      {CAT_TABS.map(c=>{
                        /* v7.365: 공식 + 사내 합산. 탭 숫자와 실제 목록 건수가 어긋나면
                           "17종이라더니 19개네" 가 되어 숫자 자체를 안 믿게 된다.
                           ⚠ 둘 다 아직 안 왔으면 null (숫자를 안 그린다). 0 을 먼저 그렸다가
                             숫자가 바뀌면 깜빡여 보인다. */
                        const qn = compatCounts ? (compatCounts[c]||0) : null;
                        const inn = icRows ? (icCounts[c]||0) : null;
                        const n = (qn==null && inn==null) ? null : ((qn||0) + (inn||0));
                        const on = compatCat===c;
                        return (
                        <button key={c} onClick={()=>setCompatCat(c)}
                          style={{padding:'8px 14px',border:'none',borderRadius:8,cursor:'pointer',fontFamily:'inherit',
                            display:'flex',alignItems:'center',gap:6,
                            background:on?'var(--accent)':'transparent',
                            color:on?'#fff':'var(--fg-2)',
                            /* v7.359: 0건 카테고리는 흐리게 — 눌러 봐야 아는 건 나쁘다.
                               ⚠ 감추지는 않는다. 0건인 것도 정보다 (메모리 온보드 모델 등) */
                            opacity:n===0?0.5:1,
                            fontSize:13,fontWeight:on?700:600,transition:'all 0.15s'}}>
                          {QNAP_CAT_LABEL[c]}
                          {n!=null && (
                            <span style={{padding:'1px 6px',borderRadius:9999,fontSize:10.5,fontWeight:700,
                              background:on?'rgba(255,255,255,0.24)':'var(--bg-mute)',
                              color:on?'#fff':'var(--fg-3)'}}>{n}</span>
                          )}
                        </button>
                        );
                      })}
                    </div>

                    {/* v7.366: 선택 모드 안내. 모드가 켜졌는지 헷갈리면 클릭이 왜 복사가 안 되는지
                        알 수 없다. 무엇을 고를 수 있는지도 여기서 못박는다. */}
                    {icSelMode && (
                      <div style={{display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',marginBottom:14,
                        padding:'10px 14px',borderRadius:10,background:'rgba(186,26,26,0.07)',border:'1px solid rgba(186,26,26,0.3)'}}>
                        <Icon name="delete_sweep" size={17} style={{color:'var(--danger)',flexShrink:0}}/>
                        <span style={{fontSize:12.5,fontWeight:600,color:'var(--fg-2)',lineHeight:1.5}}>
                          삭제할 <b style={{color:'var(--purple)'}}>사내 테스트 결과</b>를 선택해 주세요 —
                          QNAP 공식 목록 항목은 선택할 수 없습니다.
                        </span>
                        <span style={{marginLeft:'auto',display:'flex',gap:6}}>
                          <button onClick={()=>setIcSel(icCat.map(r=>r.id))} className="nas-chip"
                            style={{padding:'4px 10px',fontSize:11.5}}>전체 선택 {icCat.length}</button>
                          <button onClick={()=>setIcSel([])} className="nas-chip"
                            style={{padding:'4px 10px',fontSize:11.5}}>선택 해제</button>
                        </span>
                      </div>
                    )}

                    {(() => {
                      if (compatData === null) return (
                        <div style={{padding:'36px 20px',textAlign:'center',color:'var(--fg-3)',fontSize:14}}>
                          불러오는 중…
                        </div>
                      );

                      // 행이 0개인 이유를 구분해서 안내한다.
                      //   같은 [빈 화면]이라도 원인이 다르면 직원이 할 일이 다르다.
                      const EMPTY = {
                        none:       ['check_circle',  `이 모델은 ${QNAP_CAT_LABEL[compatCat]} 호환 목록이 없습니다`,
                                     'QNAP 조회는 정상입니다. 해당 부품을 지원하지 않는 모델일 수 있습니다 (예: 메모리 온보드).'],
                        not_synced: ['schedule',      '아직 수집되지 않았습니다',
                                     '다음 자동 수집(매주 화 03:00) 후 표시됩니다.'],
                        sync_error: ['error_outline', '마지막 수집이 실패했습니다',
                                     '관리자에게 알려 주세요. 아래 원문 링크에서 직접 확인하실 수 있습니다.'],
                        unlinked:   ['link_off',      'QNAP 모델과 연결되지 않았습니다',
                                     '제품명이 QNAP 표기와 달라 자동 연결에 실패했습니다. 관리자 확인이 필요합니다.'],
                        excluded:   ['block',         '호환성 조회 대상이 아닙니다',
                                     '수리 자산 등 표준 제품이 아닌 항목입니다.'],
                        error:      ['error_outline', '조회 중 오류가 발생했습니다',
                                     compatData.error || ''],
                      };

                      // ─── v7.365: 사내 테스트를 같은 목록에 합친다 ───
                      //   ⚠ 합치는 건 [배치] 이지 [출처] 가 아니다. 행마다 뱃지를 달고
                      //     하단 출처 문구에도 별도 문장을 남긴다. 둘 다 지우지 말 것 —
                      //     그게 없으면 사내 결과가 QNAP 보증인 것처럼 보인다.
                      // 공식 목록에 이미 있는 부품이면 [행을 하나 더 만들지 않고] 뱃지만 붙인다.
                      //   ⚠ 안 그러면 같은 모델이 두 줄로 나와 어느 쪽을 믿을지 모르게 된다.
                      const icIdx = InternalCompatService.indexByModel(icCat);
                      const officialNorm = new Set();
                      if (compatData.state === 'ok') {
                        (compatData.rows || []).forEach(r => {
                          officialNorm.add(qnapNorm(r.model));
                          if (r.model_alt) officialNorm.add(qnapNorm(r.model_alt));
                        });
                      }
                      // 공식에 없는 사내 부품만 목록에 얹는다 (삼성 · Transcend 등).
                      const icExtra = icCat
                        .filter(r => !officialNorm.has(qnapNorm(r.model)) &&
                                     !(r.model_alt && officialNorm.has(qnapNorm(r.model_alt))))
                        // ⚠ is_recommended 를 건드리지 않는다. [QNAP 비권장] 과 [사내 불가] 는
                        //   근거가 다른 판정이다. 같은 칸에 넣으면 비권장 토글로 사내 불가가
                        //   같이 사라져 "왜 없어졌지" 가 된다.
                        .map(r => ({ ...r, _ic: true }));

                      if (compatData.state !== 'ok' && icExtra.length === 0) {
                        const [ic,title,desc] = EMPTY[compatData.state] || EMPTY.error;
                        /* v7.367: 2.5" SSD 가 0건인데 M.2 는 있는 모델이 있다 (TBS 계열 등).
                           ⚠ 이 안내가 없으면 "SSD 0건" 만 보고 지원 안 한다고 답하게 된다.
                             실제로 이번 M.2 누락도 그렇게 발견됐다. 옆 탭으로 보내 준다. */
                        const m2n = compatCounts ? (compatCounts[QNAP_CAT.M2SSD]||0) : 0;
                        const hintM2 = compatCat===QNAP_CAT.SSD && compatData.state==='none' && m2n>0;
                        return (
                          <div style={{padding:'36px 20px',textAlign:'center',background:'var(--bg-accent-soft)',borderRadius:12,border:'1px dashed var(--border-strong)'}}>
                            <Icon name={ic} size={30} style={{color:'var(--fg-3)',opacity:0.6}}/>
                            <div style={{marginTop:11,fontSize:14,fontWeight:600,color:'var(--fg-2)'}}>{title}</div>
                            {desc && <div style={{marginTop:6,fontSize:13,color:'var(--fg-3)',lineHeight:1.6,maxWidth:460,margin:'6px auto 0'}}>{desc}</div>}
                            {hintM2 && (
                              <button onClick={()=>setCompatCat(QNAP_CAT.M2SSD)}
                                style={{marginTop:14,display:'inline-flex',alignItems:'center',gap:7,padding:'9px 16px',
                                  border:'1px solid var(--accent)',borderRadius:10,background:'var(--bg-card)',
                                  color:'var(--accent)',fontSize:13,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                                <Icon name="memory_alt" size={17}/>이 모델은 M.2 SSD 를 씁니다 — {m2n}건 보기
                              </button>
                            )}
                          </div>
                        );
                      }

                      // 공식 조회가 실패/미연결이어도 사내 결과가 있으면 그것만이라도 보여준다.
                      //   ⚠ 확장 카드는 애초에 공식 목록이 없다. 여기서 막으면 영영 안 보인다.
                      const officialOk = compatData.state === 'ok';
                      const all = [...(officialOk ? compatData.rows : []), ...icExtra];
                      const isMem = compatCat === QNAP_CAT.MEMORY;

                      // v7.359: 목록 안에서 다시 좁힌다.
                      //   ⚠ HDD 는 200건이 넘는다. 제조사별로 묶어도 스크롤이 길어
                      //     "이 NAS 에 14TB 뭐 되나" 를 눈으로 찾아야 했다.
                      const nrm = v => String(v||'').toLowerCase().replace(/[\s-]/g,'');
                      const pq = nrm(partQ);
                      // v7.360: 각 부품에 보유 재고를 붙인다.
                      //   ⚠ QNAP 모델명과 model_alt(괄호 파트넘버) 둘 다로 찾는다.
                      //     '0B48721 (WUH722014CLE6L4)' 처럼 재고에는 alt 쪽으로 적혀 있을 수 있다.
                      const stockOf = (r) => {
                        if (!stockIdx) return null;
                        return stockIdx[qnapNorm(r.model)] || stockIdx[qnapNorm(r.model_alt)] || null;
                      };
                      const withStock = all.map(r => ({ ...r, _stock: stockOf(r) }));
                      const rows = withStock.filter(r =>
                        (!pq || nrm(r.model).includes(pq) || nrm(r.model_alt).includes(pq)) &&
                        (capFilter==null || r.capacity_gb===capFilter) &&
                        (!hideNR || r.is_recommended !== false) &&
                        (!stockOnly || r._stock)
                      );
                      // 재고 보유 종수 — 필터와 무관하게 전체 기준으로 센다
                      const stockKinds = withStock.filter(r => r._stock).length;
                      const stockQty = withStock.reduce((n,r)=> n + (r._stock ? r._stock.total : 0), 0);

                      // 용량 칩 — 전체(all) 기준으로 만든다.
                      //   ⚠ 걸러진 결과(rows) 로 만들면 용량을 하나 고르는 순간 다른 칩이 사라져
                      //     바꿔 고를 수가 없다.
                      const caps = [...new Set(all.map(r=>r.capacity_gb).filter(v=>v!=null))]
                        .sort((a,b)=>b-a);
                      const nrCount = all.filter(r=>r.is_recommended===false).length;
                      const maxCap = caps.length ? caps[0] : null;

                      const byBrand = {};
                      rows.forEach(r => { (byBrand[r.brand] = byBrand[r.brand] || []).push(r); });
                      // ⚠ 브랜드 안에서 재고 보유 항목을 앞으로 올린다.
                      //   "우리가 가진 것"이 200건 중간에 파묻히면 붙인 의미가 없다.
                      //   v7.365: 사내 [불가] 판정은 그룹 맨 뒤로. 못 쓰는 부품이 위에 있을 이유가 없다.
                      Object.values(byBrand).forEach(list =>
                        list.sort((a,b)=>
                          ((a._ic && a.result===IC_RESULT.FAIL)?1:0) - ((b._ic && b.result===IC_RESULT.FAIL)?1:0) ||
                          (b._stock?1:0)-(a._stock?1:0)));
                      // 재고 있는 브랜드를 앞으로
                      const brands = Object.keys(byBrand).sort((a,b)=>{
                        const sa = byBrand[a].some(r=>r._stock)?1:0, sb = byBrand[b].some(r=>r._stock)?1:0;
                        return sb-sa || a.localeCompare(b);
                      });

                      return (
                        <>
                          {/* 요약 — 최대 용량은 영업에서 제일 먼저 묻는 값이다 */}
                          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:12}}>
                            <span style={{fontSize:13,color:'var(--fg-2)',fontWeight:600}}>
                              {rows.length===all.length ? `${all.length}종` : `${rows.length} / ${all.length}종`}
                            </span>
                            <span style={{fontSize:13,color:'var(--fg-3)'}}>· {brands.length}개 제조사</span>
                            {maxCap!=null && (
                              <span style={{fontSize:13,color:'var(--fg-3)'}}>
                                · 최대 <b style={{color:'var(--accent)'}}>{QnapCompatService.fmtCapacity(maxCap, compatCat)}</b>
                              </span>
                            )}
                            {/* v7.360: 보유 재고 요약 */}
                            {stockIdx!==null && stockKinds>0 && (
                              <span style={{fontSize:13,color:'var(--fg-3)'}}>
                                · 보유 <b style={{color:'var(--success)'}}>{stockKinds}종 {stockQty}개</b>
                              </span>
                            )}
                            {/* v7.365: 이 목록에 사내 결과가 몇 건 섞여 있는지 — 숨기지 않는다 */}
                            {icCat.length>0 && (
                              <span style={{fontSize:13,color:'var(--fg-3)'}}>
                                · 사내 <b style={{color:'var(--purple)'}}>{icCat.length}건</b>
                              </span>
                            )}
                            <span style={{marginLeft:'auto',display:'flex',gap:6}}>
                              {stockIdx!==null && stockKinds>0 && (
                                <button onClick={()=>setStockOnly(v=>!v)} className="nas-chip" data-on={stockOnly?'1':'0'}
                                  style={{padding:'5px 11px',fontSize:12}}>
                                  <Icon name="inventory_2" size={14}/>재고만
                                </button>
                              )}
                              {nrCount>0 && (
                                <button onClick={()=>setHideNR(v=>!v)} className="nas-chip" data-on={hideNR?'1':'0'}
                                  style={{padding:'5px 11px',fontSize:12}}>
                                  <Icon name={hideNR?'visibility_off':'visibility'} size={14}/>
                                  비권장 {nrCount}건 {hideNR?'숨김':'표시'}
                                </button>
                              )}
                            </span>
                          </div>

                          {/* 목록 안 검색 + 용량 칩 */}
                          <div style={{display:'flex',gap:8,flexWrap:'wrap',alignItems:'center',marginBottom:14}}>
                            <div style={{position:'relative',flex:'0 0 auto'}}>
                              <Icon name="search" size={15} style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)',pointerEvents:'none'}}/>
                              <input value={partQ} onChange={e=>setPartQ(e.target.value)}
                                placeholder={isMem ? '부품 검색 — RAM-16G'
                                  : compatCat===QNAP_CAT.M2SSD ? '부품 검색 — WDS100 · MZ-N6E'
                                  : '부품 검색 — WD140 · MG10'}
                                style={{padding:'7px 12px 7px 31px',border:'1px solid var(--border-strong)',borderRadius:9,fontSize:12.5,fontFamily:'inherit',outline:'none',background:'var(--bg-card)',width:190}}/>
                            </div>
                            {caps.length>1 && (
                              <>
                                {/* v7.412: [전체] 칩 제거 (사용자 요청). 기본값이 capFilter=null(전체)이고,
                                    선택된 칩을 다시 누르면 null 로 돌아가므로 전체 보기는 그대로 가능하다. */}
                                {caps.map(c=>(
                                  <button key={c} onClick={()=>setCapFilter(c)} className="nas-chip"
                                    data-on={capFilter===c?'1':'0'} style={{padding:'5px 11px',fontSize:12}}>
                                    {QnapCompatService.fmtCapacity(c, compatCat)}
                                  </button>
                                ))}
                              </>
                            )}
                          </div>

                          {rows.length===0 && (
                            <div style={{padding:'30px 0',textAlign:'center',color:'var(--fg-3)'}}>
                              <Icon name="search_off" size={26} style={{opacity:0.5}}/>
                              <div style={{marginTop:8,fontSize:13,fontWeight:600}}>조건에 맞는 부품이 없습니다</div>
                              <button onClick={()=>{setPartQ('');setCapFilter(null);setHideNR(false);setStockOnly(false);}}
                                style={{marginTop:10,padding:'6px 13px',border:'1px solid var(--accent)',borderRadius:8,background:'transparent',color:'var(--accent)',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                                필터 초기화
                              </button>
                            </div>
                          )}
                          {brands.map(b => (
                            <div key={b} style={{marginBottom:18}}>
                              <div style={{fontSize:13,fontWeight:700,color:'var(--fg-2)',marginBottom:8,display:'flex',alignItems:'center',gap:7}}>
                                {b}
                                <span style={{fontSize:11,fontWeight:500,color:'var(--fg-3)'}}>{byBrand[b].length}</span>
                                {/* v7.365: 그룹 전체가 사내 결과면 제조사 옆에 한 번만 표시한다.
                                    ⚠ 행마다 붙는 뱃지와 별개다 — 제조사 줄만 보고도
                                      "이건 QNAP 이 보증한 게 아니다" 를 알 수 있어야 한다. */}
                                {byBrand[b].every(r=>r._ic) && (
                                  <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:9999,
                                    background:'rgba(123,31,162,0.12)',color:'var(--purple)',fontSize:10.5,fontWeight:700}}>
                                    <Icon name="science" size={11}/>사내 테스트
                                  </span>
                                )}
                              </div>
                              {/* v7.359: 한 줄 = 한 부품이라 폭이 남아돌았다. 반응형 다단으로 바꿔
                                  같은 높이에 3배쯤 더 보인다. 245건짜리 HDD 목록에서 체감이 크다. */}
                              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(310px,1fr))',gap:6}}>
                                {byBrand[b].map((r,i) => {
                                  const nr = r.is_recommended === false;
                                  /* v7.364: 공식 목록에도 있는 부품을 사내에서 또 테스트했으면 알려 준다.
                                     ⚠ 이게 없으면 같은 부품을 두 번 테스트하게 된다.
                                     ⚠ 사내 전용 행(_ic)에는 붙이지 않는다 — 자기 자신이라 뱃지가 겹친다. */
                                  const icHit = r._ic ? null
                                    : (icIdx[qnapNorm(r.model)] || icIdx[qnapNorm(r.model_alt)] || null);
                                  // v7.365: 사내 [불가] 판정. 재고 글로우도 끈다 — 못 쓰는 걸 강조할 이유가 없다.
                                  const icBad = r._ic && r.result === IC_RESULT.FAIL;
                                  // v7.366: 선택 삭제 모드. 사내 행만 고를 수 있다.
                                  //   ⚠ 공식 행은 우리가 지울 수 있는 게 아니다 — 흐리게 죽여
                                  //     "여긴 못 고른다" 를 눌러 보기 전에 알 수 있게 한다.
                                  const selectable = icSelMode && r._ic;
                                  const picked = selectable && icSel.includes(r.id);
                                  const dimmed = icSelMode && !r._ic;
                                  const glowOn = r._stock && !icBad && !icSelMode;
                                  return (
                                  <div key={`${r.model}-${i}`}
                                    /* v7.363: 재고 보유 행은 배송시트지 출고요청과 같은 방식으로
                                       테두리를 도는 빛을 붙인다 (색만 초록).
                                       ⚠ glow-mark 없이 glow-run 만 켜면 도는 빛이 어디를 도는지
                                         알 수 없다 — 상시 테두리가 궤도 역할을 한다. */
                                    className={'qc-row' + (glowOn ? ' glow-row glow-mark glow-run' : '')}
                                    onPointerMove={glowOn ? stockGlow.onPointerMove : undefined}
                                    onPointerLeave={glowOn ? stockGlow.onPointerLeave : undefined}
                                    /* 모델명 클릭 = 복사. 영업에서 견적서에 옮겨 적는 일이 잦다.
                                       ⚠ 이 앱엔 토스트가 없다. 새로 만들기보다 누른 카드에 직접
                                         표시하는 게 가볍고, 어느 걸 복사했는지도 분명하다.
                                       ⚠ navigator.clipboard 는 https/localhost 에서만 동작한다.
                                         실패하면 조용히 넘어간다 (사내 도메인은 https 라 문제없다). */
                                    onClick={e=>{
                                      // v7.366: 선택 모드에서는 클릭이 [선택] 이다.
                                      //   ⚠ 같은 클릭이 모드에 따라 복사도 되고 선택도 되면 헷갈린다.
                                      //     모드 중에는 복사를 아예 막는다.
                                      if (icSelMode) {
                                        if (!r._ic) return;
                                        setIcSel(v => v.includes(r.id) ? v.filter(x=>x!==r.id) : [...v, r.id]);
                                        return;
                                      }
                                      const el=e.currentTarget;
                                      try{
                                        navigator.clipboard.writeText(r.model);
                                        el.setAttribute('data-copied','1');
                                        setTimeout(()=>el.removeAttribute('data-copied'),1100);
                                      }catch(err){}
                                    }}
                                    title={icSelMode
                                      ? (r._ic ? '클릭하면 선택/해제' : '사내 테스트 결과만 삭제할 수 있습니다')
                                      : `클릭하면 모델명 복사 · ${r.model}`}
                                    style={{
                                      ...(glowOn ? STOCK_GLOW_STYLES[theme==='dark'?'dark':'light'] : null),
                                      padding:'9px 12px',borderRadius:10,cursor:'pointer',
                                      /* ⚠ 글로우가 붙은 행은 테두리를 투명하게 둔다.
                                         실선 초록 테두리와 도는 빛이 겹치면 서로를 가린다. */
                                      border:`1px solid ${glowOn?'transparent':(picked?'var(--danger)':icBad?'var(--danger)':'var(--border)')}`,
                                      background: picked ? 'rgba(186,26,26,0.08)' : (nr||icBad) ? 'var(--bg-subtle)' : 'var(--bg-card)',
                                      cursor: dimmed ? 'default' : 'pointer',
                                      opacity: dimmed ? 0.4 : (nr||icBad) ? 0.75 : 1}}>
                                    <span className="qc-copied">복사됨</span>
                                    {/* 1행: 뱃지 + 모델명 + 용량 */}
                                    <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap'}}>
                                      {/* v7.366: 체크 표시는 제일 앞. 선택 여부가 한눈에 들어와야
                                          여러 건을 고르는 동안 실수가 없다 */}
                                      {selectable && (
                                        <span style={{width:17,height:17,borderRadius:5,flexShrink:0,
                                          display:'inline-flex',alignItems:'center',justifyContent:'center',
                                          border:`1.5px solid ${picked?'var(--danger-solid)':'var(--border-strong)'}`,
                                          background:picked?'var(--danger-solid)':'transparent',color:'#fff'}}>
                                          {picked && <Icon name="check" size={13}/>}
                                        </span>
                                      )}
                                      {/* v7.360: 보유 재고 — 제일 앞에 둔다. 영업이 제일 먼저 볼 값이다 */}
                                      {r._stock && (
                                        <span style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:9999,
                                          background:'var(--success-solid)',color:'#fff',fontSize:10.5,fontWeight:700,flexShrink:0}}>
                                          <Icon name="inventory_2" size={11}/>{r._stock.total}
                                        </span>
                                      )}
                                      {/* v7.365: 사내 전용 행 — 이 뱃지가 [출처가 다르다] 는 유일한 표시다.
                                          누르면 바로 수정으로 들어간다 (행 클릭은 복사라 stopPropagation 필수) */}
                                      {r._ic && (
                                        /* ⚠ 선택 삭제 모드에서는 핸들러를 떼서 클릭이 행(=선택)으로 흐르게 둔다.
                                           stopPropagation 이 남아 있으면 뱃지 위만 선택이 안 먹어 고장으로 보인다. */
                                        <span onClick={icSelMode?undefined:(e=>{e.stopPropagation();openIcEdit(r);})}
                                          title={icSelMode?undefined:'사내 테스트 결과 — 누르면 수정'}
                                          style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:9999,flexShrink:0,cursor:'pointer',
                                            background:icBad?'rgba(186,26,26,0.12)':'rgba(123,31,162,0.12)',
                                            color:icBad?'var(--danger)':'var(--purple)',fontSize:10.5,fontWeight:700}}>
                                          <Icon name="science" size={11}/>
                                          사내 {IC_RESULT_LABEL[r.result]}
                                        </span>
                                      )}
                                      {icHit && (
                                        <span onClick={icSelMode?undefined:(e=>{e.stopPropagation();openIcEdit(icHit);})}
                                          title={(icHit.note ? icHit.note + ' · ' : '') + `사내 테스트 ${fmtDate(icHit.tested_at)}` + (icSelMode?'':' — 누르면 수정')}
                                          style={{display:'inline-flex',alignItems:'center',gap:3,padding:'1px 7px',borderRadius:9999,flexShrink:0,cursor:'pointer',
                                            background:icHit.result===IC_RESULT.FAIL?'rgba(186,26,26,0.12)':'rgba(123,31,162,0.12)',
                                            color:icHit.result===IC_RESULT.FAIL?'var(--danger)':'var(--purple)',fontSize:10.5,fontWeight:700}}>
                                          <Icon name="science" size={11}/>
                                          사내 {IC_RESULT_LABEL[icHit.result]}
                                        </span>
                                      )}
                                      {r.is_new && <Badge variant="category">NEW</Badge>}
                                      {nr && <Badge variant="category">비권장</Badge>}
                                      <span style={{fontSize:13.5,fontWeight:600,fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace',wordBreak:'break-all',minWidth:0,
                                        textDecoration: icBad?'line-through':'none'}}>
                                        {r.model}
                                      </span>
                                      {r.capacity_gb != null && (
                                        <span style={{marginLeft:'auto',fontSize:13,fontWeight:700,color:'var(--accent)',flexShrink:0}}>
                                          {QnapCompatService.fmtCapacity(r.capacity_gb, compatCat)}
                                        </span>
                                      )}
                                    </div>
                                    {/* 2행: 부가 정보 — 없으면 렌더하지 않는다 (빈 줄이 생기면 카드 높이가 들쭉날쭉) */}
                                    {(() => {
                                      const meta = isMem
                                        ? [r.ddr_gen, r.speed_mhz && `${r.speed_mhz}MHz`, r.form_factor,
                                           String(r.is_ecc)==='true' && 'ECC'].filter(Boolean)
                                        : [r.rank, r.sed && r.sed!=='Non-SED' && r.sed,
                                           r.firmwares && `FW ${r.firmwares}`].filter(Boolean);
                                      if (r.model_alt) meta.push(`(${r.model_alt})`);
                                      // 창고별 보유 — 어디 있는지 알아야 바로 꺼낸다
                                      if (r._stock) {
                                        const w = Object.entries(r._stock.byWarehouse)
                                          .map(([k,v])=>`${k} ${v}`).join(', ');
                                        meta.unshift(`보유 ${w}`);
                                      }
                                      if (!meta.length) return null;
                                      return (
                                        <div style={{marginTop:4,fontSize:11.5,color:'var(--fg-3)',lineHeight:1.5,wordBreak:'break-word'}}>
                                          {meta.join(' · ')}
                                        </div>
                                      );
                                    })()}
                                    {/* v7.365: 사내 행 전용 — 비고는 접지 않는다.
                                        불가 판정의 이유가 여기 있고, 접어 두면 아무도 안 읽는다. */}
                                    {r._ic && r.note && (
                                      <div style={{marginTop:5,fontSize:11.5,lineHeight:1.5,color:icBad?'var(--danger)':'var(--fg-2)',
                                        background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:7,padding:'5px 8px',wordBreak:'break-word'}}>
                                        {r.note}
                                      </div>
                                    )}
                                    {r._ic && (
                                      <div style={{marginTop:6,display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                                        <span style={{fontSize:11,color:'var(--fg-3)'}}>
                                          {fmtDate(r.tested_at)}
                                          {r.tested_by && ` · ${r.tested_by}`}
                                          {r.fw_version && ` · ${r.fw_version}`}
                                        </span>
                                        {/* v7.366: 수정 = 톱니바퀴 하나. 글자를 뺀 건 카드 폭이 310px 이라
                                            [수정][삭제] 두 개가 날짜·담당자 줄을 밀어내고 있었기 때문이다.
                                            ⚠ 선택 삭제 모드에서는 감춘다 — 지우려고 고르는 중에
                                              수정 창이 뜨면 흐름이 끊긴다. */}
                                        {!icSelMode && (
                                          <button onClick={e=>{e.stopPropagation();openIcEdit(r);}} title="사내 테스트 결과 수정"
                                            style={{marginLeft:'auto',display:'flex',alignItems:'center',justifyContent:'center',
                                              width:26,height:26,border:'1px solid var(--border-strong)',borderRadius:7,
                                              background:'var(--bg-card)',color:'var(--fg-2)',cursor:'pointer',fontFamily:'inherit',flexShrink:0}}>
                                            <Icon name="settings" size={15}/>
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </>
                      );
                    })()}

                    {/* 출처 — 지우지 말 것.
                        v7.364: 소스에 따라 문구가 갈린다. 사내 테스트 결과에 [QNAP 공식] 이라고
                        적히면 그 순간 우리가 QNAP 보증을 사칭하는 셈이 된다. */}
                    <div style={{marginTop:18,paddingTop:14,borderTop:'1px solid var(--border)',fontSize:12,color:'var(--fg-3)',lineHeight:1.7}}>
                      출처 · QNAP 공식 호환성 목록
                      {compatData?.syncedAt && <> · 수집 {new Date(compatData.syncedAt).toLocaleString('ko-KR')}</>}
                      {compatData?.stale && <span style={{color:'var(--accent)',fontWeight:700}}> · 오래된 정보입니다</span>}
                      {compatData?.modelId && (
                        <> · <a href={QnapCompatService.sourceUrl(compatData.modelId, compatCat)} target="_blank" rel="noreferrer"
                             style={{color:'var(--accent)',fontWeight:600}}>원문 확인</a></>
                      )}
                      <br/>정확한 최신 정보는 반드시 원문을 확인해 주세요.
                      {/* v7.365: 한 목록에 두 출처가 섞여 있으므로 두 번째 문장이 반드시 필요하다.
                          ⚠ 이 줄을 지우면 사내 테스트 결과가 QNAP 보증인 것처럼 읽힌다.
                            목록을 합친 대가로 반드시 남겨야 하는 문장이다. */}
                      {(icRows||[]).some(r=>r.category_type===compatCat) && (
                        <>
                          <br/>
                          <span style={{color:'var(--purple)',fontWeight:700}}>
                            <Icon name="science" size={12} style={{verticalAlign:'-2px',marginRight:3}}/>
                            사내
                          </span>
                          {' '}표시 항목은 <b>리버네트워크 자체 테스트 결과</b>이며 QNAP 공식 목록이 아닙니다 —
                          제조사 보증 대상이 아니므로 고객 안내 시 반드시 함께 알려 주세요.
                        </>
                      )}
                    </div>
                  </Card>

                  {/* ═══ v7.364: 사내 테스트 입력/수정 모달 ═══
                      ⚠ 전 직원이 쓴다. 필수는 [제조사 · 모델명 · 결과] 셋뿐이고 나머지는 비워도 저장된다.
                        칸이 많다고 다 채우라고 막으면 아무도 안 쓰게 된다. */}
                  {icModal && (
                    <div onClick={()=>{ if(!icBusy) setIcModal(null); }}
                      style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.45)',zIndex:9999,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
                      <div onClick={e=>e.stopPropagation()}
                        style={{background:'var(--bg-card)',borderRadius:16,padding:22,width:'100%',maxWidth:560,maxHeight:'88vh',overflowY:'auto',boxShadow:'0 12px 40px rgba(0,0,0,0.28)'}}>
                        <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                          <Icon name="science" size={19} style={{color:'var(--purple)'}}/>
                          <span style={{fontSize:16,fontWeight:700,color:'var(--fg-1)'}}>
                            사내 테스트 결과 {icModal.id?'수정':'추가'}
                          </span>
                        </div>
                        <div style={{fontSize:12,color:'var(--fg-3)',marginBottom:16,lineHeight:1.5}}>
                          대상 NAS · <b style={{color:'var(--fg-2)'}}>{icModal.product_name}</b>
                          <br/>QNAP 공식 목록과는 별도로 저장되며, 화면에서도 [사내 테스트] 로 구분되어 표시됩니다.
                        </div>

                        {(() => {
                          const set = (k,v)=> setIcModal(m=>({...m,[k]:v}));
                          const lbl = {fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5,display:'block'};
                          const fld = {width:'100%',padding:'8px 10px',fontSize:13,border:'1px solid var(--border-strong)',
                                       borderRadius:8,background:'var(--bg-card)',color:'var(--fg-1)',fontFamily:'inherit',boxSizing:'border-box'};
                          const mono = {...fld,fontFamily:'ui-monospace,SFMono-Regular,Menlo,monospace'};
                          const isMemF = Number(icModal.category_type) === QNAP_CAT.MEMORY;
                          return (
                          <>
                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:12}}>
                              <div>
                                <label style={lbl}>카테고리</label>
                                <select value={icModal.category_type} onChange={e=>set('category_type',Number(e.target.value))} style={fld}>
                                  {[QNAP_CAT.MEMORY,QNAP_CAT.HDD,QNAP_CAT.SSD,QNAP_CAT.M2SSD,QNAP_CAT.EXPANSION].map(c=>
                                    <option key={c} value={c}>{QNAP_CAT_LABEL[c]}</option>)}
                                </select>
                              </div>
                              <div>
                                <label style={lbl}>결과 <span style={{color:'var(--danger)'}}>*</span></label>
                                <div style={{display:'flex',gap:6}}>
                                  {[[IC_RESULT.OK,'호환','var(--success-solid)'],[IC_RESULT.FAIL,'불가','var(--danger-solid)']].map(([v,t,col])=>{
                                    const on = icModal.result===v;
                                    return (
                                      <button key={v} onClick={()=>set('result',v)}
                                        style={{flex:1,padding:'8px 0',borderRadius:8,cursor:'pointer',fontFamily:'inherit',fontSize:13,fontWeight:700,
                                          border:`1px solid ${on?col:'var(--border-strong)'}`,
                                          background:on?col:'var(--bg-card)',color:on?'#fff':'var(--fg-2)'}}>{t}</button>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>

                            <div style={{display:'grid',gridTemplateColumns:'1fr 1.6fr',gap:12,marginBottom:12}}>
                              <div>
                                <label style={lbl}>제조사 <span style={{color:'var(--danger)'}}>*</span></label>
                                {/* ⚠ select 로 막지 않는다 — 새 제조사가 나오면 입력 자체가 불가능해진다 */}
                                <input list="ic-brands" value={icModal.brand} onChange={e=>set('brand',e.target.value)}
                                  placeholder="삼성" style={fld}/>
                                <datalist id="ic-brands">
                                  {IC_BRANDS.map(b=><option key={b} value={b}/>)}
                                </datalist>
                              </div>
                              <div>
                                <label style={lbl}>모델명(파트넘버) <span style={{color:'var(--danger)'}}>*</span></label>
                                <input value={icModal.model} onChange={e=>set('model',e.target.value)}
                                  placeholder="M471A1K43CB1-CTD" style={mono}/>
                              </div>
                            </div>

                            <div style={{display:'grid',gridTemplateColumns:'1.6fr 1fr',gap:12,marginBottom:12}}>
                              <div>
                                <label style={lbl}>대체 모델명 <span style={{fontWeight:500,color:'var(--fg-3)'}}>(선택)</span></label>
                                <input value={icModal.model_alt} onChange={e=>set('model_alt',e.target.value)}
                                  placeholder="유통 모델명 · 다른 파트넘버" style={mono}/>
                              </div>
                              <div>
                                <label style={lbl}>용량 (GB)</label>
                                <input value={icModal.capacity_gb} onChange={e=>set('capacity_gb',e.target.value)}
                                  inputMode="numeric" placeholder={isMemF?'16':'14000'} style={fld}/>
                              </div>
                            </div>

                            {/* 메모리일 때만 — HDD 에 DDR 칸이 뜨면 무슨 값을 넣어야 할지 헷갈린다 */}
                            {isMemF && (
                              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr',gap:10,marginBottom:12}}>
                                <div>
                                  <label style={lbl}>규격</label>
                                  <select value={icModal.ddr_gen} onChange={e=>set('ddr_gen',e.target.value)} style={fld}>
                                    <option value="">-</option>
                                    {['DDR3','DDR4','DDR5'].map(v=><option key={v} value={v}>{v}</option>)}
                                  </select>
                                </div>
                                <div>
                                  <label style={lbl}>속도(MHz)</label>
                                  <input value={icModal.speed_mhz} onChange={e=>set('speed_mhz',e.target.value)}
                                    inputMode="numeric" placeholder="3200" style={fld}/>
                                </div>
                                <div>
                                  <label style={lbl}>폼팩터</label>
                                  <select value={icModal.form_factor} onChange={e=>set('form_factor',e.target.value)} style={fld}>
                                    <option value="">-</option>
                                    <option value="UD">UD (데스크톱)</option>
                                    <option value="SO">SO (노트북)</option>
                                  </select>
                                </div>
                                <div>
                                  <label style={lbl}>ECC</label>
                                  <select value={icModal.is_ecc} onChange={e=>set('is_ecc',e.target.value)} style={fld}>
                                    <option value="">-</option>
                                    <option value="true">ECC</option>
                                    <option value="false">Non-ECC</option>
                                  </select>
                                </div>
                              </div>
                            )}

                            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:10,marginBottom:12}}>
                              <div>
                                <label style={lbl}>테스트일</label>
                                <input type="date" value={icModal.tested_at} onChange={e=>set('tested_at',e.target.value)} style={fld}/>
                              </div>
                              <div>
                                <label style={lbl}>테스트 담당</label>
                                <select value={icModal.tested_by} onChange={e=>set('tested_by',e.target.value)} style={fld}>
                                  <option value="">-</option>
                                  {staffList.map(n=><option key={n} value={n}>{n}</option>)}
                                  {icModal.tested_by && !staffList.includes(icModal.tested_by) &&
                                    <option value={icModal.tested_by}>{icModal.tested_by}</option>}
                                </select>
                              </div>
                              <div>
                                <label style={lbl}>펌웨어</label>
                                <input value={icModal.fw_version} onChange={e=>set('fw_version',e.target.value)}
                                  placeholder="QTS 5.2.0" style={fld}/>
                              </div>
                            </div>

                            <div style={{marginBottom:14}}>
                              <label style={lbl}>
                                비고
                                <span style={{fontWeight:500,color:'var(--fg-3)'}}> — 조건·증상을 적어 주세요 (2개 장착 시 1개만 인식 등)</span>
                              </label>
                              <textarea value={icModal.note} onChange={e=>set('note',e.target.value)} rows={3}
                                placeholder="불가 판정이면 왜 불가인지 꼭 남겨 주세요. 나중에 같은 부품을 또 테스트하게 됩니다."
                                style={{...fld,resize:'vertical',lineHeight:1.5}}/>
                            </div>

                            {icErr && (
                              <div style={{marginBottom:12,padding:'9px 12px',borderRadius:8,fontSize:12.5,fontWeight:600,lineHeight:1.5,
                                background:'rgba(186,26,26,0.08)',border:'1px solid rgba(186,26,26,0.3)',color:'var(--danger)'}}>
                                {icErr}
                              </div>
                            )}

                            <div style={{display:'flex',gap:8}}>
                              <button disabled={icBusy} onClick={()=>setIcModal(null)}
                                style={{flex:'0 0 auto',padding:'10px 18px',border:'1px solid var(--border-strong)',borderRadius:9,
                                  background:'var(--bg-card)',color:'var(--fg-2)',fontSize:13.5,fontWeight:600,
                                  cursor:icBusy?'not-allowed':'pointer',fontFamily:'inherit'}}>
                                취소
                              </button>
                              <button disabled={icBusy} onClick={async()=>{
                                  setIcBusy(true); setIcErr('');
                                  const res = await InternalCompatService.save(icModal);
                                  setIcBusy(false);
                                  if (!res.ok) { setIcErr(res.error); return; }
                                  // 저장한 카테고리로 옮겨 준다 — 저장했는데 화면에 안 보이면 실패로 오해한다
                                  setCompatCat(Number(icModal.category_type));
                                  setIcModal(null);
                                  setIcReload(v=>v+1);
                                }}
                                style={{flex:1,padding:'10px',border:'none',borderRadius:9,
                                  background:icBusy?'var(--surface-mute)':'var(--purple-solid)',color:'#fff',fontSize:13.5,fontWeight:700,
                                  cursor:icBusy?'not-allowed':'pointer',fontFamily:'inherit'}}>
                                {icBusy?'저장 중…':'저장'}
                              </button>
                            </div>
                          </>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            );
          })()}

          {/* ═══ v7.339 NEXI (넥시 조회) ═══
              구글시트 [상품 위치] 이관 1단계 — 도면 + 위치 하이라이트까지.
              주문코드 → 위치 매핑(데이터 적재)은 아직 없다. 지금은 위치 코드를 직접 넣는다. */}
          {page==='nexi' && (()=>{
            const q = nexiQuery.trim();
            const norm = normalizeNexiCode(q);
            const found = !!q && isNexiCode(q);
            // 부분 일치 후보 — 오타/불완전 입력 시 눌러서 채울 수 있게
            const suggests = (!q || found) ? [] :
              NEXI_CODES.filter(c => normalizeNexiCode(c).startsWith(norm)).slice(0, 12);
            const zoneOf = (c) => {
              const n = normalizeNexiCode(c);
              if (n.startsWith('블')) return { name: '파렛트(블루)', color: '#1d4ed8' };
              if (n.startsWith('레')) return { name: '파렛트(레드)', color: '#be123c' };
              if (n.startsWith('그린')) return { name: '계단실', color: '#047857' };
              if (n === '옐1') return { name: '옐로존', color: '#a16207' };
              if (n === '사앞') return { name: '사무실 앞', color: 'var(--fg-2)' };
              if (/^I\d+$/.test(n)) return { name: '중앙 통로 I열', color: 'var(--fg-2)' };
              // v7.340: A0~G0 은 각 랙 왼쪽에 세워둔 세로 진열대다 (그룹 라벨이 아니다)
              if (/^[A-G]0$/.test(n)) return { name: '세로 진열대', color: 'var(--fg-2)' };
              if (/^[J-Q]\d+$/.test(n)) return { name: '우측 랙', color: 'var(--fg-2)' };
              return { name: '중앙 랙', color: 'var(--fg-2)' };
            };
            const zone = found ? zoneOf(q) : null;
            // v7.341: 2층 — 도면 칸 값이 곧 주문코드다. 같은 코드가 여러 칸에 있을 수 있어
            //   전부 하이라이트한다 (예: 801 은 3곳, 1043 도 3곳).
            const f2Hits = nexiFloor === '2' && q ? f2Find(q) : [];
            const f2Keys = f2Hits.map(([r, c]) => `${r},${c}`);
            return (
            <div>
              <div style={{marginBottom:24}}>
                <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4,display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="pin_drop" size={32} style={{color:'var(--accent)'}}/>
                  넥시 조회
                </h1>
                <p style={{fontSize:16,color:'var(--fg-2)'}}>
                  {nexiFloor==='1'
                    ? '넥시 창고 1층입니다. 위치 코드를 넣거나 도면에서 칸을 누르면 해당 위치가 표시됩니다.'
                    : '넥시 창고 2층입니다. 1층 [그린1] 계단으로 올라옵니다. 칸에 적힌 숫자가 주문코드입니다.'}
                </p>
              </div>

              {/* v7.341: 층 전환 — 층을 바꾸면 검색 대상이 달라지므로 입력값도 비운다 */}
              <div style={{display:'inline-flex',gap:4,padding:4,background:'var(--bg-subtle)',border:'1px solid var(--border)',borderRadius:12,marginBottom:16}}>
                {[['1','1층','도면 · 위치 코드'],['2','2층','격자 · 주문코드']].map(([id,label,sub])=>(
                  <button key={id} onClick={()=>{setNexiFloor(id);setNexiQuery('');}}
                    style={{padding:'9px 18px',border:'none',borderRadius:9,cursor:'pointer',fontFamily:'inherit',
                      background:nexiFloor===id?'var(--accent)':'transparent',
                      color:nexiFloor===id?'#fff':'var(--fg-2)',
                      fontSize:14,fontWeight:nexiFloor===id?700:600,transition:'all 0.15s',display:'flex',alignItems:'center',gap:7}}>
                    {label}
                    <span style={{fontSize:11,fontWeight:500,opacity:0.75}}>{sub}</span>
                  </button>
                ))}
              </div>

              {/* 검색 카드 */}
              <Card style={{padding:20,marginBottom:16}}>
                <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                  <div style={{position:'relative',flex:1,minWidth:240}}>
                    <Icon name="search" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)',pointerEvents:'none'}}/>
                    <input
                      value={nexiQuery}
                      onChange={e=>setNexiQuery(e.target.value)}
                      placeholder={nexiFloor==='1'
                        ? '위치 코드 입력 — A8 · 블13 · 레9 · I2 · Q3 · 옐1 · 그린2'
                        : '주문코드 입력 — 1265 · 214-4 · 626-1 · 7-1'}
                      style={{width:'100%',boxSizing:'border-box',padding:'13px 14px 13px 42px',border:`2px solid ${(nexiFloor==='1'?found:f2Hits.length>0)?'var(--accent)':'var(--border)'}`,borderRadius:12,fontSize:15,fontFamily:'inherit',outline:'none',background:'var(--bg-card)',transition:'border-color 0.15s'}}/>
                  </div>
                  {q && (
                    <button onClick={()=>setNexiQuery('')}
                      style={{padding:'12px 18px',border:'1px solid var(--border-strong)',borderRadius:12,background:'var(--bg-card)',fontSize:14,fontWeight:600,cursor:'pointer',fontFamily:'inherit',color:'var(--fg-2)'}}>
                      지우기
                    </button>
                  )}
                </div>

                {/* 결과 */}
                {nexiFloor==='1' && q && found && (
                  <div style={{marginTop:14,display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:'var(--bg-accent-soft)',border:'1px solid var(--accent)',borderRadius:12,flexWrap:'wrap'}}>
                    <span style={{fontSize:26,fontWeight:800,color:'var(--accent)',letterSpacing:'-0.01em'}}>{normalizeNexiCode(q)}</span>
                    <span style={{fontSize:14,fontWeight:600,color:zone.color}}>{zone.name}</span>
                    <span style={{fontSize:13,color:'var(--fg-3)'}}>· 1층</span>
                  </div>
                )}
                {/* v7.341: 2층 결과 — 같은 주문코드가 여러 칸에 있으면 전부 알린다 */}
                {nexiFloor==='2' && q && f2Hits.length>0 && (
                  <div style={{marginTop:14,display:'flex',alignItems:'center',gap:14,padding:'14px 16px',background:'var(--bg-accent-soft)',border:'1px solid var(--accent)',borderRadius:12,flexWrap:'wrap'}}>
                    <span style={{fontSize:26,fontWeight:800,color:'var(--accent)',letterSpacing:'-0.01em'}}>{q}</span>
                    <span style={{fontSize:14,fontWeight:600,color:'var(--fg-2)'}}>
                      2층 · 도면에서 <b style={{color:'var(--accent)'}}>{f2Hits.length}곳</b> 표시
                    </span>
                    {f2Hits.length>1 && (
                      <span style={{fontSize:12,color:'var(--warning)',fontWeight:600}}>같은 코드가 여러 자리에 있습니다</span>
                    )}
                  </div>
                )}
                {nexiFloor==='2' && q && f2Hits.length===0 && (
                  <div style={{marginTop:14,padding:'12px 16px',background:'rgba(245,124,0,0.08)',border:'1px solid var(--warning)',borderRadius:12,fontSize:13,fontWeight:600,color:'var(--warning)',display:'flex',alignItems:'center',gap:6}}>
                    <Icon name="warning_amber" size={16}/> 2층 도면에 없는 주문코드입니다
                  </div>
                )}
                {nexiFloor==='1' && q && !found && (
                  <div style={{marginTop:14,padding:'12px 16px',background:'rgba(245,124,0,0.08)',border:'1px solid var(--warning)',borderRadius:12}}>
                    <div style={{fontSize:13,fontWeight:600,color:'var(--warning)',display:'flex',alignItems:'center',gap:6}}>
                      <Icon name="warning_amber" size={16}/> 도면에 없는 코드입니다
                    </div>
                    {suggests.length>0 && (
                      <div style={{marginTop:10,display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                        <span style={{fontSize:12,color:'var(--fg-3)'}}>혹시 이건가요?</span>
                        {suggests.map(c=>(
                          <button key={c} onClick={()=>setNexiQuery(c)}
                            style={{padding:'4px 10px',border:'1px solid var(--border-strong)',borderRadius:999,background:'var(--bg-card)',fontSize:12,fontWeight:700,cursor:'pointer',fontFamily:'inherit',color:'var(--fg-1)'}}>{c}</button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Card>

              {/* 도면 */}
              <Card style={{padding:20}}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14,flexWrap:'wrap',gap:10}}>
                  <div style={{fontSize:15,fontWeight:700,display:'flex',alignItems:'center',gap:8}}>
                    <Icon name="map" size={20} style={{color:'var(--fg-2)'}}/>
                    넥시 창고 {nexiFloor}층 배치도
                    <span style={{fontSize:12,fontWeight:500,color:'var(--fg-3)'}}>
                      {nexiFloor==='1' ? `· 총 ${NEXI_CODES.length}칸` : `· 주문코드 ${F2_CODE_COUNT}종`}
                    </span>
                  </div>
                  {/* 범례 */}
                  <div style={{display:'flex',gap:12,flexWrap:'wrap',fontSize:11.5,color:'var(--fg-2)'}}>
                    {(nexiFloor==='1'
                      ? [['#dbeafe','#93b4e8','블 파렛트'],['#fde2e4','#eab0b6','레 파렛트'],['#d6f5e6','#10b981','그린(계단)'],['#fef9c3','#ca8a04','옐로존']]
                      : [['#1f2937','#1f2937','벽'],['#0f766e','#0f766e','문'],['#fce5cd','#e0a878','방(4·5번)'],['#d9ead3','#7fae7f','승강기'],['#fef08a','#ca8a04','방 표시']]
                    ).map(([bg,bd,label])=>(
                      <span key={label} style={{display:'inline-flex',alignItems:'center',gap:5}}>
                        <span style={{width:12,height:12,borderRadius:3,background:bg,border:`1px solid ${bd}`,display:'inline-block'}}/>{label}
                      </span>
                    ))}
                  </div>
                </div>

                {nexiFloor==='1' ? (
                  <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:14,padding:'14px 10px',overflowX:'auto'}}>
                    {/* v7.347: 표시 크기 75% (좌표는 그대로 — viewBox 는 원본 픽셀 1345×1505).
                        도면이 커서 화면을 다 먹었다. 담는 상자만 줄인다.
                        minWidth 는 720 × 0.75 = 540 으로 같이 줄여야 좁은 화면에서 어긋나지 않는다. */}
                    <div style={{width:'75%',minWidth:540,margin:'0 auto'}}>
                      <NexiMap
                        highlight={found ? q : null}
                        dark={theme==='dark'}
                        onSelect={(code)=>setNexiQuery(code)}
                      />
                    </div>
                  </div>
                ) : (
                  /* v7.341: 2층은 53×53 격자라 폭이 넓다 → 가로 스크롤. 축소하면 숫자를 못 읽는다. */
                  <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:14,padding:12,overflow:'auto',maxHeight:'72vh'}}>
                    <NexiFloor2
                      hits={f2Keys}
                      dark={theme==='dark'}
                      onSelect={(v)=>setNexiQuery(v)}
                    />
                  </div>
                )}

                <div style={{marginTop:12,fontSize:12,color:'var(--fg-3)',lineHeight:1.6}}>
                  {nexiFloor==='1' ? (
                    <>ⓘ 다음 단계에서 <b style={{color:'var(--fg-2)'}}>주문코드 → 위치</b> 데이터를 붙이면 주문코드만 넣어도 이 도면이 바로 표시됩니다.
                    지금은 도면과 하이라이트만 구현된 상태입니다.</>
                  ) : (
                    <>ⓘ 이 배치도는 구글시트 <b style={{color:'var(--fg-2)'}}>[2층 찾기]</b> 탭에서 기계적으로 추출한 것입니다 (2026-08-11 기준).
                    칸을 눌러도 검색창에 채워집니다. 자리가 바뀌면 시트를 다시 내보내 갱신해야 합니다.</>
                  )}
                </div>
              </Card>
            </div>
            );
          })()}

          {/* ═══ STOCK AUDIT (재고 실사) ═══ */}
          {page==='audit'&&(()=>{
            const summary = {
              total: auditResults.length,
              match: auditResults.filter(r=>r.kind==='match').length,
              short: auditResults.filter(r=>r.kind==='short').length,
              over: auditResults.filter(r=>r.kind==='over').length,
              only_excel: auditResults.filter(r=>r.kind==='only_excel').length,
            };
            const filtered = auditFilter === 'all' ? auditResults : auditResults.filter(r => r.kind === auditFilter);
            const kindMeta = {
              match: { label:'일치', color:'var(--success)', bg:'rgba(0,152,68,0.08)' },
              short: { label:'2층 부족', color:'var(--danger)', bg:'rgba(186,26,26,0.08)' },
              over: { label:'2층 과잉', color:'var(--warning)', bg:'rgba(255,152,0,0.1)' },
              only_excel: { label:'장부에만', color:'#1565c0', bg:'rgba(21,101,192,0.08)' },
            };
            return (<div>
              <AdminTabs page={page} onGo={(id)=>{setPage(id);setPg(0);setSearch('');setTxSubTab('list');setAdminOpen(true);}}/>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:28}}>
                <div>
                  <h1 style={{fontSize:32,fontWeight:600,letterSpacing:'-0.01em',marginBottom:4}}>재고 실사</h1>
                  <p style={{fontSize:16,color:'var(--fg-2)'}}>장부 본사와 2층 재고의 차이를 비교 분석합니다. (QNAP 카테고리만 비교)</p>
                </div>
                {auditResults.length>0 && (
                  <button onClick={exportAuditCsv} style={{display:'flex',alignItems:'center',gap:6,padding:'10px 18px',background:'var(--bg-card)',border:'1px solid var(--accent)',borderRadius:10,cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--accent)'}}>
                    <Icon name="download" size={16}/> CSV 내보내기
                  </button>
                )}
              </div>

              {/* 파일 업로드 영역 */}
              <Card style={{padding:24,marginBottom:20}}>
                <h3 style={{fontSize:16,fontWeight:600,marginBottom:14,display:'flex',alignItems:'center',gap:8}}>
                  <Icon name="upload_file" size={20} style={{color:'var(--accent)'}}/> 장부 엑셀 파일 업로드
                </h3>
                <div style={{display:'flex',gap:12,alignItems:'center',flexWrap:'wrap'}}>
                  <label htmlFor="audit-file-input" style={{display:'flex',alignItems:'center',gap:8,padding:'12px 20px',background:'var(--bg-accent-soft)',border:'1px dashed var(--accent)',borderRadius:10,cursor:'pointer',fontSize:13,fontWeight:600,color:'var(--accent)'}}>
                    <Icon name="folder_open" size={18}/> 파일 선택
                  </label>
                  <input id="audit-file-input" type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={e=>e.target.files[0]&&handleAuditFile(e.target.files[0])}/>
                  {auditFileName && <span style={{fontSize:13,color:'var(--fg-2)',fontWeight:500}}>📄 {auditFileName}</span>}
                  <button onClick={runAudit} disabled={!auditExcelRows.length||auditLoading} style={{padding:'12px 24px',background:auditExcelRows.length?'#000':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:10,cursor:auditExcelRows.length?'pointer':'default',fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:6}}>
                    <Icon name="compare_arrows" size={16}/> 비교 실행
                  </button>
                  {auditExcelRows.length>0 && <span style={{fontSize:12,color:'var(--fg-3)'}}>장부 {auditExcelRows.length}개 모델 로드됨</span>}
                </div>
                {auditMsg && <div style={{marginTop:14,padding:12,borderRadius:10,fontSize:13,fontWeight:500,display:'flex',alignItems:'center',gap:8,background:auditMsg.type==='ok'?'rgba(0,152,68,0.08)':'rgba(186,26,26,0.08)',color:auditMsg.type==='ok'?'var(--success)':'var(--danger)'}}>
                  <Icon name={auditMsg.type==='ok'?'check_circle':'error'} size={18}/>{auditMsg.msg}
                </div>}
              </Card>

              {/* 결과 영역 */}
              {auditResults.length > 0 && (<>
                {/* 요약 카드 */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))',gap:12,marginBottom:20}}>
                  {[
                    { key:'all', label:'전체', count:summary.total, color:'var(--fg-2)', bg:'#fff' },
                    { key:'match', label:'일치', count:summary.match, color:'var(--success)', bg:'rgba(0,152,68,0.08)' },
                    { key:'short', label:'2층 부족', count:summary.short, color:'var(--danger)', bg:'rgba(186,26,26,0.08)' },
                    { key:'over', label:'2층 과잉', count:summary.over, color:'var(--warning)', bg:'rgba(255,152,0,0.1)' },
                    { key:'only_excel', label:'장부에만', count:summary.only_excel, color:'#1565c0', bg:'rgba(21,101,192,0.08)' },
                  ].map(c => (
                    <button key={c.key} onClick={()=>setAuditFilter(c.key)} style={{padding:'16px 18px',background:c.bg,border:auditFilter===c.key?`2px solid ${c.color}`:'1px solid var(--border)',borderRadius:14,cursor:'pointer',textAlign:'left',transition:'all 0.15s'}}>
                      <div style={{fontSize:12,color:'var(--fg-3)',marginBottom:4,fontWeight:500}}>{c.label}</div>
                      <div style={{fontSize:24,fontWeight:700,color:c.color}}>{c.count}</div>
                    </button>
                  ))}
                </div>

                {/* 결과 테이블 */}
                <Card style={{overflow:'hidden',maxWidth:680}}>
                  <div style={{overflowX:'auto'}}>
                    <div style={{display:'grid',gridTemplateColumns:'260px 90px 100px 100px 100px',minWidth:650,borderBottom:'2px solid var(--border)',background:'var(--bg-subtle)'}}>
                      <div style={{padding:'14px 18px',fontSize:13,fontWeight:600,color:'var(--fg-2)'}}>모델명</div>
                      <div style={{padding:'14px 14px',fontSize:13,fontWeight:600,color:'var(--fg-2)'}}>구분</div>
                      <div style={{padding:'14px 14px',fontSize:13,fontWeight:600,color:'var(--fg-2)',textAlign:'right'}}>장부 본사</div>
                      <div style={{padding:'14px 14px',fontSize:13,fontWeight:600,color:'var(--fg-2)',textAlign:'right'}}>2층 재고</div>
                      <div style={{padding:'14px 14px',fontSize:13,fontWeight:700,color:'var(--accent)',textAlign:'right',background:'rgba(0,102,138,0.05)'}}>차이</div>
                    </div>
                    <div style={{maxHeight:600,overflowY:'auto'}}>
                      {filtered.map((r) => {
                        const meta = kindMeta[r.kind];
                        const diffStyle = {
                          color: r.hqDiff === 0 ? 'var(--fg-2)' : r.hqDiff > 0 ? 'var(--danger)' : 'var(--warning)',
                          fontWeight: r.hqDiff === 0 ? 500 : 700,
                        };
                        return (
                          <div key={r.model} style={{display:'grid',gridTemplateColumns:'260px 90px 100px 100px 100px',minWidth:650,borderBottom:'1px solid var(--border)',transition:'background 0.15s'}} onMouseEnter={e=>e.currentTarget.style.background='#F1F5F9'} onMouseLeave={e=>e.currentTarget.style.background=''}>
                            <div style={{padding:'14px 18px',fontSize:13,fontWeight:600,fontFamily:'monospace'}}>{r.model}</div>
                            <div style={{padding:'14px 14px'}}>
                              <span style={{padding:'3px 9px',borderRadius:6,fontSize:11,fontWeight:600,background:meta.bg,color:meta.color}}>{meta.label}</span>
                            </div>
                            <div style={{padding:'14px 14px',fontSize:14,textAlign:'right',color:r.excelHq===null?'var(--fg-disabled)':'var(--fg-2)',fontWeight:500}}>{r.excelHq ?? '-'}</div>
                            <div style={{padding:'14px 14px',fontSize:14,textAlign:'right',color:'var(--fg-2)',fontWeight:500}}>{r.sysHq}</div>
                            <div style={{padding:'14px 14px',fontSize:15,textAlign:'right',background:'rgba(0,102,138,0.04)',...diffStyle}}>{r.hqDiff > 0 ? '+' : ''}{r.hqDiff}</div>
                          </div>
                        );
                      })}
                      {filtered.length === 0 && (
                        <div style={{padding:48,textAlign:'center',color:'var(--fg-3)',fontSize:14}}>해당 분류의 모델이 없습니다.</div>
                      )}
                    </div>
                  </div>
                </Card>
                {/* 안내 박스 */}
                <div style={{marginTop:14,padding:14,background:'rgba(0,102,138,0.05)',border:'1px solid rgba(0,102,138,0.15)',borderRadius:10,fontSize:12,color:'var(--fg-2)',lineHeight:1.6,maxWidth:680}}>
                  <strong style={{color:'var(--accent)'}}>💡 차이 해석</strong><br/>
                  <span style={{color:'var(--danger)',fontWeight:600}}>+숫자 (빨강)</span>: 장부보다 2층 재고가 부족 — 실재고 누락 가능성<br/>
                  <span style={{color:'var(--warning)',fontWeight:600}}>-숫자 (주황)</span>: 2층이 더 많음 — 장부 미반영 입고 가능성<br/>
                  <span style={{color:'#1565c0',fontWeight:600}}>장부에만</span>: 장부에는 있으나 시스템 미등록 모델<br/>
                  <span style={{color:'var(--success)',fontWeight:600}}>0 (녹색)</span>: 일치 — 정상
                </div>
              </>)}
            </div>);
          })()}

          </>)}{/* v7.117: 모바일 가드 vs 페이지 렌더 ternary 닫기 */}
        </main>
      </div>

      <EventCalendar open={calendarOpen} onClose={()=>setCalendarOpen(false)}/>

      {/* 배송시트지 기간 선택용 EventCalendar (날짜 클릭 시 그 일/주/월로 점프) */}
      <EventCalendar
        open={txPickerOpen}
        onClose={()=>setTxPickerOpen(false)}
        onPickDate={(d)=>{
          // 날짜를 선택하면 항상 'day' 모드로 — 해당 날짜 하루만 보이게
          setLogPeriodMode('day');
          setLogPeriodOffset(calcDayOffsetFromDate(d));
          setLogPg(0);
        }}
      />

      {/* ═══ 📋 메모 기록 모달 (완료된 메모 — EventCalendar 스타일) ═══ */}
      {memoHistoryOpen && (()=>{
        const MH_CAL_W = 920, MH_DETAIL_W = 340, MH_CELL_H = 110;
        const mhY = memoHistoryMonth.getFullYear();
        const mhM = memoHistoryMonth.getMonth();
        const mhFirstDay = new Date(mhY, mhM, 1).getDay();
        const mhDaysInMonth = new Date(mhY, mhM+1, 0).getDate();
        const mhPad = v => String(v).padStart(2,'0');
        const mhTodayStr = (()=>{ const d=new Date(); return `${d.getFullYear()}-${mhPad(d.getMonth()+1)}-${mhPad(d.getDate())}`; })();
        // 42 cells (6 weeks)
        const mhCells = [];
        const mhPrevLast = new Date(mhY, mhM, 0).getDate();
        for (let i = mhFirstDay-1; i >= 0; i--) mhCells.push({ date: mhPrevLast-i, inMonth: false, full: null });
        for (let i = 1; i <= mhDaysInMonth; i++) mhCells.push({ date: i, inMonth: true, full: `${mhY}-${mhPad(mhM+1)}-${mhPad(i)}` });
        let mhNext = 1; while (mhCells.length < 42) { mhCells.push({ date: mhNext++, inMonth: false, full: null }); }
        // group by date
        const mhByDate = {};
        completedMemos.forEach(m => { const d = m.completed_at ? m.completed_at.slice(0,10) : null; if (d) { if (!mhByDate[d]) mhByDate[d] = []; mhByDate[d].push(m); } });
        const mhPrev = () => { const nm = new Date(mhY, mhM-1, 1); setMemoHistoryMonth(nm); setMemoHistorySelectedDate(null); loadCompletedMemos(nm); };
        const mhNextM = () => { const nm = new Date(mhY, mhM+1, 1); setMemoHistoryMonth(nm); setMemoHistorySelectedDate(null); loadCompletedMemos(nm); };
        const mhGoToday = () => { const d=new Date(); const nm = new Date(d.getFullYear(), d.getMonth(), 1); setMemoHistoryMonth(nm); setMemoHistorySelectedDate(null); loadCompletedMemos(nm); };
        const mhSelMemos = memoHistorySelectedDate ? (mhByDate[memoHistorySelectedDate] || []) : [];
        const mhModalW = memoHistorySelectedDate ? (MH_CAL_W + MH_DETAIL_W + 32) : MH_CAL_W + 32;
        const mhTotal = completedMemos.length;
        return (
          <div onClick={()=>{setMemoHistoryOpen(false);setMemoHistorySelectedDate(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:mhModalW,maxWidth:'95vw',maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',transition:'width 0.2s ease',overflow:'hidden'}}>
              {/* Header */}
              <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="history" size={24} style={{color:'var(--accent)'}}/>
                  <h2 style={{fontSize:20,fontWeight:600,margin:0,color:'var(--fg-1)'}}>메모 기록</h2>
                  <span style={{fontSize:12,color:'var(--fg-3)',marginLeft:4}}>· {mhTotal}건</span>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <button onClick={mhGoToday} style={{padding:'6px 14px',border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-card)',fontSize:13,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}>오늘</button>
                  <button onClick={mhPrev} style={{width:32,height:32,border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-card)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}><Icon name="chevron_left" size={18}/></button>
                  <span style={{padding:'6px 14px',minWidth:130,textAlign:'center',fontSize:16,fontWeight:600,color:'var(--fg-1)'}}>{mhY}년 {mhM+1}월</span>
                  <button onClick={mhNextM} style={{width:32,height:32,border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-card)',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',padding:0}}><Icon name="chevron_right" size={18}/></button>
                  <button onClick={()=>{setMemoHistoryOpen(false);setMemoHistorySelectedDate(null);}} style={{width:32,height:32,border:'none',background:'transparent',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',marginLeft:8}}><Icon name="close" size={20}/></button>
                </div>
              </div>
              {/* v7.450: 검색 트리거 — 년/월 선택 UI 아래 */}
              <div style={{padding:'8px 24px',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',flexShrink:0}}>
                <button onClick={()=>{ setMemoSearchQuery(''); setMemoSearchResults([]); setMemoSearched(false); setMemoSearchOpen(true); }}
                  title="메모 내용·작성자 검색"
                  style={{display:'inline-flex',alignItems:'center',gap:6,padding:'6px 14px',border:'1px solid var(--border-strong)',borderRadius:8,background:'var(--bg-card)',fontSize:13,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}>
                  <Icon name="search" size={16}/> 검색
                </button>
              </div>
              {/* Body — Calendar + Detail */}
              <div style={{padding:16,flex:1,display:'flex',gap:16,minHeight:0,overflow:'hidden'}}>
                {/* Calendar grid */}
                <div style={{width:MH_CAL_W,flexShrink:0,display:'flex',flexDirection:'column'}}>
                  {/* Day names */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',marginBottom:6}}>
                    {['일','월','화','수','목','금','토'].map((dn,i)=>(
                      <div key={dn} style={{padding:'8px 10px',fontSize:12,fontWeight:700,color:i===0?'var(--danger)':i===6?'var(--accent)':'var(--fg-2)',textAlign:'left',letterSpacing:'0.04em'}}>{dn}</div>
                    ))}
                  </div>
                  {/* 6-week grid */}
                  <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gridTemplateRows:`repeat(6,${MH_CELL_H}px)`,gap:4,border:'1px solid var(--border)',borderRadius:8,padding:4,background:'var(--bg-subtle)'}}>
                    {mhCells.map((cell,ci)=>{
                      const dayMemos = cell.full ? (mhByDate[cell.full] || []) : [];
                      const isToday = cell.full === mhTodayStr;
                      const isSel = cell.full === memoHistorySelectedDate;
                      const dow = ci % 7;
                      return (
                        <div key={ci} onClick={()=>{ if(cell.inMonth && cell.full) setMemoHistorySelectedDate(s=>s===cell.full?null:cell.full); }}
                          style={{height:MH_CELL_H,padding:6,background:isSel?'var(--bg-info)':isToday?'rgba(0,102,138,0.06)':cell.inMonth?'var(--bg-card)':'rgba(0,0,0,0.02)',borderRadius:6,cursor:cell.inMonth?'pointer':'default',transition:'background 0.15s',opacity:cell.inMonth?1:0.45,border:isSel?'1.5px solid var(--accent)':isToday?'1px solid var(--accent)':'1px solid transparent',overflow:'hidden',display:'flex',flexDirection:'column',boxSizing:'border-box'}}>
                          <div style={{fontSize:13,fontWeight:isToday?700:500,color:dow===0?'var(--danger)':dow===6?'var(--accent)':'var(--fg-2)',marginBottom:4,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                            <span>{cell.date}</span>
                            {dayMemos.length>0 && cell.inMonth && <span style={{fontSize:9,fontWeight:600,color:'var(--fg-3)',background:'var(--bg-card)',padding:'0 4px',borderRadius:3,border:'1px solid var(--border)'}}>{dayMemos.length}</span>}
                          </div>
                          <div style={{display:'flex',flexDirection:'column',gap:2,flex:1,minHeight:0,overflow:'hidden'}}>
                            {dayMemos.slice(0,3).map((m,j)=>{
                              const mc = memoColors[m.color] || memoColors.yellow;
                              // 서식 마크업 제거한 일반 텍스트
                              // v7.186: `.+?` → `[\s\S]+?` — 개행 걸친 색상 태그도 정상 제거
                              const plainText = (m.content || '').replace(/\*\*([^*]+)\*\*/g, '$1').replace(/\[#[0-9a-fA-F]{3,8}\]([\s\S]+?)\[\/\]/g, '$1');
                              return (
                                <div key={j} title={plainText} style={{display:'flex',alignItems:'center',gap:4,padding:'1px 5px',background:mc.bg,borderLeft:`3px solid ${mc.accent}`,borderRadius:3,fontSize:10,lineHeight:1.3,overflow:'hidden',flexShrink:0}}>
                                  <span style={{color:mc.accent,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1,minWidth:0,fontWeight:600}}>{plainText?(plainText.length>18?plainText.slice(0,18)+'…':plainText):'(빈 메모)'}</span>
                                </div>
                              );
                            })}
                            {dayMemos.length>3 && <div style={{fontSize:10,color:'var(--accent)',padding:'1px 5px',fontWeight:600,flexShrink:0}}>+{dayMemos.length-3} 더보기</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {/* Detail panel */}
                {memoHistorySelectedDate && (
                  <div style={{width:MH_DETAIL_W,flexShrink:0,border:'1px solid var(--border)',borderRadius:8,padding:14,overflowY:'auto',background:'var(--bg-subtle)'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                      <div style={{fontSize:15,fontWeight:700,color:'var(--accent)'}}>{memoHistorySelectedDate.replace(/-/g,'.')}</div>
                      <button onClick={()=>setMemoHistorySelectedDate(null)} style={{width:24,height:24,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',padding:0}}><Icon name="close" size={16}/></button>
                    </div>
                    {mhSelMemos.length === 0 ? (
                      <div style={{textAlign:'center',padding:36,color:'var(--fg-3)',fontSize:13}}>이 날짜에 완료된 메모가 없습니다.</div>
                    ) : (
                      <div style={{display:'flex',flexDirection:'column',gap:8}}>
                        <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:2}}>총 {mhSelMemos.length}건</div>
                        {mhSelMemos.map(m => {
                          const mc = memoColors[m.color] || memoColors.yellow;
                          return (
                            // v7.416: 카드 클릭 → 상세 모달(내용+첨부+다운로드). 복원 버튼은 stopPropagation
                            <div key={m.id} onClick={()=>setMemoHistoryDetail(m)} title="클릭하여 상세 보기" style={{padding:10,background:'var(--bg-card)',borderLeft:`4px solid ${mc.accent}`,borderRadius:6,boxShadow:'0 1px 2px rgba(0,0,0,0.04)',cursor:'pointer'}}>
                              <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                                <Icon name="task_alt" size={16} style={{color:'var(--success)',flexShrink:0}}/>
                                <span style={{fontSize:11,fontWeight:700,color:mc.accent,background:mc.bg,padding:'2px 8px',borderRadius:4}}>{mc.label}</span>
                                {memoImages(m).length > 0 && <span style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',display:'inline-flex',alignItems:'center',gap:3}}><Icon name="attach_file" size={12}/>{memoImages(m).length}</span>}
                              </div>
                              <div style={{fontSize:13,lineHeight:1.6,color:'var(--fg-1)',whiteSpace:'pre-wrap',wordBreak:'break-word',marginBottom:6}}>{m.content ? renderMemoContent(m.content, '#0b1c30') : '(빈 메모)'}</div>
                              <div style={{display:'flex',gap:12,fontSize:10,color:'var(--fg-3)',fontWeight:600,alignItems:'center'}}>
                                <span>{m.author||'익명'}</span>
                                <span>{m.completed_at ? new Date(m.completed_at).toLocaleTimeString('ko-KR',{hour:'2-digit',minute:'2-digit'}) : '-'}</span>
                                <button onClick={(e)=>{e.stopPropagation(); restoreMemo(m);}} title="메모장으로 복원"
                                  style={{marginLeft:'auto',display:'inline-flex',alignItems:'center',gap:4,padding:'3px 10px',border:'1px solid var(--border-strong)',borderRadius:6,background:'var(--bg-card)',color:'var(--accent)',fontSize:10,fontWeight:700,cursor:'pointer'}}
                                  onMouseEnter={e=>{e.currentTarget.style.background='var(--bg-info)';e.currentTarget.style.borderColor='var(--accent)';}}
                                  onMouseLeave={e=>{e.currentTarget.style.background='var(--bg-card)';e.currentTarget.style.borderColor='var(--border-strong)';}}>
                                  <Icon name="restore" size={13}/> 복원
                                </button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
              {/* Footer */}
              <div style={{padding:'8px 24px',borderTop:'1px solid var(--border)',display:'flex',gap:16,alignItems:'center',fontSize:11,color:'var(--fg-3)',flexWrap:'wrap',background:'var(--bg-subtle)',flexShrink:0}}>
                <span style={{display:'inline-flex',alignItems:'center',gap:4}}><Icon name="touch_app" size={13} style={{color:'var(--fg-3)'}}/>날짜 클릭 → 상세 보기</span>
                <span style={{display:'inline-flex',alignItems:'center',gap:4}}><Icon name="keyboard" size={13} style={{color:'var(--fg-3)'}}/>ESC로 닫기</span>
                {completedMemosLoading && <span style={{marginLeft:'auto',color:'var(--accent)',display:'inline-flex',alignItems:'center',gap:4,fontWeight:600}}><Icon name="progress_activity" size={12} style={{animation:'spin 1s linear infinite'}}/>로딩 중...</span>}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 📄 v7.416: 기록된 메모 상세 모달 — 내용 + 첨부 + 함께 다운로드 ═══ */}
      {/* v7.450: 메모 검색 모달 — 결과 클릭 시 기존 상세 모달(memoHistoryDetail) 재사용 */}
      {memoSearchOpen && (
        <div onClick={()=>setMemoSearchOpen(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1100,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:14,width:520,maxWidth:'92vw',maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
            <div style={{padding:'14px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
              <Icon name="search" size={20} style={{color:'var(--accent)'}}/>
              <input autoFocus value={memoSearchQuery}
                onChange={e=>setMemoSearchQuery(e.target.value)}
                onKeyDown={e=>{ if(e.key==='Enter'){e.preventDefault(); runMemoSearch();} }}
                placeholder="메모 내용·작성자로 검색"
                style={{flex:1,padding:'8px 10px',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:14,outline:'none',background:'var(--bg-card)',color:'var(--fg-1)'}}/>
              <button onClick={runMemoSearch} style={{padding:'8px 14px',border:'none',borderRadius:8,background:'var(--accent)',color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',flexShrink:0}}>검색</button>
              <button onClick={()=>setMemoSearchOpen(false)} style={{width:32,height:32,border:'none',background:'transparent',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',flexShrink:0}}><Icon name="close" size={18}/></button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:12,minHeight:130}}>
              {memoSearchLoading ? (
                <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}>검색 중…</div>
              ) : !memoSearched ? (
                <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}>검색어를 입력하고 Enter 또는 [검색]을 누르세요.</div>
              ) : memoSearchResults.length === 0 ? (
                <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}>결과가 없습니다.</div>
              ) : (
                <div style={{display:'flex',flexDirection:'column',gap:8}}>
                  <div style={{fontSize:11,color:'var(--fg-3)',padding:'0 4px 2px'}}>{memoSearchResults.length}건</div>
                  {memoSearchResults.map(m=>{
                    const mc = memoColors[m.color] || memoColors.yellow;
                    const plain = (m.content||'').replace(/\*\*([^*]+)\*\*/g,'$1').replace(/\[#[0-9a-fA-F]{3,8}\]([\s\S]+?)\[\/\]/g,'$1');
                    const dateStr = m.completed_at ? m.completed_at.slice(0,10) : '';
                    return (
                      <div key={m.id} onClick={()=>setMemoHistoryDetail(m)} title="클릭하여 상세 보기"
                        style={{padding:10,background:'var(--bg-card)',borderLeft:`4px solid ${mc.accent}`,border:'1px solid var(--border)',borderRadius:6,cursor:'pointer'}}>
                        <div style={{fontSize:13,color:'var(--fg-1)',lineHeight:1.5,display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden'}}>{plain ? highlightMatch(plain, memoSearchQuery) : '(빈 메모)'}</div>
                        <div style={{fontSize:11,color:'var(--fg-3)',marginTop:5,display:'flex',gap:8}}>
                          <span>{dateStr}</span>{m.author && <span>· {highlightMatch(m.author, memoSearchQuery)}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      {memoHistoryDetail && (()=>{
        const m = memoHistoryDetail;
        const mc = memoColors[m.color] || memoColors.yellow;
        const atts = memoImages(m);
        const when = m.completed_at ? new Date(m.completed_at).toLocaleString('ko-KR',{year:'numeric',month:'2-digit',day:'2-digit',hour:'2-digit',minute:'2-digit'}) : '-';
        return (
          <div onClick={()=>setMemoHistoryDetail(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',zIndex:1200,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:'var(--bg-card)',borderRadius:14,width:'min(560px, 96vw)',maxHeight:'88vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 50px rgba(0,0,0,0.3)',overflow:'hidden'}}>
              {/* 헤더 */}
              <div style={{display:'flex',alignItems:'center',gap:10,padding:'14px 18px',borderBottom:`1px solid var(--border)`,background:mc.bg}}>
                <Icon name="task_alt" size={18} style={{color:mc.accent}}/>
                <span style={{fontSize:12,fontWeight:800,color:mc.accent,background:'rgba(255,255,255,0.6)',padding:'2px 8px',borderRadius:5}}>{mc.label}</span>
                <div style={{marginLeft:2,fontSize:12,color:mc.accent,fontWeight:600}}>{m.author||'익명'} · {when}</div>
                <button onClick={()=>setMemoHistoryDetail(null)} style={{marginLeft:'auto',width:28,height:28,border:'none',background:'transparent',borderRadius:7,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:mc.accent}}><Icon name="close" size={18}/></button>
              </div>
              {/* 본문 */}
              <div style={{padding:'16px 18px',overflowY:'auto',flex:1}}>
                <div style={{fontSize:14,lineHeight:1.7,color:'var(--fg-1)',whiteSpace:'pre-wrap',wordBreak:'break-word'}}>{m.content ? renderMemoContent(m.content, '#0b1c30') : '(빈 메모)'}</div>
                {atts.length > 0 && (
                  <div style={{marginTop:16,paddingTop:14,borderTop:'1px dashed var(--border)'}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:8,display:'flex',alignItems:'center',gap:5}}><Icon name="attach_file" size={13}/>첨부 {atts.length}개</div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                      {atts.map(att => {
                        const url = AttachmentService.getPublicUrl(att.path);
                        const isImg = /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(att.name || att.path || '');
                        if (isImg) {
                          return (
                            <a key={att.path} href={url} download={att.name} title={att.name} onClick={(e)=>{e.preventDefault();downloadKeepingName(url,att.name);}} style={{display:'block',width:72,height:72,borderRadius:8,overflow:'hidden',border:'1px solid var(--border)'}}>
                              <img src={url} alt={att.name} style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                            </a>
                          );
                        }
                        return (
                          <a key={att.path} href={url} download={att.name} title={att.name}
                            onClick={(e)=>{e.preventDefault();downloadKeepingName(url,att.name);}}
                            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 10px',background:'rgba(0,0,0,0.05)',border:'1px solid var(--border)',borderRadius:8,textDecoration:'none',color:'var(--fg-1)',fontSize:12,fontWeight:600,maxWidth:200}}>
                            <Icon name="folder_zip" size={16} style={{color:'var(--accent)',flexShrink:0}}/>
                            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{att.name}</span>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              {/* 푸터 — 다운로드 / 복원 */}
              <div style={{display:'flex',gap:10,padding:'12px 18px',borderTop:`1px solid var(--border)`,background:'var(--bg-subtle)'}}>
                <button onClick={()=>downloadMemoBundle(m)}
                  style={{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',background:'var(--accent-solid)',color:'#fff',border:'none',borderRadius:9,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  <Icon name="download" size={16}/> 내용 + 첨부 다운로드{atts.length>0?` (${atts.length+1}개)`:''}
                </button>
                <button onClick={()=>{ restoreMemo(m); setMemoHistoryDetail(null); }}
                  style={{display:'flex',alignItems:'center',gap:6,padding:'10px 16px',background:'var(--bg-card)',color:'var(--accent)',border:'1px solid var(--border-strong)',borderRadius:9,fontSize:13,fontWeight:700,cursor:'pointer'}}>
                  <Icon name="restore" size={16}/> 메모장으로 복원
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 🔍 모델명 변경 검색 모달 ═══ */}
      {modelSearchModal && (() => {
        const doSearch = async (q) => {
          setModelSearchQuery(q);
          if (q.trim().length < 2) { setModelSearchResults([]); return; }
          setModelSearchLoading(true);
          const data = await InventoryService.searchProductsForRename(q.trim());
          const grouped = {};
          data.forEach(r => {
            if (!grouped[r.product_name]) grouped[r.product_name] = { model: r.product_name, category: r.category, in_stock: 0, total: 0 };
            grouped[r.product_name].total++;
            if (r.status === 'in_stock') grouped[r.product_name].in_stock++;
          });
          setModelSearchResults(Object.values(grouped).slice(0, 20));
          setModelSearchLoading(false);
        };
        const applyModelChange = async (newName, opts = {}) => {
          if (modelSearchModal.mode === 'add') {
            // 추가 모드
            if (!newName) { setModelSearchModal(null); return; }
            // v7.308: 카탈로그(products)에 없는 제품은 자동으로 외부 처리 —
            //   EXT- 임시 시리얼이 붙어 시리얼 스캔 없이 출고까지 진행된다.
            //   기존에는 [외부 처리로 추가] 체크박스를 사용자가 직접 켜야 했다.
            //   ★ v7.74 원칙은 유지한다: '카탈로그에 있는데 재고만 0' 인 제품은
            //     진짜 시리얼을 받아야 하므로 외부로 만들지 않는다.
            //   opts.manual 일 때만 판정한다 — 검색 결과에서 고른 제품은
            //   재고 행이 실제로 있는 제품이므로 외부가 아니다.
            let asExternal = false;
            if (opts.manual) {
              try {
                const existing = await ProductService.existingNames([newName]);
                asExternal = !existing.has(newName);
              } catch (e) {
                // 조회 실패 시 외부로 단정하지 않는다 (가짜 EXT- 시리얼이 기록되는 쪽이 더 위험).
                // 필요하면 출고 처리 화면의 외부 자동 부여로 수동 보정할 수 있다.
                console.error('카탈로그 조회 실패 — 외부 처리 판단 보류:', e);
              }
            }
            await addItemToShipment(modelSearchModal.item, newName, 1, asExternal);
          } else {
            // 변경 모드
            if (!newName || newName === modelSearchModal.oldName) { setModelSearchModal(null); return; }
            // v7.437: 추가 모드와 동일하게 external 재판정 — 미등록→등록 교체 시 EXT 임시시리얼이 남던 버그 fix.
            //   검색결과에서 고른 제품(=재고 행 존재)은 등록(비외부). 직접 입력(manual)은 카탈로그 조회로 판정.
            let asExternal = false;
            if (opts.manual) {
              try {
                const existing = await ProductService.existingNames([newName]);
                asExternal = !existing.has(newName);
              } catch (e) {
                // 조회 실패 시 외부로 단정하지 않는다 (등록 제품에 EXT 붙는 쪽이 더 위험).
                console.error('카탈로그 조회 실패 — 외부 처리 판단 보류:', e);
                asExternal = false;
              }
            }
            await saveItemProductName(modelSearchModal.item, modelSearchModal.oldName, newName, asExternal);
          }
          setModelSearchModal(null);
          setModelSearchQuery('');
          setModelSearchResults([]);
        };
        return (
          <div onClick={()=>{setModelSearchModal(null);}} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:600,maxHeight:'80vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
              <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#f0f6ff'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Icon name={modelSearchModal.mode==='add'?'add_box':'search'} size={20} style={{color:'var(--accent)'}}/>
                  <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>{modelSearchModal.mode==='add'?'품목 추가':'제품 검색'}</h3>
                  {modelSearchModal.mode!=='add' && <span style={{fontSize:12,color:'var(--fg-3)'}}>현재: <b style={{color:'var(--fg-1)'}}>{modelSearchModal.oldName}</b></span>}
                </div>
                <button onClick={()=>{setModelSearchModal(null);}} style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)'}}>
                  <Icon name="close" size={18}/>
                </button>
              </div>
              <div style={{padding:16,borderBottom:'1px solid var(--border)'}}>
                <div style={{position:'relative'}}>
                  <Icon name="search" size={18} style={{position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)'}}/>
                  <input autoFocus value={modelSearchQuery} onChange={e=>doSearch(e.target.value)}
                    onKeyDown={e=>{
                      if (e.key !== 'Enter') return;
                      e.preventDefault();
                      const q = modelSearchQuery.trim();
                      if (!q) return;
                      // 정확히 같은 이름이 검색돼 있으면 그 제품을, 아니면 입력값 그대로
                      const exact = modelSearchResults.find(r => (r.model||'').toLowerCase() === q.toLowerCase());
                      if (exact) applyModelChange(exact.model);
                      else applyModelChange(q, { manual: true });
                    }}
                    placeholder={modelSearchModal.mode==='add'?'제품명 입력 — 목록에서 고르거나, 없으면 Enter 로 그대로 추가':'제품명 입력 — 목록에서 고르거나, 없으면 Enter 로 그대로 변경'} style={{width:'100%',padding:'10px 12px 10px 38px',border:'1px solid var(--border-strong)',borderRadius:10,fontSize:14,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}/>
                </div>
                {/* v7.308: [외부 처리로 추가] 체크박스 제거 — 카탈로그 조회로 자동 판정한다 */}
                {/* v7.307: DB에 없는 제품을 이 자리에서 바로 추가한다.
                    기존에는 [수동 입력] → 브라우저 prompt() 창이 따로 떠서, 모달을 띄워 놓고
                    또 다른 창에 이름을 치는 2단계였다. 입력값을 그대로 쓸 수 있으므로 창이 필요 없다. */}
                {(() => {
                  const q = modelSearchQuery.trim();
                  const isAdd = modelSearchModal.mode === 'add';
                  if (!q) {
                    return (
                      <div style={{marginTop:8,fontSize:11,color:'var(--fg-3)'}}>
                        검색 결과에서 선택하면 즉시 적용됩니다. DB에 없는 제품은 이름을 입력한 뒤 아래 버튼(또는 Enter)으로 바로 {isAdd?'추가':'변경'}할 수 있습니다.
                        {isAdd && ' 카탈로그에 없는 제품은 EXT- 임시 시리얼이 자동으로 붙습니다.'}
                      </div>
                    );
                  }
                  const exact = modelSearchResults.some(r => (r.model||'').toLowerCase() === q.toLowerCase());
                  const same = !isAdd && q === modelSearchModal.oldName;
                  if (exact || same) return null;
                  return (
                    <button onClick={()=>applyModelChange(q, { manual: true })}
                      style={{marginTop:10,width:'100%',display:'flex',alignItems:'center',gap:8,padding:'10px 12px',border:'1.5px dashed var(--accent)',borderRadius:10,background:'var(--bg-accent-soft)',cursor:'pointer',textAlign:'left'}}>
                      <Icon name="add_circle" size={18} style={{color:'var(--accent)',flexShrink:0}}/>
                      <span style={{fontSize:13,fontWeight:700,color:'#004d6a',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        &ldquo;{q}&rdquo; 그대로 {isAdd?'추가':'변경'}
                      </span>
                      <span style={{fontSize:10.5,color:'var(--fg-3)',fontWeight:600,marginLeft:'auto',flexShrink:0}}>
                        {isAdd ? '카탈로그에 없으면 EXT- 자동 부여 · Enter' : 'DB에 없는 제품 · Enter'}
                      </span>
                    </button>
                  );
                })()}
              </div>
              <div style={{flex:1,overflowY:'auto',padding:16}}>
                {modelSearchLoading && <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}><Icon name="progress_activity" size={20} style={{animation:'spin 1s linear infinite',color:'var(--accent)'}}/></div>}
                {!modelSearchLoading && modelSearchQuery.trim().length >= 2 && modelSearchResults.length === 0 && (
                  <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}>
                    검색 결과가 없습니다
                    <div style={{marginTop:6,fontSize:11.5,color:'#bbb'}}>위의 &ldquo;그대로 {modelSearchModal.mode==='add'?'추가':'변경'}&rdquo; 버튼 또는 Enter 로 진행하세요</div>
                  </div>
                )}
                {!modelSearchLoading && modelSearchQuery.trim().length < 2 && (
                  <div style={{padding:30,textAlign:'center',color:'#bbb',fontSize:13}}>2자 이상 입력하세요</div>
                )}
                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                  {modelSearchResults.map(r => (
                    <button key={r.model} onClick={()=>applyModelChange(r.model)} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'10px 14px',border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-card)',cursor:'pointer',transition:'all 0.12s',textAlign:'left'}}
                      onMouseEnter={e=>{e.currentTarget.style.background='#f0f6ff';e.currentTarget.style.borderColor='var(--accent)';}}
                      onMouseLeave={e=>{e.currentTarget.style.background='var(--bg-card)';e.currentTarget.style.borderColor='var(--border)';}}>
                      <div style={{display:'flex',flexDirection:'column',gap:2,minWidth:0,flex:1}}>
                        <span style={{fontSize:13,fontWeight:700,color:'var(--fg-1)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.model}</span>
                        <span style={{fontSize:11,color:'var(--fg-3)'}}>{r.category || '-'}</span>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:6,flexShrink:0,marginLeft:10}}>
                        <span style={{fontSize:11,color:'var(--success)',fontWeight:700}}>재고 {r.in_stock}</span>
                        <span style={{fontSize:10,color:'#bbb'}}>/ 전체 {r.total}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 📥 v7.14 임시 시리얼 일괄 입고 모달 (HDD/SSD 등) ═══ */}
      {bulkUnknownModal && (() => {
        const m = bulkUnknownModal;
        const preview = buildBulkUnknownSerials(m);
        const previewShow = preview.slice(0, 5);
        const previewMore = preview.length - previewShow.length;
        return (
          <div onClick={()=>!m.loading && setBulkUnknownModal(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:620,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'92vh'}}>
              {/* Header */}
              <div style={{padding:'16px 22px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#fff3e0'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="inventory_2" size={22} style={{color:'var(--warning)'}}/>
                  <div>
                    <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>임시 시리얼 일괄 입고</h3>
                    <p style={{fontSize:11,color:'var(--fg-3)',margin:'2px 0 0 0'}}>HDD/SSD 등 라벨 모를 때 UNK-{`{모델}`}-{`{번호}`} 형식으로 자동 생성</p>
                  </div>
                </div>
                <button onClick={()=>!m.loading && setBulkUnknownModal(null)} disabled={m.loading}
                  style={{width:30,height:30,border:'none',background:'transparent',borderRadius:6,cursor:m.loading?'not-allowed':'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center',opacity:m.loading?0.4:1}}>
                  <Icon name="close" size={18}/>
                </button>
              </div>

              {/* Body */}
              <div style={{padding:'18px 22px',flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:14}}>
                {/* 1) 제품 선택 */}
                <div style={{position:'relative'}}>
                  <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>제품 *</label>
                  {m.product_name ? (
                    <div style={{padding:'10px 12px',border:'2px solid var(--success)',borderRadius:8,background:'rgba(0,152,68,0.06)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                      <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)'}}>{m.product_name}</span>
                      <button onClick={()=>setBulkUnknownModal(s=>s?{...s, product_name:'', product_search:'', model_code:''}:s)}
                        style={{background:'transparent',border:'none',color:'var(--fg-3)',cursor:'pointer',padding:4,display:'flex'}}>
                        <Icon name="close" size={14}/>
                      </button>
                    </div>
                  ) : (
                    <>
                      <input value={m.product_search} onChange={e=>searchBulkUnknownProduct(e.target.value)}
                        placeholder="제품명 검색 (예: IRONWOLF, EXOS, SAMSUNG)" disabled={m.loading}
                        style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',background:m.loading?'#f6f7fb':'var(--bg-card)',boxSizing:'border-box'}}
                        onFocus={e=>e.target.style.borderColor='var(--warning)'}
                        onBlur={e=>e.target.style.borderColor='var(--border)'}/>
                      {m.product_results && m.product_results.length > 0 && (
                        <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,boxShadow:'0 4px 12px rgba(0,0,0,0.08)',marginTop:4,maxHeight:200,overflowY:'auto',zIndex:10}}>
                          {m.product_results.map(p => (
                            <button key={p.id} onMouseDown={(e)=>{e.preventDefault();selectBulkUnknownProduct(p.product_name);}}
                              style={{display:'block',width:'100%',padding:'9px 14px',textAlign:'left',border:'none',background:'transparent',cursor:'pointer',fontFamily:'monospace',fontSize:13,color:'var(--fg-1)',borderBottom:'1px solid var(--border-mute)'}}
                              onMouseEnter={e=>e.currentTarget.style.background='rgba(230,81,0,0.08)'}
                              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                              {p.product_name}
                              {p.category && <span style={{fontSize:11,color:'var(--fg-3)',marginLeft:8}}>· {p.category}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* 2) 모델 코드 + 시작 번호 + 수량 */}
                <div style={{display:'grid',gridTemplateColumns:'2fr 1fr 1fr',gap:10}}>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>모델 코드 * <span style={{fontWeight:400,color:'var(--fg-3)'}}>(시리얼에 포함됨)</span></label>
                    <input value={m.model_code} onChange={e=>setBulkUnknownModal(s=>s?{...s, model_code:e.target.value.toUpperCase().replace(/\s+/g,'')}:s)}
                      placeholder="예: ST2000DM008" disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',boxSizing:'border-box'}}
                      onFocus={e=>e.target.style.borderColor='var(--warning)'}
                      onBlur={e=>{e.target.style.borderColor='var(--border)'; suggestBulkUnknownStartNumber();}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>시작 번호 *</label>
                    <input type="number" value={m.start_number} onChange={e=>setBulkUnknownModal(s=>s?{...s, start_number:e.target.value}:s)}
                      min={1} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>수량 *</label>
                    <input type="number" value={m.qty} onChange={e=>setBulkUnknownModal(s=>s?{...s, qty:e.target.value}:s)}
                      min={1} max={500} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'monospace',outline:'none',boxSizing:'border-box'}}/>
                  </div>
                </div>

                {/* 3) 시리얼 미리보기 */}
                {preview.length > 0 && (
                  <div style={{padding:'10px 14px',background:'#fff8e1',border:'1px dashed var(--warning)',borderRadius:8}}>
                    <div style={{fontSize:11,fontWeight:700,color:'var(--warning)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:6,display:'flex',alignItems:'center',gap:5}}>
                      <Icon name="preview" size={13}/>
                      미리보기 ({preview.length}개)
                    </div>
                    <div style={{display:'flex',flexWrap:'wrap',gap:5,fontFamily:'monospace',fontSize:11}}>
                      {previewShow.map(s => (
                        <span key={s} style={{padding:'3px 8px',background:'var(--bg-card)',borderRadius:5,border:'1px solid rgba(230,81,0,0.3)',color:'#bf360c',fontWeight:600}}>{s}</span>
                      ))}
                      {previewMore > 0 && (
                        <span style={{padding:'3px 8px',color:'var(--fg-3)',fontSize:11,alignSelf:'center'}}>· · · 외 {previewMore}개 (마지막: <b>{preview[preview.length-1]}</b>)</span>
                      )}
                    </div>
                  </div>
                )}

                {/* 4) 메타 입력 — 입고처/창고/입고일/제품종류 */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>입고처 (source)</label>
                    <select value={m.source} onChange={e=>setBulkUnknownModal(s=>s?{...s, source:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                      <option value="taiwan">대만 (Taiwan)</option>
                      <option value="paju">파주 창고</option>
                      <option value="hq">본사 입고</option>
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>창고 (warehouse)</label>
                    <select value={m.warehouse} onChange={e=>setBulkUnknownModal(s=>s?{...s, warehouse:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                      <option value="hq">본사 (HQ)</option>
                      <option value="paju">파주</option>
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>입고일</label>
                    <input type="date" value={m.receipt_date} onChange={e=>setBulkUnknownModal(s=>s?{...s, receipt_date:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>제품 종류</label>
                    <select value={m.stock_type} onChange={e=>setBulkUnknownModal(s=>s?{...s, stock_type:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'10px 12px',border:'1.5px solid var(--border)',borderRadius:8,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box',cursor:'pointer'}}>
                      <option value="신품">신품</option>
                      <option value="리페킹">리페킹</option>
                      <option value="조립출고">조립출고</option>
                    </select>
                  </div>
                </div>

                {/* 5) 안내 박스 */}
                <div style={{padding:'10px 14px',background:'rgba(0,102,138,0.06)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:8,fontSize:11.5,color:'var(--fg-2)',lineHeight:1.55}}>
                  <Icon name="info" size={13} style={{verticalAlign:'middle',color:'var(--accent)',marginRight:4}}/>
                  실제 시리얼 라벨 확인 후 <strong>배송시트지 입고 행에서 시리얼 chip 더블클릭</strong>으로 진짜 시리얼로 교체 가능합니다.
                </div>

                {/* 결과 메시지 */}
                {m.result && (
                  <div style={{
                    padding:'10px 14px',borderRadius:8,fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:8,
                    background:m.result.type==='ok'?'rgba(0,152,68,0.1)':'rgba(186,26,26,0.1)',
                    color:m.result.type==='ok'?'var(--success)':'var(--danger)',
                    border:`1px solid ${m.result.type==='ok'?'rgba(0,152,68,0.3)':'rgba(186,26,26,0.3)'}`,
                  }}>
                    <Icon name={m.result.type==='ok'?'check_circle':'error'} size={16}/>
                    {m.result.msg}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div style={{padding:'12px 22px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:8,background:'var(--bg-subtle)'}}>
                <button onClick={()=>setBulkUnknownModal(null)} disabled={m.loading}
                  style={{padding:'8px 18px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontWeight:600,cursor:m.loading?'not-allowed':'pointer',opacity:m.loading?0.5:1}}>
                  닫기
                </button>
                {!m.success && (
                  <button onClick={submitBulkUnknown}
                    disabled={m.loading || !m.product_name || !m.model_code || !preview.length}
                    style={{
                      padding:'8px 20px',
                      background:m.loading||!m.product_name||!m.model_code||!preview.length?'var(--surface-mute)':'var(--warning-solid)',
                      color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,
                      cursor:m.loading||!m.product_name||!m.model_code||!preview.length?'not-allowed':'pointer',
                      display:'inline-flex',alignItems:'center',gap:6,
                    }}>
                    <Icon name={m.loading?'hourglass_empty':'inventory_2'} size={16}/>
                    {m.loading?'등록 중…':`${preview.length}개 등록 실행`}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 🧪 v7.25 데모 장비 추가 모달 ═══ */}
      {demoAddModal && (() => {
        const m = demoAddModal;
        return (
          <div onClick={()=>!m.loading && setDemoAddModal(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:'var(--bg-card)',borderRadius:14,width:'100%',maxWidth:540,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'90vh'}}>
              <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#f4faf6'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Icon name="add_circle" size={20} style={{color:'var(--success)'}}/>
                  <h3 style={{fontSize:15,fontWeight:700,margin:0,color:'var(--fg-1)'}}>데모 장비 추가</h3>
                </div>
                <button onClick={()=>!m.loading && setDemoAddModal(null)} disabled={m.loading}
                  style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Icon name="close" size={16}/>
                </button>
              </div>
              <div style={{padding:'18px 20px',flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:12}}>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>카테고리 *</label>
                    <select value={m.category} onChange={e=>setDemoAddModal(s=>s?{...s,category:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}>
                      {Object.entries(DEMO_CATEGORY_LABEL).map(([v,l])=><option key={v} value={v}>{l}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>상태</label>
                    <select value={m.status} onChange={e=>setDemoAddModal(s=>s?{...s,status:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',background:'var(--bg-card)',boxSizing:'border-box'}}>
                      {['정상작동','불량','수리중','점검중','확인불가'].map(v=><option key={v} value={v}>{v}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>모델명 * <span style={{fontWeight:400,color:'var(--fg-3)'}}>(예: TS-264-8G)</span></label>
                  <div style={{position:'relative'}}>
                    <input value={m.model}
                      onChange={e=>{ const v=imeConv(e); setDemoAddModal(s=>s?{...s,model:v}:s); searchDemoModels(v); setDemoModelOpen(true); }} onCompositionEnd={e=>{ const v=normalizeScanInput(e.target.value); setDemoAddModal(s=>s?{...s,model:v}:s); searchDemoModels(v); }}
                      onFocus={()=>{ searchDemoModels(m.model); setDemoModelOpen(true); }}
                      onBlur={()=>setTimeout(()=>setDemoModelOpen(false), 150)}
                      disabled={m.loading} autoComplete="off"
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'monospace',boxSizing:'border-box'}}/>
                    {demoModelOpen && demoModelResults.length>0 && (
                      <div style={{position:'absolute',top:'100%',left:0,right:0,background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:8,marginTop:4,maxHeight:220,overflowY:'auto',zIndex:30,boxShadow:'0 8px 24px rgba(0,0,0,0.18)'}}>
                        {demoModelResults.map(r=>(
                          <div key={r.product_name}
                            onMouseDown={e=>e.preventDefault()}
                            onClick={()=>{ setDemoAddModal(s=>s?{...s,model:r.product_name}:s); setDemoModelResults([]); setDemoModelOpen(false); }}
                            style={{padding:'9px 12px',cursor:'pointer',borderBottom:'1px solid var(--border)',background:r._matchedAlias?'rgba(0,152,68,0.04)':undefined}}
                            onMouseEnter={e=>e.currentTarget.style.background=r._matchedAlias?'rgba(0,152,68,0.1)':'#F1F5F9'}
                            onMouseLeave={e=>e.currentTarget.style.background=r._matchedAlias?'rgba(0,152,68,0.04)':''}>
                            <div style={{fontSize:13,fontWeight:600,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                              {r.product_name}
                              {r._matchedAlias && <span style={{fontSize:10,fontWeight:700,padding:'1px 5px',background:'var(--success-solid)',color:'#fff',borderRadius:4}}>별칭매칭</span>}
                            </div>
                            {r._matchedAlias
                              ? <div style={{fontSize:11,color:'var(--success)'}}>← 별칭: {r._matchedAlias}</div>
                              : (r.category && <div style={{fontSize:11,color:'var(--fg-3)'}}>{r.category}</div>)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>시리얼</label>
                    <input value={m.serial_number} onChange={e=>setDemoAddModal(s=>s?{...s,serial_number:e.target.value}:s)} disabled={m.loading} placeholder="(있으면 입력)"
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'monospace',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>위치</label>
                    <input value={m.location} onChange={e=>setDemoAddModal(s=>s?{...s,location:e.target.value}:s)} disabled={m.loading} placeholder="리버"
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                  </div>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>모델/구성품 (D열)</label>
                  <input value={m.model_detail} onChange={e=>setDemoAddModal(s=>s?{...s,model_detail:e.target.value}:s)} disabled={m.loading} placeholder="HDD/SSD는 모델 코드, NAS는 O/X (구성품)"
                    style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'monospace',boxSizing:'border-box'}}/>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>비고</label>
                  <textarea value={m.note} onChange={e=>setDemoAddModal(s=>s?{...s,note:e.target.value}:s)} disabled={m.loading} rows={2}
                    style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box'}}/>
                </div>
                {m.result && (
                  <div style={{padding:'9px 12px',borderRadius:7,fontSize:12,fontWeight:600,display:'flex',alignItems:'center',gap:6,
                    background:m.result.type==='ok'?'rgba(0,152,68,0.1)':'rgba(186,26,26,0.1)',
                    color:m.result.type==='ok'?'var(--success)':'var(--danger)'}}>
                    <Icon name={m.result.type==='ok'?'check_circle':'error'} size={14}/>{m.result.msg}
                  </div>
                )}
              </div>
              <div style={{padding:'10px 20px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:6}}>
                <button onClick={()=>setDemoAddModal(null)} disabled={m.loading}
                  style={{padding:'8px 16px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:7,fontSize:13,fontWeight:600,cursor:'pointer'}}>닫기</button>
                <button onClick={submitDemoAdd} disabled={m.loading || !m.model.trim()}
                  style={{padding:'8px 18px',background:m.loading||!m.model.trim()?'var(--surface-mute)':'var(--success-solid)',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:700,cursor:m.loading||!m.model.trim()?'not-allowed':'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                  <Icon name={m.loading?'hourglass_empty':'add_circle'} size={14}/>
                  {m.loading?'추가 중…':'추가 실행'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* v7.43: 데모 대여 수정 이력 모달 */}
      {demoHistoryModal && (() => {
        const m = demoHistoryModal;
        const log = Array.isArray(m.log) ? [...m.log].reverse() : []; // 시간 역순 (최신 위)
        const ACTION_COLOR = { create: '#009844', update: '#7b1fa2', delete: '#ba1a1a' };
        const ACTION_LABEL = { create: '등록', update: '수정', delete: '삭제' };
        const FIELD_LABELS = { company: '업체명', shipment_date: '출고일', expected_return_date: '회수예정일', managers: '담당자', requester: '요청자', notes: '특이사항' };
        const SHIP_LABELS = { ship_method: '배송방법', address: '주소', recipient: '수령인', recipient_phone: '연락처' };
        const formatTs = ts => {
          try {
            const d = new Date(ts);
            const pad = n => String(n).padStart(2,'0');
            return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
          } catch (e) { return ts; }
        };
        return (
          <div onClick={()=>setDemoHistoryModal(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:'var(--bg-card)',borderRadius:14,width:'100%',maxWidth:680,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'90vh'}}>
              <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#f6f7fb'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Icon name="history" size={20} style={{color:'var(--fg-2)'}}/>
                  <div>
                    <div style={{fontSize:15,fontWeight:700,color:'var(--fg-1)'}}>수정 이력</div>
                    <div style={{fontSize:11,color:'var(--fg-3)',marginTop:1,fontFamily:'monospace'}}>{m.rental.shipment_number} · {m.rental.company}</div>
                  </div>
                </div>
                <button onClick={()=>setDemoHistoryModal(null)} style={{width:30,height:30,display:'flex',alignItems:'center',justifyContent:'center',border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:'pointer',color:'var(--fg-2)'}}>
                  <Icon name="close" size={16}/>
                </button>
              </div>
              <div style={{padding:'16px 20px',overflow:'auto',flex:1}}>
                {m.loading ? (
                  <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:13}}><Icon name="progress_activity" size={16} style={{verticalAlign:'middle',marginRight:6,animation:'spin 1s linear infinite'}}/>이력 로딩 중…</div>
                ) : m.error ? (
                  <div style={{padding:14,borderRadius:8,background:'rgba(186,26,26,0.08)',color:'var(--danger)',fontSize:13}}>이력 조회 실패: {m.error}</div>
                ) : log.length === 0 ? (
                  <div style={{padding:24,textAlign:'center',color:'var(--fg-3)',fontSize:13}}>
                    <Icon name="hourglass_empty" size={22} style={{display:'block',margin:'0 auto 6px',opacity:0.5}}/>
                    이력이 없습니다.<br/>
                    <span style={{fontSize:11,color:'#a0a0a8',marginTop:6,display:'inline-block'}}>※ Supabase에서 <code style={{background:'var(--bg-mute)',padding:'1px 4px',borderRadius:3}}>ALTER TABLE demo_rentals ADD COLUMN audit_log JSONB DEFAULT '[]'::jsonb;</code> 실행 후 새 변경부터 기록됩니다.</span>
                  </div>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:10}}>
                    {log.map((entry, idx) => {
                      const color = ACTION_COLOR[entry.action] || 'var(--fg-2)';
                      const label = ACTION_LABEL[entry.action] || entry.action;
                      const ch = entry.changes || {};
                      return (
                        <div key={idx} style={{padding:'12px 14px',background:'var(--bg-subtle)',border:`1px solid ${color}33`,borderLeft:`3px solid ${color}`,borderRadius:8}}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:6,gap:8,flexWrap:'wrap'}}>
                            <div style={{display:'flex',alignItems:'center',gap:8}}>
                              <span style={{padding:'2px 8px',borderRadius:9999,fontSize:10,fontWeight:700,background:color,color:'#fff'}}>{label}</span>
                              <span style={{fontSize:12,fontWeight:700,color:'var(--fg-1)'}}>{entry.by || '미지정'}</span>
                            </div>
                            <span style={{fontSize:11,color:'var(--fg-3)',fontFamily:'monospace'}}>{formatTs(entry.ts)}</span>
                          </div>
                          {entry.summary && (
                            <div style={{fontSize:12,color:'var(--fg-2)',marginBottom:ch.fields||ch.shipping||ch.addedItems||ch.removedItems?8:0}}>{entry.summary}</div>
                          )}
                          {(ch.fields || ch.shipping) && (
                            <div style={{display:'flex',flexDirection:'column',gap:4,marginTop:6,paddingTop:6,borderTop:'1px dashed var(--border)'}}>
                              {Object.entries(ch.fields || {}).map(([f, v]) => (
                                <div key={f} style={{fontSize:11,color:'var(--fg-2)',display:'flex',gap:6,flexWrap:'wrap'}}>
                                  <span style={{minWidth:64,fontWeight:700,color:'var(--fg-3)'}}>{FIELD_LABELS[f] || f}</span>
                                  <span style={{color:'var(--danger)',textDecoration:'line-through'}}>{v.from || '∅'}</span>
                                  <span style={{color:'var(--fg-3)'}}>→</span>
                                  <span style={{color:'var(--success)',fontWeight:600}}>{v.to || '∅'}</span>
                                </div>
                              ))}
                              {Object.entries(ch.shipping || {}).map(([f, v]) => (
                                <div key={'s_'+f} style={{fontSize:11,color:'var(--fg-2)',display:'flex',gap:6,flexWrap:'wrap'}}>
                                  <span style={{minWidth:64,fontWeight:700,color:'var(--fg-3)'}}>{SHIP_LABELS[f] || f}</span>
                                  <span style={{color:'var(--danger)',textDecoration:'line-through'}}>{v.from || '∅'}</span>
                                  <span style={{color:'var(--fg-3)'}}>→</span>
                                  <span style={{color:'var(--success)',fontWeight:600}}>{v.to || '∅'}</span>
                                </div>
                              ))}
                            </div>
                          )}
                          {(ch.addedItems?.length || ch.removedItems?.length) && (
                            <div style={{marginTop:6,paddingTop:6,borderTop:'1px dashed var(--border)',display:'flex',flexDirection:'column',gap:3}}>
                              {(ch.addedItems || []).map((it, i) => (
                                <div key={'a'+i} style={{fontSize:11,color:'var(--success)',fontFamily:'monospace'}}>+ {it}</div>
                              ))}
                              {(ch.removedItems || []).map((it, i) => (
                                <div key={'r'+i} style={{fontSize:11,color:'var(--danger)',fontFamily:'monospace',textDecoration:'line-through'}}>− {it}</div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',background:'var(--bg-subtle)',display:'flex',justifyContent:'flex-end'}}>
                <button onClick={()=>setDemoHistoryModal(null)}
                  style={{padding:'8px 18px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',color:'var(--fg-2)'}}>닫기</button>
              </div>
            </div>
          </div>
        );
      })()}

      {demoReturnModal && (() => {
        const m = demoReturnModal;
        return (
          <div onClick={()=>!m.loading && setDemoReturnModal(null)}
            style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()}
              style={{background:'var(--bg-card)',borderRadius:14,width:'100%',maxWidth:540,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'90vh'}}>
              <div style={{padding:'14px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#f4faf6'}}>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  <Icon name="assignment_return" size={20} style={{color:'var(--success)'}}/>
                  <h3 style={{fontSize:15,fontWeight:700,margin:0,color:'var(--fg-1)'}}>데모 회수 처리</h3>
                </div>
                <button onClick={()=>!m.loading && setDemoReturnModal(null)} disabled={m.loading}
                  style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                  <Icon name="close" size={16}/>
                </button>
              </div>
              <div style={{padding:'16px 20px',flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:12}}>
                <div style={{padding:'10px 12px',background:'var(--bg-subtle)',borderRadius:7,fontSize:12}}>
                  <div style={{fontSize:10,color:'var(--fg-3)',fontWeight:700,letterSpacing:'0.04em',textTransform:'uppercase',marginBottom:4}}>회수 대상</div>
                  <div style={{display:'flex',gap:10,marginBottom:4}}>
                    <span style={{fontFamily:'monospace',fontWeight:700,color:'var(--purple)'}}>{m.rental.shipment_number}</span>
                    <span style={{color:'var(--fg-2)'}}>·</span>
                    <span style={{fontWeight:700,color:'var(--fg-1)'}}>{m.rental.company}</span>
                  </div>
                  <div style={{fontSize:11,color:'var(--fg-3)'}}>출고일: {m.rental.shipment_date || '—'} · 회수 예정: {m.rental.expected_return_date || '—'}</div>
                  {m.rental.rental_items_text && (
                    <div style={{marginTop:6,padding:'6px 8px',background:'var(--bg-card)',borderRadius:5,fontSize:11,fontFamily:'monospace',color:'var(--fg-2)',whiteSpace:'pre-line',lineHeight:1.5,maxHeight:100,overflowY:'auto'}}>{m.rental.rental_items_text}</div>
                  )}
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>회수일 *</label>
                    <input type="date" value={m.return_date} onChange={e=>setDemoReturnModal(s=>s?{...s,return_date:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>담당자</label>
                    <input value={m.managers} onChange={e=>setDemoReturnModal(s=>s?{...s,managers:e.target.value}:s)} disabled={m.loading}
                      style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',boxSizing:'border-box'}}/>
                  </div>
                </div>
                <div>
                  <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>회수 특이사항</label>
                  <textarea value={m.notes} onChange={e=>setDemoReturnModal(s=>s?{...s,notes:e.target.value}:s)} disabled={m.loading} rows={2} placeholder="장비 상태 / 분실 / 파손 등 (선택)"
                    style={{width:'100%',padding:'9px 11px',border:'1.5px solid var(--border)',borderRadius:7,fontSize:13,outline:'none',fontFamily:'inherit',resize:'vertical',boxSizing:'border-box'}}/>
                </div>
                <div style={{padding:'8px 12px',background:'rgba(0,102,138,0.06)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:7,fontSize:11,color:'var(--fg-2)'}}>
                  <Icon name="info" size={12} style={{verticalAlign:'middle',color:'var(--accent)',marginRight:4}}/>
                  회수 처리 시 장비들이 자동으로 <strong>'리버'</strong>로 복원되고 다시 대여 가능 상태가 됩니다.
                </div>
                {/* v7.245: 'warn' 추가 — 회수는 됐지만 장비가 안 돌아온 경우 (주황) */}
                {m.result && (()=>{
                  const st = m.result.type === 'ok'
                    ? { bg:'rgba(0,152,68,0.1)',   fg:'#009844', icon:'check_circle' }
                    : m.result.type === 'warn'
                    ? { bg:'rgba(245,124,0,0.12)', fg:'#e65100', icon:'warning' }
                    : { bg:'rgba(186,26,26,0.1)',  fg:'#ba1a1a', icon:'error' };
                  return (
                    <div style={{padding:'9px 12px',borderRadius:7,fontSize:12,fontWeight:600,display:'flex',alignItems:'flex-start',gap:7,
                      background:st.bg, color:st.fg, whiteSpace:'pre-line', lineHeight:1.5}}>
                      <Icon name={st.icon} size={14} style={{marginTop:2,flexShrink:0}}/>{m.result.msg}
                    </div>
                  );
                })()}
              </div>
              <div style={{padding:'10px 20px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:6}}>
                <button onClick={()=>setDemoReturnModal(null)} disabled={m.loading}
                  style={{padding:'8px 16px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:7,fontSize:13,fontWeight:600,cursor:'pointer'}}>닫기</button>
                <button onClick={submitDemoReturn} disabled={m.loading}
                  style={{padding:'8px 18px',background:m.loading?'var(--surface-mute)':'var(--success-solid)',color:'#fff',border:'none',borderRadius:7,fontSize:13,fontWeight:700,cursor:m.loading?'not-allowed':'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                  <Icon name={m.loading?'hourglass_empty':'assignment_return'} size={14}/>
                  {m.loading?'처리 중…':'회수 완료'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 📋 v7.7 대량 제품 등록 모달 ═══ */}
      {bulkProductModal && (
        <div onClick={()=>!bulkProductModal.loading && setBulkProductModal(null)}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
          <div onClick={e=>e.stopPropagation()}
            style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:560,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden',display:'flex',flexDirection:'column',maxHeight:'85vh'}}>
            {/* Header */}
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#f0f6ff'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <Icon name="playlist_add" size={20} style={{color:'var(--accent)'}}/>
                <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>일괄 제품 등록</h3>
              </div>
              <button onClick={()=>!bulkProductModal.loading && setBulkProductModal(null)} disabled={bulkProductModal.loading}
                style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:bulkProductModal.loading?'not-allowed':'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center',opacity:bulkProductModal.loading?0.4:1}}>
                <Icon name="close" size={18}/>
              </button>
            </div>

            {/* Body */}
            <div style={{padding:'18px 22px',flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:12}}>
              <div style={{fontSize:12.5,color:'var(--fg-2)',lineHeight:1.55}}>
                <strong style={{color:'var(--fg-1)'}}>한 줄에 하나씩</strong> 제품명을 입력하세요. 엑셀에서 한 열을 복사해 붙여넣어도 됩니다.<br/>
                <span style={{fontSize:11,color:'var(--fg-3)'}}>이미 등록된 제품은 자동으로 스킵됩니다. 카테고리/폼팩터는 NULL로 저장.</span>
              </div>

              <textarea
                autoFocus
                value={bulkProductModal.text}
                onChange={e=>setBulkProductModal(m=>m?{...m, text:e.target.value, result:null}:m)}
                disabled={bulkProductModal.loading}
                placeholder={`예시:\nTS-264-8G\nTS-464-8G\nTS-873AeU-RP-16G\nQSW-2104-2T\n...`}
                style={{
                  width:'100%',
                  minHeight:240,
                  padding:'10px 12px',
                  border:'2px solid var(--border)',
                  borderRadius:8,
                  fontSize:13,
                  fontFamily:'monospace',
                  lineHeight:1.5,
                  outline:'none',
                  resize:'vertical',
                  background:bulkProductModal.loading?'#f6f7fb':'var(--bg-card)',
                }}
                onFocus={e=>e.target.style.borderColor='var(--accent)'}
                onBlur={e=>e.target.style.borderColor='var(--border)'}
              />

              {/* 실시간 카운트 */}
              {bulkProductModal.text.trim() && (
                <div style={{fontSize:11.5,color:'var(--fg-3)',display:'flex',gap:10,alignItems:'center'}}>
                  <Icon name="info" size={13} style={{color:'var(--accent)'}}/>
                  현재 입력: <strong style={{color:'var(--fg-1)'}}>{bulkProductModal.text.split('\n').map(s=>s.trim()).filter(Boolean).length}</strong>줄
                  <span style={{color:'#bbb'}}>·</span>
                  중복 제거 후: <strong style={{color:'var(--fg-1)'}}>{new Set(bulkProductModal.text.split('\n').map(s=>s.trim()).filter(Boolean)).size}</strong>개
                </div>
              )}

              {/* 결과 메시지 */}
              {bulkProductModal.result && (
                <div style={{
                  padding:'10px 14px',
                  borderRadius:8,
                  fontSize:13,
                  fontWeight:600,
                  background:bulkProductModal.result.type==='ok'?'rgba(0,152,68,0.1)':bulkProductModal.result.type==='warn'?'rgba(255,152,0,0.1)':'rgba(186,26,26,0.1)',
                  color:bulkProductModal.result.type==='ok'?'var(--success)':bulkProductModal.result.type==='warn'?'var(--warning)':'var(--danger)',
                  border:`1px solid ${bulkProductModal.result.type==='ok'?'rgba(0,152,68,0.3)':bulkProductModal.result.type==='warn'?'rgba(255,152,0,0.3)':'rgba(186,26,26,0.3)'}`,
                  display:'flex',
                  alignItems:'center',
                  gap:8,
                }}>
                  <Icon name={bulkProductModal.result.type==='ok'?'check_circle':bulkProductModal.result.type==='warn'?'warning':'error'} size={16}/>
                  {bulkProductModal.result.msg}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{padding:'12px 22px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',gap:8,background:'var(--bg-subtle)'}}>
              <button onClick={()=>setBulkProductModal(null)} disabled={bulkProductModal.loading}
                style={{padding:'8px 18px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontWeight:600,cursor:bulkProductModal.loading?'not-allowed':'pointer',opacity:bulkProductModal.loading?0.5:1}}>
                닫기
              </button>
              <button onClick={submitBulkProducts}
                disabled={bulkProductModal.loading || !bulkProductModal.text.trim()}
                style={{
                  padding:'8px 20px',
                  background:bulkProductModal.loading||!bulkProductModal.text.trim()?'var(--surface-mute)':'var(--accent-solid)',
                  color:'#fff',
                  border:'none',
                  borderRadius:8,
                  fontSize:13,
                  fontWeight:700,
                  cursor:bulkProductModal.loading||!bulkProductModal.text.trim()?'not-allowed':'pointer',
                  display:'inline-flex',
                  alignItems:'center',
                  gap:6,
                }}>
                <Icon name={bulkProductModal.loading?'hourglass_empty':'add_circle'} size={16}/>
                {bulkProductModal.loading?'등록 중…':'등록 실행'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 📝 카탈로그 항목 편집 모달 ═══ */}
      {catalogEditing && (
        <div onClick={()=>setCatalogEditing(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:480,boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'var(--bg-accent-soft)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <Icon name={catalogEditing.isNew?'add_circle':'edit'} size={20} style={{color:'var(--accent)'}}/>
                <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>{catalogEditing.isNew?'카탈로그에 신제품 등록':'카탈로그 항목 수정'}</h3>
              </div>
              <button onClick={()=>setCatalogEditing(null)} style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',color:'var(--fg-3)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Icon name="close" size={18}/>
              </button>
            </div>
            <div style={{padding:'20px 24px'}}>
              {!catalogEditing.isNew && (
                <div style={{padding:'10px 12px',background:'rgba(245,124,0,0.06)',border:'1px solid rgba(245,124,0,0.2)',borderRadius:8,marginBottom:16,fontSize:11,color:'var(--fg-3)',lineHeight:1.5}}>
                  ⚠ 제품명을 변경하면 inventory에서 사용 중인 모든 시리얼의 제품명도 함께 변경됩니다.
                </div>
              )}
              <div>
                <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>제품명 *</label>
                <input value={catalogEditing.product_name}
                  onChange={e=>setCatalogEditing({...catalogEditing, product_name: e.target.value})}
                  placeholder="예: TS-264-EU"
                  autoFocus
                  style={{...inputStyle,fontFamily:'monospace',fontWeight:700,fontSize:14,padding:'10px 12px'}}/>
                <div style={{marginTop:6,fontSize:11,color:'var(--fg-3)'}}>장부 표준명 — 바코드 변형은 [별칭] 버튼에서 매핑</div>
              </div>
            </div>
            <div style={{padding:'14px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',background:'var(--bg-subtle)'}}>
              <button onClick={()=>setCatalogEditing(null)} style={{padding:'9px 18px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',borderRadius:9,fontSize:13,fontWeight:600,cursor:'pointer'}}>취소</button>
              <button onClick={async ()=>{
                if (catalogEditing.isNew) {
                  const name = catalogEditing.product_name.trim();
                  if (!name) { alert('제품명을 입력하세요.'); return; }
                  const res = await ProductService.create({ product_name: name, category: null, form_factor: null });
                  if (!res.ok) { alert('등록 실패: ' + res.msg); return; }
                  setCatalogEditing(null);
                  loadCatalog();
                } else {
                  await saveCatalogItem();
                }
              }} disabled={!catalogEditing.product_name.trim()}
                style={{padding:'9px 22px',border:'none',background:(!catalogEditing.product_name.trim())?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',borderRadius:9,fontSize:13,fontWeight:700,cursor:(!catalogEditing.product_name.trim())?'default':'pointer'}}>
                저장
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 🆕 신제품 등록 모달 ═══ */}
      {newProductModal && (
        <div onClick={()=>setNewProductModal(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,padding:20,backdropFilter:'blur(2px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:520,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'#e8f5e9'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <Icon name="link" size={22} style={{color:'var(--success)'}}/>
                <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>장부 표준명 매핑</h3>
                <span style={{padding:'2px 8px',background:'var(--success-solid)',color:'#fff',borderRadius:6,fontSize:10,fontWeight:700}}>ALIAS</span>
              </div>
              <button onClick={()=>setNewProductModal(null)} style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)'}}>
                <Icon name="close" size={18}/>
              </button>
            </div>
            <div style={{padding:'20px 24px',flex:1,overflowY:'auto'}}>
              <div style={{padding:'12px 14px',background:'rgba(0,152,68,0.08)',border:'1px solid rgba(0,152,68,0.25)',borderRadius:10,marginBottom:14,fontSize:12,color:'var(--fg-2)',lineHeight:1.6}}>
                📚 <b style={{color:'var(--success)'}}>장부 기준 강제</b> — 카탈로그(products)에 없는 제품은 입고할 수 없습니다.<br/>
                바코드 <b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>"{newProductModal.name}"</b>이(가) 카탈로그의 어떤 표준 제품의 별칭인지 선택하세요.<br/>
                <span style={{fontSize:11,color:'var(--fg-3)'}}>다음 입고부터 이 바코드는 표준명으로 자동 변환됩니다.</span>
              </div>
              {/* 모드 토글 제거 — alias 등록만 허용. 신제품은 카탈로그 페이지에서 별도 추가 */}
              <div style={{padding:'8px 12px',background:'var(--bg-subtle)',border:'1px dashed var(--border-strong)',borderRadius:8,marginBottom:14,fontSize:11,color:'var(--fg-3)',display:'flex',alignItems:'center',gap:6}}>
                <Icon name="info" size={13} style={{color:'var(--fg-3)'}}/>
                정말 새로운 제품이라면 <b>카탈로그 페이지</b>에서 먼저 등록 후 입고하세요.
              </div>
              {true ? (
                /* ─── 별칭 모드 ─── */
                <>
                  <div style={{padding:'12px 14px',background:'rgba(0,152,68,0.06)',border:'1px solid rgba(0,152,68,0.25)',borderRadius:10,marginBottom:14,fontSize:12,color:'var(--fg-2)',lineHeight:1.6}}>
                    📎 <b style={{color:'var(--success)'}}>"{newProductModal.name}"</b>을(를) 기존 표준 제품의 별칭으로 등록합니다.<br/>
                    <span style={{fontSize:11,color:'var(--fg-3)'}}>다음 입고부터 이 바코드가 들어오면 자동으로 표준명으로 변환됩니다.</span>
                  </div>
                  <div style={{marginBottom:16}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>들어온 이름 (바코드)</label>
                    <input value={newProductModal.name}
                      onChange={e=>setNewProductModal({...newProductModal, name: e.target.value})}
                      style={{...inputStyle,fontFamily:'monospace',fontWeight:700,fontSize:14,padding:'10px 12px',background:'var(--bg-subtle)'}}/>
                  </div>
                  <div style={{marginBottom:16,position:'relative'}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>
                      표준 제품 선택 <span style={{color:'var(--danger)'}}>*</span>
                    </label>
                    {newProductModal.aliasFor ? (
                      <div style={{padding:'10px 12px',background:'rgba(0,152,68,0.08)',border:'1.5px solid var(--success)',borderRadius:10,display:'flex',alignItems:'center',gap:10}}>
                        <Icon name="check_circle" size={18} style={{color:'var(--success)'}}/>
                        <div style={{flex:1,fontFamily:'monospace',fontWeight:700,fontSize:14,color:'var(--success)'}}>{newProductModal.aliasFor}</div>
                        <button onClick={()=>setNewProductModal({...newProductModal, aliasFor:'', aliasSearch:'', aliasResults:[]})}
                          style={{padding:'4px 10px',border:'1px solid var(--success)',background:'var(--bg-card)',color:'var(--success)',borderRadius:6,fontSize:11,fontWeight:600,cursor:'pointer'}}>변경</button>
                      </div>
                    ) : (
                      <>
                        <input value={newProductModal.aliasSearch||''}
                          onChange={e=>searchAliasTarget(e.target.value)}
                          placeholder="제품명 검색 (예: TS-264-8G)"
                          autoFocus
                          style={{...inputStyle,fontFamily:'monospace',fontSize:13,padding:'10px 12px'}}/>
                        {newProductModal.aliasResults && newProductModal.aliasResults.length > 0 && (
                          <div style={{marginTop:6,maxHeight:200,overflowY:'auto',border:'1px solid var(--border)',borderRadius:10,background:'var(--bg-card)'}}>
                            {newProductModal.aliasResults.map(r => (
                              <div key={r.product_name} onClick={()=>setNewProductModal({...newProductModal, aliasFor: r.product_name, aliasSearch: '', aliasResults: []})}
                                style={{padding:'10px 12px',cursor:'pointer',borderBottom:'1px solid var(--border-mute)'}}
                                onMouseEnter={e=>e.currentTarget.style.background='#F1F5F9'}
                                onMouseLeave={e=>e.currentTarget.style.background=''}>
                                <div style={{fontSize:13,fontWeight:600,fontFamily:'monospace'}}>{r.product_name}</div>
                                <div style={{fontSize:11,color:'var(--fg-3)'}}>{r.category}</div>
                              </div>
                            ))}
                          </div>
                        )}
                        {newProductModal.aliasSearch && (!newProductModal.aliasResults || newProductModal.aliasResults.length === 0) && (
                          <div style={{marginTop:6,padding:10,background:'var(--bg-subtle)',borderRadius:8,fontSize:11,color:'var(--fg-3)',textAlign:'center'}}>검색 결과 없음</div>
                        )}
                      </>
                    )}
                  </div>
                </>
              ) : (
                /* ─── 신제품 등록 모드 ─── */
                <>
                  <div style={{marginBottom:16}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>제품명 *</label>
                    <input value={newProductModal.name}
                      onChange={e=>setNewProductModal({...newProductModal, name: e.target.value})}
                      placeholder="예: TS-264-EU"
                      autoFocus
                      style={{...inputStyle,fontFamily:'monospace',fontWeight:700,fontSize:14,padding:'10px 12px'}}/>
                    <div style={{marginTop:4,fontSize:10,color:'var(--fg-3)'}}>대소문자/하이픈 표기를 카탈로그 표준과 일치시키세요.</div>
                  </div>
                  <div style={{marginBottom:16}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>
                      카테고리 {newProductModal.saveToCatalog && <span style={{color:'var(--danger)'}}>*</span>}
                    </label>
                    <select value={newProductModal.category}
                      onChange={e=>setNewProductModal({...newProductModal, category: e.target.value})}
                      style={{...inputStyle,fontSize:13,padding:'10px 12px',cursor:'pointer'}}>
                      <option value="">카테고리를 선택하세요</option>
                      {productCategories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <div style={{marginTop:4,fontSize:10,color:'var(--fg-3)'}}>{productCategories.length}개 카테고리 등록됨</div>
                  </div>
                  <div style={{marginBottom:16}}>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>폼팩터 <span style={{fontWeight:500,color:'var(--fg-3)'}}>(선택)</span></label>
                    <select value={newProductModal.formFactor || ''}
                      onChange={e=>setNewProductModal({...newProductModal, formFactor: e.target.value})}
                      style={{...inputStyle,fontSize:13,padding:'10px 12px',cursor:'pointer'}}>
                      <option value="">선택 안 함</option>
                      {FORM_FACTORS.map(f => <option key={f} value={f}>{f}</option>)}
                    </select>
                    <div style={{marginTop:4,fontSize:10,color:'var(--fg-3)'}}>Tower / Rackmount / Switch 등 형태 구분 (QNAP 등 카테고리 내에서)</div>
                  </div>
                  {/* 카탈로그 저장 체크박스 */}
                  <div style={{padding:'12px 14px',background:newProductModal.saveToCatalog?'rgba(0,152,68,0.06)':'var(--bg-subtle)',border:`1.5px solid ${newProductModal.saveToCatalog?'var(--success)':'var(--border-strong)'}`,borderRadius:10,transition:'all 0.15s'}}>
                    <label style={{display:'flex',alignItems:'flex-start',gap:10,cursor:'pointer',userSelect:'none'}}>
                      <input type="checkbox" checked={newProductModal.saveToCatalog}
                        onChange={e=>setNewProductModal({...newProductModal, saveToCatalog: e.target.checked})}
                        style={{width:18,height:18,marginTop:1,accentColor:'#009844',cursor:'pointer',flexShrink:0}}/>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:700,color:newProductModal.saveToCatalog?'var(--success)':'var(--fg-3)'}}>
                          마스터 카탈로그에 영구 저장
                        </div>
                        <div style={{fontSize:11,color:'var(--fg-3)',marginTop:3,lineHeight:1.5}}>
                          {newProductModal.saveToCatalog
                            ? '✓ products 테이블에 영구 등록되어 다른 사용자도 검색·재사용 가능'
                            : '⚠ 일회성 입고로만 사용. 카탈로그에는 남기지 않음 (다음 입고 시 재선택 불필요)'
                          }
                        </div>
                      </div>
                    </label>
                  </div>
                </>
              )}
            </div>
            <div style={{padding:'14px 20px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',background:'var(--bg-subtle)'}}>
              <button onClick={()=>setNewProductModal(null)} style={{padding:'9px 18px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',borderRadius:9,fontSize:13,fontWeight:600,cursor:'pointer'}}>취소</button>
              {newProductModal.mode === 'alias' ? (
                <button onClick={confirmNewProduct} disabled={!newProductModal.name.trim()||!newProductModal.aliasFor}
                  style={{padding:'9px 22px',border:'none',background:(!newProductModal.name.trim()||!newProductModal.aliasFor)?'var(--surface-mute)':'var(--success-solid)',color:'#fff',borderRadius:9,fontSize:13,fontWeight:700,cursor:(!newProductModal.name.trim()||!newProductModal.aliasFor)?'default':'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                  <Icon name="link" size={16}/> 별칭 등록 후 입고 진행
                </button>
              ) : (
                <button onClick={confirmNewProduct} disabled={!newProductModal.name.trim()||(newProductModal.saveToCatalog&&!newProductModal.category)} style={{padding:'9px 22px',border:'none',background:(!newProductModal.name.trim()||(newProductModal.saveToCatalog&&!newProductModal.category))?'var(--surface-mute)':'var(--warning-solid)',color:'#fff',borderRadius:9,fontSize:13,fontWeight:700,cursor:(!newProductModal.name.trim()||(newProductModal.saveToCatalog&&!newProductModal.category))?'default':'pointer',display:'inline-flex',alignItems:'center',gap:5}}>
                  <Icon name="add_circle" size={16}/> {newProductModal.saveToCatalog ? '등록 후 입고 진행' : '일회성 입고 진행'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ═══ 📎 별칭 관리 모달 ═══ */}
      {aliasManageModal && (
        <div onClick={()=>setAliasManageModal(null)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1250,padding:20,backdropFilter:'blur(2px)'}}>
          <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:560,maxHeight:'85vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
            <div style={{padding:'16px 20px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'rgba(0,152,68,0.08)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <Icon name="link" size={22} style={{color:'var(--success)'}}/>
                <h3 style={{fontSize:16,fontWeight:700,margin:0,color:'var(--fg-1)'}}>별칭 관리</h3>
                <span style={{padding:'3px 10px',background:'var(--success-solid)',color:'#fff',borderRadius:6,fontSize:11,fontWeight:700,fontFamily:'monospace'}}>{aliasManageModal.productName}</span>
              </div>
              <button onClick={()=>setAliasManageModal(null)} style={{width:28,height:28,border:'none',background:'transparent',borderRadius:6,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)'}}>
                <Icon name="close" size={18}/>
              </button>
            </div>
            <div style={{padding:'18px 22px',flex:1,overflowY:'auto'}}>
              <div style={{padding:'10px 14px',background:'rgba(0,152,68,0.06)',border:'1px solid rgba(0,152,68,0.2)',borderRadius:10,marginBottom:16,fontSize:12,color:'var(--fg-2)',lineHeight:1.6}}>
                바코드/구버전 이름이 들어오면 자동으로 표준명 <b style={{color:'var(--success)',fontFamily:'monospace'}}>{aliasManageModal.productName}</b> 으로 변환됩니다.
              </div>

              {/* 새 별칭 추가 */}
              <div style={{marginBottom:18}}>
                <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>새 별칭 추가</label>
                <div style={{display:'flex',gap:6}}>
                  <input value={aliasManageModal.newAlias||''}
                    onChange={e=>setAliasManageModal({...aliasManageModal, newAlias: e.target.value})}
                    onKeyDown={async e => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const v = (aliasManageModal.newAlias||'').trim();
                        if (!v) return;
                        const r = await addAlias(aliasManageModal.productName, v, 'manual');
                        if (!r.ok) { alert(r.msg); return; }
                        await openAliasManage(aliasManageModal.productName);
                        loadCatalog();
                      }
                    }}
                    placeholder="예: TS-264-8G-EU"
                    style={{...inputStyle,flex:1,fontFamily:'monospace',fontWeight:600,fontSize:13,padding:'9px 12px'}}/>
                  <button onClick={async()=>{
                    const v = (aliasManageModal.newAlias||'').trim();
                    if (!v) return;
                    const r = await addAlias(aliasManageModal.productName, v, 'manual');
                    if (!r.ok) { alert(r.msg); return; }
                    await openAliasManage(aliasManageModal.productName);
                    loadCatalog();
                  }} disabled={!(aliasManageModal.newAlias||'').trim()}
                    style={{padding:'9px 18px',border:'none',background:(aliasManageModal.newAlias||'').trim()?'var(--success-solid)':'var(--surface-mute)',color:'#fff',borderRadius:9,fontSize:13,fontWeight:700,cursor:(aliasManageModal.newAlias||'').trim()?'pointer':'default',display:'inline-flex',alignItems:'center',gap:5}}>
                    <Icon name="add" size={15}/> 추가
                  </button>
                </div>
                <div style={{marginTop:4,fontSize:10,color:'var(--fg-3)'}}>Enter 키로 빠르게 추가 가능. 대소문자는 무시됩니다.</div>
              </div>

              {/* 별칭 목록 */}
              <div>
                <label style={{display:'flex',alignItems:'center',justifyContent:'space-between',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:8}}>
                  <span>등록된 별칭 ({aliasManageModal.aliases.length}개)</span>
                </label>
                {aliasManageModal.loading ? (
                  <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:12}}>로딩 중…</div>
                ) : aliasManageModal.aliases.length === 0 ? (
                  <div style={{padding:30,textAlign:'center',color:'var(--fg-3)',fontSize:12,background:'var(--bg-subtle)',borderRadius:10,border:'1px dashed var(--border-strong)'}}>
                    등록된 별칭이 없습니다.
                  </div>
                ) : (
                  <div style={{display:'flex',flexDirection:'column',gap:6,maxHeight:280,overflowY:'auto'}}>
                    {aliasManageModal.aliases.map(a => (
                      <div key={a.id} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 12px',background:'var(--bg-subtle)',border:'1px solid var(--border)',borderRadius:9}}>
                        <Icon name="link" size={14} style={{color:'var(--success)'}}/>
                        <div style={{flex:1,fontFamily:'monospace',fontWeight:600,fontSize:13,color:'var(--fg-1)',wordBreak:'break-all'}}>{a.alias}</div>
                        <span style={{padding:'2px 7px',background:a.source==='barcode'?'#fff3e0':a.source==='auto'?'#e3f2fd':'#f5f5f5',color:a.source==='barcode'?'var(--warning)':a.source==='auto'?'#1565c0':'var(--fg-3)',borderRadius:5,fontSize:10,fontWeight:700}}>{a.source}</span>
                        <span style={{fontSize:10,color:'#9e9e9e'}}>{a.created_at ? new Date(a.created_at).toLocaleDateString('ko-KR') : ''}</span>
                        <button onClick={async()=>{
                          if (!window.confirm(`별칭 "${a.alias}" 을(를) 삭제하시겠습니까?`)) return;
                          const ok = await deleteAlias(a.id);
                          if (ok) { await openAliasManage(aliasManageModal.productName); loadCatalog(); }
                        }} title="삭제"
                          style={{padding:'4px 8px',border:'1px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',borderRadius:5,fontSize:10,fontWeight:600,cursor:'pointer'}}>삭제</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div style={{padding:'12px 20px',borderTop:'1px solid var(--border)',display:'flex',justifyContent:'flex-end',background:'var(--bg-subtle)'}}>
              <button onClick={()=>setAliasManageModal(null)} style={{padding:'8px 22px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',borderRadius:9,fontSize:13,fontWeight:600,cursor:'pointer'}}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 💣 지뢰찾기 (이스터에그) */}
      <Minesweeper open={mineMode} onClose={()=>setMineMode(false)}/>

      {/* 🔮 3D 큐브 미로 (이스터에그, v7.85 / v7.87 lazy load) */}
      {mirrorMode && (
        <Suspense fallback={null}>
          <Mirror open={mirrorMode} onClose={()=>setMirrorMode(false)}/>
        </Suspense>
      )}

      {/* 🎯 에임 연습장 (이스터에그, v7.91) */}
      {aimMode && (
        <Suspense fallback={null}>
          <AimTrainer open={aimMode} onClose={()=>setAimMode(false)}/>
        </Suspense>
      )}

      {/* 🎆 불꽃놀이 (이스터에그, v7.371) — 빈 공간 10회 클릭. open=false 면 렌더 안 됨 */}
      <FireworksShow open={fireworksMode} onClose={()=>setFireworksMode(false)} />

      {/* 🔄 v7.86: 출고완료 시리얼 사후 교체 모달 */}
      {serialEditState && (
        <div
          onClick={e=>{ if(e.target===e.currentTarget && !serialEditBusy) closeSerialEdit(); }}
          style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',zIndex:100000,display:'flex',alignItems:'center',justifyContent:'center',padding:16,backdropFilter:'blur(2px)'}}
        >
          <div style={{background:'var(--bg-card)',borderRadius:14,padding:24,width:440,maxWidth:'92vw',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',boxSizing:'border-box'}}>
            <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:6}}>
              <Icon name="swap_horiz" size={22} style={{color:'var(--accent)'}}/>
              <span style={{fontSize:16,fontWeight:700,color:'var(--fg-1)'}}>시리얼 교체</span>
            </div>
            <div style={{fontSize:11,color:'var(--fg-3)',marginBottom:16,lineHeight:1.5}}>
              출고 기록의 시리얼을 실제 나간 시리얼로 수정합니다. 재고 상태가 자동 갱신됩니다.
            </div>

            <div style={{fontSize:12,color:'var(--fg-3)',marginBottom:12}}>
              제품: <b style={{color:'var(--fg-1)'}}>{serialEditState.productName}</b>
            </div>

            <div style={{padding:'12px 14px',background:'var(--bg-subtle)',borderRadius:10,border:'1px solid var(--border)',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',letterSpacing:'0.08em',marginBottom:4}}>기존 (출고 완료)</div>
              <div style={{fontSize:15,fontFamily:'monospace',fontWeight:700,color:'var(--fg-1)'}}>{serialEditState.oldSerial}</div>
            </div>

            <div style={{display:'flex',justifyContent:'center',margin:'4px 0'}}>
              <Icon name="arrow_downward" size={20} style={{color:'var(--accent)'}}/>
            </div>

            <div style={{marginBottom:12}}>
              <div style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',letterSpacing:'0.08em',marginBottom:6}}>새 시리얼</div>
              <input
                value={serialEditInput}
                onChange={e=>setSerialEditInput(e.target.value.toUpperCase())}
                onKeyDown={e=>{
                  if(e.key==='Enter' && serialEditCheck?.ok && !serialEditBusy) saveSerialSwap();
                  if(e.key==='Escape' && !serialEditBusy) closeSerialEdit();
                }}
                placeholder="새 시리얼 입력 또는 스캔"
                autoFocus
                disabled={serialEditBusy}
                style={{width:'100%',padding:'12px 14px',border:`1.5px solid ${serialEditCheck?.ok?'var(--success)':serialEditCheck&&!serialEditCheck.ok?'var(--danger)':'var(--border-strong)'}`,borderRadius:8,fontSize:15,fontFamily:'monospace',fontWeight:600,outline:'none',boxSizing:'border-box',background:serialEditBusy?'#f5f5f7':'var(--bg-card)'}}
              />
            </div>

            {/* 검증 결과 */}
            {serialEditCheck && (
              <div style={{padding:'10px 12px',borderRadius:8,marginBottom:14,background:serialEditCheck.ok?'rgba(0,152,68,0.08)':'rgba(186,26,26,0.08)',border:`1px solid ${serialEditCheck.ok?'rgba(0,152,68,0.35)':'rgba(186,26,26,0.35)'}`}}>
                {serialEditCheck.ok ? (
                  <>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--success)',display:'flex',alignItems:'center',gap:5}}>
                      <Icon name="check_circle" size={14}/> 교체 가능
                    </div>
                    <div style={{fontSize:11,color:'var(--fg-2)',marginTop:3}}>재고 상태 · 같은 모델 ({serialEditCheck.product_name})</div>
                  </>
                ) : (
                  <>
                    <div style={{fontSize:12,fontWeight:700,color:'var(--danger)',display:'flex',alignItems:'center',gap:5}}>
                      <Icon name="error" size={14}/> 교체 불가
                    </div>
                    <div style={{fontSize:11,color:'var(--fg-2)',marginTop:3}}>
                      {serialEditCheck.msg || serialEditCheck.error}
                    </div>
                  </>
                )}
              </div>
            )}

            <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
              <button
                onClick={closeSerialEdit}
                disabled={serialEditBusy}
                style={{padding:'10px 20px',background:'var(--bg-card)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,cursor:serialEditBusy?'not-allowed':'pointer',color:'var(--fg-2)',fontWeight:500}}
              >취소</button>
              <button
                onClick={saveSerialSwap}
                disabled={!serialEditCheck?.ok || serialEditBusy}
                style={{padding:'10px 20px',background:(serialEditCheck?.ok && !serialEditBusy)?'var(--accent-solid)':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:600,cursor:(serialEditCheck?.ok && !serialEditBusy)?'pointer':'not-allowed',display:'inline-flex',alignItems:'center',gap:5}}
              >
                {serialEditBusy ? <><Icon name="hourglass_empty" size={14}/>처리 중...</> : <><Icon name="check" size={14}/>교체 저장</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ 🌙 휴식 모드 (이스터에그) ═══ */}
      {restMode && (
        <div
          onClick={()=>setRestMode(false)}
          style={{position:'fixed',inset:0,background:'radial-gradient(ellipse at center, var(--surface-inverse) 0%, #000 100%)',zIndex:9999,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:24,animation:'fadeIn 0.4s ease-out'}}>
          {/* ── 인트로 phase: Strands 풀스크린 ── */}
          <div onClick={e=>e.stopPropagation()} style={{position:'absolute',inset:0,opacity:restPhase==='intro'?1:0,transition:'opacity 1.2s ease-out',pointerEvents:restPhase==='intro'?'auto':'none'}}>
            <Strands
              colors={['#f97316', '#7c3aed', '#06b6d4']}
              count={6}
              speed={0.45}
              amplitude={1.1}
              waviness={1.0}
              thickness={0.9}
              glow={2.8}
              taper={2.5}
              spread={1}
              intensity={0.7}
              saturation={1.4}
              scale={1.6}
              opacity={1}
              style={{width:'100vw',height:'100vh'}}
            />
            {/* 인트로 텍스트 — 하단 배치 (Strands 가운데 밝은 영역 회피) */}
            <div style={{position:'absolute',left:0,right:0,bottom:'12%',display:'flex',flexDirection:'column',alignItems:'center',gap:10,pointerEvents:'none',animation:'fadeIn 0.8s ease-out 0.3s both'}}>
              <div style={{fontSize:42}}>🌙</div>
              <div style={{fontSize:26,fontWeight:300,letterSpacing:'0.18em',color:'#fff5e6',textShadow:'0 2px 24px rgba(0,0,0,0.85), 0 0 30px rgba(0,0,0,0.6)'}}>잠시 쉬어가세요</div>
              <div style={{fontSize:12,color:'rgba(255,245,230,0.7)',letterSpacing:'0.22em',marginTop:4,textShadow:'0 1px 8px rgba(0,0,0,0.7)'}}>REST MODE</div>
            </div>
          </div>

          {/* ── 영상 phase ── */}
          {restPhase === 'video' && (
            <>
              {/* 상단 라벨 */}
              <div style={{position:'absolute',top:24,left:'50%',transform:'translateX(-50%)',display:'flex',alignItems:'center',gap:10,padding:'10px 20px',background:'rgba(255,255,255,0.08)',backdropFilter:'blur(12px)',borderRadius:9999,border:'1px solid rgba(255,255,255,0.15)',color:'#fff5e6',fontSize:13,fontWeight:600,letterSpacing:'0.08em',animation:'fadeIn 0.6s ease-out 0.4s both'}}>
                <span style={{fontSize:18}}>🌙</span>
                <span>휴식 모드</span>
                <span style={{opacity:0.5,fontSize:11}}>· 잠시 쉬어가세요</span>
                {restVideos.length > 1 && <span style={{opacity:0.4,fontSize:10,marginLeft:4}}>(랜덤 {restVideos.length}편 중)</span>}
              </div>
              {/* 영상 */}
              <div onClick={e=>e.stopPropagation()} style={{maxWidth:'min(90vw, 1280px)',maxHeight:'80vh',position:'relative',borderRadius:16,overflow:'hidden',boxShadow:'0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(255,255,255,0.08)',animation:'fadeIn 1s ease-out'}}>
                {restVideo ? (
                  <>
                    <video
                      key={restVideo}
                      src={`${process.env.PUBLIC_URL}/rest/${restVideo}`}
                      autoPlay
                      controls
                      muted={false}
                      playsInline
                      onEnded={() => {
                        // 영상이 끝나면 다음 영상(자기 자신 제외 우선)으로 자동 전환
                        if (restVideos.length === 0) return;
                        if (restVideos.length === 1) {
                          // 1개뿐이면 같은 영상을 다시 재생 (loop 효과)
                          setRestVideo(null);
                          setTimeout(() => setRestVideo(restVideos[0]), 0);
                          return;
                        }
                        const others = restVideos.filter(v => v !== restVideo);
                        const next = others[Math.floor(Math.random() * others.length)];
                        setRestVideo(next);
                      }}
                      onError={(e)=>{e.currentTarget.style.display='none';e.currentTarget.nextElementSibling.style.display='flex';}}
                      style={{display:'block',width:'100%',height:'auto',maxHeight:'80vh',background:'#000'}}
                    />
                    <div style={{display:'none',width:'min(90vw, 720px)',height:'50vh',background:'linear-gradient(135deg, #1a2942, var(--surface-inverse))',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:14,color:'#fff5e6',padding:30,textAlign:'center'}}>
                      <div style={{fontSize:48}}>⚠️</div>
                      <div style={{fontSize:18,fontWeight:700}}>영상 재생 실패</div>
                      <div style={{fontSize:13,opacity:0.7,maxWidth:480,lineHeight:1.6}}>
                        <code style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4,fontSize:11}}>public/rest/{restVideo}</code><br/>
                        파일이 손상되었거나 코덱이 지원되지 않을 수 있습니다.
                      </div>
                    </div>
                  </>
                ) : (
                  <div style={{display:'flex',width:'min(90vw, 720px)',height:'50vh',background:'linear-gradient(135deg, #1a2942, var(--surface-inverse))',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:14,color:'#fff5e6',padding:30,textAlign:'center'}}>
                    <div style={{fontSize:48}}>☕</div>
                    <div style={{fontSize:18,fontWeight:700}}>잠시 쉬어가세요</div>
                    <div style={{fontSize:13,opacity:0.7,maxWidth:480,lineHeight:1.7}}>
                      휴식 영상이 아직 준비되지 않았어요.<br/>
                      <code style={{padding:'2px 6px',background:'rgba(255,255,255,0.1)',borderRadius:4,fontSize:11}}>public/rest/</code>
                      {' 디렉터리에 영상 파일(.mp4 / .webm / .mov)을 자유롭게 넣고 재배포하면'}
                      <br/>자동으로 인식하여 랜덤 재생됩니다.
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* 닫기 버튼 — 항상 표시 */}
          <button
            onClick={(e)=>{e.stopPropagation();setRestMode(false);}}
            title="닫기 (Esc)"
            style={{position:'absolute',top:24,right:24,width:44,height:44,borderRadius:'50%',border:'1px solid rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.08)',backdropFilter:'blur(12px)',color:'#fff',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',transition:'all 0.2s',zIndex:10}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.18)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.08)'}>
            <Icon name="close" size={22}/>
          </button>
          {/* 하단 안내 — 영상 phase 일 때만 */}
          {restPhase === 'video' && (
            <div style={{position:'absolute',bottom:24,fontSize:11,color:'rgba(255,255,255,0.4)',letterSpacing:'0.05em',animation:'fadeIn 0.6s ease-out 0.8s both'}}>
              아무 곳이나 클릭하거나 Esc 키를 눌러 종료
            </div>
          )}
        </div>
      )}

      {/* ═══ 입출고 요약 모달 ═══ */}
      {showTxLogSummary && (() => {
        const sRange = getRangeForMode(summaryMode, summaryOffset);
        // 집계 (취소/환불 처리된 항목은 출고 카운트에서 제외)
        const isProcessedRow = (row) => { try { const p = JSON.parse(row.note); return !!p.processedAs; } catch(e){ return false; } };
        const isReqHeader = (row) => row.serial?.startsWith('REQ-');
        const inbound = summaryRows.filter(r => r.type === 'inbound');
        // 출고: type='outbound'이고 REQ-헤더가 아닌 행 (실제 출고된 시리얼) + 처리된 것 제외
        const outboundShipped = summaryRows.filter(r => r.type === 'outbound' && !isReqHeader(r) && !isProcessedRow(r));
        // 출고 요청: REQ-헤더 행
        const outboundRequests = summaryRows.filter(r => r.type === 'outbound' && isReqHeader(r));
        const requestsCompleted = outboundRequests.filter(r => { try { const p = JSON.parse(r.note); return p.fulfilled && !p.processedAs; } catch(e){ return false; } });
        const requestsPending = outboundRequests.filter(r => { try { const p = JSON.parse(r.note); return !p.fulfilled && !p.processedAs; } catch(e){ return false; } });
        const cancelled = summaryRows.filter(r => { try { const p = JSON.parse(r.note); return p.processedAs === 'cancel'; } catch(e){ return false; } });
        const returned  = summaryRows.filter(r => { try { const p = JSON.parse(r.note); return p.processedAs === 'return'; } catch(e){ return false; } });

        // 모델별 출고 수량 집계 — REQ-헤더의 items 배열을 사용 (다중 품목 출고요청 지원)
        const modelOutCount = {};   // {model: count}
        outboundRequests.forEach(r => {
          try {
            const p = JSON.parse(r.note);
            if (p.processedAs) return;
            const items = Array.isArray(p.items) ? p.items : [{ product_name: r.model, qty: 1 }];
            // v7.247: 부분 환불된 시리얼은 출고 수량에서 뺀다.
            //   (전량 환불이면 위 p.processedAs 에서 이미 걸러졌으므로 여기 안 온다)
            const refunded = refundedSetOf(p);
            items.forEach(it => {
              const m = it.product_name || r.model;
              const ser = Array.isArray(it.fulfilledSerials) ? it.fulfilledSerials : [];
              const fulfilledLen = refunded.size > 0
                ? ser.filter(sn => !refunded.has(sn)).length
                : ser.length;
              modelOutCount[m] = (modelOutCount[m] || 0) + fulfilledLen;
            });
          } catch(e) {
            modelOutCount[r.model] = (modelOutCount[r.model] || 0) + 1;
          }
        });
        const topOutModels = Object.entries(modelOutCount).filter(([,c]) => c > 0).sort((a,b)=>b[1]-a[1]).slice(0,10);

        // 모델별 입고 수량
        const modelInCount = {};
        inbound.forEach(r => { modelInCount[r.model] = (modelInCount[r.model] || 0) + 1; });
        const topInModels = Object.entries(modelInCount).sort((a,b)=>b[1]-a[1]).slice(0,10);

        // 일별 입출고 추이 (그 기간의 각 날짜별 카운트)
        const [sY,sM,sD] = sRange.start.split('-').map(Number);
        const [eY,eM,eD] = sRange.end.split('-').map(Number);
        const startD = new Date(sY,sM-1,sD); const endD = new Date(eY,eM-1,eD);
        const dayCount = Math.round((endD - startD) / (24*60*60*1000)) + 1;
        const daily = Array.from({length: dayCount}, (_, i) => {
          const d = new Date(startD); d.setDate(startD.getDate() + i);
          const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
          return { date: iso, day: d.getDate(), dow: d.getDay(), inbound: 0, outbound: 0 };
        });
        const dailyMap = Object.fromEntries(daily.map(d => [d.date, d]));
        inbound.forEach(r => { if (dailyMap[r.transaction_date]) dailyMap[r.transaction_date].inbound++; });
        outboundShipped.forEach(r => { if (dailyMap[r.transaction_date]) dailyMap[r.transaction_date].outbound++; });
        const maxDayValue = Math.max(1, ...daily.flatMap(d => [d.inbound, d.outbound]));

        const totalOut = outboundShipped.length;
        const totalIn = inbound.length;

        return (
          <div onClick={()=>setShowTxLogSummary(false)} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:20,maxWidth:1100,width:'100%',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
              {/* 헤더 */}
              <div style={{padding:'20px 28px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',background:'linear-gradient(90deg, var(--bg-accent-soft) 0%, var(--bg-card) 100%)'}}>
                <div style={{display:'flex',alignItems:'center',gap:12}}>
                  <div style={{width:40,height:40,borderRadius:10,background:'rgba(0,102,138,0.12)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                    <Icon name="analytics" size={22} style={{color:'var(--accent)'}}/>
                  </div>
                  <div>
                    <h2 style={{fontSize:20,fontWeight:700,color:'var(--fg-1)',marginBottom:2}}>입출고 요약</h2>
                    <div style={{fontSize:12,color:'var(--fg-3)'}}>{sRange.label}</div>
                  </div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:8}}>
                  {/* 주/월 토글 */}
                  <div style={{display:'flex',background:'var(--bg-accent-soft)',padding:4,borderRadius:10,gap:2}}>
                    <button onClick={()=>{setSummaryMode('week');setSummaryOffset(0);}} style={{padding:'6px 14px',border:'none',borderRadius:7,cursor:'pointer',background:summaryMode==='week'?'var(--bg-card)':'transparent',color:summaryMode==='week'?'var(--accent)':'var(--fg-3)',fontSize:12,fontWeight:700,boxShadow:summaryMode==='week'?'0 1px 3px rgba(0,0,0,0.08)':'none'}}>주간</button>
                    <button onClick={()=>{setSummaryMode('month');setSummaryOffset(0);}} style={{padding:'6px 14px',border:'none',borderRadius:7,cursor:'pointer',background:summaryMode==='month'?'var(--bg-card)':'transparent',color:summaryMode==='month'?'var(--accent)':'var(--fg-3)',fontSize:12,fontWeight:700,boxShadow:summaryMode==='month'?'0 1px 3px rgba(0,0,0,0.08)':'none'}}>월간</button>
                  </div>
                  {/* 좌우 네비 */}
                  <button onClick={()=>setSummaryOffset(o=>o-1)} title={summaryMode==='week'?'이전 주':'이전 달'} style={{width:32,height:32,border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="chevron_left" size={18}/></button>
                  <button onClick={()=>setSummaryOffset(o=>o+1)} title={summaryMode==='week'?'다음 주':'다음 달'} style={{width:32,height:32,border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}><Icon name="chevron_right" size={18}/></button>
                  {summaryOffset!==0 && <button onClick={()=>setSummaryOffset(0)} style={{padding:'6px 12px',border:'1px solid var(--accent)',background:'var(--bg-card)',borderRadius:8,cursor:'pointer',fontSize:11,fontWeight:600,color:'var(--accent)'}}>오늘</button>}
                  <button onClick={()=>setShowTxLogSummary(false)} title="닫기" style={{width:32,height:32,border:'1px solid var(--border-strong)',background:'var(--bg-card)',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',marginLeft:6}}><Icon name="close" size={18}/></button>
                </div>
              </div>

              {/* 본문 */}
              <div style={{flex:1,overflowY:'auto',padding:24}}>
                {summaryLoading ? (
                  <div style={{textAlign:'center',padding:80,color:'var(--fg-3)'}}>
                    <Icon name="hourglass_empty" size={40} style={{color:'var(--fg-disabled)'}}/>
                    <div style={{marginTop:12,fontSize:14}}>집계 중...</div>
                  </div>
                ) : (
                  <>
                    {/* KPI 카드 6개 */}
                    <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10,marginBottom:24}}>
                      {[
                        { label:'입고', count: totalIn, color:'var(--success)', bg:'#dcfce7', icon:'inventory_2' },
                        { label:'출고 시리얼', count: totalOut, color:'var(--danger)', bg:'#ffebee', icon:'local_shipping' },
                        { label:'출고요청 완료', count: requestsCompleted.length, color:'var(--accent)', bg:'#dce9ff', icon:'check_circle' },
                        { label:'출고요청 대기', count: requestsPending.length, color:'#f57c00', bg:'#fff8e1', icon:'pending_actions' },
                        { label:'취소', count: cancelled.length, color:'var(--warning)', bg:'#fff4e5', icon:'cancel' },
                        { label:'환불', count: returned.length, color:'#6a1b9a', bg:'#f3e5f5', icon:'undo' },
                      ].map(c => (
                        <div key={c.label} style={{padding:14,background:c.bg,borderRadius:12,border:'1px solid '+c.color+'33'}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:6}}>
                            <Icon name={c.icon} size={14} style={{color:c.color}}/>
                            <span style={{fontSize:11,color:c.color,fontWeight:700,textTransform:'uppercase',letterSpacing:'0.04em'}}>{c.label}</span>
                          </div>
                          <div style={{fontSize:24,fontWeight:700,color:c.color,lineHeight:1}}>{c.count}</div>
                        </div>
                      ))}
                    </div>

                    {/* 일별 추이 막대 차트 */}
                    <div style={{marginBottom:24}}>
                      <h3 style={{fontSize:14,fontWeight:700,marginBottom:12,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                        <Icon name="bar_chart" size={18} style={{color:'var(--accent)'}}/> 일별 입출고 추이
                      </h3>
                      <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,padding:'18px 16px'}}>
                        <div style={{display:'flex',alignItems:'flex-end',gap: dayCount > 20 ? 2 : 6, height:140,marginBottom:8}}>
                          {daily.map((d, i) => {
                            const inH = (d.inbound / maxDayValue) * 100;
                            const outH = (d.outbound / maxDayValue) * 100;
                            const isWeekend = d.dow === 0 || d.dow === 6;
                            return (
                              <div key={d.date} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2,minWidth:0}} title={`${d.date}: 입고 ${d.inbound} / 출고 ${d.outbound}`}>
                                <div style={{width:'100%',height:120,display:'flex',alignItems:'flex-end',gap:1}}>
                                  <div style={{flex:1,background:'var(--success-solid)',borderRadius:'3px 3px 0 0',height:`${inH}%`,minHeight:d.inbound>0?2:0,transition:'height 0.3s'}}/>
                                  <div style={{flex:1,background:'var(--danger-solid)',borderRadius:'3px 3px 0 0',height:`${outH}%`,minHeight:d.outbound>0?2:0,transition:'height 0.3s'}}/>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                        <div style={{display:'flex',gap: dayCount > 20 ? 2 : 6}}>
                          {daily.map(d => {
                            const isWeekend = d.dow === 0 || d.dow === 6;
                            return <div key={d.date} style={{flex:1,textAlign:'center',fontSize:9,color:isWeekend?(d.dow===0?'var(--danger)':'var(--accent)'):'var(--fg-3)',fontWeight:isWeekend?700:500,minWidth:0,overflow:'hidden'}}>{d.day}</div>;
                          })}
                        </div>
                        <div style={{display:'flex',gap:16,marginTop:12,justifyContent:'center',fontSize:11,color:'var(--fg-3)'}}>
                          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:'var(--success-solid)',borderRadius:2}}/>입고</span>
                          <span style={{display:'flex',alignItems:'center',gap:4}}><span style={{width:10,height:10,background:'var(--danger-solid)',borderRadius:2}}/>출고</span>
                        </div>
                      </div>
                    </div>

                    {/* 모델별 Top 10 — 출고 + 입고 2단 */}
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                      {/* 출고 Top */}
                      <div>
                        <h3 style={{fontSize:14,fontWeight:700,marginBottom:10,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                          <Icon name="trending_up" size={18} style={{color:'var(--danger)'}}/> 출고 모델 Top 10
                          <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:500,marginLeft:'auto'}}>총 {totalOut}건</span>
                        </h3>
                        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
                          {topOutModels.length === 0 ? (
                            <div style={{padding:30,textAlign:'center',color:'#bbb',fontSize:13}}>출고 내역 없음</div>
                          ) : topOutModels.map(([model, cnt], i) => {
                            const pct = (cnt / topOutModels[0][1]) * 100;
                            return (
                              <div key={model} style={{padding:'10px 14px',borderBottom:i<topOutModels.length-1?'1px solid #E2E8F0':'none',display:'flex',alignItems:'center',gap:10}}>
                                <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',minWidth:18}}>#{i+1}</span>
                                <span style={{fontSize:13,fontWeight:600,color:'var(--fg-1)',flex:'0 0 auto',minWidth:120}}>{model}</span>
                                <div style={{flex:1,height:8,background:'#fafafa',borderRadius:4,overflow:'hidden'}}>
                                  <div style={{width:`${pct}%`,height:'100%',background:'linear-gradient(90deg, var(--danger-solid), var(--warning-solid))',borderRadius:4}}/>
                                </div>
                                <span style={{fontSize:13,fontWeight:700,color:'var(--danger)',minWidth:30,textAlign:'right'}}>{cnt}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      {/* 입고 Top */}
                      <div>
                        <h3 style={{fontSize:14,fontWeight:700,marginBottom:10,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6}}>
                          <Icon name="trending_down" size={18} style={{color:'var(--success)'}}/> 입고 모델 Top 10
                          <span style={{fontSize:11,color:'var(--fg-3)',fontWeight:500,marginLeft:'auto'}}>총 {totalIn}건</span>
                        </h3>
                        <div style={{background:'var(--bg-card)',border:'1px solid var(--border)',borderRadius:12,overflow:'hidden'}}>
                          {topInModels.length === 0 ? (
                            <div style={{padding:30,textAlign:'center',color:'#bbb',fontSize:13}}>입고 내역 없음</div>
                          ) : topInModels.map(([model, cnt], i) => {
                            const pct = (cnt / topInModels[0][1]) * 100;
                            return (
                              <div key={model} style={{padding:'10px 14px',borderBottom:i<topInModels.length-1?'1px solid #E2E8F0':'none',display:'flex',alignItems:'center',gap:10}}>
                                <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',minWidth:18}}>#{i+1}</span>
                                <span style={{fontSize:13,fontWeight:600,color:'var(--fg-1)',flex:'0 0 auto',minWidth:120}}>{model}</span>
                                <div style={{flex:1,height:8,background:'#fafafa',borderRadius:4,overflow:'hidden'}}>
                                  <div style={{width:`${pct}%`,height:'100%',background:'linear-gradient(90deg, var(--success-solid), #66bb6a)',borderRadius:4}}/>
                                </div>
                                <span style={{fontSize:13,fontWeight:700,color:'var(--success)',minWidth:30,textAlign:'right'}}>{cnt}</span>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 킵 출고처리 모달 — 배송시트지 작성에 필요한 정보 입력 ═══ */}
      {shipKeepModal && (()=>{
        const m = shipKeepModal;
        const group = m.group;
        const activeItems = group.items.filter(i => i.status === 'active');
        const totalQty = activeItems.reduce((s, i) => s + (i.qty || 0), 0);
        const isQuick = isQuickMethod(m.method);   // v7.242
        const isVisit = m.method === '방문수령';
        return (
          <div onClick={()=>setShipKeepModal(null)} style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.55)',backdropFilter:'blur(2px)',zIndex:999,display:'flex',alignItems:'center',justifyContent:'center',padding:20,animation:'fadeIn 0.15s'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:14,padding:0,maxWidth:760,width:'100%',maxHeight:'90vh',overflow:'auto',boxShadow:'0 24px 64px rgba(0,0,0,0.3)'}}>
              {/* 헤더 */}
              <div style={{padding:'18px 24px',background:'linear-gradient(90deg, var(--success-solid) 0%, #00c853 100%)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between',borderRadius:'14px 14px 0 0'}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="local_shipping" size={22} style={{color:'#fff'}}/>
                  <div>
                    <div style={{fontSize:16,fontWeight:700}}>킵 출고처리</div>
                    <div style={{fontSize:12,opacity:0.85,marginTop:2}}>{activeItems.length}개 품목 / 총 {totalQty}개 — 배송시트지 작성</div>
                  </div>
                </div>
                <button onClick={()=>setShipKeepModal(null)} style={{background:'transparent',border:'none',color:'#fff',cursor:'pointer',padding:6,borderRadius:6,display:'flex',opacity:0.85}}
                  onMouseEnter={e=>{e.currentTarget.style.opacity='1';e.currentTarget.style.background='rgba(255,255,255,0.15)';}}
                  onMouseLeave={e=>{e.currentTarget.style.opacity='0.85';e.currentTarget.style.background='transparent';}}>
                  <Icon name="close" size={20}/>
                </button>
              </div>
              {/* 본문 */}
              <div style={{padding:'20px 24px',display:'flex',flexDirection:'column',gap:14}}>
                {/* 거래처 / 품목 요약 */}
                <div style={{padding:'10px 14px',background:'rgba(123,31,162,0.06)',border:'1px solid rgba(123,31,162,0.2)',borderRadius:8,display:'flex',alignItems:'center',gap:10,flexWrap:'wrap',fontSize:12}}>
                  <Icon name="store" size={16} style={{color:'var(--purple)'}}/>
                  <span><span style={{color:'var(--fg-3)'}}>거래처:</span> <b style={{color:'#4a148c'}}>{group.items[0]?.dealer_name || '-'}</b></span>
                  <span style={{color:'#cbd5e1'}}>·</span>
                  <span style={{color:'var(--fg-3)'}}>{activeItems.map(i => `${i.product_name}×${i.qty}`).join(', ')}</span>
                </div>

                {/* 발송 종류 */}
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>발송 종류 *</label>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                    {['택배','퀵서비스','방문수령','1층배송'].map(opt => (
                      <button key={opt} onClick={()=>setShipKeepModal(p => ({...p, method: opt}))}
                        style={{padding:'7px 14px',border:`2px solid ${m.method===opt?'var(--accent)':'var(--border-strong)'}`,background:m.method===opt?'rgba(0,102,138,0.08)':'var(--bg-card)',color:m.method===opt?'var(--accent)':'var(--fg-2)',borderRadius:8,fontSize:13,fontWeight:m.method===opt?700:600,cursor:'pointer'}}>
                        {opt}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 배송지 주소 */}
                <div>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:6,marginBottom:5}}>
                    <label style={{fontSize:12,fontWeight:700,color:'var(--fg-2)'}}>
                      배송지 주소 {isVisit ? <span style={{color:'var(--fg-3)',fontWeight:500}}>(방문수령은 비워두어도 됨)</span> : '*'}
                    </label>
                    {/* v7.195: 다음 우편번호 검색 — 방문수령 아닌 경우에만 표시 (우체국 API 대비) */}
                    {!isVisit && (
                      <button type="button"
                        onClick={() => openDaumPostcode((data) => {
                          setShipKeepModal(p => ({
                            ...p,
                            zip: data.zonecode || '',
                            addr: data.roadAddress || data.address || '',
                          }));
                        })}
                        style={{padding:'3px 9px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'rgba(0,102,138,0.08)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:6,cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:3}}>
                        <Icon name="search" size={11}/>주소검색
                      </button>
                    )}
                  </div>
                  {/* 우편번호 pill (있으면 표시) */}
                  {!isVisit && m.zip && (
                    <div style={{marginBottom:6}}>
                      <span style={{padding:'2px 9px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--bg-accent-strong)',borderRadius:9999,fontFamily:'monospace'}}>{m.zip}</span>
                    </div>
                  )}
                  <textarea value={m.addr}
                    onChange={e=>setShipKeepModal(p => ({...p, addr: e.target.value}))}
                    placeholder={isVisit ? '방문수령 안내사항 (선택)' : '주소검색 또는 직접 입력'}
                    rows={2}
                    style={{...inputStyle,fontSize:13,padding:'9px 12px',resize:'vertical',fontFamily:'inherit'}}/>
                  {/* v7.195: 상세주소 (동·호수 등) — 방문수령 아닌 경우에만 */}
                  {!isVisit && (
                    <input value={m.addrDetail || ''}
                      onChange={e=>setShipKeepModal(p => ({...p, addrDetail: e.target.value}))}
                      placeholder="상세주소 (동·호수 등)"
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',marginTop:6,fontFamily:'inherit'}}/>
                  )}
                </div>

                {/* 수령인 / 핸드폰 */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>수령인 / 업체명</label>
                    <input value={m.recipient}
                      onChange={e=>setShipKeepModal(p => ({...p, recipient: e.target.value}))}
                      placeholder="수령인 이름 또는 업체명"
                      style={{...inputStyle,fontSize:13,padding:'9px 12px'}}/>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>핸드폰 번호</label>
                    <input value={m.phone}
                      onChange={e=>setShipKeepModal(p => ({...p, phone: phoneOnChange(p.phone, e.target.value)}))}
                      placeholder="010-0000-0000"
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',fontFamily:'monospace'}}/>
                  </div>
                </div>

                {/* 매출여부 */}
                <div>
                  <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:6}}>매출여부 *</label>
                  <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:m.salesStatus==='수기입력'?8:0}}>
                    {['입력필요','입력완료','수기입력'].map(opt => {
                      const sel = m.salesStatus === opt;
                      const col = opt === '입력완료' ? '#009844' : opt === '수기입력' ? '#e65100' : '#ba1a1a';
                      return (
                        <button key={opt} onClick={()=>setShipKeepModal(p => ({...p, salesStatus: opt}))}
                          style={{padding:'7px 14px',border:`2px solid ${sel?col:'var(--border-strong)'}`,background:sel?col+'14':'var(--bg-card)',color:sel?col:'var(--fg-2)',borderRadius:8,fontSize:13,fontWeight:sel?700:600,cursor:'pointer'}}>
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                  {m.salesStatus === '수기입력' && (
                    <input value={m.salesStatusManual}
                      onChange={e=>setShipKeepModal(p => ({...p, salesStatusManual: e.target.value}))}
                      placeholder="수기 매출 정보 입력"
                      style={{...inputStyle,fontSize:12,padding:'8px 12px',borderColor:'var(--warning)',background:'#fff8e1'}}/>
                  )}
                </div>

                {/* 작성자 / 요청자 */}
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>작성자</label>
                    <select value={m.author}
                      onChange={e=>setShipKeepModal(p => ({...p, author: e.target.value}))}
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',cursor:'pointer'}}>
                      <option value="">선택</option>
                      {staffList.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={{display:'block',fontSize:12,fontWeight:700,color:'var(--fg-2)',marginBottom:5}}>요청자</label>
                    <select value={m.requester}
                      onChange={e=>setShipKeepModal(p => ({...p, requester: e.target.value}))}
                      style={{...inputStyle,fontSize:13,padding:'9px 12px',cursor:'pointer'}}>
                      <option value="">선택</option>
                      {staffList.map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>

                {/* 퀵서비스 안내 */}
                {isQuick && (
                  <div style={{padding:'8px 12px',background:'rgba(186,26,26,0.06)',border:'1px solid rgba(186,26,26,0.2)',borderRadius:7,fontSize:11,color:'var(--danger)',display:'flex',alignItems:'center',gap:6}}>
                    <Icon name="info" size={14}/>
                    퀵서비스 송장번호 / 금액 / 퀵 매출여부는 배송시트지에서 추가 입력합니다.
                  </div>
                )}
              </div>
              {/* 푸터 */}
              <div style={{padding:'14px 24px',borderTop:'1px solid var(--border-mute)',background:'var(--bg-subtle)',display:'flex',justifyContent:'space-between',alignItems:'center',gap:10,borderRadius:'0 0 14px 14px'}}>
                <button onClick={()=>setShipKeepModal(null)}
                  style={{padding:'9px 18px',border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer'}}>취소</button>
                <button onClick={()=>executeShipKeepGroup(m)}
                  disabled={!m.method || !m.salesStatus || (!isVisit && !m.addr.trim())}
                  style={{padding:'10px 24px',background:(!m.method || !m.salesStatus || (!isVisit && !m.addr.trim()))?'var(--surface-mute)':'var(--success-solid)',color:'#fff',border:'none',borderRadius:8,fontSize:14,fontWeight:700,cursor:(!m.method || !m.salesStatus || (!isVisit && !m.addr.trim()))?'default':'pointer',display:'inline-flex',alignItems:'center',gap:6}}>
                  <Icon name="local_shipping" size={16}/> 출고처리 — 배송시트지 작성
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 배송시트지 로그 상세 모달 ═══ */}
      {logModalItem && (()=>{
        const item = logModalItem;
        let shipInfo = null, processedAs = null;
        try { const p = JSON.parse(item.note); if(p.type==='ship_request'||p.type==='cancel'||p.type==='return'||p.type==='inbound') shipInfo = p; processedAs = p.processedAs || null; } catch(e){}
        if (!shipInfo) return null;
        const isProcessed = !!processedAs;
        const isIn = item.type === 'inbound';
        const items = shipInfo?.items;
        const totalQty = Array.isArray(items) ? items.reduce((s,it)=>s+(it.qty||0),0) : (shipInfo?.qty||0);
        // v7.248: 환불된 시리얼 집합 — 부분 환불(refundedSerials)과 전량 환불 모두 포함.
        //   전량 환불 건은 refundedSerials 가 없던 v7.246 이전 데이터도 있으므로,
        //   processedAs==='return' 이면 출고 시리얼 전체를 환불된 것으로 본다.
        const refundedInModal = isIn
          ? new Set()
          : (processedAs === 'return' && refundedSetOf(shipInfo).size === 0
              ? new Set(listFulfilledSerials(shipInfo).map(x => x.serial))
              : refundedSetOf(shipInfo));
        const closeModal = () => setLogModalItem(null);
        return (
          <div onClick={closeModal} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.5)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20,backdropFilter:'blur(2px)'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:16,width:'100%',maxWidth:1100,maxHeight:'92vh',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.35)',overflow:'hidden'}}>
              {/* Header */}
              <div style={{padding:'18px 24px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between',gap:12,flexShrink:0,background:isIn?'#e8f5ec':'#f0f6ff'}}>
                <div style={{display:'flex',alignItems:'center',gap:10,minWidth:0,flexWrap:'wrap'}}>
                  <Icon name={isIn?'inventory_2':'local_shipping'} size={22} style={{color:isIn?'var(--success)':'var(--accent)'}}/>
                  <h2 style={{fontSize:18,fontWeight:700,margin:0,color:'var(--fg-1)'}}>{shipInfo.reqId || item.serial}</h2>
                  <span style={{padding:'3px 10px',borderRadius:8,fontSize:12,fontWeight:600,background:isProcessed?'rgba(0,0,0,0.06)':isIn?'rgba(0,152,68,0.12)':shipInfo.fulfilled?'rgba(0,152,68,0.1)':'rgba(0,102,138,0.1)',color:isProcessed?'var(--fg-3)':isIn?'var(--success)':shipInfo.fulfilled?'var(--success)':'var(--accent)'}}>
                    {isProcessed?(processedAs==='cancel'?'주문취소':'환불 처리'):isIn?'입고 완료':shipInfo.fulfilled?'출고 완료':'출고 대기'}
                  </span>
                  <span style={{fontSize:13,color:'var(--fg-3)'}}>· {fmtDate(item.transaction_date)}</span>
                </div>
                <button onClick={closeModal} style={{width:32,height:32,border:'none',background:'transparent',borderRadius:8,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',color:'var(--fg-3)',padding:0,flexShrink:0}}>
                  <Icon name="close" size={20}/>
                </button>
              </div>
              {/* Body — scrollable */}
              <div style={{padding:20,flex:1,overflow:'auto',display:'flex',flexDirection:'column',gap:14}}>
                {/* v7.286: 모바일 스캔 라벨 증거사진 — '이 시리얼이 정말 이 박스에 있었나' 대조용.
                     저장만 하고 볼 수 없으면 증거가 아니므로 상세에서 바로 열 수 있게 한다. */}
                {shipInfo && Array.isArray(shipInfo.labelPhotos) && shipInfo.labelPhotos.length > 0 && (
                  <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                      <Icon name="photo_camera" size={15} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',letterSpacing:'0.04em'}}>라벨 증거사진</span>
                      <span style={{fontSize:11,color:'var(--fg-3)'}}>{shipInfo.labelPhotos.length}장</span>
                    </div>
                    <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                      {shipInfo.labelPhotos.map((ph,i) => {
                        const url = AttachmentService.getPublicUrl(ph);
                        return (
                          <a key={i} href={url} target="_blank" rel="noreferrer"
                            title="새 탭에서 원본 보기"
                            style={{display:'block',width:104,height:104,borderRadius:10,overflow:'hidden',
                              border:'1px solid var(--border-strong)'}}>
                            <img src={url} alt={`라벨 ${i+1}`}
                              style={{width:'100%',height:'100%',objectFit:'cover',display:'block'}}/>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 3-column info — 입고/출고 분기 */}
                {isIn ? (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14}}>
                    <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                        <Icon name="flight_takeoff" size={15} style={{color:'var(--success)'}}/>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>입고처</span>
                      </div>
                      <div style={{fontSize:15,fontWeight:600,color:'var(--fg-1)'}}>
                        {shipInfo.from === 'mixed' ? '복합 (품목별 상이)' :
                         shipInfo.from === 'taiwan' ? '대만 (Taiwan)' :
                         shipInfo.from === 'paju' ? '파주 창고' :
                         shipInfo.from === 'hq' ? '본사' :
                         shipInfo.from || '-'}
                      </div>
                    </div>
                    <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                        <Icon name="event" size={15} style={{color:'var(--success)'}}/>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>입고일 / 등록자</span>
                      </div>
                      {/* v7.235: 입고일 직접 수정 — 배송시트지 날짜 + 재고 입고일을 함께 갱신 */}
                      {isIn ? (
                        <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:4}}>
                          <input type="date" defaultValue={item.transaction_date || ''} disabled={recvDateSaving}
                            key={item.transaction_date}
                            onChange={e=>updateInboundDate(item, e.target.value)}
                            title="변경하면 재고 목록의 입고일도 함께 수정됩니다"
                            style={{padding:'5px 8px',border:'1px solid var(--border-strong)',borderRadius:7,fontSize:13.5,fontWeight:600,color:'var(--fg-1)',background:recvDateSaving?'#f1f5f9':'var(--bg-card)',fontFamily:'inherit',cursor:recvDateSaving?'wait':'pointer',outline:'none'}}/>
                          {recvDateSaving && <span style={{fontSize:11,color:'var(--fg-3)'}}>저장 중…</span>}
                        </div>
                      ) : (
                        <div style={{fontSize:14,fontWeight:600,color:'var(--fg-1)',marginBottom:4}}>{fmtDate(item.transaction_date)}</div>
                      )}
                      <div style={{fontSize:12,color:'var(--fg-2)'}}>등록자: <span style={{fontWeight:600,color:'var(--success)'}}>{item.processed_by || '-'}</span></div>
                    </div>
                    <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                        <Icon name="inventory" size={15} style={{color:'var(--success)'}}/>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>입고 요약</span>
                      </div>
                      <div style={{display:'flex',gap:10,alignItems:'baseline'}}>
                        <div><span style={{fontSize:20,fontWeight:700,color:'var(--success)'}}>{Array.isArray(items)?items.length:1}</span><span style={{fontSize:11,color:'var(--fg-3)',marginLeft:3}}>품목</span></div>
                        <span style={{color:'#bbb'}}>·</span>
                        <div><span style={{fontSize:20,fontWeight:700,color:'var(--success)'}}>{totalQty}</span><span style={{fontSize:11,color:'var(--fg-3)',marginLeft:3}}>개</span></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:14}}>
                  <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                      <Icon name="person" size={15} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>수령인 정보</span>
                    </div>
                    <div style={{fontSize:15,fontWeight:600,color:'var(--fg-1)',marginBottom:4}}>{shipInfo.recipient||'-'}</div>
                    <div style={{fontSize:13,color:'var(--fg-2)',display:'flex',alignItems:'center',gap:4}}>
                      <Icon name="call" size={13} style={{color:'var(--fg-3)'}}/> {shipInfo.phone||'-'}
                    </div>
                  </div>
                  <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8,gap:6}}>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <Icon name="location_on" size={15} style={{color:'var(--accent)'}}/>
                        <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 주소</span>
                      </div>
                      {/* v7.194: 카카오(다음) 우편번호 검색 — 자동으로 우편번호+주소 채움. 우체국 택배 API 준비 */}
                      <button
                        onClick={() => openDaumPostcode(async (data) => {
                          try {
                            const p = JSON.parse(item.note || '{}');
                            p.zip = data.zonecode || '';
                            p.addr = data.roadAddress || data.address || '';
                            const { error } = await supabase.from('transaction_log').update({ note: JSON.stringify(p) }).eq('id', item.id);
                            if (error) { alert('주소 저장 실패: ' + error.message); return; }
                            loadTxLog();
                          } catch (e) { alert('주소 저장 실패: ' + (e.message || e)); }
                        })}
                        style={{padding:'3px 8px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'rgba(0,102,138,0.08)',border:'1px solid rgba(0,102,138,0.2)',borderRadius:6,cursor:'pointer',fontFamily:'inherit',display:'inline-flex',alignItems:'center',gap:3}}>
                        <Icon name="search" size={11}/>주소검색
                      </button>
                    </div>
                    {/* 우편번호 (있으면 pill 뱃지) */}
                    {shipInfo.zip && (
                      <div style={{marginBottom:6}}>
                        <span style={{padding:'2px 9px',fontSize:11,fontWeight:700,color:'var(--accent)',background:'var(--bg-accent-strong)',borderRadius:9999,fontFamily:'monospace'}}>{shipInfo.zip}</span>
                      </div>
                    )}
                    {/* 주소 (기존 shipInfo.addr — recAddr1 매핑) */}
                    <div style={{fontSize:14,fontWeight:500,color:'var(--fg-1)',lineHeight:1.5,wordBreak:'break-all'}}>{shipInfo.addr||'-'}</div>
                    {/* v7.194: 상세주소 (신규 shipInfo.addrDetail — recAddr2 매핑) — 더블클릭 편집 */}
                    <div
                      onDoubleClick={async () => {
                        const cur = shipInfo.addrDetail || '';
                        const next = window.prompt('상세주소 (동·호수 등)', cur);
                        if (next === null || next === cur) return;
                        try {
                          const p = JSON.parse(item.note || '{}');
                          p.addrDetail = next.trim();
                          const { error } = await supabase.from('transaction_log').update({ note: JSON.stringify(p) }).eq('id', item.id);
                          if (error) { alert('저장 실패: ' + error.message); return; }
                          loadTxLog();
                        } catch (e) { alert('저장 실패: ' + (e.message || e)); }
                      }}
                      title="더블클릭하여 상세주소 편집"
                      style={{marginTop:6,fontSize:13,color:shipInfo.addrDetail?'var(--fg-2)':'#bbb',lineHeight:1.5,wordBreak:'break-all',cursor:'pointer',padding:'3px 6px',borderRadius:4,border:'1px dashed transparent'}}
                      onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--border-strong)'; e.currentTarget.style.background='var(--bg-card)';}}
                      onMouseLeave={e=>{e.currentTarget.style.borderColor='transparent'; e.currentTarget.style.background='transparent';}}>
                      {shipInfo.addrDetail || '상세주소 (더블클릭하여 입력)'}
                    </div>
                  </div>
                  <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8}}>
                      <Icon name="local_shipping" size={15} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 / 요청 정보</span>
                    </div>
                    <div style={{display:'flex',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                      <span style={{padding:'3px 10px',borderRadius:8,fontSize:11,fontWeight:600,...(shipInfo.method==='택배'?{background:'var(--bg-accent-strong)',color:'#004d6a'}:shipInfo.method==='방문수령'?{background:'rgba(0,102,138,0.12)',color:'var(--accent)'}:{background:'rgba(186,26,26,0.08)',color:'var(--danger)'}),display:'inline-flex',alignItems:'center',gap:3}}>
                        {shipInfo.method==='방문수령'&&<Icon name="storefront" size={11}/>}
                        {shipInfo.method}
                      </span>
                      {shipInfo.salesStatus&&<span style={{padding:'3px 10px',borderRadius:8,fontSize:11,fontWeight:600,background:'rgba(0,152,68,0.1)',color:'var(--success)'}}>{shipInfo.salesStatus}</span>}
                      {shipInfo.quickSalesStatus&&<span style={{padding:'3px 10px',borderRadius:8,fontSize:11,fontWeight:600,background:'rgba(186,26,26,0.1)',color:'var(--danger)',display:'inline-flex',alignItems:'center',gap:3}}><Icon name="electric_moped" size={11}/>퀵 {shipInfo.quickSalesStatus}</span>}
                    </div>
                    {shipInfo.expectedDate&&<div style={{fontSize:12,color:'var(--fg-2)',marginBottom:3,display:'flex',alignItems:'center',gap:4}}><Icon name="event" size={13} style={{color:'var(--accent)'}}/>예상 출고일: <span style={{fontWeight:600,color:'var(--accent)'}}>{fmtDate(shipInfo.expectedDate)}</span></div>}
                    <div style={{fontSize:12,color:'var(--fg-2)'}}>
                      작성자/요청자: <span style={{fontWeight:600,color:'var(--accent)'}}>{shipInfo.author||'-'}</span><span style={{color:'#bbb',margin:'0 4px'}}>/</span><span style={{fontWeight:600}}>{shipInfo.requester||item.processed_by||'-'}</span>
                    </div>
                  </div>
                </div>
                )}
                {/* RMA 상세 (있을 때만) */}
                {shipInfo.rma && (
                  <div style={{background:'rgba(230,81,0,0.05)',borderRadius:12,padding:14,border:'1px solid rgba(230,81,0,0.25)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                      <Icon name="build" size={16} style={{color:'var(--warning)'}}/>
                      <span style={{fontSize:13,fontWeight:700,color:'var(--warning)'}}>{shipInfo.emsType || 'RMA'} 정보</span>
                      {shipInfo.rmaFromDB && <span style={{fontSize:10,fontWeight:700,padding:'2px 8px',background:'rgba(0,102,138,0.1)',color:'var(--accent)',borderRadius:4}}>DB 시리얼</span>}
                      {shipInfo.repaired && <span title={shipInfo.repairedAt?'수리완료: '+new Date(shipInfo.repairedAt).toLocaleString('ko-KR'):''} style={{fontSize:10,fontWeight:700,padding:'2px 8px',background:'rgba(0,152,68,0.1)',color:'var(--success)',borderRadius:4,display:'inline-flex',alignItems:'center',gap:3}}><Icon name="build_circle" size={10}/>수리완료</span>}
                    </div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(2,1fr)',gap:10,fontSize:12}}>
                      <div><span style={{color:'var(--fg-3)',marginRight:6}}>제품명:</span><b style={{color:'var(--fg-1)'}}>{shipInfo.rmaProductName||'-'}</b></div>
                      <div><span style={{color:'var(--fg-3)',marginRight:6}}>시리얼{shipInfo.emsType === 'DOA' && shipInfo.rmaReturnSerial ? ' (나가는)' : ''}:</span><b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaSerial||'-'}</b></div>
                      {/* v7.76: DOA 반품 시리얼 표시 */}
                      {shipInfo.emsType === 'DOA' && shipInfo.rmaReturnSerial && (
                        <div style={{gridColumn:'1 / -1',padding:'8px 10px',background:'rgba(0,152,68,0.06)',borderRadius:6,border:'1px dashed var(--success)'}}>
                          {/* v7.250: 교체 관계를 한 줄로 — 이전 → 바뀐 */}
                          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                            <span style={{color:'var(--success)',fontWeight:700}}>시리얼 교체:</span>
                            <b style={{color:'#9e9e9e',fontFamily:'monospace',textDecoration:'line-through'}}>{shipInfo.rmaSerial||'-'}</b>
                            <Icon name="arrow_forward" size={13} style={{color:'var(--success)'}}/>
                            <b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaReturnSerial}</b>
                          </div>
                          <div style={{fontSize:10,color:'var(--fg-3)',marginTop:4}}>
                            {shipInfo.repaired
                              ? '반품 시리얼이 신품 재고로 등록되었고, 양쪽 비고에 교체 내역이 남았습니다.'
                              : '수리완료 시 신품 재고로 자동 등록되고, 양쪽 비고에 교체 내역이 기록됩니다.'}
                          </div>
                        </div>
                      )}
                      <div><span style={{color:'var(--fg-3)',marginRight:6}}>양식번호:</span><b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaFormNo||'-'}</b></div>
                      <div><span style={{color:'var(--fg-3)',marginRight:6}}>티켓번호:</span><b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaTicketNo||'-'}</b></div>
                      <div style={{gridColumn:'1 / -1'}}><span style={{color:'var(--fg-3)',marginRight:6}}>우체국 EMS:</span><b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaEms||'-'}</b></div>
                    </div>
                  </div>
                )}
                {/* 품목 리스트 */}
                {Array.isArray(items)&&items.length>0&&(
                  <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                      <Icon name="inventory" size={15} style={{color:isIn?'var(--success)':'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>{isIn?'입고':'요청'} 품목 ({items.length}종 · 총 {totalQty}개)</span>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {items.map((it,idx)=>{
                        // 입고는 처음부터 시리얼 확정 (it.serials), 출고는 fulfilled 진행 중 (it.fulfilledSerials)
                        // v7.458: 출고는 실제 출고분(fulfilledSerials) + 준비분(pending)을 합쳐서 센다
                        const pendingSerials = isIn ? [] : (logModalPending[it.product_name] || []);
                        const serials = isIn ? (it.serials || []) : [...(it.fulfilledSerials || []), ...pendingSerials];
                        const f = isIn ? (it.qty || serials.length) : ((it.fulfilledSerials?.length || 0) + pendingSerials.length);
                        const shipped = isIn ? true : ((it.fulfilledSerials?.length || 0) >= it.qty); // 실제 출고 완료
                        const done = isIn ? true : (f >= it.qty);                                     // 준비 완료(출고분+준비분)
                        // v7.248: 이 품목에서 환불된 개수 — 헤더에 '환불 N' 으로 덧붙인다
                        const refundedHere = isIn ? 0
                          : (it.fulfilledSerials || []).filter(sn => refundedInModal.has(sn)).length;
                        const ext = !!it.external;
                        return (
                          <div key={idx} style={{padding:'8px 12px',background:ext?'rgba(245,124,0,0.06)':done?'rgba(0,152,68,0.05)':'var(--bg-card)',borderRadius:8,border:`1px ${ext?'dashed var(--warning)':`solid ${done?'rgba(0,152,68,0.15)':'var(--border)'}`}`}}>
                            <div style={{display:'flex',alignItems:'center',gap:10}}>
                              <div style={{width:24,height:24,borderRadius:6,background:ext?'rgba(245,124,0,0.15)':done?'rgba(0,152,68,0.15)':'var(--bg-accent-strong)',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                                <Icon name={ext?'help_outline':done?'check_circle':'package_2'} size={13} style={{color:ext?'var(--warning)':done?'var(--success)':'var(--fg-2)'}}/>
                              </div>
                              <div style={{flex:1,fontSize:13,fontWeight:600,color:'var(--fg-1)',display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                                <span>{it.product_name}</span>
                                {ext&&<span style={{padding:'1px 6px',borderRadius:4,fontSize:9,fontWeight:700,background:'rgba(245,124,0,0.15)',color:'var(--warning)',border:'1px solid rgba(245,124,0,0.3)'}}>DB 미등록</span>}
                              </div>
                              <div style={{display:'flex',alignItems:'center',gap:6}}>
                                <span style={{fontSize:12,fontWeight:600,color:done?'var(--success)':'var(--fg-3)'}}>{f}/{it.qty}</span>
                                {refundedHere>0&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(123,31,162,0.1)',color:'var(--purple)'}}>환불 {refundedHere}</span>}
                                {!isIn&&!ext&&f>0&&!done&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(255,152,0,0.1)',color:'var(--warning)'}}>진행중</span>}
                                {!isIn&&!ext&&done&&shipped&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(0,152,68,0.12)',color:'var(--success)'}}>완료</span>}
                                {!isIn&&!ext&&done&&!shipped&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(0,102,138,0.1)',color:'var(--accent)'}}>준비완료</span>}
                                {!isIn&&ext&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(245,124,0,0.12)',color:'var(--warning)'}}>외부 자동완료</span>}
                                {isIn&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:700,background:'rgba(0,152,68,0.12)',color:'var(--success)'}}>입고완료</span>}
                                {isIn&&it.from&&<span style={{padding:'1px 6px',borderRadius:5,fontSize:10,fontWeight:600,background:'rgba(0,102,138,0.08)',color:'var(--accent)'}}>{it.from==='taiwan'?'대만':it.from==='paju'?'파주':it.from==='hq'?'본사':it.from}</span>}
                              </div>
                            </div>
                            {serials.length>0&&(
                              <div style={{marginTop:8,paddingTop:8,borderTop:'1px dashed var(--border)',display:'flex',gap:4,flexWrap:'wrap',alignItems:'center'}}>
                                <span style={{fontSize:10,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginRight:4}}>{isIn?'입고':(shipped?'출고':'준비')} 시리얼:</span>
                                {serials.map((sn,si)=>{
                                  // v7.86: 출고완료 fulfilledSerials 만 편집 가능
                                  // 조건: 출고 타입 & 미처리 & 비외부 & 비RMA & 출고완료 상태
                                  // v7.248: 환불된 시리얼은 회색 + 취소선. 편집도 막는다 (이미 재고로 돌아간 건)
                                  const isRefunded = !isIn && refundedInModal.has(sn);
                                  const canEditSerial = !isIn && !ext && !isProcessed && !shipInfo.rma && shipInfo.fulfilled && !isRefunded;
                                  return (
                                    <span key={si}
                                      title={isRefunded ? '환불 처리됨 — 재고로 복귀' : undefined}
                                      style={{padding:canEditSerial?'2px 4px 2px 8px':'2px 8px',borderRadius:6,
                                        background:isRefunded?'#f1f3f5':'var(--bg-subtle)',
                                        border:`1px solid ${isRefunded?'#dcdfe4':'var(--border-strong)'}`,
                                        fontSize:11,fontFamily:'monospace',fontWeight:600,
                                        color:isRefunded?'#9e9e9e':'var(--fg-1)',
                                        textDecoration:isRefunded?'line-through':'none',
                                        display:'inline-flex',alignItems:'center',gap:3}}>
                                      {isRefunded && <Icon name="undo" size={10} style={{color:'#9e9e9e',textDecoration:'none'}}/>}
                                      {sn}
                                      {canEditSerial && (
                                        <button
                                          onClick={()=>openSerialEdit(item, idx, sn, it.product_name)}
                                          title="시리얼 교체 — 실제 나간 시리얼과 다를 때"
                                          style={{padding:'2px 3px',background:'transparent',border:'none',borderRadius:3,cursor:'pointer',color:'var(--fg-3)',display:'inline-flex',alignItems:'center',transition:'all 0.15s'}}
                                          onMouseEnter={e=>{e.currentTarget.style.background='rgba(0,102,138,0.12)';e.currentTarget.style.color='var(--accent)';}}
                                          onMouseLeave={e=>{e.currentTarget.style.background='transparent';e.currentTarget.style.color='var(--fg-3)';}}
                                        >
                                          <Icon name="edit" size={11}/>
                                        </button>
                                      )}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {/* 비고 */}
                <div style={{background:'var(--bg-subtle)',borderRadius:12,padding:14,border:'1px solid var(--border)'}}>
                  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:8}}>
                    <div style={{display:'flex',alignItems:'center',gap:6}}>
                      <Icon name="edit_note" size={15} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>비고</span>
                    </div>
                    {logMemos[item.id] !== undefined && logMemos[item.id] !== (shipInfo.memo||'') && (
                      <button onClick={()=>saveMemo(item,logMemos[item.id])}
                        style={{padding:'5px 14px',borderRadius:8,border:'none',background:'var(--accent-solid)',color:'#fff',fontSize:12,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                        <Icon name="save" size={14}/> 저장
                      </button>
                    )}
                  </div>
                  <textarea
                    value={logMemos[item.id] !== undefined ? logMemos[item.id] : (shipInfo.memo||'')}
                    onChange={e=>setLogMemos(prev=>({...prev,[item.id]:e.target.value}))}
                    placeholder="비고 사항을 입력하세요..."
                    style={{width:'100%',minHeight:100,padding:10,border:'1px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'inherit',resize:'vertical',outline:'none',background:'var(--bg-card)',lineHeight:1.5,boxSizing:'border-box'}}
                  />
                </div>
                {/* 환불 사유 입력 폼 */}
                {refundingId===item.id&&(
                  <div style={{background:'var(--bg-card)',borderRadius:12,padding:14,border:'1.5px solid var(--purple)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:10}}>
                      <Icon name="undo" size={15} style={{color:'var(--purple)'}}/>
                      <span style={{fontSize:13,fontWeight:700,color:'var(--purple)'}}>환불 처리 — 대상 선택 · 사유 입력</span>
                    </div>
                    {/* v7.247: 부분 환불 — 되돌릴 시리얼을 고른다 (기본 전체 선택) */}
                    {(()=>{
                      const cand = refundableOf(shipInfo);
                      const done = refundedSetOf(shipInfo);
                      if (cand.length === 0) return null;
                      const allOn = refundSel.length === cand.length;
                      return (
                        <div style={{marginBottom:10,border:'1px solid var(--border)',borderRadius:8,overflow:'hidden'}}>
                          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:8,padding:'7px 10px',background:'#faf5fb',borderBottom:'1px solid var(--border)'}}>
                            <span style={{fontSize:11.5,fontWeight:700,color:'var(--purple)'}}>
                              환불할 시리얼 {refundSel.length} / {cand.length}개
                              {done.size > 0 && <span style={{color:'var(--fg-3)',fontWeight:600}}> · 이미 환불 {done.size}개</span>}
                            </span>
                            <button onClick={()=>setRefundSel(allOn ? [] : cand.map(x=>x.serial))}
                              style={{padding:'3px 10px',borderRadius:6,border:'1px solid var(--purple)',background:'var(--bg-card)',color:'var(--purple)',fontSize:11,fontWeight:700,cursor:'pointer',fontFamily:'inherit'}}>
                              {allOn ? '전체 해제' : '전체 선택'}
                            </button>
                          </div>
                          <div style={{maxHeight:180,overflowY:'auto',background:'var(--bg-card)'}}>
                            {cand.map(({serial, product}) => {
                              const on = refundSel.includes(serial);
                              return (
                                <label key={serial}
                                  style={{display:'flex',alignItems:'center',gap:8,padding:'6px 10px',borderBottom:'1px solid #f2f4f8',cursor:'pointer',background:on?'rgba(123,31,162,0.05)':'var(--bg-card)'}}>
                                  <input type="checkbox" checked={on}
                                    onChange={()=>setRefundSel(prev => on ? prev.filter(x=>x!==serial) : [...prev, serial])}
                                    style={{width:15,height:15,accentColor:'#7b1fa2',cursor:'pointer'}}/>
                                  <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:'var(--fg-1)'}}>{serial}</span>
                                  {product && <span style={{fontSize:11,color:'var(--fg-3)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{product}</span>}
                                </label>
                              );
                            })}
                          </div>
                          {refundSel.length > 0 && refundSel.length < cand.length && (
                            <div style={{padding:'6px 10px',background:'#fff8e1',fontSize:11,color:'var(--warning)',fontWeight:600,lineHeight:1.45}}>
                              부분 환불입니다. 이 건은 <b>출고 완료로 유지</b>되고, 입출고 요약에서 환불한 {refundSel.length}개만 수량에서 빠집니다.
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    <textarea
                      value={refundReason}
                      onChange={e=>setRefundReason(e.target.value)}
                      placeholder="환불 사유를 자세히 입력하세요 (예: 고객 변심, 제품 불량, 오배송 등)"
                      autoFocus
                      style={{width:'100%',minHeight:60,padding:10,border:'1px solid var(--border)',borderRadius:8,fontSize:13,fontFamily:'inherit',resize:'vertical',outline:'none',background:'var(--bg-subtle)',lineHeight:1.5,boxSizing:'border-box'}}
                    />
                    <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:10}}>
                      <button onClick={()=>{setRefundingId(null);setRefundReason('');setRefundSel([]);}}
                        style={{padding:'8px 16px',borderRadius:8,border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                        취소
                      </button>
                      {/* v7.247: 선택한 시리얼만 넘긴다. 전량이면 기존과 동일하게 처리된다 */}
                      <button onClick={()=>{
                          if(!refundReason.trim()){alert('환불 사유를 입력해주세요.');return;}
                          const cand = refundableOf(shipInfo);
                          if(cand.length>0 && refundSel.length===0){alert('환불할 시리얼을 하나 이상 선택해주세요.');return;}
                          const full = cand.length===0 || refundSel.length>=cand.length;
                          const msg = full
                            ? `정말 환불 처리하시겠습니까?\n\n사유: ${refundReason}\n\n출고된 시리얼 ${cand.length||''}개가 모두 재고로 복귀됩니다.`
                            : `부분 환불하시겠습니까?\n\n사유: ${refundReason}\n대상: ${refundSel.length} / ${cand.length}개\n${refundSel.slice(0,5).join(', ')}${refundSel.length>5?` 외 ${refundSel.length-5}개`:''}\n\n선택한 시리얼만 재고로 복귀하고,\n이 건은 출고 완료로 유지됩니다.`;
                          if(window.confirm(msg)) handleTxAction(item,'return',refundReason,refundSel);
                        }}
                        disabled={loading||!refundReason.trim()}
                        style={{display:'flex',alignItems:'center',gap:6,padding:'8px 18px',borderRadius:8,border:'none',background:refundReason.trim()?'var(--purple-solid)':'var(--surface-mute)',color:'#fff',fontSize:13,fontWeight:700,cursor:refundReason.trim()?'pointer':'default'}}>
                        <Icon name="check" size={15}/>
                        {(()=>{ const c=refundableOf(shipInfo).length; return (c>0 && refundSel.length<c) ? `부분 환불 (${refundSel.length}개)` : '환불 처리 완료'; })()}
                      </button>
                    </div>
                  </div>
                )}
                {/* v7.253: 되돌린 출고의 보관 시리얼 — 목록에서 비웠어도 '무엇이 나갔었는지'는 보여야 한다 */}
                {!isProcessed && !shipInfo.fulfilled && Array.isArray(shipInfo.revertedItems)
                  && shipInfo.revertedItems.some(g => g.fulfilled.length || g.reserved.length) && (
                  <div style={{background:'#f5f9ff',borderRadius:12,padding:14,border:'1px solid rgba(0,102,138,0.25)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:6,marginBottom:8,flexWrap:'wrap'}}>
                      <Icon name="history" size={15} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:700,color:'var(--accent)',textTransform:'uppercase',letterSpacing:'0.04em'}}>되돌린 출고 — 보관된 시리얼</span>
                      {shipInfo.revertedAt && <span style={{fontSize:11,color:'#9e9e9e'}}>{new Date(shipInfo.revertedAt).toLocaleString('ko-KR')}</span>}
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {shipInfo.revertedItems.map((g,gi)=>{
                        const list = [...g.fulfilled, ...g.reserved];
                        if (!list.length) return null;
                        const label = Array.isArray(shipInfo.items) && shipInfo.items[g.idx]
                          ? shipInfo.items[g.idx].product_name : (shipInfo.model || '품목');
                        return (
                          <div key={gi} style={{display:'flex',gap:6,flexWrap:'wrap',alignItems:'center'}}>
                            <span style={{fontSize:11,fontWeight:700,color:'var(--fg-2)',marginRight:2}}>{label}</span>
                            {list.map((sn,si)=>(
                              <span key={si} style={{padding:'2px 8px',borderRadius:6,background:'var(--bg-card)',border:'1px dashed var(--accent)',fontSize:11,fontFamily:'monospace',fontWeight:600,color:'var(--accent)'}}>{sn}</span>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                    <div style={{fontSize:11,color:'var(--fg-3)',marginTop:8,lineHeight:1.5}}>
                      재고로 복귀된 상태입니다. 배송시트지에서 <b>[출고요청]</b> 칩을 누르면 이 시리얼 그대로 다시 출고됩니다 (재스캔 불필요).
                      <br/>새로 스캔해서 출고하면 이 보관분은 사라집니다.
                    </div>
                  </div>
                )}
                {/* v7.247: 부분 환불 이력 — 전량 환불 전까지는 아래 배너가 안 뜨므로 여기서 보여준다 */}
                {!isProcessed && Array.isArray(shipInfo.refunds) && shipInfo.refunds.length > 0 && (
                  <div style={{display:'flex',alignItems:'flex-start',gap:6,padding:'10px 14px',borderRadius:10,background:'rgba(123,31,162,0.06)',border:'1px solid rgba(123,31,162,0.2)'}}>
                    <Icon name="undo" size={16} style={{color:'var(--purple)',marginTop:1,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--purple)',marginBottom:4}}>
                        부분 환불 {refundedSetOf(shipInfo).size} / {listFulfilledSerials(shipInfo).length}개
                        <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',marginLeft:6}}>· 나머지는 출고 상태 유지</span>
                      </div>
                      {shipInfo.refunds.map((rf, i) => (
                        <div key={i} style={{fontSize:12,color:'var(--fg-2)',lineHeight:1.5,paddingTop:i?4:0,borderTop:i?'1px dashed rgba(123,31,162,0.2)':'none',marginTop:i?4:0}}>
                          <span style={{color:'#9e9e9e',fontSize:11}}>{rf.at ? new Date(rf.at).toLocaleString('ko-KR') : ''}</span>
                          {rf.reason && <span style={{marginLeft:6}}>{rf.reason}</span>}
                          <div style={{fontFamily:'monospace',fontSize:11,color:'var(--fg-3)',marginTop:2,wordBreak:'break-all'}}>
                            {(rf.serials||[]).join(', ')}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Processed status banner */}
                {isProcessed&&(
                  <div style={{display:'flex',alignItems:'flex-start',gap:6,padding:'10px 14px',borderRadius:10,background:processedAs==='cancel'?'rgba(255,152,0,0.08)':'rgba(156,39,176,0.08)',border:`1px solid ${processedAs==='cancel'?'rgba(255,152,0,0.2)':'rgba(156,39,176,0.2)'}`}}>
                    <Icon name={processedAs==='cancel'?'cancel':'undo'} size={16} style={{color:processedAs==='cancel'?'var(--warning)':'var(--purple)',marginTop:1,flexShrink:0}}/>
                    <div style={{flex:1}}>
                      <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
                        <span style={{fontSize:13,fontWeight:600,color:processedAs==='cancel'?'var(--warning)':'var(--purple)'}}>{processedAs==='cancel'?'주문취소 처리됨':'환불 처리됨'}</span>
                        {shipInfo.processedAt&&<span style={{fontSize:12,color:'#9e9e9e'}}>{new Date(shipInfo.processedAt).toLocaleString('ko-KR')}</span>}
                      </div>
                      {processedAs==='return'&&shipInfo.refundReason&&(
                        <div style={{marginTop:6,padding:'8px 10px',background:'var(--bg-card)',borderRadius:6,fontSize:12,color:'var(--fg-2)',lineHeight:1.4}}>
                          <span style={{fontWeight:600,color:'var(--purple)'}}>환불 사유:</span> {shipInfo.refundReason}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
              {/* Footer — actions */}
              <div style={{padding:'14px 24px',borderTop:'1px solid var(--border)',display:'flex',gap:10,justifyContent:'flex-end',flexShrink:0,background:'var(--bg-subtle)',flexWrap:'wrap'}}>
                {/* v7.9: 입고 기록 삭제 버튼 — 입고 타입에만 노출 */}
                {isIn && (
                  <button onClick={()=>deleteInboundRecord(item)} disabled={loading}
                    title="이 입고 기록을 완전 삭제 (inventory 시리얼도 함께)"
                    style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',fontSize:13,fontWeight:600,cursor:'pointer',marginRight:'auto'}}>
                    <Icon name="delete_forever" size={16}/> 입고 기록 삭제
                  </button>
                )}
                {/* 미출고 — 내용 수정 / 엑셀 다운로드 / 제품 출고 / 자동 이월 (v7.436: 주문취소 버튼 제거 — 기록 삭제와 역할 중복) */}
                {item.type==='outbound'&&shipInfo.type==='ship_request'&&!isProcessed&&!shipInfo.fulfilled&&(
                  <>
                    <button onClick={()=>{closeModal();openEditModal(item, shipInfo);}}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--accent)',background:'var(--bg-card)',color:'var(--accent)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                      <Icon name="edit" size={16}/> 내용 수정
                    </button>
                    <button onClick={()=>downloadShippingExcel(item)}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid #1d6f42',background:'var(--bg-card)',color:'#1d6f42',fontSize:13,fontWeight:600,cursor:'pointer'}}
                      onMouseEnter={e=>e.currentTarget.style.background='rgba(29,111,66,0.06)'}
                      onMouseLeave={e=>e.currentTarget.style.background='var(--bg-card)'}>
                      <Icon name="grid_on" size={16}/> 엑셀 다운로드
                    </button>
                    <button onClick={async ()=>{
                        setLogModalItem(null);
                        await loadActiveRequest(shipInfo.reqId||item.serial, item.model);
                        setOutboundModalOpen(true);
                      }}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--success)',background:'var(--bg-card)',color:'var(--success)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                      <Icon name="inventory" size={16}/> 제품 출고
                    </button>
                    {/* 자동 이월 토글 — 기본 ON, OFF 시 명확히 표시 */}
                    <button onClick={()=>toggleAutoDefer(item, shipInfo)} disabled={loading}
                      title={shipInfo.autoDefer ? '자동 이월 중 — 출고완료될 때까지 매일 오늘 날짜로 갱신됩니다. 클릭하여 끄기' : '자동 이월 꺼짐 — 클릭하여 다시 켜기'}
                      style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid '+(shipInfo.autoDefer?'var(--border-strong)':'#76777d'),background:shipInfo.autoDefer?'var(--bg-card)':'rgba(0,0,0,0.04)',color:shipInfo.autoDefer?'var(--fg-3)':'var(--fg-2)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                      <Icon name={shipInfo.autoDefer?'schedule':'event_busy'} size={16}/>
                      {shipInfo.autoDefer ? '자동 이월 OFF' : '자동 이월 켜기'}
                    </button>
                  </>
                )}
                {/* 출고완료 — 환불 시작 */}
                {item.type==='outbound'&&shipInfo.type==='ship_request'&&!isProcessed&&shipInfo.fulfilled&&refundingId!==item.id&&(
                  <button onClick={()=>{setRefundingId(item.id);setRefundReason('');setRefundSel(refundableOf(shipInfo).map(x=>x.serial));}} disabled={loading}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--purple)',background:'var(--bg-card)',color:'var(--purple)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                    <Icon name="undo" size={16}/> 환불 처리
                  </button>
                )}
                {/* EMS 수리 완료 — RMA/DOA 행에만 표시 */}
                {item.type==='outbound'&&shipInfo.type==='ship_request'&&shipInfo.rma&&!isProcessed&&!shipInfo.repaired&&refundingId!==item.id&&(
                  <button onClick={()=>handleRepairComplete(item, shipInfo)} disabled={loading}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--success)',background:'var(--bg-card)',color:'var(--success)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                    <Icon name="build_circle" size={16}/> 수리 완료
                  </button>
                )}
                {/* 출고 기록 삭제 (모든 상태) */}
                {item.type==='outbound'&&shipInfo.type==='ship_request'&&refundingId!==item.id&&(
                  <button onClick={()=>handleDeleteShipment(item)} disabled={loading}
                    style={{display:'flex',alignItems:'center',gap:6,padding:'9px 18px',borderRadius:10,border:'1.5px solid var(--danger)',background:'var(--bg-card)',color:'var(--danger)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                    <Icon name="delete_forever" size={16}/> 기록 삭제
                  </button>
                )}
                <button onClick={closeModal} style={{padding:'9px 18px',borderRadius:10,border:'1px solid var(--border-strong)',background:'var(--bg-card)',color:'var(--fg-2)',fontSize:13,fontWeight:600,cursor:'pointer'}}>
                  닫기
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ v7.77: DOA 반품 시리얼 필수 입력 모달 ═══ */}
      {/* [수리 완료] 클릭 시 DOA인데 반품 시리얼이 없으면 자동으로 뜸.
          입력 후 진행하면 신품 재고 등록 + 수리완료 마킹까지 완료됨. */}
      {doaReturnSerialModal && (() => {
        const { item, shipInfo, inputSerial, submitting } = doaReturnSerialModal;
        const canProceed = inputSerial.trim().length > 0 && !submitting;
        const proceed = async () => {
          const sn = inputSerial.trim();
          if (!sn) return;
          setDoaReturnSerialModal(s => s ? { ...s, submitting: true } : s);
          const ok = await doRepairComplete(item, shipInfo, sn);
          if (ok) setDoaReturnSerialModal(null);
          else setDoaReturnSerialModal(s => s ? { ...s, submitting: false } : s);
        };
        return (
          <div onClick={()=>!submitting && setDoaReturnSerialModal(null)}
               style={{position:'fixed',inset:0,background:'rgba(11,28,48,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1200,backdropFilter:'blur(3px)',padding:20}}>
            <div onClick={e=>e.stopPropagation()}
                 style={{background:'var(--bg-card)',borderRadius:14,padding:26,maxWidth:520,width:'100%',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
              <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                <Icon name="input" size={22} style={{color:'var(--success)'}}/>
                <h3 style={{fontSize:18,fontWeight:700,color:'var(--fg-1)',margin:0}}>DOA 수리완료 — 반품 시리얼 입력</h3>
              </div>
              <p style={{fontSize:12,color:'var(--fg-3)',marginTop:0,marginBottom:16,lineHeight:1.5}}>
                벤더로부터 신품 교체로 회수된 시리얼을 입력하세요. 이 시리얼이 <b>신품 (source=DOA)</b>으로 재고에 자동 등록됩니다.
              </p>
              <div style={{padding:'12px 14px',background:'rgba(230,81,0,0.06)',border:'1px solid rgba(230,81,0,0.2)',borderRadius:8,marginBottom:14,fontSize:12}}>
                <div style={{marginBottom:5}}><span style={{color:'var(--fg-3)',marginRight:6}}>제품명:</span><b style={{color:'var(--fg-1)'}}>{shipInfo.rmaProductName || '-'}</b></div>
                <div><span style={{color:'var(--fg-3)',marginRight:6}}>원 시리얼(나가는):</span><b style={{color:'var(--fg-1)',fontFamily:'monospace'}}>{shipInfo.rmaSerial || '-'}</b> <span style={{fontSize:10,color:'var(--fg-3)',marginLeft:6}}>→ 판매불가 상태 유지</span></div>
              </div>
              <div style={{marginBottom:18}}>
                <label style={{display:'block',fontSize:11,fontWeight:700,color:'var(--success)',marginBottom:6,letterSpacing:'0.04em'}}>
                  <Icon name="input" size={12} style={{verticalAlign:'middle'}}/> 반품(들어오는) 시리얼 *
                </label>
                <input autoFocus value={inputSerial}
                  onChange={e=>setDoaReturnSerialModal(s => s ? { ...s, inputSerial: e.target.value } : s)}
                  onKeyDown={e=>{ if(e.key==='Enter' && canProceed) proceed(); }}
                  placeholder="예: WD-WCC7K5XPZK1L"
                  style={{...inputStyle,padding:'10px 14px',fontSize:14,fontFamily:'monospace',fontWeight:600,border:'2px solid var(--success)'}}/>
                <div style={{fontSize:10,color:'var(--fg-3)',marginTop:5,lineHeight:1.4}}>
                  ※ 카테고리는 카탈로그에서 자동 조회됩니다. 이미 DB에 있는 시리얼이면 등록 실패합니다.
                </div>
              </div>
              <div style={{display:'flex',justifyContent:'flex-end',gap:8}}>
                <button onClick={()=>setDoaReturnSerialModal(null)} disabled={submitting}
                        style={{padding:'10px 18px',background:'var(--bg-card)',color:'var(--fg-2)',border:'1px solid var(--border-strong)',borderRadius:8,fontSize:13,fontWeight:600,cursor:submitting?'not-allowed':'pointer'}}>취소</button>
                <button onClick={proceed} disabled={!canProceed}
                        style={{display:'inline-flex',alignItems:'center',gap:6,padding:'10px 20px',background:canProceed?'var(--success-solid)':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:8,fontSize:13,fontWeight:700,cursor:canProceed?'pointer':'not-allowed'}}>
                  <Icon name="build_circle" size={16}/> {submitting ? '처리 중...' : '신품 등록 후 수리완료'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ═══ 출고 처리 모달 (배송시트지에서 진입) ═══ */}
      {outboundModalOpen && activeRequest && (() => {
        const current = activeRequest.models.find(m => m.model === activeRequest.currentModel);
        const c = activeRequest.common || {};
        const fulfilled = current?.fulfilledSerials?.length || 0;
        const qty = current?.qty || 0;
        const pendingCount = pendingSerials.filter(p => p.model === activeRequest.currentModel).length;
        const isComplete = fulfilled >= qty;
        const allItemsReady = activeRequest.models.every(m => {
          const f = m.fulfilledSerials?.length || 0;
          const p = pendingSerials.filter(x => x.model === m.model).length;
          return (f + p) >= m.qty;
        });
        const totalPending = pendingSerials.length;
        const allFulfilled = activeRequest.models.every(m => (m.fulfilledSerials?.length || 0) >= m.qty);

        const closeProcessing = () => {
          // v7.73: 모달만 닫고 activeRequest/pendingSerials는 유지
          //        → 사용자가 스캔 중 배송시트지 확인 등을 위해 잠깐 나가도 시리얼 상태 보존
          //        상태 정리는 실제 [제품 출고] 커밋 시 or 다른 요청 열 때만 발생
          setOutboundModalOpen(false);
          setRequestSerialInput('');
          setRequestMsg(null);
          // 배송시트지 새로고침 (다른 사용자가 상태 변경했을 수 있으므로)
          if (page === 'txlog') loadTxLog();
        };

        return (
          <div onClick={closeProcessing} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.55)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1100,padding:20,backdropFilter:'blur(3px)'}}>
            <div onClick={e=>e.stopPropagation()} style={{background:'var(--bg-card)',borderRadius:20,maxWidth:1100,width:'100%',maxHeight:'92vh',overflow:'hidden',display:'flex',flexDirection:'column',boxShadow:'0 20px 60px rgba(0,0,0,0.3)'}}>
              {/* Header */}
              <div style={{padding:'18px 24px',background:'linear-gradient(90deg, var(--accent-solid) 0%, #40c2fd 100%)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <Icon name="local_shipping" size={22} style={{color:'#fff'}}/>
                  <div>
                    <div style={{fontSize:11,opacity:0.85,fontWeight:600,letterSpacing:'0.04em'}}>출고 요청 처리</div>
                    <div style={{fontSize:18,fontWeight:700,fontFamily:'monospace'}}>{activeRequest.reqId}</div>
                  </div>
                  {allFulfilled && <span style={{padding:'4px 10px',borderRadius:9999,background:'rgba(255,255,255,0.25)',fontSize:11,fontWeight:700}}>✓ 출고 완료</span>}
                </div>
                <button onClick={closeProcessing} title="닫기" style={{background:'rgba(255,255,255,0.2)',border:'none',color:'#fff',padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                  <Icon name="close" size={16}/> 닫기
                </button>
              </div>

              {/* Body */}
              <div style={{flex:1,overflowY:'auto',padding:24,background:'#f0f6ff'}}>
                {/* 3-card info */}
                <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:16}}>
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="person" size={14} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>수령인</span>
                    </div>
                    <div style={{fontSize:14,fontWeight:600,color:'var(--fg-1)',marginBottom:2}}>{c.recipient||'—'}</div>
                    <div style={{fontSize:12,color:'var(--fg-2)'}}>{c.phone||'—'}</div>
                  </div>
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="location_on" size={14} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 주소</span>
                    </div>
                    <div style={{fontSize:13,color:'var(--fg-1)',lineHeight:1.4}}>{c.addr||'—'}</div>
                  </div>
                  <div style={{background:'var(--bg-card)',borderRadius:10,padding:14,border:'1px solid var(--border)'}}>
                    <div style={{display:'flex',alignItems:'center',gap:5,marginBottom:6}}>
                      <Icon name="info" size={14} style={{color:'var(--accent)'}}/>
                      <span style={{fontSize:11,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em'}}>배송 정보</span>
                    </div>
                    <div style={{display:'flex',gap:6,marginBottom:4,flexWrap:'wrap'}}>
                      <span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,...(c.method==='택배'?{background:'var(--bg-accent-strong)',color:'#004d6a'}:c.method==='방문수령'?{background:'rgba(0,102,138,0.12)',color:'var(--accent)'}:{background:'rgba(186,26,26,0.08)',color:'var(--danger)'})}}>{c.method||'-'}</span>
                      {c.salesStatus&&<span style={{padding:'2px 8px',borderRadius:6,fontSize:11,fontWeight:600,background:'rgba(0,152,68,0.1)',color:'var(--success)'}}>{c.salesStatus}</span>}
                    </div>
                    <div style={{fontSize:12,color:'var(--fg-2)'}}>작성자/요청자: <b style={{color:'var(--accent)'}}>{c.author||'-'}</b><span style={{color:'#bbb',margin:'0 4px'}}>/</span><b>{c.requester||'-'}</b></div>
                  </div>
                </div>

                {/* Multi-model selector */}
                {activeRequest.models.length>1&&(
                  <div style={{display:'flex',gap:6,marginBottom:14,flexWrap:'wrap'}}>
                    {activeRequest.models.map(m=>{
                      // v7.67: 모달 버전 탭 카운트에도 pendingSerials 포함 (v7.66 fix가 txlog 패널에만 적용됐던 누락 보강)
                      //         → 시리얼 스캔 후에도 0/N으로 보이던 버그 해결
                      const fulfilledCount = m.fulfilledSerials.length;
                      const pendingForModel = pendingSerials.filter(p => p.model === m.model).length;
                      const totalActive = fulfilledCount + pendingForModel;
                      const done = totalActive >= m.qty;
                      const isCur = m.model === activeRequest.currentModel;
                      const auto = m.external || m.serialOptional;
                      const autoColor = m.external ? '#e65100' : m.serialOptional ? '#7b1fa2' : null;
                      const autoBg = m.external ? 'rgba(245,124,0,0.08)' : m.serialOptional ? 'rgba(123,31,162,0.08)' : null;
                      return (
                        <button key={m.model} onClick={()=>{setActiveRequest(p=>({...p,currentModel:m.model}));setRequestSerialInput('');setRequestMsg(null);setSerialSuggestions([]);setSerialSuggestionsOpen(false);setSerialSuggestionIdx(-1);focusRequestSerial();}} style={{
                          padding:'6px 14px',borderRadius:8,fontSize:13,fontWeight:600,
                          border:auto?`1px dashed ${autoColor}`:isCur?'2px solid var(--accent)':'1px solid var(--border-strong)',
                          background:auto?autoBg:isCur?'var(--bg-accent-strong)':'var(--bg-card)',color:auto?autoColor:isCur?'#004d6a':'var(--fg-2)',
                          cursor:'pointer',display:'flex',alignItems:'center',gap:5
                        }}>
                          {m.external?<Icon name="warning_amber" size={14} style={{color:'var(--warning)'}}/>:m.serialOptional?<Icon name="bolt" size={14} style={{color:'var(--purple)'}}/>:done&&<Icon name="check_circle" size={14} style={{color:'var(--success)'}}/>}
                          {m.model} ({totalActive}/{m.qty})
                          {pendingForModel > 0 && <span style={{fontSize:10,color:'var(--warning)',fontWeight:700,marginLeft:1}}>·대기{pendingForModel}</span>}
                          {m.external&&<span style={{fontSize:10,marginLeft:2,opacity:0.8}}>외부</span>}
                          {m.serialOptional&&<span style={{fontSize:10,marginLeft:2,opacity:0.8}}>S/N면제</span>}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Scan area */}
                <div style={{background:'var(--bg-card)',borderRadius:12,padding:18,border:'1px solid var(--border)',marginBottom:12}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
                    <div style={{flex:'0 0 auto',display:'flex',alignItems:'center',gap:10,padding:'10px 16px',background:'var(--bg-accent-soft)',borderRadius:10,minWidth:200}}>
                      <Icon name="package_2" size={20} style={{color:'var(--accent)'}}/>
                      <div>
                        <div style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em'}}>출고할 제품</div>
                        <div style={{fontSize:15,fontWeight:700,color:'var(--fg-1)'}}>{current?.model||'—'}</div>
                      </div>
                    </div>
                    <div style={{flex:1,minWidth:240,position:'relative'}}>
                      <Icon name="qr_code_scanner" size={20} style={{position:'absolute',left:14,top:'50%',transform:'translateY(-50%)',color:'var(--fg-3)',zIndex:1}}/>
                      <input
                        ref={reqSerialInputModalRef}
                        value={requestSerialInput}
                        onCompositionEnd={e=>setRequestSerialInput(normalizeScanInput(e.target.value))}
                        onChange={e=>{
                          const v = imeConv(e);
                          setRequestSerialInput(v);
                          // v7.196: 입력이 있을 때만 후보 표시 (기본 비표시)
                          setSerialSuggestionsOpen(v.trim().length > 0);
                          setSerialSuggestionIdx(-1);
                        }}
                        onFocus={()=>{ if (requestSerialInput.trim().length > 0) setSerialSuggestionsOpen(true); }}
                        onClick={()=>{ if (serialSuggestions.length > 0) setSerialSuggestionsOpen(true); }}   // v7.244
                        onBlur={()=>setTimeout(()=>setSerialSuggestionsOpen(false),150)}
                        onKeyDown={e=>{
                          // v7.196: 닫힌 상태에서 ↓ 누르면 전체 후보 열기 (기존 브라우징 경로 유지)
                          if (!serialSuggestionsOpen && e.key === 'ArrowDown' && serialSuggestions.length > 0) {
                            e.preventDefault(); setSerialSuggestionsOpen(true); setSerialSuggestionIdx(0); return;
                          }
                          if (serialSuggestionsOpen && serialSuggestions.length > 0) {
                            if (e.key === 'ArrowDown') { e.preventDefault(); setSerialSuggestionIdx(i => Math.min(i + 1, serialSuggestions.length - 1)); return; }
                            if (e.key === 'ArrowUp') { e.preventDefault(); setSerialSuggestionIdx(i => Math.max(i - 1, -1)); return; }
                            if (e.key === 'Tab' && serialSuggestionIdx >= 0) {
                              e.preventDefault();
                              setRequestSerialInput(serialSuggestions[serialSuggestionIdx].serial_number);
                              setSerialSuggestionsOpen(false);
                              return;
                            }
                            if (e.key === 'Escape') { setSerialSuggestionsOpen(false); return; }
                            if (e.key === 'Enter' && serialSuggestionIdx >= 0) {
                              e.preventDefault();
                              setRequestSerialInput(serialSuggestions[serialSuggestionIdx].serial_number);
                              setSerialSuggestionsOpen(false);
                              return;
                            }
                          }
                          if (e.key === 'Enter') handleRequestScan();
                        }}
                        placeholder={isComplete?'요청 수량 모두 처리 완료':'시리얼 스캔 또는 입력  (클릭 또는 ↓ 키: 재고 후보 보기)'}
                        disabled={isComplete||loading}
                        autoFocus
                        style={{...inputStyle,paddingLeft:44,fontFamily:'monospace',fontSize:15,fontWeight:600,background:isComplete?'#fafafa':'var(--bg-card)'}}/>
                      {/* v7.52: 자동완성 dropdown — 현재 모델의 in_stock 시리얼 후보 */}
                      {serialSuggestionsOpen && !isComplete && !current?.external && !current?.serialOptional && serialSuggestions.length > 0 && (
                        <div style={{position:'absolute',top:'100%',left:0,right:0,marginTop:4,background:'var(--bg-card)',border:'1.5px solid var(--accent)',borderRadius:8,boxShadow:'0 8px 20px rgba(0,0,0,0.12)',maxHeight:360,overflowY:'auto',zIndex:50}}>
                          <div style={{padding:'6px 12px',background:'var(--bg-accent-soft)',fontSize:10,fontWeight:700,color:'var(--accent)',letterSpacing:'0.04em',borderBottom:'1px solid var(--border)',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                            <span>📦 재고 시리얼 후보 ({serialSuggestions.length}{serialSuggestions.length >= 50 ? '+' : ''})</span>
                            <span style={{fontSize:9,fontWeight:600,color:'var(--fg-3)'}}>↑↓ 선택 · Tab/Enter 입력 · Esc 닫기</span>
                          </div>
                          {serialSuggestions.map((s, i) => {
                            const isHi = i === serialSuggestionIdx;
                            const q = requestSerialInput.trim().toUpperCase();
                            const sn = s.serial_number;
                            const matchIdx = q ? sn.toUpperCase().indexOf(q) : -1;
                            return (
                              <div key={sn}
                                onMouseDown={e=>{e.preventDefault();setRequestSerialInput(sn);setSerialSuggestionsOpen(false);}}
                                onMouseEnter={()=>setSerialSuggestionIdx(i)}
                                style={{padding:'10px 14px',fontFamily:'monospace',fontSize:13,fontWeight:600,color:'var(--fg-1)',cursor:'pointer',background:isHi?'rgba(0,102,138,0.08)':'var(--bg-card)',borderBottom:i<serialSuggestions.length-1?'1px solid #f0f2f6':'none',display:'flex',alignItems:'center',gap:8}}>
                                <Icon name="barcode" size={14} style={{color:'var(--fg-3)',flexShrink:0}}/>
                                {matchIdx >= 0 ? (
                                  <>
                                    <span>{sn.slice(0, matchIdx)}</span>
                                    <span style={{background:'rgba(0,152,68,0.18)',color:'var(--success)',padding:'1px 3px',borderRadius:3}}>{sn.slice(matchIdx, matchIdx + q.length)}</span>
                                    <span>{sn.slice(matchIdx + q.length)}</span>
                                  </>
                                ) : (<span>{sn}</span>)}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <button onClick={handleRequestScan} disabled={isComplete||loading||!requestSerialInput.trim()} style={{padding:'12px 22px',background:isComplete?'var(--surface-mute)':'var(--accent-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:14,fontWeight:700,cursor:isComplete?'default':'pointer',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                      <Icon name="add" size={18}/> 시리얼 추가
                    </button>
                    {/* v7.145: 임시 EXT- 부여 — 외부 플래그 유실 요청 방어
                        v7.377: v7.376 롤백. ⚠ 이 모달은 인라인/팝업 두 곳에 렌더된다 —
                          조건을 바꿀 때 한쪽만 고치면 다른 쪽에 그대로 남는다. */}
                    {!isComplete && current && (()=>{
                      const filled = (current.fulfilledSerials?.length||0) + pendingSerials.filter(p=>p.model===current.model).length;
                      const remaining = current.qty - filled;
                      if (remaining <= 0) return null;
                      return (
                        <button onClick={assignExternalToCurrentModel} disabled={loading}
                          title={`남은 ${remaining}개를 EXT-${current.model}-XX 임시 시리얼로 자동 부여`}
                          style={{padding:'12px 14px',background:'var(--warning-solid)',color:'#fff',border:'none',borderRadius:10,fontSize:13,fontWeight:700,cursor:loading?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:5,whiteSpace:'nowrap'}}>
                          <Icon name="playlist_add_check" size={16}/> 임시 EXT- {remaining}개
                        </button>
                      );
                    })()}
                    <div style={{flex:'0 0 auto',padding:'10px 14px',background:isComplete?'rgba(0,152,68,0.1)':'var(--bg-accent-soft)',borderRadius:10,textAlign:'center',minWidth:110}}>
                      <div style={{fontSize:11,color:'var(--fg-3)',fontWeight:600,letterSpacing:'0.04em'}}>진행</div>
                      <div style={{fontSize:18,fontWeight:700,color:isComplete?'var(--success)':'var(--accent)'}}>{`${fulfilled + pendingCount}/${qty}`}</div>
                      {pendingCount>0&&<div style={{fontSize:10,color:'var(--warning)',fontWeight:600,marginTop:2}}>대기 {pendingCount}</div>}
                    </div>
                  </div>
                  {requestMsg&&(<div style={{marginTop:12,padding:12,borderRadius:10,fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:8,background:requestMsg.type==='ok'?'rgba(0,152,68,0.08)':requestMsg.type==='err'?'rgba(186,26,26,0.08)':'rgba(255,152,0,0.1)',color:requestMsg.type==='ok'?'var(--success)':requestMsg.type==='err'?'var(--danger)':'var(--warning)'}}>
                    <Icon name={requestMsg.type==='ok'?'check_circle':requestMsg.type==='err'?'error':'warning'} size={18}/>{requestMsg.msg}
                  </div>)}
                </div>

                {/* v7.259: 보관 시리얼 배너 제거 — 자동 주입만으로 충분 (사용자 요청) */}
                {/* Fulfilled serials */}
                {current?.fulfilledSerials?.length>0&&(
                  <div style={{background:'var(--bg-card)',borderRadius:12,padding:14,border:'1px solid var(--border)',marginBottom:12}}>
                    <div style={{fontSize:12,fontWeight:600,color:'var(--fg-3)',textTransform:'uppercase',letterSpacing:'0.04em',marginBottom:8}}>출고 완료된 시리얼 ({current.fulfilledSerials.length}개)</div>
                    <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                      {current.fulfilledSerials.map((s,idx)=>(
                        <span key={idx} style={{display:'flex',alignItems:'center',gap:4,padding:'4px 10px',borderRadius:8,background:'rgba(0,152,68,0.1)',color:'var(--success)',fontSize:12,fontWeight:600,fontFamily:'monospace'}}>
                          <Icon name="check_circle" size={13} style={{color:'var(--success)'}}/> {s}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Commit panel */}
                {totalPending>0&&(
                  <div style={{padding:16,background:'var(--bg-card)',borderRadius:12,border:`1.5px solid ${allItemsReady?'var(--success)':'var(--border-strong)'}`}}>
                    <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12,gap:12,flexWrap:'wrap'}}>
                      <div style={{display:'flex',alignItems:'center',gap:8}}>
                        <Icon name={allItemsReady?'inventory':'pending'} size={20} style={{color:allItemsReady?'var(--success)':'var(--fg-3)'}}/>
                        <div>
                          <div style={{fontSize:14,fontWeight:700,color:allItemsReady?'var(--fg-1)':'var(--fg-3)'}}>{allItemsReady?'출고 준비 완료':'시리얼 등록 진행 중'}</div>
                          <div style={{fontSize:12,color:'var(--fg-3)'}}>
                            {allItemsReady
                              ? `${totalPending}개 시리얼이 출고 처리됩니다`
                              : (()=>{
                                  const remaining = activeRequest.models.filter(m=>{
                                    const f=m.fulfilledSerials?.length||0;
                                    const p=pendingSerials.filter(x=>x.model===m.model).length;
                                    return (f+p)<m.qty;
                                  });
                                  return `남은 품목: ${remaining.map(m=>{const f=m.fulfilledSerials?.length||0;const p=pendingSerials.filter(x=>x.model===m.model).length;return `${m.model} ${m.qty-f-p}개`;}).join(', ')}`;
                                })()
                            }
                          </div>
                        </div>
                      </div>
                      <button onClick={commitRequest} disabled={loading||!allItemsReady} style={{padding:'12px 28px',background:allItemsReady?'var(--success-solid)':'var(--surface-mute)',color:'#fff',border:'none',borderRadius:10,fontSize:15,fontWeight:700,cursor:allItemsReady&&!loading?'pointer':'not-allowed',display:'flex',alignItems:'center',gap:6,whiteSpace:'nowrap'}}>
                        <Icon name="local_shipping" size={18}/> 제품 출고
                      </button>
                    </div>
                    {/* pending 시리얼 그룹 */}
                    <div style={{borderTop:'1px dashed var(--border)',paddingTop:12,display:'flex',flexDirection:'column',gap:8}}>
                      {activeRequest.models.filter(m=>pendingSerials.some(p=>p.model===m.model)).map(m=>{
                        const modelPending = pendingSerials.filter(p=>p.model===m.model);
                        return (
                          <div key={m.model} style={{display:'flex',alignItems:'flex-start',gap:10}}>
                            <div style={{flex:'0 0 auto',padding:'3px 10px',borderRadius:6,fontSize:11,fontWeight:600,background:'var(--bg-accent-soft)',color:'var(--accent)',whiteSpace:'nowrap'}}>{m.model}</div>
                            <div style={{flex:1,display:'flex',flexWrap:'wrap',gap:5}}>
                              {modelPending.map((p,idx)=>(
                                <span key={idx} style={{display:'flex',alignItems:'center',gap:4,padding:'3px 8px',borderRadius:6,background:'rgba(245,124,0,0.1)',color:'var(--warning)',fontSize:11,fontWeight:600,fontFamily:'monospace'}}>
                                  <Icon name="pending" size={11} style={{color:'var(--warning)'}}/> {p.serial}
                                  <button onClick={()=>removePendingSerial(p.serial)} style={{background:'transparent',border:'none',cursor:'pointer',color:'var(--warning)',padding:0,marginLeft:2,display:'flex'}}>{/* v7.424: removePendingSerial 로 라우팅 — 서버 pending 삭제 + ship_hold 해제까지 (기존엔 로컬 state 만 지워 desync) */}
                                    <Icon name="close" size={12}/>
                                  </button>
                                </span>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* 모두 처리된 경우 안내 */}
                {allFulfilled && totalPending===0 && (
                  <div style={{padding:18,background:'var(--bg-card)',borderRadius:12,border:'1.5px solid var(--success)',textAlign:'center'}}>
                    <Icon name="check_circle" size={36} style={{color:'var(--success)'}}/>
                    <div style={{marginTop:6,fontSize:15,fontWeight:700,color:'var(--success)'}}>출고가 완료되었습니다</div>
                    <div style={{fontSize:12,color:'var(--fg-3)',marginTop:4}}>닫기 버튼을 눌러 배송시트지로 돌아갑니다</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      </ClickSpark>
    </>
  );
}
